import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
// Build: 2026-06-19 12:00:00 v35-contrato-autofill

// ============================================================
// CONFIGURAÇÃO DA API
// Ajuste para o seu subdomínio real
// ============================================================
const API_BASE = window.location.hostname === 'localhost' ? 'https://app.alvesecorreaadvogados.com.br/api' : window.location.origin + '/api';
const CLAUDE_URL = window.location.origin + '/claude.php'; // raiz, igual aos arquivos Google

// ============================================================
// FUNÇÕES DE COMUNICAÇÃO COM A API
// ============================================================
async function apiGet(endpoint) {
  const token = sessionStorage.getItem('ac_token') || '';
  const res = await fetch(API_BASE + endpoint, {
    headers: { 'X-Token': token }
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.erro || 'Erro na API');
  return data.data;
}

async function apiPost(endpoint, body, method = 'POST') {
  const token = sessionStorage.getItem('ac_token') || '';
  // PUT e DELETE enviados como POST com _method no body (compatibilidade PHP/Hostinger)
  const realMethod = (method === 'PUT' || method === 'DELETE') ? 'POST' : method;
  const realBody   = (method === 'PUT' || method === 'DELETE') ? { ...body, _method: method } : body;
  const res = await fetch(API_BASE + endpoint, {
    method: realMethod,
    headers: { 'Content-Type': 'application/json', 'X-Token': token },
    body: JSON.stringify(realBody)
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.erro || 'Erro na API');
  return data.data;
}

// ============================================================
// CONTEXTO DE USUÁRIO — perfil logado disponível em qualquer módulo
// sem precisar passar "usuario" por prop em toda a árvore.
// ============================================================
const UsuarioContext = React.createContext(null);
function useUsuarioAtual() { return React.useContext(UsuarioContext); }
function useIsVisualizador() { return React.useContext(UsuarioContext)?.perfil === "Visualizador"; }

// ============================================================
// ERROR BOUNDARY - Captura erros de runtime e exibe na tela
// ============================================================
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    this.setState({ info });
    console.error("ERRO CAPTURADO:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: "100vh", display: "flex", alignItems: "center",
          justifyContent: "center", background: "#1A1610", padding: 40
        }}>
          <div style={{
            background: "#fff", borderRadius: 12, padding: 40,
            maxWidth: 800, width: "100%", boxShadow: "0 8px 40px rgba(0,0,0,0.4)"
          }}>
            <h2 style={{color:"#C94C4C", marginBottom: 16, fontSize: 20}}>
              ⚠️ Erro detectado no sistema
            </h2>
            <p style={{color:"#666", marginBottom: 16, fontSize: 14}}>
              Copie a mensagem abaixo e envie para o suporte:
            </p>
            <pre style={{
              background: "#f5f5f5", padding: 16, borderRadius: 8,
              fontSize: 11, overflow: "auto", maxHeight: 400,
              border: "1px solid #ddd", color: "#333", whiteSpace: "pre-wrap",
              wordBreak: "break-all"
            }}>
              {String(this.state.error)}
              {this.state.info?.componentStack}
            </pre>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: 20, padding: "10px 24px", background: "#C9A84C",
                color: "#fff", border: "none", borderRadius: 8,
                fontSize: 14, cursor: "pointer", fontWeight: 600
              }}
            >
              🔄 Recarregar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}



// ============================================================
// TEMA E ESTILOS GLOBAIS
// ============================================================
const theme = {
  gold: "#C9A84C",
  goldLight: "#E8C97A",
  goldDark: "#A07830",
  cream: "#F9F6EF",
  white: "#FFFFFF",
  gray50: "#FAFAF8",
  gray100: "#F4F1EA",
  gray200: "#E8E3D8",
  gray300: "#D4CCB8",
  gray400: "#A89E88",
  gray500: "#7A7060",
  gray600: "#5C5448",
  gray700: "#3E3830",
  gray800: "#2A2420",
  gray900: "#1A1610",
  green: "#4A7C59",
  red: "#8C3A3A",
  blue: "#3A5C8C",
  amber: "#B87C2A",
};

// ============================================================
// CHECKLISTS POR FASE — Controladoria Operacional
// ============================================================
const CHECKLISTS_FASE = {
  "Entrada e Triagem": [
    "Procuração assinada?",
    "Documentos pessoais anexados?",
    "Custas recolhidas?"
  ],
  "Revisão Sênior": [
    "Formatação padrão do escritório?",
    "Nomes das partes consistentes?",
    "Fundamentação atualizada?",
    "Pedidos conferidos?",
    "Justiça gratuita verificada?",
    "Tramitação prioritária verificada?"
  ],
  "Protocolo": [
    "Documento em PDF assinado?",
    "Sistema judicial confirmou?",
    "Comprovante salvo no card?"
  ]
};

// Templates Tipo B — itens específicos por grupo de produto
// Aparecem nas fases Revisão Sênior e Protocolo
const TEMPLATES_CHECKLIST_B = {
  "PIS/COFINS — Monofásico / ICMS-ST": {
    "Revisão Sênior": [
      "NCM dos produtos segregados e conferidos?",
      "Período de apuração (60 meses) completo?",
      "EFD-Contribuições x NF-e conferidos?",
      "Percentual de crédito por NCM correto?",
      "PGDAS-D conferido (Simples Nacional)?",
      "Base de cálculo do ICMS-ST verificada?",
      "Tese aplicável ao período apurado?"
    ],
    "Protocolo": [
      "PERDCOMP transmitido com hash registrado?",
      "Planilha de apuração salva no card?",
      "Cliente notificado do valor apurado?"
    ]
  },
  "INSS — Cessão de Mão de Obra (CMO)": {
    "Revisão Sênior": [
      "60 competências preenchidas e conferidas?",
      "Crédito eCac/SRFB confrontado com apuração?",
      "Auditoria interna concluída?",
      "SDO PERDCOMP atualizado?",
      "Número e data do PERDCOMP registrados?",
      "Status de cada PERDCOMP atualizado?",
      "Valores de REST e COMP calculados?"
    ],
    "Protocolo": [
      "PERDCOMP(s) transmitido(s)?",
      "Hash(s) de transmissão registrado(s)?",
      "Saldo final conferido?",
      "Relatório de 60 meses salvo no card?"
    ]
  },
  "INSS — Verbas Indenizatórias": {
    "Revisão Sênior": [
      "Verbas indenizatórias segregadas da folha?",
      "Período de apuração conferido?",
      "Cálculo do INSS sobre verbas correto?",
      "Fundamentação (Temas 72 e 163 STF)?",
      "Valor de REST e COMP calculados?"
    ],
    "Protocolo": [
      "PERDCOMP transmitido?",
      "Planilha de folha de pagamento salva?",
      "Cliente notificado?"
    ]
  },
  "IR/CSLL — Construção Civil": {
    "Revisão Sênior": [
      "Empreitada global confirmada (material + mão de obra)?",
      "Classificação de receita verificada?",
      "Alíquota reduzida aplicada corretamente?",
      "Período de apuração completo?",
      "Tema 217 STJ aplicado ao período?"
    ],
    "Protocolo": [
      "PERDCOMP transmitido?",
      "Memória de cálculo salva?",
      "Nota de enquadramento arquivada?"
    ]
  },
  "IR/CSLL — Equiparação Hospitalar": {
    "Revisão Sênior": [
      "Enquadramento hospitalar confirmado?",
      "Alíquotas reduzidas aplicadas (IR 8% / CSLL 12%)?",
      "Período de apuração conferido?",
      "Tema 217 STJ (Jan/2026) aplicado?",
      "Receita segregada corretamente?"
    ],
    "Protocolo": [
      "PERDCOMP transmitido?",
      "Laudo/documento de enquadramento salvo?",
      "Memória de cálculo arquivada?"
    ]
  },
  "Exclusão ICMS da Base PIS/COFINS": {
    "Revisão Sênior": [
      "ICMS destacado nas NF-e segregado?",
      "Base de cálculo recalculada (Tema 69 STF)?",
      "Período prescricional verificado (5 anos)?",
      "EFD-Contribuições x SPED conferidos?",
      "Valor de crédito por período calculado?"
    ],
    "Protocolo": [
      "PERDCOMP transmitido?",
      "Planilha de apuração por período salva?",
      "Cliente notificado do valor apurado?"
    ]
  },
  "Gestão de Dívida / Execução Fiscal": {
    "Revisão Sênior": [
      "Saldo SRFB conferido no eCac?",
      "Saldo PGFN conferido no REGULARIZE?",
      "Prescrição dos débitos analisada?",
      "Transação tributária viável (PTI)?",
      "Certidões negativas verificadas?"
    ],
    "Protocolo": [
      "Proposta de transação enviada?",
      "Documentação de parcelamento arquivada?",
      "Cliente orientado sobre próximos passos?"
    ]
  },
  "Previdenciário — Geral": {
    "Revisão Sênior": [
      "CNIS conferido e atualizado?",
      "Vínculos empregatícios verificados?",
      "Tempo de contribuição calculado?",
      "Carência verificada?",
      "DER (Data de Entrada do Requerimento) definida?"
    ],
    "Protocolo": [
      "Requerimento protocolado no INSS?",
      "Número de protocolo registrado no card?",
      "Prazo de análise informado ao cliente?"
    ]
  },
  "Processo Judicial — Geral": {
    "Revisão Sênior": [
      "Petição revisada por Dr. Nelson ou Annie?",
      "Jurisprudência atualizada citada?",
      "Pedidos claros e sem contradição?",
      "Valor da causa calculado?",
      "Documentos anexados ao PDF final?"
    ],
    "Protocolo": [
      "Petição protocolada no sistema judicial?",
      "Número do processo registrado no card?",
      "Comprovante de protocolo salvo?",
      "Cliente notificado do protocolo?"
    ]
  },
};

// Resolve checklist global: data.checklistsGlobais sobrescreve CHECKLISTS_FASE quando definido
// Formata CPF (000.000.000-00) ou CNPJ (00.000.000/0001-00) automaticamente
const fmtCpfCnpj = raw => {
  if (raw === null || raw === undefined) return "";
  const d = String(raw).replace(/\D/g, "");
  if (!d) return "";
  if (d.length <= 11) {
    // CPF: 000.000.000-00
    return d.replace(/^(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})$/, (_, a, b, c, e) =>
      [a, b, c].filter(Boolean).join(".") + (e ? "-" + e : ""));
  }
  // CNPJ: 00.000.000/0001-00
  const s = d.slice(0, 14);
  return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})$/, (_, a, b, c, e, f) =>
    `${a}.${b}.${c}/${e}${f ? "-" + f : ""}`);
};

const resolveChecklistGlobal = (fase, checklistsGlobais = {}) => {
  const custom = checklistsGlobais[fase];
  if (custom && custom.length > 0) return custom;
  return CHECKLISTS_FASE[fase] || [];
};

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,600&family=Jost:wght@300;400;500;600&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }
  
  body {
    font-family: 'Jost', sans-serif;
    background: ${theme.gray50};
    color: ${theme.gray800};
    min-height: 100vh;
  }

  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: ${theme.gray100}; }
  ::-webkit-scrollbar-thumb { background: ${theme.gray300}; border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: ${theme.gold}; }

  .serif { font-family: 'Cormorant Garamond', serif; }

  input, select, textarea {
    font-family: 'Jost', sans-serif;
    font-size: 13px;
  }

  button { cursor: pointer; font-family: 'Jost', sans-serif; }

  .fade-in {
    animation: fadeIn 0.4s ease forwards;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes slideIn {
    from { opacity: 0; transform: translateX(-16px); }
    to { opacity: 1; transform: translateX(0); }
  }

  .slide-in { animation: slideIn 0.3s ease forwards; }
`;

// ============================================================
// DADOS INICIAIS
// ============================================================
const AREAS_INICIAIS = ["Previdenciário", "Tributário", "Trabalhista", "Empresarial", "Cível", "Família e Sucessões", "Imobiliário", "Bancário", "Ambiental", "Digital", "Agronegócio"];

const INITIAL_DATA = {
  _deletados: [], // tombstones — IDs excluídos, para o merge do servidor nunca trazê-los de volta
  areas: [...AREAS_INICIAIS],
  temasIA: [
    "Atendimento e Comunicação com o Cliente",
    "Audiência e Julgamento",
    "Contratos",
    "Criação de Peças Jurídicas",
    "Criação de Prompts",
    "Direito Civil",
    "Direito Digital",
    "Direito do Trabalho",
    "Direito Empresarial",
    "Direito Militar",
    "Direito Penal",
    "Direito Tributário",
    "Dosimetria da Pena",
    "Estratégia do Caso",
    "Extração de Dados",
    "Jurisprudência",
    "Marketing Jurídico e Vendas",
    "Negociação e Gestão de Conflitos",
    "Revisão de Peças Jurídicas",
    "Revisão e Melhoria de Textos e Documentos",
    "Segurança Pública — Policiais",
    "Transcrição e Degravação de Áudio"
  ],
  agentes: [
  { id: "AG00000001", nome: "Redator de Petições Iniciais", tema: "Criação de Peças Jurídicas", emoji: "✍️", desc: "Cria petições iniciais completas a partir dos fatos e fundamentos jurídicos fornecidos. Estrutura o pedido, causa de pedir e qualificação das partes automaticamente.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000002", nome: "Revisor de Peças Jurídicas", tema: "Revisão de Peças Jurídicas", emoji: "✅", desc: "Analisa peças jurídicas em busca de inconsistências, erros de fundamentação, problemas de coerência e pontos de melhoria. Sugere correções detalhadas.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000003", nome: "Extrator de Dados de Documentos", tema: "Extração de Dados", emoji: "🔍", desc: "Extrai informações estruturadas de contratos, decisões judiciais e documentos jurídicos. Gera tabelas e resumos organizados a partir de textos extensos.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000004", nome: "Aprimorador de Textos Jurídicos", tema: "Revisão e Melhoria de Textos e Documentos", emoji: "📄", desc: "Melhora a clareza, coesão e precisão técnica de textos jurídicos. Ajusta linguagem, corrige gramática e adapta o tom para o público-alvo do documento.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000005", nome: "Estrategista do Caso", tema: "Estratégia do Caso", emoji: "♟️", desc: "Analisa os pontos fortes e fracos do caso, identifica as melhores teses jurídicas e sugere a estratégia mais adequada para defesa ou acusação.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000006", nome: "Pesquisador de Jurisprudência", tema: "Jurisprudência", emoji: "📚", desc: "Localiza e resume jurisprudências relevantes dos principais tribunais brasileiros (STF, STJ, TJs). Apresenta ementas, teses e precedentes aplicáveis ao caso.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000007", nome: "Assistente de Atendimento ao Cliente", tema: "Atendimento e Comunicação com o Cliente", emoji: "💬", desc: "Elabora comunicações profissionais com clientes: e-mails, mensagens de WhatsApp, cartas de orientação e atualizações processuais em linguagem acessível.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000008", nome: "Preparador de Audiências", tema: "Audiência e Julgamento", emoji: "⚖️", desc: "Prepara roteiros de audiência, antecipa perguntas do juiz e da parte contrária, organiza argumentos e sugere ordem de apresentação das provas.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000009", nome: "Agente de Definição das Teses Jurídicas", tema: "Marketing Jurídico e Vendas", emoji: "📣", desc: "Cria conteúdo para redes sociais, artigos e campanhas de marketing jurídico. Gera calendário editorial, legendas e textos que atraem clientes para o escritório.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000010", nome: "Analisador e Redator de Contratos", tema: "Contratos", emoji: "📝", desc: "Analisa contratos identificando cláusulas abusivas, riscos e lacunas. Também redige contratos personalizados com base nos requisitos e necessidades das partes.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000011", nome: "Mediador e Negociador de Conflitos", tema: "Negociação e Gestão de Conflitos", emoji: "🤝", desc: "Sugere estratégias de negociação, elabora propostas de acordo e analisa ofertas da parte contrária. Auxilia na condução de mediações e conciliações.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000012", nome: "Especialista em Segurança Pública", tema: "Segurança Pública — Policiais", emoji: "🚔", desc: "Redige peças jurídicas específicas para policiais e servidores da segurança pública: mandados de segurança, recursos administrativos e defesas em sindicâncias.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000013", nome: "Transcritor Jurídico de Áudios", tema: "Transcrição e Degravação de Áudio", emoji: "🎙️", desc: "Transcreve e desgrava gravações de audiências, depoimentos e reuniões. Formata o texto em linguagem jurídica, identificando falas e momentos relevantes.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000014", nome: "Criador de Prompts Jurídicos", tema: "Criação de Prompts", emoji: "💡", desc: "Cria prompts otimizados para uso com IAs no contexto jurídico. Gera instruções precisas para obter melhores resultados em pesquisas, redações e análises.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000015", nome: "Especialista em Direito do Trabalho", tema: "Direito do Trabalho", emoji: "👷", desc: "Redige reclamações trabalhistas, recursos, contestações e pareceres. Analisa vínculos empregatícios, verbas rescisórias e calcula parcelas devidas.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000016", nome: "Consultor de Direito Empresarial", tema: "Direito Empresarial", emoji: "🏢", desc: "Auxilia na estruturação e análise de operações societárias, contratos empresariais e compliance. Redige pareceres e instrumentos do direito corporativo.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000017", nome: "Especialista em Direito Militar", tema: "Direito Militar", emoji: "🪖", desc: "Elabora peças do direito militar: defesas em Conselhos de Disciplina, recursos administrativos, habeas corpus e ações perante a Justiça Militar.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000018", nome: "Assistente de Direito Civil", tema: "Direito Civil", emoji: "🏛️", desc: "Redige peças cíveis: petições iniciais, contestações, recursos e pareceres nas áreas de família, sucessões, responsabilidade civil e direitos reais.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000019", nome: "Defensor Criminal", tema: "Direito Penal", emoji: "🔒", desc: "Elabora defesas criminais, habeas corpus, recursos e memoriais. Analisa provas, identifica nulidades processuais e sugere as melhores teses defensivas.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000020", nome: "Calculador de Dosimetria da Pena", tema: "Dosimetria da Pena", emoji: "⚖️", desc: "Calcula a dosimetria da pena nas três fases, analisa agravantes, atenuantes e causas de aumento e diminuição. Fundamenta o cálculo com base na jurisprudência vigente.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000021", nome: "Consultor Tributário", tema: "Direito Tributário", emoji: "💰", desc: "Analisa questões tributárias, elabora defesas administrativas e judiciais, recursos ao CARF e pareceres sobre planejamento fiscal e obrigações acessórias.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] },
  { id: "AG00000022", nome: "Especialista em Direito Digital", tema: "Direito Digital", emoji: "💻", desc: "Analisa crimes digitais, elabora notícias-crime, ações de remoção de conteúdo e pareceres sobre LGPD, proteção de dados e responsabilidade em plataformas digitais.", img: null, links: [{tipo: "claude", url: "https://claude.ai", label: "🟣 Abrir no Claude"}, {tipo: "gpt", url: "https://chat.openai.com", label: "💬 Abrir no ChatGPT"}] }
  ],
  vinculos: ["Sócio", "CLT", "PJ", "Estagiário", "Parceiro"],
  funcoes: ["Advogado", "Advogado Sênior", "Assistente Jurídico", "Paralegal", "Estagiário", "Administrativo", "Financeiro"],
  processos: [],
  clientes: [
    {
      id: "CLI001", nome: "João Carlos Ferreira", tipo: "PF",
      email: "joao@email.com", whatsapp: "(11) 99999-1111",
      cidade: "São Paulo", uf: "SP", endereco: "Rua das Flores, 123",
      cep: "01310-000", cpfCnpj: "123.456.789-00",
      produtosContratados: ["PROD001"], mesAnoContrato: "2024-03",
      ativo: true, obs: "", historico: [
        { id: 1, data: "2024-03-10", ocorrencia: "Reunião", descricao: "Reunião inicial para apresentação do serviço de isenção IR." },
        { id: 2, data: "2024-03-15", ocorrencia: "Fechamento de Contrato", descricao: "Contrato assinado digitalmente.", numContrato: "2024/001", valor: "2500", condicoes: "Parcelado", parcelas: "3x R$ 833,33 nos dias 15/03, 15/04, 15/05", exito: "20", dataExito: "2025-06-01" }
      ],
      contatosPJ: [], classificacao: "PF"
    },
    {
      id: "CLI002", nome: "Agropecuária Serra Verde Ltda", tipo: "PJ",
      email: "contato@serraverde.com.br", whatsapp: "(16) 98888-2222",
      cidade: "Ribeirão Preto", uf: "SP", endereco: "Av. Brasil, 500",
      cep: "14020-000", cpfCnpj: "12.345.678/0001-90",
      produtosContratados: ["PROD002", "PROD003"], mesAnoContrato: "2024-01",
      ativo: true, obs: "Cliente estratégico",
      historico: [
        { id: 1, data: "2024-01-08", ocorrencia: "Reunião", descricao: "Diagnóstico tributário inicial da empresa." },
        { id: 2, data: "2024-01-20", ocorrencia: "Fechamento de Contrato", descricao: "Contrato de recuperação de crédito tributário.", numContrato: "2024/002", valor: "8000", condicoes: "Híbrido", parcelas: "Entrada R$ 2.000 + 30% sobre o êxito", exito: "30", dataExito: "2025-03-01" },
        { id: 3, data: "2024-02-05", ocorrencia: "E-mail", descricao: "Envio de documentação complementar solicitada." }
      ], contatosPJ: [
        { nome: "Carlos Diretor", whatsapp: "(16) 99999-3333" },
        { nome: "Ana Financeiro", whatsapp: "(16) 97777-4444" }
      ], classificacao: "PJ"
    }
  ],
  produtos: [
    // ── CONSULTORIA ─────────────────────────────────────────────────
    {
      id: "CONS001", nome: "Consultoria (Pacote de Horas)",
      area: "Consultoria", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 30, risco: "Baixo",
      responsavel: "", status: "Ativo",
      baseLegal: "",
      resumo: "Contrato de consultoria jurídica por pacote de horas. O sistema controla o saldo de horas e gera lançamento automático de excedente.",
      tipo: "pacote_horas"
    },
    // ── ÁREA TRIBUTÁRIA (24 produtos) ──────────────────────────────
    {
      id: "TRIB001", nome: "Defesa em Execução Fiscal e Transação Tributária (PGFN)",
      campoRCT: "campo_unico",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 180, risco: "Alto",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Lei 13.988/2020; Lei 14.375/2022; Art. 174 do CTN",
      resumo: "Regularização de dívidas com redução de multas e juros e parcelamento em até 114 ou 133 meses.",
      trib_pf: true, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido", "Simples Nacional"],
      trib_segmentos: ["SEG001", "SEG002", "SEG003", "SEG004", "SEG005", "SEG006", "SEG007", "SEG008"],
      trib_tributos: ["TRI011", "TRI012"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB002", nome: "Defesa em Crimes Tributários",
      campoRCT: "campo_unico",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 365, risco: "Alto",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Lei 8.137/1990; Lei Complementar 225/2026",
      resumo: "Defesa da liberdade dos sócios em conflitos criminais, priorizando a quitação integral para extinguir o processo.",
      trib_pf: true, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido", "Simples Nacional"],
      trib_segmentos: ["SEG001", "SEG002", "SEG003", "SEG004", "SEG005", "SEG006"],
      trib_tributos: ["TRI011"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB003", nome: "Estudo e Planejamento Tributário (Reforma Tributária)",
      campoRCT: "campo_unico",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 90, risco: "Médio",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "EC 132/2023; Lei Complementar 214/2025; LC 224/2025",
      resumo: "Reorganização para evitar perda de créditos e aumento de carga na transição para o novo sistema.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido", "Simples Nacional"],
      trib_segmentos: ["SEG001", "SEG002", "SEG003", "SEG004", "SEG005", "SEG006", "SEG007", "SEG008"],
      trib_tributos: ["TRI001", "TRI002", "TRI004", "TRI005", "TRI009"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB004", nome: "Mandado de Segurança: Liberação de Crédito (+360 dias)",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 180, risco: "Baixo",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Art. 24 da Lei 11.457/2007; Tema Repetitivo 269 do STJ",
      resumo: "Ação para destravar créditos parados no Fisco e garantir a correção monetária plena.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido", "Simples Nacional"],
      trib_segmentos: ["SEG001", "SEG002", "SEG003", "SEG004", "SEG005", "SEG006"],
      trib_tributos: ["TRI005", "TRI012"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB005", nome: "Apuração de Créditos em Saldos de DCTF",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 90, risco: "Médio",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "IN RFB 2.055/2021; IN RFB 2.237/2024",
      resumo: "Recuperação de valores pagos a maior identificados via auditoria de obrigações acessórias.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido"],
      trib_segmentos: ["SEG001", "SEG002", "SEG003", "SEG004", "SEG005"],
      trib_tributos: ["TRI001", "TRI002", "TRI012"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB006", nome: "ITCMD – VGBL e PGBL (Investimentos)",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 180, risco: "Baixo",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Tema 1.214 do STF (RE 1.363.013)",
      resumo: "Restituição de ITCMD pago indevidamente em inventários sobre saldos de VGBL e PGBL.",
      trib_pf: true, trib_pj: false,
      trib_regimes: ["Lucro Real", "Lucro Presumido"],
      trib_segmentos: ["SEG010", "SEG008", "SEG009"],
      trib_tributos: ["TRI006"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB007", nome: "ITCMD e ITBI – Restituição (Base de Cálculo)",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 180, risco: "Baixo",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Tema Repetitivo 1.113 do STJ (para ITBI)",
      resumo: "Recuperação da diferença paga a maior em compras e doações de imóveis nos últimos 5 anos.",
      trib_pf: true, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido", "Simples Nacional"],
      trib_segmentos: ["SEG008", "SEG009", "SEG010"],
      trib_tributos: ["TRI006", "TRI007"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB008", nome: "Brasileiros no Exterior: Regularização e Riscos (CSDP/DSDP)",
      campoRCT: "campo_unico",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 60, risco: "Alto",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Instrução Normativa RFB 208/2002",
      resumo: "Proteção patrimonial e regularização fiscal para evitar que a Receita considere o contribuinte como residente fiscal no Brasil.",
      trib_pf: true, trib_pj: false,
      trib_regimes: ["Lucro Presumido", "Lucro Real"],
      trib_segmentos: ["SEG009", "SEG010"],
      trib_tributos: ["TRI001"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB009", nome: "Isenção de IR para Aposentados com Doenças Graves",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 90, risco: "Baixo",
      responsavel: "EQ001", status: "Ativo",
      baseLegal: "Art. 6º, XIV da Lei 7.713/1988",
      resumo: "Interrupção do desconto mensal e restituição retroativa dos últimos 60 meses para aposentados enfermos.",
      trib_pf: true, trib_pj: false,
      trib_regimes: ["Lucro Real", "Lucro Presumido"],
      trib_segmentos: ["SEG009"],
      trib_tributos: ["TRI001"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB010", nome: "INSS sobre Cessão de Mão de Obra (PJ – Restituição)",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 360, risco: "Médio",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Art. 31 da Lei 8.212/1991; IN RFB 2.110/2022",
      resumo: "Recuperação de créditos de INSS retidos por tomadores de serviço em favor da empresa prestadora.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido", "Simples Nacional"],
      trib_segmentos: ["SEG002", "SEG004", "SEG005", "SEG007", "SEG011"],
      trib_tributos: ["TRI003", "TRI012"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB011", nome: "INSS Pago Acima do Teto (Restituição)",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 90, risco: "Baixo",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Art. 165 do CTN; IN RFB 2.110/2022",
      resumo: "Devolução dos valores de INSS descontados além do limite máximo permitido por lei.",
      trib_pf: true, trib_pj: false,
      trib_regimes: ["Lucro Real", "Lucro Presumido"],
      trib_segmentos: ["SEG009", "SEG002"],
      trib_tributos: ["TRI003"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB012", nome: "Redução de IR para Aposentados no Exterior",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 180, risco: "Médio",
      responsavel: "EQ001", status: "Ativo",
      baseLegal: "Art. 7º da Lei 9.779/1999",
      resumo: "Redução da carga tributária sobre proventos pagos a aposentados que moram fora do Brasil.",
      trib_pf: true, trib_pj: false,
      trib_regimes: ["Lucro Real", "Lucro Presumido"],
      trib_segmentos: ["SEG009", "SEG010"],
      trib_tributos: ["TRI001"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB013", nome: "FGTS: Saques por Doenças Graves e TEA",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 90, risco: "Baixo",
      responsavel: "EQ001", status: "Ativo",
      baseLegal: "Art. 20 da Lei 8.036/1990; Jurisprudência STJ",
      resumo: "Liberação de recursos do Fundo de Garantia para suporte financeiro de famílias com autistas ou enfermos graves.",
      trib_pf: true, trib_pj: false,
      trib_regimes: ["Lucro Real", "Lucro Presumido"],
      trib_segmentos: ["SEG009"],
      trib_tributos: ["TRI010"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB014", nome: "Isenção de IPVA e Direitos Sociais (TEA/Saúde)",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 120, risco: "Baixo",
      responsavel: "EQ001", status: "Ativo",
      baseLegal: "Lei 12.764/2012; Súmula 102 TJSP; Tema 27 do STF",
      resumo: "Isenção de impostos sobre veículos e acesso a benefícios de amparo social e saúde integral.",
      trib_pf: true, trib_pj: false,
      trib_regimes: ["Lucro Real", "Lucro Presumido"],
      trib_segmentos: ["SEG009"],
      trib_tributos: ["TRI008"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB015", nome: "Verbas Indenizatórias (Folha de Pagamento)",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 180, risco: "Médio",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Tema 72 do STF; Tema 163 do STF; SC COSIT 109/2025",
      resumo: "Redução imediata do custo mensal da folha e recuperação dos últimos 5 anos.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido", "Simples Nacional"],
      trib_segmentos: ["SEG001", "SEG002", "SEG003", "SEG004", "SEG005", "SEG006", "SEG007"],
      trib_tributos: ["TRI003", "TRI012"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB016", nome: "PIS/COFINS Monofásicos e ICMS-ST",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 60, risco: "Baixo",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Lei Complementar 123/2006; Tema 1.125 do STJ; SC COSIT 100/2025",
      resumo: "Dinheiro no caixa em até 60 dias via retificação administrativa de declarações.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Simples Nacional"],
      trib_segmentos: ["SEG001", "SEG003"],
      trib_tributos: ["TRI004", "TRI005"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB017", nome: "Saldo PER/DCOMP – Restituição ou Compensação",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 120, risco: "Médio",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "IN RFB 2.055/2021; Art. 74 da Lei 9.430/1996",
      resumo: "Operacionalização de ativos fiscais para abatimento de impostos ou restituição em conta.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido", "Simples Nacional"],
      trib_segmentos: ["SEG001", "SEG002", "SEG003", "SEG004", "SEG005", "SEG006"],
      trib_tributos: ["TRI001", "TRI002", "TRI003", "TRI005", "TRI012"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB018", nome: "Saldo PER/DCOMP (+360 dias) – Mandado de Segurança",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 180, risco: "Médio",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Art. 24 da Lei 11.457/2007; Tema Repetitivo 1.003 do STJ",
      resumo: "Recuperação da liquidez de créditos parados há mais de um ano na Receita Federal.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido"],
      trib_segmentos: ["SEG001", "SEG002", "SEG003", "SEG004", "SEG005"],
      trib_tributos: ["TRI001", "TRI002", "TRI005", "TRI012"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB019", nome: "IR/CSLL – Equiparação Hospitalar (Clínicas)",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 180, risco: "Médio",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Art. 15 da Lei 9.249/1995; Tema Repetitivo 217 do STJ",
      resumo: "Redução definitiva de até 60% na carga de impostos federais para centros de diagnóstico e clínicas cirúrgicas.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Presumido"],
      trib_segmentos: ["SEG005"],
      trib_tributos: ["TRI001", "TRI002"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB020", nome: "IR/CSLL – Construção Civil (Empreitada)",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 180, risco: "Médio",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Art. 15, §1º, III, 'a' da Lei 9.249/1995; Tema 217 do STJ",
      resumo: "Otimização tributária para construtoras com atuação no setor de saúde.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido"],
      trib_segmentos: ["SEG004"],
      trib_tributos: ["TRI001", "TRI002"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB021", nome: "Situação Fiscal e Gestão de Dívida Tributária",
      campoRCT: "campo_unico",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 120, risco: "Alto",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Lei 13.988/2020; IN RFB 2.110/2022",
      resumo: "Gestão de parcelamentos e defesas em execuções fiscais para preservar o patrimônio da empresa.",
      trib_pf: true, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido", "Simples Nacional"],
      trib_segmentos: ["SEG001", "SEG002", "SEG003", "SEG004", "SEG005", "SEG006", "SEG007", "SEG008"],
      trib_tributos: ["TRI011"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB022", nome: "ICMS Saldo Acumulado (Restituição ou Compensação)",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 180, risco: "Médio",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Lei Kandir (Lei Complementar 87/1996)",
      resumo: "Transformação de créditos escriturais estaduais em dinheiro ou moeda de pagamento de fornecedores.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido"],
      trib_segmentos: ["SEG001", "SEG003", "SEG006"],
      trib_tributos: ["TRI004", "TRI012"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB023", nome: "Exclusão do ICMS da Base do PIS e da COFINS",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 360, risco: "Baixo",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Tema 69 do STF (RE 574.706/PR); Lei 14.592/2023",
      resumo: "Recuperação de vultosos créditos tributários retroativos e redução da carga mensal futura.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido"],
      trib_segmentos: ["SEG001", "SEG002", "SEG003", "SEG004", "SEG005", "SEG006"],
      trib_tributos: ["TRI004", "TRI005"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "TRIB024", nome: "Exclusão do ICMS-ST da Base do PIS e da COFINS",
      campoRCT: "60_meses",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "Não", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: 180, risco: "Baixo",
      responsavel: "EQ002", status: "Ativo",
      baseLegal: "Tema Repetitivo 1.125 do STJ; SC COSIT 100/2025",
      resumo: "Recuperação de impostos federais para empresas que atuam em setores com Substituição Tributária de ICMS.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Simples Nacional", "Lucro Presumido"],
      trib_segmentos: ["SEG001", "SEG003"],
      trib_tributos: ["TRI004", "TRI005"],
      trib_documentos: [],
      obs: ""
    },
    {
      id: "PREV001", nome: "Previdenciário Geral: Aposentadorias, Revisões e Auxílios",
      area: "Previdenciário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Gestão completa de benefícios do RGPS, abrangendo desde a concessão inicial até revisões administrativas e judiciais. Inclui auxílio por incapacidade temporária, aposentadoria por invalidez e pensões.", obs: ""
    },
    {
      id: "PREV002", nome: "BPC / LOAS (Idoso e Pessoa com Deficiência)",
      area: "Previdenciário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Benefício assistencial de um salário-mínimo focado na relativização do critério de renda familiar.", obs: ""
    },
    {
      id: "PREV003", nome: "Aposentadoria da Pessoa com Deficiência (PCD)",
      area: "Previdenciário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Benefício com requisitos reduzidos para segurados com impedimentos de longo prazo (mínimo 2 anos). Mantém as regras vantajosas pré-reforma.", obs: ""
    },
    {
      id: "PREV004", nome: "Aposentadoria Híbrida (Tempo Rural + Urbano)",
      area: "Previdenciário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Possibilidade de somar períodos de trabalho rural remoto (antes de 1991) com urbanos para atingir a carência.", obs: ""
    },
    {
      id: "PREV005", nome: "Planejamento Previdenciário",
      area: "Previdenciário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Estudo técnico-matemático para identificar o momento ideal e valor máximo do benefício, evitando o descarte indevido de contribuições.", obs: ""
    },
    {
      id: "TRIB025", nome: "Transação Tributária (PGFN)",
      campoRCT: "campo_unico",  // Template de Execução RCT
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Gestão estratégica do passivo fiscal para suspender penhoras e regularizar a empresa via Editais de Transação com descontos de até 65%.",
      trib_pf: true, trib_pj: true,
      trib_regimes: ["Lucro Real", "Lucro Presumido", "Simples Nacional"],
      trib_segmentos: ["SEG001", "SEG002", "SEG003", "SEG004", "SEG005", "SEG006", "SEG007", "SEG008"],
      trib_tributos: ["TRI011"],
      trib_documentos: [], obs: ""
    },
    {
      id: "TRIB026", nome: "Exclusão da gorjeta / taxa de serviço do Simples Nacional - PIS COFINS IRPJ CSLL",
      campoRCT: "60_meses",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Exclusão da gorjeta e taxa de serviço da base de cálculo do Simples Nacional — PIS, COFINS, IRPJ e CSLL.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Simples Nacional"],
      trib_tributos: ["TRI001","TRI002","TRI003","TRI004"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "TRIB027", nome: "Exclusão da taxa de delivery da base de cálculo de PIS/COFINS, IRPJ e CSLL",
      campoRCT: "60_meses",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Exclusão do valor cobrado de delivery (plataformas de entrega) da base de cálculo do PIS/COFINS, IRPJ e CSLL.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real","Lucro Presumido","Simples Nacional"],
      trib_tributos: ["TRI001","TRI002","TRI003","TRI004"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "TRIB028", nome: "IPI - Recuperação de Crédito",
      campoRCT: "60_meses",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Recuperação de créditos de IPI não aproveitados — Lei 9.363/1996 e art. 49 do RIPI.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real","Lucro Presumido"],
      trib_tributos: ["TRI007"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "TRIB029", nome: "IRPJ CSLL - Empresas de Tecnologia - Redução de IRPJ e CSLL (Lei do bem)",
      campoRCT: "60_meses",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Redução de IRPJ e CSLL para empresas de tecnologia enquadradas nos benefícios da Lei do Bem (Lei 11.196/2005).",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real"],
      trib_tributos: ["TRI003","TRI004"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "TRIB030", nome: "IRPJ / CSLL - Tributação adicional de 10% no Lucro Presumido - faturamento maior que 5MM/ano",
      campoRCT: "60_meses",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Exclusão da tributação adicional de 10% sobre IRPJ para empresas do Lucro Presumido com faturamento acima de R$ 5MM anuais.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Presumido"],
      trib_tributos: ["TRI003","TRI004"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "TRIB031", nome: "IRRF PF - Sobre Dividendos - Simples Nacional - Tese",
      campoRCT: "60_meses",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Alto",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Tese de não incidência de IRRF sobre distribuição de dividendos de empresas optantes pelo Simples Nacional.",
      trib_pf: true, trib_pj: false,
      trib_regimes: ["Simples Nacional"],
      trib_tributos: ["TRI003"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "TRIB032", nome: "PIS / COFINS: Exclusão do IPI não recuperável da base de Cálculo do PIS / COFINS",
      campoRCT: "60_meses",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Exclusão do IPI não recuperável da base de cálculo do PIS/COFINS — Tema 756 STF.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real","Lucro Presumido","Simples Nacional"],
      trib_tributos: ["TRI001","TRI002"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "TRIB033", nome: "PIS e COFINS - Exclusão do DIFAL da base de cálculo do PIS e COFINS",
      campoRCT: "60_meses",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Exclusão do diferencial de alíquota de ICMS (DIFAL) da base de cálculo do PIS e COFINS.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real","Lucro Presumido"],
      trib_tributos: ["TRI001","TRI002"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "TRIB034", nome: "PIS/COFINS - Exclusão do ISSQN da base de calculo do PIS/COFINS",
      campoRCT: "60_meses",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Exclusão do ISSQN da base de cálculo do PIS/COFINS — extensão do raciocínio do Tema 69 STF.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real","Lucro Presumido","Simples Nacional"],
      trib_tributos: ["TRI001","TRI002"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "TRIB035", nome: "PIS/COFINS - Revisor de PIS/COFINS (GTIN - Código de Barras)",
      campoRCT: "60_meses",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Revisão de PIS/COFINS monofásico via NCM/GTIN (código de barras) — recuperação de tributo pago indevidamente na cadeia monofásica.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real","Lucro Presumido","Simples Nacional"],
      trib_tributos: ["TRI001","TRI002"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "TRIB036", nome: "PIS/COFINS sobre a aquisição de diesel (período de 11/03/2022 a 15/08/2022)",
      campoRCT: "60_meses",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Restituição de PIS/COFINS recolhido sobre aquisição de diesel no período de alíquota zero (LC 192/2022 — 11/03/2022 a 15/08/2022).",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real","Lucro Presumido","Simples Nacional"],
      trib_tributos: ["TRI001","TRI002"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "TRIB037", nome: "PIS/COFINS ST Sobre Cigarros",
      campoRCT: "60_meses",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Recuperação de PIS/COFINS-ST pagos antecipadamente sobre cigarros na substituição tributária.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real","Lucro Presumido","Simples Nacional"],
      trib_tributos: ["TRI001","TRI002"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "TRIB038", nome: "Revisão de CAPAG (PGFN - Transação Tributária)",
      campoRCT: "campo_unico",
      area: "Tributário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Revisão da Capacidade de Pagamento (CAPAG) perante a PGFN para fins de enquadramento em Transação Tributária com melhores condições.",
      trib_pf: false, trib_pj: true,
      trib_regimes: ["Lucro Real","Lucro Presumido","Simples Nacional"],
      trib_tributos: ["TRI011"],
      trib_documentos: [], documentosPadrao: [], tarefasPadrao: [], obs: ""
    },
    {
      id: "BANC001", nome: "Fraudes Bancárias (Pix, Boleto, Falsa Central)",
      area: "Bancário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Responsabilidade objetiva das instituições por danos gerados por falha no dever de segurança (fortuito interno).", obs: ""
    },
    {
      id: "BANC002", nome: "Revisões Contratuais, Tarifas e Juros Abusivos",
      area: "Bancário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Expurgo de cláusulas que excedam significativamente a média de mercado, descaracterizando a mora.", obs: ""
    },
    {
      id: "BANC003", nome: "Negociação de Dívidas – Ação de Superendividamento",
      area: "Bancário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Reestruturação global de dívidas preservando o Mínimo Existencial. Plano de pagamento em até 5 anos.", obs: ""
    },
    {
      id: "BANC004", nome: "Contratos Agrários – Execuções Bancárias – Agro",
      area: "Bancário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Direito subjetivo do produtor rural ao alongamento da dívida por fatores alheios como clima ou mercado.", obs: ""
    },
    {
      id: "FAM001", nome: "Planejamento Patrimonial e Sucessório (Instrumentos Diversos)",
      area: "Família e Sucessões", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Organização da transferência de bens via Testamentos, Doações e Seguros para evitar litígios.", obs: ""
    },
    {
      id: "FAM002", nome: "Holding Patrimonial (Sucessão, Proteção e Estratégia Tributária)",
      area: "Família e Sucessões", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Pessoa Jurídica para gerir bens da família, protegendo ativos e facilitando a sucessão via quotas.", obs: ""
    },
    {
      id: "FAM003", nome: "Divórcio (Judicial e Extrajudicial)",
      area: "Família e Sucessões", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Dissolução do vínculo matrimonial com foco na agilidade da via administrativa.", obs: ""
    },
    {
      id: "FAM004", nome: "Inventário (Judicial e Extrajudicial)",
      area: "Família e Sucessões", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Levantamento e partilha de bens, priorizando a via extrajudicial para evitar a morosidade judiciária.", obs: ""
    },
    {
      id: "CIV001", nome: "Desbloqueio de Conta Bancária (Impenhorabilidade)",
      area: "Cível", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Defesa do patrimônio mínimo (até 40 salários-mínimos) para subsistência digna.", obs: ""
    },
    {
      id: "CIV002", nome: "Defesa em Execuções (Prescrição Intercorrente)",
      area: "Cível", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Extinção do processo quando o credor permanece inerte por período superior ao prazo prescricional.", obs: ""
    },
    {
      id: "CIV003", nome: "Cobrança de Dívidas (Recuperação Estratégica de Ativos)",
      area: "Cível", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Utilização de inteligência investigativa e ferramentas tecnológicas para localizar patrimônio oculto.", obs: ""
    },
    {
      id: "CIV004", nome: "Revisão de Contratos de Plano de Saúde – Tese Falso Coletivo",
      area: "Cível", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Anulação de reajustes abusivos em planos registrados como coletivos, mas de natureza familiar.", obs: ""
    },
    {
      id: "CIV005", nome: "Contratos de Prestação de Serviços e Outros",
      area: "Cível", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Instrumentos definindo obrigações de meio e resultado, mitigando riscos de responsabilidade civil.", obs: ""
    },
    {
      id: "IMOB001", nome: "Usucapião (Extrajudicial e Judicial)",
      area: "Imobiliário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Regularização da propriedade baseada na posse prolongada e pacífica.", obs: ""
    },
    {
      id: "IMOB002", nome: "Regularização de Imóveis (REURB) e Adjudicação Compulsória",
      area: "Imobiliário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Procedimentos para converter áreas irregulares e contratos de gaveta em matrículas individuais.", obs: ""
    },
    {
      id: "IMOB003", nome: "Contratos de Locação de Imóveis",
      area: "Imobiliário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Elaboração de instrumentos com garantias robustas e adequação à transição do IBS/CBS.", obs: ""
    },
    {
      id: "IMOB004", nome: "Contrato de Compra e Venda de Imóveis",
      area: "Imobiliário", popId: "", linkPop: "", campanha: "", valorCampanha: "",
      valor: 0, custo: 0, margem: 0, prazoMedio: "", risco: "Médio",
      responsavel: "", status: "Ativo", baseLegal: "",
      resumo: "Auditoria de riscos (due diligence) e formalização de promessas com foco na segurança do adquirente.", obs: ""
    }
  ],
  equipe: [
    {
      id: "EQ001", nome: "Dra. Maria Alves", vinculo: "Sócio",
      funcao: "Advogada", especialidade: "Previdenciário",
      email: "maria@alvescorrea.adv.br", whatsapp: "(11) 99000-0001",
      ativo: true, obs: ""
    },
    {
      id: "EQ002", nome: "Dr. Pedro Corrêa", vinculo: "Sócio",
      funcao: "Advogado", especialidade: "Tributário",
      email: "pedro@alvescorrea.adv.br", whatsapp: "(11) 99000-0002",
      ativo: true, obs: ""
    },
    {
      id: "EQ003", nome: "Ana Paula Lima", vinculo: "CLT",
      funcao: "Assistente Jurídico", especialidade: "Generalista",
      email: "ana@alvescorrea.adv.br", whatsapp: "(11) 99000-0003",
      ativo: true, obs: ""
    }
  ],
  pops: [
    {
      id: "PREV-01", nome: "Isenção IR - Aposentado com Doença Grave",
      grupo: "Produção", subgrupo: "Previdenciário",
      dataConfeccao: "2023-01-10", dataRevisao: "2024-01-10",
      versao: "2.1", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PREV-02", nome: "BPC/LOAS - Benefício Assistencial",
      grupo: "Produção", subgrupo: "Previdenciário",
      dataConfeccao: "2023-03-15", dataRevisao: "2024-03-15",
      versao: "1.3", status: "Ativo", link: "", obs: ""
    },
    {
      id: "TRIB-01", nome: "Recuperação de Crédito Tributário - RCT",
      grupo: "Produção", subgrupo: "Tributário",
      dataConfeccao: "2023-06-01", dataRevisao: "2024-06-01",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "COM-01", nome: "Atendimento Inicial",
      grupo: "Comercial", subgrupo: "Captação",
      dataConfeccao: "2023-01-01", dataRevisao: "2024-01-01",
      versao: "3.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-COM-01", nome: "Atendimento Inicial",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "COMERCIAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-COM-02", nome: "Qualificação de Lead",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "COMERCIAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-COM-03", nome: "Reunião Estratégica",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "COMERCIAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-COM-04", nome: "Emissão de Proposta",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "COMERCIAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-COM-05", nome: "Formalização Contratual",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "COMERCIAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-COM-06", nome: "Onboarding do Cliente",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "COMERCIAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-CTRL-01", nome: "Controle de Prazo Judicial",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "CONTROLE OPERACIONAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-CTRL-02", nome: "Controle de Prazo Administrativo",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "CONTROLE OPERACIONAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-CTRL-03", nome: "Atualização Semanal da Base",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "CONTROLE OPERACIONAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-CTRL-04", nome: "Monitoramento de Processos Parados",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "CONTROLE OPERACIONAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-CTRL-05", nome: "Gestão de Pendência Documental",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "CONTROLE OPERACIONAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-CTRL-06", nome: "Reclassificação de Risco",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "CONTROLE OPERACIONAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-FIN-01", nome: "Cadastro de Honorários",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "FINANCEIRO",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-FIN-02", nome: "Controle de Recebimento",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "FINANCEIRO",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-FIN-03", nome: "Controle de Êxito",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "FINANCEIRO",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-FIN-04", nome: "Apuração de Margem Real por Produto",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "FINANCEIRO",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-GOV-01", nome: "Auditoria Trimestral de POP",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "GOVERNANÇA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-GOV-02", nome: "Revisão Semestral de Produtos",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "GOVERNANÇA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-GOV-03", nome: "Avaliação de Retrabalho",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "GOVERNANÇA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-GOV-04", nome: "Análise de Gargalos",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "GOVERNANÇA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "EST-GOV-05", nome: "Controle de Concentração de Receita",
      grupo: "ESTRUTURAIS (empresa)", subgrupo: "GOVERNANÇA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PROD-PRE-01", nome: "Triagem Técnica",
      grupo: "PRODUÇÃO TRANSVERSAL (técnicos)", subgrupo: "PRÉ-PROCESSUAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PROD-PRE-02", nome: "Análise Estratégica do Caso",
      grupo: "PRODUÇÃO TRANSVERSAL (técnicos)", subgrupo: "PRÉ-PROCESSUAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PROD-PRE-03", nome: "Cálculo Jurídico",
      grupo: "PRODUÇÃO TRANSVERSAL (técnicos)", subgrupo: "PRÉ-PROCESSUAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PROD-JUD-01", nome: "Elaboração de Petição Inicial",
      grupo: "PRODUÇÃO TRANSVERSAL (técnicos)", subgrupo: "PROCESSUAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PROD-JUD-02", nome: "Elaboração de Manifestação",
      grupo: "PRODUÇÃO TRANSVERSAL (técnicos)", subgrupo: "PROCESSUAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PROD-JUD-03", nome: "Elaboração de Recurso",
      grupo: "PRODUÇÃO TRANSVERSAL (técnicos)", subgrupo: "PROCESSUAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PROD-JUD-04", nome: "Cumprimento de Sentença",
      grupo: "PRODUÇÃO TRANSVERSAL (técnicos)", subgrupo: "PROCESSUAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PROD-JUD-05", nome: "Protocolo e Controle de Prazo",
      grupo: "PRODUÇÃO TRANSVERSAL (técnicos)", subgrupo: "PROCESSUAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PROD-CON-01", nome: "Elaboração de Contrato para Cliente",
      grupo: "PRODUÇÃO TRANSVERSAL (técnicos)", subgrupo: "CONTRATUAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PROD-CON-02", nome: "Revisão Contratual",
      grupo: "PRODUÇÃO TRANSVERSAL (técnicos)", subgrupo: "CONTRATUAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PROD-CON-03", nome: "Comunicação Técnica com Cliente",
      grupo: "PRODUÇÃO TRANSVERSAL (técnicos)", subgrupo: "CONTRATUAL",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-PREV-01", nome: "Previdenciário",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA PREVIDENCIARIA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-PREV-02", nome: "Previdenciário",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA PREVIDENCIARIA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-PREV-03", nome: "Previdenciário",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA PREVIDENCIARIA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-TRIB-01", nome: "Tributário",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA TRIBUTARIA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-TRIB-02", nome: "Tributário",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA TRIBUTARIA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-TRIB-03", nome: "Tributário",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA TRIBUTARIA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-HOLD-01", nome: "Holding Geral",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA HOLDING",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-HOLD-02", nome: "Holding no Agro",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA HOLDING",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-BANC-01", nome: "Defesa em Execuções no AGRO",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA BANCARIA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-BANC-02", nome: "Bancário",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA BANCARIA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-BANC-03", nome: "Bancário",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA BANCARIA",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-FAM-01", nome: "DIVORCIO",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA FAMILIA E SUCESSOES",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-FAM-02", nome: "INVENTARIO",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA FAMILIA E SUCESSOES",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-FAM-03", nome: "FAMILIA - OUTROS",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA FAMILIA E SUCESSOES",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-CONTR-01", nome: "CONTRATO DE LOCAÇÃO",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA CONTRATOS",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-CONTR-02", nome: "CONTRATO DE COMPRA E VENDA",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA CONTRATOS",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-CONTR-03", nome: "PRESTAÇÃO DE SERVICOS (OUTROS)",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA CONTRATOS",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-IMOB-01", nome: "USUCAPIÃO E OUTRAS",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA IMOBILIARIO",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    },
    {
      id: "PRODT-IMOB-02", nome: "REGULARIZACAO DE IMOVEIS",
      grupo: "PRODUTO (específicos)", subgrupo: "AREA IMOBILIARIO",
      dataConfeccao: "2026-03-04", dataRevisao: "2026-03-04",
      versao: "1.0", status: "Ativo", link: "", obs: ""
    }
  ],
  parcerias: [
    {
      id: "PAR001", nome: "Contábil Mota & Associados",
      endereco: "Rua Direita, 200", cidade: "São Paulo", uf: "SP",
      email: "contato@motacontabil.com.br", whatsapp: "(11) 97777-5555",
      areaAtuacao: "Contabilidade", comissao: 10,
      data: "2023-06-01", status: "Ativo", obs: ""
    }
  ],
  diagnosticosRCT: [
    {
      id: "RCT001", clienteId: "CLI002", parceiraId: "PAR001",
      responsavel: "EQ002", supervisor: "EQ002",
      cnpj: "12.345.678/0001-90", regime: "Presumido",
      atividade: "Prestação de Serviços",
      dataInicio: "2024-01-20", dataFim: "", status: "Em andamento",
      dataRelatorio: "", honorarios: "OK", obs: "Diagnóstico em andamento.",
      linkPlanilha: "https://docs.google.com/spreadsheets/d/exemplo",
      produtos: [
        {
          produtoId: "RCT_CMO", nome: "Cessão de Mão de Obra",
          viabilidade: "Sim", creditoApurado: 496248.84,
          utilizacaoTotal: 496248.84, rest: 0, comp: 1293.84,
          analise: 495393.46, analiseConcluida: 863.94,
          compensOficio: 863.94, perDeferido: 0, dcompHomologado: 0,
          statusAnalise: "Concluída", obs: "DCOMP enviado."
        },
        {
          produtoId: "RCT_PISCOFINS", nome: "PIS/COFINS Monofásicos / ICMS-ST",
          viabilidade: "Sim", creditoApurado: 139602.76,
          sdoPerdcomp: -5318.05, sdoPerdcompRest: 0, sdoPerdcompComp: 1293.84,
          perdcomp: "PERCOMP", perdcompData: "2024-02-15", perdcompStatus: "Homologado",
          sdoMais360Rest: 5459, sdoMais360Comp: 0, sdoMais360MS: 0,
          statusAnalise: "Concluída", obs: ""
        },
        {
          produtoId: "RCT_IRCSLL", nome: "IR/CSLL",
          viabilidade: "Não se aplica", creditoApurado: 0,
          aliquotaIR: "", aliquotaCSLL: "", classReceita: "",
          empreitadaGlobal: false,
          statusAnalise: "N/A", obs: "Não aplicável ao perfil da empresa."
        },
        {
          produtoId: "RCT_DIVIDA", nome: "Situação Fiscal e Gestão de Dívida Tributária",
          viabilidade: "Sim", creditoApurado: 0,
          srfbValor: 890.83, srfbStatus: "SEM RELEVANCIA",
          pgfnValor: 0, pgfnStatus: "Não tem",
          statusDivida: "SEM RELEVANCIA", prescricao: "", transacao: "",
          statusAnalise: "Concluída", obs: ""
        }
      ]
    }
  ],
  leads: [
    {
      id: "LEAD001", nome: "Restaurante Bom Sabor Ltda", tipo: "PJ",
      email: "contato@bomsabor.com.br", whatsapp: "(11) 98765-4321",
      cidade: "São Paulo", uf: "SP", cpfCnpj: "", origem: "Parceria",
      parceiraId: "PAR001", responsavel: "EQ002",
      estagio: "proposta", produtosInteresse: ["PROD003"],
      valorEstimado: "6000", probabilidade: 70,
      dataEntrada: "2024-05-10", prazoFechamento: "2024-06-30",
      obs: "Empresa com faturamento acima de R$ 500k/mês. Interesse em RCT.",
      historico: [
        { id: 1, data: "2024-05-10", tipo: "Contato", descricao: "Primeiro contato via Contábil Mota." },
        { id: 2, data: "2024-05-20", tipo: "Reunião", descricao: "Reunião diagnóstico realizada. Boa receptividade." }
      ]
    },
    {
      id: "LEAD002", nome: "Carlos Eduardo Martins", tipo: "PF",
      email: "carlos@email.com", whatsapp: "(16) 99111-2222",
      cidade: "Ribeirão Preto", uf: "SP", cpfCnpj: "", origem: "Indicação",
      parceiraId: "", responsavel: "EQ001",
      estagio: "reuniao", produtosInteresse: ["PROD001"],
      valorEstimado: "2500", probabilidade: 50,
      dataEntrada: "2024-05-18", prazoFechamento: "",
      obs: "Aposentado, solicita análise de isenção IR.",
      historico: [
        { id: 1, data: "2024-05-18", tipo: "WhatsApp", descricao: "Indicado por cliente João Carlos." }
      ]
    },
    {
      id: "LEAD003", nome: "Tech Solutions Ltda", tipo: "PJ",
      email: "financeiro@techsol.com.br", whatsapp: "(11) 97000-5500",
      cidade: "Campinas", uf: "SP", cpfCnpj: "", origem: "LinkedIn",
      parceiraId: "", responsavel: "EQ002",
      estagio: "qualificacao", produtosInteresse: ["PROD003"],
      valorEstimado: "12000", probabilidade: 30,
      dataEntrada: "2024-06-01", prazoFechamento: "",
      obs: "Empresa de TI, prestadora de serviços. Em análise de viabilidade.",
      historico: []
    }
  ],
  usuarios: [
    {
      id: "USR001", equipeId: "", nome: "Admin", login: "admin", senha: "admin123",
      perfil: "Administrador", ativo: true,
      modulos: ["areas","processos","clientes","produtos","dashtrib","equipe","pops","arquivo","parcerias","base","financeiro","painelia","dashboard","agenda","vendas","marketing","crm","manual","rct","relatorios","governanca","cowork","acesso","sala","escritorio"],
      produtos: [],
      area: "Gestao", nivel_area: "TOTAL_AREA"
    },
    {
      id: "USR002", equipeId: "EQ003", nome: "Ana Paula Lima", login: "ana.paula", senha: "ana123",
      perfil: "Operador RCT", ativo: true,
      modulos: ["rct","sala"],
      produtos: ["PROD003"],
      area: "Juridico", nivel_area: "EXECUTOR"
    }
  ],
  bancos: [
    { id:"BAN001", nome:"Itaú Unibanco",   agencia:"1234-5", conta:"12345-6", titular:"Alves & Correa Advogados", tipo:"Conta Corrente PJ", status:"Ativo" },
    { id:"BAN002", nome:"Nubank PJ",        agencia:"",       conta:"",       titular:"Alves & Correa Advogados", tipo:"Conta Corrente PJ", status:"Ativo" }
  ],
  lancamentos: [
    {
      id: "LAN001", clienteId: "CLI001", contratoId: "CONT001", numContrato: "2024/001",
      produtoId: "PROD001", tipo: "Honorario", modalidade: "Parcelado",
      descricao: "Parcela 1/3 - Honorarios Isencao IR",
      valor: 833.33, dataVencimento: "2024-03-15", dataRecebimento: "2024-03-16",
      bancoBaixa: "BAN001", status: "Recebido", obs: ""
    },
    {
      id: "LAN002", clienteId: "CLI001", contratoId: "CONT001", numContrato: "2024/001",
      produtoId: "PROD001", tipo: "Honorario", modalidade: "Parcelado",
      descricao: "Parcela 2/3 - Honorarios Isencao IR",
      valor: 833.33, dataVencimento: "2024-04-15", dataRecebimento: "2024-04-14",
      status: "Recebido", obs: ""
    },
    {
      id: "LAN003", clienteId: "CLI001", contratoId: "CONT001", numContrato: "2024/001",
      produtoId: "PROD001", tipo: "Honorario", modalidade: "Parcelado",
      descricao: "Parcela 3/3 - Honorarios Isencao IR",
      valor: 833.34, dataVencimento: "2024-05-15", dataRecebimento: null,
      status: "A Receber", obs: ""
    },
    {
      id: "LAN004", clienteId: "CLI002", contratoId: "CONT002", numContrato: "2024/002",
      produtoId: "PROD003", tipo: "Honorario", modalidade: "Hibrido",
      descricao: "Entrada - Honorarios RCT",
      valor: 2000, dataVencimento: "2024-01-20", dataRecebimento: "2024-01-20",
      status: "Recebido", obs: ""
    },
    {
      id: "LAN005", clienteId: "CLI002", contratoId: "CONT002", numContrato: "2024/002",
      produtoId: "PROD003", tipo: "Exito", modalidade: "No Exito",
      descricao: "Honorarios de exito - 30% sobre credito apurado",
      valor: 6000, dataVencimento: "2025-03-01", dataRecebimento: null,
      status: "A Receber", obs: "Aguardando homologacao PERDCOMP"
    },
    {
      id: "LAN006", clienteId: "CLI001", contratoId: "CONT003", numContrato: "2024/003",
      produtoId: "PROD001", tipo: "Honorario", modalidade: "A Vista",
      descricao: "Honorarios - Processo encerrado",
      valor: 1800, dataVencimento: "2024-01-15", dataRecebimento: "2024-01-15",
      status: "Recebido", obs: ""
    }
  ],
  despesas: [
    {
      id: "DESP001", descricao: "Custas processuais - CONT001", grupo: "Custo",
      categoria: "Custas Judiciais", valor: 150, data: "2024-03-20", vencimento: "2024-03-20",
      status: "Pago", dataBaixa: "2024-03-20", bancoBaixa: "BAN001", contratoId: "CONT001", obs: ""
    },
    {
      id: "DESP002", descricao: "Honorarios periciais - CONT002", grupo: "Custo",
      categoria: "Pericia", valor: 500, data: "2024-02-10", contratoId: "CONT002", status: "A Pagar", obs: ""
    },
    {
      id: "DESP003", descricao: "Aluguel escritorio Jan/2024", grupo: "Despesa",
      categoria: "Aluguel", valor: 3500, data: "2024-01-05", contratoId: "", status: "A Pagar", obs: "Competencia Jan/2024"
    },
    {
      id: "DESP004", descricao: "Pacote Office + Software Juridico", grupo: "Despesa",
      categoria: "Software", valor: 420, data: "2024-01-10", contratoId: "", status: "A Pagar", obs: "Assinatura mensal"
    }
  ],
  contratos: [
    {
      id: "CONT001", numContrato: "2024/001",
      clienteId: "CLI001", produtoId: "PROD001", area: "Previdenciário",
      fase: "Processual", tipoProcesso: "Judicial", status: "Em andamento",
      responsavel: "EQ001", dataEntrada: "2024-03-15", ultimoAvanco: "2024-04-10",
      prazo: "2025-08-01", valorContratado: 2500, valorRecebido: 1666.66, valorAReceber: 833.34,
      teseNova: false, jurisprudenciaInstavel: false, valorElevado: false,
      dependePericia: false, documentoIncompleto: false,
      custoOperacional: 400, obs: "Processo em fase de instrução."
    },
    {
      id: "CONT002", numContrato: "2024/002",
      clienteId: "CLI002", produtoId: "PROD003", area: "Tributário",
      fase: "Pre-processual", tipoProcesso: "Administrativo", status: "Em andamento",
      responsavel: "EQ002", dataEntrada: "2024-01-20", ultimoAvanco: "2024-02-28",
      prazo: "2024-07-20", valorContratado: 8000, valorRecebido: 2000, valorAReceber: 6000,
      teseNova: true, jurisprudenciaInstavel: true, valorElevado: true,
      dependePericia: false, documentoIncompleto: true,
      custoOperacional: 800, obs: "Aguardando documentacao complementar."
    },
    {
      id: "CONT003", numContrato: "2024/003",
      clienteId: "CLI001", produtoId: "PROD001", area: "Previdenciário",
      fase: "Execucao", tipoProcesso: "Judicial", status: "Concluido",
      responsavel: "EQ001", dataEntrada: "2023-06-01", ultimoAvanco: "2024-01-15",
      prazo: "2024-02-01", valorContratado: 1800, valorRecebido: 1800, valorAReceber: 0,
      teseNova: false, jurisprudenciaInstavel: false, valorElevado: false,
      dependePericia: false, documentoIncompleto: false,
      custoOperacional: 300, obs: "Processo encerrado com exito."
    }
  ]
,
  tiposOcorrencia: [
    { id: "TOC001", nome: "Reunião",               interno: false },
    { id: "TOC002", nome: "Ligação",                interno: false },
    { id: "TOC003", nome: "E-mail",                 interno: false },
    { id: "TOC004", nome: "WhatsApp",               interno: false },
    { id: "TOC005", nome: "Protocolo",              interno: false },
    { id: "TOC006", nome: "Despacho Interno",       interno: true  },
    { id: "TOC007", nome: "Nota Interna",           interno: true  },
    { id: "TOC008", nome: "Fechamento de Contrato", interno: false },
    { id: "TOC009", nome: "Audiência",              interno: false },
    { id: "TOC010", nome: "Decisão Judicial",       interno: false }
  ],
  tiposArquivo: [
    { id: "TAR001", nome: "Modelo de Contrato" },
    { id: "TAR002", nome: "Procuração" },
    { id: "TAR003", nome: "Petição" },
    { id: "TAR004", nome: "Recurso" },
    { id: "TAR005", nome: "Parecer" },
    { id: "TAR006", nome: "Planilha" },
    { id: "TAR007", nome: "Formulário" },
    { id: "TAR008", nome: "Modelo de E-mail" },
    { id: "TAR009", nome: "Checklist" },
    { id: "TAR010", nome: "Outros" }
  ],
  documentosArquivo: [],
  agenda: [],
  baseOperacional: [],
  vendas: {
    propostas: [],
    agentesVendas: []  // IDs dos agentes selecionados do cadastro central
  },
  marketing: {
    redesSociais: [
      { id:"RS001", plataforma:"Instagram",  perfil:"", responsavel:"", status:"Ativo",  obs:"" },
      { id:"RS002", plataforma:"Facebook",   perfil:"", responsavel:"", status:"Ativo",  obs:"" },
      { id:"RS003", plataforma:"LinkedIn",   perfil:"", responsavel:"", status:"Ativo",  obs:"" },
      { id:"RS004", plataforma:"YouTube",    perfil:"", responsavel:"", status:"Pausado", obs:"" },
      { id:"RS005", plataforma:"TikTok",     perfil:"", responsavel:"", status:"Pausado", obs:"" },
      { id:"RS006", plataforma:"X (Twitter)","perfil":"", responsavel:"", status:"Pausado", obs:"" }
    ],
    agentes: [
      { id:"AG001", nome:"Gerador de Campanhas",     descricao:"Cria campanha completa para um produto jurídico",         icon:"🚀", link:"https://claude.ai", ativo:true },
      { id:"AG002", nome:"Gerador de Posts",         descricao:"Cria posts adaptados por plataforma e tom de voz",        icon:"📝", link:"https://claude.ai", ativo:true },
      { id:"AG003", nome:"Planejador Editorial",     descricao:"Sugere calendário editorial do mês por área do direito",  icon:"📆", link:"https://claude.ai", ativo:true },
      { id:"AG004", nome:"Gerador de Ideias",        descricao:"Sugere pautas de conteúdo por área e público-alvo",      icon:"💡", link:"https://claude.ai", ativo:true },
      { id:"AG005", nome:"Analisador de Métricas",   descricao:"Interpreta resultados e sugere ajustes de estratégia",   icon:"📊", link:"https://claude.ai", ativo:true },
      { id:"AG006", nome:"Gerador de E-mail Mkt",    descricao:"Cria newsletters e sequências de e-mail marketing",      icon:"📧", link:"https://claude.ai", ativo:true },
      { id:"AG007", nome:"Criador de Arte (Canva)",  descricao:"Abre o Canva para criação de artes para redes sociais",  icon:"🎨", link:"https://canva.com",  ativo:true }
    ],
    calendarioEditorial: [],
    ideias: [],
    campanhas: [],
    metricas: []
  },
  fichasCredito: [],
  // ── Tabelas do Dashboard Tributário ──────────────────────────
  segmentosTrib: [
    { id: "SEG001", nome: "Comércio / Varejo" },
    { id: "SEG002", nome: "Serviços" },
    { id: "SEG003", nome: "Indústria" },
    { id: "SEG004", nome: "Construção Civil" },
    { id: "SEG005", nome: "Saúde" },
    { id: "SEG006", nome: "Agronegócio" },
    { id: "SEG007", nome: "Tecnologia" },
    { id: "SEG008", nome: "Holding / Patrimônio" },
    { id: "SEG009", nome: "Aposentados / PF" },
    { id: "SEG010", nome: "Investidores" },
    { id: "SEG011", nome: "Transporte e Logística" },
    { id: "SEG012", nome: "Educação" },
  ],
  tributos: [
    { id: "TRI001", nome: "IR / IRPF" },
    { id: "TRI002", nome: "CSLL" },
    { id: "TRI003", nome: "INSS" },
    { id: "TRI004", nome: "ICMS" },
    { id: "TRI005", nome: "PIS/COFINS" },
    { id: "TRI006", nome: "ITCMD" },
    { id: "TRI007", nome: "ITBI" },
    { id: "TRI008", nome: "IPVA" },
    { id: "TRI009", nome: "ISS" },
    { id: "TRI010", nome: "FGTS" },
    { id: "TRI011", nome: "PGFN / Dívida Ativa" },
    { id: "TRI012", nome: "DCTF / PER/DCOMP" },
  ],

  catalogoTarefas: [
    { id: "TAR001", nome: "Solicitar documentação ao cliente",    prazoExecucaoDias: 3  },
    { id: "TAR002", nome: "Analisar documentação recebida",       prazoExecucaoDias: 5  },
    { id: "TAR003", nome: "Elaborar petição / parecer",           prazoExecucaoDias: 10 },
    { id: "TAR004", nome: "Protocolar petição",                   prazoExecucaoDias: 2  },
    { id: "TAR005", nome: "Enviar PER/DCOMP",                     prazoExecucaoDias: 3  },
    { id: "TAR006", nome: "Acompanhar prazo processual",          prazoExecucaoDias: 7  },
    { id: "TAR007", nome: "Agendar reunião com cliente",          prazoExecucaoDias: 5  },
    { id: "TAR008", nome: "Emitir relatório ao cliente",          prazoExecucaoDias: 7  },
    { id: "TAR009", nome: "Obter procuração",                     prazoExecucaoDias: 5  },
    { id: "TAR010", nome: "Consultar andamento processual",       prazoExecucaoDias: 3  },
    { id: "TAR011", nome: "Interpor recurso",                     prazoExecucaoDias: 15 },
    { id: "TAR012", nome: "Homologar acordo / transação",         prazoExecucaoDias: 10 },
    { id: "TAR013", nome: "Comunicar resultado ao cliente",       prazoExecucaoDias: 2  },
    { id: "TAR014", nome: "Arquivar processo / fechar contrato",  prazoExecucaoDias: 5  },
    { id: "TAR015", nome: "Cálculo de crédito / atualização",     prazoExecucaoDias: 7  },
    { id: "TAR016", nome: "Agendar audiência",                    prazoExecucaoDias: 10 },
    { id: "TAR017", nome: "Preparar defesa administrativa",       prazoExecucaoDias: 10 },
    { id: "TAR018", nome: "Notificar parte contrária",            prazoExecucaoDias: 3  },
    { id: "TAR019", nome: "Assinar e autenticar documentos",      prazoExecucaoDias: 3  },
    { id: "TAR020", nome: "Diligência / visita técnica",          prazoExecucaoDias: 7  },
  ],
  documentosTrib: [
    { id: "DOC001", nome: "CNPJ / Contrato Social" },
    { id: "DOC002", nome: "PGDAS-D (Simples Nacional)" },
    { id: "DOC003", nome: "EFD-Contribuições" },
    { id: "DOC004", nome: "SPED Fiscal" },
    { id: "DOC005", nome: "Folha de Pagamento (últimos 5 anos)" },
    { id: "DOC006", nome: "NF-e de Compra e Venda" },
    { id: "DOC007", nome: "PER/DCOMP anteriores" },
    { id: "DOC008", nome: "Laudos Médicos / CID" },
    { id: "DOC009", nome: "Extrato FGTS" },
    { id: "DOC010", nome: "DCTF (últimos 5 anos)" },
    { id: "DOC011", nome: "ECF (últimos 5 anos)" },
    { id: "DOC012", nome: "Escrituração Contábil Digital (ECD)" },
  ],
  salaVirtual: {
    salas: [
      { id: "sala_comercial",   nome: "Sala Comercial",           icon: "🎯", cor: "#3b82f6" },
      { id: "sala_operacional", nome: "Sala Operacional Jurídica", icon: "⚖️", cor: "#8b5cf6" },
      { id: "sala_rct",         nome: "Sala Operacional RCT",      icon: "🔍", cor: "#10b981" },
      { id: "sala_reunioes",    nome: "Sala de Reunioes",            icon: "📢", cor: "#f59e0b" }
    ],
    presencas: [],
    mensagens: [],
    configuracoes: { pollingInterval: 30, ausenciaApos: 10 }
  },
  checklistsGlobais: {},  // Sobrescreve CHECKLISTS_FASE quando preenchido — editável pelo usuário
};

const AREAS_JURIDICAS = ["Previdenciário", "Tributário", "Trabalhista", "Empresarial", "Cível", "Família e Sucessões", "Imobiliário", "Ambiental", "Digital", "Agronegócio"];
const ESPECIALIDADES = ["Previdenciário", "Tributário", "Trabalhista", "Empresarial", "Cível", "Família e Sucessões", "Imobiliário", "Ambiental", "Digital", "Generalista"];
const VINCULOS = ["Sócio", "CLT", "PJ", "Estagiário", "Parceiro"];

const FUNCOES = ["Advogado", "Advogado Sênior", "Assistente Jurídico", "Paralegal", "Estagiário", "Administrativo", "Financeiro"];
// Taxonomia oficial do catálogo de POPs do escritório (POP - ESTRUTURA.pdf, 15/07/2026)
const GRUPOS_POP = ["ESTRUTURAIS (empresa)", "PRODUÇÃO TRANSVERSAL (técnicos)", "PRODUTO (específicos)"];
const SUBGRUPOS_POP = {
  "ESTRUTURAIS (empresa)": ["COMERCIAL", "CONTROLE OPERACIONAL", "FINANCEIRO", "GOVERNANÇA"],
  "PRODUÇÃO TRANSVERSAL (técnicos)": ["PRÉ-PROCESSUAL", "PROCESSUAL", "CONTRATUAL"],
  "PRODUTO (específicos)": ["AREA PREVIDENCIARIA", "AREA TRIBUTARIA", "AREA HOLDING", "AREA BANCARIA",
    "AREA FAMILIA E SUCESSOES", "AREA CONTRATOS", "AREA IMOBILIARIO"],
};

// Catálogo oficial de 33 POPs (POP - ESTRUTURA.pdf, 15/07/2026) — usado para importação inicial em ModPOPs
const POPS_CATALOGO_OFICIAL = [
  ["EST-COM-01","Atendimento Inicial","COMERCIAL","ESTRUTURAIS (empresa)"],
  ["EST-COM-02","Qualificação de Lead","COMERCIAL","ESTRUTURAIS (empresa)"],
  ["EST-COM-03","Reunião Estratégica","COMERCIAL","ESTRUTURAIS (empresa)"],
  ["EST-COM-04","Emissão de Proposta","COMERCIAL","ESTRUTURAIS (empresa)"],
  ["EST-COM-05","Formalização Contratual","COMERCIAL","ESTRUTURAIS (empresa)"],
  ["EST-COM-06","Onboarding do Cliente","COMERCIAL","ESTRUTURAIS (empresa)"],
  ["EST-CTRL-01","Controle de Prazo Judicial","CONTROLE OPERACIONAL","ESTRUTURAIS (empresa)"],
  ["EST-CTRL-02","Controle de Prazo Administrativo","CONTROLE OPERACIONAL","ESTRUTURAIS (empresa)"],
  ["EST-CTRL-03","Atualização Semanal da Base","CONTROLE OPERACIONAL","ESTRUTURAIS (empresa)"],
  ["EST-CTRL-04","Monitoramento de Processos Parados","CONTROLE OPERACIONAL","ESTRUTURAIS (empresa)"],
  ["EST-CTRL-05","Gestão de Pendência Documental","CONTROLE OPERACIONAL","ESTRUTURAIS (empresa)"],
  ["EST-CTRL-06","Reclassificação de Risco","CONTROLE OPERACIONAL","ESTRUTURAIS (empresa)"],
  ["EST-FIN-01","Cadastro de Honorários","FINANCEIRO","ESTRUTURAIS (empresa)"],
  ["EST-FIN-02","Controle de Recebimento","FINANCEIRO","ESTRUTURAIS (empresa)"],
  ["EST-FIN-03","Controle de Êxito","FINANCEIRO","ESTRUTURAIS (empresa)"],
  ["EST-FIN-04","Apuração de Margem Real por Produto","FINANCEIRO","ESTRUTURAIS (empresa)"],
  ["EST-GOV-01","Auditoria Trimestral de POP","GOVERNANÇA","ESTRUTURAIS (empresa)"],
  ["EST-GOV-02","Revisão Semestral de Produtos","GOVERNANÇA","ESTRUTURAIS (empresa)"],
  ["EST-GOV-03","Avaliação de Retrabalho","GOVERNANÇA","ESTRUTURAIS (empresa)"],
  ["EST-GOV-04","Análise de Gargalos","GOVERNANÇA","ESTRUTURAIS (empresa)"],
  ["EST-GOV-05","Controle de Concentração de Receita","GOVERNANÇA","ESTRUTURAIS (empresa)"],
  ["PROD-PRE-01","Triagem Técnica","PRÉ-PROCESSUAL","PRODUÇÃO TRANSVERSAL (técnicos)"],
  ["PROD-PRE-02","Análise Estratégica do Caso","PRÉ-PROCESSUAL","PRODUÇÃO TRANSVERSAL (técnicos)"],
  ["PROD-PRE-03","Cálculo Jurídico","PRÉ-PROCESSUAL","PRODUÇÃO TRANSVERSAL (técnicos)"],
  ["PROD-JUD-01","Elaboração de Petição Inicial","PROCESSUAL","PRODUÇÃO TRANSVERSAL (técnicos)"],
  ["PROD-JUD-02","Elaboração de Manifestação","PROCESSUAL","PRODUÇÃO TRANSVERSAL (técnicos)"],
  ["PROD-JUD-03","Elaboração de Recurso","PROCESSUAL","PRODUÇÃO TRANSVERSAL (técnicos)"],
  ["PROD-JUD-04","Cumprimento de Sentença","PROCESSUAL","PRODUÇÃO TRANSVERSAL (técnicos)"],
  ["PROD-JUD-05","Protocolo e Controle de Prazo","PROCESSUAL","PRODUÇÃO TRANSVERSAL (técnicos)"],
  ["PROD-CON-01","Elaboração de Contrato para Cliente","CONTRATUAL","PRODUÇÃO TRANSVERSAL (técnicos)"],
  ["PROD-CON-02","Revisão Contratual","CONTRATUAL","PRODUÇÃO TRANSVERSAL (técnicos)"],
  ["PROD-CON-03","Comunicação Técnica com Cliente","CONTRATUAL","PRODUÇÃO TRANSVERSAL (técnicos)"],
  ["PRODT-PREV-01","Previdenciário","AREA PREVIDENCIARIA","PRODUTO (específicos)"],
  ["PRODT-PREV-02","Previdenciário","AREA PREVIDENCIARIA","PRODUTO (específicos)"],
  ["PRODT-PREV-03","Previdenciário","AREA PREVIDENCIARIA","PRODUTO (específicos)"],
  ["PRODT-TRIB-01","Tributário","AREA TRIBUTARIA","PRODUTO (específicos)"],
  ["PRODT-TRIB-02","Tributário","AREA TRIBUTARIA","PRODUTO (específicos)"],
  ["PRODT-TRIB-03","Tributário","AREA TRIBUTARIA","PRODUTO (específicos)"],
  ["PRODT-HOLD-01","Holding Geral","AREA HOLDING","PRODUTO (específicos)"],
  ["PRODT-HOLD-02","Holding no Agro","AREA HOLDING","PRODUTO (específicos)"],
  ["PRODT-BANC-01","Defesa em Execuções no AGRO","AREA BANCARIA","PRODUTO (específicos)"],
  ["PRODT-BANC-02","Bancário","AREA BANCARIA","PRODUTO (específicos)"],
  ["PRODT-BANC-03","Bancário","AREA BANCARIA","PRODUTO (específicos)"],
  ["PRODT-FAM-01","Divórcio","AREA FAMILIA E SUCESSOES","PRODUTO (específicos)"],
  ["PRODT-FAM-02","Inventário","AREA FAMILIA E SUCESSOES","PRODUTO (específicos)"],
  ["PRODT-FAM-03","Família - Outros","AREA FAMILIA E SUCESSOES","PRODUTO (específicos)"],
  ["PRODT-CONTR-01","Contrato de Locação","AREA CONTRATOS","PRODUTO (específicos)"],
  ["PRODT-CONTR-02","Contrato de Compra e Venda","AREA CONTRATOS","PRODUTO (específicos)"],
  ["PRODT-CONTR-03","Prestação de Serviços (Outros)","AREA CONTRATOS","PRODUTO (específicos)"],
  ["PRODT-IMOB-01","Usucapião e Outras","AREA IMOBILIARIO","PRODUTO (específicos)"],
  ["PRODT-IMOB-02","Regularização de Imóveis","AREA IMOBILIARIO","PRODUTO (específicos)"],
].map(([id,nome,subgrupo,grupo])=>({id,nome,grupo,subgrupo,dataConfeccao:"",dataRevisao:"",versao:"1.0",status:"Ativo",link:"",obs:"",produtos:[],responsavelCargo:""}));

// RCC — Responsabilidades-Chave do Cargo (RCC - Alves & Correa Advogados v1.pdf, 15/07/2026)
const CARGOS_RCC = [
  { id:"socio_executivo", nome:"Sócio Diretor Executivo",
    missao:"Conduzir o crescimento do escritório, desenvolver negócios e garantir a sustentabilidade financeira.",
    responsabilidades:["Planejamento estratégico","Desenvolvimento comercial","Marketing","Gestão financeira"],
    indicadores:["Receita","Lucro","Novos Clientes"] },
  { id:"socio_juridico", nome:"Sócio Diretor Jurídico",
    missao:"Garantir excelência técnica, produtividade e qualidade da operação jurídica.",
    responsabilidades:["Gestão técnica","Distribuição de demandas","Desenvolvimento dos advogados","Revisão estratégica","Padronização técnica","Gestão da qualidade"],
    indicadores:["Qualidade","Produtividade","Cumprimento de Metas"] },
  { id:"controladoria_juridica", nome:"Controladoria Jurídica",
    missao:"Garantir que toda operação jurídica ocorra dentro dos padrões definidos pelo escritório. Principal cargo de segurança operacional.",
    responsabilidades:["Controle de prazos","Distribuição das tarefas","Conferência de protocolos","Auditoria de processos","Controle de produtividade","Indicadores operacionais","Fluxo dos processos"],
    indicadores:["Prazos Perdidos","Processos Auditados","Tempo Médio de Distribuição"] },
  { id:"advogado_associado", nome:"Advogado Associado Especialista",
    missao:"Responder integralmente pela carteira de clientes da sua especialidade (Cível, Trabalhista, Previdenciário, Família, Empresarial, Tributário, Consumidor, Criminal, Imobiliário).",
    responsabilidades:["Atendimento técnico","Estratégia processual","Petições","Audiências","Recursos","Pareceres","Reuniões com clientes","Atualização do sistema","Cumprimento dos prazos"],
    indicadores:["Prazos Cumpridos","Produção","Satisfação do Cliente"] },
  { id:"assistente_juridico", nome:"Assistente Jurídico (Paralegal)",
    missao:"Dar suporte operacional aos Advogados Associados, permitindo maior produtividade da equipe jurídica. Essencial para a escalabilidade da operação.",
    responsabilidades:["Cadastro de processos","Organização documental","Solicitação de documentos","Protocolos","Emissão de guias","Agendamento de audiências","Alimentação do software jurídico","Elaboração de minutas simples","Contato operacional com clientes","Controle de diligências","Organização dos arquivos","Apoio às petições"],
    indicadores:["Protocolos","Pendências Resolvidas","Organização Documental"] },
  { id:"estagiario", nome:"Estagiário",
    missao:"Auxiliar a equipe jurídica em atividades operacionais e desenvolver formação técnica.",
    responsabilidades:["Pesquisa jurisprudencial","Organização documental","Minutas simples","Protocolos","Atendimento interno","Apoio aos advogados"],
    indicadores:[] },
  { id:"assistente_adm_fin", nome:"Assistente Administrativo / Financeiro",
    missao:"Garantir a organização administrativa e financeira do escritório.",
    responsabilidades:["Contas a pagar","Contas a receber","Cobranças","Emissão de contratos","Emissão de notas fiscais","Fluxo de caixa","Compras","RH operacional","Apoio administrativo aos sócios"],
    indicadores:["Fluxo de Caixa","Inadimplência","Pagamentos em Dia"] },
  { id:"comercial", nome:"Comercial / Relacionamento",
    missao:"Converter oportunidades em novos clientes.",
    responsabilidades:["Atendimento inicial","Qualificação de leads","Agendamento","Follow-up","Elaboração de propostas","Fechamento de contratos","Pós-venda"],
    indicadores:["Conversão","Tempo de Resposta","Contratos Fechados"] },
  { id:"marketing_cargo", nome:"Marketing",
    missao:"Gerir a presença digital e a geração de oportunidades comerciais (inicialmente terceirizado).",
    responsabilidades:["Gestão das redes sociais","Produção de conteúdo","Posicionamento digital","Campanhas","Captação de leads","Apoio ao comercial"],
    indicadores:["Leads Gerados","Alcance","Agendamentos"] },
];
const RISCOS = ["Baixo", "Médio", "Alto"];
const STATUS_GERAL = ["Ativo", "Pausado", "Inativo"];
const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

// ============================================================
// TELA DE LOGIN
// ============================================================
function Login({ onLogin, usuarios }) {
  const [login, setLogin] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = () => {
    if (!login || !senha) { setErro("Preencha login e senha."); return; }
    setLoading(true);
    setErro("");
    apiPost('/auth.php?action=login', { login, senha })
      .then(res => {
        onLogin(res.usuario, res.token);
      })
      .catch(e => {
        setErro(e.message || "Login ou senha incorretos.");
        setLoading(false);
      });
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: `linear-gradient(135deg, ${theme.gray900} 0%, ${theme.gray800} 50%, ${theme.gray700} 100%)`,
      position: "relative", overflow: "hidden"
    }}>
      {/* Decoração de fundo */}
      <div style={{
        position: "absolute", inset: 0, opacity: 0.04,
        backgroundImage: `repeating-linear-gradient(45deg, ${theme.gold} 0, ${theme.gold} 1px, transparent 0, transparent 50%)`,
        backgroundSize: "20px 20px"
      }} />
      <div style={{
        position: "absolute", top: "10%", right: "10%", width: 300, height: 300,
        borderRadius: "50%", background: `radial-gradient(circle, ${theme.gold}22 0%, transparent 70%)`
      }} />
      <div style={{
        position: "absolute", bottom: "10%", left: "10%", width: 200, height: 200,
        borderRadius: "50%", background: `radial-gradient(circle, ${theme.gold}15 0%, transparent 70%)`
      }} />

      <div className="fade-in" style={{
        background: "rgba(255,255,255,0.97)", borderRadius: 16,
        padding: "48px 40px", width: 400, maxWidth: "90vw",
        boxShadow: `0 32px 80px rgba(0,0,0,0.4), 0 0 0 1px ${theme.gray200}`,
        position: "relative"
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{
            width: 120, height: 120, borderRadius: "50%",
            overflow: "hidden",
            margin: "0 auto 16px",
            boxShadow: `0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 ${theme.gold}44`,
            border: `2px solid ${theme.gold}44`
          }}>
            <img src="data:image/jpeg;base64,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" alt="Alves & Correa Advogados"
              style={{width:"100%", height:"100%", objectFit:"cover"}}/>
          </div>
          <h1 className="serif" style={{ fontSize: 20, color: theme.gray900, fontWeight: 600, letterSpacing: 1 }}>
            ALVES & CORREA ADVOGADOS
          </h1>
          <div style={{ width: 40, height: 1, background: theme.gold, margin: "12px auto 0" }} />
        </div>

        <p style={{ fontSize: 12, color: theme.gray500, textAlign: "center", marginBottom: 24, letterSpacing: 1 }}>
          SISTEMA OPERACIONAL JURÍDICO
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, color: theme.gray500, letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 6 }}>
            Usuário
          </label>
          <input
            value={login} onChange={e => setLogin(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            style={{
              width: "100%", padding: "10px 14px", border: `1px solid ${theme.gray200}`,
              borderRadius: 8, fontSize: 14, color: theme.gray800,
              background: theme.gray50, outline: "none",
              transition: "border 0.2s"
            }}
            onFocus={e => e.target.style.borderColor = theme.gold}
            onBlur={e => e.target.style.borderColor = theme.gray200}
            placeholder="seu.usuario"
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ fontSize: 11, color: theme.gray500, letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 6 }}>
            Senha
          </label>
          <input
            type="password" value={senha} onChange={e => setSenha(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            style={{
              width: "100%", padding: "10px 14px", border: `1px solid ${theme.gray200}`,
              borderRadius: 8, fontSize: 14, color: theme.gray800,
              background: theme.gray50, outline: "none",
              transition: "border 0.2s"
            }}
            onFocus={e => e.target.style.borderColor = theme.gold}
            onBlur={e => e.target.style.borderColor = theme.gray200}
            placeholder="••••••••"
          />
        </div>

        {erro && (
          <div style={{
            background: "#FEF2F2", border: `1px solid ${theme.red}44`,
            borderRadius: 8, padding: "10px 14px", marginBottom: 16,
            fontSize: 13, color: theme.red
          }}>
            {erro}
          </div>
        )}

        <button
          onClick={handleLogin}
          disabled={loading}
          style={{
            width: "100%", padding: "12px",
            background: loading ? theme.gray300 : `linear-gradient(135deg, ${theme.gray900}, ${theme.gray700})`,
            color: loading ? theme.gray500 : theme.gold,
            border: "none", borderRadius: 8, fontSize: 13,
            fontWeight: 600, letterSpacing: 2, textTransform: "uppercase",
            transition: "all 0.2s", cursor: loading ? "default" : "pointer"
          }}
        >
          {loading ? "Verificando..." : "Acessar Sistema"}
        </button>

        <p style={{ fontSize: 11, color: theme.gray400, textAlign: "center", marginTop: 20 }}>
          Acesso restrito · Alves & Correa Advogados
        </p>
      </div>
    </div>
  );
}

// ============================================================
// COMPONENTES UTILITÁRIOS
// ============================================================
function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
          border: "2px solid #C9A84C", overflow: "hidden",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          background: "#1A1610",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <span style={{ color: "#C9A84C", fontSize: 11, fontWeight: 700, fontFamily: "serif", letterSpacing: 0.5 }}>A&C</span>
        </div>
        <div>
          <h1 className="serif" style={{ fontSize: 28, color: theme.gray900, fontWeight: 600, lineHeight: 1 }}>
            {title}
          </h1>
          {subtitle && <p style={{ fontSize: 13, color: theme.gray500, marginTop: 6 }}>{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

function Btn({ children, onClick, variant = "primary", size = "md", disabled }) {
  const styles = {
    primary: { bg: theme.gray900, color: theme.gold, border: "none" },
    secondary: { bg: "transparent", color: theme.gray700, border: `1px solid ${theme.gray300}` },
    gold: { bg: theme.gold, color: theme.gray900, border: "none" },
    danger: { bg: "transparent", color: theme.red, border: `1px solid ${theme.red}44` },
    success: { bg: "transparent", color: theme.green, border: `1px solid ${theme.green}44` },
  };
  const sizes = {
    sm: { padding: "6px 12px", fontSize: 11 },
    md: { padding: "8px 16px", fontSize: 12 },
    lg: { padding: "11px 24px", fontSize: 13 },
  };
  const s = styles[variant]; const z = sizes[size];
  return (
    <button
      onClick={onClick} disabled={disabled}
      style={{
        background: s.bg, color: s.color, border: s.border,
        borderRadius: 7, fontWeight: 600, letterSpacing: 0.8,
        textTransform: "uppercase", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1, transition: "all 0.15s",
        ...z
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = "0.85"; }}
      onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
    >
      {children}
    </button>
  );
}

function Badge({ children, color = "gray" }) {
  const colors = {
    gray: { bg: theme.gray100, text: theme.gray600 },
    gold: { bg: `${theme.gold}22`, text: theme.goldDark },
    green: { bg: "#EBF5EE", text: theme.green },
    red: { bg: "#FDEEEE", text: theme.red },
    blue: { bg: "#EEF3FD", text: theme.blue },
    amber: { bg: "#FDF5E8", text: theme.amber },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{
      background: c.bg, color: c.text,
      padding: "2px 8px", borderRadius: 4,
      fontSize: 10, fontWeight: 600, letterSpacing: 0.8,
      textTransform: "uppercase", whiteSpace: "nowrap"
    }}>{children}</span>
  );
}

function Card({ children, style = {}, onClick, ...rest }) {
  return (
    <div onClick={onClick} style={{
      background: theme.white, borderRadius: 12,
      border: `1px solid ${theme.gray200}`,
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      cursor: onClick ? "pointer" : undefined,
      ...style
    }} {...rest}>{children}</div>
  );
}

function FormField({ label, required, children, hint }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 11, color: theme.gray500, letterSpacing: 0.8, textTransform: "uppercase", display: "block", marginBottom: 6 }}>
        {label}{required && <span style={{ color: theme.red, marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {hint && <p style={{ fontSize: 11, color: theme.gray400, marginTop: 4 }}>{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = "text", required }) {
  return (
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} required={required}
      style={{
        width: "100%", padding: "9px 12px",
        border: `1px solid ${theme.gray200}`, borderRadius: 7,
        fontSize: 13, color: theme.gray800, background: theme.white,
        outline: "none", transition: "border 0.2s"
      }}
      onFocus={e => e.target.style.borderColor = theme.gold}
      onBlur={e => e.target.style.borderColor = theme.gray200}
    />
  );
}

const maskPhone = v => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").replace(/-$/, "");
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").replace(/-$/, "");
};

// Lista de códigos de país (DDI) para WhatsApp internacional
const DDI_PAISES = [
  { value: "55",  label: "🇧🇷 +55  Brasil" },
  { value: "351", label: "🇵🇹 +351 Portugal" },
  { value: "1",   label: "🇺🇸 +1   EUA / Canadá" },
  { value: "44",  label: "🇬🇧 +44  Reino Unido" },
  { value: "34",  label: "🇪🇸 +34  Espanha" },
  { value: "39",  label: "🇮🇹 +39  Itália" },
  { value: "49",  label: "🇩🇪 +49  Alemanha" },
  { value: "33",  label: "🇫🇷 +33  França" },
  { value: "54",  label: "🇦🇷 +54  Argentina" },
  { value: "598", label: "🇺🇾 +598 Uruguai" },
  { value: "595", label: "🇵🇾 +595 Paraguai" },
  { value: "56",  label: "🇨🇱 +56  Chile" },
  { value: "57",  label: "🇨🇴 +57  Colômbia" },
  { value: "41",  label: "🇨🇭 +41  Suíça" },
  { value: "81",  label: "🇯🇵 +81  Japão" },
  { value: "86",  label: "🇨🇳 +86  China" },
  { value: "0",   label: "🌐 Outro (digitar completo)" },
];

// Máscara do número local — aplica padrão BR apenas quando DDI=55
const maskWhatsLocal = (v, ddi) => {
  const d = v.replace(/\D/g, "");
  if ((ddi||"55") === "55") {
    const dd = d.slice(0, 11);
    if (dd.length <= 10) return dd.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").replace(/-$/, "");
    return dd.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").replace(/-$/, "");
  }
  return d.slice(0, 14); // internacional — sem máscara forçada, limite de segurança
};

// Monta número completo (DDI+local, só dígitos) para uso em links wa.me
const whatsCompleto = (ddi, numero) => {
  const ddiNum = (ddi||"55").replace(/\D/g,"");
  const numNum = (numero||"").replace(/\D/g,"");
  return (ddiNum==="0" ? "" : ddiNum) + numNum;
};

// Campo composto: select de DDI + input do número local
function CampoWhatsApp({ ddi, numero, onChangeDDI, onChangeNumero, label = "WhatsApp" }) {
  const isBR = (ddi || "55") === "55";
  return (
    <FormField label={label}>
      <div style={{ display: "flex", gap: 6 }}>
        <div style={{ width: 150, flexShrink: 0 }}>
          <Select value={ddi || "55"} onChange={onChangeDDI} options={DDI_PAISES} />
        </div>
        <div style={{ flex: 1 }}>
          <Input
            value={numero || ""}
            onChange={v => onChangeNumero(maskWhatsLocal(v, ddi))}
            placeholder={isBR ? "(00) 00000-0000" : "Número local, sem o código do país"}
          />
        </div>
      </div>
    </FormField>
  );
}

// Campo de e-mail principal + lista de e-mails adicionais (botão "+"), usado no
// cadastro de Cliente (PF/PJ) e em cada contato de PJ — ver ModClientes.
function CampoEmailsMultiplos({ email, emailsAdicionais, onChangeEmail, onChangeAdicionais, label = "E-mail" }) {
  const adicionais = emailsAdicionais || [];
  return (
    <FormField label={label}>
      <div style={{ display: "flex", gap: 6 }}>
        <Input type="email" value={email || ""} onChange={onChangeEmail} placeholder="email@exemplo.com" />
        <button type="button" onClick={() => onChangeAdicionais([...adicionais, ""])}
          title="Adicionar outro e-mail"
          style={{ flexShrink: 0, width: 36, borderRadius: 7, border: `1px solid ${theme.gray200}`,
            background: theme.white, color: theme.gray600, fontSize: 16, fontWeight: 700, cursor: "pointer" }}>
          +
        </button>
      </div>
      {adicionais.map((em, i) => (
        <div key={i} style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <Input type="email" value={em || ""} onChange={v => {
            const novos = [...adicionais]; novos[i] = v; onChangeAdicionais(novos);
          }} placeholder="outro.email@exemplo.com" />
          <button type="button" onClick={() => onChangeAdicionais(adicionais.filter((_, idx) => idx !== i))}
            title="Remover"
            style={{ flexShrink: 0, width: 36, borderRadius: 7, border: `1px solid ${theme.gray200}`,
              background: theme.white, color: theme.red, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
            ✕
          </button>
        </div>
      ))}
    </FormField>
  );
}

function Select({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value} onChange={e => onChange(e.target.value)}
      style={{
        width: "100%", padding: "9px 12px",
        border: `1px solid ${theme.gray200}`, borderRadius: 7,
        fontSize: 13, color: value ? theme.gray800 : theme.gray400,
        background: theme.white, outline: "none", transition: "border 0.2s",
        appearance: "none", cursor: "pointer"
      }}
      onFocus={e => e.target.style.borderColor = theme.gold}
      onBlur={e => e.target.style.borderColor = theme.gray200}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {(options||[]).map(o => (
        <option key={typeof o === "object" ? o.value : o} value={typeof o === "object" ? o.value : o}>
          {typeof o === "object" ? o.label : o}
        </option>
      ))}
    </select>
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} rows={rows}
      style={{
        width: "100%", padding: "9px 12px",
        border: `1px solid ${theme.gray200}`, borderRadius: 7,
        fontSize: 13, color: theme.gray800, background: theme.white,
        outline: "none", resize: "vertical", transition: "border 0.2s"
      }}
      onFocus={e => e.target.style.borderColor = theme.gold}
      onBlur={e => e.target.style.borderColor = theme.gray200}
    />
  );
}

function Modal({ open, onClose, title, children, width = 600, fullPage = false }) {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 9999, padding: fullPage ? 0 : "24px 16px"
    }} onClick={e => !fullPage && e.target === e.currentTarget && onClose()}>
      <div className="fade-in" style={{
        background: theme.white, borderRadius: fullPage ? 0 : 14,
        width: "100%", maxWidth: fullPage ? "100%" : width,
        height: fullPage ? "100%" : undefined,
        boxShadow: fullPage ? "none" : "0 24px 60px rgba(0,0,0,0.2)",
        maxHeight: fullPage ? "100%" : "90vh", overflowY: "auto",
        display: "flex", flexDirection: "column"
      }}>
        <div style={{
          padding: "20px 24px 16px", borderBottom: `1px solid ${theme.gray100}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, background: theme.white, zIndex: 1
        }}>
          <h2 className="serif" style={{ fontSize: 20, fontWeight: 600, color: theme.gray900 }}>{title}</h2>
          <button onClick={onClose} style={{
            background: "none", border: "none", fontSize: 20,
            color: theme.gray400, cursor: "pointer", lineHeight: 1, padding: 4
          }}>×</button>
        </div>
        <div style={{ padding: 24, flex: fullPage ? 1 : undefined }}>{children}</div>
      </div>
    </div>,
    document.body
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.3 }}>{icon}</div>
      <p className="serif" style={{ fontSize: 18, color: theme.gray600, fontWeight: 500 }}>{title}</p>
      <p style={{ fontSize: 13, color: theme.gray400, marginTop: 6 }}>{subtitle}</p>
    </div>
  );
}

// ============================================================
// MÓDULO: CLIENTES
// ============================================================

// MODULO: PROCESSOS
function SecaoContratoVinculado({ contratos, processoId, clienteId, clientes, setData, theme }) {
  // Resolver clienteId — pode ser ID real ou nome string (processos importados)
  const cliObj   = (clientes||[]).find(c => c.id === clienteId || c.nome === clienteId);
  const cliNome  = cliObj?.nome || clienteId || "";
  const cliIdReal= cliObj?.id   || clienteId || "";

  // Filtrar contratos do cliente por ID ou por clienteNome
  const ctsCli = (contratos||[]).filter(ct =>
    ct.clienteId === cliIdReal ||
    ct.clienteId === clienteId ||
    (cliNome && (ct.clienteNome||"").toLowerCase().trim() === cliNome.toLowerCase().trim())
  );
  const ctAtual = (contratos||[]).find(ct => (ct.processosIds||[]).includes(processoId));

  const vincular = (novoCtId) => {
    setData(prev => ({
      ...prev,
      contratos: (prev.contratos||[]).map(ct => {
        if ((ct.processosIds||[]).includes(processoId) && ct.id !== novoCtId)
          return { ...ct, processosIds: (ct.processosIds||[]).filter(pid => pid !== processoId) };
        if (ct.id === novoCtId && !(ct.processosIds||[]).includes(processoId))
          return { ...ct, processosIds: [...(ct.processosIds||[]), processoId] };
        return ct;
      })
    }));
  };

  return (
    <div style={{marginTop:12,padding:"12px 14px",background:"#f0f9ff",
      border:`1px solid ${theme.blue}33`,borderRadius:8}}>
      <p style={{fontSize:12,fontWeight:700,color:theme.gray700,marginBottom:8}}>
        📋 Contrato Vinculado na Base Operacional
      </p>
      {!clienteId ? (
        <p style={{fontSize:11,color:"#92400e",margin:0}}>⚠️ Selecione um cliente para ver os contratos.</p>
      ) : ctsCli.length === 0 ? (
        <p style={{fontSize:11,color:"#92400e",margin:0}}>
          ⚠️ Nenhum contrato encontrado para <strong>{cliNome}</strong> na Base Operacional.
          Verifique se o cadastro do cliente tem o mesmo nome nos contratos.
        </p>
      ) : (
        <>
          {ctAtual && (
            <div style={{fontSize:11,color:theme.green,fontWeight:600,marginBottom:8,
              padding:"6px 10px",background:"#f0fdf4",borderRadius:6}}>
              ✅ Vinculado: <strong>{ctAtual.numContrato}</strong>
            </div>
          )}
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {ctsCli.map(ct => {
              const sel = ctAtual?.id === ct.id;
              return (
                <label key={ct.id} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",
                  padding:"8px 12px",borderRadius:7,
                  background:sel?"#dbeafe":"#fff",
                  border:`2px solid ${sel?theme.blue:theme.gray200}`}}>
                  <input type="radio" name={`cvp_${processoId}`} checked={sel}
                    onChange={()=>vincular(ct.id)} style={{accentColor:theme.blue}}/>
                  <div style={{flex:1}}>
                    <p style={{fontSize:12,fontWeight:700,color:theme.gray900,margin:0}}>{ct.numContrato}</p>
                    <p style={{fontSize:11,color:theme.gray500,margin:"2px 0 0"}}>
                      {ct.clienteNome||cliNome}{ct.produto ? " · "+ct.produto : ""}{ct.area ? " · "+ct.area : ""}
                      {(ct.processosIds||[]).length > 0 &&
                        <span style={{marginLeft:6,padding:"1px 5px",borderRadius:3,
                          background:"#dcfce7",color:"#16a34a",fontSize:10}}>
                          {(ct.processosIds||[]).length} processo(s)
                        </span>}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
          {ctAtual && (
            <button onClick={()=>vincular("")}
              style={{marginTop:8,padding:"5px 12px",borderRadius:6,
                border:`1px solid ${theme.red}`,background:"#fef2f2",
                color:theme.red,cursor:"pointer",fontSize:11,fontWeight:600}}>
              × Desvincular
            </button>
          )}
          <p style={{fontSize:10,color:theme.gray400,marginTop:6}}>
            Selecione o contrato para vincular este processo.
          </p>
        </>
      )}
    </div>
  );
}

function ModProcessos({ data, setData, navegarPara }) {
  const { tiposOcorrencia = [] } = data;
  const { processos = [], clientes = [], equipe = [] } = data;
  const contratos       = data.contratos        || [];
  const baseOperacional = data.baseOperacional   || [];
  const areas = data.areas || AREAS_INICIAIS;
  const TIPOS_PROC = ["Judicial","Administrativo","Extrajudicial","Arbitragem"];
  const STATUS_P = ["Em andamento","Suspenso","Arquivado","Encerrado","Aguardando"];
  const TIPOS_OC = ["Distribuicao","Despacho","Decisao","Sentenca","Acordao","Audiencia","Diligencia","Recurso","Transito em Julgado","Outro"];
  const TJS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO","TRF1","TRF2","TRF3","TRF4","TRF5","STJ","STF"];
  const [busca,setBusca]=useState(""); const [fArea,setFArea]=useState(""); const [fStatus,setFStatus]=useState(""); const [fTipo,setFTipo]=useState("");
  const [modalOpen,setModalOpen]=useState(false); const [viewItem,setViewItem]=useState(null);
  const [editItem,setEditItem]=useState(null);
  const [relOpen,setRelOpen]=useState(false); const [relMes,setRelMes]=useState(new Date().toISOString().slice(0,7)); const [relTab,setRelTab]=useState("rel1");
  const [lmSincronizando,setLmSincronizando]=useState(false);
  const [lmResultado,setLmResultado]=useState(null);
  const sincronizarLegalMail = async () => {
    setLmSincronizando(true); setLmResultado(null);
    try {
      const res = await fetch("http://localhost:8765/legalmail/sincronizar", { method:"POST" });
      const json = await res.json();
      setLmResultado(json);
    } catch {
      setLmResultado({ ok:false, erro:"Não foi possível conectar ao servidor local (localhost:8765). Verifique se o serviço de Pesquisa Processual está rodando nesta máquina." });
    }
    setLmSincronizando(false);
  };
  const EMPTY={id:"",clienteId:"",contratoId:"",numeroProcesso:"",tipoProcesso:"Judicial",area:"",tjUf:"SP",responsavel:"",status:"Em andamento",dataDistribuicao:"",ocorrencias:[]};
  const normTxt=s=>(s||"").toString().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g,"").trim();
  const filtrados=processos.filter(p=>{
    const cli=clientes.find(c=>c.id===p.clienteId);
    const resp=equipe.find(e=>e.id===p.responsavel);
    const b=busca.toLowerCase().replace(/[.\-\/\s]/g,"");
    const bruto=normTxt(busca);
    return(!busca
      ||(p.numeroProcesso||"").toLowerCase().replace(/[.\-\/\s]/g,"").includes(b)
      ||normTxt(cli?.nome).includes(bruto)
      ||(cli?.cpfCnpj||"").replace(/[.\-\/\s]/g,"").includes(b)
      ||normTxt(resp?.nome).includes(bruto)
      ||(resp?.oab||"").toLowerCase().replace(/[.\-\/\s]/g,"").includes(b)
    )&&(!fArea||p.area===fArea)&&(!fStatus||p.status===fStatus)&&(!fTipo||p.tipoProcesso===fTipo);
  }).sort((a,b)=>{const na=clientes.find(c=>c.id===a.clienteId)?.nome||a.clienteId||"";const nb=clientes.find(c=>c.id===b.clienteId)?.nome||b.clienteId||"";return na.localeCompare(nb,"pt-BR");});
  const save=()=>{
    if(!editItem.numeroProcesso||!editItem.clienteId)return alert("Cliente e Numero obrigatorios.");
    const id=editItem.id||"PROC"+String(Date.now()).slice(-6); const item={...editItem,id};
    const lista=editItem.id?processos.map(p=>p.id===item.id?item:p):[...processos,item];
    const clis=clientes.map(c=>{if(c.id!==item.clienteId)return c;const ps=c.processos||[];return ps.includes(item.id)?c:{...c,processos:[...ps,item.id]};});
    setData(prev => ({...prev, processos: lista, clientes: clis}));setModalOpen(false);
  };
  const remove=id=>{if(!confirm("Remover processo?"))return;setData(prev => ({...prev, processos: processos.filter(p=>p.id!==id)}));};

  const cliN=id=>clientes.find(c=>c.id===id)?.nome||id;
  const eqN=id=>equipe.find(e=>e.id===id)?.nome||id;

  // ── CSS e imagens dos relatórios ──
  const CSS_REL = "<style>body{font-family:Arial,sans-serif;margin:0;padding:0;color:#2A2420}.page{padding:20px 40px 100px 40px;min-height:250mm;box-sizing:border-box}.hdr{text-align:right;margin-bottom:20px;padding-bottom:8px;border-bottom:2px solid #C9A84C}.hdr img{max-height:70px;max-width:280px}.ref{font-size:12px;color:#333;margin-bottom:3px}h1{font-size:14px;font-weight:bold;text-align:center;text-transform:uppercase;margin:20px 0 16px 0;border-bottom:1px solid #ccc;padding-bottom:8px}h2{font-size:12px;font-weight:bold;margin:16px 0 7px 0}p{font-size:12px;line-height:1.7;margin:0 0 10px 0}.tab{width:100%;border-collapse:collapse;margin:10px 0 14px 0;font-size:11px}.tab th{background:#1A1610;color:#C9A84C;padding:7px 10px;text-align:left;font-weight:bold;border:1px solid #555}.tab td{padding:7px 10px;border:1px solid #ddd;vertical-align:top;line-height:1.5}.tab tr:nth-child(even) td{background:#F9F7F2}.pnum{font-family:monospace;font-size:10px;color:#445;font-weight:bold}.sem-oc{color:#888;font-style:italic}.ultima-oc{color:#8B1C1C;font-size:10px;font-weight:bold}.assin{margin-top:28px;font-size:12px}.page-break{page-break-after:always}.ftr{position:fixed;bottom:0;left:0;right:0;text-align:center}.ftr img{max-width:480px;max-height:55px}.rg-page{padding:20px 30px 100px 30px}.rg-title{font-size:16px;font-weight:bold;text-align:center;text-transform:uppercase;margin:16px 0;border-bottom:2px solid #C9A84C;padding-bottom:10px}.rg-sub{font-size:11px;text-align:center;color:#666;margin-bottom:20px}.rg-sec{font-size:13px;font-weight:bold;margin:20px 0 8px 0;padding:6px 12px;background:#1A1610;color:#C9A84C;border-radius:4px}.rg-tab{width:100%;border-collapse:collapse;margin-bottom:16px;font-size:11px}.rg-tab th{background:#3A3025;color:#F4E8C1;padding:6px 10px;text-align:left;border:1px solid #555}.rg-tab td{padding:6px 10px;border:1px solid #ddd;vertical-align:top}.rg-tab tr:nth-child(even) td{background:#F9F7F2}.rg-cli{font-weight:bold;font-size:12px;color:#1A1610}.badge-ok{color:#155724;background:#d4edda;padding:1px 7px;border-radius:3px;font-size:10px;font-weight:bold}.badge-sem{color:#721c24;background:#f8d7da;padding:1px 7px;border-radius:3px;font-size:10px;font-weight:bold}@media print{@page{margin:10mm 15mm 25mm 15mm}body{padding:0}.ftr{position:fixed;bottom:0}}</style>";
  const HDR_REL = '<div class="hdr"><img src="data:image/jpeg;base64,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"/></div>';
  const FTR_REL = '<div class="ftr"><img src="data:image/jpeg;base64,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"/></div>';
  const relWin = (titulo, corpo) => {
    const win = window.open("","_blank","width=960,height=750");
    win.document.write(`<html><head><title>${titulo}</title>${CSS_REL}</head><body>${corpo}${FTR_REL}</body></html>`);
    win.document.close(); win.focus(); setTimeout(()=>win.print(), 900);
  };

  // ── Helpers ──
  const ultimaOc = p => (p.ocorrencias||[]).slice().sort((a,b)=>a.data>b.data?-1:1)[0] || null;
  const ocsMes = p => (p.ocorrencias||[]).filter(o=>o.data?.startsWith(relMes));
  const clientesComOc = () => {
    const comMov = new Set(processos.filter(p=>ocsMes(p).length>0).map(p=>p.clienteId));
    const map = {};
    processos.forEach(proc=>{
      if(!comMov.has(proc.clienteId)) return;
      if(!map[proc.clienteId]) map[proc.clienteId]={clienteId:proc.clienteId,processos:[]};
      map[proc.clienteId].processos.push({...proc,_ocsMes:ocsMes(proc)});
    });
    return Object.values(map);
  };
  const semMovimento = () => processos.filter(p=>p.status==="Em andamento" && ocsMes(p).length===0);
  const comOcMes = processos.filter(p=>ocsMes(p).length>0);

  // ── Gerador REL 1: um PDF por cliente (com ocorrência) ──
  const gerarHtmlRel1 = (clienteId, procs, nm) => {
    const cli = clientes.find(c=>c.id===clienteId);
    const nomeCliente = cli?.nome || clienteId;
    const dataGer = new Date().toLocaleDateString('pt-BR');
    const rows = procs.flatMap(proc => {
      const ocs = proc._ocsMes || [];
      const ult = ultimaOc(proc);
      if(ocs.length > 0) {
        return ocs.map(oc=>`<tr>
          <td class="pnum">${proc.numeroProcesso}<br/><span style="font-family:sans-serif;font-size:9px;color:#666">${proc.area} | TJ/${proc.tjUf}</span></td>
          <td>${oc.data}</td>
          <td>${oc.tipo}</td>
          <td>${oc.descricao}</td>
        </tr>`);
      } else {
        const ultInfo = ult ? `Última ocorrência: ${ult.data} — ${ult.tipo}` : 'Nenhuma ocorrência registrada';
        return [`<tr>
          <td class="pnum">${proc.numeroProcesso}<br/><span style="font-family:sans-serif;font-size:9px;color:#666">${proc.area} | TJ/${proc.tjUf}</span></td>
          <td colspan="2" class="sem-oc">Sem movimentação no período</td>
          <td class="ultima-oc">${ultInfo}</td>
        </tr>`];
      }
    });
    return `<div class="page">
      ${HDR_REL}
      <div class="ref"><b>Referência:</b> ${nm}</div>
      <div class="ref"><b>Cliente:</b> ${nomeCliente}</div>
      <h1>RELATÓRIO MENSAL DE ACOMPANHAMENTO PROCESSUAL</h1>
      <h2>1. Introdução</h2>
      <p>Prezado(a) <b>${nomeCliente}</b>,</p>
      <p>Encaminhamos o presente relatório mensal com o objetivo de mantê-lo(a) atualizado(a) sobre o andamento do seu processo no período de <b>${nm}</b>. Nosso compromisso é garantir transparência, acompanhamento contínuo e segurança na condução da sua demanda.</p>
      <h2>2. Andamento Processual no Período</h2>
      <p>No período analisado, foram registradas as seguintes movimentações processuais:</p>
      <table class="tab">
        <thead><tr><th>PROCESSO</th><th>DATA</th><th>TIPO</th><th>OCORRÊNCIA</th></tr></thead>
        <tbody>${rows.join('')}</tbody>
      </table>
      <p>Cada ato foi analisado com atenção e as medidas cabíveis foram adotadas dentro dos prazos legais.</p>
      <p>Além disso, realizamos a análise estratégica do impacto dessa movimentação no andamento do processo, avaliando riscos, oportunidades e os possíveis próximos desdobramentos. Essa atuação preventiva e planejada nos permite adotar as medidas mais adequadas em cada etapa, sempre com foco na proteção dos seus interesses e na condução segura da demanda.</p>
      <h2>3. Considerações Estratégicas</h2>
      <p>Seguimos atuando de forma técnica e estratégica, sempre atentos às oportunidades processuais e aos próximos desdobramentos. Nosso foco permanece na condução responsável e eficiente da sua demanda, buscando preservar seus interesses com segurança e diligência.</p>
      <p>Caso haja qualquer novidade relevante fora do ciclo mensal de relatórios, você será prontamente comunicado(a).</p>
      <h2>4. Conclusão</h2>
      <p>Reforçamos nosso compromisso com a transparência, organização e acompanhamento próximo do seu caso. Permanecemos à disposição para esclarecer eventuais dúvidas e prestar os esclarecimentos necessários.</p>
      <p>Conte conosco para seguir conduzindo sua demanda com responsabilidade e dedicação.</p>
      <div class="assin"><p>Atenciosamente,</p><p><b>Alves &amp; Correa Advogados</b></p><p>Gerado em ${dataGer}</p></div>
    </div>`;
  };

  // ── Gerador REL 2: PDF por processo sem ocorrência ──
  const gerarHtmlRel2 = (proc, nm) => {
    const cli = clientes.find(c=>c.id===proc.clienteId);
    const nomeCliente = cli?.nome || proc.clienteId;
    const dataGer = new Date().toLocaleDateString('pt-BR');
    const ult = ultimaOc(proc);
    const ultTexto = ult ? `${ult.data} (${ult.tipo})` : 'nenhuma ocorrência registrada até o momento';
    return `<div class="page">
      ${HDR_REL}
      <div class="ref"><b>Referência:</b> ${nm}</div>
      <div class="ref"><b>Cliente:</b> ${nomeCliente}</div>
      <h1>RELATÓRIO MENSAL DE ACOMPANHAMENTO PROCESSUAL</h1>
      <h2>1. Introdução</h2>
      <p>Prezado(a) <b>${nomeCliente}</b>,</p>
      <p>Encaminhamos o presente relatório mensal com o objetivo de mantê-lo(a) atualizado(a) sobre o andamento do seu processo no período de <b>${nm}</b>. Nosso compromisso é garantir transparência, acompanhamento contínuo e segurança na condução da sua demanda.</p>
      <h2>2. Andamento Processual no Período</h2>
      <p>No período analisado, não houve nova movimentação processual registrada nos autos do processo nº <b>${proc.numeroProcesso}</b> (${proc.area} | TJ/${proc.tjUf}).</p>
      <p>A última movimentação registrada data de <b>${ultTexto}</b>.</p>
      <p>Destacamos que essa situação é comum em determinados momentos do trâmite judicial, especialmente considerando os prazos processuais e a rotina do Judiciário. O processo permanece em acompanhamento contínuo, com vigilância constante por nossa equipe.</p>
      <h2>3. Considerações Estratégicas</h2>
      <p>Seguimos atuando de forma técnica e estratégica, sempre atentos às oportunidades processuais e aos próximos desdobramentos. Nosso foco permanece na condução responsável e eficiente da sua demanda, buscando preservar seus interesses com segurança e diligência.</p>
      <p>Caso haja qualquer novidade relevante fora do ciclo mensal de relatórios, você será prontamente comunicado(a).</p>
      <h2>4. Conclusão</h2>
      <p>Reforçamos nosso compromisso com a transparência, organização e acompanhamento próximo do seu caso. Permanecemos à disposição para esclarecer eventuais dúvidas e prestar os esclarecimentos necessários.</p>
      <p>Conte conosco para seguir conduzindo sua demanda com responsabilidade e dedicação.</p>
      <div class="assin"><p>Atenciosamente,</p><p><b>Alves &amp; Correa Advogados</b></p><p>Gerado em ${dataGer}</p></div>
    </div>`;
  };

  // ── Gerador RELATÓRIO GERAL (controle do escritório) ──
  const gerarHtmlRelGeral = (nm) => {
    const dataGer = new Date().toLocaleString('pt-BR');
    // Agrupar todos os processos ativos por cliente
    const mapCli = {};
    processos.forEach(proc => {
      if(!mapCli[proc.clienteId]) mapCli[proc.clienteId] = [];
      mapCli[proc.clienteId].push(proc);
    });
    const clientesList = Object.keys(mapCli).sort((a,b)=>{
      const na = clientes.find(c=>c.id===a)?.nome||a;
      const nb = clientes.find(c=>c.id===b)?.nome||b;
      return na.localeCompare(nb);
    });
    const totalProc = processos.length;
    const comMov = processos.filter(p=>ocsMes(p).length>0).length;
    const semMov = processos.filter(p=>p.status==="Em andamento" && ocsMes(p).length===0).length;
    const rows = clientesList.flatMap(cliId => {
      const cli = clientes.find(c=>c.id===cliId);
      const procs = mapCli[cliId];
      return procs.map((proc,i) => {
        const temOc = ocsMes(proc).length > 0;
        const ult = ultimaOc(proc);
        const ultTxt = ult ? `${ult.data}<br/><small>${ult.tipo}</small>` : '<span style="color:#aaa">—</span>';
        const badge = temOc
          ? `<span class="badge-ok">COM MOVIMENTO</span>`
          : (proc.status==="Em andamento" ? `<span class="badge-sem">SEM MOVIMENTO</span>` : `<span style="color:#888;font-size:10px">${proc.status}</span>`);
        const ocsTxt = temOc
          ? ocsMes(proc).map(o=>`<div style="margin-bottom:4px"><b>${o.data}</b> · ${o.tipo}<br/><span style="color:#444">${o.descricao}</span></div>`).join('')
          : `<span class="ultima-oc">Última: ${ult ? ult.data+' — '+ult.tipo : 'nenhuma'}</span>`;
        return `<tr>
          ${i===0 ? `<td class="rg-cli" rowspan="${procs.length}" style="border-right:2px solid #C9A84C">${cli?.nome||cliId}</td>` : ''}
          <td class="pnum">${proc.numeroProcesso}<br/><small>${proc.area} | TJ/${proc.tjUf}</small></td>
          <td style="text-align:center">${badge}</td>
          <td style="font-size:10px">${ultTxt}</td>
          <td style="font-size:10px;max-width:220px">${ocsTxt}</td>
        </tr>`;
      });
    });
    return `<div class="rg-page">
      ${HDR_REL}
      <div class="rg-title">RELATÓRIO GERAL DE CONTROLE PROCESSUAL</div>
      <div class="rg-sub">Período de referência: ${nm} &nbsp;|&nbsp; Gerado em: ${dataGer}</div>
      <div style="display:flex;gap:24px;margin-bottom:20px;font-size:11px">
        <div style="flex:1;background:#F4F1EA;border-radius:6px;padding:12px;text-align:center">
          <div style="font-size:22px;font-weight:bold;color:#1A1610">${totalProc}</div>
          <div style="color:#666">Total de processos</div>
        </div>
        <div style="flex:1;background:#d4edda;border-radius:6px;padding:12px;text-align:center">
          <div style="font-size:22px;font-weight:bold;color:#155724">${comMov}</div>
          <div style="color:#155724">Com movimentação</div>
        </div>
        <div style="flex:1;background:#f8d7da;border-radius:6px;padding:12px;text-align:center">
          <div style="font-size:22px;font-weight:bold;color:#721c24">${semMov}</div>
          <div style="color:#721c24">Em andamento sem movimento</div>
        </div>
        <div style="flex:1;background:#fff3cd;border-radius:6px;padding:12px;text-align:center">
          <div style="font-size:22px;font-weight:bold;color:#856404">${clientesList.length}</div>
          <div style="color:#856404">Clientes com processo</div>
        </div>
      </div>
      <table class="rg-tab">
        <thead><tr>
          <th style="width:140px">CLIENTE</th>
          <th>PROCESSO</th>
          <th style="width:100px;text-align:center">SITUAÇÃO</th>
          <th style="width:90px">ÚLTIMA OC.</th>
          <th>OCORRÊNCIAS NO PERÍODO / ÚLTIMA</th>
        </tr></thead>
        <tbody>${rows.join('')}</tbody>
      </table>
      <p style="font-size:10px;color:#888;margin-top:12px">* Relatório de uso interno — Alves &amp; Correa Advogados</p>
    </div>`;
  };

  // ── Funções de impressão ──
  const imprimirRel1 = () => {
    const [ano,mes]=relMes.split("-"); const nm=new Date(parseInt(ano),parseInt(mes)-1,1).toLocaleDateString("pt-BR",{month:"long",year:"numeric"});
    const grupos = clientesComOc();
    if(grupos.length===0) return alert("Nenhum cliente com movimentação no período.");
    // Gera UM arquivo por cliente (abre cada um numa janela)
    grupos.forEach((g,i) => {
      const cli = clientes.find(c=>c.id===g.clienteId);
      setTimeout(()=>relWin(`Rel. ${cli?.nome||g.clienteId} - ${nm}`, gerarHtmlRel1(g.clienteId,g.processos,nm)), i*400);
    });
  };
  const imprimirRel2 = () => {
    const [ano,mes]=relMes.split("-"); const nm=new Date(parseInt(ano),parseInt(mes)-1,1).toLocaleDateString("pt-BR",{month:"long",year:"numeric"});
    const lista = semMovimento();
    if(lista.length===0) return alert("Nenhum processo sem movimentação no período.");
    lista.forEach((p,i) => {
      const cli = clientes.find(c=>c.id===p.clienteId);
      setTimeout(()=>relWin(`Rel. ${cli?.nome||p.clienteId} - ${p.numeroProcesso} - ${nm}`, gerarHtmlRel2(p,nm)), i*400);
    });
  };
  const imprimirRelGeral = () => {
    const [ano,mes]=relMes.split("-"); const nm=new Date(parseInt(ano),parseInt(mes)-1,1).toLocaleDateString("pt-BR",{month:"long",year:"numeric"});
    if(processos.length===0) return alert("Nenhum processo cadastrado.");
    relWin(`Relatório Geral - ${nm}`, gerarHtmlRelGeral(nm));
  };
  return (
    <div className="fade-in">
      <PageHeader title="Processos" subtitle={filtrados.length+" processo(s)"}
        action={<div style={{display:"flex",gap:8,alignItems:"center"}}>
          <Btn variant="secondary" onClick={()=>setRelOpen(true)}>📋 Relatórios</Btn>
          <Btn variant="secondary" onClick={sincronizarLegalMail} disabled={lmSincronizando}
            title="Gera relatório de reconciliação entre processos 'Em andamento' e o Legal Mail (não envia nada automaticamente — só roda nesta máquina)">
            {lmSincronizando?"⏳ Sincronizando...":"⚖️ Sincronizar Legal Mail"}
          </Btn>
          <Btn onClick={()=>{setEditItem({...EMPTY});setModalOpen(true);}}>+ Novo Processo</Btn>
        </div>} />
      {lmResultado && (
        <Card style={{marginBottom:20,borderLeft:`4px solid ${lmResultado.ok?theme.green:theme.red}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:10}}>
            <div style={{fontWeight:700,fontSize:14}}>{lmResultado.ok?"✅ Reconciliação Legal Mail":"❌ Falha na sincronização"}</div>
            <button onClick={()=>setLmResultado(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,color:theme.gray400}}>✕</button>
          </div>
          {lmResultado.erro && <div style={{color:theme.red,fontSize:13,marginBottom:10}}>{lmResultado.erro}</div>}
          {lmResultado.resumo && (
            <div style={{display:"flex",gap:20,flexWrap:"wrap",fontSize:13,marginBottom:10}}>
              <div>Em andamento + Judicial: <b>{lmResultado.resumo.em_andamento_judicial_app}</b></div>
              <div style={{color:theme.gray400}}>Fora do escopo (não Judicial): <b>{lmResultado.resumo.em_andamento_fora_do_escopo}</b></div>
              <div>Já no Legal Mail: <b>{lmResultado.resumo.ja_presentes_legalmail}</b></div>
              <div style={{color:theme.amber}}>A adicionar (prontos): <b>{lmResultado.resumo.a_adicionar_prontos}</b></div>
              <div style={{color:theme.amber}}>Número inválido: <b>{lmResultado.resumo.invalidos_numero_cnj?.length||0}</b></div>
              <div style={{color:theme.red}}>A remover (manual): <b>{lmResultado.resumo.a_remover_manual_legalmail?.length||0}</b></div>
            </div>
          )}
          {lmResultado.saida && (
            <details>
              <summary style={{cursor:"pointer",fontSize:12,color:theme.gray400}}>Ver saída completa do script</summary>
              <pre style={{fontSize:11,background:theme.gray100,padding:10,borderRadius:6,overflowX:"auto",maxHeight:400,overflowY:"auto"}}>{lmResultado.saida}</pre>
            </details>
          )}
        </Card>
      )}
      {(()=>{
        const totalProc = processos.length;
        const vinculados = processos.filter(p=>p.contratoId).length;
        const naoVinculados = totalProc - vinculados;
        const CORES_STATUS_P = { "Em andamento":theme.green, "Suspenso":theme.amber, "Arquivado":theme.gray400, "Encerrado":theme.gray400, "Aguardando":theme.amber };
        const cards = [
          { label:"Total de Processos", val: totalProc, cor: theme.gray400 },
          { label:"Vinculados a Contrato", val: vinculados, cor: theme.green },
          { label:"Não Vinculados", val: naoVinculados, cor: naoVinculados>0?theme.amber:theme.green },
          ...STATUS_P.map(s => ({ label: s, val: processos.filter(p=>p.status===s).length, cor: CORES_STATUS_P[s]||theme.gray400 })),
        ];
        return (
          <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
            {cards.map(c=>(
              <div key={c.label} style={{background:theme.white,borderRadius:8,padding:"10px 18px",minWidth:150,borderTop:`3px solid ${c.cor}`,boxShadow:"0 1px 3px rgba(0,0,0,.06)"}}>
                <div style={{fontSize:10,color:theme.gray400,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>{c.label}</div>
                <div style={{fontSize:22,fontWeight:700,color:theme.gray900}}>{c.val}</div>
              </div>
            ))}
          </div>
        );
      })()}
      <Card style={{padding:"14px 20px",marginBottom:20,display:"flex",gap:12,flexWrap:"wrap"}}>
        <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="🔍 Buscar por nome, CPF/CNPJ, OAB do responsável ou número do processo..."
          style={{flex:1,minWidth:200,padding:"8px 12px",border:"1px solid "+theme.gray200,borderRadius:7,fontSize:13,outline:"none"}} />
        <select value={fArea} onChange={e=>setFArea(e.target.value)} style={{padding:"8px 12px",border:"1px solid "+theme.gray200,borderRadius:7,fontSize:13,outline:"none"}}>
          <option value="">Todas as areas</option>{areas.map(a=><option key={a}>{a}</option>)}</select>
        <select value={fStatus} onChange={e=>setFStatus(e.target.value)} style={{padding:"8px 12px",border:"1px solid "+theme.gray200,borderRadius:7,fontSize:13,outline:"none"}}>
          <option value="">Todos os status</option>{STATUS_P.map(s=><option key={s}>{s}</option>)}</select>
        <select value={fTipo} onChange={e=>setFTipo(e.target.value)} style={{padding:"8px 12px",border:"1px solid "+theme.gray200,borderRadius:7,fontSize:13,outline:"none"}}>
          <option value="">Todos os tipos</option>{TIPOS_PROC.map(t=><option key={t}>{t}</option>)}</select>
      </Card>
      <Card>{filtrados.length===0?<EmptyState icon="x" title="Nenhum processo" subtitle="Cadastre processos ou ajuste os filtros." />:(
        <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{borderBottom:"2px solid "+theme.gray100}}>
            {["Nr Processo","Cliente","Tipo / Area","TJ/UF","Responsavel","Status","Ultima Ocorrencia","Acoes"].map(h=>
              <th key={h} style={{padding:"12px 14px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:1,textTransform:"uppercase",fontWeight:600}}>{h}</th>)}
          </tr></thead>
          <tbody>{filtrados.map((proc,i)=>{
            // Última ocorrência da Base Operacional (via contratos vinculados)
            const ctsProc = contratos.filter(ct=>(ct.processosIds||[]).includes(proc.id));
            const ocsProc = baseOperacional
              .filter(r=>ctsProc.some(ct=>ct.id===r.contratoId))
              .flatMap(r=>(r.ocorrencias||[]))
              .sort((a,b)=>((b.data||"")>(a.data||""))?1:-1);
            const ult = ocsProc[0]||null;
            return(<tr key={proc.id} style={{borderBottom:"1px solid "+theme.gray100,background:i%2===0?theme.white:theme.gray50}}>
              <td style={{padding:"12px 14px"}}><div style={{fontSize:13,fontWeight:600,color:theme.blue,fontFamily:"monospace"}}>{proc.numeroProcesso}</div><div style={{fontSize:10,color:theme.gray400,marginTop:2}}>{proc.dataDistribuicao}</div></td>
              <td style={{padding:"12px 14px",fontSize:13,color:theme.gray700}}>{cliN(proc.clienteId)}</td>
              <td style={{padding:"12px 14px"}}><Badge color="blue">{proc.tipoProcesso}</Badge><div style={{fontSize:11,color:theme.gray500,marginTop:4}}>{proc.area}</div></td>
              <td style={{padding:"12px 14px",fontSize:13,fontWeight:600,color:theme.gray700}}>{proc.tjUf}</td>
              <td style={{padding:"12px 14px",fontSize:13,color:theme.gray600}}>{eqN(proc.responsavel)}</td>
              <td style={{padding:"12px 14px"}}><Badge color={proc.status==="Em andamento"?"blue":proc.status==="Encerrado"?"green":proc.status==="Suspenso"?"amber":"gray"}>{proc.status}</Badge></td>
              <td style={{padding:"12px 14px",maxWidth:220}}>{ult?
                <div style={{background:theme.gray50,borderRadius:6,padding:"6px 10px",borderLeft:"3px solid "+theme.gold}}>
                  <div style={{fontSize:10,color:theme.gray400,marginBottom:2}}>{ult.data}</div>
                  {(ult.tipoNome||ult.tipo)&&<div style={{fontSize:11,fontWeight:600,color:theme.amber,marginBottom:2}}>{ult.tipoNome||ult.tipo}</div>}
                  <div style={{fontSize:11,color:theme.gray600,lineHeight:1.5,display:"-webkit-box",WebkitLineClamp:3,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
                    {ult.obs||ult.descricao||"—"}
                  </div>
                </div>
                :<span style={{fontSize:12,color:theme.gray300}}>Sem ocorrências</span>}
              </td>
              <td style={{padding:"12px 14px"}}><div style={{display:"flex",gap:6}}><Btn size="sm" variant="secondary" onClick={()=>setViewItem(processos.find(p=>p.id===proc.id))}>Ver</Btn>
                    {(()=>{
                      // Fonte de verdade: proc.contratoId (campo dedicado) — não recalcular
                      // via contratos[].processosIds, que pode ficar desatualizado/inconsistente
                      // (ex.: vínculo feito direto no processo sem espelhar no contrato).
                      const ct = proc.contratoId ? contratos.find(c=>c.id===proc.contratoId) : null;
                      return ct?(
                        <>
                          <Btn size="sm" disabled style={{background:"#f0fdf4",color:theme.green,border:"1px solid #bbf7d0",cursor:"default"}}>
                            ✅ Vinculado
                          </Btn>
                          <Btn size="sm" onClick={()=>{
                            setData(prev=>({...prev,_navBase:{contratoId:ct.id,abrirOcorrencia:true}}));
                            navegarPara&&navegarPara("base");
                          }} style={{background:"#eff6ff",color:theme.blue,border:"1px solid #bfdbfe"}}>
                            📋 Ocorrência
                          </Btn>
                        </>
                      ):(
                        <Btn size="sm" onClick={()=>{setEditItem({...proc});setModalOpen(true);}}
                          style={{background:"#fffbec",color:"#92400e",border:"1px solid #fde68a",fontSize:10}}>
                          ⚠️ Vincular
                        </Btn>
                      );
                    })()}<Btn size="sm" variant="secondary" onClick={()=>{setEditItem({...proc});setModalOpen(true);}}>Editar</Btn><Btn size="sm" variant="danger" onClick={()=>remove(proc.id)}>x</Btn></div></td>
            </tr>);
          })}</tbody>
        </table></div>
      )}</Card>
      {viewItem&&<Modal open={!!viewItem} onClose={()=>setViewItem(null)} title={"Processo "+viewItem.numeroProcesso} width={750}>
        {/* Instrução para lançar ocorrência */}
        {(()=>{
          const ct=contratos.find(ct=>(ct.processosIds||[]).includes(viewItem.id));
          return (
            <div style={{padding:"10px 14px",marginBottom:12,borderRadius:8,
              background:ct?"#eff6ff":"#fffbeb",
              border:`1px solid ${ct?"#bfdbfe":"#fde68a"}`}}>
              <p style={{fontSize:12,fontWeight:700,color:ct?theme.blue:"#92400e",marginBottom:ct?4:0}}>
                {ct?"📋 Para lançar ocorrência neste processo:":"⚠️ Processo não vinculado a um contrato"}
              </p>
              {ct
                ? <p style={{fontSize:11,color:theme.gray600,margin:0}}>
                    Acesse <b>Base Operacional → Execução Operacional</b> e selecione o contrato <b>{ct.numContrato}</b> → botão <b>"+ Ocorrência"</b>
                  </p>
                : <p style={{fontSize:11,color:"#92400e",margin:0}}>
                    Vincule este processo em <b>Base Operacional → Contratos → editar contrato → Processos Vinculados</b>
                  </p>
              }
            </div>
          );
        })()}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px 20px",marginBottom:20,padding:"14px 0",borderBottom:"1px solid "+theme.gray100}}>
          <div><span style={{fontSize:11,color:theme.gray400,textTransform:"uppercase"}}>Cliente</span><div style={{fontSize:13,fontWeight:600,marginTop:2}}>{cliN(viewItem.clienteId)}</div></div>
          <div><span style={{fontSize:11,color:theme.gray400,textTransform:"uppercase"}}>CPF/CNPJ do Cliente</span><div style={{fontSize:13,fontWeight:600,marginTop:2}}>{clientes.find(c=>c.id===viewItem.clienteId)?.cpfCnpj||"—"}</div></div>
          <div><span style={{fontSize:11,color:theme.gray400,textTransform:"uppercase"}}>TJ/UF</span><div style={{fontSize:13,fontWeight:600,marginTop:2}}>{viewItem.tjUf}</div></div>
          <div><span style={{fontSize:11,color:theme.gray400,textTransform:"uppercase"}}>Tipo</span><div style={{marginTop:2}}><Badge color="blue">{viewItem.tipoProcesso}</Badge></div></div>
          <div><span style={{fontSize:11,color:theme.gray400,textTransform:"uppercase"}}>Area</span><div style={{fontSize:13,marginTop:2}}>{viewItem.area}</div></div>
          <div><span style={{fontSize:11,color:theme.gray400,textTransform:"uppercase"}}>Responsavel</span><div style={{fontSize:13,marginTop:2}}>{eqN(viewItem.responsavel)}</div></div>
          <div><span style={{fontSize:11,color:theme.gray400,textTransform:"uppercase"}}>OAB do Responsavel</span><div style={{fontSize:13,marginTop:2}}>{equipe.find(e=>e.id===viewItem.responsavel)?.oab||"—"}</div></div>
          <div><span style={{fontSize:11,color:theme.gray400,textTransform:"uppercase"}}>Status</span><div style={{marginTop:2}}><Badge color={viewItem.status==="Em andamento"?"blue":"green"}>{viewItem.status}</Badge></div></div>
        </div>
        {/* ── Ocorrências espelhadas da Base Operacional ── */}
        {(()=>{
          // Buscar contratos que vinculam este processo
          const ctsVinc = contratos.filter(ct=>(ct.processosIds||[]).includes(viewItem.id));
          // Buscar registros da base desses contratos
          const regsVinc = baseOperacional.filter(r=>ctsVinc.some(ct=>ct.id===r.contratoId));
          // Coletar todas as ocorrências, ordenadas por data desc
          const todasOcs = regsVinc.flatMap(r=>
            (r.ocorrencias||[]).map(o=>({...o,_numContrato:r.numContrato||""}))
          ).sort((a,b)=>((b.data||"")>(a.data||""))?1:-1);
          const fmtD = d=>d?new Date(d+"T12:00:00").toLocaleDateString("pt-BR"):"—";
          return (
            <div style={{marginTop:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <p style={{fontSize:12,fontWeight:700,color:"#1d4ed8"}}>
                  📋 Ocorrências na Base Operacional ({todasOcs.length})
                </p>
                {ctsVinc.length===0&&(
                  <span style={{fontSize:11,color:"#92400e",background:"#fef3c7",padding:"3px 10px",borderRadius:10}}>
                    ⚠️ Processo não vinculado a nenhum contrato
                  </span>
                )}
              </div>
              {ctsVinc.length===0
                ? <div style={{padding:"14px",background:"#fffbeb",borderRadius:8,border:"1px solid #fde68a",fontSize:12,color:"#92400e"}}>
                    Para visualizar ocorrências aqui, vincule este processo a um contrato na Base Operacional → formulário do contrato → Processos Vinculados.
                  </div>
                : todasOcs.length===0
                  ? <div style={{padding:"14px",background:"#f0fdf4",borderRadius:8,border:"1px solid #bbf7d0",fontSize:12,color:"#166534"}}>
                      Nenhuma ocorrência registrada nos contratos vinculados.
                    </div>
                  : <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:320,overflowY:"auto"}}>
                      {todasOcs.map((oc,i)=>(
                        <div key={oc.id||i} style={{padding:"10px 12px",borderRadius:8,
                          background:oc.confidencial?"#fffbeb":"#f8fafc",
                          border:`1px solid ${oc.confidencial?"#fde68a":"#e2e8f0"}`,
                          borderLeft:`3px solid ${oc.confidencial?"#d97706":"#3b82f6"}`}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                            <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
                              <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:10,
                                background:oc.confidencial?"#fef3c7":"#dbeafe",
                                color:oc.confidencial?"#92400e":"#1d4ed8"}}>
                                {oc.confidencial?"🔒 Confidencial":"📋 Visível"}
                              </span>
                              {(oc.tipoNome||oc.tipo)&&(
                                <span style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:10,background:"#dcfce7",color:"#166534"}}>
                                  {oc.tipoNome||oc.tipo}
                                </span>
                              )}
                              {oc._numContrato&&(
                                <span style={{fontSize:10,color:"#6b7280",padding:"2px 6px",borderRadius:8,background:"#f3f4f6"}}>
                                  {oc._numContrato}
                                </span>
                              )}
                            </div>
                            <span style={{fontSize:10,color:"#9ca3af",whiteSpace:"nowrap"}}>{fmtD(oc.data)}</span>
                          </div>
                          <p style={{fontSize:12,color:"#374151",lineHeight:1.6,margin:"2px 0"}}>{oc.obs||oc.descricao||"—"}</p>
                          {oc.responsavel&&equipe.find(e=>e.id===oc.responsavel)&&(
                            <p style={{fontSize:10,color:"#9ca3af",marginTop:3}}>
                              👤 {equipe.find(e=>e.id===oc.responsavel)?.nome}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
              }
            </div>
          );
        })()}
      </Modal>}

      {editItem&&<Modal open={modalOpen} onClose={()=>setModalOpen(false)} title={editItem.id?"Editar Processo":"Novo Processo"} width={680}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
          <div style={{gridColumn:"1/-1"}}><FormField label="Cliente" required><Select value={editItem.clienteId} onChange={v=>setEditItem({...editItem,clienteId:v})} options={clientes.map(c=>({value:c.id,label:c.nome}))} placeholder="Selecione o cliente..." /></FormField></div>
          {editItem.clienteId && <div style={{gridColumn:"1/-1",marginTop:-8,marginBottom:4,fontSize:11,color:theme.gray400}}>CPF/CNPJ: <b style={{color:theme.gray600}}>{clientes.find(c=>c.id===editItem.clienteId)?.cpfCnpj||"não cadastrado"}</b></div>}
          <div style={{gridColumn:"1/-1"}}>
            <FormField label="Contrato Vinculado" hint="Pré-requisito para a consulta automática de andamentos (DataJud)">
              <Select value={editItem.contratoId} onChange={v=>setEditItem({...editItem,contratoId:v})}
                options={contratos.filter(c=>!editItem.clienteId||c.clienteId===editItem.clienteId).map(c=>({value:c.id,label:c.numContrato||c.id}))}
                placeholder="Nenhum..." />
            </FormField>
          </div>
          <div style={{gridColumn:"1/-1"}}><FormField label="Numero do Processo" required hint="Ex: 1001234-56.2024.8.26.0100"><Input value={editItem.numeroProcesso} onChange={v=>setEditItem({...editItem,numeroProcesso:v})} placeholder="0000000-00.0000.0.00.0000" /></FormField></div>
          <FormField label="Tipo de Processo"><Select value={editItem.tipoProcesso} onChange={v=>setEditItem({...editItem,tipoProcesso:v})} options={TIPOS_PROC} /></FormField>
          <FormField label="Area Juridica"><Select value={editItem.area} onChange={v=>setEditItem({...editItem,area:v})} options={areas} placeholder="Selecione..." /></FormField>
          <FormField label="TJ / Tribunal"><Select value={editItem.tjUf} onChange={v=>setEditItem({...editItem,tjUf:v})} options={TJS} placeholder="Selecione..." /></FormField>
          <FormField label="Responsavel"><Select value={editItem.responsavel} onChange={v=>setEditItem({...editItem,responsavel:v})} options={equipe.filter(e=>e.ativo!==false).map(e=>({value:e.id,label:e.nome}))} placeholder="Selecione..." /></FormField>
          {editItem.responsavel && <div style={{gridColumn:"1/-1",marginTop:-8,marginBottom:4,fontSize:11,color:theme.gray400}}>OAB: <b style={{color:theme.gray600}}>{equipe.find(e=>e.id===editItem.responsavel)?.oab||"não cadastrada"}</b></div>}
          <FormField label="Data Distribuicao"><Input type="date" value={editItem.dataDistribuicao} onChange={v=>setEditItem({...editItem,dataDistribuicao:v})} /></FormField>
          <FormField label="Status"><Select value={editItem.status} onChange={v=>setEditItem({...editItem,status:v})} options={STATUS_P} /></FormField>
        </div>

        {/* Contrato Vinculado — vinculação bidirecional */}
        <SecaoContratoVinculado
          contratos={contratos}
          processoId={editItem.id}
          clienteId={editItem.clienteId}
          clientes={clientes}
          setData={setData}
          theme={theme}
        />

        <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:"1px solid "+theme.gray100,marginTop:16}}>
          <Btn variant="secondary" onClick={()=>setModalOpen(false)}>Cancelar</Btn><Btn onClick={save}>Salvar Processo</Btn>
        </div>
      </Modal>}
      <Modal open={relOpen} onClose={()=>setRelOpen(false)} title="📊 Relatório de Processos" width={860}>
        {(()=>{
          // Agrupar processos por cliente
          const porCliente = {};
          filtrados.forEach(proc=>{
            if(!porCliente[proc.clienteId]) porCliente[proc.clienteId]=[];
            porCliente[proc.clienteId].push(proc);
          });
          const grupos = Object.entries(porCliente).sort(([a],[b])=>{
            const na=clientes.find(c=>c.id===a)?.nome||a;
            const nb=clientes.find(c=>c.id===b)?.nome||b;
            return na.localeCompare(nb,"pt-BR");
          });
          const fmtD = d=>d?new Date(d+"T12:00:00").toLocaleDateString("pt-BR"):"—";
          const total = filtrados.length;
          const emAndamento = filtrados.filter(p=>p.status==="Em andamento").length;
          const encerrados  = filtrados.filter(p=>p.status==="Encerrado").length;

          const gerarPDF = () => {
            const rows = grupos.map(([cliId,procs])=>{
              const cli=clientes.find(c=>c.id===cliId);
              const procRows = procs.map((p,i)=>{
                const ocsProc = baseOperacional
                  .filter(r=>contratos.filter(ct=>(ct.processosIds||[]).includes(p.id)).some(ct=>ct.id===r.contratoId))
                  .flatMap(r=>(r.ocorrencias||[]).filter(o=>o.confidencial!==true))
                  .sort((a,b)=>((b.data||"")>(a.data||""))?1:-1);
                const ult = ocsProc[0];
                return `<tr style="background:${i%2===0?"#fff":"#f9f7f2"}">
                  <td style="padding:7px 10px;font-size:11px;font-family:monospace;color:#1d4ed8">${p.numeroProcesso||"—"}</td>
                  <td style="padding:7px 10px;font-size:11px">${p.tipoProcesso}</td>
                  <td style="padding:7px 10px;font-size:11px">${p.area||"—"}</td>
                  <td style="padding:7px 10px;font-size:11px">TJ/${p.tjUf}</td>
                  <td style="padding:7px 10px;font-size:11px">${eqN(p.responsavel)}</td>
                  <td style="padding:7px 10px;font-size:11px">${p.status}</td>
                  <td style="padding:7px 10px;font-size:11px">${ult?fmtD(ult.data)+" — "+(ult.tipoNome||ult.tipo||"")+" — "+(ult.obs||ult.descricao||"").slice(0,60):"Sem ocorrências"}</td>
                </tr>`;
              }).join("");
              return `<tr><td colspan="7" style="padding:10px;background:#1a1610;color:#c9a84c;font-size:12px;font-weight:700">${cli?.nome||cliId} — ${procs.length} processo(s)</td></tr>${procRows}`;
            }).join("");
            const html=`<html><head><title>Relatório de Processos</title><style>
              body{font-family:Arial,sans-serif;padding:24px;color:#2A2420;font-size:12px}
              h1{font-size:14px;font-weight:bold;text-align:center;text-transform:uppercase;border-bottom:2px solid #C9A84C;padding-bottom:8px;margin-bottom:16px}
              table{width:100%;border-collapse:collapse;margin-bottom:16px}
              th{background:#1a1610;color:#c9a84c;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.5px}
              .kpi{display:inline-block;padding:8px 16px;margin:0 8px 16px 0;border:1px solid #ddd;border-radius:6px;text-align:center}
              .rf{font-size:10px;color:#999;margin-top:24px;border-top:1px solid #eee;padding-top:8px;text-align:center}
              @media print{@page{margin:15mm}}
            </style></head><body>
            <h1>Relatório de Processos — Alves & Correa Advogados</h1>
            <div style="margin-bottom:16px">
              <span class="kpi"><b>${total}</b><br><small>Total</small></span>
              <span class="kpi"><b>${emAndamento}</b><br><small>Em Andamento</small></span>
              <span class="kpi"><b>${encerrados}</b><br><small>Encerrados</small></span>
              <span class="kpi"><b>${grupos.length}</b><br><small>Clientes</small></span>
            </div>
            <table><thead><tr>
              <th>Nº Processo</th><th>Tipo</th><th>Área</th><th>TJ/UF</th><th>Responsável</th><th>Status</th><th>Última Ocorrência</th>
            </tr></thead><tbody>${rows}</tbody></table>
            <div class="rf">Alves & Correa Advogados · Gerado em ${new Date().toLocaleString("pt-BR")} · Confidencial</div>
            </body></html>`;
            const w=window.open("","_blank"); w.document.write(html); w.document.close(); w.print();
          };

          return (
            <div>
              {/* KPIs */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
                {[
                  {l:"Total",v:total,c:theme.gray800},
                  {l:"Em Andamento",v:emAndamento,c:theme.blue},
                  {l:"Encerrados",v:encerrados,c:theme.green},
                  {l:"Clientes",v:grupos.length,c:theme.amber},
                ].map(k=>(
                  <div key={k.l} style={{padding:"12px 16px",borderRadius:8,border:"1px solid "+theme.gray100,background:theme.white,textAlign:"center"}}>
                    <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>{k.l}</p>
                    <p style={{fontSize:22,fontWeight:700,color:k.c}}>{k.v}</p>
                  </div>
                ))}
              </div>
              {/* Botão PDF */}
              <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
                <Btn onClick={gerarPDF}>🖨️ Gerar PDF</Btn>
              </div>
              {/* Tabela agrupada por cliente */}
              <div style={{maxHeight:440,overflowY:"auto",border:"1px solid "+theme.gray100,borderRadius:8}}>
                {grupos.length===0
                  ? <p style={{textAlign:"center",padding:24,color:theme.gray400,fontSize:13}}>Nenhum processo encontrado.</p>
                  : grupos.map(([cliId,procs])=>{
                      const cli=clientes.find(c=>c.id===cliId);
                      return (
                        <div key={cliId} style={{marginBottom:0}}>
                          <div style={{padding:"10px 14px",background:theme.gray900,color:theme.gold,fontSize:12,fontWeight:700,position:"sticky",top:0}}>
                            👤 {cli?.nome||cliId} — {procs.length} processo(s)
                          </div>
                          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                            <thead><tr style={{background:theme.gray50}}>
                              {["Nº Processo","Tipo","Área","TJ/UF","Responsável","Status","Última Ocorrência"].map(h=>(
                                <th key={h} style={{padding:"7px 10px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
                              ))}
                            </tr></thead>
                            <tbody>
                              {procs.map((p,i)=>{
                                const ctsP=contratos.filter(ct=>(ct.processosIds||[]).includes(p.id));
                                const ocsP=baseOperacional
                                  .filter(r=>ctsP.some(ct=>ct.id===r.contratoId))
                                  .flatMap(r=>(r.ocorrencias||[]).filter(o=>o.confidencial!==true))
                                  .sort((a,b)=>((b.data||"")>(a.data||""))?1:-1);
                                const ult=ocsP[0]||null;
                                return (
                                  <tr key={p.id} style={{borderBottom:"1px solid "+theme.gray100,background:i%2===0?theme.white:theme.gray50}}>
                                    <td style={{padding:"8px 10px",fontSize:12,fontWeight:600,color:theme.blue,fontFamily:"monospace"}}>{p.numeroProcesso||"—"}</td>
                                    <td style={{padding:"8px 10px"}}><Badge color="blue">{p.tipoProcesso}</Badge></td>
                                    <td style={{padding:"8px 10px",fontSize:12,color:theme.gray600}}>{p.area||"—"}</td>
                                    <td style={{padding:"8px 10px",fontSize:12,fontWeight:600}}>{p.tjUf}</td>
                                    <td style={{padding:"8px 10px",fontSize:12,color:theme.gray600}}>{eqN(p.responsavel)}</td>
                                    <td style={{padding:"8px 10px"}}><Badge color={p.status==="Em andamento"?"blue":p.status==="Encerrado"?"green":"amber"}>{p.status}</Badge></td>
                                    <td style={{padding:"8px 10px",maxWidth:220}}>
                                      {ult
                                        ? <div style={{fontSize:11}}>
                                            <div style={{color:theme.gray400,marginBottom:2}}>{fmtD(ult.data)}</div>
                                            {(ult.tipoNome||ult.tipo)&&<span style={{fontSize:10,padding:"1px 6px",borderRadius:8,background:"#dbeafe",color:theme.blue,fontWeight:600,marginRight:4}}>{ult.tipoNome||ult.tipo}</span>}
                                            <span style={{color:theme.gray600}}>{(ult.obs||ult.descricao||"").slice(0,60)}{(ult.obs||ult.descricao||"").length>60?"…":""}</span>
                                          </div>
                                        : <span style={{fontSize:11,color:theme.gray300}}>Sem ocorrências</span>
                                      }
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })
                }
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}


// MODULO: AREAS JURIDICAS
function ModAreas({ data, setData }) {
  const areas = data.areas || AREAS_INICIAIS;
  const [nova, setNova] = useState("");
  const [editIdx, setEditIdx] = useState(null);
  const [editVal, setEditVal] = useState("");

  const addArea = () => {
    const t = nova.trim();
    if (!t) return;
    if (areas.includes(t)) return alert("Area ja existe.");
    setData(prev => ({ ...prev, areas: [...(prev.areas || AREAS_INICIAIS), t] }));
    setNova("");
  };

  const saveEdit = (idx) => {
    const t = editVal.trim();
    if (!t) return;
    const novas = areas.map((a, i) => i === idx ? t : a);
    setData(prev => ({ ...prev, areas: novas }));
    setEditIdx(null);
  };

  const removeArea = (idx) => {
    if (!window.confirm("Remover esta area?")) return;
    setData(prev => ({ ...prev, areas: (prev.areas || AREAS_INICIAIS).filter((_, i) => i !== idx) }));
  };

  return (
    <div className="fade-in">
      <PageHeader title="Areas Juridicas" subtitle={`${areas.length} area${areas.length !== 1 ? "s" : ""} cadastrada${areas.length !== 1 ? "s" : ""}`} />
      <Card style={{ maxWidth: 680 }}>
        <div style={{ display: "flex", gap: 10, marginBottom: 24, padding: "0 0 20px", borderBottom: `1px solid ${theme.gray100}` }}>
          <input
            value={nova}
            onChange={e => setNova(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addArea()}
            placeholder="Nova area juridica..."
            style={{ flex: 1, padding: "9px 14px", border: `1px solid ${theme.gray200}`, borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "Jost, sans-serif" }}
          />
          <button onClick={addArea} style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: theme.gray900, color: theme.gold, fontFamily: "Jost, sans-serif", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>+ Adicionar</button>
        </div>
        {areas.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: theme.gray400, fontSize: 14 }}>Nenhuma area cadastrada.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {areas.map((area, idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 8, background: idx % 2 === 0 ? theme.white : theme.gray50, border: `1px solid ${theme.gray100}` }}>
                <span style={{ width: 28, height: 28, borderRadius: "50%", background: theme.gray900, color: theme.gold, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{idx + 1}</span>
                {editIdx === idx ? (
                  <>
                    <input value={editVal} onChange={e => setEditVal(e.target.value)} onKeyDown={e => { if (e.key === "Enter") saveEdit(idx); if (e.key === "Escape") setEditIdx(null); }} autoFocus
                      style={{ flex: 1, padding: "6px 10px", border: `1px solid ${theme.gold}`, borderRadius: 6, fontSize: 13, outline: "none", fontFamily: "Jost, sans-serif" }} />
                    <button onClick={() => saveEdit(idx)} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: theme.green, color: theme.white, fontSize: 12, cursor: "pointer" }}>Salvar</button>
                    <button onClick={() => setEditIdx(null)} style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${theme.gray200}`, background: "transparent", fontSize: 12, cursor: "pointer" }}>x</button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: 14, color: theme.gray800 }}>{area}</span>
                    <button onClick={() => { setEditIdx(idx); setEditVal(area); }} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${theme.gray200}`, background: "transparent", fontSize: 12, color: theme.gray600, cursor: "pointer" }}>Editar</button>
                    <button onClick={() => removeArea(idx)} style={{ padding: "5px 10px", borderRadius: 6, border: `1px solid ${theme.red}44`, background: "transparent", fontSize: 12, color: theme.red, cursor: "pointer" }}>x</button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${theme.gray100}`, fontSize: 12, color: theme.gray400 }}>
          As areas cadastradas aqui aparecem automaticamente nos selects de Produtos, Contratos e demais modulos.
        </div>
      </Card>
    </div>
  );
}


// ============================================================
// UTILITÁRIOS DE IMPORTAÇÃO / EXPORTAÇÃO
// ============================================================

// Exportar JSON completo do array
const exportarJSON = (nomeArquivo, lista) => {
  const json = JSON.stringify(lista, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nomeArquivo + ".json"; a.click();
  URL.revokeObjectURL(url);
};

// Exportar Excel (.xlsx) genérico (mesmo usado em Relatórios)
const exportarXLSX = (nomeArquivo, cabecalho, linhas) => {
  const run = (XL) => {
    const ws = XL.utils.aoa_to_sheet([cabecalho, ...linhas]);
    ws["!cols"] = cabecalho.map((_, ci) => ({
      wch: Math.max(cabecalho[ci].length, ...linhas.map(r => String(r[ci]??"").length), 10)
    }));
    const wb = XL.utils.book_new();
    XL.utils.book_append_sheet(wb, ws, "Dados");
    XL.writeFile(wb, nomeArquivo + ".xlsx");
  };
  if (window.XLSX) { run(window.XLSX); return; }
  const s = document.createElement("script");
  s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
  s.integrity = "sha512-r22gChDnGvBylk90+2e/ycr3RVrDi8DIOkIGNhJlKfuyQM4tIRAI062MaV8sfjQKYVGjOBaZBOA87z+IhZE9DA==";
  s.crossOrigin = "anonymous";
  s.onload = () => run(window.XLSX);
  s.onerror = () => alert("Sem conexão com internet para carregar o exportador Excel.\nTente novamente conectado.");
  document.head.appendChild(s);
};
const exportarCSV = exportarXLSX;

// Importar JSON: lê arquivo e retorna array parsed
const importarJSON = (arquivo, onSuccess, onError) => {
  const reader = new FileReader();
  reader.onload = e => {
    try {
      const dados = JSON.parse(e.target.result);
      if (!Array.isArray(dados)) throw new Error("O arquivo deve conter um array JSON.");
      onSuccess(dados);
    } catch(err) { onError(err.message); }
  };
  reader.readAsText(arquivo);
};

// Importar CSV: lê arquivo, parse simples (sep = ; ou ,), retorna array de objetos
const importarCSV = (arquivo, onSuccess, onError) => {
  const reader = new FileReader();
  reader.onload = e => {
    try {
      let txt = e.target.result;
      // remover BOM
      if(txt.charCodeAt(0) === 0xFEFF) txt = txt.slice(1);
      const linhas = txt.split(/\r?\n/).filter(l => l.trim());
      if(linhas.length < 2) throw new Error("CSV deve ter cabeçalho e ao menos uma linha de dados.");
      // detectar separador
      const sep = linhas[0].includes(";") ? ";" : ",";
      const parseLinha = l => {
        const campos = [];
        let cur = "", inQ = false;
        for(let i=0; i<l.length; i++){
          if(l[i]==='"' && !inQ){ inQ=true; continue; }
          if(l[i]==='"' && inQ && l[i+1]==='"'){ cur+='"'; i++; continue; }
          if(l[i]==='"' && inQ){ inQ=false; continue; }
          if(l[i]===sep && !inQ){ campos.push(cur.trim()); cur=""; continue; }
          cur+=l[i];
        }
        campos.push(cur.trim());
        return campos;
      };
      const cabecalho = parseLinha(linhas[0]);
      const registros = linhas.slice(1).map(l => {
        const vals = parseLinha(l);
        const obj = {};
        cabecalho.forEach((h,i) => { obj[h.trim()] = vals[i] ?? ""; });
        return obj;
      });
      onSuccess(registros, cabecalho);
    } catch(err) { onError(err.message); }
  };
  reader.readAsText(arquivo, "UTF-8");
};

// Componente visual de Import/Export para usar em cada módulo
function PainelImportExport({ modulo, onExportCSV, onExportJSON, onImportJSON, onImportCSV, totalRegistros }) {
  const [aberto, setAberto] = useState(false);
  const [msg, setMsg] = useState(null); // {tipo: "ok"|"erro", texto}
  const refJSON = useRef(); const refCSV = useRef();

  const showMsg = (tipo, texto) => {
    setMsg({tipo, texto});
    setTimeout(()=>setMsg(null), 4000);
  };

  if(!aberto) return (
    <button onClick={()=>setAberto(true)} title="Importar / Exportar dados" style={{
      padding:"8px 14px", borderRadius:7, border:`1px solid ${theme.gray200}`,
      background:theme.white, color:theme.gray600, fontSize:12, fontWeight:600,
      cursor:"pointer", display:"flex", alignItems:"center", gap:6,
      transition:"all .15s"
    }}
      onMouseEnter={e=>e.currentTarget.style.borderColor=theme.gold}
      onMouseLeave={e=>e.currentTarget.style.borderColor=theme.gray200}
    >
      ⇅ Importar / Exportar
    </button>
  );

  return (
    <div style={{
      background:theme.white, border:`1px solid ${theme.gray200}`,
      borderRadius:10, padding:"16px 20px", minWidth:320,
      boxShadow:"0 4px 16px rgba(0,0,0,.08)"
    }}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <span style={{fontSize:13,fontWeight:700,color:theme.gray800}}>⇅ Importar / Exportar — {modulo}</span>
        <button onClick={()=>{setAberto(false);setMsg(null);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,color:theme.gray400,lineHeight:1}}>×</button>
      </div>

      {msg && (
        <div style={{
          padding:"8px 12px", borderRadius:6, marginBottom:12, fontSize:12,
          background: msg.tipo==="ok" ? "#D1FAE5" : "#FEE2E2",
          color: msg.tipo==="ok" ? "#065F46" : "#991B1B",
          border:`1px solid ${msg.tipo==="ok"?"#A7F3D0":"#FECACA"}`
        }}>
          {msg.tipo==="ok"?"✅":"❌"} {msg.texto}
        </div>
      )}

      {/* EXPORTAR */}
      <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8,marginBottom:8,fontWeight:600}}>
        Exportar ({totalRegistros} registros)
      </p>
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        <button onClick={()=>{onExportCSV(); showMsg("ok","Excel exportado! Verifique a pasta Downloads.");}} style={{
          flex:1, padding:"9px 0", borderRadius:7, border:`1px solid ${theme.green}44`,
          background:`${theme.green}11`, color:theme.green, fontSize:12, fontWeight:600, cursor:"pointer"
        }}>⬇ Excel (.xlsx)</button>
        <button onClick={()=>{onExportJSON(); showMsg("ok","JSON exportado com sucesso!");}} style={{
          flex:1, padding:"9px 0", borderRadius:7, border:`1px solid ${theme.blue}44`,
          background:`${theme.blue}11`, color:theme.blue, fontSize:12, fontWeight:600, cursor:"pointer"
        }}>⬇ JSON (Backup)</button>
      </div>

      {/* IMPORTAR */}
      <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8,marginBottom:8,fontWeight:600}}>
        Importar dados
      </p>
      <div style={{display:"flex",gap:8,marginBottom:8}}>
        <button onClick={()=>refCSV.current?.click()} style={{
          flex:1, padding:"9px 0", borderRadius:7, border:`1px solid ${theme.amber}44`,
          background:`${theme.amber}11`, color:theme.amber, fontSize:12, fontWeight:600, cursor:"pointer"
        }}>⬆ Importar CSV</button>
        <button onClick={()=>refJSON.current?.click()} style={{
          flex:1, padding:"9px 0", borderRadius:7, border:`1px solid #7C3AED44`,
          background:"#7C3AED11", color:"#7C3AED", fontSize:12, fontWeight:600, cursor:"pointer"
        }}>⬆ Importar JSON</button>
      </div>
      <p style={{fontSize:10,color:theme.gray400,lineHeight:1.5,marginBottom:0}}>
        ⚠️ A importação <b>adiciona</b> os registros aos existentes (não substitui). Certifique-se de que o arquivo é do mesmo módulo.
      </p>
      <input ref={refCSV} type="file" accept=".csv" style={{display:"none"}} onChange={e=>{
        const f=e.target.files[0]; e.target.value="";
        if(!f) return;
        onImportCSV(f,
          (registros)=>showMsg("ok", `${registros} registro(s) importado(s) do CSV.`),
          (err)=>showMsg("erro", "Erro no CSV: "+err)
        );
      }}/>
      <input ref={refJSON} type="file" accept=".json" style={{display:"none"}} onChange={e=>{
        const f=e.target.files[0]; e.target.value="";
        if(!f) return;
        onImportJSON(f,
          (registros)=>showMsg("ok", `${registros} registro(s) importado(s) do JSON.`),
          (err)=>showMsg("erro", "Erro no JSON: "+err)
        );
      }}/>
    </div>
  );
}

// ============================================================
// MODAL OCORRÊNCIAS — Reutilizável em Clientes, CRM, Processos, Base
// ============================================================
function ModalOcorrencias({ entidade, tipoEntidade, tiposOcorrencia, onSave, onSaveAgenda, equipe, onClose }) {
  // entidade = objeto do cliente/lead/processo
  // tipoEntidade = "cliente" | "lead" | "processo"
  const ocorrencias = entidade?.ocorrencias || entidade?.historico || [];
  const [novaOc, setNovaOc] = useState({ tipo: "", descricao: "", data: new Date().toISOString().slice(0,10), responsavel: "", confidencial: true, origemCaptura: "Manual", legalMailUri: "", prazoVencimento: "" });
  const [editIdx, setEditIdx] = useState(null);
  const [mostrarConf, setMostrarConf] = useState(true);

  const tiposOrd = [...(tiposOcorrencia || [])].sort((a,b) => a.nome.localeCompare(b.nome));

  const salvarOcorrencia = () => {
    if (!novaOc.tipo || !novaOc.descricao) return alert("Tipo e descrição são obrigatórios.");
    const tipoObj = tiposOcorrencia.find(t => t.id === novaOc.tipo);
    const _dx = new Date();
    const hoje = _dx.getFullYear()+'-'+String(_dx.getMonth()+1).padStart(2,'0')+'-'+String(_dx.getDate()).padStart(2,'0');
    const ocData = novaOc.data || hoje;
    const oc = {
      id: "OC" + Date.now(),
      tipo: novaOc.tipo,
      tipoNome: tipoObj?.nome || novaOc.tipo,
      confidencial: novaOc.confidencial !== false,
      descricao: novaOc.descricao,
      data: ocData,
      responsavel: novaOc.responsavel || "",
      criadoEm: new Date().toISOString(),
      origemCaptura: novaOc.origemCaptura || "Manual",
      legalMailUri: novaOc.legalMailUri || "",
      prazoVencimento: novaOc.prazoVencimento || ""
    };
    let lista;
    if (editIdx !== null) {
      lista = ocorrencias.map((o,i) => i === editIdx ? { ...o, ...oc, id: o.id } : o);
      setEditIdx(null);
    } else {
      lista = [...ocorrencias, oc];
    }
    onSave(lista);
    if (ocData >= hoje && onSaveAgenda) {
      onSaveAgenda({
        id: "EVT_OC_" + Date.now(), titulo: (tipoObj?.nome||novaOc.tipo)+" — "+(entidade?.nome||""),
        tipo:"compromisso", data:ocData, horaInicio:"09:00", horaFim:"10:00",
        responsavel:novaOc.responsavel||"", descricao:novaOc.descricao,
        status:"Confirmado", alerta:"1d", origemLeadId:tipoEntidade==="lead"?entidade?.id:"",
        origemEntidadeId:entidade?.id||"", origemTipo:tipoEntidade||"",
        local:"", linkReuniao:"", clienteId:"", processoId:"", origemContrato:""
      });
    }
    setNovaOc({ tipo: "", descricao: "", data: new Date().toISOString().slice(0,10), responsavel: "", confidencial: true, origemCaptura: "Manual", legalMailUri: "", prazoVencimento: "" });
  };

  const editarOc = (idx) => {
    const oc = ocorrencias[idx];
    setNovaOc({ tipo: oc.tipo, descricao: oc.descricao, data: oc.data, responsavel: oc.responsavel||"", confidencial: oc.confidencial!==false, origemCaptura: oc.origemCaptura||"Manual", legalMailUri: oc.legalMailUri||"", prazoVencimento: oc.prazoVencimento||"" });
    setEditIdx(idx);
  };

  const removerOc = (idx) => {
    if (!confirm("Remover esta ocorrência?")) return;
    onSave(ocorrencias.filter((_,i) => i !== idx));
  };

  const ocFiltradas = mostrarConf ? [...ocorrencias] : ocorrencias.filter(o => !o.confidencial);

  return (
    <Modal open={true} onClose={onClose} title={`Ocorrências — ${entidade?.nome || ""}`} width={720}>
      {/* Formulário nova ocorrência */}
      <div style={{ background: theme.gray50, borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <p style={{ fontSize: 11, color: theme.gray500, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12, fontWeight: 600 }}>
          {editIdx !== null ? "✏️ Editando ocorrência" : "➕ Nova Ocorrência"}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
          <FormField label="Tipo de Ocorrência" required>
            <select
              value={novaOc.tipo}
              onChange={e => setNovaOc(prev => ({ ...prev, tipo: e.target.value }))}
              style={{ width: "100%", padding: "9px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none", background: "#fff" }}
            >
              <option value="">Selecione...</option>
              {tiposOrd.map(t => (
                <option key={t.id} value={t.id}>
                  {t.interno ? "🔒 " : "📋 "}{t.nome}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Data">
            <input type="date" value={novaOc.data}
              onChange={e => setNovaOc(prev => ({ ...prev, data: e.target.value }))}
              style={{ width: "100%", padding: "9px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none", boxSizing: "border-box" }}/>
          </FormField>
          <FormField label="Responsável">
            <select value={novaOc.responsavel||""}
              onChange={e => setNovaOc(prev => ({ ...prev, responsavel: e.target.value }))}
              style={{ width: "100%", padding: "9px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none", background: "#fff" }}>
              <option value="">— Selecione —</option>
              {(equipe||[]).filter(e=>e.ativo!==false).map(e => (
                <option key={e.id} value={e.id}>{e.nome}</option>
              ))}
            </select>
          </FormField>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"10px 12px",borderRadius:7,background:novaOc.confidencial?"#fef3c7":"#f0fdf4",border:`1px solid ${novaOc.confidencial?"#fde68a":theme.green+"44"}` }}>
            <input type="checkbox" checked={!!novaOc.confidencial} onChange={e=>setNovaOc(prev=>({...prev,confidencial:e.target.checked}))} style={{width:15,height:15,accentColor:"#d97706"}}/>
            <span style={{fontSize:13,fontWeight:600,color:novaOc.confidencial?"#92400e":theme.green}}>
              {novaOc.confidencial?"🔒 Confidencial — visível apenas internamente":"📋 Não confidencial — poderá ir para o relatório do cliente"}
            </span>
          </label>
        </div>
        <FormField label="Descrição" required>
          <textarea
            value={novaOc.descricao}
            onChange={e => setNovaOc(prev => ({ ...prev, descricao: e.target.value }))}
            placeholder="Descreva a ocorrência..."
            rows={3}
            style={{ width: "100%", padding: "9px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box" }}
          />
        </FormField>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 10 }}>
          <FormField label="Origem da captura">
            <select value={novaOc.origemCaptura||"Manual"} onChange={e=>setNovaOc(prev=>({...prev,origemCaptura:e.target.value}))}
              style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",background:"#fff"}}>
              {["Manual","DataJud","Legal Mail","E-mail","WhatsApp"].map(o=><option key={o}>{o}</option>)}
            </select>
          </FormField>
          <FormField label="Prazo de vencimento">
            <input type="date" value={novaOc.prazoVencimento||""} onChange={e=>setNovaOc(prev=>({...prev,prazoVencimento:e.target.value}))}
              style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          </FormField>
          <FormField label="Link Legal Mail (opcional)">
            <input type="text" value={novaOc.legalMailUri||""} onChange={e=>setNovaOc(prev=>({...prev,legalMailUri:e.target.value}))}
              placeholder="inbox_uri"
              style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          </FormField>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          {editIdx !== null && (
            <Btn variant="secondary" onClick={() => { setEditIdx(null); setNovaOc({ tipo: "", descricao: "", data: new Date().toISOString().slice(0,10), responsavel: "", confidencial: true, origemCaptura: "Manual", legalMailUri: "", prazoVencimento: "" }); }}>
              Cancelar edição
            </Btn>
          )}
          <Btn onClick={salvarOcorrencia}>{editIdx !== null ? "Salvar Edição" : "Registrar"}</Btn>
        </div>
      </div>

      {/* Filtro interno/externo */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <p style={{ fontSize: 12, color: theme.gray500 }}>
          {ocorrencias.length} ocorrência(s) registrada(s)
          {!mostrarConf && <span style={{ color: theme.amber, marginLeft: 8 }}>• Confidenciais ocultas</span>}
        </p>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: theme.gray600 }}>
          <input type="checkbox" checked={mostrarConf} onChange={e => setMostrarConf(e.target.checked)} />
          Mostrar confidenciais
        </label>
      </div>

      {/* Lista de ocorrências */}
      {ocFiltradas.length === 0
        ? <p style={{ textAlign: "center", color: theme.gray400, fontSize: 13, padding: "16px 0" }}>Nenhuma ocorrência registrada.</p>
        : <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto" }}>
            {[...ocFiltradas].reverse().map((oc, idx) => {
              const realIdx = ocorrencias.length - 1 - idx;
              return (
                <div key={oc.id || idx} style={{
                  padding: "12px 14px", borderRadius: 8,
                  border: `1px solid ${oc.confidencial ? "#fde68a" : theme.gray100}`,
                  background: oc.confidencial ? "#fffbeb" : "#fff"
                }}>
                  {(() => {
                    const hoje3 = new Date(); hoje3.setHours(0,0,0,0);
                    const venc  = oc.prazoVencimento ? new Date(oc.prazoVencimento+"T12:00:00") : null;
                    const diffDu = venc ? Math.round((venc-hoje3)/(1000*60*60*24)) : null;
                    const urgBadge = diffDu!==null
                      ? diffDu<=3  ? {label:`🔴 URGENTE (${diffDu<=0?"vencido":diffDu+"du"})`   , bg:"#fee2e2", color:"#991b1b"}
                      : diffDu<=7  ? {label:`🟡 ATENÇÃO (${diffDu}du)`                          , bg:"#fef3c7", color:"#92400e"}
                      :              {label:`🟢 MONITORANDO (${diffDu}du)`                       , bg:"#f0fdf4", color:"#166534"}
                      : null;
                    return (
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap:"wrap" }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: oc.confidencial ? "#92400e" : theme.gray700 }}>
                              {oc.confidencial ? "🔒" : "📋"} {oc.tipoNome || oc.ocorrencia || oc.tipo}
                            </span>
                            {oc.confidencial && <Badge color="amber">Confidencial</Badge>}
                            {oc.origemCaptura&&oc.origemCaptura!=="Manual"&&(
                              <span style={{fontSize:10,padding:"2px 7px",borderRadius:10,fontWeight:700,
                                background:oc.origemCaptura==="Legal Mail"?"#dbeafe":oc.origemCaptura==="DataJud"?"#fef3c7":"#f0fdf4",
                                color:oc.origemCaptura==="Legal Mail"?"#1e40af":oc.origemCaptura==="DataJud"?"#92400e":"#166534"}}>
                                {oc.origemCaptura==="Legal Mail"?"📨 Legal Mail":oc.origemCaptura==="DataJud"?"⚖️ DataJud":"🔗 "+oc.origemCaptura}
                              </span>
                            )}
                            {urgBadge&&<span style={{fontSize:10,padding:"2px 7px",borderRadius:10,fontWeight:700,background:urgBadge.bg,color:urgBadge.color}}>{urgBadge.label}</span>}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 11, color: theme.gray400 }}>
                              {oc.data ? new Date(oc.data + "T12:00:00").toLocaleDateString("pt-BR") : ""}
                            </span>
                            {oc.legalMailUri&&<a href={oc.legalMailUri} target="_blank" rel="noreferrer" style={{fontSize:11,color:theme.blue}}>📨 Abrir</a>}
                            <Btn size="sm" variant="secondary" onClick={() => editarOc(realIdx)}>✏️</Btn>
                            <Btn size="sm" variant="danger" onClick={() => removerOc(realIdx)}>×</Btn>
                          </div>
                        </div>
                        <p style={{ fontSize: 13, color: theme.gray700, lineHeight: 1.5 }}>{oc.descricao}</p>
                        {oc.responsavel && (equipe||[]).find(e=>e.id===oc.responsavel) && (
                          <p style={{ fontSize: 11, color: theme.gray400, marginTop: 4 }}>👤 {(equipe||[]).find(e=>e.id===oc.responsavel)?.nome}</p>
                        )}
                      </>
                    );
                  })()}
                </div>
              );
            })}
          </div>
      }

      <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 16, borderTop: `1px solid ${theme.gray100}`, marginTop: 16 }}>
        <Btn variant="secondary" onClick={onClose}>Fechar</Btn>
      </div>
    </Modal>
  );
}

// ============================================================
// MODAL TIPOS DE OCORRÊNCIA — Gerenciar tipos globalmente
// ============================================================
function ModalTiposOcorrencia({ tiposOcorrencia, setData, onClose }) {
  const [editItem, setEditItem] = useState(null);
  const [novoNome, setNovoNome] = useState("");
  const [novoInterno, setNovoInterno] = useState(false);

  const salvar = () => {
    if (!novoNome.trim()) return alert("Nome obrigatório.");
    if (editItem) {
      setData(prev => ({ ...prev, tiposOcorrencia: prev.tiposOcorrencia.map(t => t.id === editItem.id ? { ...t, nome: novoNome, interno: novoInterno } : t) }));
    } else {
      const novo = { id: "TOC" + Date.now(), nome: novoNome.trim(), interno: novoInterno };
      setData(prev => ({ ...prev, tiposOcorrencia: [...(prev.tiposOcorrencia||[]), novo] }));
    }
    setEditItem(null); setNovoNome(""); setNovoInterno(false);
  };

  const editar = (t) => { setEditItem(t); setNovoNome(t.nome); setNovoInterno(t.interno); };
  const remover = (id) => {
    if (!confirm("Remover tipo de ocorrência?")) return;
    setData(prev => ({ ...prev, tiposOcorrencia: prev.tiposOcorrencia.filter(t => t.id !== id) }));
  };

  return (
    <Modal open={true} onClose={onClose} title="Tipos de Ocorrência" width={560}>
      <div style={{ background: theme.gray50, borderRadius: 8, padding: 14, marginBottom: 16 }}>
        <p style={{ fontSize: 11, color: theme.gray500, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, fontWeight: 600 }}>
          {editItem ? "Editando tipo" : "Novo tipo"}
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <Input value={novoNome} onChange={setNovoNome} placeholder="Nome do tipo de ocorrência" />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: theme.gray600, cursor: "pointer", whiteSpace: "nowrap", paddingBottom: 2 }}>
            <input type="checkbox" checked={novoInterno} onChange={e => setNovoInterno(e.target.checked)} />
            🔒 Uso interno
          </label>
          <Btn onClick={salvar}>{editItem ? "Salvar" : "Adicionar"}</Btn>
          {editItem && <Btn variant="secondary" onClick={() => { setEditItem(null); setNovoNome(""); setNovoInterno(false); }}>×</Btn>}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 400, overflowY: "auto" }}>
        {(tiposOcorrencia||[]).sort((a,b) => a.nome.localeCompare(b.nome)).map(t => (
          <div key={t.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 7, border: `1px solid ${t.interno ? "#fde68a" : theme.gray100}`, background: t.interno ? "#fffbeb" : "#fff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, color: theme.gray800 }}>{t.interno ? "🔒" : "📋"} {t.nome}</span>
              {t.interno && <Badge color="amber">Interno</Badge>}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <Btn size="sm" variant="secondary" onClick={() => editar(t)}>✏️</Btn>
              <Btn size="sm" variant="danger" onClick={() => remover(t.id)}>×</Btn>
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 16, borderTop: `1px solid ${theme.gray100}`, marginTop: 16 }}>
        <Btn variant="secondary" onClick={onClose}>Fechar</Btn>
      </div>
    </Modal>
  );
}


function ModClientes({ data, setData }) {
  const isViz = useIsVisualizador();
  const { tiposOcorrencia = [], clientes, produtos, equipe } = data;
  const [ocModal, setOcModal] = useState(null);
  const [tiposModal, setTiposModal] = useState(false);
  const [busca, setBusca] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroAtivo, setFiltroAtivo] = useState("Ativo");
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  // clienteAtual: sempre sincronizado com data.clientes
  const clienteAtual = viewItem ? (clientes.find(c => c.id === viewItem.id) || viewItem) : null;
  const [histModalOpen, setHistModalOpen] = useState(false);
  const [novoHist, setNovoHist] = useState({ data: new Date().toISOString().slice(0, 10), ocorrencia: "", descricao: "", numContrato: "", valor: "", condicoes: "", parcelas: "", exito: "", dataExito: "" });

  const filtrados = clientes.filter(c => {
    const matchBusca = c.nome.toLowerCase().includes(busca.toLowerCase()) || c.cpfCnpj?.includes(busca);
    const matchTipo = !filtroTipo || c.tipo === filtroTipo;
    const matchAtivo = !filtroAtivo || (filtroAtivo === "Ativo" ? c.ativo : !c.ativo);
    return matchBusca && matchTipo && matchAtivo;
  }).sort((a,b)=>(a.nome||"").localeCompare(b.nome||"","pt-BR"));

  const EMPTY_FORM = {
    id: "", nome: "", tipo: "PF", email: "", emailsAdicionais: [], whatsapp: "", whatsappDDI: "55",
    cidade: "", uf: "SP", endereco: "", cep: "", cpfCnpj: "",
    dataNascimento: "", linkGoogle: "",
    nacionalidade: "", estadoCivil: "", profissao: "", rg: "", ufRg: "",
    produtosContratados: [], mesAnoContrato: "",
    ativo: true, obs: "", historico: [],
    finalidadeTratamento: "Prestação de serviços jurídicos",
    consentimentoLGPD: { aceito: false, data: "" },
    contatosPJ: [
      { nome: "", whatsapp: "", whatsappDDI: "55", dataNascimento: "", email: "", emailsAdicionais: [] },
      { nome: "", whatsapp: "", whatsappDDI: "55", dataNascimento: "", email: "", emailsAdicionais: [] }
    ]
  };

  const openNew = () => { setEditItem({ ...EMPTY_FORM }); setModalOpen(true); };
  const openEdit = (c) => {
    const pjDefault = { nome: "", whatsapp: "", whatsappDDI: "55", dataNascimento: "", email: "", emailsAdicionais: [] };
    const contatosPJ = (c.contatosPJ || [pjDefault, pjDefault]).map(ct => ({
      ...pjDefault, ...ct
    }));
    // Garantir pelo menos 2 contatos
    while (contatosPJ.length < 2) contatosPJ.push({...pjDefault});
    setEditItem({ ...c, whatsappDDI: c.whatsappDDI || "55", contatosPJ });
    setModalOpen(true);
  };

  const save = () => {
    if (!editItem.nome) return alert("Nome é obrigatório.");
    if (!editItem.id && !editItem.consentimentoLGPD?.aceito) {
      return alert("É necessário registrar o consentimento do titular (LGPD) antes de cadastrar um novo cliente.");
    }
    const cpfCnpjLimpo = (editItem.cpfCnpj || "").replace(/\D/g, "");
    if (cpfCnpjLimpo) {
      const duplicado = clientes.find(c => c.id !== editItem.id && (c.cpfCnpj || "").replace(/\D/g, "") === cpfCnpjLimpo);
      if (duplicado) {
        const abrir = confirm(`Já existe um cliente cadastrado com este CPF/CNPJ: "${duplicado.nome}" (${duplicado.id}).\n\nClique OK para abrir o cadastro existente, ou Cancelar para revisar os dados informados.`);
        if (abrir) { openEdit(duplicado); }
        return;
      }
    }
    const id = editItem.id || "CLI" + String(Date.now()).slice(-6);
    const item = { ...editItem, id };
    setData(prev => { const lista = editItem.id ? prev.clientes.map(c => c.id === item.id ? item : c) : [...prev.clientes, item]; return {...prev, clientes: lista}; });
    setModalOpen(false);
  };

  const remove = (id) => {
    if (!confirm("Remover cliente?")) return;
    setData(prev => ({...prev, clientes: clientes.filter(c => c.id !== id)}));
  };

  const toggleAtivo = (id) => {
    setData(prev => ({ ...prev, clientes: prev.clientes.map(c => c.id === id ? { ...c, ativo: !c.ativo } : c) }));
  };

  const produtosNomes = (ids) => ids?.map(id => produtos.find(p => p.id === id)?.nome || id).join(", ") || "-";

  const addHistorico = () => {
    if (!novoHist.ocorrencia) return alert("Selecione o tipo de ocorrência.");
    const entrada = { ...novoHist, id: Date.now() };
    const clienteAtual = clientes.find(c => c.id === viewItem.id);
    const atualizado = { ...clienteAtual, historico: [...(clienteAtual.historico || []), entrada] };
    setData(prev => ({...prev, clientes: clientes.map(c => c.id === viewItem.id ? atualizado : c)}));
    setViewItem(atualizado);
    setNovoHist({ data: new Date().toISOString().slice(0, 10), ocorrencia: "", descricao: "", numContrato: "", valor: "", condicoes: "", parcelas: "", exito: "", dataExito: "" });
    setHistModalOpen(false);
  };

  const riscoBadge = (r) => {
    if (r === "Alto") return <Badge color="red">{r}</Badge>;
    if (r === "Médio") return <Badge color="amber">{r}</Badge>;
    return <Badge color="green">{r}</Badge>;
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="Clientes"
        subtitle={`${filtrados.length} cliente${filtrados.length !== 1 ? "s" : ""} encontrado${filtrados.length !== 1 ? "s" : ""}`}
        action={
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <Btn variant="secondary" onClick={() => setTiposModal(true)}>⚙️ Tipos</Btn>
            <PainelImportExport
              modulo="Clientes"
              totalRegistros={clientes.length}
              onExportCSV={()=>exportarCSV("clientes",
                ["ID","Nome","Tipo","CPF/CNPJ","Email","WhatsApp","Cidade","UF","CEP","Endereço","Ativo","Observações"],
                clientes.map(c=>[c.id,c.nome,c.tipo||"PF",c.cpfCnpj||"—",c.email||"—",c.whatsapp||"—",c.cidade||"—",c.uf||"—",c.cep||"—",c.endereco||"—",c.ativo?"Sim":"Não",c.obs||"—"])
              )}
              onExportJSON={()=>exportarJSON("clientes_backup", clientes)}
              onImportCSV={(arquivo, onOk, onErr)=>{
                importarCSV(arquivo, (registros)=>{
                  const novos = registros.map(r=>({
                    id: r.ID||r.id||("CLI"+String(Date.now()).slice(-6)+Math.random().toString(36).slice(-3)),
                    nome: r.Nome||r.nome||"Sem nome",
                    tipo: r.Tipo||r.tipo||"PF",
                    cpfCnpj: r["CPF/CNPJ"]||r.cpfCnpj||"",
                    email: r.Email||r.email||"",
                    whatsapp: r.WhatsApp||r.whatsapp||"",
                    cidade: r.Cidade||r.cidade||"",
                    uf: r.UF||r.uf||"SP",
                    cep: r.CEP||r.cep||"",
                    endereco: r["Endereço"]||r.endereco||"",
                    ativo: (r.Ativo||r.ativo||"Sim").toLowerCase()!=="não",
                    obs: r["Observações"]||r.obs||"",
                    historico:[], produtosContratados:[], contatosPJ:[{nome:"",whatsapp:""},{nome:"",whatsapp:""}]
                  }));
                  const ids = new Set(clientes.map(x=>x.id));
                  const inserir = novos.filter(n=>!ids.has(n.id));
                  setData(prev => ({...prev, clientes: [...clientes,...inserir]}));
                  onOk(inserir.length);
                }, onErr);
              }}
              onImportJSON={(arquivo, onOk, onErr)=>{
                importarJSON(arquivo, (lista)=>{
                  const ids = new Set(clientes.map(x=>x.id));
                  const inserir = lista.filter(n=>n.nome&&!ids.has(n.id));
                  setData(prev => ({...prev, clientes: [...clientes,...inserir]}));
                  onOk(inserir.length);
                }, onErr);
              }}
            />
            {!isViz && <Btn onClick={openNew} size="md">+ Novo Cliente</Btn>}
          </div>
        }
      />

      {/* Filtros */}
      <Card style={{ padding: "14px 20px", marginBottom: 20, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="Buscar por nome ou CPF/CNPJ..."
          style={{
            flex: 1, minWidth: 200, padding: "8px 12px",
            border: `1px solid ${theme.gray200}`, borderRadius: 7,
            fontSize: 13, outline: "none"
          }}
        />
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
          style={{ padding: "8px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none" }}>
          <option value="">Todos os tipos</option>
          <option value="PF">Pessoa Física</option>
          <option value="PJ">Pessoa Jurídica</option>
        </select>
        <select value={filtroAtivo} onChange={e => setFiltroAtivo(e.target.value)}
          style={{ padding: "8px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none" }}>
          <option value="">Todos</option>
          <option value="Ativo">Ativos</option>
          <option value="Inativo">Inativos</option>
        </select>
      </Card>

      {/* Tabela */}
      <Card>
        {filtrados.length === 0 ? (
          <EmptyState icon="👤" title="Nenhum cliente encontrado" subtitle="Ajuste os filtros ou cadastre um novo cliente." />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.gray100}` }}>
                  {["Nome / CPF·CNPJ", "Tipo", "WhatsApp", "Produtos", "Último Contrato", "Status", "Ações"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 10, color: theme.gray400, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtrados.map((c, i) => {
                  const contratos = (c.historico || []).filter(h => h.ocorrencia === "Fechamento de Contrato");
                  const ultimo = contratos[contratos.length - 1];
                  return (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${theme.gray100}`, background: i % 2 === 0 ? theme.white : theme.gray50 }}>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ fontWeight: 500, fontSize: 14, color: theme.gray900 }}>{c.nome}</div>
                      <div style={{ fontSize: 11, color: theme.gray400 }}>{c.cpfCnpj} {c.cidade && `· ${c.cidade}/${c.uf}`}</div>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <Badge color={c.tipo === "PJ" ? "blue" : "gray"}>{c.tipo}</Badge>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: theme.gray600 }}>{c.whatsapp ? `+${c.whatsappDDI||"55"} ${c.whatsapp}` : "-"}</td>
                    <td style={{ padding: "12px 16px", fontSize: 12, color: theme.gray500, maxWidth: 180 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {produtosNomes(c.produtosContratados)}
                      </div>
                      <div style={{ fontSize: 10, color: theme.gray400, marginTop: 2 }}>
                        {contratos.length} contrato{contratos.length !== 1 ? "s" : ""}
                      </div>
                    </td>
                                        <td style={{ padding: "12px 16px", minWidth: 140 }}>
                      {contratos.length === 0 ? (
                        <span style={{ fontSize: 12, color: theme.gray300 }}>Sem contrato</span>
                      ) : (() => {
                        const ult = contratos[contratos.length - 1];
                        const dias = ult.data ? Math.floor((new Date() - new Date(ult.data)) / 86400000) : null;
                        const cor = dias === null ? theme.gray400 : dias > 90 ? theme.red : dias > 30 ? theme.amber : theme.green;
                        return (
                          <div style={{ background: theme.gray50, borderRadius: 6, padding: "7px 10px", borderLeft: `3px solid ${cor}` }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: theme.gray800 }}>{ult.data || "-"}</div>
                            {dias !== null && (
                              <div style={{ fontSize: 11, marginTop: 2, color: cor, fontWeight: 500 }}>
                                {dias === 0 ? "Hoje" : `${dias} dia${dias !== 1 ? "s" : ""} atras`}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <Badge color={c.ativo ? "green" : "gray"}>{c.ativo ? "Ativo" : "Inativo"}</Badge>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Btn size="sm" variant="secondary" onClick={() => setViewItem(clientes.find(x => x.id === c.id))}>Ver</Btn>
                        <Btn size="sm" variant="secondary" onClick={() => setOcModal(c)}>📋 Ocorrências</Btn>
                        {!isViz && <Btn size="sm" variant="secondary" onClick={() => openEdit(c)}>Editar</Btn>}
                        {!isViz && <Btn size="sm" variant="danger" onClick={() => remove(c.id)}>×</Btn>}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal Formulário */}
      {editItem && (
        <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editItem.id ? "Editar Cliente" : "Novo Cliente"} width={700}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <FormField label="Classificação" required>
                <div style={{ display: "flex", gap: 12 }}>
                  {["PF", "PJ"].map(t => (
                    <label key={t} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
                      <input type="radio" checked={editItem.tipo === t} onChange={() => setEditItem({ ...editItem, tipo: t })} />
                      {t === "PF" ? "Pessoa Física" : "Pessoa Jurídica"}
                    </label>
                  ))}
                </div>
              </FormField>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <FormField label="Nome completo / Razão Social" required>
                <Input value={editItem.nome} onChange={v => setEditItem({ ...editItem, nome: v })} placeholder="Nome do cliente" required />
              </FormField>
            </div>

            <FormField label={editItem.tipo === "PJ" ? "CNPJ" : "CPF"}>
              <Input value={editItem.cpfCnpj} onChange={v => setEditItem({ ...editItem, cpfCnpj: v })} placeholder={editItem.tipo === "PJ" ? "00.000.000/0001-00" : "000.000.000-00"} />
            </FormField>

            <CampoEmailsMultiplos
              email={editItem.email}
              emailsAdicionais={editItem.emailsAdicionais}
              onChangeEmail={v => setEditItem({ ...editItem, email: v })}
              onChangeAdicionais={v => setEditItem({ ...editItem, emailsAdicionais: v })}
            />

            <CampoWhatsApp
              label="WhatsApp / Telefone"
              ddi={editItem.whatsappDDI}
              numero={editItem.whatsapp}
              onChangeDDI={v=>setEditItem({...editItem, whatsappDDI: v})}
              onChangeNumero={v=>setEditItem({...editItem, whatsapp: v})}
            />

            <FormField label="CEP">
              <Input value={editItem.cep} onChange={v => setEditItem({ ...editItem, cep: v })} placeholder="00000-000" />
            </FormField>

            <div style={{ gridColumn: "1 / -1" }}>
              <FormField label="Endereço">
                <Input value={editItem.endereco} onChange={v => setEditItem({ ...editItem, endereco: v })} placeholder="Rua, número, complemento" />
              </FormField>
            </div>

            <FormField label="Cidade">
              <Input value={editItem.cidade} onChange={v => setEditItem({ ...editItem, cidade: v })} placeholder="Cidade" />
            </FormField>

            <FormField label="UF">
              <Select value={editItem.uf} onChange={v => setEditItem({ ...editItem, uf: v })} options={UFS} />
            </FormField>

            {/* Contatos PJ */}
            {editItem.tipo === "PJ" && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ borderTop: `1px solid ${theme.gray100}`, margin: "8px 0 16px", paddingTop: 16 }}>
                  <p style={{ fontSize: 11, color: theme.gray500, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>Contatos da Empresa</p>
                  {[0, 1].map(i => (
                    <div key={i} style={{ marginBottom: 16, padding: "12px 14px", borderRadius: 8,
                      background: theme.gray50, border: `1px solid ${theme.gray200}` }}>
                      <p style={{ fontSize: 11, fontWeight: 700, color: theme.gray600, marginBottom: 10 }}>
                        Contato {i + 1}
                      </p>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                        <FormField label="Nome">
                          <Input
                            value={editItem.contatosPJ?.[i]?.nome || ""}
                            onChange={v => {
                              const pj = [...(editItem.contatosPJ || [{}, {}])];
                              pj[i] = { ...pj[i], nome: v };
                              setEditItem({ ...editItem, contatosPJ: pj });
                            }}
                            placeholder="Nome do contato"
                          />
                        </FormField>
                        <CampoWhatsApp
                          ddi={editItem.contatosPJ?.[i]?.whatsappDDI}
                          numero={editItem.contatosPJ?.[i]?.whatsapp || ""}
                          onChangeDDI={v => {
                            const pj = [...(editItem.contatosPJ || [{}, {}])];
                            pj[i] = { ...pj[i], whatsappDDI: v };
                            setEditItem({ ...editItem, contatosPJ: pj });
                          }}
                          onChangeNumero={v => {
                            const pj = [...(editItem.contatosPJ || [{}, {}])];
                            pj[i] = { ...pj[i], whatsapp: v };
                            setEditItem({ ...editItem, contatosPJ: pj });
                          }}
                        />
                        <FormField label="Data de Nascimento">
                          <Input type="date"
                            value={editItem.contatosPJ?.[i]?.dataNascimento || ""}
                            onChange={v => {
                              const pj = [...(editItem.contatosPJ || [{}, {}])];
                              pj[i] = { ...pj[i], dataNascimento: v };
                              setEditItem({ ...editItem, contatosPJ: pj });
                            }}
                          />
                        </FormField>
                        <CampoEmailsMultiplos
                          email={editItem.contatosPJ?.[i]?.email}
                          emailsAdicionais={editItem.contatosPJ?.[i]?.emailsAdicionais}
                          onChangeEmail={v => {
                            const pj = [...(editItem.contatosPJ || [{}, {}])];
                            pj[i] = { ...pj[i], email: v };
                            setEditItem({ ...editItem, contatosPJ: pj });
                          }}
                          onChangeAdicionais={v => {
                            const pj = [...(editItem.contatosPJ || [{}, {}])];
                            pj[i] = { ...pj[i], emailsAdicionais: v };
                            setEditItem({ ...editItem, contatosPJ: pj });
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <FormField label="Mês/Ano do Contrato">
              <input
                type="month" value={editItem.mesAnoContrato}
                onChange={e => setEditItem({ ...editItem, mesAnoContrato: e.target.value })}
                style={{ width: "100%", padding: "9px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none" }}
              />
            </FormField>

            <FormField label="Status">
              <Select
                value={editItem.ativo ? "Ativo" : "Inativo"}
                onChange={v => setEditItem({ ...editItem, ativo: v === "Ativo" })}
                options={["Ativo", "Inativo"]}
              />
            </FormField>

            <div style={{ gridColumn: "1 / -1" }}>
              <FormField label="Produtos Contratados">
                <ProdutoSelector
                  produtos={produtos}
                  value={editItem.produtosContratados||[]}
                  onChange={v=>setEditItem({...editItem,produtosContratados:v})}
                />
              </FormField>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <FormField label="Indicado por (Parceiro)">
                <select value={editItem.parceiraId||""}
                  onChange={e=>setEditItem({...editItem,parceiraId:e.target.value})}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",background:"#fff"}}>
                  <option value="">— Nenhum / Direto —</option>
                  {(data.parcerias||[]).filter(p=>p.status==="Ativo").map(p=>(
                    <option key={p.id} value={p.id}>{p.nome}{p.comissao?` (${p.comissao}%)`:""}</option>
                  ))}
                </select>
              </FormField>
            </div>

            <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
              <FormField label="Data de Nascimento / Fundação">
                <Input type="date" value={editItem.dataNascimento||""} onChange={v=>setEditItem({...editItem,dataNascimento:v})}/>
              </FormField>
              <FormField label="Link Google Meu Negócio">
                <Input value={editItem.linkGoogle||""} onChange={v=>setEditItem({...editItem,linkGoogle:v})} placeholder="https://g.page/..."/>
              </FormField>
            </div>

            {editItem.tipo==="PF" && (
              <div style={{gridColumn:"1/-1",background:"#f0f9ff",borderRadius:8,padding:"14px 16px",border:"1px solid #bfdbfe"}}>
                <p style={{fontSize:12,fontWeight:700,color:theme.blue,marginBottom:10}}>📋 Qualificação para Documentos (Contratos/Procurações)</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"0 16px"}}>
                  <FormField label="Nacionalidade">
                    <Input value={editItem.nacionalidade||""} onChange={v=>setEditItem({...editItem,nacionalidade:v})} placeholder="brasileiro(a)"/>
                  </FormField>
                  <FormField label="Estado Civil">
                    <Input value={editItem.estadoCivil||""} onChange={v=>setEditItem({...editItem,estadoCivil:v})} placeholder="casado(a)"/>
                  </FormField>
                  <FormField label="Profissão">
                    <Input value={editItem.profissao||""} onChange={v=>setEditItem({...editItem,profissao:v})} placeholder="empresário(a)"/>
                  </FormField>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:"0 16px",marginTop:10}}>
                  <FormField label="RG nº">
                    <Input value={editItem.rg||""} onChange={v=>setEditItem({...editItem,rg:v})} placeholder="00.000.000-0"/>
                  </FormField>
                  <FormField label="UF do RG">
                    <Input value={editItem.ufRg||""} onChange={v=>setEditItem({...editItem,ufRg:v})} placeholder="Preencher UF"/>
                  </FormField>
                </div>
              </div>
            )}

            <div style={{ gridColumn: "1 / -1" }}>
              <FormField label="📁 Pasta do Cliente (link nuvem)">
                <Input
                  value={editItem.pastaCliente||""}
                  onChange={v => setEditItem({ ...editItem, pastaCliente: v })}
                  placeholder="https://drive.google.com/... ou https://onedrive.live.com/..."
                />
              </FormField>
              {editItem.pastaCliente && (
                <a href={editItem.pastaCliente} target="_blank" rel="noreferrer"
                  style={{fontSize:11,color:"#0284c7",display:"inline-flex",
                    alignItems:"center",gap:4,marginTop:4,textDecoration:"none"}}>
                  🔗 Testar link ↗
                </a>
              )}
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <FormField label={<span>🔒 Dados de Acesso <span style={{fontSize:10,color:theme.gray400,fontWeight:400}}>— uso interno · não compartilhar</span></span>}>
                <Textarea value={editItem.dadosAcesso||""} onChange={v => setEditItem({ ...editItem, dadosAcesso: v })}
                  placeholder="Ex: e-CAC — CPF: 000.000.000-00 / Senha: ... | PGFN — login: ... | Certificado digital: ..."
                  rows={3}/>
              </FormField>
            </div>

            <div style={{ gridColumn: "1 / -1", padding: "12px 14px", borderRadius: 8, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
              <FormField label="🛡️ LGPD — Finalidade do Tratamento">
                <Select value={editItem.finalidadeTratamento} onChange={v => setEditItem({ ...editItem, finalidadeTratamento: v })}
                  options={["Prestação de serviços jurídicos","Prospecção comercial","Execução de contrato em andamento","Cumprimento de obrigação legal"]} />
              </FormField>
              {!editItem.id && (
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, cursor: "pointer", fontSize: 12, color: theme.gray700 }}>
                  <input type="checkbox" checked={!!editItem.consentimentoLGPD?.aceito}
                    onChange={e => setEditItem({ ...editItem, consentimentoLGPD: { aceito: e.target.checked, data: e.target.checked ? new Date().toISOString() : "" } })}
                    style={{ marginTop: 2 }} />
                  <span>Confirmo que o titular foi informado sobre o tratamento de seus dados pessoais, nos termos da Política de Privacidade do escritório (Lei nº 13.709/2018 — LGPD), e que há base legal para este cadastro.</span>
                </label>
              )}
              {editItem.id && editItem.consentimentoLGPD?.data && (
                <p style={{ fontSize: 11, color: theme.gray500, marginTop: 8 }}>
                  Consentimento registrado em {new Date(editItem.consentimentoLGPD.data).toLocaleString("pt-BR")}.
                </p>
              )}
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <FormField label="Observações">
                <Textarea value={editItem.obs} onChange={v => setEditItem({ ...editItem, obs: v })} placeholder="Anotações sobre o cliente..." />
              </FormField>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8, paddingTop: 16, borderTop: `1px solid ${theme.gray100}` }}>
            <Btn variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Btn>
            {!isViz && <Btn variant="primary" onClick={save}>Salvar Cliente</Btn>}
          </div>
        </Modal>
      )}

      {/* Modal Visualização */}
      {viewItem && (
        <Modal open={!!viewItem} onClose={() => setViewItem(null)} title="Ficha do Cliente" width={720}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
            <div>
              <p style={{ fontSize: 11, color: theme.gray400, textTransform: "uppercase", letterSpacing: 1 }}>Nome</p>
              <p style={{ fontSize: 16, fontWeight: 500, color: theme.gray900, marginTop: 2 }}>{viewItem.nome}</p>
            </div>
            <div>
              <p style={{ fontSize: 11, color: theme.gray400, textTransform: "uppercase", letterSpacing: 1 }}>Tipo</p>
              <div style={{ marginTop: 4 }}><Badge color={viewItem.tipo === "PJ" ? "blue" : "gray"}>{viewItem.tipo}</Badge></div>
            </div>
            <div>
              <p style={{ fontSize: 11, color: theme.gray400, textTransform: "uppercase", letterSpacing: 1 }}>CPF/CNPJ</p>
              <p style={{ fontSize: 13, color: theme.gray700, marginTop: 2 }}>{viewItem.cpfCnpj || "-"}</p>
            </div>
            <div>
              <p style={{ fontSize: 11, color: theme.gray400, textTransform: "uppercase", letterSpacing: 1 }}>WhatsApp</p>
              <p style={{ fontSize: 13, color: theme.gray700, marginTop: 2 }}>{viewItem.whatsapp || "-"}</p>
            </div>
            <div>
              <p style={{ fontSize: 11, color: theme.gray400, textTransform: "uppercase", letterSpacing: 1 }}>E-mail</p>
              <p style={{ fontSize: 13, color: theme.gray700, marginTop: 2 }}>{viewItem.email || "-"}</p>
            </div>
            <div>
              <p style={{ fontSize: 11, color: theme.gray400, textTransform: "uppercase", letterSpacing: 1 }}>Cidade/UF</p>
              <p style={{ fontSize: 13, color: theme.gray700, marginTop: 2 }}>{viewItem.cidade}/{viewItem.uf}</p>
            </div>
            {viewItem.tipo!=="PJ" && (viewItem.rg||viewItem.estadoCivil||viewItem.profissao||viewItem.nacionalidade) && (
              <div style={{ gridColumn: "1 / -1", background:"#f0f9ff", borderRadius:8, padding:"12px 16px", border:"1px solid #bfdbfe" }}>
                <p style={{ fontSize: 11, color: theme.blue, textTransform: "uppercase", letterSpacing: 1, fontWeight:700, marginBottom:8 }}>📋 Qualificação</p>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"8px 16px" }}>
                  <div><p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase"}}>Nacionalidade</p><p style={{fontSize:13,color:theme.gray700,marginTop:2}}>{viewItem.nacionalidade||"—"}</p></div>
                  <div><p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase"}}>Estado Civil</p><p style={{fontSize:13,color:theme.gray700,marginTop:2}}>{viewItem.estadoCivil||"—"}</p></div>
                  <div><p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase"}}>Profissão</p><p style={{fontSize:13,color:theme.gray700,marginTop:2}}>{viewItem.profissao||"—"}</p></div>
                  <div><p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase"}}>RG</p><p style={{fontSize:13,color:theme.gray700,marginTop:2}}>{viewItem.rg||"—"}{viewItem.ufRg?`/${viewItem.ufRg}`:""}</p></div>
                </div>
              </div>
            )}
            <div style={{ gridColumn: "1 / -1" }}>
              <p style={{ fontSize: 11, color: theme.gray400, textTransform: "uppercase", letterSpacing: 1 }}>Produtos Contratados</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {viewItem.produtosContratados?.length > 0
                  ? viewItem.produtosContratados.map(id => {
                    const p = produtos.find(x => x.id === id);
                    return p ? <Badge key={id} color="gold">{p.nome}</Badge> : null;
                  })
                  : <span style={{ fontSize: 13, color: theme.gray400 }}>Nenhum produto vinculado</span>}
              </div>
            </div>

            {viewItem.tipo === "PJ" && viewItem.contatosPJ?.filter(c => c.nome).length > 0 && (
              <div style={{ gridColumn: "1 / -1" }}>
                <p style={{ fontSize: 11, color: theme.gray400, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Contatos da Empresa</p>
                <div style={{ display: "flex", gap: 12 }}>
                  {viewItem.contatosPJ.filter(c => c.nome).map((c, i) => (
                    <div key={i} style={{ background: theme.gray50, borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
                      <p style={{ fontWeight: 500, color: theme.gray800 }}>{c.nome}</p>
                      <p style={{ color: theme.gray500, marginTop: 2 }}>{c.whatsapp}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Histórico */}
          <div style={{ borderTop: `1px solid ${theme.gray100}`, paddingTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <p className="serif" style={{ fontSize: 18, fontWeight: 600 }}>Histórico do Cliente</p>
              <Btn size="sm" onClick={() => setOcModal(clienteAtual)}>📋 Registrar Ocorrências</Btn>
            </div>
            {(!(clienteAtual?.ocorrencias||clienteAtual?.historico) || (clienteAtual?.ocorrencias||clienteAtual?.historico||[]).length === 0) ? (
              <p style={{ fontSize: 13, color: theme.gray400, textAlign: "center", padding: "20px 0" }}>Nenhuma ocorrência registrada.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[...(clienteAtual?.ocorrencias||clienteAtual?.historico||[])].reverse().map((h, i) => (
                  <div key={i} style={{ background: theme.gray50, borderRadius: 8, padding: "12px 16px", borderLeft: `3px solid ${h.ocorrencia === "Fechamento de Contrato" ? theme.gold : theme.gray300}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <Badge color={h.ocorrencia === "Fechamento de Contrato" ? "gold" : "gray"}>{h.ocorrencia}</Badge>
                      <span style={{ fontSize: 11, color: theme.gray400 }}>{h.data}</span>
                    </div>
                    {h.descricao && <p style={{ fontSize: 13, color: theme.gray700, marginTop: 6 }}>{h.descricao}</p>}
                    {h.ocorrencia === "Fechamento de Contrato" && (
                      <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: "4px 16px" }}>
                        {h.numContrato && <span style={{ fontSize: 11, color: theme.gray500 }}>Contrato: <strong>{h.numContrato}</strong></span>}
                        {h.valor && <span style={{ fontSize: 11, color: theme.gray500 }}>Valor: <strong>R$ {h.valor}</strong></span>}
                        {h.condicoes && <span style={{ fontSize: 11, color: theme.gray500 }}>Condições: {h.condicoes}</span>}
                        {h.exito && <span style={{ fontSize: 11, color: theme.gray500 }}>Êxito: {h.exito}% em {h.dataExito}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, paddingTop: 16, borderTop: `1px solid ${theme.gray100}` }}>
            <Btn variant="secondary" onClick={() => openEdit(viewItem)}>Editar</Btn>
            <Btn variant="secondary" onClick={() => setViewItem(null)}>Fechar</Btn>
          </div>
        </Modal>
      )}

      {/* Modal Nova Ocorrência */}
      <Modal open={histModalOpen} onClose={() => setHistModalOpen(false)} title="Registrar Ocorrência" width={560}>
        <FormField label="Data" required>
          <Input type="date" value={novoHist.data} onChange={v => setNovoHist({ ...novoHist, data: v })} />
        </FormField>
        <FormField label="Tipo de Ocorrência" required>
          <Select
            value={novoHist.ocorrencia} onChange={v => setNovoHist({ ...novoHist, ocorrencia: v })}
            options={["Reunião", "E-mail", "Conversa", "Ligação", "WhatsApp", "Fechamento de Contrato", "Outro"]}
            placeholder="Selecione..."
          />
        </FormField>
        <FormField label="Descrição">
          <Textarea value={novoHist.descricao} onChange={v => setNovoHist({ ...novoHist, descricao: v })} placeholder="Detalhes da ocorrência..." />
        </FormField>

        {novoHist.ocorrencia === "Fechamento de Contrato" && (
          <div style={{ background: `${theme.gold}11`, border: `1px solid ${theme.gold}33`, borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: theme.goldDark, marginBottom: 12 }}>Dados do Contrato</p>
            <FormField label="Número do Contrato">
              <Input value={novoHist.numContrato} onChange={v => setNovoHist({ ...novoHist, numContrato: v })} placeholder="2024/001" />
            </FormField>
            <FormField label="Valor (R$)">
              <Input value={novoHist.valor} onChange={v => setNovoHist({ ...novoHist, valor: v })} placeholder="0,00" />
            </FormField>
            <FormField label="Condições de Pagamento">
              <Select
                value={novoHist.condicoes} onChange={v => setNovoHist({ ...novoHist, condicoes: v })}
                options={["À vista", "Parcelado", "À prazo", "No êxito", "Híbrido"]}
                placeholder="Selecione..."
              />
            </FormField>
            <FormField label="Parcelas / Detalhes">
              <Textarea value={novoHist.parcelas} onChange={v => setNovoHist({ ...novoHist, parcelas: v })} rows={2} placeholder="Ex: 3x R$ 500 nos dias 10/01, 10/02, 10/03" />
            </FormField>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <FormField label="Percentual de Êxito (%)">
                <Input value={novoHist.exito} onChange={v => setNovoHist({ ...novoHist, exito: v })} placeholder="Ex: 20" />
              </FormField>
              <FormField label="Data prevista do Êxito">
                <Input type="date" value={novoHist.dataExito} onChange={v => setNovoHist({ ...novoHist, dataExito: v })} />
              </FormField>
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 8 }}>
          <Btn variant="secondary" onClick={() => setHistModalOpen(false)}>Cancelar</Btn>
          <Btn onClick={addHistorico}>Registrar</Btn>
        </div>
      </Modal>
      {/* Modal Ocorrências */}
      {ocModal && (
        <ModalOcorrencias
          entidade={ocModal}
          tipoEntidade="cliente"
          tiposOcorrencia={tiposOcorrencia}
          equipe={data.equipe||[]}
          onSave={(lista) => {
            setData(prev => ({ ...prev, clientes: prev.clientes.map(cl => cl.id === ocModal.id ? { ...cl, ocorrencias: lista } : cl) }));
            setOcModal(prev => prev ? { ...prev, ocorrencias: lista } : null);
            if (viewItem && viewItem.id === ocModal.id) setViewItem(prev => prev ? { ...prev, ocorrencias: lista } : null);
          }}
          onSaveAgenda={evt=>setData(prev=>({...prev,agenda:[...(prev.agenda||[]),evt]}))}
          onClose={() => setOcModal(null)}
        />
      )}
      {/* Modal Tipos de Ocorrência */}
      {tiposModal && (
        <ModalTiposOcorrencia
          tiposOcorrencia={tiposOcorrencia}
          setData={setData}
          onClose={() => setTiposModal(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// MÓDULO: PRODUTOS
// ============================================================
function ModProdutos({ data, setData, setAtivo }) {
  const isViz = useIsVisualizador();
  const { produtos, equipe, pops } = data;
  const [abaAtiva, setAbaAtiva] = useState("lista"); // "lista" | "dashboard"
  const [modalCatalogo, setModalCatalogo] = useState(false);
  const [catAreas, setCatAreas] = useState([]); // áreas selecionadas no catálogo


  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filtroArea, setFiltroArea] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("Ativo");
  const [busca, setBusca] = useState("");
  const [selecionado, setSelecionado] = useState(null);

  // Ordenar alfabeticamente e filtrar
  const filtrados = [...produtos]
    .filter(p =>
      (!filtroArea || p.area === filtroArea) &&
      (!filtroStatus || p.status === filtroStatus) &&
      (!busca || p.nome.toLowerCase().includes(busca.toLowerCase()) || (p.area||"").toLowerCase().includes(busca.toLowerCase()))
    )
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  // Produto selecionado para detalhe
  const prodDetalhe = selecionado ? produtos.find(p => p.id === selecionado) : null;

  const EMPTY = {
    id: "", nome: "", area: "", popId: "", linkPop: "",
    campanha: "Não", valorCampanha: "", valor: "", custo: "",
    margem: "", prazoMedio: "", risco: "Médio",
    responsavel: "", status: "Ativo", obs: "", resumo: "", descricao: "", landingPages: [], parceiraId: "",
    jurisprudencia: "", status2026: "", documentacaoRef: "", ferramentasManual: "", planoAlternativo: "", orientacoesAtendimento: "",
    trib_segmentos: [], trib_regimes: [], trib_tributos: [],
    trib_pf: false, trib_pj: false, trib_documentos: [],
    tarefasPadrao: [], documentosPadrao: [],
    checklistsPorFase: {},   // {fase: ["item1","item2",...]} — Tipo B por produto
    ferramentasExecucao: [],  // [{id, nome, tipo, url, obs}]
    campoRCT: "perdcomp"      // template de execução no RCT: perdcomp|cmo|verbas|ircsll_cc|ircsll_sd|divida
  };

  const openNew = () => { setEditItem({ ...EMPTY }); setModalOpen(true); };
  const openEdit = p => { setEditItem({ ...p }); setModalOpen(true); };

  const save = () => {
    if (!editItem.nome || !editItem.area) return alert("Nome e Área são obrigatórios.");
    const custo = parseFloat(editItem.custo) || 0;
    const valor = parseFloat(editItem.valor) || 0;
    const margem = valor > 0 ? Math.round(((valor - custo) / valor) * 100) : 0;
    const id = editItem.id || "PROD" + String(Date.now()).slice(-6);
    const item = { ...editItem, id, margem };
    setData(prev => { const lista = editItem.id ? prev.produtos.map(p => p.id === item.id ? item : p) : [...prev.produtos, item]; return {...prev, produtos: lista}; });
    setModalOpen(false);
    setSelecionado(id);
  };

  const remove = id => {
    if (!confirm("Remover produto?")) return;
    setData(prev => ({...prev, produtos: prev.produtos.filter(p => p.id !== id)}));
    if (selecionado === id) setSelecionado(null);
  };

  const riscoBadge = r => {
    if (r === "Alto") return <Badge color="red">{r}</Badge>;
    if (r === "Médio") return <Badge color="amber">{r}</Badge>;
    return <Badge color="green">{r}</Badge>;
  };

  // Agrupar por área para a lista lateral
  const grupos = {};
  filtrados.forEach(p => {
    if (!grupos[p.area]) grupos[p.area] = [];
    grupos[p.area].push(p);
  });
  const areasOrdenadas = Object.keys(grupos).sort((a, b) => a.localeCompare(b, "pt-BR"));

  return (
    <div className="fade-in">
      <PageHeader
        title="Produtos"
        subtitle={`${filtrados.length} produto${filtrados.length !== 1 ? "s" : ""} • ordenados alfabeticamente por área`}
        action={
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <div style={{ display: "flex", background: theme.gray100, borderRadius: 8, padding: 3 }}>
              {[["lista","📋 Lista"]].map(([id,label]) => (
                <button key={id} onClick={() => { setAbaAtiva(id); if(id==="dashboard") setFiltroArea("Tributário"); else setFiltroArea(""); }} style={{
                  padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: abaAtiva===id ? 700 : 400,
                  background: abaAtiva===id ? theme.white : "transparent",
                  color: abaAtiva===id ? theme.gray900 : theme.gray500,
                  border: "none", cursor: "pointer", boxShadow: abaAtiva===id ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
                }}>{label}</button>
              ))}
            </div>
            <PainelImportExport
              modulo="Produtos"
              totalRegistros={(data.produtos||[]).length}
              onExportCSV={()=>exportarCSV("produtos",
                ["ID","Nome","Área","Modalidade","Honorários","Descrição","Status","Observações"],
                (data.produtos||[]).map(p=>[p.id||"—",p.nome,p.area||"—",p.modalidade||"—",p.honorarios||"—",p.resumo||p.descricao||"—",p.status||"Ativo",p.obs||"—"])
              )}
              onExportJSON={()=>exportarJSON("produtos_backup", data.produtos||[])}
              onImportCSV={(arquivo, onOk, onErr)=>{
                importarCSV(arquivo, (registros)=>{
                  const prods = data.produtos||[];
                  const novos = registros.map(r=>({
                    id: r.ID||r.id||("PROD"+String(Date.now()).slice(-5)+Math.random().toString(36).slice(-2)),
                    nome: r.Nome||r.nome||"Sem nome",
                    area: r["Área"]||r.area||"",
                    modalidade: r.Modalidade||r.modalidade||"",
                    honorarios: r["Honorários"]||r.honorarios||"",
                    resumo: r["Descrição"]||r.resumo||r.descricao||"",
                    descricao: r["Descrição"]||r.descricao||"",
                    status: r.Status||r.status||"Ativo",
                    obs: r["Observações"]||r.obs||"",
                    landingPages:[]
                  }));
                  const ids = new Set(prods.map(x=>x.id));
                  const inserir = novos.filter(n=>!ids.has(n.id));
                  setData(prev => { const ids2=new Set(prev.produtos.map(x=>x.id)); const ins2=inserir.filter(n=>!ids2.has(n.id)); return {...prev, produtos:[...prev.produtos,...ins2]}; });
                  onOk(inserir.length);
                }, onErr);
              }}
              onImportJSON={(arquivo, onOk, onErr)=>{
                importarJSON(arquivo, (lista)=>{
                  const prods = data.produtos||[];
                  const ids = new Set(prods.map(x=>x.id));
                  const inserir = lista.filter(n=>n.nome&&!ids.has(n.id));
                  setData(prev => { const ids2=new Set(prev.produtos.map(x=>x.id)); const ins2=inserir.filter(n=>n.nome&&!ids2.has(n.id)); return {...prev, produtos:[...prev.produtos,...ins2]}; });
                  onOk(inserir.length);
                }, onErr);
              }}
            />
            <Btn variant="secondary" onClick={()=>setAtivo&&setAtivo("dashtrib")} style={{background:`${theme.gold}18`,border:`1px solid ${theme.gold}`,color:theme.goldDark,fontWeight:700}}>📊 Dashboard Tributário</Btn>
            <Btn variant="secondary" onClick={()=>{setCatAreas([]);setModalCatalogo(true);}}>📋 Catálogo</Btn>
            {!isViz && <Btn onClick={openNew}>+ Novo Produto</Btn>}
          </div>
        }
      />

      {/* Barra de filtros */}
      <Card style={{ padding: "12px 16px", marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="🔍  Buscar produto ou área..."
          style={{ flex: 1, minWidth: 200, padding: "8px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none", fontFamily: "Jost, sans-serif" }}
          onFocus={e => e.target.style.borderColor = theme.gold}
          onBlur={e => e.target.style.borderColor = theme.gray200}
        />
        <select value={filtroArea} onChange={e => { setFiltroArea(e.target.value); setSelecionado(null); }}
          style={{ padding: "8px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none", fontFamily: "Jost, sans-serif" }}>
          <option value="">Todas as áreas</option>
          {(data.areas || AREAS_INICIAIS).map(a => <option key={a}>{a}</option>)}
        </select>
        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
          style={{ padding: "8px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none", fontFamily: "Jost, sans-serif" }}>
          <option value="">Todos</option>
          <option>Ativo</option>
          <option>Pausado</option>
        </select>
        {(filtroArea || filtroStatus !== "Ativo" || busca) && (
          <button onClick={() => { setFiltroArea(""); setFiltroStatus("Ativo"); setBusca(""); setSelecionado(null); }}
            style={{ padding: "7px 14px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 12, background: "transparent", cursor: "pointer", color: theme.gray500, fontFamily: "Jost, sans-serif" }}>
            ✕ Limpar
          </button>
        )}
      </Card>

      {/* Layout: lista lateral + detalhe */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>

        {/* LISTA LATERAL */}
        <div style={{ width: 320, flexShrink: 0 }}>
          <Card style={{ padding: 0, overflow: "hidden", maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}>
            {filtrados.length === 0 ? (
              <div style={{ padding: 32, textAlign: "center", color: theme.gray400, fontSize: 13 }}>
                Nenhum produto encontrado.
              </div>
            ) : areasOrdenadas.map(area => (
              <div key={area}>
                {/* Cabeçalho da área */}
                <div style={{
                  padding: "8px 14px", background: theme.gray900,
                  color: theme.gold, fontSize: 11, fontWeight: 700,
                  letterSpacing: "0.06em", textTransform: "uppercase",
                  position: "sticky", top: 0, zIndex: 1
                }}>
                  {area} <span style={{ opacity: 0.6, fontWeight: 400 }}>({grupos[area].length})</span>
                </div>
                {/* Itens da área */}
                {grupos[area].map(p => (
                  <div
                    key={p.id}
                    onClick={() => setSelecionado(p.id)}
                    style={{
                      padding: "10px 14px",
                      cursor: "pointer",
                      borderBottom: `1px solid ${theme.gray100}`,
                      background: selecionado === p.id ? theme.gold + "18" : "transparent",
                      borderLeft: selecionado === p.id ? `3px solid ${theme.gold}` : "3px solid transparent",
                      transition: "all 0.15s"
                    }}
                    onMouseEnter={e => { if (selecionado !== p.id) e.currentTarget.style.background = theme.gray50; }}
                    onMouseLeave={e => { if (selecionado !== p.id) e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ fontSize: 13, fontWeight: selecionado === p.id ? 600 : 400, color: theme.gray900, lineHeight: 1.3 }}>
                      {p.nome}
                    </div>
                    <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, color: p.status === "Ativo" ? theme.green : theme.gray400, fontWeight: 600 }}>
                        ● {p.status}
                      </span>
                      {p.risco && <span style={{ fontSize: 10, color: theme.gray400 }}>• {p.risco}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </Card>
        </div>

        {/* PAINEL DE DETALHE */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!prodDetalhe ? (
            <Card style={{ padding: 48, textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
              <p style={{ fontSize: 15, fontWeight: 600, color: theme.gray700, marginBottom: 6 }}>Selecione um produto</p>
              <p style={{ fontSize: 13, color: theme.gray400 }}>Clique em qualquer item da lista para ver os detalhes.</p>
            </Card>
          ) : (() => {
            const p = prodDetalhe;
            const resp = equipe.find(e => e.id === p.responsavel);
            const pop = pops.find(x => x.id === p.popId);
            return (
              <Card style={{ padding: 28 }}>
                {/* Cabeçalho do detalhe */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                  <div style={{ flex: 1 }}>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.gray900, lineHeight: 1.3, marginBottom: 10 }}>{p.nome}</h2>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Badge color="blue">{p.area}</Badge>
                      {p.risco === "Alto" && <Badge color="red">{p.risco}</Badge>}
                      {p.risco === "Médio" && <Badge color="amber">{p.risco}</Badge>}
                      {p.risco === "Baixo" && <Badge color="green">{p.risco}</Badge>}
                      <Badge color={p.status === "Ativo" ? "green" : "gray"}>{p.status}</Badge>
                      {p.campanha === "Sim" && <Badge color="amber">Campanha ativa</Badge>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginLeft: 16, flexShrink: 0 }}>
                    {!isViz && <Btn size="sm" variant="secondary" onClick={() => openEdit(p)}>✏️ Editar</Btn>}
                    {!isViz && <Btn size="sm" variant="danger" onClick={() => remove(p.id)}>Remover</Btn>}
                  </div>
                </div>

                {/* Posicionamento técnico / resumo */}
                {(p.resumo || p.descricao) && (
                  <div style={{ background: theme.gray50, border: `1px solid ${theme.gray200}`, borderRadius: 10, padding: "14px 18px", marginBottom: 20 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: theme.gray400, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Posicionamento Técnico</p>
                    <p style={{ fontSize: 14, color: theme.gray700, lineHeight: 1.6 }}>{p.resumo || p.descricao}</p>
                  </div>
                )}

                {/* Grid de dados */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
                  {[
                    { label: "Valor", valor: p.valor ? `R$ ${Number(p.valor).toLocaleString("pt-BR")}` : "—" },
                    { label: "Custo", valor: p.custo ? `R$ ${Number(p.custo).toLocaleString("pt-BR")}` : "—" },
                    { label: "Margem", valor: p.margem ? `${p.margem}%` : "—" },
                    { label: "Prazo médio", valor: p.prazoMedio ? `${p.prazoMedio} dias` : "—" },
                    { label: "Responsável", valor: resp?.nome || "—" },
                    { label: "POP vinculado", valor: pop?.nome || p.popId || "—" },
                  ].map(({ label, valor }) => (
                    <div key={label} style={{ background: theme.white, border: `1px solid ${theme.gray100}`, borderRadius: 8, padding: "10px 14px" }}>
                      <p style={{ fontSize: 10, color: theme.gray400, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{label}</p>
                      <p style={{ fontSize: 14, fontWeight: 600, color: theme.gray800 }}>{valor}</p>
                    </div>
                  ))}
                </div>

                {/* Base legal */}
                {p.baseLegal && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: theme.gray400, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Base Legal</p>
                    <p style={{ fontSize: 13, color: theme.gray600, lineHeight: 1.5 }}>{p.baseLegal}</p>
                  </div>
                )}

                {/* Campos do Manual Técnico — texto de referência/direcionamento */}
                {[
                  ["Jurisprudência", p.jurisprudencia],
                  ["Status 2026", p.status2026],
                  ["Documentação Necessária (referência)", p.documentacaoRef],
                  ["Ferramentas (referência do Manual)", p.ferramentasManual],
                  ["Plano Alternativo", p.planoAlternativo],
                  ["Orientações de Atendimento", p.orientacoesAtendimento],
                ].map(([label, valor]) => valor && (
                  <div key={label} style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: theme.gray400, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{label}</p>
                    <p style={{ fontSize: 13, color: theme.gray600, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{valor}</p>
                  </div>
                ))}

                {/* Link POP */}
                {p.linkPop && (
                  <div style={{ marginBottom: 16 }}>
                    <a href={p.linkPop} target="_blank" rel="noreferrer"
                      style={{ fontSize: 13, color: theme.blue, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      📄 Abrir POP no Drive
                    </a>
                  </div>
                )}

                {/* Landing Pages */}
                {(p.landingPages||[]).length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: theme.gray400, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Landing Pages</p>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {p.landingPages.map((lp, i) => (
                        <a key={i} href={lp.link} target="_blank" rel="noreferrer"
                          style={{ fontSize: 12, color: theme.blue, textDecoration: "none", background: theme.blue + "11", padding: "4px 10px", borderRadius: 5, border: "1px solid " + theme.blue + "33" }}>
                          {lp.nome || `LP ${i+1}`}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Observações */}
                {p.obs && (
                  <div style={{ background: theme.gray50, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: theme.gray600, lineHeight: 1.5 }}>
                    <strong style={{ fontSize: 11, color: theme.gray400, textTransform: "uppercase", letterSpacing: "0.05em" }}>Obs: </strong>{p.obs}
                  </div>
                )}

                {/* ID do produto */}
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${theme.gray100}` }}>
                  <span style={{ fontSize: 10, color: theme.gray300, fontFamily: "monospace" }}>ID: {p.id}</span>
                </div>
              </Card>
            );
          })()}
        </div>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editItem?.id ? "Editar Produto" : "Novo Produto"} width={680}>
        {editItem && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Nome do Produto" required>
                  <Input value={editItem.nome} onChange={v => setEditItem({ ...editItem, nome: v })} placeholder="Nome do produto/serviço" />
                </FormField>
              </div>
              <FormField label="Área Jurídica" required>
                <Select value={editItem.area} onChange={v => setEditItem({ ...editItem, area: v })} options={data.areas || AREAS_INICIAIS} placeholder="Selecione..." />
              </FormField>
              <FormField label="POP Vinculado">
                <Select value={editItem.popId} onChange={v => setEditItem({ ...editItem, popId: v })}
                  options={pops.map(p => ({ value: p.id, label: `${p.id} — ${p.nome}` }))} placeholder="Selecione o POP..." />
              </FormField>
              <FormField label="Link do POP">
                <Input value={editItem.linkPop} onChange={v => setEditItem({ ...editItem, linkPop: v })} placeholder="https://drive.google.com/..." />
              </FormField>
              <FormField label="Responsável">
                <Select value={editItem.responsavel} onChange={v => setEditItem({ ...editItem, responsavel: v })}
                  options={equipe.filter(e => e.ativo).map(e => ({ value: e.id, label: e.nome }))} placeholder="Selecione..." />
              </FormField>
              <FormField label="Valor (R$)">
                <Input type="number" value={editItem.valor} onChange={v => setEditItem({ ...editItem, valor: v })} placeholder="0,00" />
              </FormField>
              <FormField label="Custo Operacional (R$)">
                <Input type="number" value={editItem.custo} onChange={v => setEditItem({ ...editItem, custo: v })} placeholder="0,00" />
              </FormField>
              <FormField label="Prazo Médio (dias)">
                <Input type="number" value={editItem.prazoMedio} onChange={v => setEditItem({ ...editItem, prazoMedio: v })} placeholder="90" />
              </FormField>
              <FormField label="Risco">
                <Select value={editItem.risco} onChange={v => setEditItem({ ...editItem, risco: v })} options={RISCOS} />
              </FormField>
              <FormField label="Tem Campanha?">
                <Select value={editItem.campanha} onChange={v => setEditItem({ ...editItem, campanha: v })} options={["Sim", "Não"]} />
              </FormField>
              {editItem.campanha === "Sim" && (
                <div style={{ gridColumn: "1/-1" }}>
                  <FormField label="Detalhes da Campanha">
                    <Input value={editItem.valorCampanha} onChange={v => setEditItem({ ...editItem, valorCampanha: v })} placeholder="Ex: Instagram - Jan/2024" />
                  </FormField>
                </div>
              )}
              <FormField label="Status">
                <Select value={editItem.status} onChange={v => setEditItem({ ...editItem, status: v })} options={["Ativo", "Pausado"]} />
              </FormField>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Posicionamento Técnico / Descrição">
                  <Textarea value={editItem.resumo||editItem.descricao||""} onChange={v => setEditItem({ ...editItem, resumo: v, descricao: v })} placeholder="Descrição resumida do produto (Posicionamento Técnico)..." rows={3} />
                </FormField>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Base Legal">
                  <Input value={editItem.baseLegal||""} onChange={v => setEditItem({ ...editItem, baseLegal: v })} placeholder="Ex: Lei 8.213/1991; Art. 203 CF..." />
                </FormField>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Jurisprudência">
                  <Textarea value={editItem.jurisprudencia||""} onChange={v => setEditItem({ ...editItem, jurisprudencia: v })} placeholder="Súmulas, temas repetitivos, precedentes vinculantes..." rows={2} />
                </FormField>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Status 2026">
                  <Textarea value={editItem.status2026||""} onChange={v => setEditItem({ ...editItem, status2026: v })} placeholder="Situação atual da tese/tema, marcos de calendário, grau de consolidação..." rows={2} />
                </FormField>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Documentação Necessária (referência)">
                  <Textarea value={editItem.documentacaoRef||""} onChange={v => setEditItem({ ...editItem, documentacaoRef: v })} placeholder="Texto descritivo da documentação necessária (não altera o checklist de documentos vinculado abaixo)..." rows={2} />
                </FormField>
              </div>
              <FormField label="Ferramentas (referência do Manual)">
                <Input value={editItem.ferramentasManual||""} onChange={v => setEditItem({ ...editItem, ferramentasManual: v })} placeholder="Ex: E-Auditoria 4 | Sistema Cálculo Jurídico..." />
              </FormField>
              <FormField label="Plano Alternativo">
                <Input value={editItem.planoAlternativo||""} onChange={v => setEditItem({ ...editItem, planoAlternativo: v })} placeholder="Execução manual quando a ferramenta não cobrir o caso..." />
              </FormField>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Orientações de Atendimento">
                  <Textarea value={editItem.orientacoesAtendimento||""} onChange={v => setEditItem({ ...editItem, orientacoesAtendimento: v })} placeholder="Recomendações práticas para a equipe comercial/operacional no atendimento ao cliente..." rows={2} />
                </FormField>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Landing Pages">
                  {(editItem.landingPages||[]).map((lp,li) => (<div key={li} style={{display:"flex",gap:8,marginBottom:8,alignItems:"center"}}><input value={lp.nome} onChange={e=>{const a=[...(editItem.landingPages||[])];a[li]={...a[li],nome:e.target.value};setEditItem({...editItem,landingPages:a});}} placeholder="Nome" style={{width:140,padding:"7px",border:"1px solid "+theme.gray200,borderRadius:6,fontSize:12,outline:"none"}} /><input value={lp.descricaoLp||""} onChange={e=>{const a=[...(editItem.landingPages||[])];a[li]={...a[li],descricaoLp:e.target.value};setEditItem({...editItem,landingPages:a});}} placeholder="Descricao" style={{flex:1,padding:"7px",border:"1px solid "+theme.gray200,borderRadius:6,fontSize:12,outline:"none"}} /><input value={lp.link||""} onChange={e=>{const a=[...(editItem.landingPages||[])];a[li]={...a[li],link:e.target.value};setEditItem({...editItem,landingPages:a});}} placeholder="https://..." style={{width:190,padding:"7px",border:"1px solid "+theme.gray200,borderRadius:6,fontSize:12,outline:"none"}} /><button onClick={()=>setEditItem({...editItem,landingPages:(editItem.landingPages||[]).filter((_,i)=>i!==li)})} style={{padding:"5px 10px",border:"1px solid "+theme.red,borderRadius:5,background:"transparent",color:theme.red,cursor:"pointer"}}>x</button></div>))}
                  <button onClick={()=>setEditItem({...editItem,landingPages:[...(editItem.landingPages||[]),{nome:"",descricaoLp:"",link:""}]})} style={{marginTop:6,padding:"6px 14px",border:"1px solid "+theme.gray200,borderRadius:6,background:"transparent",fontSize:12,cursor:"pointer"}}>+ LP</button>
                </FormField>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Observações">
                  <Textarea value={editItem.obs} onChange={v => setEditItem({ ...editItem, obs: v })} placeholder="Notas sobre o produto..." />
                </FormField>
              </div>
            </div>
            {editItem.valor && editItem.custo && (
              <div style={{ background: `${theme.green}11`, border: `1px solid ${theme.green}33`, borderRadius: 8, padding: "10px 16px", marginBottom: 16 }}>
                <p style={{ fontSize: 12, color: theme.green }}>
                  Margem estimada: <strong>{Math.round(((editItem.valor - editItem.custo) / editItem.valor) * 100)}%</strong>
                  {" "}(R$ {(editItem.valor - editItem.custo).toLocaleString("pt-BR")})
                </p>
              </div>
            )}
            {/* Classificação Tributária — só aparece se área = Tributário */}
            {editItem.area === "Tributário" && (
              <div style={{ borderTop: `1px solid ${theme.gold}33`, marginTop: 8, paddingTop: 16, background: `${theme.gold}08`, borderRadius: 8, padding: 16, marginBottom: 8 }}>
                <p style={{ fontSize: 11, color: theme.goldDark, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, marginBottom: 14 }}>
                  ⚖️ Classificação Tributária
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                  {/* Pessoa Física / Jurídica */}
                  <FormField label="Destinatário">
                    <div style={{ display: "flex", gap: 16, padding: "10px 0" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, color: theme.gray700 }}>
                        <input type="checkbox" checked={editItem.trib_pf||false} onChange={e => setEditItem({...editItem, trib_pf: e.target.checked})} />
                        👤 Pessoa Física
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, color: theme.gray700 }}>
                        <input type="checkbox" checked={editItem.trib_pj||false} onChange={e => setEditItem({...editItem, trib_pj: e.target.checked})} />
                        🏢 Pessoa Jurídica
                      </label>
                    </div>
                  </FormField>
                  {/* Regimes tributários */}
                  <FormField label="Regime Tributário">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "6px 0" }}>
                      {["Lucro Real", "Lucro Presumido", "Simples Nacional"].map(r => {
                        const sel = (editItem.trib_regimes||[]).includes(r);
                        return (
                          <label key={r} onClick={() => {
                            const lista = sel ? (editItem.trib_regimes||[]).filter(x=>x!==r) : [...(editItem.trib_regimes||[]), r];
                            setEditItem({...editItem, trib_regimes: lista});
                          }} style={{
                            display: "flex", alignItems: "center", gap: 5, cursor: "pointer",
                            padding: "5px 10px", borderRadius: 6, fontSize: 12,
                            border: `1px solid ${sel ? theme.gold : theme.gray200}`,
                            background: sel ? `${theme.gold}18` : theme.white,
                            color: sel ? theme.goldDark : theme.gray600, fontWeight: sel ? 700 : 400
                          }}>
                            <div style={{ width: 12, height: 12, borderRadius: 3, border: `2px solid ${sel ? theme.gold : theme.gray300}`, background: sel ? theme.gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {sel && <span style={{ color: "#fff", fontSize: 8, fontWeight: 900 }}>✓</span>}
                            </div>
                            {r}
                          </label>
                        );
                      })}
                    </div>
                  </FormField>
                  {/* Segmentos */}
                  <div style={{ gridColumn: "1/-1" }}>
                    <FormField label="Segmentos de Mercado">
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "6px 0" }}>
                        {(data.segmentosTrib||[]).map(s => {
                          const sel = (editItem.trib_segmentos||[]).includes(s.id);
                          return (
                            <label key={s.id} onClick={() => {
                              const lista = sel ? (editItem.trib_segmentos||[]).filter(x=>x!==s.id) : [...(editItem.trib_segmentos||[]), s.id];
                              setEditItem({...editItem, trib_segmentos: lista});
                            }} style={{
                              display: "flex", alignItems: "center", gap: 5, cursor: "pointer",
                              padding: "5px 10px", borderRadius: 6, fontSize: 12,
                              border: `1px solid ${sel ? theme.blue : theme.gray200}`,
                              background: sel ? "#EEF3FD" : theme.white,
                              color: sel ? theme.blue : theme.gray600, fontWeight: sel ? 700 : 400
                            }}>
                              <div style={{ width: 12, height: 12, borderRadius: 3, border: `2px solid ${sel ? theme.blue : theme.gray300}`, background: sel ? theme.blue : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {sel && <span style={{ color: "#fff", fontSize: 8, fontWeight: 900 }}>✓</span>}
                              </div>
                              {s.nome}
                            </label>
                          );
                        })}
                      </div>
                    </FormField>
                  </div>
                  {/* Tributos */}
                  <div style={{ gridColumn: "1/-1" }}>
                    <FormField label="Tributos Envolvidos">
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "6px 0" }}>
                        {(data.tributos||[]).map(t => {
                          const sel = (editItem.trib_tributos||[]).includes(t.id);
                          return (
                            <label key={t.id} onClick={() => {
                              const lista = sel ? (editItem.trib_tributos||[]).filter(x=>x!==t.id) : [...(editItem.trib_tributos||[]), t.id];
                              setEditItem({...editItem, trib_tributos: lista});
                            }} style={{
                              display: "flex", alignItems: "center", gap: 5, cursor: "pointer",
                              padding: "5px 10px", borderRadius: 6, fontSize: 12,
                              border: `1px solid ${sel ? "#7c3aed" : theme.gray200}`,
                              background: sel ? "#f5f3ff" : theme.white,
                              color: sel ? "#7c3aed" : theme.gray600, fontWeight: sel ? 700 : 400
                            }}>
                              <div style={{ width: 12, height: 12, borderRadius: 3, border: `2px solid ${sel ? "#7c3aed" : theme.gray300}`, background: sel ? "#7c3aed" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {sel && <span style={{ color: "#fff", fontSize: 8, fontWeight: 900 }}>✓</span>}
                              </div>
                              {t.nome}
                            </label>
                          );
                        })}
                      </div>
                    </FormField>
                  </div>
                  {/* Documentos */}
                  <div style={{ gridColumn: "1/-1" }}>
                    <FormField label="Documentos Necessários para Execução">
                      <p style={{fontSize:9,color:theme.gray400,marginBottom:6}}>Estes documentos serão o checklist automático de Entrada e Triagem na execução operacional.</p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "6px 0" }}>
                        {(data.documentosTrib||[]).map(d => {
                          const sel = (editItem.documentosPadrao||[]).includes(d.id);
                          return (
                            <label key={d.id} onClick={() => {
                              const lista = sel ? (editItem.documentosPadrao||[]).filter(x=>x!==d.id) : [...(editItem.documentosPadrao||[]), d.id];
                              setEditItem({...editItem, documentosPadrao: lista, trib_documentos: lista});
                            }} style={{
                              display: "flex", alignItems: "center", gap: 5, cursor: "pointer",
                              padding: "5px 10px", borderRadius: 6, fontSize: 12,
                              border: `1px solid ${sel ? theme.green : theme.gray200}`,
                              background: sel ? "#f0fdf4" : theme.white,
                              color: sel ? theme.green : theme.gray600, fontWeight: sel ? 700 : 400
                            }}>
                              <div style={{ width: 12, height: 12, borderRadius: 3, border: `2px solid ${sel ? theme.green : theme.gray300}`, background: sel ? theme.green : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {sel && <span style={{ color: "#fff", fontSize: 8, fontWeight: 900 }}>✓</span>}
                              </div>
                              {d.nome}
                            </label>
                          );
                        })}
                      </div>
                    </FormField>
                  </div>
                </div>
              </div>
            )}
            {/* ── Tarefas Padrão — seleção do catálogo */}
            <div style={{ borderTop: `1px solid ${theme.gray100}`, marginTop: 16, paddingTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: theme.gray800 }}>📋 Tarefas Padrão (selecione do catálogo)</p>
                <span style={{ fontSize: 10, color: theme.gray400 }}>{(editItem.tarefasPadrao||[]).length} selecionada(s)</span>
              </div>
              {(data.catalogoTarefas||[]).length === 0
                ? <p style={{ fontSize: 11, color: theme.gray400, fontStyle: "italic" }}>Catálogo vazio. Adicione tarefas em Dashboard Tributário → ⚙️ Tarefas.</p>
                : <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "4px 0" }}>
                    {(data.catalogoTarefas||[]).map(t => {
                      const sel = (editItem.tarefasPadrao||[]).includes(t.id);
                      return (
                        <label key={t.id} style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 10px", borderRadius:20, border:`1px solid ${sel?theme.amber:theme.gray200}`, background:sel?theme.amber+'15':theme.white, cursor:"pointer", fontSize:11, color:sel?theme.amber:theme.gray700, fontWeight:sel?700:400, userSelect:"none" }}>
                          <input type="checkbox" checked={sel} onChange={() => {
                            const lista = sel ? (editItem.tarefasPadrao||[]).filter(x=>x!==t.id) : [...(editItem.tarefasPadrao||[]), t.id];
                            setEditItem({...editItem, tarefasPadrao: lista});
                          }} style={{ display:"none" }}/>
                          {sel ? '✓ ' : ''}{t.nome}
                        </label>
                      );
                    })}
                  </div>
              }
            </div>
            {/* ── Documentos e Tarefas Padrão — seleção unificada para áreas não-tributárias */}
            {editItem.area !== "Tributário" && (
              <div style={{ borderTop: `1px solid ${theme.gray100}`, marginTop: 10, paddingTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: theme.gray800 }}>📄 Documentos Necessários</p>
                  <span style={{ fontSize: 10, color: theme.gray400 }}>{(editItem.documentosPadrao||[]).length} selecionado(s)</span>
                </div>
                <p style={{fontSize:9,color:theme.gray400,marginBottom:8}}>Serão o checklist automático de Entrada e Triagem na execução operacional.</p>
                {(data.documentosTrib||[]).length === 0
                  ? <p style={{ fontSize: 11, color: theme.gray400, fontStyle: "italic" }}>Catálogo vazio. Adicione documentos em Dashboard Tributário → ⚙️ Documentos.</p>
                  : <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "4px 0" }}>
                      {(data.documentosTrib||[]).map(d => {
                        const sel = (editItem.documentosPadrao||[]).includes(d.id);
                        return (
                          <label key={d.id} style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 10px", borderRadius:20, border:`1px solid ${sel?theme.green:theme.gray200}`, background:sel?theme.green+'15':theme.white, cursor:"pointer", fontSize:11, color:sel?theme.green:theme.gray700, fontWeight:sel?700:400, userSelect:"none" }}>
                            <input type="checkbox" checked={sel} onChange={() => {
                              const lista = sel ? (editItem.documentosPadrao||[]).filter(x=>x!==d.id) : [...(editItem.documentosPadrao||[]), d.id];
                              setEditItem({...editItem, documentosPadrao: lista});
                            }} style={{ display:"none" }}/>
                            {sel ? '✓ ' : ''}{d.nome}
                          </label>
                        );
                      })}
                    </div>
                }
              </div>
            )}
            {/* ── Template RCT ── */}
            <div style={{marginBottom:12,padding:"12px 14px",borderRadius:8,background:"#fffbeb",border:"1px solid #fde68a"}}>
              <p style={{fontSize:12,fontWeight:700,color:"#92400e",marginBottom:8}}>⚙️ Template de Execução RCT</p>
              <FormField label="Template de apuração na fase Produção">
                <Select value={editItem.campoRCT||"60_meses"}
                  onChange={v=>setEditItem(prev=>({...prev,campoRCT:v}))}
                  options={[
                    {value:"60_meses",    label:"Template 2 — Período de 60 meses (Mês + Valor)"},
                    {value:"campo_unico", label:"Template 1 — Campo único (Valor Apurado)"},
                  ]}/>
              </FormField>
              <p style={{fontSize:10,color:"#92400e",marginTop:6}}>
                Template 1: produtos de consultoria/gestão sem apuração periódica.
                Template 2: recuperação de crédito tributário com apuração mensal (60 competências).
              </p>
            </div>

            {/* ── Ferramentas de Execução ── */}
            <div style={{marginTop:16,padding:"14px 16px",borderRadius:8,background:"#f0fdf4",border:"1px solid #bbf7d0"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <p style={{fontSize:12,fontWeight:700,color:theme.green}}>🛠️ Ferramentas de Execução</p>
                <button onClick={()=>setEditItem(prev=>({...prev,
                  ferramentasExecucao:[...(prev.ferramentasExecucao||[]),
                    {id:"FERR"+Date.now(),nome:"",tipo:"sistema",url:"",obs:""}
                  ]}))}
                  style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:`1px solid ${theme.green}`,
                    background:"#dcfce7",color:theme.green,cursor:"pointer",fontWeight:600}}>
                  + Adicionar Ferramenta
                </button>
              </div>
              {(editItem.ferramentasExecucao||[]).length===0&&(
                <p style={{fontSize:11,color:theme.gray400}}>Nenhuma ferramenta cadastrada. Clique em "+ Adicionar Ferramenta".</p>
              )}
              {(editItem.ferramentasExecucao||[]).map((ferr,fi)=>(
                <div key={ferr.id||fi} style={{display:"grid",gridTemplateColumns:"2fr 1fr 2fr auto",
                  gap:"0 10px",marginBottom:8,padding:"10px 12px",background:"#fff",
                  borderRadius:7,border:`1px solid ${theme.gray200}`,alignItems:"end"}}>
                  <FormField label="Nome da Ferramenta">
                    <Input value={ferr.nome||""} onChange={v=>setEditItem(prev=>({...prev,
                      ferramentasExecucao:prev.ferramentasExecucao.map((f,i)=>i===fi?{...f,nome:v}:f)
                    }))} placeholder="Ex: Planilha PIS/COFINS, e-Auditoria"/>
                  </FormField>
                  <FormField label="Tipo">
                    <Select value={ferr.tipo||"sistema"}
                      onChange={v=>setEditItem(prev=>({...prev,
                        ferramentasExecucao:prev.ferramentasExecucao.map((f,i)=>i===fi?{...f,tipo:v}:f)
                      }))}
                      options={["sistema","excel","google_sheets","pdf","outro"]}
                      placeholder="Tipo"/>
                  </FormField>
                  <FormField label="Link / URL">
                    <Input value={ferr.url||""} onChange={v=>setEditItem(prev=>({...prev,
                      ferramentasExecucao:prev.ferramentasExecucao.map((f,i)=>i===fi?{...f,url:v}:f)
                    }))} placeholder="https://..."/>
                  </FormField>
                  <button onClick={()=>setEditItem(prev=>({...prev,
                    ferramentasExecucao:prev.ferramentasExecucao.filter((_,i)=>i!==fi)
                  }))} style={{fontSize:14,color:theme.red,background:"none",border:"none",
                    cursor:"pointer",padding:"4px 8px",alignSelf:"center",marginTop:16}}>×</button>
                </div>
              ))}
            </div>
            {/* ── Checklists por Fase Operacional ── */}
            <div style={{marginTop:16,padding:"14px 16px",borderRadius:8,background:"#fef9ec",border:"1px solid #fde68a"}}>
              <p style={{fontSize:12,fontWeight:700,color:"#92400e",marginBottom:4}}>📋 Checklists por Fase Operacional</p>
              <p style={{fontSize:11,color:theme.gray500,marginBottom:10}}>
                Entrada e Produção são derivados automaticamente dos documentos e tarefas acima. Revisão e Protocolo permitem itens adicionais específicos deste produto.
              </p>

              {/* Seletor de template B */}
              <div style={{background:"#fff7ed",borderRadius:7,padding:"10px 12px",marginBottom:14,border:"1px solid #fed7aa"}}>
                <p style={{fontSize:10,fontWeight:700,color:"#ea580c",textTransform:"uppercase",letterSpacing:.6,marginBottom:8}}>
                  ⚡ Carregar Template Pré-definido — Revisão Sênior + Protocolo
                </p>
                <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  <select id="selTmplB" style={{flex:1,minWidth:200,padding:"7px 10px",border:"1px solid #fed7aa",borderRadius:6,fontSize:12,outline:"none",background:"#fff"}}>
                    <option value="">Selecione um template...</option>
                    {Object.keys(TEMPLATES_CHECKLIST_B).map(k => (
                      <option key={k} value={k}>{k}</option>
                    ))}
                  </select>
                  <button onClick={()=>{
                    const sel = document.getElementById("selTmplB")?.value;
                    if (!sel || !TEMPLATES_CHECKLIST_B[sel]) return;
                    const tmpl = TEMPLATES_CHECKLIST_B[sel];
                    setEditItem(prev => ({
                      ...prev,
                      checklistsPorFase: {
                        ...(prev.checklistsPorFase||{}),
                        "Revisão Sênior": tmpl["Revisão Sênior"] || (prev.checklistsPorFase?.["Revisão Sênior"]||[]),
                        "Protocolo":      tmpl["Protocolo"]      || (prev.checklistsPorFase?.["Protocolo"]     ||[]),
                      }
                    }));
                  }} style={{padding:"7px 14px",borderRadius:6,border:"none",background:"#ea580c",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                    ↓ Usar Template
                  </button>
                  <button onClick={()=>setEditItem(prev=>({...prev,checklistsPorFase:{...(prev.checklistsPorFase||{}),"Revisão Sênior":[],"Protocolo":[]}}))}
                    style={{padding:"7px 10px",borderRadius:6,border:"1px solid #fca5a5",background:"#fef2f2",color:theme.red,fontSize:11,cursor:"pointer"}}>
                    🗑 Limpar itens
                  </button>
                </div>
                <p style={{fontSize:9,color:"#92400e",marginTop:6}}>
                  O template preenche os itens das fases Revisão Sênior e Protocolo. Você pode editar, adicionar ou remover itens depois.
                </p>
              </div>

              {/* ── ENTRADA E TRIAGEM — read-only, derivado de documentosPadrao ── */}
              {(()=>{
                const COR = "#0ea5e9";
                const catDocs = data.documentosTrib || [];
                const docNomes = catDocs.filter(d => (editItem.documentosPadrao||[]).includes(d.id)).map(d => d.nome);
                // Compat: se só tem trib_documentos (legado), usar eles
                const docNomesCompat = docNomes.length > 0 ? docNomes
                  : catDocs.filter(d => (editItem.trib_documentos||[]).includes(d.id)).map(d => d.nome);
                return (
                  <div style={{marginBottom:10,background:theme.white,borderRadius:7,padding:"10px 12px",border:`1px solid ${COR}33`}}>
                    <p style={{fontSize:11,fontWeight:700,color:COR,marginBottom:6}}>📥 Entrada e Triagem <span style={{fontSize:9,fontWeight:400,color:theme.gray400}}>— gerado automaticamente dos documentos acima</span></p>
                    {docNomesCompat.length === 0
                      ? <p style={{fontSize:10,color:theme.gray400,fontStyle:"italic"}}>Nenhum documento selecionado. Selecione documentos acima para gerar o checklist.</p>
                      : <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                          {docNomesCompat.map((n,i) => (
                            <span key={i} style={{fontSize:10,padding:"3px 9px",borderRadius:20,background:COR+"15",color:COR,border:`1px solid ${COR}33`}}>✓ {n}</span>
                          ))}
                        </div>
                    }
                  </div>
                );
              })()}

              {/* ── PRODUÇÃO — read-only, derivado de tarefasPadrao ── */}
              {(()=>{
                const COR = "#d97706";
                const catTarefas = data.catalogoTarefas || [];
                const tarefaNomes = catTarefas.filter(t => (editItem.tarefasPadrao||[]).includes(t.id)).map(t => t.nome);
                return (
                  <div style={{marginBottom:10,background:theme.white,borderRadius:7,padding:"10px 12px",border:`1px solid ${COR}33`}}>
                    <p style={{fontSize:11,fontWeight:700,color:COR,marginBottom:6}}>⚙️ Produção <span style={{fontSize:9,fontWeight:400,color:theme.gray400}}>— gerado automaticamente das tarefas acima</span></p>
                    {tarefaNomes.length === 0
                      ? <p style={{fontSize:10,color:theme.gray400,fontStyle:"italic"}}>Nenhuma tarefa selecionada. Selecione tarefas acima para gerar o checklist.</p>
                      : <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                          {tarefaNomes.map((n,i) => (
                            <span key={i} style={{fontSize:10,padding:"3px 9px",borderRadius:20,background:COR+"15",color:COR,border:`1px solid ${COR}33`}}>✓ {n}</span>
                          ))}
                        </div>
                    }
                  </div>
                );
              })()}

              {/* ── REVISÃO SÊNIOR e PROTOCOLO — global + produto ── */}
              {["Revisão Sênior","Protocolo"].map(FASE=>{
                const COR = FASE==="Revisão Sênior"?"#7c3aed":"#ea580c";
                const ICON = FASE==="Revisão Sênior"?"🔍":"📨";
                const globalItems = resolveChecklistGlobal(FASE, data.checklistsGlobais||{});
                const prodExtras = (editItem.checklistsPorFase||{})[FASE] || [];

                const updGlobal = (idx,val) => setData(prev=>{
                  const glob={...(prev.checklistsGlobais||{})};
                  const items=[...(glob[FASE]||resolveChecklistGlobal(FASE,glob))];
                  items[idx]=val;
                  return{...prev,checklistsGlobais:{...glob,[FASE]:items}};
                });
                const remGlobal = (idx) => setData(prev=>{
                  const glob={...(prev.checklistsGlobais||{})};
                  const items=[...(glob[FASE]||resolveChecklistGlobal(FASE,glob))];
                  items.splice(idx,1);
                  return{...prev,checklistsGlobais:{...glob,[FASE]:items}};
                });
                const addGlobal = () => setData(prev=>{
                  const glob={...(prev.checklistsGlobais||{})};
                  const items=[...(glob[FASE]||resolveChecklistGlobal(FASE,glob)),""];
                  return{...prev,checklistsGlobais:{...glob,[FASE]:items}};
                });
                const addProd = () => setEditItem(prev=>({...prev,checklistsPorFase:{...(prev.checklistsPorFase||{}),[FASE]:[...(prev.checklistsPorFase?.[FASE]||[]),""]}  }));
                const editProd = (i,val) => setEditItem(prev=>({...prev,checklistsPorFase:{...(prev.checklistsPorFase||{}),[FASE]:(prev.checklistsPorFase?.[FASE]||[]).map((it,j)=>j===i?val:it)}}));
                const remProd  = (i) => setEditItem(prev=>({...prev,checklistsPorFase:{...(prev.checklistsPorFase||{}),[FASE]:(prev.checklistsPorFase?.[FASE]||[]).filter((_,j)=>j!==i)}}));

                return (
                  <div key={FASE} style={{marginBottom:10,background:theme.white,borderRadius:7,padding:"10px 12px",border:`1px solid ${COR}33`}}>
                    <p style={{fontSize:11,fontWeight:700,color:COR,marginBottom:8}}>{ICON} {FASE}</p>

                    {/* Global — escritório */}
                    <div style={{background:theme.gray50,borderRadius:6,padding:"8px 10px",marginBottom:8}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                        <p style={{fontSize:9,color:theme.gray500,fontWeight:700,textTransform:"uppercase",letterSpacing:.6}}>✏️ Checklist Global do Escritório</p>
                        <button onClick={addGlobal} style={{fontSize:10,padding:"2px 7px",borderRadius:4,border:`1px solid ${COR}`,background:COR+"15",color:COR,cursor:"pointer",fontWeight:600}}>+ Global</button>
                      </div>
                      {globalItems.map((item,i)=>(
                        <div key={i} style={{display:"flex",gap:5,marginBottom:4,alignItems:"center"}}>
                          <input value={item} onChange={e=>updGlobal(i,e.target.value)}
                            style={{flex:1,padding:"5px 9px",border:`1px solid ${theme.gray200}`,borderRadius:6,fontSize:11,outline:"none",background:"#fff"}}/>
                          <button onClick={()=>remGlobal(i)} style={{fontSize:13,color:theme.red,background:"none",border:"none",cursor:"pointer"}}>×</button>
                        </div>
                      ))}
                    </div>

                    {/* Produto-specific */}
                    <p style={{fontSize:9,color:theme.amber,fontWeight:700,textTransform:"uppercase",letterSpacing:.6,marginBottom:5}}>Adicionais deste produto</p>
                    {prodExtras.length===0&&<p style={{fontSize:10,color:theme.gray400,fontStyle:"italic",marginBottom:5}}>Nenhum item adicional.</p>}
                    {prodExtras.map((item,i)=>(
                      <div key={i} style={{display:"flex",gap:5,marginBottom:4,alignItems:"center"}}>
                        <input value={item} onChange={e=>editProd(i,e.target.value)} placeholder="Item específico deste produto..."
                          style={{flex:1,padding:"5px 9px",border:`1px solid ${theme.gray200}`,borderRadius:6,fontSize:11,outline:"none"}}/>
                        <button onClick={()=>remProd(i)} style={{fontSize:13,color:theme.red,background:"none",border:"none",cursor:"pointer"}}>×</button>
                      </div>
                    ))}
                    <button onClick={addProd} style={{fontSize:10,padding:"3px 8px",borderRadius:5,border:`1px solid ${theme.amber}`,background:theme.amber+"15",color:theme.amber,cursor:"pointer",fontWeight:600}}>+ Item do produto</button>
                  </div>
                );
              })}
            </div>

            {/* ── Perfil de Captação ── */}
            <div style={{marginTop:16,padding:"14px 16px",borderRadius:8,background:"#f0f9ff",border:"1px solid #bfdbfe"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <p style={{fontSize:12,fontWeight:700,color:"#1e40af",margin:0}}>🎯 Perfil de Captação</p>
                <span style={{fontSize:10,color:theme.gray400}}>Dados para prospecção e CRM</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
                <FormField label="UF Alvo">
                  <Select
                    value={(editItem.captacao||{}).ufAlvo||""}
                    onChange={v=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),ufAlvo:v}}))}
                    options={[{value:"",label:"Todas as UFs"},...UFS.map(u=>({value:u,label:u}))]}
                    placeholder="Todas as UFs"
                  />
                </FormField>
                <FormField label="Município Alvo">
                  <Input
                    value={(editItem.captacao||{}).municipioAlvo||""}
                    onChange={v=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),municipioAlvo:v}}))}
                    placeholder="Ex: São Paulo, Campinas, Curitiba"
                  />
                </FormField>
                <FormField label="Canal de Captação">
                  <Select
                    value={(editItem.captacao||{}).canal||""}
                    onChange={v=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),canal:v}}))}
                    options={[
                      {value:"tese_segmento",     label:"Tese por Segmento"},
                      {value:"prospeccao_ativa",  label:"Prospecção Ativa"},
                      {value:"indicacao",         label:"Indicação"},
                      {value:"evento",            label:"Evento / Palestra"},
                      {value:"digital",           label:"Marketing Digital"},
                      {value:"parceria",          label:"Parceria Comercial"},
                    ]}
                    placeholder="Selecione o canal..."
                  />
                </FormField>
                <FormField label="Perfil do Cliente-Alvo">
                  <Select
                    value={(editItem.captacao||{}).perfilCliente||""}
                    onChange={v=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),perfilCliente:v}}))}
                    options={[
                      {value:"PJ", label:"Pessoa Jurídica"},
                      {value:"PF", label:"Pessoa Física"},
                      {value:"ambos", label:"PF e PJ"},
                    ]}
                    placeholder="Selecione..."
                  />
                </FormField>

                <FormField label="Faturamento Mensal Mínimo (R$)">
                  <Input
                    type="number"
                    value={(editItem.captacao||{}).faturamentoMensalMinimo||""}
                    onChange={v=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),faturamentoMensalMinimo:v}}))}
                    placeholder="Ex: 50000"
                  />
                </FormField>
                <FormField label="Valor Mínimo da Execução Tributária (R$)">
                  <Input
                    type="number"
                    value={(editItem.captacao||{}).valorExecucaoMin||""}
                    onChange={v=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),valorExecucaoMin:v}}))}
                    placeholder="Ex: 100000"
                  />
                </FormField>

                <FormField label="Score de Viabilidade (1–5)">
                  <Select
                    value={String((editItem.captacao||{}).scoreViabilidade||"")}
                    onChange={v=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),scoreViabilidade:parseInt(v)||""} }))}
                    options={[
                      {value:"5",label:"5 — Alta viabilidade"},
                      {value:"4",label:"4 — Boa viabilidade"},
                      {value:"3",label:"3 — Média"},
                      {value:"2",label:"2 — Baixa"},
                      {value:"1",label:"1 — Complexa"},
                    ]}
                    placeholder="Selecione..."
                  />
                </FormField>
                <FormField label="Regime(s) Tributário(s) Alvo">
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",paddingTop:8}}>
                    {["Simples Nacional","Lucro Presumido","Lucro Real","MEI","Profissional Liberal"].map(r=>{
                      const selecionados = (editItem.captacao||{}).regimesAlvo||[];
                      const ativo = selecionados.includes(r);
                      return (
                        <button key={r} onClick={()=>setEditItem(prev=>({
                          ...prev,captacao:{...(prev.captacao||{}),
                            regimesAlvo: ativo
                              ? selecionados.filter(x=>x!==r)
                              : [...selecionados, r]
                          }
                        }))} style={{padding:"4px 10px",borderRadius:5,cursor:"pointer",fontSize:11,fontWeight:600,
                          border:`1px solid ${ativo?"#1e40af":theme.gray200}`,
                          background:ativo?"#dbeafe":theme.white,
                          color:ativo?"#1e40af":theme.gray500}}>
                          {ativo?"✓ ":""}{r}
                        </button>
                      );
                    })}
                  </div>
                </FormField>
              </div>

              {/* CNAEs alvo — array dinâmico */}
              <div style={{marginTop:12}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <p style={{fontSize:11,fontWeight:700,color:"#1e40af",margin:0}}>CNAEs Alvo</p>
                  <button onClick={()=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),
                    cnaeAlvo:[...((prev.captacao||{}).cnaeAlvo||[]),""]
                  }}))} style={{fontSize:11,padding:"3px 10px",borderRadius:5,
                    border:"1px solid #1e40af",background:"#dbeafe",color:"#1e40af",cursor:"pointer",fontWeight:600}}>
                    + CNAE
                  </button>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                  {((editItem.captacao||{}).cnaeAlvo||[]).map((cnae,i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:4}}>
                      <input
                        value={cnae}
                        onChange={e=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),
                          cnaeAlvo:((prev.captacao||{}).cnaeAlvo||[]).map((c,j)=>j===i?e.target.value:c)
                        }}))}
                        placeholder="Ex: 86.10-1"
                        style={{width:110,padding:"5px 8px",border:`1px solid ${theme.gray200}`,
                          borderRadius:5,fontSize:12,outline:"none"}}
                      />
                      <button onClick={()=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),
                        cnaeAlvo:((prev.captacao||{}).cnaeAlvo||[]).filter((_,j)=>j!==i)
                      }}))} style={{background:"none",border:"none",color:theme.red,cursor:"pointer",fontSize:14,padding:"2px"}}>×</button>
                    </div>
                  ))}
                  {((editItem.captacao||{}).cnaeAlvo||[]).length===0&&(
                    <p style={{fontSize:11,color:theme.gray400,fontStyle:"italic"}}>Nenhum CNAE cadastrado.</p>
                  )}
                </div>
              </div>

              {/* Estratégia de abordagem */}
              <FormField label="Estratégia de Abordagem" style={{marginTop:12}}>
                <Textarea
                  value={(editItem.captacao||{}).estrategia||""}
                  onChange={v=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),estrategia:v}}))}
                  placeholder="Ex: Clínicas no Lucro Presumido com faturamento acima de R$ 50k/mês nos últimos 5 anos"
                  rows={2}
                />
              </FormField>

              {/* Observações adicionais da captação — array dinâmico */}
              <div style={{marginTop:12}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <p style={{fontSize:11,fontWeight:700,color:"#1e40af",margin:0}}>Observações / Pontos de Abordagem</p>
                  <button onClick={()=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),
                    obsItens:[...((prev.captacao||{}).obsItens||[]),""]
                  }}))} style={{fontSize:11,padding:"3px 10px",borderRadius:5,
                    border:"1px solid #1e40af",background:"#dbeafe",color:"#1e40af",cursor:"pointer",fontWeight:600}}>
                    + Item
                  </button>
                </div>
                {((editItem.captacao||{}).obsItens||[]).map((obs,i)=>(
                  <div key={i} style={{display:"flex",gap:6,marginBottom:6,alignItems:"center"}}>
                    <input
                      value={obs}
                      onChange={e=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),
                        obsItens:((prev.captacao||{}).obsItens||[]).map((o,j)=>j===i?e.target.value:o)
                      }}))}
                      placeholder={`Ponto de abordagem ${i+1}`}
                      style={{flex:1,padding:"6px 10px",border:`1px solid ${theme.gray200}`,
                        borderRadius:6,fontSize:12,outline:"none"}}
                    />
                    <button onClick={()=>setEditItem(prev=>({...prev,captacao:{...(prev.captacao||{}),
                      obsItens:((prev.captacao||{}).obsItens||[]).filter((_,j)=>j!==i)
                    }}))} style={{background:"none",border:"none",color:theme.red,cursor:"pointer",fontSize:16}}>×</button>
                  </div>
                ))}
                {((editItem.captacao||{}).obsItens||[]).length===0&&(
                  <p style={{fontSize:11,color:theme.gray400,fontStyle:"italic"}}>Clique em "+ Item" para adicionar pontos de abordagem.</p>
                )}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 16, borderTop: `1px solid ${theme.gray100}`, marginTop: 16 }}>
              <Btn variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Btn>
              {!isViz && <Btn onClick={save}>Salvar Produto</Btn>}
            </div>
          </>
        )}
      </Modal>

      {/* ══════ MODAL CATÁLOGO DE PRODUTOS ══════ */}
      {modalCatalogo&&(()=>{
        const AREAS_DISP = [...new Set(produtos.map(p=>p.area))].filter(Boolean).sort();
        const areasSel   = catAreas.length>0 ? catAreas : AREAS_DISP;
        const prodsSel   = produtos
          .filter(p=>p.status==="Ativo"&&areasSel.includes(p.area))
          .sort((a,b)=>a.area.localeCompare(b.area,"pt-BR")||a.nome.localeCompare(b.nome,"pt-BR"));

        // Agrupar por área
        const porArea = {};
        prodsSel.forEach(p=>{
          if(!porArea[p.area]) porArea[p.area]=[];
          porArea[p.area].push(p);
        });

        const COR_AREA = {
          "Tributário":"#1a3a5c","Cível":"#1a3a2a","Previdenciário":"#2a1a3a",
          "Bancário":"#1a2a3a","Família e Sucessões":"#3a1a2a","Imobiliário":"#2a2a1a",
        };
        const corArea = a => COR_AREA[a]||"#1a1a2e";

        const gerarHTMLDocumentos = () => {
          const catDocs = data.documentosTrib || [];
          const resolveNomesDoc = p => {
            const ids = (p.documentosPadrao||[]).length > 0 ? p.documentosPadrao : (p.trib_documentos||[]);
            return ids.map(id => catDocs.find(d=>d.id===id)?.nome || id).filter(Boolean);
          };
          const rows = Object.entries(porArea).map(([area, prods]) => {
            const linhas = prods.map((p,i) => {
              const docs = resolveNomesDoc(p);
              return `
              <tr style="background:${i%2===0?"#f9f7f2":"#fff"}">
                <td style="padding:9px 12px;font-size:10.5pt;font-weight:600;color:#1a1a1a;border:1px solid #ddd;vertical-align:top;width:28%">${p.nome}</td>
                <td style="padding:9px 12px;font-size:9.5pt;color:#333;border:1px solid #ddd;vertical-align:top">
                  ${docs.length===0
                    ? '<span style="color:#aaa;font-style:italic;">Nenhum documento cadastrado</span>'
                    : docs.map((d,di)=>`<span style="display:inline-block;background:#e8f4fd;color:#1a3a5c;border:1px solid #bde0f7;border-radius:3px;padding:2px 8px;margin:2px 3px 2px 0;font-size:9pt;font-weight:500">${di+1}. ${d}</span>`).join("")
                  }
                </td>
                <td style="padding:9px 12px;font-size:9pt;text-align:center;border:1px solid #ddd;color:#555;vertical-align:top;width:8%">${docs.length}</td>
              </tr>`;
            }).join("");
            return `
              <tr>
                <td colspan="3" style="padding:10px 14px;background:${corArea(area)};color:#fff;font-weight:700;font-size:11.5pt;letter-spacing:0.5px">
                  ${area}&nbsp;<span style="font-size:9.5pt;opacity:.7;font-weight:400">(${prods.length} produto${prods.length>1?"s":""})</span>
                </td>
              </tr>
              ${linhas}`;
          }).join("");

          return `<!DOCTYPE html><html><head><meta charset="UTF-8">
            <title>Documentos por Produto — Alves &amp; Correa Advogados</title>
            <style>
              @page { size: A4 portrait; margin: 15mm 12mm 18mm 12mm; }
              body { font-family: Arial, sans-serif; color: #1a1a1a; font-size: 10.5pt; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
              h1 { font-size: 15pt; font-weight: bold; text-align: center; margin-bottom: 4px; }
              .sub { text-align: center; color: #666; font-size: 9.5pt; margin-bottom: 6px; }
              .linha-ouro { width: 80px; height: 3px; background: #C9A84C; margin: 6px auto 14px; }
              .filtro { text-align:center; font-size:9pt; color:#C9A84C; font-weight:700; margin-bottom:12px; }
              table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
              th { background: #1a1610; color: #C9A84C; padding: 7px 12px; text-align: left; font-size: 8.5pt; letter-spacing: 0.5px; text-transform: uppercase; border: 1px solid #333; }
              .rodape { margin-top: 14px; font-size: 8pt; color: #999; text-align: right; border-top: 1px solid #ddd; padding-top: 5px; }
              @media print { tr { page-break-inside: avoid; } }
            </style>
            </head><body>
            <h1>Produtos — Documentos Necessários para Atendimento</h1>
            <div class="linha-ouro"></div>
            <div class="sub">Alves &amp; Correa Advogados &nbsp;·&nbsp; ${new Date().toLocaleDateString("pt-BR")}</div>
            ${catAreas.length>0 ? `<div class="filtro">Área(s): ${catAreas.join(" · ")}</div>` : ""}
            <table>
              <thead>
                <tr>
                  <th style="width:28%">Produto / Tese</th>
                  <th>Documentos Necessários</th>
                  <th style="width:8%;text-align:center">Qtd</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
            <div class="rodape">Uso interno · Alves &amp; Correa Advogados · ${prodsSel.length} produtos · Gerado em ${new Date().toLocaleString("pt-BR")}</div>
            </body></html>`;
        };

        const imprimirDocumentos = () => {
          const win = window.open("","_blank","width=900,height=800");
          if (!win) return alert("Permita pop-ups para gerar o PDF.");
          win.document.write(gerarHTMLDocumentos());
          win.document.close();
          win.focus();
          setTimeout(()=>win.print(), 600);
        };

        const gerarHTMLCatalogo = () => {
          const rows = Object.entries(porArea).map(([area, prods])=>{
            const linhas = prods.map((p,i)=>`
              <tr style="background:${i%2===0?"#f9f7f2":"#fff"}">
                <td style="padding:8px 12px;font-size:11pt;font-weight:600;color:#1a1a1a;border:1px solid #ddd">${p.nome}</td>
                <td style="padding:8px 12px;font-size:10pt;color:#444;border:1px solid #ddd;max-width:300px">${p.resumo||"—"}</td>
                <td style="padding:8px 12px;font-size:9pt;color:#555;border:1px solid #ddd">${p.baseLegal||"—"}</td>
                <td style="padding:8px 12px;font-size:10pt;color:#444;border:1px solid #ddd;text-align:center">${p.prazoMedio?p.prazoMedio+" dias":"—"}</td>
                <td style="padding:8px 12px;font-size:10pt;border:1px solid #ddd;text-align:center">
                  <span style="padding:2px 8px;border-radius:4px;font-weight:700;font-size:9pt;
                    background:${p.risco==="Alto"?"#fee2e2":p.risco==="Médio"?"#fef9c3":"#dcfce7"};
                    color:${p.risco==="Alto"?"#991b1b":p.risco==="Médio"?"#854d0e":"#166534"}">${p.risco||"—"}</span>
                </td>
              </tr>`).join("");
            return `
              <tr>
                <td colspan="5" style="padding:10px 14px;background:${corArea(area)};color:#fff;font-weight:700;font-size:12pt;letter-spacing:0.5px">
                  ${area} &nbsp;<span style="font-size:10pt;opacity:.7;font-weight:400">(${prods.length} produto${prods.length>1?"s":""})</span>
                </td>
              </tr>
              ${linhas}`;
          }).join("");

          return `<!DOCTYPE html><html><head><meta charset="UTF-8">
            <title>Catálogo de Produtos — Alves &amp; Correa Advogados</title>
            <style>
              @page { size: A4 landscape; margin: 15mm 12mm; }
              body { font-family: Arial, sans-serif; color: #1a1a1a; font-size: 11pt; }
              h1 { font-size: 16pt; font-weight: bold; text-align: center; margin-bottom: 4px; }
              .sub { text-align: center; color: #666; font-size: 10pt; margin-bottom: 18px; }
              .linha-ouro { width: 80px; height: 3px; background: #C9A84C; margin: 8px auto 18px; }
              table { width: 100%; border-collapse: collapse; margin-bottom: 0; }
              th { background: #1a1610; color: #C9A84C; padding: 8px 12px; text-align: left; font-size: 9pt; letter-spacing: 0.5px; text-transform: uppercase; border: 1px solid #333; }
              .rodape { margin-top: 18px; font-size: 8pt; color: #999; text-align: right; border-top: 1px solid #ddd; padding-top: 6px; }
            </style>
            </head><body>
            <h1>Catálogo de Produtos Jurídicos</h1>
            <div class="linha-ouro"></div>
            <div class="sub">Alves &amp; Correa Advogados &nbsp;·&nbsp; Especialistas em Recuperação de Ativos e Direito Empresarial &nbsp;·&nbsp; ${new Date().toLocaleDateString("pt-BR")}</div>
            <table>
              <thead>
                <tr>
                  <th style="width:22%">Produto / Tese</th>
                  <th style="width:35%">Descrição</th>
                  <th style="width:27%">Base Legal</th>
                  <th style="width:8%;text-align:center">Prazo Médio</th>
                  <th style="width:8%;text-align:center">Risco</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
            <div class="rodape">Documento confidencial · Gerado em ${new Date().toLocaleString("pt-BR")} · Alves &amp; Correa Advogados · ${prodsSel.length} produtos</div>
            </body></html>`;
        };

        const imprimirPDF = () => {
          const win = window.open("","_blank","width=1200,height=800");
          win.document.write(gerarHTMLCatalogo());
          win.document.close();
          win.focus();
          setTimeout(()=>win.print(), 800);
        };

        const baixarWord = () => {
          const html = gerarHTMLCatalogo();
          const blob = new Blob(["﻿"+html], {type:"application/msword"});
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement("a");
          a.href = url;
          a.download = `Catalogo_Produtos_Alves_Correa_${new Date().toISOString().slice(0,10)}.doc`;
          a.click();
          URL.revokeObjectURL(url);
        };

        return (
          <div style={{
            position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:1000,
            background:"rgba(0,0,0,.7)",display:"flex",alignItems:"stretch",justifyContent:"center"
          }}>
            <div style={{
              background:theme.white,width:"100%",maxWidth:1200,display:"flex",
              flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,.4)"
            }}>
              {/* Cabeçalho do modal */}
              <div style={{
                background:theme.gray900,padding:"16px 28px",display:"flex",
                alignItems:"center",justifyContent:"space-between",flexShrink:0
              }}>
                <div style={{display:"flex",alignItems:"center",gap:14}}>
                  <span style={{fontSize:20}}>📋</span>
                  <div>
                    <p className="serif" style={{color:theme.cream,fontSize:16,fontWeight:700}}>Catálogo de Produtos Jurídicos</p>
                    <p style={{color:theme.gold,fontSize:11,marginTop:2}}>{prodsSel.length} produto{prodsSel.length!==1?"s":""} · Alves & Correa Advogados</p>
                  </div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <Btn onClick={imprimirDocumentos} style={{background:"#1a3a5c",color:"#fff",border:"none",fontSize:12}}>
                    📄 Rel. Documentos
                  </Btn>
                  <Btn onClick={baixarWord} style={{background:"#2b5fdb",color:"#fff",border:"none",fontSize:12}}>
                    📄 Exportar Word
                  </Btn>
                  <Btn onClick={imprimirPDF} style={{background:theme.red,color:"#fff",border:"none",fontSize:12}}>
                    🖨️ Catálogo PDF
                  </Btn>
                  <button onClick={()=>setModalCatalogo(false)} style={{
                    background:"none",border:`1px solid ${theme.gray600}`,color:theme.gray300,
                    borderRadius:6,padding:"7px 14px",cursor:"pointer",fontSize:12
                  }}>✕ Fechar</button>
                </div>
              </div>

              {/* Seletor de áreas */}
              <div style={{
                padding:"12px 28px",borderBottom:`1px solid ${theme.gray200}`,
                display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",background:theme.gray50,flexShrink:0
              }}>
                <span style={{fontSize:12,fontWeight:600,color:theme.gray600}}>Áreas:</span>
                <button onClick={()=>setCatAreas([])} style={{
                  padding:"5px 14px",borderRadius:20,fontSize:12,cursor:"pointer",fontWeight:600,
                  border:`1px solid ${catAreas.length===0?theme.gold:theme.gray200}`,
                  background:catAreas.length===0?theme.gold:theme.white,
                  color:catAreas.length===0?theme.gray900:theme.gray500
                }}>Todas ({produtos.filter(p=>p.status==="Ativo").length})</button>
                {AREAS_DISP.map(a=>{
                  const sel = catAreas.includes(a);
                  const qtd = produtos.filter(p=>p.area===a&&p.status==="Ativo").length;
                  return (
                    <button key={a} onClick={()=>setCatAreas(prev=>sel?prev.filter(x=>x!==a):[...prev,a])} style={{
                      padding:"5px 14px",borderRadius:20,fontSize:12,cursor:"pointer",fontWeight:sel?700:400,
                      border:`1px solid ${sel?corArea(a):theme.gray200}`,
                      background:sel?corArea(a):theme.white,
                      color:sel?"#fff":theme.gray600
                    }}>{a} ({qtd})</button>
                  );
                })}
              </div>

              {/* Tabela de produtos */}
              <div style={{flex:1,overflowY:"auto",padding:"0"}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead style={{position:"sticky",top:0,zIndex:1}}>
                    <tr style={{background:theme.gray900}}>
                      {["Produto / Tese","Área","Descrição","Base Legal","Prazo","Risco"].map((h,i)=>(
                        <th key={h} style={{
                          padding:"10px 16px",textAlign:"left",fontSize:10,
                          color:theme.gold,letterSpacing:.8,textTransform:"uppercase",
                          fontWeight:700,whiteSpace:"nowrap",
                          width:["28%","10%","30%","22%","6%","7%"][i]
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(porArea).map(([area,prods])=>(
                      <>
                        <tr key={"h-"+area} style={{background:corArea(area)}}>
                          <td colSpan={6} style={{
                            padding:"8px 16px",color:"#fff",fontWeight:700,fontSize:13,
                            letterSpacing:.5
                          }}>
                            {area}
                            <span style={{fontSize:11,opacity:.7,fontWeight:400,marginLeft:8}}>
                              {prods.length} produto{prods.length>1?"s":""}
                            </span>
                          </td>
                        </tr>
                        {prods.map((p,i)=>(
                          <tr key={p.id} style={{
                            borderBottom:`1px solid ${theme.gray100}`,
                            background:i%2===0?theme.white:theme.gray50
                          }}>
                            <td style={{padding:"10px 16px",fontSize:12,fontWeight:600,color:theme.gray900}}>{p.nome}</td>
                            <td style={{padding:"10px 16px"}}>
                              <span style={{
                                fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:600,
                                background:corArea(area)+"22",color:corArea(area),
                                whiteSpace:"nowrap"
                              }}>{area}</span>
                            </td>
                            <td style={{padding:"10px 16px",fontSize:12,color:theme.gray600,lineHeight:1.5}}>{p.resumo||"—"}</td>
                            <td style={{padding:"10px 16px",fontSize:11,color:theme.gray500,lineHeight:1.4}}>{p.baseLegal||"—"}</td>
                            <td style={{padding:"10px 16px",fontSize:12,color:theme.gray600,textAlign:"center",whiteSpace:"nowrap"}}>{p.prazoMedio?p.prazoMedio+"d":"—"}</td>
                            <td style={{padding:"10px 16px",textAlign:"center"}}>
                              <span style={{
                                fontSize:10,padding:"3px 8px",borderRadius:4,fontWeight:700,
                                background:p.risco==="Alto"?"#fee2e2":p.risco==="Médio"?"#fef9c3":"#dcfce7",
                                color:p.risco==="Alto"?"#991b1b":p.risco==="Médio"?"#854d0e":"#166534"
                              }}>{p.risco||"—"}</span>
                            </td>
                          </tr>
                        ))}
                      </>
                    ))}
                  </tbody>
                </table>
                {prodsSel.length===0&&(
                  <div style={{padding:"40px",textAlign:"center",color:theme.gray400}}>
                    <p style={{fontSize:32,marginBottom:8}}>📋</p>
                    <p style={{fontSize:14}}>Nenhum produto ativo encontrado para as áreas selecionadas.</p>
                  </div>
                )}
              </div>

              {/* Rodapé */}
              <div style={{
                padding:"10px 28px",borderTop:`1px solid ${theme.gray200}`,
                background:theme.gray50,display:"flex",justifyContent:"space-between",
                alignItems:"center",flexShrink:0
              }}>
                <p style={{fontSize:11,color:theme.gray400}}>
                  {prodsSel.length} produto{prodsSel.length!==1?"s":""} selecionado{prodsSel.length!==1?"s":""}
                  {catAreas.length>0?` · ${catAreas.length} área${catAreas.length>1?"s":""}`:` · Todas as áreas`}
                </p>
                <p style={{fontSize:11,color:theme.gray400}}>
                  Alves & Correa Advogados · {new Date().toLocaleDateString("pt-BR")}
                </p>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}




// ============================================================
// MODAL: CRÉDITOS RECUPERADOS
// ============================================================
function ModalCreditosRecuperados({ data, produtos, tribLista, onClose }) {
  const lancamentos  = data.lancamentos  || [];
  const contratos    = data.contratos    || [];
  const clientes     = data.clientes     || [];
  const fichas       = data.fichasCredito || [];

  const fmtMoeda = v => 'R$ ' + parseFloat(v||0).toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2});
  const fmtData  = d => d ? new Date(d+'T00:00:00').toLocaleDateString('pt-BR') : '—';

  // Consolidar apenas fichas tributárias com dados do contrato/cliente
  const fichasComDados = fichas.map(ficha => {
    const cont = contratos.find(ct => ct.id === ficha.contratoId);
    if (!cont) return null;
    const cli  = clientes.find(cl => cl.id === ficha.clienteId);
    const prod = produtos.find(p  => p.id  === ficha.produtoId);
    const areaCont = cont.area || prod?.area || '';
    if (areaCont !== 'Tributário') return null;
    const totalCompensado = (ficha.compensacoes||[])
      .filter(c => c.status !== 'Glosado')
      .reduce((s,c) => s + (parseFloat(c.valorCreditoCompensado)||0), 0);
    const totalCorrecao = (ficha.compensacoes||[])
      .filter(c => c.status !== 'Glosado')
      .reduce((s,c) => s + (parseFloat(c.correcaoMonetaria)||0), 0);
    const totalHonorarios = (ficha.compensacoes||[])
      .filter(c => c.status !== 'Glosado')
      .reduce((s,c) => s + (parseFloat(c.valorHonorarios)||0), 0);
    const saldoACompensar = (parseFloat(ficha.valorCreditoInicial)||0) - totalCompensado - totalCorrecao;
    return {
      id:               ficha.id,
      contratoId:       cont.id,
      numContrato:      cont.numContrato || '—',
      cliente:          cli ? cli.nome : '—',
      produto:          prod ? prod.nome : '—',
      creditoInicial:   parseFloat(ficha.valorCreditoInicial)||0,
      totalCompensado,
      totalCorrecao,
      totalHonorarios,
      saldoACompensar:  Math.max(0, saldoACompensar),
      percHonorarios:   parseFloat(ficha.percHonorarios)||0,
      nComps:           (ficha.compensacoes||[]).length,
      dataConclusao:    cont.dataConclusao || '',
      status:           cont.status || '—',
    };
  }).filter(Boolean);

  // KPIs
  const totalCredInicial  = fichasComDados.reduce((s,f) => s + f.creditoInicial, 0);
  const totalCompensado   = fichasComDados.reduce((s,f) => s + f.totalCompensado, 0);
  const totalSaldo        = fichasComDados.reduce((s,f) => s + f.saldoACompensar, 0);
  const totalHonorarios   = fichasComDados.reduce((s,f) => s + f.totalHonorarios, 0);
  const percGeral         = totalCredInicial > 0 ? Math.round(totalCompensado/totalCredInicial*100) : 0;

  return ReactDOM.createPortal((
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:3000,display:'flex',alignItems:'flex-start',justifyContent:'center',overflowY:'auto',padding:'32px 16px'}}>
      <div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:1100,boxShadow:'0 24px 64px rgba(0,0,0,.25)'}}>
        {/* Header */}
        <div style={{background:'linear-gradient(135deg,#0ea5e9,#0284c7)',borderRadius:'16px 16px 0 0',padding:'20px 28px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div>
            <h2 style={{color:'#fff',fontSize:20,fontWeight:700,fontFamily:'serif',margin:0}}>💲 Créditos Recuperados</h2>
            <p style={{color:'rgba(255,255,255,.8)',fontSize:12,margin:'4px 0 0'}}>Painel de controle de recuperação de créditos tributários</p>
          </div>
          <button onClick={onClose} style={{background:'rgba(255,255,255,.2)',border:'none',color:'#fff',borderRadius:8,padding:'8px 16px',cursor:'pointer',fontSize:13}}>✕ Fechar</button>
        </div>

        <div style={{padding:'24px 28px'}}>
          {/* KPIs */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:24}}>
            {[
              {l:'Fichas Ativas',      v:fichasComDados.length,         icon:'📋', c:'#0284c7'},
              {l:'Crédito Total',      v:fmtMoeda(totalCredInicial),    icon:'💰', c:'#16a34a'},
              {l:'Total Compensado',   v:fmtMoeda(totalCompensado),     icon:'✅', c:'#0284c7'},
              {l:'Saldo a Compensar',  v:fmtMoeda(totalSaldo),          icon:'⏳', c:totalSaldo>0?'#d97706':'#16a34a'},
              {l:'Honorários Gerados', v:fmtMoeda(totalHonorarios),     icon:'⚖️', c:'#7c3aed'},
            ].map(k => (
              <div key={k.l} style={{background:'#f8fafc',borderRadius:10,padding:'14px 16px',borderLeft:'4px solid '+k.c}}>
                <p style={{fontSize:10,color:'#64748b',textTransform:'uppercase',letterSpacing:.6,margin:'0 0 4px'}}>{k.icon} {k.l}</p>
                <p style={{fontSize:15,fontWeight:700,color:k.c,margin:0}}>{k.v}</p>
              </div>
            ))}
          </div>

          {/* Barra de progresso geral */}
          <div style={{background:'#f1f5f9',borderRadius:8,padding:'12px 16px',marginBottom:20}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
              <span style={{fontSize:12,fontWeight:600,color:'#374151'}}>Progresso geral de compensação</span>
              <span style={{fontSize:12,fontWeight:700,color:'#0284c7'}}>{percGeral}%</span>
            </div>
            <div style={{background:'#e2e8f0',borderRadius:4,height:8}}>
              <div style={{background:'linear-gradient(90deg,#0ea5e9,#0284c7)',borderRadius:4,height:8,width:percGeral+'%',transition:'width .3s'}}/>
            </div>
          </div>

          {/* Tabela */}
          {fichasComDados.length === 0 ? (
            <div style={{textAlign:'center',padding:'40px 0',color:'#94a3b8'}}>
              <p style={{fontSize:32,margin:'0 0 8px'}}>📋</p>
              <p style={{fontSize:14}}>Nenhuma ficha de crédito registrada.</p>
              <p style={{fontSize:12}}>Acesse um contrato tributário e clique em 💳 Ficha de Crédito.</p>
            </div>
          ) : (
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:'#f1f5f9'}}>
                    {['Contrato','Cliente','Produto','Crédito Inicial','Compensado','Saldo a Comp.','Honorários','% Honor.','Comps.','Conclusão','Status'].map(h => (
                      <th key={h} style={{padding:'10px 12px',textAlign:'left',fontWeight:600,color:'#374151',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fichasComDados.map((f,i) => (
                    <tr key={f.id} style={{borderBottom:'1px solid #f1f5f9',background:i%2===0?'#fff':'#f8fafc'}}>
                      <td style={{padding:'10px 12px',fontWeight:600,color:'#1e40af'}}>{f.numContrato}</td>
                      <td style={{padding:'10px 12px',color:'#374151',whiteSpace:'normal',wordBreak:'break-word',lineHeight:1.35}} title={f.cliente}>{f.cliente}</td>
                      <td style={{padding:'10px 12px',color:'#374151',maxWidth:260,whiteSpace:'normal',wordBreak:'break-word',lineHeight:1.35}} title={f.produto}>{f.produto}</td>
                      <td style={{padding:'10px 12px',textAlign:'right',fontWeight:600,color:'#374151'}}>{fmtMoeda(f.creditoInicial)}</td>
                      <td style={{padding:'10px 12px',textAlign:'right',fontWeight:600,color:'#16a34a'}}>{fmtMoeda(f.totalCompensado)}</td>
                      <td style={{padding:'10px 12px',textAlign:'right',fontWeight:700,color:f.saldoACompensar>0?'#d97706':'#16a34a'}}>{fmtMoeda(f.saldoACompensar)}</td>
                      <td style={{padding:'10px 12px',textAlign:'right',color:'#7c3aed'}}>{fmtMoeda(f.totalHonorarios)}</td>
                      <td style={{padding:'10px 12px',textAlign:'center',color:'#374151'}}>{f.percHonorarios}%</td>
                      <td style={{padding:'10px 12px',textAlign:'center',color:'#374151'}}>{f.nComps}</td>
                      <td style={{padding:'10px 12px',color:'#374151'}}>{f.dataConclusao ? fmtData(f.dataConclusao) : '—'}</td>
                      <td style={{padding:'10px 12px'}}>
                        <span style={{background:f.status==='Concluído'?'#dcfce7':f.status==='Em andamento'?'#dbeafe':'#f3f4f6',color:f.status==='Concluído'?'#166534':f.status==='Em andamento'?'#1e40af':'#374151',borderRadius:12,padding:'2px 10px',fontSize:11,fontWeight:600}}>
                          {f.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{background:'#f0fdf4',borderTop:'2px solid #16a34a'}}>
                    <td colSpan={3} style={{padding:'12px 14px',fontWeight:700,color:'#14532d',fontSize:13}}>TOTAIS</td>
                    <td style={{padding:'12px 14px',textAlign:'right',fontWeight:700,color:'#14532d'}}>{fmtMoeda(totalCredInicial)}</td>
                    <td style={{padding:'12px 14px',textAlign:'right',fontWeight:700,color:'#16a34a'}}>{fmtMoeda(totalCompensado)}</td>
                    <td style={{padding:'12px 14px',textAlign:'right',fontWeight:700,color:'#d97706'}}>{fmtMoeda(totalSaldo)}</td>
                    <td style={{padding:'12px 14px',textAlign:'right',fontWeight:700,color:'#7c3aed'}}>{fmtMoeda(totalHonorarios)}</td>
                    <td colSpan={4}/>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}

function ModDashTrib({ data, setData, setAtivo, anterior }) {
  const produtos   = data.produtos     || [];
  const segLista   = data.segmentosTrib || [];
  const tribLista  = data.tributos      || [];

  const [modalTabelas,  setModalTabelas]  = React.useState(null);
  const [novoNomeTbl,   setNovoNomeTbl]   = React.useState("");
  const [editIdTbl,     setEditIdTbl]     = React.useState(null);
  const [modalCreditos, setModalCreditos] = React.useState(false);
  const [filtPF,        setFiltPF]        = React.useState(false);
  const [filtPJ,        setFiltPJ]        = React.useState(false);
  const [filtRegimes,   setFiltRegimes]   = React.useState([]);
  const [filtSegmentos, setFiltSegmentos] = React.useState([]);
  const [filtTributos,  setFiltTributos]  = React.useState([]);
  const [subAba,        setSubAba]        = React.useState("filtros");

  const REGIMES_LISTA = ["Lucro Real", "Lucro Presumido", "Simples Nacional"];
  const prodsTrib = produtos.filter(p => p.area === "Tributário" && p.status === "Ativo");

  const prodsFiltrados = prodsTrib.filter(p => {
    if (filtPF && !p.trib_pf) return false;
    if (filtPJ && !p.trib_pj) return false;
    if (filtRegimes.length   > 0 && !filtRegimes.every(r  => (p.trib_regimes   ||[]).includes(r)))  return false;
    if (filtSegmentos.length > 0 && !filtSegmentos.some(s => (p.trib_segmentos ||[]).includes(s)))  return false;
    if (filtTributos.length  > 0 && !filtTributos.some(t  => (p.trib_tributos  ||[]).includes(t)))  return false;
    return true;
  });

  const toggleFiltro = (arr, setArr, val) =>
    setArr(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);

  const limparFiltros = () => {
    setFiltPF(false); setFiltPJ(false);
    setFiltRegimes([]); setFiltSegmentos([]); setFiltTributos([]);
  };

  const temFiltro = filtPF || filtPJ || filtRegimes.length > 0 ||
                   filtSegmentos.length > 0 || filtTributos.length > 0;

  const corRisco = r => r==="Alto" ? theme.red  : r==="Médio" ? theme.amber : theme.green;
  const bgRisco  = r => r==="Alto" ? "#FEF2F2"  : r==="Médio" ? "#FEFCE8"  : "#F0FDF4";

  // Modal de tabelas
  const salvarTabela = () => {
    if (!novoNomeTbl.trim()) return;
    const configs = {
      segmentos: "segmentosTrib", tributos: "tributos", documentos: "documentosTrib", tarefas: "catalogoTarefas"
    };
    const prefixos = { segmentos: "SEG", tributos: "TRI", documentos: "DOC", tarefas: "TAR" };
    const campo = configs[modalTabelas];
    if (editIdTbl) {
      setData(prev => ({ ...prev, [campo]: (prev[campo]||[]).map(x => x.id===editIdTbl ? {...x, nome: novoNomeTbl.trim()} : x) }));
      setEditIdTbl(null);
    } else {
      const novo = { id: prefixos[modalTabelas] + Date.now().toString().slice(-6), nome: novoNomeTbl.trim() };
      setData(prev => ({ ...prev, [campo]: [...(prev[campo]||[]), novo] }));
    }
    setNovoNomeTbl("");
  };

  const removerTabela = (campo, id) => {
    if (!confirm("Remover item?")) return;
    setData(prev => ({ ...prev, [campo]: (prev[campo]||[]).filter(x => x.id !== id) }));
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="Dashboard Tributário"
        subtitle={`${prodsTrib.length} produtos tributários ativos · ${segLista.length} segmentos · ${tribLista.length} tributos`}
        action={
          <div style={{display:"flex", gap:8, alignItems:"center"}}>
            <Btn size="sm" variant="secondary" onClick={() => setModalTabelas("segmentos")}>⚙️ Segmentos</Btn>
            <Btn size="sm" variant="secondary" onClick={() => setModalTabelas("tributos")}>⚙️ Tributos</Btn>
            <Btn size="sm" variant="secondary" onClick={() => setModalTabelas("documentos")}>⚙️ Documentos</Btn>
            <Btn size="sm" variant="secondary" onClick={() => setModalTabelas("tarefas")}>⚙️ Tarefas</Btn>
            {setAtivo && (
              <Btn variant="secondary" onClick={() => setAtivo(anterior||"produtos")}>
                ← {anterior==="financeiro"?"Financeiro":"Produtos"}
              </Btn>
            )}
            <Btn
              onClick={() => setModalCreditos(true)}
              style={{background:"linear-gradient(135deg,#0ea5e9,#0284c7)",color:"#fff",border:"none",fontWeight:700,fontSize:13}}
            >
              💲 Créditos Recuperados
            </Btn>
          </div>
        }
      />

      {/* KPIs */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:20}}>
        {[
          {l:"Produtos Tributários", v:prodsTrib.length,                                                    icon:"⚖️", c:theme.goldDark},
          {l:"Para Pessoa Física",   v:prodsTrib.filter(p=>p.trib_pf).length,                              icon:"👤", c:theme.blue},
          {l:"Para Pessoa Jurídica", v:prodsTrib.filter(p=>p.trib_pj).length,                              icon:"🏢", c:theme.green},
          {l:"Segmentos Atendidos",  v:segLista.filter(s=>prodsTrib.some(p=>(p.trib_segmentos||[]).includes(s.id))).length, icon:"🏭", c:theme.gray700},
          {l:"Resultado dos Filtros",v:prodsFiltrados.length,                                               icon:"🔍", c:temFiltro?theme.amber:theme.gray400},
        ].map(k => (
          <Card key={k.l} style={{padding:"14px 18px"}}>
            <div style={{display:"flex", justifyContent:"space-between"}}>
              <p style={{fontSize:10, color:theme.gray400, textTransform:"uppercase", letterSpacing:.8}}>{k.l}</p>
              <span style={{fontSize:16}}>{k.icon}</span>
            </div>
            <p style={{fontSize:26, fontWeight:700, color:k.c, marginTop:6}}>{k.v}</p>
          </Card>
        ))}
      </div>

      {/* Sub-abas */}
      <div style={{display:"flex", gap:4, borderBottom:`2px solid ${theme.gray200}`, marginBottom:24}}>
        {[
          ["filtros",  "🎯 Diagnóstico por Perfil"],
          ["matriz",   "📊 Matriz Produto × Segmento"],
          ["mapa",     "🗺️ Mapa de Cobertura"],
        ].map(([id, label]) => (
          <button key={id} onClick={() => { setSubAba(id); if(id!=="filtros") limparFiltros(); }}
            style={{
              padding:"10px 20px", border:"none", background:"none", cursor:"pointer",
              fontSize:13, fontWeight:subAba===id ? 700 : 400,
              color:subAba===id ? theme.gray900 : theme.gray500,
              borderBottom:subAba===id ? `2px solid ${theme.gold}` : "2px solid transparent",
              marginBottom:-2, transition:"all .15s"
            }}>{label}</button>
        ))}
      </div>

      {/* ══ DIAGNÓSTICO POR PERFIL ══ */}
      {subAba === "filtros" && (
        <div style={{display:"grid", gridTemplateColumns:"280px 1fr", gap:20}}>

          {/* Painel de filtros */}
          <Card style={{padding:"18px 20px", alignSelf:"start"}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16}}>
              <p style={{fontSize:12, fontWeight:700, color:theme.gray700}}>🎯 Perfil do Cliente</p>
              {temFiltro && <Btn size="sm" variant="secondary" onClick={limparFiltros}>Limpar</Btn>}
            </div>

            {/* PF / PJ */}
            <p style={{fontSize:10, color:theme.gray400, textTransform:"uppercase", letterSpacing:.8, marginBottom:8}}>Tipo de Pessoa</p>
            <div style={{display:"flex", gap:8, marginBottom:18}}>
              {[["pf","👤 Pessoa Física", filtPF, ()=>setFiltPF(!filtPF)],
                ["pj","🏢 Pessoa Jurídica", filtPJ, ()=>setFiltPJ(!filtPJ)]].map(([k,l,sel,fn]) => (
                <label key={k} onClick={fn} style={{
                  flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:5,
                  cursor:"pointer", padding:"8px 6px", borderRadius:8, fontSize:11, textAlign:"center",
                  border:`1px solid ${sel ? theme.gold : theme.gray200}`,
                  background:sel ? `${theme.gold}18` : theme.white,
                  color:sel ? theme.goldDark : theme.gray600, fontWeight:sel ? 700 : 400
                }}>{l}</label>
              ))}
            </div>

            {/* Regime */}
            <p style={{fontSize:10, color:theme.gray400, textTransform:"uppercase", letterSpacing:.8, marginBottom:8}}>Regime Tributário</p>
            <div style={{display:"flex", flexDirection:"column", gap:6, marginBottom:18}}>
              {REGIMES_LISTA.map(r => {
                const sel = filtRegimes.includes(r);
                return (
                  <label key={r} onClick={() => toggleFiltro(filtRegimes, setFiltRegimes, r)} style={{
                    display:"flex", alignItems:"center", gap:8, cursor:"pointer",
                    padding:"8px 10px", borderRadius:7, fontSize:12,
                    border:`1px solid ${sel ? theme.gold : theme.gray100}`,
                    background:sel ? `${theme.gold}12` : theme.gray50,
                    color:sel ? theme.goldDark : theme.gray600
                  }}>
                    <div style={{
                      width:14, height:14, borderRadius:3, flexShrink:0,
                      border:`2px solid ${sel ? theme.gold : theme.gray300}`,
                      background:sel ? theme.gold : "transparent"
                    }}/>
                    {r}
                  </label>
                );
              })}
            </div>

            {/* Segmentos */}
            <p style={{fontSize:10, color:theme.gray400, textTransform:"uppercase", letterSpacing:.8, marginBottom:8}}>
              Segmento de Atuação
            </p>
            {segLista.length === 0
              ? <p style={{fontSize:11, color:theme.amber, fontStyle:"italic", marginBottom:16}}>
                  ⚠️ Nenhum segmento cadastrado. Use ⚙️ Segmentos para adicionar.
                </p>
              : <div style={{display:"flex", flexDirection:"column", gap:5, marginBottom:18, maxHeight:220, overflowY:"auto"}}>
                  {segLista.map(s => {
                    const sel = filtSegmentos.includes(s.id);
                    const qtd = prodsTrib.filter(p => (p.trib_segmentos||[]).includes(s.id)).length;
                    return (
                      <label key={s.id} onClick={() => toggleFiltro(filtSegmentos, setFiltSegmentos, s.id)} style={{
                        display:"flex", alignItems:"center", justifyContent:"space-between",
                        cursor:"pointer", padding:"7px 10px", borderRadius:7, fontSize:12,
                        border:`1px solid ${sel ? theme.blue : theme.gray100}`,
                        background:sel ? "#EEF3FD" : theme.gray50,
                        color:sel ? theme.blue : theme.gray600
                      }}>
                        <span style={{display:"flex", alignItems:"center", gap:8}}>
                          <div style={{
                            width:12, height:12, borderRadius:3, flexShrink:0,
                            border:`2px solid ${sel ? theme.blue : theme.gray300}`,
                            background:sel ? theme.blue : "transparent"
                          }}/>
                          {s.nome}
                        </span>
                        <span style={{
                          fontSize:10, fontWeight:600, minWidth:20, textAlign:"right",
                          color:sel ? theme.blue : theme.gray400
                        }}>{qtd}</span>
                      </label>
                    );
                  })}
                </div>
            }

            {/* Tributos */}
            <p style={{fontSize:10, color:theme.gray400, textTransform:"uppercase", letterSpacing:.8, marginBottom:8}}>
              Tributo Envolvido
            </p>
            {tribLista.length === 0
              ? <p style={{fontSize:11, color:theme.amber, fontStyle:"italic"}}>
                  ⚠️ Nenhum tributo cadastrado. Use ⚙️ Tributos para adicionar.
                </p>
              : <div style={{display:"flex", flexWrap:"wrap", gap:5}}>
                  {tribLista.map(t => {
                    const sel = filtTributos.includes(t.id);
                    const qtd = prodsTrib.filter(p => (p.trib_tributos||[]).includes(t.id)).length;
                    return (
                      <label key={t.id} onClick={() => toggleFiltro(filtTributos, setFiltTributos, t.id)} style={{
                        display:"flex", alignItems:"center", gap:4, cursor:"pointer",
                        padding:"5px 10px", borderRadius:20, fontSize:11,
                        border:`1px solid ${sel ? "#7c3aed" : theme.gray200}`,
                        background:sel ? "#f5f3ff" : theme.white,
                        color:sel ? "#7c3aed" : theme.gray500, fontWeight:sel ? 700 : 400
                      }}>
                        {t.nome}
                        {qtd > 0 && <span style={{fontSize:9, opacity:.7}}>({qtd})</span>}
                      </label>
                    );
                  })}
                </div>
            }
          </Card>

          {/* Resultados */}
          <div>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14}}>
              <p style={{fontSize:12, color:theme.gray500, fontWeight:600, textTransform:"uppercase", letterSpacing:.8}}>
                {prodsFiltrados.length} produto{prodsFiltrados.length!==1?"s":""} encontrado{prodsFiltrados.length!==1?"s":""}
                {temFiltro && <span style={{color:theme.amber, marginLeft:8, fontWeight:400}}>• filtros ativos</span>}
              </p>
              {temFiltro && (
                <div style={{display:"flex", gap:5, flexWrap:"wrap"}}>
                  {filtPF && <Badge color="blue">👤 PF</Badge>}
                  {filtPJ && <Badge color="green">🏢 PJ</Badge>}
                  {filtRegimes.map(r   => <Badge key={r} color="amber">{r}</Badge>)}
                  {filtSegmentos.map(s => { const sg=segLista.find(x=>x.id===s); return sg ? <Badge key={s} color="blue">{sg.nome}</Badge> : null; })}
                  {filtTributos.map(t  => { const tr=tribLista.find(x=>x.id===t); return tr ? <Badge key={t} color="gray">{tr.nome}</Badge> : null; })}
                </div>
              )}
            </div>

            {prodsFiltrados.length === 0
              ? <Card style={{padding:"40px", textAlign:"center"}}>
                  <p style={{fontSize:36, marginBottom:12}}>🔍</p>
                  <p style={{color:theme.gray400, fontSize:14, marginBottom:16}}>
                    {temFiltro
                      ? "Nenhum produto encontrado com os filtros selecionados."
                      : "Nenhum produto tributário ativo cadastrado."}
                  </p>
                  {temFiltro && <Btn variant="secondary" onClick={limparFiltros}>Limpar filtros</Btn>}
                </Card>
              : <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12}}>
                  {prodsFiltrados.map(p => {
                    const segsNomes = (p.trib_segmentos||[]).map(sid => segLista.find(x=>x.id===sid)?.nome).filter(Boolean);
                    const tribNomes = (p.trib_tributos||[]).map(tid => tribLista.find(x=>x.id===tid)?.nome).filter(Boolean);
                    return (
                      <Card key={p.id} style={{padding:"16px 18px", borderLeft:`3px solid ${corRisco(p.risco)}`}}>
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8}}>
                          <p style={{fontWeight:700, fontSize:13, color:theme.gray900, lineHeight:1.3, flex:1, marginRight:8}}>{p.nome}</p>
                          <span style={{
                            fontSize:10, padding:"2px 8px", borderRadius:4, fontWeight:700, flexShrink:0,
                            background:bgRisco(p.risco), color:corRisco(p.risco)
                          }}>{p.risco}</span>
                        </div>

                        {p.resumo && <p style={{fontSize:11, color:theme.gray500, lineHeight:1.5, marginBottom:10}}>{p.resumo}</p>}

                        <div style={{display:"flex", flexWrap:"wrap", gap:4, marginBottom:6}}>
                          {p.trib_pf && <span style={{fontSize:10, padding:"2px 8px", borderRadius:10, background:"#EEF3FD", color:theme.blue, fontWeight:600}}>👤 PF</span>}
                          {p.trib_pj && <span style={{fontSize:10, padding:"2px 8px", borderRadius:10, background:"#F0FDF4", color:theme.green, fontWeight:600}}>🏢 PJ</span>}
                          {(p.trib_regimes||[]).map(r => (
                            <span key={r} style={{fontSize:10, padding:"2px 8px", borderRadius:4, background:`${theme.gold}18`, color:theme.goldDark, fontWeight:600}}>{r}</span>
                          ))}
                        </div>

                        {segsNomes.length > 0 && (
                          <div style={{display:"flex", flexWrap:"wrap", gap:3, marginBottom:5}}>
                            {segsNomes.map(s => (
                              <span key={s} style={{fontSize:10, padding:"2px 7px", borderRadius:4, background:"#EEF3FD", color:theme.blue}}>{s}</span>
                            ))}
                          </div>
                        )}

                        {tribNomes.length > 0 && (
                          <div style={{display:"flex", flexWrap:"wrap", gap:3}}>
                            {tribNomes.map(t => (
                              <span key={t} style={{fontSize:10, padding:"2px 7px", borderRadius:4, background:"#f5f3ff", color:"#7c3aed"}}>{t}</span>
                            ))}
                          </div>
                        )}

                        {p.baseLegal && (
                          <p style={{fontSize:9, color:theme.gray300, marginTop:8, borderTop:`1px solid ${theme.gray100}`, paddingTop:6, lineHeight:1.4}}>{p.baseLegal}</p>
                        )}
                      </Card>
                    );
                  })}
                </div>
            }
          </div>
        </div>
      )}

      {/* ══ MATRIZ PRODUTO × SEGMENTO ══ */}
      {subAba === "matriz" && (
        <div>
          <p style={{fontSize:13, color:theme.gray500, marginBottom:16, lineHeight:1.6}}>
            Cada célula indica quantos produtos atendem a combinação <strong>Segmento × Tributo</strong>.
            Passe o mouse sobre uma célula para ver os produtos correspondentes.
          </p>
          {segLista.length === 0 || tribLista.length === 0
            ? <Card style={{padding:"40px", textAlign:"center"}}>
                <p style={{fontSize:36, marginBottom:12}}>⚙️</p>
                <p style={{color:theme.gray400, fontSize:14}}>
                  Cadastre Segmentos e Tributos usando os botões ⚙️ no topo da página para visualizar a matriz.
                </p>
              </Card>
            : <div style={{overflowX:"auto"}}>
                <table style={{borderCollapse:"collapse", fontSize:11, minWidth:"100%"}}>
                  <thead>
                    <tr>
                      <th style={{
                        padding:"10px 14px", textAlign:"left", background:theme.gray900,
                        color:theme.gold, fontWeight:700, fontSize:10, letterSpacing:.5,
                        whiteSpace:"nowrap", minWidth:160, borderRight:`2px solid ${theme.gray700}`
                      }}>
                        Segmento ↓ / Tributo →
                      </th>
                      {tribLista.map(t => (
                        <th key={t.id} style={{
                          padding:"10px 10px", background:theme.gray900, color:theme.cream,
                          fontWeight:600, fontSize:10, textAlign:"center", whiteSpace:"nowrap",
                          letterSpacing:.3, border:`1px solid ${theme.gray700}`
                        }}>{t.nome}</th>
                      ))}
                      <th style={{
                        padding:"10px 10px", background:`${theme.gold}33`, color:theme.goldDark,
                        fontWeight:700, fontSize:10, textAlign:"center", border:`1px solid ${theme.gray200}`
                      }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {segLista.map((seg, si) => {
                      const prodsDoSeg = prodsTrib.filter(p => (p.trib_segmentos||[]).includes(seg.id));
                      return (
                        <tr key={seg.id} style={{background:si%2===0?theme.white:theme.gray50}}>
                          <td style={{
                            padding:"9px 14px", fontWeight:600, fontSize:12, color:theme.gray800,
                            whiteSpace:"nowrap", borderRight:`2px solid ${theme.gray200}`,
                            background:si%2===0?"#f8f7f4":"#f0ede8"
                          }}>{seg.nome}</td>
                          {tribLista.map(t => {
                            const matches = prodsDoSeg.filter(p => (p.trib_tributos||[]).includes(t.id));
                            const n = matches.length;
                            return (
                              <td key={t.id}
                                title={n > 0 ? matches.map(p=>p.nome).join("\n") : ""}
                                style={{
                                  padding:"9px 10px", textAlign:"center",
                                  cursor:n>0?"pointer":"default",
                                  border:`1px solid ${theme.gray100}`,
                                  background:n>=4?"#dcfce7":n>=2?"#fef9c3":n>=1?"#eff6ff":theme.white,
                                  transition:"opacity .15s"
                                }}
                                onMouseEnter={e=>{if(n>0)e.currentTarget.style.opacity=".7";}}
                                onMouseLeave={e=>e.currentTarget.style.opacity="1"}
                              >
                                {n > 0
                                  ? <span style={{fontWeight:700, fontSize:13, color:n>=4?theme.green:n>=2?theme.amber:theme.blue}}>{n}</span>
                                  : <span style={{color:theme.gray200, fontSize:10}}>—</span>
                                }
                              </td>
                            );
                          })}
                          <td style={{
                            padding:"9px 10px", textAlign:"center", fontWeight:700, fontSize:14,
                            color:prodsDoSeg.length>0?theme.goldDark:theme.gray300,
                            background:`${theme.gold}11`, border:`1px solid ${theme.gray200}`
                          }}>{prodsDoSeg.length || "—"}</td>
                        </tr>
                      );
                    })}
                    <tr style={{background:theme.gray900, borderTop:`2px solid ${theme.gray700}`}}>
                      <td style={{padding:"9px 14px", color:theme.gold, fontWeight:700, fontSize:11}}>Total por Tributo</td>
                      {tribLista.map(t => {
                        const n = prodsTrib.filter(p => (p.trib_tributos||[]).includes(t.id)).length;
                        return (
                          <td key={t.id} style={{
                            padding:"9px 10px", textAlign:"center",
                            color:n>0?theme.cream:theme.gray600, fontWeight:700, fontSize:13
                          }}>{n || "—"}</td>
                        );
                      })}
                      <td style={{padding:"9px 10px", textAlign:"center", color:theme.gold, fontWeight:700, fontSize:14}}>
                        {prodsTrib.length}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Legenda */}
                <div style={{display:"flex", gap:20, marginTop:14, padding:"10px 16px", background:theme.gray50, borderRadius:8, fontSize:11, color:theme.gray500}}>
                  {[[theme.green,"#dcfce7","4+ produtos"],[theme.amber,"#fef9c3","2-3 produtos"],[theme.blue,"#eff6ff","1 produto"],["gray",theme.white,"sem produto"]].map(([cor,bg,txt])=>(
                    <span key={txt} style={{display:"flex", alignItems:"center", gap:6}}>
                      <span style={{width:16, height:16, borderRadius:3, background:bg, border:`1px solid ${cor}44`, display:"inline-block"}}/>
                      {txt}
                    </span>
                  ))}
                </div>
              </div>
          }
        </div>
      )}

      {/* ══ MAPA DE COBERTURA ══ */}
      {subAba === "mapa" && (
        <div>
          <p style={{fontSize:13, color:theme.gray500, marginBottom:20, lineHeight:1.6}}>
            Para cada segmento de mercado: quantos produtos estão disponíveis, quais tributos são cobertos
            e onde há oportunidades de expansão do portfólio.
          </p>
          {segLista.length === 0
            ? <Card style={{padding:"40px", textAlign:"center"}}>
                <p style={{fontSize:36, marginBottom:12}}>⚙️</p>
                <p style={{color:theme.gray400, fontSize:14}}>Cadastre segmentos via ⚙️ Segmentos para visualizar o mapa.</p>
              </Card>
            : <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:14}}>
                {segLista.map(seg => {
                  const prods    = prodsTrib.filter(p => (p.trib_segmentos||[]).includes(seg.id));
                  const pfCount  = prods.filter(p => p.trib_pf).length;
                  const pjCount  = prods.filter(p => p.trib_pj).length;
                  const tribsCob = [...new Set(prods.flatMap(p => p.trib_tributos||[]))];
                  const nivel    = prods.length === 0 ? "Sem cobertura"
                                 : prods.length <= 2  ? "Cobertura baixa"
                                 : prods.length <= 5  ? "Cobertura média"
                                 : "Cobertura alta";
                  const corCob   = prods.length === 0 ? theme.gray300
                                 : prods.length <= 2  ? theme.red
                                 : prods.length <= 5  ? theme.amber
                                 : theme.green;
                  return (
                    <Card key={seg.id} style={{padding:"18px 20px", borderTop:`3px solid ${corCob}`}}>
                      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10}}>
                        <p style={{fontWeight:700, fontSize:14, color:theme.gray900}}>{seg.nome}</p>
                        <span style={{
                          fontSize:10, padding:"3px 10px", borderRadius:4, fontWeight:700,
                          background:corCob+"22", color:corCob, whiteSpace:"nowrap"
                        }}>{nivel}</span>
                      </div>

                      <div style={{display:"flex", gap:14, marginBottom:12, paddingBottom:12, borderBottom:`1px solid ${theme.gray100}`}}>
                        {[
                          {v:prods.length,   l:"Produtos",  c:corCob},
                          {v:pfCount,        l:"PF",        c:theme.blue},
                          {v:pjCount,        l:"PJ",        c:theme.green},
                          {v:tribsCob.length,l:"Tributos",  c:"#7c3aed"},
                        ].map(k => (
                          <div key={k.l} style={{textAlign:"center", flex:1}}>
                            <p style={{fontSize:22, fontWeight:700, color:k.c, lineHeight:1}}>{k.v}</p>
                            <p style={{fontSize:9, color:theme.gray400, textTransform:"uppercase", marginTop:3}}>{k.l}</p>
                          </div>
                        ))}
                      </div>

                      {prods.length > 0
                        ? <div style={{display:"flex", flexDirection:"column", gap:4}}>
                            {prods.slice(0, 3).map(p => (
                              <div key={p.id} style={{
                                display:"flex", justifyContent:"space-between", alignItems:"center",
                                padding:"5px 8px", borderRadius:6, background:theme.gray50
                              }}>
                                <span style={{fontSize:11, color:theme.gray700, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{p.nome}</span>
                                <span style={{fontSize:9, color:corRisco(p.risco), fontWeight:700, marginLeft:6, flexShrink:0}}>{p.risco}</span>
                              </div>
                            ))}
                            {prods.length > 3 && (
                              <p style={{fontSize:10, color:theme.gray400, textAlign:"center", marginTop:4}}>
                                +{prods.length - 3} produto{prods.length-3>1?"s":""}
                              </p>
                            )}
                          </div>
                        : <div style={{textAlign:"center", padding:"12px 0"}}>
                            <p style={{fontSize:12, color:theme.gray400}}>Nenhum produto para este segmento</p>
                            <p style={{fontSize:11, color:theme.amber, marginTop:4}}>⚠️ Oportunidade de expansão</p>
                          </div>
                      }
                    </Card>
                  );
                })}
              </div>
          }
        </div>
      )}

      {/* Modal de tabelas */}
      {modalTabelas && (() => {
        const cfgs = {
          segmentos: { titulo:"Segmentos de Atuação",     campo:"segmentosTrib",    cor:theme.blue   },
          tributos:  { titulo:"Tributos",                  campo:"tributos",          cor:"#7c3aed"    },
          documentos:{ titulo:"Documentos Necessários",    campo:"documentosTrib",   cor:theme.green  },
          tarefas:   { titulo:"Catálogo de Tarefas",       campo:"catalogoTarefas",  cor:theme.amber  },
        };
        const cfg   = cfgs[modalTabelas];
        const lista = data[cfg.campo] || [];
        return (
          <Modal open={true}
            onClose={() => { setModalTabelas(null); setNovoNomeTbl(""); setEditIdTbl(null); }}
            title={`⚙️ ${cfg.titulo}`} width={500}>
            <div style={{display:"flex", gap:8, marginBottom:16}}>
              <Input value={novoNomeTbl} onChange={setNovoNomeTbl}
                placeholder={`Nome da ${modalTabelas==="tarefas"?"tarefa":modalTabelas==="segmentos"?"segmento":modalTabelas==="tributos"?"tributo":"documento"}...`} />
              <Btn onClick={salvarTabela}>{editIdTbl ? "Salvar" : "+ Adicionar"}</Btn>
              {editIdTbl && <Btn variant="secondary" onClick={()=>{setEditIdTbl(null);setNovoNomeTbl("");}}>✕</Btn>}
            </div>
            <div style={{display:"flex", flexDirection:"column", gap:6, maxHeight:380, overflowY:"auto"}}>
              {lista.length === 0
                ? <p style={{fontSize:13, color:theme.gray400, textAlign:"center", padding:"20px 0"}}>Nenhum item cadastrado.</p>
                : lista.map((item, i) => (
                    <div key={item.id} style={{
                      display:"flex", alignItems:"center", gap:8, padding:"10px 14px",
                      borderRadius:8, background:i%2===0?theme.white:theme.gray50,
                      border:`1px solid ${theme.gray100}`
                    }}>
                      <div style={{width:8, height:8, borderRadius:"50%", background:cfg.cor, flexShrink:0}}/>
                      <span style={{flex:1, fontSize:13, color:theme.gray800}}>{item.nome}</span>
                      <span style={{fontSize:10, color:theme.gray400, marginRight:4}}>{item.id}</span>
                      <button onClick={() => { setEditIdTbl(item.id); setNovoNomeTbl(item.nome); }}
                        style={{background:"none", border:"none", cursor:"pointer", color:theme.gray400, fontSize:14, padding:"2px 4px"}}>✏️</button>
                      <button onClick={() => removerTabela(cfg.campo, item.id)}
                        style={{background:"none", border:"none", cursor:"pointer", color:theme.red, fontSize:16, padding:"2px 4px"}}>×</button>
                    </div>
                  ))
              }
            </div>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center",
              paddingTop:14, borderTop:`1px solid ${theme.gray100}`, marginTop:14}}>
              <p style={{fontSize:11, color:theme.gray400}}>{lista.length} item{lista.length!==1?"s":""} cadastrado{lista.length!==1?"s":""}</p>
              <Btn onClick={() => { setModalTabelas(null); setNovoNomeTbl(""); setEditIdTbl(null); }}>Fechar</Btn>
            </div>
          </Modal>
        );
      })()}

      {modalCreditos && (
        <ModalCreditosRecuperados
          data={data}
          produtos={produtos}
          tribLista={tribLista}
          onClose={() => setModalCreditos(false)}
        />
      )}

    </div>
  );
}


// ============================================================
// MÓDULO: EQUIPE
// ============================================================
// Componente de select com botão de gerenciar - definido fora do ModEquipe
function SelectComGerenciar({ label, value, onChange, lista, onGerenciar, required }) {
  return (
    <FormField label={label} required={required}>
      <div style={{ display: "flex", gap: 6 }}>
        <div style={{ flex: 1 }}>
          <Select value={value} onChange={onChange} options={lista} placeholder="Selecione..." />
        </div>
        <button
          type="button"
          onClick={onGerenciar}
          title="Gerenciar lista"
          style={{
            padding: "0 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7,
            background: theme.gray50, color: theme.gray600, cursor: "pointer",
            fontSize: 13, flexShrink: 0, display: "flex", alignItems: "center", gap: 4
          }}
        >⚙️</button>
      </div>
    </FormField>
  );
}

// Modal de gerenciamento de lista simples (Vínculos ou Funções)
function ModalGerenciarLista({ open, titulo, lista, onClose, onSalvar }) {
  const [novoItem, setNovoItem] = useState("");
  const [editIdx, setEditIdx] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [listaLocal, setListaLocal] = useState([]);

  // Sincroniza lista local quando o modal abre
  React.useEffect(() => {
    if (open) { setListaLocal([...lista]); setNovoItem(""); setEditIdx(null); }
  }, [open]);

  const adicionar = () => {
    const t = novoItem.trim();
    if (!t) return;
    if (listaLocal.includes(t)) return alert("Item já existe.");
    const nova = [...listaLocal, t];
    setListaLocal(nova);
    onSalvar(nova);
    setNovoItem("");
  };

  const salvarEdicao = (idx) => {
    const t = editVal.trim();
    if (!t) return;
    const nova = listaLocal.map((v, i) => i === idx ? t : v);
    setListaLocal(nova);
    onSalvar(nova);
    setEditIdx(null);
  };

  const remover = (idx) => {
    if (!confirm("Remover este item?")) return;
    const nova = listaLocal.filter((_, i) => i !== idx);
    setListaLocal(nova);
    onSalvar(nova);
  };

  return (
    <Modal open={open} onClose={onClose} title={`Gerenciar ${titulo}`} width={420}>
      {open && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            <input
              value={novoItem}
              onChange={e => setNovoItem(e.target.value)}
              onKeyDown={e => e.key === "Enter" && adicionar()}
              placeholder={`Novo item...`}
              style={{ flex: 1, padding: "9px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none", fontFamily: "Jost, sans-serif" }}
              onFocus={e => e.target.style.borderColor = theme.gold}
              onBlur={e => e.target.style.borderColor = theme.gray200}
            />
            <Btn onClick={adicionar}>+ Adicionar</Btn>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 340, overflowY: "auto" }}>
            {listaLocal.length === 0 && (
              <p style={{ color: theme.gray400, fontSize: 13, textAlign: "center", padding: 20 }}>Nenhum item cadastrado.</p>
            )}
            {listaLocal.map((item, idx) => (
              <div key={idx} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                borderRadius: 7, border: `1px solid ${theme.gray100}`,
                background: idx % 2 === 0 ? theme.white : theme.gray50
              }}>
                <span style={{
                  width: 24, height: 24, borderRadius: "50%", background: theme.gray900,
                  color: theme.gold, display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 700, flexShrink: 0
                }}>{idx + 1}</span>

                {editIdx === idx ? (
                  <>
                    <input
                      value={editVal}
                      onChange={e => setEditVal(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") salvarEdicao(idx); if (e.key === "Escape") setEditIdx(null); }}
                      autoFocus
                      style={{ flex: 1, padding: "5px 8px", border: `1px solid ${theme.gold}`, borderRadius: 5, fontSize: 13, outline: "none", fontFamily: "Jost, sans-serif" }}
                    />
                    <button onClick={() => salvarEdicao(idx)}
                      style={{ padding: "4px 10px", borderRadius: 5, border: "none", background: theme.gold, color: theme.gray900, fontSize: 12, cursor: "pointer", fontWeight: 600 }}>✓</button>
                    <button onClick={() => setEditIdx(null)}
                      style={{ padding: "4px 8px", borderRadius: 5, border: `1px solid ${theme.gray200}`, background: "transparent", fontSize: 12, cursor: "pointer", color: theme.gray500 }}>✕</button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: 13, color: theme.gray800 }}>{item}</span>
                    <button onClick={() => { setEditIdx(idx); setEditVal(item); }}
                      style={{ padding: "4px 10px", borderRadius: 5, border: `1px solid ${theme.gray200}`, background: "transparent", fontSize: 12, cursor: "pointer", color: theme.gray600 }}>✏️</button>
                    <button onClick={() => remover(idx)}
                      style={{ padding: "4px 10px", borderRadius: 5, border: `1px solid ${theme.red}33`, background: "transparent", fontSize: 12, cursor: "pointer", color: theme.red }}>✕</button>
                  </>
                )}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 16, borderTop: `1px solid ${theme.gray100}`, marginTop: 16 }}>
            <Btn onClick={onClose}>Fechar</Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

function ModEquipe({ data, setData }) {
  const isViz = useIsVisualizador();
  const { equipe } = data;
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filtroAtivo, setFiltroAtivo] = useState("Ativo");
  const [gerenciar, setGerenciar] = useState(null); // { chave, titulo }

  const vinculos = data.vinculos && data.vinculos.length ? data.vinculos : VINCULOS;
  const funcoes  = data.funcoes  && data.funcoes.length  ? data.funcoes  : FUNCOES;
  const areas    = data.areas    && data.areas.length    ? data.areas    : AREAS_INICIAIS;

  const filtrados = equipe.filter(e =>
    !filtroAtivo || (filtroAtivo === "Ativo" ? e.ativo !== false : e.ativo === false)
  ).sort((a,b)=>(a.nome||"").localeCompare(b.nome||"","pt-BR"));

  const EMPTY = { id: "", nome: "", vinculo: "", funcao: "", especialidade: "", oab: "", email: "", whatsapp: "", whatsappDDI: "55", ativo: true, obs: "" };

  const openNew  = () => { setEditItem({ ...EMPTY }); setModalOpen(true); };
  const openEdit = e  => { setEditItem({ ...e, whatsappDDI: e.whatsappDDI || "55" }); setModalOpen(true); };

  const save = () => {
    if (!editItem.nome) return alert("Nome é obrigatório.");
    const id = editItem.id || "EQ" + String(Date.now()).slice(-6);
    const item = { ...editItem, id };
    setData(prev => {
      const lista = item.id && prev.equipe.find(e => e.id === item.id)
        ? prev.equipe.map(e => e.id === item.id ? item : e)
        : [...prev.equipe, item];
      return { ...prev, equipe: lista };
    });
    setModalOpen(false);
  };

  const remove = id => {
    if (!confirm("Remover membro?")) return;
    setData(prev => ({ ...prev, equipe: prev.equipe.filter(e => e.id !== id) }));
  };

  const salvarLista = (chave, nova) => {
    setData(prev => ({ ...prev, [chave]: nova }));
  };

  const listaGerenciar = gerenciar?.chave === "vinculos" ? vinculos : gerenciar?.chave === "funcoes" ? funcoes : [];

  return (
    <div className="fade-in">
      <PageHeader
        title="Equipe"
        subtitle="Membros e colaboradores do escritório"
        action={!isViz && <Btn onClick={openNew}>+ Novo Membro</Btn>}
      />

      <Card style={{ padding: "14px 20px", marginBottom: 20 }}>
        <select value={filtroAtivo} onChange={e => setFiltroAtivo(e.target.value)}
          style={{ padding: "8px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none" }}>
          <option value="">Todos</option>
          <option value="Ativo">Ativos</option>
          <option value="Inativo">Inativos</option>
        </select>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
        {filtrados.length === 0 ? (
          <div style={{ gridColumn: "1/-1" }}>
            <Card><EmptyState icon="👥" title="Nenhum membro encontrado" subtitle="Cadastre a equipe do escritório." /></Card>
          </div>
        ) : filtrados.map(e => (
          <Card key={e.id} style={{ padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
              <div style={{
                width: 48, height: 48, borderRadius: "50%",
                background: `linear-gradient(135deg, ${theme.gray800}, ${theme.gray600})`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
              }}>
                <span className="serif" style={{ color: theme.gold, fontSize: 18, fontWeight: 600 }}>
                  {e.nome.split(" ").map(n => n[0]).slice(0, 2).join("")}
                </span>
              </div>
              <div>
                <p style={{ fontWeight: 600, fontSize: 14, color: theme.gray900 }}>{e.nome}</p>
                <p style={{ fontSize: 12, color: theme.gray500 }}>{e.funcao}</p>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {e.vinculo      && <Badge color="gold">{e.vinculo}</Badge>}
              {e.especialidade && <Badge color="blue">{e.especialidade}</Badge>}
              <Badge color={e.ativo !== false ? "green" : "gray"}>{e.ativo !== false ? "Ativo" : "Inativo"}</Badge>
            </div>
            <div style={{ fontSize: 12, color: theme.gray500, marginBottom: 12 }}>
              {e.email    && <div>📧 {e.email}</div>}
              {e.whatsapp && <div style={{ marginTop: 3 }}>📱 +{e.whatsappDDI||"55"} {e.whatsapp}</div>}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 12, borderTop: `1px solid ${theme.gray100}` }}>
              {!isViz && <Btn size="sm" variant="secondary" onClick={() => openEdit(e)}>Editar</Btn>}
              {!isViz && <Btn size="sm" variant="danger"    onClick={() => remove(e.id)}>Remover</Btn>}
            </div>
          </Card>
        ))}
      </div>

      {/* Modal cadastro/edição */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editItem?.id ? "Editar Membro" : "Novo Membro"} width={580}>
        {editItem && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Nome completo" required>
                  <Input value={editItem.nome} onChange={v => setEditItem({ ...editItem, nome: v })} placeholder="Nome do membro" />
                </FormField>
              </div>
              <SelectComGerenciar
                label="Vínculo" value={editItem.vinculo}
                onChange={v => setEditItem({ ...editItem, vinculo: v })}
                lista={vinculos}
                onGerenciar={() => setGerenciar({ chave: "vinculos", titulo: "Vínculos" })}
              />
              <SelectComGerenciar
                label="Função" value={editItem.funcao}
                onChange={v => setEditItem({ ...editItem, funcao: v })}
                lista={funcoes}
                onGerenciar={() => setGerenciar({ chave: "funcoes", titulo: "Funções" })}
              />
              <FormField label="Especialidade (Área Jurídica)">
                <Select
                  value={editItem.especialidade}
                  onChange={v => setEditItem({ ...editItem, especialidade: v })}
                  options={areas}
                  placeholder="Selecione a área..."
                />
              </FormField>
              <FormField label="OAB">
                <Input value={editItem.oab||""} onChange={v => setEditItem({ ...editItem, oab: v })} placeholder="Ex: OAB/SP 398.875" />
              </FormField>
              <FormField label="Status">
                <Select value={editItem.ativo !== false ? "Ativo" : "Inativo"} onChange={v => setEditItem({ ...editItem, ativo: v === "Ativo" })} options={["Ativo", "Inativo"]} />
              </FormField>
              <FormField label="E-mail">
                <Input type="email" value={editItem.email} onChange={v => setEditItem({ ...editItem, email: v })} placeholder="email@escritorio.adv.br" />
              </FormField>
              <CampoWhatsApp
                label="WhatsApp / Telefone"
                ddi={editItem.whatsappDDI}
                numero={editItem.whatsapp}
                onChangeDDI={v=>setEditItem({...editItem, whatsappDDI: v})}
                onChangeNumero={v=>setEditItem({...editItem, whatsapp: v})}
              />
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Observações">
                  <Textarea value={editItem.obs} onChange={v => setEditItem({ ...editItem, obs: v })} placeholder="Notas sobre o membro..." />
                </FormField>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 16, borderTop: `1px solid ${theme.gray100}` }}>
              <Btn variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Btn>
              {!isViz && <Btn onClick={save}>Salvar Membro</Btn>}
            </div>
          </>
        )}
      </Modal>

      {/* Modal gerenciar lista */}
      <ModalGerenciarLista
        open={!!gerenciar}
        titulo={gerenciar?.titulo || ""}
        lista={listaGerenciar}
        onClose={() => setGerenciar(null)}
        onSalvar={nova => salvarLista(gerenciar.chave, nova)}
      />
    </div>
  );
}


function ModPOPs({ data, setData }) {
  const isViz = useIsVisualizador();
  const { pops } = data;
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filtroGrupo, setFiltroGrupo] = useState("");
  const [aba, setAba] = useState("pops"); // "pops" | "rcc"

  const filtrados = pops.filter(p => !filtroGrupo || p.grupo === filtroGrupo).sort((a,b)=>(a.titulo||a.nome||"").localeCompare(b.titulo||b.nome||"","pt-BR"));

  const EMPTY = { id: "", nome: "", grupo: "", subgrupo: "", dataConfeccao: "", dataRevisao: "", versao: "1.0", status: "Ativo", link: "", obs: "", produtos: [], responsavelCargo: "" };

  const openNew = () => { setEditItem({ ...EMPTY }); setModalOpen(true); };
  const openEdit = p => { setEditItem({ ...EMPTY, ...p }); setModalOpen(true); };

  // Importa o catálogo oficial de 33 POPs (POP - ESTRUTURA.pdf) — só insere os que ainda não existem (por ID)
  const importarCatalogoOficial = () => {
    const idsExistentes = new Set(pops.map(p=>p.id));
    const novos = POPS_CATALOGO_OFICIAL.filter(p=>!idsExistentes.has(p.id));
    if (novos.length === 0) { alert("Todos os POPs do catálogo oficial já estão cadastrados."); return; }
    if (!confirm(`Importar ${novos.length} POP(s) do catálogo oficial que ainda não existem no sistema?`)) return;
    setData(prev => ({...prev, pops: [...prev.pops, ...novos]}));
    alert(`${novos.length} POP(s) importado(s) com sucesso.`);
  };

  const save = () => {
    if (!editItem.id || !editItem.nome) return alert("ID e Nome são obrigatórios.");
    const item = { ...editItem };
    setData(prev => { const lista = prev.pops.find(p => p.id === item.id) ? prev.pops.map(p => p.id === item.id ? item : p) : [...prev.pops, item]; return {...prev, pops: lista}; });
    setModalOpen(false);
  };

  const remove = id => { if (!confirm("Remover POP?")) return; setData(prev => ({...prev, pops: pops.filter(p => p.id !== id)})); };

  const vencendoHoje = (dataRevisao) => {
    if (!dataRevisao) return false;
    const diff = (new Date(dataRevisao) - new Date()) / (1000 * 60 * 60 * 24);
    return diff <= 30;
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="POPs"
        subtitle="Biblioteca de Procedimentos Operacionais Padrão"
        action={
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {!isViz && <Btn variant="secondary" onClick={importarCatalogoOficial}>📥 Importar Catálogo Oficial</Btn>}
            <PainelImportExport
              modulo="POPs"
              totalRegistros={pops.length}
              onExportCSV={()=>exportarCSV("pops",
                ["ID","Nome","Grupo","Subgrupo","Versão","Data Confecção","Data Revisão","Status","Link","Observações"],
                pops.map(p=>[p.id,p.nome,p.grupo||"—",p.subgrupo||"—",p.versao||"1.0",p.dataConfeccao||"—",p.dataRevisao||"—",p.status||"Ativo",p.link||"—",p.obs||"—"])
              )}
              onExportJSON={()=>exportarJSON("pops_backup", pops)}
              onImportCSV={(arquivo, onOk, onErr)=>{
                importarCSV(arquivo, (registros)=>{
                  const novos = registros.map(r=>({
                    id: r.ID||r.id||("POP"+String(Date.now()).slice(-5)+Math.random().toString(36).slice(-2)),
                    nome: r.Nome||r.nome||"Sem nome",
                    grupo: r.Grupo||r.grupo||"",
                    subgrupo: r.Subgrupo||r.subgrupo||"",
                    versao: r["Versão"]||r.versao||"1.0",
                    dataConfeccao: r["Data Confecção"]||r.dataConfeccao||"",
                    dataRevisao: r["Data Revisão"]||r.dataRevisao||"",
                    status: r.Status||r.status||"Ativo",
                    link: r.Link||r.link||"",
                    obs: r["Observações"]||r.obs||"",
                    produtos:[]
                  }));
                  const ids = new Set(pops.map(x=>x.id));
                  const inserir = novos.filter(n=>!ids.has(n.id));
                  setData(prev => ({...prev, pops: [...pops,...inserir]}));
                  onOk(inserir.length);
                }, onErr);
              }}
              onImportJSON={(arquivo, onOk, onErr)=>{
                importarJSON(arquivo, (lista)=>{
                  const ids = new Set(pops.map(x=>x.id));
                  const inserir = lista.filter(n=>n.nome&&!ids.has(n.id));
                  setData(prev => ({...prev, pops: [...pops,...inserir]}));
                  onOk(inserir.length);
                }, onErr);
              }}
            />
            {!isViz && <Btn onClick={openNew}>+ Novo POP</Btn>}
          </div>
        }
      />

      <div style={{display:"flex",gap:4,background:theme.gray100,borderRadius:8,padding:3,marginBottom:20,width:"fit-content"}}>
        {[["pops",`📋 Lista de POPs (${pops.length})`],["rcc",`🗂️ Cargos (RCC)`]].map(([v,l])=>(
          <button key={v} onClick={()=>setAba(v)} style={{
            padding:"7px 16px",borderRadius:6,border:"none",cursor:"pointer",fontSize:13,
            fontWeight:aba===v?700:400,
            background:aba===v?theme.white:"transparent",
            color:aba===v?theme.gray900:theme.gray400,
            boxShadow:aba===v?"0 1px 3px rgba(0,0,0,.1)":"none"}}>
            {l}
          </button>
        ))}
      </div>

      {aba==="rcc" && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:16}}>
          {CARGOS_RCC.map(c=>(
            <Card key={c.id}>
              <p style={{fontSize:15,fontWeight:700,color:theme.gray900,marginBottom:6}}>{c.nome}</p>
              <p style={{fontSize:12,color:theme.gray600,marginBottom:12,lineHeight:1.5}}>{c.missao}</p>
              {c.responsabilidades.length>0 && (
                <>
                  <p style={{fontSize:10,fontWeight:700,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Responsabilidades-Chave (RCC)</p>
                  <ul style={{margin:"0 0 12px",paddingLeft:18,fontSize:12,color:theme.gray700}}>
                    {c.responsabilidades.map((r,i)=><li key={i} style={{marginBottom:2}}>{r}</li>)}
                  </ul>
                </>
              )}
              {c.indicadores.length>0 && (
                <>
                  <p style={{fontSize:10,fontWeight:700,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Indicadores</p>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                    {c.indicadores.map((k,i)=><Badge key={i} color="amber">{k}</Badge>)}
                  </div>
                </>
              )}
            </Card>
          ))}
        </div>
      )}

      {aba==="pops" && <>
      <Card style={{ padding: "14px 20px", marginBottom: 20 }}>
        <select value={filtroGrupo} onChange={e => setFiltroGrupo(e.target.value)}
          style={{ padding: "8px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none" }}>
          <option value="">Todos os grupos</option>
          {GRUPOS_POP.map(g => <option key={g}>{g}</option>)}
        </select>
      </Card>

      <Card>
        {filtrados.length === 0 ? (
          <EmptyState icon="📋" title="Nenhum POP cadastrado" subtitle="Adicione os procedimentos do escritório." />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.gray100}` }}>
                  {["ID", "Nome", "Grupo", "Subgrupo", "Cargo Responsável", "Produtos", "Versão", "Revisão", "Status", "Ações"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 10, color: theme.gray400, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtrados.map((p, i) => (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${theme.gray100}`, background: i % 2 === 0 ? theme.white : theme.gray50 }}>
                    <td style={{ padding: "12px 16px" }}>
                      <code style={{ fontSize: 12, background: theme.gray100, padding: "2px 6px", borderRadius: 4, color: theme.gray700 }}>{p.id}</code>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ fontWeight: 500, fontSize: 13, color: theme.gray900 }}>{p.nome}</div>
                      {p.link && <a href={p.link} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: theme.blue }}>Ver POP ↗</a>}
                    </td>
                    <td style={{ padding: "12px 16px" }}><Badge color="gray">{p.grupo}</Badge></td>
                    <td style={{ padding: "12px 16px" }}><Badge color="blue">{p.subgrupo}</Badge></td>
                    <td style={{ padding: "12px 16px", fontSize: 12, color: theme.gray700 }}>
                      {CARGOS_RCC.find(c=>c.id===p.responsavelCargo)?.nome || <span style={{color:theme.gray300}}>—</span>}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      {(p.produtos||[]).length===0
                        ? <span style={{fontSize:11,color:theme.gray300}}>—</span>
                        : <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                            {(p.produtos||[]).map(pid=>{
                              const prod=data.produtos?.find(x=>x.id===pid);
                              return prod?<Badge key={pid} color="amber">{prod.nome}</Badge>:null;
                            })}
                          </div>}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 13, color: theme.gray600 }}>v{p.versao}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ fontSize: 12, color: vencendoHoje(p.dataRevisao) ? theme.red : theme.gray600 }}>
                        {p.dataRevisao || "-"}
                        {vencendoHoje(p.dataRevisao) && " ⚠️"}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px" }}><Badge color={p.status === "Ativo" ? "green" : "gray"}>{p.status}</Badge></td>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {!isViz && <Btn size="sm" variant="secondary" onClick={() => openEdit(p)}>Editar</Btn>}
                        {!isViz && <Btn size="sm" variant="danger" onClick={() => remove(p.id)}>×</Btn>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      </>}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editItem?.nome ? "Editar POP" : "Novo POP"} width={620}>
        {editItem && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
              <FormField label="ID do POP" required hint="Ex: PREV-01, COM-02, TRIB-03">
                <Input value={editItem.id} onChange={v => setEditItem({ ...editItem, id: v.toUpperCase() })} placeholder="PREV-01" />
              </FormField>
              <FormField label="Versão">
                <Input value={editItem.versao} onChange={v => setEditItem({ ...editItem, versao: v })} placeholder="1.0" />
              </FormField>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Nome do POP" required>
                  <Input value={editItem.nome} onChange={v => setEditItem({ ...editItem, nome: v })} placeholder="Nome completo do procedimento" />
                </FormField>
              </div>
              <FormField label="Grupo">
                <Select value={editItem.grupo} onChange={v => setEditItem({ ...editItem, grupo: v, subgrupo: "" })} options={GRUPOS_POP} placeholder="Selecione..." />
              </FormField>
              <FormField label="Subgrupo">
                <Select value={editItem.subgrupo} onChange={v => setEditItem({ ...editItem, subgrupo: v })}
                  options={editItem.grupo ? SUBGRUPOS_POP[editItem.grupo] || [] : []} placeholder="Selecione o grupo primeiro..." />
              </FormField>
              <FormField label="Data de Confecção">
                <Input type="date" value={editItem.dataConfeccao} onChange={v => setEditItem({ ...editItem, dataConfeccao: v })} />
              </FormField>
              <FormField label="Data de Revisão">
                <Input type="date" value={editItem.dataRevisao} onChange={v => setEditItem({ ...editItem, dataRevisao: v })} />
              </FormField>
              <FormField label="Status">
                <Select value={editItem.status} onChange={v => setEditItem({ ...editItem, status: v })} options={["Ativo", "Pausado"]} />
              </FormField>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Cargo Responsável (RCC)" hint="Vincula este POP ao cargo que executa/responde por ele — ver aba Cargos (RCC)">
                  <Select value={editItem.responsavelCargo} onChange={v => setEditItem({ ...editItem, responsavelCargo: v })}
                    options={CARGOS_RCC.map(c=>({value:c.id,label:c.nome}))} placeholder="Selecione o cargo..." />
                </FormField>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Produtos Relacionados" hint="Selecione um ou mais produtos vinculados a este POP">
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "10px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 8, minHeight: 44, background: theme.white }}>
                    {(data.produtos||[]).length === 0
                      ? <span style={{ fontSize: 12, color: theme.gray400 }}>Nenhum produto cadastrado no sistema.</span>
                      : (data.produtos||[]).map(prod => {
                          const sel = (editItem.produtos||[]).includes(prod.id);
                          return (
                            <div key={prod.id}
                              onClick={() => {
                                const atual = editItem.produtos || [];
                                const novo = sel ? atual.filter(x => x !== prod.id) : [...atual, prod.id];
                                setEditItem({ ...editItem, produtos: novo });
                              }}
                              style={{
                                padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                                fontWeight: sel ? 600 : 400,
                                border: `2px solid ${sel ? theme.gold : theme.gray200}`,
                                background: sel ? theme.gold + "22" : theme.gray50,
                                color: sel ? theme.amber : theme.gray600,
                                userSelect: "none", transition: "all .15s"
                              }}>
                              {sel ? "✓ " : ""}{prod.nome}
                            </div>
                          );
                        })
                    }
                  </div>
                  {(editItem.produtos||[]).length > 0 && (
                    <div style={{ marginTop: 6, fontSize: 11, color: theme.gray500 }}>
                      {(editItem.produtos||[]).length} produto(s) selecionado(s)
                      <span onClick={() => setEditItem({ ...editItem, produtos: [] })}
                        style={{ marginLeft: 10, color: theme.red, cursor: "pointer" }}>Limpar</span>
                    </div>
                  )}
                </FormField>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Link do documento">
                  <Input value={editItem.link} onChange={v => setEditItem({ ...editItem, link: v })} placeholder="https://drive.google.com/..." />
                </FormField>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Observações">
                  <Textarea value={editItem.obs} onChange={v => setEditItem({ ...editItem, obs: v })} placeholder="Notas sobre o POP..." />
                </FormField>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 16, borderTop: `1px solid ${theme.gray100}` }}>
              <Btn variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Btn>
              {!isViz && <Btn onClick={save}>Salvar POP</Btn>}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

// ============================================================
// MÓDULO: PARCERIAS COMERCIAIS
// ============================================================
function PainelRepasses({ parcerias, contratos, clientes, despesas, setData, theme, Card, Badge, Btn }) {
  const isViz = useIsVisualizador();
  const _d = new Date();
  const mesAtualPadr = _d.getFullYear()+'-'+String(_d.getMonth()+1).padStart(2,'0');
  const [filtroParcRep, setFiltroParcRep] = React.useState("");
  const [periodoRepIni, setPeriodoRepIni] = React.useState(mesAtualPadr+"-01");
  const [periodoRepFim, setPeriodoRepFim] = React.useState(_d.toISOString().slice(0,10));

  const despComissoes = (despesas||[]).filter(d=>{
    if(d.categoria!=="Comissão de Parceria") return false;
    if(filtroParcRep && !d.descricao?.includes(parcerias.find(p=>p.id===filtroParcRep)?.nome||"___")) return false;
    const dData = d.data||"";
    if(periodoRepIni && dData < periodoRepIni) return false;
    if(periodoRepFim && dData > periodoRepFim) return false;
    return true;
  });
  const fmtR = v => "R$ "+(parseFloat(v)||0).toLocaleString("pt-BR",{minimumFractionDigits:2});
  const fmtD = d => d ? new Date(d+"T12:00:00").toLocaleDateString("pt-BR") : "—";
  const totalPendente = despComissoes.filter(d=>d.status==="A Pagar").reduce((s,d)=>s+(parseFloat(d.valor)||0),0);
  const totalPago     = despComissoes.filter(d=>d.status==="Pago").reduce((s,d)=>s+(parseFloat(d.valor)||0),0);
  return (
    <div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:16,alignItems:"flex-end"}}>
        <div>
          <p style={{fontSize:11,color:theme.gray500,marginBottom:4}}>Parceiro</p>
          <select value={filtroParcRep} onChange={e=>setFiltroParcRep(e.target.value)}
            style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none",background:"#fff",minWidth:180}}>
            <option value="">Todos os parceiros</option>
            {parcerias.filter(p=>p.status==="Ativo").map(p=>(
              <option key={p.id} value={p.id}>{p.nome}</option>
            ))}
          </select>
        </div>
        <div>
          <p style={{fontSize:11,color:theme.gray500,marginBottom:4}}>Data início</p>
          <input type="date" value={periodoRepIni} onChange={e=>setPeriodoRepIni(e.target.value)}
            style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none"}}/>
        </div>
        <div>
          <p style={{fontSize:11,color:theme.gray500,marginBottom:4}}>Data fim</p>
          <input type="date" value={periodoRepFim} onChange={e=>setPeriodoRepFim(e.target.value)}
            style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none"}}/>
        </div>
        <button onClick={()=>{
          const parcNome = filtroParcRep ? parcerias.find(p=>p.id===filtroParcRep)?.nome : "Todos os Parceiros";
          const rows = despComissoes.map(d=>{
            const cont=contratos.find(ct=>ct.id===d.contratoId);
            const cli=clientes.find(cl=>cl.id===cont?.clienteId);
            return '<tr><td>'+fmtD(d.data)+'</td><td>'+(cont?.numContrato||"—")+'</td><td>'+(cli?.nome||"—")+'</td><td>'+fmtR(d.valor)+'</td><td>'+fmtD(d.data)+'</td><td>'+d.status+'</td></tr>';
          }).join('');
          const html='<html><head><style>body{font-family:serif;padding:32px;}h1{font-size:18px;}h2{font-size:13px;color:#666;margin-top:0;}table{width:100%;border-collapse:collapse;font-size:12px;}th{background:#1a1610;color:#c9a84c;padding:7px 10px;text-align:left;}td{padding:7px 10px;border-bottom:1px solid #eee;}tfoot td{font-weight:700;background:#f9f9f9;}.kpi{display:inline-block;padding:8px 16px;margin:0 8px 12px 0;border:1px solid #ddd;border-radius:6px;text-align:center;}</style></head><body><h1>Extrato de Repasse de Parceria</h1><h2>'+parcNome+' · Período: '+fmtD(periodoRepIni)+' a '+fmtD(periodoRepFim)+'</h2><div><span class="kpi"><b>'+fmtR(totalPendente+totalPago)+'</b><br><small>Total</small></span><span class="kpi"><b>'+fmtR(totalPendente)+'</b><br><small>A Pagar</small></span><span class="kpi"><b>'+fmtR(totalPago)+'</b><br><small>Pago</small></span></div><table><thead><tr><th>Data</th><th>Contrato</th><th>Cliente</th><th>Valor Repasse</th><th>Vencimento</th><th>Status</th></tr></thead><tbody>'+rows+'</tbody><tfoot><tr><td colspan="3">Total</td><td>'+fmtR(totalPendente+totalPago)+'</td><td></td><td></td></tr></tfoot></table><p style="font-size:10px;color:#999;margin-top:24px;border-top:1px solid #eee;padding-top:8px;">Alves & Correa Advogados · Gerado em '+new Date().toLocaleString('pt-BR')+' · Confidencial</p></body></html>';
          const w=window.open('','_blank'); w.document.write(html); w.document.close(); w.print();
        }} style={{padding:"8px 16px",borderRadius:7,border:`1px solid ${theme.gold}`,background:`${theme.gold}18`,color:"#92400e",fontSize:12,fontWeight:600,cursor:"pointer",alignSelf:"flex-end"}}>
          🖨️ Extrato PDF
        </button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
        {[
          {l:"Total Repasses",v:fmtR(totalPendente+totalPago),c:theme.gray800},
          {l:"A Pagar",       v:fmtR(totalPendente),          c:theme.amber},
          {l:"Pago",          v:fmtR(totalPago),              c:theme.green},
        ].map(k=>(
          <Card key={k.l} style={{padding:"14px 18px"}}>
            <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{k.l}</p>
            <p style={{fontSize:22,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
          </Card>
        ))}
      </div>
      <Card>
        {despComissoes.length===0
          ? <p style={{textAlign:"center",padding:"32px 0",color:theme.gray400,fontSize:13}}>Nenhum repasse encontrado para o filtro selecionado.</p>
          : <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:theme.gray50}}>
                    {["Data","Parceiro","Contrato","Cliente","Valor Repasse","Vencimento","Status","Ações"].map(h=>(
                      <th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {despComissoes.sort((a,b)=>(a.data||"")>(b.data||"")?-1:1).map((d,i)=>{
                    const parc = parcerias.find(p=>d.descricao&&d.descricao.includes(p.nome));
                    const cont = contratos.find(ct=>ct.id===d.contratoId);
                    const cli  = clientes.find(cl=>cl.id===cont?.clienteId);
                    return (
                      <tr key={d.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?"#fff":theme.gray50}}>
                        <td style={{padding:"9px 14px",color:theme.gray500,whiteSpace:"nowrap"}}>{fmtD(d.data)}</td>
                        <td style={{padding:"9px 14px",fontWeight:600,color:theme.gray900}}>{parc?.nome||"—"}</td>
                        <td style={{padding:"9px 14px",color:theme.gray700}}>{cont?.numContrato||"—"}</td>
                        <td style={{padding:"9px 14px",color:theme.gray700}}>{cli?.nome||"—"}</td>
                        <td style={{padding:"9px 14px",fontWeight:700,color:theme.blue}}>{fmtR(d.valor)}</td>
                        <td style={{padding:"9px 14px",color:theme.gray500,whiteSpace:"nowrap"}}>{fmtD(d.data)}</td>
                        <td style={{padding:"9px 14px"}}><Badge color={d.status==="Pago"?"green":"amber"}>{d.status}</Badge></td>
                        <td style={{padding:"9px 14px"}}>
                          {d.status!=="Pago"&&!isViz&&(
                            <Btn size="sm" onClick={()=>setData(prev=>({...prev,
                              despesas:(prev.despesas||[]).map(x=>x.id===d.id?{...x,status:"Pago",dataPagamento:new Date().toISOString().slice(0,10)}:x)
                            }))}>✅ Pagar</Btn>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        }
      </Card>
    </div>
  );
}


function ModParcerias({ data, setData }) {
  const isViz = useIsVisualizador();
  const { parcerias } = data;
  const contratos = data.contratos || [];
  const clientes  = data.clientes  || [];
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [aba, setAba] = useState("lista"); // "lista" | "dashboard"
  const [ufSelecionada, setUfSelecionada] = useState(null);

  const EMPTY = { id: "", nome: "", endereco: "", cidade: "", uf: "SP", email: "", whatsapp: "", whatsappDDI: "55", areaAtuacao: "", comissao: "", data: "", status: "Ativo", obs: "" };

  const openNew = () => { setEditItem({ ...EMPTY }); setModalOpen(true); };
  const openEdit = p => { setEditItem({ ...p, whatsappDDI: p.whatsappDDI || "55" }); setModalOpen(true); };

  const save = () => {
    if (!editItem.nome) return alert("Nome é obrigatório.");
    const id = editItem.id || "PAR" + String(Date.now()).slice(-6);
    const item = { ...editItem, id };
    setData(prev => { const lista = editItem.id ? prev.parcerias.map(p => p.id === item.id ? item : p) : [...prev.parcerias, item]; return {...prev, parcerias: lista}; });
    setModalOpen(false);
  };

  const remove = id => { if (!confirm("Remover parceria?")) return; setData(prev => ({...prev, parcerias: parcerias.filter(p => p.id !== id)})); };

  // --- Dashboard: agrupar por UF ---
  const porUF = parcerias.reduce((acc, p) => {
    const uf = p.uf || "??";
    if (!acc[uf]) acc[uf] = [];
    acc[uf].push(p);
    return acc;
  }, {});

  const ufsOrdenadas = Object.entries(porUF).sort((a, b) => b[1].length - a[1].length);
  const totalAtivos = parcerias.filter(p => p.status === "Ativo").length;
  const totalUFs = ufsOrdenadas.length;
  const parcUFSel = ufSelecionada ? (porUF[ufSelecionada] || []) : [];

  // Agrupar cidades da UF selecionada
  const cidadesPorUF = parcUFSel.reduce((acc, p) => {
    const cid = p.cidade || "Não informado";
    if (!acc[cid]) acc[cid] = [];
    acc[cid].push(p);
    return acc;
  }, {});

  // Cores para as barras
  const CORES_UF = [theme.gold, theme.blue, theme.green, theme.amber, theme.red, "#8b5cf6", "#ec4899", "#06b6d4", "#f97316", "#14b8a6"];

  return (
    <div className="fade-in">
      <PageHeader
        title="Parcerias Comerciais"
        subtitle="Parceiros e indicadores com comissão"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ display: "flex", background: theme.gray100, borderRadius: 8, padding: 3 }}>
              {[["lista","📋 Lista"],["dashboard","📊 Dashboard"],["repasses","💸 Repasses"]].map(([id,label]) => (
                <button key={id} onClick={() => setAba(id)} style={{
                  padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: aba===id ? 700 : 400,
                  background: aba===id ? theme.white : "transparent",
                  color: aba===id ? theme.gray900 : theme.gray500,
                  border: "none", cursor: "pointer", boxShadow: aba===id ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
                }}>{label}</button>
              ))}
            </div>
            {!isViz && <Btn onClick={openNew}>+ Nova Parceria</Btn>}
          </div>
        }
      />

      {aba === "lista" && (
        <Card>
          {parcerias.length === 0 ? (
            <EmptyState icon="🤝" title="Nenhuma parceria cadastrada" subtitle="Adicione parceiros e indicadores." />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${theme.gray100}` }}>
                    {["Nome", "Cidade/UF", "Área", "Comissão", "Contato", "Status", "Ações"].map(h => (
                      <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 10, color: theme.gray400, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...parcerias].sort((a,b)=>(a.nome||"").localeCompare(b.nome||"","pt-BR")).map((p, i) => (
                    <tr key={p.id} style={{ borderBottom: `1px solid ${theme.gray100}`, background: i % 2 === 0 ? theme.white : theme.gray50 }}>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ fontWeight: 500, fontSize: 14, color: theme.gray900 }}>{p.nome}</div>
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: theme.gray600 }}>{p.cidade}/{p.uf}</td>
                      <td style={{ padding: "12px 16px" }}><Badge color="blue">{p.areaAtuacao}</Badge></td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ fontWeight: 700, color: theme.green }}>{p.comissao}%</span>
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: theme.gray600 }}>
                        <div>{p.email}</div>
                        <div>{p.whatsapp}</div>
                      </td>
                      <td style={{ padding: "12px 16px" }}><Badge color={p.status === "Ativo" ? "green" : "gray"}>{p.status}</Badge></td>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          {!isViz && <Btn size="sm" variant="secondary" onClick={() => openEdit(p)}>Editar</Btn>}
                          {!isViz && <Btn size="sm" variant="danger" onClick={() => remove(p.id)}>×</Btn>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {aba === "repasses" && (
        <PainelRepasses
          parcerias={parcerias} contratos={contratos} clientes={clientes}
          despesas={data.despesas||[]} setData={setData}
          theme={theme} Card={Card} Badge={Badge} Btn={Btn}
        />
      )}

      {aba === "dashboard" && (
        <div>
          {/* KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
            {[
              { l: "Total de Parceiros", v: parcerias.length, icon: "🤝", c: theme.gray800 },
              { l: "Parceiros Ativos",   v: totalAtivos,       icon: "✅", c: theme.green },
              { l: "Estados (UFs)",      v: totalUFs,          icon: "🗺️", c: theme.blue },
              { l: "Municípios",         v: Object.values(porUF).flat().map(p=>p.cidade).filter((v,i,a)=>v&&a.indexOf(v)===i).length, icon: "📍", c: theme.amber },
            ].map(k => (
              <Card key={k.l} style={{ padding: "16px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <p style={{ fontSize: 10, color: theme.gray400, textTransform: "uppercase", letterSpacing: 0.8 }}>{k.l}</p>
                  <span style={{ fontSize: 15 }}>{k.icon}</span>
                </div>
                <p style={{ fontSize: 26, fontWeight: 700, color: k.c, marginTop: 8 }}>{k.v}</p>
              </Card>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {/* Parceiros por UF */}
            <Card style={{ padding: "20px 24px" }}>
              <p style={{ fontSize: 11, color: theme.gray500, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 16, fontWeight: 600 }}>
                Parceiros por Estado (UF)
              </p>
              {ufsOrdenadas.length === 0
                ? <p style={{ color: theme.gray400, fontSize: 13, textAlign: "center", padding: "20px 0" }}>Nenhum parceiro cadastrado.</p>
                : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {ufsOrdenadas.map(([uf, lista], idx) => {
                      const max = ufsOrdenadas[0][1].length || 1;
                      const pct = (lista.length / max) * 100;
                      const ativos = lista.filter(p => p.status === "Ativo").length;
                      const cor = CORES_UF[idx % CORES_UF.length];
                      return (
                        <div key={uf}
                          onClick={() => setUfSelecionada(ufSelecionada === uf ? null : uf)}
                          style={{ cursor: "pointer", padding: "10px 12px", borderRadius: 8,
                            background: ufSelecionada === uf ? `${cor}12` : theme.gray50,
                            border: `1px solid ${ufSelecionada === uf ? cor : theme.gray100}`,
                            transition: "all 0.15s"
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: theme.gray800, minWidth: 28 }}>{uf}</span>
                              <span style={{ fontSize: 11, color: theme.gray500 }}>{lista.length} parceiro{lista.length !== 1 ? "s" : ""}</span>
                              {ativos < lista.length && <Badge color="amber">{lista.length - ativos} inativo{lista.length - ativos !== 1 ? "s" : ""}</Badge>}
                            </div>
                            <span style={{ fontSize: 11, color: cor, fontWeight: 700 }}>{ativos} ativo{ativos !== 1 ? "s" : ""}</span>
                          </div>
                          <div style={{ background: theme.gray100, borderRadius: 4, height: 6, overflow: "hidden" }}>
                            <div style={{ width: `${pct}%`, height: "100%", background: cor, borderRadius: 4, transition: "width 0.5s" }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
              }
            </Card>

            {/* Detalhamento da UF selecionada */}
            <Card style={{ padding: "20px 24px" }}>
              {!ufSelecionada
                ? <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 200, color: theme.gray400 }}>
                    <span style={{ fontSize: 40, marginBottom: 12 }}>🗺️</span>
                    <p style={{ fontSize: 13 }}>Clique em um estado para ver os detalhes</p>
                  </div>
                : <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: theme.gray800 }}>
                        📍 {ufSelecionada} — {parcUFSel.length} parceiro{parcUFSel.length !== 1 ? "s" : ""}
                      </p>
                      <Btn size="sm" variant="secondary" onClick={() => setUfSelecionada(null)}>×</Btn>
                    </div>

                    {/* Por município */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 380, overflowY: "auto" }}>
                      {Object.entries(cidadesPorUF).sort((a,b) => b[1].length - a[1].length).map(([cidade, lista]) => (
                        <div key={cidade}>
                          <p style={{ fontSize: 11, color: theme.gray500, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
                            📌 {cidade} ({lista.length})
                          </p>
                          {lista.map(p => (
                            <div key={p.id} style={{ padding: "8px 12px", borderRadius: 7, border: `1px solid ${theme.gray100}`, marginBottom: 6, background: p.status === "Ativo" ? "#fff" : theme.gray50 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: theme.gray800 }}>{p.nome}</span>
                                <Badge color={p.status === "Ativo" ? "green" : "gray"}>{p.status}</Badge>
                              </div>
                              <div style={{ fontSize: 11, color: theme.gray500, marginTop: 4, display: "flex", gap: 12 }}>
                                {p.areaAtuacao && <span>🏷 {p.areaAtuacao}</span>}
                                {p.comissao && <span>💰 {p.comissao}%</span>}
                                {p.whatsapp && <span>📱 +{p.whatsappDDI||"55"} {p.whatsapp}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </>
              }
            </Card>
          </div>

          {/* Distribuição por Área de Atuação */}
          <Card style={{ padding: "20px 24px", marginTop: 20 }}>
            <p style={{ fontSize: 11, color: theme.gray500, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 16, fontWeight: 600 }}>
              Distribuição por Área de Atuação
            </p>
            {(() => {
              const porArea = parcerias.reduce((acc, p) => {
                const area = p.areaAtuacao || "Não informado";
                acc[area] = (acc[area] || 0) + 1;
                return acc;
              }, {});
              const areas = Object.entries(porArea).sort((a,b) => b[1]-a[1]);
              const maxA = areas[0]?.[1] || 1;
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
                  {areas.map(([area, qtd], idx) => (
                    <div key={area} style={{ padding: "10px 14px", borderRadius: 8, background: theme.gray50, border: `1px solid ${theme.gray100}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: theme.gray700 }}>{area}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: CORES_UF[idx % CORES_UF.length] }}>{qtd}</span>
                      </div>
                      <div style={{ background: theme.gray200, borderRadius: 3, height: 4 }}>
                        <div style={{ width: `${(qtd/maxA)*100}%`, height: "100%", background: CORES_UF[idx % CORES_UF.length], borderRadius: 3 }} />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </Card>
        </div>
      )}

      {/* ── ABA REPASSES ── */}
      {aba === "repasses" && (
        <PainelRepasses
          parcerias={parcerias}
          contratos={contratos}
          clientes={clientes}
          despesas={data.despesas||[]}
          setData={setData}
          theme={theme}
          Card={Card}
          Badge={Badge}
          Btn={Btn}
        />
      )}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editItem?.id ? "Editar Parceria" : "Nova Parceria"} width={620}>
        {editItem && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Nome / Razão Social" required>
                  <Input value={editItem.nome} onChange={v => setEditItem({ ...editItem, nome: v })} placeholder="Nome do parceiro" />
                </FormField>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Endereço">
                  <Input value={editItem.endereco} onChange={v => setEditItem({ ...editItem, endereco: v })} placeholder="Rua, número" />
                </FormField>
              </div>
              <FormField label="Cidade">
                <Input value={editItem.cidade} onChange={v => setEditItem({ ...editItem, cidade: v })} placeholder="Cidade" />
              </FormField>
              <FormField label="UF">
                <Select value={editItem.uf} onChange={v => setEditItem({ ...editItem, uf: v })} options={UFS} />
              </FormField>
              <FormField label="E-mail">
                <Input type="email" value={editItem.email} onChange={v => setEditItem({ ...editItem, email: v })} placeholder="email@parceiro.com" />
              </FormField>
              <CampoWhatsApp
                ddi={editItem.whatsappDDI}
                numero={editItem.whatsapp}
                onChangeDDI={v=>setEditItem({...editItem, whatsappDDI: v})}
                onChangeNumero={v=>setEditItem({...editItem, whatsapp: v})}
              />
              <FormField label="Área de Atuação">
                <Input value={editItem.areaAtuacao} onChange={v => setEditItem({ ...editItem, areaAtuacao: v })} placeholder="Ex: Contabilidade, Imóveis" />
              </FormField>
              <FormField label="Comissão (%)">
                <Input type="number" value={editItem.comissao} onChange={v => setEditItem({ ...editItem, comissao: v })} placeholder="10" />
              </FormField>
              <FormField label="Data de início">
                <Input type="date" value={editItem.data} onChange={v => setEditItem({ ...editItem, data: v })} />
              </FormField>
              <FormField label="Status">
                <Select value={editItem.status} onChange={v => setEditItem({ ...editItem, status: v })} options={["Ativo", "Inativo"]} />
              </FormField>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Observações">
                  <Textarea value={editItem.obs} onChange={v => setEditItem({ ...editItem, obs: v })} placeholder="Notas sobre a parceria..." />
                </FormField>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 16, borderTop: `1px solid ${theme.gray100}` }}>
              <Btn variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Btn>
              {!isViz && <Btn onClick={save}>Salvar Parceria</Btn>}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

// ============================================================
// MÓDULO: BASE OPERACIONAL
// ============================================================

// ============================================================
// CSS IMPRESSÃO — PROCURAÇÃO E DECLARAÇÃO
// ============================================================
const PRINT_STYLE_BASE = `
  @media print {
    body > * { display: none !important; }
    #print-area-base { display: block !important; }
  }
  #print-area-base {
    display: none;
    font-family: 'Georgia', serif;
    color: #1a1a1a;
    padding: 40px 60px;
    max-width: 800px;
    margin: 0 auto;
    font-size: 13px;
    line-height: 1.8;
  }
  .pb-header { text-align:center; border-bottom:3px double #1a1a1a; padding-bottom:20px; margin-bottom:28px; }
  .pb-header h1 { font-size:18px; font-weight:700; letter-spacing:2px; text-transform:uppercase; margin:0 0 4px; }
  .pb-header p  { font-size:11px; color:#555; margin:0; letter-spacing:1px; }
  .pb-titulo    { text-align:center; font-size:16px; font-weight:700; text-transform:uppercase; letter-spacing:2px; margin:24px 0 4px; }
  .pb-subtitulo { text-align:center; font-size:13px; font-weight:700; letter-spacing:1px; margin:0 0 22px; }
  .pb-secao     { font-size:12.5px; font-weight:700; text-transform:uppercase; margin:18px 0 6px; }
  .pb-corpo     { text-align:justify; font-size:13px; margin-bottom:14px; }
  .pb-poderes   { margin:16px 0; padding:12px 20px; border-left:4px solid #1a1a1a; background:#f9f9f9; }
  .pb-poderes p { margin:4px 0; font-size:12px; }
  .pb-assinaturas { display:grid; grid-template-columns:1fr 1fr; gap:60px; margin-top:70px; }
  .pb-assinatura  { border-top:1px solid #1a1a1a; padding-top:10px; text-align:center; }
  .pb-assinatura p { margin:2px 0; font-size:12px; }
  .pb-assinaturas-uma { display:flex; justify-content:center; margin-top:70px; }
  .pb-assinaturas-uma .pb-assinatura { min-width:320px; }
  .pb-rodape-data { margin-top:50px; font-size:12px; }
  .pb-rodape    { margin-top:40px; text-align:center; font-size:10px; color:#999; border-top:1px solid #ddd; padding-top:10px; }
`;


// ============================================================
// MODAL: FICHA DE CONTROLE DE CRÉDITO TRIBUTÁRIO
// ============================================================
function ModalFichaCredito({ contrato, data, setData, onClose }) {
  const clientes    = data.clientes    || [];
  const produtos    = data.produtos    || [];
  const lancamentos = data.lancamentos || [];
  const fichas      = data.fichasCredito || [];

  const cli  = clientes.find(x => x.id === contrato.clienteId);
  const prod = produtos.find(x => x.id === contrato.produtoId);

  // Encontrar ou criar ficha para este contrato
  const fichaExist = fichas.find(f => f.contratoId === contrato.id);

  const EMPTY_FICHA = {
    id: "FCR" + Date.now().toString().slice(-8),
    contratoId:          contrato.id,
    clienteId:           contrato.clienteId,
    produtoId:           contrato.produtoId,
    dataApuracao:        contrato.dataEntrada || "",
    valorCreditoInicial: parseFloat(contrato.valorCreditoApurado) || 0,
    percHonorarios:      parseFloat(contrato.percHonorarios)      || 0,
    obs:                 "",
    compensacoes:        []
  };

  const [ficha, setFichaLocal] = React.useState(fichaExist || EMPTY_FICHA);

  // Persistir a ficha-base no estado global assim que o modal for aberto
  // e sincronizar valorCreditoInicial com valorCreditoApurado do contrato
  // (resolve casos em que o crédito foi preenchido DEPOIS da ficha ser criada)
  React.useEffect(() => {
    const creditoDoContrato = parseFloat(contrato.valorCreditoApurado) || 0;
    setData(prev => {
      const fichasPrev = prev.fichasCredito || [];
      const idxExist = fichasPrev.findIndex(f => f.contratoId === contrato.id);
      if (idxExist < 0) {
        // Ficha ainda não existe — criar com o crédito atual do contrato
        return { ...prev, fichasCredito: [...fichasPrev, EMPTY_FICHA] };
      }
      const fichaAtual = fichasPrev[idxExist];
      // Ficha existe mas valorCreditoInicial está zerado e o contrato já tem o valor
      // → atualizar automaticamente para refletir o valor do contrato
      if ((parseFloat(fichaAtual.valorCreditoInicial)||0) === 0 && creditoDoContrato > 0) {
        const fichaCorrigida = { ...fichaAtual, valorCreditoInicial: creditoDoContrato, percHonorarios: parseFloat(contrato.percHonorarios)||fichaAtual.percHonorarios };
        setFichaLocal(fichaCorrigida);
        return { ...prev, fichasCredito: fichasPrev.map((f,i) => i===idxExist ? fichaCorrigida : f) };
      }
      return prev;
    });
  }, [contrato.id]);

  const [novaComp, setNovaComp] = React.useState({
    data: "", numeroPerdcomp: "", valorCreditoCompensado: "",
    correcaoMonetaria: "0", dataVencHonorarios: "", status: "Aguardando homologação", obs: ""
  });
  const [salvando, setSalvando] = React.useState(false);
  const [erro,     setErro]     = React.useState("");

  const fmtR$ = v => "R$ " + parseFloat(v||0).toLocaleString("pt-BR", {minimumFractionDigits:2, maximumFractionDigits:2});
  const fmtDt = d => d ? new Date(d+"T00:00:00").toLocaleDateString("pt-BR") : "—";
  const hojeStr = new Date().toISOString().slice(0,10);

  // Cálculos automáticos da nova compensação
  const credComp  = parseFloat(novaComp.valorCreditoCompensado) || 0;
  const correcao  = parseFloat(novaComp.correcaoMonetaria)      || 0;
  const totalComp = credComp + correcao;
  const honorAuto = parseFloat((totalComp * (ficha.percHonorarios/100)).toFixed(2));

  // Saldos calculados
  const totalCompensado = ficha.compensacoes
    .filter(c => c.status !== "Glosado")
    .reduce((s,c) => s + (parseFloat(c.valorCreditoCompensado)||0), 0);
  const totalCorrecao   = ficha.compensacoes
    .filter(c => c.status !== "Glosado")
    .reduce((s,c) => s + (parseFloat(c.correcaoMonetaria)||0), 0);
  const totalHonorarios = ficha.compensacoes
    .filter(c => c.status !== "Glosado")
    .reduce((s,c) => s + (parseFloat(c.valorHonorarios)||0), 0);
  const saldoCredito    = ficha.valorCreditoInicial - totalCompensado - totalCorrecao;
  const saldoHonorarios = (parseFloat(contrato.valorContratado)||0) - totalHonorarios;

  // Salvar ficha (criar ou atualizar)
  const salvarFicha = (fichaAtualizada) => {
    setData(prev => {
      const fichasPrev = prev.fichasCredito || [];
      const idxMesmoId = fichasPrev.findIndex(f => f.id === fichaAtualizada.id);
      const idxMesmoContrato = fichasPrev.findIndex(f => f.contratoId === fichaAtualizada.contratoId);
      const idx = idxMesmoId >= 0 ? idxMesmoId : idxMesmoContrato;
      const fichasAtt = idx >= 0
        ? fichasPrev.map((f, i) => i === idx ? {...f, ...fichaAtualizada} : f)
        : [...fichasPrev, fichaAtualizada];
      return {...prev, fichasCredito: fichasAtt};
    });
  };

  // Registrar nova compensação
  const registrarCompensacao = () => {
    setErro("");
    if (!novaComp.data)                          return setErro("Informe a data da compensação.");
    if (!novaComp.numeroPerdcomp.trim())         return setErro("Informe o número do PERDCOMP.");
    if (!credComp || credComp <= 0)              return setErro("Informe o valor do crédito compensado.");
    if (credComp > saldoCredito + 0.01)          return setErro(`Valor excede o saldo disponível (${fmtR$(saldoCredito)}).`);
    if (!novaComp.dataVencHonorarios)            return setErro("Informe o vencimento dos honorários.");

    const idComp  = "CMP" + Date.now().toString().slice(-8);
    const idLan   = "LAN" + Date.now().toString().slice(-7);

    const novaCompFinal = {
      id:                     idComp,
      data:                   novaComp.data,
      numeroPerdcomp:         novaComp.numeroPerdcomp.trim(),
      valorCreditoCompensado: credComp,
      correcaoMonetaria:      correcao,
      valorTotalCompensado:   totalComp,
      valorHonorarios:        honorAuto,
      dataVencHonorarios:     novaComp.dataVencHonorarios,
      lancamentoId:           idLan,
      status:                 novaComp.status,
      obs:                    novaComp.obs
    };

    const fichaAtt = {...ficha, compensacoes: [...ficha.compensacoes, novaCompFinal]};
    setFichaLocal(fichaAtt);

    // Lançamento desta compensação no Financeiro
    const novoLanComp = {
      id:              idLan,
      clienteId:       contrato.clienteId,
      contratoId:      contrato.id,
      numContrato:     contrato.numContrato,
      produtoId:       contrato.produtoId,
      tipo:            "Honorario",
      modalidade:      "Êxito sobre Compensação",
      descricao:       `Honorários — Compensação ${novaComp.numeroPerdcomp.trim()} (${fmtDt(novaComp.data)})`,
      valor:           honorAuto,
      dataVencimento:  novaComp.dataVencHonorarios,
      dataRecebimento: "",
      bancoBaixa:      "",
      status:          "A Receber",
      obs:             `PERDCOMP: ${novaComp.numeroPerdcomp.trim()} · Crédito: ${fmtR$(credComp)} · Correção: ${fmtR$(correcao)}`
    };

    // Reduzir o lançamento inicial de honorários
    const lanInicialId = contrato.lancamentoInicialId;
    const lans = lancamentos.map(l => {
      if (l.id !== lanInicialId) return l;
      const novoValor = Math.max(0, parseFloat(l.valor||0) - honorAuto);
      return {...l, valor: parseFloat(novoValor.toFixed(2)),
        obs: (l.obs||"") + ` | Reduzido R$${honorAuto.toFixed(2)} — Comp. ${novaComp.numeroPerdcomp.trim()}`};
    });

    // Salvar tudo
    setData(prev => {
      const fichasPrev = prev.fichasCredito || [];
      const idxMesmoId = fichasPrev.findIndex(f => f.id === fichaAtt.id);
      const idxMesmoContrato = fichasPrev.findIndex(f => f.contratoId === fichaAtt.contratoId);
      const idx = idxMesmoId >= 0 ? idxMesmoId : idxMesmoContrato;
      const fichasAtt = idx >= 0
        ? fichasPrev.map((f, i) => i === idx ? {...f, ...fichaAtt} : f)
        : [...fichasPrev, fichaAtt];
      return {
        ...prev,
        fichasCredito: fichasAtt,
        lancamentos:   [...lans, novoLanComp]
      };
    });

    // Limpar formulário
    setNovaComp({
      data:"", numeroPerdcomp:"", valorCreditoCompensado:"",
      correcaoMonetaria:"0", dataVencHonorarios:"", status:"Aguardando homologação", obs:""
    });
  };

  // Alterar status de uma compensação (Homologado / Glosado)
  const alterarStatus = (idComp, novoStatus) => {
    const compAnt = ficha.compensacoes.find(c => c.id === idComp);
    if (!compAnt) return;

    const compsAtt = ficha.compensacoes.map(c =>
      c.id === idComp ? {...c, status: novoStatus} : c
    );
    const fichaAtt = {...ficha, compensacoes: compsAtt};
    setFichaLocal(fichaAtt);

    // Se glosado: cancelar lançamento e recompor o inicial
    if (novoStatus === "Glosado") {
      const lans = lancamentos.map(l => {
        if (l.id === compAnt.lancamentoId) return {...l, status:"Cancelado", obs:(l.obs||"")+" | Glosado pela SRFB"};
        if (l.id === contrato.lancamentoInicialId) {
          const resto = parseFloat(l.valor||0) + parseFloat(compAnt.valorHonorarios||0);
          return {...l, valor: parseFloat(resto.toFixed(2)), obs:(l.obs||"")+" | Recomposto por glosa"};
        }
        return l;
      });
      setData(prev => {
        const fichasPrev = prev.fichasCredito || [];
        const idxMesmoId = fichasPrev.findIndex(f => f.id === fichaAtt.id);
        const idxMesmoContrato = fichasPrev.findIndex(f => f.contratoId === fichaAtt.contratoId);
        const idx = idxMesmoId >= 0 ? idxMesmoId : idxMesmoContrato;
        const fichasAtt = idx >= 0
          ? fichasPrev.map((f, i) => i === idx ? {...f, ...fichaAtt} : f)
          : [...fichasPrev, fichaAtt];
        return {...prev, fichasCredito: fichasAtt, lancamentos: lans};
      });
    } else {
      salvarFicha(fichaAtt);
    }
  };

  const COR_STATUS = { "Aguardando homologação":"amber", "Homologado":"green", "Glosado":"red" };

  const conteudo = (
    <div style={{
      position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:9999,
      background:"rgba(0,0,0,.6)",display:"flex",alignItems:"center",justifyContent:"center"
    }} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{
        background:theme.white,borderRadius:16,width:"min(98vw,1100px)",
        maxHeight:"92vh",display:"flex",flexDirection:"column",
        boxShadow:"0 24px 64px rgba(0,0,0,.3)"
      }}>
        {/* Header */}
        <div style={{
          background:"linear-gradient(135deg,#1e3a8a,#1d4ed8)",
          padding:"18px 28px",borderRadius:"16px 16px 0 0",
          display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0
        }}>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <span style={{fontSize:28}}>💳</span>
            <div>
              <p className="serif" style={{color:"#fff",fontSize:17,fontWeight:700}}>
                Ficha de Controle de Crédito Tributário
              </p>
              <p style={{color:"rgba(255,255,255,.7)",fontSize:12,marginTop:2}}>
                {cli?.nome} · {prod?.nome} · Contrato {contrato.numContrato}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            background:"rgba(255,255,255,.15)",border:"none",color:"#fff",
            borderRadius:8,padding:"8px 14px",cursor:"pointer",fontSize:13,fontWeight:600
          }}>✕ Fechar</button>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"22px 28px"}}>

          {/* Cards de saldo */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,marginBottom:22}}>
            {[
              {l:"Crédito Apurado",      v:fmtR$(ficha.valorCreditoInicial), c:"#1d4ed8", bg:"#eff6ff"},
              {l:"Total Compensado",     v:fmtR$(totalCompensado),           c:theme.green, bg:"#f0fdf4"},
              {l:"Total Correção SELIC", v:fmtR$(totalCorrecao),             c:theme.amber, bg:"#fefce8"},
              {l:"Saldo a Compensar",    v:fmtR$(saldoCredito),              c:saldoCredito>0?"#1d4ed8":theme.gray400, bg:saldoCredito>0?"#eff6ff":theme.gray50},
              {l:"Honorários Gerados",   v:fmtR$(totalHonorarios),           c:"#7c3aed", bg:"#f5f3ff"},
            ].map(k => (
              <div key={k.l} style={{background:k.bg,borderRadius:10,padding:"12px 14px",border:`1px solid ${k.c}33`}}>
                <p style={{fontSize:10,color:k.c,textTransform:"uppercase",letterSpacing:.8,fontWeight:600,marginBottom:4}}>{k.l}</p>
                <p style={{fontSize:18,fontWeight:800,color:k.c}}>{k.v}</p>
              </div>
            ))}
          </div>

          {/* Aviso quando crédito apurado não foi preenchido no contrato */}
          {(parseFloat(ficha.valorCreditoInicial)||0) === 0 && (
            <div style={{background:"#fef3c7",border:"1px solid #fbbf24",borderRadius:8,padding:"10px 14px",marginBottom:16,display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:18}}>⚠️</span>
              <p style={{fontSize:12,color:"#92400e",margin:0}}>
                O <strong>Valor do Crédito Apurado</strong> não foi preenchido neste contrato. Acesse <strong>Contratos → Editar</strong> e informe o campo "Valor do Crédito Apurado" para habilitar os cálculos desta ficha e a consolidação no Dashboard Tributário.
              </p>
            </div>
          )}

          {/* Informações da ficha */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:22,padding:"14px 18px",background:theme.gray50,borderRadius:10,border:`1px solid ${theme.gray100}`}}>
            <div>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>Data de Apuração</p>
              <p style={{fontSize:13,fontWeight:600,color:theme.gray800,marginTop:3}}>{fmtDt(ficha.dataApuracao)}</p>
            </div>
            <div>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>% de Honorários</p>
              <p style={{fontSize:13,fontWeight:600,color:theme.gray800,marginTop:3}}>{ficha.percHonorarios}%</p>
            </div>
            <div>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>Saldo de Honorários (Estimado)</p>
              <p style={{fontSize:13,fontWeight:600,color:saldoHonorarios>=0?theme.green:theme.red,marginTop:3}}>{fmtR$(saldoHonorarios)}</p>
            </div>
          </div>

          {/* Formulário de nova compensação */}
          <div style={{background:"#eff6ff",borderRadius:12,padding:"18px 20px",marginBottom:22,border:"1px solid #bfdbfe"}}>
            <p style={{fontSize:13,fontWeight:700,color:"#1e40af",marginBottom:14}}>➕ Registrar Nova Compensação</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:12}}>
              <div>
                <p style={{fontSize:11,color:theme.gray600,fontWeight:600,marginBottom:4}}>Data da Compensação *</p>
                <input type="date" value={novaComp.data} onChange={e=>setNovaComp({...novaComp,data:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,fontFamily:"Jost,sans-serif"}}/>
              </div>
              <div>
                <p style={{fontSize:11,color:theme.gray600,fontWeight:600,marginBottom:4}}>Nº PERDCOMP *</p>
                <input value={novaComp.numeroPerdcomp} onChange={e=>setNovaComp({...novaComp,numeroPerdcomp:e.target.value})}
                  placeholder="ex: 24.000.00000.000000"
                  style={{width:"100%",padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,fontFamily:"Jost,sans-serif"}}/>
              </div>
              <div>
                <p style={{fontSize:11,color:theme.gray600,fontWeight:600,marginBottom:4}}>Status</p>
                <select value={novaComp.status} onChange={e=>setNovaComp({...novaComp,status:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,fontFamily:"Jost,sans-serif"}}>
                  {["Aguardando homologação","Homologado","Glosado"].map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <p style={{fontSize:11,color:theme.gray600,fontWeight:600,marginBottom:4}}>Crédito Compensado (R$) *</p>
                <input type="number" value={novaComp.valorCreditoCompensado} onChange={e=>setNovaComp({...novaComp,valorCreditoCompensado:e.target.value})}
                  placeholder="0,00"
                  style={{width:"100%",padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,fontFamily:"Jost,sans-serif"}}/>
              </div>
              <div>
                <p style={{fontSize:11,color:theme.gray600,fontWeight:600,marginBottom:4}}>Correção Monetária SELIC (R$)</p>
                <input type="number" value={novaComp.correcaoMonetaria} onChange={e=>setNovaComp({...novaComp,correcaoMonetaria:e.target.value})}
                  placeholder="0,00"
                  style={{width:"100%",padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,fontFamily:"Jost,sans-serif"}}/>
              </div>
              <div>
                <p style={{fontSize:11,color:theme.gray600,fontWeight:600,marginBottom:4}}>Vencimento dos Honorários *</p>
                <input type="date" value={novaComp.dataVencHonorarios} onChange={e=>setNovaComp({...novaComp,dataVencHonorarios:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,fontFamily:"Jost,sans-serif"}}/>
              </div>
            </div>

            {/* Preview automático */}
            {credComp > 0 && (
              <div style={{display:"flex",gap:16,padding:"10px 14px",background:"#dbeafe",borderRadius:8,marginBottom:12,fontSize:12}}>
                <span style={{color:"#1e40af"}}>💡 <strong>Total Compensado:</strong> {fmtR$(totalComp)}</span>
                <span style={{color:"#7c3aed"}}>💰 <strong>Honorários calculados ({ficha.percHonorarios}%):</strong> {fmtR$(honorAuto)}</span>
                <span style={{color:saldoCredito-credComp>=0?theme.green:theme.red}}>
                  📊 <strong>Saldo após:</strong> {fmtR$(saldoCredito-credComp-correcao)}
                </span>
              </div>
            )}

            {erro && <p style={{color:theme.red,fontSize:12,marginBottom:10,fontWeight:600}}>⚠ {erro}</p>}

            <div style={{display:"flex",gap:10}}>
              <textarea value={novaComp.obs} onChange={e=>setNovaComp({...novaComp,obs:e.target.value})}
                placeholder="Observações (opcional)..." rows={2}
                style={{flex:1,padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,fontFamily:"Jost,sans-serif",resize:"none"}}/>
              <button onClick={registrarCompensacao} style={{
                padding:"0 24px",borderRadius:9,border:"none",cursor:"pointer",
                background:"linear-gradient(135deg,#1e40af,#3b82f6)",color:"#fff",
                fontSize:13,fontWeight:700,flexShrink:0
              }}>✓ Registrar</button>
            </div>
          </div>

          {/* Tabela de compensações */}
          <div>
            <p style={{fontSize:13,fontWeight:700,color:theme.gray900,marginBottom:12}}>
              📋 Histórico de Compensações ({ficha.compensacoes.length})
            </p>
            {ficha.compensacoes.length === 0
              ? <div style={{textAlign:"center",padding:"30px 0",color:theme.gray400,background:theme.gray50,borderRadius:10}}>
                  <p style={{fontSize:24,marginBottom:8}}>📭</p>
                  <p style={{fontSize:13}}>Nenhuma compensação registrada ainda.</p>
                </div>
              : <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:theme.gray900}}>
                        {["Data","PERDCOMP","Crédito Comp.","Correção SELIC","Total Comp.","Honorários","Venc. Honor.","Status","Ações"].map(h=>(
                          <th key={h} style={{padding:"9px 12px",textAlign:"left",color:theme.gold,fontSize:10,fontWeight:700,letterSpacing:.8,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ficha.compensacoes.map((comp,idx)=>(
                        <tr key={comp.id} style={{
                          borderBottom:`1px solid ${theme.gray100}`,
                          background:comp.status==="Glosado"?"#fef2f2":idx%2===0?theme.white:theme.gray50,
                          opacity:comp.status==="Glosado"?.7:1
                        }}>
                          <td style={{padding:"9px 12px",whiteSpace:"nowrap",color:theme.gray700}}>{fmtDt(comp.data)}</td>
                          <td style={{padding:"9px 12px",fontFamily:"monospace",fontSize:11,color:theme.gray800,whiteSpace:"normal",wordBreak:"break-all"}}>{comp.numeroPerdcomp}</td>
                          <td style={{padding:"9px 12px",textAlign:"right",fontWeight:600,color:"#1d4ed8"}}>{fmtR$(comp.valorCreditoCompensado)}</td>
                          <td style={{padding:"9px 12px",textAlign:"right",color:theme.amber}}>{fmtR$(comp.correcaoMonetaria)}</td>
                          <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700,color:theme.gray900}}>{fmtR$(comp.valorTotalCompensado)}</td>
                          <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700,color:"#7c3aed"}}>{fmtR$(comp.valorHonorarios)}</td>
                          <td style={{padding:"9px 12px",whiteSpace:"nowrap",color:theme.gray600}}>{fmtDt(comp.dataVencHonorarios)}</td>
                          <td style={{padding:"9px 12px"}}>
                            <span style={{
                              fontSize:10,padding:"3px 8px",borderRadius:4,fontWeight:700,
                              background:comp.status==="Homologado"?"#f0fdf4":comp.status==="Glosado"?"#fef2f2":"#fefce8",
                              color:comp.status==="Homologado"?theme.green:comp.status==="Glosado"?theme.red:theme.amber
                            }}>{comp.status}</span>
                          </td>
                          <td style={{padding:"9px 12px",whiteSpace:"nowrap"}}>
                            {comp.status==="Aguardando homologação"&&(
                              <div style={{display:"flex",gap:4}}>
                                <button onClick={()=>alterarStatus(comp.id,"Homologado")} style={{
                                  fontSize:10,padding:"3px 8px",borderRadius:4,border:"none",cursor:"pointer",
                                  background:"#f0fdf4",color:theme.green,fontWeight:700
                                }}>✓ Homologar</button>
                                <button onClick={()=>alterarStatus(comp.id,"Glosado")} style={{
                                  fontSize:10,padding:"3px 8px",borderRadius:4,border:"none",cursor:"pointer",
                                  background:"#fef2f2",color:theme.red,fontWeight:700
                                }}>✕ Glosar</button>
                              </div>
                            )}
                            {comp.status==="Homologado"&&<span style={{fontSize:10,color:theme.gray400}}>Encerrado</span>}
                            {comp.status==="Glosado"&&<span style={{fontSize:10,color:theme.red}}>Glosado</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{background:theme.gray900,fontWeight:700}}>
                        <td colSpan={2} style={{padding:"10px 12px",color:theme.gold,fontSize:11}}>TOTAIS (excl. glosados)</td>
                        <td style={{padding:"10px 12px",textAlign:"right",color:"#93c5fd",fontSize:12}}>{fmtR$(totalCompensado)}</td>
                        <td style={{padding:"10px 12px",textAlign:"right",color:theme.amber,fontSize:12}}>{fmtR$(totalCorrecao)}</td>
                        <td style={{padding:"10px 12px",textAlign:"right",color:theme.cream,fontSize:12}}>{fmtR$(totalCompensado+totalCorrecao)}</td>
                        <td style={{padding:"10px 12px",textAlign:"right",color:"#c4b5fd",fontSize:12}}>{fmtR$(totalHonorarios)}</td>
                        <td colSpan={3}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
            }
          </div>

        </div>
      </div>
    </div>
  );
  return ReactDOM.createPortal(conteudo, document.body);
}
function PainelAuditoria({ registros, mesAuditoria, getNomeCli, fmtData, equipe, data, setData, theme, Btn }) {
  const todasOcs = registros.flatMap(r=>
    (r.ocorrencias||[])
      .filter(oc=>(oc.data||"").slice(0,7)===mesAuditoria)
      .map(oc=>({...oc,_reg:r}))
  ).sort((a,b)=>a.data>b.data?-1:1);
  const confCount = todasOcs.filter(o=>o.confidencial!==false).length;
  const visiveis  = todasOcs.filter(o=>o.confidencial===false).length;
  return (
    <>
      <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <span style={{fontSize:12,padding:"4px 12px",borderRadius:20,background:theme.gray100,color:theme.gray700}}>📊 {todasOcs.length} ocorrência(s)</span>
        <span style={{fontSize:12,padding:"4px 12px",borderRadius:20,background:"#fef3c7",color:"#92400e"}}>🔒 {confCount} confidencial(is)</span>
        <span style={{fontSize:12,padding:"4px 12px",borderRadius:20,background:"#f0fdf4",color:theme.green}}>📋 {visiveis} para o cliente</span>
      </div>
      {todasOcs.length===0
        ? <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"20px 0"}}>Nenhuma ocorrência no período selecionado.</p>
        : <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{background:theme.gray50}}>
                  {["Contrato","Cliente","Data","Responsável","Ocorrência","Origem","Status"].map(h=>(
                    <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {todasOcs.map((oc,i)=>(
                  <tr key={oc.id||i} style={{borderBottom:`1px solid ${theme.gray100}`,background:oc.confidencial!==false?"#fffbeb":"#fff"}}>
                    <td style={{padding:"9px 12px",fontWeight:600,color:theme.gray900,whiteSpace:"nowrap"}}>{oc._reg.numContrato}</td>
                    <td style={{padding:"9px 12px",color:theme.gray700}}>{getNomeCli(oc._reg.clienteId)}</td>
                    <td style={{padding:"9px 12px",color:theme.gray500,whiteSpace:"nowrap"}}>{fmtData(oc.data)}</td>
                    <td style={{padding:"9px 12px",color:theme.gray600}}>{equipe.find(e=>e.id===oc.responsavel)?.nome||"—"}</td>
                    <td style={{padding:"9px 12px",color:theme.gray700,maxWidth:300}}>{oc.obs}</td>
                    <td style={{padding:"9px 12px",whiteSpace:"nowrap"}}>
                      {oc.origemCaptura&&oc.origemCaptura!=="Manual"?(
                        <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:700,
                          background:oc.origemCaptura==="Legal Mail"?"#dbeafe":oc.origemCaptura==="DataJud"?"#fef3c7":"#f0fdf4",
                          color:oc.origemCaptura==="Legal Mail"?"#1e40af":oc.origemCaptura==="DataJud"?"#92400e":"#166534"}}>
                          {oc.origemCaptura==="Legal Mail"?"📨 Legal Mail":oc.origemCaptura==="DataJud"?"⚖️ DataJud":"🔗 "+oc.origemCaptura}
                        </span>
                      ):<span style={{fontSize:11,color:theme.gray400}}>Manual</span>}
                    </td>
                    <td style={{padding:"9px 12px"}}>
                      <button onClick={()=>{
                        const novaConf = oc.confidencial===false;
                        setData(prev=>({...prev,baseOperacional:(prev.baseOperacional||[]).map(r=>r.id!==oc._reg.id?r:{...r,ocorrencias:(r.ocorrencias||[]).map(o=>o.id===oc.id?{...o,confidencial:novaConf}:o)})}));
                      }} style={{padding:"4px 10px",borderRadius:12,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,
                        background:oc.confidencial!==false?"#fef3c7":"#f0fdf4",
                        color:oc.confidencial!==false?"#92400e":theme.green}}>
                        {oc.confidencial!==false?"🔒 Confidencial":"📋 Visível"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      }
      {todasOcs.some(o=>o.confidencial===false)&&(
        <div style={{marginTop:16,paddingTop:14,borderTop:`1px solid ${theme.gray100}`,display:"flex",justifyContent:"flex-end"}}>
          <Btn onClick={()=>{
            const ocsCliente=todasOcs.filter(o=>o.confidencial===false);
            const rows=ocsCliente.map(oc=>'<tr><td>'+oc._reg.numContrato+'</td><td>'+getNomeCli(oc._reg.clienteId)+'</td><td>'+fmtData(oc.data)+'</td><td>'+(equipe.find(e=>e.id===oc.responsavel)?.nome||'—')+'</td><td>'+oc.obs+'</td></tr>').join('');
            const html='<html><head><style>body{font-family:serif;padding:32px;}h1{font-size:20px;}table{width:100%;border-collapse:collapse;font-size:13px;}th{background:#f3f4f6;padding:8px 12px;text-align:left;}td{padding:8px 12px;border-bottom:1px solid #e5e7eb;}</style></head><body><h1>Relatório de Ocorrências — '+mesAuditoria+'</h1><p style="color:#6b7280;margin-bottom:24px;">Gerado em '+new Date().toLocaleDateString('pt-BR')+'</p><table><thead><tr><th>Contrato</th><th>Cliente</th><th>Data</th><th>Responsável</th><th>Ocorrência</th></tr></thead><tbody>'+rows+'</tbody></table></body></html>';
            const w=window.open('','_blank'); w.document.write(html); w.document.close(); w.print();
          }}>🖨️ Gerar Relatório do Cliente</Btn>
        </div>
      )}
    </>
  );
}

function BadgeDocumentos({ documentos, theme }) {
  const pend = documentos.filter(d => !d.recebido && d.obrigatorio !== false).length;
  return <span style={{fontSize:10,padding:'2px 6px',borderRadius:10,background:pend===0?theme.green+'22':theme.amber+'22',color:pend===0?theme.green:theme.amber,fontWeight:600}}>{pend===0?'✅ Completo':pend+' pendente'+(pend!==1?'s':'')}</span>;
}


function BadgeTarefas({ tarefas, diasAteVencer, theme }) {
  const venc = (tarefas||[]).filter(t=>t.status!=='Concluída'&&t.prazoAtual&&diasAteVencer(t.prazoAtual)<0).length;
  const pend = (tarefas||[]).filter(t=>t.status!=='Concluída').length;
  if (venc > 0) return <span style={{fontSize:10,padding:'2px 6px',borderRadius:10,background:theme.red+'22',color:theme.red,fontWeight:600}}>⚠️ {venc} vencida{venc!==1?'s':''}</span>;
  if (pend === 0 && (tarefas||[]).length > 0) return <span style={{fontSize:10,padding:'2px 6px',borderRadius:10,background:theme.green+'22',color:theme.green,fontWeight:600}}>✅ Concluídas</span>;
  return null;
}


function ModalOcorrenciasContrato({ ocClienteModal, clientes, data, setData, setOcClienteModal, novaOcCli, setNovaOcCli, hoje, theme, FormField, Input, Btn, Modal }) {
  if (!ocClienteModal) return null;
  const cont = ocClienteModal;
  const cli  = clientes.find(c => c.id === cont.clienteId);
  const TIPOS_OC_CLI = ['Distribuicao','Despacho','Decisao','Sentenca','Recurso','Peticao','Audiencia','Atendimento','Follow-up','Outros'];
  const historicoFiltrado = (cli?.historico||[]).filter(h => h.numContrato===cont.numContrato||h.contratoId===cont.id||!h.numContrato);

  const salvarOcorrencia = () => {
    if (!novaOcCli.tipo || !novaOcCli.descricao) return;
    const oc = { id:'OC'+Date.now().toString().slice(-7), tipo:novaOcCli.tipo, descricao:novaOcCli.descricao, data:hoje, numContrato:cont.numContrato, contratoId:cont.id };
    setData(prev => ({...prev, clientes: prev.clientes.map(cl => cl.id===cont.clienteId ? {...cl, historico:[...(cl.historico||[]),oc]} : cl)}));
    setNovaOcCli({tipo:'', descricao:'', numContrato:cont.numContrato});
  };

  return (
    <Modal open={!!ocClienteModal} onClose={()=>{setOcClienteModal(null);setNovaOcCli({tipo:'',descricao:'',numContrato:cont.numContrato});}} title={'Ocorrências — '+(cont.clienteNome||'Contrato')+' · '+cont.numContrato} width={620}>
      <div style={{background:theme.gold+'08',border:'1px solid '+theme.gold+'33',borderRadius:8,padding:'14px 16px',marginBottom:16}}>
        <p style={{fontSize:12,fontWeight:700,color:theme.gray800,marginBottom:10}}>+ Registrar Nova Ocorrência</p>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 16px'}}>
          <FormField label="Tipo">
            <select value={novaOcCli.tipo} onChange={e=>setNovaOcCli({...novaOcCli,tipo:e.target.value})}
              style={{width:'100%',padding:'8px 10px',border:'1px solid '+theme.gray200,borderRadius:7,fontSize:12}}>
              <option value="">Selecione...</option>
              {TIPOS_OC_CLI.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </FormField>
          <FormField label="Data"><Input type="date" value={novaOcCli.data||hoje} onChange={v=>setNovaOcCli({...novaOcCli,data:v})}/></FormField>
        </div>
        <FormField label="Descrição">
          <textarea value={novaOcCli.descricao} onChange={e=>setNovaOcCli({...novaOcCli,descricao:e.target.value})}
            rows={2} style={{width:'100%',padding:'8px 10px',border:'1px solid '+theme.gray200,borderRadius:7,fontSize:12,resize:'vertical'}}/>
        </FormField>
        <div style={{display:'flex',justifyContent:'flex-end'}}>
          <Btn size="sm" onClick={salvarOcorrencia}>Registrar</Btn>
        </div>
      </div>
      <div style={{maxHeight:300,overflowY:'auto'}}>
        {historicoFiltrado.length===0
          ? <p style={{fontSize:12,color:theme.gray400,textAlign:'center',padding:'20px 0'}}>Nenhuma ocorrência registrada.</p>
          : [...historicoFiltrado].reverse().map(h=>(
            <div key={h.id} style={{padding:'10px 12px',borderBottom:'1px solid '+theme.gray100}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                <span style={{fontSize:11,fontWeight:700,color:theme.gold}}>{h.tipo}</span>
                <span style={{fontSize:10,color:theme.gray400}}>{h.data ? new Date(h.data+'T00:00:00').toLocaleDateString('pt-BR') : ''}</span>
              </div>
              <p style={{fontSize:12,color:theme.gray700,margin:0}}>{h.descricao}</p>
            </div>
          ))
        }
      </div>
      <div style={{display:'flex',justifyContent:'flex-end',paddingTop:12}}>
        <Btn variant="secondary" onClick={()=>{setOcClienteModal(null);setNovaOcCli({tipo:'',descricao:''});}}>Fechar</Btn>
      </div>
    </Modal>
  );
}


// Componente de input monetário — digita livremente, formata ao sair
function InputMoeda({ value, onChange, placeholder, style }) {
  const [texto, setTexto] = React.useState('');
  const [focado, setFocado] = React.useState(false);

  const fmtBR = n => n > 0 ? n.toLocaleString('pt-BR', {minimumFractionDigits:2, maximumFractionDigits:2}) : '';
  const parseBR = v => {
    const s = (v+'').trim().replace(/[^\d,]/g,'');
    if (!s) return 0;
    // Tratar vírgula como decimal
    const partes = s.split(',');
    if (partes.length === 2) return parseFloat(partes[0] + '.' + partes[1]) || 0;
    return parseFloat(partes[0]) || 0;
  };

  const handleFocus = () => {
    setFocado(true);
    setTexto(value > 0 ? fmtBR(value) : '');
  };

  const handleChange = e => {
    const v = e.target.value.replace(/[^\d,]/g,'');
    setTexto(v);
  };

  const handleBlur = e => {
    setFocado(false);
    const n = parseBR(e.target.value);
    onChange(n);
    setTexto('');
  };

  return (
    <input
      type="text"
      value={focado ? texto : fmtBR(value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder || '0,00'}
      style={{width:'100%',padding:'8px 10px',border:'1px solid #e2e8f0',borderRadius:7,fontSize:13,outline:'none', ...style}}
    />
  );
}


// ═══════════════════════════════════════════════════════════════
// Helper: Relatório Mensal de Ocorrências — Base Operacional
// ═══════════════════════════════════════════════════════════════
function RelMensalBase({ registros, contratos, processos, clientes, produtos, equipe, mesAuditoria, setMesAuditoria, getNomeCli, getNomeProd, fmtData, theme, Card, Btn }) {
  const [subAba, setSubAba] = React.useState("auditoria"); // auditoria | rel1 | rel2
  const [filtroCliente, setFiltroCliente] = React.useState("");
  const [filtroProduto, setFiltroProduto] = React.useState("");
  const [filtroContrato, setFiltroContrato] = React.useState("");

  // Contratos ativos
  const contratosAtivos = contratos.filter(ct =>
    ct.status === "Em andamento" || ct.status === "Ativo"
  );

  // Para cada contrato ativo, buscar ocorrências do mês no registro da base operacional
  const dadosContratos = contratosAtivos.map(ct => {
    const reg = registros.find(r => r.contratoId === ct.id);
    const todasOcs = reg?.ocorrencias || [];
    const ocsNaoConf = todasOcs.filter(o =>
      o.confidencial === false &&
      (o.data || "").slice(0, 7) === mesAuditoria
    );
    const ocsConf = todasOcs.filter(o =>
      o.confidencial !== false &&
      (o.data || "").slice(0, 7) === mesAuditoria
    );
    const procsVinc = (ct.processosIds || []).map(pid =>
      processos.find(p => p.id === pid)
    ).filter(Boolean);
    return { ct, reg, ocsNaoConf, ocsConf, procsVinc };
  });

  // Aplicar filtros
  const dadosFiltrados = dadosContratos.filter(d => {
    if (filtroCliente && d.ct.clienteId !== filtroCliente) return false;
    if (filtroProduto && d.ct.produtoId !== filtroProduto) return false;
    if (filtroContrato && d.ct.id !== filtroContrato) return false;
    return true;
  });

  // Agrupar por cliente para lógica do Rel.1 / Rel.2
  const porCliente = {};
  dadosFiltrados.forEach(d => {
    const cliId = d.ct.clienteId;
    if (!porCliente[cliId]) porCliente[cliId] = [];
    porCliente[cliId].push(d);
  });

  // Rel.1: clientes que têm AO MENOS 1 contrato com ocorrências não confidenciais no mês
  const clientesRel1 = Object.entries(porCliente).filter(([, lista]) =>
    lista.some(d => d.ocsNaoConf.length > 0)
  );

  // Rel.2: clientes que NÃO tiveram nenhuma ocorrência não confidencial
  const clientesRel2 = Object.entries(porCliente).filter(([, lista]) =>
    lista.every(d => d.ocsNaoConf.length === 0)
  );

  const fmtMes = m => {
    if (!m) return "";
    const [y, mo] = m.split("-");
    const meses = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
    return meses[parseInt(mo) - 1] + "/" + y;
  };

  const RODAPE = `<div style="margin-top:32px;padding-top:16px;border-top:2px solid #C9A84C;display:grid;grid-template-columns:1fr 1fr;gap:16px;font-size:11px;color:#5C5448;">
    <div><b>Nelson Jose Alves</b><br>Advogado · OAB 398.875/SP</div>
    <div><b>Annie Lemos Alves Correa</b><br>Advogada · OAB 446.942/SP</div>
  </div>
  <div style="text-align:center;margin-top:8px;font-size:10px;color:#999;">Alves &amp; Correa Advogados · Gerado em ${new Date().toLocaleString("pt-BR")} · Confidencial</div>`;

  const ESTILO = `body{font-family:Arial,sans-serif;padding:28px;color:#2A2420;font-size:12px}
    h1{font-size:14px;font-weight:bold;text-align:center;text-transform:uppercase;border-bottom:2px solid #C9A84C;padding-bottom:8px;margin-bottom:4px}
    h2{font-size:12px;font-weight:bold;color:#1A1610;margin:20px 0 4px;text-transform:uppercase;letter-spacing:.5px}
    h3{font-size:11px;font-weight:bold;color:#5C5448;margin:12px 0 4px;border-left:3px solid #C9A84C;padding-left:8px}
    p{margin:4px 0;font-size:12px;line-height:1.6}
    .ref{font-size:11px;color:#888;text-align:center;margin-bottom:20px}
    .ocs{width:100%;border-collapse:collapse;margin:8px 0 16px;font-size:11px}
    .ocs th{background:#1A1610;color:#C9A84C;padding:6px 10px;text-align:left}
    .ocs td{padding:6px 10px;border-bottom:1px solid #eee}
    .ocs tr:nth-child(even) td{background:#F9F7F2}
    .proc{display:inline-block;padding:2px 8px;border-radius:10px;background:#dbeafe;color:#1d4ed8;font-size:10px;margin:2px}
    @media print{@page{margin:15mm}}`;

  const gerarPDF = (tipo) => {
    const lista = tipo === 1 ? clientesRel1 : clientesRel2;
    if (lista.length === 0) return alert("Nenhum cliente para este relatório no período selecionado.");
    const mesFormatado = fmtMes(mesAuditoria);
    let corpo = "";

    lista.forEach(([cliId, contrList]) => {
      const cli = clientes.find(cl => cl.id === cliId);
      const nomeCliente = cli?.nome || "—";

      corpo += `<div style="page-break-inside:avoid;margin-bottom:32px;border:1px solid #eee;border-radius:8px;padding:20px">`;
      corpo += `<p class="ref">Referência: ${mesFormatado} &nbsp;|&nbsp; Cliente: <b>${nomeCliente}</b></p>`;
      corpo += `<h2>1. Introdução</h2>`;
      corpo += `<p>Prezado(a) ${nomeCliente},</p>`;
      corpo += `<p>Encaminhamos o presente relatório mensal com o objetivo de mantê-lo(a) atualizado(a) sobre o andamento do seu processo no período de ${mesFormatado}. Nosso compromisso é garantir transparência, acompanhamento contínuo e segurança na condução da sua demanda.</p>`;
      corpo += `<h2>2. Andamento Processual no Período</h2>`;

      if (tipo === 1) {
        contrList.forEach(d => {
          const prod = produtos.find(p => p.id === d.ct.produtoId);
          corpo += `<h3>${d.ct.numContrato || "—"} · ${prod?.nome || "—"}</h3>`;
          if (d.procsVinc.length > 0) {
            corpo += `<p>` + d.procsVinc.map(p =>
              `<span class="proc">⚖️ ${p.numeroProcesso || "Sem nº"} · ${p.tipoProcesso}</span>`
            ).join(" ") + `</p>`;
          }
          if (d.ocsNaoConf.length > 0) {
            corpo += `<table class="ocs"><thead><tr><th>Data</th><th>Tipo</th><th>Movimentação</th></tr></thead><tbody>`;
            d.ocsNaoConf.forEach(o => {
              const tipoLabel = o.tipoNome||o.tipo||"—";
              corpo += `<tr><td style="white-space:nowrap">${fmtData(o.data)}</td><td style="white-space:nowrap;color:#5C5448">${tipoLabel}</td><td>${o.obs || o.descricao || "—"}</td></tr>`;
            });
            corpo += `</tbody></table>`;
          } else {
            corpo += `<p><i>Sem movimentação não confidencial neste contrato no período.</i></p>`;
          }
        });
        corpo += `<p>Cada ato foi analisado com atenção e as medidas cabíveis foram adotadas dentro dos prazos legais. Além disso, realizamos a análise estratégica do impacto dessa movimentação no andamento do processo, avaliando riscos, oportunidades e os possíveis próximos desdobramentos.</p>`;
      } else {
        contrList.forEach(d => {
          const prod = produtos.find(p => p.id === d.ct.produtoId);
          corpo += `<h3>${d.ct.numContrato || "—"} · ${prod?.nome || "—"}</h3>`;
          if (d.procsVinc.length > 0) {
            corpo += `<p>` + d.procsVinc.map(p =>
              `<span class="proc">⚖️ ${p.numeroProcesso || "Sem nº"} · ${p.tipoProcesso}</span>`
            ).join(" ") + `</p>`;
          }
        });
        corpo += `<p>No período analisado, não houve nova movimentação processual registrada nos autos. Destacamos que essa situação é comum em determinados momentos do trâmite judicial, especialmente considerando os prazos processuais e a rotina do Judiciário. O processo permanece em acompanhamento contínuo, com vigilância constante por nossa equipe.</p>`;
      }

      corpo += `<h2>3. Considerações Estratégicas</h2>`;
      corpo += `<p>Seguimos atuando de forma técnica e estratégica, sempre atentos às oportunidades processuais e aos próximos desdobramentos. Nosso foco permanece na condução responsável e eficiente da sua demanda, buscando preservar seus interesses com segurança e diligência.</p>`;
      corpo += `<p>Caso haja qualquer novidade relevante fora do ciclo mensal de relatórios, você será prontamente comunicado(a).</p>`;
      corpo += `<h2>4. Conclusão</h2>`;
      corpo += `<p>Reforçamos nosso compromisso com a transparência, organização e acompanhamento próximo do seu caso. Permanecemos à disposição para esclarecer eventuais dúvidas e prestar os esclarecimentos necessários.</p>`;
      corpo += `<p>Conte conosco para seguir conduzindo sua demanda com responsabilidade e dedicação.</p>`;
      corpo += `<p style="margin-top:12px"><b>Atenciosamente,</b></p>`;
      corpo += RODAPE;
      corpo += `</div>`;
    });

    const titulo = tipo === 1 ? "Relatório Mensal — Com Ocorrências" : "Relatório Mensal — Sem Ocorrências";
    const html = `<html><head><title>${titulo}</title><style>${ESTILO}</style></head><body>
      <h1>Relatório Mensal de Acompanhamento Processual</h1>
      ${corpo}
    </body></html>`;
    const w = window.open("", "_blank"); w.document.write(html); w.document.close(); w.print();
  };

  // ── Filtros UI ──────────────────────────────────────────────
  const FiltrosUI = () => (
    <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:16,alignItems:"flex-end"}}>
      <div>
        <p style={{fontSize:11,color:theme.gray500,marginBottom:4}}>Mês/Ano</p>
        <input type="month" value={mesAuditoria} onChange={e=>setMesAuditoria(e.target.value)}
          style={{padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none"}}/>
      </div>
      <div>
        <p style={{fontSize:11,color:theme.gray500,marginBottom:4}}>Cliente</p>
        <select value={filtroCliente} onChange={e=>setFiltroCliente(e.target.value)}
          style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none",background:"#fff",minWidth:160}}>
          <option value="">Todos</option>
          {clientes.filter(cl=>cl.ativo!==false).sort((a,b)=>a.nome.localeCompare(b.nome,"pt-BR")).map(cl=>(
            <option key={cl.id} value={cl.id}>{cl.nome}</option>
          ))}
        </select>
      </div>
      <div>
        <p style={{fontSize:11,color:theme.gray500,marginBottom:4}}>Produto</p>
        <select value={filtroProduto} onChange={e=>setFiltroProduto(e.target.value)}
          style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none",background:"#fff",minWidth:160}}>
          <option value="">Todos</option>
          {[...new Set(contratosAtivos.map(ct=>ct.produtoId))].map(pid=>(
            <option key={pid} value={pid}>{getNomeProd(pid)}</option>
          ))}
        </select>
      </div>
      <div>
        <p style={{fontSize:11,color:theme.gray500,marginBottom:4}}>Contrato</p>
        <select value={filtroContrato} onChange={e=>setFiltroContrato(e.target.value)}
          style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none",background:"#fff",minWidth:140}}>
          <option value="">Todos</option>
          {contratosAtivos.map(ct=>(
            <option key={ct.id} value={ct.id}>{ct.numContrato||ct.id}</option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div>
      {/* Sub-abas */}
      <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
        {[
          ["auditoria","🔍 Auditoria"],
          ["rel1","📄 Relatório 1 — Com Ocorrências"],
          ["rel2","📄 Relatório 2 — Sem Ocorrências"],
        ].map(([id,label])=>(
          <button key={id} onClick={()=>setSubAba(id)} style={{
            padding:"8px 16px",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:600,
            border:`1px solid ${subAba===id?theme.gold:theme.gray200}`,
            background:subAba===id?`${theme.gold}18`:theme.white,
            color:subAba===id?theme.goldDark||"#92400e":theme.gray600,
            transition:"all .15s"
          }}>{label}</button>
        ))}
      </div>

      {/* ── SUB-ABA: AUDITORIA ── */}
      {subAba==="auditoria"&&(
        <div>
          <FiltrosUI/>
          <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
            <span style={{fontSize:12,padding:"4px 12px",borderRadius:20,background:theme.gray100,color:theme.gray700}}>
              📊 {dadosFiltrados.reduce((s,d)=>s+d.ocsNaoConf.length+d.ocsConf.length,0)} ocorrência(s) no período
            </span>
            <span style={{fontSize:12,padding:"4px 12px",borderRadius:20,background:"#fef3c7",color:"#92400e"}}>
              🔒 {dadosFiltrados.reduce((s,d)=>s+d.ocsConf.length,0)} confidencial(is)
            </span>
            <span style={{fontSize:12,padding:"4px 12px",borderRadius:20,background:"#f0fdf4",color:theme.green}}>
              📋 {dadosFiltrados.reduce((s,d)=>s+d.ocsNaoConf.length,0)} para o cliente
            </span>
            <span style={{fontSize:12,padding:"4px 12px",borderRadius:20,background:"#eff6ff",color:theme.blue}}>
              📁 {clientesRel1.length} cliente(s) no Rel.1 · {clientesRel2.length} no Rel.2
            </span>
          </div>
          {dadosFiltrados.length===0
            ? <Card><p style={{textAlign:"center",padding:"32px 0",color:theme.gray400,fontSize:13}}>Nenhum contrato ativo encontrado para o filtro selecionado.</p></Card>
            : <Card>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:theme.gray50}}>
                        {["Cliente","Contrato","Produto","Processos","Ocs. Visíveis","Ocs. Conf.","Rel."].map(h=>(
                          <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dadosFiltrados.map((d,i)=>{
                        const temOcs = d.ocsNaoConf.length > 0;
                        return (
                          <tr key={d.ct.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?"#fff":theme.gray50}}>
                            <td style={{padding:"9px 12px",fontWeight:600,color:theme.gray900}}>{getNomeCli(d.ct.clienteId)}</td>
                            <td style={{padding:"9px 12px",color:theme.gray700}}>{d.ct.numContrato||"—"}</td>
                            <td style={{padding:"9px 12px",color:theme.gray600,maxWidth:200}}>{getNomeProd(d.ct.produtoId)}</td>
                            <td style={{padding:"9px 12px"}}>
                              {d.procsVinc.map(p=>(
                                <span key={p.id} style={{fontSize:10,padding:"2px 6px",borderRadius:8,background:"#dbeafe",color:theme.blue,marginRight:4,whiteSpace:"nowrap"}}>
                                  ⚖️ {p.numeroProcesso||"Sem nº"}
                                </span>
                              ))}
                            </td>
                            <td style={{padding:"9px 12px",textAlign:"center"}}>
                              {d.ocsNaoConf.length>0
                                ? <span style={{fontWeight:700,color:theme.green}}>{d.ocsNaoConf.length}</span>
                                : <span style={{color:theme.gray400}}>—</span>}
                            </td>
                            <td style={{padding:"9px 12px",textAlign:"center"}}>
                              {d.ocsConf.length>0
                                ? <span style={{fontWeight:700,color:theme.amber}}>{d.ocsConf.length}</span>
                                : <span style={{color:theme.gray400}}>—</span>}
                            </td>
                            <td style={{padding:"9px 12px",textAlign:"center"}}>
                              <span style={{fontSize:11,padding:"3px 8px",borderRadius:10,fontWeight:600,
                                background:temOcs?"#dcfce7":"#eff6ff",
                                color:temOcs?theme.green:theme.blue}}>
                                {temOcs?"Rel.1":"Rel.2"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
          }
        </div>
      )}

      {/* ── SUB-ABA: REL.1 ── */}
      {subAba==="rel1"&&(
        <div>
          <FiltrosUI/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{display:"flex",gap:10}}>
              <span style={{fontSize:12,padding:"4px 12px",borderRadius:20,background:"#dcfce7",color:theme.green,fontWeight:600}}>
                {clientesRel1.length} cliente(s) com ocorrências em {fmtMes(mesAuditoria)}
              </span>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <span style={{fontSize:11,color:theme.gray400,fontStyle:"italic"}}>
                📬 Para envio ao cliente, use a aba Comunicações → Fila (dia 5)
              </span>
              <Btn onClick={()=>gerarPDF(1)}>🖨️ Imprimir Arquivo Interno</Btn>
            </div>
          </div>
          {clientesRel1.length===0
            ? <Card><p style={{textAlign:"center",padding:"32px 0",color:theme.gray400,fontSize:13}}>Nenhum cliente com ocorrências não confidenciais no período.</p></Card>
            : clientesRel1.map(([cliId, contrList])=>{
                const cli = clientes.find(cl=>cl.id===cliId);
                return (
                  <Card key={cliId} style={{marginBottom:16,border:`1px solid ${theme.green}44`}}>
                    <p style={{fontSize:14,fontWeight:700,color:theme.gray900,marginBottom:12}}>
                      👤 {cli?.nome||"—"}
                    </p>
                    {contrList.map(d=>(
                      <div key={d.ct.id} style={{marginBottom:12,paddingLeft:12,borderLeft:`3px solid ${theme.gold}`}}>
                        <p style={{fontSize:12,fontWeight:600,color:theme.gray800,marginBottom:4}}>
                          {d.ct.numContrato||"—"} · {getNomeProd(d.ct.produtoId)}
                        </p>
                        {d.procsVinc.map(p=>(
                          <span key={p.id} style={{fontSize:10,padding:"2px 8px",borderRadius:8,background:"#dbeafe",color:theme.blue,marginRight:4}}>⚖️ {p.numeroProcesso||"Sem nº"} · {p.tipoProcesso}</span>
                        ))}
                        {d.ocsNaoConf.length>0
                          ? <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,marginTop:8}}>
                              <thead><tr style={{background:theme.gray50}}>
                                <th style={{padding:"6px 10px",textAlign:"left",fontSize:10,color:theme.gray400}}>Data</th>
                                <th style={{padding:"6px 10px",textAlign:"left",fontSize:10,color:theme.gray400}}>Tipo</th>
                                <th style={{padding:"6px 10px",textAlign:"left",fontSize:10,color:theme.gray400}}>Movimentação</th>
                              </tr></thead>
                              <tbody>
                                {d.ocsNaoConf.map((o,i)=>(
                                  <tr key={o.id||i} style={{borderBottom:`1px solid ${theme.gray100}`}}>
                                    <td style={{padding:"6px 10px",whiteSpace:"nowrap",color:theme.gray500}}>{fmtData(o.data)}</td>
                                    <td style={{padding:"6px 10px",whiteSpace:"nowrap"}}>{(o.tipoNome||o.tipo)&&<span style={{fontSize:10,padding:"2px 6px",borderRadius:8,background:"#dbeafe",color:"#1d4ed8",fontWeight:600}}>{o.tipoNome||o.tipo}</span>}</td>
                                    <td style={{padding:"6px 10px",color:theme.gray700}}>{o.obs||o.descricao||"—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          : <p style={{fontSize:11,color:theme.gray400,fontStyle:"italic",marginTop:6}}>Sem ocorrências visíveis neste contrato.</p>
                        }
                      </div>
                    ))}
                  </Card>
                );
              })
          }
        </div>
      )}

      {/* ── SUB-ABA: REL.2 ── */}
      {subAba==="rel2"&&(
        <div>
          <FiltrosUI/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <span style={{fontSize:12,padding:"4px 12px",borderRadius:20,background:"#eff6ff",color:theme.blue,fontWeight:600}}>
              {clientesRel2.length} cliente(s) sem ocorrências em {fmtMes(mesAuditoria)}
            </span>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <span style={{fontSize:11,color:theme.gray400,fontStyle:"italic"}}>
                📬 Para envio ao cliente, use a aba Comunicações → Fila (dia 5)
              </span>
              <Btn onClick={()=>gerarPDF(2)}>🖨️ Imprimir Arquivo Interno</Btn>
            </div>
          </div>
          {clientesRel2.length===0
            ? <Card><p style={{textAlign:"center",padding:"32px 0",color:theme.gray400,fontSize:13}}>Nenhum cliente sem ocorrências no período.</p></Card>
            : clientesRel2.map(([cliId, contrList])=>{
                const cli = clientes.find(cl=>cl.id===cliId);
                return (
                  <Card key={cliId} style={{marginBottom:16,border:`1px solid ${theme.blue}44`}}>
                    <p style={{fontSize:14,fontWeight:700,color:theme.gray900,marginBottom:8}}>
                      👤 {cli?.nome||"—"}
                    </p>
                    {contrList.map(d=>(
                      <div key={d.ct.id} style={{marginBottom:8,paddingLeft:12,borderLeft:`3px solid ${theme.blue}`}}>
                        <p style={{fontSize:12,fontWeight:600,color:theme.gray800}}>
                          {d.ct.numContrato||"—"} · {getNomeProd(d.ct.produtoId)}
                        </p>
                        {d.procsVinc.map(p=>(
                          <span key={p.id} style={{fontSize:10,padding:"2px 8px",borderRadius:8,background:"#dbeafe",color:theme.blue,marginRight:4}}>⚖️ {p.numeroProcesso||"Sem nº"} · {p.tipoProcesso}</span>
                        ))}
                      </div>
                    ))}
                    <p style={{fontSize:11,color:theme.gray500,fontStyle:"italic",marginTop:4}}>
                      Sem movimentação registrada no período.
                    </p>
                  </Card>
                );
              })
          }
        </div>
      )}
    </div>
  );
}


function SecaoProcessosVinculados({ processos, clientes, clienteId, processosIds, onToggle, theme }) {
  // Resolver clienteId — pode ser ID real ou nome (contratos importados)
  const cliObj  = (clientes||[]).find(c => c.id === clienteId || c.nome === clienteId);
  const cliNome = cliObj?.nome || clienteId || "";
  const cliId   = cliObj?.id   || clienteId || "";

  // Filtrar processos do cliente — por ID direto, por nome do cliente, ou pelo clienteId como nome
  const procsCli = (processos||[]).filter(p => {
    if (p.clienteId === cliId) return true;
    if (p.clienteId === clienteId) return true;
    // Verificar se o clienteId do processo resolve para o mesmo nome
    const pCli = (clientes||[]).find(c => c.id === p.clienteId);
    if (pCli && cliNome && pCli.nome.toLowerCase().trim() === cliNome.toLowerCase().trim()) return true;
    return false;
  });

  return (
    <div style={{marginTop:12,padding:"12px 14px",background:"#f0f9ff",border:`1px solid ${theme.blue}33`,borderRadius:8}}>
      <p style={{fontSize:12,fontWeight:700,color:theme.gray700,marginBottom:10}}>
        ⚖️ Processos Vinculados a este Contrato
        {cliNome && <span style={{fontSize:11,color:theme.gray500,fontWeight:400}}> — {cliNome}</span>}
      </p>
      {!clienteId ? (
        <p style={{fontSize:11,color:"#92400e",margin:0}}>⚠️ Contrato sem cliente definido.</p>
      ) : procsCli.length === 0 ? (
        <div style={{padding:"8px 12px",background:"#fffbec",border:"1px solid #fde68a",borderRadius:7,fontSize:11,color:"#92400e"}}>
          ⚠️ Nenhum processo cadastrado para <strong>{cliNome}</strong>.
          Acesse <strong>Processos → + Novo Processo</strong> para cadastrar.
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {procsCli.map(p => {
            const sel = (processosIds||[]).includes(p.id);
            return (
              <label key={p.id} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",
                padding:"8px 12px",borderRadius:7,
                background:sel?"#dbeafe":"#fff",
                border:`2px solid ${sel?theme.blue:theme.gray200}`}}>
                <input type="checkbox" checked={sel}
                  onChange={e=>onToggle(p.id, e.target.checked)}
                  style={{accentColor:theme.blue}}/>
                <div style={{flex:1}}>
                  <p style={{fontSize:12,fontWeight:700,color:theme.gray900,margin:0,fontFamily:"monospace"}}>
                    {p.numeroProcesso||"Sem número"}
                  </p>
                  <p style={{fontSize:11,color:theme.gray500,margin:"2px 0 0"}}>
                    {p.tipoProcesso||"—"} · {p.area||"—"} · TJ/{p.tjUf||"—"} · {p.status||"—"}
                  </p>
                </div>
                {sel && <span style={{fontSize:10,padding:"2px 7px",borderRadius:3,
                  background:"#dbeafe",color:theme.blue,fontWeight:600}}>✓ Vinculado</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================
// DADOS EMPRESARIAIS FORM — Espelho do contrato (Entrada e Triagem)
// Campos: faturamentos, regimes, atividadePrincipal, atividadesSecundarias
// Obrigatório para avançar da Entrada e Triagem → Produção (PJ)
// ============================================================
function DadosEmpresariaisForm({ contrato, setData, onDadosOk, clienteTipo }) {
  const isPJ = clienteTipo === "PJ";

  // Para PF: notificar OK imediatamente e não renderizar nada
  React.useEffect(() => { if (!isPJ) onDadosOk?.(true); }, [isPJ]);
  if (!isPJ) return null;
  // Lê dados do contrato ou estado local enquanto edita
  const [editando, setEditando] = useState(false);
  const [fat,  setFat]  = useState(() => contrato?.faturamentos || []);
  const [reg,  setReg]  = useState(() => contrato?.regimes || []);
  const [atP,  setAtP]  = useState(() => contrato?.atividadePrincipal || "");
  const [atS,  setAtS]  = useState(() => contrato?.atividadesSecundarias || []);
  const [saved, setSaved] = useState(false);

  // Sincronia: se contrato muda de fora, resetar
  const ct = contrato;
  const fatSalvo = ct?.faturamentos || [];
  const regSalvo = ct?.regimes || [];
  const atPSalvo = ct?.atividadePrincipal || "";
  const atSSalvo = ct?.atividadesSecundarias || [];

  // Computed: dados OK = pelo menos 1 faturamento, 1 regime e atividade principal preenchidos
  const dadosOk = (fatSalvo.length > 0 && fatSalvo.some(f => f.valor)) &&
                  regSalvo.length > 0 &&
                  atPSalvo.trim().length > 0;

  // Notificar pai
  React.useEffect(() => { onDadosOk?.(dadosOk); }, [dadosOk]);

  const salvar = () => {
    // contratoId pode vir do contrato ou do próprio reg via regM.contratoId
    const ctId = ct?.id;
    if (!ctId) { setSaved(true); setTimeout(() => setSaved(false), 2500); return; }
    setData(prev => ({
      ...prev,
      contratos: (prev.contratos || []).map(c =>
        c.id === ctId
          ? { ...c, faturamentos: fat, regimes: reg, atividadePrincipal: atP, atividadesSecundarias: atS }
          : c
      )
    }));
    setSaved(true);
    setEditando(false);
    setTimeout(() => setSaved(false), 2500);
  };

  const fmtFat = f => `${f.ano}: R$ ${f.valor || "—"}`;
  const fmtReg = r => `${r.tipo}${r.dataInicio ? ` (${r.dataInicio})` : ""}${r.dataFim ? ` até ${r.dataFim}` : ""}`;

  const REGIMES_OPC = ["Simples Nacional", "Lucro Presumido", "Lucro Real", "MEI"];
  const COR_CAMPO = { ok: "#f0fdf4", borda: "#bbf7d0", corTit: theme.green };
  const COR_VAZIO = { ok: "#fef9ec", borda: "#fde68a", corTit: "#92400e" };

  const statusCor = dadosOk ? COR_CAMPO : COR_VAZIO;

  return (
    <div style={{ marginBottom: 24, borderRadius: 10,
      background: statusCor.ok, border: `2px solid ${statusCor.borda}` }}>
      {/* Header */}
      <div style={{ padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center",
        borderBottom: `1px solid ${statusCor.borda}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>🏢</span>
          <div>
            <p style={{ fontSize: 14, fontWeight: 700, color: statusCor.corTit, margin: 0 }}>Dados Empresariais</p>
            <p style={{ fontSize: 11, color: theme.gray500, margin: "2px 0 0" }}>
              {dadosOk ? "✅ Preenchido — obrigatório confirmado" : "⚠️ Obrigatório para avançar de fase — preencha e salve"}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {saved && <span style={{ fontSize: 11, color: theme.green, fontWeight: 700 }}>✅ Salvo!</span>}
          {!editando
            ? <button onClick={() => setEditando(true)}
                style={{ fontSize: 12, padding: "6px 14px", borderRadius: 6, cursor: "pointer",
                  border: `1px solid ${statusCor.corTit}`, background: "transparent", color: statusCor.corTit, fontWeight: 600 }}>
                ✏️ {dadosOk ? "Editar" : "Preencher"}
              </button>
            : <>
                <button onClick={() => { setFat(fatSalvo); setReg(regSalvo); setAtP(atPSalvo); setAtS(atSSalvo); setEditando(false); }}
                  style={{ fontSize: 12, padding: "6px 12px", borderRadius: 6, cursor: "pointer",
                    border: `1px solid ${theme.gray200}`, background: theme.white, color: theme.gray500 }}>
                  Cancelar
                </button>
                <button onClick={salvar}
                  style={{ fontSize: 12, padding: "6px 16px", borderRadius: 6, cursor: "pointer",
                    border: "none", background: theme.green, color: "#fff", fontWeight: 700 }}>
                  💾 Salvar
                </button>
              </>
          }
        </div>
      </div>

      {/* Conteúdo */}
      <div style={{ padding: "16px 18px" }}>

        {/* ── FATURAMENTO ANUAL ── */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: theme.gray800, margin: 0 }}>
              💰 Faturamento Anual
              {!editando && fatSalvo.length === 0 && <span style={{ fontSize: 11, color: theme.red, fontWeight: 400, marginLeft: 8 }}>Não preenchido</span>}
            </p>
            {editando && (
              <button onClick={() => setFat(p => [...p, { id: "FAT"+Date.now(), ano: String(new Date().getFullYear()), valor: "" }])}
                style={{ fontSize: 11, padding: "3px 9px", borderRadius: 5, border: `1px solid ${theme.green}`,
                  background: "#f0fdf4", color: theme.green, cursor: "pointer", fontWeight: 600 }}>
                + Ano
              </button>
            )}
          </div>
          {!editando ? (
            fatSalvo.length === 0
              ? <p style={{ fontSize: 12, color: theme.gray400, fontStyle: "italic" }}>— Não informado</p>
              : <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {fatSalvo.map((f, i) => (
                    <span key={i} style={{ fontSize: 13, padding: "5px 12px", borderRadius: 7,
                      background: theme.white, border: `1px solid ${theme.gray200}`, fontWeight: 600, color: theme.gray800 }}>
                      {fmtFat(f)}
                    </span>
                  ))}
                </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {fat.map((f, i) => (
                <div key={f.id || i} style={{ display: "grid", gridTemplateColumns: "110px 1fr 30px", gap: 8, alignItems: "end" }}>
                  <div>
                    <p style={{ fontSize: 10, color: theme.gray400, marginBottom: 3 }}>Ano</p>
                    <input value={f.ano || ""} onChange={e => setFat(p => p.map((x,j)=>j===i?{...x,ano:e.target.value}:x))}
                      placeholder="2023" style={{ width: "100%", padding: "7px 10px", border: `1px solid ${theme.gray200}`, borderRadius: 6, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <p style={{ fontSize: 10, color: theme.gray400, marginBottom: 3 }}>Valor (R$)</p>
                    <input value={f.valor || ""} onChange={e => {
                      const limpo = e.target.value.replace(/[^\d]/g, "");
                      const num = parseInt(limpo || "0", 10) / 100;
                      const mask = num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      setFat(p => p.map((x,j)=>j===i?{...x,valor:mask}:x));
                    }} placeholder="0,00" style={{ width: "100%", padding: "7px 10px", border: `1px solid ${theme.gray200}`, borderRadius: 6, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                  </div>
                  <button onClick={() => setFat(p => p.filter((_,j)=>j!==i))}
                    style={{ fontSize: 16, color: theme.red, background: "none", border: "none", cursor: "pointer", paddingBottom: 4 }}>×</button>
                </div>
              ))}
              {fat.length === 0 && <p style={{ fontSize: 12, color: theme.gray400, fontStyle: "italic" }}>Clique em "+ Ano" para adicionar</p>}
            </div>
          )}
        </div>

        {/* ── REGIMES TRIBUTÁRIOS ── */}
        <div style={{ marginBottom: 18, paddingTop: 14, borderTop: `1px solid ${statusCor.borda}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: theme.gray800, margin: 0 }}>
              📊 Regimes Tributários
              {!editando && regSalvo.length === 0 && <span style={{ fontSize: 11, color: theme.red, fontWeight: 400, marginLeft: 8 }}>Não preenchido</span>}
            </p>
            {editando && (
              <button onClick={() => setReg(p => [...p, { id: "REG"+Date.now(), tipo: "Simples Nacional", dataInicio: "", dataFim: "" }])}
                style={{ fontSize: 11, padding: "3px 9px", borderRadius: 5, border: `1px solid ${theme.blue}`,
                  background: "#eff6ff", color: theme.blue, cursor: "pointer", fontWeight: 600 }}>
                + Regime
              </button>
            )}
          </div>
          {!editando ? (
            regSalvo.length === 0
              ? <p style={{ fontSize: 12, color: theme.gray400, fontStyle: "italic" }}>— Não informado</p>
              : <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {regSalvo.map((r, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 12px",
                      borderRadius: 7, background: theme.white, border: `1px solid ${theme.blue}33` }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: theme.blue }}>{r.tipo}</span>
                      {r.dataInicio && <span style={{ fontSize: 11, color: theme.gray500 }}>de {r.dataInicio}</span>}
                      {r.dataFim && <span style={{ fontSize: 11, color: theme.gray500 }}>até {r.dataFim}</span>}
                    </div>
                  ))}
                </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {reg.map((r, i) => (
                <div key={r.id || i} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr auto", gap: 8, alignItems: "end",
                  padding: "10px 12px", background: theme.white, borderRadius: 7, border: `1px solid ${theme.gray200}` }}>
                  <div>
                    <p style={{ fontSize: 10, color: theme.gray400, marginBottom: 3 }}>Regime</p>
                    <select value={r.tipo} onChange={e => setReg(p => p.map((x,j)=>j===i?{...x,tipo:e.target.value}:x))}
                      style={{ width: "100%", padding: "7px 10px", border: `1px solid ${theme.gray200}`, borderRadius: 6, fontSize: 13, outline: "none" }}>
                      {REGIMES_OPC.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <p style={{ fontSize: 10, color: theme.gray400, marginBottom: 3 }}>Data Início</p>
                    <input type="date" value={r.dataInicio || ""} onChange={e => setReg(p => p.map((x,j)=>j===i?{...x,dataInicio:e.target.value}:x))}
                      style={{ width: "100%", padding: "7px 10px", border: `1px solid ${theme.gray200}`, borderRadius: 6, fontSize: 13, outline: "none" }} />
                  </div>
                  <div>
                    <p style={{ fontSize: 10, color: theme.gray400, marginBottom: 3 }}>Data Fim</p>
                    <input type="date" value={r.dataFim || ""} onChange={e => setReg(p => p.map((x,j)=>j===i?{...x,dataFim:e.target.value}:x))}
                      style={{ width: "100%", padding: "7px 10px", border: `1px solid ${theme.gray200}`, borderRadius: 6, fontSize: 13, outline: "none" }} />
                  </div>
                  <button onClick={() => setReg(p => p.filter((_,j)=>j!==i))}
                    style={{ fontSize: 16, color: theme.red, background: "none", border: "none", cursor: "pointer", alignSelf: "center", marginTop: 16 }}>×</button>
                </div>
              ))}
              {reg.length === 0 && <p style={{ fontSize: 12, color: theme.gray400, fontStyle: "italic" }}>Clique em "+ Regime"</p>}
            </div>
          )}
        </div>

        {/* ── ATIVIDADES DA EMPRESA ── */}
        <div style={{ paddingTop: 14, borderTop: `1px solid ${statusCor.borda}` }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: theme.gray800, marginBottom: 10 }}>
            🏭 Atividades da Empresa
            {!editando && !atPSalvo.trim() && <span style={{ fontSize: 11, color: theme.red, fontWeight: 400, marginLeft: 8 }}>Não preenchido</span>}
          </p>
          {!editando ? (
            <div>
              {!atPSalvo.trim()
                ? <p style={{ fontSize: 12, color: theme.gray400, fontStyle: "italic" }}>— Não informado</p>
                : <>
                    <div style={{ marginBottom: 8, padding: "8px 12px", borderRadius: 7, background: theme.white, border: `1px solid ${theme.green}44` }}>
                      <p style={{ fontSize: 10, color: theme.green, fontWeight: 700, marginBottom: 2, textTransform: "uppercase", letterSpacing: .6 }}>Principal</p>
                      <p style={{ fontSize: 13, color: theme.gray800, margin: 0 }}>{atPSalvo}</p>
                    </div>
                    {atSSalvo.length > 0 && (
                      <div>
                        <p style={{ fontSize: 10, color: theme.gray500, fontWeight: 600, marginBottom: 5, textTransform: "uppercase", letterSpacing: .6 }}>Secundárias</p>
                        {atSSalvo.map((a, i) => (
                          <p key={i} style={{ fontSize: 12, color: theme.gray600, paddingLeft: 10, marginBottom: 3 }}>• {a}</p>
                        ))}
                      </div>
                    )}
                  </>
              }
            </div>
          ) : (
            <div>
              <p style={{ fontSize: 11, color: theme.gray500, marginBottom: 5 }}>Atividade Principal (CNAE)</p>
              <input value={atP} onChange={e => setAtP(e.target.value)}
                placeholder="Ex: 6911-7/01 — Atividades jurídicas..."
                style={{ width: "100%", padding: "9px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none", boxSizing: "border-box", marginBottom: 12 }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <p style={{ fontSize: 11, color: theme.gray600, fontWeight: 600 }}>Atividades Secundárias</p>
                <button onClick={() => setAtS(p => [...p, ""])}
                  style={{ fontSize: 11, padding: "3px 9px", borderRadius: 5, border: `1px solid ${theme.green}`,
                    background: "#f0fdf4", color: theme.green, cursor: "pointer", fontWeight: 600 }}>
                  + Adicionar
                </button>
              </div>
              {atS.map((a, i) => (
                <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  <input value={a} onChange={e => setAtS(p => p.map((x,j)=>j===i?e.target.value:x))}
                    placeholder={`Atividade secundária ${i+1}`}
                    style={{ flex: 1, padding: "7px 10px", border: `1px solid ${theme.gray200}`, borderRadius: 6, fontSize: 13, outline: "none" }} />
                  <button onClick={() => setAtS(p => p.filter((_,j)=>j!==i))}
                    style={{ fontSize: 14, color: theme.red, background: "none", border: "none", cursor: "pointer" }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// NOVA OCORRÊNCIA FORM — sub-componente próprio (evita hook em IIFE)
// ============================================================
const TIPOS_OC_EXEC = [
  { id: "Interna", label: "🔒 Interna" },
  { id: "Externa", label: "📤 Externa" },
  { id: "Urgente", label: "🚨 Urgente" },
  { id: "Cliente", label: "👤 Cliente" },
];
const COR_OC_EXEC = { Interna: "#3A5C8C", Externa: "#4A7C59", Urgente: "#8C3A3A", Cliente: "#B87C2A", Sistema: "#A89E88" };

function NovaOcorrenciaForm({ reg, setData, usuario }) {
  const [tipoOc, setTipoOc] = useState("Interna");
  const [descOc, setDescOc] = useState("");
  const [confOc, setConfOc] = useState(false);

  const saveOc = () => {
    if (!descOc.trim()) return;
    const agora = new Date().toISOString();
    const oc = { id: Date.now(), tipo: tipoOc, descricao: descOc.trim(), data: agora.slice(0,10), hora: agora.slice(11,16), responsavel: usuario?.id || "", confidencial: confOc };
    const baseId = "BASE_" + reg.contratoId;
    setData(prev => {
      const base = prev.baseOperacional || [];
      if (base.some(b => b.contratoId === reg.contratoId))
        return { ...prev, baseOperacional: base.map(b => b.contratoId === reg.contratoId ? { ...b, ocorrencias: [...(b.ocorrencias || []), oc] } : b) };
      const { _virtual, ...real } = reg;
      return { ...prev, baseOperacional: [...base, { ...real, id: baseId, ocorrencias: [oc] }] };
    });
    setDescOc("");
  };

  return (
    <div style={{ marginBottom: 16, padding: "12px 14px", borderRadius: 8, background: theme.gray50, border: `1px solid ${theme.gray200}` }}>
      <p style={{ fontSize: 11, fontWeight: 700, color: theme.gray700, marginBottom: 10, textTransform: "uppercase", letterSpacing: .8 }}>📝 Nova Ocorrência</p>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        {TIPOS_OC_EXEC.map(t => (
          <button key={t.id} onClick={() => setTipoOc(t.id)}
            style={{ fontSize: 10, padding: "4px 10px", borderRadius: 20, cursor: "pointer",
              fontWeight: tipoOc === t.id ? 700 : 400,
              border: `1px solid ${tipoOc === t.id ? COR_OC_EXEC[t.id] : theme.gray200}`,
              background: tipoOc === t.id ? COR_OC_EXEC[t.id] + "20" : "#fff",
              color: tipoOc === t.id ? COR_OC_EXEC[t.id] : theme.gray500 }}>
            {t.label}
          </button>
        ))}
      </div>
      <textarea value={descOc} onChange={e => setDescOc(e.target.value)}
        placeholder="Descreva a ocorrência..."
        rows={2} style={{ width: "100%", padding: "8px 10px", border: `1px solid ${theme.gray200}`, borderRadius: 6,
          fontSize: 12, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
        <label style={{ fontSize: 11, color: theme.gray500, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
          <input type="checkbox" checked={confOc} onChange={e => setConfOc(e.target.checked)} style={{ accentColor: theme.gray600 }} />
          Confidencial
        </label>
        <Btn size="sm" onClick={saveOc} disabled={!descOc.trim()}>Registrar</Btn>
      </div>
    </div>
  );
}

// ============================================================
// MODAL CARD EXECUÇÃO — Detalhe do card operacional
// Exibe: SLA, checklist de fase, tarefas, ocorrências, avanço
// ============================================================
function SecaoProcessosProtocolo({ reg, contrato, cliente, setData, theme, processosCadastrados }) {
  const [novoNr, setNovoNr] = React.useState("");
  const [novaArea, setNovaArea] = React.useState("Tributário");
  const [novoTJ, setNovoTJ] = React.useState("SP");
  const [novoTipo, setNovoTipo] = React.useState("Judicial");

  const processosCont = (contrato?.processosIds||[]);
  const temProcessos  = processosCont.length > 0;

  // Cadastrar novo processo e já vincular ao contrato
  const cadastrarEVincular = () => {
    if (!novoNr.trim()) return alert("Informe o número do processo.");
    const novoId = "PROC" + Date.now();
    const novoProc = {
      id: novoId,
      clienteId: cliente?.id || contrato?.clienteId || "",
      numeroProcesso: novoNr.trim(),
      tipoProcesso: novoTipo,
      area: novaArea,
      tjUf: novoTJ,
      responsavel: contrato?.responsavel || "",
      status: "Em andamento",
      dataDistribuicao: new Date().toISOString().slice(0,10),
      ocorrencias: []
    };
    setData(prev => ({
      ...prev,
      processos: [...(prev.processos||[]), novoProc],
      contratos: (prev.contratos||[]).map(ct =>
        ct.id === contrato?.id
          ? { ...ct, processosIds: [...(ct.processosIds||[]), novoId] }
          : ct
      )
    }));
    setNovoNr(""); setNovaArea("Tributário"); setNovoTJ("SP");
  };

  const desvincular = (pid) => {
    setData(prev => ({
      ...prev,
      contratos: (prev.contratos||[]).map(ct =>
        ct.id === contrato?.id
          ? { ...ct, processosIds: (ct.processosIds||[]).filter(x=>x!==pid) }
          : ct
      )
    }));
  };

  const AREAS = ["Tributário","Previdenciário","Trabalhista","Cível","Criminal","Família","Imobiliário","Empresarial","Consumidor","Eleitoral","Outro"];
  const UFS   = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
        paddingBottom: 10, borderBottom: `2px solid ${theme.gray100}` }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: theme.gray800,
          textTransform: "uppercase", letterSpacing: .8, margin: 0 }}>
          ⚖️ Processos Vinculados
        </p>
        {!temProcessos && (
          <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8,
            background: "#fef3c7", color: "#92400e", fontWeight: 600 }}>
            ⚠️ Obrigatório para concluir o Protocolo
          </span>
        )}
        {temProcessos && (
          <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8,
            background: "#dcfce7", color: "#16a34a", fontWeight: 600 }}>
            ✅ {processosCont.length} processo(s) vinculado(s)
          </span>
        )}
      </div>

      {/* Lista de processos já vinculados */}
      {processosCont.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
          {processosCont.map((pid, i) => {
            const proc = (processosCadastrados||[]).find(p=>p.id===pid);
            return (
              <div key={pid} style={{ display: "flex", alignItems: "center", gap: 10,
                padding: "8px 12px", borderRadius: 7,
                background: theme.white, border: `1px solid ${theme.green}44`,
                borderLeft: `3px solid ${theme.green}` }}>
                <span style={{ fontSize: 13, fontFamily: "monospace", fontWeight: 600, color: theme.gray800, flex: 1 }}>
                  ⚖️ {proc ? `${proc.numeroProcesso} · ${proc.tipoProcesso} · ${proc.area} · TJ/${proc.tjUf}` : pid}
                </span>
                <button onClick={() => desvincular(pid)}
                  style={{ background: "none", border: "none", color: theme.red,
                    cursor: "pointer", fontSize: 16, padding: "2px 6px" }}>×</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Formulário de cadastro + vinculação */}
      <div style={{ padding: "14px 16px", borderRadius: 8,
        background: "#f0f9ff", border: `1px solid ${theme.blue}33` }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: theme.gray700, marginBottom: 10 }}>
          + Cadastrar e Vincular Processo
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: "0 10px", marginBottom: 10 }}>
          <div>
            <p style={{ fontSize: 10, color: theme.gray500, marginBottom: 4, textTransform: "uppercase" }}>Nº do Processo</p>
            <input value={novoNr} onChange={e=>setNovoNr(e.target.value)}
              placeholder="0000000-00.0000.0.00.0000"
              style={{ width: "100%", padding: "8px 10px", border: `1px solid ${theme.gray200}`,
                borderRadius: 6, fontSize: 12, outline: "none", boxSizing: "border-box" }}/>
          </div>
          <div>
            <p style={{ fontSize: 10, color: theme.gray500, marginBottom: 4, textTransform: "uppercase" }}>Área</p>
            <select value={novaArea} onChange={e=>setNovaArea(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", border: `1px solid ${theme.gray200}`,
                borderRadius: 6, fontSize: 12, outline: "none" }}>
              {AREAS.map(a=><option key={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <p style={{ fontSize: 10, color: theme.gray500, marginBottom: 4, textTransform: "uppercase" }}>TJ/UF</p>
            <select value={novoTJ} onChange={e=>setNovoTJ(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", border: `1px solid ${theme.gray200}`,
                borderRadius: 6, fontSize: 12, outline: "none" }}>
              {UFS.map(u=><option key={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <p style={{ fontSize: 10, color: theme.gray500, marginBottom: 4, textTransform: "uppercase" }}>Tipo</p>
            <select value={novoTipo} onChange={e=>setNovoTipo(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", border: `1px solid ${theme.gray200}`,
                borderRadius: 6, fontSize: 12, outline: "none" }}>
              {["Judicial","Administrativo","Extrajudicial"].map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <button onClick={cadastrarEVincular}
          style={{ padding: "8px 20px", borderRadius: 7, border: "none",
            background: theme.blue, color: "#fff", cursor: "pointer",
            fontSize: 12, fontWeight: 700 }}>
          + Cadastrar e Vincular
        </button>
      </div>
    </div>
  );
}

function ModalCardExecucao({ reg, contrato, cliente, produto, equipe, setData, usuario, processos, FASES_KANBAN, faseDe, COR_ST, checklistsGlobais, onClose, onMoverFase, onAbrirProcuracao, onAbrirDeclaracao }) {
  const faseAtual  = faseDe(reg.status);
  const faseObj    = FASES_KANBAN.find(f => f.id === faseAtual);
  const faseIdx    = FASES_KANBAN.findIndex(f => f.id === faseAtual);
  const proxFase   = FASES_KANBAN[faseIdx + 1];

  // Estado: dados empresariais OK — derivado do contrato (PJ only)
  const [dadosEmpresariaisOk, setDadosEmpresariaisOk] = useState(() => {
    const ct = contrato;
    return !!(ct?.faturamentos?.length > 0 && ct.faturamentos.some(f => f.valor) &&
              ct?.regimes?.length > 0 &&
              ct?.atividadePrincipal?.trim());
  });

  // Conclusões por fase — campo editável pelo Controlador/Responsável
  const CAMPO_CONCLUSAO = {
    "Entrada e Triagem": "conclusaoEntrada",
    "Produção":          "conclusaoProducao",
    "Revisão Sênior":    "conclusaoRevisao",
    "Protocolo":         "conclusaoProtocolo",
    "Concluído":         "conclusaoFinal",
    "Arquivo":           "conclusaoArquivo",
  };
  const campoConclusao = CAMPO_CONCLUSAO[faseAtual] || null;
  const [conclusaoTexto, setConclusaoTexto] = useState(
    campoConclusao ? (reg[campoConclusao] || "") : ""
  );

  const salvarConclusao = (texto) => {
    if (!campoConclusao) return;
    const baseId = "BASE_" + reg.contratoId;
    setData(prev => {
      const base = prev.baseOperacional || [];
      const upd  = b => ({ ...b, [campoConclusao]: texto });
      if (base.some(b => b.contratoId === reg.contratoId))
        return { ...prev, baseOperacional: base.map(b =>
          b.contratoId === reg.contratoId ? upd(b) : b
        )};
      const { _virtual, ...real } = reg;
      return { ...prev, baseOperacional: [...base, upd({ ...real, id: baseId })] };
    });
  };

  // ── Checklist: estado local puro — sem useEffect que reseta ──────
  const buildChecklistFromReg = (r, fase) => {
    if (fase === "Entrada e Triagem") {
      const stored = (r.checklists || {})[fase];
      if (stored && stored.length > 0) return stored;
      return (r.documentos || []).filter(d => d && d.nome)
        .map(d => ({ item: d.nome || "—", done: !!d.recebido, tipo: "B", docId: d.docId }));
    }
    if (fase === "Produção") {
      const stored = (r.checklists || {})[fase];
      if (stored && stored.length > 0) return stored;
      return (r.tarefas || []).filter(t => t && t.titulo)
        .map(t => ({ item: t.titulo || "—", done: t.status === "Concluída", tipo: "B", tarefaId: t.id }));
    }
    const stored = (r.checklists || {})[fase];
    if (stored && stored.length > 0) return stored;
    const tipoA = resolveChecklistGlobal(fase, checklistsGlobais || {}).map(item => ({ item, done: false, tipo: "A" }));
    const tipoB = ((r.checklistsPorFase || {})[fase] || []).filter(i => i && i.trim()).map(item => ({ item, done: false, tipo: "B" }));
    return [...tipoA, ...tipoB];
  };

  const [checklistItems, setChecklistItems] = useState(() => buildChecklistFromReg(reg, faseAtual));
  const clDone = checklistItems.filter(i => i.done).length;
  const checklistCompleto = checklistItems.length === 0 || clDone === checklistItems.length;

  // SLA por fase
  const PRAZO_H = { "Entrada e Triagem": 4, "Produção": 72, "Revisão Sênior": 24, "Protocolo": 4 };
  const prazoH   = PRAZO_H[faseAtual];
  const dtFase   = reg.dataEntradaFase || reg.dataInicio;
  const horasNaFase = dtFase
    ? (Date.now() - new Date(dtFase.length === 10 ? dtFase + "T00:00" : dtFase).getTime()) / (1000 * 60 * 60)
    : null;
  const slaPct   = (horasNaFase && prazoH) ? Math.min((horasNaFase / prazoH) * 100, 100) : null;
  const slaCor   = !slaPct ? theme.gray400 : slaPct < 60 ? theme.green : slaPct < 90 ? theme.amber : theme.red;
  const slaVencido = horasNaFase > prazoH;

  const tarefas     = reg.tarefas     || [];
  const ocorrencias = (reg.ocorrencias || []).slice().reverse();
  const getNomeResp = id => equipe.find(e => e.id === id)?.nome || "—";
  const fmtData     = d  => d ? new Date((d.length === 10 ? d + "T12:00" : d)).toLocaleDateString("pt-BR") : "—";

  const toggleItem = (idx, val) => {
    const agora = new Date().toISOString();
    const item  = checklistItems[idx];
    const baseId = "BASE_" + reg.contratoId;

    // Atualização imediata no estado local (resposta visual instantânea)
    setChecklistItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, done: val, doneAt: val ? agora : null } : it
    ));

    // Persistir na fonte de dados
    setData(prev => {
      const base = prev.baseOperacional || [];
      const updReg = b => {
        if (item?.tipo === "B" && item?.docId && faseAtual === "Entrada e Triagem") {
          return { ...b, documentos: (b.documentos || []).map(d =>
            d.docId === item.docId ? { ...d, recebido: val, dataRecebimento: val ? agora : "" } : d
          )};
        }
        if (item?.tipo === "B" && item?.tarefaId && faseAtual === "Produção") {
          return { ...b, tarefas: (b.tarefas || []).map(t =>
            t.id === item.tarefaId ? { ...t, status: val ? "Concluída" : "Em andamento", dataConclusao: val ? agora.slice(0,10) : "" } : t
          )};
        }
        const cls   = b.checklists || {};
        const items = cls[faseAtual] || checklistItems;
        return { ...b, checklists: { ...cls, [faseAtual]: items.map((it, i) =>
          i === idx ? { ...it, done: val, doneAt: val ? agora : null } : it
        )}};
      };
      if (base.some(b => b.contratoId === reg.contratoId))
        return { ...prev, baseOperacional: base.map(b => b.contratoId === reg.contratoId ? updReg(b) : b) };
      const { _virtual, ...real } = reg;
      return { ...prev, baseOperacional: [...base, updReg({ ...real, id: baseId })] };
    });
  };

  // Pendência Externa — sobrestamento automático
  const marcarPendenciaExterna = (idx) => {
    const agora = new Date().toISOString();
    const baseId = "BASE_" + reg.contratoId;
    setData(prev => {
      const base = prev.baseOperacional || [];
      const horasAcumuladas = reg.slaAcumuladoH || (
        reg.dataEntradaFase
          ? (Date.now() - new Date(reg.dataEntradaFase.length === 10 ? reg.dataEntradaFase + "T00:00" : reg.dataEntradaFase).getTime()) / (1000 * 60 * 60)
          : 0
      );
      const ocorrencia = { id: Date.now(), tipo: "Sistema", descricao: `Pendência externa registrada no item "${checklistItems[idx]?.item}". Card movido para Sobrestado. SLA pausado.`, data: agora.slice(0,10), confidencial: false };
      const upd = b => {
        const cls = b.checklists || {};
        const items = cls[faseAtual] || [];
        return { ...b,
          status: "Sobrestado",
          faseAnterior: faseAtual,
          slaAcumuladoH: Math.round(horasAcumuladas * 10) / 10,
          dataSobrestamento: agora,
          checklists: { ...cls, [faseAtual]: items.map((it, i) => i === idx ? { ...it, pendenciaExterna: true } : it) },
          ocorrencias: [...(b.ocorrencias || []), ocorrencia]
        };
      };
      if (base.some(b => b.contratoId === reg.contratoId))
        return { ...prev, baseOperacional: base.map(b => b.contratoId === reg.contratoId ? upd(b) : b) };
      const { _virtual, ...real } = reg;
      return { ...prev, baseOperacional: [...base, upd({ ...real, id: baseId })] };
    });
    onClose();
  };

  // Regularizar pendência — retorna à fase anterior retomando SLA
  const regularizarPendencia = (dataRetorno) => {
    const agora = new Date().toISOString();
    const baseId = "BASE_" + reg.contratoId;
    setData(prev => {
      const base = prev.baseOperacional || [];
      const faseVoltar = reg.faseAnterior || "Produção";
      const slaJaUsado = reg.slaAcumuladoH || 0;
      // Ajusta dataEntradaFase para "agora - horas já usadas" para que SLA continue de onde parou
      const dtAjustada = new Date(Date.now() - slaJaUsado * 60 * 60 * 1000).toISOString();
      const ocorrencia = { id: Date.now(), tipo: "Sistema", descricao: `Pendência externa regularizada. Retorno à fase ${faseVoltar}. SLA retomado (${slaJaUsado}h acumuladas).`, data: agora.slice(0,10), confidencial: false };
      const upd = b => ({ ...b,
        status: faseVoltar, faseAnterior: null, dataSobrestamento: null,
        dataEntradaFase: dtAjustada, slaAcumuladoH: slaJaUsado,
        ocorrencias: [...(b.ocorrencias || []), ocorrencia]
      });
      if (base.some(b => b.contratoId === reg.contratoId))
        return { ...prev, baseOperacional: base.map(b => b.contratoId === reg.contratoId ? upd(b) : b) };
      const { _virtual, ...real } = reg;
      return { ...prev, baseOperacional: [...base, upd({ ...real, id: baseId })] };
    });
    onClose();
  };

  return (
    <Modal open={true} onClose={onClose} title={produto?.nome || "Detalhe do Card"} width={1100}>

      {/* ── CABEÇALHO: SLA + info básica ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: faseObj?.cor || theme.gray600 }}>
            {faseObj?.icon} {faseAtual}
          </span>
          {horasNaFase !== null && prazoH ? (
            <span style={{ fontSize: 14, fontWeight: 700, color: slaCor }}>
              {slaVencido ? "⚠️ " : "⏱ "}{Math.round(horasNaFase)}h / {prazoH}h SLA {slaVencido ? "— VENCIDO" : ""}
            </span>
          ) : (
            <span style={{ fontSize: 13, color: theme.gray400 }}>Sem SLA definido</span>
          )}
        </div>
        {slaPct !== null && (
          <div style={{ height: 10, borderRadius: 5, background: theme.gray100, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ height: "100%", width: `${slaPct}%`, background: slaCor, borderRadius: 5, transition: "width .3s" }} />
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
          {[
            ["Cliente",       cliente?.nome || "—"],
            ["Contrato",      contrato?.numContrato || reg.numContrato || "—"],
            ["Responsável",   getNomeResp(contrato?.responsavel || reg.responsavel || "")],
            ["Data Entrada",  fmtData(contrato?.dataEntrada || reg.dataInicio || "")],
          ].map(([l, v]) => (
            <div key={l} style={{ background: theme.gray50, borderRadius: 8, padding: "12px 16px" }}>
              <p style={{ fontSize: 11, color: theme.gray400, textTransform: "uppercase", letterSpacing: .8, marginBottom: 4 }}>{l}</p>
              <p style={{ fontSize: 15, fontWeight: 600, color: theme.gray800 }}>{v}</p>
            </div>
          ))}
        </div>

        {/* Pasta do Cliente — segunda linha */}
        {(()=>{
          const pasta = cliente?.pastaCliente;
          return (
            <div style={{ marginTop: 10, padding: "10px 16px", borderRadius: 8,
              background: "#f0f9ff", border: "1px solid #bae6fd",
              display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>📁</span>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 10, color: "#0369a1", textTransform: "uppercase",
                  letterSpacing: .8, fontWeight: 700, margin: "0 0 3px" }}>Pasta do Cliente</p>
                {pasta ? (
                  <a href={pasta} target="_blank" rel="noreferrer"
                    style={{ fontSize: 12, color: "#0284c7", fontWeight: 600,
                      textDecoration: "none", wordBreak: "break-all" }}>
                    🔗 {pasta}
                  </a>
                ) : (
                  <p style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic", margin: 0 }}>
                    Nenhum link cadastrado — edite o cliente para adicionar.
                  </p>
                )}
              </div>
              {pasta && (
                <a href={pasta} target="_blank" rel="noreferrer"
                  style={{ padding: "6px 14px", borderRadius: 7, background: "#0284c7",
                    color: "#fff", fontSize: 11, fontWeight: 700, textDecoration: "none",
                    whiteSpace: "nowrap" }}>
                  Abrir ↗
                </a>
              )}
            </div>
          );
        })()}

        {/* Documentos Auxiliares — Procuração e Declaração */}
        {(onAbrirProcuracao || onAbrirDeclaracao) && (
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            {onAbrirProcuracao && (
              <button onClick={onAbrirProcuracao} style={{
                flex: 1, padding: "10px 14px", borderRadius: 8, cursor: "pointer",
                border: `1px solid ${theme.gray200}`, background: theme.white,
                color: theme.gray700, fontSize: 12, fontWeight: 600,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6
              }}>📜 Emitir Procuração</button>
            )}
            {onAbrirDeclaracao && (
              <button onClick={onAbrirDeclaracao} style={{
                flex: 1, padding: "10px 14px", borderRadius: 8, cursor: "pointer",
                border: `1px solid ${theme.gray200}`, background: theme.white,
                color: theme.gray700, fontSize: 12, fontWeight: 600,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6
              }}>📋 Declaração de Hipossuficiência</button>
            )}
          </div>
        )}
      </div>

      {/* ── CHECKLIST ── */}
      {checklistItems.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
            paddingBottom: 10, borderBottom: `2px solid ${theme.gray100}` }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: theme.gray800, textTransform: "uppercase", letterSpacing: .8, margin: 0 }}>
              ✅ Checklist — {faseAtual}
            </p>
            <span style={{ fontSize: 13, color: clDone === checklistItems.length ? theme.green : theme.amber, fontWeight: 700 }}>
              {clDone}/{checklistItems.length} concluídos
            </span>
            {faseAtual === "Sobrestado" && reg.faseAnterior && (
              <span style={{ fontSize: 12, color: theme.amber, fontWeight: 400 }}>⏳ SLA pausado — {reg.slaAcumuladoH || 0}h acumuladas</span>
            )}
          </div>
          {(faseAtual === "Entrada e Triagem" || faseAtual === "Produção")
            ? (
              // Entrada e Produção: todos itens são do produto — exibir direto sem subgrupos
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {checklistItems.map((it, idx) => (
                  <div key={idx} style={{
                    borderRadius: 8,
                    border: `1px solid ${it.pendenciaExterna ? "#fecaca" : it.done ? "#bbf7d0" : theme.gray200}`,
                    background: it.pendenciaExterna ? "#fef2f2" : it.done ? "#f0fdf4" : theme.white
                  }}>
                    <label style={{ display: "flex", gap: 14, alignItems: "center", cursor: "pointer", padding: "11px 16px" }}>
                      <input type="checkbox" checked={it.done} onChange={e => toggleItem(idx, e.target.checked)}
                        style={{ width: 18, height: 18, accentColor: theme.green, cursor: "pointer", flexShrink: 0 }} />
                      <span style={{ fontSize: 14, flex: 1, color: it.pendenciaExterna ? theme.red : it.done ? theme.green : theme.gray700,
                        textDecoration: it.done ? "line-through" : "none" }}>{it.item || "—"}</span>
                      {it.done && it.doneAt && (
                        <span style={{ fontSize: 11, color: theme.green, whiteSpace: "nowrap" }}>
                          ✓ {new Date(it.doneAt).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                      {it.pendenciaExterna && <span style={{ fontSize: 11, color: theme.red, fontWeight: 700 }}>⏸ PENDÊNCIA EXTERNA</span>}
                    </label>
                    {!it.done && !it.pendenciaExterna && (
                      <div style={{ padding: "0 16px 10px", display: "flex", justifyContent: "flex-end" }}>
                        <button onClick={() => marcarPendenciaExterna(idx)}
                          style={{ fontSize: 11, padding: "4px 12px", borderRadius: 5, border: `1px solid ${theme.red}`,
                            background: "#fef2f2", color: theme.red, cursor: "pointer", fontWeight: 600 }}>
                          ⏸ Marcar Pendência Externa
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
            : (
              // Revisão/Protocolo/outras: subgrupos Padrão + Específico
              [{ label: "Padrão do escritório", cor: theme.green, filter: it => it.tipo !== "B" },
               { label: "Específico do produto", cor: theme.amber, filter: it => it.tipo === "B" }
              ].map(grp => {
                const its = checklistItems.filter(grp.filter);
                if (!its.length) return null;
                return (
                  <div key={grp.label} style={{ marginBottom: 14 }}>
                    <p style={{ fontSize: 11, color: grp.cor, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>{grp.label}</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                      {its.map(it => {
                        const idx = checklistItems.indexOf(it);
                        return (
                          <div key={idx} style={{
                            borderRadius: 8, border: `1px solid ${it.pendenciaExterna ? "#fecaca" : it.done ? (grp.cor === theme.amber ? "#fde68a" : "#bbf7d0") : theme.gray200}`,
                            background: it.pendenciaExterna ? "#fef2f2" : it.done ? (grp.cor === theme.amber ? "#fffbeb" : "#f0fdf4") : theme.white
                          }}>
                            <label style={{ display: "flex", gap: 14, alignItems: "center", cursor: "pointer", padding: "11px 16px" }}>
                              <input type="checkbox" checked={it.done} onChange={e => toggleItem(idx, e.target.checked)}
                                style={{ width: 18, height: 18, accentColor: grp.cor, cursor: "pointer", flexShrink: 0 }} />
                              <span style={{ fontSize: 14, flex: 1, color: it.pendenciaExterna ? theme.red : it.done ? grp.cor : theme.gray700,
                                textDecoration: it.done ? "line-through" : "none" }}>{it.item || "—"}</span>
                              {it.done && it.doneAt && (
                                <span style={{ fontSize: 11, color: grp.cor, whiteSpace: "nowrap" }}>
                                  ✓ {new Date(it.doneAt).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                                </span>
                              )}
                              {it.pendenciaExterna && <span style={{ fontSize: 11, color: theme.red, fontWeight: 700 }}>⏸ PENDÊNCIA EXTERNA</span>}
                            </label>
                            {!it.done && !it.pendenciaExterna && (
                              <div style={{ padding: "0 16px 10px", display: "flex", justifyContent: "flex-end" }}>
                                <button onClick={() => marcarPendenciaExterna(idx)}
                                  style={{ fontSize: 11, padding: "4px 12px", borderRadius: 5, border: `1px solid ${theme.red}`,
                                    background: "#fef2f2", color: theme.red, cursor: "pointer", fontWeight: 600 }}>
                                  ⏸ Marcar Pendência Externa
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )
          }
          {!checklistCompleto && !["Sobrestado","Arquivo"].includes(faseAtual) && (
            <p style={{ fontSize: 13, color: theme.amber, marginTop: 8, fontWeight: 600 }}>
              ⚠️ Conclua todos os itens para liberar o avanço de fase
            </p>
          )}
        </div>
      )}

      {/* ── SOBRESTADO ── */}
      {faseAtual === "Sobrestado" && reg.faseAnterior && (
        <div style={{ marginBottom: 24, padding: "16px 18px", borderRadius: 10, background: "#fef9ec", border: "2px solid #fde68a" }}>
          <p style={{ fontSize: 15, fontWeight: 700, color: "#92400e", marginBottom: 8 }}>⏳ Card Sobrestado — Pendência Externa</p>
          <p style={{ fontSize: 13, color: theme.gray600, marginBottom: 12 }}>
            Fase anterior: <strong>{reg.faseAnterior}</strong> · SLA acumulado: <strong>{reg.slaAcumuladoH || 0}h</strong>
            {reg.dataSobrestamento && <span style={{ color: theme.gray400 }}> · Desde {new Date(reg.dataSobrestamento).toLocaleDateString("pt-BR")}</span>}
          </p>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <input type="date" id="dtRegulariza" defaultValue={new Date().toISOString().slice(0,10)}
              style={{ padding: "9px 14px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 14, flex: 1 }} />
            <Btn onClick={() => {
              const dt = document.getElementById("dtRegulariza")?.value || new Date().toISOString().slice(0,10);
              regularizarPendencia(dt);
            }}>✅ Regularizar e Retornar à Fase</Btn>
          </div>
        </div>
      )}

      {/* ── DADOS EMPRESARIAIS (sempre na Entrada e Triagem) ── */}
      {faseAtual === "Entrada e Triagem" && (
        <DadosEmpresariaisForm
          contrato={contrato}
          setData={setData}
          onDadosOk={ok => setDadosEmpresariaisOk(ok)}
          clienteTipo={cliente?.tipo || (String(contrato?.cnpj||"").replace(/\D/g,"").length === 14 ? "PJ" : String(cliente?.cpfCnpj||"").replace(/\D/g,"").length === 14 ? "PJ" : "PF")}
        />
      )}

      {/* ── TEMPLATE RCT (só na Produção, só para produtos tributários) ── */}
      {faseAtual === "Produção" && produto?.campoRCT && (
        <TemplateRCTProduto
          reg={reg}
          contrato={contrato}
          produto={produto}
          setData={setData}
          onCreditoAtualizado={valor => {
            if (!contrato?.id) return;
            setData(prev => {
              const novosValores = {
                ...(prev.contratos.find(c=>c.id===contrato.id)?.valorCreditoApuradoProduto||{}),
                [produto.id]: valor
              };
              const totalGeral = Object.values(novosValores).reduce((s,v)=>s+(parseFloat(v)||0),0);
              return {
                ...prev,
                contratos: (prev.contratos||[]).map(c =>
                  c.id === contrato.id
                    ? {...c,
                        valorCreditoApuradoProduto: novosValores,
                        valorCreditoApurado: totalGeral
                      }
                    : c
                )
              };
            });
          }}
        />
      )}

      {/* ── TAREFAS (oculto em Entrada e Triagem e Produção — itens já aparecem no checklist) ── */}
      {tarefas.length > 0 && faseAtual !== "Produção" && faseAtual !== "Entrada e Triagem" && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
            paddingBottom: 10, borderBottom: `2px solid ${theme.gray100}` }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: theme.gray800, textTransform: "uppercase", letterSpacing: .8, margin: 0 }}>
              📋 Tarefas
            </p>
            <span style={{ fontSize: 13, fontWeight: 700, color: tarefas.filter(t => t.status === "Concluída").length === tarefas.length ? theme.green : theme.amber }}>
              {tarefas.filter(t => t.status === "Concluída").length}/{tarefas.length} concluídas
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {tarefas.map((t, i) => (
              <div key={t.id || i} style={{
                display: "flex", gap: 14, alignItems: "center", padding: "12px 16px", borderRadius: 8,
                background: t.status === "Concluída" ? "#f0fdf4" : theme.white,
                border: `1px solid ${t.status === "Concluída" ? "#bbf7d0" : theme.gray200}`
              }}>
                <span style={{ fontSize: 14, flex: 1, color: t.status === "Concluída" ? theme.green : theme.gray700,
                  textDecoration: t.status === "Concluída" ? "line-through" : "none" }}>{t.titulo}</span>
                {t.dataConclusao && <span style={{ fontSize: 11, color: theme.gray400 }}>{t.dataConclusao}</span>}
                <Badge color={COR_ST[t.status] || "gray"} style={{ fontSize: 10 }}>{t.status || "Pendente"}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── NOVA OCORRÊNCIA ── */}
      <div style={{ marginBottom: 24, borderTop: `2px solid ${theme.gray100}`, paddingTop: 16 }}>
        <NovaOcorrenciaForm reg={reg} setData={setData} usuario={usuario} />
      </div>

      {/* ── HISTÓRICO ── */}
      {ocorrencias.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
            paddingBottom: 10, borderBottom: `2px solid ${theme.gray100}` }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: theme.gray800, textTransform: "uppercase", letterSpacing: .8, margin: 0 }}>
              🕐 Histórico de Ocorrências
            </p>
            <span style={{ fontSize: 13, color: theme.gray400 }}>({ocorrencias.length})</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {ocorrencias.map((oc, i) => {
              const COR_OC2 = { Interna: theme.blue, Externa: theme.green, Urgente: theme.red, Cliente: theme.amber, Sistema: theme.gray400 };
              return (
                <div key={oc.id || i} style={{ padding: "12px 16px", borderRadius: 8,
                  background: theme.white, border: `1px solid ${(COR_OC2[oc.tipo] || theme.gray200) + "55"}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: COR_OC2[oc.tipo] || theme.gray600 }}>
                        {oc.tipoNome || oc.tipo || "Registro"}
                      </span>
                      {oc.confidencial && <span style={{ fontSize: 11, color: theme.gray400 }}>🔒 Confidencial</span>}
                    </div>
                    <span style={{ fontSize: 11, color: theme.gray400 }}>{oc.data || "—"}{oc.hora ? ` às ${oc.hora}` : ""}</span>
                  </div>
                  {(oc.descricao || oc.obs) && (
                    <p style={{ fontSize: 13, color: theme.gray700, margin: 0, lineHeight: 1.6 }}>
                      {oc.descricao || oc.obs}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── PROCESSOS VINCULADOS — fase Protocolo ── */}
      {faseAtual === "Protocolo" && (
        <SecaoProcessosProtocolo
          reg={reg}
          contrato={contrato}
          cliente={cliente}
          setData={setData}
          theme={theme}
          processosCadastrados={processos||[]}
        />
      )}

      {/* ── CONCLUSÃO DA FASE ── */}
      {campoConclusao && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
            paddingBottom: 10, borderBottom: `2px solid ${theme.gray100}` }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: theme.gray800,
              textTransform: "uppercase", letterSpacing: .8, margin: 0 }}>
              ✍️ Conclusão — {faseAtual}
            </p>
            <span style={{ fontSize: 11, color: theme.gray400 }}>
              {faseAtual === "Concluído" || faseAtual === "Arquivo"
                ? "Registro final do processo"
                : "Integra o Relatório de Conclusão ao cliente"}
            </span>
          </div>
          <textarea
            value={conclusaoTexto}
            onChange={e => setConclusaoTexto(e.target.value)}
            placeholder={
              faseAtual === "Entrada e Triagem" ? "Situação da documentação · Pendências · Observações para Produção..."
            : faseAtual === "Produção"          ? "Resultado da apuração · Valor do crédito · Metodologia utilizada · Observações técnicas..."
            : faseAtual === "Revisão Sênior"    ? "Resultado da revisão · Ajustes realizados · Aprovação para protocolo..."
            : faseAtual === "Protocolo"         ? "Confirmação do protocolo · Número(s) de protocolo · Prazos esperados..."
            : faseAtual === "Concluído"         ? "Resultado final do processo · Crédito apurado · Próximos passos ao cliente..."
            :                                     "Observações finais · Arquivamento..."
            }
            rows={5}
            style={{ width: "100%", padding: "12px 14px", border: `2px solid ${theme.gold}44`,
              borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical",
              outline: "none", boxSizing: "border-box", background: theme.gold+"05",
              lineHeight: 1.7, color: theme.gray800 }}
            onFocus={e => e.target.style.borderColor = theme.gold}
            onBlur={e => {
              e.target.style.borderColor = theme.gold + "44";
              if (conclusaoTexto !== (reg[campoConclusao]||"")) salvarConclusao(conclusaoTexto);
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <span style={{ fontSize: 11, color: theme.gray400 }}>
              {conclusaoTexto ? `${conclusaoTexto.length} caracteres · salvo automaticamente` : "Salvo automaticamente ao sair do campo"}
            </span>
            {conclusaoTexto !== (reg[campoConclusao]||"") && (
              <button onClick={() => salvarConclusao(conclusaoTexto)}
                style={{ fontSize: 11, padding: "4px 12px", borderRadius: 5, cursor: "pointer",
                  border: `1px solid ${theme.green}`, background: "#f0fdf4", color: theme.green, fontWeight: 600 }}>
                💾 Salvar
              </button>
            )}
            {conclusaoTexto && conclusaoTexto === (reg[campoConclusao]||"") && (
              <span style={{ fontSize: 11, color: theme.green, fontWeight: 600 }}>✓ Salvo</span>
            )}
          </div>
        </div>
      )}

      {/* ── AVANÇO DE FASE ── */}
      {proxFase && !["Concluído", "Arquivo"].includes(faseAtual) && (()=>{
        const isPJ = cliente?.tipo === "PJ" ||
                     String(contrato?.cnpj||"").replace(/\D/g,"").length === 14 ||
                     String(cliente?.cpfCnpj||"").replace(/\D/g,"").length === 14;
        const bloqDados    = faseAtual === "Entrada e Triagem" && isPJ && !dadosEmpresariaisOk;
        const temProcesso  = (contrato?.processosIds||[]).length > 0;
        const bloqProtocolo = faseAtual === "Protocolo" && !temProcesso;
        const conclusaoOk = !campoConclusao || (conclusaoTexto||"").trim().length > 0;
        const podavancar = checklistCompleto && !bloqDados && !bloqProtocolo && conclusaoOk;
        let msgStatus;
        if (bloqDados && !checklistCompleto)
          msgStatus = `⚠️ Dados empresariais e ${checklistItems.length - clDone} item(s) do checklist pendentes`;
        else if (bloqDados)
          msgStatus = "⚠️ Preencha e salve os Dados Empresariais para avançar";
        else if (bloqProtocolo)
          msgStatus = "⚠️ Cadastre e vincule ao menos um processo para concluir o Protocolo";
        else if (!checklistCompleto)
          msgStatus = `⚠️ ${clDone}/${checklistItems.length} itens do checklist pendentes`;
        else if (!conclusaoOk)
          msgStatus = `⚠️ Preencha o relatório de Conclusão — ${faseAtual} para avançar`;
        else
          msgStatus = `✅ Pronto para avançar → ${proxFase.id}`;
        return (
          <div style={{ borderTop: `2px solid ${theme.gray100}`, paddingTop: 16,
            display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, color: podavancar ? theme.green : theme.amber, fontWeight: 600 }}>
              {msgStatus}
            </span>
            <Btn disabled={!podavancar}
              onClick={() => { if (podavancar) { onMoverFase(reg, proxFase.id); onClose(); } }}
              variant={podavancar ? "primary" : "secondary"}>
              → {proxFase.icon} {proxFase.id}
            </Btn>
          </div>
        );
      })()}
    </Modal>
  );
}

// ============================================================
// TEMPLATE RCT — PRODUÇÃO
// Template 1: Campo único (Valor Apurado)
// Template 2: 60 meses (Mês + Valor, total automático)
// Alimenta valorCreditoApurado no contrato
// ============================================================
function TemplateRCTProduto({ reg, contrato, produto, setData, onCreditoAtualizado }) {
  const tmpl = produto?.campoRCT || "campo_unico";
  const baseId = "BASE_" + reg.contratoId;

  // Estado local para o template — inicializadores totalmente defensivos
  const [valorUnico, setValorUnico] = React.useState(() => {
    try { return reg?.templateRCT?.valorUnico || contrato?.valorCreditoApurado || ""; }
    catch(e) { return ""; }
  });
  const [meses, setMeses] = React.useState(() => {
    try {
      if (reg?.templateRCT?.meses?.length === 60) return reg.templateRCT.meses;
      // Gerar 60 meses a partir da dataEntrada ou hoje
      const raw = contrato?.dataEntrada || new Date().toISOString().slice(0,10);
      const parts = String(raw).slice(0,7).split("-");
      const anoB = parseInt(parts[0]||"2026",10);
      const mesB = parseInt(parts[1]||"1",10);
      return Array.from({length: 60}, (_, i) => {
        const d = new Date(anoB, mesB - 1 - i, 1);
        const label = d.toLocaleDateString("pt-BR",{month:"2-digit",year:"numeric"});
        return { mes: label, valor: "" };
      }).reverse();
    } catch(e) {
      return Array.from({length: 60}, (_, i) => ({ mes: `Mês ${i+1}`, valor: "" }));
    }
  });
  const [salvo, setSalvo] = React.useState(false);

  const fmtMoeda = v => {
    const d = String(v||"").replace(/[^\d]/g,"");
    const n = parseInt(d||"0",10)/100;
    return n ? n.toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2}) : "";
  };
  const parseMoeda = v => parseFloat(String(v||"").replace(/\./g,"").replace(",",".")) || 0;

  const totalMeses = meses.reduce((acc,m) => acc + parseMoeda(m.valor), 0);
  const valorFinal = tmpl === "campo_unico" ? parseMoeda(valorUnico) : totalMeses;

  const salvar = () => {
    const templateData = tmpl === "campo_unico"
      ? { tipo: "campo_unico", valorUnico }
      : { tipo: "60_meses", meses, total: totalMeses };

    setData(prev => {
      const base = prev.baseOperacional || [];
      const upd = b => ({...b, templateRCT: templateData,
        creditoApurado: valorFinal });
      if (base.some(b => b.contratoId === reg.contratoId))
        return {...prev, baseOperacional: base.map(b => b.contratoId === reg.contratoId ? upd(b) : b)};
      const {_virtual, ...real} = reg;
      return {...prev, baseOperacional: [...base, upd({...real, id: baseId})]};
    });
    onCreditoAtualizado?.(valorFinal);
    setSalvo(true);
    setTimeout(() => setSalvo(false), 2500);
  };

  const COR_GOLD = "#C9A84C";

  return (
    <div style={{marginBottom:24}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,
        paddingBottom:10,borderBottom:`2px solid ${theme.gold}33`}}>
        <p style={{fontSize:15,fontWeight:700,color:theme.gray800,
          textTransform:"uppercase",letterSpacing:.8,margin:0}}>
          📊 {tmpl==="campo_unico" ? "Valor do Crédito Apurado" : "Crédito Apurado — 60 Meses"}
        </p>
        <span style={{fontSize:11,color:theme.gray400}}>
          {tmpl==="campo_unico" ? "Template 1 — Campo único" : "Template 2 — Período 60 meses"}
        </span>
      </div>

      {tmpl === "campo_unico" && (
        <div style={{background:"#f0fdf4",borderRadius:8,padding:"16px 18px",border:"1px solid #bbf7d0"}}>
          <p style={{fontSize:12,color:theme.gray600,marginBottom:10}}>
            Informe o valor total do crédito apurado para este produto.
          </p>
          <div style={{display:"flex",gap:12,alignItems:"center"}}>
            <div style={{flex:1}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>
                Valor do Crédito Apurado (R$)
              </p>
              <input
                value={valorUnico}
                onChange={e => {
                  const d = e.target.value.replace(/[^\d]/g,"");
                  const n = parseInt(d||"0",10)/100;
                  setValorUnico(n ? n.toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2}) : "");
                }}
                placeholder="0,00"
                style={{width:"100%",padding:"10px 14px",border:`2px solid ${COR_GOLD}`,
                  borderRadius:7,fontSize:15,fontWeight:700,outline:"none",boxSizing:"border-box",
                  textAlign:"right",background:"#fff",color:theme.gray900}}
              />
            </div>
            <div style={{textAlign:"center",padding:"10px 14px",borderRadius:8,
              background:theme.gold+"10",border:`1px solid ${theme.gold}44`,minWidth:140}}>
              <p style={{fontSize:9,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>Total</p>
              <p style={{fontSize:20,fontWeight:700,color:theme.green}}>
                R$ {valorUnico || "0,00"}
              </p>
            </div>
          </div>
        </div>
      )}

      {tmpl === "60_meses" && (
        <div style={{background:"#f0f9ff",borderRadius:8,padding:"16px 18px",border:"1px solid #bfdbfe"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <p style={{fontSize:12,color:theme.gray600,margin:0}}>
              Informe o valor do crédito apurado por competência (60 meses).
            </p>
            <div style={{textAlign:"right",padding:"8px 14px",borderRadius:8,
              background:theme.green+"15",border:`1px solid ${theme.green}44`}}>
              <p style={{fontSize:9,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:2}}>Total Geral</p>
              <p style={{fontSize:18,fontWeight:700,color:theme.green,margin:0}}>
                R$ {totalMeses.toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2})}
              </p>
            </div>
          </div>
          <div style={{maxHeight:360,overflowY:"auto",border:`1px solid ${theme.gray200}`,borderRadius:7}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{background:theme.gray900,position:"sticky",top:0}}>
                  <th style={{padding:"8px 12px",textAlign:"left",fontSize:10,color:COR_GOLD,
                    textTransform:"uppercase",letterSpacing:.8,fontWeight:600}}>#</th>
                  <th style={{padding:"8px 12px",textAlign:"left",fontSize:10,color:COR_GOLD,
                    textTransform:"uppercase",letterSpacing:.8,fontWeight:600}}>Competência</th>
                  <th style={{padding:"8px 12px",textAlign:"right",fontSize:10,color:COR_GOLD,
                    textTransform:"uppercase",letterSpacing:.8,fontWeight:600}}>Valor do Crédito (R$)</th>
                </tr>
              </thead>
              <tbody>
                {meses.map((m,i) => (
                  <tr key={i} style={{background:i%2===0?"#fff":"#f0f9ff",borderBottom:`1px solid ${theme.gray100}`}}>
                    <td style={{padding:"6px 12px",fontSize:11,color:theme.gray400}}>{i+1}</td>
                    <td style={{padding:"6px 12px",fontSize:12,fontWeight:600,color:theme.gray800}}>{m.mes}</td>
                    <td style={{padding:"6px 8px",textAlign:"right"}}>
                      <input
                        value={m.valor}
                        onChange={e => {
                          const d = e.target.value.replace(/[^\d]/g,"");
                          const n = parseInt(d||"0",10)/100;
                          const v = n ? n.toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2}) : "";
                          setMeses(prev => prev.map((x,j) => j===i ? {...x,valor:v} : x));
                        }}
                        placeholder="0,00"
                        style={{width:160,padding:"5px 10px",border:`1px solid ${theme.gray200}`,
                          borderRadius:5,fontSize:12,textAlign:"right",outline:"none",
                          background:m.valor?"#f0fff4":"#fff",
                          color:m.valor?theme.green:theme.gray600}}
                      />
                    </td>
                  </tr>
                ))}
                <tr style={{background:theme.gray900}}>
                  <td colSpan={2} style={{padding:"10px 12px",fontSize:12,fontWeight:700,color:COR_GOLD}}>
                    TOTAL GERAL (60 MESES)
                  </td>
                  <td style={{padding:"10px 12px",textAlign:"right",fontSize:14,fontWeight:700,color:COR_GOLD}}>
                    R$ {totalMeses.toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2})}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:12}}>
        {salvo
          ? <span style={{fontSize:13,color:theme.green,fontWeight:600}}>✅ Salvo! Valor transferido para o contrato.</span>
          : <span style={{fontSize:11,color:theme.gray400}}>
              O valor será acumulado no campo "Valor do Crédito Apurado" do contrato.
            </span>
        }
        <button onClick={salvar}
          style={{padding:"9px 22px",borderRadius:7,border:"none",cursor:"pointer",
            background:theme.gray900,color:COR_GOLD,fontSize:13,fontWeight:700}}>
          💾 Salvar e Atualizar Contrato
        </button>
      </div>
    </div>
  );
}

// ============================================================
// RELATÓRIO DE CONCLUSÃO — GERENCIAL / CLIENTE
// Gerado pela Controladoria após fase Concluído
// Linguagem formal e técnica
// ============================================================
function RelatorioConclusao({ registros, contratos, clientes, produtos, faseDe, equipe, setData }) {
  const [regSel, setRegSel] = React.useState(null);

  const fmtD = d => d ? new Date((d.length===10?d+"T12:00":d)).toLocaleDateString("pt-BR") : "—";
  const fmtMoeda = v => {
    const n = parseFloat(String(v||"0").replace(/\./g,"").replace(",",".")) || 0;
    return n.toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
  };
  const getNomeResp = id => (equipe||[]).find(e=>e.id===id)?.nome || id || "—";

  // Contratos em Concluído ou Produção (pode gerar antes de concluir)
  const regsDisp = registros
    .filter(r => ["Concluído","Arquivo","Produção","Revisão Sênior","Protocolo"].includes(faseDe(r.status)))
    .sort((a,b) => {
      const cA = clientes.find(c=>c.id===a.clienteId)?.nome||"";
      const cB = clientes.find(c=>c.id===b.clienteId)?.nome||"";
      return cA.localeCompare(cB,"pt-BR");
    });

  const gerarHTML = (r) => {
    const ct  = contratos.find(c=>c.id===r.contratoId) || {};
    const cl  = clientes.find(c=>c.id===r.clienteId)   || {};
    const pIds = (r.produtosIds||[]).length>0 ? r.produtosIds : [r.produtoId].filter(Boolean);
    const prods = pIds.map(id=>produtos.find(p=>p.id===id)).filter(Boolean);
    const totalCredito = parseFloat(ct.valorCreditoApurado||0);
    const hasCredito = totalCredito > 0;
    const parcProd = ct.valorCreditoApuradoProduto || {};
    const hoje = new Date().toLocaleDateString("pt-BR",{day:"numeric",month:"long",year:"numeric"});

    const prodRows = prods.map(p => {
      const valorApurado   = parseFloat(parcProd[p.id]||0);      // via template fase Produção
      const valorContrato  = parseFloat(ct.valorCreditoApurado||0); // via formulário do contrato
      // Prevalece sempre o valor apurado pelo template
      // Valor do contrato é apenas parâmetro de conciliação
      const temApurado     = valorApurado > 0;
      const temContrato    = !temApurado && prods.length === 1 && valorContrato > 0;
      const valorExibir    = temApurado ? valorApurado : 0;
      const temCredito     = temApurado;
      return `
      <tr>
        <td style="padding:10px 14px;font-size:11pt;font-weight:600;border:1px solid #ddd;vertical-align:top">${p.nome||"—"}</td>
        <td style="padding:10px 14px;font-size:10.5pt;border:1px solid #ddd;vertical-align:top;color:#555">${p.baseLegal||p.resumo||"—"}</td>
        <td style="padding:10px 14px;font-size:11pt;text-align:center;border:1px solid #ddd;font-weight:700;color:${temCredito?"#155724":temContrato?"#856404":"#721c24"}">
          ${temCredito ? "✓ Crédito Apurado" : temContrato ? "⚠️ Valor a confirmar" : "✗ Sem crédito"}
        </td>
        <td style="padding:10px 14px;font-size:11pt;text-align:right;border:1px solid #ddd;font-weight:700;color:${temCredito?"#155724":temContrato?"#856404":"#888"}">
          ${temCredito
            ? valorExibir.toLocaleString("pt-BR",{style:"currency",currency:"BRL"})
            : temContrato
              ? valorContrato.toLocaleString("pt-BR",{style:"currency",currency:"BRL"}) + "<br><span style='font-size:8pt;font-weight:400;color:#856404'>⚠️ Informado no contrato</span>"
              : "—"}
        </td>
      </tr>`;
    }).join("");

    // Nota de rodapé se há valores apenas informados no contrato (não apurados via template)
    const temValorSomenteContrato = prods.some(p => !parseFloat(parcProd[p.id]||0)) && totalCredito > 0;
    const notaRodape = temValorSomenteContrato
      ? `<p style="font-size:9pt;color:#856404;background:#fff8e1;border:1px solid #ffd54f;padding:8px 12px;border-radius:4px;margin-top:10px">
          ⚠️ <strong>Nota:</strong> Valores marcados com "Informado no contrato" foram inseridos manualmente no formulário de contrato.
          Para formalização definitiva ao cliente, recomenda-se confirmar mediante apuração pelo Template de Produção.
         </p>`
      : "";

    const conclusoes = [
      ["Entrada e Triagem", r.conclusaoEntrada],
      ["Produção",          r.conclusaoProducao],
      ["Revisão Sênior",    r.conclusaoRevisao],
      ["Protocolo",         r.conclusaoProtocolo],
      ["Conclusão Final",   r.conclusaoFinal],
    ].filter(([,v]) => v && v.trim());

    return `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Relatório de Conclusão — ${cl.nome||"—"}</title>
<style>
  @page { size:A4 portrait; margin:18mm 16mm 22mm 16mm; }
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:11pt;color:#1A1610;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .hdr{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:12px;border-bottom:3px solid #C9A84C;margin-bottom:22px}
  .hdr-esq{font-size:14pt;font-weight:bold}
  .hdr-sub{font-size:9pt;color:#888;margin-top:3px}
  .titulo{text-align:center;margin-bottom:20px}
  .titulo h1{font-size:16pt;font-weight:bold;text-transform:uppercase;letter-spacing:1px;color:#1A1610}
  .titulo p{font-size:9.5pt;color:#888;margin-top:5px}
  .linha-ouro{width:70px;height:3px;background:#C9A84C;margin:8px auto 14px}
  .sec{margin-bottom:18px}
  .sec-titulo{font-size:10pt;font-weight:bold;text-transform:uppercase;letter-spacing:.8px;padding:7px 14px;background:#1A1610;color:#C9A84C;border-radius:4px;margin-bottom:10px}
  .grid-2{display:grid;grid-template-columns:1fr 1fr;gap:6px 28px;margin-bottom:6px}
  .campo label{font-size:8.5pt;color:#888;text-transform:uppercase;letter-spacing:.5px;display:block;margin-bottom:2px}
  .campo span{font-size:11pt;font-weight:600;color:#1A1610}
  table{width:100%;border-collapse:collapse;font-size:10.5pt}
  th{background:#1A1610;color:#C9A84C;padding:8px 14px;text-align:left;font-size:9pt;text-transform:uppercase;letter-spacing:.5px;border:1px solid #333}
  .resultado-box{padding:14px 18px;border-radius:6px;margin-bottom:16px}
  .box-credito{background:#d4edda;border:2px solid #c3e6cb}
  .box-sem{background:#f8f9fa;border:1px solid #dee2e6}
  .total-box{text-align:right;padding:12px 14px;background:#f9f6ef;border:2px solid #C9A84C;border-radius:6px;margin-top:10px}
  .conclusao-box{background:#f9f6ef;border-left:4px solid #C9A84C;padding:14px 16px;margin-bottom:12px;border-radius:0 6px 6px 0}
  .conclusao-box p{font-size:11pt;line-height:1.8;white-space:pre-wrap;color:#1A1610}
  .analise{background:#f9f6ef;border:1px solid #e0d9c8;border-radius:6px;padding:16px 18px;margin-bottom:16px}
  .analise p{font-size:11pt;line-height:1.9;color:#1A1610}
  .assin{display:grid;grid-template-columns:1fr 1fr;gap:0 60px;margin-top:32px;padding-top:16px;border-top:1px solid #ccc}
  .assin-item{text-align:center}
  .assin-linha{border-top:1px solid #555;margin-bottom:6px}
  .assin-nome{font-size:10pt;font-weight:bold}
  .assin-sub{font-size:8.5pt;color:#888}
  .ftr{margin-top:24px;padding-top:10px;border-top:1px solid #ddd;font-size:8pt;color:#aaa;display:flex;justify-content:space-between}
  @media print{body{print-color-adjust:exact}}
</style></head><body>

<div class="hdr">
  <div>
    <div class="hdr-esq">Alves &amp; Correa Advogados</div>
    <div class="hdr-sub">OAB/SP · Nelson J. Alves (OAB ___) · Annie Lemos Alves Correa (OAB 446.942/SP)</div>
  </div>
  <div style="text-align:right">
    <div style="font-size:9pt;color:#888">Emitido em</div>
    <div style="font-weight:bold;font-size:10.5pt">${hoje}</div>
    <div style="font-size:8.5pt;color:#aaa;margin-top:2px">Documento Confidencial</div>
  </div>
</div>

<div class="titulo">
  <h1>Relatório de Conclusão de Serviços Jurídicos</h1>
  <div class="linha-ouro"></div>
  <p>Documento técnico-gerencial elaborado para formalizar o resultado das atividades realizadas</p>
</div>

<div class="sec">
  <div class="sec-titulo">1. Identificação das Partes</div>
  <div class="grid-2">
    <div class="campo"><label>Cliente / Contratante</label><span>${cl.nome||"—"}</span></div>
    <div class="campo"><label>CPF / CNPJ</label><span>${(cl.cpfCnpj||"Não informado")}</span></div>
    <div class="campo"><label>Contrato</label><span>${ct.numContrato||"—"}</span></div>
    <div class="campo"><label>Data de Contratação</label><span>${fmtD(ct.dataEntrada||"")}</span></div>
    <div class="campo"><label>Responsável</label><span>${getNomeResp(ct.responsavel||r.responsavel||"")}</span></div>
    <div class="campo"><label>Data deste Relatório</label><span>${hoje}</span></div>
  </div>
  ${cl.email||cl.endereco?`<div class="grid-2" style="margin-top:8px">
    ${cl.email?`<div class="campo"><label>E-mail</label><span>${cl.email}</span></div>`:""}
    ${cl.endereco?`<div class="campo"><label>Endereço</label><span>${cl.endereco}${cl.cidade?", "+cl.cidade:""}${cl.uf?" — "+cl.uf:""}</span></div>`:""}
  </div>`:""}
</div>

<div class="sec">
  <div class="sec-titulo">2. Objeto dos Serviços Contratados</div>
  <table>
    <thead><tr>
      <th style="width:35%">Produto / Tese Jurídica</th>
      <th style="width:35%">Base Legal / Fundamentação</th>
      <th style="width:16%;text-align:center">Resultado</th>
      <th style="width:14%;text-align:right">Valor Apurado</th>
    </tr></thead>
    <tbody>${prodRows||`<tr><td colspan="4" style="padding:10px 14px;color:#888;font-style:italic">Nenhum produto cadastrado.</td></tr>`}</tbody>
  </table>
  <div class="total-box">
    <span style="font-size:10pt;color:#888;text-transform:uppercase;letter-spacing:.5px">Total Geral de Créditos Apurados</span>
    <p style="font-size:16pt;font-weight:bold;color:${hasCredito?"#155724":"#888"};margin-top:4px">
      ${hasCredito ? totalCredito.toLocaleString("pt-BR",{style:"currency",currency:"BRL"}) : "Não se aplica"}
    </p>
  </div>
  ${notaRodape}
</div>

${hasCredito ? `
<div class="sec">
  <div class="sec-titulo">3. Resultado Apurado</div>
  <div class="resultado-box box-credito">
    <p style="font-size:12pt;font-weight:bold;color:#155724;margin-bottom:8px">✅ Crédito Tributário / Valor Apurado</p>
    <p style="font-size:11pt;line-height:1.8;color:#1A1610">
      Com base na análise técnica realizada, foram identificados e apurados créditos tributários no montante total de 
      <strong>${totalCredito.toLocaleString("pt-BR",{style:"currency",currency:"BRL"})}</strong>, 
      referentes aos produtos objeto deste contrato. Os valores foram calculados com rigor técnico, observando a legislação aplicável 
      e a jurisprudência dos Tribunais Superiores, e encontram-se devidamente documentados nos registros do escritório.
    </p>
    ${prods.length > 1 ? `<p style="font-size:11pt;line-height:1.8;margin-top:8px;color:#1A1610">
      A composição dos valores por produto consta da tabela acima, sendo que eventuais diferenças de valores entre produtos 
      decorrem das particularidades de cada tese e do período de apuração aplicável.
    </p>` : ""}
  </div>
</div>` : `
<div class="sec">
  <div class="sec-titulo">3. Resultado da Análise</div>
  <div class="resultado-box box-sem">
    <p style="font-size:12pt;font-weight:bold;color:#555;margin-bottom:8px">📋 Análise Concluída — Sem Crédito Identificado</p>
    <p style="font-size:11pt;line-height:1.8;color:#1A1610">
      Após análise técnica aprofundada dos documentos e informações disponibilizados, não foram identificados créditos tributários 
      passíveis de recuperação para o(s) produto(s) objeto deste contrato, dentro do período e das condições analisadas. 
      A análise foi conduzida com observância dos parâmetros técnicos e jurídicos aplicáveis.
    </p>
  </div>
</div>`}

${conclusoes.length > 0 ? `
<div class="sec">
  <div class="sec-titulo">4. Registro das Fases — Conclusões Técnicas</div>
  ${conclusoes.map(([fase, txt]) => `
  <div class="conclusao-box">
    <p style="font-size:8.5pt;font-weight:bold;text-transform:uppercase;letter-spacing:.8px;color:#C9A84C;margin-bottom:8px">${fase}</p>
    <p>${txt}</p>
  </div>`).join("")}
</div>` : ""}

<div class="sec">
  <div class="sec-titulo">${conclusoes.length > 0 ? "5" : "4"}. Análise Geral e Recomendações</div>
  <div class="analise">
    <p>
      O presente relatório consolida os resultados obtidos no âmbito dos serviços jurídicos prestados pelo escritório 
      <strong>Alves &amp; Correa Advogados</strong>, nos termos do Contrato n.º ${ct.numContrato||"—"}, celebrado com 
      <strong>${cl.nome||"o contratante"}</strong>.
    </p>
    <p style="margin-top:10px">
      ${hasCredito 
        ? `Os trabalhos desenvolvidos resultaram na identificação e apuração de créditos tributários no valor total de 
           <strong>${totalCredito.toLocaleString("pt-BR",{style:"currency",currency:"BRL"})}</strong>, 
           os quais poderão ser utilizados mediante restituição ou compensação, conforme procedimentos administrativos aplicáveis perante a Receita Federal do Brasil e/ou PGFN.`
        : `Os trabalhos desenvolvidos compreenderam análise técnica criteriosa da situação fiscal e jurídica do cliente, 
           não tendo sido identificados créditos tributários recuperáveis dentro dos parâmetros e período analisados. 
           O resultado negativo não implica ausência de oportunidades futuras, podendo ser reavaliado ante alterações legislativas ou novas informações disponibilizadas.`
      }
    </p>
    <p style="margin-top:10px">
      Todos os procedimentos foram conduzidos em conformidade com a legislação tributária vigente, observando os 
      precedentes dos Tribunais Superiores e as orientações normativas da Receita Federal do Brasil, assegurando 
      a segurança jurídica das medidas adotadas.
    </p>
    ${ct.regimes?.length > 0 ? `<p style="margin-top:10px">
      No tocante ao enquadramento fiscal, o cliente operou ${ct.regimes.map(r=>r.tipo).join(", ")} no período analisado, 
      fator determinante para a identificação das teses aplicáveis.
    </p>` : ""}
  </div>
</div>

<div class="assin">
  <div class="assin-item">
    <div style="height:40px"></div>
    <div class="assin-linha"></div>
    <div class="assin-nome">Nelson J. Alves</div>
    <div class="assin-sub">Advogado — OAB/SP ___</div>
    <div class="assin-sub">Alves &amp; Correa Advogados</div>
  </div>
  <div class="assin-item">
    <div style="height:40px"></div>
    <div class="assin-linha"></div>
    <div class="assin-nome">Annie Lemos Alves Correa</div>
    <div class="assin-sub">Advogada — OAB/SP 446.942</div>
    <div class="assin-sub">Alves &amp; Correa Advogados</div>
  </div>
</div>

<div class="ftr">
  <span>Alves &amp; Correa Advogados · Uso Confidencial</span>
  <span>Gerado em ${new Date().toLocaleString("pt-BR")}</span>
</div>
</body></html>`;
  };

  const imprimir = () => {
    if (!regSel) return;
    const win = window.open("","_blank","width=900,height=800");
    if (!win) return alert("Permita pop-ups para gerar o PDF.");
    win.document.write(gerarHTML(regSel));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 600);
  };

  return (
    <div>
      <div style={{marginBottom:16,padding:"12px 16px",background:theme.gray50,borderRadius:8,border:`1px solid ${theme.gray200}`}}>
        <p style={{fontSize:13,fontWeight:700,color:theme.gray800,margin:0}}>📋 Relatório de Conclusão de Serviços — Documento ao Cliente</p>
        <p style={{fontSize:11,color:theme.gray500,margin:"3px 0 0"}}>Selecione o contrato à esquerda e visualize o relatório à direita.</p>
      </div>

      {/* Split layout */}
      <div style={{display:"grid",gridTemplateColumns:"320px 1fr",gap:16,alignItems:"start"}}>

        {/* ── Coluna esquerda: seletor ── */}
        <div style={{position:"sticky",top:16}}>
          <p style={{fontSize:11,fontWeight:700,color:theme.gray600,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>Contratos Disponíveis</p>
          <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:"calc(100vh - 260px)",overflowY:"auto",
            border:`1px solid ${theme.gray200}`,borderRadius:8,padding:8,background:theme.white}}>
            {regsDisp.length===0 && <p style={{fontSize:12,color:theme.gray400,padding:"8px",textAlign:"center",fontStyle:"italic"}}>Nenhum contrato disponível.</p>}
            {regsDisp.map(r => {
              const ct  = contratos.find(c=>c.id===r.contratoId);
              const cl  = clientes.find(c=>c.id===r.clienteId);
              const sel = regSel?.contratoId === r.contratoId;
              const total = parseFloat(ct?.valorCreditoApurado||0);
              const temApurado = ct?.valorCreditoApuradoProduto && Object.values(ct.valorCreditoApuradoProduto).some(v=>parseFloat(v)>0);
              return (
                <div key={r.contratoId} onClick={()=>setRegSel(r)}
                  style={{padding:"10px 12px",borderRadius:7,cursor:"pointer",
                    border:`2px solid ${sel?theme.gold:theme.gray200}`,
                    background:sel?theme.gold+"10":theme.white,transition:"all .1s"}}>
                  <p style={{fontSize:12,fontWeight:700,color:theme.gray900,margin:0}}>{cl?.nome||"—"}</p>
                  <p style={{fontSize:10,color:theme.gray500,margin:"2px 0 0"}}>{ct?.numContrato||"—"} · {faseDe(r.status)}</p>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:5}}>
                    <span style={{fontSize:9,padding:"1px 6px",borderRadius:3,fontWeight:600,
                      background:temApurado?"#d4edda":"#fff8e1",
                      color:temApurado?theme.green:"#856404"}}>
                      {temApurado ? "✓ Apurado" : total>0 ? "⚠️ Informado" : "Sem crédito"}
                    </span>
                    {total>0&&<span style={{fontSize:10,fontWeight:700,color:temApurado?theme.green:"#856404"}}>
                      {total.toLocaleString("pt-BR",{style:"currency",currency:"BRL"})}
                    </span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Coluna direita: preview ── */}
        <div>
        {!regSel ? (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",
            minHeight:300,background:theme.gray50,borderRadius:10,border:`1px dashed ${theme.gray300}`}}>
            <div style={{textAlign:"center",color:theme.gray400}}>
              <p style={{fontSize:28,marginBottom:8}}>📋</p>
              <p style={{fontSize:13,fontWeight:600}}>Selecione um contrato</p>
              <p style={{fontSize:11,marginTop:4}}>O relatório aparecerá aqui</p>
            </div>
          </div>
        ) : (()=>{
          const ct  = contratos.find(c=>c.id===regSel.contratoId) || {};
          const cl  = clientes.find(c=>c.id===regSel.clienteId)   || {};
          const pIds = (regSel.produtosIds||[]).length>0 ? regSel.produtosIds : [regSel.produtoId].filter(Boolean);
          const prods = pIds.map(id=>produtos.find(p=>p.id===id)).filter(Boolean);
          const total = parseFloat(ct.valorCreditoApurado||0);
          const parcProd = ct.valorCreditoApuradoProduto || {};
        const conclusoes = [
          ["Entrada e Triagem",regSel.conclusaoEntrada],
          ["Produção",         regSel.conclusaoProducao],
          ["Revisão Sênior",   regSel.conclusaoRevisao],
          ["Protocolo",        regSel.conclusaoProtocolo],
          ["Conclusão Final",  regSel.conclusaoFinal],
        ].filter(([,v]) => v && v.trim());

        return (
          <div style={{background:theme.white,borderRadius:10,border:`1px solid ${theme.gray200}`,padding:"20px 24px",marginBottom:16}}>
            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",paddingBottom:12,marginBottom:16,borderBottom:`2px solid ${theme.gold}`}}>
              <div>
                <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0}}>Alves &amp; Correa Advogados</p>
                <p style={{fontSize:10,color:theme.gray400,margin:"2px 0 0"}}>Relatório de Conclusão de Serviços Jurídicos</p>
              </div>
              <p style={{fontSize:11,color:theme.gray500}}>{new Date().toLocaleDateString("pt-BR",{day:"2-digit",month:"long",year:"numeric"})}</p>
            </div>

            {/* Identificação */}
            <div style={{marginBottom:16}}>
              <p style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.8,color:"#fff",background:theme.gray900,padding:"5px 10px",borderRadius:4,marginBottom:8}}>🏷️ Identificação</p>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {[["Cliente",cl.nome||"—"],["CPF/CNPJ",cl.cpfCnpj||"—"],["Contrato",ct.numContrato||"—"],
                  ["Data Contratação",fmtD(ct.dataEntrada||"")],["Responsável",(equipe||[]).find(e=>e.id===ct.responsavel)?.nome||"—"],["Fase",faseDe(regSel.status)]
                ].map(([l,v])=>(
                  <div key={l} style={{background:theme.gray50,borderRadius:6,padding:"8px 12px"}}>
                    <p style={{fontSize:9,color:theme.gray400,textTransform:"uppercase",letterSpacing:.6,marginBottom:2}}>{l}</p>
                    <p style={{fontSize:12,fontWeight:600,color:theme.gray800}}>{v}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Produtos e resultados */}
            <div style={{marginBottom:16}}>
              <p style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.8,color:"#fff",background:theme.gray900,padding:"5px 10px",borderRadius:4,marginBottom:8}}>📦 Produtos e Resultados</p>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {prods.map(p => {
                  const vProd = parseFloat(parcProd[p.id]||0);
                  const v = vProd > 0 ? vProd : (prods.length===1 && total>0 ? total : 0);
                  const isConsol = vProd===0 && v>0;
                  return (
                    <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                      padding:"10px 14px",borderRadius:7,
                      background:v>0?"#f0fdf4":"#f8f9fa",
                      border:`1px solid ${v>0?"#bbf7d0":"#dee2e6"}`}}>
                      <div>
                        <p style={{fontSize:13,fontWeight:600,color:theme.gray900,margin:0}}>{p.nome}</p>
                        <p style={{fontSize:10,color:theme.gray500,margin:"2px 0 0"}}>{p.baseLegal||p.resumo||""}</p>
                      </div>
                      <div style={{textAlign:"right",minWidth:150}}>
                        <p style={{fontSize:11,fontWeight:700,color:v>0?theme.green:"#888",margin:0}}>
                          {v>0 ? "✓ Crédito Apurado" : "— Sem crédito"}
                        </p>
                        {v>0 && <>
                          <p style={{fontSize:14,fontWeight:700,color:theme.green,margin:"2px 0 0"}}>
                            {v.toLocaleString("pt-BR",{style:"currency",currency:"BRL"})}
                          </p>
                          {isConsol && <p style={{fontSize:9,color:theme.gray400,margin:0}}>Valor consolidado</p>}
                        </>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{textAlign:"right",marginTop:10,padding:"10px 14px",borderRadius:7,
                background:theme.gold+"10",border:`2px solid ${theme.gold}44`}}>
                <p style={{fontSize:10,color:theme.gray500,textTransform:"uppercase",letterSpacing:.6}}>Total Geral</p>
                <p style={{fontSize:18,fontWeight:700,color:total>0?theme.green:"#888",margin:"3px 0 0"}}>
                  {total>0 ? total.toLocaleString("pt-BR",{style:"currency",currency:"BRL"}) : "Sem crédito identificado"}
                </p>
              </div>
            </div>

            {/* Conclusões por fase */}
            {conclusoes.length > 0 && (
              <div style={{marginBottom:16}}>
                <p style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.8,color:"#fff",background:theme.gray900,padding:"5px 10px",borderRadius:4,marginBottom:8}}>✍️ Conclusões por Fase</p>
                {conclusoes.map(([fase,txt]) => (
                  <div key={fase} style={{background:theme.gold+"08",border:`1px solid ${theme.gold}44`,borderLeft:`4px solid ${theme.gold}`,borderRadius:"0 7px 7px 0",padding:"10px 14px",marginBottom:8}}>
                    <p style={{fontSize:10,fontWeight:700,color:theme.gold,textTransform:"uppercase",letterSpacing:.6,marginBottom:4}}>{fase}</p>
                    <p style={{fontSize:12,color:theme.gray800,lineHeight:1.7,whiteSpace:"pre-wrap",margin:0}}>{txt}</p>
                  </div>
                ))}
              </div>
            )}
            {conclusoes.length === 0 && (
              <div style={{background:"#fef9ec",border:"1px solid #fde68a",borderRadius:7,padding:"10px 14px",marginBottom:16}}>
                <p style={{fontSize:11,color:"#92400e",margin:0}}>⚠️ Conclusões ainda não redigidas. Acesse os cards de cada fase na aba Execução para preenchê-las.</p>
              </div>
            )}

            {/* Botão PDF */}
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:8}}>
              <button onClick={imprimir} style={{padding:"10px 24px",borderRadius:8,border:"none",cursor:"pointer",
                background:theme.gray900,color:theme.gold,fontSize:13,fontWeight:700}}>
                🖨️ Gerar Relatório PDF
              </button>
            </div>
          </div>
        );
        })()}
        </div>{/* fim coluna direita */}
      </div>{/* fim split */}
    </div>
  );
}

// ============================================================
// RELATÓRIO DE ENCERRAMENTO — ENTRADA E TRIAGEM
// Gerado pela Controladoria para Estrategista e Controlador
// ============================================================
function RelatorioEntradaTriagem({ registros, contratos, clientes, produtos, faseDe, getNomeProd, equipe }) {
  const [regSel,    setRegSel]    = React.useState(null);
  const [imprimindo, setImprimindo] = React.useState(false);

  const regsEntrada = registros
    .filter(r => faseDe(r.status) === "Entrada e Triagem" || faseDe(r.status) === "Produção")
    .sort((a,b) => (a.clienteId||"").localeCompare(b.clienteId||"","pt-BR"));

  const fmtD = d => d ? new Date((d.length===10?d+"T12:00":d)).toLocaleDateString("pt-BR") : "—";
  const fmtDT = d => d ? new Date(d).toLocaleString("pt-BR",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}) : "—";
  const getNomeResp = id => (equipe||[]).find(e=>e.id===id)?.nome || "—";

  const gerarHTML = (r, ct, cl, conclusaoTxt) => {
    const pIds = (r.produtosIds||[]).length>0 ? r.produtosIds : [r.produtoId].filter(Boolean);
    const prodNomes = pIds.map(id=>produtos.find(p=>p.id===id)?.nome||id).join(", ");
    const docs = (r.documentos||[]);
    const ocorrs = (r.ocorrencias||[]).filter(o=>!o.confidencial);
    const pendentes = docs.filter(d=>!d.recebido);
    const recebidos = docs.filter(d=>d.recebido);
    const COR_OC = {Interna:"#3A5C8C",Externa:"#4A7C59",Urgente:"#8C3A3A",Cliente:"#B87C2A",Sistema:"#888"};
    return `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Relatório Entrada e Triagem — ${cl?.nome||"—"}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:Arial,sans-serif;font-size:12px;color:#1A1610;background:#fff;}
  .page{padding:24px 36px;}
  .hdr{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:12px;border-bottom:3px solid #C9A84C;margin-bottom:20px;}
  .hdr-escritorio{font-size:14px;font-weight:bold;color:#1A1610;}
  .hdr-sub{font-size:10px;color:#666;margin-top:3px;}
  .titulo{text-align:center;margin-bottom:18px;}
  .titulo h1{font-size:18px;font-weight:bold;text-transform:uppercase;color:#1A1610;letter-spacing:1px;}
  .titulo p{font-size:11px;color:#666;margin-top:4px;}
  .secao{margin-bottom:18px;}
  .sec-titulo{font-size:12px;font-weight:bold;text-transform:uppercase;letter-spacing:.8px;padding:6px 12px;background:#1A1610;color:#C9A84C;border-radius:4px;margin-bottom:8px;}
  .grid-2{display:grid;grid-template-columns:1fr 1fr;gap:6px 20px;}
  .campo{margin-bottom:5px;}
  .campo label{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.6px;display:block;}
  .campo span{font-size:12px;font-weight:600;color:#1A1610;}
  table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:8px;}
  th{background:#1A1610;color:#C9A84C;padding:6px 10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.6px;}
  td{padding:6px 10px;border-bottom:1px solid #eee;vertical-align:top;}
  tr:nth-child(even) td{background:#F9F6EF;}
  .badge-ok{color:#155724;background:#d4edda;padding:1px 7px;border-radius:3px;font-size:10px;font-weight:bold;}
  .badge-pend{color:#721c24;background:#f8d7da;padding:1px 7px;border-radius:3px;font-size:10px;font-weight:bold;}
  .badge-info{color:#004085;background:#cce5ff;padding:1px 7px;border-radius:3px;font-size:10px;}
  .conclusao-box{background:#f9f6ef;border:1px solid #C9A84C;border-radius:6px;padding:14px 16px;margin-top:8px;}
  .conclusao-box p{font-size:12px;line-height:1.7;white-space:pre-wrap;}
  .status-final{text-align:center;margin:20px 0;padding:12px;border-radius:6px;font-weight:bold;font-size:13px;}
  .status-ok{background:#d4edda;color:#155724;border:1px solid #c3e6cb;}
  .status-pend{background:#f8d7da;color:#721c24;border:1px solid #f5c6cb;}
  .ftr{margin-top:30px;padding-top:12px;border-top:1px solid #ccc;font-size:10px;color:#888;display:flex;justify-content:space-between;}
  @media print{@page{margin:15mm 15mm 20mm 15mm} body{print-color-adjust:exact;-webkit-print-color-adjust:exact;}}
</style></head><body><div class="page">
<div class="hdr">
  <div><div class="hdr-escritorio">Alves & Correa Advogados</div><div class="hdr-sub">OAB/SP — app.alvesecorreaadvogados.com.br</div></div>
  <div style="text-align:right"><div style="font-size:10px;color:#888;">Emitido em</div><div style="font-weight:bold;font-size:11px;">${new Date().toLocaleDateString("pt-BR",{day:"2-digit",month:"long",year:"numeric"})}</div></div>
</div>
<div class="titulo">
  <h1>Relatório de Encerramento — Entrada e Triagem</h1>
  <p>Documento gerencial · Uso interno · Controladoria</p>
</div>

<div class="secao">
  <div class="sec-titulo">1. Identificação</div>
  <div class="grid-2">
    <div class="campo"><label>Cliente</label><span>${cl?.nome||"—"}</span></div>
    <div class="campo"><label>CPF / CNPJ</label><span>${fmtCpfCnpj(ct?.cnpj||cl?.cpfCnpj||"")}</span></div>
    <div class="campo"><label>Contrato</label><span>${ct?.numContrato||r.numContrato||"—"}</span></div>
    <div class="campo"><label>Responsável</label><span>${getNomeResp(ct?.responsavel||r.responsavel||"")}</span></div>
    <div class="campo"><label>Data de Aprovação / Entrada</label><span>${fmtD(ct?.dataEntrada||r.dataInicio||"")}</span></div>
    <div class="campo"><label>Fase atual</label><span>${faseDe(r.status)}</span></div>
  </div>
</div>

<div class="secao">
  <div class="sec-titulo">2. Produtos Contratados</div>
  <p style="font-size:12px;font-weight:600;padding:6px 0;">${prodNomes||"—"}</p>
</div>

${(ct?.faturamentos?.length>0||ct?.regimes?.length>0||ct?.atividadePrincipal) ? `
<div class="secao">
  <div class="sec-titulo">3. Dados Empresariais</div>
  ${ct?.faturamentos?.length>0?`
  <p style="font-size:11px;font-weight:bold;margin-bottom:4px;color:#555;">Faturamento Anual</p>
  <table><thead><tr><th>Ano</th><th>Valor (R$)</th></tr></thead><tbody>
  ${ct.faturamentos.map(f=>`<tr><td>${f.ano||"—"}</td><td>${f.valor||"—"}</td></tr>`).join("")}
  </tbody></table>`:""}
  ${ct?.regimes?.length>0?`
  <p style="font-size:11px;font-weight:bold;margin:8px 0 4px;color:#555;">Regimes Tributários</p>
  <table><thead><tr><th>Regime</th><th>Início</th><th>Fim</th></tr></thead><tbody>
  ${ct.regimes.map(rg=>`<tr><td>${rg.tipo||"—"}</td><td>${fmtD(rg.dataInicio)}</td><td>${fmtD(rg.dataFim)||"Em vigor"}</td></tr>`).join("")}
  </tbody></table>`:""}
  ${ct?.atividadePrincipal?`<div class="campo" style="margin-top:8px;"><label>Atividade Principal (CNAE)</label><span>${ct.atividadePrincipal}</span></div>`:""}
  ${ct?.atividadesSecundarias?.length>0?`<div class="campo" style="margin-top:6px;"><label>Atividades Secundárias</label><span>${ct.atividadesSecundarias.join(" · ")}</span></div>`:""}
</div>` : ""}

<div class="secao">
  <div class="sec-titulo">4. Checklist de Documentos</div>
  <table><thead><tr><th>Documento</th><th>Status</th><th>Recebido em</th></tr></thead><tbody>
  ${docs.length===0?`<tr><td colspan="3" style="color:#888;font-style:italic;">Nenhum documento no checklist.</td></tr>`:
    docs.map(d=>`<tr>
      <td>${d.nome||"—"}</td>
      <td><span class="${d.recebido?"badge-ok":"badge-pend"}">${d.recebido?"✓ Recebido":"✗ Pendente"}</span>${d.pendenciaExterna?` <span class="badge-info">⏸ Pend. Externa</span>`:""}</td>
      <td>${d.dataRecebimento?fmtDT(d.dataRecebimento):"—"}</td>
    </tr>`).join("")}
  </tbody></table>
  ${pendentes.length>0?`<p style="font-size:11px;color:#721c24;font-weight:bold;margin-top:4px;">⚠️ ${pendentes.length} documento(s) pendente(s)</p>`:`<p style="font-size:11px;color:#155724;font-weight:bold;margin-top:4px;">✅ Todos os documentos recebidos</p>`}
</div>

${ocorrs.length>0?`
<div class="secao">
  <div class="sec-titulo">5. Ocorrências Registradas</div>
  <table><thead><tr><th>Data</th><th>Tipo</th><th>Descrição</th></tr></thead><tbody>
  ${ocorrs.map(oc=>`<tr><td style="white-space:nowrap;">${oc.data||"—"}${oc.hora?` ${oc.hora}`:""}</td><td><span style="color:${COR_OC[oc.tipo]||"#888"};font-weight:bold;">${oc.tipo}</span></td><td>${oc.descricao||"—"}</td></tr>`).join("")}
  </tbody></table>
</div>`:`
<div class="secao">
  <div class="sec-titulo">5. Ocorrências</div>
  <p style="font-size:11px;color:#888;font-style:italic;padding:6px 0;">Nenhuma ocorrência registrada nesta fase.</p>
</div>`}

<div class="secao">
  <div class="sec-titulo">6. Conclusão e Encaminhamento</div>
  <div class="conclusao-box"><p>${conclusaoTxt||"(Sem conclusão registrada.)"}</p></div>
</div>

<div class="status-final ${pendentes.length===0?"status-ok":"status-pend"}">
  ${pendentes.length===0?"✅ Fase concluída — documentação completa. Pronto para Produção.":"⚠️ Fase com pendências — "+pendentes.length+" documento(s) não recebido(s)."}
</div>

<div class="ftr">
  <span>Alves & Correa Advogados — Controladoria Digital</span>
  <span>Gerado em ${new Date().toLocaleString("pt-BR")}</span>
</div>
</div></body></html>`;
  };

  const imprimir = (r) => {
    const ct = contratos.find(c=>c.id===r.contratoId);
    const cl = clientes.find(c=>c.id===r.clienteId);
    const html = gerarHTML(r, ct, cl, r.conclusaoEntrada||"");
    const win = window.open("","_blank","width=900,height=700");
    if (!win) return alert("Permita pop-ups para gerar o PDF.");
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,
        padding:"12px 16px",background:theme.gray50,borderRadius:8,border:`1px solid ${theme.gray200}`}}>
        <div>
          <p style={{fontSize:13,fontWeight:700,color:theme.gray800,margin:0}}>📄 Relatório de Encerramento — Entrada e Triagem</p>
          <p style={{fontSize:11,color:theme.gray500,margin:"3px 0 0"}}>Selecione o contrato à esquerda e visualize o relatório à direita.</p>
        </div>
        <span style={{fontSize:12,color:theme.gray400}}>{regsEntrada.length} contrato(s)</span>
      </div>

      {/* Split layout */}
      <div style={{display:"grid",gridTemplateColumns:"300px 1fr",gap:16,alignItems:"start"}}>

        {/* ── Coluna esquerda: seletor ── */}
        <div style={{position:"sticky",top:16}}>
          <p style={{fontSize:11,fontWeight:700,color:theme.gray600,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>Contratos Disponíveis</p>
          <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:"calc(100vh - 260px)",overflowY:"auto",
            border:`1px solid ${theme.gray200}`,borderRadius:8,padding:8,background:theme.white}}>
            {regsEntrada.length===0 && <p style={{fontSize:12,color:theme.gray400,padding:"8px",textAlign:"center",fontStyle:"italic"}}>Nenhum contrato em Entrada e Triagem ou Produção.</p>}
            {regsEntrada.map(r=>{
              const ct=contratos.find(c=>c.id===r.contratoId);
              const cl=clientes.find(c=>c.id===r.clienteId);
              const sel=regSel?.contratoId===r.contratoId;
              const docs=(r.documentos||[]);
              const pend=docs.filter(d=>!d.recebido).length;
              const temConclusao=!!(r.conclusaoEntrada||"").trim();
              return (
                <div key={r.contratoId} onClick={()=>setRegSel(r)}
                  style={{padding:"10px 12px",borderRadius:7,cursor:"pointer",
                    border:`2px solid ${sel?theme.gold:theme.gray200}`,
                    background:sel?theme.gold+"10":theme.white,transition:"all .1s"}}>
                  <p style={{fontSize:12,fontWeight:700,color:theme.gray900,margin:0}}>{cl?.nome||"—"}</p>
                  <p style={{fontSize:10,color:theme.gray500,margin:"2px 0 0"}}>{ct?.numContrato||r.numContrato||"—"} · {faseDe(r.status)}</p>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:5}}>
                    <span style={{fontSize:9,padding:"1px 6px",borderRadius:3,fontWeight:700,
                      background:pend===0?"#d4edda":"#f8d7da",
                      color:pend===0?"#155724":"#721c24"}}>
                      {pend===0?"✅ Docs OK":`⚠️ ${pend} pend.`}
                    </span>
                    <span style={{fontSize:9,color:temConclusao?theme.green:theme.gray400}}>
                      {temConclusao ? "✍️ Com conclusão" : "Sem conclusão"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Coluna direita: preview ── */}
        <div>
        {!regSel ? (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",
            minHeight:300,background:theme.gray50,borderRadius:10,border:`1px dashed ${theme.gray300}`}}>
            <div style={{textAlign:"center",color:theme.gray400}}>
              <p style={{fontSize:28,marginBottom:8}}>📄</p>
              <p style={{fontSize:13,fontWeight:600}}>Selecione um contrato</p>
              <p style={{fontSize:11,marginTop:4}}>O relatório aparecerá aqui</p>
            </div>
          </div>
        ) : (()=>{
          const ct=contratos.find(c=>c.id===regSel.contratoId);
          const cl=clientes.find(c=>c.id===regSel.clienteId);
          const pIds=(regSel.produtosIds||[]).length>0?regSel.produtosIds:[regSel.produtoId].filter(Boolean);
          const prodNomes=pIds.map(id=>produtos.find(p=>p.id===id)?.nome||id).join(", ");
          const docs=(regSel.documentos||[]);
          const ocorrs=(regSel.ocorrencias||[]).filter(o=>!o.confidencial);
          const recebidos=docs.filter(d=>d.recebido);
          const pendentes=docs.filter(d=>!d.recebido);
          const COR_OC2={Interna:theme.blue,Externa:theme.green,Urgente:theme.red,Cliente:theme.amber,Sistema:theme.gray400};
          return (
            <>
            {/* Preview on-screen */}
            <div style={{background:theme.white,borderRadius:10,border:`1px solid ${theme.gray200}`,
              padding:"20px 24px",marginBottom:16}}>

              {/* Header preview */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                paddingBottom:12,marginBottom:16,borderBottom:`2px solid ${theme.gold}`}}>
                <div>
                  <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0}}>Alves & Correa Advogados</p>
                  <p style={{fontSize:10,color:theme.gray400,margin:"2px 0 0"}}>Relatório de Encerramento — Entrada e Triagem</p>
                </div>
                <span style={{fontSize:11,color:theme.gray500}}>{new Date().toLocaleDateString("pt-BR",{day:"2-digit",month:"long",year:"numeric"})}</span>
              </div>

              {/* 1. Identificação */}
              <div style={{marginBottom:16}}>
                <p style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.8,
                  color:"#fff",background:theme.gray900,padding:"5px 10px",borderRadius:4,marginBottom:8}}>
                  🏷️ Identificação
                </p>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                  {[["Cliente",cl?.nome||"—"],["CNPJ/CPF",fmtCpfCnpj(ct?.cnpj||cl?.cpfCnpj||"")],
                    ["Contrato",ct?.numContrato||regSel.numContrato||"—"],
                    ["Responsável",equipe.find(e=>e.id===(ct?.responsavel||regSel.responsavel))?.nome||"—"],
                    ["Data Entrada",fmtD(ct?.dataEntrada||regSel.dataInicio||"")],
                    ["Fase",faseDe(regSel.status)]
                  ].map(([l,v])=>(
                    <div key={l} style={{background:theme.gray50,borderRadius:6,padding:"8px 12px"}}>
                      <p style={{fontSize:9,color:theme.gray400,textTransform:"uppercase",letterSpacing:.6,marginBottom:2}}>{l}</p>
                      <p style={{fontSize:12,fontWeight:600,color:theme.gray800}}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* 2. Produtos */}
              <div style={{marginBottom:14}}>
                <p style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.8,
                  color:"#fff",background:theme.gray900,padding:"5px 10px",borderRadius:4,marginBottom:6}}>
                  📦 Produtos Contratados
                </p>
                <p style={{fontSize:12,fontWeight:600,color:theme.gray700,padding:"6px 0"}}>{prodNomes||"—"}</p>
              </div>

              {/* 3. Checklist docs */}
              <div style={{marginBottom:14}}>
                <p style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.8,
                  color:"#fff",background:theme.gray900,padding:"5px 10px",borderRadius:4,marginBottom:8}}>
                  📋 Documentos ({recebidos.length}/{docs.length} recebidos)
                </p>
                {docs.length===0
                  ? <p style={{fontSize:11,color:theme.gray400,fontStyle:"italic"}}>Nenhum documento no checklist.</p>
                  : <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      {docs.map((d,i)=>(
                        <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                          padding:"6px 10px",borderRadius:6,
                          background:d.recebido?"#f0fdf4":"#fef9f9",
                          border:`1px solid ${d.recebido?"#bbf7d0":"#fecaca"}`}}>
                          <span style={{fontSize:12,color:d.recebido?theme.green:theme.red}}>{d.nome||"—"}</span>
                          <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            {d.pendenciaExterna&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:"#e0e7ff",color:"#3730a3",fontWeight:700}}>⏸ Pend. Ext.</span>}
                            <span style={{fontSize:10,fontWeight:700,color:d.recebido?theme.green:theme.red}}>{d.recebido?"✓ Recebido":"✗ Pendente"}</span>
                            {d.recebido&&d.dataRecebimento&&<span style={{fontSize:9,color:theme.gray400}}>{fmtDT(d.dataRecebimento)}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                }
                {pendentes.length>0
                  ? <p style={{fontSize:11,color:theme.red,fontWeight:700,marginTop:6}}>⚠️ {pendentes.length} documento(s) pendente(s)</p>
                  : <p style={{fontSize:11,color:theme.green,fontWeight:700,marginTop:6}}>✅ Documentação completa</p>
                }
              </div>

              {/* 4. Ocorrências */}
              {ocorrs.length>0&&(
                <div style={{marginBottom:14}}>
                  <p style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.8,
                    color:"#fff",background:theme.gray900,padding:"5px 10px",borderRadius:4,marginBottom:8}}>
                    📝 Ocorrências ({ocorrs.length})
                  </p>
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    {ocorrs.map((oc,i)=>(
                      <div key={i} style={{padding:"7px 10px",borderRadius:6,background:theme.gray50,border:`1px solid ${theme.gray200}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                          <span style={{fontSize:10,fontWeight:700,color:COR_OC2[oc.tipo]||theme.gray600}}>{oc.tipo||"Registro"}</span>
                          <span style={{fontSize:9,color:theme.gray400}}>{oc.data||"—"}{oc.hora?` ${oc.hora}`:""}</span>
                        </div>
                        <p style={{fontSize:11,color:theme.gray700,margin:0,lineHeight:1.4}}>{oc.descricao}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 5. Conclusão — lida do registro (redigida no card de Execução) */}
              <div style={{marginBottom:16}}>
                <p style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.8,
                  color:"#fff",background:theme.gray900,padding:"5px 10px",borderRadius:4,marginBottom:8}}>
                  ✍️ Conclusão da Triagem
                </p>
                {regSel.conclusaoEntrada
                  ? <div style={{background:theme.gold+"08",border:`1px solid ${theme.gold}44`,
                      borderRadius:7,padding:"12px 14px"}}>
                      <p style={{fontSize:12,color:theme.gray800,lineHeight:1.7,whiteSpace:"pre-wrap",margin:0}}>
                        {regSel.conclusaoEntrada}
                      </p>
                    </div>
                  : <div style={{background:"#fef9ec",border:"1px solid #fde68a",borderRadius:7,padding:"10px 14px"}}>
                      <p style={{fontSize:11,color:"#92400e",margin:0}}>
                        ⚠️ Conclusão não redigida. Acesse o card da fase <strong>Entrada e Triagem</strong> na aba Execução para preencher.
                      </p>
                    </div>
                }
              </div>

              {/* Status final */}
              <div style={{textAlign:"center",padding:"12px",borderRadius:7,fontWeight:700,fontSize:13,
                background:pendentes.length===0?"#d4edda":"#f8d7da",
                color:pendentes.length===0?"#155724":"#721c24",
                border:`1px solid ${pendentes.length===0?"#c3e6cb":"#f5c6cb"}`,marginBottom:16}}>
                {pendentes.length===0
                  ? "✅ Fase concluída — documentação completa. Pronto para encaminhamento à Produção."
                  : `⚠️ Fase com pendências — ${pendentes.length} documento(s) não recebido(s).`}
              </div>
            </div>

            {/* Botão gerar PDF */}
            <div style={{display:"flex",justifyContent:"flex-end"}}>
              <button onClick={()=>imprimir(regSel)} style={{
                padding:"10px 24px",borderRadius:8,border:"none",cursor:"pointer",
                background:theme.gray900,color:theme.gold,fontSize:13,fontWeight:700,
                display:"flex",alignItems:"center",gap:8}}>
                🖨️ Gerar PDF / Imprimir
              </button>
            </div>
            </>
          );
        })()}
          </div>{/* fim coluna direita */}
        </div>{/* fim split */}
      </div>
    );
}

// ============================================================
// CONTROLADORIA DIGITAL — Componente independente
// Props: registros, getNomeCli, getNomeProd, getNomeResp,
//        FASES_KANBAN, faseDe, COR_ST, setData, equipe, usuario
// ============================================================
function ControladoriaDigital({ registros, contratos, clientes, produtos, getNomeCli, getNomeProd, getNomeResp, FASES_KANBAN, faseDe, COR_ST, setData, equipe, usuario, setMesaFase }) {
  const [visaoCtrl,      setVisaoCtrl]      = useState("estrategista");
  const [filtroFaseCtrl, setFiltroFaseCtrl] = useState("");
  const [filtroRespCtrl, setFiltroRespCtrl] = useState("");

  const hoje5 = new Date(); hoje5.setHours(0,0,0,0);
  const fmtD5 = d => d ? new Date(d+"T12:00").toLocaleDateString("pt-BR") : "—";
  const diasNaFase = r => {
    const d = r.dataInicio || r.dataEntrada || "";
    if (!d) return null;
    return Math.ceil((hoje5 - new Date(d+"T12:00")) / (1000*60*60*24));
  };
  const faseAtual = r => faseDe(r.status);

  // KPIs por fase
  const kpisFases = FASES_KANBAN.map(f=>({
    ...f, total: registros.filter(r=>faseAtual(r)===f.id).length
  }));

  // Registros com responsável por fase (operador/revisor)
  const regsCtrl = registros.map(r=>({
    ...r,
    faseAtual: faseAtual(r),
    operador: r.responsavelProducao || r.responsavel || "",
    revisor:  r.responsavelRevisao  || "",
    diasFase: diasNaFase(r),
    cliNome:  getNomeCli(r.clienteId),
    prodNome: getNomeProd(r.produtoId),
  })).filter(r=>
    (!filtroFaseCtrl || r.faseAtual===filtroFaseCtrl) &&
    (!filtroRespCtrl || r.operador===filtroRespCtrl || r.revisor===filtroRespCtrl)
  );

  // Fila de entrada (Controlador)
  const filaEntrada = registros.filter(r=>faseAtual(r)==="Entrada e Triagem")
    .sort((a,b)=>((a.dataInicio||"")>(b.dataInicio||"")?1:-1));

  // Cards vencidos ou urgentes por prazo de fase
  const PRAZO_FASE_H = {"Entrada e Triagem":4,"Produção":72,"Revisão Sênior":24,"Protocolo":4};
  const urgentes = registros.filter(r=>{
    const prazoH = PRAZO_FASE_H[faseAtual(r)];
    if (!prazoH || !r.dataInicio) return false;
    const horas = (hoje5 - new Date(r.dataInicio+"T12:00")) / (1000*60*60);
    return horas > prazoH;
  });

  return (
    <div>
      {/* Seletor de visão */}
      <div style={{display:"flex",gap:8,marginBottom:20,alignItems:"center",flexWrap:"wrap"}}>
        {[
          ["estrategista","📊 Estrategista"],
          ["controlador","🎛️ Controlador"],
          ["operador","⚙️ Operador / Revisor"],
          ["relatorio","📄 Relatório E&T"],
          ["conclusao","📋 Rel. Conclusão"],
        ].map(([v,l])=>(
          <button key={v} onClick={()=>setVisaoCtrl(v)} style={{
            padding:"8px 18px",borderRadius:20,border:"none",cursor:"pointer",
            fontSize:12,fontWeight:visaoCtrl===v?700:400,
            background:visaoCtrl===v?"#1a1610":theme.gray100,
            color:visaoCtrl===v?"#C9A84C":theme.gray500,
          }}>{l}</button>
        ))}
      </div>

      {/* ── VISÃO ESTRATEGISTA ── */}
      {visaoCtrl==="estrategista"&&(
        <div>
          {/* KPIs por fase com listagem expandida */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,minmax(0,1fr))",gap:6,marginBottom:20}}>
            {kpisFases.map(f=>{
              const regsNaFase = registros
                .filter(r => faseAtual(r) === f.id)
                .sort((a,b) => getNomeCli(a.clienteId).localeCompare(getNomeCli(b.clienteId),"pt-BR"));
              return (
                <div key={f.id} style={{borderRadius:8,border:`1px solid ${theme.gray200}`,
                  borderTop:`3px solid ${f.cor}`,background:theme.white,overflow:"hidden",minWidth:0}}>
                  {/* Header compacto */}
                  <div style={{padding:"8px 6px",textAlign:"center",background:theme.white}}>
                    <p style={{fontSize:14,margin:"0 0 2px"}}>{f.icon}</p>
                    <p style={{fontSize:18,fontWeight:700,color:f.cor,margin:0,cursor:"pointer",lineHeight:1}}
                      onClick={()=>{setFiltroFaseCtrl(f.id);setVisaoCtrl("controlador");}}>
                      {f.total}
                    </p>
                    <p style={{fontSize:8,color:theme.gray400,lineHeight:1.3,marginTop:2,
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",padding:"0 2px"}}
                      title={f.id}>{f.id}</p>
                    <button onClick={()=>setMesaFase(f.id)}
                      title={`Mesa de Trabalho — ${f.id}`}
                      style={{marginTop:5,padding:"2px 0",borderRadius:6,width:"100%",
                        border:`1px solid ${f.cor}44`,background:f.cor+"11",
                        color:f.cor,cursor:"pointer",fontSize:9,fontWeight:700,
                        fontFamily:"inherit"}}>
                      🏢 Mesa
                    </button>
                  </div>
                  {/* Listagem compacta */}
                  {regsNaFase.length > 0 && (
                    <div style={{borderTop:`1px solid ${theme.gray100}`,
                      maxHeight:180,overflowY:"auto",padding:"2px 0"}}>
                      {regsNaFase.map(r => {
                        const dias = diasNaFase(r);
                        const prazoH = PRAZO_FASE_H[f.id];
                        const urgente = prazoH && dias && (dias*24) > prazoH;
                        return (
                          <div key={r.id} style={{padding:"4px 6px",
                            borderBottom:`1px solid ${theme.gray50}`,
                            background:urgente?"#fff8f8":theme.white}}>
                            <p style={{fontSize:9,fontWeight:600,color:urgente?theme.red:theme.gray900,
                              margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                              title={getNomeCli(r.clienteId)}>
                              {getNomeCli(r.clienteId)}
                            </p>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:1}}>
                              <p style={{fontSize:8,color:theme.gray400,margin:0,
                                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}
                                title={getNomeProd(r.produtoId)}>
                                {getNomeProd(r.produtoId)}
                              </p>
                              {dias != null && (
                                <span style={{fontSize:8,fontWeight:700,flexShrink:0,marginLeft:2,
                                  color:urgente?theme.red:theme.gray400}}>
                                  {urgente?"⚠️":""}{dias}d
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Alertas urgentes */}
          {urgentes.length>0&&(
            <Card style={{marginBottom:16,border:"2px solid #fecaca",background:"#fef2f2"}}>
              <p style={{fontSize:12,fontWeight:700,color:theme.red,marginBottom:10}}>
                ⚠️ Demandas fora do prazo da fase ({urgentes.length})
              </p>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {urgentes.map(r=>(
                  <div key={r.id} style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",padding:"8px 10px",borderRadius:6,
                    background:"#fff",border:"1px solid #fecaca"}}>
                    <div>
                      <p style={{fontSize:12,fontWeight:600,margin:0}}>{getNomeCli(r.clienteId)}</p>
                      <p style={{fontSize:10,color:theme.gray500,margin:"2px 0 0"}}>
                        {getNomeProd(r.produtoId)} · {faseDe(r.status)}
                      </p>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <span style={{fontSize:11,fontWeight:700,color:theme.red}}>
                        {diasNaFase(r)}d na fase
                      </span>
                      <p style={{fontSize:10,color:theme.gray400,margin:"2px 0 0"}}>
                        Resp: {getNomeResp(r.responsavel)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Tabela geral */}
          <Card>
            <p style={{fontSize:11,fontWeight:700,color:theme.gray700,marginBottom:12,
              textTransform:"uppercase",letterSpacing:.8}}>
              📋 Visão Geral — Todas as Demandas
            </p>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead><tr style={{background:theme.gray50}}>
                  {["Cliente","Produto","Fase","Operador","Revisor","Dias na Fase","Docs Pend.","Status"].map(h=>(
                    <th key={h} style={{padding:"8px 10px",textAlign:"left",fontSize:10,
                      color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>
                      {h}
                    </th>
                  ))}
                </tr></thead>
                <tbody>
                  {registros.map((r,i)=>{
                    const fase = faseAtual(r);
                    const faseObj = FASES_KANBAN.find(f=>f.id===fase);
                    const dias = diasNaFase(r);
                    const prazoH = PRAZO_FASE_H[fase];
                    const urgente = prazoH && dias && (dias*24)>prazoH;
                    return(
                      <tr key={r.id} style={{borderBottom:"1px solid "+theme.gray100,
                        background:urgente?"#fef2f2":i%2===0?theme.white:theme.gray50}}>
                        <td style={{padding:"8px 10px",fontWeight:600}}>{getNomeCli(r.clienteId)}</td>
                        <td style={{padding:"8px 10px",fontSize:11,color:theme.gray600}}>{getNomeProd(r.produtoId)}</td>
                        <td style={{padding:"8px 10px"}}>
                          <span style={{fontSize:10,padding:"2px 8px",borderRadius:8,fontWeight:700,
                            background:(faseObj?.cor||theme.gray400)+"22",
                            color:faseObj?.cor||theme.gray400}}>
                            {faseObj?.icon} {fase}
                          </span>
                        </td>
                        <td style={{padding:"8px 10px",fontSize:11}}>{getNomeResp(r.responsavelProducao||r.responsavel||"")}</td>
                        <td style={{padding:"8px 10px",fontSize:11}}>{getNomeResp(r.responsavelRevisao||"")||"—"}</td>
                        <td style={{padding:"8px 10px"}}>
                          {dias!==null&&<span style={{fontSize:11,fontWeight:urgente?700:400,
                            color:urgente?theme.red:theme.gray600}}>
                            {urgente?"⚠️ ":""}{dias}d
                          </span>}
                        </td>
                        <td style={{padding:"8px 10px",fontSize:11,
                          color:r.docsPendentes>0?theme.amber:theme.green,fontWeight:r.docsPendentes>0?700:400}}>
                          {r.docsPendentes>0?`📄 ${r.docsPendentes}`:"✓"}
                        </td>
                        <td style={{padding:"8px 10px"}}>
                          <Badge color={COR_ST[r.status]||"gray"} style={{fontSize:9}}>{r.status}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ── VISÃO CONTROLADOR ── */}
      {visaoCtrl==="controlador"&&(
        <div>
          {/* Filtros */}
          <Card style={{padding:"12px 16px",marginBottom:16,display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
            <select value={filtroFaseCtrl} onChange={e=>setFiltroFaseCtrl(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12}}>
              <option value="">Todas as fases</option>
              {FASES_KANBAN.map(f=><option key={f.id} value={f.id}>{f.icon} {f.id}</option>)}
            </select>
            <select value={filtroRespCtrl} onChange={e=>setFiltroRespCtrl(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12}}>
              <option value="">Todos os responsáveis</option>
              {equipe.map(e=><option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
            {(filtroFaseCtrl||filtroRespCtrl)&&(
              <Btn size="sm" variant="secondary" onClick={()=>{setFiltroFaseCtrl("");setFiltroRespCtrl("");}}>✕ Limpar</Btn>
            )}
          </Card>

          {/* Fila de Entrada */}
          {!filtroFaseCtrl&&(
            <Card style={{marginBottom:16,border:"2px solid #0ea5e933",background:"#f0f9ff"}}>
              <p style={{fontSize:12,fontWeight:700,color:"#0ea5e9",marginBottom:10}}>
                📥 Fila de Entrada e Triagem ({filaEntrada.length})
              </p>
              {filaEntrada.length===0
                ? <p style={{fontSize:12,color:theme.green,fontWeight:600}}>✅ Fila vazia</p>
                : filaEntrada.map((r,i)=>(
                    <div key={r.id} style={{display:"flex",justifyContent:"space-between",
                      alignItems:"center",padding:"8px 10px",marginBottom:6,
                      borderRadius:6,background:"#fff",border:"1px solid #bfdbfe"}}>
                      <div>
                        <p style={{fontSize:12,fontWeight:700,margin:0}}>{getNomeCli(r.clienteId)}</p>
                        <p style={{fontSize:10,color:theme.gray500,margin:"2px 0 0"}}>
                          {getNomeProd(r.produtoId)} · Entrada: {fmtD5(r.dataInicio)}
                        </p>
                      </div>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        <span style={{fontSize:11,color:"#0ea5e9",fontWeight:600}}>
                          ⏱ {diasNaFase(r)||0}d
                        </span>
                        <span title="O avanço de fase é liberado automaticamente pela Mesa de Trabalho após conclusão de todas as obrigações."
                          style={{fontSize:10,padding:"4px 10px",borderRadius:6,
                          border:"1px solid #e5e7eb",background:"#f3f4f6",
                          color:"#9ca3af",cursor:"default",fontWeight:500}}>
                          🔒 Mesa
                        </span>
                      </div>
                    </div>
                  ))
              }
            </Card>
          )}

          {/* Lista filtrada */}
          <Card>
            <p style={{fontSize:11,fontWeight:700,color:theme.gray700,marginBottom:12,
              textTransform:"uppercase",letterSpacing:.8}}>
              📋 Demandas {filtroFaseCtrl?`— ${filtroFaseCtrl}`:"— Todas as Fases"}
              <span style={{marginLeft:8,fontSize:11,color:theme.gray400,fontWeight:400}}>
                ({regsCtrl.length} registro{regsCtrl.length!==1?"s":""})
              </span>
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {regsCtrl.map(r=>{
                const faseObj=FASES_KANBAN.find(f=>f.id===r.faseAtual);
                return(
                  <div key={r.id} style={{padding:"12px 14px",borderRadius:8,
                    border:`1px solid ${faseObj?.cor||theme.gray200}33`,
                    background:theme.white}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
                          <p style={{fontSize:13,fontWeight:700,margin:0}}>{r.cliNome}</p>
                          <span style={{fontSize:10,padding:"2px 8px",borderRadius:8,fontWeight:700,
                            background:(faseObj?.cor||theme.gray400)+"22",
                            color:faseObj?.cor||theme.gray400}}>
                            {faseObj?.icon} {r.faseAtual}
                          </span>
                        </div>
                        <p style={{fontSize:11,color:theme.gray500,margin:0}}>{r.prodNome}</p>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <p style={{fontSize:11,color:theme.gray600,margin:0}}>
                          ⚙️ {getNomeResp(r.responsavelProducao||r.responsavel||"")}
                        </p>
                        <p style={{fontSize:11,color:"#7c3aed",margin:"2px 0 0"}}>
                          🔍 {getNomeResp(r.responsavelRevisao||"")||"—"}
                        </p>
                        {r.diasFase&&<p style={{fontSize:10,color:theme.gray400,margin:"4px 0 0"}}>
                          ⏱ {r.diasFase}d na fase
                        </p>}
                      </div>
                    </div>
                  </div>
                );
              })}
              {regsCtrl.length===0&&<EmptyState icon="📋" title="Nenhuma demanda encontrada" subtitle="Ajuste os filtros."/>}
            </div>
          </Card>
        </div>
      )}

      {/* ── VISÃO OPERADOR / REVISOR ── */}
      {visaoCtrl==="operador"&&(
        <div>
          <Card style={{padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"center"}}>
            <p style={{fontSize:12,color:theme.gray600,margin:0}}>Filtrar por responsável:</p>
            <select value={filtroRespCtrl} onChange={e=>setFiltroRespCtrl(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,flex:1}}>
              <option value="">Todos</option>
              {equipe.map(e=><option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </Card>

          {/* Cards do operador */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            {/* Coluna: Em Produção */}
            <Card>
              <p style={{fontSize:11,fontWeight:700,color:"#d97706",marginBottom:12,
                textTransform:"uppercase",letterSpacing:.8}}>
                ⚙️ Em Produção
              </p>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {registros.filter(r=>faseAtual(r)==="Produção"&&
                  (!filtroRespCtrl||(r.responsavelProducao||r.responsavel)===filtroRespCtrl))
                  .map(r=>(
                    <div key={r.id} style={{padding:"10px 12px",borderRadius:8,
                      border:`1px solid #d9770633`,background:"#fffbeb"}}>
                      <p style={{fontSize:12,fontWeight:700,margin:"0 0 2px"}}>{getNomeCli(r.clienteId)}</p>
                      <p style={{fontSize:10,color:theme.gray500,margin:"0 0 6px"}}>{getNomeProd(r.produtoId)}</p>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:10,color:"#d97706"}}>⏱ {diasNaFase(r)||0}d</span>
                        <span style={{fontSize:10,padding:"2px 8px",borderRadius:4,background:"#7c3aed15",color:"#7c3aed",fontWeight:600}}>
                          🔍 Revisão Sênior
                        </span>
                      </div>
                    </div>
                  ))}
                {registros.filter(r=>faseAtual(r)==="Produção"&&
                  (!filtroRespCtrl||(r.responsavelProducao||r.responsavel)===filtroRespCtrl)).length===0&&
                  <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"12px 0"}}>Sem itens</p>
                }
              </div>
            </Card>

            {/* Coluna: Em Revisão Sênior */}
            <Card>
              <p style={{fontSize:11,fontWeight:700,color:"#7c3aed",marginBottom:12,
                textTransform:"uppercase",letterSpacing:.8}}>
                🔍 Em Revisão Sênior
              </p>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {registros.filter(r=>faseAtual(r)==="Revisão Sênior"&&
                  (!filtroRespCtrl||(r.responsavelRevisao||r.responsavel)===filtroRespCtrl))
                  .map(r=>(
                    <div key={r.id} style={{padding:"10px 12px",borderRadius:8,
                      border:`1px solid #7c3aed33`,background:"#faf5ff"}}>
                      <p style={{fontSize:12,fontWeight:700,margin:"0 0 2px"}}>{getNomeCli(r.clienteId)}</p>
                      <p style={{fontSize:10,color:theme.gray500,margin:"0 0 6px"}}>{getNomeProd(r.produtoId)}</p>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:10,color:"#7c3aed"}}>⏱ {diasNaFase(r)||0}d</span>
                        <span style={{fontSize:10,padding:"2px 8px",borderRadius:4,background:"#ea580c15",color:"#ea580c",fontWeight:600}}>
                          📨 Protocolo
                        </span>
                      </div>
                    </div>
                  ))}
                {registros.filter(r=>faseAtual(r)==="Revisão Sênior"&&
                  (!filtroRespCtrl||(r.responsavelRevisao||r.responsavel)===filtroRespCtrl)).length===0&&
                  <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"12px 0"}}>Sem itens</p>
                }
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ── VISÃO RELATÓRIO E&T ── */}
      {visaoCtrl==="relatorio"&&(
        <RelatorioEntradaTriagem
          registros={registros}
          contratos={contratos}
          clientes={clientes}
          produtos={produtos}
          faseDe={faseDe}
          getNomeProd={getNomeProd}
          equipe={equipe}
        />
      )}

      {/* ── VISÃO RELATÓRIO DE CONCLUSÃO ── */}
      {visaoCtrl==="conclusao"&&(
        <RelatorioConclusao
          registros={registros}
          contratos={contratos}
          clientes={clientes}
          produtos={produtos}
          faseDe={faseDe}
          equipe={equipe}
          setData={setData}
        />
      )}
    </div>
  );
}

// ============================================================
// COMUNICAÇÕES COM O CLIENTE — Módulo 8
// Kit Boas-Vindas · Linha de Confiança (7 dias) · Datas Especiais
// ============================================================

const LINK_GOOGLE_ESCRITORIO = "https://g.page/alvescorreaadvogados";
const GOOGLE_MAPS_ESCRITORIO = LINK_GOOGLE_ESCRITORIO;

const COMUNICACAO_GATILHOS = {
  // Régua Dia 1 (=contrato assinado)
  "boas_vindas_d1": { titulo: "Kit de Boas-Vindas — Dia 1", modulo: "Kit de Boas-Vindas", canal: "WhatsApp + E-mail", diasApos: 0 },
  "boas_vindas_d2": { titulo: "Linha de Confiança — Dia 2 (Confirmação Checklist)", modulo: "Linha de Confiança", canal: "WhatsApp", diasApos: 2 },
  "boas_vindas_d3": { titulo: "Linha de Confiança — Dia 3 (Bastidores)", modulo: "Linha de Confiança", canal: "WhatsApp", diasApos: 3 },
  "boas_vindas_d5": { titulo: "Linha de Confiança — Dia 5 (Check-in)", modulo: "Linha de Confiança", canal: "WhatsApp", diasApos: 5 },
  "boas_vindas_d7": { titulo: "Linha de Confiança — Dia 7 (Marco Legal)", modulo: "Linha de Confiança", canal: "E-mail + WhatsApp", diasApos: 7 },
  // Datas Especiais
  "aniversario_cliente": { titulo: "Aniversário do Cliente", modulo: "Datas Especiais", canal: "WhatsApp", diasApos: null },
  "aniversario_caso_1a": { titulo: "1 Ano do Início do Caso", modulo: "Datas Especiais", canal: "E-mail + WhatsApp", diasApos: 365 },
  "fim_de_ano":    { titulo: "Mensagem de Fim de Ano", modulo: "Datas Especiais", canal: "E-mail + WhatsApp", diasApos: null },
  "dia_advogado":  { titulo: "Dia do Advogado (11/ago)", modulo: "Datas Especiais", canal: "E-mail + WhatsApp", diasApos: null },
  "dia_cliente":   { titulo: "Dia do Cliente (15/set)", modulo: "Datas Especiais", canal: "E-mail + WhatsApp", diasApos: null },
};

const gerarMensagemComunicacao = (tipo, cl, ct, prod, equipe, modelosComunicacao) => {
  const nomeResp = (equipe||[]).find(e=>e.id===ct?.responsavel)?.nome || "Dr. Nelson José Alves";
  const isPJ = cl?.tipo === "PJ";
  // Para PJ: usar nome do contato principal se disponível
  const contatoPJ = isPJ ? (cl?.contatosPJ||[]).find(c=>c.nome?.trim()) : null;
  const nomeContato = contatoPJ?.nome || cl?.nome || "[NOME]";
  const nomeEmpresa = isPJ ? cl?.nome || "" : "";
  const ddiUsado = contatoPJ?.whatsappDDI || cl?.whatsappDDI || "55";
  const whats = whatsCompleto(ddiUsado, contatoPJ?.whatsapp || cl?.whatsapp || "");
  const prefixoPJ = isPJ && nomeEmpresa ? `Att. ${nomeEmpresa}\n\n` : "";
  const assinLonga = `\n\nAtenciosamente,\nAlves & Correa Advogados\nDr. Nelson José Alves · OAB/SP 398.875\nDra. Annie Lemos Alves Correa · OAB/SP 446.942`;
  const assinatura = assinLonga;

  const prodNome = prod?.nome || (ct?.produtoId ? "o seu caso" : "a recuperação do seu crédito tributário");
  const dataEnt  = ct?.dataEntrada || "";
  const addDias  = (d, n) => {
    if (!d) return "[DATA]";
    const dt = new Date(d+"T12:00"); dt.setDate(dt.getDate()+n);
    return dt.toLocaleDateString("pt-BR");
  };

  const msgs = {
    boas_vindas_d1: `${prefixoPJ}Olá, ${nomeContato}! É um prazer tê-lo(a) como cliente do escritório Alves & Correa Advogados. Recebi seu caso — ${prodNome} — com muita atenção, e posso garantir que cuidaremos de tudo com o maior cuidado e dedicação. Nos próximos dias você receberá todas as informações para darmos início aos trabalhos. Qualquer dúvida, estou à disposição! 🤝${assinatura}`,

    boas_vindas_d2: `${prefixoPJ}Olá, ${nomeContato}! Tudo bem? Passando para confirmar que você recebeu o nosso Kit de Boas-Vindas 😊 Se tiver dúvida sobre algum documento da lista, pode me chamar à vontade. Assim que tivermos tudo em mãos, já damos início aos trabalhos!${assinatura}`,

    boas_vindas_d3: `${prefixoPJ}Olá, ${nomeContato}! Uma atualização rápida: estamos com o seu caso em análise. Já identificamos os pontos prioritários de ${prodNome}. Em breve teremos mais novidades. Pode ficar tranquilo(a)! 💼${assinatura}`,

    boas_vindas_d5: `${prefixoPJ}Olá, ${nomeContato}! Como você está? Queria saber se está se sentindo bem atendido(a) até agora — se tem alguma dúvida, algo que ainda não ficou claro ou qualquer ponto que queira conversar. Estou à disposição! Para nós, o mais importante é que você se sinta seguro(a) durante todo o processo. 🙏${assinatura}`,

    boas_vindas_d7: `${prefixoPJ}Prezado(a) ${nomeContato},\n\nCompletamos a primeira semana de trabalho no seu caso e gostaríamos de compartilhar um resumo:\n\n✅ Análise inicial do caso concluída\n✅ Documentos recebidos: [listar]\n⏳ Pendente: [listar, se houver]\n📋 Próximo passo: [descrever] — prazo estimado: ${addDias(dataEnt, 30)}\n\nEstamos avançando conforme planejado. Qualquer dúvida, estamos à disposição.${assinLonga}`,

    aniversario_cliente: `${prefixoPJ}Olá, ${nomeContato}! 🎂 O escritório Alves & Correa Advogados quis fazer questão de lembrar do seu dia. Desejamos um feliz aniversário com muita saúde, conquistas e — claro — cada vez menos dores de cabeça jurídicas! 😄 Conte sempre conosco.${assinLonga}`,

    aniversario_caso_1a: `${prefixoPJ}Prezado(a) ${nomeContato},\n\nHoje se completa 1 (um) ano desde que iniciamos juntos o trabalho em ${prodNome} (Contrato ${ct?.numContrato||""}).\n\nNesta data, gostaríamos de compartilhar um breve balanço:\n• Status atual: [descrever]\n• Principais avanços: [listar]\n• Próximos passos: [descrever]\n\nSeguimos firmes na defesa dos seus interesses. Obrigado pela confiança de sempre!${assinLonga}`,

    fim_de_ano: `${prefixoPJ}Prezado(a) ${nomeContato},\n\nEncerramos mais um ano com a gratidão de ter contado com a sua confiança. Foi um ano de muito trabalho, aprendizados e resultados — e a sua parceria foi parte essencial disso.\n\nQue o próximo ano traga novas conquistas para você e sua família. Estaremos sempre aqui!${assinLonga}`,

    dia_advogado: `${prefixoPJ}Prezado(a) ${nomeContato},\n\nHoje, 11 de agosto, celebramos o Dia do Advogado. Para nós, é também o dia de renovar nosso compromisso com a defesa dos seus direitos e interesses.\n\nAgradecemos pela sua confiança!${assinLonga}`,

    dia_cliente: `${prefixoPJ}Prezado(a) ${nomeContato},\n\nHoje é o Dia do Cliente — e isso nos deu a oportunidade de pensar em você. Muito obrigado por nos escolher como parceiro jurídico. Sua confiança é o que nos motiva a fazer cada vez melhor.${assinLonga}`,

    relatorio_mensal: `${prefixoPJ}Prezado(a) ${nomeContato},\n\nSegue em anexo o Relatório Mensal de Acompanhamento do seu processo referente ao mês de ${new Date().toLocaleDateString("pt-BR",{month:"long",year:"numeric"})}.\n\nO documento contém o resumo das movimentações do período e as considerações estratégicas da nossa equipe.\n\nQualquer dúvida, estamos à disposição.${assinLonga}`,

    relatorio_emergencial: `${prefixoPJ}Prezado(a) ${nomeContato},\n\nGostaríamos de informar sobre uma movimentação relevante no seu processo que requer sua atenção.\n\nSegue em anexo o comunicado com os detalhes e as providências que estamos adotando.\n\nEstamos acompanhando o caso de perto e mantendo você informado(a) em tempo real.${assinLonga}`,
  };

  // Modelo customizado sobrescreve o padrão — substituir variáveis
  const modeloCustom = modelosComunicacao?.[tipo];
  if (modeloCustom) {
    const texto = modeloCustom
      .replace(/\[Nome do Cliente\]/g, nomeContato)
      .replace(/\[Produto\/Tese\]/g, prodNome)
      .replace(/\[Contrato\]/g, ct?.numContrato||"[Contrato]")
      .replace(/\[DATA\]/g, addDias(dataEnt, 30));
    return { mensagem: texto, whatsappNum: whats };
  }

  return { mensagem: msgs[tipo] || "", whatsappNum: whats };
};

// ── ModeloItem: componente individual para cada modelo de comunicação ──────
function ModeloItem({ m, g, textoPadrao, customizado, setData, equipe }) {
  const textoAtual = customizado ?? textoPadrao;
  const [editando, setEditando] = React.useState(false);
  const [rascunho, setRascunho] = React.useState(textoAtual);
  React.useEffect(()=>{ setRascunho(customizado ?? textoPadrao); }, [customizado, textoPadrao]);

  return (
    <div style={{marginBottom:14,borderRadius:10,overflow:"hidden",
      border:`1px solid ${editando?g.cor:theme.gray200}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        padding:"10px 16px",background:editando?g.cor+"0A":theme.gray50}}>
        <div>
          <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:0}}>{m.label}</p>
          <div style={{display:"flex",gap:8,marginTop:3,alignItems:"center"}}>
            <span style={{fontSize:10,color:theme.gray400}}>{m.canal}</span>
            {customizado && <span style={{fontSize:9,padding:"1px 6px",borderRadius:3,
              background:"#fef3c7",color:"#92400e",fontWeight:700}}>✏️ Customizado</span>}
          </div>
        </div>
        <div style={{display:"flex",gap:6}}>
          {customizado && (
            <button onClick={()=>{
              setData(prev=>({...prev,modelosComunicacao:{...((prev.modelosComunicacao)||{}),[m.tipo]:undefined}}));
              setRascunho(textoPadrao); setEditando(false);
            }} style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${theme.red}`,
              background:"#fef2f2",color:theme.red,cursor:"pointer",fontSize:11,fontWeight:600}}>
              ↩ Restaurar padrão
            </button>
          )}
          <button onClick={()=>{setEditando(e=>!e); setRascunho(textoAtual);}}
            style={{padding:"5px 12px",borderRadius:6,
              border:`1px solid ${editando?g.cor:theme.gray200}`,
              background:editando?g.cor+"10":theme.white,
              color:editando?g.cor:theme.gray600,cursor:"pointer",fontSize:11,fontWeight:600}}>
            {editando ? "✕ Fechar" : "✏️ Editar"}
          </button>
        </div>
      </div>
      <div style={{padding:"12px 16px",background:theme.white}}>
        {!editando
          ? <pre style={{fontSize:12,color:theme.gray700,lineHeight:1.7,
              whiteSpace:"pre-wrap",fontFamily:"inherit",margin:0}}>{textoAtual}</pre>
          : <>
              <textarea value={rascunho} onChange={e=>setRascunho(e.target.value)}
                rows={8} style={{width:"100%",padding:"10px 12px",
                  border:`1px solid ${g.cor}`,borderRadius:7,fontSize:12,
                  fontFamily:"inherit",resize:"vertical",outline:"none",
                  boxSizing:"border-box",lineHeight:1.7}}/>
              <p style={{fontSize:10,color:theme.gray400,margin:"6px 0 10px"}}>
                Use [Nome do Cliente], [Produto/Tese], [Contrato], [DATA] como variáveis automáticas.
              </p>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <button onClick={()=>setEditando(false)} style={{padding:"7px 16px",
                  borderRadius:6,border:`1px solid ${theme.gray200}`,background:theme.white,
                  cursor:"pointer",fontSize:12}}>Cancelar</button>
                <button onClick={()=>{
                  setData(prev=>({...prev,
                    modelosComunicacao:{...((prev.modelosComunicacao)||{}),[m.tipo]:rascunho}
                  }));
                  setEditando(false);
                }} style={{padding:"7px 18px",borderRadius:6,border:"none",
                  background:g.cor,color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>
                  💾 Salvar Modelo
                </button>
              </div>
            </>
        }
      </div>
    </div>
  );
}

function ModComunicacoes({ data, setData, usuario }) {
  const clientes  = data.clientes       || [];
  const contratos = data.contratos      || [];
  const produtos  = data.produtos       || [];
  const equipe    = data.equipe         || [];
  const comuns    = data.comunicacoes   || [];
  const registros = data.baseOperacional|| [];

  const [abaComun, setAbaComun] = React.useState("fila");
  const [busca, setBusca] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [forcarRelMensal, setForcarRelMensal] = React.useState(false);

  // ── E-mail institucional (juridico@ / contato@) ────────────
  const [emailConta, setEmailConta] = React.useState("cliente");
  const [emailLista, setEmailLista] = React.useState([]);
  const [emailCarregando, setEmailCarregando] = React.useState(false);
  const [emailErro, setEmailErro] = React.useState("");
  const [emailAberto, setEmailAberto] = React.useState(null); // detalhe da mensagem aberta
  const [emailComposer, setEmailComposer] = React.useState(false);
  const [emailForm, setEmailForm] = React.useState({ para:"", assunto:"", corpo:"" });
  const [emailEnviando, setEmailEnviando] = React.useState(false);

  const emailToken = () => sessionStorage.getItem('ac_token') || '';

  const carregarEmails = React.useCallback(() => {
    setEmailCarregando(true); setEmailErro("");
    fetch(`${API_BASE}/email/index.php?conta=${emailConta}&limit=30`, { headers:{ 'X-Token': emailToken() } })
      .then(r=>r.json())
      .then(res=>{
        if (!res.ok) { setEmailErro(res.erro||"Falha ao carregar e-mails."); return; }
        setEmailLista(res.dados.itens || []);
      })
      .catch(()=>setEmailErro("Falha de conexão com o servidor de e-mail."))
      .finally(()=>setEmailCarregando(false));
  }, [emailConta]);

  React.useEffect(() => {
    if (abaComun === "email") carregarEmails();
  }, [abaComun, emailConta, carregarEmails]);

  const abrirEmail = (uid) => {
    setEmailAberto({ carregando:true });
    fetch(`${API_BASE}/email/index.php/${uid}?conta=${emailConta}`, { headers:{ 'X-Token': emailToken() } })
      .then(r=>r.json())
      .then(res=>{
        if (!res.ok) { setEmailErro(res.erro||"Falha ao abrir e-mail."); setEmailAberto(null); return; }
        setEmailAberto(res.dados);
        setEmailLista(lst=>lst.map(m=>m.uid===uid?{...m,lido:true}:m));
      })
      .catch(()=>{ setEmailErro("Falha de conexão com o servidor de e-mail."); setEmailAberto(null); });
  };

  const enviarEmailForm = () => {
    if (!emailForm.para || !emailForm.assunto || !emailForm.corpo) {
      alert("Preencha destinatário, assunto e corpo antes de enviar.");
      return;
    }
    setEmailEnviando(true);
    fetch(`${API_BASE}/email/index.php/enviar`, {
      method:"POST",
      headers:{ 'Content-Type':'application/json', 'X-Token': emailToken() },
      body: JSON.stringify({ conta: emailConta, ...emailForm })
    })
      .then(r=>r.json())
      .then(res=>{
        if (!res.ok) { alert("Falha ao enviar: " + (res.erro||"erro desconhecido")); return; }
        setEmailComposer(false);
        setEmailForm({ para:"", assunto:"", corpo:"" });
        alert("E-mail enviado com sucesso.");
      })
      .catch(()=>alert("Falha de conexão ao enviar o e-mail."))
      .finally(()=>setEmailEnviando(false));
  };

  const hoje = new Date();
  const hojeStr = hoje.toISOString().slice(0,10);
  // Aprovação liberada para todos os usuários logados do sistema
  // (o controle de acesso já é feito no login — apenas equipe autorizada acessa)
  const isAprovador = !!usuario;

  // ── Gerar fila automática ──────────────────────────────────
  const gerarFila = () => {
    const fila = [];
    contratos.forEach(ct => {
      const cl = clientes.find(c=>c.id===ct.clienteId);
      if (!cl || !ct.dataEntrada) return;
      const prod = produtos.find(p=>p.id===ct.produtoId);
      const dataEnt = new Date(ct.dataEntrada+"T12:00");

      // Régua 7 dias
      Object.entries(COMUNICACAO_GATILHOS).forEach(([tipo, cfg]) => {
        if (cfg.diasApos === null) return;
        if (cfg.diasApos > 7) return; // só régua 7 dias aqui
        const dataDisparo = new Date(dataEnt);
        dataDisparo.setDate(dataDisparo.getDate() + cfg.diasApos);
        const dispStr = dataDisparo.toISOString().slice(0,10);
        // Verificar se já foi enviado
        const jaEnviado = comuns.some(c => c.contratoId===ct.id && c.tipo===tipo);
        if (!jaEnviado && dispStr <= hojeStr) {
          fila.push({ tipo, cfg, cl, ct, prod, dataDisparo: dispStr, id: ct.id+"_"+tipo });
        }
      });

      // Aniversário de 1 ano do caso
      const data1a = new Date(dataEnt); data1a.setFullYear(data1a.getFullYear()+1);
      const str1a = data1a.toISOString().slice(0,10);
      const ja1a = comuns.some(c => c.contratoId===ct.id && c.tipo==="aniversario_caso_1a");
      if (!ja1a && str1a <= hojeStr) {
        fila.push({ tipo:"aniversario_caso_1a", cfg:COMUNICACAO_GATILHOS["aniversario_caso_1a"],
          cl, ct, prod, dataDisparo:str1a, id:ct.id+"_aniversario_caso_1a" });
      }
    });

    // Aniversário de clientes (PF: cl.dataNascimento / PJ: contatos)
    clientes.forEach(cl => {
      const datasAniv = [];
      if (cl.tipo !== "PJ" && cl.dataNascimento) {
        datasAniv.push({ dataNascimento: cl.dataNascimento, nome: cl.nome, whats: whatsCompleto(cl.whatsappDDI||"55", cl.whatsapp||"") });
      }
      if (cl.tipo === "PJ") {
        (cl.contatosPJ||[]).forEach(ct => {
          if (ct.dataNascimento && ct.nome) {
            datasAniv.push({ dataNascimento: ct.dataNascimento, nome: ct.nome, whats: whatsCompleto(ct.whatsappDDI||"55", ct.whatsapp||"") });
          }
        });
      }
      datasAniv.forEach(({ dataNascimento, nome, whats }) => {
        const nasc = new Date(dataNascimento+"T12:00");
        const anivEsteAno = new Date(hoje.getFullYear(), nasc.getMonth(), nasc.getDate());
        const anivStr = anivEsteAno.toISOString().slice(0,10);
        const jaAniv = comuns.some(c => c.clienteId===cl.id && c.tipo==="aniversario_cliente" &&
          c.nomeContato===nome && c.dataEnvio?.startsWith(hoje.getFullYear()+""));
        if (!jaAniv && anivStr === hojeStr) {
          fila.push({ tipo:"aniversario_cliente", cfg:COMUNICACAO_GATILHOS["aniversario_cliente"],
            cl: {...cl, nome: cl.tipo==="PJ" ? `${nome} (${cl.nome})` : cl.nome,
                 whatsapp: whats || cl.whatsapp },
            ct:null, prod:null, dataDisparo:hojeStr,
            id:cl.id+"_aniversario_"+nome,
            nomeContato: nome });
        }
      });   // fim datasAniv.forEach
    });     // fim clientes.forEach
    // Datas coletivas
    const datasColetivas = [
      { tipo:"fim_de_ano",   data: hoje.getFullYear()+"-12-20" },
      { tipo:"dia_advogado", data: hoje.getFullYear()+"-08-11" },
      { tipo:"dia_cliente",  data: hoje.getFullYear()+"-09-15" },
    ];
    datasColetivas.forEach(({tipo, data:d}) => {
      if (d !== hojeStr) return;
      clientes.filter(cl=>cl.ativo!==false).forEach(cl => {
        const jaEnv = comuns.some(c=>c.clienteId===cl.id && c.tipo===tipo &&
          c.dataEnvio?.startsWith(hoje.getFullYear()+""));
        if (!jaEnv) {
          fila.push({ tipo, cfg:COMUNICACAO_GATILHOS[tipo], cl, ct:null, prod:null,
            dataDisparo:d, id:cl.id+"_"+tipo });
        }
      });
    });

    // ── Relatório Mensal — dia 5 de cada mês (ou disparo manual) ──
    const diaHoje = hoje.getDate();
    const mesRef  = hojeStr.slice(0,7);
    if (diaHoje === 5 || forcarRelMensal) {
      contratos.forEach(ct => {
        if (ct.status !== "Em andamento" && ct.status !== "Ativo") return;
        const cl   = clientes.find(c=>c.id===ct.clienteId);
        const prod = produtos.find(p=>p.id===ct.produtoId);
        const reg  = registros.find(r=>r.contratoId===ct.id);
        if (!cl) return;
        const jaEnv = comuns.some(c=>c.contratoId===ct.id && c.tipo==="relatorio_mensal" &&
          c.dataEnvio?.startsWith(mesRef));
        if (!jaEnv) {
          fila.push({
            tipo: "relatorio_mensal",
            cfg: { titulo: `Relatório Mensal — ${mesRef}`, modulo: "Relatório Mensal", canal: "WhatsApp + PDF", diasApos: null },
            cl, ct, prod, reg: reg||{}, dataDisparo: hojeStr, mesRef,
            id: ct.id+"_relmensal_"+mesRef
          });
        }
      });
    }

    // ── Relatório Emergencial — ocorrência Urgente ou futura ──
    contratos.forEach(ct => {
      if (ct.status !== "Em andamento" && ct.status !== "Ativo") return;
      const cl   = clientes.find(c=>c.id===ct.clienteId);
      const prod = produtos.find(p=>p.id===ct.produtoId);
      const reg  = registros.find(r=>r.contratoId===ct.id);
      if (!cl || !reg) return;
      const ocsUrgentes = (reg.ocorrencias||[]).filter(o => {
        if (o.tipo !== "Urgente" && o.tipo !== "Externa") return false;
        // Data futura ou hoje
        const d = o.data||"";
        return d >= hojeStr && !o._emergencialDisparado;
      });
      ocsUrgentes.forEach(oc => {
        const jaEnv = comuns.some(c=>c.contratoId===ct.id && c.tipo==="relatorio_emergencial" &&
          c.ocorrenciaId===oc.id);
        if (!jaEnv) {
          fila.push({
            tipo: "relatorio_emergencial",
            cfg: { titulo: `Comunicado Emergencial — ${oc.descricao?.slice(0,40)||"Ocorrência"}`, modulo: "Relatório Mensal", canal: "WhatsApp + PDF", diasApos: null },
            cl, ct, prod, reg, dataDisparo: hojeStr, mesRef: hojeStr.slice(0,7),
            ocorrenciaId: oc.id,
            id: ct.id+"_emergencial_"+oc.id
          });
        }
      });
    });

    return fila;
  };

  const fila = gerarFila();
  const filtFila = busca
    ? fila.filter(f=>(f.cl?.nome||"").toLowerCase().includes(busca.toLowerCase()) ||
        f.cfg.titulo.toLowerCase().includes(busca.toLowerCase()))
    : fila;

  const gerarPDFKitBoasVindas = (item) => {
    const cl = item.cl || {};
    const ct = item.ct || {};
    const prod = item.prod || {};
    const nomeResp = (equipe||[]).find(e=>e.id===ct.responsavel)?.nome || "Dr. Nelson José Alves";
    const isPJ = cl.tipo === "PJ";
    const contatoPJ = isPJ ? (cl.contatosPJ||[]).find(c=>c.nome?.trim()) : null;
    const nomeContato = contatoPJ?.nome || cl.nome || "—";
    const nomeEmpresa = isPJ ? cl.nome || "" : "";
    const dataEnt = ct.dataEntrada || new Date().toISOString().slice(0,10);
    const addDias = (d, n) => {
      const dt = new Date(d+"T12:00"); dt.setDate(dt.getDate()+n);
      return dt.toLocaleDateString("pt-BR");
    };
    const prodNome = prod.nome || "Serviços Jurídicos";
    const docs = (item.reg?.documentos||[]).filter(d=>d.nome).map(d=>d.nome);
    const mc = data.modelosComunicacao || {};

    // Textos customizáveis — fallback para o padrão
    const txt_boasvindas = (mc.pdf_boasvindas_intro||"É um prazer tê-lo(a) como cliente do escritório Alves & Correa Advogados. Recebemos o seu caso com muita atenção, e podemos garantir que cuidaremos de tudo com o maior cuidado e dedicação.")
      .replace(/\[Nome do Cliente\]/g, nomeContato).replace(/\[Produto\/Tese\]/g, prodNome);
    const txt_cron = mc.pdf_boasvindas_cronograma || "Em [prazo], esperamos ter o levantamento completo. Em [prazo], já teremos o pedido protocolado.";
    const txt_google = mc.pdf_boasvindas_google || "Nossa maior recompensa é a confiança de cada cliente. Se quiser conhecer um pouco mais sobre o nosso trabalho — ou deixar seu registro após a conclusão do caso — acesse nossa página no Google. Agradecemos desde já. Será uma honra conquistar sua avaliação ao final!";

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Kit de Boas-Vindas — ${cl.nome||"—"}</title>
<style>
@page{size:A4;margin:18mm 16mm 20mm 16mm}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:11.5pt;color:#1A1610;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.hdr{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:12px;border-bottom:3px solid #C9A84C;margin-bottom:22px}
.hdr-esq{font-size:14pt;font-weight:bold}
.hdr-sub{font-size:9pt;color:#888;margin-top:3px}
.titulo{text-align:center;margin-bottom:22px}
.titulo h1{font-size:17pt;font-weight:bold;text-transform:uppercase;letter-spacing:1px;color:#1A1610}
.linha-ouro{width:70px;height:3px;background:#C9A84C;margin:8px auto 14px}
.sec{margin-bottom:18px}
.sec-titulo{font-size:10.5pt;font-weight:bold;text-transform:uppercase;letter-spacing:.8px;padding:7px 14px;background:#1A1610;color:#C9A84C;border-radius:4px;margin-bottom:10px}
p{font-size:11.5pt;line-height:1.8;margin-bottom:8px}
ul{margin:8px 0 12px 20px;font-size:11pt;line-height:1.8}
li{margin-bottom:4px}
.cron-item{display:flex;align-items:flex-start;gap:12px;padding:8px 12px;border-radius:6px;background:#f9f6ef;border-left:4px solid #C9A84C;margin-bottom:8px}
.cron-data{min-width:80px;font-weight:bold;font-size:10.5pt;color:#C9A84C}
.cron-desc{font-size:11pt;color:#1A1610}
.checklist-item{display:flex;align-items:center;gap:10px;padding:7px 12px;border-radius:5px;background:#f0f9ff;border:1px solid #bfdbfe;margin-bottom:6px;font-size:11pt}
.google-box{background:#f9f6ef;border:1px solid #C9A84C;border-radius:6px;padding:14px 16px;text-align:center}
.assin{display:grid;grid-template-columns:1fr 1fr;gap:0 60px;margin-top:32px;padding-top:16px;border-top:1px solid #ccc}
.assin-item{text-align:center}
.assin-linha{border-top:1px solid #555;margin-bottom:6px}
.ftr{margin-top:20px;padding-top:10px;border-top:1px solid #ddd;font-size:8.5pt;color:#aaa;display:flex;justify-content:space-between}
</style></head><body>

<div class="hdr">
  <div>
    <div class="hdr-esq">Alves &amp; Correa Advogados</div>
    <div class="hdr-sub">Dr. Nelson José Alves · OAB/SP 398.875 &nbsp;|&nbsp; Dra. Annie Lemos Alves Correa · OAB/SP 446.942</div>
  </div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">Emitido em</div>
  <div style="font-weight:bold;font-size:11pt">${new Date().toLocaleDateString("pt-BR",{day:"2-digit",month:"long",year:"numeric"})}</div></div>
</div>

<div class="titulo">
  <h1>Kit de Boas-Vindas</h1>
  <div class="linha-ouro"></div>
  <p style="font-size:11pt;color:#888;text-align:center">Bem-vindo(a) ao escritório Alves &amp; Correa Advogados</p>
</div>

${isPJ ? `<p style="font-weight:700;color:#1A1610;margin-bottom:4px">Att. ${nomeEmpresa}</p>` : ""}
<div class="sec">
  <div class="sec-titulo">1. Mensagem de Boas-Vindas</div>
  <p>Olá, ${nomeContato}!</p>
  <p>${txt_boasvindas}</p>
  <p>Nos próximos dias você receberá todas as informações para darmos início aos trabalhos. Qualquer dúvida, estamos à disposição!</p>
</div>

<div class="sec">
  <div class="sec-titulo">2. Informações do Escritório</div>
  <ul>
    <li><strong>Horário de atendimento:</strong> Segunda a Sexta, das 8h às 18h</li>
    <li><strong>Canais oficiais:</strong> WhatsApp e e-mail (contato@alvescorreaadvogados.com.br)</li>
    <li><strong>SLA de resposta:</strong> até 4 horas úteis para mensagens</li>
    <li><strong>Responsável pelo seu caso:</strong> ${nomeResp}</li>
  </ul>
</div>

<div class="sec">
  <div class="sec-titulo">3. Cronograma do Seu Caso</div>
  <div class="cron-item"><span class="cron-data">Semana 1</span><span class="cron-desc">Análise do caso e levantamento de documentos</span></div>
  <div class="cron-item"><span class="cron-data">Semana 2–3</span><span class="cron-desc">Elaboração da estratégia jurídica</span></div>
  <div class="cron-item"><span class="cron-data">${addDias(dataEnt, 30)}</span><span class="cron-desc">Levantamento completo dos créditos / primeira análise</span></div>
  <div class="cron-item"><span class="cron-data">${addDias(dataEnt, 60)}</span><span class="cron-desc">Protocolo esperado / primeira movimentação formal</span></div>
</div>

<div class="sec">
  <div class="sec-titulo">4. Checklist de Documentos Necessários</div>
  <p style="font-size:10.5pt;color:#555;margin-bottom:10px">Por favor, providencie os documentos abaixo pelo canal oficial do escritório:</p>
  ${docs.length > 0
    ? docs.map(d=>`<div class="checklist-item">☐ &nbsp;${d}</div>`).join("")
    : `<div class="checklist-item">☐ &nbsp;RG e CPF / CNPJ (cópia digitalizada)</div>
       <div class="checklist-item">☐ &nbsp;Comprovante de endereço atualizado</div>
       <div class="checklist-item">☐ &nbsp;Procuração eletrônica assinada (modelo em anexo)</div>
       <div class="checklist-item">☐ &nbsp;[Documentos específicos do caso — a informar]</div>`
  }
</div>

${cl.linkGoogle ? `
<div class="sec">
  <div class="sec-titulo">5. Avalie Nosso Trabalho</div>
  <div class="google-box">
    <p style="font-size:11pt;margin-bottom:10px">${txt_google}</p>
    <p style="font-weight:bold;color:#C9A84C;font-size:12pt">🔗 ${cl.linkGoogle}</p>
  </div>
</div>` : ""}

<div class="assin">
  <div class="assin-item"><div style="height:40px"></div><div class="assin-linha"></div>
    <div style="font-size:11pt;font-weight:bold">Nelson José Alves</div>
    <div style="font-size:9pt;color:#888">Advogado · OAB/SP 398.875</div></div>
  <div class="assin-item"><div style="height:40px"></div><div class="assin-linha"></div>
    <div style="font-size:11pt;font-weight:bold">Annie Lemos Alves Correa</div>
    <div style="font-size:9pt;color:#888">Advogada · OAB/SP 446.942</div></div>
</div>

<div class="ftr">
  <span>Alves &amp; Correa Advogados · contato@alvescorreaadvogados.com.br</span>
  <span>São Paulo/SP · Foz do Iguaçu/PR</span>
</div>
</body></html>`;

    const win = window.open("","_blank","width=900,height=800");
    if (!win) return alert("Permita pop-ups para gerar o PDF.");
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(()=>win.print(), 600);
  };

  const gerarPDFRelatorioMensal = (item) => {
    const cl = item.cl || {};
    const ct = item.ct || {};
    const prod = item.prod || {};
    const reg = item.reg || {};
    const mesRef = item.mesRef || new Date().toISOString().slice(0,7);
    const [y,m] = mesRef.split("-");
    const meses = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
    const mesFormatado = meses[parseInt(m)-1]+"/"+y;
    const isPJ = cl.tipo === "PJ";
    const contatoPJ = isPJ ? (cl.contatosPJ||[]).find(c=>c.nome?.trim()) : null;
    const nomeContato = contatoPJ?.nome || cl.nome || "—";
    const nomeEmpresa = isPJ ? cl.nome || "" : "";

    const todasOcs = reg.ocorrencias || [];
    const ocsNaoConf = todasOcs.filter(o => o.confidencial === false && (o.data||"").slice(0,7) === mesRef);
    const temOcs = ocsNaoConf.length > 0;
    const conclusao = reg.conclusaoProducao || reg.conclusaoRevisao || reg.conclusaoFinal || "";

    const mc = data.modelosComunicacao || {};
    const txt_intro = (mc.pdf_relatorio_intro ||
      "Encaminhamos o presente relatório mensal de acompanhamento do seu processo referente ao período de [MES]. Nosso compromisso é garantir transparência, acompanhamento contínuo e segurança na condução da sua demanda.")
      .replace(/\[MES\]/g, mesFormatado).replace(/\[Nome do Cliente\]/g, nomeContato);
    const txt_com_ocs = mc.pdf_relatorio_com_ocs ||
      "Cada ato foi analisado com atenção e as medidas cabíveis foram adotadas dentro dos prazos legais.";
    const txt_sem_ocs = mc.pdf_relatorio_sem_ocs ||
      "No período analisado, não houve nova movimentação processual registrada nos autos. Essa situação é comum em determinados momentos do trâmite, especialmente considerando os prazos processuais e a rotina do Judiciário. O processo permanece em acompanhamento contínuo.";
    const txt_consideracoes = mc.pdf_relatorio_consideracoes ||
      "Seguimos atuando de forma técnica e estratégica, sempre atentos às oportunidades processuais e aos próximos desdobramentos. Nosso foco permanece na condução responsável e eficiente da sua demanda.\n\nCaso haja qualquer novidade relevante fora do ciclo mensal, você será prontamente comunicado(a).";

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Relatório Mensal — ${cl.nome||"—"} — ${mesFormatado}</title>
<style>
@page{size:A4;margin:18mm 16mm 20mm 16mm}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:11.5pt;color:#1A1610;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.hdr{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:12px;border-bottom:3px solid #C9A84C;margin-bottom:22px}
.hdr-esq{font-size:14pt;font-weight:bold}.hdr-sub{font-size:9pt;color:#888;margin-top:3px}
.titulo{text-align:center;margin-bottom:22px}
.titulo h1{font-size:17pt;font-weight:bold;text-transform:uppercase;letter-spacing:1px}
.linha-ouro{width:70px;height:3px;background:#C9A84C;margin:8px auto 14px}
.sec{margin-bottom:18px}
.sec-titulo{font-size:10.5pt;font-weight:bold;text-transform:uppercase;letter-spacing:.8px;padding:7px 14px;background:#1A1610;color:#C9A84C;border-radius:4px;margin-bottom:10px}
p{font-size:11.5pt;line-height:1.8;margin-bottom:8px}
table{width:100%;border-collapse:collapse;font-size:10.5pt;margin-bottom:10px}
th{background:#1A1610;color:#C9A84C;padding:7px 12px;text-align:left;font-size:9.5pt;text-transform:uppercase;letter-spacing:.5px;border:1px solid #333}
td{padding:7px 12px;border-bottom:1px solid #eee;vertical-align:top}
tr:nth-child(even) td{background:#f9f6ef}
.assin{display:grid;grid-template-columns:1fr 1fr;gap:0 60px;margin-top:32px;padding-top:16px;border-top:1px solid #ccc}
.assin-item{text-align:center}.assin-linha{border-top:1px solid #555;margin-bottom:6px}
.ftr{margin-top:20px;padding-top:10px;border-top:1px solid #ddd;font-size:8.5pt;color:#aaa;display:flex;justify-content:space-between}
</style></head><body>

<div class="hdr">
  <div><div class="hdr-esq">Alves &amp; Correa Advogados</div>
  <div class="hdr-sub">Dr. Nelson José Alves · OAB/SP 398.875 &nbsp;|&nbsp; Dra. Annie Lemos Alves Correa · OAB/SP 446.942</div></div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">Referência</div>
  <div style="font-weight:bold;font-size:11pt">${mesFormatado}</div></div>
</div>

<div class="titulo">
  <h1>Relatório Mensal de Acompanhamento</h1>
  <div class="linha-ouro"></div>
  <p style="font-size:11pt;color:#888;text-align:center">Uso confidencial · ${cl.nome||"—"} · Contrato ${ct.numContrato||"—"}</p>
</div>

${isPJ ? `<p style="font-weight:700;color:#1A1610;margin-bottom:4px">Att. ${nomeEmpresa}</p>` : ""}

<div class="sec">
  <div class="sec-titulo">1. Introdução</div>
  <p>Prezado(a) ${nomeContato},</p>
  <p>${txt_intro}</p>
</div>

<div class="sec">
  <div class="sec-titulo">2. Andamento no Período — ${prod.nome||"—"}</div>
  ${temOcs ? `
  <table>
    <thead><tr><th style="width:14%">Data</th><th style="width:18%">Tipo</th><th>Movimentação</th></tr></thead>
    <tbody>
      ${ocsNaoConf.map(o=>`<tr><td style="white-space:nowrap">${o.data||"—"}</td><td style="color:#5C5448">${o.tipo||"—"}</td><td>${o.descricao||o.obs||"—"}</td></tr>`).join("")}
    </tbody>
  </table>
  <p>${txt_com_ocs}</p>
  ` : `
  <p>${txt_sem_ocs}</p>
  `}
</div>

${conclusao ? `
<div class="sec">
  <div class="sec-titulo">3. Situação Atual</div>
  <p>${conclusao}</p>
</div>` : ""}

<div class="sec">
  <div class="sec-titulo">${conclusao ? "4" : "3"}. Considerações Estratégicas e Próximos Passos</div>
  <p>${txt_consideracoes.replace(/\n/g,"</p><p>")}</p>
</div>

<div class="assin">
  <div class="assin-item"><div style="height:40px"></div><div class="assin-linha"></div>
    <div style="font-size:11pt;font-weight:bold">Nelson José Alves</div>
    <div style="font-size:9pt;color:#888">Advogado · OAB/SP 398.875</div></div>
  <div class="assin-item"><div style="height:40px"></div><div class="assin-linha"></div>
    <div style="font-size:11pt;font-weight:bold">Annie Lemos Alves Correa</div>
    <div style="font-size:9pt;color:#888">Advogada · OAB/SP 446.942</div></div>
</div>
<div class="ftr">
  <span>Alves &amp; Correa Advogados · Documento Confidencial</span>
  <span>Gerado em ${new Date().toLocaleString("pt-BR")}</span>
</div>
</body></html>`;

    const win = window.open("","_blank","width=900,height=800");
    if (!win) return alert("Permita pop-ups para gerar o PDF.");
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(()=>win.print(), 600);
  };

  const aprovar = (item) => {
    const { mensagem, whatsappNum } = gerarMensagemComunicacao(item.tipo, item.cl, item.ct, item.prod, equipe, data.modelosComunicacao);
    setPreview({ ...item, mensagem, whatsappNum });
  };

  const confirmarEnvio = (item) => {
    // Abrir WhatsApp Web
    const msg = encodeURIComponent(item.mensagem);
    const num = item.whatsappNum || "";
    window.open(`https://wa.me/${num}?text=${msg}`, "_blank");
    // Registrar no histórico — chave suficiente para anti-duplicidade
    setData(prev => ({
      ...prev,
      comunicacoes: [...(prev.comunicacoes||[]), {
        id: Date.now().toString(),
        tipo: item.tipo,
        titulo: item.cfg.titulo,
        clienteId: item.cl?.id,
        contratoId: item.ct?.id || null,
        nomeContato: item.nomeContato || null,
        mesRef: item.mesRef || null,
        ocorrenciaId: item.ocorrenciaId || null,
        dataEnvio: new Date().toISOString(),
        canal: item.cfg.canal,
        aprovadoPor: usuario?.nome || "—",
        mensagem: item.mensagem,
      }]
    }));
    setPreview(null);
  };

  const historico = [...(comuns)].reverse();
  const filtHist = busca
    ? historico.filter(h => {
        const cl=clientes.find(c=>c.id===h.clienteId);
        return (cl?.nome||"").toLowerCase().includes(busca.toLowerCase()) ||
          h.titulo.toLowerCase().includes(busca.toLowerCase());
      })
    : historico;

  const COR_MOD = {
    "Kit de Boas-Vindas": theme.blue,
    "Linha de Confiança": theme.green,
    "Datas Especiais": theme.amber,
  };

  return (
    <div>
      <PageHeader title="Comunicações com o Cliente" subtitle="Módulo 8 — Relacionamento · Fila de aprovação e histórico" />

      {/* Busca + abas */}
      <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
        <input value={busca} onChange={e=>setBusca(e.target.value)}
          placeholder="🔍 Buscar cliente ou tipo de comunicado..."
          style={{flex:1,minWidth:220,padding:"9px 14px",border:`1px solid ${theme.gray200}`,
            borderRadius:8,fontSize:13,outline:"none"}}/>
        <button onClick={()=>setForcarRelMensal(f=>!f)} style={{
          padding:"9px 16px",borderRadius:8,border:`1px solid ${forcarRelMensal?theme.green:theme.gray200}`,
          background:forcarRelMensal?"#f0fdf4":theme.white,
          color:forcarRelMensal?theme.green:theme.gray600,
          cursor:"pointer",fontSize:12,fontWeight:600,whiteSpace:"nowrap"}}>
          📨 {forcarRelMensal?"✓ Rel. Mensal ativo":"Gerar Relatórios do Mês"}
        </button>
        <div style={{display:"flex",gap:4,background:theme.gray100,borderRadius:8,padding:3}}>
          {[["fila",`📬 Fila (${fila.length})`],["agenda","📅 Agenda"],["historico",`📋 Histórico (${comuns.length})`],["email","✉️ E-mail"],["modelos","📝 Modelos"]].map(([v,l])=>(
            <button key={v} onClick={()=>setAbaComun(v)} style={{
              padding:"6px 14px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,
              fontWeight:abaComun===v?700:400,
              background:abaComun===v?theme.white:"transparent",
              color:abaComun===v?theme.gray900:theme.gray400,
              boxShadow:abaComun===v?"0 1px 3px rgba(0,0,0,.1)":"none"}}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Aviso perfil */}
      {!isAprovador && (
        <div style={{padding:"10px 16px",borderRadius:8,background:"#fff8e1",border:"1px solid #fde68a",marginBottom:16}}>
          <p style={{fontSize:12,color:"#92400e",margin:0}}>🔒 Somente Dr. Nelson e Dra. Annie podem aprovar e disparar comunicados.</p>
        </div>
      )}

      {/* Modal de preview */}
      {preview && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:9999,
          display:"flex",alignItems:"flex-start",justifyContent:"center",
          overflowY:"auto",padding:"24px 16px"}}>
          <div style={{background:theme.white,borderRadius:12,padding:"24px 28px",maxWidth:640,
            width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,.3)",marginBottom:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0}}>
                📬 {preview.cfg.titulo}
              </p>
              <button onClick={()=>setPreview(null)} style={{background:"none",border:"none",
                fontSize:20,cursor:"pointer",color:theme.gray400}}>×</button>
            </div>
            <div style={{padding:"8px 12px",borderRadius:6,background:theme.gray50,marginBottom:12}}>
              <p style={{fontSize:11,color:theme.gray500,margin:0}}>
                Cliente: <strong>{preview.cl?.nome}</strong> · Canal: <strong>{preview.cfg.canal}</strong>
                {preview.ct && ` · Contrato: ${preview.ct.numContrato}`}
              </p>
            </div>
            {/* Botão PDF — Kit Boas-Vindas e Relatório Mensal */}
            {(preview.tipo === "boas_vindas_d1" || preview.tipo === "relatorio_mensal" || preview.tipo === "relatorio_emergencial") && (
              <div style={{padding:"10px 14px",borderRadius:8,background:"#f0f9ff",
                border:"1px solid #bfdbfe",marginBottom:14}}>
                <p style={{fontSize:12,fontWeight:700,color:"#1e40af",marginBottom:8}}>
                  📎 Este comunicado acompanha um documento em PDF
                </p>
                <p style={{fontSize:11,color:"#374151",marginBottom:10}}>
                  Baixe o PDF abaixo, depois abra o WhatsApp e anexe-o à mensagem antes de enviar.
                </p>
                <button onClick={()=>{
                  if (preview.tipo === "boas_vindas_d1") gerarPDFKitBoasVindas(preview);
                  else gerarPDFRelatorioMensal(preview);
                }} style={{padding:"8px 18px",borderRadius:7,border:"none",
                  background:"#1e40af",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>
                  📄 Baixar PDF
                </button>
              </div>
            )}
            <div style={{marginBottom:16}}>
              <p style={{fontSize:11,fontWeight:700,color:theme.gray600,textTransform:"uppercase",
                letterSpacing:.8,marginBottom:8}}>Mensagem gerada</p>
              <textarea value={preview.mensagem}
                onChange={e=>setPreview(prev=>({...prev,mensagem:e.target.value}))}
                rows={10} style={{width:"100%",padding:"12px 14px",
                  border:`1px solid ${theme.gray200}`,borderRadius:8,fontSize:13,
                  fontFamily:"inherit",resize:"vertical",outline:"none",boxSizing:"border-box",
                  lineHeight:1.7}}/>
              <p style={{fontSize:11,color:theme.gray400,marginTop:4}}>
                Você pode editar o texto acima antes de enviar.
              </p>
            </div>
            {!isAprovador ? (
              <p style={{fontSize:12,color:theme.red,textAlign:"center"}}>
                🔒 Somente Dr. Nelson ou Dra. Annie podem aprovar este envio.
              </p>
            ) : (
              <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
                <button onClick={()=>setPreview(null)} style={{padding:"9px 20px",borderRadius:7,
                  border:`1px solid ${theme.gray200}`,background:theme.white,cursor:"pointer",fontSize:13}}>
                  Cancelar
                </button>
                <button onClick={()=>confirmarEnvio(preview)} style={{padding:"9px 22px",
                  borderRadius:7,border:"none",background:theme.green,color:theme.white,
                  cursor:"pointer",fontSize:13,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
                  📲 Abrir WhatsApp e Registrar Envio
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ABA FILA */}
      {abaComun==="fila" && (
        <div>
          {filtFila.length===0 ? (
            <Card><EmptyState icon="📬" title="Fila vazia" subtitle="Nenhum comunicado pendente para hoje. Verifique novamente amanhã."/></Card>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {filtFila.map(item=>{
                const corMod = COR_MOD[item.cfg.modulo] || theme.gray500;
                return (
                  <div key={item.id} style={{background:theme.white,borderRadius:10,padding:"14px 18px",
                    border:`1px solid ${theme.gray200}`,
                    borderLeft:`4px solid ${corMod}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:5}}>
                          <span style={{fontSize:11,padding:"2px 8px",borderRadius:4,fontWeight:700,
                            background:corMod+"18",color:corMod}}>
                            {item.cfg.modulo}
                          </span>
                          <span style={{fontSize:10,color:theme.gray400}}>{item.cfg.canal}</span>
                        </div>
                        <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0}}>
                          {item.cfg.titulo}
                        </p>
                        <p style={{fontSize:12,color:theme.gray600,margin:"4px 0 0"}}>
                          Cliente: <strong>{item.cl?.nome||"—"}</strong>
                          {item.ct && <span> · Contrato: {item.ct.numContrato}</span>}
                          {item.ct?.produtoId && <span> · {item.prod?.nome||""}</span>}
                        </p>
                        <p style={{fontSize:11,color:theme.gray400,margin:"3px 0 0"}}>
                          📅 Data disparo: {new Date(item.dataDisparo+"T12:00").toLocaleDateString("pt-BR")}
                        </p>
                      </div>
                    {(()=>{
                      // Verificar se já foi enviado
                      const jaFeito = comuns.some(c => {
                        if (c.tipo !== item.tipo) return false;
                        if (item.tipo === "relatorio_mensal")
                          return c.contratoId === item.ct?.id && c.mesRef === item.mesRef;
                        if (item.tipo === "relatorio_emergencial")
                          return c.contratoId === item.ct?.id && c.ocorrenciaId === item.ocorrenciaId;
                        if (item.tipo === "aniversario_cliente")
                          return c.clienteId === item.cl?.id && c.nomeContato === item.nomeContato &&
                            c.dataEnvio?.startsWith(new Date().getFullYear()+"");
                        return c.contratoId === item.ct?.id && c.tipo === item.tipo &&
                          c.dataEnvio?.startsWith(new Date().getFullYear()+"-");
                      });
                      return jaFeito ? (
                        <span style={{padding:"9px 14px",borderRadius:7,
                          background:"#d4edda",color:"#155724",fontSize:12,
                          fontWeight:700,marginLeft:12,whiteSpace:"nowrap"}}>
                          ✓ Enviado
                        </span>
                      ) : (
                        <button onClick={()=>{ aprovar(item); }}
                          style={{padding:"9px 18px",borderRadius:7,border:"none",cursor:"pointer",
                            background:theme.gray900,color:theme.gold,fontSize:12,fontWeight:700,
                            whiteSpace:"nowrap",marginLeft:12}}>
                          📲 Revisar e Enviar
                        </button>
                      );
                    })()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ABA AGENDA */}
      {abaComun==="agenda" && (
        <Card>
          <p style={{fontSize:12,fontWeight:700,color:theme.gray700,marginBottom:14,
            textTransform:"uppercase",letterSpacing:.8}}>📅 Próximos Disparos — 30 dias</p>
          {(()=>{
            const proximos = [];
            contratos.forEach(ct=>{
              const cl=clientes.find(c=>c.id===ct.clienteId);
              if(!cl||!ct.dataEntrada) return;
              const prod=produtos.find(p=>p.id===ct.produtoId);
              const dataEnt=new Date(ct.dataEntrada+"T12:00");
              Object.entries(COMUNICACAO_GATILHOS).forEach(([tipo,cfg])=>{
                if(cfg.diasApos===null||cfg.diasApos>7) return;
                const dt=new Date(dataEnt); dt.setDate(dt.getDate()+cfg.diasApos);
                const dtStr=dt.toISOString().slice(0,10);
                const jaEnv=comuns.some(c=>c.contratoId===ct.id&&c.tipo===tipo);
                const diff=Math.ceil((dt-hoje)/(1000*60*60*24));
                if(!jaEnv&&diff>0&&diff<=30)
                  proximos.push({tipo,cfg,cl,ct,prod,dataDisparo:dtStr,diff});
              });
              // 1 ano
              const dt1a=new Date(dataEnt); dt1a.setFullYear(dt1a.getFullYear()+1);
              const str1a=dt1a.toISOString().slice(0,10);
              const diff1a=Math.ceil((dt1a-hoje)/(1000*60*60*24));
              const ja1a=comuns.some(c=>c.contratoId===ct.id&&c.tipo==="aniversario_caso_1a");
              if(!ja1a&&diff1a>0&&diff1a<=30)
                proximos.push({tipo:"aniversario_caso_1a",cfg:COMUNICACAO_GATILHOS["aniversario_caso_1a"],cl,ct,prod,dataDisparo:str1a,diff:diff1a});
            });
            // Aniversários (PF + contatos PJ)
            clientes.forEach(cl=>{
              const datasAniv = [];
              if (cl.tipo !== "PJ" && cl.dataNascimento)
                datasAniv.push({ dataNascimento: cl.dataNascimento, nome: cl.nome });
              if (cl.tipo === "PJ")
                (cl.contatosPJ||[]).forEach(ct => {
                  if (ct.dataNascimento && ct.nome)
                    datasAniv.push({ dataNascimento: ct.dataNascimento, nome: `${ct.nome} (${cl.nome})` });
                });
              datasAniv.forEach(({ dataNascimento, nome }) => {
                const nasc=new Date(dataNascimento+"T12:00");
                const aniv=new Date(hoje.getFullYear(),nasc.getMonth(),nasc.getDate());
                const diff=Math.ceil((aniv-hoje)/(1000*60*60*24));
                const jaEnv=comuns.some(c=>c.clienteId===cl.id&&c.tipo==="aniversario_cliente"&&c.nomeContato===nome&&c.dataEnvio?.startsWith(hoje.getFullYear()+""));
                if(!jaEnv&&diff>0&&diff<=30)
                  proximos.push({tipo:"aniversario_cliente",cfg:COMUNICACAO_GATILHOS["aniversario_cliente"],
                    cl:{...cl,nome},ct:null,prod:null,dataDisparo:aniv.toISOString().slice(0,10),diff});
              });
            });
            // Datas coletivas — mostrar independentemente da janela de 30 dias
            const datasColetAgenda = [
              { tipo:"dia_advogado", data: hoje.getFullYear()+"-08-11", label:"Dia do Advogado (11/ago)" },
              { tipo:"dia_cliente",  data: hoje.getFullYear()+"-09-15", label:"Dia do Cliente (15/set)" },
              { tipo:"fim_de_ano",   data: hoje.getFullYear()+"-12-20", label:"Fim de Ano (20/dez)" },
            ];
            datasColetAgenda.forEach(({tipo, data:d, label})=>{
              const diff = Math.ceil((new Date(d+"T12:00")-hoje)/(1000*60*60*24));
              const jaEnv = comuns.some(c=>c.tipo===tipo && c.dataEnvio?.startsWith(hoje.getFullYear()+""));
              if (!jaEnv && diff > 0)
                proximos.push({tipo, cfg:{...COMUNICACAO_GATILHOS[tipo], titulo:label},
                  cl:{nome:"Todos os clientes ativos"},ct:null,prod:null,
                  dataDisparo:d, diff});
            });

            proximos.sort((a,b)=>a.diff-b.diff);
            if(!proximos.length) return <EmptyState icon="📅" title="Nenhum disparo agendado" subtitle=""/>;
            return (
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {proximos.map((p,i)=>{
                  const corMod=COR_MOD[p.cfg.modulo]||theme.gray500;
                  return(
                    <div key={i} style={{display:"flex",gap:14,alignItems:"center",padding:"10px 14px",
                      borderRadius:8,background:p.diff<=3?theme.gold+"0A":theme.gray50,
                      border:`1px solid ${p.diff<=3?theme.gold+"44":theme.gray200}`}}>
                      <div style={{textAlign:"center",minWidth:48,padding:"6px 8px",borderRadius:7,
                        background:corMod+"18"}}>
                        <p style={{fontSize:16,fontWeight:700,color:corMod,margin:0}}>{p.diff}</p>
                        <p style={{fontSize:8,color:corMod,textTransform:"uppercase",letterSpacing:.5}}>dias</p>
                      </div>
                      <div style={{flex:1}}>
                        <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:0}}>{p.cfg.titulo}</p>
                        <p style={{fontSize:11,color:theme.gray500,margin:"2px 0 0"}}>
                          {p.cl?.nome} · {new Date(p.dataDisparo+"T12:00").toLocaleDateString("pt-BR")}
                        </p>
                      </div>
                      <span style={{fontSize:10,padding:"2px 8px",borderRadius:4,fontWeight:700,
                        background:corMod+"18",color:corMod}}>{p.cfg.modulo}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </Card>
      )}

      {/* ABA HISTÓRICO */}
      {abaComun==="historico" && (
        <Card>
          <p style={{fontSize:12,fontWeight:700,color:theme.gray700,marginBottom:14,
            textTransform:"uppercase",letterSpacing:.8}}>📋 Histórico de Envios</p>
          {filtHist.length===0
            ? <EmptyState icon="📋" title="Nenhum comunicado enviado ainda" subtitle=""/>
            : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {filtHist.map((h,i)=>{
                  const cl=clientes.find(c=>c.id===h.clienteId);
                  const corMod=COR_MOD[COMUNICACAO_GATILHOS[h.tipo]?.modulo||""]||theme.gray400;
                  return(
                    <div key={h.id||i} style={{padding:"10px 14px",borderRadius:8,
                      background:theme.white,border:`1px solid ${theme.gray100}`,
                      borderLeft:`3px solid ${corMod}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                        <div>
                          <p style={{fontSize:13,fontWeight:600,color:theme.gray900,margin:0}}>{h.titulo}</p>
                          <p style={{fontSize:11,color:theme.gray500,margin:"3px 0 0"}}>
                            {cl?.nome||"—"} · {h.canal} · Aprovado por: {h.aprovadoPor}
                          </p>
                        </div>
                        <span style={{fontSize:10,color:theme.gray400,whiteSpace:"nowrap",marginLeft:12}}>
                          {new Date(h.dataEnvio).toLocaleDateString("pt-BR")}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
          }
        </Card>
      )}

      {/* ABA E-MAIL */}
      {abaComun==="email" && (
        <Card>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,marginBottom:14}}>
            <div style={{display:"flex",gap:4,background:theme.gray100,borderRadius:8,padding:3}}>
              {[["cliente","cliente@"],["contato","contato@"]].map(([v,l])=>(
                <button key={v} onClick={()=>{setEmailConta(v);setEmailAberto(null);}} style={{
                  padding:"6px 14px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,
                  fontWeight:emailConta===v?700:400,
                  background:emailConta===v?theme.white:"transparent",
                  color:emailConta===v?theme.gray900:theme.gray400,
                  boxShadow:emailConta===v?"0 1px 3px rgba(0,0,0,.1)":"none"}}>
                  {l}
                </button>
              ))}
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={carregarEmails} style={{padding:"8px 14px",borderRadius:8,
                border:`1px solid ${theme.gray200}`,background:theme.white,cursor:"pointer",fontSize:12,fontWeight:600}}>
                🔄 Atualizar
              </button>
              <button onClick={()=>setEmailComposer(true)} style={{padding:"8px 14px",borderRadius:8,
                border:"none",background:theme.blue,color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>
                ✏️ Novo E-mail
              </button>
            </div>
          </div>

          {emailErro && (
            <div style={{padding:"10px 14px",borderRadius:8,background:"#fef2f2",border:"1px solid #fecaca",marginBottom:14}}>
              <p style={{fontSize:12,color:theme.red,margin:0}}>⚠️ {emailErro}</p>
            </div>
          )}

          {emailCarregando ? (
            <p style={{fontSize:12,color:theme.gray400}}>Carregando mensagens...</p>
          ) : emailLista.length===0 ? (
            <EmptyState icon="✉️" title="Nenhuma mensagem encontrada" subtitle=""/>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {emailLista.map(m=>(
                <div key={m.uid} onClick={()=>abrirEmail(m.uid)} style={{padding:"10px 14px",borderRadius:8,
                  background:m.lido?theme.white:"#eff6ff",border:`1px solid ${theme.gray100}`,
                  cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                  <div style={{minWidth:0,flex:1}}>
                    <p style={{fontSize:13,fontWeight:m.lido?500:700,color:theme.gray900,margin:0,
                      whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.assunto}</p>
                    <p style={{fontSize:11,color:theme.gray500,margin:"3px 0 0",
                      whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.de}</p>
                  </div>
                  <span style={{fontSize:10,color:theme.gray400,whiteSpace:"nowrap"}}>
                    {m.data ? new Date(m.data).toLocaleDateString("pt-BR") : ""}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Modal — ler mensagem */}
          {emailAberto && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:9999,
              display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:"24px 16px"}}>
              <div style={{background:theme.white,borderRadius:12,padding:"24px 28px",maxWidth:680,
                width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,.3)",marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
                  <p style={{fontSize:15,fontWeight:700,color:theme.gray900,margin:0}}>
                    {emailAberto.carregando ? "Carregando..." : emailAberto.assunto}
                  </p>
                  <button onClick={()=>setEmailAberto(null)} style={{background:"none",border:"none",
                    fontSize:20,cursor:"pointer",color:theme.gray400}}>×</button>
                </div>
                {!emailAberto.carregando && (
                  <>
                    <div style={{padding:"8px 12px",borderRadius:6,background:theme.gray50,marginBottom:14}}>
                      <p style={{fontSize:11,color:theme.gray500,margin:0}}>
                        De: <strong>{emailAberto.de}</strong><br/>
                        Para: {emailAberto.para}<br/>
                        Data: {emailAberto.data}
                      </p>
                    </div>
                    <div style={{fontSize:13,color:theme.gray700,lineHeight:1.7,maxHeight:400,overflowY:"auto"}}
                      dangerouslySetInnerHTML={{__html: emailAberto.corpo}} />
                    <div style={{marginTop:16,display:"flex",justifyContent:"flex-end"}}>
                      <button onClick={()=>{
                        setEmailForm({para:(emailAberto.de.match(/<(.+)>/)?.[1])||emailAberto.de, assunto:"Re: "+emailAberto.assunto, corpo:""});
                        setEmailAberto(null); setEmailComposer(true);
                      }} style={{padding:"8px 18px",borderRadius:7,border:"none",
                        background:theme.blue,color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>
                        ↩️ Responder
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Modal — compor/enviar */}
          {emailComposer && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:9999,
              display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:"24px 16px"}}>
              <div style={{background:theme.white,borderRadius:12,padding:"24px 28px",maxWidth:640,
                width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,.3)",marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0}}>
                    ✏️ Novo E-mail — de {emailConta}@alvesecorreaadvogados.com.br
                  </p>
                  <button onClick={()=>setEmailComposer(false)} style={{background:"none",border:"none",
                    fontSize:20,cursor:"pointer",color:theme.gray400}}>×</button>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  <input value={emailForm.para} onChange={e=>setEmailForm(f=>({...f,para:e.target.value}))}
                    placeholder="Destinatário (e-mail)" style={{padding:"9px 12px",
                    border:`1px solid ${theme.gray200}`,borderRadius:8,fontSize:13,outline:"none"}}/>
                  <input value={emailForm.assunto} onChange={e=>setEmailForm(f=>({...f,assunto:e.target.value}))}
                    placeholder="Assunto" style={{padding:"9px 12px",
                    border:`1px solid ${theme.gray200}`,borderRadius:8,fontSize:13,outline:"none"}}/>
                  <textarea value={emailForm.corpo} onChange={e=>setEmailForm(f=>({...f,corpo:e.target.value}))}
                    placeholder="Mensagem" rows={10} style={{padding:"12px 14px",
                    border:`1px solid ${theme.gray200}`,borderRadius:8,fontSize:13,fontFamily:"inherit",
                    resize:"vertical",outline:"none",boxSizing:"border-box",lineHeight:1.7}}/>
                </div>
                <div style={{marginTop:16,display:"flex",justifyContent:"flex-end",gap:8}}>
                  <button onClick={()=>setEmailComposer(false)} style={{padding:"9px 18px",borderRadius:7,
                    border:`1px solid ${theme.gray200}`,background:theme.white,cursor:"pointer",fontSize:12,fontWeight:600}}>
                    Cancelar
                  </button>
                  <button onClick={enviarEmailForm} disabled={emailEnviando} style={{padding:"9px 18px",borderRadius:7,
                    border:"none",background:theme.blue,color:"#fff",cursor:emailEnviando?"default":"pointer",
                    fontSize:12,fontWeight:700,opacity:emailEnviando?.6:1}}>
                    {emailEnviando?"Enviando...":"📤 Enviar"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* ABA MODELOS */}
      {abaComun==="modelos" && (()=>{
        const modelosSalvos = data.modelosComunicacao || {};

        const GRUPOS_MODELOS = [
          {
            grupo: "Kit de Boas-Vindas",
            cor: theme.blue,
            modelos: [
              { tipo:"boas_vindas_d1", label:"Dia 1 — Boas-Vindas", canal:"WhatsApp + PDF" },
            ]
          },
          {
            grupo: "Linha de Montagem da Confiança",
            cor: theme.green,
            modelos: [
              { tipo:"boas_vindas_d2", label:"Dia 2 — Confirmação do Checklist", canal:"WhatsApp" },
              { tipo:"boas_vindas_d3", label:"Dia 3 — Bastidores", canal:"WhatsApp" },
              { tipo:"boas_vindas_d5", label:"Dia 5 — Check-in", canal:"WhatsApp" },
              { tipo:"boas_vindas_d7", label:"Dia 7 — Marco Legal", canal:"E-mail + WhatsApp" },
            ]
          },
          {
            grupo: "Datas Especiais",
            cor: theme.amber,
            modelos: [
              { tipo:"aniversario_cliente", label:"Aniversário do Cliente", canal:"WhatsApp" },
              { tipo:"aniversario_caso_1a", label:"1 Ano do Caso", canal:"E-mail + WhatsApp" },
              { tipo:"dia_advogado",        label:"Dia do Advogado (11/ago)", canal:"E-mail + WhatsApp" },
              { tipo:"dia_cliente",         label:"Dia do Cliente (15/set)", canal:"E-mail + WhatsApp" },
              { tipo:"fim_de_ano",          label:"Fim de Ano (20/dez)", canal:"E-mail + WhatsApp" },
            ]
          },
          {
            grupo: "Relatórios",
            cor: theme.gray600,
            modelos: [
              { tipo:"relatorio_mensal",      label:"Aviso WhatsApp — Relatório Mensal", canal:"WhatsApp + PDF" },
              { tipo:"relatorio_emergencial", label:"Aviso WhatsApp — Comunicado Emergencial", canal:"WhatsApp + PDF" },
            ]
          },
          {
            grupo: "PDFs — Kit de Boas-Vindas",
            cor: "#6d28d9",
            modelos: [
              { tipo:"pdf_boasvindas_intro",       label:"Seção 1 — Texto de Boas-Vindas",         canal:"PDF Kit" },
              { tipo:"pdf_boasvindas_google",       label:"Seção 5 — Solicitação de Avaliação Google", canal:"PDF Kit" },
            ]
          },
          {
            grupo: "PDFs — Relatório Mensal",
            cor: "#065f46",
            modelos: [
              { tipo:"pdf_relatorio_intro",         label:"Seção 1 — Texto de Introdução",          canal:"PDF Rel. Mensal" },
              { tipo:"pdf_relatorio_com_ocs",       label:"Seção 2 — Texto pós-tabela (com ocorrências)", canal:"PDF Rel. 1" },
              { tipo:"pdf_relatorio_sem_ocs",       label:"Seção 2 — Texto sem movimentação",       canal:"PDF Rel. 2" },
              { tipo:"pdf_relatorio_consideracoes", label:"Seção 3/4 — Considerações e Próximos Passos", canal:"PDF Rel. Mensal" },
            ]
          },
        ];

        // Gera texto de prévia para cada modelo
        const textoModelo = (tipo) => {
          // Tipos PDF — textos padrão diretos (não passam por gerarMensagemComunicacao)
          const defaultsPDF = {
            pdf_boasvindas_intro:
              "É um prazer tê-lo(a) como cliente do escritório Alves & Correa Advogados. Recebemos o seu caso — [Produto/Tese] — com muita atenção, e podemos garantir que cuidaremos de tudo com o maior cuidado e dedicação.",
            pdf_boasvindas_google:
              "Nossa maior recompensa é a confiança de cada cliente. Se quiser conhecer um pouco mais sobre o nosso trabalho — ou deixar seu registro após a conclusão do caso — acesse nossa página no Google. Agradecemos desde já. Será uma honra conquistar sua avaliação ao final!",
            pdf_relatorio_intro:
              "Encaminhamos o presente relatório mensal de acompanhamento do seu processo referente ao período de [MES]. Nosso compromisso é garantir transparência, acompanhamento contínuo e segurança na condução da sua demanda.",
            pdf_relatorio_com_ocs:
              "Cada ato foi analisado com atenção e as medidas cabíveis foram adotadas dentro dos prazos legais.",
            pdf_relatorio_sem_ocs:
              "No período analisado, não houve nova movimentação processual registrada nos autos. Essa situação é comum em determinados momentos do trâmite, especialmente considerando os prazos processuais e a rotina do Judiciário. O processo permanece em acompanhamento contínuo.",
            pdf_relatorio_consideracoes:
              "Seguimos atuando de forma técnica e estratégica, sempre atentos às oportunidades processuais e aos próximos desdobramentos. Nosso foco permanece na condução responsável e eficiente da sua demanda.\n\nCaso haja qualquer novidade relevante fora do ciclo mensal, você será prontamente comunicado(a).",
          };
          if (defaultsPDF[tipo]) return defaultsPDF[tipo];
          // Tipos WhatsApp — gera via função com dados de exemplo
          const clEx  = { nome:"[Nome do Cliente]", tipo:"PF", whatsapp:"" };
          const ctEx  = { dataEntrada: new Date().toISOString().slice(0,10), numContrato:"[Nº Contrato]", responsavel:"" };
          const prEx  = { nome:"[Produto/Tese]" };
          const { mensagem } = gerarMensagemComunicacao(tipo, clEx, ctEx, prEx, equipe, modelosSalvos);
          return mensagem;
        };

        return (
          <div>
            <div style={{padding:"12px 16px",background:theme.gray50,borderRadius:8,
              border:`1px solid ${theme.gray200}`,marginBottom:20}}>
              <p style={{fontSize:13,fontWeight:700,color:theme.gray800,margin:0}}>📝 Modelos de Comunicação</p>
              <p style={{fontSize:11,color:theme.gray500,margin:"3px 0 0"}}>
                Personalize os textos padrão. Campos em [colchetes] são preenchidos automaticamente pelo sistema.
                Modelos customizados têm prioridade sobre os padrões do sistema.
              </p>
            </div>

            {GRUPOS_MODELOS.map(g=>(
              <div key={g.grupo} style={{marginBottom:24}}>
                {/* Cabeçalho do grupo */}
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,
                  paddingBottom:8,borderBottom:`2px solid ${g.cor}22`}}>
                  <div style={{width:10,height:10,borderRadius:"50%",background:g.cor,flexShrink:0}}/>
                  <p style={{fontSize:13,fontWeight:700,color:g.cor,margin:0}}>{g.grupo}</p>
                </div>

                {g.modelos.map(m=>{
                  const textoPadrao = textoModelo(m.tipo);
                  const customizado = modelosSalvos[m.tipo];
                  return (
                    <ModeloItem key={m.tipo} m={m} g={g}
                      textoPadrao={textoPadrao}
                      customizado={customizado}
                      setData={setData}
                      equipe={equipe}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

// ── Mesa de Trabalho por Fase ────────────────────────────────
const MESA_CONTEUDO = {
  "Entrada e Triagem": {
    cargo: "Gestor de Entrada e Controladoria",
    missao: "Garantir que toda demanda recebida pelo escritório seja capturada, classificada, validada, distribuída, monitorada e concluída dentro dos padrões operacionais definidos, assegurando fluxo contínuo, cumprimento dos SLAs e risco zero de perda de prazo.",
    responsabilidade: "Controlar integralmente a linha de produção jurídica do escritório, garantindo que nenhuma movimentação processual, intimação, publicação, prazo, tarefa ou demanda permaneça sem tratamento, responsável definido, acompanhamento ou conclusão.",
    objetivos: ["Triar 100% das demandas recebidas em até 4 horas","Garantir 100% de captura das movimentações processuais","Garantir 100% de processamento das publicações e intimações","Manter 0 perda de prazo processual","Manter 0 demanda sem responsável definido","Garantir que 95% dos cards permaneçam dentro do SLA","Garantir auditoria semanal de 100% dos cards sobrestados"],
    kpis: ["% de demandas triadas dentro de 4 horas","% de movimentações processuais capturadas","% de publicações tratadas","% de intimações tratadas","Quantidade de prazos perdidos","Quantidade de cards atrasados","Quantidade de cards sem responsável","Tempo médio de distribuição","Quantidade de cards sobrestados auditados","Tempo médio de permanência por etapa"],
    atividades: ["Monitorar diariamente: Diário Oficial, Tribunais, Portais eletrônicos, Caixa de e-mails institucional, Sistemas automatizados","Capturar: Intimações, Publicações, Movimentações processuais, Novas demandas","Ler e interpretar cada demanda recebida","Classificar: Tipo de demanda, Produto contratado, Área jurídica, Grau de prioridade, Responsável","Definir prazos internos","Conferir documentação obrigatória — validar checklist documental","Identificar pendências e solicitar complementação documental ao cliente","Avaliar se a demanda pode avançar ou deve permanecer bloqueada"]
  },
  "Produção": {
    cargo: "Operador Jurídico",
    missao: "Executar com excelência técnica todas as atividades jurídicas recebidas da Controladoria, observando os POPs, padrões de qualidade, estratégias definidas pelo escritório e prazos internos estabelecidos.",
    responsabilidade: "Transformar demandas jurídicas em entregas técnicas completas, corretas e prontas para revisão, cumprindo integralmente os procedimentos operacionais, requisitos do produto e prazos internos definidos.",
    objetivos: ["Concluir demandas simples em até 72 horas","Concluir demandas complexas em até 5 dias úteis","Garantir que 95% das demandas sejam aprovadas na revisão sem retrabalho relevante","Garantir aderência de 100% aos POPs e checklists","Manter produtividade compatível com a capacidade definida para sua carteira"],
    kpis: ["Tempo médio de produção","Quantidade de tarefas concluídas","Taxa de aprovação na revisão","Taxa de retrabalho","Cumprimento dos SLAs","Taxa de conformidade com POPs","Produtividade individual","Índice de qualidade técnica"],
    atividades: ["Receber card enviado pela Controladoria — ler integralmente a tarefa","Analisar documentação disponível e validar informações","Realizar pesquisa legislativa, jurisprudencial e doutrinária","Levantar precedentes estratégicos e avaliar riscos processuais","Elaborar petições iniciais, contestações, recursos, manifestações, contratos, pareceres, notificações e requerimentos","Utilizar IA conforme protocolos internos — utilizar modelos homologados","Alimentar sistemas jurídicos e atualizar status dos cards","Revisar o próprio trabalho — conferir pedidos, fundamentos, coerência jurídica e estratégia","Finalizar checklist de produção e encaminhar para Revisão Sênior"]
  },
  "Revisão Sênior": {
    cargo: "Revisor Sênior",
    missao: "Garantir a excelência técnica, jurídica e estratégica de todas as entregas produzidas pelo escritório antes de seu protocolo ou envio ao cliente.",
    responsabilidade: "Validar integralmente a qualidade técnica das demandas produzidas, assegurando aderência à estratégia processual, atualização jurídica, correção formal e conformidade com os padrões estabelecidos pelo escritório.",
    objetivos: ["Revisar 100% das demandas recebidas","Concluir revisões em até 24 horas","Garantir taxa de erro crítico próxima de zero","Garantir aderência integral aos padrões técnicos do escritório","Reduzir retrabalhos e riscos processuais"],
    kpis: ["Tempo médio de revisão","Taxa de aprovação direta","Taxa de retrabalho solicitado","Quantidade de erros críticos identificados","Quantidade de protocolos devolvidos por falha técnica","Cumprimento do SLA de 24 horas","Índice de conformidade técnica"],
    atividades: ["Receber demandas concluídas pela Produção — ler integralmente a peça","Validar coerência da narrativa, enquadramento jurídico, fundamentos legais e jurisprudências utilizadas","Conferir competência, legitimidade das partes, documentos obrigatórios, prazos e pedidos","Validar padrão visual do escritório, linguagem jurídica, coerência lógica, ortografia e gramática","Aprovar a demanda ou devolver para correção com apontamentos registrados","Alimentar banco de erros recorrentes","Encaminhar ao setor de Protocolo — LIMITE: não pode protocolar documentos"]
  },
  "Protocolo": {
    cargo: "Operador de Protocolo",
    missao: "Garantir que todas as demandas aprovadas pela Revisão Sênior sejam protocoladas corretamente, dentro dos prazos estabelecidos, com total rastreabilidade e segurança operacional.",
    responsabilidade: "Executar o protocolo de documentos judiciais, administrativos e extrajudiciais, assegurando que todas as peças aprovadas sejam efetivamente entregues aos órgãos competentes, com registro adequado dos comprovantes e atualização dos sistemas internos.",
    objetivos: ["Protocolar 100% das demandas recebidas","Realizar protocolos em até 4 horas após recebimento","Garantir 100% de registro dos comprovantes","Garantir 0 perda de prazo por falha operacional","Garantir 100% de atualização do sistema jurídico"],
    kpis: ["Tempo médio de protocolo","Quantidade de protocolos realizados","Cumprimento do SLA de 4 horas","Quantidade de protocolos devolvidos","Quantidade de erros operacionais","Quantidade de prazos perdidos","Taxa de registro de comprovantes","Taxa de atualização do sistema"],
    atividades: ["Receber demandas aprovadas pela Revisão Sênior — conferir autorização","Validar peça final: assinatura digital, anexos obrigatórios, procurações e tribunal competente","Acessar sistema competente e realizar o protocolo — confirmar recebimento","Salvar e nomear comprovantes conforme padrão interno — anexar ao card","Atualizar sistema jurídico — registrar data, hora e número do protocolo","Encaminhar card para Arquivo — LIMITE: não pode alterar conteúdo jurídico nem aprovar sem revisão"]
  },
  "Arquivo": {
    cargo: "Operador de Arquivo",
    missao: "Garantir a organização, preservação, rastreabilidade e disponibilidade de todas as informações, documentos e registros produzidos pelo escritório, assegurando histórico completo e recuperação rápida dos dados sempre que necessário.",
    responsabilidade: "Executar o encerramento operacional das demandas concluídas, garantindo que toda documentação, registros, comprovantes, históricos e informações estejam corretamente organizados, classificados, armazenados e vinculados ao processo ou cliente correspondente.",
    objetivos: ["Arquivar 100% das demandas concluídas","Garantir 100% de rastreabilidade documental","Garantir localização de qualquer documento em até 2 minutos","Garantir 100% de conformidade com padrões de nomenclatura e organização","Garantir inexistência de documentos órfãos ou sem classificação"],
    kpis: ["Quantidade de demandas arquivadas","Tempo médio de arquivamento","Taxa de documentos corretamente classificados","Taxa de conformidade documental","Quantidade de documentos não localizados","Quantidade de inconsistências encontradas em auditorias","Taxa de atualização dos registros"],
    atividades: ["Receber cards concluídos após protocolo — validar conclusão das etapas anteriores","Classificar documentos e organizar pastas físicas e digitais com padrão de nomenclatura","Vincular documentos ao cliente, processo e produto","Registrar histórico da demanda: responsáveis, datas relevantes, protocolos e comprovantes","Encerrar card operacional e confirmar arquivamento completo","Realizar auditorias periódicas — identificar inconsistências e garantir aderência aos padrões","Preservar documentos estratégicos, modelos aprovados e precedentes internos"]
  },
  "Sobrestado": {
    cargo: "Gestão de Sobrestamentos",
    missao: "",
    responsabilidade: "",
    objetivos: [],
    kpis: [],
    atividades: []
  },
  "Concluído": {
    cargo: "Controle de Conclusões",
    missao: "",
    responsabilidade: "",
    objetivos: [],
    kpis: [],
    atividades: []
  },
};

// Painel lateral com resumo do cliente — Fase 3, item 4 ("Pasta do Cliente nas Mesas")
// Aparece ao abrir um contrato dentro da Mesa de Trabalho: dados cadastrais, processos
// ativos, ocorrências e documentos do cliente vinculado ao contrato.
function PastaCliente({ cliente, reg, data, aberta, onToggle }) {
  if (!cliente) return null;
  const processosAtivos = (data.processos||[]).filter(p => p.clienteId === cliente.id);
  const ocorrencias = (reg.ocorrencias||[]).slice().reverse().slice(0,8);
  const documentos = reg.documentos||[];
  const docsRecebidos = documentos.filter(d=>d.recebido).length;

  return (
    <>
      <button onClick={onToggle} title={aberta?"Fechar pasta do cliente":"Abrir pasta do cliente"}
        style={{position:"fixed",top:"50%",right:aberta?340:0,transform:"translateY(-50%)",
          zIndex:10001,padding:"14px 6px",borderRadius:"8px 0 0 8px",border:"none",
          background:theme.gray900,color:theme.gold,cursor:"pointer",fontSize:16,
          transition:"right .25s ease",boxShadow:"-2px 0 8px rgba(0,0,0,.15)"}}>
        {aberta?"›":"📁"}
      </button>
      <div style={{position:"fixed",top:0,right:aberta?0:-340,width:340,height:"100vh",
        background:theme.white,zIndex:10000,boxShadow:"-4px 0 16px rgba(0,0,0,.15)",
        overflowY:"auto",transition:"right .25s ease",padding:"20px 18px"}}>
        <p style={{fontSize:11,fontWeight:700,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:2}}>
          📁 Pasta do Cliente
        </p>
        <p style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:4}}>{cliente.nome}</p>
        <p style={{fontSize:12,color:theme.gray500,marginBottom:16}}>
          {cliente.tipo||"PF"} · {cliente.cpfCnpj||"—"}
        </p>

        <div style={{padding:"10px 12px",borderRadius:8,background:theme.gray50,marginBottom:16,fontSize:12,lineHeight:1.8}}>
          <div>📱 {whatsCompleto(cliente.whatsappDDI,cliente.whatsapp) || "—"}</div>
          <div>✉️ {cliente.email || "—"}</div>
          <div>📍 {cliente.cidade||"—"}/{cliente.uf||"—"}</div>
        </div>

        <p style={{fontSize:11,fontWeight:700,color:theme.gray600,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>
          ⚖️ Processos ativos ({processosAtivos.length})
        </p>
        {processosAtivos.length===0
          ? <p style={{fontSize:12,color:theme.gray400,marginBottom:16}}>Nenhum processo vinculado.</p>
          : <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
              {processosAtivos.map(p=>(
                <div key={p.id} style={{padding:"8px 10px",borderRadius:6,background:theme.gray50,fontSize:11}}>
                  <div style={{fontWeight:600,color:theme.gray900}}>{p.numeroProcesso||p.id}</div>
                  <div style={{color:theme.gray500}}>{p.status||"—"} {p.faseAtual?`· ${p.faseAtual}`:""}</div>
                </div>
              ))}
            </div>
        }

        <p style={{fontSize:11,fontWeight:700,color:theme.gray600,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>
          🔔 Ocorrências recentes
        </p>
        {ocorrencias.length===0
          ? <p style={{fontSize:12,color:theme.gray400,marginBottom:16}}>Nenhuma ocorrência registrada.</p>
          : <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
              {ocorrencias.map((o,i)=>(
                <div key={o.id||i} style={{padding:"8px 10px",borderRadius:6,background:theme.gray50,fontSize:11}}>
                  <div style={{color:theme.gray900}}>{o.descricao||o.tipo||"—"}</div>
                  <div style={{color:theme.gray400}}>{o.data||""}</div>
                </div>
              ))}
            </div>
        }

        <p style={{fontSize:11,fontWeight:700,color:theme.gray600,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>
          📄 Documentos ({docsRecebidos}/{documentos.length})
        </p>
        {documentos.length===0
          ? <p style={{fontSize:12,color:theme.gray400}}>Nenhum documento na checklist.</p>
          : <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {documentos.map((d,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:theme.gray700}}>
                  <span>{d.recebido?"✅":"⬜"}</span><span>{d.nome}</span>
                </div>
              ))}
            </div>
        }
      </div>
    </>
  );
}

function MesaTrabalho({ fase, registros, contratos, clientes, produtos, equipe, data, setData, FASES_KANBAN, faseDe, PRAZO_H_OP, onFechar, onAbrirContrato, onAbrirProcuracao, onAbrirDeclaracao }) {
  const [aba, setAba] = React.useState("contratos");
  const [modalSelAg, setModalSelAg] = React.useState(false);
  const [contratoAberto, setContratoAberto] = React.useState(null); // reg selecionado

  const todosAgentes = data.agentes || [];
  const chave = "mesaAgentes_" + fase.replace(/\s+/g,"_");
  const idsSel = data[chave] || [];
  const agentesFixos = todosAgentes.filter(a => idsSel.includes(a.id));

  const toggleAg = (id) => {
    const nova = idsSel.includes(id) ? idsSel.filter(x=>x!==id) : [...idsSel, id];
    setData(prev => ({...prev, [chave]: nova}));
  };

  const faseObj  = FASES_KANBAN.find(f => f.id === fase) || {cor:"#888", icon:"📋", id:fase};
  const conteudo = MESA_CONTEUDO[fase] || {cargo:fase, missao:"", responsabilidade:"", objetivos:[], kpis:[], atividades:[]};
  const temConteudo = !!(conteudo.missao || conteudo.objetivos.length > 0);

  const getNomeCli  = id => (data.clientes||[]).find(c=>c.id===id)?.nome || id || "—";
  const getNomeProd = id => (data.produtos||[]).find(p=>p.id===id)?.nome || id || "—";
  const regsNaFase  = registros.filter(r => faseDe(r.status) === fase)
    .sort((a,b) => getNomeCli(a.clienteId).localeCompare(getNomeCli(b.clienteId),"pt-BR"));

  // Abrir contrato dentro da mesa
  const abrirContrato = (r) => {
    const regReal = registros.find(x => x.contratoId === r.contratoId) || r;
    setContratoAberto(regReal);
  };

  // Fechar modal de contrato — volta para a mesa
  const fecharContrato = () => setContratoAberto(null);

  // Mover fase dentro da mesa
  const moverFaseMesa = (reg, novaFase) => {
    setData(prev => ({
      ...prev,
      baseOperacional: (prev.baseOperacional||[]).map(r =>
        r.contratoId === reg.contratoId ? {...r, status: novaFase, dataEntradaFase: new Date().toISOString().slice(0,10)} : r
      )
    }));
    fecharContrato();
  };

  const [pastaClienteAberta, setPastaClienteAberta] = React.useState(true);

  return (
    <div style={{minHeight:"100vh",background:theme.gray50}}>
      {/* Modal contrato aberto dentro da Mesa */}
      {contratoAberto && (()=>{
        const ct = (data.contratos||[]).find(c=>c.id===contratoAberto.contratoId);
        const cl = (data.clientes||[]).find(c=>c.id===contratoAberto.clienteId);
        const pr = (data.produtos||[]).find(p=>p.id===contratoAberto.produtoId);
        return <>
          <ModalCardExecucao
            key={contratoAberto.contratoId}
            reg={contratoAberto} contrato={ct} cliente={cl} produto={pr}
            equipe={equipe} setData={setData} usuario={data.usuario||""}
            processos={data.processos||[]}
            FASES_KANBAN={FASES_KANBAN} faseDe={faseDe}
            COR_ST={{}}
            checklistsGlobais={data.checklistsGlobais||{}}
            onClose={fecharContrato}
            onMoverFase={moverFaseMesa}
          />
          <PastaCliente cliente={cl} reg={contratoAberto} data={data}
            aberta={pastaClienteAberta} onToggle={()=>setPastaClienteAberta(a=>!a)}/>
        </>;
      })()}
      {/* HERO HEADER */}
      <div style={{background:`linear-gradient(135deg, #1a1610 0%, ${faseObj.cor}cc 100%)`,
        padding:"32px 32px 28px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",inset:0,opacity:.06,
          backgroundImage:"radial-gradient(circle at 20% 50%, white 1px, transparent 1px)",
          backgroundSize:"24px 24px"}}/>
        <button onClick={onFechar}
          style={{position:"absolute",top:16,right:20,background:"rgba(255,255,255,.15)",
            border:"1px solid rgba(255,255,255,.25)",color:"#fff",borderRadius:8,
            padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:600}}>
          ← Voltar
        </button>
        <div style={{position:"relative"}}>
          <p style={{fontSize:36,margin:"0 0 6px"}}>{faseObj.icon}</p>
          <h1 style={{fontSize:26,fontWeight:800,color:"#fff",margin:"0 0 6px",letterSpacing:-.5}}>
            Mesa de Trabalho — {fase}
          </h1>
          <p style={{fontSize:13,color:"rgba(255,255,255,.7)",margin:"0 0 16px"}}>
            {conteudo.cargo}
          </p>
          {conteudo.missao && (
            <div style={{maxWidth:680,padding:"12px 16px",borderRadius:8,
              background:"rgba(255,255,255,.12)",border:"1px solid rgba(255,255,255,.2)"}}>
              <p style={{fontSize:12,color:"rgba(255,255,255,.9)",lineHeight:1.7,margin:0}}>
                <strong style={{color:"#fff"}}>🎯 Missão:</strong> {conteudo.missao}
              </p>
            </div>
          )}
          {/* KPIs rápidos */}
          <div style={{display:"flex",gap:16,marginTop:16}}>
            {[
              {label:"Contratos na fase",valor:regsNaFase.length,cor:"#fff"},
              {label:"Agentes IA",valor:agentesFixos.length,cor:theme.gold},
            ].map((k,i)=>(
              <div key={i} style={{padding:"8px 16px",borderRadius:8,
                background:"rgba(255,255,255,.1)",border:"1px solid rgba(255,255,255,.15)"}}>
                <p style={{fontSize:20,fontWeight:800,color:k.cor,margin:0}}>{k.valor}</p>
                <p style={{fontSize:10,color:"rgba(255,255,255,.6)",margin:0}}>{k.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ABAS */}
      <div style={{borderBottom:`1px solid ${theme.gray200}`,background:theme.white,
        padding:"0 32px",display:"flex",gap:0}}>
        {[["contratos","📋 Contratos"],["objetivos","🎯 Objetivos & KPIs"],["ia","🤖 Agentes IA"]].map(([v,l])=>(
          <button key={v} onClick={()=>setAba(v)} style={{
            padding:"14px 20px",border:"none",background:"transparent",cursor:"pointer",
            fontSize:13,fontWeight:aba===v?700:400,fontFamily:"inherit",
            color:aba===v?faseObj.cor:theme.gray400,
            borderBottom:aba===v?`3px solid ${faseObj.cor}`:"3px solid transparent",
            marginBottom:-1}}>
            {l}
            {v==="contratos"&&regsNaFase.length>0&&(
              <span style={{marginLeft:6,fontSize:10,padding:"1px 7px",borderRadius:10,
                background:faseObj.cor,color:"#fff",fontWeight:700}}>
                {regsNaFase.length}
              </span>
            )}
            {v==="ia"&&agentesFixos.length>0&&(
              <span style={{marginLeft:6,fontSize:10,padding:"1px 7px",borderRadius:10,
                background:faseObj.cor,color:"#fff",fontWeight:700}}>
                {agentesFixos.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* CONTEÚDO */}
      <div style={{padding:"28px 32px",maxWidth:1100,margin:"0 auto"}}>

        {/* ABA CONTRATOS */}
        {aba==="contratos" && (
          <div>
            {regsNaFase.length===0 ? (
              <div style={{textAlign:"center",padding:"60px 0"}}>
                <p style={{fontSize:48,marginBottom:12}}>📭</p>
                <p style={{fontSize:16,fontWeight:600,color:theme.gray600}}>Nenhum contrato nesta fase</p>
                <p style={{fontSize:13,color:theme.gray400}}>Os contratos aparecerão aqui quando avançarem para esta fase.</p>
              </div>
            ) : (
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:14}}>
                {regsNaFase.map(r=>{
                  const ct = (data.contratos||[]).find(c=>c.id===r.contratoId);
                  // Calcular SLA
                  const prazoH = (PRAZO_H_OP||{})[fase];
                  let slaInfo = null;
                  if (prazoH) {
                    const dt = r.dataEntradaFase || r.dataInicio;
                    if (dt) {
                      const h = (Date.now() - new Date(dt.length===10?dt+"T00:00":dt).getTime()) / 3600000;
                      const pct = Math.min((h/prazoH)*100, 100);
                      const cor = pct < 60 ? theme.green : pct < 90 ? theme.amber : theme.red;
                      slaInfo = { h: Math.round(h), prazoH, pct, cor, vencido: h > prazoH };
                    }
                  }
                  return (
                    <div key={r.id} onClick={()=>abrirContrato(r)}
                      style={{borderRadius:10,background:theme.white,overflow:"hidden",
                        border:`1px solid ${slaInfo?.vencido?theme.red+"88":theme.gray200}`,
                        borderLeft:`5px solid ${slaInfo?.vencido?theme.red:faseObj.cor}`,
                        cursor:"pointer",boxShadow:slaInfo?.vencido?"0 2px 8px rgba(220,38,38,.15)":"0 1px 4px rgba(0,0,0,.06)"}}
                      onMouseEnter={e=>e.currentTarget.style.boxShadow=slaInfo?.vencido?"0 4px 16px rgba(220,38,38,.2)":"0 4px 16px rgba(0,0,0,.12)"}
                      onMouseLeave={e=>e.currentTarget.style.boxShadow=slaInfo?.vencido?"0 2px 8px rgba(220,38,38,.15)":"0 1px 4px rgba(0,0,0,.06)"}>
                      <div style={{padding:"14px 16px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                          <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0,flex:1}}>
                            {getNomeCli(r.clienteId)}
                          </p>
                          {slaInfo?.vencido && (
                            <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,
                              background:theme.red,color:"#fff",fontWeight:700,
                              flexShrink:0,marginLeft:8,whiteSpace:"nowrap"}}>
                              ⚠️ SLA Vencido
                            </span>
                          )}
                        </div>
                        <p style={{fontSize:12,color:theme.gray500,margin:"0 0 10px"}}>
                          {getNomeProd(r.produtoId)}{ct?.numContrato?" · "+ct.numContrato:""}
                        </p>
                        {slaInfo && (
                          <div>
                            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                              <span style={{fontSize:10,color:slaInfo.cor,fontWeight:600}}>
                                ⏱ {slaInfo.h}h / {slaInfo.prazoH}h SLA
                              </span>
                              <span style={{fontSize:10,color:slaInfo.cor,fontWeight:700}}>
                                {Math.round(slaInfo.pct)}%
                              </span>
                            </div>
                            <div style={{height:4,borderRadius:4,background:theme.gray100,overflow:"hidden"}}>
                              <div style={{height:"100%",borderRadius:4,width:`${slaInfo.pct}%`,
                                background:slaInfo.cor,transition:"width .3s"}}/>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ABA OBJETIVOS */}
        {aba==="objetivos" && (
          <div>
            {!temConteudo ? (
              <div style={{textAlign:"center",padding:"60px 0"}}>
                <p style={{fontSize:48,marginBottom:12}}>📝</p>
                <p style={{fontSize:16,fontWeight:600,color:theme.gray600}}>Objetivos não configurados</p>
                <p style={{fontSize:13,color:theme.gray400}}>As definições para esta fase ainda não foram inseridas.</p>
              </div>
            ) : (
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
                {conteudo.responsabilidade && (
                  <div style={{gridColumn:"1/-1",padding:"18px 20px",borderRadius:10,
                    background:faseObj.cor+"0D",border:`1px solid ${faseObj.cor}33`}}>
                    <p style={{fontSize:11,fontWeight:700,color:faseObj.cor,textTransform:"uppercase",
                      letterSpacing:.8,marginBottom:8}}>🏆 Responsabilidade Chave</p>
                    <p style={{fontSize:13,lineHeight:1.8,color:theme.gray700,margin:0}}>{conteudo.responsabilidade}</p>
                  </div>
                )}
                {conteudo.objetivos.length>0 && (
                  <div style={{padding:"18px 20px",borderRadius:10,background:theme.white,
                    border:`1px solid ${theme.gray200}`,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                    <p style={{fontSize:11,fontWeight:700,color:theme.gray600,textTransform:"uppercase",
                      letterSpacing:.8,marginBottom:12}}>📌 Objetivos e Metas</p>
                    {conteudo.objetivos.map((o,i)=>(
                      <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:8}}>
                        <span style={{fontSize:10,padding:"2px 7px",borderRadius:10,flexShrink:0,marginTop:2,
                          background:faseObj.cor+"22",color:faseObj.cor,fontWeight:700}}>META {i+1}</span>
                        <p style={{fontSize:12,lineHeight:1.6,color:theme.gray700,margin:0}}>{o}</p>
                      </div>
                    ))}
                  </div>
                )}
                {conteudo.kpis.length>0 && (
                  <div style={{padding:"18px 20px",borderRadius:10,background:theme.white,
                    border:`1px solid ${theme.gray200}`,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                    <p style={{fontSize:11,fontWeight:700,color:theme.gray600,textTransform:"uppercase",
                      letterSpacing:.8,marginBottom:12}}>📊 KPIs</p>
                    {conteudo.kpis.map((k,i)=>(
                      <div key={i} style={{padding:"6px 10px",borderRadius:6,marginBottom:6,
                        background:theme.gray50,border:`1px solid ${theme.gray100}`,fontSize:11,color:theme.gray700}}>
                        📈 {k}
                      </div>
                    ))}
                  </div>
                )}
                {conteudo.atividades.length>0 && (
                  <div style={{gridColumn:"1/-1",padding:"18px 20px",borderRadius:10,background:theme.white,
                    border:`1px solid ${theme.gray200}`,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
                    <p style={{fontSize:11,fontWeight:700,color:theme.gray600,textTransform:"uppercase",
                      letterSpacing:.8,marginBottom:12}}>⚡ Atividades</p>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                      {conteudo.atividades.map((a,i)=>(
                        <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                          <span style={{color:faseObj.cor,flexShrink:0,marginTop:2}}>▸</span>
                          <p style={{fontSize:12,lineHeight:1.6,color:theme.gray700,margin:0}}>{a}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ABA AGENTES IA */}
        {aba==="ia" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
              padding:"10px 14px",borderRadius:8,background:"#eff6ff",border:"1px solid #bfdbfe",marginBottom:20}}>
              <p style={{fontSize:12,color:"#1e40af",margin:0}}>
                🔗 Agentes do cadastro central <strong>IA — Assistentes Jurídicos</strong>.
              </p>
              <button onClick={()=>setModalSelAg(true)}
                style={{padding:"7px 16px",borderRadius:7,border:"1px solid #1e40af",
                  background:"#dbeafe",color:"#1e40af",cursor:"pointer",fontSize:12,fontWeight:700}}>
                ⚙️ Selecionar Agentes
              </button>
            </div>

            {modalSelAg && (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:9999,
                display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:"24px 16px"}}>
                <div style={{background:theme.white,borderRadius:12,padding:"24px 28px",maxWidth:560,
                  width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,.3)",marginBottom:24}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                    <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0}}>
                      ⚙️ Selecionar Agentes — {fase}
                    </p>
                    <button onClick={()=>setModalSelAg(false)}
                      style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:theme.gray400}}>×</button>
                  </div>
                  <p style={{fontSize:11,color:theme.gray500,marginBottom:14}}>
                    Marque os agentes que devem aparecer nesta mesa de trabalho.
                  </p>
                  <div style={{maxHeight:360,overflowY:"auto",display:"flex",flexDirection:"column",gap:8}}>
                    {todosAgentes.length===0
                      ? <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"20px 0"}}>
                          Nenhum agente cadastrado no módulo IA.
                        </p>
                      : todosAgentes.map(a=>{
                          const sel = idsSel.includes(a.id);
                          return (
                            <div key={a.id} onClick={()=>toggleAg(a.id)}
                              style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",
                                borderRadius:8,cursor:"pointer",transition:"all .1s",
                                border:`2px solid ${sel?theme.gold:theme.gray200}`,
                                background:sel?theme.gold+"0A":theme.white}}>
                              <span style={{fontSize:22}}>{a.emoji||a.icon||"🤖"}</span>
                              <div style={{flex:1}}>
                                <p style={{fontSize:13,fontWeight:600,color:theme.gray900,margin:0}}>{a.nome}</p>
                                <p style={{fontSize:11,color:theme.gray500,margin:"2px 0 0"}}>{a.tema||""}</p>
                              </div>
                              <span style={{fontSize:18,color:sel?theme.gold:theme.gray300}}>
                                {sel?"✓":"○"}
                              </span>
                            </div>
                          );
                        })
                    }
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                    marginTop:16,paddingTop:14,borderTop:`1px solid ${theme.gray100}`}}>
                    <span style={{fontSize:11,color:theme.gray400}}>{idsSel.length} agente(s) selecionado(s)</span>
                    <button onClick={()=>setModalSelAg(false)}
                      style={{padding:"9px 24px",borderRadius:8,border:"none",
                        background:theme.gray900,color:theme.gold,cursor:"pointer",fontSize:13,fontWeight:700}}>
                      ✓ Confirmar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {agentesFixos.length===0
              ? <div style={{textAlign:"center",padding:"40px 0"}}>
                  <p style={{fontSize:48,marginBottom:12}}>🤖</p>
                  <p style={{fontSize:16,fontWeight:600,color:theme.gray600}}>Nenhum agente definido</p>
                  <p style={{fontSize:13,color:theme.gray400}}>Clique em "Selecionar Agentes" para adicionar.</p>
                </div>
              : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:16,marginBottom:24}}>
                  {agentesFixos.map(a=><CardAgentePublico key={a.id} ag={a}/>)}
                </div>
            }
            {/* Assistente IA integrado na Mesa */}
            <div style={{marginTop:8}}>
              <p style={{fontSize:11,fontWeight:700,color:theme.gray600,
                textTransform:"uppercase",letterSpacing:.8,marginBottom:12}}>
                💬 Assistente IA — {fase}
              </p>
              <AssistenteChat data={data}
                contextoExtra={`Você está na Mesa de Trabalho da fase ${fase}. ${
                  conteudo.missao?"Missão desta fase: "+conteudo.missao:""}`}/>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function ModBase({ data, setData, usuario }) {
  const baseOp       = data.baseOperacional || [];
  const lancamentos  = data.lancamentos    || [];
  const clientes_sv  = data.clientes       || [];
  const contratos = data.contratos       || [];
  const clientes  = data.clientes        || [];
  const produtos  = data.produtos        || [];
  const equipe    = data.equipe          || [];
  const parcerias        = data.parcerias        || [];
  const processos        = data.processos        || [];
  const tiposOcorrencia  = data.tiposOcorrencia  || [];

  // ── Navegação externa (ex: vindo do ModProcessos) ──────────
  React.useEffect(()=>{
    const nav = data._navBase;
    if(!nav) return;
    if(nav.contratoId){
      setAba("operacional");
      setRegSelId(nav.contratoId);
    }
    setData(prev=>{const d={...prev};delete d._navBase;return d;});
  },[data._navBase]);

  // ── Estados gerais ─────────────────────────────
  const [aba,          setAba]          = useState("contratos");
  const [busca,        setBusca]        = useState("");
  const [filtroArea,   setFiltroArea]   = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroRisco,  setFiltroRisco]  = useState("");
  const [filtroResp,   setFiltroResp]   = useState("");
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editItem,     setEditItem]     = useState(null);
  const [viewItem,     setViewItem]     = useState(null);
  const [ocModal,      setOcModal]      = useState(null);
  const [ocClienteModal, setOcClienteModal] = useState(null); // {contrato, cliente}
  const [novaOcCli,      setNovaOcCli]      = useState({tipo:"", descricao:""});

  // ── Estados operacionais ───────────────────────
  const [regSelId,     setRegSelId]     = useState(null); // contratoId do selecionado
  const [modalFicha,   setModalFicha]   = useState(false);
  const [contratoFichaModal, setContratoFichaModal] = useState(null);
  const [modalTaref,   setModalTaref]   = useState(false);
  const [modalOcorrB,  setModalOcorrB]  = useState(false);
  const [modalPDF,     setModalPDF]     = useState(false);
  const [modalNovoReg, setModalNovoReg] = useState(false);
  const [pdfTipoBase,  setPdfTipoBase]  = useState("procuracao");
  const [qualifForm,   setQualifForm]   = useState({nacionalidade:"",estadoCivil:"",profissao:"",rg:"",ufRg:""});
  const [novaOcorrB, setNovaOcorrB] = useState({obs:"",data:"",responsavel:"",confidencial:true,tipo:"",tipoNome:""});
  const [mesAuditoria, setMesAuditoria] = useState(new Date().toISOString().slice(0,7));
  const [modalTS, setModalTS] = useState(false);
  const [editEntryTS, setEditEntryTS] = useState(null); // null = novo, objeto = editar
  const [novaEntryTS, setNovaEntryTS] = useState({data:"",horas:"",descricao:"",membroId:""});
  const [editTaref,    setEditTaref]    = useState(null);
  // States Controladoria Digital — gerenciados em <ControladoriaDigital />
  const [mesaFase, setMesaFase] = useState(null); // null = funil, string = fase aberta na mesa

  // Formatar valor para exibição (número → "1.234,56")
  const fmtValInput = v => {
    const n = parseFloat(v) || 0;
    if (n === 0) return '';
    return n.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  };
  // Converter texto digitado → número (aceita "1.234,56" ou "1234.56")
  const parseMoeda = v => {
    const s = (v+'').trim();
    // Se tem vírgula como decimal: remover pontos de milhar, trocar vírgula por ponto
    if (s.includes(',')) return parseFloat(s.replace(/\./g,'').replace(',','.')) || 0;
    // Se só tem ponto: assumir decimal
    return parseFloat(s) || 0;
  };
  // Aplicar máscara enquanto digita (permite digitação livre, formata ao sair)
  const maskInput = (v, prev) => {
    // Remover tudo exceto dígitos e vírgula
    const digits = v.replace(/[^\d,]/g, '');
    return digits;
  };

  const [novoRegDados, setNovoRegDados] = useState({});
  const [filtStatusOp, setFiltStatusOp] = useState("");
  const [buscaOp,      setBuscaOp]      = useState("");
  // Estados do board por cliente
  const [visaoExec,    setVisaoExec]    = useState("kanban"); // "kanban" | "lista"
  const [clienteSel,   setClienteSel]   = useState(null); // cliente selecionado no board
  const [buscaCliente, setBuscaCliente] = useState("");   // busca na tela de seleção
  const [cardModalOpen,setCardModalOpen]= useState(false);// modal de edição do card
  const [cardEdit,     setCardEdit]     = useState(null); // card sendo editado

  const hoje    = new Date();
  const hojeStr = hoje.toISOString().slice(0,10);

  // ── Lógica de risco (aba Contratos) ──────────
  const calcDiasParado = (u) => !u ? 0 : Math.floor((hoje - new Date(u)) / 86400000);
  const calcDiasPrazo  = (p) => !p ? null : Math.floor((new Date(p) - hoje) / 86400000);
  const calcScore = (c) => {
    let s = 0;
    if(c.teseNova) s+=2; if(c.jurisprudenciaInstavel) s+=2;
    if(c.valorElevado) s+=2; if(c.dependePericia) s+=1; if(c.documentoIncompleto) s+=1;
    const dp = calcDiasParado(c.ultimoAvanco);
    if(dp>30) s+=2; if(dp>60) s+=1;
    const pp = calcDiasPrazo(c.prazo);
    if(pp!==null&&pp<=3) s+=3; else if(pp!==null&&pp<=7) s+=1;
    return s;
  };
  const classRisco = (s) => s>=6?"Alto":s>=3?"Medio":"Baixo";

  const enriched = contratos.map(c => {
    const pIds = (c.produtosIds||[]).length > 0 ? c.produtosIds : [c.produtoId].filter(Boolean);
    const produtoNome = pIds.map(pId => produtos.find(x=>x.id===pId)?.nome || pId).filter(Boolean).join(" · ") || c.produtoId;
    return {
      ...c,
      clienteNome:    clientes.find(x=>x.id===c.clienteId)?.nome || c.clienteId,
      produtoNome,
      responsavelNome:equipe.find(x=>x.id===c.responsavel)?.nome || c.responsavel,
      diasParado:  calcDiasParado(c.ultimoAvanco),
      diasPrazo:   calcDiasPrazo(c.prazo),
      scoreRisco:  calcScore(c),
      classRisco:  classRisco(calcScore(c)),
      margemReal:  (c.valorContratado||0)-(c.custoOperacional||0),
      margemPerc:  c.valorContratado>0 ? Math.round(((c.valorContratado-c.custoOperacional)/c.valorContratado)*100) : 0,
      indiceRecebimento: c.valorContratado>0 ? Math.round(((c.valorRecebido||0)/c.valorContratado)*100) : 0,
      mc: (c.valorContratado||0) - (produtos.find(p=>p.id===c.produtoId)?.custo||0),
      mcPerc: c.valorContratado>0 ? Math.round(((c.valorContratado||0)-(produtos.find(p=>p.id===c.produtoId)?.custo||0))/c.valorContratado*100) : 0
    };
  });

  const filtrados = enriched.filter(c => {
    const mb = !busca||c.clienteNome.toLowerCase().includes(busca.toLowerCase())||c.numContrato?.includes(busca)||c.produtoNome?.toLowerCase().includes(busca.toLowerCase());
    return mb&&(!filtroArea||c.area===filtroArea)&&(!filtroStatus||c.status===filtroStatus)&&(!filtroRisco||c.classRisco===filtroRisco)&&(!filtroResp||c.responsavel===filtroResp);
  }).sort((a,b) => a.clienteNome.localeCompare(b.clienteNome,'pt-BR'));

  const prazosCriticosArr = contratos.filter(c=>{if(!c.prazo||c.status==="Concluido")return false;const d=Math.floor((new Date(c.prazo)-hoje)/86400000);return d>=0&&d<=3;});
  const paradosMais20   = contratos.filter(c=>!c.ultimoAvanco||c.status==="Concluido"?false:calcDiasParado(c.ultimoAvanco)>20);
  const altosRisco      = enriched.filter(c=>c.classRisco==="Alto"&&c.status!=="Concluido");
  // Uniao por ID de contrato — um mesmo contrato pode aparecer em mais de um criterio simultaneamente
  const idsAtencao = new Set([...prazosCriticosArr, ...paradosMais20, ...altosRisco].map(c=>c.id));
  const precisamAtencao = idsAtencao.size;

  const EMPTY_CONT = {
    id:"", numContrato:"", clienteId:"", produtoId:"", produtosIds:[], area:"",
    cnpj:"",
    fase:"Pré-processual", tipoProcesso:"Administrativo", status:"Em andamento",
    responsavel:"", dataEntrada:hojeStr, horaEntrada:"", ultimoAvanco:hojeStr,
    prazo:"", dataConclusao:"", dataVencimento:"",
    dataNascimento:"",
    faturamentoAnual:"", faturamentos:[],
    regimes:[], atividadePrincipal:"", atividadesSecundarias:[],
    valorContratado:0, valorRecebido:0, valorAReceber:0,
    valorCreditoApurado:0, percHonorarios:0, lancamentoInicialId:"",
    horasPacote:0, timesheetEntries:[], pacoteEsgotado:false, processosIds:[], temParceria:false, parceiraIdContrato:"", percComissaoContrato:0, valorComissao:0, despComissaoId:"",
    teseNova:false, jurisprudenciaInstavel:false, valorElevado:false,
    dependePericia:false, documentoIncompleto:false,
    custoOperacional:0, obs:"",
    condicaoPagamento:"avista", numParcelas:1, vencPrimeiraParcela:"",
    formaDataPgto:"fixa", faseMomento:"", faseDescricao:"",
    objetivoEspecifico:"",
    componentesFinanceiros:[],
    contratantesAdicionais:[]
  };
  const STATUS_P = ["Em andamento","Suspenso","Arquivado","Encerrado","Aguardando"];
  const TIPOS_OC = ["Distribuicao","Despacho","Decisao","Sentenca","Recurso","Peticao","Audiencia","Outros"];
  // ── Componentes Financeiros do Contrato (adicional ao Honorário Inicial já existente) ──
  const TIPOS_COMPONENTE_FIN = ["Honorário mensal","Honorário anual","Honorário de êxito","Honorário por hora","Honorário por ato","Honorário por fase","Honorário por evento","Honorário de disponibilidade (retainer)","Honorário híbrido","Custas","Despesas","Reembolsos","Tributos","Repasses"];
  const FORMAS_PRECIFICACAO_FIN = ["Valor fixo","Percentual","Valor por hora","Valor por ato","Valor por fase","Tabela/Faixas","Permuta","Bens","Modelo misto","Valor manual"];
  const BASES_CALCULO_FIN = ["Valor da causa","Valor do contrato","Valor recuperado","Valor recebido","Valor do acordo","Proveito econômico","Economia obtida","Horas","Quantidade","RMA's (Benefício Previdenciário)","Outro","Base manual"];
  const CONDICOES_PGTO_FIN = ["À vista","Entrada + saldo","Parcelas fixas","Parcelas variáveis","Escalonado","Mensal","Anual","Por evento","Por fase","Recorrente","No êxito"];
  const GATILHOS_FIN = ["Assinatura","Protocolo","Audiência","Sentença","Acordo","Recebimento","Recurso","Execução","Encerramento","Data fixa","Data relativa"];
  const EMPTY_COMPONENTE_FIN = {id:"",tipo:"Honorário mensal",descricao:"",formaPrecificacao:"Valor fixo",baseCalculo:"",valor:"",percentual:"",qtdRMAs:"",valorEstimado:"",condicaoPagamento:"Mensal",gatilho:"Assinatura",dataGatilho:"",ativo:true,obs:""};
  // ── Estrutura de 5 categorias — Lançamentos de Valores do Contrato ──
  const CATEGORIAS_COMPONENTE = [
    {value:"valorFixo",             label:"1) Valor Fixo"},
    {value:"valorADefinir",         label:"2) Valor a Definir"},
    {value:"porcentagem",           label:"3) Porcentagem"},
    {value:"honorariosAdicionais",  label:"4) Honorários Adicionais"},
    {value:"outros",                label:"5) Outros"},
  ];
  const MOMENTOS_FASE = ["Assinatura do Contrato","Protocolo Processo/Procedimento","Audiência","Recebimento","Encerramento do Processo/Procedimento","Momento Processual Específico","Outro"];
  const BASES_PORCENTAGEM = ["Valor da Causa","Valor Recuperado","Valor Recebido","Acordo","Proveito Econômico","Outro"];
  const MOMENTOS_PORCENTAGEM = ["Protocolo Processo/Procedimento","Audiência","Recebimento","Encerramento do Processo/Procedimento","Momento Processual Específico"];
  const EMPTY_COMPONENTE_V2 = {
    id:"", ativo:true, categoria:"valorFixo", descricao:"", obs:"",
    // 1) Valor Fixo
    vfModalidade:"avista", vfFormaData:"fixa",
    vfDataFixa:"", vfMomento:"", vfFaseDescricao:"", vfDataEstimada:"",
    vfValor:"",
    vfParceladoTipo:"parcelasIguais", vfNumParcelas:2, vfVencPrimeira:"", vfParcelasManual:[],
    // 2) Valor a Definir (Previdência)
    vadQtdRMAs:"", vadMomento:"implementacao",
    vadImplForma:"avista", vadImplParceladoTipo:"parcelasIguais", vadImplDataInicio:"",
    vadRecForma:"avista", vadRecDataTipo:"fixa", vadRecDataFixa:"",
    vadValorEstimado:"",
    // 3) Porcentagem
    pctPercentual:"", pctBase:"", pctBaseOutro:"", pctMomento:"", pctValorEstimado:"",
    // 4) Honorários Adicionais
    haTipo:"fixo", haValorFixo:"",
    haVariavelTipo:"porHora", haValorHora:"", haPercentual:"", haBase:"", haValorEstimado:"",
    haData:"fixa", haDataFixa:"",
    // 5) Outros
    outrosTipo:"permuta", outrosValor:"",
  };

  const saveCont = () => {
    const pIds = (editItem.produtosIds||[]).length > 0 ? editItem.produtosIds : (editItem.produtoId ? [editItem.produtoId] : []);
    if(!editItem.clienteId || pIds.length === 0) return alert("Cliente e ao menos um Produto são obrigatórios.");
    const primeiroProdId = pIds[0];
    const prod = produtos.find(p=>p.id===primeiroProdId);
    const areaTrib = editItem.area==='Tributário' || prod?.area==='Tributário';
    const id   = editItem.id||"CONT"+Date.now().toString().slice(-6);
    const num  = editItem.numContrato||`${new Date().getFullYear()}/${String(contratos.length+1).padStart(3,"0")}`;
    const val  = parseFloat(editItem.valorContratado)||0;
    const rec  = parseFloat(editItem.valorRecebido)||0;
    const dataConcl = (editItem.status==="Concluído"||editItem.status==="Concluido")
      ? (editItem.dataConclusao||hojeStr) : (editItem.dataConclusao||"");
    const lanInicialId = editItem.lancamentoInicialId || "";
    const item = {...editItem, id, numContrato:num, area:editItem.area||prod?.area||"",
      produtoId: primeiroProdId,
      produtosIds: pIds,
      valorContratado:val, valorRecebido:rec, valorAReceber:val-rec,
      dataConclusao:dataConcl,
      valorCreditoApurado: parseFloat(editItem.valorCreditoApurado)||0,
      percHonorarios: parseFloat(editItem.percHonorarios)||0,
      lancamentoInicialId: lanInicialId };
    // Se novo contrato, criar também na base operacional
    if(!editItem.id){
      // Mesclar docs e tarefas de TODOS os produtos selecionados
      const catalogoDocs  = data.documentosTrib    || [];
      const catalogoTarefs = data.catalogoTarefas   || [];
      const docsAll = []; const tarefasAll = [];
      const docIdsVisto = new Set(); const tarIdsVisto = new Set();
      pIds.forEach(pId => {
        const prodObj = produtos.find(p=>p.id===pId);
        const docIds = resolveDocIds(prodObj);
        docIds.forEach(docId => {
          if (!docIdsVisto.has(docId)) {
            docIdsVisto.add(docId);
            const cat = catalogoDocs.find(x=>x.id===docId) || {id:docId,nome:docId};
            docsAll.push({docId:cat.id, nome:cat.nome, recebido:false, dataRecebimento:"", obs:"", obrigatorio:true});
          }
        });
        (prodObj?.tarefasPadrao||[]).forEach((tarId, idx) => {
          if (!tarIdsVisto.has(tarId)) {
            tarIdsVisto.add(tarId);
            const cat = catalogoTarefs.find(x=>x.id===tarId) || {id:tarId,nome:tarId,prazoExecucaoDias:7};
            const prazo=new Date(hojeStr+"T00:00:00"); prazo.setDate(prazo.getDate()+(parseInt(cat.prazoExecucaoDias)||7));
            tarefasAll.push({id:"TRF"+Date.now().toString().slice(-5)+Math.random().toString(36).slice(-3)+tarefasAll.length,ordem:tarefasAll.length+1,titulo:cat.nome,descricao:"",prazoOriginal:prazo.toISOString().slice(0,10),prazoAtual:prazo.toISOString().slice(0,10),prorrogado:false,motivoProrrogacao:"",responsavel:editItem.responsavel||"",status:"Pendente",dataConclusao:"",ocorrencias:[]});
          }
        });
      });
      const prodObj = produtos.find(p=>p.id===primeiroProdId);
      const dtAprovacao = editItem.horaEntrada
        ? `${editItem.dataEntrada||hojeStr}T${editItem.horaEntrada}:00`
        : (editItem.dataEntrada||hojeStr);
      const novoBase={id:"BASE"+Date.now().toString().slice(-7),contratoId:id,clienteId:editItem.clienteId,produtoId:primeiroProdId,produtosIds:pIds,numContrato:num,dataInicio:editItem.dataEntrada||hojeStr,dataEntradaFase:dtAprovacao,status:"Aguardando Documentação",documentos:docsAll,tarefas:tarefasAll,checklistsPorFase:prodObj?.checklistsPorFase||{},procuracao:{gerada:false,dataGeracao:""},declaracao:{gerada:false,dataGeracao:""},ocorrencias:[{data:hojeStr,obs:"Registro criado automaticamente."}]};
      // Lançamento(s) automático(s) no Financeiro
      const condPgto   = editItem.condicaoPagamento || "avista";
      const nParcelas  = condPgto === "prazo" ? (parseInt(editItem.numParcelas)||1) : 1;
      const vencBase   = editItem.vencPrimeiraParcela || editItem.dataVencimento || "";
      const vlrParcela = nParcelas > 1 ? Math.round((val / nParcelas) * 100) / 100 : val;
      const nomeProd   = prodObj?.nome || num;
      // Gerar datas de vencimento das parcelas
      const gerarVenc = (base, parcelaIdx) => {
        if(!base) return "";
        const d = new Date(base + "T12:00:00");
        d.setMonth(d.getMonth() + parcelaIdx);
        return d.toISOString().slice(0,10);
      };
      // Criar array de lançamentos (1 para à vista, N para parcelado) — só se houver Valor Contratado preenchido (>0)
      const novosLans = val<=0 ? [] : Array.from({length: nParcelas}, (_, i) => ({
        id: "LAN" + (Date.now() + i).toString().slice(-8),
        clienteId:      editItem.clienteId,
        contratoId:     id,
        numContrato:    num,
        produtoId:      editItem.produtoId,
        tipo:           "Honorario",
        modalidade:     nParcelas > 1 ? `Parcelado ${nParcelas}x` : "À Vista",
        descricao:      nParcelas > 1
          ? `Honorários — ${nomeProd} (${i+1}/${nParcelas})`
          : `Honorários — ${nomeProd}`,
        valor:          i === nParcelas - 1
          ? Math.round((val - vlrParcela * (nParcelas - 1)) * 100) / 100 // última parcela absorve centavos
          : vlrParcela,
        dataVencimento:  gerarVenc(vencBase, i),
        dataRecebimento: "",
        bancoBaixa:      "",
        status:          "A Receber",
        numParcela:      nParcelas > 1 ? i + 1 : null,
        totalParcelas:   nParcelas > 1 ? nParcelas : null,
        pagamentos:      [],
        saldoDevedor:    null,
        obs:             "Lançamento automático ao criar contrato."
      }));
      // Salvar ID do primeiro lançamento no contrato para controle futuro
      const itemComLan = {...item,
        condicaoPagamento: condPgto,
        numParcelas: nParcelas,
        lancamentoInicialId: novosLans[0]?.id || ""
      };
      // Lançamentos a partir dos "Lançamentos de Valores do Contrato" (5 categorias)
      const componentesAtivos = (editItem.componentesFinanceiros||[]).filter(c=>c.ativo!==false);
      const clienteNome = clientes.find(c=>c.id===editItem.clienteId)?.nome || editItem.clienteId;
      const historico = `${clienteNome} — Contrato ${num} — ${nomeProd}`;
      const CAT_LABEL_G = {valorFixo:"1) Valor Fixo",valorADefinir:"2) Valor a Definir",porcentagem:"3) Porcentagem",honorariosAdicionais:"4) Honorários Adicionais",outros:"5) Outros"};
      const gerarVenc30 = (baseData, idx) => {
        if(!baseData) return "";
        const d = new Date(baseData + "T12:00:00");
        d.setDate(d.getDate() + 30*idx);
        return d.toISOString().slice(0,10);
      };
      const lansComponentes = componentesAtivos.flatMap((cf,ci) => {
        // Valor Fixo PARCELADO gera N lançamentos (parcelas iguais a cada 30 dias, ou linhas manuais)
        if(cf.categoria==="valorFixo" && cf.vfModalidade==="parcelado"){
          if(cf.vfParceladoTipo==="entradaManual"){
            const linhas = (cf.vfParcelasManual||[]).filter(l=>parseFloat(l.valor)>0);
            return linhas.map((l,li)=>({
              id: "LAN"+(Date.now()+2000+ci*100+li).toString().slice(-8),
              clienteId: editItem.clienteId, contratoId: id, numContrato: num, produtoId: editItem.produtoId,
              tipo:"Honorario", modalidade:CAT_LABEL_G.valorFixo,
              descricao:`${historico} — ${CAT_LABEL_G.valorFixo}${cf.descricao?" — "+cf.descricao:""} (${li+1}/${linhas.length})`,
              valor: parseFloat(l.valor)||0, dataVencimento: l.vencimento||"",
              dataRecebimento:"", bancoBaixa:"", status:"A Receber",
              numParcela: li+1, totalParcelas: linhas.length, pagamentos:[], saldoDevedor:null,
              obs:`Parcela ${li+1}/${linhas.length} — entrada manual (com carência, se aplicável).`
            }));
          }
          const n = parseInt(cf.vfNumParcelas)||2;
          const total = parseFloat(cf.vfValor)||0;
          const vlrParc = Math.round((total/n)*100)/100;
          return Array.from({length:n},(_,i)=>({
            id: "LAN"+(Date.now()+2000+ci*100+i).toString().slice(-8),
            clienteId: editItem.clienteId, contratoId: id, numContrato: num, produtoId: editItem.produtoId,
            tipo:"Honorario", modalidade:CAT_LABEL_G.valorFixo,
            descricao:`${historico} — ${CAT_LABEL_G.valorFixo}${cf.descricao?" — "+cf.descricao:""} (${i+1}/${n})`,
            valor: i===n-1 ? Math.round((total - vlrParc*(n-1))*100)/100 : vlrParc,
            dataVencimento: gerarVenc30(cf.vfVencPrimeira, i),
            dataRecebimento:"", bancoBaixa:"", status:"A Receber",
            numParcela: i+1, totalParcelas: n, pagamentos:[], saldoDevedor:null,
            obs:`Parcela ${i+1}/${n} — Valor Fixo parcelado, vencimento a cada 30 dias.`
          }));
        }
        const CAT_LABEL = CAT_LABEL_G;
        let valor = 0, estimado = false, dataVencimento = "", obs = "";
        if(cf.categoria==="valorFixo"){
          // À Vista (parcelado já foi tratado acima)
          valor = parseFloat(cf.vfValor)||0;
          dataVencimento = cf.vfFormaData==="porFase" ? (cf.vfDataEstimada||"") : (cf.vfDataFixa||"");
          obs = cf.vfFormaData==="porFase" ? `À vista — Fase: ${cf.vfMomento||"—"}${cf.vfFaseDescricao?" ("+cf.vfFaseDescricao+")":""}. Data estimada, sujeita a alteração.` : "À vista — data fixa.";
        } else if(cf.categoria==="valorADefinir"){
          estimado = true;
          valor = parseFloat(cf.vadValorEstimado)||0;
          obs = `Previdência${cf.vadQtdRMAs?" — "+cf.vadQtdRMAs+" RMA's":""}. Momento: ${cf.vadMomento==="implementacao"?"Implementação do Benefício":"Recebimento Acumulado"}. Valor real só é apurado no momento indicado — não conta como receita até ser confirmado.`;
        } else if(cf.categoria==="porcentagem"){
          estimado = true;
          valor = parseFloat(cf.pctValorEstimado)||0;
          obs = `${cf.pctPercentual?cf.pctPercentual+"% sobre ":""}${cf.pctBase==="Outro"?(cf.pctBaseOutro||"base a definir"):(cf.pctBase||"base a definir")}. Momento: ${cf.pctMomento||"—"}. Valor real só é apurado quando a base for confirmada.`;
        } else if(cf.categoria==="honorariosAdicionais"){
          if(cf.haTipo==="fixo"){
            valor = parseFloat(cf.haValorFixo)||0;
            dataVencimento = cf.haData==="fixa" ? (cf.haDataFixa||"") : "";
            obs = `Honorário Anual — Valor Fixo. ${cf.haData==="protocolo"?"Vencimento na data do protocolo.":""}`;
          } else {
            estimado = true;
            valor = parseFloat(cf.haValorEstimado)||0;
            obs = `Honorário Anual — Variável (${cf.haVariavelTipo==="porHora"?`R$ ${cf.haValorHora||0}/hora`:`${cf.haPercentual||0}% sobre ${cf.haBase||"base a definir"}`}). Valor real só é apurado ao final do período.`;
          }
        } else if(cf.categoria==="outros"){
          valor = parseFloat(cf.outrosValor)||0;
          obs = cf.outrosTipo==="permuta" ? "Permuta." : "Bens.";
        }
        return [{
          id: "LAN" + (Date.now()+1000+ci).toString().slice(-8),
          clienteId:      editItem.clienteId,
          contratoId:     id,
          numContrato:    num,
          produtoId:      editItem.produtoId,
          tipo:           "Honorario",
          modalidade:     estimado ? "Estimado" : CAT_LABEL[cf.categoria],
          descricao:      `${historico} — ${CAT_LABEL[cf.categoria]}${cf.descricao?" — "+cf.descricao:""}`,
          valor,
          dataVencimento,
          dataRecebimento:"",
          bancoBaixa:     "",
          status:         estimado ? "Estimado (Projeção)" : "A Receber",
          numParcela:     null,
          totalParcelas:  null,
          pagamentos:     [],
          saldoDevedor:   null,
          obs
        }];
      });
      // Comissão de parceria: NÃO gerar despesa agora
      // A despesa será gerada automaticamente ao registrar o recebimento dos honorários
      setData(prev=>({...prev,
        contratos: [...prev.contratos, itemComLan],
        baseOperacional: [...(prev.baseOperacional||[]), novoBase],
        lancamentos: [...(prev.lancamentos||[]), ...novosLans, ...lansComponentes],
        fichasCredito: prev.fichasCredito || [],
        despesas: prev.despesas||[]
      }));
    } else {
      // Edição de contrato existente — apenas atualizar
      setData(prev=>({...prev,
        contratos: prev.contratos.map(ct=>ct.id===id ? item : ct)
      }));
    }
    setModalOpen(false);
    // Abertura automática da Ficha de Crédito — todo novo contrato tributário
    if (!editItem.id) {
      const areaCont = item.area || (produtos.find(p => p.id === item.produtoId)?.area) || '';
      if (areaCont === 'Tributário') {
        setAba('operacional');
        setRegSelId(item.id);
        setTimeout(() => {
          setContratoFichaModal(itemComLan || item);
        }, 300);
      }
    }
  };

  const removerCont = id => {
    if(!confirm("Remover contrato e seus lançamentos vinculados?")) return;
    setData(prev=>({
      ...prev,
      contratos:   prev.contratos.filter(c=>c.id!==id),
      lancamentos: prev.lancamentos.filter(l=>l.contratoId!==id),
      despesas:    (prev.despesas||[]).filter(d=>d.contratoId!==id)
    }));
  };

  const BadgeRisco = ({r}) => {
    const cor = r==="Alto"?theme.red:r==="Medio"?theme.amber:theme.green;
    return <span style={{fontSize:10,fontWeight:700,color:cor,padding:"2px 8px",borderRadius:4,background:cor+"18",letterSpacing:.5}}>RISCO {r.toUpperCase()}</span>;
  };
  const BadgeStatus = s => {
    if(s==="Em andamento") return <Badge color="blue">{s}</Badge>;
    if(s==="Concluido")    return <Badge color="green">Concluído</Badge>;
    if(s==="Aguardando")   return <Badge color="amber">{s}</Badge>;
    return <Badge color="gray">{s}</Badge>;
  };

  // ── Lógica operacional ────────────────────────
  const updReg = (id,campos) => {
    setData(prev=>{
      const existe = (prev.baseOperacional||[]).some(r=>r.id===id);
      if(!existe){
        const regVirt = registros.find(r=>r.id===id);
        if(!regVirt) return prev;
        const {totalTarefas,concluidas,docsPendentes,tarefasVencidas,progresso,_virtual,...regBase}=regVirt;
        return {...prev, baseOperacional:[...(prev.baseOperacional||[]),{...regBase,...campos}]};
      }
      return {...prev, baseOperacional:(prev.baseOperacional||[]).map(r=>r.id===id?{...r,...campos}:r)};
    });
    if(regSel?.id===id) setRegSelId(prev=>prev);
  };

  const verificarConclusao = (reg,tarefasAtt) => {
    const todas = tarefasAtt||reg.tarefas||[];
    if(todas.length===0) return;
    if(todas.every(t=>t.status==="Concluída")&&reg.status!=="Concluído"){
      const novaBase=baseOp.map(r=>r.id===reg.id?{...r,tarefas:todas,status:"Concluído"}:r);
      const novaCont=contratos.map(c=>c.id===reg.contratoId?{...c,status:"Concluido",dataConclusao:hojeStr}:c);
      setData(prev=>({...prev,baseOperacional:novaBase,contratos:novaCont}));
      if(regSel?.id===reg.id) // regSel é derivado automaticamente
      alert(`✅ Todas as tarefas concluídas!\nContrato ${reg.numContrato} marcado como Concluído.`);
    } else {
      const novaBase=baseOp.map(r=>r.id===reg.id?{...r,tarefas:todas}:r);
      setData(prev=>({...prev,baseOperacional:novaBase}));
    }
  };

  const marcarDocRecebido = (reg,di,recebido) => {
    const docs=reg.documentos.map((d,i)=>i===di?{...d,recebido,dataRecebimento:recebido?hojeStr:""}:d);
    const todasRec=docs.filter(d=>d.obrigatorio!==false).every(d=>d.recebido);
    updReg(reg.id,{documentos:docs,status:todasRec&&reg.status==="Aguardando Documentação"?"Em Execução":reg.status});
  };

  const atualizarTarefa = (reg,ti,campos) => {
    const tarefas=(reg.tarefas||[]).map((t,i)=>i===ti?{...t,...campos}:t);
    verificarConclusao(reg,tarefas);
  };

  const fmtData  = d => d?new Date(d+"T12:00:00").toLocaleDateString("pt-BR"):"—";
  const getNomeCli = id=>clientes.find(c=>c.id===id)?.nome||"—";
  const getNomeProd= id=>produtos.find(p=>p.id===id)?.nome||"—";
  const getNomeResp= id=>equipe.find(e=>e.id===id)?.nome||"—";
  const diasAteVencer=d=>!d?null:Math.ceil((new Date(d+"T00:00:00")-hoje)/86400000);

  const STATUS_BASE=["Entrada e Triagem","Produção","Revisão Sênior","Protocolo","Sobrestado","Concluído","Arquivo","Aguardando Documentação","Em Execução","Suspenso","Arquivado"];
  const FASES_KANBAN=[
    {id:"Entrada e Triagem", icon:"📥", cor:"#0ea5e9", prazo:"4h",    desc:"Triagem e classificação da demanda"},
    {id:"Produção",          icon:"⚙️", cor:"#d97706", prazo:"72h",   desc:"Execução técnica pelo operador"},
    {id:"Revisão Sênior",    icon:"🔍", cor:"#7c3aed", prazo:"24h",   desc:"Filtro de qualidade técnica"},
    {id:"Protocolo",         icon:"📨", cor:"#ea580c", prazo:"4h",    desc:"Protocolo no sistema judicial"},
    {id:"Sobrestado",        icon:"⏳", cor:"#6b7280", prazo:"—",     desc:"Bloqueado por fatores externos"},
    {id:"Concluído",         icon:"✅", cor:"#16a34a", prazo:"—",     desc:"Demanda finalizada"},
    {id:"Arquivo",           icon:"🗂️", cor:"#374151", prazo:"—",     desc:"Histórico arquivado"},
  ];
  // Mapeamento retrocompat: status antigos → novas fases
  const FASE_COMPAT = {
    "Em Execução":            "Produção",
    "Aguardando Documentação":"Entrada e Triagem",
    "Suspenso":               "Sobrestado",
    "Arquivado":              "Arquivo",
  };
  const faseDe = s => FASE_COMPAT[s] || s;
  const STATUS_TAREF=["Pendente","Em andamento","Concluída","Bloqueada"];
  const COR_ST={"Entrada e Triagem":"blue","Produção":"amber","Revisão Sênior":"purple","Protocolo":"orange","Sobrestado":"gray","Concluído":"green","Arquivo":"gray","Aguardando Documentação":"amber","Em Execução":"blue","Suspenso":"gray","Arquivado":"gray"};
  const COR_TR={Pendente:"gray","Em andamento":"blue",Concluída:"green",Bloqueada:"red"};

  // Garantir que TODOS os contratos aparecem na execução
  const contratosIds = new Set(contratos.map(ct=>ct.id));
  const baseOpIds = new Set(baseOp.map(r=>r.contratoId));

  // Catálogos para enriquecimento
  const catDocsEnr   = data.documentosTrib  || [];
  const catTarefEnr  = data.catalogoTarefas || [];

  // Helper: resolve IDs de docs do produto (compat trib_documentos → documentosPadrao)
  const resolveDocIds = prod =>
    (prod?.documentosPadrao||[]).length > 0
      ? (prod.documentosPadrao||[])
      : (prod?.trib_documentos||[]);

  // Helper: monta array de documentos a partir de IDs
  const buildDocs = (docIds, contratoId) =>
    docIds.map((docId, idx) => {
      const cat = catDocsEnr.find(x=>x.id===docId) || {id:docId, nome:docId};
      return {docId:cat.id, nome:cat.nome, recebido:false, dataRecebimento:"", obs:"", obrigatorio:true};
    });

  // Helper: monta array de tarefas a partir de IDs
  const buildTarefas = (tarIds, contratoId, responsavel) =>
    tarIds.map((tarId, idx) => {
      const cat = catTarefEnr.find(x=>x.id===tarId) || {id:tarId, nome:tarId};
      return {id:"TRF"+contratoId+idx, ordem:idx+1, titulo:cat.nome, descricao:"",
        prazoOriginal:"", prazoAtual:"", prorrogado:false, motivoProrrogacao:"",
        responsavel: responsavel||"", status:"Pendente", dataConclusao:"", ocorrencias:[]};
    });

  // registrosBase — enriquece documentos e tarefas se vazios, considerando todos os produtos do contrato
  const registrosBase = baseOp.filter(r=>contratosIds.has(r.contratoId)).map(r=>{
    const ct   = contratos.find(c=>c.id===r.contratoId);
    // Todos os produtos do contrato (multi-produto compat)
    const pIds = (r.produtosIds||[]).length > 0
      ? r.produtosIds
      : (ct?.produtosIds||[]).length > 0
        ? ct.produtosIds
        : [r.produtoId||ct?.produtoId].filter(Boolean);

    // Enriquecer documentos se estiver vazio — UNION dedup por docId (igual ao RCT)
    let documentos = (r.documentos||[]);
    if (documentos.length === 0 && pIds.length > 0) {
      const docSet = new Set(); const docsEnr = [];
      pIds.forEach(pId => {
        const prod = produtos.find(p=>p.id===pId);
        resolveDocIds(prod).forEach(docId => {
          if (!docSet.has(docId)) {
            docSet.add(docId);
            const cat = catDocsEnr.find(x=>x.id===docId) || {id:docId, nome:docId};
            docsEnr.push({docId:cat.id, nome:cat.nome, recebido:false, dataRecebimento:"", obs:"", obrigatorio:true});
          }
        });
      });
      if (docsEnr.length > 0) documentos = docsEnr;
    }

    // Enriquecer tarefas se estiver vazio — UNION dedup por tarId
    let tarefas = (r.tarefas||[]);
    if (tarefas.length === 0 && pIds.length > 0) {
      const tarSet = new Set(); const tarefasEnr = [];
      pIds.forEach(pId => {
        const prod = produtos.find(p=>p.id===pId);
        (prod?.tarefasPadrao||[]).forEach(tarId => {
          if (!tarSet.has(tarId)) {
            tarSet.add(tarId);
            const cat = catTarefEnr.find(x=>x.id===tarId) || {id:tarId, nome:tarId};
            tarefasEnr.push({id:"TRF"+r.contratoId+tarefasEnr.length, ordem:tarefasEnr.length+1, titulo:cat.nome, descricao:"",
              prazoOriginal:"", prazoAtual:"", prorrogado:false, motivoProrrogacao:"",
              responsavel: ct?.responsavel||"", status:"Pendente", dataConclusao:"", ocorrencias:[]});
          }
        });
      });
      if (tarefasEnr.length > 0) tarefas = tarefasEnr;
    }

    const tot=tarefas.length;
    const conc=tarefas.filter(t=>t.status==="Concluída").length;
    const docsPend=documentos.filter(d=>!d.recebido&&d.obrigatorio!==false).length;
    const venc=tarefas.filter(t=>t.status!=="Concluída"&&t.prazoAtual&&diasAteVencer(t.prazoAtual)<0).length;
    return {...r, documentos, tarefas, produtosIds: pIds,
      totalTarefas:tot, concluidas:conc, docsPendentes:docsPend, tarefasVencidas:venc,
      progresso:tot>0?Math.round(conc/tot*100):0};
  });

  // registrosVirtuais — contratos sem registro na base
  const registrosVirtuais = contratos
    .filter(ct=>!baseOpIds.has(ct.id) && contratosIds.has(ct.id))
    .map(ct=>{
      const pIds = (ct.produtosIds||[]).length > 0 ? ct.produtosIds : [ct.produtoId].filter(Boolean);
      const docsAll = []; const tarefasAll = [];
      const docIdsV = new Set(); const tarIdsV = new Set();
      pIds.forEach(pId => {
        const prod = produtos.find(p=>p.id===pId);
        resolveDocIds(prod).forEach(docId => {
          if (!docIdsV.has(docId)) {
            docIdsV.add(docId);
            const cat = catDocsEnr.find(x=>x.id===docId) || {id:docId,nome:docId};
            docsAll.push({docId:cat.id, nome:cat.nome, recebido:false, dataRecebimento:"", obs:"", obrigatorio:true});
          }
        });
        (prod?.tarefasPadrao||[]).forEach((tarId,idx) => {
          if (!tarIdsV.has(tarId)) {
            tarIdsV.add(tarId);
            const cat = catTarefEnr.find(x=>x.id===tarId) || {id:tarId,nome:tarId};
            tarefasAll.push({id:"TRF"+ct.id+tarefasAll.length,ordem:tarefasAll.length+1,titulo:cat.nome,descricao:"",prazoOriginal:"",prazoAtual:"",prorrogado:false,motivoProrrogacao:"",responsavel:ct.responsavel||"",status:"Pendente",dataConclusao:"",ocorrencias:[]});
          }
        });
      });
      const prodNomes = pIds.map(pId => produtos.find(p=>p.id===pId)?.nome || pId).join(" · ");
      return {
        id:"VIRT_"+ct.id, contratoId:ct.id, clienteId:ct.clienteId,
        produtoId:pIds[0]||"", produtosIds:pIds, prodNomes,
        numContrato:ct.numContrato||ct.id, dataInicio:ct.dataEntrada||hojeStr,
        dataEntradaFase: ct.horaEntrada ? `${ct.dataEntrada||hojeStr}T${ct.horaEntrada}:00` : (ct.dataEntrada||hojeStr),
        status: ct.status==="Concluido"||ct.status==="Concluído" ? "Concluído"
               : ct.status==="Arquivado" ? "Arquivo"
               : "Aguardando Documentação",  // → faseDe = "Entrada e Triagem"
        documentos:docsAll, tarefas:tarefasAll, checklistsPorFase:{},
        procuracao:{gerada:false,dataGeracao:""}, declaracao:{gerada:false,dataGeracao:""}, ocorrencias:[],
        _virtual:true,
        totalTarefas:tarefasAll.length, concluidas:0, docsPendentes:docsAll.filter(d=>d.obrigatorio!==false).length,
        tarefasVencidas:0, progresso:0
      };
    });
  const registros = [...registrosBase, ...registrosVirtuais];
  // Derivar regSel do estado (sempre atualizado)
  const regSel = regSelId ? (registros.find(r=>r.contratoId===regSelId) || null) : null;

  const filtOp=registros.filter(r=>{
    const b=buscaOp.toLowerCase();
    return(!buscaOp||getNomeCli(r.clienteId).toLowerCase().includes(b)||(r.numContrato||"").includes(b)||getNomeProd(r.produtoId).toLowerCase().includes(b))
        &&(!filtStatusOp||r.status===filtStatusOp);
  });

  // HTML Procuração
  const htmlProcuracao=(reg, qualif)=>{
    const cli=clientes.find(c=>c.id===reg.clienteId);
    const ct=contratos.find(c=>c.id===reg.contratoId);
    const isPJ = cli?.tipo==="PJ";
    const q = qualif||{};
    const endCompleto = cli
      ? `${cli.endereco||"[ENDEREÇO – RUA, N. BAIRRO]"}${cli.cep?", CEP "+cli.cep:""}, ${cli.cidade||"[CIDADE]"}/${cli.uf||"[UF]"}`
      : "[ENDEREÇO – RUA, N. BAIRRO, CEP, CIDADE, UF]";
    const qualificacao = isPJ
      ? `<strong>${cli?.nome||"[NOME DA EMPRESA]"}</strong>, pessoa jurídica de direito privado, inscrita no CNPJ sob nº ${cli?.cpfCnpj||"[CNPJ]"}, com sede em ${endCompleto}, neste ato representada na forma de seu contrato/estatuto social`
      : `${cli?.nome||"[NOME]"}, ${q.nacionalidade||"[NACIONALIDADE]"}, ${q.estadoCivil||"[ESTADO CIVIL]"}, ${q.profissao||"[PROFISSÃO]"}, portador(a) da carteira de identidade nº ${q.rg||"[NÚMERO RG]"} / ${q.ufRg||"[UF]"} e inscrito(a) no CPF sob nº ${cli?.cpfCnpj||"[NÚMERO CPF]"}, residente e domiciliado(a) na ${endCompleto}`;
    const objetivo = (ct?.objetivoEspecifico||"").trim() || "[Descrição do objetivo específico – incluir manualmente]";
    return `<div class="pb-titulo" style="margin-bottom:4px">PROCURAÇÃO</div>
    <div class="pb-subtitulo">"AD JUDICIA ET EXTRA"</div>
    <p class="pb-secao">OUTORGANTE:</p>
    <p class="pb-corpo">${qualificacao}.</p>
    <p class="pb-secao">OUTORGADOS:</p>
    <p class="pb-corpo"><strong>NELSON JOSÉ ALVES</strong>, brasileiro, advogado, inscrito na OAB/SP sob o número 398.875, <strong>ANNIE LEMOS ALVES CORREA</strong>, brasileira, advogada, inscrita na OAB/SP sob o número 446.942, integrantes da sociedade de advogados <strong>ALVES &amp; CORREA ADVOGADOS</strong>, inscrita na OAB SP sob o número: 24.956, com sede na Avenida Paulista, 807, conjunto 2315, bairro Bela Vista — CEP 01311-915 — São Paulo — SP, e-mail juridico@alvesecorreaadvogados.com.br.</p>
    <p class="pb-secao">PODERES:</p>
    <p class="pb-corpo">A outorgante nomeia os outorgados seus procuradores, conferindo-lhes os poderes da cláusula "ad judicia" e "ad extra", para representá-lo em juízo ou fora dele, outorgando-lhes ainda os especiais poderes para transigir, fazer acordo, firmar compromisso, renunciar, desistir, reconhecer a procedência do pedido, receber intimações, receber e dar quitação, levantar e sacar alvarás, praticar todos atos perante repartições públicas Federais, Estaduais e Municipais, e órgãos da administração pública direta e indireta, praticar quaisquer atos perante particulares ou empresas privadas, recorrer a quaisquer instâncias e tribunais, podendo atuar em conjunto ou separadamente, substabelecer esta, a outrem, com ou sem reservas de iguais poderes, dando tudo por bom e valioso, para ${objetivo}.</p>
    <div class="pb-rodape-data">São Paulo, ${new Date().toLocaleDateString("pt-BR",{day:"numeric",month:"long",year:"numeric"})}</div>
    <div class="pb-assinaturas-uma">
      <div class="pb-assinatura"><p><strong>${cli?.nome||"OUTORGANTE"}</strong></p><p style="color:#888">OUTORGANTE</p></div>
    </div>`;
  };

  const htmlDeclaracao=(reg, qualif)=>{
    const cli=clientes.find(c=>c.id===reg.clienteId);
    const isPJ = cli?.tipo==="PJ";
    const q = qualif||{};
    const endCompleto = cli
      ? `${cli.endereco||"[ENDEREÇO – RUA, N. BAIRRO]"}${cli.cep?", CEP "+cli.cep:""}, ${cli.cidade||"[CIDADE]"}/${cli.uf||"[UF]"}`
      : "[ENDEREÇO – RUA, N. BAIRRO, CEP, CIDADE, UF]";
    const qualificacao = isPJ
      ? `<strong>${cli?.nome||"[NOME DA EMPRESA]"}</strong>, pessoa jurídica de direito privado, inscrita no CNPJ sob nº ${cli?.cpfCnpj||"[CNPJ]"}, com sede em ${endCompleto}, neste ato representada na forma de seu contrato/estatuto social`
      : `${cli?.nome||"[NOME]"}, ${q.nacionalidade||"[NACIONALIDADE]"}, ${q.estadoCivil||"[ESTADO CIVIL]"}, ${q.profissao||"[PROFISSÃO]"}, portador(a) da carteira de identidade nº ${q.rg||"[NÚMERO RG]"} / ${q.ufRg||"[UF]"} e inscrito(a) no CPF sob nº ${cli?.cpfCnpj||"[NÚMERO CPF]"}, residente e domiciliado(a) na ${endCompleto}`;
    return `<div class="pb-titulo">DECLARAÇÃO DE HIPOSSUFICIÊNCIA</div>
    <p class="pb-corpo" style="margin-top:18px">${qualificacao}.</p>
    <p class="pb-corpo"><strong>DECLARO</strong>, para todos os fins de direito e sob as penas da lei, que, em razão de minha condição financeira, estou impossibilitado(a) de arcar com as despesas inerentes ao presente processo, por "insuficiência de recursos", sem prejuízo do meu sustento e de minha família, nos termos do art. 5º, inciso LXXIV, da Constituição da República, e do artigo 98 e seguintes do Código de Processo Civil de 2015 — CPC/15, necessitando, portanto, da <strong>Gratuidade da Justiça</strong>, conforme clara redação do Código de Processo Civil de 2015.</p>
    <p class="pb-corpo">Por essa razão, requeiro a concessão integral da <strong>justiça gratuita</strong>, abrangendo todos os atos do processo, na forma do art. 98 e seguintes, do CPC/15.</p>
    <div class="pb-rodape-data">São Paulo, ${new Date().toLocaleDateString("pt-BR",{day:"numeric",month:"long",year:"numeric"})}</div>
    <div class="pb-assinaturas-uma">
      <div class="pb-assinatura"><p><strong>${cli?.nome||"DECLARANTE"}</strong></p><p style="color:#888">DECLARANTE</p></div>
    </div>`;
  };

  const imprimirBase=()=>{
    if(!regSel) return;
    const titulo = pdfTipoBase==="procuracao" ? "Procuração" : "Declaração de Hipossuficiência";
    const corpo = pdfTipoBase==="procuracao" ? htmlProcuracao(regSel,qualifForm) : htmlDeclaracao(regSel,qualifForm);
    const cssStandalone = `
      @page { size: A4; margin: 18mm 20mm 22mm 20mm; }
      * { box-sizing: border-box; }
      body { font-family: 'Georgia', serif; color:#1a1a1a; font-size:13px; line-height:1.8;
        max-width:800px; margin:0 auto; padding:30px 10px; }
      .pb-titulo    { text-align:center; font-size:16px; font-weight:700; text-transform:uppercase; letter-spacing:2px; margin:0 0 4px; }
      .pb-subtitulo { text-align:center; font-size:13px; font-weight:700; letter-spacing:1px; margin:0 0 22px; }
      .pb-secao     { font-size:12.5px; font-weight:700; text-transform:uppercase; margin:18px 0 6px; }
      .pb-corpo     { text-align:justify; font-size:13px; margin-bottom:14px; }
      .pb-assinaturas-uma { display:flex; justify-content:center; margin-top:70px; }
      .pb-assinaturas-uma .pb-assinatura { min-width:320px; border-top:1px solid #1a1a1a; padding-top:10px; text-align:center; }
      .pb-assinaturas-uma .pb-assinatura p { margin:2px 0; font-size:12px; }
      .pb-rodape-data { margin-top:50px; font-size:12px; }
    `;
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
      <title>${titulo} — Alves &amp; Correa Advogados</title>
      <style>${cssStandalone}</style></head><body>${corpo}</body></html>`;
    const win = window.open("","_blank","width=900,height=800");
    if(!win){ alert("Permita pop-ups para gerar o PDF."); return; }
    win.document.write(html);
    win.document.close(); win.focus();
    setTimeout(()=>win.print(), 600);
  };

  const ABAS_BASE=[
    ["dashboard","📊 Dashboard"],
    ["contratos","📋 Contratos"],
    ["operacional","⚙️ Execução"],
    ["controladoria","🎛️ Controladoria"],
    ["relatorios","📋 Pendências"],
    ["relmensal","📨 Rel. Mensal"],
    ["comunicacoes","📬 Comunicações"],
  ];

  return (
    <div className="fade-in">
      <style>{PRINT_STYLE_BASE}</style>

      <PageHeader title="Base Operacional" subtitle="Central de contratos, processos e prazos" />

      {/* Banner de atenção */}
      {precisamAtencao>0&&(
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 20px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,marginBottom:20,fontSize:13}}>
          <span style={{fontSize:20}}>⚠️</span>
          <div>
            <strong style={{color:theme.amber}}>{precisamAtencao} contrato{precisamAtencao!==1?"s":""} requer{precisamAtencao===1?"":"em"} atenção</strong>
            <p style={{fontSize:11,color:theme.gray600,margin:"2px 0 0"}}>Verifique prazos críticos (≤3 dias), processos parados (+20 dias) e risco alto.</p>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
        {[
          {l:"Contratos",           v:filtrados.length,                                                                     c:theme.gray900},
          {l:"Contratos Fechados",  v:registros.length,                                                                     c:theme.blue},
          {l:"Em Execução",         v:registros.filter(r=>!["Concluído","Arquivo"].includes(faseDe(r.status))).length,      c:theme.amber},
          {l:"Concluídos",          v:registros.filter(r=>faseDe(r.status)==="Concluído").length,                           c:theme.green},
        ].map(k=>(
          <Card key={k.l} style={{padding:"14px 18px"}}>
            <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{k.l}</p>
            <p style={{fontSize:k.l==="Contratos"?28:18,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
          </Card>
        ))}
      </div>

      {/* Abas */}
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:`2px solid ${theme.gray100}`}}>
        {ABAS_BASE.map(([id,lbl])=>(
          <button key={id} onClick={()=>setAba(id)} style={{
            padding:"9px 12px",fontSize:12,fontWeight:aba===id?700:400,cursor:"pointer",
            border:"none",background:"transparent",
            color:aba===id?theme.gold:theme.gray500,
            borderBottom:aba===id?`2px solid ${theme.gold}`:'2px solid transparent',
            marginBottom:-2,fontFamily:"Jost,sans-serif"
          }}>{lbl}</button>
        ))}
      </div>

      {/* ======= ABA DASHBOARD ======= */}
      {aba==="dashboard"&&(
        <div>
          {/* KPIs executivos */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
            {[
              {l:"Em Execução",        v:registros.filter(r=>r.status==="Em Execução").length,             icon:"⚙️", c:theme.blue},
              {l:"Aguard. Docs",       v:registros.filter(r=>r.status==="Aguardando Documentação").length, icon:"📄", c:theme.amber},
              {l:"Tarefas Vencidas",   v:registros.reduce((s,r)=>s+r.tarefasVencidas,0),                  icon:"⚠️", c:theme.red},
              {l:"Concluídos",         v:registros.filter(r=>r.status==="Concluído").length,               icon:"✅", c:theme.green},
            ].map(k=>(
              <Card key={k.l} style={{padding:"14px 18px"}}>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{k.l}</p>
                  <span style={{fontSize:16}}>{k.icon}</span>
                </div>
                <p style={{fontSize:26,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
              </Card>
            ))}
          </div>

          {/* Cards por contrato */}
          <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:14}}>Visão por Contrato</p>
          {registros.length===0
            ? <Card><EmptyState icon="📋" title="Nenhum contrato na base" subtitle="Crie contratos para visualizar o dashboard."/></Card>
            : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:14}}>
                {[...registros].sort((a,b)=>b.tarefasVencidas-a.tarefasVencidas||b.docsPendentes-a.docsPendentes).map(r=>{
                  const semProblema = r.tarefasVencidas===0 && r.docsPendentes===0 && r.status!=="Aguardando Documentação";
                  return (
                    <Card key={r.id} onClick={()=>{setAba("operacional");setRegSelId(r.contratoId);}} style={{
                      padding:0,overflow:"hidden",cursor:"pointer",
                      border:`2px solid ${r.tarefasVencidas>0?theme.red:r.docsPendentes>0?theme.amber:theme.gray100}`,
                      transition:"border-color .15s"
                    }}>
                      {/* Topo */}
                      <div style={{padding:"12px 16px",background:`linear-gradient(135deg,${theme.gray900},${theme.gray700})`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div style={{flex:1,minWidth:0}}>
                          <p style={{fontSize:13,fontWeight:700,color:"#fff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{getNomeCli(r.clienteId)}</p>
                          <p style={{fontSize:10,color:"rgba(255,255,255,.6)",marginTop:2}}>{r.numContrato} · {getNomeProd(r.produtoId)}</p>
                        </div>
                        <Badge color={COR_ST[r.status]||"gray"} style={{flexShrink:0,marginLeft:8,fontSize:9}}>{r.status}</Badge>
                      </div>
                      {/* Corpo */}
                      <div style={{padding:"12px 16px"}}>
                        {/* Barra progresso */}
                        <div style={{marginBottom:10}}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                            <span style={{fontSize:11,color:theme.gray500}}>Progresso</span>
                            <span style={{fontSize:11,fontWeight:700,color:r.progresso===100?theme.green:theme.blue}}>{r.progresso}%</span>
                          </div>
                          <div style={{background:theme.gray100,borderRadius:4,height:6,overflow:"hidden"}}>
                            <div style={{height:"100%",background:r.progresso===100?theme.green:theme.blue,width:`${r.progresso}%`,borderRadius:4}}/>
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",marginTop:3,fontSize:10,color:theme.gray400}}>
                            <span>{r.concluidas}/{r.totalTarefas} tarefas</span>
                            <span>{(r.documentos||[]).filter(d=>d.recebido).length}/{(r.documentos||[]).length} docs</span>
                          </div>
                        </div>
                        {/* Alertas */}
                        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          {r.tarefasVencidas>0 && <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:`${theme.red}18`,color:theme.red,fontWeight:600}}>⚠️ {r.tarefasVencidas} tarefa{r.tarefasVencidas!==1?"s":""} vencida{r.tarefasVencidas!==1?"s":""}</span>}
                          {r.docsPendentes>0  && <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:`${theme.amber}18`,color:theme.amber,fontWeight:600}}>📄 {r.docsPendentes} doc{r.docsPendentes!==1?"s":""} pendente{r.docsPendentes!==1?"s":""}</span>}
                          {semProblema        && <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:`${theme.green}18`,color:theme.green,fontWeight:600}}>✅ Em dia</span>}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
          }
        </div>
      )}

      {/* ======= ABA CONTRATOS (tabela completa) ======= */}
      {aba==="contratos"&&(
        <div>
          {/* Header aba contratos */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <p style={{fontSize:12,color:theme.gray500}}>{filtrados.length} contrato{filtrados.length!==1?"s":""}</p>
            <Btn onClick={()=>{setEditItem({...EMPTY_CONT});setModalOpen(true);}}>+ Novo Contrato</Btn>
          </div>
          {/* Filtros */}
          <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
            <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="🔍 Buscar cliente, contrato ou produto..."
              style={{flex:1,minWidth:180,padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none"}}
              onFocus={e=>e.target.style.borderColor=theme.gold} onBlur={e=>e.target.style.borderColor=theme.gray200}/>
            <select value={filtroArea} onChange={e=>setFiltroArea(e.target.value)}
              style={{padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none"}}>
              <option value="">Todas as áreas</option>
              {[...new Set(contratos.map(c=>c.area).filter(Boolean))].sort().map(a=><option key={a}>{a}</option>)}
            </select>
            <select value={filtroStatus} onChange={e=>setFiltroStatus(e.target.value)}
              style={{padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none"}}>
              <option value="">Todos os status</option>
              {STATUS_P.concat(["Concluido"]).map(s=><option key={s}>{s}</option>)}
            </select>
            <select value={filtroRisco} onChange={e=>setFiltroRisco(e.target.value)}
              style={{padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none"}}>
              <option value="">Todos os riscos</option>
              <option value="Alto">Risco Alto</option>
              <option value="Medio">Risco Médio</option>
              <option value="Baixo">Risco Baixo</option>
            </select>
            <select value={filtroResp} onChange={e=>setFiltroResp(e.target.value)}
              style={{padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none"}}>
              <option value="">Todos responsáveis</option>
              {equipe.filter(e=>e.ativo).map(e=><option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </div>

          {filtrados.length===0
            ? <Card><EmptyState icon="📋" title="Nenhum contrato encontrado" subtitle='Clique em "+ Novo Contrato" para cadastrar.'/></Card>
            : <Card style={{padding:0,overflow:"hidden"}}>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead>
                      <tr style={{borderBottom:`2px solid ${theme.gray100}`,background:theme.gray50}}>
                        {["Contrato","Cliente","Produto / Área","Responsável","Parado","Conclusão Prevista","Valores","Risco","Status","Ações"].map(h=>(
                          <th key={h} style={{padding:"8px 10px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtrados.map((c,i)=>{
                        const vencido = c.diasPrazo!==null&&c.diasPrazo<0;
                        const critico = c.diasPrazo!==null&&c.diasPrazo>=0&&c.diasPrazo<=3;
                        return (
                          <tr key={c.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50,opacity:c.status==="Arquivado" ? 0.6 : 1}}>
                            <td style={{padding:"8px 10px",whiteSpace:"nowrap"}}>
                              <p style={{fontSize:12,fontWeight:700,color:theme.gray900}}>{c.numContrato}</p>
                              <p style={{fontSize:10,color:theme.gray400}}>{fmtData(c.dataEntrada)}</p>
                            </td>
                            <td style={{padding:"8px 10px",maxWidth:160}}>
                              <p style={{fontSize:12,fontWeight:600,color:theme.gray900,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.clienteNome}</p>
                              {c.cnpj&&<p style={{fontSize:10,color:theme.gray400,marginTop:1}}>{c.cnpj}</p>}
                            </td>
                            <td style={{padding:"8px 10px"}}>
                              <p style={{fontSize:11,color:theme.gray700}}>{c.produtoNome||"—"}</p>
                              <Badge color="blue" style={{fontSize:9,marginTop:3}}>{c.area}</Badge>
                            </td>

                            <td style={{padding:"8px 10px",fontSize:11,color:theme.gray600,whiteSpace:"nowrap"}}>{c.responsavelNome||"—"}</td>
                            <td style={{padding:"8px 10px",whiteSpace:"nowrap"}}>
                              {c.diasParado>0&&(
                                <p style={{fontSize:11,fontWeight:700,color:c.diasParado>20?theme.red:theme.amber}}>
                                  {c.diasParado>20?"⚠️ ":""}{c.diasParado}d {c.diasParado>20?"PARADO":""}
                                </p>
                              )}
                            </td>
                            <td style={{padding:"8px 10px",whiteSpace:"nowrap"}}>
                              {c.prazo&&(
                                <>
                                  <p style={{fontSize:11,color:theme.gray600}}>{fmtData(c.prazo)}</p>
                                  {(vencido||critico)&&(
                                    <p style={{fontSize:10,fontWeight:700,color:vencido?theme.red:theme.amber}}>
                                      {vencido?`${Math.abs(c.diasPrazo)}d VENCIDO`:critico?"🔴 CRÍTICO":""}
                                    </p>
                                  )}
                                </>
                              )}
                            </td>
                            <td style={{padding:"8px 10px",whiteSpace:"nowrap"}}>
                              <p style={{fontSize:11,color:theme.gray800}}>R$ {(c.valorContratado||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}</p>
                              <p style={{fontSize:10,color:theme.green}}>Rec: R$ {(c.valorRecebido||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}</p>
                              <p style={{fontSize:10,color:theme.gold}}>A rec: R$ {(c.valorAReceber||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}</p>
                              <p style={{fontSize:10,color:theme.blue}}>Rec: {c.indiceRecebimento}%</p>
                              <p style={{fontSize:10,color:theme.gray400}}>MC: {c.mcPerc}%</p>
                            </td>
                            <td style={{padding:"8px 10px"}}>
                              <BadgeRisco r={c.classRisco}/>
                              <p style={{fontSize:9,color:theme.gray400,marginTop:2}}>Score: {c.scoreRisco}</p>
                              <div style={{display:"flex",gap:3,marginTop:3,flexWrap:"wrap"}}>
                                {c.teseNova&&<span title="Tese Nova" style={{fontSize:9,background:theme.amber+"22",color:theme.amber,padding:"1px 4px",borderRadius:3}}>TN</span>}
                                {c.jurisprudenciaInstavel&&<span title="Jurisprudência" style={{fontSize:9,background:theme.red+"22",color:theme.red,padding:"1px 4px",borderRadius:3}}>JI</span>}
                                {c.valorElevado&&<span title="Valor Elevado" style={{fontSize:9,background:theme.blue+"22",color:theme.blue,padding:"1px 4px",borderRadius:3}}>VE</span>}
                                {c.dependePericia&&<span title="Perícia" style={{fontSize:9,background:theme.gray400+"22",color:theme.gray500,padding:"1px 4px",borderRadius:3}}>PE</span>}
                                {c.documentoIncompleto&&<span title="Doc Incompleto" style={{fontSize:9,background:theme.red+"22",color:theme.red,padding:"1px 4px",borderRadius:3}}>DI</span>}
                              </div>
                            </td>
                            <td style={{padding:"8px 10px"}}>{BadgeStatus(c.status)}</td>
                            <td style={{padding:"8px 10px"}}>
                              <div style={{display:"flex",gap:4,flexWrap:"nowrap"}}>
                                <Btn size="sm" variant="secondary" onClick={()=>setViewItem(c)}>VER</Btn>
                                <Btn size="sm" variant="secondary" onClick={()=>{setAba('operacional');setRegSelId(c.id);}}>OCORRÊNCIAS</Btn>
                                <Btn size="sm" variant="secondary" onClick={()=>{
                                  const cliEdit = clientes.find(cl=>cl.id===c.clienteId);
                                  setEditItem({
                                    ...c,
                                    cnpj: c.cnpj || (cliEdit?.cpfCnpj ? fmtCpfCnpj(cliEdit.cpfCnpj) : ""),
                                    dataNascimento: c.dataNascimento || cliEdit?.dataNascimento || ""
                                  });
                                  setModalOpen(true);
                                }}>EDITAR</Btn>
                                {(c.area||produtos.find(p=>p.id===c.produtoId)?.area||"")==="Tributário" && (
                                  <Btn size="sm"
                                    style={{background:"#0ea5e9",color:"#fff",border:"none"}}
                                    onClick={()=>setContratoFichaModal(c)}>
                                    💳 Ficha
                                  </Btn>
                                )}
                                <Btn size="sm" variant="danger" onClick={()=>removerCont(c.id)}>×</Btn>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
          }
        </div>
      )}

      {/* ======= ABA EXECUÇÃO OPERACIONAL — BOARD POR CLIENTE ======= */}
      {/* ======= ABA EXECUÇÃO OPERACIONAL ======= */}
      {aba==="operacional" && mesaFase && (
        <MesaTrabalho
          fase={mesaFase}
          registros={registros}
          contratos={contratos}
          clientes={clientes}
          produtos={produtos}
          equipe={equipe}
          data={data}
          setData={setData}
          FASES_KANBAN={FASES_KANBAN}
          faseDe={faseDe}
          PRAZO_H_OP={{"Entrada e Triagem":4,"Produção":72,"Revisão Sênior":24,"Protocolo":4}}
          onFechar={()=>setMesaFase(null)}
          onAbrirContrato={(reg)=>{}}
        />
      )}

      {aba==="operacional" && !mesaFase &&(()=>{
        const clientesComContratos = clientes.filter(cl =>
          contratos.some(ct => ct.clienteId === cl.id)
        ).sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "pt-BR"));

        const clientesFiltrados = buscaCliente
          ? clientesComContratos.filter(cl =>
              (cl.nome || "").toLowerCase().includes(buscaCliente.toLowerCase()) ||
              (cl.cpfCnpj || "").includes(buscaCliente))
          : clientesComContratos;

        const registrosCliente = clienteSel
          ? registros.filter(r => r.clienteId === clienteSel.id)
          : [];

        // ── SLA por fase
        const PRAZO_H_OP = { "Entrada e Triagem": 4, "Produção": 72, "Revisão Sênior": 24, "Protocolo": 4 };
        const calcSLAOp = r => {
          const fase  = faseDe(r.status);
          const prazo = PRAZO_H_OP[fase];
          if (!prazo) return null;
          const dt = r.dataEntradaFase || r.dataInicio;
          if (!dt) return null;
          const ms = Date.now() - new Date(dt.length === 10 ? dt + "T00:00" : dt).getTime();
          const h  = ms / (1000 * 60 * 60);
          const pct = Math.min((h / prazo) * 100, 100);
          const cor = pct < 60 ? theme.green : pct < 90 ? theme.amber : theme.red;
          return { h: Math.round(h), prazo, pct, cor, vencido: h > prazo };
        };

        // ── Checklist helper
        const getTipoBOp = (r, faseId) => {
          if (faseId === "Entrada e Triagem")
            return (r.documentos || []).map(d => ({ item: d.nome, done: !!d.recebido, tipo: "B", docId: d.docId }));
          if (faseId === "Produção")
            return (r.tarefas || []).map(t => ({ item: t.titulo, done: t.status === "Concluída", tipo: "B", tarefaId: t.id }));
          return ((r.checklistsPorFase || {})[faseId] || [])
            .filter(i => i && i.trim())
            .map(item => ({ item, done: false, tipo: "B" }));
        };

        const getChecklistOp = (r, faseId) => {
          // Entrada e Produção: APENAS itens do produto (sem global)
          if (faseId === "Entrada e Triagem" || faseId === "Produção") {
            const stored = (r.checklists || {})[faseId];
            if (stored && stored.length > 0) return stored;
            return getTipoBOp(r, faseId);
          }
          // Demais fases: global + produto
          const stored = (r.checklists || {})[faseId];
          if (stored) return stored;
          const tipoA = resolveChecklistGlobal(faseId, data.checklistsGlobais || {})
            .map(item => ({ item, done: false, tipo: "A" }));
          const tipoB = getTipoBOp(r, faseId);
          return [...tipoA, ...tipoB];
        };

        // ── Mover fase com rastreamento de data de entrada na fase
        const moverFaseOp = (reg, novaFase) => {
          const agora = new Date().toISOString();
          const ocorrencia = {
            id: Date.now(), tipo: "Sistema",
            descricao: `Fase alterada: ${faseDe(reg.status)} → ${novaFase}`,
            data: agora.slice(0, 10), responsavel: usuario?.id || "", confidencial: false
          };
          const baseId = "BASE_" + reg.contratoId;
          setData(prev => {
            const base = prev.baseOperacional || [];
            if (base.some(b => b.contratoId === reg.contratoId)) {
              return { ...prev, baseOperacional: base.map(b =>
                b.contratoId === reg.contratoId
                  ? { ...b, status: novaFase, dataEntradaFase: agora, ocorrencias: [...(b.ocorrencias || []), ocorrencia] }
                  : b
              )};
            }
            const { _virtual, ...real } = reg;
            return { ...prev, baseOperacional: [...base, { ...real, id: baseId, status: novaFase, dataEntradaFase: agora, ocorrencias: [ocorrencia] }] };
          });
        };

        // ── TELA SELEÇÃO — KANBAN POR FASE + LISTA ──────────────────
        if (!clienteSel) return (
          <div>
            {/* ── Barra superior: busca + toggle visão ── */}
            <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
              <input value={buscaCliente} onChange={e => setBuscaCliente(e.target.value)}
                placeholder="🔍 Buscar cliente, produto ou contrato..."
                style={{ flex: 1, minWidth: 240, padding: "9px 14px", border: `1px solid ${theme.gray200}`,
                  borderRadius: 8, fontSize: 13, outline: "none" }}
                onFocus={e => e.target.style.borderColor = theme.gold}
                onBlur={e => e.target.style.borderColor = theme.gray200} />
              {buscaCliente && (
                <button onClick={() => setBuscaCliente("")}
                  style={{ padding: "8px 12px", borderRadius: 7, border: `1px solid ${theme.gray200}`,
                    background: theme.white, color: theme.gray500, cursor: "pointer", fontSize: 12 }}>✕ Limpar</button>
              )}
              <div style={{ display: "flex", gap: 4, background: theme.gray100, borderRadius: 8, padding: 3 }}>
                {[["kanban","📋 Fases"], ["lista","☰ Lista"]].map(([v, l]) => (
                  <button key={v} onClick={() => setVisaoExec(v)}
                    style={{ padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer",
                      fontSize: 12, fontWeight: visaoExec === v || (!visaoExec && v === "kanban") ? 700 : 400,
                      background: visaoExec === v || (!visaoExec && v === "kanban") ? theme.white : "transparent",
                      color: visaoExec === v || (!visaoExec && v === "kanban") ? theme.gray900 : theme.gray400,
                      boxShadow: visaoExec === v || (!visaoExec && v === "kanban") ? "0 1px 3px rgba(0,0,0,.1)" : "none" }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {/* ── KPIs resumo — 7 colunas unificadas ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))", gap: 6, marginBottom: 20 }}>
              {FASES_KANBAN.map(f => {
                const n = registros.filter(r => faseDe(r.status) === f.id).length;
                return (
                  <div key={f.id} style={{ borderRadius: 8, borderTop: `3px solid ${f.cor}`,
                    background: theme.white, border: `1px solid ${theme.gray200}`,
                    borderTop: `3px solid ${f.cor}`, overflow: "hidden", minWidth: 0 }}>
                    <div style={{ padding: "8px 6px", textAlign: "center" }}>
                      <p style={{ fontSize: 13, margin: "0 0 2px" }}>{f.icon}</p>
                      <p style={{ fontSize: 18, fontWeight: 700, color: f.cor, margin: 0,
                        lineHeight: 1, cursor: "pointer" }}
                        onClick={() => setVisaoExec("kanban")}>
                        {n}
                      </p>
                      <p style={{ fontSize: 10, fontWeight: 700, color: theme.gray700, textTransform: "uppercase",
                        letterSpacing: .5, marginTop: 2, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "0 2px" }}
                        title={f.id}>{f.id}</p>
                      {f.prazo !== "—" && (
                        <p style={{ fontSize: 8, color: f.cor, marginTop: 1 }}>SLA: {f.prazo}</p>
                      )}
                      <button onClick={() => setMesaFase(f.id)}
                        title={`Mesa de Trabalho — ${f.id}`}
                        style={{ marginTop: 5, padding: "8px 0", borderRadius: 6, width: "100%",
                          border: `1px solid ${f.cor}44`, background: f.cor + "11",
                          color: f.cor, cursor: "pointer", fontSize: 11, fontWeight: 700,
                          fontFamily: "inherit" }}>
                        🏢 Mesa
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Filtro de busca aplicado ── */}
            {(()=>{
              const bL = buscaCliente.toLowerCase();
              const regsFiltrados = bL
                ? registros.filter(r =>
                    getNomeCli(r.clienteId).toLowerCase().includes(bL) ||
                    (r.numContrato||"").toLowerCase().includes(bL) ||
                    getNomeProd(r.produtoId).toLowerCase().includes(bL) ||
                    (r.prodNomes||"").toLowerCase().includes(bL))
                : registros;

              const visao = visaoExec || "kanban";

              // ── VISÃO LISTA ──────────────────────────────────────────
              if (visao === "lista") {
                const listaOrdenada = [...regsFiltrados].sort((a, b) =>
                  getNomeCli(a.clienteId).localeCompare(getNomeCli(b.clienteId), "pt-BR")
                );
                return (
                  <Card style={{ padding: 0, overflow: "hidden" }}>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: theme.gray900 }}>
                            {["Cliente", "Produto(s)", "Fase", "SLA", "Responsável", "Alertas"].map(h => (
                              <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 10,
                                color: theme.gold, textTransform: "uppercase", letterSpacing: .8, fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {listaOrdenada.length === 0 && (
                            <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", fontSize: 13, color: theme.gray400 }}>Nenhum resultado encontrado</td></tr>
                          )}
                          {listaOrdenada.map((r, i) => {
                            const fase   = FASES_KANBAN.find(f => f.id === faseDe(r.status));
                            const sla    = calcSLAOp(r);
                            const cli    = clientes.find(c => c.id === r.clienteId);
                            const temAlerta = r.tarefasVencidas > 0 || r.docsPendentes > 0 || sla?.vencido;
                            const prodNomes = (r.produtosIds||[]).length > 1
                              ? (r.produtosIds||[]).map(pId => produtos.find(p=>p.id===pId)?.nome||"").filter(Boolean).join(", ")
                              : getNomeProd(r.produtoId);
                            return (
                              <tr key={r.id}
                                onClick={() => alert(`⚠️ Acesso bloqueado.\n\nEsta atividade só pode ser aberta através da Mesa de Trabalho da fase "${faseDe(r.status)}".\n\nClique no botão 🏢 Mesa no card dessa fase, no topo da tela.`)}
                                style={{ borderBottom: `1px solid ${theme.gray100}`,
                                  background: temAlerta ? "#fef9f9" : i % 2 === 0 ? theme.white : theme.gray50,
                                  cursor: "pointer", transition: "background .1s" }}
                                onMouseEnter={e => e.currentTarget.style.background = theme.gold + "10"}
                                onMouseLeave={e => e.currentTarget.style.background = temAlerta ? "#fef9f9" : i % 2 === 0 ? theme.white : theme.gray50}>
                                <td style={{ padding: "10px 14px", fontWeight: 700, fontSize: 13 }}>
                                  {getNomeCli(r.clienteId)}
                                  {(()=>{ const cl=clientes.find(c=>c.id===r.clienteId); return cl?.cpfCnpj ? <p style={{fontSize:10,color:theme.gray400,fontWeight:400,marginTop:2}}>{fmtCpfCnpj(cl.cpfCnpj)}</p> : null; })()}
                                </td>
                                <td style={{ padding: "10px 14px", fontSize: 12, color: theme.gray600, maxWidth: 220 }}>
                                  <span title={prodNomes}>{prodNomes.length > 40 ? prodNomes.slice(0,40)+"…" : prodNomes}</span>
                                </td>
                                <td style={{ padding: "10px 14px" }}>
                                  <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, fontWeight: 700,
                                    background: (fase?.cor || theme.gray400) + "22", color: fase?.cor || theme.gray400 }}>
                                    {fase?.icon} {faseDe(r.status)}
                                  </span>
                                </td>
                                <td style={{ padding: "10px 14px" }}>
                                  {sla ? (
                                    <div style={{ minWidth: 80 }}>
                                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                                        <span style={{ fontSize: 10, color: sla.cor, fontWeight: 700 }}>
                                          {sla.vencido ? "⚠️ " : ""}{sla.h}h/{sla.prazo}h
                                        </span>
                                      </div>
                                      <div style={{ height: 3, borderRadius: 2, background: theme.gray100 }}>
                                        <div style={{ height: "100%", width: `${sla.pct}%`, background: sla.cor, borderRadius: 2 }} />
                                      </div>
                                    </div>
                                  ) : <span style={{ fontSize: 11, color: theme.gray400 }}>—</span>}
                                </td>
                                <td style={{ padding: "10px 14px", fontSize: 12, color: theme.gray600 }}>
                                  {getNomeResp(contratos.find(c=>c.id===r.contratoId)?.responsavel || r.responsavel || "")}
                                </td>
                                <td style={{ padding: "10px 14px" }}>
                                  <div style={{ display: "flex", gap: 4 }}>
                                    {sla?.vencido && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "#fee2e2", color: theme.red, fontWeight: 700 }}>⏰ SLA</span>}
                                    {r.docsPendentes > 0 && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "#fef3c7", color: "#92400e" }}>📄 {r.docsPendentes}</span>}
                                    {r.tarefasVencidas > 0 && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "#fee2e2", color: theme.red }}>⚠️ {r.tarefasVencidas}</span>}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                );
              }

              // ── VISÃO KANBAN POR FASE ────────────────────────────────
              return (
                <div style={{ paddingBottom: 16 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))", gap: 8 }}>
                    {FASES_KANBAN.map(fase => {
                      const isFinal = ["Concluído","Arquivo"].includes(fase.id);
                      const isSobr  = fase.id === "Sobrestado";
                      // Registros desta fase, filtrados pela busca, ordenados por nome do cliente
                      const cardsNaFase = regsFiltrados
                        .filter(r => faseDe(r.status) === fase.id)
                        .sort((a, b) => getNomeCli(a.clienteId).localeCompare(getNomeCli(b.clienteId), "pt-BR"));
                      return (
                        <div key={fase.id} style={{ minWidth: 0 }}>
                          {/* Header */}
                          <div style={{ padding: "9px 12px", borderRadius: "8px 8px 0 0",
                            background: fase.cor + "18", borderTop: `3px solid ${fase.cor}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: fase.cor }}>{fase.icon} {fase.id}</span>
                              <span style={{ fontSize: 10, background: fase.cor, color: "#fff", borderRadius: "50%",
                                width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                                {cardsNaFase.length}
                              </span>
                            </div>
                            {fase.prazo !== "—" && (
                              <p style={{ fontSize: 9, color: fase.cor, marginTop: 2, fontWeight: 600 }}>⏱ SLA: {fase.prazo}</p>
                            )}
                          </div>
                          {/* Itens */}
                          <div style={{ height: "50vh", overflowY: "auto", background: isSobr ? "#fffbeb" : isFinal ? theme.gray100 : theme.gray50,
                            borderRadius: "0 0 8px 8px", padding: 6,
                            border: `1px solid ${theme.gray200}`, borderTop: "none",
                            display: "flex", flexDirection: "column", gap: 6 }}>
                            {cardsNaFase.length === 0 && (
                              <p style={{ fontSize: 11, color: theme.gray300, textAlign: "center", padding: "24px 0" }}>Vazio</p>
                            )}
                            {cardsNaFase.map(r => {
                              const cliNome  = getNomeCli(r.clienteId);
                              const sla      = calcSLAOp(r);
                              const cl       = clientes.find(c => c.id === r.clienteId);
                              const temPend  = r.tarefasVencidas > 0 || r.docsPendentes > 0;
                              const prodNome = (r.produtosIds||[]).length > 1
                                ? `${getNomeProd(r.produtoId)} +${(r.produtosIds.length-1)}`
                                : getNomeProd(r.produtoId);
                              return (
                                <div key={r.id}
                                  onClick={() => alert(`⚠️ Acesso bloqueado.\n\nEsta atividade só pode ser aberta através da Mesa de Trabalho da fase "${fase.id}".\n\nClique no botão 🏢 Mesa no card dessa fase, no topo da tela.`)}
                                  style={{ background: theme.white, borderRadius: 7, padding: "9px 10px",
                                    border: `1px solid ${sla?.vencido ? theme.red + "88" : temPend ? theme.amber + "66" : theme.gray200}`,
                                    cursor: "pointer", transition: "all .1s", boxShadow: "0 1px 2px rgba(0,0,0,.05)" }}
                                  onMouseEnter={e => e.currentTarget.style.borderColor = theme.gold}
                                  onMouseLeave={e => e.currentTarget.style.borderColor = sla?.vencido ? theme.red + "88" : temPend ? theme.amber + "66" : theme.gray200}>
                                  {/* Cliente */}
                                  <p style={{ fontSize: 12, fontWeight: 700, color: theme.gray900, marginBottom: 1, lineHeight: 1.3 }}>
                                    {cliNome}
                                  </p>
                                  {cl?.cpfCnpj && (
                                    <p style={{ fontSize: 9, color: theme.gray400, marginBottom: sla ? 4 : 2 }}>
                                      {fmtCpfCnpj(cl.cpfCnpj)}
                                    </p>
                                  )}
                                  {/* Produto */}
                                  <p style={{ fontSize: 10, color: theme.gray500, marginBottom: sla ? 5 : 3,
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                    title={prodNome}>
                                    {prodNome}
                                  </p>
                                  {/* SLA bar */}
                                  {sla && (
                                    <div style={{ marginBottom: 4 }}>
                                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 1 }}>
                                        <span style={{ fontSize: 9, color: sla.cor, fontWeight: 700 }}>
                                          {sla.vencido ? "⚠️ " : ""}{sla.h}h/{sla.prazo}h
                                        </span>
                                        <span style={{ fontSize: 9, color: theme.gray400 }}>SLA</span>
                                      </div>
                                      <div style={{ height: 3, borderRadius: 2, background: theme.gray100 }}>
                                        <div style={{ height: "100%", width: `${sla.pct}%`, background: sla.cor, borderRadius: 2 }} />
                                      </div>
                                    </div>
                                  )}
                                  {/* Alertas */}
                                  <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                                    {r.docsPendentes > 0 && (
                                      <span style={{ fontSize: 8, padding: "1px 4px", borderRadius: 3, background: "#fef3c7", color: "#92400e" }}>📄 {r.docsPendentes}</span>
                                    )}
                                    {r.tarefasVencidas > 0 && (
                                      <span style={{ fontSize: 8, padding: "1px 4px", borderRadius: 3, background: "#fee2e2", color: theme.red }}>⚠️ {r.tarefasVencidas}</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        );

        // ── BOARD DO CLIENTE (Kanban) ────────────────────────────────
        return (
          <div>
            {/* Header cliente */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20,
              padding: "14px 18px", borderRadius: 10,
              background: `linear-gradient(135deg,${theme.gray900},${theme.gray700})` }}>
              <button onClick={() => { setClienteSel(null); setBuscaCliente(""); }}
                style={{ background: "rgba(255,255,255,.1)", border: "none", color: "#fff",
                  borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                ← Clientes
              </button>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 16, fontWeight: 700, color: "#fff", margin: 0 }}>{clienteSel.nome}</p>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,.5)", margin: "2px 0 0" }}>
                  {registrosCliente.length} item{registrosCliente.length !== 1 ? "s" : ""} em execução · {clienteSel.cpfCnpj || "—"}
                </p>
              </div>
              <Btn onClick={() => { setEditItem({ ...EMPTY_CONT, clienteId: clienteSel.id }); setModalOpen(true); }}
                style={{ background: "#C9A84C", border: "none", color: "#1a1610" }}>
                + Novo Contrato
              </Btn>
            </div>

            {registrosCliente.length === 0
              ? <Card><EmptyState icon="📋" title="Nenhum item em execução" subtitle="Clique em '+ Novo Contrato' para iniciar um processo operacional para este cliente." /></Card>
              : (
                /* Board Kanban 7 colunas */
                <div style={{ overflowX: "auto", paddingBottom: 16 }}>
                  <div style={{ display: "flex", gap: 12, minWidth: 1640 }}>
                    {FASES_KANBAN.map(fase => {
                      const isFinal = ["Concluído","Arquivo"].includes(fase.id);
                      const isSobr  = fase.id === "Sobrestado";
                      const cards = registrosCliente.filter(r => faseDe(r.status) === fase.id);
                      return (
                        <div key={fase.id} style={{ flex: isFinal || isSobr ? "0 0 180px" : "0 0 210px", minWidth: isFinal || isSobr ? 180 : 210 }}>
                          {/* Header coluna */}
                          <div style={{ padding: "9px 12px", borderRadius: "8px 8px 0 0",
                            background: fase.cor + "18", borderTop: `3px solid ${fase.cor}`, marginBottom: 1 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: fase.cor }}>
                                {fase.icon} {fase.id}
                              </span>
                              <span style={{ fontSize: 10, background: fase.cor, color: "#fff",
                                borderRadius: "50%", width: 18, height: 18, display: "flex",
                                alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                                {cards.length}
                              </span>
                            </div>
                            {fase.prazo !== "—" && (
                              <p style={{ fontSize: 9, color: fase.cor, marginTop: 2, fontWeight: 600 }}>⏱ SLA: {fase.prazo}</p>
                            )}
                          </div>

                          {/* Cards */}
                          <div style={{ minHeight: 340, background: isSobr ? "#fffbeb" : isFinal ? theme.gray100 : theme.gray50,
                            borderRadius: "0 0 8px 8px", padding: 8,
                            border: `1px solid ${theme.gray200}`, borderTop: "none",
                            display: "flex", flexDirection: "column", gap: 8 }}>
                            {cards.length === 0 && (
                              <p style={{ fontSize: 11, color: theme.gray300, textAlign: "center", padding: "24px 0" }}>Vazio</p>
                            )}
                            {cards.map(r => {
                              const prod   = produtos.find(p => p.id === r.produtoId);
                              const ct     = contratos.find(c => c.id === r.contratoId);
                              const sla    = calcSLAOp(r);
                              const clFase = getChecklistOp(r, fase.id);
                              const clDone = clFase.filter(i => i.done).length;
                              const clTot  = clFase.length;
                              const clOk   = clTot === 0 || clDone === clTot;
                              const tarefasTot  = (r.tarefas || []).length;
                              const tarefasConc = (r.tarefas || []).filter(t => t.status === "Concluída").length;
                              const temUrgencia = r.tarefasVencidas > 0 || r.docsPendentes > 0;
                              const temPendExt  = clFase.some(i => i.pendenciaExterna);
                              return (
                                <div key={r.id}
                                  style={{ background: theme.white, borderRadius: 8,
                                    border: `2px solid ${temPendExt ? "#fca5a5" : sla?.vencido ? theme.red + "88" : temUrgencia ? theme.amber + "88" : theme.gray200}`,
                                    cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,.06)", transition: "all .15s",
                                    overflow: "hidden" }}
                                  onClick={() => alert(`⚠️ Acesso bloqueado.\n\nEsta atividade só pode ser aberta através da Mesa de Trabalho da fase "${fase.id}".\n\nClique no botão 🏢 Mesa no card dessa fase, no topo da tela.`)}>

                                  {/* Card header */}
                                  <div style={{ padding: "9px 11px 0" }}>
                                    <p style={{ fontSize: 11, fontWeight: 700, color: theme.gray900, marginBottom: 1, lineHeight: 1.3 }}>
                                      {prod?.nome || "—"}
                                    </p>
                                    <p style={{ fontSize: 9, color: theme.gray500, marginBottom: 5 }}>
                                      {r.numContrato || ct?.numContrato || "—"}
                                    </p>

                                    {/* SLA bar */}
                                    {sla && (
                                      <div style={{ marginBottom: 5 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 1 }}>
                                          <span style={{ fontSize: 9, color: sla.cor, fontWeight: 700 }}>
                                            {sla.vencido ? "⚠️ " : ""}{sla.h}h/{sla.prazo}h
                                          </span>
                                          <span style={{ fontSize: 9, color: theme.gray400 }}>SLA</span>
                                        </div>
                                        <div style={{ height: 3, borderRadius: 2, background: theme.gray100 }}>
                                          <div style={{ height: "100%", width: `${sla.pct}%`, background: sla.cor, borderRadius: 2 }} />
                                        </div>
                                      </div>
                                    )}

                                    {/* Badges */}
                                    <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 5 }}>
                                      {clTot > 0 && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: clOk ? "#dcfce7" : "#fef3c7", color: clOk ? theme.green : "#92400e", fontWeight: 700 }}>✓ {clDone}/{clTot}</span>}
                                      {tarefasTot > 0 && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: tarefasConc === tarefasTot ? "#dcfce7" : theme.gray100, color: tarefasConc === tarefasTot ? theme.green : theme.gray500 }}>📋 {tarefasConc}/{tarefasTot}</span>}
                                      {r.docsPendentes > 0 && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: "#fef3c7", color: "#92400e" }}>📄 {r.docsPendentes}</span>}
                                      {temPendExt && <span style={{ fontSize: 8, padding: "1px 5px", borderRadius: 4, background: "#fee2e2", color: theme.red, fontWeight: 700 }}>⏸ PEND.EXT</span>}
                                    </div>

                                    <p style={{ fontSize: 9, color: theme.gray400, marginBottom: 6 }}>
                                      👤 {getNomeResp(ct?.responsavel || r.responsavel || "")}
                                    </p>
                                  </div>

                                  {/* Inline checklist */}
                                  {clTot > 0 && (
                                    <div style={{ borderTop: `1px solid ${theme.gray100}`, padding: "6px 11px" }}>
                                      {clFase.slice(0, 4).map((it, i) => (
                                        <div key={i} style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 2 }}>
                                          <span style={{ fontSize: 10, color: it.pendenciaExterna ? theme.red : it.done ? theme.green : theme.gray300, flexShrink: 0 }}>
                                            {it.pendenciaExterna ? "⏸" : it.done ? "✓" : "○"}
                                          </span>
                                          <span style={{ fontSize: 9, color: it.pendenciaExterna ? theme.red : it.done ? theme.green : theme.gray500, textDecoration: it.done ? "line-through" : "none", lineHeight: 1.2 }}>
                                            {(it.item||"").length > 28 ? (it.item||"").slice(0, 28) + "…" : (it.item||"")}
                                          </span>
                                        </div>
                                      ))}
                                      {clFase.length > 4 && (
                                        <p style={{ fontSize: 9, color: theme.gray400, marginTop: 2 }}>+{clFase.length - 4} mais...</p>
                                      )}
                                    </div>
                                  )}

                                  {/* Avançar fase */}
                                  {(()=>{
                                    const fIdx = FASES_KANBAN.findIndex(f => f.id === fase.id);
                                    const proxFase = FASES_KANBAN[fIdx + 1];
                                    if (!proxFase || isFinal) return null;
                                    const avisoMesa = () => alert(`⚠️ Acesso bloqueado.\n\nEsta atividade só pode ser aberta ou avançada de fase através da Mesa de Trabalho da fase "${fase.id}".\n\nClique no botão 🏢 Mesa no card dessa fase, no topo da tela.`);
                                    if (isSobr && r.faseAnterior) return (
                                      <button onClick={e => { e.stopPropagation(); avisoMesa(); }}
                                        style={{ width: "100%", padding: "5px 0", fontSize: 10, fontWeight: 600, borderRadius: "0 0 6px 6px",
                                          border: "none", borderTop: `1px solid #fde68a`, background: "#fef9ec", color: "#92400e", cursor: "pointer" }}>
                                        ↩ Regularizar Pendência
                                      </button>
                                    );
                                    return (
                                      <button
                                        onClick={e => { e.stopPropagation(); avisoMesa(); }}
                                        style={{ width: "100%", padding: "5px 0", fontSize: 10, fontWeight: 600,
                                          borderRadius: "0 0 6px 6px", cursor: "pointer",
                                          border: "none", borderTop: `1px solid ${clOk && !temPendExt ? proxFase.cor : theme.amber}33`,
                                          background: clOk && !temPendExt ? proxFase.cor + "18" : "#fffbeb",
                                          color: clOk && !temPendExt ? proxFase.cor : theme.amber }}>
                                        {clOk && !temPendExt ? `→ ${proxFase.icon} ${proxFase.id}` : temPendExt ? "⏸ Pend. Externa" : "📋 Completar"}
                                      </button>
                                    );
                                  })()}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )
            }

            {/* Modal detalhe do card */}
            {cardModalOpen && cardEdit && (()=>{
              const regM = registros.find(r => r.contratoId === cardEdit.contratoId) || cardEdit;
              const ctM  = contratos.find(c => c.id === regM.contratoId);
              const clM  = clientes.find(c => c.id === regM.clienteId);
              const prM  = produtos.find(p => p.id === regM.produtoId);
              // Procuração/Declaração DESATIVADAS na Base Operacional (10/07/2026) — emissão oficial ficou
              // centralizada no módulo Vendas. Base Operacional só coleta os documentos (checklist).
              return <ModalCardExecucao
                key={regM.contratoId + "_" + (regM.status||"")}
                reg={regM} contrato={ctM} cliente={clM} produto={prM}
                equipe={equipe} setData={setData} usuario={usuario}
                processos={data.processos||[]}
                FASES_KANBAN={FASES_KANBAN} faseDe={faseDe}
                COR_ST={COR_ST} checklistsGlobais={data.checklistsGlobais||{}}
                onClose={() => setCardModalOpen(false)}
                onMoverFase={(reg, novaFase) => { moverFaseOp(reg, novaFase); setCardModalOpen(false); }}
              />;
            })()}
          </div>
        );
      })()}




      {/* ======= ABA CONTROLADORIA DIGITAL ======= */}
      {aba==="controladoria"&&<ControladoriaDigital
        registros={registros}
        contratos={contratos}
        clientes={clientes}
        produtos={produtos}
        getNomeCli={getNomeCli}
        getNomeProd={getNomeProd}
        getNomeResp={getNomeResp}
        FASES_KANBAN={FASES_KANBAN}
        faseDe={faseDe}
        COR_ST={COR_ST}
        setData={setData}
        equipe={equipe}
        usuario={usuario}
        setMesaFase={setMesaFase}
      />}

      {/* MESA DE TRABALHO */}
      {/* ======= ABA RELATÓRIO DE PENDÊNCIAS ======= */}
      {aba==="relatorios"&&(
        <div>
          {/* KPIs resumo */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:24}}>
            {[
              {l:"Docs Pendentes (total)",  v:registros.reduce((s,r)=>s+r.docsPendentes,0),     icon:"📄", c:theme.amber},
              {l:"Tarefas Vencidas (total)",v:registros.reduce((s,r)=>s+r.tarefasVencidas,0),   icon:"⚠️", c:theme.red},
              {l:"Contratos com pendência", v:registros.filter(r=>r.docsPendentes>0||r.tarefasVencidas>0).length, icon:"📋", c:theme.blue},
            ].map(k=>(
              <Card key={k.l} style={{padding:"14px 18px"}}>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{k.l}</p>
                  <span style={{fontSize:16}}>{k.icon}</span>
                </div>
                <p style={{fontSize:26,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
              </Card>
            ))}
          </div>

          {/* Pendências de documentos */}
          <Card style={{padding:"18px 20px",marginBottom:20}}>
            <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:14}}>📄 Pendências de Documentação</p>
            {registros.filter(r=>(r.documentos||[]).some(d=>!d.recebido&&d.obrigatorio!==false)).length===0
              ? <div style={{padding:"12px 0",textAlign:"center",color:theme.green,fontSize:13,fontWeight:600}}>✅ Nenhuma pendência de documentação!</div>
              : <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead>
                      <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                        {["Contrato","Cliente","Produto","Documento Pendente","Obrigatório"].map(h=>(
                          <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {registros.flatMap(r=>
                        (r.documentos||[])
                          .filter(d=>!d.recebido&&d.obrigatorio!==false)
                          .map((d,di)=>({r,d,di}))
                      ).map(({r,d,di},i)=>(
                        <tr key={r.id+di} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                          <td style={{padding:"9px 12px",fontSize:12,fontWeight:600,color:theme.gray900}}>{r.numContrato}</td>
                          <td style={{padding:"9px 12px",fontSize:12,color:theme.gray700}}>{getNomeCli(r.clienteId)}</td>
                          <td style={{padding:"9px 12px",fontSize:11,color:theme.gray500}}>{getNomeProd(r.produtoId)}</td>
                          <td style={{padding:"9px 12px"}}>
                            <span style={{fontSize:12,color:theme.amber,fontWeight:600}}>📄 {d.nome}</span>
                          </td>
                          <td style={{padding:"9px 12px"}}>
                            <Badge color={d.obrigatorio!==false?"red":"gray"} style={{fontSize:9}}>{d.obrigatorio!==false?"Obrigatório":"Opcional"}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
            }
          </Card>

          {/* Pendências de tarefas */}
          <Card style={{padding:"18px 20px",marginBottom:20}}>
            <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:14}}>⚠️ Tarefas Vencidas ou Urgentes</p>
            {registros.filter(r=>(r.tarefas||[]).some(t=>t.status!=="Concluída"&&t.prazoAtual&&diasAteVencer(t.prazoAtual)<=3)).length===0
              ? <div style={{padding:"12px 0",textAlign:"center",color:theme.green,fontSize:13,fontWeight:600}}>✅ Nenhuma tarefa vencida ou urgente!</div>
              : <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead>
                      <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                        {["Contrato","Cliente","Tarefa","Status","Prazo","Situação"].map(h=>(
                          <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {registros.flatMap(r=>
                        (r.tarefas||[])
                          .filter(t=>t.status!=="Concluída"&&t.prazoAtual&&diasAteVencer(t.prazoAtual)<=3)
                          .map((t,ti)=>({r,t,ti}))
                      ).sort((a,b)=>diasAteVencer(a.t.prazoAtual)-diasAteVencer(b.t.prazoAtual))
                       .map(({r,t,ti},i)=>{
                        const diff=diasAteVencer(t.prazoAtual);
                        const vencida=diff<0;
                        return (
                          <tr key={r.id+ti} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                            <td style={{padding:"9px 12px",fontSize:12,fontWeight:600,color:theme.gray900}}>{r.numContrato}</td>
                            <td style={{padding:"9px 12px",fontSize:12,color:theme.gray700}}>{getNomeCli(r.clienteId)}</td>
                            <td style={{padding:"9px 12px",fontSize:12,color:theme.gray800,fontWeight:600}}>{t.titulo}</td>
                            <td style={{padding:"9px 12px"}}><Badge color={COR_TR[t.status]||"gray"} style={{fontSize:9}}>{t.status}</Badge></td>
                            <td style={{padding:"9px 12px",fontSize:12,color:vencida?theme.red:theme.amber,fontWeight:600}}>{fmtData(t.prazoAtual)}</td>
                            <td style={{padding:"9px 12px"}}>
                              <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,fontWeight:700,
                                background:vencida?`${theme.red}18`:`${theme.amber}18`,
                                color:vencida?theme.red:theme.amber}}>
                                {vencida?`${Math.abs(diff)}d atraso`:diff===0?"Vence hoje":`${diff}d restante${diff!==1?"s":""}`}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
            }
          </Card>

          {/* Resumo por contrato */}
          <Card style={{padding:"18px 20px"}}>
            <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:14}}>📋 Resumo de Pendências por Contrato</p>
            {registros.length===0
              ? <EmptyState icon="📋" title="Nenhum contrato" subtitle="Crie contratos para ver o resumo."/>
              : <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead>
                      <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                        {["Contrato","Cliente","Status","Progresso","Docs Pendentes","Tarefas Vencidas","Situação"].map(h=>(
                          <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...registros].sort((a,b)=>b.tarefasVencidas-a.tarefasVencidas||b.docsPendentes-a.docsPendentes).map((r,i)=>{
                        const ok = r.tarefasVencidas===0 && r.docsPendentes===0;
                        return (
                          <tr key={r.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50,
                            cursor:"pointer"}} onClick={()=>{setAba("operacional");setRegSelId(r.contratoId);}}>
                            <td style={{padding:"9px 12px",fontSize:12,fontWeight:700,color:theme.gray900}}>{r.numContrato}</td>
                            <td style={{padding:"9px 12px",fontSize:12,color:theme.gray700}}>{getNomeCli(r.clienteId)}</td>
                            <td style={{padding:"9px 12px"}}><Badge color={COR_ST[r.status]||"gray"} style={{fontSize:9}}>{r.status}</Badge></td>
                            <td style={{padding:"9px 12px"}}>
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                <div style={{background:theme.gray100,borderRadius:3,height:6,width:80,overflow:"hidden"}}>
                                  <div style={{height:"100%",background:r.progresso===100?theme.green:theme.blue,width:`${r.progresso}%`,borderRadius:3}}/>
                                </div>
                                <span style={{fontSize:11,fontWeight:600,color:r.progresso===100?theme.green:theme.gray700}}>{r.progresso}%</span>
                              </div>
                            </td>
                            <td style={{padding:"9px 12px",textAlign:"center"}}>
                              <span style={{fontSize:13,fontWeight:700,color:r.docsPendentes>0?theme.amber:theme.green}}>{r.docsPendentes}</span>
                            </td>
                            <td style={{padding:"9px 12px",textAlign:"center"}}>
                              <span style={{fontSize:13,fontWeight:700,color:r.tarefasVencidas>0?theme.red:theme.green}}>{r.tarefasVencidas}</span>
                            </td>
                            <td style={{padding:"9px 12px"}}>
                              {ok
                                ? <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:`${theme.green}18`,color:theme.green,fontWeight:600}}>✅ Em dia</span>
                                : <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:`${theme.red}18`,color:theme.red,fontWeight:600}}>⚠️ Pendente</span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
            }
          </Card>

          {/* ── Auditoria de Ocorrências ── */}
          <Card style={{padding:"18px 20px",marginTop:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900}}>📋 Auditoria de Ocorrências</p>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <span style={{fontSize:12,color:theme.gray500}}>Período:</span>
                <input type="month" value={mesAuditoria} onChange={e=>setMesAuditoria(e.target.value)}
                  style={{padding:"6px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none"}}/>
              </div>
            </div>
            <PainelAuditoria registros={registros} mesAuditoria={mesAuditoria} getNomeCli={getNomeCli}
              fmtData={fmtData} equipe={equipe} data={data} setData={setData} theme={theme} Btn={Btn}/>
          </Card>
        </div>
      )}

      {/* ======= ABA REL. MENSAL ======= */}
      {aba==="relmensal"&&(
        <RelMensalBase
          registros={registros}
          contratos={contratos}
          processos={processos}
          clientes={clientes}
          produtos={produtos}
          equipe={equipe}
          mesAuditoria={mesAuditoria}
          setMesAuditoria={setMesAuditoria}
          getNomeCli={getNomeCli}
          getNomeProd={getNomeProd}
          fmtData={fmtData}
          theme={theme}
          Card={Card}
          Btn={Btn}
        />
      )}

      {/* ======= ABA COMUNICAÇÕES ======= */}
      {aba==="comunicacoes"&&(
        <ModComunicacoes
          data={data}
          setData={setData}
          usuario={usuario}
        />
      )}

      {/* ======= MODAL NOVO/EDITAR CONTRATO ======= */}
      <Modal open={modalOpen} onClose={()=>setModalOpen(false)} title={editItem?.id?"Editar Contrato":"Novo Contrato"} width={660} fullPage>
        {editItem&&(
          <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
              <div style={{gridColumn:"1/-1",marginBottom:8}}>
                <p className="serif" style={{fontSize:17,fontWeight:800,color:theme.gray900,paddingBottom:6,borderBottom:`2px solid ${theme.gray200}`}}>1 — Dados do Cliente</p>
              </div>
              <FormField label="Cliente" required>
                <Select value={editItem.clienteId}
                  onChange={v=>{
                    const clSel = clientes.find(c=>c.id===v) || null;
                    const cpfNum = clSel && typeof clSel.cpfCnpj === "string"
                      ? clSel.cpfCnpj.replace(/\D/g,"") : "";
                    const isPF = cpfNum.length < 14 && clSel?.tipo !== "PJ";
                    setEditItem(prev=>({
                      ...prev,
                      clienteId: v,
                      // CPF/CNPJ — preenche automaticamente para qualquer tipo de documento
                      cnpj: clSel?.cpfCnpj ? fmtCpfCnpj(clSel.cpfCnpj) : (prev.cnpj||""),
                      // Espelhar dataNascimento do cliente PF automaticamente
                      dataNascimento: isPF && clSel?.dataNascimento ? clSel.dataNascimento : (prev.dataNascimento||"")
                    }));
                  }}
                  options={[...clientes].sort((a,b)=>(a.nome||"").localeCompare(b.nome||"","pt-BR")).map(c=>({value:c.id,label:c.nome}))}
                  placeholder="Selecione o cliente..."/>
              </FormField>

              {/* ── CNPJ / CPF do contrato — com máscara ── */}
              {editItem.clienteId && (cl2 => {
                const cpfN2 = cl2 && typeof cl2.cpfCnpj === "string" ? cl2.cpfCnpj.replace(/\D/g,"") : "";
                const isPJ2 = cpfN2.length >= 14;
                const label = isPJ2 ? "🏢 CNPJ" : "👤 CPF";
                const mask  = v => {
                  const d = v.replace(/\D/g,"");
                  return isPJ2 ? fmtCpfCnpj(d) : fmtCpfCnpj(d);
                };
                return (
                  <FormField label={label}>
                    <Input value={editItem.cnpj||""} onChange={v=>setEditItem(prev=>({...prev,cnpj:mask(v)}))}
                      placeholder={isPJ2?"00.000.000/0001-00":"000.000.000-00"}/>
                  </FormField>
                );
              })(clientes.find(c=>c.id===editItem.clienteId))}

              {/* ── Contratantes Adicionais (co-contratantes) ── */}
              <div style={{gridColumn:"1/-1",borderRadius:8,padding:"12px 16px",background:"#fefce8",border:"1px solid #fde68a",marginBottom:4}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <p style={{fontSize:12,fontWeight:700,color:"#92400e"}}>👥 Contratantes Adicionais <span style={{fontWeight:400,color:theme.gray500}}>(opcional — quando há mais de um nome no contrato)</span></p>
                  <button onClick={()=>setEditItem(prev=>({...prev,contratantesAdicionais:[...(prev.contratantesAdicionais||[]),{id:"CTA"+Date.now(),clienteId:"",nomeLivre:""}]}))}
                    style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:"1px solid #92400e",background:"#fefce8",color:"#92400e",cursor:"pointer",fontWeight:600}}>+ Adicionar Contratante</button>
                </div>
                {(editItem.contratantesAdicionais||[]).length===0 && <p style={{fontSize:11,color:theme.gray400}}>O titular acima é o único contratante. Clique em "+ Adicionar Contratante" se houver co-contratantes (ex: cônjuges, sócios).</p>}
                {(editItem.contratantesAdicionais||[]).map((ca,cai)=>{
                  const updCa = patch => setEditItem(prev=>({...prev,contratantesAdicionais:prev.contratantesAdicionais.map((x,i)=>i===cai?{...x,...patch}:x)}));
                  return (
                    <div key={ca.id||cai} style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:"0 10px",alignItems:"end",marginBottom:6}}>
                      <FormField label="Cliente já cadastrado">
                        <Select value={ca.clienteId||""} onChange={v=>updCa({clienteId:v,nomeLivre:v?"":ca.nomeLivre})} options={[...clientes].sort((a,b)=>(a.nome||"").localeCompare(b.nome||"","pt-BR")).map(c=>({value:c.id,label:c.nome}))} placeholder="— ou digite o nome ao lado —"/>
                      </FormField>
                      <FormField label="Nome (se ainda não cadastrado)">
                        <Input value={ca.nomeLivre||""} onChange={v=>updCa({nomeLivre:v,clienteId:v?"":ca.clienteId})} placeholder="Nome completo"/>
                      </FormField>
                      <button onClick={()=>setEditItem(prev=>({...prev,contratantesAdicionais:prev.contratantesAdicionais.filter((_,i)=>i!==cai)}))} style={{fontSize:16,color:theme.red,background:"none",border:"none",cursor:"pointer",padding:"4px 6px",marginBottom:2}}>×</button>
                    </div>
                  );
                })}
                <p style={{fontSize:10,color:theme.gray500,marginTop:2}}>O contratante principal (acima) continua sendo o titular financeiro e do CRM. Os adicionais aparecem na ficha e podem ser referenciados na redação dos documentos gerados.</p>
              </div>

              <div style={{gridColumn:"1/-1",marginTop:10,marginBottom:8}}>
                <p className="serif" style={{fontSize:17,fontWeight:800,color:theme.gray900,paddingBottom:6,borderBottom:`2px solid ${theme.gray200}`}}>2 — Produtos Contratados</p>
              </div>
              {/* ── Campo multi-produto com ProdutoSelector (igual ao CRM) ── */}
              <div style={{gridColumn:"1/-1",borderRadius:8,padding:"12px 16px",background:"#fafaf8",border:"1px solid "+theme.gray200,marginBottom:4}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <p style={{fontSize:12,fontWeight:700,color:theme.gray800}}>📦 Produtos Contratados <span style={{color:theme.red}}>*</span></p>
                  <span style={{fontSize:10,color:theme.gray400}}>{(editItem.produtosIds||[]).length} selecionado(s)</span>
                </div>
                <ProdutoSelector
                  produtos={produtos}
                  value={(()=>{
                    // Compat: se produtosIds vazio mas produtoId preenchido, usar produtoId
                    const ids = editItem.produtosIds||[];
                    if(ids.length>0) return ids;
                    if(editItem.produtoId) return [editItem.produtoId];
                    return [];
                  })()}
                  onChange={v=>setEditItem(prev=>({
                    ...prev,
                    produtosIds: v,
                    produtoId: v[0]||prev.produtoId||""
                  }))}
                />
              </div>
              <div style={{gridColumn:"1/-1",marginTop:10,marginBottom:8}}>
                <p className="serif" style={{fontSize:17,fontWeight:800,color:theme.gray900,paddingBottom:6,borderBottom:`2px solid ${theme.gray200}`}}>3 — Dados do Processo</p>
              </div>
              <FormField label="Fase">
                <Select value={editItem.fase} onChange={v=>setEditItem({...editItem,fase:v})} options={["Pré-processual","Administrativa","Processual","Recursal","Execução"]}/>
              </FormField>
              <FormField label="Tipo de Processo">
                <Select value={editItem.tipoProcesso} onChange={v=>setEditItem({...editItem,tipoProcesso:v})} options={["Administrativo","Judicial","Extrajudicial"]}/>
              </FormField>
              <FormField label="Status">
                <Select value={editItem.status} onChange={v=>setEditItem({...editItem,status:v})} options={STATUS_P}/>
              </FormField>
              <FormField label="Responsável">
                <Select value={editItem.responsavel} onChange={v=>setEditItem({...editItem,responsavel:v})} options={equipe.filter(e=>e.ativo!==false).map(e=>({value:e.id,label:e.nome}))} placeholder="Selecione..."/>
              </FormField>
              {/* ── Data Zero — Aprovação do Contrato ── */}
              <div style={{gridColumn:"1/-1",background:"#f0fdf4",borderRadius:8,padding:"12px 16px",border:"1px solid #bbf7d0",marginTop:4}}>
                <p style={{fontSize:12,fontWeight:700,color:theme.green,marginBottom:10}}>🗓️ Data Zero — Aprovação do Contrato</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px"}}>
                  <FormField label="Data de Aprovação">
                    <Input type="date" value={editItem.dataEntrada||hojeStr} onChange={v=>setEditItem({...editItem,dataEntrada:v})}/>
                  </FormField>
                  <FormField label="Hora da Aprovação (SLA)">
                    <Input type="time" value={editItem.horaEntrada||""} onChange={v=>setEditItem({...editItem,horaEntrada:v})} placeholder="00:00"/>
                  </FormField>
                </div>
                <p style={{fontSize:10,color:theme.gray500,marginTop:6}}>⏱ A contagem do SLA de 4h (Entrada e Triagem) inicia neste momento. Preencher a hora é obrigatório para controle preciso.</p>
              </div>
              <FormField label="Último Avanço">
                <Input type="date" value={editItem.ultimoAvanco} onChange={v=>setEditItem({...editItem,ultimoAvanco:v})}/>
              </FormField>
              <FormField label="Data de Conclusão Prevista">
                <Input type="date" value={editItem.prazo} onChange={v=>setEditItem({...editItem,prazo:v})}/>
              </FormField>
              <div style={{gridColumn:"1/-1",marginTop:10,marginBottom:8}}>
                <p className="serif" style={{fontSize:17,fontWeight:800,color:theme.gray900,paddingBottom:6,borderBottom:`2px solid ${theme.gray200}`}}>4 — Dados Financeiros</p>
              </div>
              <FormField label="Custo Operacional (R$)">
                <InputMoeda value={parseFloat(editItem.custoOperacional)||0} onChange={n=>setEditItem(prev=>({...prev,custoOperacional:n}))} placeholder="0,00"/>
              </FormField>
              {/* ── Honorário Inicial + Condições de Pagamento — SOMENTE para contratos já existentes (modelo legado). Contratos NOVOS usam a seção "Lançamentos de Valores do Contrato" abaixo. ── */}
              {!!editItem.id && (<>
              <FormField label="Valor Contratado (R$)">
                <InputMoeda value={parseFloat(editItem.valorContratado)||0} onChange={n=>setEditItem(prev=>({...prev,valorContratado:n}))} placeholder="0,00"/>
              </FormField>
              <FormField label="Vencimento dos Honorários">
                <Input type="date" value={editItem.dataVencimento} onChange={v=>setEditItem({...editItem,dataVencimento:v})}/>
              </FormField>
              <div style={{gridColumn:"1/-1",background:"#f0f9ff",borderRadius:8,padding:"14px 16px",border:"1px solid #bfdbfe"}}>
                <p style={{fontSize:12,fontWeight:700,color:theme.blue,marginBottom:10}}>💳 Condições de Pagamento</p>
                <div style={{display:"flex",gap:16,marginBottom:10}}>
                  {[["avista","À Vista"],["prazo","A Prazo (Parcelado)"]].map(([v,l])=>(
                    <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13,fontWeight:editItem.condicaoPagamento===v?700:400,color:editItem.condicaoPagamento===v?theme.blue:theme.gray600}}>
                      <input type="radio" name="condPgto" value={v}
                        checked={editItem.condicaoPagamento===v}
                        onChange={()=>setEditItem(prev=>({...prev,condicaoPagamento:v,numParcelas:v==="avista"?1:prev.numParcelas||2}))}
                        style={{accentColor:theme.blue}}/>
                      {l}
                    </label>
                  ))}
                </div>
                <div style={{display:"flex",gap:16,marginBottom:10}}>
                  {[["fixa","Data Fixa"],["porFase","Por Fase Processual"]].map(([v,l])=>(
                    <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,fontWeight:(editItem.formaDataPgto||"fixa")===v?700:400,color:(editItem.formaDataPgto||"fixa")===v?theme.blue:theme.gray600}}>
                      <input type="radio" name="formaDataPgto" value={v}
                        checked={(editItem.formaDataPgto||"fixa")===v}
                        onChange={()=>setEditItem(prev=>({...prev,formaDataPgto:v}))}
                        style={{accentColor:theme.blue}}/>
                      {l}
                    </label>
                  ))}
                </div>
                {editItem.formaDataPgto==="porFase"&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px",marginBottom:10}}>
                    <FormField label="Momento">
                      <Select value={editItem.faseMomento||""} onChange={v=>setEditItem(prev=>({...prev,faseMomento:v}))} options={GATILHOS_FIN} placeholder="Selecione..."/>
                    </FormField>
                    <FormField label="Descrição da Fase">
                      <Input value={editItem.faseDescricao||""} onChange={v=>setEditItem(prev=>({...prev,faseDescricao:v}))} placeholder="Ex: audiência de instrução"/>
                    </FormField>
                    <p style={{gridColumn:"1/-1",fontSize:10,color:theme.gray500,marginTop:-4}}>A data abaixo (fixa ou 1ª parcela) passa a ser tratada como "data estimada" — pode ser alterada sem afetar o momento processual definido acima.</p>
                  </div>
                )}
                {editItem.condicaoPagamento==="prazo"&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px"}}>
                    <FormField label="Número de Parcelas">
                      <input type="number" min="2" max="120"
                        value={editItem.numParcelas||2}
                        onChange={e=>setEditItem(prev=>({...prev,numParcelas:parseInt(e.target.value)||2}))}
                        style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}/>
                    </FormField>
                    <FormField label="Vencimento da 1ª Parcela">
                      <Input type="date" value={editItem.vencPrimeiraParcela||""}
                        onChange={v=>setEditItem(prev=>({...prev,vencPrimeiraParcela:v,dataVencimento:v}))}/>
                    </FormField>
                    {(editItem.numParcelas>=2&&editItem.vencPrimeiraParcela&&editItem.valorContratado>0)&&(()=>{
                      const vlrParc = parseFloat(editItem.valorContratado)/parseInt(editItem.numParcelas||1);
                      return (
                        <div style={{gridColumn:"1/-1",marginTop:6,padding:"8px 10px",background:"#dbeafe",borderRadius:6,fontSize:11,color:theme.blue}}>
                          📋 {editItem.numParcelas}x de {`R$ ${vlrParc.toLocaleString("pt-BR",{minimumFractionDigits:2})}`} — 1ª em {new Date(editItem.vencPrimeiraParcela+"T12:00").toLocaleDateString("pt-BR")}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              </>)}

              {/* ── Dados Financeiros do Contrato — Componentes Adicionais ── */}
              <div style={{gridColumn:"1/-1",background:"#faf5ff",borderRadius:8,padding:"14px 16px",border:"1px solid #e9d5ff",marginTop:4}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <p style={{fontSize:12,fontWeight:700,color:"#7c3aed"}}>💼 Lançamentos de Valores do Contrato</p>
                  <button onClick={()=>setEditItem(prev=>({...prev,componentesFinanceiros:[...(prev.componentesFinanceiros||[]),{...EMPTY_COMPONENTE_V2,id:"CFIN"+Date.now()}]}))}
                    style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:"1px solid #7c3aed",background:"#faf5ff",color:"#7c3aed",cursor:"pointer",fontWeight:600}}>+ Adicionar Lançamento</button>
                </div>
                <p style={{fontSize:10,color:theme.gray500,marginBottom:10}}>O valor total do contrato é a somatória de todos os lançamentos abaixo (mais o Honorário Inicial acima, se houver). Ao SALVAR um contrato NOVO, cada lançamento ativo gera automaticamente um registro em Receitas: categorias de valor fechado (1-Valor Fixo, 4-Honorários fixos, 5-Outros) viram "A Receber" normal; categorias sem valor fechado (2-Valor a Definir, 3-Porcentagem, 4-Honorários variáveis) viram "Estimado (Projeção)" — não somam como receita real até apuradas.</p>
                {(editItem.componentesFinanceiros||[]).length===0&&<p style={{fontSize:11,color:theme.gray400,textAlign:"center",padding:"6px 0"}}>Nenhum lançamento adicional. Clique em "+ Adicionar Lançamento" para registrar Valor Fixo adicional, Valor a Definir, Porcentagem, Honorários Adicionais ou Outros (Permuta/Bens).</p>}
                {(editItem.componentesFinanceiros||[]).length>0 && (()=>{
                  const ativos = (editItem.componentesFinanceiros||[]).filter(c=>c.ativo!==false);
                  const valorFechado = c => {
                    if(c.categoria==="valorFixo") return parseFloat(c.vfValor)||0;
                    if(c.categoria==="honorariosAdicionais") return c.haTipo==="fixo" ? (parseFloat(c.haValorFixo)||0) : 0;
                    if(c.categoria==="outros") return parseFloat(c.outrosValor)||0;
                    return 0;
                  };
                  const valorEstimadoDe = c => {
                    if(c.categoria==="valorADefinir") return parseFloat(c.vadValorEstimado)||0;
                    if(c.categoria==="porcentagem") return parseFloat(c.pctValorEstimado)||0;
                    if(c.categoria==="honorariosAdicionais"&&c.haTipo==="variavel") return parseFloat(c.haValorEstimado)||0;
                    return 0;
                  };
                  const fechadosAtivos = ativos.filter(c=>valorFechado(c)>0);
                  const estimadosAtivos = ativos.filter(c=>valorEstimadoDe(c)>0);
                  const somaComponentesFixos = ativos.reduce((s,c)=>s+valorFechado(c),0);
                  const somaEstimadaTotal = ativos.reduce((s,c)=>s+valorEstimadoDe(c),0);
                  const honorarioInicial = parseFloat(editItem.valorContratado)||0;
                  const totalAcumulado = honorarioInicial + somaComponentesFixos;
                  return (
                    <div style={{background:"#fff",border:"1px solid #e9d5ff",borderRadius:7,padding:"10px 14px",marginBottom:12}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:theme.gray600,marginBottom:2}}>
                        <span>Honorário Inicial (Valor Contratado)</span>
                        <span>{`R$ ${honorarioInicial.toLocaleString("pt-BR",{minimumFractionDigits:2})}`}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:theme.gray600,marginBottom:6}}>
                        <span>+ Lançamentos de valor fechado ({fechadosAtivos.length})</span>
                        <span>{`R$ ${somaComponentesFixos.toLocaleString("pt-BR",{minimumFractionDigits:2})}`}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:700,color:"#7c3aed",borderTop:"1px solid #e9d5ff",paddingTop:6}}>
                        <span>= Total Acumulado do Contrato</span>
                        <span>{`R$ ${totalAcumulado.toLocaleString("pt-BR",{minimumFractionDigits:2})}`}</span>
                      </div>
                      {estimadosAtivos.length>0 && (
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:12,fontWeight:600,color:"#b45309",marginTop:6,paddingTop:6,borderTop:"1px dashed #fde68a"}}>
                          <span>≈ Valor Estimado Total ({estimadosAtivos.length} — Projeção, não é receita)</span>
                          <span>{`R$ ${somaEstimadaTotal.toLocaleString("pt-BR",{minimumFractionDigits:2})}`}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {(editItem.componentesFinanceiros||[]).map((cf,ci)=>{
                  const upd = (patch) => setEditItem(prev=>({...prev,componentesFinanceiros:prev.componentesFinanceiros.map((c,i)=>i===ci?{...c,...patch}:c)}));
                  const numInput = (val,onChange,extra={}) => <input type="number" min="0" step="0.5" value={val||""} onChange={e=>onChange(e.target.value)} style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}} {...extra}/>;
                  return (
                    <div key={cf.id||ci} style={{padding:"12px 14px",background:"#fff",borderRadius:7,border:`1px solid ${theme.gray200}`,marginBottom:10}}>
                      <div style={{display:"grid",gridTemplateColumns:"1.3fr 1fr auto",gap:"0 12px",alignItems:"end",marginBottom:8}}>
                        <FormField label="Categoria"><Select value={cf.categoria} onChange={v=>upd({categoria:v})} options={CATEGORIAS_COMPONENTE}/></FormField>
                        <FormField label="Ativo">
                          <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13,padding:"9px 0"}}>
                            <input type="checkbox" checked={cf.ativo!==false} onChange={e=>upd({ativo:e.target.checked})}/> {cf.ativo!==false?"Sim":"Não (pausado)"}
                          </label>
                        </FormField>
                        <button onClick={()=>setEditItem(prev=>({...prev,componentesFinanceiros:prev.componentesFinanceiros.filter((_,i)=>i!==ci)}))} style={{fontSize:16,color:theme.red,background:"none",border:"none",cursor:"pointer",padding:"4px 6px",marginBottom:2}}>×</button>
                      </div>
                      <FormField label="Descrição"><Input value={cf.descricao||""} onChange={v=>upd({descricao:v})} placeholder="Ex: Honorários de êxito na fase recursal"/></FormField>

                      {/* ── 1) VALOR FIXO ── */}
                      {cf.categoria==="valorFixo" && (
                        <div style={{marginTop:10,paddingTop:10,borderTop:`1px dashed ${theme.gray200}`}}>
                          <div style={{display:"flex",gap:16,marginBottom:8}}>
                            {[["avista","À Vista"],["parcelado","Parcelado"]].map(([v,l])=>(
                              <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,fontWeight:cf.vfModalidade===v?700:400}}>
                                <input type="radio" name={"vfMod"+ci} checked={cf.vfModalidade===v} onChange={()=>upd({vfModalidade:v})}/>{l}
                              </label>
                            ))}
                          </div>
                          <div style={{display:"flex",gap:16,marginBottom:8}}>
                            {[["fixa","Data Fixa"],["porFase","Por Fase"]].map(([v,l])=>(
                              <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,fontWeight:cf.vfFormaData===v?700:400}}>
                                <input type="radio" name={"vfData"+ci} checked={cf.vfFormaData===v} onChange={()=>upd({vfFormaData:v})}/>{l}
                              </label>
                            ))}
                          </div>
                          {cf.vfFormaData==="porFase" && (
                            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 12px",marginBottom:8}}>
                              <FormField label="Momento"><Select value={cf.vfMomento} onChange={v=>upd({vfMomento:v})} options={MOMENTOS_FASE} placeholder="Selecione..."/></FormField>
                              <FormField label="Descrição da Fase"><Input value={cf.vfFaseDescricao||""} onChange={v=>upd({vfFaseDescricao:v})}/></FormField>
                            </div>
                          )}
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 12px"}}>
                            <FormField label="Valor (R$)"><InputMoeda value={parseFloat(cf.vfValor)||0} onChange={n=>upd({vfValor:n})} placeholder="0,00"/></FormField>
                            <FormField label={cf.vfFormaData==="porFase"?"Data Estimada":"Data Fixa"}>
                              <Input type="date" value={(cf.vfFormaData==="porFase"?cf.vfDataEstimada:cf.vfDataFixa)||""} onChange={v=>upd(cf.vfFormaData==="porFase"?{vfDataEstimada:v}:{vfDataFixa:v})}/>
                            </FormField>
                          </div>
                          {cf.vfModalidade==="parcelado" && (
                            <div style={{marginTop:8}}>
                              <div style={{display:"flex",gap:16,marginBottom:8}}>
                                {[["parcelasIguais","Parcelas Iguais"],["entradaManual","Entrada Manual (linhas com carência)"]].map(([v,l])=>(
                                  <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,fontWeight:cf.vfParceladoTipo===v?700:400}}>
                                    <input type="radio" name={"vfParc"+ci} checked={cf.vfParceladoTipo===v} onChange={()=>upd({vfParceladoTipo:v})}/>{l}
                                  </label>
                                ))}
                              </div>
                              {cf.vfParceladoTipo==="parcelasIguais" ? (
                                <>
                                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 12px"}}>
                                    <FormField label="Número de Parcelas">{numInput(cf.vfNumParcelas,v=>upd({vfNumParcelas:parseInt(v)||2}),{min:2,step:1})}</FormField>
                                    <FormField label="Vencimento da 1ª Parcela"><Input type="date" value={cf.vfVencPrimeira||""} onChange={v=>upd({vfVencPrimeira:v})}/></FormField>
                                  </div>
                                  <p style={{fontSize:10,color:theme.gray500,marginTop:4}}>Gera {cf.vfNumParcelas||2} lançamentos iguais em Receitas, vencendo a cada 30 dias a partir da 1ª parcela.</p>
                                </>
                              ) : (
                                <div>
                                  {(cf.vfParcelasManual||[]).map((linha,li)=>(
                                    <div key={li} style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:"0 10px",alignItems:"end",marginBottom:6}}>
                                      <FormField label={`Parcela ${li+1} — Valor (R$)`}>
                                        <InputMoeda value={parseFloat(linha.valor)||0} onChange={n=>upd({vfParcelasManual:cf.vfParcelasManual.map((x,i)=>i===li?{...x,valor:n}:x)})} placeholder="0,00"/>
                                      </FormField>
                                      <FormField label="Vencimento">
                                        <Input type="date" value={linha.vencimento||""} onChange={v=>upd({vfParcelasManual:cf.vfParcelasManual.map((x,i)=>i===li?{...x,vencimento:v}:x)})}/>
                                      </FormField>
                                      <button onClick={()=>upd({vfParcelasManual:cf.vfParcelasManual.filter((_,i)=>i!==li)})} style={{fontSize:16,color:theme.red,background:"none",border:"none",cursor:"pointer",padding:"4px 6px",marginBottom:2}}>×</button>
                                    </div>
                                  ))}
                                  <button onClick={()=>upd({vfParcelasManual:[...(cf.vfParcelasManual||[]),{valor:"",vencimento:""}]})}
                                    style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:"1px solid #7c3aed",background:"#faf5ff",color:"#7c3aed",cursor:"pointer",fontWeight:600,marginTop:2}}>+ Adicionar Linha (parcela/carência)</button>
                                  <p style={{fontSize:10,color:theme.gray500,marginTop:6}}>Cada linha vira um lançamento próprio em Receitas, com o valor e vencimento exatos — use para carência, entrada diferente do restante, ou datas que não seguem um ciclo fixo.</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── 2) VALOR A DEFINIR (Previdência) ── */}
                      {cf.categoria==="valorADefinir" && (
                        <div style={{marginTop:10,paddingTop:10,borderTop:`1px dashed ${theme.gray200}`}}>
                          <p style={{fontSize:11,fontWeight:600,color:theme.gray700,marginBottom:6}}>Previdência</p>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 12px",marginBottom:8}}>
                            <FormField label="Quantidade de RMA's">{numInput(cf.vadQtdRMAs,v=>upd({vadQtdRMAs:v}),{step:1})}</FormField>
                            <FormField label="Momento">
                              <Select value={cf.vadMomento} onChange={v=>upd({vadMomento:v})} options={[{value:"implementacao",label:"Implementação do Benefício"},{value:"recebimentoAcumulado",label:"Recebimento Acumulado"}]}/>
                            </FormField>
                          </div>
                          {cf.vadMomento==="implementacao" && (
                            <div style={{display:"flex",gap:16,marginBottom:8}}>
                              {[["avista","À Vista"],["parcelado","Parcelado"]].map(([v,l])=>(
                                <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,fontWeight:cf.vadImplForma===v?700:400}}>
                                  <input type="radio" name={"vadImpl"+ci} checked={cf.vadImplForma===v} onChange={()=>upd({vadImplForma:v})}/>{l}
                                </label>
                              ))}
                              {cf.vadImplForma==="parcelado" && (
                                <Select value={cf.vadImplParceladoTipo} onChange={v=>upd({vadImplParceladoTipo:v})} options={[{value:"parcelasIguais",label:"Parcelas Iguais"},{value:"entradaManual",label:"Entrada Manual"}]}/>
                              )}
                              <FormField label="Data de Início"><Input type="date" value={cf.vadImplDataInicio||""} onChange={v=>upd({vadImplDataInicio:v})}/></FormField>
                            </div>
                          )}
                          {cf.vadMomento==="recebimentoAcumulado" && (
                            <div style={{display:"flex",gap:16,marginBottom:8,alignItems:"end"}}>
                              {[["avista","À Vista"],["dataPagamento","Data de Pagamento"]].map(([v,l])=>(
                                <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,fontWeight:cf.vadRecForma===v?700:400}}>
                                  <input type="radio" name={"vadRec"+ci} checked={cf.vadRecForma===v} onChange={()=>upd({vadRecForma:v})}/>{l}
                                </label>
                              ))}
                              {cf.vadRecForma==="dataPagamento" && (<>
                                <Select value={cf.vadRecDataTipo} onChange={v=>upd({vadRecDataTipo:v})} options={[{value:"fixa",label:"Fixa"},{value:"momentoRecebimento",label:"Momento do Recebimento"}]}/>
                                {cf.vadRecDataTipo==="fixa" && <FormField label="Data"><Input type="date" value={cf.vadRecDataFixa||""} onChange={v=>upd({vadRecDataFixa:v})}/></FormField>}
                              </>)}
                            </div>
                          )}
                          <FormField label="Valor Estimado (R$) — Projeção de Faturamento">
                            <InputMoeda value={parseFloat(cf.vadValorEstimado)||0} onChange={n=>upd({vadValorEstimado:n})} placeholder="0,00"/>
                          </FormField>
                        </div>
                      )}

                      {/* ── 3) PORCENTAGEM ── */}
                      {cf.categoria==="porcentagem" && (
                        <div style={{marginTop:10,paddingTop:10,borderTop:`1px dashed ${theme.gray200}`}}>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 12px",marginBottom:8}}>
                            <FormField label="Percentual (%)">{numInput(cf.pctPercentual,v=>upd({pctPercentual:v}),{max:100,step:0.5})}</FormField>
                            <FormField label="Momento"><Select value={cf.pctMomento} onChange={v=>upd({pctMomento:v})} options={MOMENTOS_PORCENTAGEM} placeholder="Selecione..."/></FormField>
                          </div>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 12px",marginBottom:8}}>
                            <FormField label="Base"><Select value={cf.pctBase} onChange={v=>upd({pctBase:v})} options={BASES_PORCENTAGEM} placeholder="Selecione..."/></FormField>
                            {cf.pctBase==="Outro" && <FormField label="Descrição da Base"><Input value={cf.pctBaseOutro||""} onChange={v=>upd({pctBaseOutro:v})}/></FormField>}
                          </div>
                          <FormField label="Valor Estimado (R$) — Projeção de Faturamento">
                            <InputMoeda value={parseFloat(cf.pctValorEstimado)||0} onChange={n=>upd({pctValorEstimado:n})} placeholder="0,00"/>
                          </FormField>
                        </div>
                      )}

                      {/* ── 4) HONORÁRIOS ADICIONAIS ── */}
                      {cf.categoria==="honorariosAdicionais" && (
                        <div style={{marginTop:10,paddingTop:10,borderTop:`1px dashed ${theme.gray200}`}}>
                          <p style={{fontSize:11,fontWeight:600,color:theme.gray700,marginBottom:6}}>Honorários Anuais</p>
                          <div style={{display:"flex",gap:16,marginBottom:8}}>
                            {[["fixo","Valor Fixo"],["variavel","Variável"]].map(([v,l])=>(
                              <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,fontWeight:cf.haTipo===v?700:400}}>
                                <input type="radio" name={"haTipo"+ci} checked={cf.haTipo===v} onChange={()=>upd({haTipo:v})}/>{l}
                              </label>
                            ))}
                          </div>
                          {cf.haTipo==="fixo" && (
                            <FormField label="Valor (R$)"><InputMoeda value={parseFloat(cf.haValorFixo)||0} onChange={n=>upd({haValorFixo:n})} placeholder="0,00"/></FormField>
                          )}
                          {cf.haTipo==="variavel" && (
                            <>
                              <div style={{display:"flex",gap:16,marginBottom:8}}>
                                {[["porHora","Por Hora"],["porcentagem","Porcentagem"]].map(([v,l])=>(
                                  <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,fontWeight:cf.haVariavelTipo===v?700:400}}>
                                    <input type="radio" name={"haVar"+ci} checked={cf.haVariavelTipo===v} onChange={()=>upd({haVariavelTipo:v})}/>{l}
                                  </label>
                                ))}
                              </div>
                              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 12px",marginBottom:8}}>
                                {cf.haVariavelTipo==="porHora"
                                  ? <FormField label="Valor por Hora (R$)"><InputMoeda value={parseFloat(cf.haValorHora)||0} onChange={n=>upd({haValorHora:n})} placeholder="0,00"/></FormField>
                                  : <FormField label="Percentual (%)">{numInput(cf.haPercentual,v=>upd({haPercentual:v}),{max:100,step:0.5})}</FormField>}
                                {cf.haVariavelTipo==="porcentagem" && <FormField label="Base"><Select value={cf.haBase} onChange={v=>upd({haBase:v})} options={BASES_PORCENTAGEM} placeholder="Selecione..."/></FormField>}
                              </div>
                              <FormField label="Valor Estimado (R$) — Projeção de Faturamento">
                                <InputMoeda value={parseFloat(cf.haValorEstimado)||0} onChange={n=>upd({haValorEstimado:n})} placeholder="0,00"/>
                              </FormField>
                            </>
                          )}
                          <div style={{display:"flex",gap:16,marginTop:8}}>
                            {[["fixa","Data Fixa"],["protocolo","Data do Protocolo"]].map(([v,l])=>(
                              <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,fontWeight:cf.haData===v?700:400}}>
                                <input type="radio" name={"haData"+ci} checked={cf.haData===v} onChange={()=>upd({haData:v})}/>{l}
                              </label>
                            ))}
                          </div>
                          {cf.haData==="fixa" && <FormField label="Data"><Input type="date" value={cf.haDataFixa||""} onChange={v=>upd({haDataFixa:v})}/></FormField>}
                        </div>
                      )}

                      {/* ── 5) OUTROS ── */}
                      {cf.categoria==="outros" && (
                        <div style={{marginTop:10,paddingTop:10,borderTop:`1px dashed ${theme.gray200}`}}>
                          <div style={{display:"flex",gap:16,marginBottom:8}}>
                            {[["permuta","Permuta"],["bens","Bens"]].map(([v,l])=>(
                              <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,fontWeight:cf.outrosTipo===v?700:400}}>
                                <input type="radio" name={"outrosTipo"+ci} checked={cf.outrosTipo===v} onChange={()=>upd({outrosTipo:v})}/>{l}
                              </label>
                            ))}
                          </div>
                          <FormField label="Valor (R$)"><InputMoeda value={parseFloat(cf.outrosValor)||0} onChange={n=>upd({outrosValor:n})} placeholder="0,00"/></FormField>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <FormField label="Data de Conclusão Real">
                <Input type="date" value={editItem.dataConclusao||''} onChange={v=>setEditItem({...editItem,dataConclusao:v})}/>
              </FormField>
              <div style={{gridColumn:"1/-1",marginTop:10,marginBottom:8}}>
                <p className="serif" style={{fontSize:17,fontWeight:800,color:theme.gray900,paddingBottom:6,borderBottom:`2px solid ${theme.gray200}`}}>5 — Parceria</p>
              </div>
              {/* ── Parceria ── */}
              <div style={{gridColumn:"1/-1",background:editItem.temParceria?"#fef9ec":"#f9fafb",border:`1px solid ${editItem.temParceria?"#fde68a":theme.gray100}`,borderRadius:8,padding:"12px 14px",marginTop:4}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:editItem.temParceria?12:0}}>
                  <span style={{fontSize:12,fontWeight:600,color:theme.gray700}}>🤝 Parceria neste Contrato:</span>
                  <label style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:13}}>
                    <input type="radio" name="temParceria" value="nao"
                      checked={!editItem.temParceria}
                      onChange={()=>setEditItem(prev=>({...prev,temParceria:false,parceiraIdContrato:"",percComissaoContrato:0,valorComissao:0}))}/>
                    Não
                  </label>
                  <label style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:13}}>
                    <input type="radio" name="temParceria" value="sim"
                      checked={!!editItem.temParceria}
                      onChange={()=>{
                        const cli = clientes.find(cl=>cl.id===editItem.clienteId);
                        const parcId = cli?.parceiraId||"";
                        const parc = parcerias.find(p=>p.id===parcId);
                        const perc = parc ? (parseFloat(parc.comissao)||0) : 0;
                        const val = parseFloat(((editItem.valorContratado||0)*perc/100).toFixed(2));
                        setEditItem(prev=>({...prev,temParceria:true,parceiraIdContrato:parcId,percComissaoContrato:perc,valorComissao:val}));
                      }}/>
                    Sim
                  </label>
                </div>
                {editItem.temParceria&&(
                  <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:10}}>
                    <FormField label="Parceiro">
                      <select value={editItem.parceiraIdContrato||""}
                        onChange={e=>{
                          const parc=parcerias.find(p=>p.id===e.target.value);
                          const perc=parc?(parseFloat(parc.comissao)||0):0;
                          const val=parseFloat(((editItem.valorContratado||0)*perc/100).toFixed(2));
                          setEditItem(prev=>({...prev,parceiraIdContrato:e.target.value,percComissaoContrato:perc,valorComissao:val}));
                        }}
                        style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",background:"#fff"}}>
                        <option value="">— Selecione —</option>
                        {parcerias.filter(p=>p.status==="Ativo").map(p=>(
                          <option key={p.id} value={p.id}>{p.nome}{p.comissao?` — ${p.comissao}%`:""}</option>
                        ))}
                      </select>
                      {!editItem.parceiraIdContrato&&<p style={{fontSize:11,color:theme.red,marginTop:3}}>⚠️ Selecione o parceiro</p>}
                    </FormField>
                    <FormField label="% Comissão">
                      <input type="number" min="0" max="100" step="0.5"
                        value={editItem.percComissaoContrato||""}
                        onChange={e=>{
                          const perc=parseFloat(e.target.value)||0;
                          const val=parseFloat(((editItem.valorContratado||0)*perc/100).toFixed(2));
                          setEditItem(prev=>({...prev,percComissaoContrato:perc,valorComissao:val}));
                        }}
                        style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
                    </FormField>
                    <FormField label="Valor Repasse (R$)">
                      <InputMoeda value={editItem.valorComissao||0}
                        onChange={n=>setEditItem(prev=>({...prev,valorComissao:n}))}
                        placeholder="0,00"/>
                    </FormField>
                  </div>
                )}
              </div>
              {(produtos.find(p=>p.id===editItem.produtoId)?.tipo)==="pacote_horas"&&(
                <FormField label="Horas do Pacote">
                  <input type="number" min="0" step="0.5"
                    value={editItem.horasPacote||""}
                    onChange={e=>setEditItem(prev=>({...prev,horasPacote:parseFloat(e.target.value)||0}))}
                    placeholder="Ex: 10"
                    style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
                </FormField>
              )}
              {(editItem.area==="Tributário"||(produtos.find(p=>p.id===editItem.produtoId)?.area)==="Tributário")&&(
                <>
                  <FormField label="Valor do Crédito Apurado (R$)">
                    <InputMoeda value={parseFloat(editItem.valorCreditoApurado)||0}
                      onChange={n=>{
                        const perc=parseFloat(editItem.percHonorarios)||0;
                        setEditItem(prev=>({...prev,
                          valorCreditoApurado:n,
                          valorContratado:perc>0?parseFloat((n*perc/100).toFixed(2)):prev.valorContratado
                        }));
                      }}
                      placeholder="0,00"/>
                  </FormField>
                  <FormField label="Honorários sobre o Crédito (%)">
                    <Input type="number" value={editItem.percHonorarios||""}
                      onChange={v=>{
                        const perc=parseFloat(v)||0;
                        const cred=parseFloat(editItem.valorCreditoApurado)||0;
                        setEditItem(prev=>({...prev,
                          percHonorarios:v,
                          valorContratado:cred>0&&perc>0?parseFloat((cred*perc/100).toFixed(2)):prev.valorContratado
                        }));
                      }}
                      placeholder="ex: 30"/>
                  </FormField>
                </>
              )}
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Fatores de Risco">
                  <div style={{display:"flex",gap:20,flexWrap:"wrap",padding:"8px 0"}}>
                    {[["teseNova","Tese Nova"],["jurisprudenciaInstavel","Jurisprudência Instável"],["valorElevado","Valor Elevado"],["dependePericia","Depende de Perícia"],["documentoIncompleto","Documento Incompleto"]].map(([k,lbl])=>(
                      <label key={k} style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:theme.gray700,cursor:"pointer"}}>
                        <input type="checkbox" checked={!!editItem[k]} onChange={e=>setEditItem({...editItem,[k]:e.target.checked})}/>{lbl}
                      </label>
                    ))}
                  </div>
                </FormField>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Observações">
                  <Textarea value={editItem.obs} onChange={v=>setEditItem({...editItem,obs:v})} placeholder="Notas sobre o processo..." rows={3}/>
                </FormField>
              </div>

              {/* ── Objetivo Específico da Procuração ── */}
              <div style={{gridColumn:"1/-1",background:"#fffbeb",borderRadius:8,padding:"12px 16px",border:"1px solid #fde68a",marginBottom:16}}>
                <p style={{fontSize:12,fontWeight:700,color:"#92400e",marginBottom:6}}>📜 Objetivo Específico da Procuração</p>
                <p style={{fontSize:11,color:"#92400e",marginBottom:10,opacity:.85}}>
                  Complementa a cláusula de poderes ao final do texto da procuração — ex: "representá-lo(a) administrativamente e judicialmente em processo de execução fiscal nº 0001234-56.2026.4.03.6100, perante a PGFN".
                </p>
                <Textarea value={editItem.objetivoEspecifico} onChange={v=>setEditItem({...editItem,objetivoEspecifico:v})}
                  placeholder='Ex: representá-lo(a) administrativamente e judicialmente perante a PGFN / Receita Federal, no processo de recuperação de créditos tributários objeto deste contrato.' rows={3}/>
              </div>

              {/* ── Data de Nascimento + Idade ── */}
              <div style={{gridColumn:"1/-1",background:"#faf5ff",borderRadius:8,padding:"12px 16px",border:"1px solid #e9d5ff"}}>
                <p style={{fontSize:12,fontWeight:700,color:"#7c3aed",marginBottom:10}}>🎂 Dados Pessoais — Prioridade Processual</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"0 16px",alignItems:"end"}}>
                  <FormField label="Data de Nascimento">
                    <Input type="date" value={editItem.dataNascimento||""} onChange={v=>setEditItem({...editItem,dataNascimento:v})}/>
                  </FormField>
                  {editItem.dataNascimento&&(()=>{
                    const hoje_=new Date(); const nasc=new Date(editItem.dataNascimento+"T12:00");
                    const idade=hoje_.getFullYear()-nasc.getFullYear()-((hoje_.getMonth()<nasc.getMonth()||(hoje_.getMonth()===nasc.getMonth()&&hoje_.getDate()<nasc.getDate()))?1:0);
                    return <>
                      <div style={{paddingBottom:8}}>
                        <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>Idade</p>
                        <p style={{fontSize:18,fontWeight:700,color:idade>=65?"#7c3aed":theme.gray800}}>{idade} anos</p>
                      </div>
                      {idade>=65&&<div style={{padding:"8px 12px",borderRadius:7,background:"#f5f3ff",border:"1px solid #c4b5fd",paddingBottom:10}}>
                        <p style={{fontSize:11,fontWeight:700,color:"#7c3aed",margin:0}}>⚡ Prioridade Processual</p>
                        <p style={{fontSize:10,color:"#6d28d9",margin:"3px 0 0"}}>Acima de 65 anos. Verificar tramitação prioritária.</p>
                      </div>}
                    </>;
                  })()}
                </div>
              </div>

              {/* ── Faturamento Anual ── */}
              <div style={{gridColumn:"1/-1",background:"#f0fdf4",borderRadius:8,padding:"14px 16px",border:"1px solid #bbf7d0"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <p style={{fontSize:12,fontWeight:700,color:theme.green}}>💰 Faturamento Anual</p>
                  <button onClick={()=>setEditItem(prev=>({...prev,faturamentos:[...(prev.faturamentos||[]),{id:"FAT"+Date.now(),ano:String(new Date().getFullYear()),valor:""}]}))}
                    style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:"1px solid "+theme.green,background:"#f0fdf4",color:theme.green,cursor:"pointer",fontWeight:600}}>+ Adicionar Ano</button>
                </div>
                {(editItem.faturamentos||[]).length===0&&<p style={{fontSize:11,color:theme.gray400,textAlign:"center",padding:"6px 0"}}>Clique em "+ Adicionar Ano" para inserir os últimos 5 anos.</p>}
                {(editItem.faturamentos||[]).map((fat,fi)=>(
                  <div key={fat.id||fi} style={{display:"grid",gridTemplateColumns:"120px 1fr 32px",gap:"0 10px",marginBottom:8,alignItems:"end"}}>
                    <FormField label="Ano"><Input value={fat.ano||""} placeholder="2023" onChange={v=>setEditItem(prev=>({...prev,faturamentos:prev.faturamentos.map((f,i)=>i===fi?{...f,ano:v}:f)}))}/></FormField>
                    <FormField label="Valor (R$)"><Input value={fat.valor||""} placeholder="0,00" onChange={v=>{
                      const limpo=v.replace(/[^\d]/g,""); const num=parseInt(limpo||"0",10)/100;
                      const mask=num.toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2});
                      setEditItem(prev=>({...prev,faturamentos:prev.faturamentos.map((f,i)=>i===fi?{...f,valor:mask}:f)}));
                    }}/></FormField>
                    <button onClick={()=>setEditItem(prev=>({...prev,faturamentos:prev.faturamentos.filter((_,i)=>i!==fi)}))} style={{fontSize:16,color:theme.red,background:"none",border:"none",cursor:"pointer",padding:"4px 6px",marginBottom:2}}>×</button>
                  </div>
                ))}
              </div>

              {/* ── Regimes Tributários ── */}
              <div style={{gridColumn:"1/-1",background:"#f0f9ff",borderRadius:8,padding:"14px 16px",border:"1px solid #bfdbfe"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <p style={{fontSize:12,fontWeight:700,color:theme.blue}}>📊 Regimes Tributários (por período)</p>
                  <button onClick={()=>setEditItem(prev=>({...prev,regimes:[...(prev.regimes||[]),{id:"REG"+Date.now(),tipo:"Simples Nacional",dataInicio:"",dataFim:""}]}))}
                    style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:`1px solid ${theme.blue}`,background:"#eff6ff",color:theme.blue,cursor:"pointer",fontWeight:600}}>+ Adicionar Regime</button>
                </div>
                {(editItem.regimes||[]).length===0&&<p style={{fontSize:11,color:theme.gray400,textAlign:"center",padding:"8px 0"}}>Clique em "+ Adicionar Regime" para registrar o histórico tributário.</p>}
                {(editItem.regimes||[]).map((reg,ri)=>(
                  <div key={reg.id||ri} style={{display:"grid",gridTemplateColumns:"1.5fr 1fr 1fr auto",gap:"0 12px",marginBottom:8,alignItems:"end",padding:"10px 12px",background:"#fff",borderRadius:7,border:`1px solid ${theme.gray200}`}}>
                    <FormField label="Regime"><Select value={reg.tipo} onChange={v=>setEditItem(prev=>({...prev,regimes:prev.regimes.map((r,i)=>i===ri?{...r,tipo:v}:r)}))} options={["Simples Nacional","Lucro Presumido","Lucro Real","MEI"]} placeholder="Selecione..."/></FormField>
                    <FormField label="Data Início"><Input type="date" value={reg.dataInicio||""} onChange={v=>setEditItem(prev=>({...prev,regimes:prev.regimes.map((r,i)=>i===ri?{...r,dataInicio:v}:r)}))}/></FormField>
                    <FormField label="Data Fim"><Input type="date" value={reg.dataFim||""} onChange={v=>setEditItem(prev=>({...prev,regimes:prev.regimes.map((r,i)=>i===ri?{...r,dataFim:v}:r)}))}/></FormField>
                    <button onClick={()=>setEditItem(prev=>({...prev,regimes:prev.regimes.filter((_,i)=>i!==ri)}))} style={{fontSize:14,color:theme.red,background:"none",border:"none",cursor:"pointer",padding:"4px 8px",alignSelf:"center",marginTop:16}}>×</button>
                  </div>
                ))}
              </div>

              {/* ── Atividades da Empresa ── */}
              <div style={{gridColumn:"1/-1",background:"#f0fdf4",borderRadius:8,padding:"14px 16px",border:"1px solid #bbf7d0"}}>
                <p style={{fontSize:12,fontWeight:700,color:theme.green,marginBottom:10}}>🏭 Atividades da Empresa</p>
                <FormField label="Atividade Principal (CNAE)">
                  <Input value={editItem.atividadePrincipal||""} onChange={v=>setEditItem(prev=>({...prev,atividadePrincipal:v}))} placeholder="Ex: 6911-7/01 — Atividades jurídicas..."/>
                </FormField>
                <div style={{marginTop:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <p style={{fontSize:11,color:theme.gray600,fontWeight:600}}>Atividades Secundárias</p>
                    <button onClick={()=>setEditItem(prev=>({...prev,atividadesSecundarias:[...(prev.atividadesSecundarias||[]),""]}))}
                      style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:`1px solid ${theme.green}`,background:"#f0fdf4",color:theme.green,cursor:"pointer",fontWeight:600}}>+ Adicionar Atividade</button>
                  </div>
                  {(editItem.atividadesSecundarias||[]).length===0&&<p style={{fontSize:11,color:theme.gray400}}>Nenhuma atividade secundária. Clique em "+ Adicionar Atividade".</p>}
                  {(editItem.atividadesSecundarias||[]).map((atv,ai)=>(
                    <div key={ai} style={{display:"flex",gap:8,marginBottom:6,alignItems:"center"}}>
                      <Input value={atv} onChange={v=>setEditItem(prev=>({...prev,atividadesSecundarias:prev.atividadesSecundarias.map((a,i)=>i===ai?v:a)}))} placeholder={`Atividade secundária ${ai+1}`} style={{flex:1}}/>
                      <button onClick={()=>setEditItem(prev=>({...prev,atividadesSecundarias:prev.atividadesSecundarias.filter((_,i)=>i!==ai)}))} style={{fontSize:14,color:theme.red,background:"none",border:"none",cursor:"pointer",padding:"4px 8px"}}>×</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* ── Processos Vinculados ── */}
            <SecaoProcessosVinculados
              processos={processos}
              clientes={clientes}
              clienteId={editItem.clienteId}
              processosIds={editItem.processosIds||[]}
              onToggle={(pid, checked)=>{
                const prev = editItem.processosIds||[];
                setEditItem(prev2=>({...prev2,
                  processosIds: checked ? [...prev, pid] : prev.filter(x=>x!==pid)
                }));
              }}
              theme={theme}
            />
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalOpen(false)}>Cancelar</Btn>
              <Btn onClick={saveCont}>Salvar Contrato</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* MODAL VER CONTRATO — ficha completa, somente leitura */}
      {viewItem&&(()=>{
        const cliVer = clientes.find(cl=>cl.id===viewItem.clienteId);
        const parceriaVer = parcerias.find(p=>p.id===viewItem.parceiraIdContrato);
        const isTrib = (viewItem.area||"")==="Tributário";
        const Bloco = ({titulo, cor, children}) => (
          <div style={{marginBottom:16}}>
            <p style={{fontSize:11,fontWeight:700,color:cor||theme.gray500,textTransform:"uppercase",letterSpacing:.8,marginBottom:8,borderBottom:`1px solid ${theme.gray100}`,paddingBottom:4}}>{titulo}</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 24px"}}>{children}</div>
          </div>
        );
        const Campo = ({l,v}) => (
          <div>
            <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{l}</p>
            <p style={{fontSize:13,fontWeight:600,color:theme.gray800,marginTop:2}}>{v||"—"}</p>
          </div>
        );
        return (
        <Modal open={!!viewItem} onClose={()=>setViewItem(null)} title={`Contrato ${viewItem.numContrato}`} width={780}>

          {/* ── Dados do Cliente ── */}
          <Bloco titulo="👤 Dados do Cliente" cor={theme.blue}>
            <Campo l="Nome / Razão Social" v={cliVer?.nome||viewItem.clienteNome}/>
            <Campo l="Tipo" v={cliVer?.tipo}/>
            <Campo l={viewItem.cnpj && viewItem.cnpj.replace(/\D/g,"").length>=14 ? "CNPJ" : "CPF"} v={viewItem.cnpj||cliVer?.cpfCnpj}/>
            <Campo l="E-mail" v={cliVer?.email}/>
            <Campo l="WhatsApp" v={cliVer?.whatsapp ? `+${cliVer.whatsappDDI||"55"} ${cliVer.whatsapp}` : "—"}/>
            <Campo l="Cidade / UF" v={cliVer ? `${cliVer.cidade||"—"} / ${cliVer.uf||"—"}` : "—"}/>
            <Campo l="Endereço" v={cliVer?.endereco}/>
            <Campo l="CEP" v={cliVer?.cep}/>
            {cliVer?.tipo!=="PJ" && <Campo l="Data de Nascimento" v={fmtData(viewItem.dataNascimento||cliVer?.dataNascimento)}/>}
            {cliVer?.tipo==="PJ" && (cliVer.contatosPJ||[]).filter(ct=>ct.nome).map((ct,i)=>(
              <Campo key={i} l={`Contato — ${ct.nome}`} v={ct.whatsapp?`+${ct.whatsappDDI||"55"} ${ct.whatsapp}`:ct.email||"—"}/>
            ))}
          </Bloco>

          {/* ── Dados do Contrato ── */}
          <Bloco titulo="📋 Dados do Contrato" cor={theme.gold}>
            <Campo l="Produto(s)" v={viewItem.produtoNome}/>
            <Campo l="Área" v={viewItem.area}/>
            <Campo l="Fase" v={viewItem.fase}/>
            <Campo l="Tipo de Processo" v={viewItem.tipoProcesso}/>
            <Campo l="Status" v={viewItem.status}/>
            <Campo l="Responsável" v={viewItem.responsavelNome}/>
          </Bloco>

          {/* ── Datas ── */}
          <Bloco titulo="🗓️ Datas" cor={theme.green}>
            <Campo l="Data de Aprovação (Data Zero)" v={fmtData(viewItem.dataEntrada)}/>
            <Campo l="Hora da Aprovação (SLA)" v={viewItem.horaEntrada}/>
            <Campo l="Último Avanço" v={fmtData(viewItem.ultimoAvanco)}/>
            <Campo l="Conclusão Prevista" v={fmtData(viewItem.prazo)}/>
            <Campo l="Conclusão Real" v={fmtData(viewItem.dataConclusao)}/>
            <Campo l="Vencimento dos Honorários" v={fmtData(viewItem.dataVencimento)}/>
          </Bloco>

          {/* ── Financeiro ── */}
          <Bloco titulo="💰 Financeiro" cor={theme.amber}>
            <Campo l="Valor Contratado" v={`R$ ${(viewItem.valorContratado||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}`}/>
            <Campo l="Valor Recebido" v={`R$ ${(viewItem.valorRecebido||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}`}/>
            <Campo l="A Receber" v={`R$ ${(viewItem.valorAReceber||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}`}/>
            <Campo l="Índice de Recebimento" v={viewItem.valorContratado>0?`${Math.round((viewItem.valorRecebido/viewItem.valorContratado)*100)}%`:"—"}/>
            <Campo l="Custo Operacional" v={`R$ ${(viewItem.custoOperacional||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}`}/>
            <Campo l="MC — Margem de Contribuição" v={(()=>{const custoProd=produtos.find(p=>p.id===viewItem.produtoId)?.custo||0;const mc=(viewItem.valorContratado||0)-custoProd;const perc=viewItem.valorContratado>0?Math.round((mc/viewItem.valorContratado)*100):0;return `R$ ${mc.toLocaleString("pt-BR",{minimumFractionDigits:2})} (${perc}%)`;})()}/>
            <Campo l="Condição de Pagamento" v={viewItem.condicaoPagamento==="prazo"?`A Prazo — ${viewItem.numParcelas||1}x`:"À Vista"}/>
            {viewItem.condicaoPagamento==="prazo" && <Campo l="Vencimento 1ª Parcela" v={fmtData(viewItem.vencPrimeiraParcela)}/>}
          </Bloco>

          {/* ── Dados Financeiros do Contrato — Componentes Adicionais (visualização) ── */}
          {(viewItem.componentesFinanceiros||[]).length>0 && (
            <Bloco titulo="💼 Dados Financeiros do Contrato — Componentes Adicionais">
              {(()=>{
                const ativos = viewItem.componentesFinanceiros.filter(c=>c.ativo!==false);
                const fixosAtivos = ativos.filter(c=>c.formaPrecificacao!=="Percentual");
                const somaComponentesFixos = fixosAtivos.reduce((s,c)=>s+(parseFloat(c.valor)||0),0);
                const honorarioInicial = parseFloat(viewItem.valorContratado)||0;
                const totalAcumulado = honorarioInicial + somaComponentesFixos;
                return (
                  <div style={{gridColumn:"1/-1",background:"#fff",border:"1px solid #e9d5ff",borderRadius:7,padding:"10px 14px",marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:700,color:"#7c3aed"}}>
                      <span>Total Acumulado do Contrato (Honorário Inicial + Componentes Fixos)</span>
                      <span>{`R$ ${totalAcumulado.toLocaleString("pt-BR",{minimumFractionDigits:2})}`}</span>
                    </div>
                  </div>
                );
              })()}
              {viewItem.componentesFinanceiros.map((cf,ci)=>{
                const jaGerado = (cf.lancamentosGeradosIds||[]).length>0;
                const gerarLancamentoComponente = () => {
                  if (jaGerado && !window.confirm("Este componente já gerou lançamento(s) anteriormente. Gerar mais um mesmo assim?")) return;
                  let valor = parseFloat(cf.valor)||0;
                  if (cf.formaPrecificacao === "Percentual") {
                    const baseInput = window.prompt(`Informe o valor base (R$) para calcular ${cf.percentual||0}% — Base: ${cf.baseCalculo||"não definida"}`, "");
                    if (baseInput === null) return;
                    const baseVal = parseFloat(String(baseInput).replace(/\./g,"").replace(",","."))||0;
                    if (baseVal<=0) return alert("Valor base inválido.");
                    valor = parseFloat((baseVal*(parseFloat(cf.percentual)||0)/100).toFixed(2));
                  }
                  if (valor<=0) return alert("Não foi possível determinar o valor do lançamento. Preencha o valor ou percentual do componente.");

                  const atualizarComponenteGerado = (novoId) => {
                    setData(prev=>({...prev, contratos: prev.contratos.map(ct=>{
                      if(ct.id!==viewItem.id) return ct;
                      return {...ct, componentesFinanceiros:(ct.componentesFinanceiros||[]).map(c=>c.id===cf.id?{...c,lancamentosGeradosIds:[...(c.lancamentosGeradosIds||[]),novoId]}:c)};
                    })}));
                    setViewItem(prev=>({...prev, componentesFinanceiros:(prev.componentesFinanceiros||[]).map(c=>c.id===cf.id?{...c,lancamentosGeradosIds:[...(c.lancamentosGeradosIds||[]),novoId]}:c)}));
                  };

                  if (cf.tipo === "Repasses") {
                    const novaDesp = {
                      id: "DESP_CF_"+Date.now().toString().slice(-8),
                      descricao: `Repasse — ${cf.descricao||viewItem.numContrato}`,
                      grupo: "Custo", categoria: "Repasse",
                      valor, data: hojeStr, dataPagamento: "",
                      contratoId: viewItem.id, status: "A Pagar", bancoBaixa: "",
                      obs: "Gerado a partir de Componente Financeiro do contrato — Fase 2a (geração manual)."
                    };
                    setData(prev=>({...prev, despesas:[...(prev.despesas||[]), novaDesp]}));
                    atualizarComponenteGerado(novaDesp.id);
                    alert("Despesa de repasse gerada no Financeiro: R$ "+valor.toLocaleString("pt-BR",{minimumFractionDigits:2}));
                    return;
                  }

                  const tipoLan = cf.tipo==="Honorário de êxito" ? "Êxito"
                    : cf.tipo==="Reembolsos" ? "Reembolso"
                    : ["Honorário mensal","Honorário anual","Honorário por hora","Honorário por ato","Honorário por fase","Honorário por evento","Honorário de disponibilidade (retainer)","Honorário híbrido"].includes(cf.tipo) ? "Honorario"
                    : "Outros";
                  const novoLan = {
                    id: "LAN_CF_"+Date.now().toString().slice(-8),
                    clienteId: viewItem.clienteId,
                    contratoId: viewItem.id,
                    numContrato: viewItem.numContrato||"",
                    produtoId: viewItem.produtoId,
                    tipo: tipoLan,
                    modalidade: "À Vista",
                    descricao: `${cf.tipo} — ${cf.descricao||viewItem.numContrato}`,
                    valor,
                    dataVencimento: hojeStr,
                    dataRecebimento: "",
                    bancoBaixa: "",
                    status: "A Receber",
                    pagamentos: [],
                    saldoDevedor: null,
                    obs: "Gerado manualmente a partir de Componente Financeiro do contrato — Fase 2a."
                  };
                  setData(prev=>({...prev, lancamentos:[...(prev.lancamentos||[]), novoLan]}));
                  atualizarComponenteGerado(novoLan.id);
                  alert("Lançamento gerado no Financeiro: R$ "+valor.toLocaleString("pt-BR",{minimumFractionDigits:2}));
                };
                return (
                  <div key={cf.id||ci} style={{gridColumn:"1/-1",padding:"10px 12px",background:cf.ativo===false?"#f9fafb":"#faf5ff",borderRadius:7,border:`1px solid ${cf.ativo===false?theme.gray200:"#e9d5ff"}`,marginBottom:8,opacity:cf.ativo===false?0.6:1}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                      <div>
                        <p style={{fontSize:12,fontWeight:700,color:"#7c3aed",marginBottom:4}}>{cf.tipo}{cf.ativo===false?" — Pausado":""}{jaGerado?` — ✓ ${cf.lancamentosGeradosIds.length} lançamento(s) gerado(s)`:""}</p>
                        {cf.descricao && <p style={{fontSize:11,color:theme.gray600,marginBottom:4}}>{cf.descricao}</p>}
                        <p style={{fontSize:11,color:theme.gray700}}>
                          {cf.formaPrecificacao==="Percentual"
                            ? `${cf.percentual||0}% sobre ${cf.baseCalculo||"—"}`
                            : `R$ ${(parseFloat(cf.valor)||0).toLocaleString("pt-BR",{minimumFractionDigits:2})} (${cf.formaPrecificacao})`}
                          {" — "}{cf.condicaoPagamento}{" — Gatilho: "}{cf.gatilho}
                          {cf.gatilho==="Data fixa" && cf.dataGatilho ? ` (${fmtData(cf.dataGatilho)})` : ""}
                          {cf.gatilho==="Data relativa" && cf.obs ? ` (${cf.obs})` : ""}
                        </p>
                      </div>
                      {cf.ativo!==false && (
                        <button onClick={gerarLancamentoComponente}
                          style={{flexShrink:0,fontSize:11,padding:"5px 10px",borderRadius:6,border:"1px solid #7c3aed",background:"#fff",color:"#7c3aed",cursor:"pointer",fontWeight:600,whiteSpace:"nowrap"}}>
                          💰 Gerar Lançamento
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </Bloco>
          )}

          {/* ── Dados Tributários (apenas se área = Tributário) ── */}
          {isTrib && (
            <Bloco titulo="⚖️ Dados Tributários" cor="#7c3aed">
              <Campo l="Regime(s) Tributário(s)" v={(viewItem.regimes||[]).join(", ")||"—"}/>
              <Campo l="Faturamento Anual" v={viewItem.faturamentoAnual?`R$ ${parseFloat(viewItem.faturamentoAnual).toLocaleString("pt-BR",{minimumFractionDigits:2})}`:"—"}/>
              <Campo l="Atividade Principal" v={viewItem.atividadePrincipal||"—"}/>
              <Campo l="Atividades Secundárias" v={(viewItem.atividadesSecundarias||[]).join(", ")||"—"}/>
              <Campo l="Crédito Apurado" v={viewItem.valorCreditoApurado&&parseFloat(viewItem.valorCreditoApurado)>0?`R$ ${parseFloat(viewItem.valorCreditoApurado).toLocaleString("pt-BR",{minimumFractionDigits:2})}`:"—"}/>
              <Campo l="Honorários sobre o Crédito" v={viewItem.percHonorarios?`${viewItem.percHonorarios}%`:"—"}/>
            </Bloco>
          )}

          {/* ── Parceria (se aplicável) ── */}
          {viewItem.temParceria && (
            <Bloco titulo="🤝 Parceria" cor="#0ea5e9">
              <Campo l="Parceiro(a)" v={parceriaVer?.nome}/>
              <Campo l="Comissão" v={viewItem.percComissaoContrato?`${viewItem.percComissaoContrato}%`:"—"}/>
              <Campo l="Valor da Comissão" v={viewItem.valorComissao?`R$ ${parseFloat(viewItem.valorComissao).toLocaleString("pt-BR",{minimumFractionDigits:2})}`:"—"}/>
            </Bloco>
          )}

          {/* ── Sinalizadores ── */}
          {(viewItem.teseNova||viewItem.jurisprudenciaInstavel||viewItem.valorElevado||viewItem.dependePericia||viewItem.documentoIncompleto) && (
            <div style={{marginBottom:16,display:"flex",gap:8,flexWrap:"wrap"}}>
              {viewItem.teseNova&&<Badge color="blue">📘 Tese Nova</Badge>}
              {viewItem.jurisprudenciaInstavel&&<Badge color="amber">⚖️ Jurisprudência Instável</Badge>}
              {viewItem.valorElevado&&<Badge color="blue">💰 Valor Elevado</Badge>}
              {viewItem.dependePericia&&<Badge color="gray">🔬 Depende Perícia</Badge>}
              {viewItem.documentoIncompleto&&<Badge color="red">📄 Documento Incompleto</Badge>}
            </div>
          )}

          {/* ── Observações ── */}
          {viewItem.obs&&<div style={{background:theme.gray50,borderRadius:8,padding:"10px 14px",marginBottom:14}}>
            <p style={{fontSize:11,color:theme.gray400,marginBottom:4}}>OBSERVAÇÕES</p>
            <p style={{fontSize:13,color:theme.gray700}}>{viewItem.obs}</p>
          </div>}

          {/* ── Objetivo da Procuração ── */}
          {viewItem.objetivoEspecifico&&<div style={{background:"#fffbeb",borderRadius:8,padding:"10px 14px",marginBottom:14,border:"1px solid #fde68a"}}>
            <p style={{fontSize:11,color:"#92400e",marginBottom:4,fontWeight:700}}>📜 OBJETIVO ESPECÍFICO DA PROCURAÇÃO</p>
            <p style={{fontSize:13,color:"#78350f"}}>{viewItem.objetivoEspecifico}</p>
          </div>}

          {/* ── Dados Pessoais (Data de Nascimento do contrato) ── */}
          {viewItem.dataNascimento&&<div style={{background:"#faf5ff",borderRadius:8,padding:"10px 14px",marginBottom:14,border:"1px solid #e9d5ff"}}>
            <p style={{fontSize:11,color:"#7c3aed",marginBottom:4,fontWeight:700}}>🎂 DADOS PESSOAIS — PRIORIDADE PROCESSUAL</p>
            <p style={{fontSize:13,color:"#6d28d9"}}>Data de nascimento: <strong>{fmtData(viewItem.dataNascimento)}</strong></p>
          </div>}

          <div style={{display:"flex",justifyContent:"space-between",paddingTop:12,borderTop:`1px solid ${theme.gray100}`}}>
            <Btn variant="secondary" onClick={()=>{
              const cliVer2 = clientes.find(cl=>cl.id===viewItem.clienteId);
              setEditItem({
                ...viewItem,
                cnpj: viewItem.cnpj || (cliVer2?.cpfCnpj ? fmtCpfCnpj(cliVer2.cpfCnpj) : ""),
                dataNascimento: viewItem.dataNascimento || cliVer2?.dataNascimento || ""
              });
              setViewItem(null); setModalOpen(true);
            }}>✏️ Editar</Btn>
            <Btn variant="secondary" onClick={()=>setViewItem(null)}>Fechar</Btn>
          </div>
        </Modal>
        );
      })()}

      {/* MODAL OCORRÊNCIAS — ficha do cliente com visualização e inclusão */}
      <ModalOcorrenciasContrato
        ocClienteModal={ocClienteModal}
        clientes={clientes}
        data={data}
        setData={setData}
        setOcClienteModal={setOcClienteModal}
        novaOcCli={novaOcCli}
        setNovaOcCli={setNovaOcCli}
        hoje={hoje}
        theme={theme}
        FormField={FormField}
        Input={Input}
        Btn={Btn}
        Modal={Modal}
      />

      {/* MODAL FICHA DE CRÉDITO */}
      {contratoFichaModal && (
        <ModalFichaCredito
          contrato={contratoFichaModal}
          data={data}
          setData={setData}
          onClose={()=>setContratoFichaModal(null)}
        />
      )}

      {/* MODAL PDF OPERACIONAL */}
      <Modal open={modalPDF} onClose={()=>setModalPDF(false)} title={pdfTipoBase==="procuracao"?"📜 Procuração":"📋 Declaração de Hipossuficiência"} width={760}>
        {regSel&&(()=>{
          const cliPDF = clientes.find(c=>c.id===regSel.clienteId);
          const isPF = cliPDF?.tipo !== "PJ";
          const salvarQualifNoCliente = () => {
            setData(prev=>({...prev, clientes:(prev.clientes||[]).map(c=>
              c.id===cliPDF.id ? {...c, nacionalidade:qualifForm.nacionalidade, estadoCivil:qualifForm.estadoCivil, profissao:qualifForm.profissao, rg:qualifForm.rg, ufRg:qualifForm.ufRg} : c
            )}));
            alert("Dados salvos! Serão reaproveitados automaticamente na próxima emissão para este cliente.");
          };
          return (
          <>
            {isPF && (
              <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"14px 16px",marginBottom:16}}>
                <p style={{fontSize:12,fontWeight:700,color:"#92400e",marginBottom:10}}>✏️ Dados complementares (exigidos pelo modelo jurídico — preencha e o texto atualiza ao lado)</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"0 12px",marginBottom:10}}>
                  <FormField label="Nacionalidade">
                    <Input value={qualifForm.nacionalidade} onChange={v=>setQualifForm({...qualifForm,nacionalidade:v})} placeholder="brasileiro(a)"/>
                  </FormField>
                  <FormField label="Estado Civil">
                    <Input value={qualifForm.estadoCivil} onChange={v=>setQualifForm({...qualifForm,estadoCivil:v})} placeholder="casado(a)"/>
                  </FormField>
                  <FormField label="Profissão">
                    <Input value={qualifForm.profissao} onChange={v=>setQualifForm({...qualifForm,profissao:v})} placeholder="empresário(a)"/>
                  </FormField>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"2fr 1fr auto",gap:"0 12px",alignItems:"end"}}>
                  <FormField label="RG nº">
                    <Input value={qualifForm.rg} onChange={v=>setQualifForm({...qualifForm,rg:v})} placeholder="00.000.000-0"/>
                  </FormField>
                  <FormField label="UF do RG">
                    <Input value={qualifForm.ufRg} onChange={v=>setQualifForm({...qualifForm,ufRg:v})} placeholder="SP"/>
                  </FormField>
                  <Btn variant="secondary" onClick={salvarQualifNoCliente}>💾 Salvar no cliente</Btn>
                </div>
              </div>
            )}
            <div style={{background:theme.gray50,borderRadius:8,padding:"20px 32px",marginBottom:16,maxHeight:"50vh",overflowY:"auto",border:`1px solid ${theme.gray200}`}}
              dangerouslySetInnerHTML={{__html:pdfTipoBase==="procuracao"?htmlProcuracao(regSel,qualifForm):htmlDeclaracao(regSel,qualifForm)}}/>
            <div style={{padding:"8px 12px",background:`${theme.amber}11`,border:`1px solid ${theme.amber}33`,borderRadius:7,marginBottom:14,fontSize:12,color:theme.amber}}>
              💡 Clique em "Imprimir / Salvar PDF" e escolha <strong>"Salvar como PDF"</strong>.
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
              <Btn variant="secondary" onClick={()=>setModalPDF(false)}>Fechar</Btn>
              <Btn onClick={imprimirBase}>🖨️ Imprimir / Salvar PDF</Btn>
            </div>
          </>
          );
        })()}
      </Modal>

      {/* MODAL TAREFA OPERACIONAL */}
      <Modal open={modalTaref} onClose={()=>setModalTaref(false)} title={editTaref?._idx!==undefined?"Editar Tarefa":"Nova Tarefa"} width={520}>
        {editTaref&&(
          <>
            <FormField label="Título" required><Input value={editTaref.titulo} onChange={v=>setEditTaref({...editTaref,titulo:v})} placeholder="Ex: Coleta de documentos"/></FormField>
            <FormField label="Descrição"><Textarea value={editTaref.descricao} onChange={v=>setEditTaref({...editTaref,descricao:v})} rows={2}/></FormField>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px"}}>
              <FormField label="Prazo"><Input type="date" value={editTaref.prazoAtual} onChange={v=>setEditTaref({...editTaref,prazoAtual:v,prazoOriginal:editTaref.prazoOriginal||v})}/></FormField>
              <FormField label="Status"><Select value={editTaref.status} onChange={v=>setEditTaref({...editTaref,status:v,dataConclusao:v==="Concluída"?hojeStr:editTaref.dataConclusao})} options={STATUS_TAREF}/></FormField>
              <FormField label="Responsável"><Select value={editTaref.responsavel} onChange={v=>setEditTaref({...editTaref,responsavel:v})} options={equipe.filter(e=>e.ativo!==false).map(e=>({value:e.id,label:e.nome}))} placeholder="Selecione..."/></FormField>
              <FormField label="Data de Conclusão"><Input type="date" value={editTaref.dataConclusao} onChange={v=>setEditTaref({...editTaref,dataConclusao:v})}/></FormField>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
              <input type="checkbox" checked={editTaref.prorrogado} onChange={e=>setEditTaref({...editTaref,prorrogado:e.target.checked})} id="chk-pr"/>
              <label htmlFor="chk-pr" style={{fontSize:13,color:theme.gray700,cursor:"pointer"}}>Prazo prorrogado</label>
            </div>
            {editTaref.prorrogado&&<FormField label="Motivo da Prorrogação"><Textarea value={editTaref.motivoProrrogacao} onChange={v=>setEditTaref({...editTaref,motivoProrrogacao:v})} rows={2}/></FormField>}
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:12,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalTaref(false)}>Cancelar</Btn>
              <Btn onClick={()=>{
                if(!editTaref.titulo) return alert("Título obrigatório.");
                if(editTaref._idx!==undefined){
                  const{_idx,...t}=editTaref; atualizarTarefa(regSel,_idx,t);
                } else {
                  const tarefas=[...(regSel.tarefas||[]),editTaref];
                  const nova=baseOp.map(r=>r.id===regSel.id?{...r,tarefas}:r);
                  setData(prev=>({...prev,baseOperacional:nova}));
                  // regSel é derivado automaticamente
                }
                setModalTaref(false);
              }}>Salvar</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* MODAL OCORRÊNCIA OPERACIONAL */}
      {/* ══ MODAL TIMESHEET ══ */}
      <Modal open={modalTS} onClose={()=>setModalTS(false)} title="⏱️ Lançar Horas" width={480}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <FormField label="Data *">
            <input type="date" value={novaEntryTS.data||hojeStr}
              onChange={e=>setNovaEntryTS(prev=>({...prev,data:e.target.value}))}
              style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          </FormField>
          <FormField label="Horas *">
            <input type="number" min="0.5" step="0.5" value={novaEntryTS.horas||""}
              onChange={e=>setNovaEntryTS(prev=>({...prev,horas:e.target.value}))}
              placeholder="Ex: 1.5"
              style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          </FormField>
        </div>
        <FormField label="Membro da Equipe *">
          <select value={novaEntryTS.membroId||""} onChange={e=>setNovaEntryTS(prev=>({...prev,membroId:e.target.value}))}
            style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",background:"#fff",marginBottom:12}}>
            <option value="">— Selecione —</option>
            {equipe.filter(e=>e.ativo!==false).map(e=>(<option key={e.id} value={e.id}>{e.nome}</option>))}
          </select>
        </FormField>
        <FormField label="Descrição da Atividade *">
          <Textarea value={novaEntryTS.descricao||""} onChange={v=>setNovaEntryTS(prev=>({...prev,descricao:v}))} rows={3} placeholder="Descreva a atividade realizada..."/>
        </FormField>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:12,borderTop:`1px solid ${theme.gray100}`}}>
          <Btn variant="secondary" onClick={()=>setModalTS(false)}>Cancelar</Btn>
          <Btn onClick={()=>{
            if(!novaEntryTS.horas||!novaEntryTS.descricao.trim()||!novaEntryTS.membroId) return alert("Preencha todos os campos obrigatórios.");
            const hLanc = parseFloat(novaEntryTS.horas)||0;
            if(hLanc<=0) return alert("Informe um número de horas válido.");
            const contrato = contratos.find(ct=>ct.id===regSel.contratoId);
            if(!contrato) return;
            const horasPacote = contrato.horasPacote||0;
            const valorContratado = contrato.valorContratado||0;
            const valorHora = horasPacote>0 ? valorContratado/horasPacote : 0;
            const entriesAtuais = regSel.timesheetEntries||[];

            let novasEntries;
            if(editEntryTS) {
              // EDIÇÃO — atualizar a entry existente
              novasEntries = entriesAtuais.map(e => e.id===editEntryTS.id
                ? {...e, data:novaEntryTS.data||hojeStr, horas:hLanc, descricao:novaEntryTS.descricao, membroId:novaEntryTS.membroId}
                : e
              );
            } else {
              // NOVO lançamento
              const novaEntry = {
                id:"TS"+Date.now().toString().slice(-8),
                data:novaEntryTS.data||hojeStr,
                horas:hLanc,
                descricao:novaEntryTS.descricao,
                membroId:novaEntryTS.membroId,
                criadoEm:new Date().toISOString()
              };
              novasEntries = [...entriesAtuais, novaEntry];
            }

            const horasUsadasNova = novasEntries.reduce((s,e)=>s+(parseFloat(e.horas)||0),0);
            const saldoNovo = horasPacote - horasUsadasNova;
            const pacoteEsgotado = saldoNovo <= 0;

            // Para novo lançamento: verificar excedente
            const horasJaUsadas = entriesAtuais.reduce((s,e)=>s+(parseFloat(e.horas)||0),0);
            const saldoAtual = horasPacote - horasJaUsadas;
            const horasExcedente = !editEntryTS && hLanc > saldoAtual ? hLanc - saldoAtual : 0;

            // Salvar entries no registro
            updReg(regSel.id,{timesheetEntries:novasEntries, pacoteEsgotado});
            // Se excedente → lançamento automático no Financeiro
            if(horasExcedente>0&&valorHora>0){
              const valorExcedente = parseFloat((horasExcedente*valorHora).toFixed(2));
              const lanExcedente = {
                id:"LAN_TS_"+Date.now().toString().slice(-8),
                clienteId: contrato.clienteId,
                contratoId: contrato.id,
                numContrato: contrato.numContrato||"",
                produtoId: contrato.produtoId,
                tipo:"Honorario",
                modalidade:"À Vista",
                descricao:"Horas Excedentes — "+contrato.numContrato+" ("+horasExcedente.toFixed(1)+"h × R$ "+valorHora.toLocaleString("pt-BR",{minimumFractionDigits:2})+")",
                valor:valorExcedente,
                dataVencimento:novaEntryTS.data||hojeStr,
                dataRecebimento:"",
                bancoBaixa:"",
                status:"A Receber",
                obs:"Lançamento automático — excedente de horas do pacote."
              };
              setData(prev=>({...prev, lancamentos:[...(prev.lancamentos||[]),lanExcedente]}));
              alert("Pacote zerado! "+horasExcedente.toFixed(1)+"h excedente = R$ "+valorExcedente.toLocaleString("pt-BR",{minimumFractionDigits:2})+". Lancamento gerado no Financeiro. Novos lancamentos bloqueados.");
            }
            setNovaEntryTS({data:"",horas:"",descricao:"",membroId:""});
            setEditEntryTS(null);
            setModalTS(false);
        }}>
  {editEntryTS ? "Salvar Alteração" : "Salvar"}
</Btn>
        </div>
      </Modal>

      <Modal open={modalOcorrB} onClose={()=>setModalOcorrB(false)} title="+ Nova Ocorrência" width={560}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
          <FormField label="Tipo de Ocorrência">
            <select value={novaOcorrB.tipo||""} onChange={e=>{
                const t=tiposOcorrencia.find(x=>x.id===e.target.value);
                setNovaOcorrB(prev=>({...prev,tipo:e.target.value,tipoNome:t?.nome||""}));
              }}
              style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",background:"#fff"}}>
              <option value="">— Selecione —</option>
              {[...tiposOcorrencia].sort((a,b)=>a.nome.localeCompare(b.nome,"pt-BR")).map(t=>(
                <option key={t.id} value={t.id}>{t.confidencial!==false?"🔒 ":""}{t.nome}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Data">
            <input type="date" value={novaOcorrB.data||hojeStr}
              onChange={e=>setNovaOcorrB(prev=>({...prev,data:e.target.value}))}
              style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",boxSizing:"border-box"}}/>
          </FormField>
          <FormField label="Responsável">
            <select value={novaOcorrB.responsavel||""} onChange={e=>setNovaOcorrB(prev=>({...prev,responsavel:e.target.value}))}
              style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",background:"#fff"}}>
              <option value="">— Selecione —</option>
              {equipe.filter(e=>e.ativo!==false).map(e=>(<option key={e.id} value={e.id}>{e.nome}</option>))}
            </select>
          </FormField>
        </div>
        <div style={{marginBottom:12}}>
          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"10px 12px",borderRadius:7,background:novaOcorrB.confidencial?"#fef3c7":"#f0fdf4",border:`1px solid ${novaOcorrB.confidencial?"#fde68a":theme.green+"44"}`}}>
            <input type="checkbox" checked={!!novaOcorrB.confidencial} onChange={e=>setNovaOcorrB(prev=>({...prev,confidencial:e.target.checked}))} style={{width:15,height:15,accentColor:"#d97706"}}/>
            <span style={{fontSize:13,fontWeight:600,color:novaOcorrB.confidencial?"#92400e":theme.green}}>
              {novaOcorrB.confidencial?"🔒 Confidencial — visível apenas internamente":"📋 Não confidencial — poderá ir para o relatório do cliente"}
            </span>
          </label>
        </div>
        <FormField label="Descrição *">
          <Textarea value={novaOcorrB.obs||""} onChange={v=>setNovaOcorrB(prev=>({...prev,obs:v}))} rows={4} placeholder="Descreva a ocorrência..."/>
        </FormField>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px",marginBottom:8}}>
          <FormField label="Origem da captura">
            <select value={novaOcorrB.origemCaptura||"Manual"} onChange={e=>setNovaOcorrB(prev=>({...prev,origemCaptura:e.target.value}))}
              style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",background:"#fff"}}>
              {["Manual","DataJud","Legal Mail","E-mail","WhatsApp"].map(o=><option key={o}>{o}</option>)}
            </select>
          </FormField>
          <FormField label="Link Legal Mail (opcional)">
            <Input value={novaOcorrB.legalMailUri||""} onChange={v=>setNovaOcorrB(prev=>({...prev,legalMailUri:v}))} placeholder="inbox_uri do Legal Mail"/>
          </FormField>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:12,borderTop:`1px solid ${theme.gray100}`}}>
          <Btn variant="secondary" onClick={()=>{setModalOcorrB(false);setNovaOcorrB({obs:"",data:"",responsavel:"",confidencial:true,origemCaptura:"Manual",legalMailUri:""});}}>Cancelar</Btn>
          <Btn onClick={()=>{
            if(!(novaOcorrB.obs||"").trim()) return alert("Preencha a descrição.");
            const _dx=new Date(); const hojeLocal=_dx.getFullYear()+'-'+String(_dx.getMonth()+1).padStart(2,'0')+'-'+String(_dx.getDate()).padStart(2,'0');
            const ocData=novaOcorrB.data||hojeLocal;
            const novaOc={id:"OCB"+Date.now(),data:ocData,obs:novaOcorrB.obs,responsavel:novaOcorrB.responsavel||"",confidencial:novaOcorrB.confidencial!==false,criadoEm:new Date().toISOString(),origemCaptura:novaOcorrB.origemCaptura||"Manual",legalMailUri:novaOcorrB.legalMailUri||""};
            // Novo ciclo: se o contrato já estava Concluído/Arquivado, qualquer movimentação o reabre em Entrada e Triagem
            const faseAtualReg = faseDe(regSel.status);
            const reabrindo = faseAtualReg==="Concluído" || faseAtualReg==="Arquivo";
            const camposReg = reabrindo
              ? { ocorrencias:[...(regSel.ocorrencias||[]), novaOc, {id:"OCB"+(Date.now()+1),data:ocData,obs:`Novo ciclo iniciado automaticamente — movimentação registrada após conclusão/arquivamento. Card retornado para Entrada e Triagem.`,responsavel:"",confidencial:false,criadoEm:new Date().toISOString(),origemCaptura:"Sistema"}],
                  status:"Aguardando Documentação", dataEntradaFase:new Date().toISOString(), faseAnterior:null }
              : { ocorrencias:[...(regSel.ocorrencias||[]), novaOc] };
            updReg(regSel.id,camposReg);
            if(ocData>=hojeLocal){
              setData(prev=>({...prev,agenda:[...(prev.agenda||[]),{
                id:"EVT_OCB_"+Date.now(),
                titulo:"Ocorrência — "+getNomeCli(regSel.clienteId)+" · "+regSel.numContrato,
                tipo:"compromisso",data:ocData,horaInicio:"09:00",horaFim:"10:00",
                responsavel:novaOcorrB.responsavel||"",descricao:novaOcorrB.obs,
                status:"Confirmado",alerta:"1d",origemBaseId:regSel.id,
                origemContrato:regSel.numContrato||"",local:"",linkReuniao:"",
                clienteId:regSel.clienteId||"",processoId:"",origemLeadId:""
              }]}));
            }
            setNovaOcorrB({obs:"",data:"",responsavel:"",confidencial:true});
            setModalOcorrB(false);
          }}>Salvar</Btn>
        </div>
      </Modal>

      {/* ÁREA IMPRESSÃO */}
      <div id="print-area-base"
        dangerouslySetInnerHTML={{__html:regSel?(pdfTipoBase==="procuracao"?htmlProcuracao(regSel,qualifForm):htmlDeclaracao(regSel,qualifForm)):""}}/>
    </div>
  );
}




// ── Helper: Gráfico Receita vs Despesa ──────────────────────────
// ── Helper: MC por Área ─────────────────────────────────────────

// ============================================================
// RÉGUA DE COBRANÇA — Módulo Financeiro
// 5 comunicados automáticos por vencimento de honorários
// ============================================================

const REGUA_COBRANCA = {
  "cob_m3":  { titulo:"Lembrete Preventivo (–3 dias)",     diasVenc:-3,  cor:"#3A5C8C", icon:"📅", tom:"preventivo" },
  "cob_0":   { titulo:"Aviso de Vencimento (Hoje)",         diasVenc:0,   cor:"#B87C2A", icon:"💳", tom:"vencimento" },
  "cob_p3":  { titulo:"Cobrança Gentil (+3 dias)",          diasVenc:3,   cor:"#B87C2A", icon:"⚠️", tom:"gentil" },
  "cob_p10": { titulo:"Cobrança Firme (+10 dias)",          diasVenc:10,  cor:"#8C3A3A", icon:"🔴", tom:"firme" },
  "cob_p30": { titulo:"Notificação Extrajudicial (+30 dias)",diasVenc:30, cor:"#1A1610", icon:"⚖️", tom:"juridico" },
};

const gerarMensagemCobranca = (tipo, cl, lan, bancos, modelosCobranca) => {
  const hoje = new Date().toLocaleDateString("pt-BR",{day:"numeric",month:"long",year:"numeric"});
  const isPJ = cl?.tipo === "PJ";
  const contatoPJ = isPJ ? (cl?.contatosPJ||[]).find(c=>c.nome?.trim()) : null;
  const nomeContato = contatoPJ?.nome || cl?.nome || "[NOME]";
  const nomeEmpresa = isPJ ? cl?.nome || "" : "";
  const prefixo = isPJ && nomeEmpresa ? `Att. ${nomeEmpresa}\n\n` : "";
  const ddiUsadoCob = contatoPJ?.whatsappDDI || cl?.whatsappDDI || "55";
  const whats = whatsCompleto(ddiUsadoCob, contatoPJ?.whatsapp || cl?.whatsapp || "");
  const valor = `R$ ${(parseFloat(lan?.valor||0)).toLocaleString("pt-BR",{minimumFractionDigits:2})}`;
  const venc = lan?.dataVencimento ? new Date(lan.dataVencimento+"T12:00").toLocaleDateString("pt-BR") : "[DATA]";
  const desc = lan?.descricao || "Honorários Advocatícios";
  const banco = bancos?.find(b=>b.id===(lan?.bancoBaixa||""));
  const dadosPagto = lan?.linkPagamento
    ? `💳 Link para pagamento: ${lan.linkPagamento}`
    : banco
      ? `💳 PIX / Dados Bancários: ${banco.nome} · Ag. ${banco.agencia} · Cc ${banco.conta}`
      : "💳 Entre em contato para dados de pagamento";
  const assin = `\n\nAtenciosamente,\nAlves & Correa Advogados\nDr. Nelson José Alves · OAB/SP 398.875\nDra. Annie Lemos Alves Correa · OAB/SP 446.942`;

  // Verifica modelo customizado
  const mc = modelosCobranca || {};

  const msgs = {
    cob_m3: mc.cob_m3 || `${prefixo}Prezado(a) ${nomeContato},\n\nEsperamos que esteja bem. Passamos para lembrá-lo(a), de forma amigável, que a fatura referente aos honorários advocatícios vencerá em 3 (três) dias — ${venc}.\n\nDetalhes:\n• Referência: ${desc}\n• Valor: ${valor}\n• Vencimento: ${venc}\n\n${dadosPagto}\n\nCaso já tenha efetuado o pagamento, por favor desconsidere esta mensagem. Agradecemos pela confiança!${assin}`,

    cob_0: mc.cob_0 || `${prefixo}Prezado(a) ${nomeContato},\n\nInformamos que hoje é a data de vencimento dos honorários advocatícios referentes a: ${desc}.\n\nValor: ${valor}\n${dadosPagto}\n\nCaso haja qualquer dificuldade, estamos disponíveis para encontrar a melhor solução em conjunto.${assin}`,

    cob_p3: mc.cob_p3 || `${prefixo}Prezado(a) ${nomeContato},\n\nVerificamos que o pagamento referente aos honorários com vencimento em ${venc} ainda não foi identificado em nosso sistema.\n\nPendência: ${valor}\n${dadosPagto}\n\nCaso o pagamento já tenha sido realizado, por gentileza encaminhe o comprovante. Estamos à disposição para dialogar sobre condições de regularização.${assin}`,

    cob_p10: mc.cob_p10 || `${prefixo}Prezado(a) ${nomeContato},\n\nConstatamos que, transcorridos 10 dias do vencimento (${venc}), o débito referente a ${desc} — ${valor} — permanece em aberto.\n\nSolicitamos a regularização no prazo máximo de 5 dias corridos. Caso haja interesse em negociar condições, agendemos uma conversa.\n\n📞 contato@alvescorreaadvogados.com.br${assin}`,

    cob_p30: mc.cob_p30 || `${prefixo}Prezado(a) ${nomeContato},\n\nEste escritório vem comunicar formalmente que o débito de honorários vencido em ${venc} — ${valor} — permanece em aberto após múltiplos contatos.\n\nDiante da ausência de regularização, encontramo-nos na necessidade de adotar as medidas previstas no instrumento contratual, incluindo suspensão dos serviços e encaminhamento à cobrança judicial.\n\nÉ possível evitar essas medidas mediante regularização imediata ou contato até [DATA LIMITE].\n\nEste comunicado produz os efeitos legais pertinentes (art. 397 do Código Civil).${assin}`,
  };

  const msg = msgs[tipo] || "";
  return { mensagem: msg, whatsappNum: whats };
};

function ModCobrancaFin({ data, setData, lancamentos, clientes, contratos, bancos, fmtMoeda, fmtData, getNomeCli, hoje }) {
  const [abaC, setAbaC] = React.useState("fila");
  const [busca, setBusca] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const cobHistorico = data.cobHistorico || [];
  const modelosCobranca = data.modelosCobranca || {};

  const fmtMoedaL = v => `R$ ${(parseFloat(v)||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}`;

  // ── Gerar fila automática ──────────────────────────────
  const gerarFilaCobranca = () => {
    const fila = [];
    const hojeDate = new Date(hoje+"T12:00");

    lancamentos.filter(l => l.status === "A Receber" || l.status === "Pendente").forEach(lan => {
      if (!lan.dataVencimento) return;
      const cl = clientes.find(c=>c.id===lan.clienteId);
      if (!cl) return;
      const vencDate = new Date(lan.dataVencimento+"T12:00");
      const diffDias = Math.round((hojeDate - vencDate)/(1000*60*60*24));

      Object.entries(REGUA_COBRANCA).forEach(([tipo, cfg]) => {
        // -3 dias: dispara quando faltam 3 dias (diff = -3)
        // 0: no dia do vencimento (diff = 0)
        // +3: 3 dias após (diff = 3)
        // +10: 10 dias após (diff = 10)
        // +30: 30 dias após (diff >= 30)
        const match = tipo === "cob_p30"
          ? diffDias >= 30
          : diffDias === cfg.diasVenc;

        if (!match) return;

        const jaEnv = cobHistorico.some(h =>
          h.lancamentoId === lan.id && h.tipo === tipo &&
          (tipo !== "cob_p30" ? h.dataEnvio?.startsWith(hoje.slice(0,7)) : true)
        );
        if (jaEnv) return;

        fila.push({ tipo, cfg, cl, lan, id: lan.id+"_"+tipo, diffDias });
      });
    });

    return fila.sort((a,b) => Math.abs(a.diffDias) - Math.abs(b.diffDias));
  };

  const fila = gerarFilaCobranca();
  const filtFila = busca
    ? fila.filter(f => (f.cl?.nome||"").toLowerCase().includes(busca.toLowerCase()) ||
        f.cfg.titulo.toLowerCase().includes(busca.toLowerCase()))
    : fila;

  const aprovarCobranca = (item) => {
    const { mensagem, whatsappNum } = gerarMensagemCobranca(item.tipo, item.cl, item.lan, bancos, modelosCobranca);
    setPreview({ ...item, mensagem, whatsappNum });
  };

  const confirmarEnvioCobranca = (item) => {
    const msg = encodeURIComponent(item.mensagem);
    window.open(`https://wa.me/${item.whatsappNum||""}?text=${msg}`, "_blank");
    setData(prev => ({
      ...prev,
      cobHistorico: [...(prev.cobHistorico||[]), {
        id: Date.now().toString(),
        tipo: item.tipo,
        titulo: item.cfg.titulo,
        lancamentoId: item.lan?.id,
        clienteId: item.cl?.id,
        valor: item.lan?.valor,
        dataEnvio: new Date().toISOString(),
      }]
    }));
    setPreview(null);
  };

  const COR_TOM = { preventivo:theme.blue, vencimento:theme.amber, gentil:theme.amber, firme:theme.red, juridico:theme.gray900 };

  // ── Agenda ──
  const proximosVenc = lancamentos
    .filter(l => l.status === "A Receber" || l.status === "Pendente")
    .filter(l => l.dataVencimento)
    .map(l => {
      const vencDate = new Date(l.dataVencimento+"T12:00");
      const hojeDate = new Date(hoje+"T12:00");
      const diff = Math.round((vencDate - hojeDate)/(1000*60*60*24));
      return { ...l, diff, cl: clientes.find(c=>c.id===l.clienteId) };
    })
    .filter(l => l.diff >= -30 && l.diff <= 30)
    .sort((a,b) => a.diff - b.diff);

  return (
    <div>
      <div style={{marginBottom:16,padding:"12px 16px",background:theme.gray50,borderRadius:8,border:`1px solid ${theme.gray200}`}}>
        <p style={{fontSize:13,fontWeight:700,color:theme.gray800,margin:0}}>💰 Régua de Cobrança</p>
        <p style={{fontSize:11,color:theme.gray500,margin:"3px 0 0"}}>Comunicados automáticos de cobrança por vencimento de honorários · Aprovação antes do disparo.</p>
      </div>

      {/* KPIs rápidos */}
      {(()=>{
        const atrasados = lancamentos.filter(l=>{
          if (l.status!=="A Receber"&&l.status!=="Pendente") return false;
          const diff = Math.round((new Date(hoje+"T12:00")-new Date((l.dataVencimento||hoje)+"T12:00"))/(1000*60*60*24));
          return diff > 0;
        });
        const total = atrasados.reduce((s,l)=>s+(parseFloat(l.valor)||0),0);
        const m3  = atrasados.filter(l=>{ const d=Math.round((new Date(hoje+"T12:00")-new Date(l.dataVencimento+"T12:00"))/(1000*60*60*24)); return d>=1&&d<=9; });
        const m10 = atrasados.filter(l=>{ const d=Math.round((new Date(hoje+"T12:00")-new Date(l.dataVencimento+"T12:00"))/(1000*60*60*24)); return d>=10&&d<=29; });
        const m30 = atrasados.filter(l=>{ const d=Math.round((new Date(hoje+"T12:00")-new Date(l.dataVencimento+"T12:00"))/(1000*60*60*24)); return d>=30; });
        return (
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
            {[
              ["Total Inadimplente", fmtMoedaL(total), theme.red, `${atrasados.length} lançamento(s)`],
              ["1–9 dias", fmtMoedaL(m3.reduce((s,l)=>s+(parseFloat(l.valor)||0),0)), theme.amber, `${m3.length} cliente(s)`],
              ["+10 dias", fmtMoedaL(m10.reduce((s,l)=>s+(parseFloat(l.valor)||0),0)), theme.red, `${m10.length} cliente(s)`],
              ["+30 dias", fmtMoedaL(m30.reduce((s,l)=>s+(parseFloat(l.valor)||0),0)), theme.gray900, `${m30.length} cliente(s)`],
            ].map(([l,v,c,s])=>(
              <div key={l} style={{padding:"12px 14px",borderRadius:8,background:theme.white,
                border:`1px solid ${theme.gray200}`,borderTop:`3px solid ${c}`}}>
                <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.6,marginBottom:4}}>{l}</p>
                <p style={{fontSize:16,fontWeight:700,color:c,margin:0}}>{v}</p>
                <p style={{fontSize:10,color:theme.gray400,marginTop:2}}>{s}</p>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Sub-abas */}
      <div style={{display:"flex",gap:4,background:theme.gray100,borderRadius:8,padding:3,marginBottom:16,width:"fit-content"}}>
        {[["fila",`📬 Fila (${fila.length})`],["agenda","📅 Agenda"],["historico",`📋 Histórico (${cobHistorico.length})`],["modelos","📝 Modelos"]].map(([v,l])=>(
          <button key={v} onClick={()=>setAbaC(v)} style={{
            padding:"6px 14px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,
            fontWeight:abaC===v?700:400,
            background:abaC===v?theme.white:"transparent",
            color:abaC===v?theme.gray900:theme.gray400,
            boxShadow:abaC===v?"0 1px 3px rgba(0,0,0,.1)":"none"}}>
            {l}
          </button>
        ))}
      </div>

      {/* Modal preview */}
      {preview && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:9999,
          display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:"24px 16px"}}>
          <div style={{background:theme.white,borderRadius:12,padding:"24px 28px",maxWidth:640,
            width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,.3)",marginBottom:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0}}>{preview.cfg.icon} {preview.cfg.titulo}</p>
              <button onClick={()=>setPreview(null)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:theme.gray400}}>×</button>
            </div>
            <div style={{padding:"8px 12px",borderRadius:6,background:theme.gray50,marginBottom:12}}>
              <p style={{fontSize:11,color:theme.gray500,margin:0}}>
                Cliente: <strong>{preview.cl?.nome}</strong> ·
                Valor: <strong>{fmtMoedaL(preview.lan?.valor)}</strong> ·
                Venc: <strong>{fmtData(preview.lan?.dataVencimento)}</strong>
              </p>
            </div>
            <div style={{marginBottom:16}}>
              <p style={{fontSize:11,fontWeight:700,color:theme.gray600,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>Mensagem</p>
              <textarea value={preview.mensagem}
                onChange={e=>setPreview(p=>({...p,mensagem:e.target.value}))}
                rows={10} style={{width:"100%",padding:"12px 14px",border:`1px solid ${theme.gray200}`,
                  borderRadius:8,fontSize:12,fontFamily:"inherit",resize:"vertical",outline:"none",
                  boxSizing:"border-box",lineHeight:1.7}}/>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
              <button onClick={()=>setPreview(null)} style={{padding:"9px 20px",borderRadius:7,
                border:`1px solid ${theme.gray200}`,background:theme.white,cursor:"pointer",fontSize:13}}>Cancelar</button>
              <button onClick={()=>confirmarEnvioCobranca(preview)} style={{padding:"9px 22px",
                borderRadius:7,border:"none",background:theme.green,color:theme.white,
                cursor:"pointer",fontSize:13,fontWeight:700}}>
                📲 Abrir WhatsApp e Registrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ABA FILA */}
      {abaC==="fila" && (
        <div>
          <input value={busca} onChange={e=>setBusca(e.target.value)}
            placeholder="🔍 Buscar cliente..."
            style={{width:"100%",padding:"9px 14px",border:`1px solid ${theme.gray200}`,
              borderRadius:8,fontSize:13,outline:"none",marginBottom:12}}/>
          {filtFila.length===0
            ? <Card><EmptyState icon="💰" title="Nenhuma cobrança pendente" subtitle="Todos os honorários estão em dia ou já foram comunicados."/></Card>
            : <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {filtFila.map(item=>{
                  const jaEnv = cobHistorico.some(h=>h.lancamentoId===item.lan?.id&&h.tipo===item.tipo);
                  return (
                    <div key={item.id} style={{background:theme.white,borderRadius:10,padding:"14px 18px",
                      border:`1px solid ${theme.gray200}`,borderLeft:`4px solid ${item.cfg.cor}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                        <div style={{flex:1}}>
                          <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:5}}>
                            <span style={{fontSize:14}}>{item.cfg.icon}</span>
                            <span style={{fontSize:12,padding:"2px 8px",borderRadius:4,fontWeight:700,
                              background:item.cfg.cor+"18",color:item.cfg.cor}}>{item.cfg.titulo}</span>
                          </div>
                          <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0}}>{item.cl?.nome||"—"}</p>
                          <p style={{fontSize:12,color:theme.gray600,margin:"4px 0 0"}}>
                            {item.lan?.descricao||"Honorários"} · {fmtMoedaL(item.lan?.valor)} · Venc: {fmtData(item.lan?.dataVencimento)}
                          </p>
                        </div>
                        {jaEnv
                          ? <span style={{padding:"9px 14px",borderRadius:7,background:"#d4edda",
                              color:"#155724",fontSize:12,fontWeight:700,marginLeft:12,whiteSpace:"nowrap"}}>
                              ✓ Enviado
                            </span>
                          : <button onClick={()=>aprovarCobranca(item)}
                              style={{padding:"9px 18px",borderRadius:7,border:"none",cursor:"pointer",
                                background:theme.gray900,color:theme.gold,fontSize:12,fontWeight:700,
                                whiteSpace:"nowrap",marginLeft:12}}>
                              📲 Revisar e Enviar
                            </button>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
          }
        </div>
      )}

      {/* ABA AGENDA */}
      {abaC==="agenda" && (
        <Card>
          <p style={{fontSize:12,fontWeight:700,color:theme.gray700,marginBottom:14,
            textTransform:"uppercase",letterSpacing:.8}}>📅 Vencimentos — próximos e recentes 30 dias</p>
          {proximosVenc.length===0
            ? <EmptyState icon="📅" title="Nenhum vencimento no período" subtitle=""/>
            : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {proximosVenc.map((l,i)=>{
                  const vencido = l.diff < 0;
                  const hoje0 = l.diff === 0;
                  const cor = vencido ? theme.red : hoje0 ? theme.amber : theme.green;
                  return (
                    <div key={l.id||i} style={{display:"flex",gap:14,alignItems:"center",
                      padding:"10px 14px",borderRadius:8,
                      background:vencido?"#fff8f8":hoje0?"#fffbec":"#f0fdf4",
                      border:`1px solid ${vencido?"#fecaca":hoje0?"#fde68a":"#bbf7d0"}`}}>
                      <div style={{textAlign:"center",minWidth:52,padding:"6px 8px",borderRadius:7,
                        background:cor+"18"}}>
                        <p style={{fontSize:14,fontWeight:700,color:cor,margin:0}}>
                          {l.diff===0?"Hoje":l.diff>0?`+${l.diff}d`:`${l.diff}d`}
                        </p>
                      </div>
                      <div style={{flex:1}}>
                        <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:0}}>{l.cl?.nome||"—"}</p>
                        <p style={{fontSize:11,color:theme.gray500,margin:"2px 0 0"}}>
                          {l.descricao||"Honorários"} · {fmtMoedaL(l.valor)} · {fmtData(l.dataVencimento)}
                        </p>
                      </div>
                      <span style={{fontSize:11,padding:"3px 10px",borderRadius:5,fontWeight:700,
                        background:cor+"18",color:cor}}>
                        {vencido?"Vencido":hoje0?"Vence hoje":"A vencer"}
                      </span>
                    </div>
                  );
                })}
              </div>
          }
        </Card>
      )}

      {/* ABA HISTÓRICO */}
      {abaC==="historico" && (
        <Card>
          <p style={{fontSize:12,fontWeight:700,color:theme.gray700,marginBottom:14,
            textTransform:"uppercase",letterSpacing:.8}}>📋 Histórico de Cobranças Enviadas</p>
          {cobHistorico.length===0
            ? <EmptyState icon="📋" title="Nenhuma cobrança enviada ainda" subtitle=""/>
            : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {[...cobHistorico].reverse().map((h,i)=>{
                  const cfg = REGUA_COBRANCA[h.tipo] || {};
                  return (
                    <div key={h.id||i} style={{padding:"10px 14px",borderRadius:8,background:theme.white,
                      border:`1px solid ${theme.gray100}`,borderLeft:`3px solid ${cfg.cor||theme.gray400}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <p style={{fontSize:13,fontWeight:600,color:theme.gray900,margin:0}}>
                            {cfg.icon||"💰"} {h.titulo}
                          </p>
                          <p style={{fontSize:11,color:theme.gray500,margin:"3px 0 0"}}>
                            {getNomeCli(h.clienteId)} · {fmtMoedaL(h.valor)}
                          </p>
                        </div>
                        <span style={{fontSize:10,color:theme.gray400,whiteSpace:"nowrap",marginLeft:12}}>
                          {new Date(h.dataEnvio).toLocaleDateString("pt-BR")}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
          }
        </Card>
      )}

      {/* ABA MODELOS */}
      {abaC==="modelos" && (
        <ModModelosCobranca data={data} setData={setData} bancos={bancos} />
      )}
    </div>
  );
}

// ── Modelos de cobrança editáveis ──────────────────────────
function ModModelosCobranca({ data, setData, bancos }) {
  const mc = data.modelosCobranca || {};
  const DEFAULTS = {
    cob_m3:  "Prezado(a) [NOME],\n\nEsperamos que esteja bem. Passamos para lembrá-lo(a), de forma amigável, que a fatura referente aos honorários advocatícios vencerá em 3 (três) dias — [VENCIMENTO].\n\nDetalhes:\n• Referência: [DESCRIÇÃO]\n• Valor: [VALOR]\n\n[DADOS PAGAMENTO]\n\nAgradecemos pela confiança!\n\nAtenciosamente,\nAlves & Correa Advogados\nDr. Nelson José Alves · OAB/SP 398.875\nDra. Annie Lemos Alves Correa · OAB/SP 446.942",
    cob_0:   "Prezado(a) [NOME],\n\nInformamos que hoje é a data de vencimento dos honorários advocatícios referentes a: [DESCRIÇÃO].\n\nValor: [VALOR]\n[DADOS PAGAMENTO]\n\nCaso haja qualquer dificuldade, estamos disponíveis para encontrar a melhor solução.\n\nAtenciosamente,\nAlves & Correa Advogados\nDr. Nelson José Alves · OAB/SP 398.875\nDra. Annie Lemos Alves Correa · OAB/SP 446.942",
    cob_p3:  "Prezado(a) [NOME],\n\nVerificamos que o pagamento referente aos honorários com vencimento em [VENCIMENTO] ainda não foi identificado em nosso sistema.\n\nPendência: [VALOR]\n[DADOS PAGAMENTO]\n\nCaso o pagamento já tenha sido realizado, por gentileza encaminhe o comprovante. Estamos à disposição.\n\nAtenciosamente,\nAlves & Correa Advogados\nDr. Nelson José Alves · OAB/SP 398.875\nDra. Annie Lemos Alves Correa · OAB/SP 446.942",
    cob_p10: "Prezado(a) [NOME],\n\nConstatamos que, transcorridos 10 dias do vencimento ([VENCIMENTO]), o débito referente a [DESCRIÇÃO] — [VALOR] — permanece em aberto.\n\nSolicitamos a regularização no prazo máximo de 5 dias corridos.\n📞 contato@alvescorreaadvogados.com.br\n\nAtenciosamente,\nAlves & Correa Advogados\nDr. Nelson José Alves · OAB/SP 398.875\nDra. Annie Lemos Alves Correa · OAB/SP 446.942",
    cob_p30: "Prezado(a) [NOME],\n\nEste escritório vem comunicar formalmente que o débito de honorários vencido em [VENCIMENTO] — [VALOR] — permanece em aberto após múltiplos contatos.\n\nDiante da ausência de regularização, serão adotadas as medidas previstas em contrato (suspensão dos serviços, cobrança judicial).\n\nÉ possível evitar essas medidas mediante regularização imediata ou contato até [DATA LIMITE].\n\nAtenciosamente,\nAlves & Correa Advogados\nDr. Nelson José Alves · OAB/SP 398.875\nDra. Annie Lemos Alves Correa · OAB/SP 446.942",
  };

  const ITENS = Object.entries(REGUA_COBRANCA).map(([tipo,cfg])=>({tipo,cfg}));

  return (
    <div>
      <div style={{padding:"10px 14px",borderRadius:8,background:"#fffbec",border:"1px solid #fde68a",marginBottom:16}}>
        <p style={{fontSize:12,color:"#92400e",margin:0}}>
          Variáveis automáticas: <strong>[NOME]</strong> · <strong>[VALOR]</strong> · <strong>[VENCIMENTO]</strong> · <strong>[DESCRIÇÃO]</strong> · <strong>[DADOS PAGAMENTO]</strong> · <strong>[DATA LIMITE]</strong>
        </p>
      </div>
      {ITENS.map(({tipo,cfg})=>(
        <ModeloCobrancaItem key={tipo} tipo={tipo} cfg={cfg}
          textoPadrao={DEFAULTS[tipo]||""}
          customizado={mc[tipo]}
          setData={setData}/>
      ))}
      {/* PDF Notificação Extrajudicial */}
      <div style={{marginTop:20,padding:"14px 16px",borderRadius:10,background:theme.gray50,border:`1px solid ${theme.gray200}`}}>
        <p style={{fontSize:13,fontWeight:700,color:theme.gray800,marginBottom:4}}>⚖️ PDF — Notificação Extrajudicial por Inadimplência</p>
        <p style={{fontSize:11,color:theme.gray500,marginBottom:12}}>Documento formal para acionamento judicial. Gerado sob demanda, não é disparado automaticamente.</p>
        <button onClick={()=>{
          const win = window.open("","_blank","width=900,height=700");
          if(!win) return alert("Permita pop-ups.");
          win.document.write(gerarPDFNotificacaoExtrajudicial());
          win.document.close(); win.focus();
          setTimeout(()=>win.print(),600);
        }} style={{padding:"9px 20px",borderRadius:7,border:"none",background:theme.gray900,
          color:theme.gold,cursor:"pointer",fontSize:12,fontWeight:700}}>
          📄 Gerar PDF — Notificação Extrajudicial
        </button>
      </div>
    </div>
  );
}

function ModeloCobrancaItem({ tipo, cfg, textoPadrao, customizado, setData }) {
  const textoAtual = customizado ?? textoPadrao;
  const [editando, setEditando] = React.useState(false);
  const [rascunho, setRascunho] = React.useState(textoAtual);
  React.useEffect(()=>{ setRascunho(customizado ?? textoPadrao); },[customizado, textoPadrao]);

  return (
    <div style={{marginBottom:14,borderRadius:10,overflow:"hidden",
      border:`1px solid ${editando?cfg.cor:theme.gray200}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        padding:"10px 16px",background:editando?cfg.cor+"0A":theme.gray50}}>
        <div>
          <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:0}}>{cfg.icon} {cfg.titulo}</p>
          {customizado&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:3,
            background:"#fef3c7",color:"#92400e",fontWeight:700}}>✏️ Customizado</span>}
        </div>
        <div style={{display:"flex",gap:6}}>
          {customizado&&<button onClick={()=>{
            setData(p=>({...p,modelosCobranca:{...(p.modelosCobranca||{}),[tipo]:undefined}}));
            setRascunho(textoPadrao); setEditando(false);
          }} style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${theme.red}`,
            background:"#fef2f2",color:theme.red,cursor:"pointer",fontSize:11,fontWeight:600}}>
            ↩ Restaurar
          </button>}
          <button onClick={()=>{setEditando(e=>!e); setRascunho(textoAtual);}}
            style={{padding:"5px 12px",borderRadius:6,
              border:`1px solid ${editando?cfg.cor:theme.gray200}`,
              background:editando?cfg.cor+"10":theme.white,
              color:editando?cfg.cor:theme.gray600,cursor:"pointer",fontSize:11,fontWeight:600}}>
            {editando?"✕ Fechar":"✏️ Editar"}
          </button>
        </div>
      </div>
      <div style={{padding:"12px 16px",background:theme.white}}>
        {!editando
          ? <pre style={{fontSize:12,color:theme.gray700,lineHeight:1.7,whiteSpace:"pre-wrap",fontFamily:"inherit",margin:0}}>{textoAtual}</pre>
          : <>
              <textarea value={rascunho} onChange={e=>setRascunho(e.target.value)} rows={8}
                style={{width:"100%",padding:"10px 12px",border:`1px solid ${cfg.cor}`,borderRadius:7,
                  fontSize:12,fontFamily:"inherit",resize:"vertical",outline:"none",boxSizing:"border-box",lineHeight:1.7}}/>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:10}}>
                <button onClick={()=>setEditando(false)} style={{padding:"7px 16px",borderRadius:6,
                  border:`1px solid ${theme.gray200}`,background:theme.white,cursor:"pointer",fontSize:12}}>Cancelar</button>
                <button onClick={()=>{
                  setData(p=>({...p,modelosCobranca:{...(p.modelosCobranca||{}),[tipo]:rascunho}}));
                  setEditando(false);
                }} style={{padding:"7px 18px",borderRadius:6,border:"none",background:cfg.cor,
                  color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>💾 Salvar</button>
              </div>
            </>
        }
      </div>
    </div>
  );
}

const gerarPDFNotificacaoExtrajudicial = () => `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>Notificação Extrajudicial — Alves & Correa Advogados</title>
<style>
@page{size:A4;margin:25mm 20mm 25mm 20mm}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:12pt;color:#1A1610;line-height:1.8}
.hdr{text-align:center;border-bottom:2px solid #C9A84C;padding-bottom:14px;margin-bottom:20px}
.hdr h2{font-size:14pt;color:#1A1610}
.hdr p{font-size:10pt;color:#888;margin-top:4px}
h1{font-size:14pt;font-weight:bold;text-transform:uppercase;text-align:center;margin:20px 0 16px;letter-spacing:1px}
.data{text-align:right;margin-bottom:20px;font-size:11pt}
p{margin-bottom:12px;font-size:12pt;line-height:1.8}
.destaque{background:#fff8e1;border:1px solid #ffd54f;border-radius:4px;padding:10px 14px;margin:14px 0;font-weight:bold;text-align:center;font-size:12pt}
.medidas{margin:12px 0 12px 24px}
.medidas li{margin-bottom:6px;font-size:12pt}
.assin{display:grid;grid-template-columns:1fr 1fr;gap:0 60px;margin-top:40px;padding-top:16px;border-top:1px solid #ccc}
.assin-item{text-align:center}
.assin-linha{border-top:1px solid #555;margin-bottom:8px;margin-top:40px}
.ftr{margin-top:20px;padding-top:10px;border-top:1px solid #ddd;font-size:9pt;color:#aaa;text-align:center}
</style></head><body>
<div class="hdr">
  <h2>Alves &amp; Correa Advogados</h2>
  <p>Dr. Nelson José Alves · OAB/SP 398.875 | Dra. Annie Lemos Alves Correa · OAB/SP 446.942</p>
  <p>São Paulo/SP · Foz do Iguaçu/PR · contato@alvescorreaadvogados.com.br</p>
</div>
<h1>Notificação Extrajudicial</h1>
<p class="data">São Paulo, _____ de __________________ de _______</p>
<p><strong>Notificado(a):</strong> _______________________________________________</p>
<p><strong>CPF/CNPJ:</strong> _______________________________________________</p>
<p><strong>Endereço:</strong> _______________________________________________</p>
<p>Este escritório vem, por meio da presente notificação extrajudicial, comunicar formalmente que o débito de honorários advocatícios vencido em ___/___/______, no valor de R$ __________________, referente a __________________________________, permanece em aberto, não obstante os contatos realizados nas datas ________________.</p>
<p>Diante da ausência de regularização ou de qualquer manifestação, este escritório se vê compelido a adotar as medidas previstas no instrumento contratual, quais sejam:</p>
<ul class="medidas">
  <li>I. Suspensão imediata de todos os trabalhos em andamento;</li>
  <li>II. Retenção de documentos e peças processuais até a quitação integral do débito;</li>
  <li>III. Encaminhamento do caso à cobrança judicial e/ou inclusão em cadastro de inadimplentes.</li>
</ul>
<div class="destaque">🔴 DÉBITO TOTAL: R$ __________________ · Prazo para regularização: ___/___/______</div>
<p>Ressaltamos que ainda é possível evitar o encaminhamento judicial mediante regularização imediata ou contato para negociação até a data limite indicada acima.</p>
<p>Este documento serve como notificação extrajudicial formal, nos termos do art. 397 do Código Civil Brasileiro, produzindo todos os efeitos legais pertinentes.</p>
<div class="assin">
  <div class="assin-item"><div class="assin-linha"></div>
    <strong>Nelson José Alves</strong><br><span style="font-size:10pt;color:#888">Advogado · OAB/SP 398.875</span>
  </div>
  <div class="assin-item"><div class="assin-linha"></div>
    <strong>Annie Lemos Alves Correa</strong><br><span style="font-size:10pt;color:#888">Advogada · OAB/SP 446.942</span>
  </div>
</div>
<div class="ftr">Alves &amp; Correa Advogados · São Paulo/SP · Foz do Iguaçu/PR · contato@alvescorreaadvogados.com.br</div>
</body></html>`;

function ModFinanceiro({ data, setData }) {
  const lancamentos = data.lancamentos  || [];
  const despesas    = data.despesas     || [];
  const clientes    = data.clientes     || [];
  const contratos   = data.contratos   || [];
  const produtos    = data.produtos    || [];
  const equipe      = data.equipe      || [];
  const bancos      = data.bancos      || [];

  const [aba,         setAba]         = useState("dashboard");
  const [filtroStatus,setFiltroStatus]= useState("");
  const [filtroCliente,setFiltroCliente]=useState("");
  const [busca,       setBusca]       = useState("");
  const [periodoFiltro,setPeriodoFiltro]=useState("mes"); // mes|tri|sem|ano|todos
  const [periodoIni,  setPeriodoIni]  = useState("");
  const [periodoFim,  setPeriodoFim]  = useState("");
  const [modalLan,    setModalLan]    = useState(false);
  const [modalDesp,   setModalDesp]   = useState(false);
  const [modalBaixa,  setModalBaixa]  = useState(false);
  const [modalBanco,  setModalBanco]  = useState(false);
  const [editLan,     setEditLan]     = useState(null);
  const [viewLan,     setViewLan]     = useState(null);
  const [editDesp,    setEditDesp]    = useState(null);
  const [editBanco,   setEditBanco]   = useState(null);
  const [itemBaixa,   setItemBaixa]   = useState(null); // {item, tipo:"lan"|"desp"}
  const [baixaDados,  setBaixaDados]  = useState({data:"", bancoId:"", obs:""});

  const hoje = new Date().toISOString().slice(0,10);
  const mesAtual = new Date().toISOString().slice(0,7);

  const fmtMoeda = v => `R$ ${(parseFloat(v)||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}`;
  const fmtData  = d => d ? new Date(d+"T12:00:00").toLocaleDateString("pt-BR") : "—";
  const getNomeCli = id => clientes.find(c=>c.id===id)?.nome||"—";
  const getNomeBanco = id => bancos.find(b=>b.id===id)?.nome||"—";
  const getNomeProd = id => produtos.find(p=>p.id===id)?.nome||"—";

  // Cálculos globais
  // Calcular datas do período selecionado
  const getPeriodoDatas = () => {
    const hoje2 = new Date();
    if(periodoFiltro==="mes")  return [hoje2.toISOString().slice(0,7)+"-01", hoje2.toISOString().slice(0,10)];
    if(periodoFiltro==="tri")  { const d=new Date(hoje2); d.setMonth(d.getMonth()-2); return [d.toISOString().slice(0,7)+"-01",hoje2.toISOString().slice(0,10)]; }
    if(periodoFiltro==="sem")  { const d=new Date(hoje2); d.setMonth(d.getMonth()-5); return [d.toISOString().slice(0,7)+"-01",hoje2.toISOString().slice(0,10)]; }
    if(periodoFiltro==="ano")  return [hoje2.getFullYear()+"-01-01",hoje2.toISOString().slice(0,10)];
    if(periodoFiltro==="custom") return [periodoIni||"2000-01-01",periodoFim||hoje2.toISOString().slice(0,10)];
    return ["2000-01-01",hoje2.toISOString().slice(0,10)]; // todos
  };
  const [pIni,pFim] = getPeriodoDatas();
  const lanPeriodo  = lancamentos.filter(l=>(l.dataVencimento||l.dataRecebimento||"")>=pIni&&(l.dataVencimento||l.dataRecebimento||"")<=pFim);
  const despPeriodo = despesas.filter(d=>(d.data||d.dataPagamento||"")>=pIni&&(d.data||d.dataPagamento||"")<=pFim);

  const totalRecebido  = lancamentos.filter(l=>l.status==="Recebido").reduce((s,l)=>s+(l.valor||0),0);
  // Helper: valor pendente real (saldo devedor para parciais)
  const calcValorPendente = l => {
    if(l.status==="Recebido Parcial") return parseFloat(l.saldoDevedor)||0;
    if(["Recebido","Cancelado","Estimado (Projeção)"].includes(l.status)) return 0;
    return parseFloat(l.valor)||0;
  };
  const totalAReceber      = lanPeriodo.reduce((s,l)=>s+calcValorPendente(l),0);
  const totalAReceberGeral = lancamentos.reduce((s,l)=>s+calcValorPendente(l),0);
  const totalPago      = despesas.filter(d=>d.status==="Pago").reduce((s,d)=>s+(d.valor||0),0);
  // Helper: valor pendente real para despesas (saldo devedor para parciais)
  const calcValorPendenteDesp = d => {
    if(d.status==="Pago Parcial") return parseFloat(d.saldoDevedor)||0;
    if(["Pago","Cancelado"].includes(d.status)) return 0;
    return parseFloat(d.valor)||0;
  };
  const totalAPagar      = despPeriodo.reduce((s,d)=>s+calcValorPendenteDesp(d),0);
  const totalAPagarGeral = despesas.reduce((s,d)=>s+calcValorPendenteDesp(d),0);
  const saldoLiquido   = totalRecebido - totalPago;

  // Vencidos
  const lanVencidos    = lancamentos.filter(l=>l.status==="Pendente"&&l.dataVencimento&&l.dataVencimento<hoje);
  const despVencidas   = despesas.filter(d=>d.status==="Pendente"&&d.data&&d.data<hoje);

  // Recebimentos do mês
  const recebMes = lancamentos.filter(l=>l.status==="Recebido"&&(l.dataRecebimento||"").slice(0,7)===mesAtual)
    .reduce((s,l)=>s+(l.valor||0),0);
  const pagMes = despesas.filter(d=>d.status==="Pago"&&(d.dataPagamento||d.data||"").slice(0,7)===mesAtual)
    .reduce((s,d)=>s+(d.valor||0),0);

  // MC por produto (valor contratado - custo do produto)
  const mcPorProduto = produtos.map(p=>{
    const contsP = contratos.filter(c=>c.produtoId===p.id);
    const valorTotal = contsP.reduce((s,c)=>s+(c.valorContratado||0),0);
    const custoTotal = contsP.length * (parseFloat(p.custo)||0);
    const mc = valorTotal - custoTotal;
    const mcPerc = valorTotal>0 ? Math.round(mc/valorTotal*100) : 0;
    return { id:p.id, nome:p.nome, area:p.area, valorTotal, custoTotal, mc, mcPerc, qtd:contsP.length };
  }).filter(p=>p.valorTotal>0).sort((a,b)=>b.mc-a.mc);

  const mcPorArea = {};
  mcPorProduto.forEach(p=>{
    if(!mcPorArea[p.area]) mcPorArea[p.area]={area:p.area,valorTotal:0,custoTotal:0,mc:0};
    mcPorArea[p.area].valorTotal+=p.valorTotal;
    mcPorArea[p.area].custoTotal+=p.custoTotal;
    mcPorArea[p.area].mc+=p.mc;
  });
  const mcAreaArr = Object.values(mcPorArea).sort((a,b)=>b.mc-a.mc);

  // Baixar recebimento ou pagamento
  const abrirBaixa = (item, tipo) => {
    setItemBaixa({item, tipo});
    setBaixaDados({data:hoje, bancoId:bancos[0]?.id||"", valorBaixa:String(item.valor||""), obs:""});
    setModalBaixa(true);
  };

  const confirmarBaixa = () => {
    if(!baixaDados.bancoId) return alert("Selecione o banco.");
    if(!baixaDados.valorBaixa) return alert("Informe o valor.");
    if(!itemBaixa) return;
    const {item, tipo} = itemBaixa;
    // Se recebimento parcial anterior: usar saldo devedor como base
    const valorOriginal = (item.status==="Recebido Parcial" && parseFloat(item.saldoDevedor)>0)
      ? parseFloat(item.saldoDevedor)
      : parseFloat(item.valor)||0;
    const valorOriginalTotal = parseFloat(item.valor)||0;
    const valorPago     = parseFloat(baixaDados.valorBaixa)||0;
    const diferenca     = valorPago - valorOriginal;

    if(tipo==="lan") {
      let novoStatus = "Recebido";
      let obsExtra   = baixaDados.obs||"";
      let novasLancs = null;

      if(valorPago < valorOriginal) {
        // Recebimento parcial — registrar no histórico, manter saldo no mesmo lançamento
        novoStatus = "Recebido Parcial";
        const saldo = Math.round((valorOriginal - valorPago) * 100) / 100;
        obsExtra = (obsExtra?obsExtra+" | ":"") + `Parcial: ${fmtMoeda(valorPago)} em ${baixaDados.data||hoje}. Saldo: ${fmtMoeda(saldo)}`;
        const novoPgto = {
          id: "PGT"+Date.now(),
          data: baixaDados.data || hoje,
          valor: valorPago,
          banco: baixaDados.bancoId || "",
          obs: baixaDados.obs || ""
        };
        novasLancs = (prev) => prev.map(l => l.id === item.id
          ? {...l,
              status: "Recebido Parcial",
              saldoDevedor: saldo,
              proximoVencimento: baixaDados.proximoVencimento || "",
              pagamentos: [...(l.pagamentos||[]), novoPgto],
              obs: obsExtra
            }
          : l
        );
      } else if(valorPago === valorOriginal && item.status === "Recebido Parcial") {
        // Quitação do saldo restante — marcar como Recebido total
        novoStatus = "Recebido";
        const totalPagoAntes = (item.pagamentos||[]).reduce((s,p)=>s+(parseFloat(p.valor)||0),0);
        const novoPgto = {
          id: "PGT"+Date.now(),
          data: baixaDados.data || hoje,
          valor: valorPago,
          banco: baixaDados.bancoId || "",
          obs: baixaDados.obs || ""
        };
        obsExtra = `Quitado. Total recebido: ${fmtMoeda(totalPagoAntes + valorPago)}`;
        novasLancs = (prev) => prev.map(l => l.id === item.id
          ? {...l,
              status: "Recebido",
              saldoDevedor: 0,
              proximoVencimento: "",
              dataRecebimento: baixaDados.data || hoje,
              bancoBaixa: baixaDados.bancoId || "",
              pagamentos: [...(l.pagamentos||[]), novoPgto],
              obs: obsExtra
            }
          : l
        );
      } else if(valorPago > valorOriginal) {
        // Recebimento com juros/acréscimo
        novoStatus = "Recebido";
        obsExtra = (obsExtra?obsExtra+" | ":"") + `Valor original: ${fmtMoeda(valorOriginal)} | Acréscimo: ${fmtMoeda(diferenca)}`;
      }

      setData(prev=>{
        // Se recebimento parcial: usar novasLancs que já faz o map completo
        // Se recebimento total/acréscimo: fazer map padrão + append opcional
        const lansAtt = novasLancs && (valorPago < valorOriginal || (valorPago === valorOriginal && item.status === "Recebido Parcial"))
          ? novasLancs(prev.lancamentos)
          : [
              ...prev.lancamentos.map(l=>
                l.id===item.id ? {...l,
                  status:novoStatus, valor:valorPago,
                  dataRecebimento:baixaDados.data,
                  bancoBaixa:baixaDados.bancoId,
                  obs: obsExtra ? ((l.obs?l.obs+" | ":"")+obsExtra) : l.obs
                } : l
              ),
            ];
        const contrAtt = item.contratoId ? prev.contratos.map(ct=>{
          if(ct.id !== item.contratoId) return ct;
          // Calcular valorRecebido somando os pagamentos registrados de cada lançamento
          const recebido = lansAtt
            .filter(l=>l.contratoId===ct.id)
            .reduce((s,l)=>{
              if(l.status==='Recebido') {
                // Se tem histórico de pagamentos, somar os pagamentos
                const pgtos = l.pagamentos||[];
                if(pgtos.length>0) return s + pgtos.reduce((ps,p)=>ps+(parseFloat(p.valor)||0),0);
                return s + (parseFloat(l.valorPagoFinal)||parseFloat(l.valor)||0);
              }
              if(l.status==='Recebido Parcial') {
                // Somar pagamentos já registrados
                return s + (l.pagamentos||[]).reduce((ps,p)=>ps+(parseFloat(p.valor)||0),0);
              }
              return s;
            }, 0);
          return {...ct,
            valorRecebido: parseFloat(recebido.toFixed(2)),
            valorAReceber: parseFloat(Math.max(0,(ct.valorContratado||0)-recebido).toFixed(2))
          };
        }) : prev.contratos;
        // Gerar despesa de comissão de parceria baseada no valor efetivamente recebido
        const contrato = prev.contratos.find(ct=>ct.id===item.contratoId);
        const despComissaoNova = (contrato?.temParceria && contrato?.parceiraIdContrato) ? (() => {
          const parc = (data.parcerias||[]).find(p=>p.id===contrato.parceiraIdContrato);
          const percComissao = parseFloat(contrato.percComissaoContrato) || 0;
          const valorComissao = Math.round((valorPago * percComissao / 100) * 100) / 100;
          if(valorComissao <= 0) return null;
          // Vencimento: dia 5 do mês seguinte ao recebimento
          const dRec = new Date((baixaDados.data||new Date().toISOString().slice(0,10))+"T12:00:00");
          dRec.setMonth(dRec.getMonth()+1); dRec.setDate(5);
          const vencComissao = dRec.toISOString().slice(0,10);
          const cli = prev.clientes?.find(cl=>cl.id===contrato.clienteId);
          return {
            id: "DESP_PAR_"+Date.now().toString().slice(-8),
            descricao: "Comissão Parceria — "+contrato.numContrato+" — "+(cli?.nome||"")+" — "+(parc?.nome||""),
            grupo: "Custo", categoria: "Comissão de Parceria",
            valor: valorComissao,
            data: vencComissao, dataPagamento: "",
            contratoId: contrato.id, status: "A Pagar",
            bancoBaixa: "",
            obs: `Comissão ${percComissao}% sobre recebimento de ${fmtMoeda(valorPago)} em ${baixaDados.data||hoje}`
          };
        })() : null;
        const despAtt = despComissaoNova
          ? [...(prev.despesas||[]), despComissaoNova]
          : (prev.despesas||[]);
        return {...prev, lancamentos:lansAtt, contratos:contrAtt, despesas:despAtt};
      });
    } else {
      // ── Pagamento de despesa ──────────────────────────────
      // Base de comparação: saldo devedor se parcial anterior
      const valorBaseDesp = (item.status==="Pago Parcial" && parseFloat(item.saldoDevedor)>0)
        ? parseFloat(item.saldoDevedor)
        : valorOriginal;

      if(valorPago < valorBaseDesp) {
        // Pagamento parcial — histórico, sem criar nova despesa [SALDO]
        const saldo = Math.round((valorBaseDesp - valorPago)*100)/100;
        const novoPgtoDesp = {
          id: "PGT"+Date.now(),
          data: baixaDados.data || hoje,
          valor: valorPago,
          banco: baixaDados.bancoId || "",
          obs: baixaDados.obs || ""
        };
        const obsExtra = `Parcial: ${fmtMoeda(valorPago)} em ${baixaDados.data||hoje}. Saldo: ${fmtMoeda(saldo)}`;
        setData(prev=>({...prev, despesas: prev.despesas.map(d=>
          d.id===item.id ? {...d,
            status: "Pago Parcial",
            saldoDevedor: saldo,
            proximoVencimento: baixaDados.proximoVencimento || "",
            pagamentos: [...(d.pagamentos||[]), novoPgtoDesp],
            obs: obsExtra
          } : d
        )}));
      } else if(valorPago === valorBaseDesp && item.status==="Pago Parcial") {
        // Quitação do saldo restante
        const totalPagoAntes = (item.pagamentos||[]).reduce((s,p)=>s+(parseFloat(p.valor)||0),0);
        const novoPgtoDesp = {
          id: "PGT"+Date.now(),
          data: baixaDados.data || hoje,
          valor: valorPago,
          banco: baixaDados.bancoId || "",
          obs: baixaDados.obs || ""
        };
        setData(prev=>({...prev, despesas: prev.despesas.map(d=>
          d.id===item.id ? {...d,
            status: "Pago",
            saldoDevedor: 0,
            proximoVencimento: "",
            dataPagamento: baixaDados.data || hoje,
            bancoBaixa: baixaDados.bancoId || "",
            pagamentos: [...(d.pagamentos||[]), novoPgtoDesp],
            obs: `Quitado. Total pago: ${fmtMoeda(totalPagoAntes + valorPago)}`
          } : d
        )}));
      } else {
        // Pagamento total ou com acréscimo
        const obsExtra = valorPago > valorOriginal
          ? `${baixaDados.obs||""} | Valor original: ${fmtMoeda(valorOriginal)} | Acréscimo: ${fmtMoeda(diferenca)}`
          : (baixaDados.obs||"");
        setData(prev=>({...prev, despesas: prev.despesas.map(d=>
          d.id===item.id ? {...d,
            status: "Pago",
            dataPagamento: baixaDados.data,
            bancoBaixa: baixaDados.bancoId,
            obs: obsExtra ? ((d.obs?d.obs+" | ":"")+obsExtra) : d.obs
          } : d
        )}));
      }
    }
    setModalBaixa(false);
    setItemBaixa(null);
  };

  const salvarLan = () => {
    if(!editLan.clienteId||!editLan.valor) return alert("Cliente e valor são obrigatórios.");
    const id = editLan.id||"LAN"+Date.now().toString().slice(-6);
    const novo = {...editLan, id, valor:parseFloat(editLan.valor)||0};
    setData(prev=>{
      const lansAtt = editLan.id
        ? prev.lancamentos.map(l=>l.id===id?novo:l)
        : [...prev.lancamentos, novo];
      const contrAtt = novo.contratoId ? prev.contratos.map(ct=>{
        if(ct.id!==novo.contratoId) return ct;
        const recebido = lansAtt
          .filter(l=>l.contratoId===ct.id&&(l.status==='Recebido'||l.status==='Recebido Parcial'))
          .reduce((s,l)=>s+(parseFloat(l.valor)||0), 0);
        return {...ct,
          valorRecebido: parseFloat(recebido.toFixed(2)),
          valorAReceber: parseFloat(Math.max(0,(ct.valorContratado||0)-recebido).toFixed(2))
        };
      }) : prev.contratos;
      return {...prev, lancamentos:lansAtt, contratos:contrAtt};
    });
    setModalLan(false);
  };

  const salvarDesp = () => {
    if(!editDesp.descricao||!editDesp.valor) return alert("Descrição e valor são obrigatórios.");
    const id = editDesp.id||"DESP"+Date.now().toString().slice(-6);

    // Edição de despesa existente — atualizar apenas
    if(editDesp.id) {
      const novo = {...editDesp, id, valor:parseFloat(editDesp.valor)||0};
      setData(prev=>({...prev, despesas: prev.despesas.map(d=>d.id===id?novo:d)}));
      setModalDesp(false);
      return;
    }

    const tipo  = editDesp.tipoLancDesp || "unico";
    const valor = parseFloat(editDesp.valor)||0;
    const base  = editDesp.vencPrimeiraDesp || editDesp.data || hoje;

    const gerarVencDesp = (baseDate, idx) => {
      if(!baseDate) return "";
      const d = new Date(baseDate+"T12:00:00");
      d.setMonth(d.getMonth()+idx);
      return d.toISOString().slice(0,10);
    };

    let novasDespesas = [];

    if(tipo === "parcelado") {
      const n = parseInt(editDesp.numParcelasDesp)||2;
      const vlrParcela = Math.round((valor/n)*100)/100;
      novasDespesas = Array.from({length:n}, (_,i) => ({
        ...editDesp,
        id: "DESP"+(Date.now()+i).toString().slice(-7),
        valor: i===n-1 ? Math.round((valor-vlrParcela*(n-1))*100)/100 : vlrParcela,
        data: gerarVencDesp(base, i),
        dataPagamento: "",
        status: "A Pagar",
        descricao: `${editDesp.descricao} (${i+1}/${n})`,
        numParcela: i+1,
        totalParcelas: n,
        tipoLancDesp: "parcelado",
        obs: editDesp.obs || ""
      }));
    } else if(tipo === "recorrente") {
      const n = parseInt(editDesp.numMesesDesp)||12;
      const mesNomes = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
      novasDespesas = Array.from({length:n}, (_,i) => {
        const d = new Date(base+"T12:00:00");
        d.setMonth(d.getMonth()+i);
        const mesAno = mesNomes[d.getMonth()]+"/"+d.getFullYear();
        return {
          ...editDesp,
          id: "DESP"+(Date.now()+i).toString().slice(-7),
          valor,
          data: d.toISOString().slice(0,10),
          dataPagamento: "",
          status: "A Pagar",
          descricao: `${editDesp.descricao} — ${mesAno}`,
          tipoLancDesp: "recorrente",
          obs: editDesp.obs || ""
        };
      });
    } else {
      // Único — comportamento original
      novasDespesas = [{...editDesp, id, valor, tipoLancDesp:"unico"}];
    }

    setData(prev=>({...prev, despesas:[...(prev.despesas||[]), ...novasDespesas]}));
    setModalDesp(false);
  };

  const salvarBanco = () => {
    if(!editBanco.nome) return alert("Nome do banco é obrigatório.");
    const id = editBanco.id||"BAN"+Date.now().toString().slice(-6);
    const novo = {...editBanco, id};
    setData(prev=>({...prev, bancos: editBanco.id
      ? (prev.bancos||[]).map(b=>b.id===id?novo:b)
      : [...(prev.bancos||[]), novo]
    }));
    setModalBanco(false);
  };

  const remover = (campo, id) => {
    if(!confirm("Remover este registro?")) return;
    setData(prev=>{
      const novosCampo = prev[campo].filter(x=>x.id!==id);
      if(campo==='lancamentos'){
        const lanRemovido = prev.lancamentos.find(x=>x.id===id);
        if(lanRemovido&&lanRemovido.contratoId){
          const contrAtt = prev.contratos.map(ct=>{
            if(ct.id!==lanRemovido.contratoId) return ct;
            // Recalcular valorRecebido/valorAReceber
            const recebido = novosCampo
              .filter(l=>l.contratoId===ct.id&&(l.status==='Recebido'||l.status==='Recebido Parcial'))
              .reduce((s,l)=>s+(parseFloat(l.valor)||0), 0);
            // Limpar lancamentoInicialId se for o lançamento removido
            const lanInicialId = ct.lancamentoInicialId===id ? '' : ct.lancamentoInicialId;
            return {...ct,
              lancamentoInicialId: lanInicialId,
              valorRecebido: parseFloat(recebido.toFixed(2)),
              valorAReceber: parseFloat(Math.max(0,(ct.valorContratado||0)-recebido).toFixed(2))
            };
          });
          return {...prev, lancamentos:novosCampo, contratos:contrAtt};
        }
      }
      return {...prev, [campo]: novosCampo};
    });
  };

  const EMPTY_LAN  = {id:"",clienteId:"",contratoId:"",produtoId:"",tipo:"Honorario",modalidade:"À vista",descricao:"",valor:"",dataVencimento:hoje,dataRecebimento:"",status:"A Receber",bancoBaixa:"",linkPagamento:"",obs:""};
  const EMPTY_DESP = {id:"",descricao:"",grupo:"Custo",categoria:"",valor:"",data:hoje,dataPagamento:"",contratoId:"",status:"A Pagar",bancoBaixa:"",obs:""};
  const EMPTY_BAN  = {id:"",nome:"",agencia:"",conta:"",titular:"Alves & Correa Advogados",tipo:"Conta Corrente PJ",status:"Ativo"};

  // Inadimplência: lançamentos pendentes com vencimento > 5 dias atrás
  const totalInadimplente = lancamentos
    .filter(l=>(l.status==="Pendente"||l.status==="A Receber"||l.status==="Recebido Parcial")&&
      (l.dataVencimento||l.proximoVencimento)&&
      ((new Date(hoje)-new Date((l.proximoVencimento||l.dataVencimento)))/(1000*60*60*24))>5)
    .reduce((s,l)=>s+calcValorPendente(l),0);

  // Ticket médio (receitas recebidas)
  const lanRecebidos = lancamentos.filter(l=>l.status==="Recebido");
  const ticketMedio = lanRecebidos.length>0
    ? lanRecebidos.reduce((s,l)=>s+(l.valor||0),0)/lanRecebidos.length : 0;

  // MC total do escritório
  const mcTotal = mcPorProduto.reduce((s,p)=>s+p.mc,0);
  const mcPercTotal = mcPorProduto.reduce((s,p)=>s+p.valorTotal,0)>0
    ? Math.round(mcTotal/mcPorProduto.reduce((s,p)=>s+p.valorTotal,0)*100) : 0;

  const COR_ST = {Recebido:"green","Recebido Parcial":"blue",Pago:"green","Pago Parcial":"blue","A Receber":"amber","A Pagar":"amber",Pendente:"amber",Vencido:"red",Cancelado:"gray"};
  const STATUS_LAN  = ["A Receber","Recebido","Recebido Parcial","Vencido","Cancelado","Estimado (Projeção)"];
  const STATUS_DESP = ["A Pagar","Pago","Pago Parcial","Vencido","Cancelado"];
  const TIPOS_LAN   = ["Honorario","Êxito","Reembolso","Adiantamento","Outros"];
  const TIPOS_DESP  = ["Custas Judiciais","Honorários Periciais","Pessoal","Infraestrutura","Marketing","Impostos","Outros"];
  const TIPOS_BANCOS = ["Conta Corrente PJ","Conta Poupança","Conta Corrente PF","Conta Digital PJ"];

  // Contas a Receber: apenas pendentes/vencidas/parcial
  const STATUS_PENDENTES_REC = ["A Receber","Pendente","Recebido Parcial","Vencido"];
  const STATUS_PENDENTES_PAG = ["A Pagar","Pendente","Pago Parcial","Vencido"];

  const recFiltrados = lancamentos.filter(l=>{
    const b=busca.toLowerCase();
    // Lista negra: excluir apenas Recebido e Cancelado (aceita A Receber, Pendente, etc.)
    const STATUS_QUITADOS_REC = ["Recebido","Cancelado"];
    const st = l.status||"A Receber";
    return !STATUS_QUITADOS_REC.includes(st)
        &&(!busca||getNomeCli(l.clienteId).toLowerCase().includes(b)||(l.descricao||"").toLowerCase().includes(b))
        &&(!filtroStatus||st===filtroStatus)
        &&(!filtroCliente||l.clienteId===filtroCliente);
  });

  const pagFiltrados = despesas.filter(d=>{
    const b=busca.toLowerCase();
    // Lista negra: excluir apenas Pago e Cancelado (aceita A Pagar, Pendente, Em andamento, etc.)
    const STATUS_QUITADOS_PAG = ["Pago","Cancelado"];
    const st = d.status||"A Pagar";
    return !STATUS_QUITADOS_PAG.includes(st)
        &&(!busca||(d.descricao||"").toLowerCase().includes(b))
        &&(!filtroStatus||st===filtroStatus);
  });

  // Receitas e Saídas: TODOS (histórico completo)
  const lanFiltrados = lancamentos.filter(l=>{
    const b=busca.toLowerCase();
    return(!busca||getNomeCli(l.clienteId).toLowerCase().includes(b)||(l.descricao||"").toLowerCase().includes(b))
        &&(!filtroStatus||l.status===filtroStatus)
        &&(!filtroCliente||l.clienteId===filtroCliente);
  });
  const despFiltradas = despesas.filter(d=>{
    const b=busca.toLowerCase();
    return(!busca||(d.descricao||"").toLowerCase().includes(b))
        &&(!filtroStatus||d.status===filtroStatus);
  });

  const ABAS = [
    ["dashboard","🏠 Dashboard"],
    ["receber","💳 Contas a Receber"],
    ["pagar","💸 Contas a Pagar"],
    ["lancamentos","📥 Receitas"],
    ["despesas","📤 Saídas"],
    ["bancos","🏦 Bancos"],
    ["cobranca","💰 Cobranças"],
  ];

  // Barra de progresso simples inline
  const BarraMC = ({perc, cor}) => (
    <div style={{background:"#eee",borderRadius:4,height:6,overflow:"hidden",flex:1}}>
      <div style={{height:"100%",background:cor||theme.green,width:`${Math.min(perc,100)}%`,borderRadius:4,transition:"width .3s"}}/>
    </div>
  );

  // Bloco D — Meta de Receita do Mês
  const metaFinanceiro  = parseFloat(data.metaFinanceiro) || 0;
  const faltamFin       = Math.max(0, metaFinanceiro - recebMes);
  const pctMetaFin      = metaFinanceiro > 0 ? Math.min(100, Math.round(recebMes/metaFinanceiro*100)) : 0;
  const [editMetaFin,  setEditMetaFin]  = useState(false);
  const [metaFinInput, setMetaFinInput] = useState("");

  const kpisData = [
    {l:"Saldo Liquido",   v:fmtMoeda(saldoLiquido),       c:saldoLiquido>=0?theme.green:theme.red,   icon:"💰", sub:"recebido - pago"},
    {l:"A Receber",       v:fmtMoeda(totalAReceberGeral), c:theme.blue,    icon:"💳", sub:lancamentos.filter(l=>l.status==="Pendente").length+" lancamentos"},
    {l:"A Pagar",         v:fmtMoeda(totalAPagar),        c:theme.amber,   icon:"💸", sub:despesas.filter(d=>d.status==="Pendente").length+" despesas"},
    {l:"Recebido/Mes",    v:fmtMoeda(recebMes),           c:theme.green,   icon:"📈", sub:"mes atual"},
    {l:"Inadimplencia",   v:fmtMoeda(totalInadimplente),  c:totalInadimplente>0?theme.red:theme.green, icon:"⚠️", sub:">5 dias vencido"},
    {l:"MC Total",        v:fmtMoeda(mcTotal),            c:mcPercTotal>=60?theme.green:mcPercTotal>=40?theme.blue:theme.amber, icon:"📊", sub:mcPercTotal+"% margem"},
  ];
  return (
    <div className="fade-in">
      <PageHeader title="Financeiro" subtitle="Gestão financeira completa do escritório"
        action={
          <div style={{display:"flex",gap:8}}>
            {aba==="receber"    &&<Btn onClick={()=>{setEditLan({...EMPTY_LAN});setModalLan(true);}}>+ Lançamento</Btn>}
            {aba==="pagar"      &&<Btn onClick={()=>{setEditDesp({...EMPTY_DESP});setModalDesp(true);}}>+ Despesa</Btn>}
            {aba==="lancamentos"&&<Btn onClick={()=>{setEditLan({...EMPTY_LAN});setModalLan(true);}}>+ Lançamento</Btn>}
            {aba==="despesas"   &&<Btn onClick={()=>{setEditDesp({...EMPTY_DESP});setModalDesp(true);}}>+ Despesa</Btn>}
            {aba==="bancos"     &&<Btn onClick={()=>{setEditBanco({...EMPTY_BAN});setModalBanco(true);}}>+ Banco</Btn>}
          </div>
        }
      />

      {/* Abas */}
      <div style={{display:"flex",gap:2,marginBottom:20,borderBottom:`2px solid ${theme.gray100}`,overflowX:"auto"}}>
        {ABAS.map(([id,lbl])=>(
          <button key={id} onClick={()=>{setAba(id);setBusca("");setFiltroStatus("");}} style={{
            padding:"10px 14px",fontSize:12,fontWeight:aba===id?700:400,cursor:"pointer",
            border:"none",background:"transparent",whiteSpace:"nowrap",
            color:aba===id?theme.gold:theme.gray500,
            borderBottom:aba===id?`2px solid ${theme.gold}`:'2px solid transparent',
            marginBottom:-2,fontFamily:"Jost,sans-serif"
          }}>{lbl}</button>
        ))}
      </div>

      {/* ══════ DASHBOARD ══════ */}
      {aba==="dashboard"&&(
        <div>
          {/* KPIs principais */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:12,marginBottom:20}}>
            {kpisData.map(k=>(
              <Card key={k.l} style={{padding:"14px 16px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div>
                    <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{k.l}</p>
                    {k.sub&&<p style={{fontSize:10,color:theme.gray400,marginTop:1}}>{k.sub}</p>}
                  </div>
                  <span style={{fontSize:16}}>{k.icon}</span>
                </div>
                <p style={{fontSize:16,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
              </Card>
            ))}
          </div>

          {/* Seletor de Período */}
          <Card style={{padding:"10px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            <span style={{fontSize:12,fontWeight:600,color:theme.gray700,flexShrink:0}}>📅 Período:</span>
            {[["mes","Mês atual"],["tri","Trimestre"],["sem","Semestre"],["ano","Ano"],["todos","Todos"],["custom","Personalizado"]].map(([v,l])=>(
              <button key={v} onClick={()=>setPeriodoFiltro(v)} style={{
                padding:"5px 12px",borderRadius:20,fontSize:12,cursor:"pointer",
                fontFamily:"Jost,sans-serif",border:"none",
                background:periodoFiltro===v?theme.gray900:'transparent',
                color:periodoFiltro===v?theme.gold:theme.gray500,
                fontWeight:periodoFiltro===v?700:400,transition:"all .15s"
              }}>{l}</button>
            ))}
            {periodoFiltro==="custom"&&(
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input type="date" value={periodoIni} onChange={e=>setPeriodoIni(e.target.value)}
                  style={{padding:"5px 8px",border:`1px solid ${theme.gray200}`,borderRadius:6,fontSize:12,outline:"none"}}/>
                <span style={{fontSize:12,color:theme.gray400}}>até</span>
                <input type="date" value={periodoFim} onChange={e=>setPeriodoFim(e.target.value)}
                  style={{padding:"5px 8px",border:`1px solid ${theme.gray200}`,borderRadius:6,fontSize:12,outline:"none"}}/>
              </div>
            )}
            <span style={{marginLeft:"auto",fontSize:11,color:theme.gray400}}>
              KPIs "A Receber" e "A Pagar" filtrados pelo período selecionado
            </span>
          </Card>

          {/* Alertas */}
          {(lanVencidos.length>0||despVencidas.length>0)&&(
            <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
              {lanVencidos.length>0&&(
                <div style={{flex:1,padding:"12px 18px",background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,display:"flex",gap:10,alignItems:"center"}}>
                  <span style={{fontSize:20}}>⚠️</span>
                  <div>
                    <p style={{fontSize:13,fontWeight:700,color:theme.red}}>{lanVencidos.length} recebimento{lanVencidos.length!==1?"s":""} vencido{lanVencidos.length!==1?"s":""}</p>
                    <p style={{fontSize:11,color:theme.red}}>{fmtMoeda(lanVencidos.reduce((s,l)=>s+(l.valor||0),0))} em atraso</p>
                  </div>
                  <Btn size="sm" variant="secondary" onClick={()=>setAba("receber")} style={{marginLeft:"auto"}}>Ver →</Btn>
                </div>
              )}
              {despVencidas.length>0&&(
                <div style={{flex:1,padding:"12px 18px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,display:"flex",gap:10,alignItems:"center"}}>
                  <span style={{fontSize:20}}>⚠️</span>
                  <div>
                    <p style={{fontSize:13,fontWeight:700,color:theme.amber}}>{despVencidas.length} pagamento{despVencidas.length!==1?"s":""} vencido{despVencidas.length!==1?"s":""}</p>
                    <p style={{fontSize:11,color:theme.amber}}>{fmtMoeda(despVencidas.reduce((s,d)=>s+(d.valor||0),0))} pendente</p>
                  </div>
                  <Btn size="sm" variant="secondary" onClick={()=>setAba("pagar")} style={{marginLeft:"auto"}}>Ver →</Btn>
                </div>
              )}
            </div>
          )}

          {/* ══ Gráfico Receita vs Despesa (6 meses) ══ */}
          <Card style={{padding:"18px 20px",marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900}}>📈 Receita vs Despesa — Últimos 6 meses</p>
              <div style={{display:"flex",gap:16,fontSize:11}}>
                <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:12,height:12,borderRadius:2,background:theme.green,display:"inline-block"}}/> Receita</span>
                <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:12,height:12,borderRadius:2,background:theme.red,display:"inline-block"}}/> Despesa</span>
                <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:12,height:12,borderRadius:2,background:theme.blue,display:"inline-block"}}/> Saldo</span>
              </div>
            </div>
            <GraficoReceitaDespesa lancamentos={lancamentos} despesas={despesas} fmtMoeda={fmtMoeda} theme={theme} />
          </Card>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
            {/* MC por Área */}
            <Card style={{padding:"18px 20px"}}>
              <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:4}}>📊 MC por Área Jurídica</p>
              <p style={{fontSize:11,color:theme.gray400,marginBottom:14}}>Margem de Contribuição = Receita − Custo por área</p>
              <MCporArea arr={mcAreaArr} fmtMoeda={fmtMoeda} theme={theme} BarraMC={BarraMC} />
            </Card>

            {/* MC por Produto (top 8) */}
            <Card style={{padding:"18px 20px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                <div>
                  <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900}}>📦 MC por Produto (Top 8)</p>
                  <p style={{fontSize:11,color:theme.gray400,marginTop:2}}>Ordenado por maior margem em R$</p>
                </div>
                {mcPorProduto.length>0&&(
                  <div style={{textAlign:"right"}}>
                    <p style={{fontSize:11,color:theme.gray400}}>MC Total</p>
                    <p style={{fontSize:15,fontWeight:700,color:mcPercTotal>=60?theme.green:theme.amber}}>{fmtMoeda(mcTotal)} ({mcPercTotal}%)</p>
                  </div>
                )}
              </div>
              <MCporProduto arr={mcPorProduto} fmtMoeda={fmtMoeda} theme={theme} BarraMC={BarraMC} Badge={Badge} />
            </Card>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            {/* Top clientes */}
            <Card style={{padding:"18px 20px"}}>
              <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:4}}>👤 Top Clientes por Receita</p>
              <p style={{fontSize:11,color:theme.gray400,marginBottom:14}}>Total recebido por cliente (todas as competências)</p>
              <TopClientes lancamentos={lancamentos} getNomeCli={getNomeCli} fmtMoeda={fmtMoeda} theme={theme} BarraMC={BarraMC} />
            </Card>

            {/* Próximos vencimentos */}
            <Card style={{padding:"18px 20px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div>
                  <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900}}>📅 Próximos Vencimentos</p>
                  <p style={{fontSize:11,color:theme.gray400,marginTop:2}}>Próximos 30 dias — recebimentos e pagamentos</p>
                </div>
                <div style={{textAlign:"right"}}>
                  <p style={{fontSize:10,color:theme.gray400}}>💳 Receber</p>
                  <p style={{fontSize:13,fontWeight:700,color:theme.blue}}>{fmtMoeda(lancamentos.filter(l=>l.status==='Pendente'&&l.dataVencimento).reduce((s,l)=>s+(l.valor||0),0))}</p>
                </div>
              </div>
              <ProximosVencimentos lancamentos={lancamentos} despesas={despesas} hoje={hoje} fmtMoeda={fmtMoeda} fmtData={fmtData} getNomeCli={getNomeCli} theme={theme} setAba={setAba} />
            </Card>

            {/* ══ Bloco D — Meta de Receita do Mês ══ */}
            <Card style={{padding:"20px 24px",marginTop:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                <p className="serif" style={{fontSize:17,fontWeight:700,color:theme.gray900}}>🎯 Meta de Receita — {new Date().toLocaleString("pt-BR",{month:"long",year:"numeric"})}</p>
                {!editMetaFin
                  ? <Btn size="sm" variant="secondary" onClick={()=>{setMetaFinInput(String(metaFinanceiro||""));setEditMetaFin(true);}}>
                      ✏️ {metaFinanceiro>0?"Editar meta":"Definir meta"}
                    </Btn>
                  : <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <input type="number" value={metaFinInput} onChange={e=>setMetaFinInput(e.target.value)}
                        placeholder="Meta em R$" min="0"
                        style={{width:140,padding:"6px 10px",border:`1px solid ${theme.gold}`,borderRadius:6,fontSize:13,outline:"none"}}/>
                      <Btn size="sm" onClick={()=>{setData(prev=>({...prev,metaFinanceiro:parseFloat(metaFinInput)||0}));setEditMetaFin(false);}}>Salvar</Btn>
                      <Btn size="sm" variant="secondary" onClick={()=>setEditMetaFin(false)}>✕</Btn>
                    </div>
                }
              </div>

              {metaFinanceiro===0
                ? <div style={{textAlign:"center",padding:"20px 0",color:theme.gray400,fontSize:13}}>
                    Clique em "Definir meta" para configurar a meta mensal de receita.
                  </div>
                : <>
                    <div style={{marginBottom:20}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                        <span style={{fontSize:12,color:theme.gray500}}>Receita recebida no mês</span>
                        <span style={{fontSize:13,fontWeight:700,color:pctMetaFin>=100?theme.green:pctMetaFin>=70?theme.amber:theme.red}}>{pctMetaFin}%</span>
                      </div>
                      <div style={{height:16,borderRadius:8,background:theme.gray100,overflow:"hidden"}}>
                        <div style={{
                          height:"100%",borderRadius:8,transition:"width .4s ease",
                          width:`${pctMetaFin}%`,
                          background: pctMetaFin>=100
                            ? `linear-gradient(90deg,${theme.green},#16a34a)`
                            : pctMetaFin>=70
                            ? `linear-gradient(90deg,${theme.amber},#d97706)`
                            : `linear-gradient(90deg,${theme.gold},#b8860b)`
                        }}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
                        <span style={{fontSize:11,color:theme.gray400}}>R$ 0</span>
                        <span style={{fontSize:11,color:theme.gray400}}>{fmtMoeda(metaFinanceiro)}</span>
                      </div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
                      {[
                        {l:"Meta do Mês",    v:fmtMoeda(metaFinanceiro), c:theme.gray700, bg:"#f8f9fa"},
                        {l:"Recebido",       v:fmtMoeda(recebMes),       c:recebMes>=metaFinanceiro?theme.green:theme.blue, bg:recebMes>=metaFinanceiro?"#f0fdf4":"#eff6ff"},
                        {l:"Faltam",         v:faltamFin>0?fmtMoeda(faltamFin):"✅ Meta atingida", c:faltamFin>0?theme.amber:theme.green, bg:faltamFin>0?"#fffbeb":"#f0fdf4"},
                        {l:"Pago no mês",    v:fmtMoeda(pagMes),         c:theme.gray700, bg:"#f8f9fa"},
                      ].map(k=>(
                        <div key={k.l} style={{padding:"14px 16px",borderRadius:8,background:k.bg,border:`1px solid ${theme.gray100}`}}>
                          <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>{k.l}</p>
                          <p style={{fontSize:15,fontWeight:700,color:k.c}}>{k.v}</p>
                        </div>
                      ))}
                    </div>
                  </>
              }
            </Card>

        </div>
          </div>
      )}

      {/* ══════ CONTAS A RECEBER ══════ */}
      {aba==="receber"&&(
        <div>
          <Card style={{padding:"12px 16px",marginBottom:14,display:"flex",gap:10,flexWrap:"wrap"}}>
            <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="🔍 Buscar cliente ou descrição..."
              style={{flex:1,minWidth:180,padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}
              onFocus={e=>e.target.style.borderColor=theme.gold} onBlur={e=>e.target.style.borderColor=theme.gray200}/>
            <select value={filtroStatus} onChange={e=>setFiltroStatus(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
              <option value="">Todos os status</option>
              {STATUS_LAN.map(s=><option key={s}>{s}</option>)}
            </select>
            <select value={filtroCliente} onChange={e=>setFiltroCliente(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
              <option value="">Todos os clientes</option>
              {clientes.map(c=><option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
            {(busca||filtroStatus||filtroCliente)&&<Btn size="sm" variant="secondary" onClick={()=>{setBusca("");setFiltroStatus("");setFiltroCliente("");}}>✕</Btn>}
          </Card>
          {/* KPIs receber */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:14}}>
            {[
              {l:"Total a Receber", v:fmtMoeda(totalAReceberGeral), c:theme.blue},
              {l:"Total Recebido",  v:fmtMoeda(totalRecebido), c:theme.green},
              {l:"Vencidos",        v:fmtMoeda(lanVencidos.reduce((s,l)=>s+(l.valor||0),0)), c:theme.red},
              {l:"Qtd. Pendentes",  v:lancamentos.filter(l=>l.status==="Pendente").length, c:theme.amber},
            ].map(k=>(
              <Card key={k.l} style={{padding:"12px 16px"}}>
                <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{k.l}</p>
                <p style={{fontSize:18,fontWeight:700,color:k.c,marginTop:4}}>{k.v}</p>
              </Card>
            ))}
          </div>
          <Card>
            {recFiltrados.length===0
              ? <EmptyState icon="💳" title="Nenhum lançamento" subtitle='Clique em "+ Lançamento" para adicionar.'/>
              : <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead>
                      <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                        {["Cliente","Descrição","Tipo","Vencimento","Recebimento","Valor","Status","Banco","Ações"].map(h=>(
                          <th key={h} style={{padding:"10px 12px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <LinhasReceitas dados={recFiltrados} hoje={hoje} fmtData={fmtData} fmtMoeda={fmtMoeda} getNomeCli={getNomeCli} getNomeBanco={getNomeBanco} COR_ST={COR_ST} Badge={Badge} Btn={Btn} theme={theme} abrirBaixa={abrirBaixa} setEditLan={setEditLan} setModalLan={setModalLan} setViewLan={setViewLan} remover={remover} />
                    </tbody>
                  </table>
                </div>
            }
          </Card>
        </div>
      )}

      {/* ══════ CONTAS A PAGAR ══════ */}
      {aba==="pagar"&&(
        <div>
          <Card style={{padding:"12px 16px",marginBottom:14,display:"flex",gap:10,flexWrap:"wrap"}}>
            <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="🔍 Buscar descrição..."
              style={{flex:1,minWidth:180,padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}
              onFocus={e=>e.target.style.borderColor=theme.gold} onBlur={e=>e.target.style.borderColor=theme.gray200}/>
            <select value={filtroStatus} onChange={e=>setFiltroStatus(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
              <option value="">Todos os status</option>
              {STATUS_DESP.map(s=><option key={s}>{s}</option>)}
            </select>
            {(busca||filtroStatus)&&<Btn size="sm" variant="secondary" onClick={()=>{setBusca("");setFiltroStatus("");}}>✕</Btn>}
          </Card>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:14}}>
            {[
              {l:"Total a Pagar",  v:fmtMoeda(totalAPagar), c:theme.amber},
              {l:"Total Pago",     v:fmtMoeda(totalPago),   c:theme.green},
              {l:"Vencidas",       v:fmtMoeda(despVencidas.reduce((s,d)=>s+(d.valor||0),0)), c:theme.red},
              {l:"Qtd. Pendentes", v:despesas.filter(d=>d.status==="Pendente").length, c:theme.amber},
            ].map(k=>(
              <Card key={k.l} style={{padding:"12px 16px"}}>
                <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{k.l}</p>
                <p style={{fontSize:18,fontWeight:700,color:k.c,marginTop:4}}>{k.v}</p>
              </Card>
            ))}
          </div>
          <Card>
            {pagFiltrados.length===0
              ? <EmptyState icon="💸" title="Nenhuma despesa" subtitle='Clique em "+ Despesa" para adicionar.'/>
              : <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead>
                      <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                        {["Descrição","Categoria","Vencimento","Pagamento","Valor","Status","Banco","Ações"].map(h=>(
                          <th key={h} style={{padding:"10px 12px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <LinhasDespesas dados={pagFiltrados} hoje={hoje} fmtData={fmtData} fmtMoeda={fmtMoeda} getNomeBanco={getNomeBanco} COR_ST={COR_ST} Badge={Badge} Btn={Btn} theme={theme} abrirBaixa={abrirBaixa} setEditDesp={setEditDesp} setModalDesp={setModalDesp} remover={remover} />
                    </tbody>
                  </table>
                </div>
            }
          </Card>
        </div>
      )}

      {/* ══════ LANÇAMENTOS (todos) ══════ */}
      {aba==="lancamentos"&&(
        <Card>
          <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
            <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="🔍 Buscar..."
              style={{flex:1,minWidth:180,padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}
              onFocus={e=>e.target.style.borderColor=theme.gold} onBlur={e=>e.target.style.borderColor=theme.gray200}/>
            <select value={filtroStatus} onChange={e=>setFiltroStatus(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
              <option value="">Todos</option>
              {STATUS_LAN.map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          {lanFiltrados.length===0
            ? <EmptyState icon="📋" title="Nenhum lançamento" subtitle='Clique em "+ Lançamento" para adicionar.'/>
            : <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead>
                    <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                      {["Cliente","Descrição","Tipo","Vencimento","Valor","Status","Data Baixa","Banco","Ações"].map(h=>(
                        <th key={h} style={{padding:"10px 12px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lanFiltrados.map((l,i)=>(
                      <tr key={l.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                        <td style={{padding:"10px 12px",fontSize:12,fontWeight:600,color:theme.gray900}}>{getNomeCli(l.clienteId)}</td>
                        <td style={{padding:"10px 12px",fontSize:12,color:theme.gray700}}>{l.descricao||"—"}</td>
                        <td style={{padding:"10px 12px"}}><Badge color="blue" style={{fontSize:9}}>{l.tipo}</Badge></td>
                        <td style={{padding:"10px 12px",fontSize:12,color:theme.gray600}}>{fmtData(l.dataVencimento)}</td>
                        <td style={{padding:"10px 12px",fontSize:13,fontWeight:700,color:theme.gray900}}>{fmtMoeda(l.valor)}</td>
                        <td style={{padding:"10px 12px"}}><Badge color={COR_ST[l.status]||"gray"}>{l.status}</Badge></td>
                        <td style={{padding:"10px 12px",fontSize:12,color:l.dataRecebimento?theme.green:theme.gray400}}>{l.dataRecebimento?fmtData(l.dataRecebimento):'—'}</td>
                        <td style={{padding:"10px 12px",fontSize:12,fontWeight:600,color:theme.gray700}}>{l.bancoBaixa?getNomeBanco(l.bancoBaixa):'—'}</td>
                        <td style={{padding:"10px 12px"}}>
                          <div style={{display:"flex",gap:4}}>
                            {(l.status==="A Receber"||l.status==="Pendente"||l.status==="Recebido Parcial")&&<Btn size="sm" onClick={()=>abrirBaixa(l,"lan")} style={{background:theme.green,color:"#fff",border:"none",fontSize:11}}>💰 Receber</Btn>}
                            <Btn size="sm" variant="secondary" onClick={()=>setViewLan(l)}>👁️</Btn>
                            <Btn size="sm" variant="secondary" onClick={()=>{setEditLan({...l});setModalLan(true);}}>✏️</Btn>
                            <Btn size="sm" variant="danger" onClick={()=>remover("lancamentos",l.id)}>×</Btn>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          }
        </Card>
      )}

      {/* ══════ DESPESAS (todas) ══════ */}
      {aba==="despesas"&&(
        <Card>
          <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
            <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="🔍 Buscar..."
              style={{flex:1,minWidth:180,padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}
              onFocus={e=>e.target.style.borderColor=theme.gold} onBlur={e=>e.target.style.borderColor=theme.gray200}/>
            <select value={filtroStatus} onChange={e=>setFiltroStatus(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
              <option value="">Todos</option>
              {STATUS_DESP.map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          {despFiltradas.length===0
            ? <EmptyState icon="📋" title="Nenhuma despesa" subtitle='Clique em "+ Despesa" para adicionar.'/>
            : <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead>
                    <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                      {["Descrição","Categoria","Vencimento","Valor","Status","Data Pagto","Banco","Ações"].map(h=>(
                        <th key={h} style={{padding:"10px 12px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {despFiltradas.map((d,i)=>(
                      <tr key={d.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                        <td style={{padding:"10px 12px",fontSize:12,fontWeight:600,color:theme.gray900}}>{d.descricao}</td>
                        <td style={{padding:"10px 12px"}}><Badge color="gray" style={{fontSize:9}}>{d.categoria||d.grupo}</Badge></td>
                        <td style={{padding:"10px 12px",fontSize:12,color:theme.gray600}}>{fmtData(d.data)}</td>
                        <td style={{padding:"10px 12px",fontSize:13,fontWeight:700,color:theme.gray900}}>{fmtMoeda(d.valor)}</td>
                        <td style={{padding:"10px 12px"}}><Badge color={COR_ST[d.status]||"gray"}>{d.status}</Badge></td>
                        <td style={{padding:"10px 12px",fontSize:12,color:d.dataPagamento?theme.green:theme.gray400}}>{d.dataPagamento?fmtData(d.dataPagamento):'—'}</td>
                        <td style={{padding:"10px 12px",fontSize:12,fontWeight:600,color:theme.gray700}}>{d.bancoBaixa?getNomeBanco(d.bancoBaixa):'—'}</td>
                        <td style={{padding:"10px 12px"}}>
                          <div style={{display:"flex",gap:4}}>
                            {(!d.status||d.status==="A Pagar"||d.status==="Pendente"||d.status==="Pago Parcial")&&<Btn size="sm" onClick={()=>abrirBaixa(d,"desp")} style={{background:theme.amber,color:"#fff",border:"none",fontSize:11}}>✅ Pagar</Btn>}
                            <Btn size="sm" variant="secondary" onClick={()=>{setEditDesp({...d});setModalDesp(true);}}>✏️</Btn>
                            <Btn size="sm" variant="danger" onClick={()=>remover("despesas",d.id)}>×</Btn>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          }
        </Card>
      )}

      {/* ══════ BANCOS ══════ */}
      {aba==="bancos"&&(
        <div>
          {bancos.length===0
            ? <Card><EmptyState icon="🏦" title="Nenhum banco cadastrado" subtitle='Clique em "+ Banco" para cadastrar as contas bancárias do escritório.'/></Card>
            : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:14}}>
                {bancos.map(b=>(
                  <Card key={b.id} style={{padding:0,overflow:"hidden",opacity:b.status==="Inativo" ? 0.6 : 1}}>
                    <div style={{padding:"14px 18px",background:`linear-gradient(135deg,${theme.gray900},${theme.gray700})`,display:"flex",alignItems:"center",gap:12}}>
                      <span style={{fontSize:28}}>🏦</span>
                      <div style={{flex:1}}>
                        <p style={{fontSize:15,fontWeight:700,color:"#fff"}}>{b.nome}</p>
                        <p style={{fontSize:11,color:"rgba(255,255,255,.6)",marginTop:2}}>{b.tipo}</p>
                      </div>
                      <Badge color={b.status==="Ativo"?"green":"gray"}>{b.status}</Badge>
                    </div>
                    <div style={{padding:"14px 18px"}}>
                      {b.agencia&&<p style={{fontSize:12,color:theme.gray700,marginBottom:4}}>Agência: <strong>{b.agencia}</strong></p>}
                      {b.conta&&  <p style={{fontSize:12,color:theme.gray700,marginBottom:4}}>Conta: <strong>{b.conta}</strong></p>}
                      <p style={{fontSize:12,color:theme.gray500,marginBottom:12}}>Titular: {b.titular}</p>
                      <div style={{display:"flex",gap:8}}>
                        <Btn size="sm" variant="secondary" onClick={()=>{setEditBanco({...b});setModalBanco(true);}}>✏️ Editar</Btn>
                        <Btn size="sm" variant="danger" onClick={()=>remover("bancos",b.id)}>×</Btn>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
          }
        </div>
      )}

      {/* ══════ ABA COBRANÇAS ══════ */}
      {aba==="cobranca"&&(
        <ModCobrancaFin
          data={data}
          setData={setData}
          lancamentos={lancamentos}
          clientes={clientes}
          contratos={contratos}
          bancos={bancos}
          fmtMoeda={fmtMoeda}
          fmtData={fmtData}
          getNomeCli={getNomeCli}
          hoje={hoje}
        />
      )}

      {/* ══════ MODAL BAIXA ══════ */}
      <Modal open={modalBaixa} onClose={()=>setModalBaixa(false)}
        title={itemBaixa?.tipo==="lan"?"💰 Registrar Recebimento":"✅ Registrar Pagamento"} width={460}>
        <ModalBaixaConteudo itemBaixa={itemBaixa} baixaDados={baixaDados} setBaixaDados={setBaixaDados} bancos={bancos} confirmarBaixa={confirmarBaixa} setModalBaixa={setModalBaixa} fmtMoeda={fmtMoeda} getNomeCli={getNomeCli} theme={theme} hoje={hoje} />
      </Modal>

      {/* ══════ MODAL LANÇAMENTO ══════ */}
      <Modal open={modalLan} onClose={()=>setModalLan(false)} title={editLan?.id?"Editar Lançamento":"Novo Lançamento"} width={580}>
        {editLan&&(
          <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
              <FormField label="Cliente" required>
                <Select value={editLan.clienteId} onChange={v=>setEditLan({...editLan,clienteId:v})} options={clientes.map(c=>({value:c.id,label:c.nome}))} placeholder="Selecione..."/>
              </FormField>
              <FormField label="Tipo">
                <Select value={editLan.tipo} onChange={v=>setEditLan({...editLan,tipo:v})} options={TIPOS_LAN}/>
              </FormField>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Descrição">
                  <Input value={editLan.descricao} onChange={v=>setEditLan({...editLan,descricao:v})} placeholder="Ex: Parcela 1/3 — Honorários RCT"/>
                </FormField>
              </div>
              <FormField label="Valor (R$)" required>
                <Input type="number" value={editLan.valor} onChange={v=>setEditLan({...editLan,valor:v})} placeholder="0,00"/>
              </FormField>
              <FormField label="Modalidade">
                <Select value={editLan.modalidade} onChange={v=>setEditLan({...editLan,modalidade:v})} options={["À vista","Parcelado","Êxito","Mensal","Outros"]}/>
              </FormField>
              <FormField label="Data de Vencimento">
                <Input type="date" value={editLan.dataVencimento} onChange={v=>setEditLan({...editLan,dataVencimento:v})}/>
              </FormField>
              <FormField label="Status">
                <Select value={editLan.status} onChange={v=>setEditLan({...editLan,status:v})} options={STATUS_LAN}/>
              </FormField>
              <FormField label="Contrato">
                <Select value={editLan.contratoId} onChange={v=>setEditLan({...editLan,contratoId:v})} options={contratos.filter(c=>!editLan.clienteId||c.clienteId===editLan.clienteId).map(c=>({value:c.id,label:c.numContrato||c.id}))} placeholder="Nenhum..."/>
              </FormField>
              <FormField label="Produto">
                <Select value={editLan.produtoId} onChange={v=>setEditLan({...editLan,produtoId:v})} options={produtos.map(p=>({value:p.id,label:p.nome}))} placeholder="Nenhum..."/>
              </FormField>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Observações">
                  <Textarea value={editLan.obs} onChange={v=>setEditLan({...editLan,obs:v})} rows={2}/>
                </FormField>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:14,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalLan(false)}>Cancelar</Btn>
              <Btn onClick={salvarLan}>Salvar</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* ══════ MODAL VER — FICHA DO LANÇAMENTO (somente leitura) ══════ */}
      {viewLan && (()=>{
        const cli = clientes.find(c=>c.id===viewLan.clienteId);
        const ct  = contratos.find(c=>c.id===viewLan.contratoId);
        const prod = produtos.find(p=>p.id===viewLan.produtoId);
        const Campo = ({l,v}) => (
          <div>
            <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{l}</p>
            <p style={{fontSize:13,fontWeight:600,color:theme.gray800,marginTop:2}}>{v||"—"}</p>
          </div>
        );
        return (
        <Modal open={!!viewLan} onClose={()=>setViewLan(null)} title="Ficha do Lançamento" width={620}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"14px 24px",marginBottom:20}}>
            <Campo l="Cliente" v={cli?.nome||viewLan.clienteId}/>
            <Campo l="Status" v={<Badge color={COR_ST[viewLan.status]||"gray"}>{viewLan.status}</Badge>}/>
            <div style={{gridColumn:"1/-1"}}><Campo l="Descrição" v={viewLan.descricao}/></div>
            <Campo l="Tipo" v={viewLan.tipo}/>
            <Campo l="Modalidade" v={viewLan.modalidade}/>
            <Campo l="Valor" v={fmtMoeda(viewLan.valor)}/>
            {viewLan.saldoDevedor>0 && <Campo l="Saldo Devedor" v={fmtMoeda(viewLan.saldoDevedor)}/>}
            <Campo l="Vencimento" v={fmtData(viewLan.dataVencimento)}/>
            <Campo l="Data de Recebimento" v={viewLan.dataRecebimento?fmtData(viewLan.dataRecebimento):"—"}/>
            <Campo l="Banco da Baixa" v={viewLan.bancoBaixa?getNomeBanco(viewLan.bancoBaixa):"—"}/>
            {viewLan.numParcela && <Campo l="Parcela" v={`${viewLan.numParcela}/${viewLan.totalParcelas}`}/>}
            <Campo l="Contrato" v={ct?.numContrato||"—"}/>
            <Campo l="Produto" v={prod?.nome||"—"}/>
            <div style={{gridColumn:"1/-1"}}><Campo l="Observações" v={viewLan.obs}/></div>
            {(viewLan.pagamentos||[]).length>0 && (
              <div style={{gridColumn:"1/-1"}}>
                <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Histórico de Pagamentos</p>
                {viewLan.pagamentos.map((p,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:theme.gray700,padding:"4px 0",borderBottom:`1px solid ${theme.gray100}`}}>
                    <span>{fmtData(p.data)}</span><span>{fmtMoeda(p.valor)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:14,borderTop:`1px solid ${theme.gray100}`}}>
            <Btn variant="secondary" onClick={()=>setViewLan(null)}>Fechar</Btn>
            <Btn onClick={()=>{setEditLan({...viewLan});setViewLan(null);setModalLan(true);}}>✏️ Editar</Btn>
          </div>
        </Modal>
        );
      })()}

      {/* ══════ MODAL DESPESA ══════ */}
      <Modal open={modalDesp} onClose={()=>setModalDesp(false)} title={editDesp?.id?"Editar Despesa":"Nova Despesa"} width={520}>
        {editDesp&&(
          <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Descrição" required>
                  <Input value={editDesp.descricao} onChange={v=>setEditDesp({...editDesp,descricao:v})} placeholder="Ex: Custas processuais CONT001"/>
                </FormField>
              </div>
              <FormField label="Categoria">
                <Select value={editDesp.categoria||editDesp.grupo} onChange={v=>setEditDesp({...editDesp,categoria:v,grupo:v})} options={TIPOS_DESP}/>
              </FormField>
              <FormField label="Valor (R$)" required>
                <Input type="number" value={editDesp.valor} onChange={v=>setEditDesp({...editDesp,valor:v})} placeholder="0,00"/>
              </FormField>
              <FormField label="Data de Vencimento">
                <Input type="date" value={editDesp.data} onChange={v=>setEditDesp({...editDesp,data:v})}/>
              </FormField>
              <FormField label="Status">
                <Select value={editDesp.status} onChange={v=>setEditDesp({...editDesp,status:v})} options={STATUS_DESP}/>
              </FormField>
              <FormField label="Contrato vinculado">
                <Select value={editDesp.contratoId} onChange={v=>setEditDesp({...editDesp,contratoId:v})} options={contratos.map(c=>({value:c.id,label:c.numContrato||c.id}))} placeholder="Nenhum..."/>
              </FormField>
              {/* ── Condições de Lançamento ── */}
              {!editDesp.id && (
              <div style={{gridColumn:"1/-1",background:"#f0f9ff",borderRadius:8,padding:"14px 16px",border:"1px solid #bfdbfe",marginTop:4}}>
                <p style={{fontSize:12,fontWeight:700,color:theme.blue,marginBottom:10}}>💳 Condições de Lançamento</p>
                <div style={{display:"flex",gap:16,marginBottom:10,flexWrap:"wrap"}}>
                  {[["unico","Único"],["parcelado","Parcelado"],["recorrente","Recorrente (Fixo Mensal)"]].map(([v,l])=>(
                    <label key={v} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13,
                      fontWeight:editDesp.tipoLancDesp===v?700:400,
                      color:editDesp.tipoLancDesp===v?theme.blue:theme.gray600}}>
                      <input type="radio" name="tipoLancDesp" value={v}
                        checked={(editDesp.tipoLancDesp||"unico")===v}
                        onChange={()=>setEditDesp(prev=>({...prev,tipoLancDesp:v}))}
                        style={{accentColor:theme.blue}}/>
                      {l}
                    </label>
                  ))}
                </div>
                {(editDesp.tipoLancDesp==="parcelado")&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px"}}>
                    <FormField label="Número de Parcelas">
                      <input type="number" min="2" max="120"
                        value={editDesp.numParcelasDesp||2}
                        onChange={e=>setEditDesp(prev=>({...prev,numParcelasDesp:parseInt(e.target.value)||2}))}
                        style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}/>
                    </FormField>
                    <FormField label="Vencimento da 1ª Parcela">
                      <Input type="date" value={editDesp.vencPrimeiraDesp||""}
                        onChange={v=>setEditDesp(prev=>({...prev,vencPrimeiraDesp:v,data:v}))}/>
                    </FormField>
                    {(editDesp.numParcelasDesp>=2&&editDesp.vencPrimeiraDesp&&parseFloat(editDesp.valor)>0)&&(()=>{
                      const vlr=parseFloat(editDesp.valor)/parseInt(editDesp.numParcelasDesp||1);
                      return <div style={{gridColumn:"1/-1",marginTop:6,padding:"8px 10px",background:"#dbeafe",borderRadius:6,fontSize:11,color:theme.blue}}>
                        📋 {editDesp.numParcelasDesp}x de R$ {vlr.toLocaleString("pt-BR",{minimumFractionDigits:2})} — 1ª em {new Date(editDesp.vencPrimeiraDesp+"T12:00").toLocaleDateString("pt-BR")}
                      </div>;
                    })()}
                  </div>
                )}
                {(editDesp.tipoLancDesp==="recorrente")&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px"}}>
                    <FormField label="Repetir por (meses)">
                      <input type="number" min="2" max="120"
                        value={editDesp.numMesesDesp||12}
                        onChange={e=>setEditDesp(prev=>({...prev,numMesesDesp:parseInt(e.target.value)||12}))}
                        style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}/>
                    </FormField>
                    <FormField label="Vencimento do 1º Mês">
                      <Input type="date" value={editDesp.vencPrimeiraDesp||""}
                        onChange={v=>setEditDesp(prev=>({...prev,vencPrimeiraDesp:v,data:v}))}/>
                    </FormField>
                    {(editDesp.numMesesDesp>=2&&editDesp.vencPrimeiraDesp&&parseFloat(editDesp.valor)>0)&&(
                      <div style={{gridColumn:"1/-1",marginTop:6,padding:"8px 10px",background:"#dcfce7",borderRadius:6,fontSize:11,color:theme.green}}>
                        🔄 {editDesp.numMesesDesp} lançamentos de R$ {parseFloat(editDesp.valor).toLocaleString("pt-BR",{minimumFractionDigits:2})}/mês — 1º em {new Date(editDesp.vencPrimeiraDesp+"T12:00").toLocaleDateString("pt-BR")}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Observações">
                  <Textarea value={editDesp.obs} onChange={v=>setEditDesp({...editDesp,obs:v})} rows={2}/>
                </FormField>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:14,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalDesp(false)}>Cancelar</Btn>
              <Btn onClick={salvarDesp}>Salvar</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* ══════ MODAL BANCO ══════ */}
      <Modal open={modalBanco} onClose={()=>setModalBanco(false)} title={editBanco?.id?"Editar Banco":"Novo Banco"} width={480}>
        {editBanco&&(
          <>
            <FormField label="Nome do Banco" required>
              <Input value={editBanco.nome} onChange={v=>setEditBanco({...editBanco,nome:v})} placeholder="Ex: Itaú Unibanco, Nubank PJ, Bradesco..."/>
            </FormField>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px"}}>
              <FormField label="Agência">
                <Input value={editBanco.agencia} onChange={v=>setEditBanco({...editBanco,agencia:v})} placeholder="1234-5"/>
              </FormField>
              <FormField label="Conta Corrente">
                <Input value={editBanco.conta} onChange={v=>setEditBanco({...editBanco,conta:v})} placeholder="12345-6"/>
              </FormField>
            </div>
            <FormField label="Titular da Conta">
              <Input value={editBanco.titular} onChange={v=>setEditBanco({...editBanco,titular:v})} placeholder="Alves & Correa Advogados"/>
            </FormField>
            <FormField label="Tipo de Conta">
              <Select value={editBanco.tipo} onChange={v=>setEditBanco({...editBanco,tipo:v})} options={TIPOS_BANCOS}/>
            </FormField>
            <FormField label="Status">
              <Select value={editBanco.status} onChange={v=>setEditBanco({...editBanco,status:v})} options={["Ativo","Inativo"]}/>
            </FormField>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:14,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalBanco(false)}>Cancelar</Btn>
              <Btn onClick={salvarBanco}>Salvar Banco</Btn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}


// CSS de impressão para o módulo Vendas (propostas e contratos PDF)
const PRINT_STYLE = `
  @media print {
    body > * { display: none !important; }
    #print-area { display: block !important; }
    @page { size: A4; margin: 15mm 20mm; }
  }
  #print-area {
    display: none;
    font-family: 'Georgia', serif;
    color: #1a1a1a;
    font-size: 11pt;
    line-height: 1.6;
  }
`;


// ── Helper: Linhas da tabela Receitas ───────────────────────────
function LinhasReceitas({ dados, hoje, fmtData, fmtMoeda, getNomeCli, getNomeBanco, COR_ST, Badge, Btn, theme, abrirBaixa, setEditLan, setModalLan, setViewLan, remover }) {
  return dados.map((l, i) => {
    const vencido = (l.status === "Pendente"||l.status==="A Receber"||l.status==="Recebido Parcial") && l.dataVencimento && l.dataVencimento < hoje;
    const isParcial = l.status === "Recebido Parcial";
    const totalPago = (l.pagamentos||[]).reduce((s,p)=>s+(parseFloat(p.valor)||0),0);
    return (
      <React.Fragment key={l.id}>
      <tr style={{ borderBottom: isParcial?'none':'1px solid '+theme.gray100, background: i % 2 === 0 ? theme.white : theme.gray50 }}>
        <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600, color: theme.gray900 }}>
          {getNomeCli(l.clienteId)}
          {l.numParcela&&<span style={{fontSize:10,marginLeft:4,padding:"1px 5px",borderRadius:8,background:"#dbeafe",color:theme.blue}}>{l.numParcela}/{l.totalParcelas}</span>}
        </td>
        <td style={{ padding: '10px 12px', fontSize: 12, color: theme.gray700 }}>{l.descricao || '—'}</td>
        <td style={{ padding: '10px 12px' }}><Badge color="blue" style={{ fontSize: 9 }}>{l.tipo}</Badge></td>
        <td style={{ padding: '10px 12px', fontSize: 12, color: vencido ? theme.red : theme.gray600, fontWeight: vencido ? 700 : 400 }}>{fmtData(l.dataVencimento)}{vencido ? ' ⚠️' : ''}</td>
        <td style={{ padding: '10px 12px', fontSize: 12, color: l.dataRecebimento ? theme.green : theme.gray400 }}>{l.dataRecebimento ? fmtData(l.dataRecebimento) : '—'}</td>
        <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, color: theme.gray900 }}>
          {fmtMoeda(l.valor)}
          {isParcial&&l.saldoDevedor>0&&<div style={{fontSize:10,color:theme.amber,fontWeight:600}}>Saldo: {fmtMoeda(l.saldoDevedor)}</div>}
        </td>
        <td style={{ padding: '10px 12px' }}><Badge color={COR_ST[l.status] || 'gray'}>{l.status}</Badge></td>
        <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600, color: theme.gray700 }}>{l.bancoBaixa ? getNomeBanco(l.bancoBaixa) : '—'}</td>
        <td style={{ padding: '10px 12px' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {(l.status === 'A Receber' || l.status === 'Pendente' || l.status === 'Recebido Parcial') && <Btn size="sm" onClick={() => abrirBaixa(l, 'lan')} style={{ background: theme.green, color: '#fff', border: 'none', fontSize: 11 }}>Receber</Btn>}
            <Btn size="sm" variant="secondary" onClick={() => setViewLan(l)}>👁️</Btn>
            <Btn size="sm" variant="secondary" onClick={() => { setEditLan({ ...l }); setModalLan(true); }}>✏️</Btn>
            <Btn size="sm" variant="danger" onClick={() => remover('lancamentos', l.id)}>×</Btn>
          </div>
        </td>
      </tr>
      {/* Sub-linha: histórico de pagamentos parciais */}
      {isParcial && (l.pagamentos||[]).length > 0 && (
        <tr style={{background:i%2===0?"#fefce8":"#fef9c3",borderBottom:`1px solid ${theme.gray100}`}}>
          <td colSpan={9} style={{padding:"6px 20px"}}>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <span style={{fontSize:10,fontWeight:700,color:"#92400e"}}>💰 Pagamentos registrados:</span>
              {(l.pagamentos||[]).map((p,pi)=>(
                <span key={pi} style={{fontSize:10,padding:"2px 8px",borderRadius:8,background:"#fef3c7",color:"#92400e"}}>
                  {fmtData(p.data)}: {fmtMoeda(p.valor)} {p.banco?`• ${getNomeBanco(p.banco)}`:""}
                </span>
              ))}
              {l.proximoVencimento&&<span style={{fontSize:10,color:theme.amber,fontWeight:600}}>• Próx. venc.: {fmtData(l.proximoVencimento)}</span>}
              {l.saldoDevedor>0&&<span style={{fontSize:10,color:theme.red,fontWeight:700}}>• Saldo devedor: {fmtMoeda(l.saldoDevedor)}</span>}
            </div>
          </td>
        </tr>
      )}
      </React.Fragment>
    );
  });
}

// ── Helper: Linhas da tabela Despesas ────────────────────────────
function LinhasDespesas({ dados, hoje, fmtData, fmtMoeda, getNomeBanco, COR_ST, Badge, Btn, theme, abrirBaixa, setEditDesp, setModalDesp, remover }) {
  return dados.map((d, i) => {
    const vencida = (d.status==='Pendente'||d.status==='A Pagar') && d.data && d.data < hoje;
    return (
      <React.Fragment key={d.id}>
      <tr style={{ borderBottom: (d.status==="Pago Parcial"&&(d.pagamentos||[]).length>0)?'none':'1px solid '+theme.gray100, background: i % 2 === 0 ? theme.white : theme.gray50 }}>
        <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600, color: theme.gray900 }}>
          {d.descricao}
          {d.tipoLancDesp==="parcelado"&&d.numParcela&&(
            <span style={{fontSize:10,marginLeft:4,padding:"1px 5px",borderRadius:8,background:"#fef3c7",color:"#92400e"}}>
              {d.numParcela}/{d.totalParcelas}
            </span>
          )}
          {d.tipoLancDesp==="recorrente"&&(
            <span style={{fontSize:10,marginLeft:4,padding:"1px 5px",borderRadius:8,background:"#dcfce7",color:theme.green}}>
              🔄
            </span>
          )}
        </td>
        <td style={{ padding: '10px 12px' }}><Badge color="gray" style={{ fontSize: 9 }}>{d.categoria || d.grupo}</Badge></td>
        <td style={{ padding: '10px 12px', fontSize: 12, color: vencida ? theme.red : theme.gray600, fontWeight: vencida ? 700 : 400 }}>{fmtData(d.data)}{vencida ? ' ⚠️' : ''}</td>
        <td style={{ padding: '10px 12px', fontSize: 12, color: theme.green }}>{d.dataPagamento ? fmtData(d.dataPagamento) : '—'}</td>
        <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, color: theme.gray900 }}>{fmtMoeda(d.valor)}</td>
        <td style={{ padding: '10px 12px' }}><Badge color={COR_ST[d.status] || 'gray'}>{d.status}</Badge></td>
        <td style={{ padding: '10px 12px', fontSize: 11, color: theme.gray500 }}>{d.bancoBaixa ? getNomeBanco(d.bancoBaixa) : '—'}</td>
        <td style={{ padding: '10px 12px' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {(!d.status || d.status === 'A Pagar' || d.status === 'Pendente' || d.status === 'Pago Parcial') && <Btn size="sm" onClick={() => abrirBaixa(d, 'desp')} style={{ background: theme.amber, color: '#fff', border: 'none', fontSize: 11 }}>Pagar</Btn>}
            <Btn size="sm" variant="secondary" onClick={() => { setEditDesp({ ...d }); setModalDesp(true); }}>✏️</Btn>
            <Btn size="sm" variant="danger" onClick={() => remover('despesas', d.id)}>×</Btn>
          </div>
        </td>
      </tr>
      {(d.status==="Pago Parcial")&&(d.pagamentos||[]).length>0&&(
        <tr style={{background:i%2===0?"#fefce8":"#fef9c3",borderBottom:`1px solid ${theme.gray100}`}}>
          <td colSpan={8} style={{padding:"6px 20px"}}>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <span style={{fontSize:10,fontWeight:700,color:"#92400e"}}>💸 Pagamentos registrados:</span>
              {(d.pagamentos||[]).map((p,pi)=>(
                <span key={pi} style={{fontSize:10,padding:"2px 8px",borderRadius:8,background:"#fef3c7",color:"#92400e"}}>
                  {fmtData(p.data)}: {fmtMoeda(p.valor)} {p.banco?`• ${getNomeBanco(p.banco)}`:""}
                </span>
              ))}
              {d.proximoVencimento&&<span style={{fontSize:10,color:theme.amber,fontWeight:600}}>• Próx. venc.: {fmtData(d.proximoVencimento)}</span>}
              {d.saldoDevedor>0&&<span style={{fontSize:10,color:theme.red,fontWeight:700}}>• Saldo devedor: {fmtMoeda(d.saldoDevedor)}</span>}
            </div>
          </td>
        </tr>
      )}
      </React.Fragment>
    );
  });
}


function MCporArea({ arr, fmtMoeda, theme, BarraMC }) {
  if (!arr || arr.length === 0) return <p style={{ fontSize: 12, color: theme.gray400, textAlign: 'center', padding: '16px 0' }}>Sem contratos com custo cadastrado.</p>;
  const maxMC = Math.max(...arr.map(x => x.mc));
  return arr.map((a, i) => {
    const perc = maxMC > 0 ? Math.round(a.mc / maxMC * 100) : 0;
    const mcPercReal = a.valorTotal > 0 ? Math.round(a.mc / a.valorTotal * 100) : 0;
    const cor = mcPercReal >= 70 ? theme.green : mcPercReal >= 50 ? theme.blue : mcPercReal >= 30 ? theme.amber : theme.red;
    return (
      <div key={a.area} style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: theme.gray900 }}>{a.area}</span>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: cor }}>{fmtMoeda(a.mc)}</span>
            <span style={{ fontSize: 11, color: theme.gray400, marginLeft: 6 }}>{mcPercReal}%</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <BarraMC perc={perc} cor={cor} />
          <span style={{ fontSize: 10, color: theme.gray400, minWidth: 60, textAlign: 'right' }}>{fmtMoeda(a.valorTotal)}</span>
        </div>
      </div>
    );
  });
}

// ── Helper: MC por Produto ───────────────────────────────────────
function MCporProduto({ arr, fmtMoeda, theme, BarraMC, Badge }) {
  if (!arr || arr.length === 0) return <p style={{ fontSize: 12, color: theme.gray400, textAlign: 'center', padding: '16px 0' }}>Sem contratos com custo cadastrado.</p>;
  const maxMC = arr[0] ? arr[0].mc : 1;
  return arr.slice(0, 8).map((p, i) => {
    const perc = maxMC > 0 ? Math.round(p.mc / maxMC * 100) : 0;
    const cor = p.mcPerc >= 70 ? theme.green : p.mcPerc >= 50 ? theme.blue : p.mcPerc >= 30 ? theme.amber : theme.red;
    return (
      <div key={p.id} style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 12, color: theme.gray800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }} title={p.nome}>{p.nome}</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            <Badge color={p.mcPerc >= 70 ? 'green' : p.mcPerc >= 50 ? 'blue' : p.mcPerc >= 30 ? 'amber' : 'red'} style={{ fontSize: 9 }}>{p.mcPerc}%</Badge>
            <span style={{ fontSize: 11, fontWeight: 700, color: cor }}>{fmtMoeda(p.mc)}</span>
          </div>
        </div>
        <BarraMC perc={perc} cor={cor} />
      </div>
    );
  });
}


function GraficoReceitaDespesa({ lancamentos, despesas, fmtMoeda, theme }) {
  const meses = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(); d.setMonth(d.getMonth() - i);
    const mes = d.toISOString().slice(0, 7);
    const nomeMes = d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
    const rec = lancamentos.filter(l => l.status === 'Recebido' && (l.dataRecebimento || '').startsWith(mes)).reduce((s, l) => s + (l.valor || 0), 0);
    const pag = despesas.filter(x => x.status === 'Pago' && (x.dataPagamento || x.data || '').startsWith(mes)).reduce((s, x) => s + (x.valor || 0), 0);
    meses.push({ mes: nomeMes, rec, pag, saldo: rec - pag });
  }
  const maxVal = Math.max(...meses.map(m => Math.max(m.rec, m.pag)), 1);
  const alt = 120;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: alt + 60 }}>
      {meses.map((m, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: alt }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <span style={{ fontSize: 9, color: theme.green, fontWeight: 700 }}>{m.rec > 0 ? 'R$' + (m.rec / 1000).toFixed(0) + 'k' : ''}</span>
              <div style={{ width: 18, height: Math.max(m.rec / maxVal * alt, 2), background: theme.green, borderRadius: '3px 3px 0 0', minHeight: 2 }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <span style={{ fontSize: 9, color: theme.red, fontWeight: 700 }}>{m.pag > 0 ? 'R$' + (m.pag / 1000).toFixed(0) + 'k' : ''}</span>
              <div style={{ width: 18, height: Math.max(m.pag / maxVal * alt, 2), background: theme.red, borderRadius: '3px 3px 0 0', minHeight: 2 }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <span style={{ fontSize: 9, color: m.saldo >= 0 ? theme.blue : theme.amber, fontWeight: 700 }}>{(m.rec > 0 || m.pag > 0) ? (m.saldo >= 0 ? '+' : '') + (m.saldo / 1000).toFixed(0) + 'k' : ''}</span>
              <div style={{ width: 18, height: Math.max(Math.abs(m.saldo) / maxVal * alt, 2), background: m.saldo >= 0 ? theme.blue : theme.amber, borderRadius: '3px 3px 0 0', minHeight: 2 }} />
            </div>
          </div>
          <span style={{ fontSize: 10, color: theme.gray500, textTransform: 'capitalize' }}>{m.mes}</span>
          <span style={{ fontSize: 9, color: m.saldo >= 0 ? theme.green : theme.red }}>{m.saldo >= 0 ? '+' : ''}{(m.saldo / 1000).toFixed(0)}k</span>
        </div>
      ))}
    </div>
  );
}

// ── Helper: Top Clientes ─────────────────────────────────────────
function TopClientes({ lancamentos, getNomeCli, fmtMoeda, theme, BarraMC }) {
  const porCli = {};
  lancamentos.filter(l => l.status === 'Recebido').forEach(l => {
    if (!porCli[l.clienteId]) porCli[l.clienteId] = { nome: getNomeCli(l.clienteId), total: 0 };
    porCli[l.clienteId].total += l.valor || 0;
  });
  const arr = Object.values(porCli).sort((a, b) => b.total - a.total).slice(0, 5);
  const maxV = arr[0] ? arr[0].total : 1;
  if (arr.length === 0) return <p style={{ fontSize: 12, color: theme.gray400, textAlign: 'center', padding: '16px 0' }}>Sem recebimentos registrados.</p>;
  return arr.map((cl, i) => (
    <div key={cl.nome} style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontSize: 12, color: theme.gray800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>{cl.nome}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: theme.green }}>{fmtMoeda(cl.total)}</span>
      </div>
      <BarraMC perc={Math.round(cl.total / maxV * 100)} cor={theme.green} />
    </div>
  ));
}

// ── Helper: Próximos Vencimentos ─────────────────────────────────
function ProximosVencimentos({ lancamentos, despesas, hoje, fmtMoeda, fmtData, getNomeCli, theme, setAba }) {
  const d30 = new Date(); d30.setDate(d30.getDate() + 30);
  const d30str = d30.toISOString().slice(0, 10);
  const recArr = lancamentos.filter(l => l.status === 'Pendente' && l.dataVencimento && l.dataVencimento <= d30str)
    .sort((a, b) => a.dataVencimento.localeCompare(b.dataVencimento)).slice(0, 4)
    .map(l => ({ ...l, _tipo: 'rec' }));
  const pagArr = despesas.filter(x => x.status === 'Pendente' && x.data && x.data <= d30str)
    .sort((a, b) => a.data.localeCompare(b.data)).slice(0, 4)
    .map(x => ({ ...x, dataVencimento: x.data, _tipo: 'pag' }));
  const todos = [...recArr, ...pagArr].sort((a, b) => a.dataVencimento.localeCompare(b.dataVencimento)).slice(0, 7);
  if (todos.length === 0) return <p style={{ fontSize: 12, color: theme.gray400, textAlign: 'center', padding: '16px 0' }}>Nenhum vencimento nos proximos 30 dias.</p>;
  return todos.map((x, i) => {
    const venc = x.dataVencimento < hoje;
    return (
      <div key={x.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid ' + theme.gray100 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 14 }}>{x._tipo === 'rec' ? '💳' : '💸'}</span>
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: theme.gray900 }}>{x._tipo === 'rec' ? getNomeCli(x.clienteId) : x.descricao}</p>
            <p style={{ fontSize: 10, color: venc ? theme.red : theme.gray400 }}>{fmtData(x.dataVencimento)}{venc ? ' VENCIDA' : ''}</p>
          </div>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: x._tipo === 'rec' ? theme.green : theme.red }}>{fmtMoeda(x.valor)}</span>
      </div>
    );
  });
}

// ── Helper: Modal Baixa ──────────────────────────────────────────
function ModalBaixaConteudo({ itemBaixa, baixaDados, setBaixaDados, bancos, confirmarBaixa, setModalBaixa, fmtMoeda, getNomeCli, theme, hoje }) {
  if (!itemBaixa) return null;
  // Se parcial anterior: usar saldoDevedor como base, não valor original
  const valorOrigTotal = parseFloat(itemBaixa.item.valor) || 0;
  const saldoAnterior  = parseFloat(itemBaixa.item.saldoDevedor) || 0;
  const valorOrig = (itemBaixa.item.status === "Recebido Parcial" && saldoAnterior > 0)
    ? saldoAnterior
    : valorOrigTotal;
  const valorInf  = parseFloat(baixaDados.valorBaixa) || 0;
  const dif       = valorInf - valorOrig;
  return (
    <>
      <div style={{ background: theme.gray50, borderRadius: 8, padding: '12px 16px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ fontSize: 13, fontWeight: 600, color: theme.gray900 }}>{itemBaixa.tipo === 'lan' ? getNomeCli(itemBaixa.item.clienteId) : itemBaixa.item.descricao}</p>
          <p style={{ fontSize: 11, color: theme.gray500, marginTop: 2 }}>{itemBaixa.tipo === 'lan' ? (itemBaixa.item.descricao || '') : (itemBaixa.item.categoria || '')}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 10, color: theme.gray400 }}>
            {itemBaixa.item.status==="Recebido Parcial"&&saldoAnterior>0?"Saldo a Receber":"Valor original"}
          </p>
          <p style={{ fontSize: 18, fontWeight: 700, color: theme.blue }}>{fmtMoeda(valorOrig)}</p>
          {itemBaixa.item.status==="Recebido Parcial"&&saldoAnterior>0&&(
            <p style={{fontSize:10,color:theme.gray400,marginTop:2}}>Total: {fmtMoeda(valorOrigTotal)}</p>
          )}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <FormField label={itemBaixa.tipo === 'lan' ? 'Valor Recebido (R$)' : 'Valor Pago (R$)'} required>
          <Input type="number" value={baixaDados.valorBaixa} onChange={v => setBaixaDados({ ...baixaDados, valorBaixa: v })} placeholder={String(valorOrig)} />
        </FormField>
        <FormField label={itemBaixa.tipo === 'lan' ? 'Data do Recebimento' : 'Data do Pagamento'} required>
          <Input type="date" value={baixaDados.data} onChange={v => setBaixaDados({ ...baixaDados, data: v })} />
        </FormField>
      </div>
      {baixaDados.valorBaixa && valorInf !== valorOrig && (
        <div style={{ padding: '8px 14px', borderRadius: 7, marginBottom: 10, background: dif < 0 ? theme.red + '18' : theme.green + '18', border: '1px solid ' + (dif < 0 ? theme.red : theme.green) }}>
          {dif < 0
            ? <p style={{ fontSize: 12, color: theme.red, fontWeight: 600 }}>Saldo devedor: {fmtMoeda(Math.abs(dif))} - sera criado novo lancamento</p>
            : <p style={{ fontSize: 12, color: theme.green, fontWeight: 600 }}>Acrescimo: +{fmtMoeda(dif)} - sera registrado na observacao</p>
          }
        </div>
      )}
      <FormField label="Banco utilizado" required>
        <select value={baixaDados.bancoId} onChange={e => setBaixaDados({ ...baixaDados, bancoId: e.target.value })}
          style={{ width: '100%', padding: '9px 12px', border: '1px solid ' + theme.gray200, borderRadius: 7, fontSize: 13, outline: 'none' }}>
          <option value="">Selecione o banco...</option>
          {bancos.filter(b => b.status === 'Ativo').map(b => (
            <option key={b.id} value={b.id}>{b.nome} - Ag. {b.agencia || '-'} Cc. {b.conta || '-'} {b.titular}</option>
          ))}
        </select>
        {bancos.filter(b => b.status === 'Ativo').length === 0 && (
          <p style={{ fontSize: 11, color: theme.amber, marginTop: 4 }}>Nenhum banco ativo. Cadastre na aba Bancos.</p>
        )}
      </FormField>
      {/* Próximo vencimento — visível quando valor < original (parcial) */}
      {baixaDados.valorBaixa && parseFloat(baixaDados.valorBaixa) < (parseFloat(itemBaixa.item.valor)||0) && itemBaixa.tipo==="lan" && (
        <FormField label="📅 Próximo Vencimento (saldo restante)">
          <Input type="date" value={baixaDados.proximoVencimento||""}
            onChange={v=>setBaixaDados({...baixaDados, proximoVencimento:v})}/>
        </FormField>
      )}
      <FormField label="Observacoes">
        <Textarea value={baixaDados.obs} onChange={v => setBaixaDados({ ...baixaDados, obs: v })} rows={2} placeholder="No do comprovante, referencia, observacoes..." />
      </FormField>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 14, borderTop: '1px solid ' + theme.gray100 }}>
        <Btn variant="secondary" onClick={() => setModalBaixa(false)}>Cancelar</Btn>
        <Btn onClick={confirmarBaixa} style={{ background: itemBaixa.tipo === 'lan' ? theme.green : theme.amber, border: 'none', color: '#fff' }}>
          {itemBaixa.tipo === 'lan' ? 'Confirmar Recebimento' : 'Confirmar Pagamento'}
        </Btn>
      </div>
    </>
  );
}


// ── Modal PDF Vendas — Preview + Imprimir ──
function ModalPDFVendas({ prop, tipo, htmlProposta, htmlContrato, imprimir, onClose }) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:9999,
      display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:"24px 16px"}}>
      <div style={{background:theme.white,borderRadius:12,width:"100%",maxWidth:780,
        boxShadow:"0 20px 60px rgba(0,0,0,.3)",marginBottom:24,overflow:"hidden"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
          padding:"16px 24px",borderBottom:`1px solid ${theme.gray100}`}}>
          <p style={{fontSize:15,fontWeight:700,color:theme.gray900,margin:0}}>
            {tipo==="proposta"?"📄 Proposta Comercial":"📋 Contrato de Prestação de Serviços"}
          </p>
          <button onClick={onClose} style={{background:"none",border:"none",
            fontSize:22,cursor:"pointer",color:theme.gray400,lineHeight:1}}>×</button>
        </div>
        <div style={{padding:"20px 24px"}}>
          <div style={{background:theme.gray50,borderRadius:8,padding:"20px 32px",
            maxHeight:"60vh",overflowY:"auto",border:`1px solid ${theme.gray200}`}}
            dangerouslySetInnerHTML={{__html: tipo==="proposta"
              ? htmlProposta(prop) : htmlContrato(prop)}}/>
          <div style={{padding:"8px 12px",background:`${theme.amber}11`,
            border:`1px solid ${theme.amber}33`,borderRadius:7,margin:"14px 0",
            fontSize:12,color:theme.amber}}>
            💡 Clique em "Imprimir / Salvar PDF", selecione a impressora e escolha <strong>"Salvar como PDF"</strong>.
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
            <button onClick={onClose} style={{padding:"9px 20px",borderRadius:8,
              border:`1px solid ${theme.gray200}`,background:theme.white,
              cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>Fechar</button>
            <button onClick={imprimir} style={{padding:"9px 22px",borderRadius:8,
              border:"none",background:theme.gray900,color:theme.gold,
              cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>
              🖨️ Imprimir / Salvar PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModVendas({ data, setData }) {
  const vendas     = data.vendas     || {};
  const propostas  = vendas.propostas      || [];
  const todosAgentes   = data.agentes || [];  // Cadastro central
  const idsVendas      = vendas.agentesVendas || [];  // IDs selecionados
  const agentes        = todosAgentes.filter(a => idsVendas.includes(a.id));
  const [modalSelAg, setModalSelAg] = React.useState(false);
  const clientes   = data.clientes   || [];
  const leads      = data.leads      || [];
  const produtos   = data.produtos   || [];
  const contratos  = data.contratos  || [];
  const equipe     = data.equipe     || [];
  const rctDiags   = data.rctDiagnosticos || [];

  const [aba,         setAba]         = useState("dashboard");
  const [modalProp,   setModalProp]   = useState(false);
  const [modalPDF,        setModalPDF]        = useState(false);
  const [modalConv,       setModalConv]       = useState(false);
  const [modalOcorr,      setModalOcorr]      = useState(false);
  const [modalAg,         setModalAg]         = useState(false);
  const [modalAssinatura, setModalAssinatura] = useState(false);
  const [dataAssForm,     setDataAssForm]     = useState("");
  const [modalDocAux,     setModalDocAux]     = useState(null); // { tipo, texto }
  const [pdfTipo,         setPdfTipo]         = useState("proposta");
  const [modoEdicaoPDF,   setModoEdicaoPDF]   = useState(false); // false=preview, true=editar
  const [propSel,     setPropSel]     = useState(null);
  const [editProp,    setEditProp]    = useState(null);
  const [editAg,      setEditAg]      = useState(null);
  const [novaOcorr,   setNovaOcorr]  = useState("");
  const [filtStatus,  setFiltStatus]  = useState("");
  const [filtResp,    setFiltResp]    = useState("");
  const [busca,       setBusca]       = useState("");
  const [convDados,   setConvDados]   = useState({});

  const hoje = new Date().toISOString().slice(0,10);
  const anoAtual = new Date().getFullYear();

  const STATUS_PROP = ["Rascunho","Enviada","Aguardando Aprovação","Proposta Aprovada","Contrato Gerado","Contrato Assinado","Recusada","Expirada"];
  const COR_STATUS  = {
    "Rascunho":"gray","Enviada":"blue","Aguardando Aprovação":"amber",
    "Proposta Aprovada":"green","Contrato Gerado":"purple",
    "Contrato Assinado":"green","Recusada":"red","Expirada":"gray",
    // retrocompat
    "Aprovada":"green"
  };
  const ETAPAS = [
    { status:"Rascunho",          label:"Rascunho",       icon:"📝", cor:"#94a3b8" },
    { status:"Enviada",           label:"Enviada",         icon:"📤", cor:"#3b82f6" },
    { status:"Proposta Aprovada", label:"Prop. Aprovada",  icon:"✅", cor:"#22c55e" },
    { status:"Contrato Gerado",   label:"Contrato Gerado", icon:"📋", cor:"#7c3aed" },
    { status:"Contrato Assinado", label:"Assinado",        icon:"🖊️", cor:"#16a34a" },
  ];
  const idxEtapa = s => ETAPAS.findIndex(e=>e.status===s||(s==="Aprovada"&&e.status==="Proposta Aprovada"));
  const FORMAS_PAG = ["À vista","2x","3x","6x","12x","Êxito","Êxito + entrada","Mensal","Personalizado"];

  const setVendas = (campo, valor) =>
    setData(prev => ({ ...prev, vendas: { ...(prev.vendas||{}), [campo]: valor } }));

  // Sincronização CRM ↔ Vendas: proposta e contrato são emitidos só no Vendas,
  // mas o Lead correspondente no CRM precisa refletir o estágio real automaticamente
  // (evita "Proposta"/"Envio de Contrato" desatualizados no funil do CRM).
  const ORDEM_ESTAGIO_LEAD = ["recepcao","qualificacao","reuniao","proposta","negociacao","envio_contrato","contrato_pendente","contrato_assinado"];
  const sincronizarEstagioLead = (leadId, novoEstagio) => {
    if (!leadId || !novoEstagio) return;
    setData(prev => {
      const lead = (prev.leads||[]).find(l=>l.id===leadId);
      if (!lead) return prev;
      const idxAtual = ORDEM_ESTAGIO_LEAD.indexOf(lead.estagio);
      const idxNovo  = ORDEM_ESTAGIO_LEAD.indexOf(novoEstagio);
      // Só avança — nunca retrocede um estágio manualmente ajustado pela equipe, e nunca mexe em lead perdido/não qualificado
      if (idxNovo === -1 || idxNovo <= idxAtual || ["perdido","nao_qualificado","contrato_cancelado"].includes(lead.estagio)) return prev;
      const est = ESTAGIOS.find(e=>e.id===novoEstagio);
      const leadAtt = {
        ...lead, estagio: novoEstagio,
        historicoEstagios: [...(lead.historicoEstagios||[]), {
          id: Date.now(), estagio: novoEstagio, label: est?.label||novoEstagio,
          data: new Date().toISOString().slice(0,10), responsavel: lead.responsavel||"",
          obs: "Sincronizado automaticamente a partir do Vendas"
        }]
      };
      return { ...prev, leads: prev.leads.map(l=>l.id===leadId?leadAtt:l) };
    });
  };

  // Gerar número de proposta automático
  const gerarNumProp = () => {
    const seq = propostas.filter(p => p.numero?.includes(String(anoAtual))).length + 1;
    return `PROP-${anoAtual}-${String(seq).padStart(3,"0")}`;
  };

  const EMPTY_PROP = {
    id:"", numero:"", origem:"direto",
    leadId:"", clienteId:"", produtoId:"", rctId:"",
    responsavel:"", dataEmissao:hoje,
    dataValidade: new Date(Date.now()+30*86400000).toISOString().slice(0,10),
    valorHonorarios:"", formaPagamento:"À vista", parcelamento:"",
    percentualExito:"", percentualEndividamento:"", descricao:"", condicoes:
`1. Os honorários acordados referem-se exclusivamente aos serviços descritos nesta proposta.
2. Quaisquer serviços adicionais serão objeto de nova proposta.
3. O escritório compromete-se a manter sigilo sobre todas as informações do cliente.
4. A proposta é válida pelo prazo indicado, podendo ser renovada mediante acordo entre as partes.
5. O pagamento deverá ser feito através de transferência bancária na conta da CONTRATADA: Banco Inter nº 077, Agência 01-9, Conta Corrente 1150292-4 · PIX CNPJ: 29.574.525/0001-12 · Alves & Correa Advogados.
6. Documentos necessários: [preenchido automaticamente conforme produto selecionado]`,
    status:"Rascunho", ocorrencias:[], contratoGerado:"", clienteCriado:"",
    dataAssinatura:"", dataEnvio:"", dataAprovacao:"", dataGeracaoContrato:"",
    // Fase 1 — Comercial/Marketing
    atendimentoId: "",   // vínculo com atendimento (WhatsApp/Instagram)
    resumoMax: "",       // resumo executivo para IA (máx 280 chars)
    discPerfil: "",      // perfil DISC do cliente/lead (D/I/S/C)
    scoreBant: 0         // score BANT calculado (0–4)
  };

  const saveProp = () => {
    if (!editProp.produtoId) return alert("Selecione um produto.");
    const id  = editProp.id  || "PROP"+Date.now().toString().slice(-7);
    const num = editProp.numero || gerarNumProp();
    const novo = { ...editProp, id, numero:num };
    setVendas("propostas", editProp.id
      ? propostas.map(p => p.id===id ? novo : p)
      : [...propostas, novo]);
    if (novo.leadId) sincronizarEstagioLead(novo.leadId, "proposta");
    setModalProp(false);
  };

  const saveAg = () => {
    if (!editAg.nome||!editAg.link) return alert("Nome e link são obrigatórios.");
    const id = editAg.id || "AV"+Date.now().toString().slice(-6);
    const novo = { ...editAg, id };
    setVendas("agentesVendas", editAg.id ? agentes.map(a=>a.id===id?novo:a) : [...agentes,novo]);
    setModalAg(false);
  };

  const removerProp = id => {
    const prop = propostas.find(p=>p.id===id);
    if (!prop) return;
    // Verificar se há contrato vinculado na Base Operacional
    if (prop.contratoGerado) {
      const contBo = (data.baseOperacional||[]).find(r=>r.contratoId===prop.contratoGerado);
      if (contBo) {
        const confirma = window.confirm(
          `Esta proposta está vinculada ao contrato ${contratos.find(c=>c.id===prop.contratoGerado)?.numContrato||prop.contratoGerado} na Base Operacional.\n\nDeseja TAMBÉM remover o contrato da Base Operacional?\n\n⚠️ Esta ação é irreversível.\n\n[OK] = Remover proposta E contrato da Base\n[Cancelar] = Manter proposta e contrato`
        );
        if (!confirma) return;
        // Remover proposta + contrato na Base Operacional
        setData(prev=>({
          ...prev,
          vendas: { ...prev.vendas, propostas: (prev.vendas?.propostas||[]).filter(p=>p.id!==id) },
          contratos: (prev.contratos||[]).filter(c=>c.id!==prop.contratoGerado),
          baseOperacional: (prev.baseOperacional||[]).filter(r=>r.contratoId!==prop.contratoGerado),
        }));
        if (propSel?.id===id) setPropSel(null);
        return;
      }
    }
    // Sem contrato vinculado — confirmar e excluir apenas a proposta
    if (!confirm(`Remover a proposta ${prop.numero||""}? Esta ação é irreversível.`)) return;
    setVendas("propostas", propostas.filter(p=>p.id!==id));
    if (propSel?.id===id) setPropSel(null);
  };

  const alterarStatus = (prop, novoStatus, obs="") => {
    const ocorr = { data: hoje, status: novoStatus, obs, usuario: "Dr. Nelson J. Alves" };
    const atualizada = { ...prop, status: novoStatus, ocorrencias: [...(prop.ocorrencias||[]), ocorr] };
    setVendas("propostas", propostas.map(p=>p.id===prop.id?atualizada:p));
    if (propSel?.id === prop.id) setPropSel(atualizada);
  };

  const adicionarOcorrencia = () => {
    if (!novaOcorr.trim()) return;
    const ocorr = { data: hoje, status: propSel.status, obs: novaOcorr, usuario:"Dr. Nelson J. Alves" };
    const atualizada = { ...propSel, ocorrencias: [...(propSel.ocorrencias||[]), ocorr] };
    setVendas("propostas", propostas.map(p=>p.id===propSel.id?atualizada:p));
    setPropSel(atualizada);
    setNovaOcorr("");
    setModalOcorr(false);
  };

  // Converter proposta aprovada → cliente + contrato
  const iniciarConversao = (prop) => {
    const cli = clientes.find(c=>c.id===prop.clienteId);
    const ld  = leads.find(l=>l.id===prop.leadId);
    const prod = produtos.find(p=>p.id===prop.produtoId);
    setConvDados({
      propId: prop.id,
      nomeCliente: cli?.nome || ld?.nome || "",
      cpfCnpj: cli?.cpfCnpj || ld?.cpfCnpj || "",
      email: cli?.email || ld?.email || "",
      whatsapp: cli?.whatsapp || ld?.whatsapp || "",
      area: prod?.area || "",
      numContrato: `${anoAtual}/${String(contratos.length+1).padStart(3,"0")}`,
      valorContratado: prop.valorHonorarios || "",
      responsavel: prop.responsavel || "",
      dataEntrada: hoje,
      obs: `Gerado a partir da proposta ${prop.numero}`,
      clienteExistente: !!cli,
      clienteId: cli?.id || "",
    });
    setModalConv(true);
  };

  const confirmarConversao = () => {
    const prop = propostas.find(p=>p.id===convDados.propId);
    if (!prop) return;

    let clienteId = convDados.clienteId;
    let novoData  = { ...data };

    // Criar cliente se não existir
    if (!convDados.clienteExistente) {
      const novoCli = {
        id: "CLI"+Date.now().toString().slice(-6),
        nome: convDados.nomeCliente, tipo:"PJ",
        email: convDados.email, whatsapp: convDados.whatsapp,
        cpfCnpj: convDados.cpfCnpj, cidade:"", uf:"SP",
        endereco:"", cep:"", produtosContratados:[prop.produtoId],
        ativo:true, dataEntrada: hoje, obs: convDados.obs,
      };
      clienteId = novoCli.id;
      novoData = { ...novoData, clientes: [...(novoData.clientes||[]), novoCli] };
    }

    // Criar contrato
    const novoCont = {
      id: "CONT"+Date.now().toString().slice(-6),
      numContrato: convDados.numContrato,
      clienteId, produtoId: prop.produtoId,
      area: convDados.area, fase:"Pré-processual",
      tipoProcesso:"Administrativo", status:"Em andamento",
      responsavel: convDados.responsavel,
      dataEntrada: convDados.dataEntrada,
      ultimoAvanco: hoje, prazo:"",
      valorContratado: parseFloat(convDados.valorContratado)||0,
      valorRecebido:0, valorAReceber: parseFloat(convDados.valorContratado)||0,
      teseNova:false, jurisprudenciaInstavel:false, valorElevado:false,
      dependePericia:false, documentoIncompleto:false,
      custoOperacional:0, obs: convDados.obs,
      origemProposta: prop.id,
    };
    novoData = { ...novoData, contratos: [...(novoData.contratos||[]), novoCont] };

    // Atualizar proposta
    const propAtual = {
      ...prop, status:"Contrato Gerado", clienteId,
      dataAprovacao: prop.dataAprovacao || hoje,
      dataGeracaoContrato: hoje,
      contratoGerado: novoCont.id, clienteCriado: clienteId,
      ocorrencias: [...(prop.ocorrencias||[]), {
        data:hoje, status:"Contrato Gerado",
        obs:`Proposta aprovada. Contrato ${convDados.numContrato} gerado. Cliente ${convDados.nomeCliente} ${convDados.clienteExistente?"vinculado":"criado"}.`,
        usuario:"Dr. Nelson J. Alves"
      }]
    };
    novoData = { ...novoData, vendas: { ...(novoData.vendas||{}),
      propostas: novoData.vendas?.propostas?.map(p=>p.id===prop.id?propAtual:p)||[propAtual]
    }};
    // Criar registro na Base Operacional
    const prod = (novoData.produtos||[]).find(p=>p.id===prop.produtoId);
    const docsBase = (prod?.documentosPadrao||[]).map(d=>({
      docId:d.id, nome:d.nome, recebido:false, dataRecebimento:"", obs:""
    }));
    const hoje2 = hoje;
    const tarefasBase = (prod?.tarefasPadrao||[]).map(t=>({
      id:"TRF"+Date.now().toString().slice(-5)+Math.random().toString(36).slice(-3),
      ordem:t.ordem, titulo:t.titulo, descricao:t.descricao||"",
      prazoOriginal: (() => { const d=new Date(hoje2+"T00:00:00"); d.setDate(d.getDate()+(parseInt(t.prazoExecucaoDias)||7)); return d.toISOString().slice(0,10); })(),
      prazoAtual: (() => { const d=new Date(hoje2+"T00:00:00"); d.setDate(d.getDate()+(parseInt(t.prazoExecucaoDias)||7)); return d.toISOString().slice(0,10); })(),
      prorrogado:false, motivoProrrogacao:"",
      responsavel:t.responsavelPadrao||convDados.responsavel||"",
      status:"Pendente", dataConclusao:"", ocorrencias:[]
    }));
    const novoRegistroBase = {
      id:"BASE"+Date.now().toString().slice(-7),
      contratoId:novoCont.id, clienteId, produtoId:prop.produtoId,
      numContrato:convDados.numContrato,
      dataInicio:hoje2, status:"Aguardando Documentação",
      documentos:docsBase, tarefas:tarefasBase,
      procuracao:{gerada:false,dataGeracao:""},
      declaracao:{gerada:false,dataGeracao:""},
      ocorrencias:[{data:hoje2,obs:`Registro criado automaticamente a partir da proposta ${prop.numero}.`}]
    };
    novoData = { ...novoData, baseOperacional:[...(novoData.baseOperacional||[]),novoRegistroBase] };

    setData(novoData);
    setModalConv(false);
    setPropSel(propAtual);
    if (prop.leadId) sincronizarEstagioLead(prop.leadId, "envio_contrato");
    alert(`✅ Proposta aprovada!\n\nContrato ${convDados.numContrato} criado.\nCliente ${convDados.nomeCliente} ${convDados.clienteExistente?"vinculado":"cadastrado"}.\nRegistro criado na Base Operacional.`);
  };

  // Abrir janela de impressão
  const abrirPDF = (prop, tipo) => {
    setPropSel(prop); setPdfTipo(tipo); setModalPDF(true);
  };

  const CSS_DOC_AUX = `
    @page { size: A4; margin: 22mm 18mm 24mm 18mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12pt; color: #1A1610; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .pb-wrap { max-width: 680px; margin: 0 auto; }
    .pb-header { text-align: center; border-bottom: 3px solid #C9A84C; padding-bottom: 14px; margin-bottom: 24px; }
    .pb-header h1 { font-size: 18pt; font-weight: 900; letter-spacing: 2px; color: #1A1610; }
    .pb-header p { font-size: 10pt; color: #888; margin-top: 4px; }
    .pb-titulo { font-size: 14pt; font-weight: 700; text-transform: uppercase; text-align: center; letter-spacing: 1px; margin-bottom: 4px; }
    .pb-subtitulo { font-size: 12pt; font-weight: 700; letter-spacing: 1px; text-align: center; margin-bottom: 22px; }
    .pb-secao { font-size: 11.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; margin: 14px 0 6px; color: #1A1610; }
    .pb-corpo { font-size: 12pt; line-height: 1.9; margin-bottom: 16px; text-align: justify; }
    .pb-poderes { background: #f9f6ef; border-left: 4px solid #C9A84C; padding: 14px 18px; border-radius: 0 6px 6px 0; margin: 16px 0; }
    .pb-poderes p { font-size: 11.5pt; line-height: 1.8; margin-bottom: 6px; }
    .pb-rodape-data { margin-top: 50px; font-size: 12pt; }
    .pb-assinaturas { display: grid; grid-template-columns: 1fr 1fr; gap: 0 60px; margin-top: 50px; padding-top: 16px; border-top: 1px solid #ccc; }
    .pb-assinaturas-uma { display: flex; justify-content: center; margin-top: 70px; }
    .pb-assinaturas-uma .pb-assinatura { min-width: 320px; border-top: 1px solid #1a1a1a; padding-top: 10px; text-align: center; }
    .pb-assinaturas-uma .pb-assinatura:before { content: none; }
    .pb-assinatura { text-align: center; }
    .pb-assinatura:before { content: ""; display: block; border-top: 1px solid #555; margin-bottom: 10px; margin-top: 40px; }
    .pb-assinatura p { font-size: 11.5pt; font-weight: 700; margin-bottom: 3px; }
  `;

  const gerarTextoDocAux = (prop, tipo) => {
    const prod = produtos.find(p=>p.id===prop.produtoId);
    const cli  = clientes.find(c=>c.id===prop.clienteId) || leads.find(l=>l.id===prop.leadId);
    const nomeCli  = cli?.nome     || "[NOME]";
    const cpfCnpj  = cli?.cpfCnpj  || "[NÚMERO CPF/CNPJ]";
    const cidade   = cli?.cidade   || "[CIDADE]";
    const uf       = cli?.uf       || "[UF]";
    const cep      = cli?.cep      || "[CEP]";
    const endereco = cli?.endereco || "[ENDEREÇO – RUA, N. BAIRRO]";
    const endCompleto = `${endereco}${cep ? ", CEP " + cep : ""}, ${cidade}/${uf}`;
    // Fonte única: mesmos campos de qualificação do Cadastro de Cliente (nacionalidade/estadoCivil/profissao/rg/ufRg)
    // usados pela Base Operacional — evita regras/textos divergentes entre os dois pontos de emissão.
    const isPJ = cli?.tipo === "PJ";
    const qualificacaoPF = `${nomeCli}, ${cli?.nacionalidade||"[NACIONALIDADE]"}, ${cli?.estadoCivil||"[ESTADO CIVIL]"}, ${cli?.profissao||"[PROFISSÃO]"}, portador(a) da carteira de identidade nº ${cli?.rg||"[NÚMERO RG]"} / ${cli?.ufRg||"[UF RG]"} e inscrito(a) no CPF sob nº ${cpfCnpj}, residente e domiciliado(a) na ${endCompleto}`;
    const qualificacaoPJ = `${nomeCli}, pessoa jurídica de direito privado, inscrita no CNPJ sob nº ${cpfCnpj}, com sede em ${endCompleto}, neste ato representada na forma de seu contrato/estatuto social`;
    const qualificacao = isPJ ? qualificacaoPJ : qualificacaoPF;

    if (tipo === "procuracao") {
      return `OUTORGANTE:
${qualificacao}

OUTORGADOS:
NELSON JOSÉ ALVES, brasileiro, advogado, inscrito na OAB/SP sob o número 398.875, ANNIE LEMOS ALVES CORREA, brasileira, advogada, inscrita na OAB/SP sob o número 446.942, integrantes da sociedade de advogados, ALVES & CORREA ADVOGADOS, inscrita na OAB SP sob o número: 24.956, com sede na Avenida Paulista, 807, conjunto 2315, bairro Bela Vista - CEP 01311-915 – São Paulo – SP, e-mail: juridico@alvesecorreaadvogados.com.br.

PODERES:
A outorgante nomeia os outorgados seus procuradores, conferindo-lhes os poderes da cláusula "ad judicia" e "ad extra", para representá-lo em juízo ou fora dele, outorgando-lhes ainda os especiais poderes para transigir, fazer acordo, firmar compromisso, renunciar, desistir, reconhecer a procedência do pedido, receber intimações, receber e dar quitação, levantar e sacar alvarás, praticar todos atos perante repartições públicas Federais, Estaduais e Municipais, e órgãos da administração pública direta e indireta, praticar quaisquer atos perante particulares ou empresas privadas, recorrer a quaisquer instâncias e tribunais, podendo atuar em conjunto ou separadamente, substabelecer esta, a outrem, com ou sem reservas de iguais poderes, dando tudo por bom e valioso, para fim específico de representá-lo(a) administrativamente e judicialmente em processos em face do: [INFORMAR PARTE CONTRÁRIA / OBJETO — Ex: ${prod?.nome||"serviços advocatícios contratados"}].`;
    } else {
      return `DECLARANTE:
${qualificacao}

DECLARAÇÃO:
DECLARO, para todos os fins de direito e sob as penas da lei, que, em razão de minha condição financeira, estou impossibilitado(a) de arcar com as despesas inerentes ao presente processo, por "insuficiência de recursos", sem prejuízo do meu sustento e de minha família, nos termos do art. 5º, inciso LXXIV, da Constituição da República, e do artigo 98 e seguintes do Código de Processo Civil de 2015 – CPC/15, necessitando, portanto, da Gratuidade da Justiça, conforme clara redação do Código de Processo Civil de 2015.

Por essa razão, requeiro a concessão integral da justiça gratuita, abrangendo todos os atos do processo, na forma do art. 98 e seguintes, do CPC/15.`;
    }
  };

  // Monta os parágrafos por BLOCO (separado por linha em branco), não por linha isolada —
  // evita que quebras de linha acidentais (colar de texto externo, edição manual) partam
  // uma mesma cláusula em vários parágrafos curtos. Preserva o texto do usuário ao salvar,
  // só normaliza a exibição/impressão.
  const montarParagrafosDocAux = (texto) => {
    const blocos = (texto||"").split(/\n\s*\n/);
    return blocos.map(bloco => {
      const linhasBloco = bloco.split("\n").map(l=>l.trim()).filter(Boolean);
      if (linhasBloco.length === 0) return "";
      const primeira = linhasBloco[0];
      if (["OUTORGANTE:","OUTORGADOS:","PODERES:","DECLARANTE:","DECLARAÇÃO:"].includes(primeira)) {
        const resto = linhasBloco.slice(1).join(" ");
        return `<p class="pb-secao">${primeira}</p>` + (resto ? `<p class="pb-corpo">${resto}</p>` : "");
      }
      return `<p class="pb-corpo">${linhasBloco.join(" ")}</p>`;
    }).join("");
  };

  const imprimirDocAux = (tipo, texto, nomeCliente) => {
    const titulo = tipo === "procuracao" ? "Procuração" : "Declaração de Hipossuficiência";
    const linhas = montarParagrafosDocAux(texto);
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>${titulo} — Alves & Correa Advogados</title>
<style>${CSS_DOC_AUX}</style></head><body><div class="pb-wrap">
<div class="pb-header"><h1>Alves &amp; Correa Advogados</h1>
<p>Dr. Nelson José Alves · OAB/SP 398.875 | Dra. Annie Lemos Alves Correa · OAB/SP 446.942</p></div>
<div class="pb-titulo"${tipo==="procuracao"?"":' style="margin-bottom:22px"'}>${titulo.toUpperCase()}</div>
${tipo==="procuracao"?'<div class="pb-subtitulo">"AD JUDICIA ET EXTRA"</div>':""}
${linhas}
<div class="pb-rodape-data">São Paulo, ${new Date().toLocaleDateString("pt-BR",{day:"numeric",month:"long",year:"numeric"})}</div>
<div class="pb-assinaturas-uma">
  <div class="pb-assinatura"><p>${nomeCliente||"[NOME DO SIGNATÁRIO]"}</p><p style="color:#888;font-size:10pt">${tipo==="procuracao"?"OUTORGANTE":"DECLARANTE"}</p></div>
</div>
</div></body></html>`;
    const win = window.open("","_blank","width=900,height=800");
    if (!win) { alert("Permita pop-ups para gerar o PDF."); return; }
    win.document.write(html);
    win.document.close(); win.focus();
    setTimeout(()=>win.print(), 600);
  };

  const abrirDocAux = (prop, tipo) => {
    // Usa versão salva se existir, senão gera o padrão
    const textoSalvo = prop.textosEditados?.[tipo];
    const texto = textoSalvo || gerarTextoDocAux(prop, tipo);
    setModalDocAux({ tipo, texto, propId: prop.id, temVersaoSalva: !!textoSalvo });
  };

  const CSS_DOC = `
    @page { size: A4; margin: 20mm 18mm 24mm 18mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11.5pt; color: #1A1610; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .doc-wrap { max-width: 700px; margin: 0 auto; padding: 0; }
    /* Cabeçalho */
    .doc-header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 16px; border-bottom: 3px solid #C9A84C; margin-bottom: 28px; }
    .doc-logo h1 { font-size: 20pt; font-weight: 900; letter-spacing: 2px; color: #1A1610; line-height: 1.1; }
    .doc-logo p { font-size: 10pt; color: #888; letter-spacing: 3px; text-transform: uppercase; margin-top: 3px; }
    .doc-meta { text-align: right; }
    .doc-meta .tipo { font-size: 11pt; font-weight: 700; color: #1A1610; text-transform: uppercase; letter-spacing: 1px; }
    .doc-meta .num { font-size: 13pt; font-weight: 900; color: #C9A84C; margin: 3px 0; }
    .doc-meta .sub { font-size: 10pt; color: #888; }
    /* Seções */
    .sec { margin-bottom: 20px; }
    .sec-titulo { font-size: 9pt; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 5px 12px; background: #1A1610; color: #C9A84C; border-radius: 3px; margin-bottom: 12px; }
    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; }
    .campo label { font-size: 9pt; color: #888; text-transform: uppercase; letter-spacing: .5px; display: block; margin-bottom: 2px; }
    .campo span { font-size: 11.5pt; font-weight: 600; color: #1A1610; }
    /* Destaque honorários */
    .honorarios { background: #f9f6ef; border: 1px solid #C9A84C44; border-left: 4px solid #C9A84C; border-radius: 6px; padding: 14px 18px; margin-bottom: 20px; }
    .honorarios .label { font-size: 9pt; color: #888; text-transform: uppercase; letter-spacing: .8px; }
    .honorarios .valor { font-size: 20pt; font-weight: 900; color: #1A1610; margin: 4px 0; }
    .honorarios .forma { font-size: 11pt; color: #555; }
    /* Cláusulas */
    .clausula { margin-bottom: 14px; }
    .clausula strong { font-size: 11pt; color: #1A1610; }
    .clausula p { font-size: 11pt; line-height: 1.8; color: #333; margin-top: 5px; }
    /* Assinaturas */
    .assinaturas { display: grid; grid-template-columns: 1fr 1fr; gap: 0 60px; margin-top: 40px; padding-top: 16px; border-top: 1px solid #ddd; }
    .assinatura { text-align: center; }
    .assin-linha { border-top: 1px solid #555; margin-bottom: 8px; margin-top: 44px; }
    .assin-nome { font-size: 11.5pt; font-weight: 700; }
    .assin-sub { font-size: 9.5pt; color: #888; margin-top: 2px; }
    /* Rodapé */
    .rodape { margin-top: 24px; padding-top: 10px; border-top: 1px solid #eee; font-size: 8.5pt; color: #aaa; display: flex; justify-content: space-between; }
  `;

  const wrapHTML = (titulo, corpo) => `<!DOCTYPE html><html lang="pt-BR">
<head><meta charset="UTF-8"><title>${titulo} — Alves & Correa Advogados</title>
<style>${CSS_DOC}</style></head>
<body><div class="doc-wrap">${corpo}</div></body></html>`;

  const imprimir = () => {
    if (!propSel) return;
    const html = pdfTipo === "proposta" ? htmlProposta(propSel) : htmlContrato(propSel);
    const titulo = pdfTipo === "proposta" ? "Proposta Comercial" : "Contrato de Prestação de Serviços";
    const win = window.open("", "_blank", "width=900,height=800");
    if (!win) { alert("Permita pop-ups para gerar o PDF."); return; }
    win.document.write(wrapHTML(titulo, html));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 600);
  };

  // Helpers
  const getNomeProd = id => produtos.find(p=>p.id===id)?.nome||"—";
  const getNomeCli  = id => clientes.find(c=>c.id===id)?.nome || leads.find(l=>l.id===id)?.nome || "—";
  const getNomeResp = id => equipe.find(e=>e.id===id)?.nome||"—";
  const fmtMoeda    = v  => v ? `R$ ${parseFloat(v).toLocaleString("pt-BR",{minimumFractionDigits:2})}` : "—";
  const fmtData     = d  => d ? new Date(d+"T12:00:00").toLocaleDateString("pt-BR") : "—";

  // KPIs
  const totalProposto  = propostas.reduce((s,p)=>s+(parseFloat(p.valorHonorarios)||0),0);
  const propAprovadas  = propostas.filter(p=>["Aprovada","Proposta Aprovada","Contrato Gerado","Contrato Assinado"].includes(p.status));
  const propFechadas   = propostas.filter(p=>["Contrato Assinado","Contrato Gerado"].includes(p.status));
  const totalAprovado  = propAprovadas.reduce((s,p)=>s+(parseFloat(p.valorHonorarios)||0),0);
  const taxaAprov      = propostas.length>0 ? Math.round(propAprovadas.length/propostas.length*100) : 0;
  const aguardando     = propostas.filter(p=>["Enviada","Aguardando Aprovação"].includes(p.status)).length;

  // Bloco C — KPIs avançados de vendas
  const ticketMedioVendas = propFechadas.length > 0
    ? propFechadas.reduce((s,p)=>s+(parseFloat(p.valorHonorarios)||0),0) / propFechadas.length : 0;

  // Bloco D — Meta vs Realizado (vendas)
  const metaVendas    = parseFloat(vendas.metaVendas) || 0;
  const mesAtualV     = new Date().toISOString().slice(0,7);
  const realizadoMesV = propostas
    .filter(p=>["Contrato Assinado","Contrato Gerado"].includes(p.status)
      && (p.dataAssinatura||p.dataGeracaoContrato||p.dataEmissao||"").slice(0,7)===mesAtualV)
    .reduce((s,p)=>s+(parseFloat(p.valorHonorarios)||0),0);
  const faltamV   = Math.max(0, metaVendas - realizadoMesV);
  const pctMetaV  = metaVendas > 0 ? Math.min(100, Math.round(realizadoMesV/metaVendas*100)) : 0;
  const [editMetaV,  setEditMetaV]  = useState(false);
  const [metaVInput, setMetaVInput] = useState("");

  // Funil com % de conversão por etapa (etapa → próxima)
  const ETAPAS_FUNIL = [
    {status:"Rascunho",          label:"Rascunho"},
    {status:"Enviada",           label:"Enviada"},
    {status:"Proposta Aprovada", label:"Aprovada"},
    {status:"Contrato Gerado",   label:"Contrato"},
    {status:"Contrato Assinado", label:"Assinado"},
  ];
  const funilCounts = ETAPAS_FUNIL.map(e=>({
    ...e, qtd: propostas.filter(p=>p.status===e.status||(p.status==="Aprovada"&&e.status==="Proposta Aprovada")).length
  }));
  const funilMax = Math.max(...funilCounts.map(e=>e.qtd),1);

  // Ranking de agentes por faturamento (propostas fechadas)
  const rankingAgentes = equipe.map(ag=>{
    const props = propFechadas.filter(p=>p.responsavel===ag.id);
    return { nome:ag.nome, qtd:props.length, valor:props.reduce((s,p)=>s+(parseFloat(p.valorHonorarios)||0),0) };
  }).filter(a=>a.qtd>0).sort((a,b)=>b.valor-a.valor).slice(0,5);

  // Produtos campeões por % do total faturado
  const totalFaturadoProd = propFechadas.reduce((s,p)=>s+(parseFloat(p.valorHonorarios)||0),0)||1;
  const produtosMap = {};
  propFechadas.forEach(p=>{
    const nome = produtos.find(x=>x.id===p.produtoId)?.nome||"Outros";
    if(!produtosMap[nome]) produtosMap[nome]={valor:0,qtd:0};
    produtosMap[nome].valor+=(parseFloat(p.valorHonorarios)||0);
    produtosMap[nome].qtd++;
  });
  const produtosCampeoes = Object.entries(produtosMap).map(([nome,d])=>({nome,...d,pct:Math.round(d.valor/totalFaturadoProd*100)})).sort((a,b)=>b.valor-a.valor).slice(0,6);

  const propsFiltradas = propostas.filter(p => {
    const b = busca.toLowerCase();
    const nomeCli = getNomeCli(p.clienteId||p.leadId).toLowerCase();
    return (!busca  || p.numero?.toLowerCase().includes(b) || nomeCli.includes(b) || getNomeProd(p.produtoId).toLowerCase().includes(b))
        && (!filtStatus || p.status===filtStatus)
        && (!filtResp   || p.responsavel===filtResp);
  }).sort((a,b)=>b.dataEmissao?.localeCompare(a.dataEmissao));

  // HTML da proposta para impressão
  const htmlProposta = (prop) => {
    if (!prop) return "";
    const prod = produtos.find(p=>p.id===prop.produtoId);
    const cli  = clientes.find(c=>c.id===prop.clienteId) || leads.find(l=>l.id===prop.leadId);
    const nomeCliente = cli?.nome || "—";

    // Resolver documentos: IDs → nomes via documentosTrib
    const todosDocsTrib = data.documentosTrib || [];
    const docsProd = (prod?.documentosPadrao||prod?.trib_documentos||[])
      .map(d => {
        const id = typeof d==="string"?d:(d.id||d.docId||"");
        return typeof d==="string"
          ? (todosDocsTrib.find(x=>x.id===d)?.nome || d)
          : (d.nome || todosDocsTrib.find(x=>x.id===id)?.nome || id);
      }).filter(Boolean);

    const temDocs = docsProd.length > 0;
    const numHon = temDocs ? "III" : "II";
    const numCond = temDocs ? "IV" : "III";

    const honorarios = [];
    if (prop.valorHonorarios) honorarios.push(`Honorários: ${fmtMoeda(prop.valorHonorarios)}${prop.formaPagamento?" — "+prop.formaPagamento:""}${prop.parcelamento?" — "+prop.parcelamento:""}`);
    if (prop.percentualExito) honorarios.push(`Honorário de êxito: ${prop.percentualExito}% sobre o valor efetivamente recuperado/obtido`);
    if (prop.percentualEndividamento) honorarios.push(`Honorários equivalentes a ${prop.percentualEndividamento}% do valor total do endividamento`);

    const separador = `<hr style="border:none;border-top:1px solid #ddd;margin:18px 0"/>`;

    // Condições gerais — remover item 6 (documentos já estão no item II)
    const condicoesDefault = `1. Os honorários acordados referem-se exclusivamente aos serviços descritos nesta proposta.\n2. Quaisquer serviços adicionais serão objeto de nova proposta.\n3. O escritório compromete-se a manter sigilo sobre todas as informações do cliente.\n4. A proposta é válida pelo prazo indicado, podendo ser renovada mediante acordo entre as partes.\n5. O pagamento deverá ser feito através de transferência bancária na conta da CONTRATADA: Banco Inter nº 077, Agência 01-9, Conta Corrente 1150292-4 · PIX CNPJ: 29.574.525/0001-12 · Alves & Correa Advogados.`;
    const condicoes = (prop.condicoes||"")
      .replace(/\n?6\.\s*Documentos necessários[^\n]*/g, "")
      .trim() || condicoesDefault;

    return `
      <div class="doc-header">
        <div class="doc-logo">
          <h1>ALVES &amp; CORREA</h1>
          <p>Advogados</p>
        </div>
        <div class="doc-meta">
          <div class="tipo">Proposta de Honorários</div>
          <div class="tipo">Advocatícios</div>
          <div class="num">${prop.numero||""}</div>
          <div class="sub">Emissão: ${fmtData(prop.dataEmissao)}</div>
          <div class="sub">Válida até: ${fmtData(prop.dataValidade)}</div>
        </div>
      </div>

      <p style="font-size:11.5pt;line-height:1.8;margin-bottom:6px">
        À <strong>${nomeCliente}</strong>${cli?.cpfCnpj?`<br>CPF/CNPJ: ${cli.cpfCnpj}`:""}${cli?.email?`<br>E-mail: ${cli.email}`:""}
      </p>
      <p style="font-size:11.5pt;line-height:1.8;margin-bottom:18px">
        <strong>Ref.:</strong> Proposta para Honorários Advocatícios
      </p>

      <p style="font-size:11.5pt;line-height:1.8;margin-bottom:18px">
        Através da presente, levamos ao conhecimento de V.Sa., nossa proposta para a prestação de serviços jurídicos, especificados abaixo, bem como da documentação necessária para análise e instrução do processo.
      </p>

      <div class="clausula">
        <strong>I – DOS SERVIÇOS JURÍDICOS CONTRATADOS</strong>
        <p>A presente proposta tem por objeto a prestação de serviços jurídicos conforme abaixo descrito:</p>
        ${prod?.nome?`<p style="margin-top:8px"><strong>Serviço:</strong> ${prod.nome}${prod.area?" — Área do "+prod.area:""}</p>`:""}
        ${prop.descricao?`<p style="margin-top:6px">${prop.descricao}</p>`:""}
        <p style="margin-top:10px"><strong>Parágrafo Único:</strong> Os serviços iniciarão na data da aprovação da presente proposta e terminarão com o acordo entre as partes, com a decisão judicial de mérito ou terminativa.</p>
      </div>

      ${temDocs?`
      ${separador}
      <div class="clausula">
        <strong>II – DA DOCUMENTAÇÃO</strong>
        <p>Especificamos abaixo, os documentos necessários para acompanhamento do processo:</p>
        <ul style="margin-top:8px;padding-left:20px;line-height:2">
          ${docsProd.map(d=>`<li style="font-size:11pt">${d}</li>`).join("")}
        </ul>
      </div>`:""}

      ${separador}
      <div class="clausula">
        <strong>${numHon} – DOS HONORÁRIOS E CONDIÇÕES DE PAGAMENTO</strong>
        <p>Pela prestação dos serviços jurídicos objeto da presente proposta, ficam estabelecidos os seguintes honorários:</p>
        <ul style="margin-top:8px;padding-left:20px;line-height:2">
          ${honorarios.map(h=>`<li style="font-size:11pt">${h}</li>`).join("")}
        </ul>
      </div>

      ${separador}
      <div class="clausula">
        <strong>${numCond} – DAS CONDIÇÕES GERAIS</strong>
        <p style="white-space:pre-line;line-height:1.8;margin-top:8px">${condicoes}</p>
      </div>

      <div style="margin-top:32px;line-height:1.8">
        <p style="font-size:11.5pt">Cordialmente,</p>
        <p style="font-size:11.5pt;margin-top:4px">São Paulo, ${new Date().toLocaleDateString("pt-BR",{day:"numeric",month:"long",year:"numeric"})}</p>
      </div>

      <div class="assinaturas">
        <div class="assinatura">
          <div class="assin-linha"></div>
          <p class="assin-nome">Annie Lemos Alves Correa</p>
          <p class="assin-sub">OAB/SP 446.942</p>
        </div>
        <div class="assinatura">
          <div class="assin-linha"></div>
          <p class="assin-nome">Nelson José Alves</p>
          <p class="assin-sub">OAB/SP 398.875</p>
        </div>
      </div>
      <div class="rodape">
        <span>Alves &amp; Correa Advogados · contato@alvescorreaadvogados.com.br</span>
        <span>São Paulo/SP · Foz do Iguaçu/PR</span>
      </div>
      ${prop.textosEditados?.proposta_obs ? `<div style="margin-top:16px;padding:12px 16px;border:1px solid #C9A84C44;border-radius:6px;background:#f9f6ef"><p style="font-size:10pt;font-weight:bold;color:#1A1610;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px">Observações</p><p style="font-size:11pt;line-height:1.8;white-space:pre-line">${prop.textosEditados.proposta_obs}</p></div>` : ""}
    `;
  };

  const htmlContrato = (prop) => {
    if (!prop) return "";
    const prod = produtos.find(p=>p.id===prop.produtoId);
    const cli  = clientes.find(c=>c.id===prop.clienteId) || leads.find(l=>l.id===prop.leadId);
    const numCont = prop.contratoGerado
      ? contratos.find(c=>c.id===prop.contratoGerado)?.numContrato||""
      : `${anoAtual}/${String(contratos.length+1).padStart(3,"0")}`;
    const area = prod?.area ? `direito ${prod.area}` : "direito Tributário";
    // Item 6: documentos necessários — resolver IDs contra documentosTrib
    const todosDocsTrib = data.documentosTrib || [];
    const docsProd = (prod?.documentosPadrao||prod?.trib_documentos||[])
      .map(d => {
        const id = typeof d === "string" ? d : (d.id||d.docId||"");
        const nome = typeof d === "string"
          ? (todosDocsTrib.find(x=>x.id===d)?.nome || d)
          : (d.nome || todosDocsTrib.find(x=>x.id===id)?.nome || id);
        return nome;
      })
      .filter(Boolean);
    const item6 = docsProd.length > 0
      ? `\n6. Documentos necessários para execução dos serviços: ${docsProd.join(", ")}.`
      : `\n6. Documentos necessários: a serem informados conforme levantamento inicial do processo.`;
    const condicoesDefault = `1. Os honorários acordados referem-se exclusivamente aos serviços descritos nesta proposta.\n2. Quaisquer serviços adicionais serão objeto de nova proposta.\n3. O escritório compromete-se a manter sigilo sobre todas as informações do cliente.\n4. A proposta é válida pelo prazo indicado, podendo ser renovada mediante acordo entre as partes.\n5. O pagamento deverá ser feito através de transferência bancária na conta da CONTRATADA: Banco Inter nº 077, Agência 01-9, Conta Corrente 1150292-4 · PIX CNPJ: 29.574.525/0001-12 · Alves & Correa Advogados.${item6}`;
    const condicoes = (prop.condicoes && prop.condicoes.trim()) ? prop.condicoes : condicoesDefault;
    return `
      <div class="doc-header">
        <div class="doc-logo">
          <h1>ALVES &amp; CORREA</h1>
          <p>Advogados</p>
        </div>
        <div class="doc-meta">
          <div class="tipo">Contrato de Prestação</div>
          <div class="tipo">de Serviços Advocatícios</div>
          <div class="num">Nº ${numCont}</div>
          <div class="sub">Data: ${fmtData(hoje)}</div>
        </div>
      </div>
      <p style="font-size:11.5pt;line-height:1.8;margin-bottom:18px;">
        Pelo presente instrumento particular de contrato de prestação de serviços advocatícios, que entre si fazem:
      </p>
      <div class="clausula">
        <strong>CLÁUSULA 1ª — DAS PARTES</strong>
        <p><strong>CONTRATANTE:</strong> ${cli?.nome||"___________________"}, inscrito(a) no CPF/CNPJ sob o nº ${cli?.cpfCnpj||"___________________"}, doravante denominado(a) simplesmente CONTRATANTE.</p>
        <p style="margin-top:8px"><strong>CONTRATADA:</strong> <strong>ALVES &amp; CORREA ADVOGADOS</strong>, inscrita na OAB/SP, com sede na cidade de São Paulo/SP, doravante denominada CONTRATADA.</p>
      </div>
      <div class="clausula">
        <strong>CLÁUSULA 2ª — DO OBJETO</strong>
        <p>O presente contrato tem por objeto a prestação de serviços advocatícios referentes a <strong>${prod?.nome||"___________________"}</strong>${prod?.area?`, na área do ${area}`:""}. ${prop.descricao||""}</p>
      </div>
      <div class="clausula">
        <strong>CLÁUSULA 3ª — DOS HONORÁRIOS</strong>
        <p>Pelos serviços prestados, o CONTRATANTE pagará à CONTRATADA o valor de <strong>${fmtMoeda(prop.valorHonorarios)}</strong>, na forma de pagamento: <strong>${prop.formaPagamento||"À vista"}</strong>${prop.parcelamento?", "+prop.parcelamento:""}.${prop.percentualExito?` Adicionalmente, será devido honorário de êxito de ${prop.percentualExito}% sobre o valor efetivamente recuperado/obtido.`:""}</p>
      </div>
      <div class="clausula">
        <strong>CLÁUSULA 4ª — DO PRAZO</strong>
        <p>O presente contrato vigorará por prazo indeterminado, podendo ser rescindido por qualquer das partes mediante notificação prévia de 30 (trinta) dias, ressalvadas as obrigações já assumidas.</p>
      </div>
      <div class="clausula">
        <strong>CLÁUSULA 5ª — DAS OBRIGAÇÕES DA CONTRATADA</strong>
        <p>A CONTRATADA obriga-se a: (i) prestar os serviços com zelo e diligência; (ii) manter sigilo sobre as informações do CONTRATANTE; (iii) informar periodicamente sobre o andamento dos serviços; (iv) agir com ética e em conformidade com o Código de Ética da OAB.</p>
      </div>
      <div class="clausula">
        <strong>CLÁUSULA 6ª — DAS OBRIGAÇÕES DO CONTRATANTE</strong>
        <p>O CONTRATANTE obriga-se a: (i) fornecer todos os documentos e informações necessários; (ii) efetuar os pagamentos nos prazos acordados; (iii) comunicar qualquer alteração relevante que possa afetar os serviços contratados.</p>
      </div>
      <div class="clausula">
        <strong>CLÁUSULA 7ª — DAS CONDIÇÕES GERAIS</strong>
        <p style="white-space:pre-line;line-height:1.8">${condicoes}</p>
      </div>
      <div class="clausula">
        <strong>CLÁUSULA 8ª — DA RESCISÃO</strong>
        <p>O presente contrato poderá ser rescindido por descumprimento de qualquer de suas cláusulas, por mútuo acordo, ou por iniciativa de qualquer das partes mediante aviso prévio de 30 dias.</p>
      </div>
      <div class="clausula">
        <strong>CLÁUSULA 9ª — DO FORO</strong>
        <p>Fica eleito o foro da Comarca de São Paulo, Estado de São Paulo, para dirimir quaisquer controvérsias oriundas do presente contrato.</p>
      </div>
      <div class="assinaturas">
        <div class="assinatura">
          <div class="assin-linha"></div>
          <p class="assin-nome">${cli?.nome||"CONTRATANTE"}</p>
          <p class="assin-sub">CPF/CNPJ: ${cli?.cpfCnpj||"___________________"}</p>
          <p class="assin-sub">Contratante</p>
        </div>
        <div class="assinatura">
          <div class="assin-linha"></div>
          <p class="assin-nome">Alves &amp; Correa Advogados</p>
          <p class="assin-sub">Contratada</p>
        </div>
      </div>
      <div class="rodape">
        <span>Alves &amp; Correa Advogados · contato@alvescorreaadvogados.com.br</span>
        <span>São Paulo, ${new Date().toLocaleDateString("pt-BR",{day:"numeric",month:"long",year:"numeric"})}</span>
      </div>
      ${prop.textosEditados?.contrato_obs ? `<div style="margin-top:16px;padding:12px 16px;border:1px solid #C9A84C44;border-radius:6px;background:#f9f6ef"><p style="font-size:10pt;font-weight:bold;color:#1A1610;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px">Aditamentos / Observações</p><p style="font-size:11pt;line-height:1.8;white-space:pre-line">${prop.textosEditados.contrato_obs}</p></div>` : ""}
    `;
  };

  const ABAS_VD = [
    ["dashboard","🏠 Dashboard"],
    ["propostas","📄 Propostas"],
    ["agentes","🤖 Agentes IA"],
  ];

  return (
    <div className="fade-in">
      {/* CSS de impressão injetado */}
      <style>{PRINT_STYLE}</style>

      <PageHeader title="Vendas" subtitle="Propostas comerciais, contratos e gestão do funil de vendas"
        action={
          <div style={{display:"flex",gap:8}}>
            {aba==="propostas"&&<Btn onClick={()=>{setEditProp({...EMPTY_PROP,numero:gerarNumProp()});setModalProp(true);}}>+ Nova Proposta</Btn>}
            {aba==="agentes"  && null /* agentes gerenciados no módulo IA */}
          </div>
        }
      />

      {/* Abas */}
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:`2px solid ${theme.gray100}`,paddingBottom:0}}>
        {ABAS_VD.map(([id,lbl])=>(
          <button key={id} onClick={()=>setAba(id)} style={{
            padding:"10px 16px",fontSize:12,fontWeight:aba===id?700:400,cursor:"pointer",
            border:"none",background:"transparent",
            color:aba===id?theme.gold:theme.gray500,
            borderBottom:aba===id?`2px solid ${theme.gold}`:"2px solid transparent",
            marginBottom:-2,fontFamily:"Jost,sans-serif"
          }}>{lbl}</button>
        ))}
      </div>

      {/* ======= DASHBOARD ======= */}
      {aba==="dashboard"&&(
        <div>
          {/* KPIs */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:14,marginBottom:24}}>
            {[
              {l:"Total Propostas",   v:propostas.length,             icon:"📄", c:theme.blue},
              {l:"Aguardando",        v:aguardando,                   icon:"⏳", c:theme.amber},
              {l:"Taxa de Aprovação", v:`${taxaAprov}%`,              icon:"✅", c:theme.green},
              {l:"Valor Aprovado",    v:fmtMoeda(totalAprovado),      icon:"💰", c:theme.goldDark},
              {l:"Ticket Médio",      v:fmtMoeda(ticketMedioVendas),  icon:"🎫", c:theme.blue},
            ].map(k=>(
              <Card key={k.l} style={{padding:"14px 18px"}}>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{k.l}</p>
                  <span style={{fontSize:16}}>{k.icon}</span>
                </div>
                <p style={{fontSize:["Valor Aprovado","Ticket Médio"].includes(k.l)?14:24,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
              </Card>
            ))}
          </div>

          {/* Funil com % conversão */}
          <Card style={{padding:"20px 24px",marginBottom:20}}>
            <p className="serif" style={{fontSize:17,fontWeight:700,color:theme.gray900,marginBottom:16}}>📊 Funil de Vendas — Conversão por Etapa</p>
            <div style={{display:"flex",gap:6,alignItems:"flex-end",height:110}}>
              {funilCounts.map((e,i)=>{
                const h   = Math.max((e.qtd/funilMax)*90,4);
                const pct = i>0&&funilCounts[i-1].qtd>0 ? Math.round(e.qtd/funilCounts[i-1].qtd*100) : null;
                const COR = ["#94a3b8","#3b82f6","#22c55e","#7c3aed","#16a34a"];
                return (
                  <React.Fragment key={e.status}>
                    {i>0&&<div style={{display:"flex",alignItems:"center",paddingBottom:30,color:theme.gray400,fontSize:11}}>
                      {pct!==null?`${pct}%`:"→"}
                    </div>}
                    <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                      <span style={{fontSize:13,fontWeight:700,color:COR[i]}}>{e.qtd}</span>
                      <div style={{width:"100%",height:h,background:COR[i],borderRadius:"4px 4px 0 0",transition:"height .3s",opacity:.85}}/>
                      <span style={{fontSize:9,color:theme.gray500,textAlign:"center",lineHeight:1.2}}>{e.label}</span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <p style={{fontSize:11,color:theme.gray400,marginTop:12}}>
              Taxa geral de fechamento: <strong style={{color:theme.gray700}}>{propostas.length>0?Math.round(propFechadas.length/propostas.length*100):0}%</strong>
              {" · "}{propFechadas.length} contratos assinados de {propostas.length} propostas
            </p>
          </Card>

          {/* Ranking agentes + Produtos campeões */}
          {(rankingAgentes.length>0||produtosCampeoes.length>0)&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
              {rankingAgentes.length>0&&(
                <Card style={{padding:"18px 20px"}}>
                  <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:14}}>🏆 Ranking de Agentes — Faturamento</p>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {rankingAgentes.map((ag,i)=>(
                      <div key={ag.nome} style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{width:22,height:22,borderRadius:11,background:i===0?theme.gold:theme.gray100,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:i===0?"#fff":theme.gray500,flexShrink:0}}>{i+1}</span>
                        <span style={{flex:1,fontSize:12,color:theme.gray800,fontWeight:i===0?700:400}}>{ag.nome}</span>
                        <span style={{fontSize:12,color:theme.gray500}}>{ag.qtd} prop.</span>
                        <span style={{fontSize:13,fontWeight:700,color:theme.gray900}}>{fmtMoeda(ag.valor)}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
              {produtosCampeoes.length>0&&(
                <Card style={{padding:"18px 20px"}}>
                  <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:14}}>🥇 Produtos Campeões — % do Faturamento</p>
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {produtosCampeoes.map((p,i)=>(
                      <div key={p.nome} style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                            <span style={{fontSize:12,color:theme.gray800,fontWeight:i===0?700:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:160}}>{p.nome}</span>
                            <span style={{fontSize:12,fontWeight:700,color:theme.gray700,flexShrink:0}}>{p.pct}%</span>
                          </div>
                          <div style={{background:theme.gray100,borderRadius:99,height:6,overflow:"hidden"}}>
                            <div style={{width:`${p.pct}%`,height:"100%",background:theme.gold,borderRadius:99}}/>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* ══ Bloco D — Meta de Vendas do Mês ══ */}
          <Card style={{padding:"20px 24px",marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <p className="serif" style={{fontSize:17,fontWeight:700,color:theme.gray900}}>🎯 Meta de Vendas — {new Date().toLocaleString("pt-BR",{month:"long",year:"numeric"})}</p>
              {!editMetaV
                ? <Btn size="sm" variant="secondary" onClick={()=>{setMetaVInput(String(metaVendas||""));setEditMetaV(true);}}>
                    ✏️ {metaVendas>0?"Editar meta":"Definir meta"}
                  </Btn>
                : <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <input type="number" value={metaVInput} onChange={e=>setMetaVInput(e.target.value)}
                      placeholder="Meta em R$" min="0"
                      style={{width:140,padding:"6px 10px",border:`1px solid ${theme.gold}`,borderRadius:6,fontSize:13,outline:"none"}}/>
                    <Btn size="sm" onClick={()=>{setVendas("metaVendas",parseFloat(metaVInput)||0);setEditMetaV(false);}}>Salvar</Btn>
                    <Btn size="sm" variant="secondary" onClick={()=>setEditMetaV(false)}>✕</Btn>
                  </div>
              }
            </div>

            {metaVendas===0
              ? <div style={{textAlign:"center",padding:"20px 0",color:theme.gray400,fontSize:13}}>
                  Clique em "Definir meta" para configurar a meta mensal de vendas.
                </div>
              : <>
                  {/* Gauge de progresso */}
                  <div style={{marginBottom:20}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                      <span style={{fontSize:12,color:theme.gray500}}>Progresso do mês</span>
                      <span style={{fontSize:13,fontWeight:700,color:pctMetaV>=100?theme.green:pctMetaV>=70?theme.amber:theme.red}}>{pctMetaV}%</span>
                    </div>
                    <div style={{height:16,borderRadius:8,background:theme.gray100,overflow:"hidden",position:"relative"}}>
                      <div style={{
                        height:"100%",borderRadius:8,transition:"width .4s ease",
                        width:`${pctMetaV}%`,
                        background: pctMetaV>=100
                          ? `linear-gradient(90deg,${theme.green},#16a34a)`
                          : pctMetaV>=70
                          ? `linear-gradient(90deg,${theme.amber},#d97706)`
                          : `linear-gradient(90deg,${theme.gold},#b8860b)`
                      }}/>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
                      <span style={{fontSize:11,color:theme.gray400}}>R$ 0</span>
                      <span style={{fontSize:11,color:theme.gray400}}>{fmtMoeda(metaVendas)}</span>
                    </div>
                  </div>

                  {/* Tabela Meta vs Realizado */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
                    {[
                      {l:"Meta do Mês",      v:fmtMoeda(metaVendas),     c:theme.gray700,  bg:"#f8f9fa"},
                      {l:"Realizado",         v:fmtMoeda(realizadoMesV),  c:realizadoMesV>=metaVendas?theme.green:theme.blue, bg:realizadoMesV>=metaVendas?"#f0fdf4":"#eff6ff"},
                      {l:"Faltam",            v:faltamV>0?fmtMoeda(faltamV):"✅ Meta atingida", c:faltamV>0?theme.amber:theme.green, bg:faltamV>0?"#fffbeb":"#f0fdf4"},
                      {l:"Contratos no mês",  v:propostas.filter(p=>["Contrato Assinado","Contrato Gerado"].includes(p.status)&&(p.dataAssinatura||p.dataGeracaoContrato||p.dataEmissao||"").slice(0,7)===mesAtualV).length, c:theme.gray700, bg:"#f8f9fa"},
                    ].map(k=>(
                      <div key={k.l} style={{padding:"14px 16px",borderRadius:8,background:k.bg,border:`1px solid ${theme.gray100}`}}>
                        <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>{k.l}</p>
                        <p style={{fontSize:15,fontWeight:700,color:k.c}}>{k.v}</p>
                      </div>
                    ))}
                  </div>
                </>
            }
          </Card>

          {/* Propostas recentes */}
          <Card style={{padding:"20px 24px"}}>
            <p className="serif" style={{fontSize:17,fontWeight:700,color:theme.gray900,marginBottom:16}}>📋 Propostas Recentes</p>
            {propostas.length===0
              ? <EmptyState icon="📄" title="Nenhuma proposta cadastrada" subtitle='Vá para a aba "Propostas" e clique em "+ Nova Proposta".'/>
              : <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {[...propostas].sort((a,b)=>b.dataEmissao?.localeCompare(a.dataEmissao)).slice(0,6).map(p=>(
                    <div key={p.id} onClick={()=>{setPropSel(p);setAba("propostas");}} style={{
                      display:"flex",alignItems:"center",gap:14,padding:"12px 16px",borderRadius:8,
                      border:`1px solid ${theme.gray100}`,cursor:"pointer",background:theme.white,
                      transition:"background .15s"
                    }} onMouseEnter={e=>e.currentTarget.style.background=theme.gray50}
                       onMouseLeave={e=>e.currentTarget.style.background=theme.white}>
                      <div style={{width:36,height:36,borderRadius:8,background:`${theme.gold}18`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>📄</div>
                      <div style={{flex:1,minWidth:0}}>
                        <p style={{fontSize:13,fontWeight:600,color:theme.gray900}}>{p.numero} — {getNomeCli(p.clienteId||p.leadId)}</p>
                        <p style={{fontSize:11,color:theme.gray500,marginTop:2}}>{getNomeProd(p.produtoId)} · {fmtData(p.dataEmissao)}</p>
                      </div>
                      <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
                        <span style={{fontSize:13,fontWeight:700,color:theme.gray800}}>{fmtMoeda(p.valorHonorarios)}</span>
                        <Badge color={COR_STATUS[p.status]}>{p.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
            }
          </Card>
        </div>
      )}

      {/* ======= PROPOSTAS ======= */}
      {aba==="propostas"&&(
        <div style={{display:"grid",gridTemplateColumns:propSel?"1fr 380px":"1fr",gap:20}}>
          {/* Lista */}
          <div>
            <Card style={{padding:"12px 18px",marginBottom:14,display:"flex",gap:10,flexWrap:"wrap"}}>
              <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="🔍 Buscar proposta, cliente ou produto..."
                style={{flex:1,minWidth:180,padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}
                onFocus={e=>e.target.style.borderColor=theme.gold} onBlur={e=>e.target.style.borderColor=theme.gray200}/>
              <select value={filtStatus} onChange={e=>setFiltStatus(e.target.value)}
                style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
                <option value="">Todos os status</option>
                {STATUS_PROP.map(s=><option key={s}>{s}</option>)}
              </select>
              <select value={filtResp} onChange={e=>setFiltResp(e.target.value)}
                style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
                <option value="">Todos responsáveis</option>
                {equipe.filter(e=>e.ativo).map(e=><option key={e.id} value={e.id}>{e.nome}</option>)}
              </select>
              {(busca||filtStatus||filtResp)&&<Btn size="sm" variant="secondary" onClick={()=>{setBusca("");setFiltStatus("");setFiltResp("");}}>✕</Btn>}
            </Card>

            {propsFiltradas.length===0
              ? <Card><EmptyState icon="📄" title="Nenhuma proposta" subtitle='Clique em "+ Nova Proposta" para começar.'/></Card>
              : <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {propsFiltradas.map(p=>{
                    const ativo = propSel?.id===p.id;
                    return (
                      <Card key={p.id} onClick={()=>setPropSel(ativo?null:p)} style={{
                        padding:"14px 18px",cursor:"pointer",
                        border:`2px solid ${ativo?theme.gold:theme.gray100}`,
                        background:ativo?`${theme.gold}06`:theme.white,
                        transition:"border-color .15s"
                      }}>
                        <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                              <p style={{fontSize:13,fontWeight:700,color:theme.gray900}}>{p.numero}</p>
                              <Badge color={COR_STATUS[p.status]}>{p.status}</Badge>
                            </div>
                            <p style={{fontSize:13,color:theme.gray700,fontWeight:500}}>{getNomeCli(p.clienteId||p.leadId)}</p>
                            <p style={{fontSize:11,color:theme.gray500,marginTop:3}}>{getNomeProd(p.produtoId)} · Emissão: {fmtData(p.dataEmissao)} · Válida até: {fmtData(p.dataValidade)}</p>
                          </div>
                          <div style={{textAlign:"right",flexShrink:0}}>
                            <p style={{fontSize:16,fontWeight:700,color:theme.gray900}}>{fmtMoeda(p.valorHonorarios)}</p>
                            <p style={{fontSize:11,color:theme.gray500,marginTop:2}}>{p.formaPagamento}</p>
                          </div>
                        </div>
                        <div style={{display:"flex",gap:6,marginTop:12}}>
                          <Btn size="sm" variant="secondary" onClick={e=>{e.stopPropagation();abrirPDF(p,"proposta");}}>📄 Proposta PDF</Btn>
                          {p.status==="Aprovada"&&<Btn size="sm" variant="secondary" onClick={e=>{e.stopPropagation();abrirPDF(p,"contrato");}}>📋 Contrato PDF</Btn>}
                          {!["Aprovada","Recusada","Expirada"].includes(p.status)&&<Btn size="sm" onClick={e=>{e.stopPropagation();iniciarConversao(p);}} style={{background:theme.green,color:"#fff",border:"none"}}>✅ Aprovar</Btn>}
                          {!["Aprovada","Recusada","Expirada"].includes(p.status)&&<Btn size="sm" variant="danger" onClick={e=>{e.stopPropagation();alterarStatus(p,"Recusada","Proposta recusada pelo cliente.");}}>✕ Recusar</Btn>}
                          <Btn size="sm" variant="secondary" onClick={e=>{e.stopPropagation();setEditProp({...p});setModalProp(true);}}>✏️</Btn>
                          <Btn size="sm" variant="danger" onClick={e=>{e.stopPropagation();removerProp(p.id);}}>×</Btn>
                        </div>
                      </Card>
                    );
                  })}
                </div>
            }
          </div>

          {/* Painel lateral detalhe */}
          {propSel&&(
            <div>
              <Card style={{padding:"18px 20px",marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                  <div>
                    <p className="serif" style={{fontSize:17,fontWeight:700,color:theme.gray900}}>{propSel.numero}</p>
                    <Badge color={COR_STATUS[propSel.status]||"gray"} style={{marginTop:4}}>{propSel.status}</Badge>
                  </div>
                  <button onClick={()=>setPropSel(null)} style={{background:"none",border:"none",fontSize:20,color:theme.gray400,cursor:"pointer"}}>×</button>
                </div>

                {/* ── STEPPER ── */}
                <div style={{marginBottom:12,padding:"12px 0 8px",borderBottom:`1px solid ${theme.gray100}`}}>
                  <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>Linha do Tempo</p>
                  <div style={{position:"relative"}}>
                    <div style={{position:"absolute",top:16,left:16,right:16,height:2,background:theme.gray100,zIndex:0}}/>
                    <div style={{position:"absolute",top:16,left:16,height:2,zIndex:1,
                      background:"linear-gradient(90deg,#22c55e,#7c3aed)",
                      width: `${Math.max(0, idxEtapa(propSel.status) / (ETAPAS.length-1) * 100)}%`,
                      transition:"width .4s"}}/>
                    <div style={{display:"flex",justifyContent:"space-between",position:"relative",zIndex:2}}>
                      {ETAPAS.map((e,i)=>{
                        const idx = idxEtapa(propSel.status);
                        const concluido = i < idx;
                        const atual = i === idx;
                        return (
                          <div key={e.status} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,flex:1}}>
                            <div style={{
                              width:32,height:32,borderRadius:"50%",
                              display:"flex",alignItems:"center",justifyContent:"center",
                              fontSize:concluido?14:atual?16:12,
                              background:concluido?e.cor:atual?e.cor:"#f1f5f9",
                              border:`2px solid ${concluido||atual?e.cor:theme.gray200}`,
                              color:concluido||atual?"#fff":theme.gray300,
                              boxShadow:atual?`0 0 0 4px ${e.cor}22`:"none",
                              transition:"all .3s"
                            }}>
                              {concluido ? "✓" : e.icon}
                            </div>
                            <p style={{fontSize:9,fontWeight:atual?700:400,
                              color:atual?e.cor:concluido?theme.gray500:theme.gray300,
                              textAlign:"center",lineHeight:1.2,maxWidth:52}}>
                              {e.label}
                            </p>
                            {(concluido||atual) && (
                              <p style={{fontSize:8,color:theme.gray300,textAlign:"center"}}>
                                {e.status==="Enviada"&&propSel.dataEnvio ? new Date(propSel.dataEnvio+"T12:00").toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"}) : ""}
                                {e.status==="Proposta Aprovada"&&propSel.dataAprovacao ? new Date(propSel.dataAprovacao+"T12:00").toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"}) : ""}
                                {e.status==="Contrato Gerado"&&propSel.dataGeracaoContrato ? new Date(propSel.dataGeracaoContrato+"T12:00").toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"}) : ""}
                                {e.status==="Contrato Assinado"&&propSel.dataAssinatura ? new Date(propSel.dataAssinatura+"T12:00").toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"}) : ""}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* ── DATAS DO CICLO — auto-avanço de status ── */}
                {(()=>{
                  const salvarData = (campo, valor) => {
                    if (!valor) return;
                    let novoStatus = propSel.status;
                    // Regras de auto-avanço
                    if (campo === "dataEnvio" && idxEtapa(propSel.status) < 1)
                      novoStatus = "Enviada";
                    if (campo === "dataAprovacao" && idxEtapa(propSel.status) < 2)
                      novoStatus = "Proposta Aprovada";
                    if (campo === "dataGeracaoContrato" && idxEtapa(propSel.status) < 3)
                      novoStatus = "Contrato Gerado";
                    if (campo === "dataAssinatura" && idxEtapa(propSel.status) < 4)
                      novoStatus = "Contrato Assinado";
                    const obs = {
                      dataEnvio:           `Proposta enviada ao cliente em ${new Date(valor+"T12:00").toLocaleDateString("pt-BR")}.`,
                      dataAprovacao:       `Proposta aprovada em ${new Date(valor+"T12:00").toLocaleDateString("pt-BR")}.`,
                      dataGeracaoContrato: `Contrato gerado em ${new Date(valor+"T12:00").toLocaleDateString("pt-BR")}.`,
                      dataAssinatura:      `Contrato assinado pelas partes em ${new Date(valor+"T12:00").toLocaleDateString("pt-BR")}.`,
                    }[campo] || "";
                    const upd = {
                      ...propSel,
                      [campo]: valor,
                      status: novoStatus,
                      ocorrencias: [...(propSel.ocorrencias||[]), { data: hoje, obs, usuario:"" }]
                    };
                    setVendas("propostas", propostas.map(p=>p.id===propSel.id?upd:p));
                    setPropSel(upd);
                    if (upd.leadId) {
                      const MAPA_ESTAGIO_PROPOSTA = {
                        "Enviada": "proposta",
                        "Proposta Aprovada": "negociacao",
                        "Contrato Gerado": "envio_contrato",
                        "Contrato Assinado": "contrato_assinado",
                      };
                      if (MAPA_ESTAGIO_PROPOSTA[novoStatus]) sincronizarEstagioLead(upd.leadId, MAPA_ESTAGIO_PROPOSTA[novoStatus]);
                    }
                  };
                  const campos = [
                    { campo:"dataEnvio",           label:"📤 Enviada ao cliente",    etapa:1 },
                    { campo:"dataAprovacao",        label:"✅ Proposta aprovada",     etapa:2 },
                    { campo:"dataGeracaoContrato",  label:"📋 Contrato gerado",       etapa:3 },
                    { campo:"dataAssinatura",       label:"🖊️ Contrato assinado",    etapa:4 },
                  ];
                  return (
                    <div style={{marginBottom:12,padding:"10px 0",borderBottom:`1px solid ${theme.gray100}`}}>
                      <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>
                        Datas — preencher avança o status automaticamente
                      </p>
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {campos.map(({campo,label,etapa})=>{
                          const concluido = idxEtapa(propSel.status) >= etapa;
                          return (
                            <div key={campo} style={{display:"flex",alignItems:"center",gap:8}}>
                              <span style={{fontSize:11,flex:1,color:concluido?theme.gray700:theme.gray400,
                                fontWeight:concluido?600:400}}>{label}</span>
                              <input
                                type="date"
                                value={propSel[campo]||""}
                                onChange={e=>salvarData(campo, e.target.value)}
                                style={{
                                  padding:"4px 8px",borderRadius:6,fontSize:11,
                                  border:`1px solid ${concluido?theme.green:theme.gray200}`,
                                  background:concluido?"#f0fdf4":theme.white,
                                  color:concluido?theme.green:theme.gray600,
                                  outline:"none",cursor:"pointer",width:130
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Dados resumo */}
                {[
                  {l:"Cliente",    v:getNomeCli(propSel.clienteId||propSel.leadId)},
                  {l:"Produto",    v:getNomeProd(propSel.produtoId)},
                  {l:"Valor",      v:fmtMoeda(propSel.valorHonorarios)},
                  {l:"Pagamento",  v:propSel.formaPagamento+(propSel.parcelamento?" — "+propSel.parcelamento:"")},
                  {l:"Emissão",    v:fmtData(propSel.dataEmissao)},
                  {l:"Validade",   v:fmtData(propSel.dataValidade)},
                  {l:"Responsável",v:getNomeResp(propSel.responsavel)},
                  ...(propSel.dataAssinatura?[{l:"Assinatura",v:fmtData(propSel.dataAssinatura)}]:[]),
                ].map(r=>(
                  <div key={r.l} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${theme.gray100}`}}>
                    <span style={{fontSize:11,color:theme.gray400}}>{r.l}</span>
                    <span style={{fontSize:12,fontWeight:600,color:theme.gray800,textAlign:"right",maxWidth:"60%"}}>{r.v||"—"}</span>
                  </div>
                ))}

                {propSel.contratoGerado&&(
                  <div style={{marginTop:10,padding:"8px 12px",background:`${theme.green}11`,borderRadius:7,border:`1px solid ${theme.green}33`,fontSize:12,color:theme.green}}>
                    ✅ Contrato: {contratos.find(c=>c.id===propSel.contratoGerado)?.numContrato||propSel.contratoGerado}
                  </div>
                )}

                {/* Botões de ação por etapa */}
                <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:8}}>

                  {/* Gerar PDF proposta — sempre */}
                  <Btn size="sm" onClick={()=>abrirPDF(propSel,"proposta")} style={{width:"100%"}}>
                    📄 Gerar Proposta PDF
                  </Btn>

                  {/* Marcar como Enviada */}
                  {["Rascunho","Aguardando Aprovação"].includes(propSel.status)&&(
                    <Btn size="sm" onClick={()=>{
                      const upd={...propSel,status:"Enviada",dataEnvio:hoje,
                        ocorrencias:[...(propSel.ocorrencias||[]),{data:hoje,obs:"Proposta marcada como enviada ao cliente.",usuario:""}]};
                      setVendas("propostas",propostas.map(p=>p.id===propSel.id?upd:p));
                      setPropSel(upd);
                    }} style={{width:"100%",background:"#3b82f6",border:"none",color:"#fff"}}>
                      📤 Marcar como Enviada
                    </Btn>
                  )}

                  {/* Aprovar Proposta */}
                  {!["Proposta Aprovada","Contrato Gerado","Contrato Assinado","Recusada","Expirada","Aprovada"].includes(propSel.status)&&(
                    <Btn size="sm" onClick={()=>iniciarConversao(propSel)}
                      style={{width:"100%",background:theme.green,border:"none",color:"#fff"}}>
                      ✅ Aprovar Proposta
                    </Btn>
                  )}

                  {/* Gerar Contrato PDF — após aprovação */}
                  {["Proposta Aprovada","Aprovada","Contrato Gerado","Contrato Assinado"].includes(propSel.status)&&(
                    <Btn size="sm" onClick={()=>abrirPDF(propSel,"contrato")} style={{width:"100%"}}
                      variant="secondary">
                      📋 Gerar Contrato PDF
                    </Btn>
                  )}

                  {/* Registrar Assinatura */}
                  {["Contrato Gerado","Proposta Aprovada","Aprovada"].includes(propSel.status)&&(
                    <Btn size="sm" onClick={()=>setModalAssinatura(true)}
                      style={{width:"100%",background:"#7c3aed",border:"none",color:"#fff"}}>
                      🖊️ Registrar Assinatura
                    </Btn>
                  )}

                  {/* Procuração e Declaração — disponíveis após aprovação */}
                  {["Proposta Aprovada","Contrato Gerado","Contrato Assinado","Aprovada"].includes(propSel.status)&&(
                    <div style={{display:"flex",gap:6,marginTop:4}}>
                      <button onClick={()=>abrirDocAux(propSel,"procuracao")}
                        style={{flex:1,padding:"7px 8px",borderRadius:7,border:`1px solid ${theme.gray200}`,
                          background:theme.white,cursor:"pointer",fontSize:11,fontWeight:600,color:theme.gray700}}>
                        📜 Procuração
                      </button>
                      <button onClick={()=>abrirDocAux(propSel,"hipossuficiencia")}
                        style={{flex:1,padding:"7px 8px",borderRadius:7,border:`1px solid ${theme.gray200}`,
                          background:theme.white,cursor:"pointer",fontSize:11,fontWeight:600,color:theme.gray700}}>
                        📋 Decl. Hipossu.
                      </button>
                    </div>
                  )}

                  {/* Recusar */}
                  {!["Proposta Aprovada","Contrato Gerado","Contrato Assinado","Recusada","Expirada","Aprovada"].includes(propSel.status)&&(
                    <Btn size="sm" variant="danger" onClick={()=>alterarStatus(propSel,"Recusada","Recusada pelo cliente.")}
                      style={{width:"100%"}}>
                      ✕ Recusar
                    </Btn>
                  )}
                </div>
              </Card>

              {/* Histórico */}
              <Card style={{padding:"16px 18px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <p style={{fontSize:13,fontWeight:700,color:theme.gray900}}>📋 Histórico</p>
                  <Btn size="sm" variant="secondary" onClick={()=>setModalOcorr(true)}>+ Ocorrência</Btn>
                </div>
                {(propSel.ocorrencias||[]).length===0
                  ? <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"8px 0"}}>Sem ocorrências registradas.</p>
                  : [...(propSel.ocorrencias||[])].reverse().map((oc,i)=>(
                      <div key={i} style={{padding:"8px 0",borderBottom:`1px solid ${theme.gray100}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                          <Badge color={COR_STATUS[oc.status]||"gray"} style={{fontSize:9}}>{oc.status}</Badge>
                          <span style={{fontSize:10,color:theme.gray400}}>{fmtData(oc.data)}</span>
                        </div>
                        {oc.obs&&<p style={{fontSize:11,color:theme.gray600,marginTop:2,lineHeight:1.5}}>{oc.obs}</p>}
                      </div>
                    ))
                }
              </Card>
            </div>
          )}
        </div>
      )}

      {/* ======= AGENTES IA ======= */}
      {aba==="agentes" && (
        <div>
          {/* Aviso + botão selecionar */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
            padding:"10px 14px",borderRadius:8,background:"#eff6ff",border:"1px solid #bfdbfe",marginBottom:16}}>
            <p style={{fontSize:12,color:"#1e40af",margin:0}}>
              🔗 Agentes do cadastro central <strong>IA — Assistentes Jurídicos</strong>.
            </p>
            <button onClick={()=>setModalSelAg(true)} style={{padding:"7px 16px",borderRadius:7,
              border:"1px solid #1e40af",background:"#dbeafe",color:"#1e40af",
              cursor:"pointer",fontSize:12,fontWeight:700}}>
              ⚙️ Selecionar Agentes
            </button>
          </div>

          {/* Modal de seleção */}
          {modalSelAg && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:9999,
              display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:"24px 16px"}}>
              <div style={{background:theme.white,borderRadius:12,padding:"24px 28px",maxWidth:560,
                width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,.3)",marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0}}>⚙️ Selecionar Agentes — Vendas</p>
                  <button onClick={()=>setModalSelAg(false)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:theme.gray400}}>×</button>
                </div>
                <p style={{fontSize:11,color:theme.gray500,marginBottom:14}}>
                  Marque os agentes do cadastro central que devem aparecer no módulo de Vendas.
                </p>
                <div style={{maxHeight:360,overflowY:"auto",display:"flex",flexDirection:"column",gap:8}}>
                  {todosAgentes.length===0
                    ? <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"20px 0"}}>Nenhum agente cadastrado no módulo IA.</p>
                    : todosAgentes.map(a=>{
                        const sel = idsVendas.includes(a.id);
                        return (
                          <div key={a.id} onClick={()=>{
                            const novaLista = sel
                              ? idsVendas.filter(id=>id!==a.id)
                              : [...idsVendas, a.id];
                            setVendas("agentesVendas", novaLista);
                          }} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",
                            borderRadius:8,cursor:"pointer",
                            border:`2px solid ${sel?theme.gold:theme.gray200}`,
                            background:sel?theme.gold+"0A":theme.white,transition:"all .1s"}}>
                            <span style={{fontSize:22}}>{a.emoji||a.icon||"🤖"}</span>
                            <div style={{flex:1}}>
                              <p style={{fontSize:13,fontWeight:600,color:theme.gray900,margin:0}}>{a.nome}</p>
                              <p style={{fontSize:11,color:theme.gray500,margin:"2px 0 0"}}>{a.tema||""} {a.desc?"· "+a.desc.slice(0,50):""}</p>
                            </div>
                            <span style={{fontSize:18,color:sel?theme.gold:theme.gray300}}>{sel?"✓":"○"}</span>
                          </div>
                        );
                      })
                  }
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:16,paddingTop:14,borderTop:`1px solid ${theme.gray100}`}}>
                  <span style={{fontSize:11,color:theme.gray400}}>{idsVendas.length} agente(s) selecionado(s)</span>
                  <button onClick={()=>setModalSelAg(false)} style={{padding:"9px 24px",borderRadius:8,
                    border:"none",background:theme.gray900,color:theme.gold,cursor:"pointer",fontSize:13,fontWeight:700}}>
                    ✓ Confirmar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Grid de agentes selecionados */}
          {agentes.length===0
            ? <Card><EmptyState icon="🤖" title="Nenhum agente selecionado" subtitle="Clique em 'Selecionar Agentes' para escolher os agentes que aparecerão aqui."/></Card>
            : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:16}}>
                {agentes.map(a=><CardAgentePublico key={a.id} ag={a}/>)}
              </div>
          }
        </div>
      )}

      {/* MODAL NOVA PROPOSTA */}
      <Modal open={modalProp} onClose={()=>setModalProp(false)} title={editProp?.id?"Editar Proposta":"Nova Proposta"} width={680}>
        {editProp&&(
          <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
              <FormField label="Número"><Input value={editProp.numero} onChange={v=>setEditProp({...editProp,numero:v})} placeholder="PROP-2026-001"/></FormField>
              <FormField label="Status">
                <select value={editProp.status} onChange={e=>setEditProp({...editProp,status:e.target.value})}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
                  {STATUS_PROP.map(s=><option key={s}>{s}</option>)}
                </select>
              </FormField>
              <FormField label="Origem">
                <Select value={editProp.origem} onChange={v=>setEditProp({...editProp,origem:v,leadId:"",clienteId:""})} options={[{value:"direto",label:"Cliente direto"},{value:"lead",label:"Lead do CRM"}]}/>
              </FormField>
              {editProp.origem==="lead"
                ? <FormField label="Lead"><Select value={editProp.leadId} onChange={v=>setEditProp({...editProp,leadId:v})} options={leads.map(l=>({value:l.id,label:l.nome}))} placeholder="Selecione o lead..."/></FormField>
                : <FormField label="Cliente"><Select value={editProp.clienteId} onChange={v=>setEditProp({...editProp,clienteId:v})} options={clientes.map(c=>({value:c.id,label:c.nome}))} placeholder="Selecione o cliente..."/></FormField>
              }
              <FormField label="Produto" required><Select value={editProp.produtoId} onChange={v=>{
                const prodSel = produtos.find(p=>p.id===v);
                const todosDocsTrib = data.documentosTrib || [];
                const docsProd = (prodSel?.documentosPadrao||prodSel?.trib_documentos||[])
                  .map(d => {
                    const id = typeof d === "string" ? d : (d.id||d.docId||"");
                    return typeof d === "string"
                      ? (todosDocsTrib.find(x=>x.id===d)?.nome || d)
                      : (d.nome || todosDocsTrib.find(x=>x.id===id)?.nome || id);
                  })
                  .filter(Boolean);
                const item6 = docsProd.length>0
                  ? `\n6. Documentos necessários: ${docsProd.join(", ")}.`
                  : `\n6. Documentos necessários: a serem informados conforme levantamento inicial.`;
                const condicoesAtualizadas = (editProp.condicoes||"")
                  .replace(/\n6\..*$/s, "")  // remove item 6 antigo se houver
                  .trimEnd() + item6;
                setEditProp({...editProp, produtoId:v, condicoes:condicoesAtualizadas});
              }} options={produtos.map(p=>({value:p.id,label:p.nome}))} placeholder="Selecione o produto..."/></FormField>
              <FormField label="Responsável"><Select value={editProp.responsavel} onChange={v=>setEditProp({...editProp,responsavel:v})} options={equipe.filter(e=>e.ativo!==false).map(e=>({value:e.id,label:e.nome}))} placeholder="Selecione..."/></FormField>
              <FormField label="Valor dos Honorários (R$)"><Input type="number" value={editProp.valorHonorarios} onChange={v=>setEditProp({...editProp,valorHonorarios:v})} placeholder="0,00"/></FormField>
              <div style={{gridColumn:"1/-1",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,padding:"12px 14px",marginBottom:4}}>
                <p style={{fontSize:12,fontWeight:700,color:"#166534",marginBottom:8}}>🧮 Calculadora de Honorários Líquidos — Simples Nacional (Anexo IV)</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px"}}>
                  <FormField label="Faturamento do escritório últimos 12 meses (RBT12)">
                    <Input type="number" value={editProp.rbt12Calculo||""} onChange={v=>setEditProp({...editProp,rbt12Calculo:v})} placeholder="Ex: 480000"/>
                  </FormField>
                </div>
                {(()=>{
                  const bruto = parseFloat(editProp.valorHonorarios)||0;
                  const rbt12 = parseFloat(editProp.rbt12Calculo)||0;
                  if (!bruto || !rbt12) return <p style={{fontSize:11,color:theme.gray500}}>Informe o valor dos honorários e o RBT12 para calcular.</p>;
                  // Anexo IV — LC 123/2006 (Resolução CGSN 140/2018) — advocacia (sociedade de advogados)
                  const FAIXAS_ANEXO_IV = [
                    { ate: 180000,   aliq: 0.045,  pd: 0 },
                    { ate: 360000,   aliq: 0.09,   pd: 8100 },
                    { ate: 720000,   aliq: 0.102,  pd: 12420 },
                    { ate: 1800000,  aliq: 0.14,   pd: 39780 },
                    { ate: 3600000,  aliq: 0.22,   pd: 183780 },
                    { ate: 4800000,  aliq: 0.33,   pd: 828000 },
                  ];
                  const faixa = FAIXAS_ANEXO_IV.find(f=>rbt12<=f.ate) || FAIXAS_ANEXO_IV[FAIXAS_ANEXO_IV.length-1];
                  const aliqEfetiva = Math.max(0, ((rbt12*faixa.aliq)-faixa.pd)/rbt12);
                  const valorImposto = bruto*aliqEfetiva;
                  const liquido = bruto-valorImposto;
                  // Repartição percentual do DAS por tributo (Anexo IV, mesma faixa) — Resolução CGSN 140/2018, Anexo VI (tabela de repartição do Anexo IV)
                  const REPARTICAO = [
                    { irpj:0.188, csll:0.152, cofins:0.1767, pis:0.0383, iss:0.445 },
                    { irpj:0.198, csll:0.152, cofins:0.2055, pis:0.0445, iss:0.40 },
                    { irpj:0.208, csll:0.152, cofins:0.1973, pis:0.0427, iss:0.40 },
                    { irpj:0.178, csll:0.192, cofins:0.189,  pis:0.041,  iss:0.40 },
                    { irpj:0.188, csll:0.192, cofins:0.181,  pis:0.039,  iss:0.40 },
                    { irpj:0.535, csll:0.215, cofins:0.2055, pis:0.0445, iss:0.00 },
                  ];
                  const idxFaixa = FAIXAS_ANEXO_IV.indexOf(faixa);
                  const rep = REPARTICAO[idxFaixa];
                  const fmt = v=>"R$ "+v.toLocaleString("pt-BR",{minimumFractionDigits:2});
                  return (
                    <div style={{marginTop:8}}>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:10}}>
                        <div><p style={{fontSize:10,color:theme.gray500}}>Alíquota efetiva</p><p style={{fontSize:16,fontWeight:700,color:theme.gray800}}>{(aliqEfetiva*100).toFixed(2)}%</p></div>
                        <div><p style={{fontSize:10,color:theme.gray500}}>Total de impostos (DAS)</p><p style={{fontSize:16,fontWeight:700,color:"#b45309"}}>{fmt(valorImposto)}</p></div>
                        <div><p style={{fontSize:10,color:theme.gray500}}>Honorário líquido</p><p style={{fontSize:16,fontWeight:700,color:"#166534"}}>{fmt(liquido)}</p></div>
                      </div>
                      <div style={{overflowX:"auto"}}>
                        <table style={{width:"100%",fontSize:11,borderCollapse:"collapse"}}>
                          <thead><tr style={{color:theme.gray400,textTransform:"uppercase",fontSize:9}}>
                            <th style={{textAlign:"left",padding:"3px 8px"}}>ISS</th><th style={{textAlign:"left",padding:"3px 8px"}}>PIS</th>
                            <th style={{textAlign:"left",padding:"3px 8px"}}>COFINS</th><th style={{textAlign:"left",padding:"3px 8px"}}>IRPJ</th><th style={{textAlign:"left",padding:"3px 8px"}}>CSLL</th>
                          </tr></thead>
                          <tbody><tr>
                            <td style={{padding:"3px 8px"}}>{fmt(valorImposto*rep.iss)}</td>
                            <td style={{padding:"3px 8px"}}>{fmt(valorImposto*rep.pis)}</td>
                            <td style={{padding:"3px 8px"}}>{fmt(valorImposto*rep.cofins)}</td>
                            <td style={{padding:"3px 8px"}}>{fmt(valorImposto*rep.irpj)}</td>
                            <td style={{padding:"3px 8px"}}>{fmt(valorImposto*rep.csll)}</td>
                          </tr></tbody>
                        </table>
                      </div>
                      <p style={{fontSize:10,color:theme.gray400,marginTop:6}}>Cálculo conforme Anexo IV do Simples Nacional (LC 123/2006, Resolução CGSN 140/2018). CPP (INSS Patronal) não incluída — recolhida separadamente pelos sócios. Confirme a faixa/RBT12 com a contabilidade do escritório antes de usar o valor líquido como referência definitiva.</p>
                    </div>
                  );
                })()}
              </div>
              <FormField label="Forma de Pagamento"><Select value={editProp.formaPagamento} onChange={v=>setEditProp({...editProp,formaPagamento:v})} options={FORMAS_PAG}/></FormField>
              <FormField label="Detalhes do Pagamento"><Input value={editProp.parcelamento} onChange={v=>setEditProp({...editProp,parcelamento:v})} placeholder="Ex: entrada + 5x mensais"/></FormField>
              <FormField label="% Honorário de Êxito"><Input value={editProp.percentualExito} onChange={v=>setEditProp({...editProp,percentualExito:v})} placeholder="Ex: 20"/></FormField>
              <FormField label="% do Valor do Endividamento"><Input value={editProp.percentualEndividamento} onChange={v=>setEditProp({...editProp,percentualEndividamento:v})} placeholder="Ex: 5"/></FormField>
              <FormField label="% do Valor do Endividamento"><Input value={editProp.percentualEndividamento} onChange={v=>setEditProp({...editProp,percentualEndividamento:v})} placeholder="Ex: 5 (% sobre o total da dívida)"/></FormField>
              <FormField label="Data de Emissão"><Input type="date" value={editProp.dataEmissao} onChange={v=>setEditProp({...editProp,dataEmissao:v})}/></FormField>
              <FormField label="Válida até"><Input type="date" value={editProp.dataValidade} onChange={v=>setEditProp({...editProp,dataValidade:v})}/></FormField>
              <div style={{gridColumn:"1/-1"}}><FormField label="Descrição / Escopo dos Serviços"><Textarea value={editProp.descricao} onChange={v=>setEditProp({...editProp,descricao:v})} placeholder="Descreva o escopo dos serviços nesta proposta..." rows={3}/></FormField></div>
              <div style={{gridColumn:"1/-1"}}><FormField label="Condições Gerais (editável)"><Textarea value={editProp.condicoes} onChange={v=>setEditProp({...editProp,condicoes:v})} rows={10}/></FormField></div>

              {/* Fase 1 — Campos Comercial/Marketing */}
              <FormField label="Canal de Origem">
                <Select value={editProp.origemCanal||""} onChange={v=>setEditProp({...editProp,origemCanal:v})}
                  options={[
                    {value:"",label:"Não informado"},
                    {value:"WhatsApp",label:"📱 WhatsApp"},
                    {value:"Instagram DM",label:"📸 Instagram DM"},
                    {value:"LinkedIn",label:"💼 LinkedIn"},
                    {value:"Site — Formulário",label:"🌐 Site — Formulário"},
                    {value:"Google",label:"🔍 Google"},
                    {value:"Indicação Direta",label:"🤝 Indicação Direta"},
                    {value:"Parceiro",label:"🤝 Parceiro"},
                    {value:"Evento",label:"🎤 Evento"},
                    {value:"Telefone",label:"📞 Telefone"},
                    {value:"E-mail",label:"✉️ E-mail"},
                  ]}/>
              </FormField>
              <FormField label="Perfil DISC">
                <Select value={editProp.discPerfil||""} onChange={v=>setEditProp({...editProp,discPerfil:v})}
                  options={[
                    {value:"",label:"Não avaliado"},
                    {value:"D",label:"D — Dominante (direto, decidido)"},
                    {value:"I",label:"I — Influente (entusiasta, social)"},
                    {value:"S",label:"S — Estável (paciente, leal)"},
                    {value:"C",label:"C — Cauteloso (analítico, preciso)"},
                  ]}/>
              </FormField>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Resumo Executivo (para IA — máx 280 caracteres)">
                  <Textarea value={editProp.resumoMax||""} onChange={v=>setEditProp({...editProp,resumoMax:v.slice(0,280)})}
                    placeholder="Contexto resumido do caso para uso pela IA na geração de propostas e comunicação..." rows={2}/>
                  <span style={{fontSize:10,color:theme.gray400}}>{(editProp.resumoMax||"").length}/280</span>
                </FormField>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalProp(false)}>Cancelar</Btn>
              <Btn onClick={saveProp}>Salvar Proposta</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* MODAL PDF VISUALIZAÇÃO + EDIÇÃO */}
      {modalPDF && propSel && (
        <ModalPDFVendas
          prop={propSel}
          tipo={pdfTipo}
          htmlProposta={htmlProposta}
          htmlContrato={htmlContrato}
          imprimir={imprimir}
          onClose={()=>setModalPDF(false)}
        />
      )}

      {/* MODAL PROCURAÇÃO / DECLARAÇÃO — edição antes da impressão */}
      {modalDocAux && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:9999,
          display:"flex",alignItems:"flex-start",justifyContent:"center",
          overflowY:"auto",padding:"24px 16px"}}>
          <div style={{background:theme.white,borderRadius:12,padding:"24px 28px",
            maxWidth:680,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,.3)",marginBottom:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0}}>
                {modalDocAux.tipo==="procuracao"?"📜 Instrumento Particular de Procuração":"📋 Declaração de Hipossuficiência"}
              </p>
              <button onClick={()=>setModalDocAux(null)}
                style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:theme.gray400}}>×</button>
            </div>
            <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"center"}}>
              <div style={{flex:1,padding:"8px 12px",borderRadius:6,background:"#fffbec",border:"1px solid #fde68a"}}>
                <p style={{fontSize:11,color:"#92400e",margin:0}}>
                  ✏️ Edite o texto abaixo. Campos entre [colchetes] precisam ser preenchidos manualmente.
                  {modalDocAux.temVersaoSalva && <strong> · Versão editada carregada.</strong>}
                </p>
              </div>
              {modalDocAux.temVersaoSalva && (
                <button onClick={()=>{
                  const propAtual = propostas.find(p=>p.id===modalDocAux.propId);
                  if (!propAtual) return;
                  const textoDefault = gerarTextoDocAux(propAtual, modalDocAux.tipo);
                  setModalDocAux(prev=>({...prev, texto:textoDefault, temVersaoSalva:false}));
                }} style={{padding:"6px 12px",borderRadius:6,border:`1px solid ${theme.red}`,
                  background:"#fef2f2",color:theme.red,cursor:"pointer",fontSize:11,fontWeight:600,whiteSpace:"nowrap"}}>
                  ↩ Restaurar padrão
                </button>
              )}
            </div>
            <textarea
              value={modalDocAux.texto}
              onChange={e=>setModalDocAux(prev=>({...prev,texto:e.target.value}))}
              rows={18}
              style={{width:"100%",padding:"12px 14px",border:`1px solid ${theme.gray200}`,
                borderRadius:8,fontSize:12,fontFamily:"inherit",resize:"vertical",
                outline:"none",boxSizing:"border-box",lineHeight:1.8}}
            />
            <div style={{display:"flex",justifyContent:"space-between",gap:10,marginTop:16}}>
              <button onClick={()=>{
                // Salvar versão editada na proposta
                const propAtual = propostas.find(p=>p.id===modalDocAux.propId);
                if (!propAtual) return;
                const upd = {...propAtual,
                  textosEditados:{...(propAtual.textosEditados||{}), [modalDocAux.tipo]: modalDocAux.texto}
                };
                setVendas("propostas", propostas.map(p=>p.id===upd.id?upd:p));
                if (propSel?.id===upd.id) setPropSel(upd);
                alert("✅ Versão salva com sucesso.");
              }} style={{padding:"9px 18px",borderRadius:7,border:`1px solid ${theme.green}`,
                background:"#f0fdf4",color:theme.green,cursor:"pointer",fontSize:12,fontWeight:600}}>
                💾 Salvar versão editada
              </button>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setModalDocAux(null)} style={{padding:"9px 20px",
                  borderRadius:7,border:`1px solid ${theme.gray200}`,background:theme.white,
                  cursor:"pointer",fontSize:13}}>Cancelar</button>
                <button onClick={()=>{
                  imprimirDocAux(modalDocAux.tipo, modalDocAux.texto, getNomeCli(propSel?.clienteId||propSel?.leadId));
                  setModalDocAux(null);
                }} style={{padding:"9px 22px",borderRadius:7,border:"none",
                  background:theme.gray900,color:theme.gold,cursor:"pointer",
                  fontSize:13,fontWeight:700}}>
                  🖨️ Imprimir / Salvar PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ASSINATURA */}
      <Modal open={modalAssinatura} onClose={()=>setModalAssinatura(false)}
        title="🖊️ Registrar Assinatura do Contrato" width={420}>
        <div style={{padding:"10px 14px",background:"#f5f3ff",border:"1px solid #c4b5fd",borderRadius:7,marginBottom:16,fontSize:12,color:"#5b21b6"}}>
          Confirme que o contrato foi assinado por ambas as partes e informe a data da assinatura.
        </div>
        <FormField label="Data da Assinatura">
          <Input type="date" value={dataAssForm||hoje} onChange={setDataAssForm}/>
        </FormField>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
          <Btn variant="secondary" onClick={()=>setModalAssinatura(false)}>Cancelar</Btn>
          <Btn onClick={()=>{
            const dataAss = dataAssForm || hoje;
            const upd = {
              ...propSel,
              status: "Contrato Assinado",
              dataAssinatura: dataAss,
              ocorrencias: [...(propSel.ocorrencias||[]), {
                data: hoje,
                obs: `Contrato assinado pelas partes em ${new Date(dataAss+"T12:00").toLocaleDateString("pt-BR")}.`,
                usuario: ""
              }]
            };
            setVendas("propostas", propostas.map(p=>p.id===propSel.id?upd:p));
            setPropSel(upd);
            setModalAssinatura(false);
            setDataAssForm("");
          }} style={{background:"#7c3aed",border:"none",color:"#fff"}}>
            ✅ Confirmar Assinatura
          </Btn>
        </div>
      </Modal>

      {/* MODAL CONVERSÃO */}
      <Modal open={modalConv} onClose={()=>setModalConv(false)} title="✅ Aprovar Proposta — Criar Contrato" width={580}>
        <div style={{padding:"10px 14px",background:`${theme.green}11`,border:`1px solid ${theme.green}33`,borderRadius:7,marginBottom:16,fontSize:12,color:theme.green}}>
          Esta ação irá {convDados.clienteExistente?"vincular o cliente existente":"criar um novo cliente"} e gerar automaticamente um contrato no módulo Contratos.
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
          <div style={{gridColumn:"1/-1"}}><FormField label="Nome do Cliente"><Input value={convDados.nomeCliente||""} onChange={v=>setConvDados({...convDados,nomeCliente:v})} placeholder="Nome completo ou Razão Social"/></FormField></div>
          <FormField label="CPF / CNPJ"><Input value={convDados.cpfCnpj||""} onChange={v=>setConvDados({...convDados,cpfCnpj:v})}/></FormField>
          <FormField label="WhatsApp"><Input value={convDados.whatsapp||""} onChange={v=>setConvDados({...convDados,whatsapp:v})}/></FormField>
          <FormField label="E-mail"><Input value={convDados.email||""} onChange={v=>setConvDados({...convDados,email:v})}/></FormField>
          <FormField label="Nº do Contrato"><Input value={convDados.numContrato||""} onChange={v=>setConvDados({...convDados,numContrato:v})}/></FormField>
          <FormField label="Área Jurídica"><Input value={convDados.area||""} onChange={v=>setConvDados({...convDados,area:v})}/></FormField>
          <FormField label="Responsável"><Select value={convDados.responsavel||""} onChange={v=>setConvDados({...convDados,responsavel:v})} options={equipe.filter(e=>e.ativo!==false).map(e=>({value:e.id,label:e.nome}))} placeholder="Selecione..."/></FormField>
          <div style={{gridColumn:"1/-1"}}><FormField label="Observações"><Textarea value={convDados.obs||""} onChange={v=>setConvDados({...convDados,obs:v})} rows={2}/></FormField></div>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
          <Btn variant="secondary" onClick={()=>setModalConv(false)}>Cancelar</Btn>
          <Btn onClick={confirmarConversao} style={{background:theme.green,border:"none",color:"#fff"}}>✅ Confirmar Aprovação</Btn>
        </div>
      </Modal>

      {/* MODAL OCORRÊNCIA */}
      <Modal open={modalOcorr} onClose={()=>setModalOcorr(false)} title="+ Nova Ocorrência" width={460}>
        <FormField label="Descrição da Ocorrência">
          <Textarea value={novaOcorr} onChange={setNovaOcorr} placeholder="Descreva o que aconteceu, contato realizado, decisão tomada..." rows={4}/>
        </FormField>
        <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:12,borderTop:`1px solid ${theme.gray100}`}}>
          <Btn variant="secondary" onClick={()=>setModalOcorr(false)}>Cancelar</Btn>
          <Btn onClick={adicionarOcorrencia}>Salvar Ocorrência</Btn>
        </div>
      </Modal>

      {/* MODAL AGENTE */}
      <Modal open={modalAg} onClose={()=>setModalAg(false)} title={editAg?.id?"Editar Agente":"Novo Agente"} width={460}>
        {editAg&&(
          <>
            <div style={{display:"grid",gridTemplateColumns:"60px 1fr",gap:"0 16px"}}>
              <FormField label="Ícone"><Input value={editAg.icon} onChange={v=>setEditAg({...editAg,icon:v})} placeholder="🤖"/></FormField>
              <FormField label="Nome" required><Input value={editAg.nome} onChange={v=>setEditAg({...editAg,nome:v})}/></FormField>
            </div>
            <FormField label="Link" required><Input value={editAg.link} onChange={v=>setEditAg({...editAg,link:v})} placeholder="https://claude.ai"/></FormField>
            <FormField label="Descrição"><Textarea value={editAg.descricao} onChange={v=>setEditAg({...editAg,descricao:v})} rows={2}/></FormField>
            <FormField label="Status"><Select value={editAg.ativo?"Ativo":"Inativo"} onChange={v=>setEditAg({...editAg,ativo:v==="Ativo"})} options={["Ativo","Inativo"]}/></FormField>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:12,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalAg(false)}>Cancelar</Btn>
              <Btn onClick={saveAg}>Salvar</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* ÁREA DE IMPRESSÃO — oculta na tela, visível no print */}
      <style>{PRINT_STYLE}</style>
      <div id="print-area"
        dangerouslySetInnerHTML={{__html: propSel ? (pdfTipo==="proposta" ? htmlProposta(propSel) : htmlContrato(propSel)) : ""}}
      />
    </div>
  );
}


// ============================================================
// MÓDULO MANUAL DO SISTEMA — v2 (Resumido + Completo)
// ============================================================

// CSS de impressão A4 — injetado uma vez globalmente
const MANUAL_PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  #manual-print-area, #manual-print-area * { visibility: visible !important; }
  #manual-print-area {
    position: fixed !important;
    top: 0 !important; left: 0 !important;
    width: 100% !important;
    padding: 15mm 20mm !important;
    background: white !important;
    font-family: Georgia, serif !important;
    font-size: 11pt !important;
    line-height: 1.6 !important;
    color: #111 !important;
  }
  @page { size: A4; margin: 15mm 20mm; }
}
#manual-print-area { display: none; }
`;

// Conteúdo RESUMIDO — para consulta rápida na tela
const MANUAL_RESUMIDO = {
  visaoGeral: `O Sistema Operacional Alves & Correa Advogados centraliza todas as operacoes do escritorio em uma aplicacao web. Acesse pelo navegador em https://app.alvesecorreaadvogados.com.br

**Fluxo principal:** Lead (CRM) → Proposta (Vendas) → Aprovacao → Contrato + Execucao (Base Operacional) → Conclusao automatica

**Tecnologia:** React 18 + PHP 8.0 + MySQL 8.0 · Hospedagem: Hostinger`,

  modulos: [
    { id:"agenda",     titulo:"📅 Agenda",                     resumo:"Calendario de audiencias, reunioes, compromissos e prazos. Integracao automatica com prazos dos contratos. Envio por WhatsApp." },
    { id:"arquivo",    titulo:"🗂️ Arquivo de Documentos",      resumo:"Repositorio de modelos, procuracoes, peticoes e templates. Acesso via link para OneDrive/Drive." },
    { id:"areas",      titulo:"⚖ Areas Juridicas",             resumo:"Cadastro das areas de atuacao. Base de classificacao para processos, produtos e contratos." },
    { id:"cowork",     titulo:"🧠 Assistente Cowork",           resumo:"Assistente de IA treinado no sistema. Responde duvidas de uso, analise juridica basica e orienta novos colaboradores. Disponivel tambem como botao flutuante em todos os modulos." },
    { id:"base",       titulo:"📁 Base Operacional",            resumo:"Central de contratos com 4 abas. Mesa de Trabalho por fase (Objetivos, KPIs, Agentes IA, Contratos). Botão 🏢 Mesa nos cards de fase. Pasta do Cliente com link direto para nuvem em todos os formulários. Checklist de documentos e tarefas, ocorrências, SLA por fase." },
    { id:"clientes",   titulo:"👤 Clientes",                    resumo:"Cadastro PF e PJ. Campos: Pasta do Cliente 📁 (link nuvem — carregado em todos os módulos) e Dados de Acesso 🔒 (uso interno: e-CAC, PGFN, certificados). Histórico de ocorrências e produtos contratados." },
    { id:"crm",        titulo:"🎯 CRM Comercial",               resumo:"Funil de vendas com 7 estagios. Leads ordenados alfabeticamente. Ocorrencias com responsavel, confidencialidade e envio automatico para Agenda. Conversao em cliente com migracao de dados." },
    { id:"dashboard",  titulo:"📊 Dashboard",                   resumo:"KPIs executivos em tempo real: receita, inadimplencia, contratos ativos, prazos criticos e processos parados. Filtros por periodo." },
    { id:"dashtrib",   titulo:"📊 Dashboard Tributario",        resumo:"3 visoes: Diagnostico por Perfil (filtros PF/PJ + Regime + Segmento + Tributo), Matriz Produto x Segmento e Mapa de Cobertura. Botao Creditos Recuperados com 4 KPIs e historico de compensacoes." },
    { id:"equipe",     titulo:"👥 Equipe",                      resumo:"Membros com cargo, OAB, vinculo e especialidade. Referencia de responsavel em todos os modulos." },
    { id:"financeiro", titulo:"💰 Financeiro",                  resumo:"6 abas: Dashboard (KPIs + graficos + atalho para Creditos Tributarios), Contas a Receber, Contas a Pagar, Receitas, Saidas e Bancos. Sincronismo automatico com contratos." },
    { id:"governanca", titulo:"🏛️ Governanca",                  resumo:"3 abas: SLA e Prazos, Auditoria de POPs e Inconsistencias detectadas automaticamente." },
    { id:"painelia",   titulo:"🤖 IA — Assistentes",            resumo:"Painel de agentes de IA organizados por tema juridico. Acesso direto ao ChatGPT, Claude, Gemini por area de atuacao." },
    { id:"marketing",  titulo:"📣 Marketing",                   resumo:"6 abas: Dashboard de redes sociais, Agentes de IA, Calendario Editorial, Ideias, Campanhas e Metricas." },
    { id:"parcerias",  titulo:"🤝 Parcerias",                   resumo:"Cadastro de parceiros com comissao, area de atuacao, cidade/UF e status. Vinculacao a clientes e diagnosticos RCT." },
    { id:"pops",       titulo:"📋 POPs",                        resumo:"Procedimentos Operacionais Padrao com versao, data de revisao e vinculacao a produtos. Alerta de revisoes vencidas." },
    { id:"processos",  titulo:"📋 Processos",                   resumo:"Gestao de processos judiciais e administrativos. Tribunal, numero, area, responsavel, ocorrencias e status." },
    { id:"produtos",   titulo:"📦 Produtos",                    resumo:"Catalogo com 47 produtos juridicos. Documentos e Tarefas Padrao configurados via checkboxes do catalogo centralizado (Dashboard Tributario → Tabelas). Carregados automaticamente na execucao do contrato." },
    { id:"rct",        titulo:"🔍 RCT Tributario",              resumo:"Diagnostico de creditos tributarios por cliente. 7 produtos, tabela CMO de 60 competencias, E-CAC e relatorio PDF." },
    { id:"relatorios", titulo:"📄 Relatorios",                  resumo:"10 relatorios com exportacao PDF e CSV: Clientes, Lides, Contratos, Processos Parados, POPs, Produtos, Financeiro, RCT, Equipe e Parcerias." },
    { id:"vendas",     titulo:"📊 Vendas",                      resumo:"Propostas comerciais com aprovacao automatica (cria cliente + contrato). Geracao de PDF da proposta." },
    { id:"acesso",     titulo:"🔐 Controle de Acesso",          resumo:"Gerenciamento de usuarios, perfis e permissoes por modulo. 7 perfis pre-configurados." },
    { id:"sala",       titulo:"💻 Sala Virtual",                resumo:"4 salas de trabalho com presenca em tempo real (polling 30s), chat interno, sistema de knock, Monitoramento (admin) e Produtividade (admin). Controle de acesso exigeSala disponivel." },
  ],

  faq: [
    { p:"Como gerar uma proposta em PDF?",        r:`Vendas → selecione a proposta → Proposta PDF → Imprimir / Salvar PDF → Salvar como PDF no browser.` },
    { p:"Como aprovar uma proposta?",             r:`Vendas → clique Aprovar → confirme no modal. O sistema cria automaticamente o cliente, contrato e registro de execucao.` },
    { p:"Como marcar um documento como recebido?",r:`Base Operacional → Execucao Operacional → selecione contrato → secao Documentos → marque o checkbox. Data registrada automaticamente.` },
    { p:"Como gerar a Procuracao?",               r:`Modulo Vendas → aba Propostas → selecione a proposta do cliente → botao 📜 Procuracao (disponivel apos aprovacao). Os dados de qualificacao (nacionalidade, estado civil, profissao, RG) vem automaticamente do Cadastro de Cliente. Gerar PDF/Imprimir. (Nao e mais emitida na Base Operacional desde 10/07/2026 — la so ocorre a coleta/checklist do documento.)` },
    { p:"O que e o Score de Risco?",              r:`Pontuacao automatica de 0 a 10 por contrato: tese nova (+2), jurisprudencia instavel (+2), valor elevado (+2), depende pericia (+1), documentacao incompleta (+1), inativo mais de 30 dias (+2). Score maior ou igual a 6 = risco alto.` },
    { p:"Como registrar um recebimento?",         r:`Financeiro → Contas a Receber → clique Receber na linha. Informe valor recebido, data e banco. O contrato e atualizado automaticamente (valorRecebido e valorAReceber).` },
    { p:"O que e o Mapa de Cobertura?",           r:`Dashboard Tributario → sub-aba Mapa de Cobertura. Para cada segmento de mercado, mostra quantos produtos estao disponiveis: Sem cobertura (0), Cobertura baixa (1-2), Cobertura media (3-5), Cobertura alta (6+).` },
    { p:"Como usar o Dashboard Tributario?",      r:`Modulo Produtos → botao Dashboard Tributario. Ou acesse diretamente pelo menu. Use os filtros PF/PJ + Regime + Segmento + Tributo para identificar produtos para o perfil do cliente.` },
    { p:"O que e a Ficha de Controle de Credito?",r:`Base Operacional → Execucao Operacional → selecione contrato tributario com Valor Credito Apurado preenchido → botao Ficha de Credito. Registra cada compensacao (PERDCOMP), calcula honorarios automaticamente e lanca no Financeiro.` },
    { p:"Como registrar uma compensacao tributaria?",r:`Ficha de Credito → formulario Nova Compensacao → preencher: data, numero PERDCOMP, valor do credito compensado, correcao SELIC (manual), vencimento dos honorarios → clicar Registrar. O sistema calcula os honorarios (% do contrato x total compensado), lanca no Financeiro e reduz o lancamento inicial de honorarios.` },
    { p:"Como um contrato fica Concluido automaticamente?", r:`Quando 100% das tarefas obrigatorias do checklist sao marcadas como concluidas na aba Execucao Operacional, o status muda para Concluido e a dataConclusao e preenchida automaticamente.` },
    { p:"Como enviar WhatsApp pelo sistema?",     r:`Agenda → selecione evento → botao WhatsApp. Ou Clientes → ficha do cliente → icone WhatsApp. Abre o WhatsApp Web com mensagem pre-formatada.` },
    { p:"Como exportar dados para Excel/CSV?",    r:`Modulo Relatorios → selecione o tipo → botao CSV. Disponivel para: Clientes, Lides, Contratos, Processos Parados, POPs, Produtos, Equipe, Parcerias.` },
    { p:"Ao criar um contrato, o lancamento no Financeiro e automatico?",r:`Sim. Ao salvar um novo contrato na Base Operacional, o sistema cria automaticamente um lancamento de honorarios no Financeiro (status A Receber) com o valor contratado e a data de vencimento informada no contrato.` },
    { p:"Esqueci minha senha. O que fazer?",      r:`Contate o administrador do sistema. O administrador acessa Controle de Acesso → edita o usuario → define nova senha.` },
  ],
};

const MANUAL_COMPLETO_PARTES = [
  {
    titulo: "Parte 1 — Introducao ao Sistema",
    secoes: [
      { titulo: "1.1 O que e o Sistema Operacional Juridico", conteudo: `O Sistema Operacional Juridico da Alves & Correa Advogados e uma aplicacao web de pagina unica (SPA) desenvolvida em React, que centraliza todas as operacoes do escritorio.

**URL de acesso:** https://app.alvesecorreaadvogados.com.br
**Tecnologia:** React 18 + PHP 8.0 + MySQL 8.0 · Hospedagem: Hostinger
**Persistencia:** Dados salvos automaticamente no banco a cada 1,5 segundos apos alteracao.` },
      { titulo: "1.2 Estrutura de Modulos", conteudo: `O sistema possui 23 modulos organizados no menu lateral em ordem alfabetica:

**Operacao juridica:** Agenda · Areas Juridicas · Base Operacional · Processos · Clientes
**Comercial:** CRM Comercial · Vendas · Parcerias · Marketing
**Tributario:** Produtos · Dashboard Tributario · RCT Tributario
**Financeiro:** Financeiro
**Gestao:** Dashboard · Equipe · POPs · Relatorios · Governanca
**IA e Suporte:** IA Assistentes · Assistente Cowork · Arquivo de Documentos
**Administracao:** Manual do Sistema · Controle de Acesso` },
      { titulo: "1.3 Perfis de Usuario", conteudo: `7 perfis pre-configurados:

**Administrador** — Acesso total. Bypass automatico de permissoes.
**Socio** — Todos os modulos operacionais exceto Controle de Acesso.
**Advogado** — Clientes, Base, CRM, RCT, Processos, IA, Cowork.
**Operador RCT** — RCT Tributario e Clientes.
**Assistente** — Clientes, Base Operacional, Processos.
**Financeiro** — Clientes, Financeiro, Dashboard, Relatorios.
**Visualizador** — Apenas Dashboard.` },
      { titulo: "1.4 Padrao de Operacoes (CRUD)", conteudo: `Todos os modulos seguem o mesmo padrao:

**Criar:** Botao + Novo [item] → preencher modal → Salvar.
**Editar:** Botao de edicao ou clique na linha → editar → Salvar.
**Remover:** Botao X → confirmar. Para lancamentos recebidos, o contrato e atualizado automaticamente.
**Filtrar:** Campo de busca ou dropdowns na barra superior.

Dados salvos automaticamente no servidor 1,5s apos cada alteracao.` },
    ]
  },
  {
    titulo: "Parte 2 — Modulos de Cadastro",
    secoes: [
      { titulo: "2.1 Areas Juridicas", conteudo: `Tabela de referencia para classificacao de processos, contratos e produtos.

**Areas padrao:** Tributario · Previdenciario · Civel · Trabalhista · Empresarial · Bancario · Imobiliario · Familia e Sucessoes · Saude.` },
      { titulo: "2.2 Processos", conteudo: `Cadastro de processos judiciais e administrativos vinculados aos clientes.

**Campos:** Cliente · Numero do Processo · Tipo (Judicial/Administrativo/Extrajudicial/Arbitragem) · Area Juridica · TJ/UF · Responsavel · Data de Distribuicao · Status

**Vinculacao com Contratos:** cada processo pode ser vinculado a um ou mais contratos na Base Operacional (formulario do contrato → secao Processos Vinculados).

**Ocorrencias:** as ocorrencias dos processos sao registradas exclusivamente na Base Operacional (aba Execucao Operacional). O modulo Processos exibe as ocorrencias espelhadas — somente leitura.

**Botao "Ocorrencia" na linha:** ao clicar, navega automaticamente para a Base Operacional → Execucao Operacional, abrindo o contrato vinculado para registro da ocorrencia.

**Relatório de Processos:** botao "Relatorios" no topo → agrupa processos por cliente com ultima ocorrencia da Base Operacional. Gera PDF.

**Ultima Ocorrencia na tabela:** buscada automaticamente da Base Operacional via contratos vinculados.` },
      { titulo: "2.3 Clientes", conteudo: `Cadastro PF e PJ com historico de ocorrencias.

**PF:** Nome, CPF, e-mail, WhatsApp, cidade/UF, data de entrada.
**PJ adicionais:** CNPJ, razao social, contatos, socios.

**Historico de ocorrencias:** Registro de reunioes, ligacoes, e-mails com data e descricao. Base do alerta de dias sem contato no Dashboard.` },
      { titulo: "2.4 Produtos", conteudo: `Catalogo de servicos juridicos do escritorio.

**Campos:** Nome, Area, Valor, Custo, Margem (auto), Prazo Medio, Risco, Resumo tecnico, Base Legal, POP vinculado, Landing Pages.

**Classificacao Tributaria** (apenas para area Tributario):
• Destinatario: PF e/ou PJ
• Regimes: Lucro Real / Lucro Presumido / Simples Nacional
• Segmentos de atuacao (ex: Saude, Construcao, Tecnologia)
• Tributos recuperaveis (ex: PIS/COFINS, INSS)

**Documentos Necessarios (Checklist Padrao):**
Selecao via checkboxes do catalogo centralizado (Dashboard Tributario → Tabelas → Documentos).
Documentos marcados sao carregados automaticamente na execucao de cada contrato.

**Tarefas Padrao (Sequencia Operacional):**
Selecao via checkboxes do Catalogo de Tarefas (Dashboard Tributario → Tabelas → Tarefas).
Tarefas marcadas sao carregadas automaticamente na execucao do contrato com prazos calculados.

**Observacao:** O catalogo de documentos e tarefas e gerenciado centralmente — nao e necessario digitar os itens em cada produto.

**Tipo especial — Consultoria (Pacote de Horas):** produto com campo "tipo: pacote_horas" ativa o modulo Timesheet automaticamente nos contratos vinculados.

**Template de Execucao RCT (campoRCT):**
Campo exclusivo para produtos da area Tributario. Define qual formulario de execucao sera usado no modulo RCT:
• perdcomp — Padrao (maioria dos produtos)
• cmo — INSS Cessao de Mao de Obra (ativa controle de 60 competencias mensais)
• verbas — Verbas Indenizatorias
• ircsll_cc — IR/CSLL Construcao Civil
• ircsll_sd — IR/CSLL Equiparacao Hospitalar
• divida — Situacao Fiscal e Divida Tributaria

**Ferramentas de Execucao:**
Links para ferramentas externas usadas na execucao do produto (e-Auditoria, planilhas, sistemas).
Campos: Nome · Tipo (Sistema / Excel / Google Sheets / PDF / Outro) · Link/URL
As ferramentas aparecem como botoes cliclaves na aba "Produtos Viaveis" do diagnostico RCT.` },
      { titulo: "2.5 Equipe", conteudo: `Cadastro dos membros com vinculo, funcao, OAB, especialidade e contatos. Referenciado em contratos, processos, RCTs e tarefas.` },
      { titulo: "2.6 POPs", conteudo: `Procedimentos Operacionais Padrao com versao e data de revisao. POPs com revisao vencida aparecem em vermelho na Governanca. Vinculados a produtos para consulta na execucao.` },
      { titulo: "2.7 Parcerias", conteudo: `Cadastro de parceiros comerciais e controle de repasses de comissao.

**Campos do parceiro:** Nome · Endereco · Cidade/UF · Email · WhatsApp · Area de Atuacao · Comissao (%) · Data de inicio · Status

**Vinculacao ao Cliente:** no cadastro do cliente existe o campo "Indicado por (Parceiro)". Ao converter Lead em Cliente, o parceiro e migrado automaticamente.

**Vinculacao ao Contrato:** no formulario do contrato, campo "Parceria neste Contrato" (Sim/Nao). Se Sim: parceiro preenchido automaticamente do cliente, % comissao herdado do cadastro do parceiro (ajustavel por contrato), valor de repasse calculado automaticamente.

**Lancamento automatico:** ao criar contrato com parceria, gera despesa "Comissao de Parceria" no Financeiro (Contas a Pagar). Vencimento: dia 5 do mes seguinte ao recebimento dos honorarios.

**Aba Repasses:** filtre por parceiro e periodo. Botao "Pagar" por repasse individual. Botao "Extrato PDF".

**Exclusao de contrato:** exclui automaticamente a despesa de comissao vinculada.` },
    ]
  },
  {
    titulo: "Parte 3 — Modulos de Operacao",
    secoes: [
      { titulo: "3.1 Base Operacional — Contratos", conteudo: `Central de gestao de contratos em execucao.

**4 abas:** Dashboard · Contratos · Execucao Operacional · Relatorio de Pendencias

**Campos do contrato:**
• Campos basicos: Cliente, Produto, Area, Fase, Tipo de Processo, Status, Responsavel
• Datas: Data de Entrada (auto), Vencimento dos Honorarios, Data de Conclusao Real
• Valores: Valor Contratado (mascara R$), Custo Operacional (mascara R$)
• Campos tributarios (area Tributario): Valor do Credito Apurado, % Honorarios sobre o Credito
  — Valor Contratado calculado automaticamente: Credito x %
  — Validacao obrigatoria: Credito Apurado, % Honorarios e Vencimento devem ser preenchidos

**Acoes por contrato:** VER · OCORRENCIAS (navega para Execucao Operacional) · EDITAR · EXCLUIR

**Botao Ficha de Credito (💳):** aparece para contratos tributarios com Credito Apurado > 0.
Ao salvar novo contrato tributario com credito, abre a Ficha de Credito automaticamente.

**Sincronismo Financeiro:** ao criar contrato → lancamento automatico no Financeiro.
Ao dar baixa, editar ou excluir lancamento → valores do contrato atualizados automaticamente.` },
      { titulo: "3.2 Base Operacional — Execucao Operacional", conteudo: `Painel lateral de execucao do contrato selecionado.

**Exibe:** progresso das tarefas (barra), checklist de tarefas, checklist de documentos, historico de ocorrencias.

**Checklist de Documentos:**
• Lista dos documentos definidos no produto (via catalogo)
• Checkbox por documento — ao marcar, exibe campo de data de recebimento (editavel) e campo de observacao
• Badge de progresso: "X/Y docs" ou "Completo"
• Botao "+ Doc" para adicionar documento avulso

**Checklist de Tarefas:**
• Lista das tarefas definidas no produto (via catalogo) com prazo e responsavel
• Checkbox por tarefa — ao marcar, exibe campo de data de conclusao editavel
• Indicadores: vencida (vermelho) · urgente (ambar) · concluida (verde)
• Botao "+ Tarefa" para adicionar tarefa avulsa

**Ocorrencias:**
• Campos: Data (padrao: hoje) · Responsavel (select equipe) · Confidencial (padrao: sim) · Descricao
• 🔒 Confidencial: visivel apenas internamente — nao vai para o relatorio do cliente
• 📋 Nao confidencial: pode ir para o relatorio do cliente
• Se data >= hoje: lancamento automatico na Agenda

**Painel de Auditoria (aba Relatorio de Pendencias):**
• Seletor de mes/ano
• Lista todas as ocorrencias do periodo com status de confidencialidade
• Botao toggle por ocorrencia: 🔒 Confidencial ↔ 📋 Visivel
• Botao "Gerar Relatorio do Cliente": imprime apenas ocorrencias nao confidenciais

**Botoes de acao:** 💳 Ficha de Credito (se tributario) · + Ocorrencia
**Atencao:** Procuracao e Declaracao de Hipossuficiencia NAO sao mais emitidas aqui (desativado em 10/07/2026) — a Base Operacional so coleta esses documentos (checklist). Emissao oficial ficou centralizada no modulo Vendas (ver secao 3.4).

**Card Timesheet:** aparece automaticamente para contratos "Consultoria (Pacote de Horas)" — ver secao 3.10.` },
      { titulo: "3.3 CRM Comercial", conteudo: `Funil de vendas com 7 estagios: Lead → Qualificacao → Reuniao → Proposta → Negociacao → Fechado → Perdido.

**3 visoes:** Funil Visual (Kanban) · Lista com filtros · Indicadores de conversao.

**Lista de leads:** ordenada alfabeticamente.

**Produtos de Interesse:** selecao por checkboxes, lista em ordem alfabetica.

**Ocorrencias no CRM:**
• Campos: Tipo · Data · Responsavel (select equipe) · 🔒 Confidencial (padrao: sim) · Descricao
• Se data >= hoje: lancamento automatico na Agenda com vinculo ao lead (badge 🎯 Lead CRM)
• Filtro: "Mostrar confidenciais" — oculta ou exibe ocorrencias confidenciais na lista

**Conversao em cliente:** Converter em Cliente → dados migrados → lead fechado → cliente criado.

**Pipeline ponderado:** Valor estimado x probabilidade (%).` },
      { titulo: "3.4 Vendas", conteudo: `Propostas comerciais com geracao de PDF e aprovacao automatica.

**Fluxo:** + Nova Proposta → PDF → Aprovar → sistema cria Cliente + Contrato + Execucao automaticamente.

**Documentos gerados aqui (ponto oficial unico desde 10/07/2026):** Proposta (PDF) · Contrato de Prestacao de Servicos (PDF) · 📜 Procuracao (PDF) · 📋 Declaracao de Hipossuficiencia (PDF) — disponiveis na proposta apos aprovacao. Dados de qualificacao do cliente (nacionalidade, estado civil, profissao, RG/UF) vem automaticamente do Cadastro de Cliente — preencha la antecipadamente para nao aparecer com colchetes no documento. Assinatura da Procuracao/Declaracao e SOMENTE do cliente (outorgante/declarante) — o escritorio nao assina esses dois documentos (diferente do Contrato, que tem assinatura dupla CONTRATANTE x CONTRATADA).

**Agentes de IA:** Gerador de Proposta · Gerador de Contrato · Analise de Proposta · Sugestao de Preco.` },
      { titulo: "3.5 Agenda", conteudo: `Calendario de compromissos, audiencias, reunioes e prazos.

**Integracao:** Prazos dos contratos aparecem automaticamente.
**WhatsApp:** Selecione evento → WhatsApp → mensagem pre-formatada.` },
      { titulo: "3.6 Financeiro", conteudo: `Gestao financeira completa com 6 abas e sincronismo automatico com contratos.

**Dashboard Financeiro:**
6 KPIs: Saldo Liquido (recebido - pago, acumulado) · A Receber (total geral pendente) · A Pagar · Recebido/Mes · Inadimplencia (>5 dias) · MC Total.
Graficos: Receita vs Despesa 6 meses · MC por Area · MC por Produto · Top Clientes · Proximos Vencimentos.

**Regra de Receitas:** toda entrada de lancamento e receita, independente do tipo (Honorario, Receita ou outros). Nao ha filtro por tipo de lancamento.

**Contas a Receber:** lista todos os lancamentos pendentes com "Total a Receber" acumulado (sem filtro de periodo).

**Sincronismo com Contratos:**
• Criar contrato → lancamento automatico (Honorarios)
• Dar baixa → atualiza valorRecebido/valorAReceber do contrato
• Editar lancamento → recalcula contrato vinculado
• Excluir lancamento → reverte valores do contrato

**Saldo Liquido:** soma de tudo recebido menos tudo pago (historico completo). Para ver por periodo, usar os filtros Mes/Trimestre/Semestre/Ano.` },
      { titulo: "3.7 RCT Tributario", conteudo: `Diagnostico de creditos tributarios por cliente.

**7 produtos:** INSS Cessao Mao de Obra · Verbas Indenizatorias · PIS/COFINS Monofasicos · ICMS-ST · IR/CSLL Construcao Civil · IR/CSLL Equiparacao Hospitalar · Situacao Fiscal e Gestao de Divida.

**Tabela CMO:** 60 meses com colunas automaticas (Auditoria e Saldo).

**Relatorio PDF:** aprovacao → geracao com logo e consolidacao de creditos.` },
      { titulo: "3.8 IA — Assistentes Juridicos", conteudo: `Painel de agentes organizados por tema. Links para ChatGPT, Claude, Gemini e outros.` },
      { titulo: "3.9 Assistente Cowork", conteudo: `Assistente de IA treinado no sistema, acessivel de duas formas:

**Modulo dedicado (menu):** chat completo com 8 topicos rapidos, renderizacao markdown, historico da sessao, respostas de ate 1.200 tokens.

**Botao flutuante (canto inferior direito):** disponivel em todos os modulos. Sabe em qual modulo o usuario esta. Chat rapido de ate 600 tokens.

**O que responde:**
• Uso do sistema: como usar qualquer modulo, fluxos, duvidas operacionais
• Analise juridica basica: tributario, previdenciario, civel e outras areas
• Treinamento: orientar novos colaboradores

**Pre-requisito:** arquivo claude.php com chave da API Anthropic em public_html/api/.` },
      { titulo: "3.10 Timesheet — Consultoria (Pacote de Horas)", conteudo: `Controle de horas para contratos de consultoria por pacote.

**Ativacao:** o card de Timesheet aparece automaticamente na Execucao Operacional quando o contrato usa o produto "Consultoria (Pacote de Horas)".

**Configuracao do contrato:**
• Produto: Consultoria (Pacote de Horas)
• Campo "Horas do Pacote": total de horas contratadas (ex: 10h)
• Valor Contratado: valor total do pacote (ex: R$ 5.000)
• Valor/hora calculado automaticamente: Valor Contratado / Horas do Pacote

**Lancamento automatico no Financeiro:** ao criar o contrato, gera lancamento de honorarios no Financeiro (mesmo comportamento dos demais contratos).

**Card Timesheet na Execucao Operacional:**
• KPIs em tempo real: Pacote · Utilizadas · Saldo
• Saldo colorido: verde (positivo) · ambar (zerado) · vermelho (excedente)
• Historico de lancamentos com membro, data e horas
• Botao "+ Lanctar Horas" (bloqueado quando pacote esgotado)
• Badge "Pacote Esgotado" apos zerar

**Modal de lancamento de horas:**
Campos obrigatorios: Data · Horas · Membro da Equipe · Descricao da Atividade
Qualquer membro da equipe pode lancar. Nao requer aprovacao.

**Logica de excedente:**
• Pacote com 2h de saldo + lancamento de 3h = 1h excedente
• Sistema: usa as 2h restantes + detecta 1h excedente
• Aviso automatico ao usuario
• Lancamento de excedente gerado automaticamente no Financeiro (valor = horas excedentes x valor/hora do pacote)
• Contrato bloqueado para novos lancamentos (status: Pacote Esgotado)
• Para continuar: abrir novo contrato (novo pacote ou hora avulsa)

**Relatorio Timesheet (modulo Relatorios → aba Timesheet):**
• Lista todos os contratos "Consultoria (Pacote)" ativos
• Por contrato: KPIs (Pacote / Utilizadas / Saldo / Valor Total)
• Tabela com historico: Data · Membro · Atividade · Horas · Saldo Acumulado
• Saldo acumulado linha a linha — rastreabilidade completa` },
      { titulo: "3.11 Sala Virtual de Trabalho", conteudo: `Ambiente de presenca digital da equipe com salas por area.

**Salas disponiveis:** Sala Comercial · Sala Operacional Juridica · Sala Operacional RCT · Sala de Reunioes

**Acesso:** menu lateral → Sala Virtual. Ao abrir o modulo, o usuario e registrado como "Online" automaticamente.

**Status de presenca:**
• Online (verde) — ativo nos ultimos 5 minutos
• Ocupado (amarelo) — definido manualmente
• Em reuniao (vermelho) — definido manualmente
• Ausente (cinza) — sem atividade ha mais de 10 minutos

**Tarefa ativa:** o sistema exibe automaticamente o contrato em andamento do responsavel, com link direto para a Base Operacional.

**Chat por sala:** mensagens abertas para todos os membros da sala. Historico salvo no banco.

**Chat direto:** clique em "Mensagem" no card do colega para conversa privada.

**Notificacao (Knock):** botao "Chamar" envia alerta visual para o colega.

**Sair da Sala:** botao visivel para usuarios com acesso via sala obrigatorio. Inatividade de 30 minutos encerra a sessao automaticamente.

**Controle de acesso por sala:** configurado no modulo Controle de Acesso → editar usuario → secao Sala Virtual. Marcar "Acesso obrigatorio via Sala Virtual" e selecionar as salas permitidas.

**Usuarios sem restricao:** acessam os modulos diretamente, sem passar pela sala.

**Usuarios com restricao (exigeSala=true):** devem entrar na Sala Virtual antes de acessar qualquer modulo. Todos podem visualizar todas as salas e ver quem esta presente.` },

      { titulo: "3.12 Relatorio Mensal de Ocorrencias", conteudo: `Aba exclusiva na Base Operacional para geracao dos relatorios mensais enviados aos clientes.

**Localizacao:** Base Operacional → aba "Rel. Mensal"

**Sub-abas:**

Auditoria (interna):
• Filtros: Mes/Ano · Cliente · Produto · Contrato
• Tabela com todos os contratos ativos
• Classificacao automatica: Rel.1 (com ocorrencias) ou Rel.2 (sem ocorrencias)
• KPIs: total de ocorrencias / confidenciais / visiveis / distribuicao

Relatorio 1 — Com Ocorrencias:
• Clientes que tiveram ocorrencias nao confidenciais no mes
• Regra: se um contrato do cliente teve ocorrencia, todos os contratos do cliente entram no Rel.1
• Exibe: contrato · processos vinculados · tabela de ocorrencias (Data · Tipo · Descricao)
• Botao "Gerar PDF" — consolida todos os clientes em um unico PDF

Relatorio 2 — Sem Ocorrencias:
• Clientes sem ocorrencias nao confidenciais no mes
• Botao "Gerar PDF"

**Textos padrao nos PDFs:**
Introducao + Andamento Processual + Consideracoes Estrategicas + Conclusao
Rodape fixo: Nelson Jose Alves (OAB 398.875/SP) · Annie Lemos Alves Correa (OAB 446.942/SP) · Alves & Correa Advogados

**Fluxo recomendado:**
1. Lancamentos de ocorrencias ao longo do mes (Base Operacional → Execucao)
2. No fechamento: revisar na aba Auditoria (marcar confidenciais)
3. Gerar Rel.1 para clientes com movimentacao
4. Gerar Rel.2 para clientes sem movimentacao` },


    ]
  },
  {
    titulo: "Parte 4 — Gestao e Administracao",
    secoes: [
      { titulo: "4.1 Relatorios", conteudo: `10 relatorios com exportacao PDF (imprimir) e CSV (Excel).

1. Clientes · 2. Lides/Leads · 3. Contratos em Andamento · 4. Processos Parados · 5. POPs · 6. Produtos · 7. Financeiro (DRE simplificado) · 8. RCT Tributario · 9. Equipe · 10. Parcerias` },
      { titulo: "4.2 Governanca", conteudo: `3 abas de monitoramento de qualidade:

**SLA e Prazos:** score, prazos criticos (≤7 dias), vencidos, processos parados, carga por responsavel.
**Auditoria de POPs:** revisoes vencidas (vermelho) e vencendo em 30 dias (amarelo).
**Inconsistencias:** contratos sem responsavel · clientes sem contratos · diagnosticos sem supervisor · leads >60 dias sem contato · POPs vencidos.` },
      { titulo: "4.3 Controle de Acesso", conteudo: `Gerenciamento de usuarios e permissoes do sistema.

**Campos do usuario:** Nome · Login · Senha · Perfil · Vincular a membro da Equipe · Modulos permitidos · Produtos liberados

**Perfis disponiveis:** Administrador · Socio · Advogado · Assistente · Financeiro · Estagiario

**Modulos permitidos:** checkboxes por grupo (Operacao, Juridico, Cadastros, Gestao, Administracao). O Administrador tem acesso irrestrito a tudo.

**Sala Virtual (secao especifica):**
• Checkbox "Acesso obrigatorio via Sala Virtual": ao marcar, o usuario deve entrar na Sala Virtual antes de acessar qualquer modulo
• Salas permitidas: checkboxes das salas que o usuario pode acessar (Sala Comercial, Sala Operacional Juridica, Sala Operacional RCT, Sala de Reunioes)
• Se nao marcado: usuario acessa os modulos diretamente

**Inatividade:** usuarios com acesso via sala obrigatorio sao desconectados da sala apos 30 minutos de inatividade, bloqueando o acesso aos modulos ate nova entrada na sala.

**Observacao:** o Administrador nunca e obrigado a passar pela sala, independente da configuracao.` },
      { titulo: "4.4 Dashboard Executivo", conteudo: `5 KPIs financeiros + 5 KPIs operacionais. Graficos: receita 6 meses · processos por status · contratos por area · funil de leads · margem por produto. Alertas com links diretos. Clientes sem contato (filtro 30/60/90/120 dias).` },
    ]
  },
  {
    titulo: "Parte 5 — Dashboard Tributario",
    secoes: [
      { titulo: "5.1 Visao Geral — Dashboard Tributario", conteudo: `Modulo independente de analise estrategica do portfolio tributario.

**Como acessar:** Menu lateral → Dashboard Tributario · Modulo Produtos → botao Dashboard Tributario · Dashboard Financeiro → card Creditos Tributarios Recuperados.

**Estrutura:** 5 KPIs + 3 sub-abas + botao Creditos Recuperados.

**Botao de retorno dinamico:** ao acessar via Financeiro, o botao mostra "← Financeiro"; ao acessar via Produtos, mostra "← Produtos".

**Tabelas editaveis (botoes no topo):** Segmentos · Tributos · Documentos — editadas sem necessidade de deploy.` },
      { titulo: "5.2 Sub-aba Diagnostico por Perfil", conteudo: `Ferramenta para identificar produtos do escritorio que atendem o perfil de um cliente.

**Filtros:** Tipo de Pessoa (PF/PJ) · Regime Tributario · Segmento de Atuacao · Tributo Envolvido.

**Resultado:** cards de produtos compativeis com nome, risco, resumo, tags de regimes/segmentos/tributos e base legal.

**Exemplo:** Cliente PJ, Lucro Presumido, Saude → sistema mostra IR/CSLL Equiparacao Hospitalar, Verbas Indenizatorias, Saldo PER/DCOMP e outros.` },
      { titulo: "5.3 Sub-aba Matriz Produto x Segmento", conteudo: `Tabela cruzada Segmento x Tributo com contagem de produtos.

**Cores:** Verde = 4+ produtos · Amarelo = 2-3 · Azul = 1 · Branco = nenhum (lacuna).

**Hover:** mostra os nomes dos produtos da celula.

**Legenda** exibida abaixo da tabela.` },
      { titulo: "5.4 Sub-aba Mapa de Cobertura", conteudo: `Para cada segmento: quantos produtos estao disponiveis, quantos sao PF/PJ, quantos tributos sao cobertos.

**Niveis:** Sem cobertura (cinza) · Baixa 1-2 (vermelho) · Media 3-5 (ambar) · Alta 6+ (verde).

**Segmentos sem cobertura:** exibem alerta de Oportunidade de Expansao.` },
      { titulo: "5.5 Botao Creditos Recuperados", conteudo: `Botao azul celeste no topo do Dashboard Tributario. Abre modal em tela cheia com:

**4 KPIs:**
• Credito Total Apurado: soma dos valorCreditoInicial de todas as fichas
• Total Compensado: soma dos valorCreditoCompensado (excluindo glosados)
• Saldo a Compensar: creditoInicial - compensado - correcao SELIC
• Total de Honorarios Gerados: soma dos honorarios das compensacoes

**Grafico de linha:** evolucao do credito compensado ao longo do tempo (barras mensais + linha acumulada).

**Tabela por ficha:** Cliente · Contrato · Produto · Tributos · Credito Apurado · Compensado · Saldo.

**Fallback:** se nao ha fichas de credito cadastradas, exibe contratos tributarios concluidos com valorCreditoApurado preenchido.` },
      { titulo: "5.6 Gerenciamento das Tabelas de Referencia", conteudo: `Segmentos, Tributos, Documentos e Tarefas sao editaveis via botoes no topo do Dashboard Tributario.

**4 botoes de tabelas:**
• ⚙️ Segmentos — segmentos de atuacao dos clientes
• ⚙️ Tributos — tributos recuperaveis
• ⚙️ Documentos — catalogo de documentos para checklist dos produtos (usado em todos os produtos)
• ⚙️ Tarefas — catalogo de tarefas operacionais para sequencia dos produtos (usado em todos os produtos)

**Catalogo de Documentos:** usado na configuracao dos produtos (secao "Documentos Necessarios"). Os documentos marcados no produto sao carregados automaticamente na execucao dos contratos.

**Catalogo de Tarefas (20 itens padrao):** usado na configuracao dos produtos (secao "Tarefas Padrao"). As tarefas marcadas no produto sao carregadas automaticamente na execucao com prazo calculado.

**Adicionar item:** digitar o nome e clicar "+ Adicionar". Editar ou excluir via icones na lista.` },
    ]
  },
  {
    titulo: "Parte 6 — Ficha de Controle de Credito Tributario",
    secoes: [
      { titulo: "6.1 O que e a Ficha de Credito", conteudo: `A Ficha de Controle de Credito Tributario permite acompanhar a utilizacao gradual de um credito tributario apurado, registrar cada compensacao (PERDCOMP) com seus respectivos honorarios, e manter auditoria completa do saldo.

**Contexto:** Creditos tributarios nao sao recebidos em dinheiro — sao compensados com tributos futuros via PERDCOMP junto a SRFB. Cada compensacao reduz o saldo do credito e gera honorarios proporcionais.

**Como acessar:** Base Operacional → Execucao Operacional → selecione um contrato da area Tributario com Valor do Credito Apurado preenchido → botao Ficha de Credito (azul, visivel apenas para estes contratos).` },
      { titulo: "6.2 Campos do Contrato Tributario", conteudo: `Para usar a Ficha de Credito, o contrato deve ter preenchidos:

**Valor do Credito Apurado (R$):** valor total do credito tributario identificado para o cliente. Este campo e a base (saldo inicial) da ficha.

**Honorarios sobre o Credito (%):** percentual fixo de honorarios que o escritorio recebe sobre cada valor compensado. Definido uma vez no contrato, vale para todas as compensacoes.

**Calculo automatico:** ao preencher os dois campos acima, o campo Valor Contratado e calculado automaticamente como: Valor Credito x % / 100. Editavel manualmente se necessario.

**Vencimento dos Honorarios:** data de vencimento do lancamento inicial de honorarios no Financeiro. Este lancamento sera reduzido a cada compensacao registrada.` },
      { titulo: "6.3 Estrutura da Ficha de Credito", conteudo: `A ficha exibe 5 cards de saldo no topo:

• **Credito Apurado:** valor inicial do credito (= valorCreditoInicial da ficha)
• **Total Compensado:** soma dos valorCreditoCompensado de compensacoes nao glosadas
• **Total Correcao SELIC:** soma das correcoes informadas manualmente
• **Saldo a Compensar:** creditoInicial - compensado - correcao
• **Honorarios Gerados:** soma dos valorHonorarios das compensacoes

Abaixo dos KPIs: informacoes da ficha (data apuracao, % honorarios, saldo estimado de honorarios).

Formulario de nova compensacao (area azul).

Tabela de historico de compensacoes.` },
      { titulo: "6.4 Registrar uma Compensacao", conteudo: `No formulario Nova Compensacao, preencha:

**Data da Compensacao:** data em que o PERDCOMP foi transmitido.
**N PERDCOMP:** numero do PERDCOMP (ex: 24.000.00000.000000). Obrigatorio.
**Credito Compensado (R$):** valor do credito utilizado nesta compensacao. Nao pode exceder o saldo disponivel.
**Correcao Monetaria SELIC (R$):** valor manual da correcao aplicada ao credito (informado pelo contador/advogado com base na tabela SELIC da Receita Federal).
**Vencimento dos Honorarios:** data de vencimento dos honorarios desta compensacao especifica.
**Status:** Aguardando homologacao / Homologado / Glosado.

**Preview automatico:** o sistema mostra em tempo real o Total Compensado, os Honorarios calculados e o Saldo apos a compensacao.

**Ao clicar Registrar:**
1. Calcula valorTotalCompensado = creditoCompensado + correcaoMonetaria
2. Calcula valorHonorarios = valorTotalCompensado x percHonorarios/100
3. Cria novo lancamento no Financeiro (status A Receber, vencimento = dataVencHonorarios)
4. Reduz o lancamento inicial de honorarios: subtrair o valorHonorarios do valor atual
5. Atualiza os KPIs da ficha` },
      { titulo: "6.5 Homologacao e Glosa", conteudo: `Cada compensacao pode ter seu status alterado:

**Homologar:** compensacao aceita pela SRFB. Clique Homologar na linha. Status = Homologado. O lancamento de honorarios permanece ativo no Financeiro.

**Glosar:** compensacao rejeitada pela SRFB. Clique Glosar na linha.
Efeitos automaticos da glosa:
• Status da compensacao = Glosado (linha em vermelho, excluida dos totais)
• Lancamento de honorarios da compensacao = Cancelado no Financeiro
• Lancamento inicial de honorarios = recomposto (valor aumentado de volta)
• Saldo do credito = restaurado

**Atencao:** compensacoes Homologadas nao podem ser revertidas pela interface — contate o administrador se necessario.` },
      { titulo: "6.6 Auditoria e Correcao SELIC", conteudo: `**Coluna Correcao SELIC:** informada manualmente pelo usuario em cada compensacao. Deve corresponder ao valor da correcao calculada pela tabela SELIC disponibilizada pela Receita Federal para o periodo entre a data de apuracao e a data da compensacao.

**Por que manual:** a taxa SELIC acumulada varia diariamente e depende de tabela oficial da SRFB. O sistema nao faz consulta automatica a fontes externas.

**Impacto na auditoria:** o Total Compensado + Total Correcao deve corresponder ao valor aceito pela SRFB no PERDCOMP. Se houver diferenca, revisar os valores informados.

**Saldo final esperado:** quando o credito for totalmente utilizado, o Saldo a Compensar deve ser zero ou proximo de zero (pequenas diferencas por arredondamento SELIC sao normais).` },
    ]
  },
  {
    titulo: "Parte 7 — Fluxos de Trabalho",
    secoes: [
      { titulo: "7.1 Fluxo Comercial Completo", conteudo: `Do Lead ao Contrato:

1. CRM: + Novo Lead → preencher dados → estagio Lead
2. CRM: Avancar estagios conforme evolucao
3. Vendas: + Nova Proposta → preencher produto, valor, condicoes
4. Vendas: Proposta PDF → enviar ao cliente
5. Vendas: Aprovar → sistema cria automaticamente Cliente + Contrato + Execucao + Lead fechado
6. Base Operacional: Acompanhar execucao — tarefas, documentos, procuracoes
7. Financeiro: Lancamento de honorarios ja esta criado automaticamente (A Receber)
8. Financeiro: Registrar baixa quando recebido → contrato atualizado automaticamente
9. Base Operacional: 100% tarefas concluidas → status Concluido automatico` },
      { titulo: "7.2 Fluxo de Credito Tributario", conteudo: `Do contrato ao controle de compensacoes:

1. Base Operacional: criar contrato tributario com Valor Credito Apurado e % Honorarios preenchidos
2. Sistema: cria lancamento inicial de honorarios no Financeiro automaticamente
3. Base Operacional → Execucao: selecionar o contrato → botao Ficha de Credito
4. Ficha: preencher Data de Apuracao (pre-carregada com data do contrato)
5. A cada PERDCOMP transmitido: Registrar Compensacao com Data, N PERDCOMP, Valor Compensado e Correcao SELIC
6. Sistema: calcula honorarios, lanca no Financeiro, reduz lancamento inicial
7. Quando SRFB homologa: clicar Homologar na linha
8. Se glosado: clicar Glosar → lancamento cancelado, saldo restaurado automaticamente
9. Dashboard Tributario → Creditos Recuperados: acompanhar posicao consolidada de todas as fichas` },
      { titulo: "7.3 Fluxo de Diagnostico RCT", conteudo: `Do cliente ao relatorio tributario:

1. Identificar cliente com potencial de credito no CRM ou Clientes
2. RCT Tributario: + Novo Diagnostico → selecionar cliente, CNPJ, regime, responsavel
3. Para cada produto: definir Viabilidade (Em analise / Sim / Nao / N/A)
4. Para produtos viaveis: preencher credito apurado, PERDCOMP, saldos
5. CMO: preencher tabela de 60 competencias (colunas de auditoria e saldo calculadas automaticamente)
6. Acompanhar E-CAC: + Ocorrencia → data, status PERDCOMP, observacao
7. Gerar Relatorio → revisar → aprovar → Gerar PDF
8. Financeiro: lancar honorarios de exito quando credito homologado` },
      { titulo: "7.4 Checklist de Uso Diario", conteudo: `**Manha:**
• Dashboard → verificar alertas (prazos criticos, processos parados, inadimplencia)
• Agenda → confirmar compromissos do dia
• Financeiro → Contas a Receber → vencimentos do dia
• CRM → leads sem contato ha mais de 3 dias

**Ao longo do dia:**
• Base Operacional → Execucao → marcar tarefas concluidas
• Base Operacional → Execucao → registrar documentos recebidos
• Clientes → registrar ocorrencias de contatos

**Semana:**
• Governanca → verificar inconsistencias e POPs vencidos
• Financeiro → registrar despesas da semana
• Fichas de Credito → verificar PERDCOMP pendentes de retorno da SRFB

**Mes:**
• Financeiro → fechar posicao financeira
• Relatorios → DRE simplificado
• Dashboard Tributario → Creditos Recuperados → posicao consolidada` },
    ]
  },
  {
    titulo: "Parte 8 — Infraestrutura Tecnica",
    secoes: [
      { titulo: "8.1 Arquitetura do Sistema", conteudo: `**Frontend:** React 18 (SPA) · Build: Create React App → main.HASH.js
**Backend:** PHP 8.0 (dados.php, auth.php, usuarios.php, claude.php)
**Banco:** MySQL 8.0 — u423008535_app_juridico
**Hospedagem:** Hostinger · app.alvesecorreaadvogados.com.br
**IA:** Anthropic Claude API via claude.php (requer chave API em public_html/api/)

**Fluxo de dados:** Browser → React → fetch() → PHP → MySQL → JSON → React state

**Deploy:** Build React → upload main.*.js + index.html → Ctrl+Shift+R no browser.` },
      { titulo: "8.2 Banco de Dados", conteudo: `**Banco:** u423008535_app_juridico

**Tabelas:**
• dados (id, dados JSON) — estado completo da aplicacao em 1 registro
• usuarios (id, nome, email, senha_hash, perfil, modulos, status)
• logs (id, usuario_id, acao, data)

**Campos principais do JSON:**
clientes · contratos · lancamentos · despesas · bancos · diagnosticosRCT · leads · produtos · pops · parcerias · equipe · processos · agenda · **fichasCredito** · segmentosTrib · tributos · documentosTrib · vendas · marketing.

**Backup:** exportar JSON via phpMyAdmin mensalmente.` },
      { titulo: "8.3 Historico de Versoes", conteudo: `**v5.5 build v34-fix3-sync2 (2026-04-10) — versao atual:**

Modulo Sala Virtual:
• 4 salas: Comercial · Operacional Juridica · Operacional RCT · Reunioes
• Presenca em tempo real via polling (30s)
• Status automatico (Online/Ocupado/Em reuniao/Ausente)
• Tarefa ativa exibida automaticamente
• Chat por sala e chat direto entre membros
• Knock (chamar atencao) com notificacao visual
• Controle de acesso: exigeSala e salasPermitidas no ModAcesso
• Bloqueio de modulos ate entrada na sala (usuarios configurados)
• Timeout 30min de inatividade

Parcerias — Repasses:
• Campo "Indicado por" no cadastro do cliente
• Campo Parceria (Sim/Nao) no contrato com calculo automatico
• Lancamento automatico de despesa no Financeiro
• Vencimento atualizado ao dar baixa nos honorarios
• Aba Repasses com filtro por parceiro/periodo e PDF

Ocorrencias — Base Operacional:
• Campo Tipo de Ocorrencia (tabela tiposOcorrencia)
• Badge de tipo na exibicao do historico
• Ocorrencias centralizadas na Base (bloqueado no ModProcessos)
• Espelhamento de ocorrencias na ficha do processo (leitura)
• Botao "Ocorrencia" na tabela de processos — navega direto para Base

Relatorio Mensal de Ocorrencias:
• Aba "Rel. Mensal" na Base Operacional
• Sub-abas: Auditoria · Rel.1 (com ocorrencias) · Rel.2 (sem ocorrencias)
• PDFs consolidados por cliente com textos padrao do escritorio
• Rodape fixo com dados dos advogados

Vinculacao Processo-Contrato:
• Campo processosIds no contrato
• Secao "Processos Vinculados" no formulario do contrato
• Ultima ocorrencia na tabela de processos via Base Operacional
• Relatorio de processos por cliente (novo — substituiu Rel.1/Rel.2 antigos)

Correcoes gerais:
• Select protegido com ||[] globalmente (fix TypeError map)
• equipe.filter(e=>e.ativo!==false) em 11 lugares
• usuarioAtual disponivel antes do renderConteudo

**v5.5 build v34-fix3-sync2 (2026-04-07):**
• Timesheet (Consultoria Pacote de Horas)
• Modulo Parcerias — estrutura base
• Relatorio Mensal — estrutura base

**v5.0 (2024):**
• Base Operacional completa · RCT Tributario · CRM · Governanca · Vendas

**v4.0 (2023):**
• Estrutura inicial: Clientes, Processos, Equipe, POPs, Financeiro basico` },
    ]
  },
  ,
  {
    titulo: "Parte 10 — Guia Operacional: RCT Tributario",
    secoes: [
      {
        titulo: "10.1 O que e o Modulo RCT e quando usar",
        conteudo: `O modulo RCT (Recuperacao de Credito Tributario) e o ponto de entrada para todos os trabalhos de credito tributario do escritorio. Ele organiza o ciclo completo desde a triagem comercial ate a aprovacao do diagnostico pelo cliente.

OBJETIVO PRINCIPAL
Identificar, para cada cliente ou lead, quais produtos tributarios sao viaveis para recuperacao de creditos, quais documentos sao necessarios, e controlar o status de aprovacao do diagnostico.

QUANDO USAR
• Ao prospectar um novo cliente ou lead com potencial tributario
• Ao iniciar o processo de analise de creditos de um cliente existente
• Para acompanhar o status de diagnosticos em andamento
• Para controlar quais diagnosticos ja foram aprovados e estao aguardando formalizacao do contrato

FLUXO RESUMIDO
1. Criar diagnostico → selecionar cliente/lead
2. Cadastrar regimes tributarios e atividades da empresa
3. Analisar cada produto tributario → marcar viabilidade
4. Receber documentos → marcar no checklist
5. Concluir diagnostico → registrar aprovacao do cliente
6. Acompanhar pelo dashboard ate assinatura do contrato`
      },
      {
        titulo: "10.2 Como criar um novo diagnostico",
        conteudo: `PASSO A PASSO — CRIANDO UM DIAGNOSTICO

1. ACESSAR O MODULO
   Clique em "RCT" no menu lateral. A tela principal mostra o dashboard com KPIs e a lista de diagnosticos existentes.

2. INICIAR O CADASTRO
   Clique no botao "+ Novo Diagnostico" no canto superior direito.

3. DADOS DO CLIENTE
   • Cliente / Lead: selecione da lista (inclui clientes ativos e leads do CRM)
   • CNPJ: preenchido automaticamente se ja cadastrado no cliente
   • Responsavel: advogado ou colaborador responsavel pelo diagnostico
   • Supervisor: responsavel pela revisao (opcional)
   • Parceria de Origem: se o lead veio de um parceiro cadastrado

4. REGIMES TRIBUTARIOS
   Por que e importante: cada regime tem regras diferentes para recuperacao de creditos. Um cliente que mudou de regime ao longo do tempo pode ter creditos em periodos distintos.
   
   Como cadastrar:
   • Clique em "+ Adicionar Regime"
   • Selecione o regime: Simples Nacional / Lucro Presumido / Lucro Real / MEI
   • Informe a Data de Inicio do periodo naquele regime
   • Informe a Data de Fim (deixe em branco se ainda esta nesse regime)
   • Repita para cada periodo de regime diferente
   
   Exemplo pratico:
   Empresa que era Simples Nacional ate fev/2023 e migrou para Lucro Presumido:
   → Regime 1: Simples Nacional | Inicio: 01/01/2019 | Fim: 28/02/2023
   → Regime 2: Lucro Presumido  | Inicio: 01/03/2023 | Fim: (em branco)

5. ATIVIDADES DA EMPRESA
   • Atividade Principal: ramo principal da empresa (ex: Prestacao de Servicos de TI)
   • Atividades Secundarias: clique "+ Adicionar Atividade" para cada CNAE secundario relevante
   Por que importa: certas teses tributarias dependem do ramo de atividade (ex: CMO para prestadoras de servico, equiparacao hospitalar para clinicas medicas)

6. DATAS E STATUS
   • Data de Inicio: quando o diagnostico foi aberto (preenchida automaticamente com a data atual)
   • Data Prevista para Conclusao: prazo estimado para entrega do diagnostico ao cliente
   • Status: Em andamento (padrao ao criar)

7. SALVAR
   Clique em "Salvar Diagnostico". O sistema gera automaticamente a grade com todos os produtos tributarios para analise.`
      },
      {
        titulo: "10.3 Analisando os produtos tributarios",
        conteudo: `Apos salvar o diagnostico, a secao "Analise por Produto" exibe todos os produtos tributarios ativos do catalogo. Este e o nucleo do diagnostico.

ENTENDENDO A GRADE DE PRODUTOS
Cada produto aparece como um card colorido:
• Amarelo — Em analise (padrao inicial de todos os produtos)
• Verde — Viabilidade: Sim (produto viavel para aquele cliente)
• Cinza — Nao viavel ou Nao se aplica

O contador no topo mostra: X Viaveis · Y Em analise · Z Nao viavel

COMO ANALISAR CADA PRODUTO
1. Clique no card do produto na grade (ele fica destacado com borda dourada)
2. O painel de detalhes abre abaixo
3. Altere o campo "Viabilidade":
   • Sim — cliente tem potencial real para esse produto
   • Nao — nao se aplica ao perfil do cliente
   • Nao se aplica — produto incompativel com o regime ou atividade
   • Em analise — aguardando mais informacoes para decidir

4. Se Viabilidade = Sim, preencha os campos tecnicos disponiveis:
   • Credito Apurado (R$): estimativa do credito recuperavel
   • Campos PERDCOMP: saldos, referencias, status
   • Status da Analise: estagio da apuracao tecnica
   • Observacao tecnica: notas internas sobre o produto

CHECKLIST DE DOCUMENTOS (aparece ao marcar Sim)
Ao marcar viabilidade = Sim, o sistema exibe automaticamente os documentos necessarios para aquele produto (vindos do cadastro do produto).
• Marque o checkbox ao lado de cada documento recebido
• O sistema registra automaticamente a data de recebimento
• A data pode ser editada se necessario
• Documentos obrigatorios estao marcados com * obrigatorio

PRODUTOS COM CONTROLE ESPECIAL
Produto INSS Cessao de Mao de Obra (CMO):
Ao marcar Sim, alem do checklist de documentos, aparece o controle de 60 competencias mensais com sub-abas "Consolidado" e "Competencias".
Sub-aba Consolidado: campos gerais do produto (PERDCOMP, saldos, etc.)
Sub-aba Competencias (60 meses): tabela mensal dos ultimos 60 meses onde o colaborador preenche coluna a coluna os valores apurados, sendo 4 colunas calculadas automaticamente.

FERRAMENTAS DE EXECUCAO
Para produtos com ferramentas cadastradas (e-Auditoria, planilhas, etc.), os botoes aparecem logo abaixo do checklist de documentos. Clique diretamente no botao para abrir a ferramenta externa.`
      },
      {
        titulo: "10.4 Controle de documentos e relatorio de pendencias",
        conteudo: `O controle de documentos permite rastrear exatamente quais documentos foram recebidos e quais ainda estao pendentes para cada produto viavel.

ONDE FICA O CONTROLE
• Durante o cadastro/edicao: na aba de cada produto (aparece ao marcar viabilidade = Sim)
• Na visualizacao do diagnostico: aba "📋 Documentos"
• Acesso rapido: botao "📋" na tabela da tela principal abre direto na aba de documentos

COMO REGISTRAR RECEBIMENTO DE DOCUMENTO
1. Abra o diagnostico para edicao (botao "Editar")
2. Clique no produto na grade
3. No checklist de documentos, marque o checkbox ao lado do documento recebido
4. O sistema registra automaticamente a data de hoje
5. Para corrigir a data, clique no campo de data que aparece apos marcar
6. Salve o diagnostico

VISUALIZANDO AS PENDENCIAS (aba Documentos)
A aba "📋 Documentos" no card "Ver" mostra:
• KPIs no topo: total de produtos viaveis · documentos recebidos · documentos pendentes
• Por produto: lista completa com status de cada documento
• Badge verde "✅ X recebidos" ou amarelo "⏳ Y pendentes" em cada produto

GERANDO O RELATORIO PDF PARA O CLIENTE
O relatorio de documentos e o documento que sera enviado ao cliente solicitando os documentos que ainda nao foram entregues.

Como gerar:
1. Abra o diagnostico (botao "Ver")
2. Clique na aba "📋 Documentos"
3. Clique em "🖨️ Exportar PDF"
4. O sistema abre a janela de impressao com o relatorio formatado

O relatorio contem:
• Cabecalho: Alves & Correa Advogados
• Dados do cliente: nome, CNPJ, regime tributario
• KPIs: produtos viaveis, documentos recebidos, documentos pendentes
• Tabela por produto: lista de documentos com status ✅ Recebido ou ⏳ Pendente
• Mensagem padrao: "Solicitamos o envio dos documentos pendentes para darmos inicio ao diagnostico tributario"
• Rodape: nomes e numeros OAB dos advogados

PONTO DE ATENCAO
Os documentos do checklist vem do cadastro do produto (Modulo Produtos → editar produto → secao Documentos). Se um produto nao mostra documentos no checklist, e necessario cadastra-los primeiro no modulo Produtos.`
      },
      {
        titulo: "10.5 Concluindo e aprovando o diagnostico",
        conteudo: `Apos concluir a analise de todos os produtos e coletar os documentos necessarios, o proximo passo e registrar a conclusao do diagnostico e o status de aprovacao do cliente.

SECAO CONCLUSAO E APROVACAO
Fica no formulario de edicao do diagnostico, logo antes das Observacoes Gerais.

CAMPOS DISPONIVEIS

Data de Conclusao:
Informe a data em que o diagnostico foi finalizado e entregue ao cliente.
IMPORTANTE: ao informar a data de conclusao, o campo Status muda automaticamente para "Concluido". Isso evita inconsistencias (ter data de conclusao com status "Em andamento").

Status de Aprovacao — 4 opcoes:

1. PENDENTE DE APROVACAO (padrao)
   Diagnostico concluido, aguardando retorno do cliente.
   Quando usar: logo apos entregar o diagnostico ao cliente.

2. APROVADO
   Cliente aprovou o diagnostico e confirmou interesse em prosseguir.
   Ao selecionar: aparece o campo "Data de Aprovacao" — registre a data em que o cliente confirmou.
   O diagnostico aparecera no painel de "Aprovados sem Contrato" no dashboard.

3. CONTRATO ASSINADO
   O contrato foi formalizado e assinado pelo cliente.
   Ao selecionar: aparece o campo "Data de Assinatura do Contrato".
   O diagnostico sai do painel de "Aprovados sem Contrato".

4. NAO APROVADO
   Cliente nao teve interesse em prosseguir.
   O diagnostico e encerrado sem geracao de contrato.

FLUXO TIPICO DE APROVACAO
Diagnostico criado → Em andamento
  ↓ Analise concluida
Conclusao registrada → Status: Concluido | Aprovacao: Pendente
  ↓ Cliente aprova
Aprovado → registrar data de aprovacao
  ↓ Contrato formalizado
Contrato Assinado → registrar data de assinatura

PONTOS DE ATENCAO
• Um diagnostico "Aprovado" sem contrato assinado aparece em destaque no dashboard — isso serve de lembrete para acompanhar a formalizacao do contrato
• Nao ha criacao automatica de contrato — o contrato deve ser criado manualmente na Base Operacional quando formalizado
• O historico completo do diagnostico (produtos, creditos, documentos) fica preservado para referencia futura`
      },
      {
        titulo: "10.6 Dashboard e acompanhamento",
        conteudo: `A tela principal do RCT e um dashboard de gestao que permite visualizar rapidamente o status de todos os diagnosticos.

PRIMEIRA LINHA DE KPIs
• Diagnosticos: total de diagnosticos cadastrados no sistema
• Em Andamento: diagnosticos com status "Em andamento" ou "Em Execucao"
• Concluidos: diagnosticos com status iniciando por "Concluido"

SEGUNDA LINHA DE KPIs
• Total Credito: soma de todos os creditos apurados em todos os diagnosticos
• Aprovados: quantos diagnosticos tem status de aprovacao = "Aprovado" (aguardando contrato)
• Contrato Assinado: quantos contratos ja foram formalizados
• Nao Aprovados: diagnosticos encerrados sem aprovacao

CARD "APROVADOS" — CLICAVEL
Quando ha diagnosticos aprovados sem contrato assinado, o card "Aprovados" fica com borda verde e mostra o badge "X sem contrato".
Ao clicar: expande uma tabela com a lista completa desses diagnosticos, mostrando cliente, responsavel, produtos viaveis, credito apurado e data de aprovacao.
Botao "→ Ver" na tabela abre o diagnostico diretamente.
Clique novamente no card para recolher a lista.

DASHBOARD DE PRAZOS
Aparece automaticamente quando ha diagnosticos com "Data Prevista para Conclusao" cadastrada.
4 KPIs de prazo:
• Vencidos (vermelho): data prevista ja passou
• Vencem em 7 dias (amarelo): atencao imediata necessaria
• No Prazo (verde): dentro do prazo normal
• Sem Data (cinza): diagnosticos sem data prevista cadastrada

A tabela de alertas mostra apenas os diagnosticos vencidos e proximos do prazo, com o numero de dias de atraso ou restantes. O botao "→ Ver" abre o diagnostico diretamente para acao.

FILTROS E BUSCA
Acima da tabela de diagnosticos ha filtros por:
• Busca por texto: nome do cliente, responsavel, numero
• Status: filtrar por situacao do diagnostico
• Responsavel: filtrar por membro da equipe

TABELA DE DIAGNOSTICOS
Colunas: Cliente / CNPJ · Regime · Responsavel · Parceria · Produtos Viaveis · Credito Total · Status · Aprovacao · Inicio · Acoes

Botoes de acao por linha:
• "Ver" — abre o diagnostico em modo de visualizacao (sem editar)
• "📋" — abre direto na aba de Documentos (pendencias)
• "Editar" — abre para edicao completa
• "📄" — gera relatorio PDF do diagnostico
• "×" — exclui o diagnostico (use com cautela)`
      },
      {
        titulo: "10.7 Perguntas frequentes — RCT",
        conteudo: `PERGUNTA: Cadastrei um produto no modulo Produtos mas nao aparece no RCT.
RESPOSTA: Verifique se o produto esta com status "Ativo" e se a area esta cadastrada como "Tributario" (exatamente assim, com acento). Apenas produtos ativos da area Tributario aparecem no diagnostico RCT.

PERGUNTA: O checklist de documentos esta vazio mesmo apos marcar viabilidade = Sim.
RESPOSTA: Os documentos precisam estar cadastrados no produto. Acesse Modulo Produtos → localize o produto → clique em Editar → role ate a secao "Documentos Necessarios (Checklist Padrao)" → selecione os documentos ou adicione novos. Salve o produto e reabra o diagnostico.

PERGUNTA: Marquei a data de conclusao mas o status nao mudou para Concluido.
RESPOSTA: A mudanca automatica ocorre ao informar a data. Se o status nao atualizou, verifique se a data foi salva corretamente e salve o diagnostico novamente.

PERGUNTA: Um diagnostico aprovado nao aparece no card "Aprovados sem Contrato".
RESPOSTA: O card mostra apenas diagnosticos com statusAprovacao = "Aprovado". Se o status ja foi alterado para "Contrato Assinado", o diagnostico sai automaticamente da lista. Verifique o campo "Status de Aprovacao" no formulario.

PERGUNTA: Posso ter mais de um diagnostico para o mesmo cliente?
RESPOSTA: Sim. E possivel criar multiplos diagnosticos para o mesmo cliente (ex: um por ano, ou um para cada grupo de produtos). Cada diagnostico e independente com seu proprio historico.

PERGUNTA: As ferramentas de execucao nao aparecem no produto.
RESPOSTA: As ferramentas precisam ser cadastradas no produto. Acesse Modulo Produtos → editar o produto → secao "Ferramentas de Execucao" → clique "+ Adicionar Ferramenta" → preencha nome, tipo e URL → salve. As ferramentas aparecerao no proximo acesso ao diagnostico.

PERGUNTA: Como imprimir o relatorio de documentos para enviar ao cliente?
RESPOSTA: Abra o diagnostico (botao "Ver") → clique na aba "📋 Documentos" → clique em "🖨️ Exportar PDF". Uma nova janela abre com o relatorio formatado. Use Ctrl+P ou o botao de impressao do navegador para imprimir ou salvar como PDF.

PERGUNTA: O controle CMO de 60 meses nao aparece.
RESPOSTA: O controle de 60 meses e exclusivo do produto "INSS sobre Cessao de Mao de Obra". Verifique se esse produto esta com viabilidade = Sim no diagnostico. O produto tambem precisa ter o campo "Template de Execucao RCT" configurado como "cmo" no cadastro do produto.

PERGUNTA: Como vincular o diagnostico aprovado a um contrato?
RESPOSTA: Atualmente o vinculo e feito de forma manual. Apos a aprovacao do diagnostico, crie o contrato normalmente na Base Operacional informando o cliente correspondente. O registro do diagnostico aprovado serve como historico e referencia para a execucao do contrato.`
      }
    ]
  }
  ,
  {
    titulo: "Parte 10b — Guia Operacional: CRM",
    secoes: [
      { titulo: "10.8 Modulo CRM — Gestao de Leads e Relacionamento", conteudo: `O modulo CRM controla todo o ciclo comercial do escritorio, desde o primeiro contato com um lead ate a assinatura do contrato.

OBJETIVO PRINCIPAL
A) Registro de relacionamento: historico completo de contatos, reunioes, propostas e ocorrencias de cada lead.
B) Controle comercial: acompanhar cada proposta com datas de envio, aprovacao, contrato e conversao em cliente.

FUNIL — 10 ESTAGIOS NA ORDEM OPERACIONAL
1. Lead — Contato inicial sem qualificacao
2. Qualificacao — Avaliando perfil do potencial cliente
3. Reuniao — Reuniao agendada ou realizada
4. Documentacao — Coletando documentos para analise
5. Pre-Analise — Analisando viabilidade antes da proposta
6. Proposta — Proposta enviada ao cliente
7. Negociacao — Cliente em processo de decisao
8. Envio de Contrato — Contrato formal enviado para assinatura
9. Contrato Assinado — Contrato assinado, pronto para conversao
10. Nao Qualificado — Lead encerrado sem avanco

COMO AVANCAR O ESTAGIO
1. Abra o lead (Editar ou clique no card do funil)
2. Altere o campo Estagio para a fase atual
3. O sistema registra a data da mudanca automaticamente
4. Para estagio Proposta em diante, aparecem os campos de datas comerciais
5. Salve

PREENCHIMENTO AUTOMATICO DE DATAS
Ao mudar o estagio, o sistema preenche automaticamente:
Proposta -> Data de Envio da Proposta
Negociacao -> Data de Aprovacao da Proposta
Envio de Contrato -> Data de Envio do Contrato
Contrato Assinado -> Data de Assinatura + oferta de conversao em cliente
As datas podem ser editadas manualmente no formulario do lead.` },
      { titulo: "10.9 CRM — Visoes e Dashboard", conteudo: `O modulo CRM tem 4 visoes de trabalho:

FUNIL VISUAL
Kanban com uma coluna por estagio. Cada card mostra:
Nome do lead, produto de interesse, valor estimado
Icone de tempo: tempo em dias na fase atual (calculado automaticamente)
Badges de pendencia:
- "Envio pendente" (proposta nao enviada)
- "Aprovacao pendente"
- "Contrato pendente"
- Badge verde "Assinado" quando contrato assinado
Clique no card para ver o historico completo.

LISTA
Tabela com todos os leads, com filtro por estagio e busca por nome.

CONTROLE COMERCIAL
Visao dedicada ao acompanhamento de propostas. Contem:
5 KPIs: Em Proposta, Em Negociacao, Contrato Enviado, Contrato Assinado, Proposta Pendente
Tabela com todas as datas por lead:
- Lead, Responsavel, Estagio atual
- Data Envio Proposta (vermelho se pendente)
- Data Aprovacao
- Data Envio Contrato
- Data Assinatura (verde quando preenchida)
- Dias no estagio: verde abaixo de 7 dias, amarelo acima de 7, laranja acima de 14
Clique em qualquer linha para abrir o historico do lead.

INDICADORES
Dashboard com metricas gerais: taxa de conversao, valor em negociacao, leads por origem.

PONTO DE ATENCAO
A aba Controle mostra apenas leads que chegaram ao estagio Proposta ou que tem pelo menos uma data comercial preenchida.` },
      { titulo: "10.10 CRM — Historico, Timeline e Conversao", conteudo: `HISTORICO DE OCORRENCIAS
Registra contatos, reunioes, e-mails e qualquer interacao com o lead.
Como registrar:
1. Abra o lead (botao "Ver")
2. Clique em "+ Registrar Ocorrencia"
3. Informe: data, tipo e descricao
4. Salve
Esse historico e fundamental para consultar o que foi discutido antes de uma reuniao e manter rastreabilidade para futuros contatos.

TIMELINE DE ESTAGIO
Ao visualizar um lead (botao "Ver"), aparece a secao "Historico de Estagios" com:
Cada estagio pelo qual o lead passou, data de entrada e tempo em cada fase.
Exemplo:
Lead -> 28/01 (3 dias nesta fase)
Qualificacao -> 31/01 (7 dias nesta fase)
Reuniao -> 07/02 ...
Permite identificar gargalos (onde leads ficam parados por mais tempo).

DATAS DO PROCESSO COMERCIAL
Ao visualizar lead no estagio Proposta ou posterior, aparece o card "Datas do Processo Comercial" com:
Data de Envio da Proposta, Data de Aprovacao, Data de Envio do Contrato, Data de Assinatura.

CONVERSAO EM CLIENTE
Ao atingir "Contrato Assinado":
O sistema pergunta automaticamente se deseja converter em cliente.
Ao confirmar: lead vira cliente com todos os dados ja preenchidos.
O historico do lead permanece no CRM para consulta futura.
Para converter manualmente: abra o lead (Ver) e clique em "Converter em Cliente".

PERGUNTAS FREQUENTES
P: As datas comerciais nao aparecem no lead.
R: So aparecem no estagio Proposta ou posterior, e somente se pelo menos uma data estiver preenchida.

P: A timeline de estagios esta vazia para leads antigos.
R: O historico registra automaticamente a partir da primeira edicao apos a atualizacao. Ao editar e salvar um lead existente mudando o estagio, o registro comeca.

P: Lead chegou em Contrato Assinado mas nao apareceu a pergunta de conversao.
R: A pergunta aparece apenas na primeira vez que o estagio e alterado para Contrato Assinado. Para converter manualmente, use o botao "Converter em Cliente" no viewItem do lead.` }
    ]
  }

];

// ── PARTES ADICIONADAS NA SESSÃO 2026-05-20 ────────────────────────
const MANUAL_PARTES_EXTRA = [
  {
    titulo: "Parte 11 — Base Operacional: Fluxo Completo",
    icone: "⚙️",
    secoes: [
      {
        titulo: "11.1 Visão Geral e Abas",
        conteudo: `Build: v34-fix3-sync2-ctrl28 (2026-05-20/21)

A Base Operacional é o módulo central de gestão de contratos, execução por fases, controladoria e comunicação com o cliente.

ABAS DISPONÍVEIS:
📊 Dashboard       — KPIs globais: contratos por fase, SLA, alertas
📋 Contratos       — Cadastro e gestão de contratos (multi-produto)
⚙️ Execução        — Kanban 7 fases + visão lista + board por cliente
🎛️ Controladoria   — 5 visões: Estrategista, Controlador, Operador, Rel. E&T, Rel. Conclusão
📋 Pendências      — Relatório de pendências operacionais
📨 Rel. Mensal     — Relatório mensal de ocorrências
📬 Comunicações    — Kit Boas-Vindas, Linha de Confiança, Datas Especiais`
      },
      {
        titulo: "11.2 Cadastro de Contratos",
        conteudo: `Campos obrigatórios: Cliente + ao menos 1 Produto (via ProdutoSelector — por área, sem duplicatas)

Campos novos nesta versão:
• produtosIds[]       — multi-produto
• cnpj                — CNPJ/CPF com máscara (pré-preenchido do cliente)
• dataNascimento      — Espelhado automaticamente do cliente PF; badge ⚡ Prioridade Processual ≥ 65 anos
• faturamentos[]      — Faturamento anual por ano (dinâmico)
• regimes[]           — Regimes tributários com data início/fim
• atividadePrincipal  — CNAE texto livre
• atividadesSecundarias[] — Array dinâmico

Ao selecionar cliente PF: dataNascimento é preenchida automaticamente.
Ao selecionar cliente PJ com CNPJ: campo cnpj é pré-preenchido com máscara.`
      },
      {
        titulo: "11.3 Execução Operacional — Kanban 7 Fases",
        conteudo: `Fases e SLA:
• Entrada e Triagem   (4h)  — status: "Aguardando Documentação"
• Produção           (72h)  — status: "Em Produção"
• Revisão Sênior     (24h)  — status: "Em Revisão"
• Protocolo           (4h)  — status: "Em Protocolo"
• Sobrestado           (—)  — SLA pausado; retorna à fase anterior ao regularizar
• Concluído / Arquivo  (—)

Tela de abertura: toggle Kanban/Lista. Busca global em tempo real.
Clique em qualquer card → board Kanban do cliente → clique no produto → ModalCardExecucao (1100px).`
      },
      {
        titulo: "11.4 Modal do Card — Seções e Gate de Avanço",
        conteudo: `Seções do ModalCardExecucao:
1. Cabeçalho: fase + barra SLA + 4 campos info
2. Checklist por fase:
   - Entrada: documentos do produto (r.documentos[])
   - Produção: tarefas do produto (r.tarefas[])
   - Revisão/Protocolo: itens globais (Tipo A) + extras do produto (Tipo B)
3. Dados Empresariais (PJ, Entrada e Triagem — OBRIGATÓRIO para avançar)
4. Template RCT (Produção — produtos com campoRCT):
   - Template 1 campo_unico: campo único de valor
   - Template 2 60_meses: tabela 60 competências, total automático
   - Valor acumula em contrato.valorCreditoApurado
5. Conclusão por fase: textarea com salvo automático (onBlur)
6. Nova Ocorrência (4 tipos + Confidencial)
7. Histórico de Ocorrências
8. Gate de avanço: checklist + dados empresariais (PJ) + sem pendência externa`
      },
      {
        titulo: "11.5 Controladoria, Produtos TRIB e Sessão B",
        conteudo: `CONTROLADORIA — 5 visões:
• Estrategista: KPIs 7 fases com listagem de contratos por card
• Controlador: fila com filtros (somente visualização)
• Operador/Revisor: cards Produção/Revisão (somente visualização, sem botões de ação)
• Relatório E&T: layout split (lista esquerda + preview direita); PDF via window.print()
• Relatório Conclusão: relatório gerencial ao cliente; distinção valor apurado vs. informado

PRODUTOS TRIBUTÁRIOS:
38 produtos TRIB (TRIB001-TRIB038) com campoRCT definido.
campo_unico: 7 produtos | 60_meses: 31 produtos
Migração automática: produtos do banco sem campoRCT recebem o valor por match de nome.

SESSÃO B — CHECKLISTS POR PRODUTO:
TEMPLATES_CHECKLIST_B: 9 grupos (PIS/COFINS, CMO, Verbas Indenizatórias, IR/CSLL CC, IR/CSLL SD, Exclusão ICMS, Gestão de Dívida, Previdenciário, Processo Judicial).
No cadastro de produto: seletor de template + botão "↓ Usar Template".

PERGUNTAS FREQUENTES:
P: Checklist não marca ao clicar. R: useState puro sem useEffect (ctrl19). key={contratoId+"_"+status}.
P: Dados Empresariais não aparecem. R: PJ only — verificar tipo="PJ" OU CNPJ 14 dígitos.
P: Template RCT não aparece na Produção. R: Verificar campoRCT no cadastro do produto.`
      }
    ]
  },
  {
    titulo: "Parte 12 — Módulo de Comunicações com o Cliente",
    icone: "📬",
    secoes: [
      {
        titulo: "12.1 Objetivo e Fluxo de Aprovação",
        conteudo: `Módulo 8 — Relacionamento Digital. Build: ctrl28 (2026-05-21)

Automação do relacionamento com geração automática de mensagens e aprovação antes do disparo via WhatsApp Web.

ACESSO: Base Operacional → aba 📬 Comunicações

FLUXO:
1. Gatilho automático gera a mensagem na Fila
2. Clique "📲 Revisar e Enviar"
3. Modal abre com texto pré-preenchido e editável
4. Clique "📲 Abrir WhatsApp e Registrar Envio"
5. WhatsApp Web abre com mensagem pronta (wa.me/número?text=mensagem)
6. Envio registrado no Histórico`
      },
      {
        titulo: "12.2 Módulos e Gatilhos",
        conteudo: `MÓDULO 1 — KIT DE BOAS-VINDAS (Dia 1)
Gatilho: dataEntrada + 0 dias. Canal: WhatsApp + E-mail.

MÓDULO 2 — LINHA DE MONTAGEM DA CONFIANÇA
Dia 2: Confirmação do Kit e checklist
Dia 3: Bastidores — caso em análise
Dia 5: Check-in emocional
Dia 7: Marco Legal — resumo formal da semana

MÓDULO 3 — DATAS ESPECIAIS
O PESSOAL: aniversário do cliente (PF: dataNascimento / PJ: contatos da empresa)
O COLETIVO: Fim de Ano (20/dez) · Dia do Advogado (11/ago) · Dia do Cliente (15/set)
O PROCESSUAL: 1 ano após dataEntrada do contrato`
      },
      {
        titulo: "12.3 Personalização e Campos Necessários",
        conteudo: `PERSONALIZAÇÃO:
PF: "Olá, [Nome do Cliente]!"
PJ: "Att. [Nome da Empresa]\n\nOlá, [Nome do Contato]!"
Contato PJ: usa Contato 1 do cadastro (nome + WhatsApp + dataNascimento + e-mail)

ASSINATURA PADRÃO (todas as mensagens):
  Atenciosamente,
  Alves & Correa Advogados
  Dr. Nelson José Alves · OAB/SP 398.875
  Dra. Annie Lemos Alves Correa · OAB/SP 446.942

CAMPOS NECESSÁRIOS NO CADASTRO DO CLIENTE:
• dataNascimento  — Aniversário (PF) e Datas Especiais
• linkGoogle      — Link Google Meu Negócio
• email           — Canal de e-mail
• whatsapp        — Canal WhatsApp
• contatosPJ[]:
    nome / whatsapp / dataNascimento / email

PERGUNTAS FREQUENTES:
P: Fila aparece vazia. R: Comunicados gerados para hoje. Verificar dataEntrada do contrato ou aniversários cadastrados.
P: Botão travado. R: Verificado e corrigido no ctrl27k — variáveis prodNome/addDias/dataEnt ausentes na função.
P: WhatsApp abre sem texto. R: Verificar campo whatsapp do cliente preenchido.`
      }
    ]
  },
  {
    titulo: "Parte 12 — Atualizações Recentes (Jun 2026)",
    icone: "🆕",
    secoes: [
      {
        titulo: "12.1 Mesa de Trabalho por Fase (Base Operacional)",
        conteudo: `COMO ACESSAR:
Base Operacional → aba Execução → cards de resumo no topo → botão 🏢 Mesa em cada fase.

O QUE É:
Cada fase da execução possui uma sala/mesa de trabalho dedicada. Ao entrar, o executor vê apenas os contratos da sua fase, os objetivos do seu cargo, os KPIs esperados e os agentes IA configurados para auxiliá-lo.

ABAS DA MESA:
📋 Contratos — lista de todos os contratos na fase atual, com destaque de SLA vencido (barra vermelha + badge ⚠️ SLA Vencido). Clique no card abre o formulário do contrato sem sair da mesa.

🎯 Objetivos & KPIs — missão do cargo, responsabilidade chave, metas numeradas, KPIs em grid e atividades principais. Conteúdo extraído das descrições de cargo oficiais.

🤖 Agentes IA — agentes fixos definidos para a fase. Para configurar: botão ⚙️ Gerenciar Agentes → marque os agentes desejados → Confirmar. A seleção fica salva permanentemente por fase.

REGRA DE SEGURANÇA:
O botão "→ Produção" da visão Controlador foi removido. O avanço de fase só ocorre pela Mesa, após conclusão de todas as obrigações do checklist.

FASES COM CONTEÚDO CONFIGURADO:
• 📥 Entrada e Triagem   → Gestor de Entrada e Controladoria
• ⚙️ Produção            → Operador Jurídico
• 🔍 Revisão Sênior      → Revisor Sênior
• 📨 Protocolo           → Operador de Protocolo
• 🗂️ Arquivo             → Operador de Arquivo
• ⏳ Sobrestado          → mesa em branco (configurável)
• ✅ Concluído           → mesa em branco (configurável)`
      },
      {
        titulo: "12.2 Pasta do Cliente (link nuvem)",
        conteudo: `ONDE CADASTRAR:
Clientes → editar cliente → campo 📁 Pasta do Cliente (link nuvem).
Aceita qualquer URL: Google Drive, OneDrive, Dropbox, SharePoint etc.

ONDE APARECE:
Em todas as fases da execução (ModalCardExecucao) — banner azul na segunda linha do formulário, com botão Abrir ↗ que abre a pasta diretamente em nova aba.

Cadastrado uma vez no cliente → carregado automaticamente em todos os contratos.

USO PRÁTICO:
O executor abre o formulário do contrato na Mesa → clica Abrir ↗ → acessa a pasta do cliente na nuvem → confere os documentos → preenche o checklist.`
      },
      {
        titulo: "12.3 Cadastro de Clientes e Leads — Novos Campos",
        conteudo: `CAMPOS ADICIONADOS (disponíveis em Clientes e CRM/Leads):

📁 Pasta do Cliente
Link para a pasta do cliente na nuvem (Drive, OneDrive etc.). Carregado automaticamente na execução dos contratos.

🔒 Dados de Acesso
Campo texto livre para uso interno. Exemplos: e-CAC (CPF + senha), PGFN (login), Certificado Digital (serial, vencimento), outros portais tributários.
⚠️ Aviso: dados ficam armazenados no banco local. Não compartilhar externamente.`
      },
      {
        titulo: "12.4 CRM Comercial — Novo Funil (9 Colunas)",
        conteudo: `ESTÁGIOS REESTRUTURADOS:
1. 📥 Recepção           — entrada do lead, docs recebidos e pré-análise
2. 🔍 Qualificação        — avaliação de fit; se reprovado → vai para Perdido
3. 📅 Reunião             — reunião de apresentação agendada/realizada
4. 📄 Proposta            — proposta de honorários enviada
5. 🤝 Negociação          — negociação de valores e condições
6. 📨 Envio de Contrato   — contrato enviado para assinatura
7. ⏳ Pend. Assinatura    — aguardando assinatura das partes
8. ✅ Contrato Assinado   — fechamento; migra para Base Operacional
9. ❌ Perdido/Descartado  — qualquer etapa pode mover para cá

KANBAN UNIFICADO:
Leads e clientes (Expansão) aparecem na mesma esteira. A separação é feita apenas por badge no card: "lead" (azul) ou "expansão" (verde). Retrocompatibilidade: estágios antigos são mapeados automaticamente.`
      },
      {
        titulo: "12.5 Agenda — Integração Google Calendar",
        conteudo: `CONFIGURAÇÃO:
Agenda → aba 🔗 Google Calendar → botão 🔑 Conectar Google → autorizar via OAuth 2.0.
Conta: alvesecorreaadvogados@gmail.com

SINCRONIZAÇÃO AUTOMÁTICA (Opção C):
• SOJ → Google: ao salvar qualquer evento na Agenda, ele é criado automaticamente no Google Calendar. O ID do Google é salvo no evento para futura atualização ou exclusão.
• Google → SOJ: ao abrir a aba 🔗 Google Calendar, o sistema busca os eventos do mês atual e importa os novos silenciosamente.
• Exclusão: ao remover evento no SOJ, ele é removido do Google Calendar se estiver sincronizado.

ARQUIVOS PHP (raiz do public_html no Hostinger):
google_auth.php · google_callback.php · google_calendar.php · google_config.php`
      },
      {
        titulo: "12.6 Proposta Comercial — Novo Layout",
        conteudo: `ESTRUTURA DO PDF:
À [Nome do cliente] + CPF/CNPJ + e-mail
Ref.: Proposta para Honorários Advocatícios

I   — DOS SERVIÇOS JURÍDICOS CONTRATADOS
      + Parágrafo Único (vigência)
─────────────────────────────────────────
II  — DA DOCUMENTAÇÃO (aparece somente se o produto tiver documentos cadastrados)
─────────────────────────────────────────
III — DOS HONORÁRIOS E CONDIÇÕES DE PAGAMENTO
      Lista com: valor fixo + % êxito + % endividamento (quando preenchidos)
─────────────────────────────────────────
IV  — DAS CONDIÇÕES GERAIS
      5 itens padrão (item 6 "Documentos necessários" removido automaticamente)

NOVOS CAMPOS NA PROPOSTA:
• % do Valor do Endividamento — aparece no item III quando preenchido
• Separadores HR entre seções
• Documentos resolvidos por nome (não mais códigos internos)
• Assinatura: Dra. Annie Correa + Dr. Nelson Alves (sem assinatura do cliente)`
      },
      {
        titulo: "12.7 Agente DataJud — Atualização Automática de Processos",
        conteudo: `DESCRIÇÃO:
Script externo Python executado via Claude Code. Roda automaticamente todos os dias às 7h.

FUNCIONAMENTO:
1. Lê todos os processos ativos cadastrados no módulo Processos do SOJ
2. Consulta a API DataJud (CNJ) para cada número de processo
3. Identifica movimentações novas desde a última verificação
4. Atualiza o módulo Processos com as movimentações
5. Lança as ocorrências nos contratos vinculados (fase, prazo, movimentação)

COBERTURA:
API DataJud do CNJ — agrega praticamente todos os tribunais brasileiros: STJ, STF, TST, TJ-SP, TJ-PR e demais TJs, TRFs (1ª a 6ª Região), TRTs, TSE e outros.

STATUS ATUAL: ⏸️ SUSPENSO
Motivo: as ocorrências são lançadas nos contratos vinculados ao processo. O pré-requisito é que todos os processos estejam vinculados aos seus respectivos contratos na Base Operacional. A vinculação está em andamento (módulo Processos → campo "Contrato Vinculado" + Base Operacional → "Processos Vinculados").

RETOMADA:
Quando a vinculação processo↔contrato estiver completa, o agente pode ser reativado no Claude Code para retomar as atualizações diárias automáticas.`
      }
    ]
  },
  {
    titulo: "Parte 13 — Atualizações Jul/2026 (E-mail, Legal Mail, NEO+ADA, LGPD)",
    icone: "🆕",
    secoes: [
      {
        titulo: "13.1 Dados Financeiros do Contrato — Componentes",
        conteudo: `Além do Honorário Inicial (fixo, existente há tempos), o contrato agora suporta múltiplos "componentes financeiros" simultâneos — útil quando o honorário combina modalidades (ex.: inicial fixo + êxito percentual + mensal).

ONDE: ficha de edição do Contrato, seção "💼 Dados Financeiros do Contrato" (após "Condições de Pagamento").

COMO USAR:
1. Botão "+ Adicionar Componente" — cria uma linha independente, sem limite de quantidade
2. Cada componente tem: tipo (mensal, anual, êxito, por hora/ato/fase/evento, retainer, híbrido, custas, despesas, reembolsos, tributos, repasses), forma de precificação (fixo/percentual), condição de pagamento e gatilho
3. Campo "ativo" permite pausar um componente sem apagar o histórico

GERAR LANÇAMENTO (manual, não automático):
Na ficha de visualização do contrato (não na edição), cada componente ativo tem botão "💰 Gerar Lançamento":
- Componente fixo → lança direto em Financeiro
- Componente percentual → pede o valor base no momento (ex.: valor do acordo) e calcula
- Tipo "Repasses" → gera despesa, não lançamento a receber
- Cada geração fica registrada no componente (contador de lançamentos já gerados), evitando duplicar sem confirmação

"Total Acumulado do Contrato" (Honorário Inicial + componentes fixos ativos) aparece tanto na edição quanto na ficha de visualização.`
      },
      {
        titulo: "13.2 Qualificação do Cliente (para documentos)",
        conteudo: `Os campos de qualificação (RG, Estado Civil, Profissão, Nacionalidade, UF do RG) agora ficam no CADASTRO DO CLIENTE (não mais só num modal avulso na hora de gerar documento).

ONDE: módulo Clientes → edição → seção "📋 Qualificação para Documentos" (só aparece para cliente Pessoa Física).

Por quê fica no Cliente e não no Contrato: são dados da PESSOA, não do contrato — um cliente pode ter vários contratos e o dado não deve ser repreenchido a cada novo documento gerado (Procuração, Declaração de Hipossuficiência, Contrato de Prestação de Serviços).

Preenchendo esses campos no cadastro do cliente, eles já vêm prontos automaticamente na hora de gerar qualquer um dos 3 documentos no módulo Vendas.`
      },
      {
        titulo: "13.3 Emissão de Documentos — Só pelo Vendas",
        conteudo: `Procuração, Declaração de Hipossuficiência, Proposta e Contrato de Prestação de Serviços são emitidos EXCLUSIVAMENTE pelo módulo Vendas — a Base Operacional não emite mais esses documentos (só coleta/confere via checklist, ex.: "Procuração assinada?").

Se precisar gerar Procuração ou Declaração para um cliente já em execução na Base Operacional, abra o contrato correspondente no módulo Vendas para emitir — não existe mais botão de emissão dentro do card de execução.`
      },
      {
        titulo: "13.4 Canal de E-mail no Atendimento",
        conteudo: `O módulo Atendimento (Central de Atendimento IA) ganhou o canal de E-mail, ao lado de WhatsApp e Instagram — contas cliente@ e contato@alvesecorreaadvogados.com.br.

DIFERENÇA DE MODELO: cada e-mail recebido vira um atendimento NOVO e separado (modelo de inbox tradicional) — diferente de WhatsApp/Instagram, que agrupam por conversa contínua com o mesmo contato.

MODO HOMOLOGAÇÃO: toda resposta gerada pela IA (nos 3 canais) fica retida até um advogado revisar e clicar "✓ Aprovar e Enviar" (o texto pode ser editado antes de enviar) ou "✕ Descartar". Enquanto esse modo estiver ativo, a IA nunca envia nada sozinha.

LIXEIRA E BLOQUEIO DE SPAM: botão 🗑️ move o atendimento pra lixeira (reversível). Botão "🚫 Bloquear" (só e-mail) impede que aquele remetente/domínio gere novo atendimento — cai direto na lixeira sem acionar a IA.

BADGE DE SCORE BANT: cada atendimento qualificado como Lead mostra um badge "BANT X%" — score calculado automaticamente pela IA a cada mensagem trocada (Budget/Authority/Need/Timeline). Só vira Lead visível no CRM quando o score atinge 60%.

WATCHDOG (alerta de atendimento parado): ícone ⏰ aparece e se repete conforme o tempo sem resposta ao lead/cliente: 4h (nível 1, alerta visual), 24h (nível 2, e-mail automático ao advogado responsável), 72h (nível 3, cria lembrete na Agenda).`
      },
      {
        titulo: "13.5 Legal Mail — Monitoramento Automático de Processos",
        conteudo: `Substitui o DataJud (suspenso, ver 12.7) como fonte principal de atualização automática de processos — baixa cópia íntegra dos autos e monitora em tempo real (não só metadados, como o DataJud).

FUNCIONAMENTO: cron diário (08h) consulta o Legal Mail, identifica movimentações novas nos processos monitorados e lança automaticamente como ocorrência no contrato vinculado (badge "Legal Mail" na ocorrência, com link direto pro documento).

COBERTURA: só processos com status "Em andamento", "Suspenso" ou "Aguardando" são monitorados de verdade pelo Legal Mail. Processos "Encerrados" têm contrato vinculado na Base Operacional (pra manter o histórico organizado), mas não entram no monitoramento ativo do Legal Mail.

PROCESSOS NOVOS: ao cadastrar um processo novo que deva ser monitorado, garanta que ele tenha um Contrato Vinculado (módulo Processos) — sem isso, o Legal Mail não tem onde lançar a ocorrência.`
      },
      {
        titulo: "13.6 NEO + ADA — Campanhas Pagas (Windsor.ai)",
        conteudo: `Módulo Marketing → aba "📈 NEO + ADA (Campanhas Pagas)".

ADA (leitura — Analytics): mostra métricas de campanhas PAGAS (Meta Ads, Google Ads, GA4, Instagram Ads) — investimento, cliques, impressões, conversões. Não mostra dado orgânico (isso é o módulo "Redes Sociais", que é só cadastro/status, sem métrica real).
Campos: conector, período (7/30/90 dias, mês/ano atual, mês/ano passado, todo período), ID da conta (Account ID do Meta Ads/Google Ads/GA4 — os IDs ficam salvos no banco, não no navegador, então aparecem iguais em qualquer computador).

NEO (escrita — ações reais): cria, pausa ou ajusta orçamento de campanha de verdade (dinheiro real). Restrito a Administrador/Sócio. Exige digitar a frase exata "CONFIRMO A EXECUCAO" antes de qualquer ação — não é clique nem checkbox.

LIMITAÇÃO DE PLANO: a conta Windsor.ai contratada permite conectar até 3 fontes de dados simultâneas (plano Básico) — hoje: Meta Ads, Google Ads e GA4.`
      },
      {
        titulo: "13.7 LGPD — Consentimento, Log de Acesso e Retenção",
        conteudo: `CONSENTIMENTO: cadastro de cliente NOVO (Pessoa Física ou Jurídica) exige marcar o campo de consentimento LGPD + escolher a finalidade do tratamento antes de salvar. Clientes já cadastrados antes dessa mudança não foram afetados retroativamente.

LOG DE ACESSO: toda leitura e gravação de dados no sistema é registrada (usuário, ação, data/hora, IP) numa tabela de auditoria — não aparece na tela do usuário comum, é controle interno para eventual fiscalização da ANPD.

RETENÇÃO: os logs de acesso são apagados automaticamente após 12 meses (rotina mensal automática) — não ficam guardados indefinidamente.

Política de Privacidade e Termos de Uso: documentos formais gerados no papel timbrado do escritório — em revisão final antes da publicação pública.`
      }
    ]
  }
];
// Merge com partes originais para exibição no ModManual
function ModManual({ data, usuario }) {
  const todasPartes = [...MANUAL_COMPLETO_PARTES, ...MANUAL_PARTES_EXTRA].filter(p => Array.isArray(p?.secoes));
  const [modo,       setModo]       = React.useState("resumido"); // "resumido" | "completo"
  const [abaChat,    setAbaChat]    = React.useState(false);
  const [pergunta,   setPergunta]   = React.useState("");
  const [carregando, setCarregando] = React.useState(false);
  const [historico,  setHistorico]  = React.useState([]);
  const [busca,      setBusca]      = React.useState("");
  const [buscaAtiva, setBuscaAtiva] = React.useState(false);
  const inputRef  = React.useRef(null);
  const buscaRef  = React.useRef(null);

  // Busca em tempo real
  const resultadosBusca = React.useMemo(()=>{
    const q = busca.toLowerCase().trim();
    if(q.length < 2) return [];
    const res = [];
    // Busca resumido
    MANUAL_RESUMIDO.modulos.forEach(m=>{
      if(!m||!m.titulo) return;
      if(m.titulo.toLowerCase().includes(q)||m.resumo.toLowerCase().includes(q))
        res.push({tipo:"Módulo",titulo:m.titulo,trecho:m.resumo,ancora:"mod-"+m.id});
    });
    MANUAL_RESUMIDO.faq.forEach((f,i)=>{
      if(f.p.toLowerCase().includes(q)||f.r.toLowerCase().includes(q))
        res.push({tipo:"FAQ",titulo:f.p,trecho:f.r.slice(0,100)+"...",ancora:"faq-"+i});
    });
    // Busca completo
    todasPartes.forEach((parte,pi)=>{
      (parte.secoes||[]).forEach((s,si)=>{
        if(s.titulo.toLowerCase().includes(q)||s.conteudo.toLowerCase().includes(q))
          res.push({tipo:"Manual",titulo:s.titulo,trecho:s.conteudo.slice(0,100)+"...",ancora:"sec-"+pi+"-"+si,modoNeed:"completo"});
      });
    });
    return res.slice(0,8);
  },[busca]);

  const irPara = (r) => {
    setBusca(""); setBuscaAtiva(false); setAbaChat(false);
    if(r.modoNeed==="completo") setModo("completo");
    setTimeout(()=>{
      const el = document.getElementById(r.ancora);
      if(el) el.scrollIntoView({behavior:"smooth",block:"start"});
    }, r.modoNeed==="completo" ? 300 : 100);
  };

  const perguntarIA = async () => {
    if(!pergunta.trim()||carregando) return;
    const q = pergunta.trim();
    setPergunta(""); setCarregando(true);
    const novoHist = [...historico,{tipo:"user",texto:q}];
    setHistorico(novoHist);
    try {
      // Contexto dinâmico com dados reais do sistema
      const produtosCtx = (data.produtos||[]).slice(0,40).map(p=>
        `• ${p.nome}${p.area?" ["+p.area+"]":""}${p.descricao?" — "+p.descricao:""}${
          (p.documentosPadrao||p.trib_documentos||[]).length>0
            ? "\n  Documentos: "+(p.documentosPadrao||p.trib_documentos||[]).map(d=>{
                const id = typeof d==="string"?d:(d.id||d.docId||"");
                return typeof d==="string"
                  ? ((data.documentosTrib||[]).find(x=>x.id===d)?.nome||d)
                  : (d.nome||id);
              }).filter(Boolean).join(", ")
            : ""
        }`
      ).join("\n");

      const ctx = `Você é o assistente especializado do Sistema Operacional Jurídico — Alves & Correa Advogados v5.5.
Responda de forma objetiva e prática. Indique o caminho de navegação quando relevante.
Quando o usuário perguntar sobre um produto ou tese jurídica, informe os documentos necessários.

ESCRITÓRIO:
${data.escritorio?.nome||"Alves & Correa Advogados"} · ${data.escritorio?.email||"contato@alvescorreaadvogados.com.br"}
Advogados: Nelson José Alves (OAB/SP 398.875) e Annie Lemos Alves Correa (OAB/SP 446.942)

MÓDULOS DO SISTEMA:
${MANUAL_RESUMIDO.modulos.filter(m=>m&&m.titulo).map(m=>m.titulo+": "+m.resumo).join("\n")}

PRODUTOS E TESES JURÍDICAS CADASTRADOS (${(data.produtos||[]).length} produtos):
${produtosCtx||"Nenhum produto cadastrado ainda."}

PROCESSOS ATIVOS: ${(data.processos||[]).length} processos cadastrados
CONTRATOS ATIVOS: ${(data.contratos||[]).length} contratos
CLIENTES: ${(data.clientes||[]).length} clientes cadastrados

FAQ:
${MANUAL_RESUMIDO.faq.map(f=>"P: "+f.p+" R: "+f.r).join("\n")}`;

      const apiKey = data.escritorio?.anthropicApiKey || data.anthropicApiKey || "";
      if (!apiKey) throw new Error("chave_ausente");
      const resp = await fetch(CLAUDE_URL,{
        method:"POST",
        headers:{"Content-Type":"application/json","X-Token":sessionStorage.getItem('ac_token')||''},
        body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:1000,
          system:ctx, messages:[{role:"user",content:q}],
          _apiKey: apiKey})
      });
      if(!resp.ok) throw new Error("API indisponível");
      const d = await resp.json();
      const texto = d.content?.[0]?.text||"Não foi possível obter resposta.";
      setHistorico([...novoHist,{tipo:"ai",texto}]);
    } catch(e) {
      const msg = e.message==="chave_ausente"
        ? "⚠️ Chave API não configurada. Acesse Dados do Escritório → aba Configurações → campo Chave API Anthropic."
        : "⚠️ Assistente IA indisponível: "+e.message;
      setHistorico([...novoHist,{tipo:"ai",texto:msg}]);
    } finally { setCarregando(false); }
  };

  const imprimirManual = () => {
    // Gerar HTML completo como string e abrir nova janela
    const dataHoje = new Date().toLocaleDateString("pt-BR",{day:"numeric",month:"long",year:"numeric"});
    const nomeArq = `Manual_Sistema_Alves_Correa_Advogados_v5.5_${new Date().toISOString().slice(0,10)}`;

    const secaoHTML = (s, idx) => `
      <div class="secao">
        <h3>${s.titulo}</h3>
        ${s.conteudo.split("\n").map(l=>{
          if(!l.trim()) return "<br/>";
          if(l.startsWith("**")&&l.endsWith("**")) return `<h4>${l.replace(/\*\*/g,"")}</h4>`;
          if(l.startsWith("• ")) return `<li>${l.slice(2)}</li>`;
          if(l.match(/^\d+\./)) return `<li>${l.slice(l.indexOf(".")+1).trim()}</li>`;
          return `<p>${l.replace(/\*\*/g,"<strong>").replace(/\*\*/g,"</strong>")}</p>`;
        }).join("")}
      </div>`;

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8"/>
<title>${nomeArq}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Georgia,serif;color:#111;font-size:11pt;line-height:1.6;padding:20mm 0;background:#e8e8e8}
.wrapper{max-width:210mm;margin:0 auto;background:white;padding:20mm 25mm;box-shadow:0 2px 20px rgba(0,0,0,0.15);min-height:297mm}
@media screen{body{padding:10mm;}.wrapper{margin:0 auto 20mm;}}
@page{size:A4;margin:15mm 20mm}
@media print{body{background:white;padding:0}.wrapper{box-shadow:none;padding:0;margin:0;max-width:100%}}
.capa{text-align:center;padding:60mm 0 30mm;page-break-after:always}
.capa h1{font-size:28pt;font-weight:700;margin-bottom:6mm}
.capa .sub{font-size:16pt;font-weight:600;color:#444;margin-bottom:4mm}
.capa .ver{font-size:11pt;color:#888}
.capa .linha{border-bottom:3pt solid #C9A84C;width:60%;margin:8mm auto}
.parte{page-break-before:always;margin-bottom:8mm}
.parte h2{font-size:16pt;font-weight:700;border-left:5pt solid #C9A84C;padding-left:8pt;margin-bottom:6mm;padding-top:2mm}
.secao{margin-bottom:6mm;padding-bottom:4mm;border-bottom:0.5pt solid #ddd}
h3{font-size:13pt;font-weight:700;margin-bottom:3mm;margin-top:4mm;color:#222}
h4{font-size:11pt;font-weight:700;margin-bottom:2mm;margin-top:3mm;color:#333}
p{margin-bottom:2mm;text-align:justify}
li{margin-bottom:1.5mm;margin-left:6mm;list-style:disc}
ol li{list-style:decimal}
br{display:block;margin:1mm 0}
.rodape{text-align:center;font-size:9pt;color:#999;border-top:0.5pt solid #ddd;padding-top:4mm;margin-top:8mm}
.indice{page-break-after:always}
.indice h2{font-size:16pt;font-weight:700;border-left:5pt solid #C9A84C;padding-left:8pt;margin-bottom:6mm}
.indice-item{display:flex;justify-content:space-between;padding:2mm 0;border-bottom:0.5pt dotted #ccc;font-size:11pt}
strong{font-weight:700}
</style>
</head>
<body>
<div class="wrapper">
<div class="capa">
  <div class="linha"></div>
  <h1>Manual do Sistema</h1>
  <p class="sub">Alves &amp; Correa Advogados</p>
  <p class="ver">Sistema Operacional Jurídico · Versão 5.5</p>
  <p class="ver">Gerado em: ${dataHoje}</p>
  <p class="ver" style="margin-top:4mm">Classificação: Uso Interno</p>
  <div class="linha"></div>
</div>

<div class="indice">
  <h2>Índice</h2>
  ${todasPartes.map((parte,pi)=>`
    <div class="indice-item"><strong>${parte.titulo}</strong></div>
    ${(parte.secoes||[]).map(s=>`<div class="indice-item" style="padding-left:10mm;font-size:10pt"><span>${s.titulo}</span></div>`).join("")}
  `).join("")}
</div>

${todasPartes.map((parte,pi)=>`
  <div class="parte">
    <h2>${parte.titulo}</h2>
    ${(parte.secoes||[]).map((s,si)=>secaoHTML(s,si)).join("")}
  </div>
`).join("")}

<div class="rodape">
  Alves &amp; Correa Advogados · Sistema Operacional Jurídico v5.5 · ${dataHoje} · Uso Interno
</div>
</div>
</body>
</html>`;

    const jan = window.open("","_blank");
    jan.document.write(html);
    jan.document.title = nomeArq;
    jan.document.close();
    setTimeout(()=>jan.print(), 600);
  };

  const renderLinha = (linha, i) => {
    if(!linha.trim()) return <div key={i} style={{height:6}}/>;
    if(linha.startsWith("**")&&linha.endsWith("**"))
      return <p key={i} style={{fontWeight:700,color:theme.gray900,marginBottom:4,marginTop:12,fontSize:14}}>{linha.replace(/\*\*/g,"")}</p>;
    if(linha.startsWith("• "))
      return <div key={i} style={{display:"flex",gap:8,marginBottom:4}}>
        <span style={{color:theme.gold,flexShrink:0,fontWeight:700}}>•</span>
        <span style={{fontSize:13,color:theme.gray700,lineHeight:1.6}}>{linha.slice(2)}</span>
      </div>;
    if(linha.match(/^\d+\./))
      return <div key={i} style={{display:"flex",gap:10,marginBottom:5}}>
        <span style={{color:theme.gold,fontWeight:700,flexShrink:0,minWidth:22}}>{linha.split(".")[0]}.</span>
        <span style={{fontSize:13,color:theme.gray700,lineHeight:1.6}}>{linha.slice(linha.indexOf(".")+1).trim()}</span>
      </div>;
    return <p key={i} style={{fontSize:13,color:theme.gray700,lineHeight:1.6,marginBottom:3}}>{linha}</p>;
  };

  return (
    <div className="fade-in">
      {/* CSS impressão */}
      <style>{MANUAL_PRINT_CSS}</style>

      {/* ── CABEÇALHO ── */}
      <div style={{marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div>
            <h1 className="serif" style={{fontSize:24,fontWeight:700,color:theme.gray900}}>📖 Manual do Sistema</h1>
            <p style={{fontSize:13,color:theme.gray500,marginTop:4}}>Alves & Correa Advogados · Sistema Operacional Jurídico v5.5</p>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <Btn variant="secondary" onClick={()=>{setAbaChat(!abaChat);}} style={{fontSize:12}}>
              {abaChat?"📖 Ver Manual":"🤖 Assistente IA"}
            </Btn>
            <Btn onClick={imprimirManual} style={{fontSize:12}}>🖨️ Gerar PDF A4</Btn>
          </div>
        </div>

        {/* Barra de busca */}
        <div style={{position:"relative"}}>
          <div style={{display:"flex",gap:10,alignItems:"center",padding:"12px 18px",background:theme.white,border:`2px solid ${buscaAtiva?theme.gold:theme.gray200}`,borderRadius:12,boxShadow:"0 2px 8px rgba(0,0,0,0.05)",transition:"border-color .2s"}}>
            <span style={{fontSize:18,flexShrink:0}}>🔍</span>
            <input ref={buscaRef} value={busca} onChange={e=>{setBusca(e.target.value);setBuscaAtiva(true);}}
              onFocus={()=>setBuscaAtiva(true)} onBlur={()=>setTimeout(()=>setBuscaAtiva(false),200)}
              placeholder="Pesquisar no manual... ex: procuração, Score de Risco, PER/DCOMP, tarefas, proposta, WhatsApp..."
              style={{flex:1,border:"none",outline:"none",fontSize:14,fontFamily:"Jost,sans-serif",color:theme.gray800,background:"transparent"}}
            />
            {busca&&<button onClick={()=>{setBusca("");setBuscaAtiva(false);}} style={{background:"none",border:"none",fontSize:16,color:theme.gray400,cursor:"pointer"}}>✕</button>}
          </div>
          {buscaAtiva && busca.length>=2 && (
            <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:9999,background:theme.white,borderRadius:10,boxShadow:"0 8px 32px rgba(0,0,0,0.15)",border:`1px solid ${theme.gray100}`,overflow:"hidden"}}>
              {resultadosBusca.length===0
                ? <div style={{padding:"14px 18px",fontSize:13,color:theme.gray400,textAlign:"center"}}>Nenhum resultado para "{busca}"</div>
                : <>
                    <div style={{padding:"7px 16px",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,background:theme.gray50,borderBottom:`1px solid ${theme.gray100}`}}>
                      {resultadosBusca.length} resultado{resultadosBusca.length!==1?"s":""}
                    </div>
                    {resultadosBusca.map((r,i)=>(
                      <div key={i} onMouseDown={()=>irPara(r)} style={{display:"flex",gap:10,padding:"11px 16px",cursor:"pointer",borderBottom:`1px solid ${theme.gray100}`,transition:"background .1s"}}
                        onMouseEnter={e=>e.currentTarget.style.background=`${theme.gold}08`}
                        onMouseLeave={e=>e.currentTarget.style.background=theme.white}>
                        <Badge color={r.tipo==="Módulo"?"blue":r.tipo==="FAQ"?"amber":"green"} style={{fontSize:9,flexShrink:0,alignSelf:"flex-start",marginTop:2}}>{r.tipo}</Badge>
                        <div style={{flex:1,minWidth:0}}>
                          <p style={{fontSize:13,fontWeight:600,color:theme.gray900,marginBottom:2}}>{r.titulo}</p>
                          <p style={{fontSize:11,color:theme.gray500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.trecho}</p>
                        </div>
                        <span style={{fontSize:12,color:theme.gold,flexShrink:0,alignSelf:"center",fontWeight:700}}>→</span>
                      </div>
                    ))}
                  </>
              }
            </div>
          )}
        </div>

        {/* Toggle de modo */}
        {!abaChat && (
          <div style={{display:"flex",gap:4,marginTop:14,background:theme.gray100,borderRadius:10,padding:3,width:"fit-content"}}>
            {[["resumido","⚡ Consulta Rápida"],["completo","📚 Manual Completo"]].map(([id,lbl])=>(
              <button key={id} onClick={()=>setModo(id)} style={{
                padding:"8px 20px",borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:"Jost,sans-serif",
                fontWeight:modo===id?700:400,border:"none",
                background:modo===id?theme.white:"transparent",
                color:modo===id?theme.gray900:theme.gray500,
                boxShadow:modo===id?"0 1px 4px rgba(0,0,0,0.1)":"none",
                transition:"all .15s"
              }}>{lbl}</button>
            ))}
          </div>
        )}
      </div>

      {/* ── ASSISTENTE IA ── */}
      {abaChat&&(
        <div>
          <Card style={{padding:"16px 20px",marginBottom:14,background:`linear-gradient(135deg,${theme.gray900},${theme.gray800})`}}>
            <div style={{display:"flex",gap:12,alignItems:"center"}}>
              <span style={{fontSize:26}}>🤖</span>
              <div>
                <p className="serif" style={{fontSize:15,fontWeight:700,color:"#fff"}}>Assistente IA do Sistema</p>
                <p style={{fontSize:11,color:"rgba(255,255,255,.6)",marginTop:2}}>Pergunte sobre qualquer funcionalidade, módulo ou fluxo do sistema.</p>
              </div>
            </div>
          </Card>
          {historico.length===0&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
              {["Como aprovar uma proposta?","Como gerar a Procuração?","O que é o Score de Risco?","Como funciona a Conclusão automática?","Como enviar WhatsApp pelo sistema?","O que é MC — Margem de Contribuição?"].map((s,i)=>(
                <button key={i} onClick={()=>{setPergunta(s);inputRef.current?.focus();}} style={{padding:"9px 12px",borderRadius:8,textAlign:"left",fontSize:12,border:`1px solid ${theme.gray200}`,background:theme.white,cursor:"pointer",color:theme.gray700,lineHeight:1.4,fontFamily:"Jost,sans-serif"}}>💬 {s}</button>
              ))}
            </div>
          )}
          {historico.length>0&&(
            <Card style={{padding:"14px 18px",marginBottom:12,maxHeight:380,overflowY:"auto"}}>
              {historico.map((h,i)=>(
                <div key={i} style={{display:"flex",gap:10,marginBottom:12,flexDirection:h.tipo==="user"?"row-reverse":"row"}}>
                  <div style={{width:28,height:28,borderRadius:"50%",flexShrink:0,background:h.tipo==="user"?theme.gold:theme.gray900,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12}}>
                    {h.tipo==="user"?"👤":"🤖"}
                  </div>
                  <div style={{maxWidth:"82%",padding:"10px 14px",borderRadius:10,background:h.tipo==="user"?`${theme.gold}15`:theme.gray50,border:`1px solid ${h.tipo==="user"?`${theme.gold}33`:theme.gray100}`}}>
                    <p style={{fontSize:13,color:theme.gray800,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{h.texto}</p>
                  </div>
                </div>
              ))}
              {carregando&&<div style={{display:"flex",gap:10}}><div style={{width:28,height:28,borderRadius:"50%",background:theme.gray900,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12}}>🤖</div><div style={{padding:"10px 14px",borderRadius:10,background:theme.gray50,border:`1px solid ${theme.gray100}`}}><p style={{fontSize:12,color:theme.gray400}}>Analisando...</p></div></div>}
            </Card>
          )}
          <Card style={{padding:"12px 14px"}}>
            <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
              <textarea ref={inputRef} value={pergunta} onChange={e=>setPergunta(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();perguntarIA();}}}
                placeholder="Digite sua pergunta... (Enter para enviar, Shift+Enter = nova linha)"
                rows={2} style={{flex:1,padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:8,fontSize:13,resize:"none",outline:"none",fontFamily:"Jost,sans-serif"}}
                onFocus={e=>e.target.style.borderColor=theme.gold} onBlur={e=>e.target.style.borderColor=theme.gray200}
              />
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                <Btn onClick={perguntarIA} disabled={!pergunta.trim()||carregando}>{carregando?"⏳":"↑ Enviar"}</Btn>
                {historico.length>0&&<Btn size="sm" variant="secondary" onClick={()=>setHistorico([])}>🗑️</Btn>}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ── MODO RESUMIDO ── */}
      {!abaChat&&modo==="resumido"&&(
        <div>
          {/* Visão geral */}
          <Card style={{padding:"20px 24px",marginBottom:16,background:`linear-gradient(135deg,${theme.gray900},${theme.gray800})`}}>
            <p className="serif" style={{fontSize:18,fontWeight:700,color:"#fff",marginBottom:8}}>🏛️ Visão Geral</p>
            {MANUAL_RESUMIDO.visaoGeral.split("\n").map((l,i)=>{
              if(!l.trim()) return <div key={i} style={{height:4}}/>;
              if(l.startsWith("**")&&l.endsWith("**")) return <p key={i} style={{fontWeight:700,color:theme.gold,marginBottom:2,fontSize:12}}>{l.replace(/\*\*/g,"")}</p>;
              return <p key={i} style={{fontSize:12,color:"rgba(255,255,255,.8)",lineHeight:1.6,marginBottom:2}}>{l}</p>;
            })}
          </Card>

          {/* Grid de módulos */}
          <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:12}}>📚 Módulos do Sistema</p>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:10,marginBottom:24}}>
            {MANUAL_RESUMIDO.modulos.map(m=>(
              <Card key={m.id} id={"mod-"+m.id} style={{padding:"14px 16px",border:`1px solid ${theme.gray100}`,transition:"all .15s",cursor:"default"}}>
                <p style={{fontSize:15,fontWeight:700,color:theme.gray900,marginBottom:5}}>{m.titulo}</p>
                <p style={{fontSize:12,color:theme.gray600,lineHeight:1.6}}>{m.resumo}</p>
              </Card>
            ))}
          </div>

          {/* FAQ */}
          <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:12}}>❓ Perguntas Frequentes</p>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {MANUAL_RESUMIDO.faq.map((f,i)=>(
              <Card key={i} id={"faq-"+i} style={{padding:"14px 18px"}}>
                <p style={{fontSize:13,fontWeight:700,color:theme.gray900,marginBottom:6}}>❓ {f.p}</p>
                <p style={{fontSize:13,color:theme.gray700,lineHeight:1.6,background:theme.gray50,padding:"8px 12px",borderRadius:7,borderLeft:`3px solid ${theme.gold}`}}>{f.r}</p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ── MODO COMPLETO ── */}
      {!abaChat&&modo==="completo"&&(
        <div>
          {/* Capa do manual completo */}
          <div style={{textAlign:"center",padding:"32px 0 24px",borderBottom:`3px solid ${theme.gold}`,marginBottom:28}}>
            <p style={{fontSize:36,marginBottom:10}}>📚</p>
            <p className="serif" style={{fontSize:26,fontWeight:700,color:theme.gray900,marginBottom:4}}>Manual do Sistema</p>
            <p style={{fontSize:15,fontWeight:600,color:theme.gray600,marginBottom:4}}>Alves &amp; Correa Advogados</p>
            <p style={{fontSize:12,color:theme.gray400}}>Sistema Operacional Jurídico v5.5 · Uso Interno</p>
            <div style={{marginTop:14,display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
              {[`${(data.produtos||[]).length} produtos`,`${(data.clientes||[]).length} clientes`,`${(data.contratos||[]).length} contratos`,`${(data.equipe||[]).filter(e=>e.ativo).length} colaboradores`].map(s=>(
                <span key={s} style={{fontSize:12,padding:"3px 12px",borderRadius:20,background:`${theme.gold}18`,color:theme.goldDark,fontWeight:600}}>{s}</span>
              ))}
            </div>
          </div>

          {/* Índice */}
          <Card style={{padding:"20px 24px",marginBottom:24}}>
            <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:14}}>📋 Índice</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px 24px"}}>
              {todasPartes.map((parte,pi)=>
                (parte.secoes||[]).map((s,si)=>(
                  <div key={pi+"-"+si} style={{display:"flex",alignItems:"center",gap:8,padding:"3px 0",borderBottom:`1px solid ${theme.gray100}`,cursor:"pointer"}}
                    onClick={()=>{setTimeout(()=>{const el=document.getElementById("sec-"+pi+"-"+si);if(el)el.scrollIntoView({behavior:"smooth",block:"start"});},100);}}>
                    <span style={{fontSize:11,color:theme.gray400,minWidth:18}}>{pi+1}.{si+1}</span>
                    <span style={{fontSize:12,color:theme.blue}}>{s.titulo}</span>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Partes e seções */}
          {todasPartes.map((parte,pi)=>(
            <div key={pi} style={{marginBottom:32}}>
              <div style={{padding:"10px 20px",background:theme.gray900,color:theme.gold,borderRadius:8,marginBottom:16,fontSize:15,fontWeight:700,letterSpacing:.5}}>
                {parte.titulo}
              </div>
              {(parte.secoes||[]).map((s,si)=>(
                <Card key={si} id={"sec-"+pi+"-"+si} style={{padding:"22px 26px",marginBottom:12}}>
                  <p className="serif" style={{fontSize:17,fontWeight:700,color:theme.gray900,marginBottom:8}}>{s.titulo}</p>
                  <div style={{width:36,height:3,background:theme.gold,borderRadius:2,marginBottom:16}}/>
                  {s.conteudo.split("\n").map(renderLinha)}
                </Card>
              ))}
            </div>
          ))}

          {/* Rodapé */}
          <div style={{textAlign:"center",padding:"16px 0",color:theme.gray400,fontSize:11,borderTop:`1px solid ${theme.gray100}`,marginTop:8}}>
            Alves & Correa Advogados · Sistema Operacional Jurídico v5.5 · Uso Interno
          </div>
        </div>
      )}
    </div>
  );
}



function ModMarketing({ data, setData }) {
  const mkt      = data.marketing || {};
  const redesSociais        = mkt.redesSociais        || [];
  const todosAgentes        = data.agentes || [];  // Cadastro central
  const idsMkt              = mkt.agentes || [];   // IDs selecionados para Marketing
  const agentes             = todosAgentes.filter(a => idsMkt.includes(a.id));
  const [modalSelAgMkt, setModalSelAgMkt] = React.useState(false);
  const calendarioEditorial = mkt.calendarioEditorial || [];
  const ideias              = mkt.ideias              || [];
  const campanhas           = mkt.campanhas           || [];
  const metricas            = mkt.metricas            || [];
  const produtos            = data.produtos           || [];
  const equipe              = data.equipe             || [];

  const atendimentos = mkt.atendimentos || [];

  const [aba,        setAba]        = useState("dashboard");
  const [modalCal,   setModalCal]   = useState(false);
  const [modalIdeia, setModalIdeia] = useState(false);
  const [modalCamp,  setModalCamp]  = useState(false);
  const [modalRS,    setModalRS]    = useState(false);
  const [modalAg,    setModalAg]    = useState(false);
  const [modalAtend, setModalAtend] = useState(false);
  const [editCal,    setEditCal]    = useState(null);
  const [editIdeia,  setEditIdeia]  = useState(null);
  const [editCamp,   setEditCamp]   = useState(null);
  const [editRS,     setEditRS]     = useState(null);
  const [editAg,     setEditAg]     = useState(null);
  const [editAtend,  setEditAtend]  = useState(null);
  const [promptSel,  setPromptSel]  = useState("");
  const [copiado,    setCopiado]    = useState(false);
  const [filtCalMes, setFiltCalMes] = useState(new Date().toISOString().slice(0,7));
  const [filtCalPlat,setFiltCalPlat]= useState("");
  const [filtStatus, setFiltStatus] = useState("");
  const [filtCanal,  setFiltCanal]  = useState("");

  const PLATAFORMAS = ["Instagram","Facebook","LinkedIn","YouTube","TikTok","X (Twitter)"];
  const ICON_PLAT   = { Instagram:"📸", Facebook:"👥", LinkedIn:"💼", YouTube:"▶️", TikTok:"🎵", "X (Twitter)":"𝕏" };
  const COR_PLAT    = { Instagram:"#E1306C", Facebook:"#1877F2", LinkedIn:"#0A66C2", YouTube:"#FF0000", TikTok:"#010101", "X (Twitter)":"#14171A" };
  const TIPO_CONT   = ["Post","Reels / Vídeo","Stories","Artigo","Newsletter","Carrossel","Thread"];
  const STATUS_CAL  = ["Ideia","Produção","Revisão","Agendado","Publicado","Cancelado"];
  const COR_STATUS  = { Ideia:theme.gray400, Produção:theme.blue, Revisão:theme.amber, Agendado:theme.gold, Publicado:theme.green, Cancelado:theme.red };

  const setMkt = (campo, valor) => setData(prev => ({ ...prev, marketing: { ...(prev.marketing||{}), [campo]: valor } }));

  const PROMPTS = [
    { label:"Campanha completa", texto: `Você é especialista em marketing jurídico. Crie uma campanha completa para o escritório Alves & Correa Advogados para o produto/serviço [PRODUTO], incluindo: objetivo da campanha, público-alvo, mensagem principal, posts para Instagram, LinkedIn e Facebook (3 de cada), sugestão de hashtags, e calendário de publicação para 30 dias. Tom de voz: profissional, didático e acessível.` },
    { label:"Posts para Instagram", texto:`Crie 5 posts para Instagram para o escritório Alves & Correa Advogados sobre [TEMA JURÍDICO]. Cada post deve ter: legenda completa (até 2200 caracteres), 10 hashtags relevantes, sugestão de imagem/arte, e chamada para ação (CTA). Tom: profissional mas acessível para leigos.` },
    { label:"Posts para LinkedIn", texto:`Crie 3 artigos curtos para LinkedIn sobre [TEMA JURÍDICO] para o escritório Alves & Correa Advogados. Perfil do público: empresários, contadores e gestores financeiros. Inclua: título impactante, introdução, desenvolvimento (3 pontos), conclusão e CTA. Máximo 1300 caracteres cada.` },
    { label:"Calendário editorial", texto:`Crie um calendário editorial completo para [MÊS/ANO] para o escritório Alves & Correa Advogados. Inclua publicações para Instagram, LinkedIn e Facebook. Distribua conteúdos sobre: recuperação de créditos tributários, planejamento tributário, direito previdenciário e direito empresarial. Formato de tabela com: data, plataforma, tipo de conteúdo, tema, legenda resumida e hashtags.` },
    { label:"Ideias de conteúdo", texto:`Sugira 20 ideias de conteúdo para redes sociais do escritório Alves & Correa Advogados, focando em [ÁREA JURÍDICA]. Para cada ideia inclua: título, formato sugerido (post, reels, carrossel), plataforma ideal e por que esse conteúdo gera engajamento. Foco em educação jurídica para leigos.` },
    { label:"Análise de métricas", texto:`Analise os seguintes resultados de redes sociais do escritório Alves & Correa Advogados e sugira melhorias: [COLE AQUI OS DADOS DE MÉTRICAS]. Responda: quais conteúdos performaram melhor e por quê, o que deve ser replicado, o que deve ser abandonado, e sugestões práticas para os próximos 30 dias.` },
    { label:"E-mail marketing", texto:`Crie uma newsletter mensal para clientes e prospects do escritório Alves & Correa Advogados sobre [TEMA]. Inclua: assunto (3 opções de subject line), abertura personalizada, seção de novidades tributárias, dica prática do mês, caso de sucesso (fictício ou anonimizado), e CTA para agendamento de consulta. Máximo 500 palavras.` },
  ];

  const copiarPrompt = () => {
    if (!promptSel) return;
    navigator.clipboard.writeText(promptSel).then(() => { setCopiado(true); setTimeout(()=>setCopiado(false),2500); });
  };

  // ── LUMI + RAVI — geração de conteúdo integrada (Fase 3, item 2) ──
  const [gerandoConteudo, setGerandoConteudo] = useState(false);
  const gerarConteudoIA = async () => {
    if (!editCal.titulo) return alert("Preencha o Título/Tema antes de gerar com IA.");
    setGerandoConteudo(true);
    try {
      const prod = produtos.find(p=>p.id===editCal.produtoId);
      const res = await fetch(`${API_BASE}/marketing/gerar_conteudo.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Token': sessionStorage.getItem('ac_token')||'' },
        body: JSON.stringify({ titulo: editCal.titulo, plataforma: editCal.plataforma, tipo: editCal.tipo,
          descricao: editCal.descricao, produtoNome: prod?.nome||'' })
      });
      const json = await res.json();
      if (!json.ok) { alert('Falha ao gerar conteúdo: ' + (json.erro||'erro desconhecido')); return; }
      setEditCal(prev => ({ ...prev,
        legenda: json.dados.legenda || '',
        hashtags: (json.dados.hashtags||[]).join(' '),
        roteiro: json.dados.roteiro || prev.roteiro,
        complianceOAB: null // conteúdo mudou — precisa validar de novo
      }));
    } catch { alert('Falha de conexão ao gerar conteúdo.'); }
    setGerandoConteudo(false);
  };

  // ── LEXA — Compliance OAB (Fase 3, item 1) — etapa obrigatória antes de "Publicado" ──
  const [validandoOAB, setValidandoOAB] = useState(false);
  const validarComplianceOAB = async () => {
    const texto = editCal.legenda || editCal.descricao;
    if (!texto) return alert("Não há texto para validar — preencha a legenda ou a descrição.");
    setValidandoOAB(true);
    try {
      const res = await fetch(`${API_BASE}/marketing/validate-oab.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Token': sessionStorage.getItem('ac_token')||'' },
        body: JSON.stringify({ texto })
      });
      const json = await res.json();
      if (!json.ok) { alert('Falha ao validar: ' + (json.erro||'erro desconhecido')); return; }
      setEditCal(prev => ({ ...prev, complianceOAB: { ...json.dados, validadoEm: new Date().toISOString() } }));
    } catch { alert('Falha de conexão ao validar compliance OAB.'); }
    setValidandoOAB(false);
  };

  // ── NEO + ADA — Campanhas Pagas via Windsor.ai (Fase 4) ──────────────
  const usuarioLogado = useUsuarioAtual();
  const podeExecutarNEO = ['Administrador','Sócio'].includes(usuarioLogado?.perfil);
  const CONECTORES_ADA = [
    { id:'facebook',           label:'📘 Meta Ads' },
    { id:'google_ads',         label:'🔍 Google Ads' },
    { id:'google_analytics_4', label:'📊 GA4' },
    { id:'instagram_insights', label:'📸 Instagram' },
  ];
  const [adaConector, setAdaConector]     = useState('facebook');
  const [adaDados, setAdaDados]           = useState(null);
  const [adaCarregando, setAdaCarregando] = useState(false);
  const [adaErro, setAdaErro]             = useState('');
  const [adaPeriodo, setAdaPeriodo]       = useState('last_30d');
  const contasWindsor = mkt.contasWindsor || {};
  const adaAccount = contasWindsor['ada_'+adaConector] || '';
  const setAdaAccount = (v) => setMkt('contasWindsor', { ...contasWindsor, ['ada_'+adaConector]: v });
  const ADA_PERIODOS = [
    { id:'last_7d',   label:'Últimos 7 dias' },
    { id:'last_30d',  label:'Últimos 30 dias' },
    { id:'last_90d',  label:'Últimos 90 dias' },
    { id:'this_month',label:'Este mês' },
    { id:'last_month',label:'Mês passado' },
    { id:'this_year', label:'Este ano' },
    { id:'last_year', label:'Ano passado' },
    { id:'all_time',  label:'Todo o período' },
  ];

  const carregarADA = () => {
    setAdaCarregando(true); setAdaErro('');
    const qsAccount = adaAccount ? `&account=${encodeURIComponent(adaAccount)}` : '';
    fetch(`${API_BASE}/marketing/windsor_read.php?connector=${adaConector}&date_preset=${adaPeriodo}${qsAccount}`, {
      headers: { 'X-Token': sessionStorage.getItem('ac_token')||'' }
    }).then(r=>r.json()).then(res=>{
      if (!res.ok) { setAdaErro(res.erro||'Falha ao carregar dados.'); return; }
      // Windsor.ai retorna {data:[...]} — normaliza pra sempre trabalhar com array puro
      const linhas = Array.isArray(res.dados) ? res.dados : (res.dados?.data || []);
      setAdaDados(linhas);
    }).catch(()=>setAdaErro('Falha de conexão com o Windsor.ai.'))
      .finally(()=>setAdaCarregando(false));
  };

  const [neoConector, setNeoConector]   = useState('google_ads');
  const neoAccount = contasWindsor['neo_'+neoConector] || '';
  const setNeoAccount = (v) => setMkt('contasWindsor', { ...contasWindsor, ['neo_'+neoConector]: v });
  const [neoAcoes, setNeoAcoes]         = useState([]);
  const [neoAcaoId, setNeoAcaoId]       = useState('');
  const [neoParamsJson, setNeoParamsJson] = useState('{}');
  const [neoFrase, setNeoFrase]         = useState('');
  const [neoExecutando, setNeoExecutando] = useState(false);
  const [neoResultado, setNeoResultado] = useState(null);
  const [neoErro, setNeoErro]           = useState('');

  const carregarAcoesNEO = () => {
    setNeoErro(''); setNeoAcoes([]); setNeoAcaoId('');
    fetch(`${API_BASE}/marketing/windsor_actions.php?connector=${neoConector}`, {
      headers: { 'X-Token': sessionStorage.getItem('ac_token')||'' }
    }).then(r=>r.json()).then(res=>{
      if (!res.ok) { setNeoErro(res.erro||'Falha ao listar ações.'); return; }
      setNeoAcoes(res.dados||[]);
    }).catch(()=>setNeoErro('Falha de conexão com o Windsor.ai.'));
  };

  const executarNEO = () => {
    if (neoFrase.trim() !== 'CONFIRMO A EXECUCAO') {
      alert('Digite exatamente "CONFIRMO A EXECUCAO" (sem acento) para confirmar.');
      return;
    }
    let paramsObj;
    try { paramsObj = JSON.parse(neoParamsJson || '{}'); }
    catch { alert('Parâmetros inválidos — deve ser um JSON válido.'); return; }

    if (!confirm(`⚠️ Isso vai executar "${neoAcaoId}" na conta ${neoAccount} (${neoConector}) — ação real, envolve dinheiro/campanha ativa. Confirma?`)) return;

    setNeoExecutando(true); setNeoResultado(null); setNeoErro('');
    fetch(`${API_BASE}/marketing/windsor_actions.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Token': sessionStorage.getItem('ac_token')||'' },
      body: JSON.stringify({ connector: neoConector, account: neoAccount, action: neoAcaoId, params: paramsObj, frase_confirmacao: neoFrase.trim() })
    }).then(r=>r.json()).then(res=>{
      if (!res.ok) { setNeoErro(res.erro||'Falha ao executar.'); return; }
      setNeoResultado(res.dados);
      setNeoFrase('');
    }).catch(()=>setNeoErro('Falha de conexão ao executar ação.'))
      .finally(()=>setNeoExecutando(false));
  };

  const saveCal = () => {
    if (!editCal.titulo || !editCal.data) return alert("Título e data são obrigatórios.");
    if (editCal.status === "Publicado" && !editCal.complianceOAB?.aprovado) {
      return alert("🛡️ Compliance OAB obrigatório: valide o conteúdo com a LEXA (e resolva os alertas) antes de marcar como Publicado.");
    }
    const id = editCal.id || "CAL"+Date.now().toString().slice(-6);
    const novo = { ...editCal, id };
    setMkt("calendarioEditorial", editCal.id
      ? calendarioEditorial.map(x => x.id===id?novo:x)
      : [...calendarioEditorial, novo]);
    setModalCal(false);
  };

  const saveIdeia = () => {
    if (!editIdeia.titulo) return alert("Título é obrigatório.");
    const id = editIdeia.id || "IDI"+Date.now().toString().slice(-6);
    const novo = { ...editIdeia, id };
    setMkt("ideias", editIdeia.id ? ideias.map(x=>x.id===id?novo:x) : [...ideias,novo]);
    setModalIdeia(false);
  };

  const saveCamp = () => {
    if (!editCamp.nome) return alert("Nome da campanha é obrigatório.");
    const id = editCamp.id || "CMP"+Date.now().toString().slice(-6);
    const novo = { ...editCamp, id };
    setMkt("campanhas", editCamp.id ? campanhas.map(x=>x.id===id?novo:x) : [...campanhas,novo]);
    setModalCamp(false);
  };

  const saveRS = () => {
    const id = editRS.id || "RS"+Date.now().toString().slice(-6);
    const novo = { ...editRS, id };
    setMkt("redesSociais", editRS.id ? redesSociais.map(x=>x.id===id?novo:x) : [...redesSociais,novo]);
    setModalRS(false);
  };

  const saveAg = () => {
    if (!editAg.nome || !editAg.link) return alert("Nome e link são obrigatórios.");
    const id = editAg.id || "AG"+Date.now().toString().slice(-6);
    const novo = { ...editAg, id };
    setMkt("agentes", editAg.id ? agentes.map(x=>x.id===id?novo:x) : [...agentes,novo]);
    setModalAg(false);
  };

  const saveAtend = () => {
    if (!editAtend.nomeContato && !editAtend.canal) return alert("Informe ao menos o canal ou o nome do contato.");
    const id = editAtend.id || "ATD"+Date.now().toString().slice(-6);
    const novo = { ...editAtend, id };
    setMkt("atendimentos", editAtend.id ? atendimentos.map(x=>x.id===id?novo:x) : [...atendimentos, novo]);
    setModalAtend(false);
  };

  const remover = (campo, id) => {
    if (!confirm("Remover este item?")) return;
    const listas = { calendarioEditorial, ideias, campanhas, agentes, atendimentos };
    setMkt(campo, listas[campo].filter(x=>x.id!==id));
  };

  const calFiltrado = calendarioEditorial
    .filter(x => (!filtCalMes||x.data?.slice(0,7)===filtCalMes) && (!filtCalPlat||x.plataforma===filtCalPlat))
    .sort((a,b)=>a.data?.localeCompare(b.data));

  const totCamp = campanhas.reduce((s,c)=>s+(parseFloat(c.orcamento)||0),0);
  const totLead = campanhas.reduce((s,c)=>s+(parseInt(c.leads)||0),0);
  const pubMes  = calendarioEditorial.filter(x=>x.data?.slice(0,7)===new Date().toISOString().slice(0,7)&&x.status==="Publicado").length;
  const agendMes= calendarioEditorial.filter(x=>x.data?.slice(0,7)===new Date().toISOString().slice(0,7)&&x.status==="Agendado").length;

  const CANAIS_ORIG = ["WhatsApp","Instagram DM","Facebook Messenger","LinkedIn","Site/Formulário","Indicação","E-mail","Telefone","Outro"];
  const STATUS_ATEND = ["Novo","Em atendimento","Qualificado","Proposta enviada","Convertido","Descartado"];
  const COR_ATEND = { Novo:theme.blue, "Em atendimento":theme.amber, Qualificado:theme.gold, "Proposta enviada":theme.purple||"#7c3aed", Convertido:theme.green, Descartado:theme.red };

  const leadsPorCanal = CANAIS_ORIG.map(canal=>({
    canal,
    total: atendimentos.filter(a=>a.canal===canal).length,
  })).filter(x=>x.total>0).sort((a,b)=>b.total-a.total);

  // ── Bloco C: KPIs de Conversão e ROI ──────────────────────────────────
  const totalLeads       = atendimentos.length;
  const leadsConvertidos = atendimentos.filter(a=>a.status==="Convertido").length;
  const txConvLeads      = totalLeads>0 ? Math.round(leadsConvertidos/totalLeads*100) : 0;

  const roiCampanhas = campanhas
    .filter(c=>c.orcamento>0)
    .map(c=>{
      const orcamento = parseFloat(c.orcamento)||0;
      const receita   = parseFloat(c.receitaGerada||0);
      const roi       = orcamento>0 ? Math.round((receita-orcamento)/orcamento*100) : null;
      return { nome: c.nome, orcamento, receita, roi };
    })
    .filter(c=>c.roi!==null)
    .sort((a,b)=>b.roi-a.roi)
    .slice(0,6);
  const totalInvestido   = campanhas.reduce((s,c)=>s+(parseFloat(c.orcamento)||0),0);
  const totalRecGerada   = campanhas.reduce((s,c)=>s+(parseFloat(c.receitaGerada||0)),0);
  const roiGlobal        = totalInvestido>0 ? Math.round((totalRecGerada-totalInvestido)/totalInvestido*100) : null;
  // ─────────────────────────────────────────────────────────────────────

  const ABAS = [
    ["dashboard","🏠 Dashboard"],
    ["redesSociais","🌐 Redes Sociais"],
    ["calendario","📅 Calendário Editorial"],
    ["ideias","💡 Ideias"],
    ["campanhas","💰 Campanhas"],
    ["agentes","🤖 Agentes IA"],
    ["neoada","📈 NEO + ADA (Campanhas Pagas)"],
  ];

  return (
    <div className="fade-in">
      <PageHeader title="Marketing" subtitle="Gestão de conteúdo, campanhas e agentes de IA para redes sociais" action={
        <div style={{display:"flex",gap:8}}>
          {aba==="calendario"   && <Btn onClick={()=>{setEditCal({id:"",titulo:"",plataforma:"Instagram",tipo:"Post",data:"",status:"Ideia",produtoId:"",responsavel:"",descricao:"",link:""});setModalCal(true);}}>+ Conteúdo</Btn>}
          {aba==="ideias"       && <Btn onClick={()=>{setEditIdeia({id:"",titulo:"",plataforma:"",tipo:"",prioridade:"Média",status:"Nova",produtoId:"",descricao:""});setModalIdeia(true);}}>+ Ideia</Btn>}
          {aba==="campanhas"    && <Btn onClick={()=>{setEditCamp({id:"",nome:"",plataforma:"",origemCanal:"",utmSource:"",utmMedium:"",utmCampaign:"",produtoId:"",dataInicio:"",dataFim:"",orcamento:"",objetivo:"",leads:"",alcance:"",status:"Planejamento",obs:""});setModalCamp(true);}}>+ Campanha</Btn>}
          {aba==="atendimentos" && <Btn onClick={()=>{setEditAtend({id:"",canal:"WhatsApp",nomeContato:"",data:new Date().toISOString().slice(0,10),campanhaId:"",status:"Novo",obs:""});setModalAtend(true);}}>+ Atendimento</Btn>}
          {aba==="agentes"      && <Btn onClick={()=>{setEditAg({id:"",nome:"",descricao:"",icon:"🤖",link:"",ativo:true});setModalAg(true);}}>+ Agente</Btn>}
        </div>
      }/>

      {/* Abas */}
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:`2px solid ${theme.gray100}`,paddingBottom:0}}>
        {ABAS.map(([id,lbl])=>(
          <button key={id} onClick={()=>setAba(id)} style={{
            padding:"10px 16px",fontSize:12,fontWeight:aba===id?700:400,cursor:"pointer",
            border:"none",background:"transparent",
            color:aba===id?theme.gold:theme.gray500,
            borderBottom:aba===id?`2px solid ${theme.gold}`:"2px solid transparent",
            marginBottom:-2,transition:"color .15s",fontFamily:"Jost,sans-serif"
          }}>{lbl}</button>
        ))}
      </div>

      {/* ======= DASHBOARD ======= */}
      {aba==="dashboard" && (
        <div>
          {/* KPIs */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:14,marginBottom:24}}>
            {[
              {l:"Redes Ativas",    v:redesSociais.filter(r=>r.status==="Ativo").length,                                     icon:"🌐", c:theme.green},
              {l:"Publicados/mês",  v:pubMes,                                                                                icon:"✅", c:theme.blue},
              {l:"Agendados/mês",   v:agendMes,                                                                              icon:"📆", c:theme.gold},
              {l:"Ideias banco",    v:ideias.filter(x=>x.status==="Nova").length,                                            icon:"💡", c:theme.amber},
              {l:"Atendimentos",    v:atendimentos.filter(a=>a.status==="Novo"||a.status==="Em atendimento").length,         icon:"📲", c:theme.blue},
            ].map(k=>(
              <Card key={k.l} style={{padding:"14px 18px"}}>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{k.l}</p>
                  <span style={{fontSize:16}}>{k.icon}</span>
                </div>
                <p style={{fontSize:26,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
              </Card>
            ))}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:24}}>
            {/* Prompt Generator */}
            <Card style={{padding:"20px 24px"}}>
              <p className="serif" style={{fontSize:17,fontWeight:700,color:theme.gray900,marginBottom:4}}>🤖 Gerador de Prompts</p>
              <p style={{fontSize:12,color:theme.gray500,marginBottom:16}}>Selecione um modelo, personalize e copie para usar no agente de IA.</p>
              <select onChange={e=>setPromptSel(e.target.value)} style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,marginBottom:10,outline:"none"}}>
                <option value="">Escolha um modelo de prompt...</option>
                {PROMPTS.map((p,i)=><option key={i} value={p.texto}>{p.label}</option>)}
              </select>
              {promptSel && (
                <>
                  <textarea value={promptSel} onChange={e=>setPromptSel(e.target.value)}
                    style={{width:"100%",minHeight:140,padding:"10px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,resize:"vertical",fontFamily:"Jost,sans-serif",color:theme.gray800,boxSizing:"border-box",outline:"none"}}/>
                  <div style={{display:"flex",gap:8,marginTop:8}}>
                    <Btn onClick={copiarPrompt} style={{flex:1}}>
                      {copiado?"✅ Copiado!":"📋 Copiar Prompt"}
                    </Btn>
                    <a href="https://claude.ai" target="_blank" rel="noreferrer">
                      <Btn variant="secondary">Abrir Claude ↗</Btn>
                    </a>
                  </div>
                </>
              )}
            </Card>

            {/* Agentes rápidos */}
            <Card style={{padding:"20px 24px"}}>
              <p className="serif" style={{fontSize:17,fontWeight:700,color:theme.gray900,marginBottom:4}}>⚡ Acesso Rápido — Agentes</p>
              <p style={{fontSize:12,color:theme.gray500,marginBottom:16}}>Clique para abrir o agente diretamente.</p>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {agentes.filter(a=>a.ativo).slice(0,6).map(a=>(
                  <a key={a.id} href={a.link} target="_blank" rel="noreferrer" style={{textDecoration:"none"}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:8,border:`1px solid ${theme.gray100}`,background:theme.white,cursor:"pointer",transition:"background .15s"}}
                      onMouseEnter={e=>e.currentTarget.style.background=theme.gray50}
                      onMouseLeave={e=>e.currentTarget.style.background=theme.white}>
                      <span style={{fontSize:20,flexShrink:0}}>{a.icon}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <p style={{fontSize:13,fontWeight:600,color:theme.gray900}}>{a.nome}</p>
                        <p style={{fontSize:11,color:theme.gray500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.descricao}</p>
                      </div>
                      <span style={{fontSize:12,color:theme.blue}}>↗</span>
                    </div>
                  </a>
                ))}
              </div>
            </Card>
          </div>

          {/* Status redes sociais */}
          <Card style={{padding:"20px 24px",marginBottom:24}}>
            <p className="serif" style={{fontSize:17,fontWeight:700,color:theme.gray900,marginBottom:16}}>🌐 Status das Redes Sociais</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:12}}>
              {redesSociais.map(r=>(
                <div key={r.id} style={{padding:"14px 16px",borderRadius:10,border:`1px solid ${COR_PLAT[r.plataforma]||theme.gray200}22`,background:`${COR_PLAT[r.plataforma]||theme.gray400}08`,textAlign:"center"}}>
                  <p style={{fontSize:24,marginBottom:6}}>{ICON_PLAT[r.plataforma]||"📱"}</p>
                  <p style={{fontSize:13,fontWeight:700,color:theme.gray900}}>{r.plataforma}</p>
                  <Badge color={r.status==="Ativo"?"green":"gray"} style={{marginTop:6,fontSize:10}}>{r.status}</Badge>
                  {r.perfil&&<p style={{fontSize:10,color:theme.blue,marginTop:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.perfil}</p>}
                </div>
              ))}
            </div>
          </Card>

          {/* Leads por canal de origem */}
          {leadsPorCanal.length > 0 && (
            <Card style={{padding:"20px 24px",marginBottom:24}}>
              <p className="serif" style={{fontSize:17,fontWeight:700,color:theme.gray900,marginBottom:16}}>📲 Atendimentos por Canal de Origem</p>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {leadsPorCanal.map(({canal,total})=>{
                  const maxVal = leadsPorCanal[0].total;
                  const pct    = Math.round((total/maxVal)*100);
                  return (
                    <div key={canal} style={{display:"flex",alignItems:"center",gap:12}}>
                      <span style={{width:140,fontSize:12,color:theme.gray700,flexShrink:0}}>{canal}</span>
                      <div style={{flex:1,background:theme.gray100,borderRadius:99,height:10,overflow:"hidden"}}>
                        <div style={{width:`${pct}%`,height:"100%",background:theme.gold,borderRadius:99,transition:"width .4s"}}/>
                      </div>
                      <span style={{width:30,textAlign:"right",fontSize:12,fontWeight:700,color:theme.gray800}}>{total}</span>
                    </div>
                  );
                })}
              </div>
              <p style={{fontSize:11,color:theme.gray400,marginTop:12}}>Total: {atendimentos.length} atendimentos registrados · {atendimentos.filter(a=>a.status==="Convertido").length} convertidos</p>
            </Card>
          )}

          {/* KPIs de conversão + ROI */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:24}}>
            {/* Taxa de Conversão */}
            <Card style={{padding:"20px 24px"}}>
              <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:16}}>🎯 Funil de Conversão — Leads</p>
              {[
                {label:"Total de Leads",         v:totalLeads,        c:theme.blue,  pct:100},
                {label:"Em Atendimento",          v:atendimentos.filter(a=>a.status==="Em atendimento").length, c:theme.amber, pct:totalLeads>0?Math.round(atendimentos.filter(a=>a.status==="Em atendimento").length/totalLeads*100):0},
                {label:"Convertidos (contratos)", v:leadsConvertidos,  c:theme.green, pct:txConvLeads},
              ].map(row=>(
                <div key={row.label} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:12,color:theme.gray700}}>{row.label}</span>
                    <span style={{fontSize:12,fontWeight:700,color:row.c}}>{row.v} <span style={{color:theme.gray400,fontWeight:400}}>({row.pct}%)</span></span>
                  </div>
                  <div style={{background:theme.gray100,borderRadius:99,height:7,overflow:"hidden"}}>
                    <div style={{width:`${row.pct}%`,height:"100%",background:row.c,borderRadius:99,transition:"width .4s"}}/>
                  </div>
                </div>
              ))}
              <p style={{fontSize:11,color:theme.gray400,marginTop:8}}>
                Taxa de conversão geral: <strong style={{color:theme.green}}>{txConvLeads}%</strong>
              </p>
            </Card>

            {/* ROI por campanha */}
            <Card style={{padding:"20px 24px"}}>
              <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:4}}>💹 ROI por Campanha</p>
              {roiGlobal!==null&&(
                <p style={{fontSize:12,color:theme.gray500,marginBottom:14}}>
                  Investido: <strong>{fmtMoeda(totalInvestido)}</strong> · Receita gerada: <strong>{fmtMoeda(totalRecGerada)}</strong> · ROI global: <strong style={{color:roiGlobal>=0?theme.green:theme.red}}>{roiGlobal}%</strong>
                </p>
              )}
              {roiCampanhas.length===0?(
                <p style={{fontSize:12,color:theme.gray400,paddingTop:8}}>Nenhuma campanha com orçamento e receita registrados.</p>
              ):(
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {roiCampanhas.map(c=>(
                    <div key={c.nome} style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{flex:1,fontSize:12,color:theme.gray800,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.nome}</span>
                      <span style={{fontSize:11,color:theme.gray500,flexShrink:0}}>{fmtMoeda(c.orcamento)}</span>
                      <span style={{
                        fontSize:12,fontWeight:700,flexShrink:0,minWidth:52,textAlign:"right",
                        color:c.roi>=0?theme.green:theme.red
                      }}>{c.roi>=0?"+":""}{c.roi}%</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Próximos do calendário */}
          {calendarioEditorial.filter(x=>x.data>=new Date().toISOString().slice(0,10)&&x.status!=="Publicado"&&x.status!=="Cancelado").slice(0,4).length > 0 && (
            <Card style={{padding:"20px 24px"}}>
              <p className="serif" style={{fontSize:17,fontWeight:700,color:theme.gray900,marginBottom:16}}>📅 Próximas Publicações</p>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {calendarioEditorial
                  .filter(x=>x.data>=new Date().toISOString().slice(0,10)&&x.status!=="Publicado"&&x.status!=="Cancelado")
                  .sort((a,b)=>a.data.localeCompare(b.data)).slice(0,5).map(x=>(
                  <div key={x.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:8,border:`1px solid ${theme.gray100}`}}>
                    <span style={{fontSize:18}}>{ICON_PLAT[x.plataforma]||"📱"}</span>
                    <div style={{flex:1}}>
                      <p style={{fontSize:13,fontWeight:600,color:theme.gray900}}>{x.titulo}</p>
                      <p style={{fontSize:11,color:theme.gray500}}>{x.plataforma} · {x.tipo} · {new Date(x.data+"T12:00:00").toLocaleDateString("pt-BR")}</p>
                    </div>
                    <Badge color={x.status==="Agendado"?"gold":x.status==="Produção"?"blue":"gray"}>{x.status}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ======= REDES SOCIAIS ======= */}
      {aba==="redesSociais" && (
        <div>
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
            <Btn onClick={()=>{setEditRS({id:"",plataforma:"Instagram",perfil:"",responsavel:"",status:"Ativo",produtos:[],obs:""});setModalRS(true);}}>+ Adicionar Rede</Btn>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:16}}>
            {redesSociais.map(r=>(
              <Card key={r.id} style={{padding:0,overflow:"hidden"}}>
                <div style={{padding:"14px 18px",background:`${COR_PLAT[r.plataforma]||"#333"}`,display:"flex",alignItems:"center",gap:12}}>
                  <span style={{fontSize:28}}>{ICON_PLAT[r.plataforma]||"📱"}</span>
                  <div style={{flex:1}}>
                    <p style={{fontSize:16,fontWeight:700,color:"#fff"}}>{r.plataforma}</p>
                    {r.perfil&&<p style={{fontSize:11,color:"rgba(255,255,255,.7)",marginTop:2}}>{r.perfil}</p>}
                  </div>
                  <Badge color={r.status==="Ativo"?"green":"gray"}>{r.status}</Badge>
                </div>
                <div style={{padding:"14px 18px"}}>
                  <div style={{fontSize:12,color:theme.gray600,marginBottom:12}}>
                    {r.responsavel&&<p>👤 Responsável: {equipe.find(e=>e.id===r.responsavel)?.nome||r.responsavel}</p>}
                    {r.obs&&<p style={{marginTop:4,fontStyle:"italic",color:theme.gray500}}>{r.obs}</p>}
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    {r.perfil&&<a href={r.perfil.startsWith("http")?r.perfil:"https://"+r.perfil} target="_blank" rel="noreferrer"><Btn size="sm" variant="secondary">↗ Acessar</Btn></a>}
                    <Btn size="sm" variant="secondary" onClick={()=>{setEditRS({...r});setModalRS(true);}}>✏️ Editar</Btn>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ======= CALENDÁRIO EDITORIAL ======= */}
      {aba==="calendario" && (
        <div>
          {/* Filtros */}
          <Card style={{padding:"12px 18px",marginBottom:16,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            <input type="month" value={filtCalMes} onChange={e=>setFiltCalMes(e.target.value)}
              style={{padding:"7px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}/>
            <select value={filtCalPlat} onChange={e=>setFiltCalPlat(e.target.value)}
              style={{padding:"7px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
              <option value="">Todas as plataformas</option>
              {PLATAFORMAS.map(p=><option key={p}>{p}</option>)}
            </select>
            <span style={{fontSize:12,color:theme.gray500,marginLeft:"auto"}}>{calFiltrado.length} item{calFiltrado.length!==1?"s":""}</span>
          </Card>

          {calFiltrado.length===0
            ? <Card><EmptyState icon="📅" title="Nenhum conteúdo neste período" subtitle='Clique em "+ Conteúdo" para planejar publicações.'/></Card>
            : (
              <div>
                {/* Agrupar por status */}
                {STATUS_CAL.map(st=>{
                  const items = calFiltrado.filter(x=>x.status===st);
                  if(items.length===0) return null;
                  return (
                    <div key={st} style={{marginBottom:20}}>
                      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                        <div style={{width:10,height:10,borderRadius:"50%",background:COR_STATUS[st]}}/>
                        <p style={{fontSize:12,fontWeight:700,color:COR_STATUS[st],textTransform:"uppercase",letterSpacing:.8}}>{st}</p>
                        <span style={{fontSize:11,color:theme.gray400}}>{items.length} item{items.length!==1?"s":""}</span>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {items.map(x=>(
                          <Card key={x.id} style={{padding:"12px 16px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:12}}>
                              <span style={{fontSize:22,flexShrink:0}}>{ICON_PLAT[x.plataforma]||"📱"}</span>
                              <div style={{flex:1,minWidth:0}}>
                                <p style={{fontSize:13,fontWeight:600,color:theme.gray900}}>{x.titulo}</p>
                                <div style={{display:"flex",gap:12,marginTop:3,fontSize:11,color:theme.gray500,flexWrap:"wrap"}}>
                                  <span>{x.plataforma}</span>
                                  <span>📝 {x.tipo}</span>
                                  <span>📅 {x.data?new Date(x.data+"T12:00:00").toLocaleDateString("pt-BR"):"-"}</span>
                                  {x.responsavel&&<span>👤 {equipe.find(e=>e.id===x.responsavel)?.nome||""}</span>}
                                </div>
                                {x.descricao&&<p style={{fontSize:11,color:theme.gray500,marginTop:4,fontStyle:"italic"}}>{x.descricao.slice(0,80)}{x.descricao.length>80?"…":""}</p>}
                              </div>
                              <div style={{display:"flex",gap:6,flexShrink:0}}>
                                {x.link&&<a href={x.link} target="_blank" rel="noreferrer"><Btn size="sm" variant="secondary">🔗</Btn></a>}
                                <Btn size="sm" variant="secondary" onClick={()=>{setEditCal({...x});setModalCal(true);}}>✏️</Btn>
                                <Btn size="sm" variant="danger" onClick={()=>remover("calendarioEditorial",x.id)}>×</Btn>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          }
        </div>
      )}

      {/* ======= IDEIAS ======= */}
      {aba==="ideias" && (
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
            {ideias.length===0
              ? <div style={{gridColumn:"1/-1"}}><Card><EmptyState icon="💡" title="Nenhuma ideia cadastrada" subtitle='Clique em "+ Ideia" para alimentar o banco de ideias.'/></Card></div>
              : ideias.map(x=>{
                  const cor = {Alta:theme.red, Média:theme.amber, Baixa:theme.green}[x.prioridade]||theme.gray400;
                  return (
                    <Card key={x.id} style={{padding:0,overflow:"hidden",opacity:x.status==="Descartada" ? 0.5 : 1}}>
                      <div style={{height:4,background:cor}}/>
                      <div style={{padding:"14px 16px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                          <span style={{fontSize:16}}>{ICON_PLAT[x.plataforma]||"💡"}</span>
                          <div style={{display:"flex",gap:4}}>
                            <Badge color={x.prioridade==="Alta"?"red":x.prioridade==="Média"?"amber":"green"}>{x.prioridade}</Badge>
                            <Badge color={x.status==="Nova"?"blue":x.status==="Em uso"?"green":"gray"}>{x.status}</Badge>
                          </div>
                        </div>
                        <p style={{fontSize:13,fontWeight:700,color:theme.gray900,marginBottom:6,lineHeight:1.4}}>{x.titulo}</p>
                        {x.plataforma&&<p style={{fontSize:11,color:theme.gray500,marginBottom:4}}>📱 {x.plataforma} · {x.tipo}</p>}
                        {x.descricao&&<p style={{fontSize:11,color:theme.gray600,fontStyle:"italic",lineHeight:1.5,marginBottom:10}}>{x.descricao}</p>}
                        <div style={{display:"flex",gap:6}}>
                          <Btn size="sm" variant="secondary" style={{flex:1}} onClick={()=>{
                            setEditCal({id:"",titulo:x.titulo,plataforma:x.plataforma||"Instagram",tipo:x.tipo||"Post",data:"",status:"Ideia",produtoId:x.produtoId||"",responsavel:"",descricao:x.descricao||"",link:""});
                            setModalCal(true); setAba("calendario");
                          }}>→ Calendário</Btn>
                          <Btn size="sm" variant="secondary" onClick={()=>{setEditIdeia({...x});setModalIdeia(true);}}>✏️</Btn>
                          <Btn size="sm" variant="danger" onClick={()=>remover("ideias",x.id)}>×</Btn>
                        </div>
                      </div>
                    </Card>
                  );
                })
            }
          </div>
        </div>
      )}

      {/* ======= CAMPANHAS ======= */}
      {aba==="campanhas" && (
        <div>
          {/* KPIs campanhas */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
            {[
              {l:"Total investido", v:`R$ ${totCamp.toLocaleString("pt-BR",{minimumFractionDigits:2})}`, icon:"💰", c:theme.gray900},
              {l:"Leads gerados",   v:totLead,    icon:"👥", c:theme.green},
              {l:"Campanhas ativas",v:campanhas.filter(c=>c.status==="Ativa").length, icon:"🚀", c:theme.blue},
              {l:"Custo/lead",      v:totLead>0?`R$ ${(totCamp/totLead).toFixed(2)}`:"—", icon:"📊", c:theme.gold},
            ].map(k=>(
              <Card key={k.l} style={{padding:"14px 18px"}}>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{k.l}</p>
                  <span style={{fontSize:16}}>{k.icon}</span>
                </div>
                <p style={{fontSize:k.l==="Total investido"||k.l==="Custo/lead"?18:24,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
              </Card>
            ))}
          </div>

          {campanhas.length===0
            ? <Card><EmptyState icon="💰" title="Nenhuma campanha cadastrada" subtitle='Clique em "+ Campanha" para registrar campanhas pagas.'/></Card>
            : <Card>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead>
                      <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                        {["Campanha","Plataforma","Período","Orçamento","Leads","Alcance","Status","Ações"].map(h=>(
                          <th key={h} style={{padding:"11px 14px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {campanhas.map((c,i)=>(
                        <tr key={c.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                          <td style={{padding:"11px 14px"}}>
                            <p style={{fontSize:13,fontWeight:600,color:theme.gray900}}>{c.nome}</p>
                            {c.objetivo&&<p style={{fontSize:11,color:theme.gray400}}>{c.objetivo}</p>}
                          </td>
                          <td style={{padding:"11px 14px"}}><span style={{fontSize:16}}>{ICON_PLAT[c.plataforma]||"📱"}</span> <span style={{fontSize:12}}>{c.plataforma}</span></td>
                          <td style={{padding:"11px 14px",fontSize:11,color:theme.gray600}}>
                            {c.dataInicio&&<span>{new Date(c.dataInicio+"T12:00:00").toLocaleDateString("pt-BR")}</span>}
                            {c.dataFim&&<span> → {new Date(c.dataFim+"T12:00:00").toLocaleDateString("pt-BR")}</span>}
                          </td>
                          <td style={{padding:"11px 14px",fontSize:13,fontWeight:600,color:theme.gray800}}>
                            {c.orcamento?`R$ ${parseFloat(c.orcamento).toLocaleString("pt-BR",{minimumFractionDigits:2})}`:"—"}
                          </td>
                          <td style={{padding:"11px 14px",fontSize:13,fontWeight:600,color:theme.green}}>{c.leads||"—"}</td>
                          <td style={{padding:"11px 14px",fontSize:12,color:theme.gray600}}>{c.alcance?parseInt(c.alcance).toLocaleString("pt-BR"):"—"}</td>
                          <td style={{padding:"11px 14px"}}><Badge color={c.status==="Ativa"?"green":c.status==="Planejamento"?"amber":"gray"}>{c.status}</Badge></td>
                          <td style={{padding:"11px 14px"}}>
                            <div style={{display:"flex",gap:5}}>
                              <Btn size="sm" variant="secondary" onClick={()=>{setEditCamp({...c});setModalCamp(true);}}>✏️</Btn>
                              <Btn size="sm" variant="danger" onClick={()=>remover("campanhas",c.id)}>×</Btn>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
          }
        </div>
      )}

      {/* ======= ATENDIMENTOS / LEADS ======= */}
      {aba==="atendimentos" && (
        <div>
          {(() => {
            const lista = atendimentos
              .filter(a=>(!filtStatus||a.status===filtStatus)&&(!filtCanal||a.canal===filtCanal))
              .sort((a,b)=>(b.data||"").localeCompare(a.data||""));
            return (
              <>
                <Card style={{padding:"12px 18px",marginBottom:16,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                  <select value={filtStatus} onChange={e=>setFiltStatus(e.target.value)}
                    style={{padding:"7px 10px",border:`1px solid ${theme.gray200}`,borderRadius:6,fontSize:12,outline:"none"}}>
                    <option value="">Todos os status</option>
                    {STATUS_ATEND.map(s=><option key={s}>{s}</option>)}
                  </select>
                  <select value={filtCanal} onChange={e=>setFiltCanal(e.target.value)}
                    style={{padding:"7px 10px",border:`1px solid ${theme.gray200}`,borderRadius:6,fontSize:12,outline:"none"}}>
                    <option value="">Todos os canais</option>
                    {CANAIS_ORIG.map(c=><option key={c}>{c}</option>)}
                  </select>
                  <span style={{fontSize:12,color:theme.gray500,marginLeft:"auto"}}>{lista.length} registro(s)</span>
                </Card>
                {lista.length === 0 ? (
                  <Card style={{padding:"40px 24px",textAlign:"center"}}>
                    <p style={{fontSize:28,marginBottom:8}}>📲</p>
                    <p style={{fontSize:14,fontWeight:600,color:theme.gray700}}>Nenhum atendimento registrado</p>
                    <p style={{fontSize:12,color:theme.gray500,marginTop:4}}>Clique em "+ Atendimento" para adicionar manualmente ou aguarde os webhooks de WhatsApp/Instagram.</p>
                  </Card>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {lista.map(a=>(
                      <Card key={a.id} style={{padding:"14px 18px"}}>
                        <div style={{display:"flex",alignItems:"flex-start",gap:14}}>
                          <div style={{width:40,height:40,borderRadius:20,background:`${COR_ATEND[a.status]||theme.gray200}22`,
                            display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                            {a.canal==="WhatsApp"?"💬":a.canal==="Instagram DM"?"📸":a.canal==="Facebook Messenger"?"👥":
                             a.canal==="LinkedIn"?"💼":a.canal==="Site/Formulário"?"🌐":a.canal==="E-mail"?"✉️":"📞"}
                          </div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                              <p style={{fontSize:14,fontWeight:700,color:theme.gray900}}>{a.nomeContato||"(sem nome)"}</p>
                              <Badge color={a.status==="Convertido"?"green":a.status==="Descartado"?"red":a.status==="Qualificado"?"gold":a.status==="Em atendimento"?"amber":"blue"}
                                style={{fontSize:10}}>{a.status}</Badge>
                            </div>
                            <p style={{fontSize:12,color:theme.gray500}}>
                              {a.canal} · {a.data?new Date(a.data+"T12:00:00").toLocaleDateString("pt-BR"):"data não informada"}
                              {a.campanhaId&&campanhas.find(c=>c.id===a.campanhaId)&&
                                <> · 📣 {campanhas.find(c=>c.id===a.campanhaId).nome}</>}
                            </p>
                            {a.obs&&<p style={{fontSize:12,color:theme.gray600,marginTop:4,fontStyle:"italic"}}>{a.obs}</p>}
                          </div>
                          <div style={{display:"flex",gap:6,flexShrink:0}}>
                            <Btn size="sm" variant="secondary" onClick={()=>{setEditAtend({...a});setModalAtend(true);}}>✏️</Btn>
                            <Btn size="sm" variant="secondary" onClick={()=>remover("atendimentos",a.id)}>🗑️</Btn>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* ======= AGENTES IA ======= */}
      {aba==="agentes" && (
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
            padding:"10px 14px",borderRadius:8,background:"#eff6ff",border:"1px solid #bfdbfe",marginBottom:16}}>
            <p style={{fontSize:12,color:"#1e40af",margin:0}}>
              🔗 Agentes do cadastro central <strong>IA — Assistentes Jurídicos</strong>.
            </p>
            <button onClick={()=>setModalSelAgMkt(true)} style={{padding:"7px 16px",borderRadius:7,
              border:"1px solid #1e40af",background:"#dbeafe",color:"#1e40af",
              cursor:"pointer",fontSize:12,fontWeight:700}}>
              ⚙️ Selecionar Agentes
            </button>
          </div>

          {modalSelAgMkt && (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:9999,
              display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:"24px 16px"}}>
              <div style={{background:theme.white,borderRadius:12,padding:"24px 28px",maxWidth:560,
                width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,.3)",marginBottom:24}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0}}>⚙️ Selecionar Agentes — Marketing</p>
                  <button onClick={()=>setModalSelAgMkt(false)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:theme.gray400}}>×</button>
                </div>
                <p style={{fontSize:11,color:theme.gray500,marginBottom:14}}>
                  Marque os agentes do cadastro central que devem aparecer no módulo de Marketing.
                </p>
                <div style={{maxHeight:360,overflowY:"auto",display:"flex",flexDirection:"column",gap:8}}>
                  {todosAgentes.length===0
                    ? <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"20px 0"}}>Nenhum agente cadastrado no módulo IA.</p>
                    : todosAgentes.map(a=>{
                        const sel = idsMkt.includes(a.id);
                        return (
                          <div key={a.id} onClick={()=>{
                            const novaLista = sel
                              ? idsMkt.filter(id=>id!==a.id)
                              : [...idsMkt, a.id];
                            setMkt("agentes", novaLista);
                          }} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",
                            borderRadius:8,cursor:"pointer",
                            border:`2px solid ${sel?theme.gold:theme.gray200}`,
                            background:sel?theme.gold+"0A":theme.white,transition:"all .1s"}}>
                            <span style={{fontSize:22}}>{a.emoji||a.icon||"🤖"}</span>
                            <div style={{flex:1}}>
                              <p style={{fontSize:13,fontWeight:600,color:theme.gray900,margin:0}}>{a.nome}</p>
                              <p style={{fontSize:11,color:theme.gray500,margin:"2px 0 0"}}>{a.tema||""} {a.desc?"· "+a.desc.slice(0,50):""}</p>
                            </div>
                            <span style={{fontSize:18,color:sel?theme.gold:theme.gray300}}>{sel?"✓":"○"}</span>
                          </div>
                        );
                      })
                  }
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:16,paddingTop:14,borderTop:`1px solid ${theme.gray100}`}}>
                  <span style={{fontSize:11,color:theme.gray400}}>{idsMkt.length} agente(s) selecionado(s)</span>
                  <button onClick={()=>setModalSelAgMkt(false)} style={{padding:"9px 24px",borderRadius:8,
                    border:"none",background:theme.gray900,color:theme.gold,cursor:"pointer",fontSize:13,fontWeight:700}}>
                    ✓ Confirmar
                  </button>
                </div>
              </div>
            </div>
          )}

          {agentes.length===0
            ? <Card><EmptyState icon="🤖" title="Nenhum agente selecionado" subtitle="Clique em 'Selecionar Agentes' para escolher os agentes do módulo de Marketing."/></Card>
            : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:16}}>
                {agentes.map(a=><CardAgentePublico key={a.id} ag={a}/>)}
              </div>
          }
        </div>
      )}

      {/* ABA NEO + ADA — Campanhas Pagas (Fase 4) */}
      {aba==="neoada" && (
        <div style={{display:"flex",flexDirection:"column",gap:20}}>

          {/* ADA — Analytics (leitura) */}
          <Card>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
              <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:0}}>📈 ADA — Analytics (Windsor.ai)</p>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {CONECTORES_ADA.map(c=>(
                  <button key={c.id} onClick={()=>setAdaConector(c.id)} style={{
                    padding:"6px 12px",borderRadius:20,fontSize:12,cursor:"pointer",
                    border:`2px solid ${adaConector===c.id?theme.gold:theme.gray200}`,
                    background:adaConector===c.id?theme.gold+"22":theme.gray50,
                    color:adaConector===c.id?theme.amber:theme.gray600,fontWeight:adaConector===c.id?700:400}}>
                    {c.label}
                  </button>
                ))}
                <select value={adaPeriodo} onChange={e=>setAdaPeriodo(e.target.value)} style={{
                    padding:"6px 10px",borderRadius:20,fontSize:12,border:`2px solid ${theme.gray200}`,background:theme.gray50,color:theme.gray600}}>
                  {ADA_PERIODOS.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
                <input placeholder="ID da conta (opcional)" value={adaAccount} onChange={e=>setAdaAccount(e.target.value)} style={{
                    padding:"6px 10px",borderRadius:20,fontSize:12,border:`2px solid ${theme.gray200}`,width:160}}/>
                <Btn size="sm" onClick={carregarADA} disabled={adaCarregando}>{adaCarregando?"Carregando...":"🔄 Buscar"}</Btn>
              </div>
            </div>
            {adaErro && <p style={{fontSize:12,color:theme.red,marginBottom:10}}>⚠️ {adaErro}</p>}
            {!adaDados ? (
              <EmptyState icon="📈" title="Nenhum dado carregado" subtitle="Escolha o conector e clique em Buscar."/>
            ) : Array.isArray(adaDados) && adaDados.length>0 ? (
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead><tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                    {Object.keys(adaDados[0]).map(k=><th key={k} style={{padding:"8px 10px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase"}}>{k}</th>)}
                  </tr></thead>
                  <tbody>
                    {adaDados.slice(0,50).map((row,i)=>(
                      <tr key={i} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                        {Object.values(row).map((v,j)=><td key={j} style={{padding:"8px 10px"}}>{String(v ?? "—")}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {adaDados.length>50 && <p style={{fontSize:11,color:theme.gray400,marginTop:8}}>Mostrando 50 de {adaDados.length} linhas.</p>}
              </div>
            ) : <EmptyState icon="📈" title="Sem dados para este período" subtitle=""/>}
          </Card>

          {/* NEO — Campanhas Pagas (escrita, com aprovação reforçada) */}
          <Card style={{border:`1px solid #fecaca`,background:"#fef2f2"}}>
            <p style={{fontSize:13,fontWeight:700,color:"#991b1b",marginBottom:4}}>🚀 NEO — Ações em Campanha Paga (dinheiro real)</p>
            <p style={{fontSize:11,color:"#991b1b",marginBottom:14}}>
              Cria, pausa ou ajusta orçamento de campanhas de verdade. Restrito a Administrador/Sócio, exige digitar a frase de confirmação.
            </p>
            {!podeExecutarNEO && (
              <div style={{padding:"10px 14px",borderRadius:8,background:"#fff",border:`1px solid #fecaca`,marginBottom:14}}>
                <p style={{fontSize:12,color:"#991b1b",margin:0}}>🔒 Seu perfil não tem permissão para executar ações de campanha paga.</p>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 14px"}}>
              <FormField label="Conector">
                <select value={neoConector} onChange={e=>{const c=e.target.value;setNeoConector(c);setNeoAcoes([]);setNeoAcaoId('');}}
                  style={{width:"100%",padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13}}>
                  <option value="google_ads">Google Ads</option>
                  <option value="facebook">Meta Ads</option>
                </select>
              </FormField>
              <FormField label="ID da Conta (account)">
                <Input value={neoAccount} onChange={setNeoAccount} placeholder="Ex: 1234567890"/>
              </FormField>
            </div>
            <Btn size="sm" onClick={carregarAcoesNEO} disabled={!neoAccount} style={{marginBottom:12}}>🔍 Listar ações disponíveis</Btn>
            {neoErro && <p style={{fontSize:12,color:theme.red,marginBottom:10}}>⚠️ {neoErro}</p>}
            {neoAcoes.length>0 && (
              <>
                <FormField label="Ação">
                  <select value={neoAcaoId} onChange={e=>setNeoAcaoId(e.target.value)}
                    style={{width:"100%",padding:"8px 10px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,marginBottom:10}}>
                    <option value="">Selecione...</option>
                    {neoAcoes.map(a=><option key={a.id} value={a.id}>{a.name||a.id} — {a.description||""}</option>)}
                  </select>
                </FormField>
                {neoAcaoId && (
                  <>
                    <FormField label="Parâmetros (JSON)" hint={JSON.stringify(neoAcoes.find(a=>a.id===neoAcaoId)?.schema?.properties||{})}>
                      <Textarea value={neoParamsJson} onChange={setNeoParamsJson} rows={4}/>
                    </FormField>
                    <FormField label={`Digite exatamente "CONFIRMO A EXECUCAO" para liberar o botão`}>
                      <Input value={neoFrase} onChange={setNeoFrase} placeholder="CONFIRMO A EXECUCAO"/>
                    </FormField>
                    <button onClick={executarNEO} disabled={!podeExecutarNEO || neoExecutando || neoFrase.trim()!=='CONFIRMO A EXECUCAO'}
                      style={{padding:"10px 20px",borderRadius:8,border:"none",cursor:(!podeExecutarNEO||neoFrase.trim()!=='CONFIRMO A EXECUCAO')?"not-allowed":"pointer",
                        background: neoFrase.trim()==='CONFIRMO A EXECUCAO'?"#dc2626":theme.gray300, color:"#fff",fontSize:13,fontWeight:700}}>
                      {neoExecutando?"Executando...":"🚀 Executar Ação Real"}
                    </button>
                  </>
                )}
              </>
            )}
            {neoResultado && (
              <div style={{marginTop:14,padding:"10px 14px",borderRadius:8,background:"#f0fdf4",border:"1px solid #bbf7d0"}}>
                <p style={{fontSize:12,color:"#166534",margin:0}}>✅ {JSON.stringify(neoResultado)}</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* MODAL CALENDÁRIO */}
      <Modal open={modalCal} onClose={()=>setModalCal(false)} title={editCal?.id?"Editar Conteúdo":"Novo Conteúdo"} width={600}>
        {editCal&&(
          <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Título / Tema" required><Input value={editCal.titulo} onChange={v=>setEditCal({...editCal,titulo:v})} placeholder="Ex: 5 erros comuns no Simples Nacional"/></FormField>
              </div>
              <FormField label="Plataforma">
                <select value={editCal.plataforma} onChange={e=>setEditCal({...editCal,plataforma:e.target.value})}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
                  {PLATAFORMAS.map(p=><option key={p}>{p}</option>)}
                </select>
              </FormField>
              <FormField label="Tipo de Conteúdo">
                <Select value={editCal.tipo} onChange={v=>setEditCal({...editCal,tipo:v})} options={TIPO_CONT}/>
              </FormField>
              <FormField label="Data de Publicação"><Input type="date" value={editCal.data} onChange={v=>setEditCal({...editCal,data:v})}/></FormField>
              <FormField label="Status">
                <select value={editCal.status} onChange={e=>setEditCal({...editCal,status:e.target.value})}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
                  {STATUS_CAL.map(s=><option key={s}>{s}</option>)}
                </select>
              </FormField>
              <FormField label="Produto Vinculado">
                <Select value={editCal.produtoId} onChange={v=>setEditCal({...editCal,produtoId:v})} options={produtos.map(p=>({value:p.id,label:p.nome}))} placeholder="Nenhum..."/>
              </FormField>
              <FormField label="Responsável">
                <Select value={editCal.responsavel} onChange={v=>setEditCal({...editCal,responsavel:v})} options={equipe.filter(e=>e.ativo!==false).map(e=>({value:e.id,label:e.nome}))} placeholder="Selecione..."/>
              </FormField>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Descrição / Pauta">
                  <Textarea value={editCal.descricao} onChange={v=>setEditCal({...editCal,descricao:v})} placeholder="Resumo do conteúdo, mensagem principal, hashtags..." rows={3}/>
                </FormField>
              </div>

              {/* LUMI + RAVI — Geração de Conteúdo Integrada (Fase 3, item 2) */}
              <div style={{gridColumn:"1/-1",padding:"12px 14px",borderRadius:8,background:"#f5f3ff",border:"1px solid #ddd6fe",marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <p style={{fontSize:12,fontWeight:700,color:"#6d28d9",margin:0}}>✨ LUMI + RAVI — Gerar conteúdo com IA</p>
                  <button onClick={gerarConteudoIA} disabled={gerandoConteudo} style={{padding:"6px 14px",borderRadius:7,
                    border:"none",background:"#7c3aed",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>
                    {gerandoConteudo?"Gerando...":"✨ Gerar com IA"}
                  </button>
                </div>
                {editCal.legenda!==undefined && (
                  <FormField label="Legenda gerada (editável)">
                    <Textarea value={editCal.legenda||""} onChange={v=>setEditCal({...editCal,legenda:v,complianceOAB:null})} rows={5}/>
                  </FormField>
                )}
                {editCal.hashtags!==undefined && (
                  <FormField label="Hashtags"><Input value={editCal.hashtags||""} onChange={v=>setEditCal({...editCal,hashtags:v})}/></FormField>
                )}
                {editCal.roteiro && (
                  <FormField label="Roteiro (RAVI — Reels/Vídeo)">
                    <Textarea value={editCal.roteiro||""} onChange={v=>setEditCal({...editCal,roteiro:v})} rows={6}/>
                  </FormField>
                )}
              </div>

              {/* LEXA — Compliance OAB (Fase 3, item 1) — etapa obrigatória */}
              <div style={{gridColumn:"1/-1",padding:"12px 14px",borderRadius:8,
                background: editCal.complianceOAB?.aprovado===true ? "#f0fdf4" : editCal.complianceOAB?.aprovado===false ? "#fef2f2" : "#fffbeb",
                border:`1px solid ${editCal.complianceOAB?.aprovado===true ? "#bbf7d0" : editCal.complianceOAB?.aprovado===false ? "#fecaca" : "#fde68a"}`,
                marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:editCal.complianceOAB?8:0}}>
                  <p style={{fontSize:12,fontWeight:700,margin:0,
                    color: editCal.complianceOAB?.aprovado===true ? "#166534" : editCal.complianceOAB?.aprovado===false ? "#991b1b" : "#92400e"}}>
                    🛡️ LEXA — Compliance OAB (Provimento 205/2021)
                    {editCal.complianceOAB?.aprovado===true && " — ✓ Aprovado"}
                    {editCal.complianceOAB?.aprovado===false && " — ⚠ Ajustes necessários"}
                  </p>
                  <button onClick={validarComplianceOAB} disabled={validandoOAB} style={{padding:"6px 14px",borderRadius:7,
                    border:"none",background:theme.gray900,color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>
                    {validandoOAB?"Validando...":"🛡️ Validar OAB"}
                  </button>
                </div>
                {editCal.complianceOAB?.alertas?.length>0 && (
                  <ul style={{margin:"6px 0",paddingLeft:18,fontSize:12,color:"#991b1b"}}>
                    {editCal.complianceOAB.alertas.map((a,i)=><li key={i} style={{marginBottom:4}}>{a}</li>)}
                  </ul>
                )}
                {editCal.complianceOAB?.aprovado===false && editCal.complianceOAB?.sugestao && (
                  <div style={{marginTop:8}}>
                    <p style={{fontSize:11,fontWeight:600,color:theme.gray600,marginBottom:4}}>Sugestão de texto corrigido pela LEXA:</p>
                    <div style={{fontSize:12,color:theme.gray700,background:"#fff",padding:"8px 10px",borderRadius:6,border:`1px solid ${theme.gray200}`,whiteSpace:"pre-wrap"}}>
                      {editCal.complianceOAB.sugestao}
                    </div>
                    <button onClick={()=>setEditCal({...editCal,legenda:editCal.complianceOAB.sugestao,complianceOAB:null})}
                      style={{marginTop:6,padding:"5px 10px",borderRadius:6,border:"none",background:"#7c3aed",color:"#fff",cursor:"pointer",fontSize:11,fontWeight:600}}>
                      Usar sugestão da LEXA
                    </button>
                  </div>
                )}
                {!editCal.complianceOAB && (
                  <p style={{fontSize:11,color:"#92400e",margin:0}}>Obrigatório validar antes de marcar como "Publicado".</p>
                )}
              </div>

              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Link do Conteúdo (após publicação)">
                  <Input value={editCal.link} onChange={v=>setEditCal({...editCal,link:v})} placeholder="https://..."/>
                </FormField>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalCal(false)}>Cancelar</Btn>
              <Btn onClick={saveCal}>Salvar</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* MODAL IDEIA */}
      <Modal open={modalIdeia} onClose={()=>setModalIdeia(false)} title={editIdeia?.id?"Editar Ideia":"Nova Ideia"} width={520}>
        {editIdeia&&(
          <>
            <FormField label="Título da Ideia" required><Input value={editIdeia.titulo} onChange={v=>setEditIdeia({...editIdeia,titulo:v})} placeholder="Ex: Carrossel explicando exclusão do ICMS do PIS/COFINS"/></FormField>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
              <FormField label="Plataforma">
                <select value={editIdeia.plataforma} onChange={e=>setEditIdeia({...editIdeia,plataforma:e.target.value})}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
                  <option value="">Qualquer</option>
                  {PLATAFORMAS.map(p=><option key={p}>{p}</option>)}
                </select>
              </FormField>
              <FormField label="Tipo de Conteúdo"><Select value={editIdeia.tipo} onChange={v=>setEditIdeia({...editIdeia,tipo:v})} options={[""].concat(TIPO_CONT)} placeholder="Qualquer..."/></FormField>
              <FormField label="Prioridade"><Select value={editIdeia.prioridade} onChange={v=>setEditIdeia({...editIdeia,prioridade:v})} options={["Alta","Média","Baixa"]}/></FormField>
              <FormField label="Status"><Select value={editIdeia.status} onChange={v=>setEditIdeia({...editIdeia,status:v})} options={["Nova","Em uso","Descartada"]}/></FormField>
            </div>
            <FormField label="Descrição / Observações"><Textarea value={editIdeia.descricao} onChange={v=>setEditIdeia({...editIdeia,descricao:v})} placeholder="Detalhes, referências, ângulo de abordagem..." rows={3}/></FormField>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalIdeia(false)}>Cancelar</Btn>
              <Btn onClick={saveIdeia}>Salvar</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* MODAL CAMPANHA */}
      <Modal open={modalCamp} onClose={()=>setModalCamp(false)} title={editCamp?.id?"Editar Campanha":"Nova Campanha"} width={600}>
        {editCamp&&(
          <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Nome da Campanha" required><Input value={editCamp.nome} onChange={v=>setEditCamp({...editCamp,nome:v})} placeholder="Ex: Campanha Simples Nacional — Jan/2026"/></FormField>
              </div>
              <FormField label="Plataforma">
                <select value={editCamp.plataforma} onChange={e=>setEditCamp({...editCamp,plataforma:e.target.value})}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
                  <option value="">Selecione...</option>
                  {PLATAFORMAS.map(p=><option key={p}>{p}</option>)}
                </select>
              </FormField>
              <FormField label="Produto Vinculado"><Select value={editCamp.produtoId} onChange={v=>setEditCamp({...editCamp,produtoId:v})} options={produtos.map(p=>({value:p.id,label:p.nome}))} placeholder="Nenhum..."/></FormField>
              <FormField label="Data Início"><Input type="date" value={editCamp.dataInicio} onChange={v=>setEditCamp({...editCamp,dataInicio:v})}/></FormField>
              <FormField label="Data Fim"><Input type="date" value={editCamp.dataFim} onChange={v=>setEditCamp({...editCamp,dataFim:v})}/></FormField>
              <FormField label="Orçamento / Investimento (R$)"><Input type="number" value={editCamp.orcamento} onChange={v=>setEditCamp({...editCamp,orcamento:v})} placeholder="0,00"/></FormField>
              <FormField label="Receita Gerada (R$)"><Input type="number" value={editCamp.receitaGerada||""} onChange={v=>setEditCamp({...editCamp,receitaGerada:v})} placeholder="0,00"/></FormField>
              <FormField label="Objetivo"><Input value={editCamp.objetivo} onChange={v=>setEditCamp({...editCamp,objetivo:v})} placeholder="Ex: Geração de leads tributários"/></FormField>
              <FormField label="Leads Gerados"><Input type="number" value={editCamp.leads} onChange={v=>setEditCamp({...editCamp,leads:v})} placeholder="0"/></FormField>
              <FormField label="Alcance (pessoas)"><Input type="number" value={editCamp.alcance} onChange={v=>setEditCamp({...editCamp,alcance:v})} placeholder="0"/></FormField>
              <FormField label="Status"><Select value={editCamp.status} onChange={v=>setEditCamp({...editCamp,status:v})} options={["Planejamento","Ativa","Pausada","Encerrada"]}/></FormField>
              <FormField label="Canal de Origem">
                <select value={editCamp.origemCanal||""} onChange={e=>setEditCamp({...editCamp,origemCanal:e.target.value})}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
                  <option value="">Selecione...</option>
                  {CANAIS_ORIG.map(c=><option key={c}>{c}</option>)}
                </select>
              </FormField>
              <FormField label="UTM Source"><Input value={editCamp.utmSource||""} onChange={v=>setEditCamp({...editCamp,utmSource:v})} placeholder="Ex: instagram, google, facebook"/></FormField>
              <FormField label="UTM Medium"><Input value={editCamp.utmMedium||""} onChange={v=>setEditCamp({...editCamp,utmMedium:v})} placeholder="Ex: cpc, organic, story"/></FormField>
              <FormField label="UTM Campaign"><Input value={editCamp.utmCampaign||""} onChange={v=>setEditCamp({...editCamp,utmCampaign:v})} placeholder="Ex: simples-nacional-jan26"/></FormField>
              <div style={{gridColumn:"1/-1"}}><FormField label="Observações"><Textarea value={editCamp.obs} onChange={v=>setEditCamp({...editCamp,obs:v})} placeholder="Notas, resultados qualitativos..." rows={2}/></FormField></div>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalCamp(false)}>Cancelar</Btn>
              <Btn onClick={saveCamp}>Salvar</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* MODAL ATENDIMENTO */}
      <Modal open={modalAtend} onClose={()=>setModalAtend(false)} title={editAtend?.id?"Editar Atendimento":"Novo Atendimento"} width={540}>
        {editAtend&&(
          <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
              <FormField label="Nome do Contato">
                <Input value={editAtend.nomeContato||""} onChange={v=>setEditAtend({...editAtend,nomeContato:v})} placeholder="Nome do lead / cliente"/>
              </FormField>
              <FormField label="Canal de Origem">
                <select value={editAtend.canal||"WhatsApp"} onChange={e=>setEditAtend({...editAtend,canal:e.target.value})}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
                  {CANAIS_ORIG.map(c=><option key={c}>{c}</option>)}
                </select>
              </FormField>
              <FormField label="Data do Contato">
                <Input type="date" value={editAtend.data||""} onChange={v=>setEditAtend({...editAtend,data:v})}/>
              </FormField>
              <FormField label="Status">
                <select value={editAtend.status||"Novo"} onChange={e=>setEditAtend({...editAtend,status:e.target.value})}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
                  {STATUS_ATEND.map(s=><option key={s}>{s}</option>)}
                </select>
              </FormField>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Campanha Vinculada">
                  <Select value={editAtend.campanhaId||""} onChange={v=>setEditAtend({...editAtend,campanhaId:v})}
                    options={campanhas.map(c=>({value:c.id,label:c.nome}))} placeholder="Nenhuma..."/>
                </FormField>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Observações">
                  <Textarea value={editAtend.obs||""} onChange={v=>setEditAtend({...editAtend,obs:v})}
                    placeholder="Resumo do contato, necessidade identificada, próximo passo..." rows={3}/>
                </FormField>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalAtend(false)}>Cancelar</Btn>
              <Btn onClick={saveAtend}>Salvar</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* MODAL REDE SOCIAL */}
      <Modal open={modalRS} onClose={()=>setModalRS(false)} title="Configurar Rede Social" width={480}>
        {editRS&&(
          <>
            <FormField label="Plataforma">
              <select value={editRS.plataforma} onChange={e=>setEditRS({...editRS,plataforma:e.target.value})}
                style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
                {PLATAFORMAS.map(p=><option key={p}>{p}</option>)}
              </select>
            </FormField>
            <FormField label="Link do Perfil"><Input value={editRS.perfil} onChange={v=>setEditRS({...editRS,perfil:v})} placeholder="https://instagram.com/alvesecorrea"/></FormField>
            <FormField label="Responsável"><Select value={editRS.responsavel} onChange={v=>setEditRS({...editRS,responsavel:v})} options={equipe.filter(e=>e.ativo!==false).map(e=>({value:e.id,label:e.nome}))} placeholder="Selecione..."/></FormField>
            <FormField label="Status"><Select value={editRS.status} onChange={v=>setEditRS({...editRS,status:v})} options={["Ativo","Pausado","Inativo"]}/></FormField>
            <FormField label="Observações"><Textarea value={editRS.obs} onChange={v=>setEditRS({...editRS,obs:v})} placeholder="Frequência de posts, foco de conteúdo..." rows={2}/></FormField>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalRS(false)}>Cancelar</Btn>
              <Btn onClick={saveRS}>Salvar</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* MODAL AGENTE */}
      <Modal open={modalAg} onClose={()=>setModalAg(false)} title={editAg?.id?"Editar Agente":"Novo Agente"} width={480}>
        {editAg&&(
          <>
            <div style={{display:"grid",gridTemplateColumns:"60px 1fr",gap:"0 16px"}}>
              <FormField label="Ícone"><Input value={editAg.icon} onChange={v=>setEditAg({...editAg,icon:v})} placeholder="🤖"/></FormField>
              <FormField label="Nome do Agente" required><Input value={editAg.nome} onChange={v=>setEditAg({...editAg,nome:v})} placeholder="Ex: Gerador de Posts"/></FormField>
            </div>
            <FormField label="Link / URL" required><Input value={editAg.link} onChange={v=>setEditAg({...editAg,link:v})} placeholder="https://claude.ai ou https://chatgpt.com"/></FormField>
            <FormField label="Descrição"><Textarea value={editAg.descricao} onChange={v=>setEditAg({...editAg,descricao:v})} placeholder="O que este agente faz..." rows={2}/></FormField>
            <FormField label="Status"><Select value={editAg.ativo?"Ativo":"Inativo"} onChange={v=>setEditAg({...editAg,ativo:v==="Ativo"})} options={["Ativo","Inativo"]}/></FormField>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalAg(false)}>Cancelar</Btn>
              <Btn onClick={saveAg}>Salvar</Btn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

// ============================================================
// MÓDULO AGENDA
// ============================================================
const TIPOS_EVENTO = [
  { id: "audiencia",       label: "Audiência",           icon: "⚖️",  cor: "#dc2626" },
  { id: "reuniao_cliente", label: "Reunião c/ Cliente",  icon: "👥",  cor: "#2563eb" },
  { id: "reuniao_interna", label: "Reunião Interna",     icon: "🏢",  cor: "#7c3aed" },
  { id: "compromisso",     label: "Compromisso",         icon: "📌",  cor: "#d97706" },
  { id: "prazo",           label: "Prazo Processual",    icon: "⏰",  cor: "#b91c1c" },
  { id: "followup",        label: "Follow-up",           icon: "📞",  cor: "#059669" },
  { id: "atividade",       label: "Atividade",           icon: "✅",  cor: "#0284c7" },
];

const ALERTAS_OPTS = [
  { value: "1h",  label: "1 hora antes"   },
  { value: "3h",  label: "3 horas antes"  },
  { value: "1d",  label: "1 dia antes"    },
  { value: "2d",  label: "2 dias antes"   },
  { value: "3d",  label: "3 dias antes"   },
  { value: "1w",  label: "1 semana antes" },
];

const MESES_AGENDA = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const DIAS_SEM    = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];

// ============================================================
// Google Calendar Sync — componente de integração
// ============================================================
const GC_BASE = 'https://app.alvesecorreaadvogados.com.br';

function GoogleCalendarSync({ agenda, onSaveAgenda }) {
  const [status,     setStatus]     = React.useState(null); // null | 'loading' | 'authenticated' | 'unauthenticated'
  const [gcEventos,  setGcEventos]  = React.useState([]);
  const [sincMsg,    setSincMsg]    = React.useState("");
  const [carregando, setCarregando] = React.useState(false);
  const [mesSync,    setMesSync]    = React.useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
  });

  // Auto-importar silenciosamente ao abrir
  const autoImportar = (agendaAtual, onSave) => {
    const d = new Date();
    const mes = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    const inicio = `${mes}-01T00:00:00-03:00`;
    const ultimo = new Date(d.getFullYear(), d.getMonth()+1, 0).getDate();
    const fim    = `${mes}-${String(ultimo).padStart(2,"0")}T23:59:59-03:00`;

    fetch(`${GC_BASE}/google_calendar.php?action=list&inicio=${encodeURIComponent(inicio)}&fim=${encodeURIComponent(fim)}`)
      .then(r=>r.json())
      .then(res => {
        if (!res.success) return;
        setGcEventos(res.eventos||[]);
        const idsExistentes = new Set((agendaAtual||[]).map(e=>e.googleId).filter(Boolean));
        const novos = (res.eventos||[])
          .filter(g => !idsExistentes.has(g.googleId))
          .map(g => ({
            id: "GC_" + g.googleId,
            googleId: g.googleId,
            titulo:    g.titulo,
            descricao: g.descricao||"",
            local:     g.local||"",
            data:      g.inicio ? g.inicio.slice(0,10) : "",
            horaInicio: g.inicio && g.inicio.length>=16 ? g.inicio.slice(11,16) : "09:00",
            horaFim:    g.fim    && g.fim.length>=16    ? g.fim.slice(11,16)    : "10:00",
            tipo:   "compromisso",
            status: "Confirmado",
            _fromGoogle: true,
          }));
        if (novos.length > 0) {
          onSave([...(agendaAtual||[]), ...novos]);
          setSincMsg(`🔄 ${novos.length} evento(s) novo(s) importado(s) automaticamente do Google.`);
        } else {
          setSincMsg("✅ Agenda sincronizada com o Google Calendar.");
        }
      })
      .catch(()=>{}); // silencioso
  };

  // Verificar status de autenticação ao montar + auto-importar
  React.useEffect(() => {
    setStatus("loading");
    fetch(`${GC_BASE}/google_auth.php?action=status`)
      .then(r=>r.json())
      .then(d => {
        const autenticado = d.authenticated;
        setStatus(autenticado ? "authenticated" : "unauthenticated");
        if (autenticado) autoImportar(agenda, onSaveAgenda);
      })
      .catch(() => setStatus("error"));
  }, []);

  // Ouvir mensagem do popup OAuth
  React.useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === "google_auth") {
        if (e.data.success) {
          setStatus("authenticated");
          setSincMsg("✅ Conta Google conectada com sucesso!");
        } else {
          setSincMsg("❌ Erro na autenticação: " + (e.data.error || "desconhecido"));
        }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const conectar = () => {
    fetch(`${GC_BASE}/google_auth.php?action=authorize`)
      .then(r=>r.json())
      .then(d => {
        if (d.url) window.open(d.url, "google_auth", "width=600,height=700");
      });
  };

  const desconectar = () => {
    fetch(`${GC_BASE}/google_auth.php?action=revoke`)
      .then(() => { setStatus("unauthenticated"); setGcEventos([]); setSincMsg("Conta Google desconectada."); });
  };

  const buscarEventosGoogle = () => {
    setCarregando(true); setSincMsg("");
    const [ano, mes] = mesSync.split("-").map(Number);
    const inicio = `${mesSync}-01T00:00:00-03:00`;
    const ultimo = new Date(ano, mes, 0).getDate();
    const fim    = `${mesSync}-${String(ultimo).padStart(2,"0")}T23:59:59-03:00`;
    fetch(`${GC_BASE}/google_calendar.php?action=list&inicio=${encodeURIComponent(inicio)}&fim=${encodeURIComponent(fim)}`)
      .then(r=>r.json())
      .then(d => {
        if (d.success) { setGcEventos(d.eventos); setSincMsg(`✅ ${d.eventos.length} evento(s) carregado(s) do Google.`); }
        else setSincMsg("❌ Erro ao buscar eventos: " + JSON.stringify(d.error));
      })
      .catch(() => setSincMsg("❌ Erro de conexão com o servidor."))
      .finally(() => setCarregando(false));
  };

  const exportarParaGoogle = () => {
    const [ano, mes] = mesSync.split("-").map(Number);
    const eventosMes = agenda.filter(e => e.data?.startsWith(mesSync) && !e.googleId);
    if (eventosMes.length === 0) { setSincMsg("⚠️ Nenhum evento novo para exportar neste mês."); return; }
    setCarregando(true); setSincMsg("");
    let ok = 0; let err = 0;
    const promises = eventosMes.map(e =>
      fetch(`${GC_BASE}/google_calendar.php?action=create`, {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: JSON.stringify({
          titulo:    e.titulo,
          descricao: e.descricao || "",
          local:     e.local || "",
          inicio:    `${e.data}T${e.horaInicio||"09:00"}:00-03:00`,
          fim:       `${e.data}T${e.horaFim||"10:00"}:00-03:00`,
        })
      }).then(r=>r.json()).then(d => {
        if (d.success) { ok++; e.googleId = d.googleId; }
        else err++;
      }).catch(() => err++)
    );
    Promise.all(promises).then(() => {
      onSaveAgenda([...agenda]);
      setSincMsg(`✅ ${ok} evento(s) exportado(s)${err>0?` | ❌ ${err} com erro`:""}.`);
      setCarregando(false);
    });
  };

  const importarDoGoogle = () => {
    if (gcEventos.length === 0) { setSincMsg("⚠️ Busque os eventos do Google primeiro."); return; }
    const idsExistentes = new Set(agenda.map(e=>e.googleId).filter(Boolean));
    const novos = gcEventos
      .filter(g => !idsExistentes.has(g.googleId))
      .map(g => ({
        id:         "GC_" + g.googleId,
        googleId:   g.googleId,
        titulo:     g.titulo,
        descricao:  g.descricao,
        local:      g.local,
        data:       g.inicio.slice(0,10),
        horaInicio: g.inicio.slice(11,16),
        horaFim:    g.fim.slice(11,16),
        tipo:       "compromisso",
        status:     "Confirmado",
        _fromGoogle: true,
      }));
    if (novos.length === 0) { setSincMsg("⚠️ Nenhum evento novo para importar."); return; }
    onSaveAgenda([...agenda, ...novos]);
    setSincMsg(`✅ ${novos.length} evento(s) importado(s) do Google para a Agenda.`);
  };

  const fmtDt = dt => dt ? new Date(dt).toLocaleString("pt-BR",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}) : "—";

  return (
    <div>
      <div style={{marginBottom:16,padding:"14px 16px",borderRadius:10,
        background:theme.gray50,border:`1px solid ${theme.gray200}`}}>
        <p style={{fontSize:13,fontWeight:700,color:theme.gray800,marginBottom:4}}>🔗 Integração Google Calendar</p>
        <p style={{fontSize:11,color:theme.gray500,margin:0}}>
          Sincronize os eventos da Agenda SOJ com o Google Calendar da conta do escritório.
        </p>
      </div>

      {/* Status de autenticação */}
      <div style={{marginBottom:20,padding:"14px 18px",borderRadius:10,
        border:`2px solid ${status==="authenticated"?theme.green:theme.gray200}`,
        background:status==="authenticated"?"#f0fdf4":theme.white,
        display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:22}}>{status==="authenticated"?"✅":status==="loading"?"⏳":"🔌"}</span>
          <div>
            <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:0}}>
              {status==="authenticated" ? "Google Calendar conectado"
               : status==="loading" ? "Verificando conexão..."
               : status==="error" ? "Erro ao verificar conexão"
               : "Google Calendar não conectado"}
            </p>
            <p style={{fontSize:11,color:theme.gray500,margin:"2px 0 0"}}>
              alvesecorreaadvogados@gmail.com
            </p>
          </div>
        </div>
        {status==="authenticated"
          ? <button onClick={desconectar} style={{padding:"7px 16px",borderRadius:7,
              border:`1px solid ${theme.red}`,background:"#fef2f2",color:theme.red,
              cursor:"pointer",fontSize:12,fontWeight:600}}>Desconectar</button>
          : status!=="loading" && (
            <button onClick={conectar} style={{padding:"9px 20px",borderRadius:8,
              border:"none",background:"#4285f4",color:"#fff",
              cursor:"pointer",fontSize:13,fontWeight:700}}>
              🔑 Conectar Google
            </button>
          )
        }
      </div>

      {status==="authenticated" && (
        <>
          {/* Controles de sincronização */}
          <div style={{display:"grid",gridTemplateColumns:"auto 1fr auto",gap:12,
            alignItems:"end",marginBottom:16}}>
            <FormField label="Mês de Sincronização">
              <input type="month" value={mesSync} onChange={e=>setMesSync(e.target.value)}
                style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,
                  borderRadius:7,fontSize:13,outline:"none"}}/>
            </FormField>
            <div/>
            <div style={{display:"flex",gap:8,paddingBottom:8}}>
              <button onClick={buscarEventosGoogle} disabled={carregando}
                style={{padding:"9px 16px",borderRadius:7,border:"none",
                  background:theme.gray900,color:theme.gold,cursor:"pointer",
                  fontSize:12,fontWeight:700,opacity:carregando?.6:1}}>
                {carregando?"⏳ Aguarde...":"🔄 Buscar do Google"}
              </button>
              <button onClick={exportarParaGoogle} disabled={carregando}
                style={{padding:"9px 16px",borderRadius:7,
                  border:`1px solid ${theme.green}`,background:"#f0fdf4",
                  color:theme.green,cursor:"pointer",fontSize:12,fontWeight:700}}>
                ↑ Exportar SOJ → Google
              </button>
              <button onClick={importarDoGoogle} disabled={carregando||gcEventos.length===0}
                style={{padding:"9px 16px",borderRadius:7,
                  border:`1px solid #3b82f6`,background:"#eff6ff",
                  color:"#3b82f6",cursor:"pointer",fontSize:12,fontWeight:700,
                  opacity:(carregando||gcEventos.length===0)?.5:1}}>
                ↓ Importar Google → SOJ
              </button>
            </div>
          </div>

          {sincMsg && (
            <div style={{padding:"10px 14px",borderRadius:8,marginBottom:16,fontSize:12,
              background:sincMsg.startsWith("✅")?"#f0fdf4":sincMsg.startsWith("❌")?"#fef2f2":"#fffbec",
              border:`1px solid ${sincMsg.startsWith("✅")?"#bbf7d0":sincMsg.startsWith("❌")?"#fecaca":"#fde68a"}`}}>
              {sincMsg}
            </div>
          )}

          {/* Lista de eventos do Google */}
          {gcEventos.length > 0 && (
            <div>
              <p style={{fontSize:11,fontWeight:700,color:theme.gray600,
                textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>
                Eventos no Google Calendar — {gcEventos.length} evento(s)
              </p>
              <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:400,overflowY:"auto"}}>
                {gcEventos.map((e,i)=>{
                  const jaImportado = agenda.some(a=>a.googleId===e.googleId);
                  return (
                    <div key={i} style={{padding:"10px 14px",borderRadius:8,
                      background:jaImportado?"#f0fdf4":theme.white,
                      border:`1px solid ${jaImportado?"#bbf7d0":theme.gray200}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <p style={{fontSize:13,fontWeight:600,color:theme.gray900,margin:0}}>
                            {jaImportado&&<span style={{marginRight:6,fontSize:10,color:theme.green}}>✓ Importado</span>}
                            {e.titulo}
                          </p>
                          <p style={{fontSize:11,color:theme.gray500,margin:"3px 0 0"}}>
                            {fmtDt(e.inicio)} → {fmtDt(e.fim)}
                            {e.local&&<span> · 📍 {e.local}</span>}
                          </p>
                        </div>
                        <a href={e.link} target="_blank" rel="noreferrer"
                          style={{fontSize:11,color:"#4285f4",textDecoration:"none"}}>
                          ↗ Google
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ModAgenda({ data, setData }) {
  const { agenda = [], clientes = [], contratos = [], equipe = [] } = data;

  const [aba,          setAba]          = useState("calendario");
  const [mesAtual,     setMesAtual]     = useState(() => { const d = new Date(); return { ano: d.getFullYear(), mes: d.getMonth() }; });
  const [diaSel,       setDiaSel]       = useState(null);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editEvento,   setEditEvento]   = useState(null);
  const [filtTipo,     setFiltTipo]     = useState("");
  const [filtStatus,   setFiltStatus]   = useState("");
  const [busca,        setBusca]        = useState("");
  const [detalheEvt,   setDetalheEvt]   = useState(null);
  const [periodoLinha, setPeriodoLinha] = useState(30);

  const hoje = new Date(); hoje.setHours(0,0,0,0);
  const hojeStr = hoje.toISOString().slice(0,10);

  const EMPTY_EVENTO = {
    id:"", titulo:"", tipo:"compromisso",
    data: hojeStr, horaInicio:"09:00", horaFim:"10:00",
    clienteId:"", processoId:"", responsavel:"",
    local:"", linkReuniao:"", descricao:"",
    status:"Confirmado", alerta:"1d", origemContrato:""
  };

  // Eventos automáticos de prazos
  const autoIds = new Set();
  const eventosPrazos = contratos
    .filter(c => c.prazo && !["Concluido","Arquivado"].includes(c.status))
    .map(c => {
      const id = "PRAZO_" + c.id;
      autoIds.add(id);
      const cli = clientes.find(cl => cl.id === c.clienteId);
      return { id, titulo: "Prazo: " + (cli?.nome || c.clienteId), tipo:"prazo",
        data: c.prazo, horaInicio:"23:59", horaFim:"23:59",
        clienteId: c.clienteId, processoId: c.id, responsavel: c.responsavel,
        status:"Confirmado", descricao:`Prazo do contrato ${c.numContrato||c.id} — ${c.area}`,
        origemContrato: c.id, _auto: true };
    });

  const todosEventos = [...agenda.filter(e => !autoIds.has(e.id)), ...eventosPrazos];

  const getTipo    = id => TIPOS_EVENTO.find(t => t.id === id) || TIPOS_EVENTO[3];
  const getNomeCli = id => clientes.find(c => c.id === id)?.nome || "";
  const getNomeResp= id => equipe.find(e => e.id === id)?.nome || "";

  const eventosNoDia = (dia) => {
    const s = `${mesAtual.ano}-${String(mesAtual.mes+1).padStart(2,"0")}-${String(dia).padStart(2,"0")}`;
    return todosEventos.filter(e => e.data === s).sort((a,b) => a.horaInicio.localeCompare(b.horaInicio));
  };

  const alertas = todosEventos.filter(e => {
    const diff = Math.ceil((new Date(e.data+"T00:00:00") - hoje) / 86400000);
    return diff >= 0 && diff <= 3 && !["Cancelado","Realizado"].includes(e.status);
  }).sort((a,b) => a.data.localeCompare(b.data));

  const proxEventos = todosEventos
    .filter(e => e.data >= hojeStr)
    .sort((a,b) => a.data.localeCompare(b.data) || a.horaInicio.localeCompare(b.horaInicio))
    .slice(0,6);


  const salvar = () => {
    if (!editEvento.titulo || !editEvento.data) return alert("Título e data são obrigatórios.");
    const id = editEvento.id || "EVT" + String(Date.now()).slice(-7);
    const isNovo = !editEvento.id;
    const novo = { ...editEvento, id };

    // Tentar sync Google em background (não bloqueia o save)
    try {
      fetch(`${GC_BASE}/google_auth.php?action=status`)
        .then(r=>r.json())
        .then(d => {
          if (!d.authenticated) return;
          if (isNovo) {
            fetch(`${GC_BASE}/google_calendar.php?action=create`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify({
                titulo: novo.titulo, descricao: novo.descricao||"",
                local: novo.local||"",
                inicio: `${novo.data}T${novo.horaInicio||"09:00"}:00-03:00`,
                fim:    `${novo.data}T${novo.horaFim||"10:00"}:00-03:00`,
              })
            }).then(r=>r.json()).then(res=>{
              if (res.success && res.googleId) {
                setData(prev=>({...prev, agenda: prev.agenda.map(e=>
                  e.id===id ? {...e, googleId:res.googleId} : e
                )}));
              }
            }).catch(()=>{});
          } else if (novo.googleId) {
            fetch(`${GC_BASE}/google_calendar.php?action=update`, {
              method:"PUT", headers:{"Content-Type":"application/json"},
              body: JSON.stringify({
                googleId: novo.googleId, titulo: novo.titulo,
                descricao: novo.descricao||"", local: novo.local||"",
                inicio: `${novo.data}T${novo.horaInicio||"09:00"}:00-03:00`,
                fim:    `${novo.data}T${novo.horaFim||"10:00"}:00-03:00`,
              })
            }).catch(()=>{});
          }
        }).catch(()=>{});
    } catch(e) {}

    setData(prev => ({
      ...prev,
      agenda: isNovo
        ? [...(prev.agenda||[]), novo]
        : prev.agenda.map(e => e.id === id ? novo : e)
    }));
    setModalOpen(false);
  };

  const remover = id => {
    if (!confirm("Remover este evento?")) return;
    const evento = (agenda||[]).find(e=>e.id===id);
    // Tentar remover do Google em background
    if (evento?.googleId) {
      try {
        fetch(`${GC_BASE}/google_auth.php?action=status`).then(r=>r.json()).then(d=>{
          if (d.authenticated) {
            fetch(`${GC_BASE}/google_calendar.php?action=delete&googleId=${encodeURIComponent(evento.googleId)}`,
              {method:"DELETE"}).catch(()=>{});
          }
        }).catch(()=>{});
      } catch(e) {}
    }
    setData(prev => ({ ...prev, agenda: prev.agenda.filter(e => e.id !== id) }));
    setDetalheEvt(null);
  };

  const marcarRealizado = id => {
    setData(prev => ({ ...prev, agenda: prev.agenda.map(e => e.id===id ? {...e,status:"Realizado"} : e) }));
    setDetalheEvt(null);
  };

  const abrirWhatsApp = (evt) => {
    const cli = clientes.find(c => c.id === evt.clienteId);
    const tel = whatsCompleto(cli?.whatsappDDI||"55", cli?.whatsapp||"");
    if (!tel) return alert("Cliente sem WhatsApp cadastrado.");
    const dataFmt = new Date(evt.data+"T12:00:00").toLocaleDateString("pt-BR");
    const msg = encodeURIComponent(
      `Olá${cli?.nome?", "+cli.nome.split(" ")[0]:""}! 👋\n\n` +
      `Passando para confirmar nosso compromisso:\n\n` +
      `📅 *${evt.titulo}*\n🗓 Data: ${dataFmt}\n⏰ Horário: ${evt.horaInicio}` +
      `${evt.horaFim&&evt.horaFim!==evt.horaInicio?" às "+evt.horaFim:""}` +
      `${evt.local?"\n📍 Local: "+evt.local:""}` +
      `\n\nPor favor, confirme sua presença. Até lá! 😊\n\n*Alves & Correa Advogados*`
    );
    window.open(`https://wa.me/${tel}?text=${msg}`, "_blank");
  };

  const novoEvento = (dataInit) => {
    setEditEvento({ ...EMPTY_EVENTO, data: dataInit || hojeStr });
    setModalOpen(true);
  };

  const { primeiro, total } = (() => {
    return { primeiro: new Date(mesAtual.ano, mesAtual.mes, 1).getDay(),
             total: new Date(mesAtual.ano, mesAtual.mes+1, 0).getDate() };
  })();

  const eventosFiltLista = todosEventos.filter(e => {
    const b = busca.toLowerCase();
    return (!busca||e.titulo.toLowerCase().includes(b)||(e.descricao||"").toLowerCase().includes(b))
        && (!filtTipo  ||e.tipo===filtTipo)
        && (!filtStatus||e.status===filtStatus);
  }).sort((a,b)=>a.data.localeCompare(b.data)||a.horaInicio.localeCompare(b.horaInicio));

  const eventosHoje = todosEventos.filter(e=>e.data===hojeStr);

  return (
    <div className="fade-in">
      <PageHeader
        title="Agenda"
        subtitle={`${eventosHoje.length} evento${eventosHoje.length!==1?"s":""} hoje • ${alertas.length} alerta${alertas.length!==1?"s":""} em 3 dias`}
        action={
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <div style={{display:"flex",background:theme.gray100,borderRadius:8,padding:3}}>
              {[["calendario","📅 Calendário"],["lista","☰ Lista"],["timeline","⏱️ Linha do Tempo"],["google","🔗 Google Calendar"]].map(([id,lbl])=>(
                <button key={id} onClick={()=>setAba(id)} style={{
                  padding:"6px 12px",borderRadius:6,fontSize:12,
                  fontWeight:aba===id?700:400,
                  background:aba===id?theme.white:"transparent",
                  color:aba===id?theme.gray900:theme.gray500,
                  border:"none",cursor:"pointer",
                  boxShadow:aba===id?"0 1px 3px rgba(0,0,0,0.1)":"none"
                }}>{lbl}</button>
              ))}
            </div>
            <Btn onClick={()=>novoEvento()}>+ Novo Evento</Btn>
          </div>
        }
      />

      {/* Alertas */}
      {alertas.length > 0 && (
        <div style={{marginBottom:20,display:"flex",flexDirection:"column",gap:6}}>
          {alertas.map(e => {
            const diff = Math.ceil((new Date(e.data+"T00:00:00")-hoje)/86400000);
            const tp   = getTipo(e.tipo);
            const urg  = diff===0||e.tipo==="prazo"||e.tipo==="audiencia";
            return (
              <div key={e.id} onClick={()=>setDetalheEvt(e)} style={{
                display:"flex",alignItems:"center",gap:12,
                padding:"10px 18px",borderRadius:8,cursor:"pointer",
                background:urg?"#FEF2F2":"#FEFCE8",
                border:`1px solid ${urg?"#FECACA":"#FDE68A"}`
              }}>
                <span style={{fontSize:18}}>{tp.icon}</span>
                <div style={{flex:1}}>
                  <span style={{fontSize:13,fontWeight:600,color:urg?theme.red:theme.amber}}>{e.titulo}</span>
                  {getNomeCli(e.clienteId)&&<span style={{fontSize:11,color:theme.gray500,marginLeft:8}}>— {getNomeCli(e.clienteId)}</span>}
                </div>
                <span style={{fontSize:12,color:urg?theme.red:theme.amber,fontWeight:600}}>
                  {diff===0?"🔴 HOJE":diff===1?"🟡 Amanhã":`🟠 Em ${diff} dias`}
                </span>
                <span style={{fontSize:11,color:theme.gray500}}>{e.horaInicio}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:14,marginBottom:20}}>
        {[
          {l:"Hoje",        v:eventosHoje.length,icon:"📅",c:theme.blue},
          {l:"Esta semana", v:todosEventos.filter(e=>{const d=Math.ceil((new Date(e.data+"T00:00:00")-hoje)/86400000);return d>=0&&d<=7;}).length,icon:"📆",c:theme.goldDark},
          {l:"Audiências",  v:todosEventos.filter(e=>e.tipo==="audiencia"&&e.data>=hojeStr).length,icon:"⚖️",c:"#dc2626"},
          {l:"Prazos (3d)", v:alertas.filter(e=>e.tipo==="prazo").length,icon:"⏰",c:theme.red},
          {l:"Pendentes",   v:todosEventos.filter(e=>e.status==="Pendente").length,icon:"⚠️",c:theme.amber},
        ].map(k=>(
          <Card key={k.l} style={{padding:"14px 18px"}}>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{k.l}</p>
              <span style={{fontSize:16}}>{k.icon}</span>
            </div>
            <p style={{fontSize:24,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
          </Card>
        ))}
      </div>

      {/* ABA CALENDÁRIO */}
      {aba==="calendario" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:20}}>
          <Card style={{padding:0,overflow:"hidden"}}>
            {/* Nav */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px",borderBottom:`1px solid ${theme.gray100}`}}>
              <button onClick={()=>setMesAtual(p=>{const d=new Date(p.ano,p.mes-1,1);return{ano:d.getFullYear(),mes:d.getMonth()};}) }
                style={{padding:"6px 14px",border:`1px solid ${theme.gray200}`,borderRadius:7,background:"transparent",cursor:"pointer",fontSize:18,lineHeight:1}}>‹</button>
              <h2 className="serif" style={{fontSize:20,fontWeight:600,color:theme.gray900}}>
                {MESES_AGENDA[mesAtual.mes]} {mesAtual.ano}
              </h2>
              <div style={{display:"flex",gap:8}}>
                <Btn size="sm" variant="secondary" onClick={()=>setMesAtual({ano:hoje.getFullYear(),mes:hoje.getMonth()})}>Hoje</Btn>
                <button onClick={()=>setMesAtual(p=>{const d=new Date(p.ano,p.mes+1,1);return{ano:d.getFullYear(),mes:d.getMonth()};}) }
                  style={{padding:"6px 14px",border:`1px solid ${theme.gray200}`,borderRadius:7,background:"transparent",cursor:"pointer",fontSize:18,lineHeight:1}}>›</button>
              </div>
            </div>
            {/* Dias semana */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",borderBottom:`1px solid ${theme.gray100}`}}>
              {DIAS_SEM.map(d=>(
                <div key={d} style={{padding:"8px 0",textAlign:"center",fontSize:11,fontWeight:700,color:theme.gray400,letterSpacing:.8}}>{d}</div>
              ))}
            </div>
            {/* Grid dias */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)"}}>
              {Array.from({length:primeiro}).map((_,i)=>(
                <div key={"v"+i} style={{minHeight:88,border:`1px solid ${theme.gray100}`,borderLeft:"none",borderTop:"none",background:theme.gray50}} />
              ))}
              {Array.from({length:total}).map((_,i)=>{
                const dia    = i+1;
                const evts   = eventosNoDia(dia);
                const dStr   = `${mesAtual.ano}-${String(mesAtual.mes+1).padStart(2,"0")}-${String(dia).padStart(2,"0")}`;
                const isHoje = dStr === hojeStr;
                const isSel  = diaSel === dia;
                const col    = (i+primeiro)%7;
                return (
                  <div key={dia} onClick={()=>setDiaSel(isSel?null:dia)} style={{
                    minHeight:88,padding:"6px 8px",
                    border:`1px solid ${theme.gray100}`,borderLeft:"none",borderTop:"none",
                    background:isSel?`${theme.gold}12`:isHoje?"#EFF6FF":col===0||col===6?theme.gray50:theme.white,
                    cursor:"pointer",borderTop:isHoje?`2px solid ${theme.blue}`:isSel?`2px solid ${theme.gold}`:`1px solid ${theme.gray100}`,
                  }}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                      <span style={{
                        fontSize:12,fontWeight:isHoje?800:400,
                        width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",
                        borderRadius:"50%",
                        background:isHoje?theme.blue:"transparent",
                        color:isHoje?"#fff":col===0||col===6?"#dc262699":theme.gray700
                      }}>{dia}</span>
                      {evts.length>0&&<span style={{fontSize:9,background:theme.gold,color:"#fff",borderRadius:10,padding:"1px 5px",fontWeight:700}}>{evts.length}</span>}
                    </div>
                    {evts.slice(0,2).map(e=>{
                      const tp=getTipo(e.tipo);
                      return (
                        <div key={e.id} onClick={ev=>{ev.stopPropagation();setDetalheEvt(e);}} style={{
                          fontSize:9,padding:"2px 5px",borderRadius:3,marginBottom:2,
                          background:tp.cor+"22",color:tp.cor,fontWeight:600,
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                          borderLeft:`2px solid ${tp.cor}`,cursor:"pointer",
                          opacity:e.status==="Cancelado" ? 0.4 : 1
                        }}>{tp.icon} {e.titulo}</div>
                      );
                    })}
                    {evts.length>2&&<div style={{fontSize:9,color:theme.gray400}}>+{evts.length-2} mais</div>}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Sidebar */}
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <Card style={{padding:"16px 18px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <p className="serif" style={{fontSize:15,fontWeight:600,color:theme.gray900}}>
                  {diaSel?`${diaSel} de ${MESES_AGENDA[mesAtual.mes]}`:"Próximos eventos"}
                </p>
                <Btn size="sm" onClick={()=>novoEvento(diaSel?`${mesAtual.ano}-${String(mesAtual.mes+1).padStart(2,"0")}-${String(diaSel).padStart(2,"0")}`:null)}>+</Btn>
              </div>
              {(diaSel?eventosNoDia(diaSel):proxEventos).length===0
                ? <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"12px 0"}}>{diaSel?"Nenhum evento neste dia.":"Sem eventos próximos."}</p>
                : (diaSel?eventosNoDia(diaSel):proxEventos).map(e=>{
                    const tp=getTipo(e.tipo);
                    return (
                      <div key={e.id} onClick={()=>setDetalheEvt(e)} style={{
                        display:"flex",gap:8,padding:"9px 10px",borderRadius:7,
                        border:`1px solid ${theme.gray100}`,marginBottom:7,cursor:"pointer",
                        borderLeft:`3px solid ${tp.cor}`,
                        background:e.status==="Realizado"?theme.gray50:theme.white,
                        opacity:e.status==="Cancelado" ? 0.5 : 1
                      }}>
                        <span style={{fontSize:16,flexShrink:0}}>{tp.icon}</span>
                        <div style={{flex:1,minWidth:0}}>
                          <p style={{fontSize:12,fontWeight:600,color:theme.gray900,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.titulo}</p>
                          <p style={{fontSize:10,color:theme.gray500,marginTop:1}}>
                            {!diaSel&&<span>{new Date(e.data+"T12:00:00").toLocaleDateString("pt-BR")} · </span>}
                            {e.horaInicio}
                          </p>
                        </div>
                        <Badge color={e.status==="Confirmado"?"green":e.status==="Pendente"?"amber":e.status==="Realizado"?"gray":"red"} style={{fontSize:9,alignSelf:"flex-start"}}>{e.status}</Badge>
                      </div>
                    );
                  })
              }
            </Card>
            <Card style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:10,fontWeight:700}}>Legenda</p>
              {TIPOS_EVENTO.map(t=>(
                <div key={t.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                  <div style={{width:10,height:10,borderRadius:2,background:t.cor,flexShrink:0}}/>
                  <span style={{fontSize:11,color:theme.gray700}}>{t.icon} {t.label}</span>
                </div>
              ))}
            </Card>
          </div>
        </div>
      )}

      {/* ABA LISTA */}
      {aba==="lista" && (
        <div>
          <Card style={{padding:"14px 20px",marginBottom:16,display:"flex",gap:10,flexWrap:"wrap"}}>
            <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="🔍 Buscar..."
              style={{flex:1,minWidth:200,padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}
              onFocus={e=>e.target.style.borderColor=theme.gold} onBlur={e=>e.target.style.borderColor=theme.gray200}/>
            <select value={filtTipo} onChange={e=>setFiltTipo(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
              <option value="">Todos os tipos</option>
              {TIPOS_EVENTO.map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
            </select>
            <select value={filtStatus} onChange={e=>setFiltStatus(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
              <option value="">Todos os status</option>
              {["Confirmado","Pendente","Cancelado","Realizado"].map(s=><option key={s}>{s}</option>)}
            </select>
            {(busca||filtTipo||filtStatus)&&<Btn size="sm" variant="secondary" onClick={()=>{setBusca("");setFiltTipo("");setFiltStatus("");}}>✕ Limpar</Btn>}
          </Card>
          <Card>
            {eventosFiltLista.length===0
              ? <EmptyState icon="📅" title="Nenhum evento encontrado" subtitle="Ajuste os filtros ou crie um novo evento."/>
              : <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead>
                      <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                        {["Evento","Data / Hora","Cliente","Tipo","Responsável","Status","Ações"].map(h=>(
                          <th key={h} style={{padding:"11px 14px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {eventosFiltLista.map((e,i)=>{
                        const tp=getTipo(e.tipo);
                        const dataEvt=new Date(e.data+"T12:00:00");
                        const passado=e.data<hojeStr&&e.status!=="Realizado";
                        return (
                          <tr key={e.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50,opacity:e.status==="Cancelado" ? 0.5 : 1}}>
                            <td style={{padding:"11px 14px"}}>
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                <span style={{fontSize:16}}>{tp.icon}</span>
                                <div>
                                  <p style={{fontSize:13,fontWeight:600,color:theme.gray900}}>{e.titulo}</p>
                                  {e.local&&<p style={{fontSize:11,color:theme.gray400}}>📍 {e.local}</p>}
                                </div>
                              </div>
                            </td>
                            <td style={{padding:"11px 14px"}}>
                              <p style={{fontSize:12,fontWeight:600,color:passado?theme.red:theme.gray800}}>
                                {dataEvt.toLocaleDateString("pt-BR",{weekday:"short",day:"numeric",month:"short"})}
                              </p>
                              <p style={{fontSize:11,color:theme.gray500}}>{e.horaInicio}{e.horaFim&&e.horaFim!==e.horaInicio?" — "+e.horaFim:""}</p>
                            </td>
                            <td style={{padding:"11px 14px",fontSize:12,color:theme.gray600}}>{getNomeCli(e.clienteId)||"—"}</td>
                            <td style={{padding:"11px 14px"}}>
                              <span style={{fontSize:11,padding:"2px 8px",borderRadius:4,background:tp.cor+"22",color:tp.cor,fontWeight:600}}>{tp.label}</span>
                            </td>
                            <td style={{padding:"11px 14px",fontSize:12,color:theme.gray600}}>{getNomeResp(e.responsavel)||"—"}</td>
                            <td style={{padding:"11px 14px"}}>
                              <Badge color={e.status==="Confirmado"?"green":e.status==="Pendente"?"amber":e.status==="Realizado"?"gray":"red"}>{e.status}</Badge>
                            </td>
                            <td style={{padding:"11px 14px"}}>
                              <div style={{display:"flex",gap:5}}>
                                <Btn size="sm" variant="secondary" onClick={()=>setDetalheEvt(e)}>Ver</Btn>
                                {e.clienteId&&!e._auto&&<Btn size="sm" variant="secondary" onClick={()=>abrirWhatsApp(e)} title="WhatsApp">📱</Btn>}
                                {!e._auto&&<Btn size="sm" variant="secondary" onClick={()=>{setEditEvento({...e});setModalOpen(true);}}>✏️</Btn>}
                                {!e._auto&&<Btn size="sm" variant="danger" onClick={()=>remover(e.id)}>×</Btn>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
            }
          </Card>
        </div>
      )}

      {/* ABA LINHA DO TEMPO */}
      {aba==="timeline" && (
        <div>
          <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center"}}>
            <span style={{fontSize:12,color:theme.gray600}}>Período:</span>
            {[7,14,30,60,90].map(d=>(
              <button key={d} onClick={()=>setPeriodoLinha(d)} style={{
                padding:"6px 14px",borderRadius:6,fontSize:12,cursor:"pointer",fontWeight:600,
                border:`1px solid ${periodoLinha===d?theme.gold:theme.gray200}`,
                background:periodoLinha===d?`${theme.gold}18`:theme.white,
                color:periodoLinha===d?theme.goldDark:theme.gray600
              }}>{d} dias</button>
            ))}
          </div>
          {(()=>{
            const fim = new Date(hoje); fim.setDate(fim.getDate()+periodoLinha);
            const fimStr = fim.toISOString().slice(0,10);
            const evts = todosEventos
              .filter(e=>e.data>=hojeStr&&e.data<=fimStr&&e.status!=="Cancelado")
              .sort((a,b)=>a.data.localeCompare(b.data));

            if (evts.length===0) return <Card><EmptyState icon="⏱️" title="Nenhum evento no período" subtitle="Cadastre eventos ou prazos de contratos para visualizar aqui."/></Card>;

            const semanas = {};
            evts.forEach(e=>{
              const d=new Date(e.data+"T00:00:00");
              const ini=new Date(d); ini.setDate(d.getDate()-d.getDay());
              const key=ini.toISOString().slice(0,10);
              if(!semanas[key]) semanas[key]={label:`Semana de ${ini.toLocaleDateString("pt-BR",{day:"numeric",month:"short"})}`,eventos:[]};
              semanas[key].eventos.push(e);
            });

            return Object.entries(semanas).sort().map(([key,sem])=>(
              <div key={key} style={{marginBottom:24}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                  <div style={{padding:"4px 14px",background:theme.gray900,color:theme.gold,borderRadius:6,fontSize:11,fontWeight:700,letterSpacing:.8}}>{sem.label}</div>
                  <span style={{fontSize:11,color:theme.gray400}}>{sem.eventos.length} evento{sem.eventos.length!==1?"s":""}</span>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {sem.eventos.map(e=>{
                    const tp=getTipo(e.tipo);
                    const dataFmt=new Date(e.data+"T12:00:00").toLocaleDateString("pt-BR",{weekday:"long",day:"numeric",month:"long"});
                    const diff=Math.ceil((new Date(e.data+"T00:00:00")-hoje)/86400000);
                    return (
                      <div key={e.id} onClick={()=>setDetalheEvt(e)} style={{
                        display:"flex",gap:16,alignItems:"center",
                        padding:"14px 18px",borderRadius:10,cursor:"pointer",
                        border:`1px solid ${tp.cor}33`,background:`${tp.cor}08`,
                        borderLeft:`5px solid ${tp.cor}`,transition:"box-shadow .15s"
                      }} onMouseEnter={el=>el.currentTarget.style.boxShadow="0 2px 10px rgba(0,0,0,.1)"}
                         onMouseLeave={el=>el.currentTarget.style.boxShadow="none"}>
                        <span style={{fontSize:24,flexShrink:0}}>{tp.icon}</span>
                        <div style={{flex:1,minWidth:0}}>
                          <p style={{fontSize:14,fontWeight:700,color:theme.gray900}}>{e.titulo}</p>
                          <div style={{display:"flex",gap:12,marginTop:4,fontSize:11,color:theme.gray500,flexWrap:"wrap"}}>
                            <span>📅 {dataFmt}</span>
                            <span>⏰ {e.horaInicio}{e.horaFim&&e.horaFim!==e.horaInicio?" — "+e.horaFim:""}</span>
                            {e.local&&<span>📍 {e.local}</span>}
                            {getNomeCli(e.clienteId)&&<span>👤 {getNomeCli(e.clienteId)}</span>}
                          </div>
                          {e.descricao&&<p style={{fontSize:12,color:theme.gray600,marginTop:6,fontStyle:"italic"}}>{e.descricao}</p>}
                          {e.origemLeadId&&<div style={{marginTop:5}}><span style={{fontSize:10,background:"#eef2ff",color:"#6366f1",fontWeight:600,padding:"2px 8px",borderRadius:10}}>🎯 Lead CRM</span></div>}
                        </div>
                        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6,flexShrink:0}}>
                          <Badge color={e.status==="Confirmado"?"green":e.status==="Pendente"?"amber":"gray"}>{e.status}</Badge>
                          <span style={{fontSize:11,fontWeight:700,color:diff===0?theme.red:diff===1?theme.amber:theme.gray500}}>
                            {diff===0?"Hoje":diff===1?"Amanhã":`Em ${diff}d`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* ABA GOOGLE CALENDAR */}
      {aba==="google" && (
        <GoogleCalendarSync
          agenda={agenda}
          onSaveAgenda={evts => setData(prev=>({...prev, agenda:evts}))}
        />
      )}

      {/* MODAL DETALHE */}
      {detalheEvt && (
        <Modal open={!!detalheEvt} onClose={()=>setDetalheEvt(null)} title="Detalhes do Evento" width={540}>
          {(()=>{
            const e=detalheEvt; const tp=getTipo(e.tipo);
            return (
              <>
                <div style={{display:"flex",alignItems:"center",gap:14,padding:"14px 18px",background:`${tp.cor}11`,borderRadius:10,marginBottom:20,border:`1px solid ${tp.cor}33`}}>
                  <span style={{fontSize:32}}>{tp.icon}</span>
                  <div style={{flex:1}}>
                    <p style={{fontSize:17,fontWeight:700,color:theme.gray900}}>{e.titulo}</p>
                    <p style={{fontSize:12,color:tp.cor,fontWeight:600,marginTop:2}}>{tp.label}</p>
                  </div>
                  <Badge color={e.status==="Confirmado"?"green":e.status==="Pendente"?"amber":e.status==="Realizado"?"gray":"red"}>{e.status}</Badge>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px 24px",marginBottom:20}}>
                  {[
                    {l:"Data",        v:new Date(e.data+"T12:00:00").toLocaleDateString("pt-BR",{weekday:"long",day:"numeric",month:"long",year:"numeric"})},
                    {l:"Horário",     v:`${e.horaInicio}${e.horaFim&&e.horaFim!==e.horaInicio?" — "+e.horaFim:""}`},
                    {l:"Cliente",     v:getNomeCli(e.clienteId)||"—"},
                    {l:"Responsável", v:getNomeResp(e.responsavel)||"—"},
                    {l:"Local",       v:e.local||"—"},
                    {l:"Alerta",      v:ALERTAS_OPTS.find(a=>a.value===e.alerta)?.label||"—"},
                  ].map(row=>(
                    <div key={row.l}>
                      <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{row.l}</p>
                      <p style={{fontSize:13,color:theme.gray800,marginTop:2,fontWeight:500}}>{row.v}</p>
                    </div>
                  ))}
                </div>
                {e.descricao&&<div style={{background:theme.gray50,borderRadius:8,padding:"10px 14px",marginBottom:16}}>
                  <p style={{fontSize:11,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>Descrição</p>
                  <p style={{fontSize:13,color:theme.gray700,lineHeight:1.6}}>{e.descricao}</p>
                </div>}
                {e.linkReuniao&&<a href={e.linkReuniao} target="_blank" rel="noreferrer" style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:13,color:theme.blue,marginBottom:14,textDecoration:"none"}}>🔗 Abrir link da reunião</a>}
                {e._auto&&<div style={{padding:"8px 14px",background:`${theme.amber}11`,border:`1px solid ${theme.amber}33`,borderRadius:7,marginBottom:14,fontSize:12,color:theme.amber}}>⚡ Gerado automaticamente a partir do prazo do contrato</div>}
                <div style={{display:"flex",gap:8,paddingTop:16,borderTop:`1px solid ${theme.gray100}`,flexWrap:"wrap"}}>
                  {e.clienteId&&!e._auto&&<Btn onClick={()=>abrirWhatsApp(e)} style={{background:"#25D366",color:"#fff",border:"none"}}>📱 WhatsApp</Btn>}
                  {!e._auto&&e.status!=="Realizado"&&<Btn variant="secondary" onClick={()=>marcarRealizado(e.id)}>✅ Realizado</Btn>}
                  {!e._auto&&<Btn variant="secondary" onClick={()=>{setEditEvento({...e});setDetalheEvt(null);setModalOpen(true);}}>✏️ Editar</Btn>}
                  {!e._auto&&<Btn variant="danger" onClick={()=>remover(e.id)}>Remover</Btn>}
                  <Btn variant="secondary" onClick={()=>setDetalheEvt(null)} style={{marginLeft:"auto"}}>Fechar</Btn>
                </div>
              </>
            );
          })()}
        </Modal>
      )}

      {/* MODAL CADASTRO */}
      <Modal open={modalOpen} onClose={()=>setModalOpen(false)} title={editEvento?.id?"Editar Evento":"Novo Evento"} width={660}>
        {editEvento && (
          <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Título do Evento" required>
                  <Input value={editEvento.titulo} onChange={v=>setEditEvento({...editEvento,titulo:v})} placeholder="Ex: Audiência de Instrução — João Carlos"/>
                </FormField>
              </div>
              <FormField label="Tipo" required>
                <select value={editEvento.tipo} onChange={e=>setEditEvento({...editEvento,tipo:e.target.value})}
                  style={{width:"100%",padding:"9px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
                  {TIPOS_EVENTO.map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
                </select>
              </FormField>
              <FormField label="Status">
                <Select value={editEvento.status} onChange={v=>setEditEvento({...editEvento,status:v})} options={["Confirmado","Pendente","Cancelado","Realizado"]}/>
              </FormField>
              <FormField label="Data" required>
                <Input type="date" value={editEvento.data} onChange={v=>setEditEvento({...editEvento,data:v})}/>
              </FormField>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <FormField label="Hora Início">
                  <Input type="time" value={editEvento.horaInicio} onChange={v=>setEditEvento({...editEvento,horaInicio:v})}/>
                </FormField>
                <FormField label="Hora Fim">
                  <Input type="time" value={editEvento.horaFim} onChange={v=>setEditEvento({...editEvento,horaFim:v})}/>
                </FormField>
              </div>
              <FormField label="Alertar antes">
                <Select value={editEvento.alerta} onChange={v=>setEditEvento({...editEvento,alerta:v})} options={ALERTAS_OPTS.map(a=>({value:a.value,label:a.label}))}/>
              </FormField>
              <FormField label="Cliente vinculado">
                <Select value={editEvento.clienteId} onChange={v=>setEditEvento({...editEvento,clienteId:v})} options={clientes.map(c=>({value:c.id,label:c.nome}))} placeholder="Nenhum..."/>
              </FormField>
              <FormField label="Contrato vinculado">
                <Select value={editEvento.processoId} onChange={v=>setEditEvento({...editEvento,processoId:v})}
                  options={contratos.filter(c=>!editEvento.clienteId||c.clienteId===editEvento.clienteId).map(c=>({value:c.id,label:(c.numContrato||c.id)+" — "+c.area}))}
                  placeholder="Nenhum..."/>
              </FormField>
              <FormField label="Responsável">
                <Select value={editEvento.responsavel} onChange={v=>setEditEvento({...editEvento,responsavel:v})} options={equipe.filter(e=>e.ativo!==false).map(e=>({value:e.id,label:e.nome}))} placeholder="Selecione..."/>
              </FormField>
              <FormField label="Local / Endereço">
                <Input value={editEvento.local} onChange={v=>setEditEvento({...editEvento,local:v})} placeholder="Ex: Fórum Central, Sala 3 ou Online"/>
              </FormField>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Link da Reunião (opcional)">
                  <Input value={editEvento.linkReuniao} onChange={v=>setEditEvento({...editEvento,linkReuniao:v})} placeholder="https://meet.google.com/... ou https://zoom.us/..."/>
                </FormField>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Descrição / Observações">
                  <Textarea value={editEvento.descricao} onChange={v=>setEditEvento({...editEvento,descricao:v})} placeholder="Detalhes, pauta, documentos necessários..." rows={3}/>
                </FormField>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
              <Btn variant="secondary" onClick={()=>setModalOpen(false)}>Cancelar</Btn>
              <Btn onClick={salvar}>Salvar Evento</Btn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}


function ModArquivo({ data, setData }) {
  const areas = data.areas || AREAS_INICIAIS;
  const tipos = data.tiposArquivo || [];
  const docs  = data.documentosArquivo || [];
  const equipe = data.equipe || [];

  const [modalOpen,   setModalOpen]   = useState(false);
  const [modalTipos,  setModalTipos]  = useState(false);
  const [editItem,    setEditItem]    = useState(null);
  const [novoTipo,    setNovoTipo]    = useState("");
  const [editTipoIdx, setEditTipoIdx] = useState(null);
  const [filtArea,    setFiltArea]    = useState("");
  const [filtTipo,    setFiltTipo]    = useState("");
  const [filtAcesso,  setFiltAcesso]  = useState("");
  const [busca,       setBusca]       = useState("");
  const [viewMode,    setViewMode]    = useState("cards");

  const EMPTY = {
    id: "", nome: "", area: "", tipoId: "", descricao: "",
    link: "", responsavel: "", dataAtualizacao: "", acesso: "Interno", status: "Ativo", obs: ""
  };

  const filtrados = docs.filter(d => {
    const b = busca.toLowerCase();
    return (!busca || d.nome.toLowerCase().includes(b) || (d.descricao||"").toLowerCase().includes(b))
        && (!filtArea   || d.area   === filtArea)
        && (!filtTipo   || d.tipoId === filtTipo)
        && (!filtAcesso || d.acesso  === filtAcesso);
  }).sort((a,b)=>(a.nome||"").localeCompare(b.nome||"","pt-BR"));

  const grupos = {};
  filtrados.forEach(d => { const g = d.area || "Sem Área"; if (!grupos[g]) grupos[g] = []; grupos[g].push(d); });
  const gruposKeys = Object.keys(grupos).sort((a,b) => a.localeCompare(b, "pt-BR"));

  const getNomeTipo = id => tipos.find(t => t.id === id)?.nome || id || "—";
  const getNomeResp = id => equipe.find(e => e.id === id)?.nome || "—";
  const iconTipo = nome => ({"Modelo de Contrato":"📝","Procuração":"✍️","Petição":"⚖️","Recurso":"📤","Parecer":"📋","Planilha":"📊","Formulário":"📄","Modelo de E-mail":"📧","Checklist":"☑️"}[nome] || "📎");

  const save = () => {
    if (!editItem.nome || !editItem.link) return alert("Nome e link são obrigatórios.");
    const id = editItem.id || "ARQ" + String(Date.now()).slice(-6);
    const novo = { ...editItem, id };
    setData(prev => ({
      ...prev,
      documentosArquivo: editItem.id
        ? prev.documentosArquivo.map(d => d.id === id ? novo : d)
        : [...(prev.documentosArquivo || []), novo]
    }));
    setModalOpen(false);
  };

  const remover = id => {
    if (!confirm("Remover este documento?")) return;
    setData(prev => ({ ...prev, documentosArquivo: prev.documentosArquivo.filter(d => d.id !== id) }));
  };

  const salvarTipo = () => {
    if (!novoTipo.trim()) return;
    if (editTipoIdx !== null) {
      setData(prev => ({ ...prev, tiposArquivo: prev.tiposArquivo.map((t,i) => i === editTipoIdx ? {...t, nome: novoTipo.trim()} : t) }));
      setEditTipoIdx(null);
    } else {
      setData(prev => ({ ...prev, tiposArquivo: [...(prev.tiposArquivo||[]), { id: "TAR"+Date.now().toString().slice(-6), nome: novoTipo.trim() }] }));
    }
    setNovoTipo("");
  };

  const removerTipo = id => {
    if (docs.some(d => d.tipoId === id)) return alert("Este tipo possui documentos vinculados. Remova-os primeiro.");
    if (!confirm("Remover tipo?")) return;
    setData(prev => ({ ...prev, tiposArquivo: prev.tiposArquivo.filter(t => t.id !== id) }));
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="Arquivo de Documentos"
        subtitle={`${filtrados.length} documento${filtrados.length !== 1 ? "s" : ""} encontrado${filtrados.length !== 1 ? "s" : ""}`}
        action={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ display: "flex", background: theme.gray100, borderRadius: 8, padding: 3 }}>
              {[["cards","⊞ Cards"],["lista","☰ Lista"]].map(([id,lbl]) => (
                <button key={id} onClick={() => setViewMode(id)} style={{
                  padding: "6px 12px", borderRadius: 6, fontSize: 12,
                  fontWeight: viewMode === id ? 700 : 400,
                  background: viewMode === id ? theme.white : "transparent",
                  color: viewMode === id ? theme.gray900 : theme.gray500,
                  border: "none", cursor: "pointer",
                  boxShadow: viewMode === id ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
                }}>{lbl}</button>
              ))}
            </div>
            <Btn size="sm" variant="secondary" onClick={() => setModalTipos(true)}>⚙️ Tipos</Btn>
            <Btn onClick={() => { setEditItem({ ...EMPTY }); setModalOpen(true); }}>+ Novo Documento</Btn>
          </div>
        }
      />

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 }}>
        {[
          { l: "Total", v: docs.length, icon: "🗂️", c: theme.gray800 },
          { l: "Ativos", v: docs.filter(d => d.status === "Ativo").length, icon: "✅", c: theme.green },
          { l: "Áreas cobertas", v: [...new Set(docs.map(d => d.area).filter(Boolean))].length, icon: "⚖️", c: theme.blue },
          { l: "Tipos cadastrados", v: tipos.length, icon: "📋", c: theme.goldDark },
        ].map(k => (
          <Card key={k.l} style={{ padding: "14px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <p style={{ fontSize: 10, color: theme.gray400, textTransform: "uppercase", letterSpacing: .8 }}>{k.l}</p>
              <span style={{ fontSize: 16 }}>{k.icon}</span>
            </div>
            <p style={{ fontSize: 24, fontWeight: 700, color: k.c, marginTop: 6 }}>{k.v}</p>
          </Card>
        ))}
      </div>

      {/* Filtros */}
      <Card style={{ padding: "14px 20px", marginBottom: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <input value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="🔍 Buscar por nome ou descrição..."
          style={{ flex: 1, minWidth: 200, padding: "8px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none", fontFamily: "Jost, sans-serif" }}
          onFocus={e => e.target.style.borderColor = theme.gold}
          onBlur={e => e.target.style.borderColor = theme.gray200} />
        <select value={filtArea} onChange={e => setFiltArea(e.target.value)}
          style={{ padding: "8px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none" }}>
          <option value="">Todas as áreas</option>
          {areas.map(a => <option key={a}>{a}</option>)}
        </select>
        <select value={filtTipo} onChange={e => setFiltTipo(e.target.value)}
          style={{ padding: "8px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none" }}>
          <option value="">Todos os tipos</option>
          {tipos.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
        <select value={filtAcesso} onChange={e => setFiltAcesso(e.target.value)}
          style={{ padding: "8px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none" }}>
          <option value="">Todos os acessos</option>
          <option>Interno</option>
          <option>Público</option>
        </select>
        {(busca || filtArea || filtTipo || filtAcesso) && (
          <Btn size="sm" variant="secondary" onClick={() => { setBusca(""); setFiltArea(""); setFiltTipo(""); setFiltAcesso(""); }}>✕ Limpar</Btn>
        )}
      </Card>

      {/* Conteúdo */}
      {docs.length === 0 ? (
        <Card><EmptyState icon="🗂️" title="Nenhum documento cadastrado" subtitle='Clique em "+ Novo Documento" para começar.' /></Card>
      ) : filtrados.length === 0 ? (
        <Card><EmptyState icon="🔍" title="Nenhum resultado" subtitle="Ajuste os filtros." /></Card>
      ) : viewMode === "cards" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {gruposKeys.map(area => (
            <div key={area}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div style={{ padding: "4px 14px", background: theme.gray900, color: theme.gold, borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: .8, textTransform: "uppercase" }}>{area}</div>
                <span style={{ fontSize: 11, color: theme.gray400 }}>{grupos[area].length} documento{grupos[area].length !== 1 ? "s" : ""}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                {grupos[area].map(doc => {
                  const tipoNome = getNomeTipo(doc.tipoId);
                  return (
                    <Card key={doc.id} style={{ padding: 0, overflow: "hidden", opacity: doc.status === "Ativo" ? 1 : .6 }}>
                      <div style={{ padding: "12px 16px", background: `linear-gradient(135deg, ${theme.gray900}, ${theme.gray700})`, display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 22 }}>{iconTipo(tipoNome)}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 700, color: "#fff", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.nome}</p>
                          <p style={{ fontSize: 10, color: "rgba(255,255,255,.6)", marginTop: 2 }}>{tipoNome}</p>
                        </div>
                        <Badge color={doc.acesso === "Público" ? "green" : "amber"}>{doc.acesso}</Badge>
                      </div>
                      <div style={{ padding: "12px 16px" }}>
                        {doc.descricao && (
                          <p style={{ fontSize: 12, color: theme.gray600, lineHeight: 1.5, marginBottom: 10,
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{doc.descricao}</p>
                        )}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, fontSize: 11, color: theme.gray500 }}>
                          {doc.responsavel && <span>👤 {getNomeResp(doc.responsavel)}</span>}
                          {doc.dataAtualizacao && <span>📅 {new Date(doc.dataAtualizacao + "T00:00:00").toLocaleDateString("pt-BR")}</span>}
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <a href={doc.link} target="_blank" rel="noreferrer" style={{
                            flex: 1, padding: "7px 0", borderRadius: 7, textAlign: "center",
                            background: `linear-gradient(135deg, ${theme.gray900}, ${theme.gray700})`,
                            color: theme.gold, fontSize: 12, fontWeight: 700, textDecoration: "none",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 5
                          }}>🔗 Abrir documento</a>
                          <Btn size="sm" variant="secondary" onClick={() => { setEditItem({ ...doc }); setModalOpen(true); }}>✏️</Btn>
                          <Btn size="sm" variant="danger" onClick={() => remover(doc.id)}>×</Btn>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.gray100}` }}>
                  {["Nome", "Área", "Tipo", "Acesso", "Responsável", "Atualização", "Status", "Ações"].map(h => (
                    <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 10, color: theme.gray400, letterSpacing: .8, textTransform: "uppercase", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtrados.map((doc, i) => (
                  <tr key={doc.id} style={{ borderBottom: `1px solid ${theme.gray100}`, background: i % 2 === 0 ? theme.white : theme.gray50 }}>
                    <td style={{ padding: "11px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 16 }}>{iconTipo(getNomeTipo(doc.tipoId))}</span>
                        <div>
                          <p style={{ fontSize: 13, fontWeight: 600, color: theme.gray900 }}>{doc.nome}</p>
                          {doc.descricao && <p style={{ fontSize: 11, color: theme.gray400, marginTop: 1 }}>{doc.descricao.slice(0,60)}{doc.descricao.length > 60 ? "…" : ""}</p>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "11px 14px" }}><Badge color="blue">{doc.area || "—"}</Badge></td>
                    <td style={{ padding: "11px 14px", fontSize: 12, color: theme.gray600 }}>{getNomeTipo(doc.tipoId)}</td>
                    <td style={{ padding: "11px 14px" }}><Badge color={doc.acesso === "Público" ? "green" : "amber"}>{doc.acesso}</Badge></td>
                    <td style={{ padding: "11px 14px", fontSize: 12, color: theme.gray600 }}>{getNomeResp(doc.responsavel)}</td>
                    <td style={{ padding: "11px 14px", fontSize: 12, color: theme.gray500 }}>
                      {doc.dataAtualizacao ? new Date(doc.dataAtualizacao + "T00:00:00").toLocaleDateString("pt-BR") : "—"}
                    </td>
                    <td style={{ padding: "11px 14px" }}><Badge color={doc.status === "Ativo" ? "green" : "gray"}>{doc.status}</Badge></td>
                    <td style={{ padding: "11px 14px" }}>
                      <div style={{ display: "flex", gap: 5 }}>
                        <a href={doc.link} target="_blank" rel="noreferrer"><Btn size="sm" variant="secondary">🔗</Btn></a>
                        <Btn size="sm" variant="secondary" onClick={() => { setEditItem({ ...doc }); setModalOpen(true); }}>✏️</Btn>
                        <Btn size="sm" variant="danger" onClick={() => remover(doc.id)}>×</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Modal Cadastro */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editItem?.id ? "Editar Documento" : "Novo Documento"} width={640}>
        {editItem && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Nome do Documento" required>
                  <Input value={editItem.nome} onChange={v => setEditItem({ ...editItem, nome: v })} placeholder="Ex: Modelo Contrato Honorários Tributário" />
                </FormField>
              </div>
              <FormField label="Área Jurídica">
                <Select value={editItem.area} onChange={v => setEditItem({ ...editItem, area: v })} options={areas} placeholder="Selecione a área..." />
              </FormField>
              <FormField label="Tipo de Documento">
                <Select value={editItem.tipoId} onChange={v => setEditItem({ ...editItem, tipoId: v })} options={tipos.map(t => ({ value: t.id, label: t.nome }))} placeholder="Selecione o tipo..." />
              </FormField>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Link de Acesso" required>
                  <Input value={editItem.link} onChange={v => setEditItem({ ...editItem, link: v })} placeholder="https://onedrive.live.com/... ou https://drive.google.com/..." />
                </FormField>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Descrição">
                  <Textarea value={editItem.descricao} onChange={v => setEditItem({ ...editItem, descricao: v })} placeholder="Breve descrição sobre o documento..." rows={2} />
                </FormField>
              </div>
              <FormField label="Responsável">
                <Select value={editItem.responsavel} onChange={v => setEditItem({ ...editItem, responsavel: v })} options={equipe.filter(e => e.ativo).map(e => ({ value: e.id, label: e.nome }))} placeholder="Selecione..." />
              </FormField>
              <FormField label="Data de Atualização">
                <Input type="date" value={editItem.dataAtualizacao} onChange={v => setEditItem({ ...editItem, dataAtualizacao: v })} />
              </FormField>
              <FormField label="Nível de Acesso">
                <Select value={editItem.acesso} onChange={v => setEditItem({ ...editItem, acesso: v })} options={["Interno", "Público"]} />
              </FormField>
              <FormField label="Status">
                <Select value={editItem.status} onChange={v => setEditItem({ ...editItem, status: v })} options={["Ativo", "Arquivado"]} />
              </FormField>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Observações">
                  <Textarea value={editItem.obs} onChange={v => setEditItem({ ...editItem, obs: v })} placeholder="Notas adicionais..." rows={2} />
                </FormField>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 16, borderTop: `1px solid ${theme.gray100}` }}>
              <Btn variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Btn>
              <Btn onClick={save}>Salvar Documento</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* Modal Tipos */}
      <Modal open={modalTipos} onClose={() => setModalTipos(false)} title="⚙️ Tipos de Documento" width={460}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <div style={{ flex: 1 }}><Input value={novoTipo} onChange={setNovoTipo} placeholder="Nome do tipo..." /></div>
          <Btn onClick={salvarTipo}>{editTipoIdx !== null ? "Salvar" : "Adicionar"}</Btn>
          {editTipoIdx !== null && <Btn variant="secondary" onClick={() => { setEditTipoIdx(null); setNovoTipo(""); }}>×</Btn>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 400, overflowY: "auto" }}>
          {tipos.map((t, i) => (
            <div key={t.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 7, border: `1px solid ${theme.gray100}`, background: editTipoIdx === i ? `${theme.gold}08` : theme.white }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16 }}>{iconTipo(t.nome)}</span>
                <span style={{ fontSize: 13, color: theme.gray800 }}>{t.nome}</span>
                <span style={{ fontSize: 10, color: theme.gray400 }}>{docs.filter(d => d.tipoId === t.id).length} doc(s)</span>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <Btn size="sm" variant="secondary" onClick={() => { setEditTipoIdx(i); setNovoTipo(t.nome); }}>✏️</Btn>
                <Btn size="sm" variant="danger" onClick={() => removerTipo(t.id)}>×</Btn>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 16, borderTop: `1px solid ${theme.gray100}`, marginTop: 16 }}>
          <Btn variant="secondary" onClick={() => setModalTipos(false)}>Fechar</Btn>
        </div>
      </Modal>
    </div>
  );
}

// Lista de todos os módulos do sistema — usada pelo Controle de Acesso
// Dashboard Executivo Diário — Fase 4, item 2 do cronograma.
// Card único para os sócios: atendimentos do dia, propostas abertas, processos
// com prazo crítico, receita do mês vs meta, ROI de campanhas ativas.
function ModDashboardExecutivo({ data }) {
  const fmtMoeda = v => 'R$ ' + parseFloat(v||0).toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2});
  const [metricasAtend, setMetricasAtend] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('ac_token') || '';
    fetch(`${API_BASE}/atendimentos/index.php/metricas`, { headers: { 'X-Token': token } })
      .then(r => r.json())
      .then(res => { if (res.ok) setMetricasAtend(res.dados); })
      .catch(() => {});
  }, []);

  const mesAtual = new Date().toISOString().slice(0, 7);

  // Propostas abertas (leads em estágio "proposta")
  const leads = data.leads || [];
  const propostas = leads.filter(l => l.estagio === 'proposta');
  const valorPropostas = propostas.reduce((s, l) => s + (parseFloat(l.valorEstimado) || 0), 0);

  // Processos com prazo crítico (≤3 dias úteis) — mesma lógica do painel de tarefas do Dashboard
  const hoje0 = new Date(); hoje0.setHours(0, 0, 0, 0);
  const ocsCriticas = (data.baseOperacional || []).flatMap(reg =>
    (reg.ocorrencias || [])
      .filter(oc => oc.prazoVencimento)
      .map(oc => {
        const venc = new Date(oc.prazoVencimento + "T12:00:00");
        const diff = Math.round((venc - hoje0) / (1000 * 60 * 60 * 24));
        return { ...oc, _diff: diff };
      })
  ).filter(oc => oc._diff <= 3);

  // Receita do mês vs Meta
  const lancamentos = data.lancamentos || [];
  const recebMes = lancamentos
    .filter(l => l.status === "Recebido" && (l.dataRecebimento || "").slice(0, 7) === mesAtual)
    .reduce((s, l) => s + (parseFloat(l.valor) || 0), 0);
  const metaFinanceiro = parseFloat(data.metaFinanceiro) || 0;
  const pctMeta = metaFinanceiro > 0 ? Math.round(recebMes / metaFinanceiro * 100) : 0;

  // ROI de campanhas ativas
  const campanhas = (data.marketing?.campanhas) || [];
  const campanhasAtivas = campanhas.filter(c => c.status !== 'Encerrada' && c.status !== 'Cancelada');
  const totalInvestido = campanhasAtivas.reduce((s, c) => s + (parseFloat(c.orcamento) || 0), 0);
  const totalReceita   = campanhasAtivas.reduce((s, c) => s + (parseFloat(c.receitaGerada) || 0), 0);
  const roiGlobal = totalInvestido > 0 ? Math.round((totalReceita - totalInvestido) / totalInvestido * 100) : null;

  const cards = [
    { label: 'Atendimentos Hoje', valor: metricasAtend ? `${metricasAtend.novos || 0} novos` : '...',
      sub: metricasAtend ? `${metricasAtend.em_andamento || 0} em andamento` : 'Carregando...', cor: theme.blue, icon: '💬' },
    { label: 'Propostas Abertas', valor: propostas.length, sub: fmtMoeda(valorPropostas), cor: theme.gold, icon: '📄' },
    { label: 'Processos c/ Prazo Crítico', valor: ocsCriticas.length, sub: '≤ 3 dias', cor: theme.red, icon: '⚖️' },
    { label: 'Receita do Mês', valor: fmtMoeda(recebMes),
      sub: metaFinanceiro > 0 ? `${pctMeta}% da meta (${fmtMoeda(metaFinanceiro)})` : 'Meta não definida', cor: theme.green, icon: '💰' },
    { label: 'ROI Campanhas Ativas', valor: roiGlobal !== null ? `${roiGlobal}%` : '—',
      sub: `Investido ${fmtMoeda(totalInvestido)}`, cor: '#8B5CF6', icon: '📈' },
  ];

  return (
    <div className="fade-in">
      <PageHeader title="Dashboard Executivo" subtitle={`Visão do dia para os sócios — ${new Date().toLocaleDateString('pt-BR')}`} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 16 }}>
        {cards.map(c => (
          <Card key={c.label} style={{ borderTop: `3px solid ${c.cor}`, textAlign: "center" }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>{c.icon}</div>
            <div style={{ fontSize: 11, color: theme.gray500, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5 }}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: theme.gray900, margin: "4px 0" }}>{c.valor}</div>
            <div style={{ fontSize: 12, color: theme.gray400 }}>{c.sub}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

const TODOS_MODULOS = [
  { id: "atendimentos", label: "Central de Atendimento",   grupo: "Operacao" },
  { id: "agenda",     label: "Agenda",                     grupo: "Operacao" },
  { id: "arquivo",    label: "Arquivo de Documentos",      grupo: "Operacao" },
  { id: "areas",      label: "Áreas Jurídicas",            grupo: "Juridico" },
  { id: "base",       label: "Base Operacional",           grupo: "Operacao" },
  { id: "clientes",   label: "Clientes",                   grupo: "Cadastros" },
  { id: "crm",        label: "CRM Comercial",              grupo: "Operacao" },
  { id: "dashboard",  label: "Dashboard",                  grupo: "Operacao" },
  { id: "dashexec",   label: "Dashboard Executivo",         grupo: "Gestao" },
  { id: "equipe",     label: "Equipe",                     grupo: "Cadastros" },
  { id: "financeiro", label: "Financeiro",                 grupo: "Gestao" },
  { id: "governanca", label: "Governança",                 grupo: "Gestao" },
  { id: "painelia",   label: "IA - Assistentes Jurídicos", grupo: "Operacao" },
  { id: "sala",       label: "Sala Virtual",               icon: "🏢" },
  { id: "manual",     label: "Manual do Sistema",          grupo: "Administracao" },
  { id: "marketing",  label: "Marketing",                  grupo: "Operacao" },
  { id: "parcerias",  label: "Parcerias",                  grupo: "Cadastros" },
  { id: "pops",       label: "POPs",                       grupo: "Cadastros" },
  { id: "processos",  label: "Processos",                  grupo: "Juridico" },
  { id: "produtos",   label: "Produtos",                   grupo: "Cadastros" },
  { id: "dashtrib",   label: "Dashboard Tributário",           grupo: "Gestao" },
  { id: "rct",        label: "RCT Tributário",             grupo: "Operacao" },
  { id: "relatorios", label: "Relatórios",                 grupo: "Gestao" },
  { id: "vendas",     label: "Vendas",                     grupo: "Operacao" },
  { id: "sala",       label: "Sala Virtual",               grupo: "Operacao" },
  { id: "cowork",     label: "Assistente Cowork",           grupo: "Administracao" },
  { id: "acesso",     label: "Controle de Acesso",         grupo: "Administracao" },
  { id: "escritorio", label: "Dados do Escritório",        grupo: "Administracao" },
];

// Perfis de acesso disponíveis no sistema
const PERFIS_SISTEMA = [
  "Administrador",
  "Sócio",
  "Advogado",
  "Operador RCT",
  "Assistente",
  "Financeiro",
  "Visualizador",
];


// ============================================================
// DADOS DO ESCRITÓRIO — Cadastro centralizado
// CNPJ · Endereço · Sócios · Dados bancários · Contatos
// ============================================================
function LandingPageItem({ lp, i, produtos, onChange, onRemove }) {
  const [aberto, setAberto] = React.useState(false);
  const qtd = (lp.produtos||[]).length;
  const nomeSelecionados = (lp.produtos||[])
    .map(id => produtos.find(p=>p.id===id)?.nome||id)
    .join(", ");

  return (
    <div style={{marginBottom:10,borderRadius:8,border:`1px solid ${theme.gray200}`,
      background:theme.gray50,overflow:"hidden"}}>
      {/* Linha principal */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 2fr auto auto",gap:"0 10px",
        padding:"10px 12px",alignItems:"end"}}>
        <FormField label="Título">
          <Input value={lp.titulo||""} onChange={v=>onChange({...lp,titulo:v})}
            placeholder="Ex: Página Principal"/>
        </FormField>
        <FormField label="URL">
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <Input value={lp.url||""} onChange={v=>onChange({...lp,url:v})}
              placeholder="https://..."/>
            {lp.url && (
              <a href={lp.url} target="_blank" rel="noreferrer"
                title="Abrir Landing Page" style={{
                  flexShrink:0,padding:"7px 10px",borderRadius:6,
                  background:theme.gray900,color:theme.gold,
                  fontSize:14,textDecoration:"none",lineHeight:1,
                  display:"flex",alignItems:"center"}}>
                ↗
              </a>
            )}
          </div>
        </FormField>
        {/* Botão produtos */}
        <div style={{paddingBottom:8}}>
          <p style={{fontSize:9,color:theme.gray400,textTransform:"uppercase",
            letterSpacing:.5,marginBottom:3}}>Produtos</p>
          <button onClick={()=>setAberto(a=>!a)} style={{
            padding:"7px 12px",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:600,
            border:`1px solid ${qtd>0?theme.gold:theme.gray200}`,
            background:qtd>0?theme.gold+"15":theme.white,
            color:qtd>0?theme.gray900:theme.gray500,
            display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap"}}>
            <span>{qtd>0?`📦 ${qtd} produto${qtd!==1?"s":""}` : "📦 Vincular"}</span>
            <span style={{fontSize:10}}>{aberto?"▲":"▼"}</span>
          </button>
        </div>
        <div style={{paddingBottom:8}}>
          <p style={{fontSize:9,color:"transparent",marginBottom:3}}>—</p>
          <button onClick={onRemove} style={{background:"none",border:"none",
            color:theme.red,cursor:"pointer",fontSize:18,padding:"4px 6px"}}>×</button>
        </div>
      </div>

      {/* Dropdown de produtos */}
      {aberto && (
        <div style={{borderTop:`1px solid ${theme.gray200}`,padding:"10px 12px",
          background:theme.white}}>
          {produtos.length===0
            ? <p style={{fontSize:11,color:theme.gray400,fontStyle:"italic"}}>
                Cadastre produtos no módulo Produtos para vinculá-los.
              </p>
            : <>
                <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:8}}>
                  {[...produtos].sort((a,b)=>(a.nome||"").localeCompare(b.nome||"","pt-BR")).map(prod=>{
                    const sel = (lp.produtos||[]).includes(prod.id);
                    return (
                      <button key={prod.id} onClick={()=>{
                        const novo = sel
                          ? (lp.produtos||[]).filter(id=>id!==prod.id)
                          : [...(lp.produtos||[]), prod.id];
                        onChange({...lp, produtos:novo});
                      }} style={{padding:"3px 10px",borderRadius:5,cursor:"pointer",
                        fontSize:11,fontWeight:sel?700:400,
                        border:`1px solid ${sel?theme.gold:theme.gray200}`,
                        background:sel?theme.gold+"15":theme.white,
                        color:sel?theme.gray900:theme.gray500}}>
                        {sel?"✓ ":""}{prod.nome}
                      </button>
                    );
                  })}
                </div>
                {qtd>0 && (
                  <p style={{fontSize:10,color:theme.gray400}}>
                    Vinculados: {nomeSelecionados}
                  </p>
                )}
              </>
          }
        </div>
      )}
    </div>
  );
}

function ModEscritorio({ data, setData }) {
  const escritorio = data.escritorio || {};
  const bancos     = data.bancos     || [];

  const [form, setForm] = React.useState({
    // Dados gerais
    nomeFantasia:    escritorio.nomeFantasia    || "Alves & Correa Advogados",
    razaoSocial:     escritorio.razaoSocial     || "",
    cnpj:            escritorio.cnpj            || "",
    oab:             escritorio.oab             || "",
    dataFundacao:    escritorio.dataFundacao    || "",
    // Endereço
    cep:             escritorio.cep             || "",
    logradouro:      escritorio.logradouro      || "",
    numero:          escritorio.numero          || "",
    complemento:     escritorio.complemento     || "",
    bairro:          escritorio.bairro          || "",
    cidade:          escritorio.cidade          || "São Paulo",
    uf:              escritorio.uf              || "SP",
    // Contato
    telefone:        escritorio.telefone        || "",
    whatsapp:        escritorio.whatsapp        || "",
    email:           escritorio.email           || "contato@alvescorreaadvogados.com.br",
    site:            escritorio.site            || "www.alvesecorreaadvogados.com.br",
    instagram:       escritorio.instagram       || "",
    linkedin:        escritorio.linkedin        || "",
    linkGoogle:      escritorio.linkGoogle      || "",
    // Landing pages e produtos
    landingPages:    escritorio.landingPages    || [],
    produtosLP:      escritorio.produtosLP      || [],
    // Filial
    filial:          escritorio.filial          || "São Paulo/SP · Foz do Iguaçu/PR",
    // Financeiro
    pixChave:        escritorio.pixChave        || "",
    pixTitular:      escritorio.pixTitular      || "Alves & Correa Advogados",
    pixBanco:        escritorio.pixBanco        || "",
    contaBancoId:    escritorio.contaBancoId    || (bancos[0]?.id || ""),
    // Sócios
    socios: escritorio.socios || [
      { nome: "Dr. Nelson José Alves",       oab: "OAB/SP 398.875", email: "", whatsapp: "", cargo: "Sócio-Fundador" },
      { nome: "Dra. Annie Lemos Alves Correa", oab: "OAB/SP 446.942", email: "", whatsapp: "", cargo: "Sócia" },
    ],
  });

  const [salvo, setSalvo] = React.useState(false);
  const [aba, setAba] = React.useState("geral");

  const fld = (label, field, opts={}) => (
    <FormField label={label}>
      <Input
        value={form[field]||""}
        onChange={v=>setForm(f=>({...f,[field]:v}))}
        placeholder={opts.placeholder||""}
        type={opts.type||"text"}
      />
    </FormField>
  );

  const salvar = () => {
    setData(prev=>({...prev, escritorio: {...form}}));
    setSalvo(true);
    setTimeout(()=>setSalvo(false), 2500);
  };

  const ABAS = [
    ["geral",     "🏢 Dados Gerais"],
    ["endereco",  "📍 Endereço"],
    ["contato",   "📞 Contato"],
    ["socios",    "👥 Sócios"],
    ["financeiro","🏦 Dados Bancários"],
  ];

  return (
    <div className="fade-in">
      <PageHeader title="Dados do Escritório" subtitle="Cadastro centralizado — usado em relatórios, PDFs e comunicações"/>

      {/* Abas */}
      <div style={{display:"flex",gap:2,marginBottom:20,borderBottom:`2px solid ${theme.gray100}`,overflowX:"auto"}}>
        {ABAS.map(([id,lbl])=>(
          <button key={id} onClick={()=>setAba(id)} style={{
            padding:"10px 16px",fontSize:12,fontWeight:aba===id?700:400,cursor:"pointer",
            border:"none",background:"transparent",whiteSpace:"nowrap",
            color:aba===id?theme.gold:theme.gray500,
            borderBottom:aba===id?`2px solid ${theme.gold}`:"2px solid transparent",
            marginBottom:-2,fontFamily:"Jost,sans-serif"}}>
            {lbl}
          </button>
        ))}
      </div>

      <Card>
        {/* ── DADOS GERAIS ── */}
        {aba==="geral" && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 24px"}}>
            <div style={{gridColumn:"1/-1"}}>
              {fld("Nome Fantasia","nomeFantasia",{placeholder:"Alves & Correa Advogados"})}
            </div>
            {fld("Razão Social","razaoSocial",{placeholder:"Alves & Correa Advogados Ltda"})}
            <FormField label="CNPJ">
              <Input value={form.cnpj} onChange={v=>{
                const d=v.replace(/\D/g,"").slice(0,14);
                const f=d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})$/,(_,a,b,c,e,g)=>`${a}.${b}.${c}/${e}${g?"-"+g:""}`);
                setForm(p=>({...p,cnpj:f||d}));
              }} placeholder="00.000.000/0001-00"/>
            </FormField>
            {fld("OAB do Escritório","oab",{placeholder:"OAB/SP 000.000"})}
            {fld("Data de Fundação","dataFundacao",{type:"date"})}
            <FormField label="Filial(is)">
              <Input value={form.filial} onChange={v=>setForm(f=>({...f,filial:v}))} placeholder="São Paulo/SP · Foz do Iguaçu/PR"/>
            </FormField>
          </div>
        )}

        {/* ── ENDEREÇO ── */}
        {aba==="endereco" && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 24px"}}>
            <FormField label="CEP">
              <Input value={form.cep} onChange={v=>setForm(f=>({...f,cep:v.replace(/\D/g,"").replace(/(\d{5})(\d{1,3})/,"$1-$2").slice(0,9)}))} placeholder="00000-000"/>
            </FormField>
            {fld("Número","numero",{placeholder:"Ex: 500"})}
            <div style={{gridColumn:"1/-1"}}>
              {fld("Logradouro","logradouro",{placeholder:"Av. Paulista"})}
            </div>
            {fld("Complemento","complemento",{placeholder:"Cj 1001"})}
            {fld("Bairro","bairro",{placeholder:"Bela Vista"})}
            {fld("Cidade","cidade",{placeholder:"São Paulo"})}
            <FormField label="UF">
              <Select value={form.uf} onChange={v=>setForm(f=>({...f,uf:v}))} options={UFS}/>
            </FormField>
          </div>
        )}

        {/* ── CONTATO ── */}
        {aba==="contato" && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 24px"}}>
            {fld("Telefone","telefone",{placeholder:"(11) 0000-0000"})}
            {fld("WhatsApp","whatsapp",{placeholder:"(11) 99999-9999"})}
            {fld("E-mail","email",{placeholder:"contato@alvescorreaadvogados.com.br"})}
            {fld("Site","site",{placeholder:"www.alvesecorreaadvogados.com.br"})}
            {fld("Instagram","instagram",{placeholder:"@alvesecorreaadv"})}
            {fld("LinkedIn","linkedin",{placeholder:"linkedin.com/company/..."})}
            <div style={{gridColumn:"1/-1"}}>
              {fld("Link Google Meu Negócio","linkGoogle",{placeholder:"https://g.page/..."})}
            </div>

            {/* Landing Pages */}
            <div style={{gridColumn:"1/-1",marginTop:8}}>
              <div style={{borderTop:`1px solid ${theme.gray100}`,paddingTop:16,marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <p style={{fontSize:12,fontWeight:700,color:theme.gray700,margin:0}}>🌐 Landing Pages</p>
                  <button onClick={()=>setForm(f=>({...f,
                    landingPages:[...(f.landingPages||[]),{titulo:"",url:"",produtos:[]}]
                  }))} style={{padding:"5px 12px",borderRadius:6,border:`1px solid ${theme.gold}`,
                    background:theme.gold+"15",color:theme.gold,cursor:"pointer",fontSize:11,fontWeight:700}}>
                    + Landing Page
                  </button>
                </div>
                {(form.landingPages||[]).length===0 && (
                  <p style={{fontSize:11,color:theme.gray400,fontStyle:"italic"}}>Nenhuma landing page cadastrada.</p>
                )}
                {(form.landingPages||[]).map((lp,i)=>(
                  <LandingPageItem key={i} lp={lp} i={i} produtos={data.produtos||[]}
                    onChange={(novoLp)=>setForm(f=>({...f,
                      landingPages:f.landingPages.map((x,j)=>j===i?novoLp:x)
                    }))}
                    onRemove={()=>setForm(f=>({...f,
                      landingPages:f.landingPages.filter((_,j)=>j!==i)
                    }))}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── SÓCIOS ── */}
        {aba==="socios" && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <p style={{fontSize:12,color:theme.gray500,margin:0}}>Sócios e advogados principais do escritório</p>
              <Btn onClick={()=>setForm(f=>({...f,socios:[...f.socios,{nome:"",oab:"",email:"",whatsapp:"",cargo:""}]}))}>
                + Sócio
              </Btn>
            </div>
            {form.socios.map((s,i)=>(
              <div key={i} style={{marginBottom:16,padding:"14px 16px",borderRadius:10,
                border:`1px solid ${theme.gray200}`,background:theme.gray50}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <p style={{fontSize:12,fontWeight:700,color:theme.gray700,margin:0}}>Sócio {i+1}</p>
                  {form.socios.length > 1 && (
                    <button onClick={()=>setForm(f=>({...f,socios:f.socios.filter((_,j)=>j!==i)}))}
                      style={{background:"none",border:"none",color:theme.red,cursor:"pointer",fontSize:12}}>
                      × Remover
                    </button>
                  )}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
                  {[["Nome Completo","nome"],["OAB","oab"],["E-mail","email"],["WhatsApp","whatsapp"],["Cargo","cargo"]].map(([lbl,key])=>(
                    <FormField key={key} label={lbl}>
                      <Input value={s[key]||""} onChange={v=>setForm(f=>({...f,
                        socios:f.socios.map((x,j)=>j===i?{...x,[key]:v}:x)
                      }))} placeholder={lbl}/>
                    </FormField>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── FINANCEIRO ── */}
        {aba==="financeiro" && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 24px"}}>
            <div style={{gridColumn:"1/-1"}}>
              <p style={{fontSize:12,fontWeight:700,color:theme.gray700,marginBottom:12}}>Chave PIX</p>
            </div>
            {fld("Chave PIX","pixChave",{placeholder:"CNPJ, e-mail, telefone ou chave aleatória"})}
            {fld("Titular da Conta PIX","pixTitular",{placeholder:"Alves & Correa Advogados"})}
            {fld("Banco do PIX","pixBanco",{placeholder:"Ex: Itaú, Nubank"})}
            <div style={{gridColumn:"1/-1",marginTop:12}}>
              <p style={{fontSize:12,fontWeight:700,color:theme.gray700,marginBottom:12}}>Conta para Recebimentos</p>
              <FormField label="Conta Bancária Principal">
                <Select
                  value={form.contaBancoId}
                  onChange={v=>setForm(f=>({...f,contaBancoId:v}))}
                  options={bancos.map(b=>({value:b.id,label:`${b.nome} — Ag. ${b.agencia} — Cc ${b.conta}`}))}
                  placeholder="Selecione a conta cadastrada em Bancos..."
                />
              </FormField>
            </div>
            {bancos.length===0 && (
              <div style={{gridColumn:"1/-1",padding:"10px 14px",borderRadius:8,
                background:"#fffbec",border:"1px solid #fde68a"}}>
                <p style={{fontSize:12,color:"#92400e",margin:0}}>
                  ⚠️ Nenhum banco cadastrado. Acesse <strong>Financeiro → Bancos</strong> para cadastrar a conta do escritório.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Chave API Anthropic */}
        <div style={{marginTop:16,padding:"14px 16px",borderRadius:8,
          background:"#f0f9ff",border:"1px solid #bae6fd"}}>
          <p style={{fontSize:11,fontWeight:700,color:"#0369a1",textTransform:"uppercase",
            letterSpacing:.8,marginBottom:8}}>🤖 Integração IA — Chave API Anthropic</p>
          <p style={{fontSize:11,color:"#0284c7",marginBottom:10}}>
            Necessária para o Assistente do Manual e os Agentes IA do sistema.
            Obtenha em <strong>console.anthropic.com</strong> → API Keys.
          </p>
          <Input
            value={form.anthropicApiKey||""}
            onChange={v=>setForm({...form,anthropicApiKey:v})}
            placeholder="sk-ant-api03-..."
            type="password"
          />
          {form.anthropicApiKey && (
            <p style={{fontSize:10,color:theme.green,marginTop:4,fontWeight:600}}>
              ✅ Chave configurada — {form.anthropicApiKey.length} caracteres
            </p>
          )}
        </div>

        {/* Botão salvar */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
          marginTop:24,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
          {salvo
            ? <span style={{fontSize:13,color:theme.green,fontWeight:600}}>✅ Dados salvos com sucesso!</span>
            : <span style={{fontSize:11,color:theme.gray400}}>Os dados são usados automaticamente em relatórios, PDFs e comunicações.</span>
          }
          <Btn onClick={salvar}>💾 Salvar Dados do Escritório</Btn>
        </div>
      </Card>
    </div>
  );
}

function ModAcesso({ data, setData, usuario }) {
  const { equipe, produtos } = data;
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [erro, setErro] = useState("");

  const EMPTY = {
    id: "", equipeId: "", nome: "", login: "", senha: "",
    perfil: "Assistente", ativo: true, modulos: [], produtos: [],
    exigeSala: false, salasPermitidas: [],
    area: "", nivel_area: ""
  };

  // Carregar usuários da API
  const carregarUsuarios = () => {
    setLoading(true);
    apiGet('/usuarios.php')
      .then(res => { setUsuarios(Array.isArray(res) ? res : []); setLoading(false); })
      .catch(e => { setErro("Erro ao carregar usuários: " + e.message); setLoading(false); });
  };

  useEffect(() => { carregarUsuarios(); }, []);

  const gruposModulos = TODOS_MODULOS.reduce((acc, m) => {
    if (!acc[m.grupo]) acc[m.grupo] = [];
    acc[m.grupo].push(m);
    return acc;
  }, {});

  const openNew  = () => { setEditItem({ ...EMPTY, modulos: [], produtos: [] }); setMostrarSenha(false); setErro(""); setModalOpen(true); };
  const openEdit = u  => { setEditItem({ ...u, senha: "" }); setMostrarSenha(false); setErro(""); setModalOpen(true); };

  const toggleModulo  = (id) => setEditItem(prev => ({ ...prev, modulos: prev.modulos.includes(id) ? prev.modulos.filter(m => m !== id) : [...prev.modulos, id] }));
  const toggleProduto = (id) => setEditItem(prev => ({ ...prev, produtos: (prev.produtos||[]).includes(id) ? (prev.produtos||[]).filter(p => p !== id) : [...(prev.produtos||[]), id] }));
  const selecionarTodos = () => setEditItem(prev => ({ ...prev, modulos: TODOS_MODULOS.map(m => m.id) }));
  const limparTodos     = () => setEditItem(prev => ({ ...prev, modulos: [] }));

  const presetPerfil = (perfil) => {
    const presets = {
      "Administrador": TODOS_MODULOS.map(m => m.id),
      "Sócio":         ["areas","clientes","produtos","dashtrib","base","financeiro","dashboard","agenda","vendas","marketing","crm","rct","processos","relatorios","governanca","painelia","arquivo","cowork","escritorio"],
      "Advogado":      ["clientes","base","crm","rct","processos","painelia","cowork"],
      "Operador RCT":  ["rct","clientes"],
      "Assistente":    ["clientes","base","processos"],
      "Financeiro":    ["clientes","financeiro","dashboard","relatorios"],
      "Visualizador":  ["dashboard"],
    };
    setEditItem(prev => ({ ...prev, perfil, modulos: presets[perfil] || [] }));
  };

  const vincularEquipe = (equipeId) => {
    const membro = equipe.find(e => e.id === equipeId);
    setEditItem(prev => ({
      ...prev, equipeId,
      nome:  membro ? membro.nome : prev.nome,
      login: membro ? membro.nome.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "") : prev.login
    }));
  };

  const save = async () => {
    if (!editItem.nome || !editItem.login) return setErro("Nome e login são obrigatórios.");
    if (!editItem.id && !editItem.senha)   return setErro("Senha obrigatória para novo usuário.");
    if (editItem.senha && editItem.senha.length < 6) return setErro("Senha deve ter mínimo 6 caracteres.");
    setSalvando(true);
    setErro("");
    try {
      const payload = {
        nome:    editItem.nome,
        login:   editItem.login,
        perfil:  editItem.perfil,
        exigeSala:       editItem.exigeSala       || false,
        salasPermitidas: editItem.salasPermitidas || [],
        modulos: editItem.modulos || [],
        ativo:   editItem.ativo ? 1 : 0,
      };
      if (editItem.senha) payload.senha = editItem.senha;

      if (editItem.id) {
        // Editar — PUT
        await apiPost(`/usuarios.php?id=${editItem.id}`, payload, 'PUT');
      } else {
        // Novo — POST
        await apiPost('/usuarios.php', payload);
      }
      await carregarUsuarios();
      setModalOpen(false);
    } catch(e) {
      setErro(e.message || "Erro ao salvar usuário.");
    } finally {
      setSalvando(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Remover usuário? Esta ação não pode ser desfeita.")) return;
    try {
      await apiPost(`/usuarios.php?id=${id}`, {}, 'DELETE');
      await carregarUsuarios();
    } catch(e) {
      alert("Erro ao remover: " + e.message);
    }
  };

  const toggleAtivo = async (u) => {
    try {
      await apiPost(`/usuarios.php?id=${u.id}`, { ativo: u.ativo ? 0 : 1 }, 'PUT');
      await carregarUsuarios();
    } catch(e) {
      alert("Erro: " + e.message);
    }
  };

  const perfilColor = (p) => {
    if (p === "Administrador") return "red";
    if (p === "Sócio")         return "gold";
    if (p === "Advogado")      return "blue";
    if (p === "Operador RCT")  return "amber";
    if (p === "Financeiro")    return "green";
    return "gray";
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: theme.gray400 }}>Carregando usuários...</div>;

  return (
    <div className="fade-in">
      <PageHeader
        title="Controle de Acesso"
        subtitle="Gerenciamento de usuários, perfis e permissões — sincronizado com o banco de dados"
        action={<Btn onClick={openNew}>+ Novo Usuário</Btn>}
      />

      {erro && !modalOpen && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "12px 16px", marginBottom: 16, color: "#DC2626", fontSize: 13 }}>
          {erro}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
        {usuarios.map(u => {
          const membro   = equipe.find(e => e.id === u.equipeId);
          const prodsVinc = (u.produtos || []).map(id => produtos.find(p => p.id === id)?.nome).filter(Boolean);
          return (
            <Card key={u.id} style={{ padding: 20, opacity: u.ativo ? 1 : 0.6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
                <div style={{
                  width: 46, height: 46, borderRadius: "50%", flexShrink: 0,
                  background: u.ativo ? `linear-gradient(135deg, ${theme.gray800}, ${theme.gray600})` : theme.gray300,
                  display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                  <span className="serif" style={{ color: theme.gold, fontSize: 15, fontWeight: 700 }}>
                    {u.nome.split(" ").map(n => n[0]).slice(0,2).join("")}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, fontSize: 14, color: theme.gray900 }}>{u.nome}</p>
                  <p style={{ fontSize: 11, color: theme.gray400, marginTop: 2 }}>@{u.login}</p>
                </div>
                <Badge color={perfilColor(u.perfil)}>{u.perfil}</Badge>
              </div>

              <div style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 10, color: theme.gray400, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
                  Módulos ({(u.modulos || []).length}/{TODOS_MODULOS.length})
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {TODOS_MODULOS.map(m => (
                    <span key={m.id} style={{
                      fontSize: 9, padding: "2px 6px", borderRadius: 3,
                      background: (u.modulos || []).includes(m.id) ? `${theme.gold}22` : theme.gray100,
                      color: (u.modulos || []).includes(m.id) ? theme.goldDark : theme.gray300,
                      fontWeight: (u.modulos || []).includes(m.id) ? 700 : 400
                    }}>{m.label}</span>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: `1px solid ${theme.gray100}` }}>
                <Badge color={u.ativo ? "green" : "gray"}>{u.ativo ? "Ativo" : "Inativo"}</Badge>
                <div style={{ display: "flex", gap: 6 }}>
                  <Btn size="sm" variant="secondary" onClick={() => toggleAtivo(u)}>{u.ativo ? "Desativar" : "Ativar"}</Btn>
                  <Btn size="sm" variant="secondary" onClick={() => openEdit(u)}>Editar</Btn>
                  {u.id !== 1 && <Btn size="sm" variant="danger" onClick={() => remove(u.id)}>×</Btn>}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editItem?.id ? "Editar Usuário" : "Novo Usuário"} width={680}>
        {editItem && (
          <>
            {erro && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", marginBottom: 14, color: "#DC2626", fontSize: 13 }}>
                {erro}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
              <div style={{ gridColumn: "1/-1" }}>
                <FormField label="Vincular a membro da Equipe">
                  <Select
                    value={editItem.equipeId}
                    onChange={vincularEquipe}
                    options={equipe.filter(e => e.ativo).map(e => ({ value: e.id, label: e.nome + " — " + e.funcao }))}
                    placeholder="Selecione (opcional)..."
                  />
                </FormField>
              </div>
              <FormField label="Nome completo" required>
                <Input value={editItem.nome} onChange={v => setEditItem(prev => ({ ...prev, nome: v }))} placeholder="Nome do usuário" />
              </FormField>
              <FormField label="Login" required>
                <Input value={editItem.login} onChange={v => setEditItem(prev => ({ ...prev, login: v.toLowerCase().replace(/\s/g, ".") }))} placeholder="nome.sobrenome" />
              </FormField>
              <FormField label={editItem.id ? "Nova senha (deixe em branco para manter)" : "Senha"} required={!editItem.id}>
                <div style={{ position: "relative" }}>
                  <input
                    type={mostrarSenha ? "text" : "password"}
                    value={editItem.senha}
                    onChange={e => setEditItem(prev => ({ ...prev, senha: e.target.value }))}
                    placeholder={editItem.id ? "Deixe em branco para não alterar" : "Mínimo 6 caracteres"}
                    style={{ width: "100%", padding: "9px 36px 9px 12px", border: `1px solid ${theme.gray200}`, borderRadius: 7, fontSize: 13, outline: "none", boxSizing: "border-box" }}
                  />
                  <button onClick={() => setMostrarSenha(!mostrarSenha)} style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", fontSize: 14, color: theme.gray400
                  }}>{mostrarSenha ? "🙈" : "👁"}</button>
                </div>
              </FormField>
              <FormField label="Status">
                <Select value={editItem.ativo ? "Ativo" : "Inativo"} onChange={v => setEditItem(prev => ({ ...prev, ativo: v === "Ativo" }))} options={["Ativo", "Inativo"]} />
              </FormField>
            </div>

            <div style={{ borderTop: `1px solid ${theme.gray100}`, margin: "8px 0 16px", paddingTop: 16 }}>
              <p style={{ fontSize: 11, color: theme.gray500, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 10 }}>
                Perfil de Acesso
                <span style={{ fontSize: 10, color: theme.gray400, marginLeft: 8, textTransform: "none", letterSpacing: 0 }}>
                  (selecionar preenche as permissões automaticamente)
                </span>
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {PERFIS_SISTEMA.map(p => (
                  <button key={p} onClick={() => presetPerfil(p)} style={{
                    padding: "6px 14px", borderRadius: 6,
                    border: `1px solid ${editItem.perfil === p ? theme.gold : theme.gray200}`,
                    background: editItem.perfil === p ? `${theme.gold}18` : theme.white,
                    color: editItem.perfil === p ? theme.goldDark : theme.gray600,
                    fontSize: 12, fontWeight: editItem.perfil === p ? 700 : 400, cursor: "pointer"
                  }}>{p}</button>
                ))}
              </div>
            </div>

            {/* Fase 1 RBAC — Área e Nível */}
            <div style={{ borderTop: `1px solid ${theme.gray100}`, margin: "8px 0 16px", paddingTop: 16 }}>
              <p style={{ fontSize: 11, color: theme.gray500, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 10 }}>
                Área e Nível de Acesso
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                <FormField label="Área">
                  <Select value={editItem.area||""} onChange={v=>setEditItem(prev=>({...prev,area:v}))}
                    options={[
                      {value:"",label:"Não definida"},
                      {value:"Juridico",   label:"⚖️ Jurídico"},
                      {value:"Financeiro", label:"💰 Financeiro"},
                      {value:"Comercial",  label:"🤝 Comercial"},
                      {value:"Marketing",  label:"📢 Marketing"},
                      {value:"Administrativo", label:"📋 Administrativo"},
                      {value:"Gestao",     label:"🏛 Gestão"},
                    ]}
                    placeholder="Selecione a área..."/>
                </FormField>
                <FormField label="Nível na Área">
                  <Select value={editItem.nivel_area||""} onChange={v=>setEditItem(prev=>({...prev,nivel_area:v}))}
                    options={[
                      {value:"",label:"Não definido"},
                      {value:"TOTAL_AREA",label:"👑 Total da Área — vê tudo da área"},
                      {value:"EXECUTOR",  label:"👤 Executor — vê apenas o que é responsável"},
                    ]}
                    placeholder="Selecione o nível..."/>
                </FormField>
              </div>
            </div>

            <div style={{ borderTop: `1px solid ${theme.gray100}`, margin: "8px 0 16px", paddingTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <p style={{ fontSize: 11, color: theme.gray500, letterSpacing: 0.8, textTransform: "uppercase" }}>
                  Módulos Liberados ({(editItem.modulos || []).length}/{TODOS_MODULOS.length})
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn size="sm" variant="secondary" onClick={selecionarTodos}>Todos</Btn>
                  <Btn size="sm" variant="secondary" onClick={limparTodos}>Limpar</Btn>
                </div>
              </div>
              {Object.entries(gruposModulos).map(([grupo, mods]) => (
                <div key={grupo} style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: 10, color: theme.gray400, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>{grupo}</p>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                    {mods.map(m => {
                      const ativo2 = (editItem.modulos || []).includes(m.id);
                      return (
                        <label key={m.id} onClick={() => toggleModulo(m.id)} style={{
                          display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                          padding: "8px 12px", borderRadius: 7,
                          border: `1px solid ${ativo2 ? theme.gold : theme.gray200}`,
                          background: ativo2 ? `${theme.gold}0F` : theme.white,
                          transition: "all 0.15s"
                        }}>
                          <div style={{
                            width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                            border: `2px solid ${ativo2 ? theme.gold : theme.gray300}`,
                            background: ativo2 ? theme.gold : "transparent",
                            display: "flex", alignItems: "center", justifyContent: "center"
                          }}>
                            {ativo2 && <span style={{ color: theme.white, fontSize: 10, fontWeight: 700 }}>✓</span>}
                          </div>
                          <span style={{ fontSize: 12, color: ativo2 ? theme.goldDark : theme.gray600, fontWeight: ativo2 ? 600 : 400 }}>
                            {m.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Sala Virtual ── */}
            <div style={{marginTop:16,padding:"14px 16px",borderRadius:8,
              background:"#f0f9ff",border:"1px solid #bfdbfe"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <p style={{fontSize:12,fontWeight:700,color:theme.blue}}>🏢 Sala Virtual de Trabalho</p>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                  <input type="checkbox"
                    checked={!!editItem.exigeSala}
                    onChange={e=>setEditItem(prev=>({...prev,exigeSala:e.target.checked,
                      salasPermitidas:e.target.checked?prev.salasPermitidas:[]}))}
                    style={{width:15,height:15,accentColor:theme.blue}}/>
                  <span style={{fontSize:12,fontWeight:600,color:theme.blue}}>
                    Acesso obrigatório via Sala Virtual
                  </span>
                </label>
              </div>
              {editItem.exigeSala&&(
                <div>
                  <p style={{fontSize:11,color:theme.gray500,marginBottom:8}}>
                    Salas permitidas para este usuário:
                  </p>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                    {(data.salaVirtual?.salas||[
                      {id:"sala_comercial",   nome:"Sala Comercial",           icon:"🎯"},
                      {id:"sala_operacional", nome:"Sala Operacional Jurídica", icon:"⚖️"},
                      {id:"sala_rct",         nome:"Sala Operacional RCT",      icon:"🔍"},
                    ]).map(sala=>{
                      const ativa=(editItem.salasPermitidas||[]).includes(sala.id);
                      return (
                        <label key={sala.id} style={{display:"flex",alignItems:"center",gap:6,
                          cursor:"pointer",padding:"7px 12px",borderRadius:7,
                          border:`1px solid ${ativa?theme.blue:theme.gray200}`,
                          background:ativa?"#dbeafe":theme.white,
                          fontSize:12,fontWeight:ativa?600:400,
                          color:ativa?theme.blue:theme.gray700}}>
                          <input type="checkbox" checked={ativa}
                            onChange={e=>{
                              const prev=editItem.salasPermitidas||[];
                              setEditItem(p=>({...p,salasPermitidas:
                                e.target.checked?[...prev,sala.id]:prev.filter(s=>s!==sala.id)
                              }));
                            }}
                            style={{accentColor:theme.blue}}/>
                          {sala.icon} {sala.nome}
                        </label>
                      );
                    })}
                  </div>
                  {(editItem.salasPermitidas||[]).length===0&&(
                    <p style={{fontSize:11,color:theme.red,marginTop:6}}>
                      ⚠️ Selecione ao menos uma sala.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 16, borderTop: `1px solid ${theme.gray100}` }}>
              <Btn variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Btn>
              <Btn onClick={save} disabled={salvando}>{salvando ? "Salvando..." : "Salvar Usuário"}</Btn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

// ============================================================
// MOD ATENDIMENTOS — Central de Atendimento (WhatsApp + Instagram)
// ============================================================
function ModAtendimentos({ data, setData }) {
  const [atendimentos, setAtendimentos] = useState([]);
  const [selecionado,  setSelecionado]  = useState(null);
  const [mensagens,    setMensagens]    = useState([]);
  const [resposta,     setResposta]     = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroCanalAt,setFiltroCanalAt]= useState('todos');
  const [filtroContaEmail, setFiltroContaEmail] = useState('todas');
  const [metricas,     setMetricas]     = useState(null);
  const [viewMode,     setViewMode]     = useState('inbox'); // 'inbox' | 'kanban'
  const [loadingResp,  setLoadingResp]  = useState(false);
  const [loadingIA,    setLoadingIA]    = useState(false);
  const [agenteAtivo,  setAgenteAtivo]  = useState(true);
  const [loadingAgente,setLoadingAgente]= useState(false);
  const intervaloRef = useRef(null);

  const token = () => sessionStorage.getItem('ac_token') || '';

  const fetchLista = async () => {
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (filtroStatus !== 'todos') params.append('status', filtroStatus);
      if (filtroCanalAt !== 'todos') params.append('canal', filtroCanalAt);
      const res = await fetch(`${API_BASE}/atendimentos/index.php?${params}`, { headers: { 'X-Token': token() } });
      const json = await res.json();
      if (json.ok) setAtendimentos(json.dados || []);
    } catch {}
  };

  const fetchMensagens = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/atendimentos/index.php/${id}`, { headers: { 'X-Token': token() } });
      const json = await res.json();
      // Guarda contra condição de corrida: ignora resposta atrasada de um atendimento
      // que não é mais o selecionado (ex.: usuário trocou de e-mail antes do fetch voltar).
      setSelecionado(atual => {
        if (json.ok && atual?.id === id) setMensagens(json.dados.mensagens || []);
        return atual;
      });
    } catch {}
  };

  const fetchMetricas = async () => {
    try {
      const res = await fetch(`${API_BASE}/atendimentos/index.php/metricas`, { headers: { 'X-Token': token() } });
      const json = await res.json();
      if (json.ok) setMetricas(json.dados);
    } catch {}
  };

  const fetchAgenteConfig = async () => {
    try {
      const res = await fetch(`${API_BASE}/atendimentos/index.php/config`, { headers: { 'X-Token': token() } });
      const json = await res.json();
      if (json.ok) setAgenteAtivo(!!json.dados.ativo);
    } catch {}
  };

  const alternarAgente = async () => {
    const novoValor = !agenteAtivo;
    const confirmMsg = novoValor
      ? 'Reativar o agente de Atendimento? Ele volta a receber e responder novos contatos por WhatsApp, Instagram e E-mail.'
      : 'Desativar o agente de Atendimento? Enquanto desligado, novos contatos por WhatsApp, Instagram e E-mail NÃO entram no sistema — nada é criado nem respondido até reativar.';
    if (!confirm(confirmMsg)) return;
    setLoadingAgente(true);
    try {
      const res = await fetch(`${API_BASE}/atendimentos/index.php/config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Token': token() },
        body: JSON.stringify({ ativo: novoValor })
      });
      const json = await res.json();
      if (json.ok) setAgenteAtivo(!!json.ativo);
    } catch {}
    setLoadingAgente(false);
  };

  useEffect(() => {
    fetchLista();
    fetchMetricas();
    fetchAgenteConfig();
    intervaloRef.current = setInterval(() => {
      fetchLista();
      if (selecionado) fetchMensagens(selecionado.id);
    }, 15000);
    return () => clearInterval(intervaloRef.current);
  }, [filtroStatus, filtroCanalAt]);

  useEffect(() => {
    if (selecionado) fetchMensagens(selecionado.id);
  }, [selecionado]);

  const abrirAtend = (a) => { setSelecionado(a); setResposta(''); };

  const enviarResposta = async () => {
    if (!resposta.trim() || !selecionado) return;
    setLoadingResp(true);
    try {
      const res = await fetch(`${API_BASE}/atendimentos/index.php/${selecionado.id}/responder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Token': token() },
        body: JSON.stringify({ mensagem: resposta })
      });
      const json = await res.json();
      if (json.ok) { setResposta(''); fetchMensagens(selecionado.id); fetchLista(); }
    } catch {}
    setLoadingResp(false);
  };

  const acionarIA = async (persona) => {
    if (!selecionado) return;
    setLoadingIA(true);
    try {
      const res = await fetch(`${API_BASE}/atendimentos/index.php/${selecionado.id}/acionar-ia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Token': token() },
        body: JSON.stringify({ persona })
      });
      const json = await res.json();
      if (json.ok) { fetchMensagens(selecionado.id); fetchLista(); }
    } catch {}
    setLoadingIA(false);
  };

  const mudarStatus = async (id, status) => {
    try {
      await fetch(`${API_BASE}/atendimentos/index.php/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Token': token() },
        body: JSON.stringify({ status })
      });
      fetchLista();
      if (selecionado?.id === id) setSelecionado(prev => ({...prev, status}));
    } catch {}
  };

  const [textoAprovacao, setTextoAprovacao] = useState({}); // { [msgId]: textoEditado }
  const [loadingAprov,   setLoadingAprov]   = useState(null); // msgId em processamento

  const aprovarMensagem = async (msgId) => {
    if (!selecionado) return;
    setLoadingAprov(msgId);
    try {
      const res = await fetch(`${API_BASE}/atendimentos/index.php/${selecionado.id}/mensagens/${msgId}/aprovar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Token': token() },
        body: JSON.stringify({ mensagem: textoAprovacao[msgId] || '' })
      });
      const json = await res.json();
      if (json.ok) { fetchMensagens(selecionado.id); fetchLista(); }
      else alert('Falha ao aprovar: ' + (json.erro || 'erro desconhecido'));
    } catch { alert('Falha de conexão ao aprovar mensagem.'); }
    setLoadingAprov(null);
  };

  const descartarMensagem = async (msgId) => {
    if (!selecionado || !confirm('Descartar esta resposta da IA sem enviar?')) return;
    setLoadingAprov(msgId);
    try {
      const res = await fetch(`${API_BASE}/atendimentos/index.php/${selecionado.id}/mensagens/${msgId}/descartar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Token': token() },
      });
      const json = await res.json();
      if (json.ok) fetchMensagens(selecionado.id);
      else alert('Falha ao descartar: ' + (json.erro || 'erro desconhecido'));
    } catch { alert('Falha de conexão ao descartar mensagem.'); }
    setLoadingAprov(null);
  };

  const qualificar = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/atendimentos/index.php/${id}/qualificar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Token': token() },
        body: JSON.stringify({})
      });
      const json = await res.json();
      if (json.ok) { alert(`Lead criado no CRM! ID: ${json.dados?.lead_id}`); fetchLista(); }
    } catch {}
  };

  const canalIcon  = (c) => c === 'whatsapp' ? '📱' : c === 'email' ? '✉️' : '📸';
  const tipoColor  = (t) => t === 'cliente' ? theme.gold : t === 'lead' ? '#4CAF50' : theme.gray400;
  const statusColor= (s) => ({ novo:'#2196F3', em_andamento:theme.gold, qualificado:'#4CAF50', encerrado:theme.gray500, excluido:'#EF4444', nao_classificado:'#9CA3AF' })[s] || theme.gray400;
  const statusLabel= (s) => ({ novo:'Novo', em_andamento:'Em andamento', qualificado:'Qualificado', encerrado:'Encerrado', excluido:'Excluído', nao_classificado:'Não classificado' })[s] || s;

  const excluirAtendimento = (id) => {
    if (!confirm('Mover este atendimento para a lixeira? Ele sai da lista principal, mas o histórico é mantido.')) return;
    mudarStatus(id, 'excluido');
    if (selecionado?.id === id) setSelecionado(null);
  };

  const bloquearRemetente = async (id, tipo) => {
    const msg = tipo === 'dominio'
      ? 'Bloquear TODO O DOMÍNIO deste remetente? Nenhum e-mail desse domínio vai mais gerar atendimento.'
      : 'Bloquear este e-mail? Próximos e-mails desse remetente vão direto para a lixeira.';
    if (!confirm(msg)) return;
    try {
      const res = await fetch(`${API_BASE}/atendimentos/index.php/${id}/bloquear-remetente`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Token': token() },
        body: JSON.stringify({ tipo })
      });
      const json = await res.json();
      if (json.ok) { alert(`Bloqueado: ${json.dados.bloqueado}`); fetchLista(); if (selecionado?.id===id) setSelecionado(null); }
      else alert('Falha ao bloquear: ' + (json.erro || 'erro desconhecido'));
    } catch { alert('Falha de conexão ao bloquear remetente.'); }
  };

  const COLUNAS_KANBAN = ['novo','em_andamento','qualificado','encerrado'];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      {/* Cabeçalho */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
        <h2 style={{ color: theme.cream, fontSize:22, fontWeight:700, margin:0 }}>
          💬 Central de Atendimento
        </h2>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={alternarAgente} disabled={loadingAgente}
            title={agenteAtivo ? 'Agente ligado — clique para desativar (nenhum contato novo entra no sistema até reativar)' : 'Agente desligado — clique para reativar'}
            style={{ padding:'6px 14px', borderRadius:6, border:'none', cursor: loadingAgente ? 'wait' : 'pointer', fontWeight:700, fontSize:13,
              display:'flex', alignItems:'center', gap:6, opacity: loadingAgente ? 0.6 : 1,
              background: agenteAtivo ? '#1f5c3d' : '#5c1f1f', color: agenteAtivo ? '#8ef0b8' : '#f08e8e' }}>
            {agenteAtivo ? '🟢 Agente ativo' : '🔴 Agente desativado'}
          </button>
          <button onClick={() => setViewMode('inbox')}
            style={{ padding:'6px 14px', borderRadius:6, border:'none', cursor:'pointer', fontWeight:600, fontSize:13,
              background: viewMode==='inbox' ? theme.gold : theme.gray700, color: viewMode==='inbox' ? '#000' : theme.gray300 }}>
            Inbox
          </button>
          <button onClick={() => setViewMode('kanban')}
            style={{ padding:'6px 14px', borderRadius:6, border:'none', cursor:'pointer', fontWeight:600, fontSize:13,
              background: viewMode==='kanban' ? theme.gold : theme.gray700, color: viewMode==='kanban' ? '#000' : theme.gray300 }}>
            Kanban
          </button>
          <button onClick={() => { fetchLista(); fetchMetricas(); }}
            style={{ padding:'6px 14px', borderRadius:6, border:'none', cursor:'pointer', fontWeight:600, fontSize:13,
              background: theme.gray700, color: theme.gray300 }}>
            ↺ Atualizar
          </button>
        </div>
      </div>

      {/* Métricas rápidas */}
      {metricas && (
        <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
          {[
            { label:'Novos',      val: metricas.novos,         cor:'#2196F3' },
            { label:'Em andamento',val: metricas.em_andamento, cor: theme.gold },
            { label:'Qualificados',val: metricas.qualificados, cor:'#4CAF50' },
            { label:'WhatsApp',   val: metricas.whatsapp,      cor:'#25D366' },
            { label:'Instagram',  val: metricas.instagram,     cor:'#E1306C' },
            { label:'E-mail',     val: metricas.email,         cor:'#60A5FA' },
            { label:'Custo IA 30d',val: `$${parseFloat(metricas.custo_ia_30d||0).toFixed(3)}`, cor: theme.gray400 },
          ].map(m => (
            <div key={m.label} style={{ background: theme.gray800, borderRadius:8, padding:'10px 16px', minWidth:110, borderTop:`3px solid ${m.cor}` }}>
              <div style={{ color: theme.gray400, fontSize:11, fontWeight:600 }}>{m.label}</div>
              <div style={{ color: theme.cream, fontSize:20, fontWeight:700 }}>{m.val ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        {['todos','novo','em_andamento','qualificado','nao_classificado','encerrado'].map(s => (
          <button key={s} onClick={() => setFiltroStatus(s)}
            style={{ padding:'5px 12px', borderRadius:20, border:`1px solid ${filtroStatus===s ? theme.gold : theme.gray700}`,
              background: filtroStatus===s ? `${theme.gold}22` : 'transparent',
              color: filtroStatus===s ? theme.gold : theme.gray400, fontSize:12, cursor:'pointer', fontWeight:600 }}>
            {statusLabel(s)}
          </button>
        ))}
        <button onClick={() => setFiltroStatus('excluido')}
          style={{ padding:'5px 12px', borderRadius:20, border:`1px solid ${filtroStatus==='excluido' ? '#EF4444' : theme.gray700}`,
            background: filtroStatus==='excluido' ? '#EF444422' : 'transparent',
            color: filtroStatus==='excluido' ? '#EF4444' : theme.gray400, fontSize:12, cursor:'pointer', fontWeight:600 }}>
          🗑️ Lixeira
        </button>
        <span style={{ margin:'0 4px', color: theme.gray600 }}>|</span>
        {['todos','whatsapp','instagram','email'].map(c => (
          <button key={c} onClick={() => setFiltroCanalAt(c)}
            style={{ padding:'5px 12px', borderRadius:20, border:`1px solid ${filtroCanalAt===c ? theme.gold : theme.gray700}`,
              background: filtroCanalAt===c ? `${theme.gold}22` : 'transparent',
              color: filtroCanalAt===c ? theme.gold : theme.gray400, fontSize:12, cursor:'pointer', fontWeight:600 }}>
            {c === 'todos' ? 'Todos canais' : c === 'whatsapp' ? '📱 WhatsApp' : c === 'email' ? '✉️ E-mail' : '📸 Instagram'}
          </button>
        ))}
        {filtroCanalAt === 'email' && (
          <>
            <span style={{ margin:'0 4px', color: theme.gray600 }}>|</span>
            {[['todas','Todas as caixas'],['cliente','✉️ cliente@'],['contato','✉️ contato@']].map(([conta,label]) => (
              <button key={conta} onClick={() => setFiltroContaEmail(conta)}
                style={{ padding:'5px 12px', borderRadius:20, border:`1px solid ${filtroContaEmail===conta ? theme.gold : theme.gray700}`,
                  background: filtroContaEmail===conta ? `${theme.gold}22` : 'transparent',
                  color: filtroContaEmail===conta ? theme.gold : theme.gray400, fontSize:12, cursor:'pointer', fontWeight:600 }}>
                {label}
              </button>
            ))}
          </>
        )}
      </div>

      {/* ── INBOX ── */}
      {viewMode === 'inbox' && (
        <div style={{ display:'flex', gap:0, height:'calc(100vh - 260px)', minHeight:520, background: theme.gray800, borderRadius:12, overflow:'hidden', border:`1px solid ${theme.gray700}` }}>
          {/* Lista lateral */}
          <div style={{ width:320, flexShrink:0, borderRight:`1px solid ${theme.gray700}`, overflowY:'auto' }}>
            {atendimentos.length === 0 && (
              <div style={{ padding:32, color: theme.gray500, textAlign:'center', fontSize:13 }}>Nenhum atendimento encontrado</div>
            )}
            {atendimentos
              .filter(a => filtroContaEmail==='todas' || a.canal!=='email' || a.inbox_thread_id===filtroContaEmail)
              .map(a => (
              <div key={a.id} onClick={() => abrirAtend(a)}
                style={{ padding:'14px 16px', borderBottom:`1px solid ${theme.gray700}`, cursor:'pointer',
                  background: selecionado?.id === a.id ? `${theme.gold}18` : 'transparent',
                  borderLeft: selecionado?.id === a.id ? `3px solid ${theme.gold}` : '3px solid transparent' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:16 }}>{canalIcon(a.canal)}</span>
                  <span style={{ color: theme.cream, fontWeight:600, fontSize:13, flex:1 }}>{a.contato_nome || a.contato_id}</span>
                  {a.canal==='email' && a.inbox_thread_id && (
                    <span title={a.inbox_thread_id} style={{ fontSize:9, padding:'1px 6px', borderRadius:8,
                      background: theme.gray700, color: theme.gray400, fontWeight:600 }}>
                      {a.inbox_thread_id.split('@')[0]}
                    </span>
                  )}
                  <span style={{ fontSize:10, color: tipoColor(a.tipo_contato), fontWeight:700, textTransform:'uppercase' }}>{a.tipo_contato}</span>
                  {a.tipo_contato==='lead' && a.score_max>0 && (
                    <span title="Score BANT" style={{ fontSize:9, padding:'1px 6px', borderRadius:8,
                      background: a.score_max>=60?'#4CAF5022':'#F59E0B22', color: a.score_max>=60?'#4CAF50':'#F59E0B', fontWeight:700 }}>
                      BANT {a.score_max}%
                    </span>
                  )}
                  {a.status !== 'excluido' && (
                    <button onClick={e => { e.stopPropagation(); excluirAtendimento(a.id); }}
                      title="Mover para a lixeira"
                      style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color: theme.gray600, padding:2 }}>
                      🗑️
                    </button>
                  )}
                </div>
                <div style={{ color: theme.gray400, fontSize:12, marginBottom:4, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
                  {a.ultima_msg || '—'}
                </div>
                <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                  <span style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:`${statusColor(a.status)}22`, color: statusColor(a.status), fontWeight:600 }}>
                    {statusLabel(a.status)}
                  </span>
                  {a.watchdog_alerta == 1 && (
                    <span title={{1:'4h sem resposta',2:'24h sem resposta — advogado notificado',3:'72h sem resposta — lembrete criado na Agenda'}[a.watchdog_nivel]||''}
                      style={{ fontSize:11, color: a.watchdog_nivel>=3?'#DC2626':a.watchdog_nivel>=2?'#F59E0B':'#FF6B6B' }}>
                      {'⏰'.repeat(Math.max(1, Math.min(3, a.watchdog_nivel||1)))}
                    </span>
                  )}
                  <span style={{ fontSize:10, color: theme.gray600, marginLeft:'auto' }}>
                    {a.ultima_atividade ? new Date(a.ultima_atividade).toLocaleString('pt-BR',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Thread de conversa */}
          <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
            {!selecionado ? (
              <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color: theme.gray500, fontSize:14 }}>
                Selecione um atendimento
              </div>
            ) : (
              <>
                {/* Header do atendimento */}
                <div style={{ padding:'12px 20px', borderBottom:`1px solid ${theme.gray700}`, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                  <span style={{ fontSize:20 }}>{canalIcon(selecionado.canal)}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ color: theme.cream, fontWeight:700, fontSize:14 }}>{selecionado.contato_nome || selecionado.contato_id}</div>
                    <div style={{ color: theme.gray400, fontSize:12 }}>{selecionado.canal} · {selecionado.tipo_contato}</div>
                  </div>
                  {/* Quick actions */}
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <select onChange={e => mudarStatus(selecionado.id, e.target.value)} value={selecionado.status}
                      style={{ padding:'5px 10px', borderRadius:6, background: theme.gray700, color: theme.cream, border:`1px solid ${theme.gray600}`, fontSize:12 }}>
                      {['novo','em_andamento','qualificado','nao_classificado','encerrado','excluido'].map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                    </select>
                    <button onClick={() => acionarIA('zion')} disabled={loadingIA}
                      style={{ padding:'5px 10px', borderRadius:6, border:'none', cursor:'pointer', fontSize:12, fontWeight:600, background:'#4CAF5022', color:'#4CAF50' }}>
                      {loadingIA ? '...' : 'ZION'}
                    </button>
                    <button onClick={() => acionarIA('atlas')} disabled={loadingIA}
                      style={{ padding:'5px 10px', borderRadius:6, border:'none', cursor:'pointer', fontSize:12, fontWeight:600, background:`${theme.gold}22`, color: theme.gold }}>
                      {loadingIA ? '...' : 'ATLAS'}
                    </button>
                    <button onClick={() => excluirAtendimento(selecionado.id)}
                      title="Mover para a lixeira"
                      style={{ padding:'5px 10px', borderRadius:6, border:`1px solid #EF444444`, cursor:'pointer', fontSize:12, fontWeight:600, background:'#EF444422', color:'#EF4444' }}>
                      🗑️
                    </button>
                    {selecionado.canal === 'email' && (
                      <button onClick={() => bloquearRemetente(selecionado.id, 'email')}
                        title="Bloquear este e-mail (próximos vão direto para a lixeira)"
                        style={{ padding:'5px 10px', borderRadius:6, border:`1px solid #EF444444`, cursor:'pointer', fontSize:12, fontWeight:600, background:'transparent', color:'#EF4444' }}>
                        🚫 Bloquear
                      </button>
                    )}
                    {/* Botão "→ CRM" desativado em 13/07/2026: gravava em leads_marketing (tabela
                        que o ModCRM nunca lê) — a sincronização automática via sincronizarLeadModCRM()
                        já cria o Lead em data.leads a qualquer novo contato. Ver project-app-controle-juridico. */}
                  </div>
                </div>

                {/* Mensagens */}
                <div style={{ flex:1, overflowY:'auto', padding:16, display:'flex', flexDirection:'column', gap:12 }}>
                  {mensagens.map((m, i) => {
                    const isMeu = ['advogado','atlas','zion','max'].includes(m.origem);
                    const pendente = m.aprovado !== null && m.aprovado !== undefined && Number(m.aprovado) === 0;
                    const descartada = m.aprovado !== null && m.aprovado !== undefined && Number(m.aprovado) === 2;
                    const ehEmail = selecionado.canal === 'email';

                    // E-mails recebidos são salvos como "De: Nome <email>\nAssunto: X\n\n<corpo>" —
                    // separa em campos visuais em vez de jogar tudo como texto corrido.
                    let deLinha = null, assuntoLinha = null, corpoTexto = m.mensagem;
                    if (ehEmail && m.origem === 'lead') {
                      const match = m.mensagem.match(/^De:\s*(.*)\nAssunto:\s*(.*)\n\n([\s\S]*)$/);
                      if (match) { deLinha = match[1]; assuntoLinha = match[2]; corpoTexto = match[3]; }
                    }

                    // Card de aprovação — largura cheia, fora do padrão de bolha de chat
                    // (é uma ação pendente, não uma mensagem já trocada com o contato).
                    if (pendente) {
                      return (
                        <div key={i} style={{ width:'100%', boxSizing:'border-box', padding:'12px 16px',
                          borderRadius:10, background:'#3a2f00', border:`1px dashed ${theme.gold}` }}>
                          <div style={{ fontSize:11, opacity:.8, marginBottom:8, textTransform:'uppercase', fontWeight:700, color: theme.gold }}>
                            {m.origem} · ⏳ aguardando aprovação
                          </div>
                          <textarea
                            defaultValue={m.mensagem}
                            onChange={e => setTextoAprovacao(t => ({...t, [m.id]: e.target.value}))}
                            rows={10}
                            style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px', borderRadius:6,
                              background: theme.gray900, color: theme.cream, border:`1px solid ${theme.gray600}`,
                              fontSize:13, fontFamily:'inherit', resize:'vertical', marginBottom:10, lineHeight:1.6 }} />
                          <div style={{ display:'flex', gap:6 }}>
                            <button onClick={() => aprovarMensagem(m.id)} disabled={loadingAprov===m.id}
                              style={{ flex:1, padding:'8px 10px', borderRadius:6, border:'none', cursor:'pointer',
                                fontSize:12, fontWeight:700, background: theme.gold, color:'#000' }}>
                              {loadingAprov===m.id ? '...' : '✓ Aprovar e Enviar'}
                            </button>
                            <button onClick={() => descartarMensagem(m.id)} disabled={loadingAprov===m.id}
                              style={{ padding:'8px 14px', borderRadius:6, border:`1px solid ${theme.gray600}`, cursor:'pointer',
                                fontSize:12, fontWeight:600, background:'transparent', color: theme.gray400 }}>
                              ✕ Descartar
                            </button>
                          </div>
                          <div style={{ fontSize:10, opacity:.6, marginTop:6, textAlign:'right', color: theme.gray400 }}>
                            {m.created_at ? new Date(m.created_at).toLocaleString('pt-BR',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : ''}
                          </div>
                        </div>
                      );
                    }

                    // Bolha de chat normal (mensagem já trocada de fato) — mensagens de e-mail
                    // ficam mais largas (90%) porque leem melhor que uma bolha de chat estreita.
                    return (
                      <div key={i} style={{ display:'flex', justifyContent: isMeu ? 'flex-end' : 'flex-start' }}>
                        <div style={{ maxWidth: ehEmail ? '90%' : '70%', width: ehEmail ? '90%' : 'auto', padding:'10px 14px',
                          borderRadius: isMeu ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                          background: descartada ? theme.gray700 : (isMeu ? (m.origem==='advogado' ? theme.gold : m.origem==='atlas' ? '#8B5CF6' : '#4CAF50') : theme.gray700),
                          opacity: descartada ? .5 : 1,
                          color: descartada ? theme.gray400 : (isMeu ? (m.origem==='advogado' ? '#000' : '#fff') : theme.cream) }}>
                          <div style={{ fontSize:10, opacity:.7, marginBottom:6, textTransform:'uppercase', fontWeight:600 }}>
                            {m.origem}{descartada ? ' · descartada' : ''}
                          </div>
                          {deLinha && (
                            <div style={{ fontSize:12, marginBottom:8, paddingBottom:8, borderBottom:'1px solid rgba(255,255,255,.15)' }}>
                              <div><strong>De:</strong> {deLinha}</div>
                              <div><strong>Assunto:</strong> {assuntoLinha}</div>
                            </div>
                          )}
                          <div style={{ fontSize:13, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{corpoTexto}</div>
                          <div style={{ fontSize:10, opacity:.6, marginTop:6, textAlign:'right' }}>
                            {m.created_at ? new Date(m.created_at).toLocaleString('pt-BR',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : ''}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Input de resposta */}
                <div style={{ padding:12, borderTop:`1px solid ${theme.gray700}`, display:'flex', gap:8 }}>
                  <textarea value={resposta} onChange={e => setResposta(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarResposta(); } }}
                    placeholder="Responder como advogado... (Enter para enviar, Shift+Enter para nova linha)"
                    style={{ flex:1, padding:'10px 12px', borderRadius:8, background: theme.gray700, color: theme.cream,
                      border:`1px solid ${theme.gray600}`, fontSize:13, resize:'none', height:64, fontFamily:'inherit' }} />
                  <button onClick={enviarResposta} disabled={loadingResp || !resposta.trim()}
                    style={{ padding:'0 20px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:700, fontSize:13,
                      background: resposta.trim() ? theme.gold : theme.gray700, color: resposta.trim() ? '#000' : theme.gray500 }}>
                    {loadingResp ? '...' : 'Enviar'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── KANBAN ── */}
      {viewMode === 'kanban' && (
        <div style={{ display:'flex', gap:12, overflowX:'auto', paddingBottom:8, width:'100%', maxWidth:'100%', boxSizing:'border-box' }}>
          {COLUNAS_KANBAN.map(status => {
            const itens = atendimentos.filter(a => a.status === status);
            return (
              <div key={status} style={{ width:260, flexShrink:0, background: theme.gray800, borderRadius:12, border:`1px solid ${theme.gray700}`, overflow:'hidden' }}>
                <div style={{ padding:'12px 16px', borderBottom:`1px solid ${theme.gray700}`, display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ width:10, height:10, borderRadius:'50%', background: statusColor(status), display:'inline-block' }} />
                  <span style={{ color: theme.cream, fontWeight:700, fontSize:13 }}>{statusLabel(status)}</span>
                  <span style={{ marginLeft:'auto', fontSize:12, color: theme.gray500, fontWeight:600 }}>{itens.length}</span>
                </div>
                <div style={{ padding:8, display:'flex', flexDirection:'column', gap:8, maxHeight:500, overflowY:'auto' }}>
                  {itens.map(a => (
                    <div key={a.id} onClick={() => { setViewMode('inbox'); abrirAtend(a); }}
                      style={{ background: theme.gray700, borderRadius:8, padding:'10px 12px', cursor:'pointer',
                        border:`1px solid ${theme.gray600}`, transition:'border-color .15s' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                        <span>{canalIcon(a.canal)}</span>
                        <span style={{ color: theme.cream, fontWeight:600, fontSize:12, flex:1 }}>{a.contato_nome || a.contato_id}</span>
                      </div>
                      <div style={{ color: theme.gray400, fontSize:11, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis', marginBottom:6 }}>
                        {a.ultima_msg || '—'}
                      </div>
                      <div style={{ display:'flex', gap:4 }}>
                        {['novo','em_andamento','qualificado','encerrado'].filter(s => s !== status).slice(0,2).map(s => (
                          <button key={s} onClick={e => { e.stopPropagation(); mudarStatus(a.id, s); }}
                            style={{ fontSize:10, padding:'2px 6px', borderRadius:4, border:`1px solid ${statusColor(s)}44`,
                              background:'transparent', color: statusColor(s), cursor:'pointer', fontWeight:600 }}>
                            → {statusLabel(s).split(' ')[0]}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {itens.length === 0 && <div style={{ color: theme.gray600, fontSize:12, textAlign:'center', padding:16 }}>Vazio</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// SIDEBAR / NAVEGAÇÃO
// ============================================================
const MENU = [
  { id: "atendimentos",label: "Atendimentos",               icon: "💬" },
  { id: "agenda",     label: "Agenda",                     icon: "📅" },
  { id: "arquivo",    label: "Arquivo de Documentos",      icon: "🗂️" },
  { id: "areas",      label: "Áreas Jurídicas",            icon: "⚖" },
  { id: "cowork",     label: "Assistente Cowork",          icon: "🧠" },
  { id: "base",       label: "Base Operacional",           icon: "📁" },
  { id: "clientes",   label: "Clientes",                   icon: "👤" },
  { id: "crm",        label: "CRM Comercial",              icon: "🎯" },
  { id: "dashboard",  label: "Dashboard",                  icon: "📊" },
  { id: "dashexec",   label: "Dashboard Executivo",        icon: "👔" },
  { id: "dashtrib",   label: "Dashboard Tributário",       icon: "📊" },
  { id: "equipe",     label: "Equipe",                     icon: "👥" },
  { id: "financeiro", label: "Financeiro",                 icon: "💰" },
  { id: "governanca", label: "Governança",                 icon: "🏛️" },
  { id: "painelia",   label: "IA - Assistentes Jurídicos", icon: "🤖" },
  { id: "marketing",  label: "Marketing",                  icon: "📣" },
  { id: "parcerias",  label: "Parcerias",                  icon: "🤝" },
  { id: "pops",       label: "POPs",                       icon: "📋" },
  { id: "processos",  label: "Processos",                  icon: "📋" },
  { id: "produtos",   label: "Produtos",                   icon: "📦" },
  { id: "rct",        label: "RCT Tributário",             icon: "🔍" },
  { id: "relatorios", label: "Relatórios",                 icon: "📄" },
  { id: "vendas",     label: "Vendas",                     icon: "📊" },
  { id: "sala",       label: "Sala Virtual",               icon: "🏢" },
  { id: "---" },
  { id: "manual",     label: "Manual do Sistema",          icon: "📖" },
  { id: "acesso",     label: "Controle de Acesso",         icon: "🔐" },
  { id: "escritorio", label: "Dados do Escritório",        icon: "🏢" },
];

function Sidebar({ ativo, setAtivo, setAnterior, expanded, setExpanded, usuario, onLogout, modulosPermitidos = [] }) {
  return (
    <div style={{
      width: expanded ? 220 : 64, minHeight: "100vh",
      background: theme.gray900,
      borderRight: `1px solid ${theme.gray800}`,
      transition: "width 0.3s ease",
      display: "flex", flexDirection: "column",
      position: "fixed", top: 0, left: 0, zIndex: 100, height: "100vh", overflow: "hidden"
    }}>
      {/* Logo */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: expanded ? "24px 20px 20px" : "24px 0 20px",
          borderBottom: `1px solid ${theme.gray800}`,
          cursor: "pointer", display: "flex", alignItems: "center",
          justifyContent: expanded ? "flex-start" : "center", gap: 12
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
          overflow: "hidden",
          boxShadow: `0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 ${theme.gold}44`,
          border: `1px solid ${theme.gold}44`
        }}>
          <img src="data:image/jpeg;base64,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" alt="A&C"
            style={{width:"100%", height:"100%", objectFit:"cover"}}/>
        </div>
        {expanded && (
          <div className="slide-in">
            <p className="serif" style={{ color: theme.cream, fontSize: 12, fontWeight: 600, letterSpacing: 0.8, lineHeight: 1.3 }}>ALVES & CORREA</p>
            <p className="serif" style={{ color: theme.gold, fontSize: 11, fontWeight: 500, letterSpacing: 0.8, lineHeight: 1.3 }}>Advogados</p>

          </div>
        )}
      </div>

      {/* Menu */}
      <nav style={{ flex: 1, padding: "12px 0", overflowY: "auto", scrollbarWidth: "none", msOverflowStyle: "none" }}>
        {MENU.map((item, i) => {
          if (item.id === "---") return <div key={i} style={{ height: 1, background: theme.gray800, margin: "8px 12px" }} />;
          const isAtivo = ativo === item.id;
          const isFuture = !!item.badge;
          const semPermissao = !modulosPermitidos.includes(item.id) && !isFuture;
          return (
            <div
              key={item.id}
              onClick={() => !isFuture && !semPermissao && (setAnterior(null), setAtivo(item.id))}
              title={!expanded ? item.label : ""}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: expanded ? "10px 16px" : "10px 0",
                justifyContent: expanded ? "flex-start" : "center",
                margin: "2px 8px", borderRadius: 8,
                background: isAtivo ? `${theme.gold}18` : "transparent",
                borderLeft: isAtivo ? `3px solid ${theme.gold}` : "3px solid transparent",
                cursor: (isFuture || semPermissao) ? "default" : "pointer",
                opacity: isFuture ? 0.45 : semPermissao ? 0.2 : 1,
                transition: "all 0.15s"
              }}
              onMouseEnter={e => { if (!isAtivo && !isFuture && !semPermissao) e.currentTarget.style.background = `${theme.gray700}80`; }}
              onMouseLeave={e => { if (!isAtivo) e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
              {expanded && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, overflow: "hidden" }}>
                  <span style={{ fontSize: 12, color: isAtivo ? theme.gold : theme.gray300, fontWeight: isAtivo ? 600 : 400, whiteSpace: "nowrap" }}>
                    {item.label}
                  </span>
                  {item.badge && (
                    <span style={{ fontSize: 8, background: theme.gray700, color: theme.gray400, padding: "1px 5px", borderRadius: 3, letterSpacing: 0.5 }}>
                      {item.badge}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{ borderTop: `1px solid ${theme.gray800}`, padding: expanded ? "14px 16px" : "14px 0", display: "flex", alignItems: "center", justifyContent: expanded ? "space-between" : "center", gap: 10 }}>
        {expanded && (
          <div style={{ overflow: "hidden" }}>
            <p style={{ fontSize: 12, color: theme.gray300, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{usuario.nome}</p>
            <p style={{ fontSize: 10, color: theme.gray500 }}>{usuario.perfil}</p>
          </div>
        )}
        <button onClick={onLogout} title="Sair do sistema" style={{
          background: "none", border: `1px solid ${theme.gray700}`, color: theme.gray400,
          cursor: "pointer", fontSize: 11, fontWeight: 600,
          flexShrink: 0, borderRadius: 6, padding: expanded ? "6px 10px" : "6px 8px",
          display: "flex", alignItems: "center", gap: 5,
          transition: "all 0.2s"
        }}
          onMouseEnter={e => { e.currentTarget.style.background=theme.red; e.currentTarget.style.color="#fff"; e.currentTarget.style.borderColor=theme.red; }}
          onMouseLeave={e => { e.currentTarget.style.background="none"; e.currentTarget.style.color=theme.gray400; e.currentTarget.style.borderColor=theme.gray700; }}
        >
          <span style={{fontSize:14}}>⏻</span>{expanded && <span>Sair</span>}
        </button>
      </div>
    </div>
  );
}


// ============================================================
// MÓDULO: DASHBOARD EXECUTIVO
// ============================================================
function ModDashboard({ data, setAtivo }) {
  const { clientes, contratos, lancamentos, despesas, produtos, equipe } = data;
  const hoje = new Date();
  const [periodo, setPeriodo] = useState("todos");

  // ── helpers ──────────────────────────────────────────────
  const nomesMes = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

  const filtrarPorPeriodo = (lista, campo) => {
    if (periodo === "todos") return lista;
    const mesesAtras = { "30d": 1, "90d": 3, "6m": 6, "12m": 12 }[periodo] || 999;
    const corte = new Date();
    corte.setMonth(corte.getMonth() - mesesAtras);
    return lista.filter(item => item[campo] && new Date(item[campo]) >= corte);
  };

  const lansFiltrados = filtrarPorPeriodo(lancamentos, "dataVencimento");

  // ── KPIs financeiros ──────────────────────────────────────
  const receitaContratada  = lansFiltrados.reduce((s,l) => s + (l.valor||0), 0);
  const receitaRecebida    = lansFiltrados.filter(l => l.status==="Recebido").reduce((s,l) => s + (l.valor||0), 0);
  const receitaAReceber    = lansFiltrados.filter(l => l.status==="A Receber").reduce((s,l) => s + (l.valor||0), 0);
  const inadimplencia      = lansFiltrados.filter(l => l.status==="A Receber" && l.dataVencimento && new Date(l.dataVencimento)<hoje).reduce((s,l)=>s+(l.valor||0),0);
  const totalCustos        = despesas.filter(d=>d.grupo==="Custo").reduce((s,d)=>s+(d.valor||0),0);
  const totalDespesas      = despesas.filter(d=>d.grupo==="Despesa").reduce((s,d)=>s+(d.valor||0),0);
  const margemBruta        = receitaRecebida - totalCustos;
  const margemLiquida      = receitaRecebida - totalCustos - totalDespesas;
  const percMargem         = receitaRecebida > 0 ? Math.round((margemLiquida/receitaRecebida)*100) : 0;

  // ── KPIs operacionais ─────────────────────────────────────
  const contratosAtivos    = contratos.filter(c => c.status==="Em andamento").length;
  const contratosJudiciais = contratos.filter(c => c.tipoProcesso==="Judicial").length;
  const contratosAdmin2    = contratos.filter(c => c.tipoProcesso==="Administrativo").length;
  const prazosCriticos     = contratos.filter(c => {
    if (!c.prazo || c.status==="Concluido") return false;
    const dias = Math.floor((new Date(c.prazo)-hoje)/(1000*60*60*24));
    return dias>=0 && dias<=3;
  }).length;
  const paradosMais20      = contratos.filter(c => {
    if (!c.ultimoAvanco || c.status==="Concluido") return false;
    return Math.floor((hoje-new Date(c.ultimoAvanco))/(1000*60*60*24)) > 20;
  });

  // ── Score médio de risco ──────────────────────────────────
  const calcScore = c => {
    let s = 0;
    if(c.teseNova) s+=2; if(c.jurisprudenciaInstavel) s+=2;
    if(c.valorElevado) s+=2; if(c.dependePericia) s+=1; if(c.documentoIncompleto) s+=1;
    const dp = c.ultimoAvanco ? Math.floor((hoje-new Date(c.ultimoAvanco))/(1000*60*60*24)) : 0;
    if(dp>30) s+=2; if(dp>60) s+=1;
    return s;
  };
  const contRiscoAlto = contratos.filter(c => calcScore(c)>=6).length;
  const contRiscoMed  = contratos.filter(c => { const s=calcScore(c); return s>=3&&s<6; }).length;

  // ── Receita por área ─────────────────────────────────────
  const receitaPorArea = {};
  lansFiltrados.forEach(l => {
    const cont = contratos.find(c=>c.id===l.contratoId);
    const area = cont?.area || "Outros";
    if(!receitaPorArea[area]) receitaPorArea[area]={contratada:0,recebida:0};
    receitaPorArea[area].contratada += l.valor||0;
    if(l.status==="Recebido") receitaPorArea[area].recebida += l.valor||0;
  });
  const areasOrdenadas = Object.entries(receitaPorArea).sort((a,b)=>b[1].contratada-a[1].contratada);
  const maxArea = areasOrdenadas[0]?.[1]?.contratada || 1;

  // ── Margem por produto ───────────────────────────────────
  const margemPorProduto = {};
  contratos.forEach(c => {
    const prod = produtos.find(p=>p.id===c.produtoId);
    const nome = prod?.nome || "Outros";
    if(!margemPorProduto[nome]) margemPorProduto[nome]={receita:0,custo:0,qtd:0};
    margemPorProduto[nome].receita += c.valorContratado||0;
    margemPorProduto[nome].custo   += c.custoOperacional||0;
    margemPorProduto[nome].qtd     += 1;
  });

  // ── Projeção mensal ──────────────────────────────────────
  const projecaoMensal = {};
  lancamentos.filter(l=>l.status==="A Receber"&&l.dataVencimento).forEach(l=>{
    const mes = l.dataVencimento.slice(0,7);
    if(!projecaoMensal[mes]) projecaoMensal[mes]=0;
    projecaoMensal[mes]+=l.valor||0;
  });
  const projecaoOrdenada = Object.entries(projecaoMensal).sort().slice(0,6);
  const maxProj = projecaoOrdenada[0] ? Math.max(...projecaoOrdenada.map(x=>x[1])) : 1;

  // ── KPIs estratégicos — Bloco C ──────────────────────────
  const lansRecebidos      = lancamentos.filter(l=>l.status==="Recebido");
  const clientesComReceita = [...new Set(lansRecebidos.map(l=>l.clienteId).filter(Boolean))];
  const ticketMedio        = lansRecebidos.length > 0
    ? lansRecebidos.reduce((s,l)=>s+(l.valor||0),0) / lansRecebidos.length : 0;
  const ltv                = clientesComReceita.length > 0
    ? lansRecebidos.reduce((s,l)=>s+(l.valor||0),0) / clientesComReceita.length : 0;

  // CAC: despesas de Marketing / clientes novos últimos 12 meses
  const dataCorteCAC = new Date(); dataCorteCAC.setFullYear(dataCorteCAC.getFullYear()-1);
  const despMarketing  = (data.despesas||[]).filter(d=>
    (d.grupo||"").toLowerCase().includes("market")||(d.categoria||"").toLowerCase().includes("market")
  ).reduce((s,d)=>s+(d.valor||0),0);
  const clientesNovos  = clientes.filter(c=>c.dataCadastro&&new Date(c.dataCadastro)>=dataCorteCAC).length;
  const cac            = clientesNovos > 0 ? despMarketing / clientesNovos : 0;

  // Churn: contratos encerrados/cancelados no mês atual / contratos que existiam no início do mês
  const mesCorr  = new Date().toISOString().slice(0,7);
  const encerradosMes = contratos.filter(c=>
    (c.status==="Concluido"||c.status==="Cancelado")&&
    (c.dataEncerramento||c.dataCancelamento||"").slice(0,7)===mesCorr
  ).length;
  const ativosInicioMes = contratos.filter(c=>
    c.status==="Em andamento"||(c.dataEncerramento||"").slice(0,7)===mesCorr
  ).length;
  const churnRate = ativosInicioMes > 0 ? Math.round((encerradosMes/ativosInicioMes)*100) : 0;

  // Índice de Saúde Financeira (0-100): margem, inadimplência, churn, processos parados, risco
  const totalRecGeral = lansRecebidos.reduce((s,l)=>s+(l.valor||0),0);
  const totalDespGeral = (data.despesas||[]).reduce((s,d)=>s+(d.valor||0),0);
  const margemPctSaude = totalRecGeral > 0 ? (totalRecGeral-totalDespGeral)/totalRecGeral : 0;
  const inadPct        = totalRecGeral > 0 ? inadimplencia / totalRecGeral : 0;
  let indiceSaude = 100;
  if(margemPctSaude < 0.10)  indiceSaude -= 30; else if(margemPctSaude < 0.25) indiceSaude -= 15;
  if(inadPct > 0.20)         indiceSaude -= 25; else if(inadPct > 0.10)        indiceSaude -= 12;
  if(churnRate > 10)         indiceSaude -= 15; else if(churnRate > 5)         indiceSaude -= 7;
  if(paradosMais20.length > 3) indiceSaude -= 15; else if(paradosMais20.length > 0) indiceSaude -= 5;
  if(contRiscoAlto > 2)      indiceSaude -= 15; else if(contRiscoAlto > 0)    indiceSaude -= 5;
  indiceSaude = Math.max(0, Math.min(100, indiceSaude));
  const corSaude = indiceSaude >= 70 ? theme.green : indiceSaude >= 40 ? theme.amber : theme.red;

  // Evolução receita 12 meses
  const rec12m = {};
  const nomesMes12 = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  lansRecebidos.filter(l=>l.dataVencimento).forEach(l=>{
    const m=l.dataVencimento.slice(0,7); rec12m[m]=(rec12m[m]||0)+(l.valor||0);
  });
  const rec12mOrdenado = Object.entries(rec12m).sort().slice(-12);
  const maxRec12m = Math.max(...rec12mOrdenado.map(x=>x[1]),1);

  // ── Alertas ──────────────────────────────────────────────
  const alertas = [];
  if(prazosCriticos>0)  alertas.push({ tipo:"danger",  icon:"🚨", msg: `${prazosCriticos} prazo(s) crítico(s) vencendo em até 3 dias`, goto:"base" });
  if(paradosMais20.length>0) alertas.push({ tipo:"warning", icon:"⏸",  msg: `${paradosMais20.length} processo(s) parado(s) há mais de 20 dias`, goto:"processos" });
  if(inadimplencia>0)   alertas.push({ tipo:"warning", icon:"⚠️",  msg: `R$ ${inadimplencia.toLocaleString("pt-BR",{minimumFractionDigits:2})} em parcelas vencidas`, goto:"financeiro" });
  if(contRiscoAlto>0)   alertas.push({ tipo:"danger",  icon:"🔴", msg: `${contRiscoAlto} processo(s) com risco alto`, goto:"base" });

  // ── Sem contato por X dias (clientes e leads) ────────────
  const [filtroSemContato, setFiltroSemContato] = useState(30);

  const ultimoContatoCliente = (cli) => {
    if (!cli.historico || cli.historico.length === 0) return cli.mesAnoContrato ? new Date(cli.mesAnoContrato + "-01") : null;
    const datas = cli.historico.map(h => new Date(h.data)).filter(d => !isNaN(d));
    return datas.length > 0 ? new Date(Math.max(...datas)) : null;
  };

  const ultimoContatoLead = (lead) => {
    if (!lead.historico || lead.historico.length === 0) return lead.dataEntrada ? new Date(lead.dataEntrada) : null;
    const datas = lead.historico.map(h => new Date(h.data)).filter(d => !isNaN(d));
    return datas.length > 0 ? new Date(Math.max(...datas)) : null;
  };

  const diasSemContato = (dt) => dt ? Math.floor((hoje - dt) / (1000*60*60*24)) : 9999;

  const clientesSemContato = clientes.filter(c => c.ativo).map(c => ({
    ...c, tipo_reg: "Cliente",
    ultimoContato: ultimoContatoCliente(c),
    dias: diasSemContato(ultimoContatoCliente(c))
  })).filter(c => c.dias >= filtroSemContato).sort((a,b) => b.dias - a.dias);

  const leadsSemContato = (data.leads || []).filter(l => l.estagio !== "fechado" && l.estagio !== "perdido").map(l => ({
    ...l, tipo_reg: "Lead",
    ultimoContato: ultimoContatoLead(l),
    dias: diasSemContato(ultimoContatoLead(l))
  })).filter(l => l.dias >= filtroSemContato).sort((a,b) => b.dias - a.dias);

  const semContatoTotal = [...clientesSemContato, ...leadsSemContato].sort((a,b) => b.dias - a.dias);

  // ── Alertas de sem contato ────────────────────────────────
  if(clientesSemContato.length > 0) alertas.push({ tipo:"warning", icon:"👥", msg: `${clientesSemContato.length} cliente(s) sem contato há mais de ${filtroSemContato} dias`, goto:"clientes" });
  if(leadsSemContato.length > 0)    alertas.push({ tipo:"warning", icon:"💡", msg: `${leadsSemContato.length} lead(s) ativo(s) sem contato há mais de ${filtroSemContato} dias`, goto:"crm" });

  // ── Responsáveis mais ativos ─────────────────────────────
  const contsPorResp = {};
  contratos.filter(c=>c.status==="Em andamento").forEach(c=>{
    if(!contsPorResp[c.responsavel]) contsPorResp[c.responsavel]=0;
    contsPorResp[c.responsavel]++;
  });

  const fmt = (v) => v.toLocaleString("pt-BR",{minimumFractionDigits:2});

  // ── Dados para gráficos ───────────────────────────────────────────────────
  // Gráfico pizza: processos por status
  const procStatus = {};
  (data.processos||[]).forEach(p=>{ procStatus[p.status]=(procStatus[p.status]||0)+1; });
  const procStatusEntries = Object.entries(procStatus);
  const totalProc = procStatusEntries.reduce((s,[,v])=>s+v,0)||1;

  // Gráfico barras: leads por estágio
  const leadsEstagio = {};
  (data.leads||[]).forEach(l=>{ leadsEstagio[l.estagio]=(leadsEstagio[l.estagio]||0)+1; });

  // Gráfico pizza: contratos por área
  const contrAreaMap = {};
  contratos.forEach(c=>{ contrAreaMap[c.area]=(contrAreaMap[c.area]||0)+1; });
  const contrAreaEntries = Object.entries(contrAreaMap).sort((a,b)=>b[1]-a[1]).slice(0,6);
  const totalContrArea = contrAreaEntries.reduce((s,[,v])=>s+v,0)||1;

  // Gráfico barras: receita por mês (últimos 6 meses)
  const recMensal = {};
  lancamentos.filter(l=>l.status==="Recebido"&&l.dataVencimento).forEach(l=>{
    const m=l.dataVencimento.slice(0,7);
    recMensal[m]=(recMensal[m]||0)+(l.valor||0);
  });
  const recMensalOrdenado = Object.entries(recMensal).sort().slice(-6);
  const maxRecMes = Math.max(...recMensalOrdenado.map(x=>x[1]),1);

  // Paleta de cores
  const CHART_COLORS = ["#C9A84C","#4C8AC9","#4CC97A","#C94C4C","#C97A4C","#7A4CC9","#4CC9C9","#C9C94C"];

  // Componente Pizza SVG (simples)
  const PizzaChart = ({entries, total, size=160}) => {
    let angle = -Math.PI/2;
    const cx=size/2, cy=size/2, r=size/2-8;
    const slices = entries.map(([label,val],i)=>{
      const pct=val/total;
      const startA=angle;
      angle+=2*Math.PI*pct;
      const x1=cx+r*Math.cos(startA), y1=cy+r*Math.sin(startA);
      const x2=cx+r*Math.cos(angle),  y2=cy+r*Math.sin(angle);
      const large=pct>0.5?1:0;
      return {label,val,pct,color:CHART_COLORS[i%CHART_COLORS.length],
        d:`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`};
    });
    return (
      <div style={{display:"flex",alignItems:"center",gap:16}}>
        <svg width={size} height={size} style={{flexShrink:0}}>
          {slices.map((s,i)=><path key={i} d={s.d} fill={s.color} stroke="#fff" strokeWidth={1.5}/>)}
        </svg>
        <div style={{fontSize:11}}>
          {slices.map((s,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:6,marginBottom:5}}>
              <div style={{width:10,height:10,borderRadius:2,background:s.color,flexShrink:0}}/>
              <span style={{color:theme.gray700}}>{s.label}</span>
              <span style={{fontWeight:700,color:theme.gray900,marginLeft:"auto",paddingLeft:8}}>{s.val} ({Math.round(s.pct*100)}%)</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Componente Barras horizontais
  const BarChart = ({entries, maxVal, color=theme.gold, height=22}) => (
    <div>
      {entries.map(([label,val],i)=>(
        <div key={i} style={{marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
            <span style={{fontSize:12,color:theme.gray700,textTransform:"capitalize"}}>{label}</span>
            <span style={{fontSize:12,fontWeight:700,color:CHART_COLORS[i%CHART_COLORS.length]}}>{val}</span>
          </div>
          <div style={{height:height,background:theme.gray100,borderRadius:4,overflow:"hidden"}}>
            <div style={{height:height,borderRadius:4,background:CHART_COLORS[i%CHART_COLORS.length],
              width:`${(val/maxVal)*100}%`,transition:"width 0.6s ease"}}/>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="fade-in">
      {/* Header + seletor período */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}>
        <div>
          <h1 className="serif" style={{fontSize:28,fontWeight:600,color:theme.gray900}}>Dashboard Executivo</h1>
          <p style={{fontSize:13,color:theme.gray500,marginTop:4}}>Visão consolidada · {hoje.toLocaleDateString("pt-BR",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</p>
        </div>
        <div style={{display:"flex",gap:6}}>
          {[["todos","Todo período"],["30d","30 dias"],["90d","90 dias"],["6m","6 meses"],["12m","12 meses"]].map(([v,l])=>(
            <button key={v} onClick={()=>setPeriodo(v)} style={{
              padding:"7px 14px",borderRadius:6,fontSize:12,fontWeight:periodo===v?700:400,
              border:`1px solid ${periodo===v?theme.gold:theme.gray200}`,
              background:periodo===v?`${theme.gold}18`:theme.white,
              color:periodo===v?theme.goldDark:theme.gray500,cursor:"pointer"
            }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Alertas */}
      {alertas.length>0 && (
        <div style={{marginBottom:24,display:"flex",flexDirection:"column",gap:8}}>
          {alertas.map((a,i)=>(
            <div key={i}
              onClick={()=>a.goto&&setAtivo&&setAtivo(a.goto)}
              style={{
                display:"flex",alignItems:"center",gap:12,padding:"11px 18px",borderRadius:8,
                background: a.tipo==="danger"?"#FEF2F2":"#FEFCE8",
                border:`1px solid ${a.tipo==="danger"?"#FECACA":"#FDE68A"}`,
                cursor:a.goto?"pointer":"default", transition:"opacity 0.15s"
              }}
              onMouseEnter={e=>{if(a.goto)e.currentTarget.style.opacity="0.8"}}
              onMouseLeave={e=>{if(a.goto)e.currentTarget.style.opacity="1"}}
            >
              <span style={{fontSize:16}}>{a.icon}</span>
              <span style={{fontSize:13,fontWeight:500,color:a.tipo==="danger"?theme.red:theme.amber}}>{a.msg}</span>
              {a.goto&&<span style={{marginLeft:"auto",fontSize:12,color:a.tipo==="danger"?theme.red:theme.amber,opacity:0.7}}>↗ Ver</span>}
            </div>
          ))}
        </div>
      )}

      {/* ── KPIs ESTRATÉGICOS ───────────────────────────────── */}
      <p style={{fontSize:10,color:theme.gray400,letterSpacing:1.2,textTransform:"uppercase",marginBottom:10}}>KPIs Estratégicos</p>
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:14,marginBottom:24}}>
        {[
          {l:"Ticket Médio",   v:`R$ ${ticketMedio.toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2})}`,  icon:"🎫", c:theme.blue,     tip:"Receita média por lançamento recebido"},
          {l:"LTV",            v:`R$ ${ltv.toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2})}`,           icon:"♻️", c:theme.green,    tip:"Receita total ÷ clientes com receita"},
          {l:"CAC",            v:cac>0?`R$ ${cac.toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2})}`:"—", icon:"🎯", c:theme.amber,   tip:"Despesas de Marketing ÷ clientes novos (12m)"},
          {l:"Churn Rate",     v:`${churnRate}%`,                                                                                 icon:"📉", c:churnRate>10?theme.red:churnRate>5?theme.amber:theme.green, tip:"Contratos encerrados no mês ÷ ativos"},
        ].map(k=>(
          <Card key={k.l} style={{padding:"14px 18px"}} title={k.tip}>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>{k.l}</p>
              <span style={{fontSize:16}}>{k.icon}</span>
            </div>
            <p style={{fontSize:20,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
            <p style={{fontSize:10,color:theme.gray400,marginTop:4}}>{k.tip}</p>
          </Card>
        ))}
        {/* Índice de Saúde Financeira */}
        <Card style={{padding:"14px 18px",background:indiceSaude>=70?"#f0fdf4":indiceSaude>=40?"#fefce8":"#fef2f2",border:`1px solid ${corSaude}33`}}>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8}}>Saúde Financeira</p>
            <span style={{fontSize:16}}>{indiceSaude>=70?"💚":indiceSaude>=40?"🟡":"🔴"}</span>
          </div>
          <p style={{fontSize:28,fontWeight:700,color:corSaude,marginTop:6}}>{indiceSaude}<span style={{fontSize:14}}>/100</span></p>
          <div style={{marginTop:6,background:`${corSaude}22`,borderRadius:99,height:6,overflow:"hidden"}}>
            <div style={{width:`${indiceSaude}%`,height:"100%",background:corSaude,borderRadius:99,transition:"width .5s"}}/>
          </div>
        </Card>
      </div>

      {/* ── EVOLUÇÃO RECEITA 12 MESES ───────────────────────── */}
      {rec12mOrdenado.length > 0 && (
        <Card style={{padding:"18px 20px",marginBottom:24}}>
          <p className="serif" style={{fontSize:16,fontWeight:700,color:theme.gray900,marginBottom:16}}>📈 Evolução da Receita — Últimos 12 meses</p>
          <div style={{display:"flex",gap:6,alignItems:"flex-end",height:120}}>
            {rec12mOrdenado.map(([mes,val])=>{
              const h = Math.max((val/maxRec12m)*100,4);
              const [ano,m] = mes.split("-");
              const label   = nomesMes12[parseInt(m,10)-1]+" "+ano.slice(2);
              const isCurr  = mes===new Date().toISOString().slice(0,7);
              return (
                <div key={mes} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                  <span style={{fontSize:9,color:theme.gray500,writingMode:"vertical-lr",transform:"rotate(180deg)",height:28,overflow:"hidden"}}>
                    {val>=1000?`R$${Math.round(val/1000)}k`:`R$${Math.round(val)}`}
                  </span>
                  <div title={`${label}: R$ ${val.toLocaleString("pt-BR",{minimumFractionDigits:2})}`}
                    style={{width:"100%",height:h,background:isCurr?theme.gold:theme.blue,borderRadius:"4px 4px 0 0",opacity:0.85,transition:"height .3s",cursor:"default"}}/>
                  <span style={{fontSize:9,color:isCurr?theme.goldDark:theme.gray500,fontWeight:isCurr?700:400,textAlign:"center",lineHeight:1.2}}>{label}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── GRÁFICOS ─────────────────────────────────────────── */}
      <p style={{fontSize:10,color:theme.gray400,letterSpacing:1.2,textTransform:"uppercase",marginBottom:10}}>Análise Visual</p>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginBottom:24}}>

        {/* Gráfico 1: Processos por Status */}
        <Card style={{padding:"18px 20px"}}>
          <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,marginBottom:14,fontWeight:600}}>Processos por Status</p>
          {procStatusEntries.length===0
            ?<div style={{textAlign:"center",color:theme.gray300,fontSize:12,padding:"20px 0"}}>Sem dados</div>
            :<PizzaChart entries={procStatusEntries} total={totalProc} size={150}/>
          }
        </Card>

        {/* Gráfico 2: Contratos por Área */}
        <Card style={{padding:"18px 20px"}}>
          <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,marginBottom:14,fontWeight:600}}>Contratos por Área</p>
          {contrAreaEntries.length===0
            ?<div style={{textAlign:"center",color:theme.gray300,fontSize:12,padding:"20px 0"}}>Sem dados</div>
            :<PizzaChart entries={contrAreaEntries} total={totalContrArea} size={150}/>
          }
        </Card>

        {/* Gráfico 3: Leads por Estágio */}
        <Card style={{padding:"18px 20px"}}>
          <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,marginBottom:14,fontWeight:600}}>Funil de Lides/Leads</p>
          {Object.keys(leadsEstagio).length===0
            ?<div style={{textAlign:"center",color:theme.gray300,fontSize:12,padding:"20px 0"}}>Sem dados</div>
            :<BarChart
                entries={["lead","qualificação","reunião","proposta","negociação","fechado","perdido"].filter(e=>leadsEstagio[e]).map(e=>[e,leadsEstagio[e]])}
                maxVal={Math.max(...Object.values(leadsEstagio),1)}
                height={18}
              />
          }
        </Card>
      </div>

      {/* Gráfico Receita Mensal — faixa larga */}
      {recMensalOrdenado.length > 0 && (
        <Card style={{padding:"18px 20px",marginBottom:20}}>
          <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,marginBottom:16,fontWeight:600}}>Receita Recebida — Últimos {recMensalOrdenado.length} Meses</p>
          <div style={{display:"flex",alignItems:"flex-end",gap:10,height:120}}>
            {recMensalOrdenado.map(([mes,val],i)=>{
              const pct=val/maxRecMes;
              const [ano,m]=mes.split("-");
              const nomeMes=["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"][parseInt(m)-1];
              return (
                <div key={mes} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                  <span style={{fontSize:9,color:theme.gray500,fontWeight:600}}>
                    {val>=1000?`R$${(val/1000).toFixed(0)}k`:val>0?`R$${val.toFixed(0)}`:"—"}
                  </span>
                  <div style={{width:"100%",borderRadius:"4px 4px 0 0",
                    background:CHART_COLORS[i%CHART_COLORS.length],
                    height:`${Math.max(pct*100,4)}%`,transition:"height .5s ease"}}/>
                  <span style={{fontSize:10,color:theme.gray500,whiteSpace:"nowrap"}}>{nomeMes}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* KPIs Financeiros — linha 1 */}
      <p style={{fontSize:10,color:theme.gray400,letterSpacing:1.2,textTransform:"uppercase",marginBottom:10}}>Financeiro</p>
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:14,marginBottom:20}}>
        {[
          {l:"Receita Contratada", v:receitaContratada, c:theme.gray800,  icon:"📋", sub:null, goto:"financeiro"},
          {l:"Receita Recebida",   v:receitaRecebida,   c:theme.green,    icon:"✅", sub:null, goto:"financeiro"},
          {l:"A Receber",          v:receitaAReceber,   c:theme.amber,    icon:"⏳", sub:null, goto:"financeiro"},
          {l:"Inadimplência",      v:inadimplencia,     c:theme.red,      icon:"⚠️", sub:null, goto:"financeiro"},
          {l:"Margem Líquida",     v:margemLiquida,     c:percMargem>=30?theme.green:percMargem>=15?theme.amber:theme.red, icon:"💹", sub:`${percMargem}%`},
        ].map(k=>(
          <Card key={k.l}
            style={{padding:"16px 18px",cursor:k.goto?"pointer":"default",transition:"box-shadow 0.15s"}}
            onClick={()=>k.goto&&setAtivo&&setAtivo(k.goto)}
            onMouseEnter={e=>{if(k.goto)e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,0.1)"}}
            onMouseLeave={e=>{if(k.goto)e.currentTarget.style.boxShadow=""}}
          >
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <p style={{fontSize:10,color:theme.gray400,letterSpacing:0.8,textTransform:"uppercase",lineHeight:1.4}}>{k.l}</p>
              <span style={{fontSize:15}}>{k.icon}{k.goto&&<span style={{fontSize:9,color:theme.gray300,marginLeft:3}}>↗</span>}</span>
            </div>
            <p style={{fontSize:18,fontWeight:700,color:k.c,marginTop:8}}>
              <span style={{fontSize:10,fontWeight:400}}>R$ </span>{fmt(k.v)}
            </p>
            {k.sub && <p style={{fontSize:11,color:k.c,marginTop:3,fontWeight:600}}>{k.sub} de margem</p>}
          </Card>
        ))}
      </div>

      {/* KPIs Operacionais — linha 2 */}
      <p style={{fontSize:10,color:theme.gray400,letterSpacing:1.2,textTransform:"uppercase",marginBottom:10}}>Operacional</p>
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:14,marginBottom:28}}>
        {[
          {l:"Contratos Ativos",   v:contratosAtivos,   c:theme.blue,   icon:"📁", goto:"base"},
          {l:"Judicial",           v:contratosJudiciais,c:theme.blue,   icon:"⚖️", goto:"processos"},
          {l:"Administrativo",     v:contratosAdmin2,   c:theme.blue,   icon:"🏛",  goto:"processos"},
          {l:"Prazos Críticos ≤3d",v:prazosCriticos,    c:prazosCriticos>0?theme.red:theme.green, icon:"🚨", goto:"base"},
          {l:"Risco Alto",         v:contRiscoAlto,     c:contRiscoAlto>0?theme.red:theme.green,  icon:"🔴", goto:"base"},
        ].map(k=>(
          <Card key={k.l}
            style={{padding:"16px 18px",cursor:k.goto?"pointer":"default",transition:"box-shadow 0.15s"}}
            onClick={()=>k.goto&&setAtivo&&setAtivo(k.goto)}
            onMouseEnter={e=>{if(k.goto)e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,0.1)"}}
            onMouseLeave={e=>{if(k.goto)e.currentTarget.style.boxShadow=""}}
          >
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <p style={{fontSize:10,color:theme.gray400,letterSpacing:0.8,textTransform:"uppercase",lineHeight:1.4}}>{k.l}</p>
              <span style={{fontSize:15}}>{k.icon}{k.goto&&<span style={{fontSize:9,color:theme.gray300,marginLeft:3}}>↗</span>}</span>
            </div>
            <p style={{fontSize:26,fontWeight:700,color:k.c,marginTop:8}}>{k.v}</p>
          </Card>
        ))}
      </div>

      {/* Gráficos — linha 3: Receita por área + Projeção */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:20}}>

        {/* Receita por Área */}
        <Card style={{padding:24}}>
          <p className="serif" style={{fontSize:18,fontWeight:600,marginBottom:4}}>Receita por Área Jurídica</p>
          <p style={{fontSize:11,color:theme.gray400,marginBottom:20}}>Contratado vs Recebido</p>
          {areasOrdenadas.length===0
            ? <p style={{color:theme.gray400,fontSize:13,textAlign:"center",padding:"20px 0"}}>Sem dados no período</p>
            : areasOrdenadas.map(([area,vals])=>{
              const pctRecebido = vals.contratada>0?(vals.recebida/vals.contratada)*100:0;
              const pctBarra    = (vals.contratada/maxArea)*100;
              return (
                <div key={area} style={{marginBottom:16}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                    <span style={{fontSize:12,fontWeight:500,color:theme.gray700}}>{area}</span>
                    <div style={{textAlign:"right"}}>
                      <span style={{fontSize:11,color:theme.green,fontWeight:600}}>R$ {fmt(vals.recebida)}</span>
                      <span style={{fontSize:10,color:theme.gray400}}> / R$ {fmt(vals.contratada)}</span>
                    </div>
                  </div>
                  <div style={{height:10,background:theme.gray100,borderRadius:5,position:"relative"}}>
                    <div style={{height:"100%",width:`${pctBarra}%`,background:theme.gray200,borderRadius:5,position:"absolute"}}/>
                    <div style={{height:"100%",width:`${(pctBarra*pctRecebido/100)}%`,background:`linear-gradient(90deg,${theme.gold},${theme.green})`,borderRadius:5,position:"absolute"}}/>
                  </div>
                  <p style={{fontSize:10,color:theme.gray400,marginTop:3}}>{Math.round(pctRecebido)}% recebido</p>
                </div>
              );
            })
          }
        </Card>

        {/* Projeção de Receita */}
        <Card style={{padding:24}}>
          <p className="serif" style={{fontSize:18,fontWeight:600,marginBottom:4}}>Projeção de Receita</p>
          <p style={{fontSize:11,color:theme.gray400,marginBottom:20}}>A receber por mês</p>
          {projecaoOrdenada.length===0
            ? <p style={{color:theme.gray400,fontSize:13,textAlign:"center",padding:"20px 0"}}>Nenhum lançamento futuro</p>
            : projecaoOrdenada.map(([mes,valor])=>{
              const [ano,m] = mes.split("-");
              const pct = (valor/maxProj)*100;
              return (
                <div key={mes} style={{marginBottom:16}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                    <span style={{fontSize:12,fontWeight:500,color:theme.gray700}}>{nomesMes[parseInt(m)-1]}/{ano}</span>
                    <span style={{fontSize:12,fontWeight:700,color:theme.amber}}>R$ {fmt(valor)}</span>
                  </div>
                  <div style={{height:10,background:theme.gray100,borderRadius:5}}>
                    <div style={{height:"100%",width:`${pct}%`,background:`linear-gradient(90deg,${theme.gold},${theme.amber})`,borderRadius:5}}/>
                  </div>
                </div>
              );
            })
          }
          <div style={{marginTop:16,padding:"10px 14px",background:`${theme.gold}11`,border:`1px solid ${theme.gold}33`,borderRadius:8}}>
            <p style={{fontSize:12,color:theme.goldDark}}>Total projetado: <strong>R$ {fmt(receitaAReceber)}</strong></p>
          </div>
        </Card>
      </div>

      {/* Margem por produto + Processos parados */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:20}}>

        {/* Margem por Produto */}
        <Card style={{padding:24}}>
          <p className="serif" style={{fontSize:18,fontWeight:600,marginBottom:4}}>Margem por Produto</p>
          <p style={{fontSize:11,color:theme.gray400,marginBottom:16}}>Receita, custo e margem real</p>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{borderBottom:`1px solid ${theme.gray100}`}}>
                {["Produto","Qtd","Receita","Custo","Margem %"].map(h=>(
                  <th key={h} style={{padding:"6px 8px",textAlign:"left",fontSize:9,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(margemPorProduto).map(([nome,v])=>{
                const marg = v.receita>0?Math.round(((v.receita-v.custo)/v.receita)*100):0;
                return (
                  <tr key={nome} style={{borderBottom:`1px solid ${theme.gray100}`}}>
                    <td style={{padding:"9px 8px",fontSize:12,color:theme.gray800,maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{nome}</td>
                    <td style={{padding:"9px 8px",fontSize:12,color:theme.gray500,textAlign:"center"}}>{v.qtd}</td>
                    <td style={{padding:"9px 8px",fontSize:11,color:theme.gray700}}>R$ {fmt(v.receita)}</td>
                    <td style={{padding:"9px 8px",fontSize:11,color:theme.red}}>R$ {fmt(v.custo)}</td>
                    <td style={{padding:"9px 8px"}}>
                      <span style={{
                        fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:4,
                        background:marg>=40?"#F0FDF4":marg>=20?"#FEFCE8":"#FEF2F2",
                        color:marg>=40?theme.green:marg>=20?theme.amber:theme.red
                      }}>{marg}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        {/* Processos parados */}
        <Card style={{padding:24}}>
          <p className="serif" style={{fontSize:18,fontWeight:600,marginBottom:4}}>Processos Parados</p>
          <p style={{fontSize:11,color:theme.gray400,marginBottom:16}}>Sem avanço há mais de 20 dias</p>
          {paradosMais20.length===0
            ? <div style={{textAlign:"center",padding:"30px 0"}}>
                <span style={{fontSize:32}}>✅</span>
                <p style={{fontSize:13,color:theme.green,marginTop:8,fontWeight:600}}>Nenhum processo parado!</p>
              </div>
            : <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {paradosMais20.map(c=>{
                  const cli = clientes.find(x=>x.id===c.clienteId);
                  const resp = equipe.find(e=>e.id===c.responsavel);
                  const diasP = Math.floor((hoje-new Date(c.ultimoAvanco))/(1000*60*60*24));
                  return (
                    <div key={c.id} style={{padding:"10px 14px",background:"#FEF2F2",borderRadius:8,borderLeft:`3px solid ${theme.red}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <p style={{fontSize:13,fontWeight:600,color:theme.gray900}}>{cli?.nome||c.clienteId}</p>
                          <p style={{fontSize:11,color:theme.gray500,marginTop:2}}>{c.numContrato} · {c.area}</p>
                          {resp && <p style={{fontSize:10,color:theme.gray400,marginTop:1}}>Resp: {resp.nome}</p>}
                        </div>
                        <div style={{textAlign:"right"}}>
                          <p style={{fontSize:20,fontWeight:700,color:theme.red}}>{diasP}d</p>
                          <p style={{fontSize:9,color:theme.red}}>PARADO</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
          }
        </Card>
      </div>

      {/* Linha 4: Custos vs Despesas + Responsáveis */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>

        {/* Resultado financeiro consolidado */}
        <Card style={{padding:24}}>
          <p className="serif" style={{fontSize:18,fontWeight:600,marginBottom:20}}>Resultado Consolidado</p>
          {[
            {l:"Receita Recebida",    v: receitaRecebida,         c:theme.green,  sinal:"+"},
            {l:"(-) Custos Operac.",  v: totalCustos,             c:theme.amber,  sinal:"-"},
            {l:"= Margem Bruta",      v: margemBruta,             c:margemBruta>=0?theme.green:theme.red, bold:true, sinal:"="},
            {l:"(-) Despesas Admin.", v: totalDespesas,           c:theme.blue,   sinal:"-"},
            {l:"= Margem Líquida",    v: margemLiquida,           c:margemLiquida>=0?theme.green:theme.red, bold:true, sinal:"="},
          ].map(r=>(
            <div key={r.l} style={{
              display:"flex",justifyContent:"space-between",alignItems:"center",
              padding:"11px 0",borderBottom:`1px solid ${theme.gray100}`
            }}>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <span style={{fontSize:12,color:theme.gray400,width:16,textAlign:"center"}}>{r.sinal}</span>
                <span style={{fontSize:13,color:theme.gray700,fontWeight:r.bold?700:400}}>{r.l}</span>
              </div>
              <span style={{fontSize:r.bold?16:14,fontWeight:700,color:r.c}}>
                R$ {fmt(r.v)}
              </span>
            </div>
          ))}
          <div style={{marginTop:16,display:"flex",justifyContent:"center"}}>
            <div style={{
              padding:"12px 24px",borderRadius:8,textAlign:"center",
              background:percMargem>=30?`${theme.green}11`:percMargem>=15?`${theme.amber}11`:`${theme.red}11`,
              border:`1px solid ${percMargem>=30?theme.green:percMargem>=15?theme.amber:theme.red}33`
            }}>
              <p style={{fontSize:11,color:theme.gray500}}>Margem Líquida %</p>
              <p style={{fontSize:28,fontWeight:700,color:percMargem>=30?theme.green:percMargem>=15?theme.amber:theme.red}}>{percMargem}%</p>
            </div>
          </div>
        </Card>

        {/* Responsáveis */}
        <Card style={{padding:24}}>
          <p className="serif" style={{fontSize:18,fontWeight:600,marginBottom:20}}>Carga por Responsável</p>
          {Object.keys(contsPorResp).length===0
            ? <p style={{color:theme.gray400,fontSize:13,textAlign:"center",padding:"20px 0"}}>Nenhum contrato ativo</p>
            : Object.entries(contsPorResp).sort((a,b)=>b[1]-a[1]).map(([respId, qtd])=>{
              const resp = equipe.find(e=>e.id===respId);
              const nome = resp?.nome || respId;
              const maxQ = Math.max(...Object.values(contsPorResp));
              return (
                <div key={respId} style={{marginBottom:16}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{
                        width:28,height:28,borderRadius:"50%",
                        background:`linear-gradient(135deg,${theme.gray800},${theme.gray600})`,
                        display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0
                      }}>
                        <span style={{color:theme.gold,fontSize:10,fontWeight:700}}>
                          {nome.split(" ").map(n=>n[0]).slice(0,2).join("")}
                        </span>
                      </div>
                      <span style={{fontSize:13,color:theme.gray700}}>{nome.split(" ").slice(0,2).join(" ")}</span>
                    </div>
                    <span style={{fontSize:14,fontWeight:700,color:theme.blue}}>{qtd} processo{qtd>1?"s":""}</span>
                  </div>
                  <div style={{height:8,background:theme.gray100,borderRadius:4}}>
                    <div style={{height:"100%",width:`${(qtd/maxQ)*100}%`,background:`linear-gradient(90deg,${theme.blue},${theme.gold})`,borderRadius:4}}/>
                  </div>
                </div>
              );
            })
          }
        </Card>
      </div>

      {/* ── BLOCO: Sem Contato ─────────────────────────────── */}
      <div style={{marginTop:20}}>
        <Card style={{padding:24}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
            <div>
              <p className="serif" style={{fontSize:20,fontWeight:600}}>Clientes e Leads sem Contato</p>
              <p style={{fontSize:11,color:theme.gray400,marginTop:2}}>
                Baseado na data da última ocorrência registrada no histórico
              </p>
            </div>
            {/* Seletor de período */}
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <span style={{fontSize:11,color:theme.gray500}}>Sem contato há mais de:</span>
              {[[30,"30 dias"],[60,"60 dias"],[90,"90 dias"],[120,"120 dias"],[365,"1 ano"]].map(([v,l])=>(
                <button key={v} onClick={()=>setFiltroSemContato(v)} style={{
                  padding:"5px 12px",borderRadius:6,fontSize:11,fontWeight:filtroSemContato===v?700:400,
                  border:`1px solid ${filtroSemContato===v?theme.gold:theme.gray200}`,
                  background:filtroSemContato===v?`${theme.gold}18`:theme.white,
                  color:filtroSemContato===v?theme.goldDark:theme.gray500,cursor:"pointer"
                }}>{l}</button>
              ))}
            </div>
          </div>

          {/* KPIs rápidos */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
            {[
              { l:"Clientes sem contato", v:clientesSemContato.length, c:clientesSemContato.length>0?theme.red:theme.green, icon:"👤" },
              { l:"Leads ativos sem contato", v:leadsSemContato.length, c:leadsSemContato.length>0?theme.amber:theme.green, icon:"💡" },
              { l:"Total a abordar", v:semContatoTotal.length, c:semContatoTotal.length>0?theme.red:theme.green, icon:"📞" },
            ].map(k=>(
              <div key={k.l} style={{
                padding:"14px 18px",borderRadius:8,
                background:k.v>0?`${k.c}0A`:`${theme.green}0A`,
                border:`1px solid ${k.v>0?k.c:theme.green}22`
              }}>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
                  <span style={{fontSize:14}}>{k.icon}</span>
                </div>
                <p style={{fontSize:24,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
              </div>
            ))}
          </div>

          {/* Lista */}
          {semContatoTotal.length === 0 ? (
            <div style={{textAlign:"center",padding:"30px 0"}}>
              <span style={{fontSize:36}}>✅</span>
              <p style={{fontSize:14,color:theme.green,marginTop:10,fontWeight:600}}>
                Todos os clientes e leads foram contatados nos últimos {filtroSemContato} dias!
              </p>
            </div>
          ) : (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                    {["Nome","Tipo","Último Contato","Dias sem Contato","Responsável",""].map(h=>(
                      <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:0.8,textTransform:"uppercase"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {semContatoTotal.map((item,i)=>{
                    const resp = item.responsavel ? equipe.find(e=>e.id===item.responsavel) : null;
                    const critico = item.dias > 90;
                    const urgente = item.dias > 60 && item.dias <= 90;
                    const cor = critico ? theme.red : urgente ? theme.amber : theme.gray600;
                    return (
                      <tr key={item.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                        <td style={{padding:"11px 14px"}}>
                          <p style={{fontSize:13,fontWeight:500,color:theme.gray900}}>{item.nome}</p>
                          {item.cidade && <p style={{fontSize:10,color:theme.gray400}}>{item.cidade}/{item.uf}</p>}
                        </td>
                        <td style={{padding:"11px 14px"}}>
                          <Badge color={item.tipo_reg==="Lead"?"amber":item.tipo==="PJ"?"blue":"gray"}>
                            {item.tipo_reg}
                          </Badge>
                        </td>
                        <td style={{padding:"11px 14px",fontSize:12,color:theme.gray600}}>
                          {item.ultimoContato
                            ? item.ultimoContato.toLocaleDateString("pt-BR")
                            : <span style={{color:theme.gray300}}>Sem registro</span>
                          }
                        </td>
                        <td style={{padding:"11px 14px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:16,fontWeight:700,color:cor}}>{item.dias === 9999 ? "—" : item.dias+"d"}</span>
                            {critico && <Badge color="red">Crítico</Badge>}
                            {urgente && <Badge color="amber">Urgente</Badge>}
                          </div>
                        </td>
                        <td style={{padding:"11px 14px",fontSize:12,color:theme.gray600}}>
                          {resp?.nome.split(" ").slice(0,2).join(" ") || "-"}
                        </td>
                        <td style={{padding:"11px 14px"}}>
                          <Badge color={critico?"red":urgente?"amber":"gray"}>
                            {critico?"Contato Urgente":urgente?"Em Atraso":"Monitorar"}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* ── PAINEL DE TAREFAS PROCESSUAIS COM PRAZO ────────────── */}
      {(()=>{
        const hoje0 = new Date(); hoje0.setHours(0,0,0,0);
        const todasOcsComPrazo = (data.baseOperacional||[]).flatMap(reg=>
          (reg.ocorrencias||[])
            .filter(oc=>oc.prazoVencimento)
            .map(oc=>{
              const venc = new Date(oc.prazoVencimento+"T12:00:00");
              const diff = Math.round((venc-hoje0)/(1000*60*60*24));
              const urgencia = diff<=0?"vencido":diff<=3?"critico":diff<=7?"atencao":"monitorando";
              return {...oc, _reg:reg, _diff:diff, _urgencia:urgencia};
            })
        ).sort((a,b)=>a._diff-b._diff);

        const urgs = [
          {key:"vencido",   label:"🔴 Vencidos",     bg:"#FEF2F2", bord:"#FECACA", cor:"#991b1b"},
          {key:"critico",   label:"🔴 Urgente (≤3du)",bg:"#FEF2F2", bord:"#FECACA", cor:"#991b1b"},
          {key:"atencao",   label:"🟡 Atenção (≤7du)",bg:"#FEFCE8", bord:"#FDE68A", cor:"#92400e"},
          {key:"monitorando",label:"🟢 Monitorando",  bg:"#F0FDF4", bord:"#BBF7D0", cor:"#166534"},
        ];

        if(todasOcsComPrazo.length===0) return null;

        return (
          <div style={{marginTop:24}}>
            <p style={{fontSize:10,color:theme.gray400,letterSpacing:1.2,textTransform:"uppercase",marginBottom:10}}>Tarefas Processuais — Prazos Ativos</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
              {urgs.map(u=>{
                const total = todasOcsComPrazo.filter(o=>o._urgencia===u.key).length;
                return (
                  <Card key={u.key} style={{padding:"12px 16px",background:u.bg,border:`1px solid ${u.bord}`}}>
                    <p style={{fontSize:10,color:u.cor,fontWeight:700,textTransform:"uppercase",letterSpacing:.6}}>{u.label}</p>
                    <p style={{fontSize:28,fontWeight:700,color:u.cor,marginTop:4}}>{total}</p>
                  </Card>
                );
              })}
            </div>
            <Card style={{padding:"18px 20px"}}>
              <p className="serif" style={{fontSize:15,fontWeight:700,color:theme.gray900,marginBottom:14}}>📋 Ocorrências com prazo — ordenadas por urgência</p>
              <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:320,overflowY:"auto"}}>
                {todasOcsComPrazo.map((oc,i)=>{
                  const u = urgs.find(x=>x.key===oc._urgencia)||urgs[3];
                  return (
                    <div key={oc.id||i} style={{display:"flex",alignItems:"flex-start",gap:12,padding:"10px 14px",borderRadius:8,border:`1px solid ${u.bord}`,background:u.bg}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                          <span style={{fontSize:12,fontWeight:700,color:u.cor}}>{u.label.split(" ")[0]} {oc._diff<=0?"VENCIDO":oc._diff+"du"}</span>
                          <span style={{fontSize:11,color:theme.gray600,fontWeight:600}}>{oc._reg.numContrato||"—"}</span>
                          {oc.origemCaptura&&oc.origemCaptura!=="Manual"&&(
                            <span style={{fontSize:10,padding:"1px 6px",borderRadius:10,fontWeight:700,
                              background:oc.origemCaptura==="Legal Mail"?"#dbeafe":"#fef3c7",
                              color:oc.origemCaptura==="Legal Mail"?"#1e40af":"#92400e"}}>
                              {oc.origemCaptura==="Legal Mail"?"📨 Legal Mail":"⚖️ "+oc.origemCaptura}
                            </span>
                          )}
                        </div>
                        <p style={{fontSize:12,color:theme.gray700,lineHeight:1.4}}>{oc.obs||oc.descricao||"—"}</p>
                        <p style={{fontSize:11,color:theme.gray400,marginTop:3}}>
                          Vence: {new Date(oc.prazoVencimento+"T12:00:00").toLocaleDateString("pt-BR")}
                          {oc.legalMailUri&&<> · <a href={oc.legalMailUri} target="_blank" rel="noreferrer" style={{color:theme.blue}}>📨 Abrir no Legal Mail</a></>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        );
      })()}

    </div>
  );
}


// ============================================================
// MÓDULO: CRM COMERCIAL
// ============================================================
const ESTAGIOS = [
  { id: "recepcao",           label: "Recepção",                    cor: "#888780", icon: "📥" },
  { id: "qualificacao",       label: "Qualificação",                cor: "#378ADD", icon: "🔍" },
  { id: "reuniao",            label: "Reunião",                     cor: "#EF9F27", icon: "📅" },
  { id: "proposta",           label: "Proposta",                    cor: "#7c3aed", icon: "📄" },
  { id: "negociacao",         label: "Negociação",                  cor: "#BA7517", icon: "🤝" },
  { id: "envio_contrato",     label: "Envio de Contrato",           cor: "#d97706", icon: "📨" },
  { id: "contrato_pendente",  label: "Pend. Assinatura",            cor: "#f97316", icon: "⏳" },
  { id: "contrato_assinado",  label: "Contrato Assinado",           cor: "#3B6D11", icon: "✅" },
  { id: "perdido",            label: "Perdido / Descartado",        cor: "#E24B4A", icon: "❌" },
  { id: "nao_qualificado",    label: "Não Qualificado",             cor: "#9CA3AF", icon: "🚫" },
];

// ============================================================
// COMPONENTE: ProdutoSelector — seleção com busca e filtro por área
// ============================================================
function ProdutoSelector({ produtos, value, onChange }) {
  const [busca, setBusca] = React.useState("");
  const [filtroArea, setFiltroArea] = React.useState("Todas");
  const selecionados = value || [];
  const ativos = (produtos||[]).filter(p => p.status === "Ativo");
  const areas = ["Todas", ...Array.from(new Set(ativos.map(p=>p.area).filter(Boolean))).sort((a,b)=>a.localeCompare(b,"pt-BR"))];
  const bL = busca.toLowerCase();
  const filtrados = ativos.filter(p => {
    if(filtroArea !== "Todas" && p.area !== filtroArea) return false;
    if(!bL) return true;
    if((p.nome||"").toLowerCase().includes(bL)) return true;
    if((p.area||"").toLowerCase().includes(bL)) return true;
    if((p.trib_tributos||[]).some(t=>(t||"").toLowerCase().includes(bL))) return true;
    if((p.resumo||"").toLowerCase().includes(bL)) return true;
    return false;
  }).sort((a,b)=>(a.area||"").localeCompare(b.area||"","pt-BR")||a.nome.localeCompare(b.nome,"pt-BR"));
  const grupos = {};
  filtrados.forEach(p=>{const ar=p.area||"Outros"; if(!grupos[ar]) grupos[ar]=[]; grupos[ar].push(p);});
  const toggle = id => onChange(selecionados.includes(id)?selecionados.filter(x=>x!==id):[...selecionados,id]);
  const nomeProd = id => ((produtos||[]).find(p=>p.id===id)||{}).nome || id;
  return (
    <div>
      <div style={{display:"flex",gap:8,marginBottom:8}}>
        <div style={{flex:1,position:"relative"}}>
          <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",color:theme.gray400,fontSize:13,pointerEvents:"none"}}>🔍</span>
          <input value={busca} onChange={e=>setBusca(e.target.value)}
            placeholder="Buscar produto, área ou tributo..."
            style={{width:"100%",padding:"7px 10px 7px 30px",borderRadius:7,border:"1px solid "+theme.gray200,fontSize:12,background:theme.white,color:theme.gray900,boxSizing:"border-box",outline:"none"}}/>
          {busca&&<button onClick={()=>setBusca("")} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:theme.gray400,fontSize:14,padding:0}}>×</button>}
        </div>
        <select value={filtroArea} onChange={e=>setFiltroArea(e.target.value)}
          style={{padding:"7px 10px",borderRadius:7,border:"1px solid "+theme.gray200,fontSize:12,background:theme.white,color:theme.gray700,cursor:"pointer",outline:"none"}}>
          {areas.map(a=><option key={a} value={a}>{a==="Todas"?"🗂 Todas":a}</option>)}
        </select>
      </div>
      {selecionados.length>0&&(
        <div style={{marginBottom:8,padding:"7px 10px",borderRadius:7,background:theme.gold+"11",border:"1px solid "+theme.gold+"44"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
            <p style={{fontSize:10,fontWeight:700,color:theme.gold,textTransform:"uppercase",letterSpacing:.8,margin:0}}>✅ {selecionados.length} selecionado{selecionados.length!==1?"s":""}</p>
            <button onClick={()=>onChange([])} style={{fontSize:10,color:theme.red,background:"none",border:"none",cursor:"pointer",padding:0}}>Limpar tudo</button>
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
            {selecionados.map(id=>(
              <span key={id} style={{fontSize:11,padding:"2px 7px",borderRadius:8,background:theme.gold,color:"#fff",fontWeight:600,display:"flex",alignItems:"center",gap:3}}>
                {nomeProd(id)}
                <button onClick={()=>toggle(id)} style={{background:"none",border:"none",cursor:"pointer",color:"#fff",fontSize:12,padding:0,lineHeight:1}}>×</button>
              </span>
            ))}
          </div>
        </div>
      )}
      <div style={{maxHeight:220,overflowY:"auto",borderRadius:7,border:"1px solid "+theme.gray200}}>
        {Object.keys(grupos).length===0&&(
          <p style={{padding:"14px",textAlign:"center",fontSize:12,color:theme.gray400}}>Nenhum produto encontrado{busca?' para "'+busca+'"':""}</p>
        )}
        {Object.entries(grupos).map(([area,prods])=>(
          <div key={area}>
            <div style={{padding:"5px 12px",background:theme.gray100,borderBottom:"1px solid "+theme.gray200,display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,zIndex:1}}>
              <p style={{fontSize:10,fontWeight:700,color:theme.gray500,textTransform:"uppercase",letterSpacing:.8,margin:0}}>{area}</p>
              <span style={{fontSize:10,color:theme.gray400}}>{prods.filter(p=>selecionados.includes(p.id)).length}/{prods.length}</span>
            </div>
            {prods.map(p=>{
              const sel=selecionados.includes(p.id);
              return(
                <label key={p.id} onClick={()=>toggle(p.id)}
                  style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",cursor:"pointer",background:sel?theme.gold+"08":theme.white,borderBottom:"1px solid "+theme.gray100,transition:"background .1s"}}>
                  <div style={{width:14,height:14,borderRadius:3,flexShrink:0,border:"2px solid "+(sel?theme.gold:theme.gray300),background:sel?theme.gold:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {sel&&<span style={{color:"white",fontSize:9,fontWeight:700}}>✓</span>}
                  </div>
                  <span style={{fontSize:12,color:sel?theme.gray900:theme.gray700,fontWeight:sel?600:400,flex:1}}>{p.nome}</span>
                  {(p.trib_tributos||[]).length>0&&(
                    <span style={{fontSize:9,color:theme.gray400}}>{(p.trib_tributos||[]).slice(0,2).join(" · ")}</span>
                  )}
                </label>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}


const ESTAGIOS_EXPANSAO_IDS = ["nova_oportunidade","reuniao","proposta","negociacao","envio_contrato","contrato_pendente","contrato_assinado","contrato_cancelado"];

function ModCRM({ data, setData }) {
  const isViz = useIsVisualizador();
  const { leads, clientes, produtos, equipe, parcerias, contratos = [], tiposOcorrencia = [] } = data;
  const [visao, setVisao] = useState("painel");
  const [modalNovoContato, setModalNovoContato] = useState(false);
  const [buscaNovoContato, setBuscaNovoContato] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [histAtlas, setHistAtlas] = useState(null);
  const [loadingHistAtlas, setLoadingHistAtlas] = useState(false);
  const [modalConv, setModalConv] = useState(false);
  const [leadConv, setLeadConv] = useState(null);
  const [filtroEstagio, setFiltroEstagio] = useState("nao_convertido");
  const [filtroResp, setFiltroResp] = useState("");
  const [busca, setBusca] = useState("");
  const [modalOcorr, setModalOcorr] = useState(false);
  const [novaOcorr, setNovaOcorr] = useState({ data: new Date().toISOString().slice(0,10), tipo: "Contato", descricao: "" });

  const hoje = new Date();

  useEffect(() => {
    if (!modalOpen || !editItem?.atendimentoId) { setHistAtlas(null); return; }
    let cancelado = false;
    setLoadingHistAtlas(true);
    const token = sessionStorage.getItem('ac_token') || '';
    fetch(`${API_BASE}/atendimentos/index.php/${editItem.atendimentoId}`, { headers: { 'X-Token': token } })
      .then(r => r.json())
      .then(json => { if (!cancelado && json.ok) setHistAtlas(json.dados); })
      .catch(() => {})
      .finally(() => { if (!cancelado) setLoadingHistAtlas(false); });
    return () => { cancelado = true; };
  }, [modalOpen, editItem?.atendimentoId]);

  const EMPTY = {
    id: "", nome: "", tipo: "PF", email: "", whatsapp: "", whatsappDDI: "55", cidade: "", uf: "SP",
    cpfCnpj: "", origem: "", parceiraId: "", responsavel: "",
    estagio: "recepcao", produtosInteresse: [],
    valorEstimado: "", probabilidade: 50,
    dataEntrada: new Date().toISOString().slice(0,10),
    prazoFechamento: "", obs: "", historico: [],
    historicoEstagios: [],
    dataEnvioProposta: "", dataAprovacaoProposta: "",
    dataEnvioContrato: "", dataContratoAssinado: "",
    tipoCRM: "lead",
    clienteVinculadoId: "",
    // Fase 1 — Comercial/Marketing
    atendimentoId: "",        // vínculo com atendimento (WhatsApp/Instagram)
    origemCanal: "",          // canal de origem detalhado (ex: "Instagram DM", "WhatsApp", "Site")
    discPerfil: "",           // perfil DISC do lead (D/I/S/C)
    scoreBant: 0,             // score BANT calculado (0–4)
    bantJson: { budget: false, authority: false, need: false, timeline: false }
  };

  const openNew = () => { setEditItem({ ...EMPTY }); setModalOpen(true); };
  const openEdit = l => { setEditItem({ ...l, whatsappDDI: l.whatsappDDI || "55" }); setModalOpen(true); };

  const save = () => {
    if (!editItem.nome) return alert("Nome é obrigatório.");
    const cpfCnpjLimpo = (editItem.cpfCnpj || "").replace(/\D/g, "");
    if (cpfCnpjLimpo) {
      const leadDup = leads.find(l => l.id !== editItem.id && (l.cpfCnpj || "").replace(/\D/g, "") === cpfCnpjLimpo);
      const clienteDup = clientes.find(c => (c.cpfCnpj || "").replace(/\D/g, "") === cpfCnpjLimpo);
      const duplicado = leadDup || clienteDup;
      if (duplicado) {
        const tipoDup = leadDup ? "lead" : "cliente";
        const abrir = confirm(`Já existe um(a) ${tipoDup} cadastrado(a) com este CPF/CNPJ: "${duplicado.nome}" (${duplicado.id}).\n\nClique OK para abrir o cadastro existente${tipoDup==="cliente"?" (aba Clientes)":""}, ou Cancelar para revisar os dados informados.`);
        if (abrir && leadDup) { openEdit(leadDup); }
        else if (abrir && clienteDup) { alert(`Cliente "${clienteDup.nome}" (${clienteDup.id}) já cadastrado — abra o módulo Clientes para editá-lo.`); }
        return;
      }
    }
    const id = editItem.id || "LEAD" + String(Date.now()).slice(-6);
    const leadAnterior = leads.find(l=>l.id===id);
    let item = { ...editItem, id };
    const estagioMudou = !leadAnterior || leadAnterior.estagio !== item.estagio;
    if(estagioMudou) {
      const est = ESTAGIOS.find(e=>e.id===item.estagio);
      item = {...item, historicoEstagios: [...(item.historicoEstagios||[]), {
        id: Date.now(), estagio: item.estagio,
        label: est?.label||item.estagio,
        data: new Date().toISOString().slice(0,10),
        responsavel: item.responsavel||""
      }]};
    }
    const hj = new Date().toISOString().slice(0,10);
    if(item.estagio==="proposta"           && !item.dataEnvioProposta)      item={...item,dataEnvioProposta:hj};
    if(item.estagio==="negociacao"         && !item.dataAprovacaoProposta)  item={...item,dataAprovacaoProposta:hj};
    if(item.estagio==="envio_contrato"     && !item.dataEnvioContrato)      item={...item,dataEnvioContrato:hj};
    if(item.estagio==="contrato_pendente"  && !item.dataEnvioContrato)      item={...item,dataEnvioContrato:hj};
    if(item.estagio==="contrato_assinado"  && !item.dataContratoAssinado)   item={...item,dataContratoAssinado:hj};
    setData(prev=>{const lista=editItem.id?prev.leads.map(l=>l.id===id?item:l):[...prev.leads,item];return {...prev,leads:lista};});
    setModalOpen(false);
    if(item.estagio==="contrato_assinado"&&leadAnterior?.estagio!=="contrato_assinado"){
      if(window.confirm("Contrato assinado! Deseja converter este lead em Cliente agora?")){
        setLeadConv(item); setModalConv(true);
      }
    }
  };

  const remove = id => {
    if (!confirm("Remover lead?")) return;
    setData(prev => ({...prev, leads: leads.filter(l => l.id !== id)}));
  };

  const avancarEstagio = (id, novoEstagio) => {
    setData(prev => { const lista = prev.leads.map(l => l.id === id ? { ...l, estagio: novoEstagio } : l); return {...prev, leads: lista}; });
  };

  const addOcorrencia = () => {
    if (!novaOcorr.descricao) return alert("Preencha a descrição.");
    setData(prev => { const lista = prev.leads.map(l => l.id === viewItem.id ? { ...l, historico: [...(l.historico||[]), { ...novaOcorr, id: Date.now() }] } : l); return {...prev, leads: lista}; });
    setViewItem(lista.find(l => l.id === viewItem.id));
    setModalOcorr(false);
    setNovaOcorr({ data: new Date().toISOString().slice(0,10), tipo: "Contato", descricao: "" });
  };

  // Converter lead em cliente
  const converterCliente = () => {
    if (!leadConv) return;
    const novoCliente = {
      id: "CLI" + String(Date.now()).slice(-6),
      nome: leadConv.nome, tipo: leadConv.tipo,
      email: leadConv.email, whatsapp: leadConv.whatsapp,
      cidade: leadConv.cidade, uf: leadConv.uf,
      cpfCnpj: leadConv.cpfCnpj, endereco: "", parceiraId: leadConv.parceiraId||"",
      cep: "", produtosContratados: leadConv.produtosInteresse || [],
      mesAnoContrato: new Date().toISOString().slice(0,7),
      ativo: true, obs: "Convertido do CRM em " + new Date().toLocaleDateString("pt-BR"),
      leadOrigemId: leadConv.id, // ← vinculo para detectar conversão já feita
      historico: [{
        id: Date.now(), data: new Date().toISOString().slice(0,10),
        ocorrencia: "Reunião", descricao: "Lead convertido do CRM. Origem: " + (leadConv.origem || "Direto")
      }],
      contatosPJ: []
    };
    const leadsAtualizados = leads.map(l => l.id === leadConv.id ? {
      ...l,
      estagio: "contrato_assinado",   // estagio correto pós-conversão
      tipoCRM: "expansao",             // ← muda para expansão
      convertidoEm: new Date().toISOString().slice(0,10),
      clienteId: novoCliente.id
    } : l);
    setData(prev => ({...prev, clientes: [...clientes, novoCliente], leads: leadsAtualizados}));
    setModalConv(false);
    setLeadConv(null);
    alert("Lead convertido em cliente com sucesso! Acesse o módulo Clientes para finalizar o cadastro e registrar o contrato.");
  };

  // Indicadores
  const leadsAtivos   = leads.filter(l => !["contrato_assinado","contrato_cancelado","nao_qualificado","fechado","perdido"].includes(l.estagio));
  const fechados      = leads.filter(l => ["contrato_assinado","fechado"].includes(l.estagio));
  const perdidos      = leads.filter(l => ["contrato_cancelado","nao_qualificado","perdido"].includes(l.estagio));
  const emProposta    = leads.filter(l => ["proposta","negociacao","envio_contrato","contrato_pendente"].includes(l.estagio));
  const contPendente  = leads.filter(l => l.estagio==="contrato_pendente");
  // Expansão = clientes convertidos ainda no CRM (tipoCRM expansao)
  const expansao      = leads.filter(l => l.tipoCRM==="expansao");
  const txConversao   = leads.length > 0 ? Math.round((fechados.length / leads.length) * 100) : 0;
  const ticketMedio = fechados.length > 0
    ? fechados.reduce((s,l) => s + (parseFloat(l.valorEstimado)||0), 0) / fechados.length : 0;
  const receitaPipeline = leadsAtivos.reduce((s,l) => s + ((parseFloat(l.valorEstimado)||0) * (l.probabilidade||0) / 100), 0);
  const diasMedioFechamento = fechados.length > 0
    ? Math.round(fechados.filter(l=>l.prazoFechamento&&l.dataEntrada).reduce((s,l)=>{
        return s + Math.floor((new Date(l.prazoFechamento)-new Date(l.dataEntrada))/(1000*60*60*24));
      }, 0) / fechados.filter(l=>l.prazoFechamento&&l.dataEntrada).length) : 0;

  const isConvertido = l => {
    const nomeLead = (l.nome||l.nomeCliente||"").trim().toLowerCase();
    return !!l.clienteId || !!l.clienteVinculadoId || l.tipoCRM==="expansao" ||
      !!clientes.find(cl=>cl.leadOrigemId===l.id) ||
      (l.cpfCnpj && !!clientes.find(cl=>cl.cpfCnpj===l.cpfCnpj)) ||
      (nomeLead.length>3 && !!clientes.find(cl=>(cl.nome||"").trim().toLowerCase()===nomeLead));
  };

  const leadsFiltrados = leads.filter(l => {
    const mB = !busca || (l.nome||l.nomeCliente||"").toLowerCase().includes(busca.toLowerCase());
    const mR = !filtroResp || l.responsavel === filtroResp;
    let mE = true;
    if (filtroEstagio === "nao_convertido")
      // Aba Leads: apenas não convertidos, exceto perdidos e contrato_assinado
      mE = !isConvertido(l) && !["perdido","nao_qualificado","contrato_cancelado","contrato_assinado"].includes(l.estagio);
    else if (filtroEstagio === "lista_funil")
      // Lista Funil: todos (leads + clientes), exceto perdidos
      mE = !["perdido","nao_qualificado","contrato_cancelado"].includes(l.estagio);
    else if (filtroEstagio === "ativo")
      mE = !["perdido","nao_qualificado","contrato_cancelado","contrato_assinado","fechado"].includes(l.estagio);
    else if (filtroEstagio === "fechado")
      mE = ["contrato_assinado","fechado"].includes(l.estagio);
    else if (filtroEstagio === "perdido")
      mE = ["perdido","nao_qualificado","contrato_cancelado"].includes(l.estagio);
    else if (filtroEstagio)
      mE = l.estagio === filtroEstagio;
    return mB && mE && mR;
  }).sort((a,b)=>(a.nome||a.nomeCliente||'').localeCompare(b.nome||b.nomeCliente||'','pt-BR'));

  const estagioDados = e => ESTAGIOS.find(x => x.id === e) || ESTAGIOS[0];

  const ORIGENS = ["Indicação","Parceria","Site","Instagram","LinkedIn","Google","Evento","Outros"];
  const TIPOS_OCORR = ["Contato","Reunião","E-mail","WhatsApp","Ligação","Proposta Enviada","Follow-up","Outros"];

  return (
    <div className="fade-in">
      <PageHeader
        title="CRM Comercial"
        subtitle="Funil de vendas e gestão de leads"
        action={
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <PainelImportExport
              modulo="Leads / Lides"
              totalRegistros={(data.leads||[]).length}
              onExportCSV={()=>exportarCSV("leads",
                ["ID","Nome","Tipo","Email","WhatsApp","Cidade","UF","Origem","Estágio","Valor Estimado","Probabilidade","Responsável","Data Entrada","Observações"],
                (data.leads||[]).map(l=>{
                  const resp = (data.equipe||[]).find(e=>e.id===l.responsavel);
                  return [l.id||"—",l.nomeCliente||"—",l.tipo||"PF",l.email||"—",l.whatsapp||"—",l.cidade||"—",l.uf||"—",l.origem||"—",l.estagio||"—",l.valorEstimado||"—",l.probabilidade||"—",resp?.nome||"—",l.dataEntrada||"—",l.obs||"—"];
                })
              )}
              onExportJSON={()=>exportarJSON("leads_backup", data.leads||[])}
              onImportCSV={(arquivo, onOk, onErr)=>{
                importarCSV(arquivo, (registros)=>{
                  const leads = data.leads||[];
                  const novos = registros.map(r=>({
                    id: r.ID||r.id||("LEAD"+String(Date.now()).slice(-5)+Math.random().toString(36).slice(-2)),
                    nomeCliente: r.Nome||r.nomeCliente||"Sem nome",
                    tipo: r.Tipo||r.tipo||"PF",
                    email: r.Email||r.email||"",
                    whatsapp: r.WhatsApp||r.whatsapp||"",
                    cidade: r.Cidade||r.cidade||"",
                    uf: r.UF||r.uf||"SP",
                    origem: r["Origem"]||r.origem||"",
                    estagio: r["Estágio"]||r.estagio||"lead",
                    valorEstimado: r["Valor Estimado"]||r.valorEstimado||"",
                    probabilidade: r.Probabilidade||r.probabilidade||"50",
                    dataEntrada: r["Data Entrada"]||r.dataEntrada||new Date().toISOString().slice(0,10),
                    obs: r["Observações"]||r.obs||"",
                    historico:[]
                  }));
                  const ids = new Set(leads.map(x=>x.id));
                  const inserir = novos.filter(n=>!ids.has(n.id));
                  setData(prev => ({...prev, leads: [...leads,...inserir]}));
                  onOk(inserir.length);
                }, onErr);
              }}
              onImportJSON={(arquivo, onOk, onErr)=>{
                importarJSON(arquivo, (lista)=>{
                  const leads = data.leads||[];
                  const ids = new Set(leads.map(x=>x.id));
                  const inserir = lista.filter(n=>(n.nomeCliente||n.nome)&&!ids.has(n.id));
                  setData(prev => ({...prev, leads: [...leads,...inserir]}));
                  onOk(inserir.length);
                }, onErr);
              }}
            />
            {!isViz && <Btn onClick={()=>{setBuscaNovoContato(""); setModalNovoContato(true);}}>+ Novo Contato</Btn>}
          </div>
        }
      />

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:10,marginBottom:24}}>
        {[
          { l:"Leads Ativos",      v: leadsAtivos.length,                     c:theme.blue,  icon:"💡", fmt:"n" },
          { l:"Fechados",          v: fechados.length,                         c:theme.green, icon:"✅", fmt:"n" },
          { l:"Taxa Conversão",    v: txConversao,                             c:txConversao>=30?theme.green:theme.amber, icon:"📈", fmt:"p" },
          { l:"Pipeline (ponder.)",v: receitaPipeline,                         c:theme.gold,  icon:"💰", fmt:"r" },
          { l:"Ticket Médio",      v: ticketMedio,                             c:theme.gray800,icon:"🎯", fmt:"r" },
        ].map(k=>(
          <Card key={k.l} style={{padding:"16px 18px"}}>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <p style={{fontSize:10,color:theme.gray400,letterSpacing:0.8,textTransform:"uppercase",lineHeight:1.4}}>{k.l}</p>
              <span style={{fontSize:15}}>{k.icon}</span>
            </div>
            <p style={{fontSize:20,fontWeight:700,color:k.c,marginTop:8}}>
              {k.fmt==="n" && k.v}
              {k.fmt==="p" && k.v + "%"}
              {k.fmt==="r" && <><span style={{fontSize:10,fontWeight:400}}>R$ </span>{k.v.toLocaleString("pt-BR",{minimumFractionDigits:2})}</>}
            </p>
          </Card>
        ))}
      </div>

      {/* Abas visão */}
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:`2px solid ${theme.gray200}`}}>
        {[["painel","🏠 Painel"],["leads","🎯 Leads"],["lista_funil","📋 Lista Funil"],["negociacoes","📊 Negociações"],["clientes_crm","👥 Clientes CRM"],["historico","📁 Histórico"]].map(([v,l])=>(
          <button key={v} onClick={()=>{
            setVisao(v);
            if(v==="leads") setFiltroEstagio("nao_convertido");
            if(v==="lista_funil") setFiltroEstagio("lista_funil");
          }} style={{
            padding:"9px 18px",border:"none",background:"none",fontSize:12,cursor:"pointer",
            fontWeight:visao===v?700:400,color:visao===v?theme.gray900:theme.gray400,
            borderBottom:visao===v?`2px solid ${theme.gold}`:"2px solid transparent",
            marginBottom:-2,transition:"all 0.15s"
          }}>{l}</button>
        ))}
      </div>

      {/* Contagem total */}
      <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"center"}}>
        <span style={{fontSize:12,color:theme.gray500}}>
          {leads.length} registro{leads.length!==1?"s":""} no CRM
        </span>
        <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,
          background:"#dbeafe",color:theme.blue,fontWeight:600}}>
          🔵 {leads.filter(l=>l.tipoCRM!=="expansao").length} leads
        </span>
        <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,
          background:"#dcfce7",color:theme.green,fontWeight:600}}>
          🟢 {leads.filter(l=>l.tipoCRM==="expansao").length} expansão
        </span>
      </div>

      {/* ── FUNIL VISUAL ── */}
      {visao==="negociacoes" && (
        <div style={{overflowX:"auto",paddingBottom:8}}>
          <div style={{display:"flex",gap:14,minWidth:980}}>
            {ESTAGIOS
              // Funil: todos exceto perdido. contrato_assinado fica até ter contrato na Base
              .filter(est => est.id !== "perdido")
              .map(est=>{
              const cards=leads.filter(l=>{
                // Perdidos/descartados — fora do funil
                if (["perdido","nao_qualificado","contrato_cancelado"].includes(l.estagio)) return false;

                // Contrato assinado — sai do funil apenas quando contrato cadastrado na Base Operacional
                if (l.estagio==="contrato_assinado" || (l.tipoCRM==="expansao" && l.estagio==="fechado")) {
                  const cliId = l.clienteId || clientes.find(cl=>cl.leadOrigemId===l.id)?.id;
                  const temContrato = cliId && contratos.some(ct=>ct.clienteId===cliId);
                  if (temContrato) return false; // saiu do funil — vai para lista
                  return est.id === "contrato_assinado"; // fica nesta coluna
                }

                if (l.estagio===est.id) return true;
                // Retrocompat
                if (est.id==="recepcao" && ["lead","nova_oportunidade","documentacao","pre_analise"].includes(l.estagio)) return true;
                return false;
              });
              const valorTotal = cards.reduce((s,l)=>s+(parseFloat(l.valorEstimado)||0),0);
              return (
                <div key={est.id} style={{flex:1,minWidth:150}}>
                  {/* Cabeçalho coluna */}
                  <div style={{
                    padding:"10px 12px",borderRadius:"8px 8px 0 0",marginBottom:1,
                    background:`${est.cor}18`,borderTop:`3px solid ${est.cor}`
                  }}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span style={{fontSize:12,fontWeight:700,color:est.cor}}>{est.icon} {est.label}</span>
                      <span style={{
                        fontSize:10,background:est.cor,color:"white",
                        borderRadius:"50%",width:18,height:18,
                        display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700
                      }}>{cards.length}</span>
                    </div>
                    {valorTotal>0 && (
                      <p style={{fontSize:9,color:est.cor,marginTop:4,fontWeight:600}}>
                        R$ {valorTotal.toLocaleString("pt-BR",{minimumFractionDigits:2})}
                      </p>
                    )}
                  </div>
                  {/* Cards do estágio */}
                  <div style={{
                    minHeight:300,background:theme.gray50,borderRadius:"0 0 8px 8px",
                    padding:8,display:"flex",flexDirection:"column",gap:8,
                    border:`1px solid ${theme.gray200}`,borderTop:"none"
                  }}>
                    {cards.length===0 && (
                      <p style={{fontSize:11,color:theme.gray300,textAlign:"center",padding:"20px 0"}}>Vazio</p>
                    )}
                    {cards.map(l=>{
                      const prod = produtos.find(p=>p.id===l.produtosInteresse?.[0]);
                      const resp = equipe.find(e=>e.id===l.responsavel);
                      return (
                        <div key={l.id} style={{
                          background:theme.white,borderRadius:8,padding:"10px 12px",
                          border:`1px solid ${theme.gray200}`,cursor:"pointer",
                          boxShadow:"0 1px 3px rgba(0,0,0,0.06)",
                          transition:"all 0.15s"
                        }}
                          onClick={()=>setViewItem(l)}
                          onMouseEnter={e=>e.currentTarget.style.boxShadow="0 3px 8px rgba(0,0,0,0.12)"}
                          onMouseLeave={e=>e.currentTarget.style.boxShadow="0 1px 3px rgba(0,0,0,0.06)"}
                        >
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                            <p style={{fontSize:13,fontWeight:600,color:theme.gray900,margin:0}}>{l.nome||l.nomeCliente||"—"}</p>
                            <span style={{fontSize:9,padding:"1px 6px",borderRadius:8,fontWeight:700,
                              background:l.tipoCRM==="expansao"?"#dcfce7":"#dbeafe",
                              color:l.tipoCRM==="expansao"?theme.green:theme.blue}}>
                              {l.tipoCRM==="expansao"?"expansão":"lead"}
                            </span>
                          </div>
                          {prod && <p style={{fontSize:10,color:theme.blue,marginBottom:4}}>{prod.nome}</p>}
                          {/* Dias no estágio atual */}
                          {(()=>{
                            const hist = l.historicoEstagios||[];
                            const ultEst = hist.length>0?hist[hist.length-1]:null;
                            if(!ultEst) return null;
                            const dias = Math.ceil((new Date()-new Date(ultEst.data+"T12:00"))/(1000*60*60*24));
                            return <p style={{fontSize:9,color:theme.gray400,marginBottom:4}}>⏱ {dias} dia{dias!==1?"s":""} nesta fase</p>;
                          })()}
                          {/* Badge datas comerciais pendentes */}
                          {(["proposta","negociacao","envio_contrato","contrato_assinado"].includes(l.estagio))&&(
                            <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:4}}>
                              {l.estagio==="proposta"&&!l.dataEnvioProposta&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#fef3c7",color:"#92400e"}}>📄 Envio pendente</span>}
                              {["negociacao","envio_contrato","contrato_assinado"].includes(l.estagio)&&!l.dataAprovacaoProposta&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#fef3c7",color:"#92400e"}}>✅ Aprovação pendente</span>}
                              {["envio_contrato","contrato_assinado"].includes(l.estagio)&&!l.dataEnvioContrato&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#dbeafe",color:"#1e40af"}}>📨 Contrato pendente</span>}
                              {l.dataContratoAssinado&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:"#dcfce7",color:"#166534"}}>✅ Assinado</span>}
                            </div>
                          )}
                          {l.valorEstimado && (
                            <p style={{fontSize:11,fontWeight:700,color:theme.gold}}>
                              R$ {parseFloat(l.valorEstimado).toLocaleString("pt-BR",{minimumFractionDigits:2})}
                            </p>
                          )}
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6}}>
                            {resp && <span style={{fontSize:9,color:theme.gray400}}>{resp.nome.split(" ")[0]}</span>}
                            <span style={{fontSize:9,color:theme.gray400}}>{l.dataEntrada}</span>
                          </div>
                          {/* Mover estágio */}
                          <div style={{display:"flex",gap:4,marginTop:8}} onClick={e=>e.stopPropagation()}>
                            {(() => {
                              // Funil: todas as colunas exceto perdido
                              const FUNIL_ATIVO = ESTAGIOS.filter(e=>e.id!=="perdido");
                              const idx  = FUNIL_ATIVO.findIndex(e=>e.id===l.estagio);
                              const prev = idx > 0 ? FUNIL_ATIVO[idx-1] : null;
                              const next = idx >= 0 && idx < FUNIL_ATIVO.length-1 ? FUNIL_ATIVO[idx+1] : null;
                              const isUltima = l.estagio === "contrato_assinado";
                              return (<>
                                {prev && (
                                  <button onClick={()=>avancarEstagio(l.id, prev.id)} style={{
                                    flex:1,fontSize:9,padding:"3px 0",borderRadius:4,cursor:"pointer",
                                    border:`1px solid ${theme.gray200}`,background:theme.gray50,color:theme.gray500
                                  }}>◀</button>
                                )}
                                {!isUltima && next && (
                                  <button onClick={()=>avancarEstagio(l.id, next.id)} style={{
                                    flex:2,fontSize:9,padding:"3px 0",borderRadius:4,cursor:"pointer",
                                    border:`1px solid ${next.cor}`,background:`${next.cor}11`,color:next.cor,fontWeight:600
                                  }}>{next.label} ▶</button>
                                )}
                                {isUltima && l.tipoCRM!=="expansao" && !l.clienteId && (
                                  // Lead sem conversão — botão converter
                                  <button onClick={()=>{setLeadConv(l);setModalConv(true);}} style={{
                                    flex:2,fontSize:9,padding:"3px 0",borderRadius:4,cursor:"pointer",
                                    border:`1px solid ${theme.green}`,background:`${theme.green}11`,
                                    color:theme.green,fontWeight:600
                                  }}>✅ Converter</button>
                                )}
                                {isUltima && (l.tipoCRM==="expansao" || l.clienteId) && (
                                  // Cliente/expansão — orientar a cadastrar na Base
                                  <span style={{flex:2,fontSize:9,padding:"3px 4px",borderRadius:4,
                                    background:"#f0fdf4",color:theme.green,fontWeight:600,
                                    textAlign:"center",border:`1px solid ${theme.green}33`}}>
                                    📁 Cadastrar na Base
                                  </span>
                                )}
                              </>);
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── LISTA ── */}
      {/* ── ABA LEADS — Prospects iniciais não convertidos ── */}
      {visao==="leads" && (
        <div>
          <div style={{padding:"16px 20px",borderRadius:10,background:"#eff6ff",
            border:"1px solid #bfdbfe",marginBottom:16}}>
            <p style={{fontSize:13,fontWeight:700,color:"#1e40af",margin:"0 0 4px"}}>
              🎯 Leads Ativos — Prospects não convertidos
            </p>
            <p style={{fontSize:11,color:"#3b82f6",margin:0}}>
              {leads.filter(l=>!isConvertido(l)&&["recepcao","qualificacao","lead","nova_oportunidade","documentacao","pre_analise"].includes(l.estagio)).length} lead(s) em prospecção · Recepção e Qualificação
            </p>
          </div>
          <Card style={{padding:0,overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead><tr style={{background:"#eff6ff"}}>
                {["Nome","Origem","Produto(s) de Interesse","Responsável","Estágio","Ações"].map(h=>(
                  <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,
                    color:"#1e40af",letterSpacing:.8,textTransform:"uppercase"}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {leads
                  .filter(l=>!isConvertido(l)&&["recepcao","qualificacao","lead","nova_oportunidade","documentacao","pre_analise"].includes(l.estagio))
                  .sort((a,b)=>(a.nome||"").localeCompare(b.nome||"","pt-BR"))
                  .map((l,i)=>{
                    const est = ESTAGIOS.find(e=>e.id===l.estagio)||ESTAGIOS[0];
                    const resp = equipe.find(e=>e.id===l.responsavel);
                    const prods = (l.produtosInteresse||[]).map(id=>produtos.find(p=>p.id===id)?.nome).filter(Boolean).join(", ");
                    return (
                      <tr key={l.id} onClick={()=>setViewItem(l)}
                        style={{borderBottom:`1px solid ${theme.gray100}`,
                          background:i%2===0?theme.white:"#f8faff",cursor:"pointer"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#eff6ff"}
                        onMouseLeave={e=>e.currentTarget.style.background=i%2===0?theme.white:"#f8faff"}>
                        <td style={{padding:"10px 14px"}}>
                          <p style={{fontSize:13,fontWeight:600,color:theme.gray900,margin:0}}>{l.nome||l.nomeCliente||"—"}</p>
                          <p style={{fontSize:10,color:theme.gray400,margin:"2px 0 0"}}>{l.tipo} · {l.cidade}/{l.uf}</p>
                        </td>
                        <td style={{padding:"10px 14px",color:theme.gray600}}>{l.origem||"—"}</td>
                        <td style={{padding:"10px 14px",color:theme.gray600,maxWidth:160}}>{prods||"—"}</td>
                        <td style={{padding:"10px 14px",color:theme.gray600}}>{resp?.nome.split(" ")[0]||"—"}</td>
                        <td style={{padding:"10px 14px"}}>
                          <span style={{fontSize:10,padding:"2px 8px",borderRadius:8,fontWeight:600,
                            background:`${est.cor}18`,color:est.cor}}>
                            {est.icon} {est.label}
                          </span>
                        </td>
                        <td style={{padding:"10px 14px"}}>
                          <div style={{display:"flex",gap:5}}>
                            <Btn size="sm" variant="secondary" onClick={e=>{e.stopPropagation();setViewItem(l);}}>Ver</Btn>
                            {!isViz && <Btn size="sm" variant="secondary" onClick={e=>{e.stopPropagation();openEdit(l);}}>Editar</Btn>}
                            <Btn size="sm" onClick={e=>{e.stopPropagation();setLeadConv(l);setModalConv(true);}}>Converter</Btn>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
            {leads.filter(l=>!isConvertido(l)&&["recepcao","qualificacao","lead","nova_oportunidade","documentacao","pre_analise"].includes(l.estagio)).length===0&&(
              <div style={{padding:"40px 0",textAlign:"center"}}>
                <EmptyState icon="🎯" title="Nenhum lead em prospecção" subtitle="Novos prospects aparecerão aqui ao serem cadastrados."/>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ── ABA LISTA FUNIL — Todos no funil, não convertidos ── */}
      {visao==="lista_funil" && (
        <div>
          {/* Cabeçalho da aba */}
          <div style={{padding:"16px 20px",borderRadius:10,background:"#f8faff",
            border:"1px solid #e0e7ff",marginBottom:16}}>
            <p style={{fontSize:13,fontWeight:700,color:"#4338ca",margin:"0 0 4px"}}>
              📋 Lista de Negociações — Leads e Clientes no Funil
            </p>
            <p style={{fontSize:11,color:"#6366f1",margin:0}}>
              {leads.filter(l=>!["perdido","nao_qualificado","contrato_cancelado"].includes(l.estagio)).length} registro(s) · prospects e clientes em negociação ativa
            </p>
          </div>
          <Card style={{padding:"14px 20px",marginBottom:16,display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
            <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="Buscar por nome, produto..."
              style={{flex:1,minWidth:200,padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}/>
            <select value={filtroEstagio} onChange={e=>setFiltroEstagio(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",minWidth:180}}>
              <option value="lista_funil">Todos (leads + clientes)</option>
              <option value="nao_convertido">Apenas Leads</option>
              {ESTAGIOS.filter(e=>!["perdido"].includes(e.id)).map(e=><option key={e.id} value={e.id}>{e.icon} {e.label}</option>)}
            </select>
            <select value={filtroResp} onChange={e=>setFiltroResp(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
              <option value="">Todos responsáveis</option>
              {equipe.filter(e=>e.ativo).map(e=><option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </Card>
          <Card>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                    {["Lead","Origem","Produto(s)","Responsável","Estágio","Valor Est.","Prob.","Entrada","Ações"].map(h=>(
                      <th key={h} style={{padding:"11px 14px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:0.8,textTransform:"uppercase"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leadsFiltrados.map((l,i)=>{
                    const est = estagioDados(l.estagio);
                    const resp = equipe.find(e=>e.id===l.responsavel);
                    const prodsNomes = (l.produtosInteresse||[]).map(id=>produtos.find(p=>p.id===id)?.nome).filter(Boolean).join(", ");
                    // Detectar conversão — mesma lógica do viewItem
                    const nomeLead = (l.nome||l.nomeCliente||"").trim().toLowerCase();
                    const cliVinculado =
                      clientes.find(cl=>cl.id===l.clienteId) ||
                      clientes.find(cl=>cl.id===l.clienteVinculadoId) ||
                      clientes.find(cl=>cl.leadOrigemId===l.id) ||
                      (l.cpfCnpj && clientes.find(cl=>cl.cpfCnpj===l.cpfCnpj)) ||
                      (nomeLead.length>3 && clientes.find(cl=>(cl.nome||"").trim().toLowerCase()===nomeLead));
                    const jaConvertido = !!l.clienteId || !!l.clienteVinculadoId || l.tipoCRM==="expansao" || !!cliVinculado;
                    const estagioTerminal = ["contrato_cancelado","nao_qualificado","perdido"].includes(l.estagio);
                    return (
                      <tr key={l.id} style={{borderBottom:`1px solid ${theme.gray100}`,
                        background:jaConvertido?"#f0fdf4":i%2===0?theme.white:theme.gray50}}>
                        <td style={{padding:"11px 14px"}}>
                          <p style={{fontSize:13,fontWeight:500,color:theme.gray900}}>{l.nome||l.nomeCliente||"—"}</p>
                          <p style={{fontSize:10,color:theme.gray400}}>{l.tipo} · {l.cidade}/{l.uf}</p>
                        </td>
                        <td style={{padding:"11px 14px",fontSize:12,color:theme.gray600}}>{l.origem||"-"}</td>
                        <td style={{padding:"11px 14px",fontSize:12,color:theme.gray600,maxWidth:140}}>{prodsNomes||"-"}</td>
                        <td style={{padding:"11px 14px",fontSize:12,color:theme.gray600}}>{resp?.nome.split(" ").slice(0,2).join(" ")||"-"}</td>
                        <td style={{padding:"11px 14px"}}>
                          {jaConvertido ? (
                            <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:5,
                              background: l.tipoCRM==="expansao"?"#ede9fe":"#dcfce7",
                              color: l.tipoCRM==="expansao"?"#6d28d9":"#16a34a",
                              display:"inline-flex",alignItems:"center",gap:4}}>
                              {l.tipoCRM==="expansao"
                                ? "🔄 Expansão"
                                : "✅ Cliente"+(cliVinculado?" — "+cliVinculado.nome.split(" ")[0]:"")}
                            </span>
                          ) : (
                            <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:5,
                              background:`${est.cor}18`,color:est.cor}}>
                              {est.icon} {est.label}
                            </span>
                          )}
                        </td>
                        <td style={{padding:"11px 14px",fontSize:13,fontWeight:600,color:theme.gold}}>
                          {l.valorEstimado ? "R$ "+parseFloat(l.valorEstimado).toLocaleString("pt-BR",{minimumFractionDigits:2}) : "-"}
                        </td>
                        <td style={{padding:"11px 14px"}}>
                          {l.probabilidade!=null && (
                            <div style={{textAlign:"center"}}>
                              <span style={{fontSize:13,fontWeight:700,color:l.probabilidade>=70?theme.green:l.probabilidade>=40?theme.amber:theme.red}}>{l.probabilidade}%</span>
                              <div style={{height:4,background:theme.gray100,borderRadius:2,marginTop:3}}>
                                <div style={{height:"100%",width:`${l.probabilidade}%`,background:l.probabilidade>=70?theme.green:l.probabilidade>=40?theme.amber:theme.red,borderRadius:2}}/>
                              </div>
                            </div>
                          )}
                        </td>
                        <td style={{padding:"11px 14px",fontSize:11,color:theme.gray400}}>{l.dataEntrada}</td>
                        <td style={{padding:"11px 14px"}}>
                          <div style={{display:"flex",gap:5}}>
                            <Btn size="sm" variant="secondary" onClick={()=>setViewItem(l)}>Ver</Btn>
                            <Btn size="sm" variant="secondary" onClick={()=>{ setViewItem(l); setModalOcorr(true); }}>📋</Btn>
                            {!isViz && <Btn size="sm" variant="secondary" onClick={()=>openEdit(l)}>Editar</Btn>}
                            {!isViz && !jaConvertido && !estagioTerminal && (
                              <Btn size="sm" onClick={()=>{setLeadConv(l);setModalConv(true);}}>Converter</Btn>
                            )}
                            {!isViz && <Btn size="sm" variant="danger" onClick={()=>remove(l.id)}>×</Btn>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {leadsFiltrados.length===0 && <EmptyState icon="🎯" title="Nenhum lead encontrado" subtitle="Cadastre leads ou ajuste os filtros." />}
            </div>
          </Card>
        </div>
      )}

      {/* ── INDICADORES ── */}
      {visao==="painel" && (
        <div>
          {/* KPIs principais */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:24}}>
            {[
              {icon:"👥",label:"Leads Ativos",valor:leadsAtivos.length,cor:theme.blue,sub:"no funil"},
              {icon:"🤝",label:"Negociações",valor:emProposta.length,cor:theme.amber,sub:"proposta/negoc."},
              {icon:"✅",label:"Fechados",valor:fechados.length,cor:theme.green,sub:"contratos assinados"},
              {icon:"📈",label:"Conversão",valor:txConversao+"%",cor:txConversao>=30?theme.green:theme.amber,sub:"taxa do período"},
            ].map(k=>(
              <div key={k.label} style={{padding:"20px 22px",borderRadius:12,background:theme.white,
                border:`1px solid ${theme.gray200}`,borderTop:`3px solid ${k.cor}`}}>
                <p style={{fontSize:22,margin:"0 0 4px"}}>{k.icon}</p>
                <p style={{fontSize:26,fontWeight:800,color:k.cor,margin:0,lineHeight:1}}>{k.valor}</p>
                <p style={{fontSize:11,fontWeight:600,color:theme.gray700,margin:"4px 0 2px"}}>{k.label}</p>
                <p style={{fontSize:10,color:theme.gray400,margin:0}}>{k.sub}</p>
              </div>
            ))}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:20}}>
            {/* Pipeline financeiro */}
            <div style={{padding:20,borderRadius:12,background:theme.white,border:`1px solid ${theme.gray200}`}}>
              <p style={{fontSize:13,fontWeight:700,color:theme.gray800,marginBottom:16}}>💰 Pipeline Financeiro</p>
              {[
                {l:"Receita Ponderada",  v:"R$ "+receitaPipeline.toLocaleString("pt-BR",{minimumFractionDigits:2}), c:theme.gold},
                {l:"Ticket Médio",       v:"R$ "+ticketMedio.toLocaleString("pt-BR",{minimumFractionDigits:2}), c:theme.amber},
                {l:"Pend. Assinatura",   v:contPendente.length+" contrato(s)", c:theme.blue},
                {l:"Expansão (clientes)",v:expansao.length+" cliente(s)", c:theme.green},
              ].map(m=>(
                <div key={m.l} style={{display:"flex",justifyContent:"space-between",
                  padding:"9px 0",borderBottom:`1px solid ${theme.gray50}`}}>
                  <span style={{fontSize:12,color:theme.gray600}}>{m.l}</span>
                  <span style={{fontSize:13,fontWeight:700,color:m.c}}>{m.v}</span>
                </div>
              ))}
            </div>

            {/* Funil resumido */}
            <div style={{padding:20,borderRadius:12,background:theme.white,border:`1px solid ${theme.gray200}`}}>
              <p style={{fontSize:13,fontWeight:700,color:theme.gray800,marginBottom:16}}>📊 Funil por Estágio</p>
              {ESTAGIOS.filter(e=>!["perdido","contrato_assinado"].includes(e.id)).map(est=>{
                const qtd = leadsAtivos.filter(l=>l.estagio===est.id||
                  (est.id==="recepcao"&&["lead","nova_oportunidade","documentacao","pre_analise"].includes(l.estagio))
                ).length;
                const max = leadsAtivos.length||1;
                return qtd > 0 ? (
                  <div key={est.id} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:11,color:theme.gray700}}>{est.icon} {est.label}</span>
                      <span style={{fontSize:11,fontWeight:700,color:est.cor}}>{qtd}</span>
                    </div>
                    <div style={{height:6,background:theme.gray100,borderRadius:3}}>
                      <div style={{height:"100%",width:`${(qtd/max)*100}%`,background:est.cor,borderRadius:3}}/>
                    </div>
                  </div>
                ) : null;
              })}
            </div>
          </div>

          {/* Leads por origem */}
          <div style={{padding:20,borderRadius:12,background:theme.white,border:`1px solid ${theme.gray200}`}}>
            <p style={{fontSize:13,fontWeight:700,color:theme.gray800,marginBottom:16}}>🎯 Leads por Origem</p>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12}}>
              {(() => {
                const por = {};
                leads.forEach(l=>{ const o=l.origem||"Direto"; if(!por[o])por[o]=0; por[o]++; });
                return Object.entries(por).sort((a,b)=>b[1]-a[1]).map(([orig,qtd])=>(
                  <div key={orig} style={{padding:"10px 14px",borderRadius:8,
                    background:theme.gray50,border:`1px solid ${theme.gray200}`}}>
                    <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:"0 0 2px"}}>{qtd}</p>
                    <p style={{fontSize:11,color:theme.gray500,margin:0}}>{orig}</p>
                  </div>
                ));
              })()}
            </div>
          </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginTop:20}}>
          <Card style={{padding:24}}>
            <p className="serif" style={{fontSize:18,fontWeight:600,marginBottom:20}}>Funil de Conversão</p>
            {ESTAGIOS.filter(e=>e.id!=="perdido").map(est=>{
              const qtd = leads.filter(l=>l.estagio===est.id).length;
              const max = leads.length||1;
              return (
                <div key={est.id} style={{marginBottom:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                    <span style={{fontSize:12,color:theme.gray700}}>{est.icon} {est.label}</span>
                    <span style={{fontSize:13,fontWeight:700,color:est.cor}}>{qtd}</span>
                  </div>
                  <div style={{height:10,background:theme.gray100,borderRadius:5}}>
                    <div style={{height:"100%",width:`${(qtd/max)*100}%`,background:est.cor,borderRadius:5,transition:"width 0.5s"}}/>
                  </div>
                </div>
              );
            })}
          </Card>
          <Card style={{padding:24}}>
            <p className="serif" style={{fontSize:18,fontWeight:600,marginBottom:20}}>Métricas Chave</p>
            {[
              {l:"Total de Leads",         v:leads.length,            u:"",   c:theme.gray800},
              {l:"Leads Ativos",           v:leadsAtivos.length,      u:"",   c:theme.blue},
              {l:"Fechados",               v:fechados.length,         u:"",   c:theme.green},
              {l:"Perdidos",               v:perdidos.length,         u:"",   c:theme.red},
              {l:"Taxa de Conversão",      v:txConversao,             u:"%",  c:txConversao>=30?theme.green:theme.amber},
              {l:"Ticket Médio",           v:"R$ "+ticketMedio.toLocaleString("pt-BR",{minimumFractionDigits:2}), u:"",c:theme.gold},
              {l:"Pipeline Ponderado",     v:"R$ "+receitaPipeline.toLocaleString("pt-BR",{minimumFractionDigits:2}),u:"",c:theme.gold},
              {l:"Prazo Médio Fechamento", v:diasMedioFechamento||"-", u: diasMedioFechamento?"dias":"",c:theme.gray700},
            ].map(m=>(
              <div key={m.l} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:`1px solid ${theme.gray100}`}}>
                <span style={{fontSize:13,color:theme.gray600}}>{m.l}</span>
                <span style={{fontSize:14,fontWeight:700,color:m.c}}>{m.v}{m.u}</span>
              </div>
            ))}
          </Card>
          <Card style={{padding:24}}>
            <p className="serif" style={{fontSize:18,fontWeight:600,marginBottom:20}}>Leads por Origem</p>
            {(() => {
              const porOrigem = {};
              leads.forEach(l=>{ const o=l.origem||"Direto"; if(!porOrigem[o])porOrigem[o]=0; porOrigem[o]++; });
              const max = Math.max(...Object.values(porOrigem),1);
              return Object.entries(porOrigem).sort((a,b)=>b[1]-a[1]).map(([orig,qtd])=>(
                <div key={orig} style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:12,color:theme.gray700}}>{orig}</span>
                    <span style={{fontSize:12,fontWeight:700,color:theme.blue}}>{qtd}</span>
                  </div>
                  <div style={{height:8,background:theme.gray100,borderRadius:4}}>
                    <div style={{height:"100%",width:`${(qtd/max)*100}%`,background:`linear-gradient(90deg,${theme.blue},${theme.gold})`,borderRadius:4}}/>
                  </div>
                </div>
              ));
            })()}
          </Card>
          <Card style={{padding:24}}>
            <p className="serif" style={{fontSize:18,fontWeight:600,marginBottom:20}}>Pipeline por Responsável</p>
            {(() => {
              const porResp = {};
              leadsAtivos.forEach(l=>{
                const r=l.responsavel||"Sem responsável";
                if(!porResp[r]) porResp[r]={qtd:0,valor:0};
                porResp[r].qtd++;
                porResp[r].valor += (parseFloat(l.valorEstimado)||0)*(l.probabilidade||0)/100;
              });
              return Object.entries(porResp).sort((a,b)=>b[1].valor-a[1].valor).map(([respId,v])=>{
                const resp = equipe.find(e=>e.id===respId);
                return (
                  <div key={respId} style={{padding:"10px 0",borderBottom:`1px solid ${theme.gray100}`}}>
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <span style={{fontSize:13,color:theme.gray700}}>{resp?.nome.split(" ").slice(0,2).join(" ")||respId}</span>
                      <div style={{textAlign:"right"}}>
                        <p style={{fontSize:12,fontWeight:700,color:theme.gold}}>R$ {v.valor.toLocaleString("pt-BR",{minimumFractionDigits:2})}</p>
                        <p style={{fontSize:10,color:theme.gray400}}>{v.qtd} lead{v.qtd>1?"s":""}</p>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </Card>
        </div>
        </div>
      )}

      {/* Modal Formulário Lead */}
      {editItem && (
        <Modal open={modalOpen} onClose={()=>setModalOpen(false)}
          title={editItem.id
            ? (editItem.tipoCRM==="expansao" ? "Editar Oportunidade" : "Editar Lead")
            : (editItem.tipoCRM==="expansao" ? "Nova Oportunidade — Cliente Existente" : "Novo Lead")}
          width={700}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
            <div style={{gridColumn:"1/-1"}}>
              <div style={{display:"flex",gap:12,marginBottom:16}}>
                {["PF","PJ"].map(t=>(
                  <label key={t} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13}}>
                    <input type="radio" checked={editItem.tipo===t} onChange={()=>setEditItem({...editItem,tipo:t})}/> {t==="PF"?"Pessoa Física":"Pessoa Jurídica"}
                  </label>
                ))}
              </div>
            </div>
            <div style={{gridColumn:"1/-1"}}>
              <FormField label="Nome / Razão Social" required>
                <Input value={editItem.nome} onChange={v=>setEditItem({...editItem,nome:v})} placeholder="Nome do lead" />
              </FormField>
              <FormField label={editItem.tipo==="PF" ? "CPF" : "CNPJ"}>
                <Input value={editItem.cpfCnpj} onChange={v=>setEditItem({...editItem,cpfCnpj:v})} placeholder={editItem.tipo==="PF" ? "000.000.000-00" : "00.000.000/0001-00"} />
              </FormField>
            </div>
            <FormField label="E-mail">
              <Input type="email" value={editItem.email} onChange={v=>setEditItem({...editItem,email:v})} placeholder="email@exemplo.com"/>
            </FormField>
            <CampoWhatsApp
              ddi={editItem.whatsappDDI}
              numero={editItem.whatsapp}
              onChangeDDI={v=>setEditItem({...editItem, whatsappDDI: v})}
              onChangeNumero={v=>setEditItem({...editItem, whatsapp: v})}
            />
            <FormField label="Cidade">
              <Input value={editItem.cidade} onChange={v=>setEditItem({...editItem,cidade:v})} placeholder="Cidade"/>
            </FormField>
            <FormField label="UF">
              <Select value={editItem.uf} onChange={v=>setEditItem({...editItem,uf:v})} options={UFS}/>
            </FormField>
            <FormField label="Origem">
              <Select value={editItem.origem} onChange={v=>setEditItem({...editItem,origem:v})} options={ORIGENS} placeholder="Selecione..."/>
            </FormField>
            <FormField label="Parceria">
              <Select value={editItem.parceiraId} onChange={v=>setEditItem({...editItem,parceiraId:v})}
                options={parcerias.filter(p=>p.status==="Ativo").map(p=>({value:p.id,label:p.nome}))} placeholder="Nenhuma..."/>
            </FormField>
            {/* Tipo CRM */}
            <FormField label="Tipo de Registro">
              <Select value={editItem.tipoCRM||"lead"}
                onChange={v=>setEditItem({...editItem,
                  tipoCRM:v,
                  estagio:v==="expansao"?"recepcao":"recepcao"
                })}
                options={[{value:"lead",label:"🔵 Lead — Novo Prospecção"},{value:"expansao",label:"🟢 Expansão — Cliente Existente"}]}/>
            </FormField>
            {editItem.tipoCRM==="expansao"&&(
              <FormField label="Cliente Vinculado">
                <Select value={editItem.clienteVinculadoId||""}
                  onChange={v=>setEditItem({...editItem,clienteVinculadoId:v})}
                  options={[{value:"",label:"Selecione o cliente..."},...(clientes||[]).map(cl=>({value:cl.id,label:cl.nome}))]}/>
              </FormField>
            )}
            {editItem.atendimentoId && (
              <div style={{gridColumn:"1/-1",marginTop:8,marginBottom:8,padding:"10px 14px",borderRadius:8,background:"#f5f3ff",border:"1px solid #ddd6fe"}}>
                <p style={{fontSize:12,fontWeight:700,color:"#6d28d9",marginBottom:6}}>
                  🤖 Origem: Atendimento ATLAS ({editItem.origemCanal||"—"})
                  {editItem.scoreBant!=null && <span style={{marginLeft:8,fontWeight:500}}>· Score BANT: {editItem.scoreBant}/4</span>}
                  {editItem.discPerfil && <span style={{marginLeft:8,fontWeight:500}}>· Perfil DISC: {editItem.discPerfil}</span>}
                </p>
                {loadingHistAtlas && <p style={{fontSize:12,color:theme.gray400}}>Carregando histórico de conversa...</p>}
                {!loadingHistAtlas && histAtlas && (
                  <div style={{maxHeight:220,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
                    {(histAtlas.mensagens||[]).length===0 && <p style={{fontSize:12,color:theme.gray400}}>Sem mensagens registradas.</p>}
                    {(histAtlas.mensagens||[]).map(m=>(
                      <div key={m.id} style={{fontSize:12,padding:"6px 10px",borderRadius:6,
                        background: m.origem==="lead" ? "#fff" : "#ede9fe",
                        border:"1px solid "+(m.origem==="lead"?theme.gray100:"#ddd6fe"),
                        alignSelf: m.origem==="lead" ? "flex-start" : "flex-end", maxWidth:"85%"}}>
                        <span style={{fontWeight:700,textTransform:"uppercase",fontSize:10,color:theme.gray400}}>{m.origem}</span>
                        <div>{m.mensagem}</div>
                      </div>
                    ))}
                  </div>
                )}
                {!loadingHistAtlas && !histAtlas && <p style={{fontSize:12,color:theme.gray400}}>Não foi possível carregar o histórico do ATLAS.</p>}
              </div>
            )}
            <FormField label="Estágio">
              <Select value={editItem.estagio} onChange={v=>setEditItem({...editItem,estagio:v})}
                options={(editItem.tipoCRM==="expansao"
                      ? ESTAGIOS.filter(e=>ESTAGIOS_EXPANSAO_IDS.includes(e.id)||["perdido","nao_qualificado"].includes(e.id))
                      : ESTAGIOS.filter(e=>!["nova_oportunidade","fechado"].includes(e.id))
                    ).map(e=>({value:e.id,label:e.icon+" "+e.label}))}/>
            </FormField>
            {/* Campos de datas por estágio */}
            {["proposta","negociacao","envio_contrato","contrato_pendente","contrato_assinado","contrato_cancelado"].includes(editItem.estagio)&&(
              <div style={{gridColumn:"1/-1",background:"#f0f9ff",borderRadius:8,padding:"12px 14px",border:"1px solid #bfdbfe"}}>
                <p style={{fontSize:11,fontWeight:700,color:theme.blue,marginBottom:8}}>📅 Datas do Processo Comercial</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px"}}>
                  <FormField label="Data Envio da Proposta">
                    <Input type="date" value={editItem.dataEnvioProposta||""} onChange={v=>setEditItem({...editItem,dataEnvioProposta:v})}/>
                  </FormField>
                  <FormField label="Data Aprovação da Proposta">
                    <Input type="date" value={editItem.dataAprovacaoProposta||""} onChange={v=>setEditItem({...editItem,dataAprovacaoProposta:v})}/>
                  </FormField>
                  <FormField label="Data Envio do Contrato">
                    <Input type="date" value={editItem.dataEnvioContrato||""} onChange={v=>setEditItem({...editItem,dataEnvioContrato:v})}/>
                  </FormField>
                  <FormField label="Data Contrato Assinado">
                    <Input type="date" value={editItem.dataContratoAssinado||""} onChange={v=>setEditItem({...editItem,dataContratoAssinado:v})}/>
                  </FormField>
                </div>
              </div>
            )}
            <FormField label="Responsável">
              <Select value={editItem.responsavel} onChange={v=>setEditItem({...editItem,responsavel:v})}
                options={equipe.filter(e=>e.ativo!==false).map(e=>({value:e.id,label:e.nome}))} placeholder="Selecione..."/>
            </FormField>
            <div style={{gridColumn:"1/-1"}}>
              <FormField label="Produtos de Interesse">
                <ProdutoSelector
                  produtos={produtos}
                  value={editItem.produtosInteresse||[]}
                  onChange={v=>setEditItem({...editItem,produtosInteresse:v})}
                />
              </FormField>
            </div>
            <FormField label="Valor Estimado (R$)">
              <Input type="number" value={editItem.valorEstimado} onChange={v=>setEditItem({...editItem,valorEstimado:v})} placeholder="0,00"/>
            </FormField>
            <FormField label="Probabilidade (%)">
              <div>
                <input type="range" min={0} max={100} step={5} value={editItem.probabilidade}
                  onChange={e=>setEditItem({...editItem,probabilidade:parseInt(e.target.value)})}
                  style={{width:"100%",accentColor:theme.gold}}/>
                <p style={{fontSize:12,color:theme.gold,fontWeight:700,textAlign:"center",marginTop:4}}>{editItem.probabilidade}%</p>
              </div>
            </FormField>
            <FormField label="Data de Entrada">
              <Input type="date" value={editItem.dataEntrada} onChange={v=>setEditItem({...editItem,dataEntrada:v})}/>
            </FormField>
            <FormField label="Prazo Esperado de Fechamento">
              <Input type="date" value={editItem.prazoFechamento} onChange={v=>setEditItem({...editItem,prazoFechamento:v})}/>
            </FormField>
            {/* Fase 1 — Canal de Origem Detalhado + Perfil DISC */}
            <FormField label="Canal de Origem">
              <Select value={editItem.origemCanal||""} onChange={v=>setEditItem({...editItem,origemCanal:v})}
                options={[
                  {value:"",label:"Não informado"},
                  {value:"WhatsApp",label:"📱 WhatsApp"},
                  {value:"Instagram DM",label:"📸 Instagram DM"},
                  {value:"Instagram Comentário",label:"📸 Instagram Comentário"},
                  {value:"LinkedIn",label:"💼 LinkedIn"},
                  {value:"Site — Formulário",label:"🌐 Site — Formulário"},
                  {value:"Google",label:"🔍 Google"},
                  {value:"Indicação Direta",label:"🤝 Indicação Direta"},
                  {value:"Parceiro",label:"🤝 Parceiro"},
                  {value:"Evento",label:"🎤 Evento"},
                  {value:"Telefone",label:"📞 Telefone"},
                  {value:"E-mail",label:"✉️ E-mail"},
                  {value:"Outros",label:"➕ Outros"},
                ]}/>
            </FormField>
            <FormField label="Perfil DISC">
              <Select value={editItem.discPerfil||""} onChange={v=>setEditItem({...editItem,discPerfil:v})}
                options={[
                  {value:"",label:"Não avaliado"},
                  {value:"D",label:"D — Dominante (decidido, objetivo, direto)"},
                  {value:"I",label:"I — Influente (entusiasta, sociável, persuasivo)"},
                  {value:"S",label:"S — Estável (paciente, leal, metódico)"},
                  {value:"C",label:"C — Conformista (analítico, preciso, cauteloso)"},
                ]}/>
            </FormField>

            {/* Fase 1 — Qualificação BANT */}
            <div style={{gridColumn:"1/-1"}}>
              <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"12px 14px",marginBottom:4}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <p style={{fontSize:11,fontWeight:700,color:"#92400e",textTransform:"uppercase",letterSpacing:.8,margin:0}}>
                    🎯 Qualificação BANT
                  </p>
                  <span style={{fontSize:12,fontWeight:700,color:"#92400e",background:"#fef3c7",padding:"2px 10px",borderRadius:12}}>
                    Score: {editItem.scoreBant||0}/4
                  </span>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {[
                    {key:"budget",    label:"💰 Budget",    desc:"Tem orçamento disponível?"},
                    {key:"authority", label:"👤 Authority", desc:"É o decisor / tem autorização?"},
                    {key:"need",      label:"⚡ Need",       desc:"Tem necessidade real e urgente?"},
                    {key:"timeline",  label:"📅 Timeline",  desc:"Tem prazo definido para decidir?"},
                  ].map(({key,label,desc})=>{
                    const bant = editItem.bantJson || {budget:false,authority:false,need:false,timeline:false};
                    const checked = !!bant[key];
                    return(
                      <label key={key} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",
                        padding:"8px 10px",borderRadius:6,
                        background:checked?"#d1fae5":"#fff",
                        border:`1px solid ${checked?"#6ee7b7":"#e5e7eb"}`}}>
                        <input type="checkbox" checked={checked}
                          onChange={e=>{
                            const novosBant={...bant,[key]:e.target.checked};
                            const score=Object.values(novosBant).filter(Boolean).length;
                            setEditItem({...editItem,bantJson:novosBant,scoreBant:score});
                          }}/>
                        <div>
                          <p style={{fontSize:12,fontWeight:700,color:checked?"#065f46":"#374151",margin:0}}>{label}</p>
                          <p style={{fontSize:10,color:checked?"#059669":"#6b7280",margin:0}}>{desc}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{gridColumn:"1/-1"}}>
              <FormField label={<span>🔒 Dados de Acesso <span style={{fontSize:10,color:theme.gray400,fontWeight:400}}>— uso interno · não compartilhar</span></span>}>
                <Textarea value={editItem.dadosAcesso||""} onChange={v=>setEditItem({...editItem,dadosAcesso:v})}
                  placeholder="Ex: e-CAC — CPF: 000.000.000-00 / Senha: ... | PGFN — login: ... | Certificado digital: ..."
                  rows={3}/>
              </FormField>
              <FormField label="Observações">
                <Textarea value={editItem.obs} onChange={v=>setEditItem({...editItem,obs:v})} placeholder="Contexto, necessidades, objeções..."/>
              </FormField>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
            <Btn variant="secondary" onClick={()=>setModalOpen(false)}>Cancelar</Btn>
            {!isViz && <Btn onClick={save}>Salvar Lead</Btn>}
          </div>
        </Modal>
      )}

      {/* ── ABA CLIENTES CRM ── */}
      {visao==="clientes_crm" && (()=>{
        const clientesCRM = leads.filter(l => isConvertido(l) && !["perdido","nao_qualificado","contrato_cancelado"].includes(l.estagio));
        const buscaCli = busca.toLowerCase();
        const clientesFiltrados = clientesCRM.filter(l =>
          !buscaCli || (l.nome||l.nomeCliente||"").toLowerCase().includes(buscaCli)
        ).sort((a,b)=>(a.nome||a.nomeCliente||"").localeCompare(b.nome||b.nomeCliente||"","pt-BR"));

        return (
          <div>
            <div style={{padding:"16px 20px",borderRadius:10,background:"#f0fdf4",
              border:"1px solid #bbf7d0",marginBottom:16,display:"flex",
              justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <p style={{fontSize:13,fontWeight:700,color:"#166534",margin:"0 0 2px"}}>
                  👥 Clientes CRM — Leads convertidos e Expansões
                </p>
                <p style={{fontSize:11,color:"#16a34a",margin:0}}>
                  {clientesCRM.length} cliente(s) no CRM ·
                  {clientesCRM.filter(l=>l.tipoCRM==="expansao").length} expansão ·
                  {clientesCRM.filter(l=>l.tipoCRM!=="expansao").length} convertidos
                </p>
              </div>
              <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="Buscar cliente..."
                style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none",width:220}}/>
            </div>

            {clientesFiltrados.length === 0 ? (
              <EmptyState icon="👥" title="Nenhum cliente no CRM"
                subtitle="Converta leads em clientes para que apareçam aqui."/>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {/* Expansão */}
                {clientesFiltrados.filter(l=>l.tipoCRM==="expansao").length > 0 && (
                  <div>
                    <p style={{fontSize:11,fontWeight:700,color:"#6366f1",textTransform:"uppercase",
                      letterSpacing:.8,marginBottom:8}}>🔄 Expansão — Nova Oportunidade com Cliente</p>
                    <Card style={{padding:0,overflow:"hidden"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                        <thead><tr style={{background:"#f5f3ff"}}>
                          {["Cliente","Produto(s) de Interesse","Estágio","Responsável","Ações"].map(h=>(
                            <th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:10,
                              color:"#6366f1",letterSpacing:.8,textTransform:"uppercase"}}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {clientesFiltrados.filter(l=>l.tipoCRM==="expansao").map((l,i)=>{
                            const est = ESTAGIOS.find(e=>e.id===l.estagio);
                            const resp = equipe.find(e=>e.id===l.responsavel);
                            const prods = (l.produtosInteresse||[]).map(id=>produtos.find(p=>p.id===id)?.nome).filter(Boolean).join(", ");
                            const cliRef = clientes.find(cl=>cl.id===l.clienteVinculadoId||cl.leadOrigemId===l.id);
                            return (
                              <tr key={l.id} onClick={()=>setViewItem(l)}
                                style={{borderBottom:`1px solid ${theme.gray100}`,
                                  background:i%2===0?theme.white:"#faf5ff",cursor:"pointer"}}
                                onMouseEnter={e=>e.currentTarget.style.background="#ede9fe"}
                                onMouseLeave={e=>e.currentTarget.style.background=i%2===0?theme.white:"#faf5ff"}>
                                <td style={{padding:"10px 14px"}}>
                                  <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:0}}>{l.nome||l.nomeCliente}</p>
                                  {cliRef&&<p style={{fontSize:10,color:"#6366f1",margin:"2px 0 0"}}>📁 {cliRef.nome}</p>}
                                </td>
                                <td style={{padding:"10px 14px",color:theme.gray600}}>{prods||"—"}</td>
                                <td style={{padding:"10px 14px"}}>
                                  <span style={{fontSize:10,padding:"2px 8px",borderRadius:8,fontWeight:600,
                                    background:`${est?.cor||theme.gray400}18`,color:est?.cor||theme.gray400}}>
                                    {est?.icon} {est?.label||l.estagio}
                                  </span>
                                </td>
                                <td style={{padding:"10px 14px",color:theme.gray600,fontSize:12}}>{resp?.nome.split(" ")[0]||"—"}</td>
                                <td style={{padding:"10px 14px"}}>
                                  <Btn size="sm" variant="secondary" onClick={e=>{e.stopPropagation();setViewItem(l);}}>Ver</Btn>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Card>
                  </div>
                )}

                {/* Convertidos */}
                {clientesFiltrados.filter(l=>l.tipoCRM!=="expansao").length > 0 && (
                  <div>
                    <p style={{fontSize:11,fontWeight:700,color:theme.green,textTransform:"uppercase",
                      letterSpacing:.8,marginBottom:8}}>✅ Leads Convertidos em Cliente</p>
                    <Card style={{padding:0,overflow:"hidden"}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                        <thead><tr style={{background:"#f0fdf4"}}>
                          {["Cliente","Produto(s)","Convertido em","Responsável","Ações"].map(h=>(
                            <th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:10,
                              color:theme.green,letterSpacing:.8,textTransform:"uppercase"}}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {clientesFiltrados.filter(l=>l.tipoCRM!=="expansao").map((l,i)=>{
                            const resp = equipe.find(e=>e.id===l.responsavel);
                            const prods = (l.produtosInteresse||[]).map(id=>produtos.find(p=>p.id===id)?.nome).filter(Boolean).join(", ");
                            const cliRef = clientes.find(cl=>cl.id===l.clienteId||cl.leadOrigemId===l.id);
                            return (
                              <tr key={l.id} onClick={()=>setViewItem(l)}
                                style={{borderBottom:`1px solid ${theme.gray100}`,
                                  background:i%2===0?theme.white:"#f0fdf4",cursor:"pointer"}}
                                onMouseEnter={e=>e.currentTarget.style.background="#dcfce7"}
                                onMouseLeave={e=>e.currentTarget.style.background=i%2===0?theme.white:"#f0fdf4"}>
                                <td style={{padding:"10px 14px"}}>
                                  <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:0}}>{l.nome||l.nomeCliente}</p>
                                  {cliRef&&<p style={{fontSize:10,color:theme.green,margin:"2px 0 0"}}>📁 {cliRef.nome}</p>}
                                </td>
                                <td style={{padding:"10px 14px",color:theme.gray600}}>{prods||"—"}</td>
                                <td style={{padding:"10px 14px",color:theme.green,fontWeight:600,fontSize:12}}>
                                  {l.convertidoEm ? new Date(l.convertidoEm+"T12:00").toLocaleDateString("pt-BR") : "—"}
                                </td>
                                <td style={{padding:"10px 14px",color:theme.gray600,fontSize:12}}>{resp?.nome.split(" ")[0]||"—"}</td>
                                <td style={{padding:"10px 14px"}}>
                                  <Btn size="sm" variant="secondary" onClick={e=>{e.stopPropagation();setViewItem(l);}}>Ver</Btn>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Card>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {visao==="historico"&&(()=>{
        const fmtD5 = d => d ? new Date(d+"T12:00").toLocaleDateString("pt-BR") : "—";
        const fechadosHist  = leads.filter(l=>["contrato_assinado","fechado"].includes(l.estagio));
        const perdidosHist  = leads.filter(l=>["perdido","nao_qualificado","contrato_cancelado"].includes(l.estagio));
        const expansaoHist  = leads.filter(l=>l.tipoCRM==="expansao");

        const TabelaLead = ({lista, corBg="#fff", emptyMsg}) => lista.length===0
          ? <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"20px 0"}}>{emptyMsg}</p>
          : <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead><tr style={{background:theme.gray50}}>
                {["Lead / Cliente","Origem","Produto(s)","Responsável","Estágio","Conversão","Ações"].map(h=>(
                  <th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,
                    color:theme.gray400,letterSpacing:.8,textTransform:"uppercase"}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {lista.map((l,i)=>{
                  const est   = ESTAGIOS.find(e=>e.id===l.estagio);
                  const resp  = equipe.find(e=>e.id===l.responsavel);
                  const prods = (l.produtosInteresse||[]).map(id=>produtos.find(p=>p.id===id)?.nome).filter(Boolean).join(", ");
                  const cliV  = clientes.find(cl=>cl.id===l.clienteId||cl.leadOrigemId===l.id);
                  return (
                    <tr key={l.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?corBg:theme.gray50,cursor:"pointer"}}
                      onClick={()=>setViewItem(l)}>
                      <td style={{padding:"9px 12px",fontWeight:600,color:theme.gray900}}>
                        {l.nome||l.nomeCliente||"—"}
                        {cliV&&<span style={{fontSize:9,marginLeft:6,padding:"1px 6px",borderRadius:8,background:"#dcfce7",color:"#16a34a",fontWeight:700}}>cliente</span>}
                      </td>
                      <td style={{padding:"9px 12px",color:theme.gray500}}>{l.origem||"—"}</td>
                      <td style={{padding:"9px 12px",color:theme.gray500,maxWidth:140}}>{prods||"—"}</td>
                      <td style={{padding:"9px 12px",color:theme.gray500}}>{resp?.nome.split(" ")[0]||"—"}</td>
                      <td style={{padding:"9px 12px"}}>
                        <span style={{fontSize:10,padding:"2px 8px",borderRadius:8,fontWeight:600,
                          background:(est?.cor||theme.gray400)+"18",color:est?.cor||theme.gray400}}>
                          {est?.icon} {est?.label||l.estagio}
                        </span>
                      </td>
                      <td style={{padding:"9px 12px",fontSize:11,color:l.convertidoEm?theme.green:theme.gray300}}>
                        {l.convertidoEm?fmtD5(l.convertidoEm):"—"}
                      </td>
                      <td style={{padding:"9px 12px"}}>
                        <button onClick={e=>{e.stopPropagation();setViewItem(l);}}
                          style={{fontSize:11,padding:"4px 10px",borderRadius:6,cursor:"pointer",
                            border:`1px solid ${theme.gray200}`,background:theme.gray50,color:theme.gray600}}>
                          Ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>;

        return (
          <div>
            {/* Resumo rápido */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:24}}>
              {[
                {icon:"✅",label:"Contratos Assinados",valor:fechadosHist.length,cor:theme.green},
                {icon:"🔄",label:"Expansão (clientes)",valor:expansaoHist.length,cor:"#6366f1"},
                {icon:"❌",label:"Perdidos/Descartados",valor:perdidosHist.length,cor:theme.red},
              ].map(k=>(
                <div key={k.label} style={{padding:"16px 20px",borderRadius:10,background:theme.white,
                  border:`1px solid ${theme.gray200}`,borderTop:`3px solid ${k.cor}`,display:"flex",gap:12,alignItems:"center"}}>
                  <span style={{fontSize:24}}>{k.icon}</span>
                  <div>
                    <p style={{fontSize:22,fontWeight:800,color:k.cor,margin:0,lineHeight:1}}>{k.valor}</p>
                    <p style={{fontSize:11,color:theme.gray500,margin:"3px 0 0"}}>{k.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Seção Fechados */}
            <div style={{marginBottom:24}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <span style={{fontSize:14,fontWeight:700,color:theme.green}}>✅ Contratos Assinados</span>
                <span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:"#dcfce7",color:theme.green,fontWeight:600}}>{fechadosHist.length}</span>
              </div>
              <div style={{background:theme.white,borderRadius:10,border:`1px solid ${theme.gray200}`,overflow:"hidden"}}>
                <TabelaLead lista={fechadosHist} corBg="#f0fdf4" emptyMsg="Nenhum contrato assinado ainda."/>
              </div>
            </div>

            {/* Seção Expansão */}
            {expansaoHist.length > 0 && (
              <div style={{marginBottom:24}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                  <span style={{fontSize:14,fontWeight:700,color:"#6366f1"}}>🔄 Expansão — Clientes com Nova Oportunidade</span>
                  <span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:"#ede9fe",color:"#6366f1",fontWeight:600}}>{expansaoHist.length}</span>
                </div>
                <div style={{background:theme.white,borderRadius:10,border:`1px solid ${theme.gray200}`,overflow:"hidden"}}>
                  <TabelaLead lista={expansaoHist} emptyMsg="Nenhuma expansão."/>
                </div>
              </div>
            )}

            {/* Seção Perdidos */}
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <span style={{fontSize:14,fontWeight:700,color:theme.red}}>❌ Perdidos / Descartados</span>
                <span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:"#fef2f2",color:theme.red,fontWeight:600}}>{perdidosHist.length}</span>
              </div>
              <div style={{background:theme.white,borderRadius:10,border:`1px solid ${theme.gray200}`,overflow:"hidden"}}>
                <TabelaLead lista={perdidosHist} emptyMsg="Nenhum lead perdido ou descartado."/>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal Visualização Lead */}
      {viewItem && (
        <Modal open={!!viewItem} onClose={()=>setViewItem(null)} title={"Lead: "+viewItem.nome} width={640}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:20}}>
            {[
              {l:"Estágio",   v:<span style={{fontSize:12,fontWeight:700,padding:"3px 10px",borderRadius:5,background:`${estagioDados(viewItem.estagio).cor}18`,color:estagioDados(viewItem.estagio).cor}}>{estagioDados(viewItem.estagio).icon} {estagioDados(viewItem.estagio).label}</span>},
              {l:"Tipo CRM",  v:<span style={{fontSize:11,padding:"2px 8px",borderRadius:8,fontWeight:700,background:viewItem.tipoCRM==="expansao"?"#dcfce7":"#dbeafe",color:viewItem.tipoCRM==="expansao"?theme.green:theme.blue}}>{viewItem.tipoCRM==="expansao"?"🟢 Expansão":"🔵 Lead"}</span>},
              {l:"Tipo",      v:<Badge color={viewItem.tipo==="PJ"?"blue":"gray"}>{viewItem.tipo}</Badge>},
              {l:"WhatsApp",  v:viewItem.whatsapp||"-"},
              {l:"E-mail",    v:viewItem.email||"-"},
              {l:"Origem",    v:viewItem.origem||"-"},
              {l:"Probabilidade", v:<span style={{fontWeight:700,color:viewItem.probabilidade>=70?theme.green:viewItem.probabilidade>=40?theme.amber:theme.red}}>{viewItem.probabilidade}%</span>},
              {l:"Valor Est.", v:viewItem.valorEstimado?"R$ "+parseFloat(viewItem.valorEstimado).toLocaleString("pt-BR",{minimumFractionDigits:2}):"-"},
              {l:"Entrada",   v:viewItem.dataEntrada||"-"},
            ].map(r=>(
              <div key={r.l}>
                <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{r.l}</p>
                <div style={{marginTop:4,fontSize:13,color:theme.gray700}}>{r.v}</div>
              </div>
            ))}
          </div>
          {viewItem.obs && (
            <div style={{padding:"10px 14px",background:theme.gray50,borderRadius:8,marginBottom:16}}>
              <p style={{fontSize:11,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8,marginBottom:4}}>Observações</p>
              <p style={{fontSize:13,color:theme.gray700}}>{viewItem.obs}</p>
            </div>
          )}
          {/* Histórico */}
          <div style={{borderTop:`1px solid ${theme.gray100}`,paddingTop:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <p className="serif" style={{fontSize:16,fontWeight:600}}>Histórico de Contatos</p>
              <Btn size="sm" onClick={()=>setModalOcorr(true)}>+ Registrar</Btn>
            </div>
            {(!viewItem.historico||viewItem.historico.length===0)
              ? <p style={{fontSize:13,color:theme.gray400,textAlign:"center",padding:"16px 0"}}>Nenhum contato registrado.</p>
              : <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {[...viewItem.historico].reverse().map((h,i)=>(
                    <div key={i} style={{padding:"10px 14px",background:theme.gray50,borderRadius:8,borderLeft:`3px solid ${theme.gold}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                        <Badge color="amber">{h.tipo}</Badge>
                        <span style={{fontSize:11,color:theme.gray400}}>{h.data}</span>
                      </div>
                      <p style={{fontSize:13,color:theme.gray700}}>{h.descricao}</p>
                    </div>
                  ))}
                </div>
            }
          </div>
          <div style={{display:"flex",justifyContent:"space-between",gap:10,marginTop:20,paddingTop:16,borderTop:`1px solid ${theme.gray100}`}}>
            <div style={{display:"flex",gap:8}}>
              {(()=>{
                const nomeLead = (viewItem.nome||viewItem.nomeCliente||"").trim().toLowerCase();

                // Detectar conversão — 5 critérios
                const cliVinculado =
                  clientes.find(cl => cl.id === viewItem.clienteId) ||
                  clientes.find(cl => cl.id === viewItem.clienteVinculadoId) ||
                  clientes.find(cl => cl.leadOrigemId === viewItem.id) ||
                  (viewItem.cpfCnpj && clientes.find(cl => cl.cpfCnpj && cl.cpfCnpj === viewItem.cpfCnpj)) ||
                  (nomeLead.length > 3 && clientes.find(cl => (cl.nome||"").trim().toLowerCase() === nomeLead));

                const jaConvertido = !!viewItem.clienteId || !!viewItem.clienteVinculadoId || viewItem.tipoCRM==="expansao" || !!cliVinculado;

                // Se convertido — mostrar badge independente do estágio
                if (jaConvertido) {
                  return (
                    <span style={{fontSize:12,padding:"7px 16px",borderRadius:8,
                      background: viewItem.tipoCRM==="expansao"?"#ede9fe":"#dcfce7",
                      color: viewItem.tipoCRM==="expansao"?"#6d28d9":"#16a34a",
                      fontWeight:700, display:"inline-flex",alignItems:"center",gap:6}}>
                      {viewItem.tipoCRM==="expansao"
                        ? "🔄 Expansão — "+( cliVinculado?.nome || "Cliente Existente")
                        : "✅ Cliente"+(cliVinculado?" — "+cliVinculado.nome:"")}
                    </span>
                  );
                }

                // Estágios terminais sem conversão — sem botão
                const estagioTerminal = ["contrato_cancelado","nao_qualificado","perdido"].includes(viewItem.estagio);
                if (estagioTerminal) return (
                  <span style={{fontSize:11,padding:"5px 12px",borderRadius:8,
                    background:"#fef2f2",color:theme.red,fontWeight:600}}>
                    ❌ {viewItem.estagio==="perdido"?"Perdido":"Descartado"}
                  </span>
                );

                // Lead ativo não convertido — botão converter
                const isAssinado = ["contrato_assinado","fechado"].includes(viewItem.estagio);
                return (
                  <Btn
                    onClick={()=>{setLeadConv(viewItem);setViewItem(null);setModalConv(true);}}
                    style={isAssinado?{background:theme.green,color:"#fff",border:"none"}:{}}>
                    {isAssinado?"✅ Converter em Cliente":"Converter em Cliente"}
                  </Btn>
                );
              })()}
            </div>
            <div style={{display:"flex",gap:8}}>
              <Btn variant="secondary" onClick={()=>{setViewItem(null);openEdit(leads.find(l=>l.id===viewItem.id));}}>Editar</Btn>
              <Btn variant="secondary" onClick={()=>setViewItem(null)}>Fechar</Btn>
            </div>
          </div>

          {/* Datas do Processo Comercial */}
          {(viewItem.dataEnvioProposta||viewItem.dataAprovacaoProposta||viewItem.dataEnvioContrato||viewItem.dataContratoAssinado)&&(
            <div style={{marginTop:16,padding:"12px 14px",borderRadius:8,background:"#f0f9ff",border:"1px solid #bfdbfe"}}>
              <p style={{fontSize:11,fontWeight:700,color:theme.blue,marginBottom:10}}>📅 Datas do Processo Comercial</p>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[{l:"Envio da Proposta",v:viewItem.dataEnvioProposta},{l:"Aprovação da Proposta",v:viewItem.dataAprovacaoProposta},{l:"Envio do Contrato",v:viewItem.dataEnvioContrato},{l:"Contrato Assinado",v:viewItem.dataContratoAssinado}].filter(d=>d.v).map(d=>(
                  <div key={d.l} style={{background:"#fff",padding:"8px 10px",borderRadius:6,border:"1px solid #bfdbfe"}}>
                    <p style={{fontSize:9,color:theme.blue,textTransform:"uppercase",letterSpacing:.8,margin:0}}>{d.l}</p>
                    <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:"2px 0 0"}}>{new Date(d.v+"T12:00").toLocaleDateString("pt-BR")}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Timeline de Estagio */}
          {(viewItem.historicoEstagios||[]).length>0&&(
            <div style={{marginTop:16}}>
              <p style={{fontSize:11,fontWeight:700,color:theme.gray700,marginBottom:10}}>📊 Histórico de Estágios</p>
              <div style={{position:"relative",paddingLeft:20}}>
                <div style={{position:"absolute",left:7,top:0,bottom:0,width:2,background:theme.gray200}}/>
                {(viewItem.historicoEstagios||[]).map((h2,hi)=>{
                  const est2=ESTAGIOS.find(e=>e.id===h2.estagio);
                  const prox2=(viewItem.historicoEstagios||[])[hi+1];
                  const dias2=prox2?Math.ceil((new Date(prox2.data+"T12:00")-new Date(h2.data+"T12:00"))/(1000*60*60*24)):null;
                  return(
                    <div key={h2.id||hi} style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:10}}>
                      <div style={{width:14,height:14,borderRadius:"50%",background:est2?.cor||theme.gray400,flexShrink:0,marginTop:2,border:"2px solid #fff",boxShadow:"0 0 0 2px "+(est2?.cor||theme.gray400)}}/>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <p style={{fontSize:12,fontWeight:700,color:theme.gray900,margin:0}}>{est2?.icon} {h2.label}</p>
                          <p style={{fontSize:11,color:theme.gray500,margin:0}}>{new Date(h2.data+"T12:00").toLocaleDateString("pt-BR")}</p>
                        </div>
                        {dias2!==null&&<p style={{fontSize:10,color:theme.gray400,margin:"2px 0 0"}}>⏱ {dias2} dia{dias2!==1?"s":""} nesta fase</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Modal Registrar Ocorrência */}
      {modalOcorr && viewItem && (
        <ModalOcorrencias
          entidade={viewItem}
          tipoEntidade="lead"
          tiposOcorrencia={tiposOcorrencia}
          equipe={equipe}
          onSave={(lista) => {
            setData(prev => ({ ...prev, leads: prev.leads.map(l => l.id === viewItem.id ? { ...l, ocorrencias: lista } : l) }));
            setViewItem(prev => prev ? { ...prev, ocorrencias: lista } : null);
          }}
          onSaveAgenda={evt=>setData(prev=>({...prev,agenda:[...(prev.agenda||[]),evt]}))}
          onClose={() => setModalOcorr(false)}
        />
      )}

      {/* Modal Novo Contato - busca clientes e leads */}
      {modalNovoContato&&(
        <Modal open={true} onClose={()=>setModalNovoContato(false)} title="Novo Contato — Verificar Cadastro" width={600}>
          <div style={{marginBottom:16}}>
            <p style={{fontSize:12,color:theme.gray600,marginBottom:12}}>Verifique se já existe cadastro antes de criar novo registro.</p>
            <Input value={buscaNovoContato} onChange={v=>setBuscaNovoContato(v)} placeholder="Buscar por nome, CNPJ ou e-mail..."/>
          </div>
          {buscaNovoContato.length>=2&&(()=>{
            const termo=buscaNovoContato.toLowerCase();
            const cliEnc=(clientes||[]).filter(cl=>(cl.nome||"").toLowerCase().includes(termo)||(cl.cpfCnpj||"").toLowerCase().includes(termo)||(cl.email||"").toLowerCase().includes(termo));
            const leadEnc=leads.filter(l=>(l.nome||"").toLowerCase().includes(termo)||(l.cpfCnpj||"").toLowerCase().includes(termo)||(l.email||"").toLowerCase().includes(termo));
            return(
              <div>
                {cliEnc.length>0&&(
                  <div style={{marginBottom:16}}>
                    <p style={{fontSize:11,fontWeight:700,color:theme.green,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>✅ Cliente(s) Ativo(s) Encontrado(s)</p>
                    {cliEnc.map(cl=>(
                      <div key={cl.id} style={{padding:"12px 14px",borderRadius:8,background:"#f0fdf4",border:"1px solid #bbf7d0",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:0}}>{cl.nome}</p>
                          <p style={{fontSize:11,color:theme.gray500,margin:"2px 0 0"}}>{cl.cpfCnpj?"CNPJ: "+cl.cpfCnpj+" · ":""}Cliente ativo</p>
                        </div>
                        <Btn onClick={()=>{setEditItem({...EMPTY,tipoCRM:"expansao",clienteVinculadoId:cl.id,clienteId:cl.id,nome:cl.nome,cpfCnpj:cl.cpfCnpj||"",email:cl.email||"",whatsapp:cl.whatsapp||"",whatsappDDI:cl.whatsappDDI||"55",estagio:"recepcao"});setModalNovoContato(false);setModalOpen(true);}}>+ Nova Oportunidade</Btn>
                      </div>
                    ))}
                  </div>
                )}
                {leadEnc.length>0&&(
                  <div style={{marginBottom:16}}>
                    <p style={{fontSize:11,fontWeight:700,color:theme.blue,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>🔄 Lead(s) Em Andamento</p>
                    {leadEnc.map(l=>(
                      <div key={l.id} style={{padding:"12px 14px",borderRadius:8,background:"#eff6ff",border:"1px solid #bfdbfe",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:0}}>{l.nome}</p>
                          <p style={{fontSize:11,color:theme.gray500,margin:"2px 0 0"}}>{(ESTAGIOS.find(e=>e.id===l.estagio)||{label:l.estagio}).label} · {(equipe.find(e=>e.id===l.responsavel)||{nome:"Sem resp."}).nome}</p>
                        </div>
                        <Btn variant="secondary" onClick={()=>{setViewItem(l);setModalNovoContato(false);}}>→ Abrir Lead</Btn>
                      </div>
                    ))}
                  </div>
                )}
                {cliEnc.length===0&&leadEnc.length===0&&(
                  <div style={{padding:"16px",textAlign:"center",borderRadius:8,background:theme.gray50,border:"1px solid "+theme.gray200}}>
                    <p style={{fontSize:13,color:theme.gray500,marginBottom:12}}>Nenhum cadastro encontrado para "{buscaNovoContato}"</p>
                    <Btn onClick={()=>{setEditItem({...EMPTY,nome:buscaNovoContato,tipoCRM:"lead",estagio:"lead"});setModalNovoContato(false);setModalOpen(true);}}>+ Cadastrar como Novo Lead</Btn>
                  </div>
                )}
              </div>
            );
          })()}
          {buscaNovoContato.length<2&&(
            <div style={{padding:"20px",textAlign:"center",borderRadius:8,background:theme.gray50}}><p style={{fontSize:12,color:theme.gray400}}>Digite pelo menos 2 caracteres para buscar</p></div>
          )}
          <div style={{display:"flex",justifyContent:"space-between",marginTop:16,paddingTop:16,borderTop:"1px solid "+theme.gray100}}>
            <Btn variant="secondary" onClick={()=>setModalNovoContato(false)}>Cancelar</Btn>
            <Btn onClick={()=>{setEditItem({...EMPTY,tipoCRM:"lead",estagio:"lead"});setModalNovoContato(false);setModalOpen(true);}}>+ Novo Lead (sem buscar)</Btn>
          </div>
        </Modal>
      )}

      {/* Modal Converter em Cliente */}
      {leadConv && (
        <Modal open={modalConv} onClose={()=>{setModalConv(false);setLeadConv(null);}} title="Converter Lead em Cliente" width={500}>
          <div style={{padding:20,background:`${theme.gold}0A`,border:`1px solid ${theme.gold}33`,borderRadius:10,marginBottom:20}}>
            <p className="serif" style={{fontSize:18,fontWeight:600,color:theme.gray900,marginBottom:4}}>{leadConv.nome}</p>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8}}>
              <Badge color={leadConv.tipo==="PJ"?"blue":"gray"}>{leadConv.tipo}</Badge>
              {leadConv.email && <span style={{fontSize:12,color:theme.gray600}}>{leadConv.email}</span>}
              {leadConv.whatsapp && <span style={{fontSize:12,color:theme.gray600}}>{leadConv.whatsapp}</span>}
            </div>
          </div>
          <p style={{fontSize:13,color:theme.gray700,marginBottom:8,lineHeight:1.7}}>
            Ao confirmar, este lead será convertido em <strong>cliente ativo</strong> no sistema com:
          </p>
          <ul style={{fontSize:13,color:theme.gray600,paddingLeft:20,lineHeight:2}}>
            <li>Dados cadastrais migrados automaticamente</li>
            <li>Produtos de interesse vinculados</li>
            <li>Origem registrada no histórico</li>
            <li>Status do lead atualizado para <strong>Fechado ✓</strong></li>
          </ul>
          <p style={{fontSize:12,color:theme.amber,marginTop:12}}>
            ⚠ Após a conversão, acesse o módulo <strong>Clientes</strong> para registrar o contrato e lançar os honorários no financeiro.
          </p>
          <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:20,borderTop:`1px solid ${theme.gray100}`,marginTop:20}}>
            <Btn variant="secondary" onClick={()=>{setModalConv(false);setLeadConv(null);}}>Cancelar</Btn>
            <Btn onClick={converterCliente}>Confirmar Conversão</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}




// ============================================================
// MÓDULO: RCT TRIBUTÁRIO
// ============================================================

const PRODUTOS_RCT_PADRAO = [
  {
    id: "RCT_CMO", nome: "INSS sobre Cessão de Mão de Obra (PJ – Restituição)", campos: "cmo",
    baseLegal: "Art. 31 da Lei 8.212/1991; IN RFB 2.110/2022",
    status2026: "Procedimento administrativo via PER/DCOMP; a IN 2.289/2025 consolidou as hipóteses de dispensa para aumentar a segurança jurídica.",
    resumo: "Recuperação de créditos de INSS retidos por tomadores de serviço em favor da empresa prestadora.",
    descricaoRelatorio: "Recuperação de saldos de retenção de 11% de INSS em notas fiscais de serviço não compensados na folha mensal. Procedimento via PER/DCOMP com base no Art. 31 da Lei 8.212/1991 e IN RFB 2.110/2022."
  },
  {
    id: "RCT_VERBAS", nome: "Verbas Indenizatórias (Folha de Pagamento)", campos: "verbas",
    baseLegal: "Tema 72 do STF; Tema 163 do STF; SC COSIT 109/2025",
    status2026: "Tese pacificada para a maioria das verbas (Aviso Prévio, 15 dias doença); novas vitórias judiciais sobre Horas Extras.",
    resumo: "Redução imediata do custo mensal da folha e recuperação dos últimos 5 anos.",
    descricaoRelatorio: "Exclusão da base de INSS Patronal (20%), SAT e Terceiros de verbas sem natureza remuneratória. Tese pacificada no STF — Temas 72 e 163. Recuperação retroativa dos últimos 60 meses."
  },
  {
    id: "RCT_PIS", nome: "PIS/COFINS Monofásicos", campos: "perdcomp",
    baseLegal: "Lei Complementar 123/2006 (Art. 18); Tema 1.125 do STJ; SC COSIT 100/2025",
    status2026: "Direito administrativo consolidado para varejistas de autopeças, bebidas, fármacos e cosméticos.",
    resumo: "Dinheiro no caixa em até 60 dias via retificação administrativa de declarações.",
    descricaoRelatorio: "Segregação de produtos tributados antecipadamente (regime monofásico) para evitar bi-tributação no Simples Nacional. Retificação administrativa de declarações com recuperação imediata dos créditos."
  },
  {
    id: "RCT_ICMSST", nome: "ICMS-ST (Substituição Tributária)", campos: "perdcomp",
    baseLegal: "Tema Repetitivo 1.125 do STJ; SC COSIT 100/2025",
    status2026: "Consolidado pelo STJ e reconhecido pela PGFN; possibilita recuperação administrativa sem necessidade de processo judicial.",
    resumo: "Recuperação de impostos federais para empresas que atuam em setores com Substituição Tributária de ICMS.",
    descricaoRelatorio: "Recuperação do ICMS-ST pago antecipadamente por contribuintes substituídos que revendem mercadorias com imposto retido na fonte. Base no Tema 1.125 do STJ — recuperação 100% administrativa."
  },
  {
    id: "RCT_IRCSLL_CC", nome: "IR/CSLL – Construção Civil (Empreitada)", campos: "ircsll_cc",
    baseLegal: "Art. 15, §1º, III, 'a' da Lei 9.249/1995; Extensão analógica do Tema 217 do STJ",
    status2026: "Em alta devido à expansão da infraestrutura de saúde; exige análise rigorosa do contrato de empreitada.",
    resumo: "Otimização tributária para construtoras com atuação no setor de saúde.",
    descricaoRelatorio: "Aplicação da base de cálculo reduzida de IRPJ e CSLL para empresas de construção civil com contratos de empreitada global (fornecimento de material + mão de obra). Recuperação retroativa dos últimos 60 meses."
  },
  {
    id: "RCT_IRCSLL_SD", nome: "IR/CSLL – Equiparação Hospitalar (Clínicas)", campos: "ircsll_sd",
    baseLegal: "Art. 15 da Lei 9.249/1995; Tema Repetitivo 217 do STJ",
    status2026: "Consolidado pelo STJ (Jan/2026): benefício independe de internação, foca na natureza hospitalar do serviço.",
    resumo: "Redução definitiva de até 60% na carga de impostos federais para centros de diagnóstico e clínicas cirúrgicas.",
    descricaoRelatorio: "Redução da base de presunção do IRPJ (de 32% para 8%) e CSLL (de 32% para 12%) para clínicas médicas equiparadas a hospitais, enquadradas no Lucro Presumido. Tese consolidada no STJ — Tema 217."
  },
  {
    id: "RCT_DIVIDA", nome: "Situação Fiscal e Gestão de Dívida Tributária", campos: "divida",
    baseLegal: "Lei 13.988/2020; IN RFB 2.110/2022",
    status2026: "Foco em Programas de Transação Integral (PTI) e editais específicos da PGFN com descontos baseados na Capag.",
    resumo: "Gestão de parcelamentos e defesas em execuções fiscais para preservar o patrimônio da empresa.",
    descricaoRelatorio: "Saneamento do passivo fiscal para emissão de CND e manutenção da regularidade operacional. Gestão estratégica via Programas de Transação Integral (PTI) com descontos de até 65% sobre encargos."
  },
];

const VIABILIDADE_OPTS = ["Em análise","Sim","Não","Não se aplica"];
const STATUS_ANALISE   = ["Pendente","Em andamento","Concluída","Homologada","N/A"];
const STATUS_DIAG      = ["Em andamento","Aguardando Documentação","Aguardando Procuração","Concluído","Concluído - Aguardando Retorno","Cancelado","Suspenso","Em Execução"];
const REGIMES          = ["Simples Nacional","Lucro Presumido","Lucro Real","MEI"];

// Gerar 60 meses anteriores ao mês atual
// Status PERDCOMP editáveis (conforme norma SRFB — editável pelo escritório)
const STATUS_PERDCOMP_DEFAULT = [
  "Análise concluída","Análise preliminar disponibilizada","Cancelado",
  "Contribuinte intimado","Despacho decisório emitido","Em análise",
  "Em discussão administrativa – CARF","Homologado","Não admitido",
  "Pedido de cancelamento deferido","PER deferido","Retificado",
  "Notificação para compensação de ofício",
];

const gerar60Meses = () => {
  const meses = [];
  const hoje = new Date();
  for (let i = 59; i >= 0; i--) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const comp = d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0");
    meses.push({
      comp,
      saldoApuradoInss: "",        // col 2 — manual
      creditoEcacSrfb: "",         // col 3 — manual
      // col 4 auditoria = (3)-(2) → automático
      perdcompEscritorio: "",      // col 5 — manual
      // col 6 saldo = (3)-(5) → automático
      saldoPerdcompAtualizado: "", // col 7 — manual
      numeroPerdcomp: "",          // col 8 — manual
      dataPerdcomp: "",            // col 9 — manual
      ecacOcorrencias: [],         // ocorrências E-CAC vinculadas a esta competência/PERDCOMP
    });
  }
  return meses;
};

const EMPTY_PRODUTO = (id, nome, campos) => ({
  produtoId: id, nome, campos,
  viabilidade: "Em análise", creditoApurado: "",
  // campos PERDCOMP comuns (PIS, ICMS-ST, e também CMO no nível consolidado)
  sdoPerdcomp: "", sdoPerdcompRest: "", sdoPerdcompComp: "",
  perdcomp: "", perdcompData: "", perdcompStatus: "",
  sdoMais360Rest: "", sdoMais360Comp: "", sdoMais360MS: "",
  // CMO: bloco 60 meses (gerado automaticamente)
  competencias: campos === "cmo" ? gerar60Meses() : [],
  statusPerdcompOpts: campos === "cmo" ? [...STATUS_PERDCOMP_DEFAULT] : [],
  // IR/CSLL CC
  aliquotaIR: "", aliquotaCSLL: "", classReceita: "", empreitadaGlobal: false,
  tipoEmpreitada: "",
  // IR/CSLL Saúde
  enquadramentoHospitalar: false, aliquotaIRSaude: "", aliquotaCSLLSaude: "",
  // Documentos: [{docId, nome, obrigatorio, recebido, dataRecebimento, obs}]
  documentosRecebidos: [],
  // Dívida
  srfbValor: "", srfbStatus: "", pgfnValor: "", pgfnStatus: "",
  statusDivida: "", prescricao: "", transacao: "",
  statusAnalise: "Pendente", obs: "",
  statusApuracao: "Em Apuração",  // "Em Apuração" | "Concluído"
});

function ModRCT({ data, setData }) {
  const isViz = useIsVisualizador();
  const { diagnosticosRCT, clientes, produtos, equipe, parcerias, leads } = data;
  // Lista unificada: clientes ativos + leads do CRM
  const clientesELeads = [
    ...clientes.filter(c => c.ativo).map(c => ({ id: c.id, nome: c.nome, cpfCnpj: c.cpfCnpj, tipo: "cliente" })),
    ...(leads || []).map(l => ({ id: l.id, nome: l.nome + " (Lead)", cpfCnpj: l.cpfCnpj, tipo: "lead" }))
  ];
  const nomeClienteOuLead = id => clientesELeads.find(x => x.id === id)?.nome || id;
  const [modalOpen, setModalOpen]     = useState(false);
  const [editItem, setEditItem]       = useState(null);
  const [viewItem, setViewItem]       = useState(null);
  const [abaView, setAbaView]         = useState("resumo");
  const [showAprovSemContrato, setShowAprovSemContrato] = useState(false);
  const [previewPDFDocs, setPreviewPDFDocs]             = useState(null); // html do PDF para prévia
  const [prodAba, setProdAba]         = useState(0);
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroResp, setFiltroResp]     = useState("");
  const [busca, setBusca]               = useState("");
  const [relatorioItem, setRelatorioItem] = useState(null);
  const [abaCMO, setAbaCMO]             = useState("consolidado");
  const [mesPag, setMesPag]             = useState(0); // paginação 60 meses (12 por página)
  const [ecacModal, setEcacModal]       = useState(null); // { prodIdx, compIdx, comp, numeroPerdcomp }

  // Produtos disponíveis: padrão + tributários cadastrados não duplicados
  const prodsTributarios = produtos.filter(p => p.area === "Tributário" && p.status === "Ativo");

  // Mapeamento de nome → template de execução (para produtos sem campoRCT definido)
  const CAMPOS_POR_NOME = {
    "INSS sobre Cessão de Mão de Obra (PJ – Restituição)": "cmo",
    "Verbas Indenizatórias (Folha de Pagamento)":          "verbas",
    "IR/CSLL – Construção Civil (Empreitada)":             "ircsll_cc",
    "IR/CSLL – Equiparação Hospitalar (Clínicas)":         "ircsll_sd",
    "Situação Fiscal e Gestão de Dívida Tributária":        "divida",
  };

  // produtosRCT = catálogo tributário ordenado por nome
  // campos: usa campoRCT do produto, depois mapeamento por nome, depois "perdcomp"
  const produtosRCT = prodsTributarios
    .map(p => ({
      id:     p.id,
      nome:   p.nome,
      campos: p.campoRCT || CAMPOS_POR_NOME[p.nome] || "perdcomp",
      // preservar dados do PRODUTOS_RCT_PADRAO para fallback de diagnósticos antigos
      _baseLegal:          PRODUTOS_RCT_PADRAO.find(rp=>rp.nome===p.nome)?.baseLegal || "",
      _resumo:             PRODUTOS_RCT_PADRAO.find(rp=>rp.nome===p.nome)?.resumo || p.resumo || "",
      _descricaoRelatorio: PRODUTOS_RCT_PADRAO.find(rp=>rp.nome===p.nome)?.descricaoRelatorio || "",
    }))
    .sort((a,b)=>a.nome.localeCompare(b.nome,"pt-BR"));

  // Fallback: diagnósticos antigos com RCT_CMO etc. → mapear para id do catálogo
  const FALLBACK_IDS = {
    "RCT_CMO":      "INSS sobre Cessão de Mão de Obra (PJ – Restituição)",
    "RCT_VERBAS":   "Verbas Indenizatórias (Folha de Pagamento)",
    "RCT_PIS":      "PIS/COFINS Monofásicos",
    "RCT_ICMSST":   "ICMS-ST (Subst. Trib.)",
    "RCT_IRCSLL_CC":"IR/CSLL – Construção Civil (Empreitada)",
    "RCT_IRCSLL_SD":"IR/CSLL – Equiparação Hospitalar (Clínicas)",
    "RCT_DIVIDA":   "Situação Fiscal e Gestão de Dívida Tributária",
  };

  const EMPTY_DIAG = {
    id: "", clienteId: "", parceiraId: "", responsavel: "", supervisor: "",
    cnpj: "", faturamentoAnual: "", faturamentos: [], regime: "", atividade: "", // faturamentoAnual mantido retrocompat
    regimes: [],           // novo: array de {id,tipo,dataInicio,dataFim}
    atividadePrincipal: "",
    atividadesSecundarias: [],
    dataInicio: new Date().toISOString().slice(0,10), dataFim: "",
    status: "Em andamento", dataRelatorio: "", dataRelatorioDocumentos: "", dataPrevistaConclusao: "", honorarios: "", obs: "",
    dataConclusao: "",
    statusAprovacao: "Pendente de Aprovação",
    dataAprovacao: "",
    dataContratoAssinado: "",
    ecacOcorrencias: [],
    produtos: produtosRCT.map(p => EMPTY_PRODUTO(p.id, p.nome, p.campos))
  };

  const openNew = () => {
    // Criar diagnóstico com documentos pré-populados por produto
    const diagNovo = {...EMPTY_DIAG};
    diagNovo.produtos = produtosRCT.map(p => {
      const novoProd = {...EMPTY_PRODUTO(p.id, p.nome, p.campos)};
      const prodCat = produtos.find(pr=>pr.id===p.id||pr.nome===p.nome);
      novoProd.documentosRecebidos = popularDocumentos(prodCat, []);
      return novoProd;
    });
    setEditItem(diagNovo);
    setProdAba(0);
    setAbaCMO("consolidado");
    setModalOpen(true);
  };
  // Helper: pré-popular documentos de um produto a partir do catálogo
  const popularDocumentos = (produtoCatalogo, docsExistentes) => {
    // documentosPadrao ou trib_documentos (campo usado no ModProdutos)
    const docsPadrao = (produtoCatalogo?.documentosPadrao||[]).length > 0
      ? produtoCatalogo.documentosPadrao
      : (produtoCatalogo?.trib_documentos || []);
    if(docsPadrao.length === 0) return docsExistentes || [];
    // Manter docs já existentes, adicionar novos que não estejam
    const base = docsExistentes || [];
    const novos = docsPadrao
      .filter(docId => !base.find(d => d.docId === docId))
      .map(docId => {
        const docMestre = (data.documentosTrib||[]).find(d=>d.id===docId);
        return {
          docId,
          nome:            docMestre?.nome || docId,
          obrigatorio:     true,
          recebido:        false,
          dataRecebimento: "",
          obs:             ""
        };
      });
    // Manter apenas docs que ainda existem no catálogo do produto
    // Remove excluídos, preserva status recebido dos que permanecem, atualiza nomes
    const baseAtualizada = base
      .filter(d => docsPadrao.includes(d.docId))
      .map(d => {
        const docMestre = (data.documentosTrib||[]).find(dm=>dm.id===d.docId);
        return docMestre ? {...d, nome: docMestre.nome} : d;
      });
    return [...baseAtualizada, ...novos];
  };

  const openEdit = d => {
    const prodsExist = d.produtos || [];
    const prodsCompletos = produtosRCT.map(p => {
      // Buscar por id direto OU por fallback de IDs antigos OU por nome
      const exist = prodsExist.find(pe =>
        pe.produtoId === p.id ||
        (FALLBACK_IDS[pe.produtoId] && FALLBACK_IDS[pe.produtoId]===p.nome) ||
        pe.nome === p.nome
      );
      if (exist) {
        const updated = {
          ...exist,
          produtoId: p.id,       // atualizar id (fallback retrocompat)
          nome: p.nome,          // ← sincronizar nome do catálogo
          campos: p.campos,      // ← sincronizar template do catálogo
        };
        if (p.campos === "cmo" && (!updated.competencias || updated.competencias.length === 0)) {
          return { ...updated, competencias: gerar60Meses() };
        }
        // Sincronizar documentos com catálogo (adiciona novos, mantém recebidos)
        const prodCat = produtos.find(pr=>pr.id===p.id||pr.nome===p.nome);
        const docsAtt = popularDocumentos(prodCat, updated.documentosRecebidos||[]);
        return {...updated, documentosRecebidos: docsAtt};
      }
      // Novo produto — pré-popular documentos
      const prodCat = produtos.find(pr=>pr.id===p.id||pr.nome===p.nome);
      const novoProd = EMPTY_PRODUTO(p.id, p.nome, p.campos);
      return {...novoProd, documentosRecebidos: popularDocumentos(prodCat, [])};
    });
    setEditItem({ ...d, produtos: prodsCompletos, ecacOcorrencias: d.ecacOcorrencias || [] });
    setProdAba(0); setAbaCMO("consolidado"); setModalOpen(true);
  };

  // openView: sincroniza documentos antes de abrir o viewItem
  const openView = (d, aba="resumo") => {
    const prodsExist = d.produtos || [];
    const prodsSinc = produtosRCT.map(p => {
      const exist = prodsExist.find(pe =>
        pe.produtoId === p.id ||
        (FALLBACK_IDS[pe.produtoId] && FALLBACK_IDS[pe.produtoId]===p.nome) ||
        pe.nome === p.nome
      );
      if(exist) {
        const prodCat = produtos.find(pr=>pr.id===p.id||pr.nome===p.nome);
        const docsAtt = popularDocumentos(prodCat, exist.documentosRecebidos||[]);
        return {
          ...exist,
          produtoId: p.id,   // atualizar id
          nome: p.nome,      // ← sincronizar nome do catálogo
          campos: p.campos,  // ← sincronizar template do catálogo
          documentosRecebidos: docsAtt
        };
      }
      const prodCat = produtos.find(pr=>pr.id===p.id||pr.nome===p.nome);
      return {...EMPTY_PRODUTO(p.id, p.nome, p.campos), documentosRecebidos: popularDocumentos(prodCat, [])};
    });
    setViewItem({...d, produtos: prodsSinc});
    setAbaView(aba);
  };

  const save = () => {
    if (!editItem.clienteId) return alert("Selecione o cliente.");
    const id   = editItem.id || "RCT" + String(Date.now()).slice(-6);
    const item = { ...editItem, id };
    setData(prev => { const lista = editItem.id ? prev.diagnosticosRCT.map(d => d.id === id ? item : d) : [...prev.diagnosticosRCT, item]; return {...prev, diagnosticosRCT: lista}; });
    setModalOpen(false);
  };

  const remove = id => {
    if (!confirm("Remover diagnóstico RCT?")) return;
    setData(prev => ({...prev, diagnosticosRCT: diagnosticosRCT.filter(d => d.id !== id)}));
  };

  const updProd = (i, field, val) => {
    const prods = editItem.produtos.map((p, idx) => idx === i ? {...p, [field]: val} : p);
    setEditItem({...editItem, produtos: prods});
  };

  const updComp = (prodIdx, compIdx, field, val) => {
    const prods = editItem.produtos.map((p, pi) => {
      if (pi !== prodIdx) return p;
      const comps = p.competencias.map((c, ci) => ci === compIdx ? {...c, [field]: val} : c);
      return {...p, competencias: comps};
    });
    setEditItem({...editItem, produtos: prods});
  };

  const addEcac = () => {
    const nova = { id: Date.now(), data: new Date().toISOString().slice(0,10), ocorrencia: "", obs: "", valor: "" };
    setEditItem({...editItem, ecacOcorrencias: [...(editItem.ecacOcorrencias||[]), nova]});
  };

  const updEcac = (idx, field, val) => {
    const lista = editItem.ecacOcorrencias.map((e,i) => i===idx ? {...e,[field]:val} : e);
    setEditItem({...editItem, ecacOcorrencias: lista});
  };

  const remEcac = idx => {
    setEditItem({...editItem, ecacOcorrencias: editItem.ecacOcorrencias.filter((_,i)=>i!==idx)});
  };

  const filtrados = diagnosticosRCT.filter(d => {
    const cli = clientesELeads.find(x => x.id === d.clienteId);
    const mB  = !busca || (cli?.nome||"").toLowerCase().includes(busca.toLowerCase()) || (d.cnpj||"").includes(busca);
    const mS  = !filtroStatus || d.status === filtroStatus;
    const mR  = !filtroResp   || d.responsavel === filtroResp;
    return mB && mS && mR;
  }).sort((a,b)=>{
    const na = clientesELeads.find(x=>x.id===a.clienteId)?.nome||"";
    const nb = clientesELeads.find(x=>x.id===b.clienteId)?.nome||"";
    return na.localeCompare(nb,"pt-BR");
  });

  // KPIs
  const totalCredito = diagnosticosRCT.reduce((s,d) =>
    s + (d.produtos||[]).reduce((ps,p) => ps + (parseFloat(p.creditoApurado)||0), 0), 0);
  const emAndamento       = diagnosticosRCT.filter(d => ["Em andamento","Em Execução"].includes(d.status)).length;
  const concluidos        = diagnosticosRCT.filter(d => d.status?.startsWith("Concluído")).length;
  // ── Função: calcular 2º dia útil após uma data ──
  const FERIADOS_NACIONAIS = ["01-01","21-04","01-05","07-09","12-10","02-11","15-11","25-12"];
  const ehDiaUtil = (d) => {
    const dow = d.getDay(); // 0=dom, 6=sab
    if(dow===0||dow===6) return false;
    const mmdd = String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0");
    return !FERIADOS_NACIONAIS.includes(mmdd);
  };
  const calcSegundoDiaUtil = (dataStr) => {
    if(!dataStr) return null;
    const d = new Date(dataStr+"T12:00:00");
    let uteis = 0;
    let tentativas = 0;
    while(uteis < 2 && tentativas < 30) {
      d.setDate(d.getDate()+1);
      if(ehDiaUtil(d)) uteis++;
      tentativas++;
    }
    return d.toISOString().slice(0,10);
  };

  // ── Diagnósticos com relatório de documentos pendente ──
  const hoje3 = new Date(); hoje3.setHours(0,0,0,0);
  // ── Diagnósticos concluídos sem relatório de diagnóstico enviado ──
  const relDiagPendentes = diagnosticosRCT.filter(d => {
    if(!d.dataConclusao) return false; // só conta após conclusão
    if(["Cancelado","Não Aprovado"].some(s=>d.status===s)) return false;
    if(d.dataRelatorio) return false; // já enviou
    return true;
  }).map(d => {
    const diffDias = Math.ceil((hoje3 - new Date(d.dataConclusao+"T12:00"))/(1000*60*60*24));
    return {...d, diasSemRelatorio: Math.max(0, diffDias)};
  }).sort((a,b)=>b.diasSemRelatorio-a.diasSemRelatorio);

  const relDocsPendentes = diagnosticosRCT.filter(d => {
    // Só para diagnósticos Em andamento com data de início
    if(!d.dataInicio) return false;
    if(["Concluído","Cancelado","Não Aprovado"].some(s=>d.status?.startsWith(s)||d.status===s)) return false;
    // Já enviou o relatório de documentos → não alerta
    if(d.dataRelatorioDocumentos) return false;
    return true;
  }).map(d => {
    const prazo = calcSegundoDiaUtil(d.dataInicio);
    const diff  = prazo ? Math.ceil((new Date(prazo+"T12:00")-hoje3)/(1000*60*60*24)) : null;
    return {...d, prazoRelDocs: prazo, diffRelDocs: diff};
  });
  const relDocsVencidos  = relDocsPendentes.filter(d=>d.diffRelDocs!==null&&d.diffRelDocs<0);
  const relDocsHoje      = relDocsPendentes.filter(d=>d.diffRelDocs===0);
  const relDocsFuturos   = relDocsPendentes.filter(d=>d.diffRelDocs!==null&&d.diffRelDocs>0);

  const aprovados             = diagnosticosRCT.filter(d => d.statusAprovacao==="Aprovado").length;
  const contratoAssinado      = diagnosticosRCT.filter(d => d.statusAprovacao==="Contrato Assinado").length;
  const naoAprovados          = diagnosticosRCT.filter(d => d.statusAprovacao==="Não Aprovado").length;
  const listAprovSemContrato  = diagnosticosRCT.filter(d => d.statusAprovacao==="Aprovado");

  const statusColor = s => {
    if (s?.startsWith("Concluído")) return "green";
    if (s === "Em andamento" || s === "Em Execução") return "blue";
    if (s === "Aguardando Documentação") return "amber";
    if (s === "Aguardando Procuração")   return "amber";
    if (s === "Cancelado")  return "red";
    if (s === "Suspenso")   return "amber";
    return "gray";
  };
  const aprov_COR = {
    "Pendente de Aprovação": {bg:"#fef3c7", color:"#92400e", dot:"#d97706"},
    "Aprovado":              {bg:"#dcfce7", color:"#166534", dot:"#22c55e"},
    "Contrato Assinado":     {bg:"#dbeafe", color:"#1e40af", dot:"#3b82f6"},
    "Não Aprovado":          {bg:"#fee2e2", color:"#991b1b", dot:"#ef4444"},
  };
  const viabColor = v => v==="Sim"?theme.green:v==="Não"?theme.red:v==="Não se aplica"?theme.gray400:theme.amber;
  const fmtR = v => v && parseFloat(v) ? "R$ "+parseFloat(v).toLocaleString("pt-BR",{minimumFractionDigits:2}) : "-";

  // ─────────────────────────────────────────────────────────
  // RENDERIZAÇÃO DO FORMULÁRIO DE PRODUTO
  // ─────────────────────────────────────────────────────────
  const renderFormProduto = () => {
    if (!editItem?.produtos?.[prodAba]) return null;
    const p   = editItem.produtos[prodAba];
    const up  = (f,v) => updProd(prodAba, f, v);
    const campos = p.campos;

    return (
      <div style={{background:theme.gray50, borderRadius:10, padding:20}}>
        {/* Viabilidade + Crédito — sempre visível */}
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 20px"}}>
          <FormField label="Viabilidade">
            <Select value={p.viabilidade} onChange={v=>up("viabilidade",v)} options={VIABILIDADE_OPTS}/>
          </FormField>
          <FormField label="Status de Apuração">
            <Select value={p.statusApuracao||"Em Apuração"}
              onChange={v=>up("statusApuracao",v)}
              options={["Em Apuração","Concluído","Pendência de Documento"]}/>
          </FormField>
          <FormField label="Crédito Apurado (R$)">
            <Input type="number" value={p.creditoApurado} onChange={v=>up("creditoApurado",v)} placeholder="0,00"/>
          </FormField>
        </div>

        {/* ── Documentos Necessários — só quando Viabilidade = Sim ── */}
        {p.viabilidade==="Sim"&&(()=>{
          const docsP=p.documentosRecebidos||[];
          const recebP=docsP.filter(d=>d.recebido).length;
          const pendP=docsP.length-recebP;
          return(
            <div style={{marginTop:14,borderRadius:8,border:`1px solid ${theme.gray200}`,overflow:"hidden"}}>
              <div style={{padding:"10px 14px",background:theme.gray50,display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:`1px solid ${theme.gray200}`}}>
                <p style={{fontSize:12,fontWeight:700,color:theme.gray800,margin:0}}>📋 Documentos Necessários</p>
                <div style={{display:"flex",gap:6}}>
                  {docsP.length>0&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:"#dcfce7",color:theme.green,fontWeight:600}}>✅ {recebP} recebido{recebP!==1?"s":""}</span>}
                  {pendP>0&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:"#fef3c7",color:"#92400e",fontWeight:600}}>⏳ {pendP} pendente{pendP!==1?"s":""}</span>}
                </div>
              </div>
              {docsP.length===0
                ?<div style={{padding:"14px",textAlign:"center"}}>
                    <p style={{fontSize:11,color:theme.gray400,fontStyle:"italic",margin:0}}>Nenhum documento cadastrado para este produto.</p>
                    <p style={{fontSize:10,color:theme.gray400,margin:"4px 0 0"}}>Cadastre em: Produtos → editar → Documentos</p>
                  </div>
                :docsP.map((doc,di)=>(
                    <div key={doc.docId||di} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:di<docsP.length-1?`1px solid ${theme.gray100}`:"none",background:doc.recebido?"#f0fdf4":theme.white}}>
                      <input type="checkbox" checked={!!doc.recebido}
                        onChange={e=>{const nds=[...docsP];nds[di]={...doc,recebido:e.target.checked,dataRecebimento:e.target.checked?(new Date().toISOString().slice(0,10)):""};up("documentosRecebidos",nds);}}
                        style={{width:16,height:16,accentColor:theme.green,cursor:"pointer",flexShrink:0}}/>
                      <span style={{fontSize:12,flex:1,color:doc.recebido?theme.gray500:theme.gray900,textDecoration:doc.recebido?"line-through":"none",fontWeight:doc.recebido?400:500}}>
                        {doc.nome}
                        {doc.obrigatorio&&!doc.recebido&&<span style={{fontSize:9,color:theme.red,marginLeft:4,fontWeight:700}}>*obrigatório</span>}
                      </span>
                      {doc.recebido&&(
                        <div style={{display:"flex",alignItems:"center",gap:5}}>
                          <span style={{fontSize:10,color:theme.gray400}}>em:</span>
                          <input type="date" value={doc.dataRecebimento||""}
                            onChange={e=>{const nds=[...docsP];nds[di]={...doc,dataRecebimento:e.target.value};up("documentosRecebidos",nds);}}
                            style={{fontSize:11,padding:"3px 6px",border:`1px solid ${theme.gray200}`,borderRadius:5,color:theme.green,background:"#f0fdf4"}}/>
                        </div>
                      )}
                      <span style={{fontSize:10,padding:"3px 8px",borderRadius:8,fontWeight:600,whiteSpace:"nowrap",background:doc.recebido?"#dcfce7":"#fef3c7",color:doc.recebido?theme.green:"#92400e"}}>
                        {doc.recebido?"✅ Recebido":"⏳ Pendente"}
                      </span>
                    </div>
                  ))
              }
            </div>
          );
        })()}




        {/* ── CMO ───────────────────────────────────────── */}
        {campos === "cmo" && p.viabilidade === "Sim" && (
          <div style={{marginTop:16}}>
            {/* Sub-abas CMO */}
            <div style={{display:"flex", gap:4, borderBottom:`1px solid ${theme.gray200}`, marginBottom:16}}>
              {[["consolidado","Consolidado"],["competencias","Competências (60 meses)"]].map(([v,l])=>(
                <button key={v} onClick={()=>setAbaCMO(v)} style={{
                  padding:"7px 14px", border:"none", background:"none", cursor:"pointer",
                  fontSize:11, fontWeight:abaCMO===v?700:400,
                  color:abaCMO===v?theme.gray900:theme.gray400,
                  borderBottom:abaCMO===v?`2px solid ${theme.gold}`:"2px solid transparent",
                  marginBottom:-1
                }}>{l}</button>
              ))}
            </div>

            {/* Consolidado CMO */}
            {abaCMO === "consolidado" && (
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 20px"}}>
                <FormField label="REST — Restituição (R$)">
                  <Input type="number" value={p.sdoPerdcompRest} onChange={v=>up("sdoPerdcompRest",v)} placeholder="0,00"/>
                </FormField>
                <FormField label="COMP — Compensação (R$)">
                  <Input type="number" value={p.sdoPerdcompComp} onChange={v=>up("sdoPerdcompComp",v)} placeholder="0,00"/>
                </FormField>
                <FormField label="PERDCOMP — Número / Referência">
                  <Input value={p.perdcomp} onChange={v=>up("perdcomp",v)} placeholder="Ex: 1.23456.78-9"/>
                </FormField>
                <FormField label="Data PERDCOMP">
                  <Input type="date" value={p.perdcompData||""} onChange={v=>up("perdcompData",v)}/>
                </FormField>
                <FormField label="Status PERDCOMP">
                  <Select value={p.perdcompStatus} onChange={v=>up("perdcompStatus",v)}
                    options={p.statusPerdcompOpts||STATUS_PERDCOMP_DEFAULT} placeholder="Selecione..."/>
                </FormField>
                <FormField label="SDO PERDCOMP Acumulado (R$)">
                  <Input type="number" value={p.sdoPerdcomp} onChange={v=>up("sdoPerdcomp",v)} placeholder="0,00"/>
                </FormField>
                <div style={{gridColumn:"1/-1"}}>
                  <p style={{fontSize:10,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,margin:"12px 0 10px"}}>SDO PERDCOMP com +360 dias</p>
                </div>
                <FormField label="Restituição +360d (R$)">
                  <Input type="number" value={p.sdoMais360Rest} onChange={v=>up("sdoMais360Rest",v)} placeholder="0,00"/>
                </FormField>
                <FormField label="Compensação +360d (R$)">
                  <Input type="number" value={p.sdoMais360Comp} onChange={v=>up("sdoMais360Comp",v)} placeholder="0,00"/>
                </FormField>
                <FormField label="MS — Mandado de Segurança (R$)">
                  <Input type="number" value={p.sdoMais360MS} onChange={v=>up("sdoMais360MS",v)} placeholder="0,00"/>
                </FormField>
                <FormField label="Status da Análise">
                  <Select value={p.statusAnalise} onChange={v=>up("statusAnalise",v)} options={STATUS_ANALISE}/>
                </FormField>
                <div style={{gridColumn:"1/-1"}}>
                  <FormField label="Observação técnica">
                    <Textarea value={p.obs} onChange={v=>up("obs",v)} placeholder="Notas técnicas..."/>
                  </FormField>
                </div>
              </div>
            )}



            {/* Competências 60 meses — geradas automaticamente, editáveis */}
            {abaCMO === "competencias" && (
              <div>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
                  <div>
                    <p style={{fontSize:12, color:theme.gray600, fontWeight:500}}>
                      60 competências geradas automaticamente · {mesPag*12+1}–{Math.min(mesPag*12+12, 60)} de 60
                    </p>
                    <p style={{fontSize:10, color:theme.gray400, marginTop:2}}>
                      Colunas 4 (Auditoria) e 6 (Saldo) calculadas automaticamente · vermelho = negativo
                    </p>
                  </div>
                  <div style={{display:"flex", gap:6}}>
                    <Btn size="sm" variant="secondary" disabled={mesPag===0} onClick={()=>setMesPag(m=>m-1)}>◀ Anterior</Btn>
                    <Btn size="sm" variant="secondary" disabled={(mesPag+1)*12>=60} onClick={()=>setMesPag(m=>m+1)}>Próximo ▶</Btn>
                  </div>
                </div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%", borderCollapse:"collapse", fontSize:11}}>
                    <thead>
                      <tr style={{background:theme.gray800, color:theme.white}}>
                        {[
                          {l:"Comp.",             tip:"Competência (auto)",                  w:64,  auto:false},
                          {l:"2 · Saldo INSS",    tip:"Saldo Apurado INSS Retido CMO",      w:100, auto:false},
                          {l:"3 · Crédito e-CAC", tip:"Crédito apresentado no e-CAC SRFB",  w:110, auto:false},
                          {l:"4 · Auditoria ⚡",  tip:"(3)-(2) — auto",                     w:100, auto:true},
                          {l:"5 · PERDCOMP",      tip:"PERDCOMP realizada pelo escritório",  w:100, auto:false},
                          {l:"6 · Saldo ⚡",      tip:"(3)-(5) — auto",                     w:90,  auto:true},
                          {l:"7 · SDO Atualiz.",  tip:"Saldo PERDCOMP Atualizado",           w:100, auto:false},
                          {l:"8 · Nº PERDCOMP",   tip:"Número da PERDCOMP",                 w:120, auto:false},
                          {l:"9 · Data PERDCOMP", tip:"Data da PERDCOMP",                   w:110, auto:false},
                          {l:"E-CAC",             tip:"Ocorrências E-CAC desta competência", w:70,  auto:false},
                        ].map(h=>(
                          <th key={h.l} title={h.tip} style={{
                            padding:"7px 8px", fontWeight:600, whiteSpace:"nowrap", fontSize:9,
                            letterSpacing:0.3, borderRight:`1px solid ${theme.gray700}`,
                            minWidth:h.w, background: h.auto ? `${theme.blue}CC` : h.l==="E-CAC" ? `${theme.gold}BB` : theme.gray800,
                            cursor:"help"
                          }}>{h.l}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(p.competencias||[]).slice(mesPag*12, mesPag*12+12).map((c,ci) => {
                        const realIdx = mesPag*12+ci;
                        const auditoria = (parseFloat(c.creditoEcacSrfb)||0) - (parseFloat(c.saldoApuradoInss)||0);
                        const saldo6    = (parseFloat(c.creditoEcacSrfb)||0) - (parseFloat(c.perdcompEscritorio)||0);
                        const hasData   = c.saldoApuradoInss||c.creditoEcacSrfb||c.perdcompEscritorio||c.numeroPerdcomp;
                        return (
                          <tr key={c.comp} style={{background:hasData?theme.white:theme.gray50, borderBottom:`1px solid ${theme.gray100}`}}>
                            {/* Col 1 — Competência (auto) */}
                            <td style={{padding:"5px 8px", fontWeight:700, color:theme.gray700, whiteSpace:"nowrap", borderRight:`1px solid ${theme.gray100}`, fontSize:12}}>{c.comp}</td>
                            {/* Col 2 — Saldo INSS (manual) */}
                            <td style={{padding:"3px 4px", borderRight:`1px solid ${theme.gray100}`}}>
                              <input type="number" value={c.saldoApuradoInss||""} onChange={e=>updComp(prodAba,realIdx,"saldoApuradoInss",e.target.value)}
                                style={{width:92,padding:"3px 6px",border:`1px solid ${theme.gray200}`,borderRadius:4,fontSize:11,outline:"none",background:c.saldoApuradoInss?theme.white:theme.gray50}}/>
                            </td>
                            {/* Col 3 — Crédito e-CAC (manual) */}
                            <td style={{padding:"3px 4px", borderRight:`1px solid ${theme.gray100}`}}>
                              <input type="number" value={c.creditoEcacSrfb||""} onChange={e=>updComp(prodAba,realIdx,"creditoEcacSrfb",e.target.value)}
                                style={{width:100,padding:"3px 6px",border:`1px solid ${theme.gray200}`,borderRadius:4,fontSize:11,outline:"none",background:c.creditoEcacSrfb?theme.white:theme.gray50}}/>
                            </td>
                            {/* Col 4 — Auditoria (auto) */}
                            <td style={{padding:"5px 8px", borderRight:`1px solid ${theme.gray100}`, textAlign:"right",
                              background: auditoria<0 ? "#FEF2F2" : (c.creditoEcacSrfb||c.saldoApuradoInss) ? "#F0FDF4" : theme.gray50}}>
                              {(c.creditoEcacSrfb||c.saldoApuradoInss)
                                ? <span style={{fontSize:11,fontWeight:700,color:auditoria<0?theme.red:theme.green}}>{auditoria.toLocaleString("pt-BR",{minimumFractionDigits:2})}</span>
                                : <span style={{fontSize:9,color:theme.gray300}}>auto</span>}
                            </td>
                            {/* Col 5 — PERDCOMP Escritório (manual) */}
                            <td style={{padding:"3px 4px", borderRight:`1px solid ${theme.gray100}`}}>
                              <input type="number" value={c.perdcompEscritorio||""} onChange={e=>updComp(prodAba,realIdx,"perdcompEscritorio",e.target.value)}
                                style={{width:92,padding:"3px 6px",border:`1px solid ${theme.gray200}`,borderRadius:4,fontSize:11,outline:"none",background:c.perdcompEscritorio?theme.white:theme.gray50}}/>
                            </td>
                            {/* Col 6 — Saldo (auto) */}
                            <td style={{padding:"5px 8px", borderRight:`1px solid ${theme.gray100}`, textAlign:"right",
                              background: saldo6<0 ? "#FEF2F2" : (c.creditoEcacSrfb||c.perdcompEscritorio) ? "#F0FDF4" : theme.gray50}}>
                              {(c.creditoEcacSrfb||c.perdcompEscritorio)
                                ? <span style={{fontSize:11,fontWeight:700,color:saldo6<0?theme.red:theme.green}}>{saldo6.toLocaleString("pt-BR",{minimumFractionDigits:2})}</span>
                                : <span style={{fontSize:9,color:theme.gray300}}>auto</span>}
                            </td>
                            {/* Col 7 — SDO Atualizado (manual) */}
                            <td style={{padding:"3px 4px", borderRight:`1px solid ${theme.gray100}`}}>
                              <input type="number" value={c.saldoPerdcompAtualizado||""} onChange={e=>updComp(prodAba,realIdx,"saldoPerdcompAtualizado",e.target.value)}
                                style={{width:92,padding:"3px 6px",border:`1px solid ${theme.gray200}`,borderRadius:4,fontSize:11,outline:"none",background:c.saldoPerdcompAtualizado?theme.white:theme.gray50}}/>
                            </td>
                            {/* Col 8 — Nº PERDCOMP (manual) */}
                            <td style={{padding:"3px 4px", borderRight:`1px solid ${theme.gray100}`}}>
                              <input type="text" value={c.numeroPerdcomp||""} onChange={e=>updComp(prodAba,realIdx,"numeroPerdcomp",e.target.value)}
                                style={{width:100,padding:"3px 6px",border:`1px solid ${theme.gray200}`,borderRadius:4,fontSize:11,outline:"none",background:c.numeroPerdcomp?theme.white:theme.gray50}}/>
                            </td>
                            {/* Col 9 — Data PERDCOMP (manual) */}
                            <td style={{padding:"3px 4px", borderRight:`1px solid ${theme.gray100}`}}>
                              <input type="date" value={c.dataPerdcomp||""} onChange={e=>updComp(prodAba,realIdx,"dataPerdcomp",e.target.value)}
                                style={{width:112,padding:"3px 6px",border:`1px solid ${theme.gray200}`,borderRadius:4,fontSize:11,outline:"none",background:c.dataPerdcomp?theme.white:theme.gray50}}/>
                            </td>
                            {/* Col E-CAC — botão com contador */}
                            <td style={{padding:"3px 6px", textAlign:"center"}}>
                              <button
                                onClick={()=>setEcacModal({prodIdx:prodAba, compIdx:realIdx, comp:c.comp, numeroPerdcomp:c.numeroPerdcomp})}
                                title="Ocorrências E-CAC desta competência"
                                style={{
                                  padding:"3px 10px", borderRadius:5, cursor:"pointer", fontSize:10, fontWeight:700,
                                  border:`1px solid ${(c.ecacOcorrencias||[]).length>0 ? theme.gold : theme.gray200}`,
                                  background:(c.ecacOcorrencias||[]).length>0 ? `${theme.gold}22` : theme.gray50,
                                  color:(c.ecacOcorrencias||[]).length>0 ? theme.goldDark : theme.gray400,
                                  whiteSpace:"nowrap"
                                }}>
                                {(c.ecacOcorrencias||[]).length>0 ? `📋 ${(c.ecacOcorrencias||[]).length}` : "+ E-CAC"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{background:`${theme.gold}11`, borderTop:`2px solid ${theme.gold}33`}}>
                        <td style={{padding:"6px 8px",fontWeight:700,fontSize:10,color:theme.goldDark}}>TOTAIS</td>
                        {["saldoApuradoInss","creditoEcacSrfb","","perdcompEscritorio","","saldoPerdcompAtualizado","",""].map((field,fi)=>{
                          if(!field) return <td key={fi} style={{padding:"5px 8px",fontSize:10,color:theme.gray400,borderRight:`1px solid ${theme.gray200}`,textAlign:"center"}}>⚡ auto</td>;
                          const tot = (p.competencias||[]).slice(mesPag*12,mesPag*12+12).reduce((s,c)=>s+(parseFloat(c[field])||0),0);
                          return <td key={field} style={{padding:"5px 8px",fontSize:11,fontWeight:700,color:theme.goldDark,borderRight:`1px solid ${theme.gray200}`,textAlign:"right"}}>{tot!==0?tot.toLocaleString("pt-BR",{minimumFractionDigits:2}):""}</td>;
                        })}
                        <td/>
                        <td style={{padding:"5px 8px",fontSize:10,color:theme.gray400,textAlign:"center"}}>
                          {(p.competencias||[]).slice(mesPag*12,mesPag*12+12).reduce((s,c)=>s+((c.ecacOcorrencias||[]).length),0) > 0
                            ? <span style={{color:theme.goldDark,fontWeight:700}}>
                                📋 {(p.competencias||[]).slice(mesPag*12,mesPag*12+12).reduce((s,c)=>s+((c.ecacOcorrencias||[]).length),0)}
                              </span>
                            : "—"}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Legenda */}
                <div style={{display:"flex",gap:16,marginTop:10,padding:"8px 14px",background:theme.gray50,borderRadius:6,fontSize:10,color:theme.gray500}}>
                  <span>⚡ Calculado automaticamente pelo sistema</span>
                  <span style={{color:theme.red}}>■ Vermelho = saldo negativo (atenção)</span>
                  <span style={{color:theme.green}}>■ Verde = saldo positivo</span>
                  <span>□ Cinza = sem dados lançados</span>
                </div>
              </div>
            )}

            {/* ── Modal E-CAC por competência ── */}
            {ecacModal && ecacModal.prodIdx===prodAba && (()=>{
              const ci = ecacModal.compIdx;
              const comp = editItem.produtos[prodAba].competencias[ci];
              const ocorrs = comp.ecacOcorrencias || [];
              const addOc = () => {
                const nova = { id: Date.now(), data: new Date().toISOString().slice(0,10), status: "", obs: "" };
                updComp(prodAba, ci, "ecacOcorrencias", [...ocorrs, nova]);
              };
              const updOc = (oi, field, val) => {
                const lista = ocorrs.map((o,i) => i===oi ? {...o,[field]:val} : o);
                updComp(prodAba, ci, "ecacOcorrencias", lista);
              };
              const remOc = (oi) => {
                updComp(prodAba, ci, "ecacOcorrencias", ocorrs.filter((_,i)=>i!==oi));
              };
              return (
                <Modal open={true} onClose={()=>setEcacModal(null)}
                  title={`E-CAC — Competência ${ecacModal.comp}${ecacModal.numeroPerdcomp ? " · PERDCOMP " + ecacModal.numeroPerdcomp : ""}`}
                  width={640}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                    <p style={{fontSize:12,color:theme.gray500}}>
                      Histórico de ocorrências vinculadas a esta competência/PERDCOMP.
                      Cada nova verificação registra uma linha mantendo o histórico completo.
                    </p>
                    <Btn size="sm" onClick={addOc}>+ Ocorrência</Btn>
                  </div>

                  {ocorrs.length===0
                    ? <div style={{textAlign:"center",padding:"24px 0"}}>
                        <p style={{fontSize:13,color:theme.gray400}}>Nenhuma ocorrência registrada para esta competência.</p>
                        <Btn style={{marginTop:12}} size="sm" onClick={addOc}>+ Registrar primeira ocorrência</Btn>
                      </div>
                    : <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {/* Cabeçalho */}
                        <div style={{display:"grid",gridTemplateColumns:"110px 1fr 1fr 28px",gap:8}}>
                          {["Data","Status PERDCOMP","Observação",""].map(h=>(
                            <p key={h} style={{fontSize:9,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8,padding:"0 4px"}}>{h}</p>
                          ))}
                        </div>
                        {ocorrs.map((o,oi)=>(
                          <div key={o.id||oi} style={{
                            display:"grid",gridTemplateColumns:"110px 1fr 1fr 28px",gap:8,alignItems:"center",
                            padding:"8px",background:theme.gray50,borderRadius:7,
                            borderLeft:`3px solid ${theme.gold}`
                          }}>
                            <input type="date" value={o.data||""} onChange={e=>updOc(oi,"data",e.target.value)}
                              style={{padding:"5px 7px",border:`1px solid ${theme.gray200}`,borderRadius:5,fontSize:12,outline:"none",width:"100%"}}/>
                            <select value={o.status||""} onChange={e=>updOc(oi,"status",e.target.value)}
                              style={{padding:"5px 7px",border:`1px solid ${theme.gray200}`,borderRadius:5,fontSize:12,outline:"none",width:"100%"}}>
                              <option value="">Selecione...</option>
                              {STATUS_PERDCOMP_DEFAULT.map(s=><option key={s} value={s}>{s}</option>)}
                            </select>
                            <input value={o.obs||""} onChange={e=>updOc(oi,"obs",e.target.value)}
                              placeholder="Observação..."
                              style={{padding:"5px 7px",border:`1px solid ${theme.gray200}`,borderRadius:5,fontSize:12,outline:"none",width:"100%"}}/>
                            <button onClick={()=>remOc(oi)}
                              style={{background:"none",border:"none",color:theme.red,cursor:"pointer",fontSize:16,padding:0,textAlign:"center"}}>×</button>
                          </div>
                        ))}
                      </div>
                  }

                  <div style={{display:"flex",justifyContent:"flex-end",paddingTop:16,borderTop:`1px solid ${theme.gray100}`,marginTop:16}}>
                    <Btn onClick={()=>setEcacModal(null)}>Fechar</Btn>
                  </div>
                </Modal>
              );
            })()}
          </div>
        )}

        {/* ── PERDCOMP padrão (PIS, ICMS-ST e novos produtos) ── */}
        {campos === "perdcomp" && p.viabilidade === "Sim" && (
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 20px", marginTop:16}}>
            <FormField label="SDO PERDCOMP (R$)">
              <Input type="number" value={p.sdoPerdcomp} onChange={v=>up("sdoPerdcomp",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="REST — Restituição (R$)">
              <Input type="number" value={p.sdoPerdcompRest} onChange={v=>up("sdoPerdcompRest",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="COMP — Compensação (R$)">
              <Input type="number" value={p.sdoPerdcompComp} onChange={v=>up("sdoPerdcompComp",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="PERDCOMP — Número / Referência">
              <Input value={p.perdcomp} onChange={v=>up("perdcomp",v)} placeholder="Número"/>
            </FormField>
            <FormField label="Data PERDCOMP">
              <Input type="date" value={p.perdcompData||""} onChange={v=>up("perdcompData",v)}/>
            </FormField>
            <FormField label="Status PERDCOMP">
              <Select value={p.perdcompStatus} onChange={v=>up("perdcompStatus",v)}
                options={["Pendente","Transmitido","Homologado","Indeferido","Em análise"]} placeholder="Selecione..."/>
            </FormField>
            <div style={{gridColumn:"1/-1"}}>
              <p style={{fontSize:10,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,margin:"12px 0 10px"}}>SDO PERDCOMP +360 dias</p>
            </div>
            <FormField label="Restituição +360d (R$)">
              <Input type="number" value={p.sdoMais360Rest} onChange={v=>up("sdoMais360Rest",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="Compensação +360d (R$)">
              <Input type="number" value={p.sdoMais360Comp} onChange={v=>up("sdoMais360Comp",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="MS — Mandado de Segurança (R$)">
              <Input type="number" value={p.sdoMais360MS} onChange={v=>up("sdoMais360MS",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="Status da Análise">
              <Select value={p.statusAnalise} onChange={v=>up("statusAnalise",v)} options={STATUS_ANALISE}/>
            </FormField>
            <div style={{gridColumn:"1/-1"}}>
              <FormField label="Observação técnica">
                <Textarea value={p.obs} onChange={v=>up("obs",v)} placeholder="Notas técnicas..."/>
              </FormField>
            </div>
          </div>
        )}

        {/* ── IR/CSLL Construção Civil ───────────────────── */}
        {campos === "ircsll_cc" && p.viabilidade === "Sim" && (
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 20px", marginTop:16}}>
            <div style={{gridColumn:"1/-1"}}>
              <p style={{fontSize:11,color:theme.gray500,marginBottom:8}}>
                Aplicável a empresas da <strong>construção civil</strong> com contratos de empreitada global (fornecimento de material + mão de obra), tributadas pelo Lucro Presumido ou Real.
              </p>
            </div>
            <FormField label="Alíquota IR (%)">
              <Input value={p.aliquotaIR} onChange={v=>up("aliquotaIR",v)} placeholder="Ex: 1,5"/>
            </FormField>
            <FormField label="Alíquota CSLL (%)">
              <Input value={p.aliquotaCSLL} onChange={v=>up("aliquotaCSLL",v)} placeholder="Ex: 1,0"/>
            </FormField>
            <FormField label="Classificação de Receita">
              <Input value={p.classReceita} onChange={v=>up("classReceita",v)} placeholder="Ex: Empreitada Global Mista"/>
            </FormField>
            <FormField label="Tipo de Empreitada">
              <Select value={p.tipoEmpreitada} onChange={v=>up("tipoEmpreitada",v)}
                options={["Global (material + mão de obra)","Mão de obra apenas","Empreitada parcial"]} placeholder="Selecione..."/>
            </FormField>
            <FormField label="Empreitada Global confirmada?">
              <div style={{display:"flex",gap:20,padding:"10px 0"}}>
                {["Sim","Não"].map(o=>(
                  <label key={o} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13}}>
                    <input type="radio" checked={p.empreitadaGlobal===(o==="Sim")} onChange={()=>up("empreitadaGlobal",o==="Sim")}/> {o}
                  </label>
                ))}
              </div>
            </FormField>
            <FormField label="SDO PERDCOMP (R$)">
              <Input type="number" value={p.sdoPerdcomp} onChange={v=>up("sdoPerdcomp",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="REST (R$)">
              <Input type="number" value={p.sdoPerdcompRest} onChange={v=>up("sdoPerdcompRest",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="COMP (R$)">
              <Input type="number" value={p.sdoPerdcompComp} onChange={v=>up("sdoPerdcompComp",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="Status da Análise">
              <Select value={p.statusAnalise} onChange={v=>up("statusAnalise",v)} options={STATUS_ANALISE}/>
            </FormField>
            <div style={{gridColumn:"1/-1"}}>
              <FormField label="Observação técnica">
                <Textarea value={p.obs} onChange={v=>up("obs",v)} placeholder="Notas técnicas..."/>
              </FormField>
            </div>
          </div>
        )}

        {/* ── IR/CSLL Saúde ─────────────────────────────── */}
        {campos === "ircsll_sd" && p.viabilidade === "Sim" && (
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 20px", marginTop:16}}>
            <div style={{gridColumn:"1/-1"}}>
              <p style={{fontSize:11,color:theme.gray500,marginBottom:8}}>
                Aplicável a <strong>clínicas médicas equiparadas a hospitais</strong>, enquadradas no regime de <strong>Lucro Presumido</strong>.
              </p>
            </div>
            <FormField label="Enquadramento hospitalar confirmado?">
              <div style={{display:"flex",gap:20,padding:"10px 0"}}>
                {["Sim","Não"].map(o=>(
                  <label key={o} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13}}>
                    <input type="radio" checked={p.enquadramentoHospitalar===(o==="Sim")} onChange={()=>up("enquadramentoHospitalar",o==="Sim")}/> {o}
                  </label>
                ))}
              </div>
            </FormField>
            <FormField label="Alíquota IR praticada (%)">
              <Input value={p.aliquotaIRSaude} onChange={v=>up("aliquotaIRSaude",v)} placeholder="Ex: 8"/>
            </FormField>
            <FormField label="Alíquota CSLL praticada (%)">
              <Input value={p.aliquotaCSLLSaude} onChange={v=>up("aliquotaCSLLSaude",v)} placeholder="Ex: 12"/>
            </FormField>
            <FormField label="SDO PERDCOMP (R$)">
              <Input type="number" value={p.sdoPerdcomp} onChange={v=>up("sdoPerdcomp",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="REST (R$)">
              <Input type="number" value={p.sdoPerdcompRest} onChange={v=>up("sdoPerdcompRest",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="COMP (R$)">
              <Input type="number" value={p.sdoPerdcompComp} onChange={v=>up("sdoPerdcompComp",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="Status da Análise">
              <Select value={p.statusAnalise} onChange={v=>up("statusAnalise",v)} options={STATUS_ANALISE}/>
            </FormField>
            <div style={{gridColumn:"1/-1"}}>
              <FormField label="Observação técnica">
                <Textarea value={p.obs} onChange={v=>up("obs",v)} placeholder="Notas técnicas..."/>
              </FormField>
            </div>
          </div>
        )}

        {/* ── Situação Fiscal e Dívida ───────────────────── */}
        {campos === "divida" && (
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 20px", marginTop:16}}>
            <FormField label="SRFB — Valor (R$)">
              <Input type="number" value={p.srfbValor} onChange={v=>up("srfbValor",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="SRFB — Status">
              <Select value={p.srfbStatus} onChange={v=>up("srfbStatus",v)}
                options={["SEM RELEVÂNCIA","RELEVANTE","PERDCOMP","EM NEGOCIAÇÃO","PARCELADO","NÃO TEM"]} placeholder="Selecione..."/>
            </FormField>
            <FormField label="PGFN — Valor (R$)">
              <Input type="number" value={p.pgfnValor} onChange={v=>up("pgfnValor",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="PGFN — Status">
              <Select value={p.pgfnStatus} onChange={v=>up("pgfnStatus",v)}
                options={["NÃO TEM","RELEVANTE","PARCELADO","TRANSAÇÃO","EM NEGOCIAÇÃO"]} placeholder="Selecione..."/>
            </FormField>
            <FormField label="Status Geral da Dívida">
              <Select value={p.statusDivida} onChange={v=>up("statusDivida",v)}
                options={["SEM RELEVÂNCIA","RELEVANTE","PERDCOMP","EM NEGOCIAÇÃO","PARCELADO","PRESCRITO","NÃO TEM"]} placeholder="Selecione..."/>
            </FormField>
            <FormField label="Prescrição">
              <Input value={p.prescricao} onChange={v=>up("prescricao",v)} placeholder="Data ou observação"/>
            </FormField>
            <div style={{gridColumn:"1/-1"}}>
              <FormField label="Transação Tributária">
                <Textarea value={p.transacao} onChange={v=>up("transacao",v)} placeholder="Detalhes da transação..."/>
              </FormField>
            </div>
            <FormField label="Status da Análise">
              <Select value={p.statusAnalise} onChange={v=>up("statusAnalise",v)} options={STATUS_ANALISE}/>
            </FormField>
            <div style={{gridColumn:"1/-1"}}>
              <FormField label="Observação técnica">
                <Textarea value={p.obs} onChange={v=>up("obs",v)} placeholder="Notas técnicas..."/>
              </FormField>
            </div>
          </div>
        )}

        {/* ── Verbas + campos genéricos ─────────────────── */}
        {(campos === "verbas" || campos === "outros") && (
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 20px", marginTop:16}}>
            <FormField label="REST — Restituição (R$)">
              <Input type="number" value={p.sdoPerdcompRest} onChange={v=>up("sdoPerdcompRest",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="COMP — Compensação (R$)">
              <Input type="number" value={p.sdoPerdcompComp} onChange={v=>up("sdoPerdcompComp",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="PERDCOMP — Número">
              <Input value={p.perdcomp} onChange={v=>up("perdcomp",v)} placeholder="Número"/>
            </FormField>
            <FormField label="Status PERDCOMP">
              <Select value={p.perdcompStatus} onChange={v=>up("perdcompStatus",v)}
                options={["Pendente","Transmitido","Homologado","Indeferido","Em análise"]} placeholder="Selecione..."/>
            </FormField>
            <FormField label="SDO +360d REST (R$)">
              <Input type="number" value={p.sdoMais360Rest} onChange={v=>up("sdoMais360Rest",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="SDO +360d COMP (R$)">
              <Input type="number" value={p.sdoMais360Comp} onChange={v=>up("sdoMais360Comp",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="MS — Mandado de Segurança (R$)">
              <Input type="number" value={p.sdoMais360MS} onChange={v=>up("sdoMais360MS",v)} placeholder="0,00"/>
            </FormField>
            <FormField label="Status da Análise">
              <Select value={p.statusAnalise} onChange={v=>up("statusAnalise",v)} options={STATUS_ANALISE}/>
            </FormField>
            <div style={{gridColumn:"1/-1"}}>
              <FormField label="Observação técnica">
                <Textarea value={p.obs} onChange={v=>up("obs",v)} placeholder="Notas técnicas..."/>
              </FormField>
            </div>
          </div>
        )}

        {/* Mensagem quando viabilidade não é Sim */}
        {p.viabilidade !== "Sim" && campos !== "divida" && (
          <div style={{marginTop:16,padding:"14px 18px",background:theme.gray100,borderRadius:8,textAlign:"center"}}>
            <p style={{fontSize:13,color:theme.gray500}}>
              {p.viabilidade==="Não se aplica" ? "Produto não aplicável ao perfil desta empresa." :
               p.viabilidade==="Não" ? "Viabilidade negativa — sem crédito a apurar." :
               "Defina a viabilidade para preencher os campos de análise."}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="RCT Tributário"
        subtitle={`Diagnóstico e controle de recuperação de crédito tributário — ${produtosRCT.length} Produto${produtosRCT.length!==1?"s":""} Tributário${produtosRCT.length!==1?"s":""}`}
        action={!isViz && <Btn onClick={openNew}>+ Novo Diagnóstico</Btn>}
      />

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:8}}>
        {[
          {l:"Diagnósticos",    v:diagnosticosRCT.length, c:theme.gray800, icon:"📋", fmt:"n"},
          {l:"Em Andamento",    v:emAndamento,             c:theme.blue,    icon:"⚡", fmt:"n"},
          {l:"Concluídos",      v:concluidos,              c:theme.green,   icon:"✅", fmt:"n"},
        ].map(k=>(
          <Card key={k.l} style={{padding:"16px 18px"}}>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <p style={{fontSize:10,color:theme.gray400,letterSpacing:0.8,textTransform:"uppercase",lineHeight:1.4}}>{k.l}</p>
              <span style={{fontSize:16}}>{k.icon}</span>
            </div>
            <p style={{fontSize:k.fmt==="r"?16:26,fontWeight:700,color:k.c,marginTop:8}}>
              {k.fmt==="n" && k.v}
              {k.fmt==="r" && <><span style={{fontSize:10,fontWeight:400}}>R$ </span>{k.v.toLocaleString("pt-BR",{minimumFractionDigits:2})}</>}
            </p>
          </Card>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:14,marginBottom:24}}>
        {/* Card Total Crédito */}
        <Card style={{padding:"16px 20px"}}>
          <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>💰 Total Crédito</p>
          <p style={{fontSize:22,fontWeight:700,color:theme.gold}}>
            R$ {parseFloat(totalCredito||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}
          </p>
        </Card>
        {/* Card Rel. Diagnóstico Pendente */}
        <Card style={{padding:"14px 16px",
          border:relDiagPendentes.filter(d=>d.diasSemRelatorio>7).length>0?`2px solid #f97316`:`1px solid ${theme.gray200}`,
          background:relDiagPendentes.filter(d=>d.diasSemRelatorio>7).length>0?"#fff7ed":theme.white}}>
          <p style={{fontSize:9,
            color:relDiagPendentes.filter(d=>d.diasSemRelatorio>7).length>0?"#c2410c":theme.gray400,
            textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>
            📄 Rel.Diag. Pendentes
          </p>
          <p style={{fontSize:22,fontWeight:700,
            color:relDiagPendentes.filter(d=>d.diasSemRelatorio>7).length>0?"#c2410c":theme.gray700}}>
            {relDiagPendentes.length}
          </p>
          {relDiagPendentes.filter(d=>d.diasSemRelatorio>7).length>0&&(
            <p style={{fontSize:10,color:"#c2410c",fontWeight:600,marginTop:2}}>
              {relDiagPendentes.filter(d=>d.diasSemRelatorio>7).length} acima de 7 dias
            </p>
          )}
        </Card>
        {/* Card Rel. Docs Pendentes */}
        <Card style={{padding:"14px 16px",cursor:"pointer",
          border:relDocsVencidos.length>0?`2px solid ${theme.red}`:relDocsHoje.length>0?`2px solid #d97706`:`1px solid ${theme.gray200}`,
          background:relDocsVencidos.length>0?"#fef2f2":relDocsHoje.length>0?"#fffbeb":theme.white}}>
          <p style={{fontSize:9,color:relDocsVencidos.length>0?theme.red:relDocsHoje.length>0?"#d97706":theme.gray400,
            textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>
            📋 Rel.Docs Pendentes
          </p>
          <p style={{fontSize:22,fontWeight:700,
            color:relDocsVencidos.length>0?theme.red:relDocsHoje.length>0?"#d97706":theme.gray700}}>
            {relDocsPendentes.length}
          </p>
          {relDocsVencidos.length>0&&<p style={{fontSize:10,color:theme.red,fontWeight:600,marginTop:2}}>{relDocsVencidos.length} em atraso</p>}
        </Card>
        {/* Card Aprovados — clicável, mostra alerta se há sem contrato */}
        <Card onClick={()=>listAprovSemContrato.length>0&&setShowAprovSemContrato(v=>!v)}
          style={{padding:"16px 20px",cursor:listAprovSemContrato.length>0?"pointer":"default",
            border:listAprovSemContrato.length>0?`2px solid #22c55e`:`1px solid ${theme.gray200}`,
            position:"relative",transition:"box-shadow .2s",
            boxShadow:showAprovSemContrato?"0 0 0 3px #22c55e33":"none"}}>
          <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>
            👍 Aprovados
            {listAprovSemContrato.length>0&&<span style={{marginLeft:6,fontSize:9,padding:"1px 6px",borderRadius:8,background:"#dcfce7",color:"#166534",fontWeight:700}}>
              {listAprovSemContrato.length} sem contrato
            </span>}
          </p>
          <p style={{fontSize:22,fontWeight:700,color:"#166534"}}>{aprovados}</p>
          {listAprovSemContrato.length>0&&(
            <p style={{fontSize:10,color:"#166534",marginTop:4,fontWeight:600}}>
              ▼ {showAprovSemContrato?"Ocultar":"Ver lista"}
            </p>
          )}
        </Card>
        {/* Card Contrato Assinado */}
        <Card style={{padding:"16px 20px"}}>
          <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>📝 Contrato Assinado</p>
          <p style={{fontSize:22,fontWeight:700,color:theme.blue}}>{contratoAssinado}</p>
        </Card>
        {/* Card Não Aprovados */}
        <Card style={{padding:"16px 20px"}}>
          <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>❌ Não Aprovados</p>
          <p style={{fontSize:22,fontWeight:700,color:theme.red}}>{naoAprovados}</p>
        </Card>
      </div>

      {/* ── Painel: Aprovados sem Contrato Assinado ── */}
      {showAprovSemContrato&&listAprovSemContrato.length>0&&(
        <Card style={{marginBottom:16,border:"2px solid #22c55e33",background:"#f0fdf4"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <p style={{fontSize:12,fontWeight:700,color:"#166534"}}>
              ✅ Diagnósticos Aprovados — Aguardando Assinatura de Contrato ({listAprovSemContrato.length})
            </p>
            <button onClick={()=>setShowAprovSemContrato(false)}
              style={{fontSize:12,color:theme.gray400,background:"none",border:"none",cursor:"pointer",padding:"4px 8px"}}>
              ✕ Fechar
            </button>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{background:"#dcfce7"}}>
                {["Cliente","Responsável","Produtos Viáveis","Crédito Apurado","Aprovado em",""].map(h=>(
                  <th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,
                    color:"#166534",letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {listAprovSemContrato.map((d,i)=>{
                const cli2  = clientesELeads.find(x=>x.id===d.clienteId);
                const resp2 = equipe.find(e=>e.id===d.responsavel);
                const viavN = (d.produtos||[]).filter(p=>p.viabilidade==="Sim").length;
                const cred2 = (d.produtos||[]).reduce((s,p)=>s+(parseFloat(p.creditoApurado)||0),0);
                return (
                  <tr key={d.id} style={{borderBottom:`1px solid #bbf7d0`,
                    background:i%2===0?"#fff":"#f0fdf4"}}>
                    <td style={{padding:"9px 12px",fontWeight:600,color:theme.gray900}}>
                      {cli2?.nome||"—"}
                    </td>
                    <td style={{padding:"9px 12px",color:theme.gray600}}>{resp2?.nome||"—"}</td>
                    <td style={{padding:"9px 12px"}}>
                      <span style={{fontSize:11,padding:"2px 8px",borderRadius:8,
                        background:"#dcfce7",color:"#166534",fontWeight:600}}>
                        {viavN} produto{viavN!==1?"s":""}
                      </span>
                    </td>
                    <td style={{padding:"9px 12px",fontWeight:700,color:theme.gold}}>
                      {cred2>0?"R$ "+cred2.toLocaleString("pt-BR",{minimumFractionDigits:2}):"—"}
                    </td>
                    <td style={{padding:"9px 12px",color:theme.gray600}}>
                      {d.dataAprovacao?new Date(d.dataAprovacao+"T12:00").toLocaleDateString("pt-BR"):"—"}
                    </td>
                    <td style={{padding:"9px 12px"}}>
                      <button onClick={()=>{openView(d,"resumo");setShowAprovSemContrato(false);}}
                        style={{fontSize:11,padding:"5px 12px",borderRadius:6,
                          border:"1px solid #22c55e",background:"#dcfce7",
                          color:"#166534",cursor:"pointer",fontWeight:600}}>
                        → Ver
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* ── Dashboard de Prazos ── */}
      {(()=>{
        const hoje2 = new Date(); hoje2.setHours(0,0,0,0);
        const comData = diagnosticosRCT.filter(d=>d.dataPrevistaConclusao&&d.status!=="Concluído");
        const vencidos   = comData.filter(d=>new Date(d.dataPrevistaConclusao+"T12:00")<hoje2);
        const em7dias    = comData.filter(d=>{const diff=Math.ceil((new Date(d.dataPrevistaConclusao+"T12:00")-hoje2)/(1000*60*60*24));return diff>=0&&diff<=7;});
        const noPrazo    = comData.filter(d=>new Date(d.dataPrevistaConclusao+"T12:00")>hoje2);
        const semData    = diagnosticosRCT.filter(d=>!d.dataPrevistaConclusao&&d.status!=="Concluído");
        const fmtD2 = d=>d?new Date(d+"T12:00").toLocaleDateString("pt-BR"):"—";
        if(comData.length===0&&semData.length===0) return null;
        return (
          <div style={{marginBottom:16}}>
            {/* KPIs de prazo */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:12}}>
              {[
                {l:"🔴 Vencidos",       v:vencidos.length,  c:"#dc2626", bg:"#fef2f2"},
                {l:"🟡 Vencem em 7d",   v:em7dias.length,   c:"#d97706", bg:"#fffbeb"},
                {l:"🟢 No Prazo",       v:noPrazo.length,   c:"#16a34a", bg:"#f0fdf4"},
                {l:"⚫ Sem Data",       v:semData.length,   c:"#6b7280", bg:"#f9fafb"},
              ].map(k=>(
                <div key={k.l} style={{padding:"12px 16px",borderRadius:8,background:k.bg,
                  border:`1px solid ${k.c}22`,textAlign:"center"}}>
                  <p style={{fontSize:10,color:k.c,fontWeight:700,marginBottom:4}}>{k.l}</p>
                  <p style={{fontSize:22,fontWeight:700,color:k.c}}>{k.v}</p>
                </div>
              ))}
            </div>
            {/* Tabela de prazos — apenas vencidos e próximos */}
            {(vencidos.length>0||em7dias.length>0)&&(
              <Card style={{marginBottom:8}}>
                <p style={{fontSize:11,fontWeight:700,color:theme.gray700,textTransform:"uppercase",
                  letterSpacing:.8,marginBottom:10}}>⚠️ Prazos que requerem atenção</p>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead><tr style={{background:theme.gray50}}>
                    {["Cliente","Responsável","Data Prevista","Situação",""].map(h=>(
                      <th key={h} style={{padding:"7px 12px",textAlign:"left",fontSize:10,
                        color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {[...vencidos,...em7dias]
                      .sort((a,b)=>(a.dataPrevistaConclusao||"")>(b.dataPrevistaConclusao||"")?1:-1)
                      .map((d,i)=>{
                        const cli2=clientesELeads.find(x=>x.id===d.clienteId);
                        const resp2=equipe.find(e=>e.id===d.responsavel);
                        const diff=Math.ceil((new Date(d.dataPrevistaConclusao+"T12:00")-hoje2)/(1000*60*60*24));
                        const venc=diff<0;
                        return (
                          <tr key={d.id} style={{borderBottom:`1px solid ${theme.gray100}`,
                            background:venc?"#fef2f2":i%2===0?theme.white:theme.gray50}}>
                            <td style={{padding:"8px 12px",fontWeight:600,color:theme.gray900}}>
                              {cli2?.nome||"—"}
                            </td>
                            <td style={{padding:"8px 12px",color:theme.gray600}}>{resp2?.nome||"—"}</td>
                            <td style={{padding:"8px 12px",fontWeight:600,
                              color:venc?"#dc2626":"#d97706"}}>
                              {fmtD2(d.dataPrevistaConclusao)}
                            </td>
                            <td style={{padding:"8px 12px"}}>
                              <span style={{fontSize:11,padding:"2px 8px",borderRadius:8,fontWeight:700,
                                background:venc?"#fecaca":"#fde68a",
                                color:venc?"#dc2626":"#92400e"}}>
                                {venc?`${Math.abs(diff)}d vencido`:`${diff}d restante${diff!==1?"s":""}`}
                              </span>
                            </td>
                            <td style={{padding:"8px 12px"}}>
                              <button onClick={()=>openView(d)}
                                style={{fontSize:11,padding:"4px 10px",borderRadius:6,
                                  border:`1px solid ${theme.blue}`,background:"#eff6ff",
                                  color:theme.blue,cursor:"pointer",fontWeight:600}}>
                                → Ver
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </Card>
            )}
          </div>
        );
      })()}

      {/* ── Alertas: Relatório de Diagnóstico Pendente ── */}
      {relDiagPendentes.length>0&&(
        <Card style={{marginBottom:16,border:relDiagPendentes.filter(d=>d.diasSemRelatorio>7).length>0?"2px solid #f9731633":"1px solid "+theme.gray200,background:relDiagPendentes.filter(d=>d.diasSemRelatorio>7).length>0?"#fff7ed":"#f8fafc"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <p style={{fontSize:12,fontWeight:700,color:relDiagPendentes.filter(d=>d.diasSemRelatorio>7).length>0?"#c2410c":theme.gray700}}>
              📄 Relatório de Diagnóstico — Pendente de Envio ({relDiagPendentes.length})
            </p>
            <p style={{fontSize:11,color:theme.gray400}}>Diagnósticos em andamento sem relatório enviado</p>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:relDiagPendentes.filter(d=>d.diasSemRelatorio>7).length>0?"#fed7aa":"#f1f5f9"}}>
              {["Cliente","Responsável","Data Conclusão","Dias s/ Relatório","Status",""].map(h=>(
                <th key={h} style={{padding:"7px 12px",textAlign:"left",fontSize:10,color:relDiagPendentes.filter(d=>d.diasSemRelatorio>7).length>0?"#c2410c":theme.gray600,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {relDiagPendentes.map((d,i)=>{
                const cli4=clientesELeads.find(x=>x.id===d.clienteId);
                const resp4=equipe.find(e=>e.id===d.responsavel);
                const urgente=d.diasSemRelatorio>7;
                const atencao=d.diasSemRelatorio>=3&&d.diasSemRelatorio<=7;
                return(
                  <tr key={d.id} style={{borderBottom:"1px solid "+theme.gray100,background:urgente?"#fff7ed":atencao?"#fffbeb":i%2===0?theme.white:theme.gray50}}>
                    <td style={{padding:"8px 12px",fontWeight:600,color:theme.gray900}}>{cli4?.nome||"—"}</td>
                    <td style={{padding:"8px 12px",color:theme.gray600}}>{resp4?.nome||"—"}</td>
                    <td style={{padding:"8px 12px",color:theme.gray500}}>{d.dataConclusao?new Date(d.dataConclusao+"T12:00").toLocaleDateString("pt-BR"):"—"}</td>
                    <td style={{padding:"8px 12px"}}>
                      <span style={{fontSize:11,padding:"3px 8px",borderRadius:8,fontWeight:600,background:urgente?"#fed7aa":atencao?"#fde68a":"#dcfce7",color:urgente?"#c2410c":atencao?"#92400e":"#166534"}}>
                        {urgente?"🔴":atencao?"🟡":"🟢"} {d.diasSemRelatorio} dia{d.diasSemRelatorio!==1?"s":""}
                      </span>
                    </td>
                    <td style={{padding:"8px 12px"}}><Badge color={statusColor(d.status)} style={{fontSize:10}}>{d.status}</Badge></td>
                    <td style={{padding:"8px 12px"}}>
                      <button onClick={()=>openView(d,"resumo")} style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:"1px solid "+theme.blue,background:"#eff6ff",color:theme.blue,cursor:"pointer",fontWeight:600}}>→ Ver</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* ── Alertas: Relatório de Análise de Documentos ── */}
      {relDocsPendentes.length>0&&(
        <Card style={{marginBottom:16,
          border:relDocsVencidos.length>0?`2px solid ${theme.red}33`:
                 relDocsHoje.length>0?`2px solid #fde68a`:`1px solid ${theme.gray200}`,
          background:relDocsVencidos.length>0?"#fef2f2":relDocsHoje.length>0?"#fffbeb":"#f8fafc"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <p style={{fontSize:12,fontWeight:700,
              color:relDocsVencidos.length>0?theme.red:relDocsHoje.length>0?"#92400e":theme.gray700}}>
              📋 Relatório de Análise de Documentos — Pendentes de Envio ({relDocsPendentes.length})
            </p>
            <p style={{fontSize:11,color:theme.gray400}}>
              Prazo: 2º dia útil após o início do diagnóstico
            </p>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{background:relDocsVencidos.length>0?"#fecaca":relDocsHoje.length>0?"#fde68a":"#f1f5f9"}}>
                {["Cliente","Responsável","Data Início","Prazo Envio","Situação",""].map(h=>(
                  <th key={h} style={{padding:"7px 12px",textAlign:"left",fontSize:10,
                    color:relDocsVencidos.length>0?"#991b1b":relDocsHoje.length>0?"#92400e":theme.gray600,
                    letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...relDocsVencidos,...relDocsHoje,...relDocsFuturos].map((d,i)=>{
                const cli3  = clientesELeads.find(x=>x.id===d.clienteId);
                const resp3 = equipe.find(e=>e.id===d.responsavel);
                const venc3 = d.diffRelDocs!==null&&d.diffRelDocs<0;
                const hoje3b= d.diffRelDocs===0;
                return (
                  <tr key={d.id} style={{borderBottom:`1px solid ${theme.gray100}`,
                    background:venc3?"#fef2f2":hoje3b?"#fffbeb":i%2===0?theme.white:theme.gray50}}>
                    <td style={{padding:"8px 12px",fontWeight:600,color:theme.gray900}}>
                      {cli3?.nome||"—"}
                    </td>
                    <td style={{padding:"8px 12px",color:theme.gray600}}>{resp3?.nome||"—"}</td>
                    <td style={{padding:"8px 12px",color:theme.gray500}}>
                      {d.dataInicio?new Date(d.dataInicio+"T12:00").toLocaleDateString("pt-BR"):"—"}
                    </td>
                    <td style={{padding:"8px 12px",fontWeight:600,
                      color:venc3?theme.red:hoje3b?"#d97706":theme.green}}>
                      {d.prazoRelDocs?new Date(d.prazoRelDocs+"T12:00").toLocaleDateString("pt-BR"):"—"}
                    </td>
                    <td style={{padding:"8px 12px"}}>
                      <span style={{fontSize:11,padding:"3px 8px",borderRadius:8,fontWeight:600,
                        background:venc3?"#fecaca":hoje3b?"#fde68a":"#dcfce7",
                        color:venc3?"#991b1b":hoje3b?"#92400e":"#166534"}}>
                        {venc3
                          ? `🔴 ${Math.abs(d.diffRelDocs)}d em atraso`
                          : hoje3b
                          ? "🟡 Enviar hoje"
                          : `🟢 ${d.diffRelDocs}d restante${d.diffRelDocs!==1?"s":""}`}
                      </span>
                    </td>
                    <td style={{padding:"8px 12px"}}>
                      <button onClick={()=>openView(d,"documentos")}
                        style={{fontSize:11,padding:"4px 10px",borderRadius:6,
                          border:`1px solid ${theme.blue}`,background:"#eff6ff",
                          color:theme.blue,cursor:"pointer",fontWeight:600}}>
                        → Ver
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Filtros */}
      <Card style={{padding:"14px 20px",marginBottom:16,display:"flex",gap:10,flexWrap:"wrap"}}>
        <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="Buscar cliente ou CNPJ..."
          style={{flex:1,minWidth:200,padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}/>
        <select value={filtroStatus} onChange={e=>setFiltroStatus(e.target.value)}
          style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
          <option value="">Todos os status</option>
          {STATUS_DIAG.map(s=><option key={s}>{s}</option>)}
        </select>
        <select value={filtroResp} onChange={e=>setFiltroResp(e.target.value)}
          style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:13,outline:"none"}}>
          <option value="">Todos responsáveis</option>
          {equipe.filter(e=>e.ativo).map(e=><option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
      </Card>

      {/* Tabela */}
      <Card>
        {filtrados.length===0
          ? <EmptyState icon="🔍" title="Nenhum diagnóstico encontrado" subtitle="Cadastre diagnósticos ou ajuste os filtros."/>
          : (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                    {["Cliente / CNPJ","Regime","Responsável","Parceria","Produtos Viáveis","Crédito Total","Status","Aprovação","Início","Ações"].map(h=>(
                      <th key={h} style={{padding:"11px 14px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:0.8,textTransform:"uppercase"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((d,i)=>{
                    const cli  = clientesELeads.find(x=>x.id===d.clienteId);
                    const resp = equipe.find(e=>e.id===d.responsavel);
                    const parc = parcerias.find(p=>p.id===d.parceiraId);
                    const creditoTotal  = (d.produtos||[]).reduce((s,p)=>s+(parseFloat(p.creditoApurado)||0),0);
                    const prodViav      = (d.produtos||[]).filter(p=>p.viabilidade==="Sim").length;
                    return (
                      <tr key={d.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                        <td style={{padding:"11px 14px"}}>
                          <p style={{fontSize:13,fontWeight:500,color:theme.gray900}}>{cli?.nome||d.clienteId}</p>
                          <p style={{fontSize:10,color:theme.gray400}}>{d.cnpj||"-"}</p>
                          {(d.faturamentos||[]).length>0&&(
                            <p style={{fontSize:10,color:theme.blue,marginTop:2}}>
                              {(d.faturamentos||[]).map(f=>f.ano+": R$ "+f.valor).join(" | ")}
                            </p>
                          )}
                          {!(d.faturamentos||[]).length&&d.faturamentoAnual&&(
                            <p style={{fontSize:10,color:theme.blue,marginTop:2}}>Fat: R$ {d.faturamentoAnual}</p>
                          )}
                        </td>
                        <td style={{padding:"11px 14px"}}><Badge color="gray">{d.regime||"-"}</Badge></td>
                        <td style={{padding:"11px 14px",fontSize:12,color:theme.gray600}}>{resp?.nome.split(" ").slice(0,2).join(" ")||"-"}</td>
                        <td style={{padding:"11px 14px",fontSize:12,color:theme.gray600}}>{parc?.nome||"-"}</td>
                        <td style={{padding:"11px 14px",textAlign:"center"}}>
                          <span style={{fontSize:14,fontWeight:700,color:theme.green}}>{prodViav}</span>
                          <span style={{fontSize:10,color:theme.gray400}}> / {(d.produtos||[]).length}</span>
                        </td>
                        <td style={{padding:"11px 14px",fontSize:13,fontWeight:700,color:theme.gold}}>
                          {creditoTotal>0 ? fmtR(creditoTotal) : <span style={{color:theme.gray300}}>—</span>}
                        </td>
                        <td style={{padding:"11px 14px"}}><Badge color={statusColor(d.status)}>{d.status}</Badge></td>
                        <td style={{padding:"8px 14px"}}>
                          {(()=>{
                            const sa=d.statusAprovacao||"Pendente de Aprovação";
                            const cor=aprov_COR[sa]||aprov_COR["Pendente de Aprovação"];
                            return <span style={{fontSize:10,padding:"3px 8px",borderRadius:8,fontWeight:600,
                              background:cor.bg,color:cor.color,whiteSpace:"nowrap",
                              display:"inline-flex",alignItems:"center",gap:4}}>
                              <span style={{width:6,height:6,borderRadius:"50%",background:cor.dot}}/>
                              {sa}
                            </span>;
                          })()}
                        </td>
                        <td style={{padding:"11px 14px",fontSize:11,color:theme.gray500}}>{d.dataInicio||"-"}</td>
                        <td style={{padding:"11px 14px"}}>
                          <div style={{display:"flex",gap:5}}>
                            <Btn size="sm" variant="secondary" onClick={()=>{openView(d,"resumo");}}>Ver</Btn>
                            <Btn size="sm" variant="secondary" onClick={()=>{openView(d,"documentos");}}>📋</Btn>
                            <Btn size="sm" variant="secondary" onClick={()=>openEdit(d)}>Editar</Btn>
                            <Btn size="sm" onClick={()=>setRelatorioItem(d)}>📄</Btn>
                            <Btn size="sm" variant="danger" onClick={()=>remove(d.id)}>×</Btn>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        }
      </Card>

      {/* ─── MODAL FORMULÁRIO ───────────────────────────── */}
      {editItem && (
        <Modal open={modalOpen} onClose={()=>setModalOpen(false)}
          title={editItem.id?"Editar Diagnóstico RCT":"Novo Diagnóstico RCT"} width={900}>

          {/* Cabeçalho */}
          <div style={{background:`${theme.gold}08`,border:`1px solid ${theme.gold}22`,borderRadius:10,padding:20,marginBottom:20}}>
            <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:1,marginBottom:14}}>Identificação</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Cliente / Lead" required>
                  <Select value={editItem.clienteId} onChange={v=>{
                    const cli=clientesELeads.find(x=>x.id===v);
                    setEditItem({...editItem,clienteId:v,cnpj:cli?.cpfCnpj||editItem.cnpj});
                  }} options={clientesELeads.map(x=>({value:x.id,label:x.nome}))} placeholder="Selecione cliente ou lead..."/>
                </FormField>
              </div>
              <FormField label="CNPJ">
                <Input value={editItem.cnpj} onChange={v=>setEditItem({...editItem,cnpj:v})} placeholder="00.000.000/0001-00"/>
              </FormField>
              {/* ── Faturamento Anual — múltiplos anos ── */}
              <div style={{gridColumn:"1/-1",background:"#f0fdf4",borderRadius:8,padding:"14px 16px",border:"1px solid #bbf7d0"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <p style={{fontSize:12,fontWeight:700,color:theme.green}}>💰 Faturamento Anual</p>
                  <button onClick={()=>setEditItem(prev=>({...prev,
                    faturamentos:[...(prev.faturamentos||[]),
                      {id:"FAT"+Date.now(),ano:String(new Date().getFullYear()),valor:""}
                    ]}))}
                    style={{fontSize:11,padding:"4px 10px",borderRadius:6,
                      border:"1px solid "+theme.green,background:"#f0fdf4",
                      color:theme.green,cursor:"pointer",fontWeight:600}}>
                    + Adicionar Ano
                  </button>
                </div>
                {(editItem.faturamentos||[]).length===0&&(
                  <p style={{fontSize:11,color:theme.gray400,textAlign:"center",padding:"6px 0"}}>
                    Nenhum faturamento cadastrado. Clique em "+ Adicionar Ano".
                  </p>
                )}
                {(editItem.faturamentos||[]).map((fat,fi)=>(
                  <div key={fat.id||fi} style={{display:"grid",gridTemplateColumns:"120px 1fr 32px",
                    gap:"0 10px",marginBottom:8,alignItems:"end"}}>
                    <FormField label="Ano">
                      <Input value={fat.ano||""} placeholder="2023"
                        onChange={v=>setEditItem(prev=>({...prev,
                          faturamentos:prev.faturamentos.map((f,i)=>i===fi?{...f,ano:v}:f)
                        }))}/>
                    </FormField>
                    <FormField label="Valor (R$)">
                      <Input value={fat.valor||""} placeholder="0,00"
                        onChange={v=>{
                          // Máscara de valor: apenas números e vírgula
                          const limpo = v.replace(/[^\d]/g,"");
                          const num = parseInt(limpo||"0",10)/100;
                          const mask = num.toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2});
                          setEditItem(prev=>({...prev,
                            faturamentos:prev.faturamentos.map((f,i)=>i===fi?{...f,valor:mask}:f)
                          }));
                        }}/>
                    </FormField>
                    <button onClick={()=>setEditItem(prev=>({...prev,
                      faturamentos:prev.faturamentos.filter((_,i)=>i!==fi)
                    }))} style={{fontSize:16,color:theme.red,background:"none",
                      border:"none",cursor:"pointer",padding:"4px 6px",marginBottom:2}}>×</button>
                  </div>
                ))}
              </div>
              {/* ── Regimes Tributários múltiplos ── */}
              <div style={{gridColumn:"1/-1",background:"#f0f9ff",borderRadius:8,padding:"14px 16px",border:"1px solid #bfdbfe"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <p style={{fontSize:12,fontWeight:700,color:theme.blue}}>📊 Regimes Tributários</p>
                  <button onClick={()=>setEditItem(prev=>({...prev,
                    regimes:[...(prev.regimes||[]),
                      {id:"REG"+Date.now(),tipo:"Simples Nacional",dataInicio:"",dataFim:""}
                    ]}))}
                    style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:`1px solid ${theme.blue}`,
                      background:"#eff6ff",color:theme.blue,cursor:"pointer",fontWeight:600}}>
                    + Adicionar Regime
                  </button>
                </div>
                {(editItem.regimes||[]).length===0&&(
                  <p style={{fontSize:11,color:theme.gray400,textAlign:"center",padding:"8px 0"}}>
                    Nenhum regime cadastrado. Clique em "+ Adicionar Regime".
                  </p>
                )}
                {(editItem.regimes||[]).map((reg,ri)=>(
                  <div key={reg.id||ri} style={{display:"grid",gridTemplateColumns:"1.5fr 1fr 1fr auto",
                    gap:"0 12px",marginBottom:8,alignItems:"end",
                    padding:"10px 12px",background:"#fff",borderRadius:7,border:`1px solid ${theme.gray200}`}}>
                    <FormField label="Regime">
                      <Select value={reg.tipo} onChange={v=>setEditItem(prev=>({...prev,
                        regimes:prev.regimes.map((r,i)=>i===ri?{...r,tipo:v}:r)
                      }))} options={REGIMES} placeholder="Selecione..."/>
                    </FormField>
                    <FormField label="Data Início">
                      <Input type="date" value={reg.dataInicio||""} onChange={v=>setEditItem(prev=>({...prev,
                        regimes:prev.regimes.map((r,i)=>i===ri?{...r,dataInicio:v}:r)
                      }))}/>
                    </FormField>
                    <FormField label="Data Fim">
                      <Input type="date" value={reg.dataFim||""} onChange={v=>setEditItem(prev=>({...prev,
                        regimes:prev.regimes.map((r,i)=>i===ri?{...r,dataFim:v}:r)
                      }))}/>
                    </FormField>
                    <button onClick={()=>setEditItem(prev=>({...prev,
                      regimes:prev.regimes.filter((_,i)=>i!==ri)
                    }))} style={{fontSize:14,color:theme.red,background:"none",border:"none",
                      cursor:"pointer",padding:"4px 8px",alignSelf:"center",marginTop:16}}>×</button>
                  </div>
                ))}
                {/* Retrocompatibilidade: regime antigo */}
                {(!editItem.regimes||editItem.regimes.length===0)&&editItem.regime&&(
                  <div style={{fontSize:11,color:theme.gray500,padding:"6px 0"}}>
                    Regime anterior: <b>{editItem.regime}</b> — clique em "+ Adicionar Regime" para migrar.
                  </div>
                )}
              </div>

              {/* ── Atividades ── */}
              <div style={{gridColumn:"1/-1",background:"#f0fdf4",borderRadius:8,padding:"14px 16px",border:"1px solid #bbf7d0"}}>
                <p style={{fontSize:12,fontWeight:700,color:theme.green,marginBottom:10}}>🏭 Atividades da Empresa</p>
                <FormField label="Atividade Principal">
                  <Input value={editItem.atividadePrincipal||editItem.atividade||""}
                    onChange={v=>setEditItem(prev=>({...prev,atividadePrincipal:v,atividade:v}))}
                    placeholder="Ex: Prestação de Serviços de TI"/>
                </FormField>
                <div style={{marginTop:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <p style={{fontSize:11,color:theme.gray600,fontWeight:600}}>Atividades Secundárias</p>
                    <button onClick={()=>setEditItem(prev=>({...prev,
                      atividadesSecundarias:[...(prev.atividadesSecundarias||[]),""]
                    }))} style={{fontSize:11,padding:"4px 10px",borderRadius:6,
                      border:`1px solid ${theme.green}`,background:"#f0fdf4",
                      color:theme.green,cursor:"pointer",fontWeight:600}}>
                      + Adicionar Atividade
                    </button>
                  </div>
                  {(editItem.atividadesSecundarias||[]).length===0&&(
                    <p style={{fontSize:11,color:theme.gray400}}>Nenhuma atividade secundária cadastrada.</p>
                  )}
                  {(editItem.atividadesSecundarias||[]).map((atv,ai)=>(
                    <div key={ai} style={{display:"flex",gap:8,marginBottom:6,alignItems:"center"}}>
                      <Input value={atv} onChange={v=>setEditItem(prev=>({...prev,
                        atividadesSecundarias:prev.atividadesSecundarias.map((a,i)=>i===ai?v:a)
                      }))} placeholder={`Atividade secundária ${ai+1}`}
                      style={{flex:1}}/>
                      <button onClick={()=>setEditItem(prev=>({...prev,
                        atividadesSecundarias:prev.atividadesSecundarias.filter((_,i)=>i!==ai)
                      }))} style={{fontSize:14,color:theme.red,background:"none",border:"none",cursor:"pointer",padding:"4px 8px"}}>×</button>
                    </div>
                  ))}
                </div>
              </div>
              <FormField label="Parceria de Origem">
                <Select value={editItem.parceiraId} onChange={v=>setEditItem({...editItem,parceiraId:v})}
                  options={parcerias.filter(p=>p.status==="Ativo").map(p=>({value:p.id,label:p.nome}))} placeholder="Nenhuma..."/>
              </FormField>
              <FormField label="Responsável Executor">
                <Select value={editItem.responsavel} onChange={v=>setEditItem({...editItem,responsavel:v})}
                  options={equipe.filter(e=>e.ativo!==false).map(e=>({value:e.id,label:e.nome}))} placeholder="Selecione..."/>
              </FormField>
              <FormField label="Supervisor / Aprovação">
                <Select value={editItem.supervisor} onChange={v=>setEditItem({...editItem,supervisor:v})}
                  options={equipe.filter(e=>e.ativo!==false).map(e=>({value:e.id,label:e.nome}))} placeholder="Selecione..."/>
              </FormField>
              <FormField label="Status">
                <Select value={editItem.status} onChange={v=>setEditItem({...editItem,status:v})} options={STATUS_DIAG}/>
              </FormField>
              <FormField label="Data de Início">
                <Input type="date" value={editItem.dataInicio} onChange={v=>setEditItem({...editItem,dataInicio:v})}/>
              </FormField>
              <FormField label="Data Prevista p/ Conclusão">
                <Input type="date" value={editItem.dataPrevistaConclusao||""} onChange={v=>setEditItem({...editItem,dataPrevistaConclusao:v})}/>
              </FormField>
              <FormField label="Data de Envio do Relatório de Diagnóstico">
                <Input type="date" value={editItem.dataRelatorio||""} onChange={v=>setEditItem({...editItem,dataRelatorio:v})}/>
              </FormField>
              <FormField label="Data de Envio do Relatório de Análise de Documentos">
                <Input type="date" value={editItem.dataRelatorioDocumentos||""} onChange={v=>setEditItem({...editItem,dataRelatorioDocumentos:v})}/>
              </FormField>
              <FormField label="Honorários">
                <Input value={editItem.honorarios} onChange={v=>setEditItem({...editItem,honorarios:v})} placeholder="OK / Pendente / Valor"/>
              </FormField>
              {/* ── Conclusão e Aprovação ── */}
              <div style={{gridColumn:"1/-1",background:"#f8fafc",borderRadius:8,padding:"14px 16px",border:"1px solid #e2e8f0",marginBottom:8}}>
                <p style={{fontSize:12,fontWeight:700,color:theme.gray800,marginBottom:10}}>🏁 Conclusão e Aprovação</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px"}}>
                  <FormField label="Data de Conclusão">
                    <Input type="date" value={editItem.dataConclusao||""}
                      onChange={v=>setEditItem(prev=>({
                        ...prev,
                        dataConclusao: v,
                        // Ao informar data de conclusão → status automático para Concluído
                        status: v ? "Concluído" : prev.status
                      }))}/>
                  </FormField>
                  <FormField label="Status de Aprovação">
                    <Select value={editItem.statusAprovacao||"Pendente de Aprovação"}
                      onChange={v=>setEditItem({...editItem,statusAprovacao:v})}
                      options={["Pendente de Aprovação","Aprovado","Contrato Assinado","Não Aprovado"]}/>
                  </FormField>
                  {(editItem.statusAprovacao==="Aprovado"||editItem.statusAprovacao==="Contrato Assinado")&&(
                    <FormField label="Data de Aprovação">
                      <Input type="date" value={editItem.dataAprovacao||""} onChange={v=>setEditItem({...editItem,dataAprovacao:v})}/>
                    </FormField>
                  )}
                  {editItem.statusAprovacao==="Contrato Assinado"&&(
                    <FormField label="Data de Assinatura do Contrato">
                      <Input type="date" value={editItem.dataContratoAssinado||""} onChange={v=>setEditItem({...editItem,dataContratoAssinado:v})}/>
                    </FormField>
                  )}
                </div>
              </div>

              <div style={{gridColumn:"1/-1"}}>
                <FormField label="Observações Gerais">
                  <Textarea value={editItem.obs} onChange={v=>setEditItem({...editItem,obs:v})} placeholder="Pendências, notas..."/>
                </FormField>
              </div>
            </div>
          </div>

          {/* E-CAC Ocorrências */}
          <div style={{border:`1px solid ${theme.gray200}`,borderRadius:10,padding:16,marginBottom:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8}}>
                Acompanhamento E-CAC — Ocorrências ({(editItem.ecacOcorrencias||[]).length})
              </p>
              <Btn size="sm" variant="secondary" onClick={addEcac}>+ Ocorrência</Btn>
            </div>
            {(editItem.ecacOcorrencias||[]).length===0
              ? <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"10px 0"}}>Nenhuma ocorrência registrada.</p>
              : <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {editItem.ecacOcorrencias.map((e,i)=>(
                    <div key={e.id} style={{display:"grid",gridTemplateColumns:"110px 1fr 1fr 100px 28px",gap:8,alignItems:"start"}}>
                      <input type="date" value={e.data||""} onChange={ev=>updEcac(i,"data",ev.target.value)}
                        style={{padding:"6px 8px",border:`1px solid ${theme.gray200}`,borderRadius:6,fontSize:12,outline:"none"}}/>
                      <input value={e.ocorrencia} onChange={ev=>updEcac(i,"ocorrencia",ev.target.value)}
                        placeholder="Ocorrência..."
                        style={{padding:"6px 8px",border:`1px solid ${theme.gray200}`,borderRadius:6,fontSize:12,outline:"none"}}/>
                      <input value={e.obs} onChange={ev=>updEcac(i,"obs",ev.target.value)}
                        placeholder="Observação..."
                        style={{padding:"6px 8px",border:`1px solid ${theme.gray200}`,borderRadius:6,fontSize:12,outline:"none"}}/>
                      <input type="number" value={e.valor||""} onChange={ev=>updEcac(i,"valor",ev.target.value)}
                        placeholder="Valor R$"
                        style={{padding:"6px 8px",border:`1px solid ${theme.gray200}`,borderRadius:6,fontSize:12,outline:"none"}}/>
                      <button onClick={()=>remEcac(i)}
                        style={{background:"none",border:"none",color:theme.red,cursor:"pointer",fontSize:16,padding:0,paddingTop:4}}>×</button>
                    </div>
                  ))}
                </div>
            }
          </div>

          {/* ── Produtos — visão geral + detalhe ── */}
          <div>
            {/* Painel de visão geral — todos os produtos */}
            <div style={{marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:1}}>
                  📊 Análise por Produto — {(editItem.produtos||[]).length} produtos
                </p>
                <div style={{display:"flex",gap:8,fontSize:10}}>
                  <span style={{color:theme.green,fontWeight:700}}>
                    ✅ {(editItem.produtos||[]).filter(p=>p.viabilidade==="Sim").length} Viáveis
                  </span>
                  <span style={{color:theme.amber,fontWeight:700}}>
                    🔄 {(editItem.produtos||[]).filter(p=>p.viabilidade==="Em análise"||!p.viabilidade).length} Em análise
                  </span>
                  <span style={{color:theme.gray400}}>
                    ❌ {(editItem.produtos||[]).filter(p=>p.viabilidade==="Não"||p.viabilidade==="Não se aplica").length} Não viável
                  </span>
                </div>
              </div>
              {/* Grade de produtos — todos visíveis com controle de viabilidade */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,
                maxHeight:220,overflowY:"auto",padding:"4px 0"}}>
                {(editItem.produtos||[]).map((p,i)=>{
                  const viabOk = p.viabilidade==="Sim";
                  const viabNo = p.viabilidade==="Não"||p.viabilidade==="Não se aplica";
                  const ativo  = prodAba===i;
                  return (
                    <button key={i} onClick={()=>{
                      setProdAba(i);
                      setAbaCMO("consolidado");
                      setMesPag(0);
                      // Popular documentos se ainda não foram carregados
                      const prod_i = (editItem.produtos||[])[i];
                      if(prod_i && (!prod_i.documentosRecebidos || prod_i.documentosRecebidos.length===0)){
                        const prodCat = produtos.find(pr=>pr.id===prod_i.produtoId||pr.nome===prod_i.nome);
                        const docs = popularDocumentos(prodCat, []);
                        if(docs.length>0) updProd(i, "documentosRecebidos", docs);
                      }
                    }}
                      style={{
                        padding:"7px 10px",borderRadius:7,cursor:"pointer",textAlign:"left",
                        border:`1px solid ${ativo?theme.gold:viabOk?"#22c55e33":viabNo?theme.gray200:theme.gray200}`,
                        background:ativo?`${theme.gold}18`:viabOk?"#f0fdf4":viabNo?theme.gray50:theme.white,
                        display:"flex",alignItems:"center",gap:6,
                        boxShadow:ativo?"0 0 0 2px "+theme.gold+"44":"none"
                      }}>
                      <span style={{width:8,height:8,borderRadius:"50%",flexShrink:0,
                        background:viabOk?theme.green:viabNo?theme.gray300:theme.amber}}/>
                      <span style={{fontSize:10,fontWeight:ativo?700:400,
                        color:ativo?theme.gray900:viabNo?theme.gray400:theme.gray700,
                        lineHeight:1.3,overflow:"hidden",display:"-webkit-box",
                        WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>
                        {p.nome}
                        {p.statusApuracao==="Concluído"&&<span style={{fontSize:8,marginLeft:3,color:theme.green}}>✓</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Detalhe do produto selecionado */}
            {renderFormProduto()}
          </div>

          <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:20,borderTop:`1px solid ${theme.gray100}`,marginTop:20}}>
            <Btn variant="secondary" onClick={()=>setModalOpen(false)}>Cancelar</Btn>
            {!isViz && <Btn onClick={save}>Salvar Diagnóstico</Btn>}
          </div>
        </Modal>
      )}

      {/* ─── MODAL VISUALIZAÇÃO ─────────────────────────── */}
      {/* ─── MODAL PRÉVIA PDF DOCUMENTOS ─────────────────────── */}
      {previewPDFDocs&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,
          background:"rgba(0,0,0,0.7)",zIndex:9999,
          display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
          padding:"20px"}}>
          <div style={{width:"100%",maxWidth:900,background:theme.gray900,
            borderRadius:"10px 10px 0 0",padding:"12px 20px",
            display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <p style={{color:"#fff",fontWeight:700,fontSize:13,margin:0}}>
              🖨️ Prévia — Relatório de Documentos
            </p>
            <div style={{display:"flex",gap:10}}>
              <button
                onClick={()=>{
                  const w=window.open("","_blank");
                  w.document.write(previewPDFDocs);
                  w.document.close();
                  w.print();
                }}
                style={{padding:"8px 20px",borderRadius:7,cursor:"pointer",
                  background:"#C9A84C",color:"#fff",border:"none",
                  fontSize:13,fontWeight:700}}>
                🖨️ Imprimir / Salvar PDF
              </button>
              <button onClick={()=>setPreviewPDFDocs(null)}
                style={{padding:"8px 16px",borderRadius:7,cursor:"pointer",
                  background:"#ef4444",color:"#fff",border:"none",
                  fontSize:13,fontWeight:700}}>
                ✕ Fechar
              </button>
            </div>
          </div>
          <iframe
            srcDoc={previewPDFDocs}
            style={{width:"100%",maxWidth:900,height:"75vh",
              border:"none",background:"#fff",
              borderRadius:"0 0 10px 10px",
              boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}}
          />
        </div>
      )}

      {viewItem && (()=>{
        const cli  = clientesELeads.find(x=>x.id===viewItem.clienteId);
        const resp = equipe.find(e=>e.id===viewItem.responsavel);
        const sup  = equipe.find(e=>e.id===viewItem.supervisor);
        const parc = parcerias.find(p=>p.id===viewItem.parceiraId);
        const creditoTotal = (viewItem.produtos||[]).reduce((s,p)=>s+(parseFloat(p.creditoApurado)||0),0);
        return (
          <Modal open={!!viewItem} onClose={()=>setViewItem(null)} title={"RCT — "+(cli?.nome||viewItem.clienteId)} width={800}>
            <div style={{display:"flex",gap:4,borderBottom:`1px solid ${theme.gray200}`,marginBottom:20}}>
              {[["resumo","Resumo"],["produtos","Produtos Viáveis"],["documentos","📋 Documentos"],["ecac","E-CAC"],["observacoes","Observações"]].map(([a,l])=>(
                <button key={a} onClick={()=>setAbaView(a)} style={{
                  padding:"8px 16px",border:"none",background:"none",cursor:"pointer",
                  fontSize:12,fontWeight:abaView===a?700:400,
                  color:abaView===a?theme.gray900:theme.gray400,
                  borderBottom:abaView===a?`2px solid ${theme.gold}`:"2px solid transparent",
                  marginBottom:-1
                }}>{l}</button>
              ))}
            </div>

            {abaView==="resumo" && (
              <div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
                  {[
                    {l:"Cliente",    v:cli?.nome||"-"},
                    {l:"CNPJ",       v:viewItem.cnpj||"-"},
                    {l:"Regime", v:(viewItem.regimes&&viewItem.regimes.length>0)
                      ? viewItem.regimes.map(r=>r.tipo+(r.dataInicio?" ("+new Date(r.dataInicio+"T12:00").toLocaleDateString("pt-BR",{month:"2-digit",year:"numeric"}):"")+(r.dataFim?"–"+new Date(r.dataFim+"T12:00").toLocaleDateString("pt-BR",{month:"2-digit",year:"numeric"})+")":"") ).join(" · ")
                      : viewItem.regime||"-"},
                    {l:"Atividade", v:(viewItem.atividadePrincipal||viewItem.atividade||"-")+(viewItem.atividadesSecundarias?.length>0?" + "+(viewItem.atividadesSecundarias.length)+" sec.":"")},
                    {l:"Responsável",v:resp?.nome||"-"},
                    {l:"Supervisor", v:sup?.nome||"-"},
                    {l:"Parceria",   v:parc?.nome||"-"},
                    {l:"Honorários", v:viewItem.honorarios||"-"},
                    {l:"Início",        v:viewItem.dataInicio||"-"},
                    {l:"Prev. Conclusão", v:(()=>{
                      const d = viewItem.dataPrevistaConclusao;
                      if(!d) return "-";
                      const diff = Math.ceil((new Date(d+"T12:00")-new Date())/(1000*60*60*24));
                      const fmt  = new Date(d+"T12:00").toLocaleDateString("pt-BR");
                      const cor  = diff<0?theme.red:diff<=7?"#d97706":theme.green;
                      const label= diff<0?` ⚠️ ${Math.abs(diff)}d vencido`:diff===0?" (hoje)":` (${diff}d)`;
                      return <span style={{color:cor,fontWeight:600}}>{fmt}{label}</span>;
                    })()},
                  ].map(r=>(
                    <div key={r.l}>
                      <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{r.l}</p>
                      <p style={{fontSize:13,color:theme.gray800,marginTop:3,fontWeight:500}}>{r.v}</p>
                    </div>
                  ))}
                </div>
                <div style={{padding:"16px 20px",background:`${theme.gold}0A`,border:`1px solid ${theme.gold}22`,borderRadius:10,marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>Crédito Total Apurado</p>
                    <p style={{fontSize:28,fontWeight:700,color:theme.gold,marginTop:4}}>R$ {creditoTotal.toLocaleString("pt-BR",{minimumFractionDigits:2})}</p>
                  </div>
                  <Badge color={statusColor(viewItem.status)}>{viewItem.status}</Badge>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
                  {(viewItem.produtos||[]).map((p,i)=>(
                    <div key={i} style={{
                      padding:"12px 16px",borderRadius:8,
                      background:p.viabilidade==="Sim"?`${theme.green}08`:p.viabilidade==="Não se aplica"?theme.gray50:`${theme.amber}08`,
                      border:`1px solid ${p.viabilidade==="Sim"?theme.green+"44":p.viabilidade==="Não se aplica"?theme.gray200:theme.amber+"44"}`
                    }}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                        <p style={{fontSize:12,fontWeight:600,color:theme.gray800}}>{p.nome}</p>
                        <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:4,color:viabColor(p.viabilidade),background:p.viabilidade==="Sim"?`${theme.green}22`:p.viabilidade==="Não se aplica"?theme.gray100:`${theme.amber}22`}}>{p.viabilidade}</span>
                      </div>
                      {parseFloat(p.creditoApurado)>0 && <p style={{fontSize:14,fontWeight:700,color:theme.gold}}>{fmtR(p.creditoApurado)}</p>}
                      <p style={{fontSize:10,color:theme.gray400,marginTop:3}}>{p.statusAnalise}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {abaView==="produtos" && (
              <div>
                {(viewItem.produtos||[]).filter(p=>p.viabilidade==="Sim").length===0
                  ? <p style={{color:theme.gray400,textAlign:"center",padding:"24px 0"}}>Nenhum produto com viabilidade confirmada.</p>
                  : (viewItem.produtos||[]).filter(p=>p.viabilidade==="Sim").map((p,i)=>(
                    <div key={i} style={{marginBottom:20,border:`1px solid ${theme.gray200}`,borderRadius:10,overflow:"hidden"}}>
                      <div style={{padding:"12px 18px",background:`${theme.green}08`,borderBottom:`1px solid ${theme.gray200}`,display:"flex",justifyContent:"space-between"}}>
                        <p style={{fontSize:14,fontWeight:700,color:theme.green}}>{p.nome}</p>
                        {parseFloat(p.creditoApurado)>0 && <p style={{fontSize:15,fontWeight:700,color:theme.gold}}>{fmtR(p.creditoApurado)}</p>}
                      </div>
                      <div style={{padding:"14px 18px",display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
                        {[
                          {l:"REST",v:fmtR(p.sdoPerdcompRest)},{l:"COMP",v:fmtR(p.sdoPerdcompComp)},
                          {l:"SDO PERDCOMP",v:fmtR(p.sdoPerdcomp)},{l:"PERDCOMP Ref.",v:p.perdcomp||"-"},
                          {l:"Status PERDCOMP",v:p.perdcompStatus||"-"},{l:"SDO +360 REST",v:fmtR(p.sdoMais360Rest)},
                          {l:"SDO +360 COMP",v:fmtR(p.sdoMais360Comp)},{l:"MS",v:fmtR(p.sdoMais360MS)},
                          {l:"Status Análise",v:p.statusAnalise||"-"},
                        ].map(r=>(<div key={r.l}><p style={{fontSize:9,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{r.l}</p><p style={{fontSize:13,fontWeight:600,color:theme.gray800,marginTop:2}}>{r.v}</p></div>))}
                      </div>
                      {p.obs && <div style={{padding:"10px 18px",background:theme.gray50,borderTop:`1px solid ${theme.gray100}`}}><p style={{fontSize:11,color:theme.gray500,fontStyle:"italic"}}>{p.obs}</p></div>}
                      {/* ── Ferramentas de Execução ── */}
                      {(()=>{
                        const prodCad = produtos.find(pr=>pr.id===p.produtoId||pr.nome===p.nome);
                        const ferrs = prodCad?.ferramentasExecucao||[];
                        const TIPO_ICON = {sistema:"🌐",excel:"📊",google_sheets:"📋",pdf:"📄",outro:"🔧"};
                        if(ferrs.length===0) return (
                          <div style={{padding:"8px 18px",borderTop:`1px solid ${theme.gray100}`,
                            background:"#f9fafb"}}>
                            <p style={{fontSize:11,color:theme.gray400,fontStyle:"italic"}}>
                              Nenhuma ferramenta cadastrada — adicione em Produtos → editar → Ferramentas de Execução.
                            </p>
                          </div>
                        );
                        return (
                          <div style={{padding:"10px 18px",borderTop:`1px solid ${theme.gray100}`,
                            background:"#f0fdf4"}}>
                            <p style={{fontSize:10,color:theme.green,fontWeight:700,
                              textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>
                              🛠️ Ferramentas de Execução
                            </p>
                            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                              {ferrs.map((ferr,fi)=>(
                                <a key={fi}
                                  href={ferr.url||"#"}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={e=>!ferr.url&&e.preventDefault()}
                                  style={{display:"flex",alignItems:"center",gap:5,
                                    padding:"7px 14px",borderRadius:8,fontSize:12,fontWeight:600,
                                    background:"#eff6ff",color:theme.blue,
                                    border:"1px solid #bfdbfe",textDecoration:"none",
                                    opacity:ferr.url?1:0.5,
                                    cursor:ferr.url?"pointer":"default"}}>
                                  {TIPO_ICON[ferr.tipo]||"🔧"} {ferr.nome||"Ferramenta"}
                                  {ferr.url&&<span style={{fontSize:10,opacity:.7}}>↗</span>}
                                </a>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ))
                }
              </div>
            )}

            {abaView==="documentos" && (()=>{
              // Apenas produtos com viabilidade Sim
              const prodsSim = (viewItem.produtos||[]).filter(p=>p.viabilidade==="Sim");
              const totalDocs   = prodsSim.reduce((s,p)=>(p.documentosRecebidos||[]).length?s+(p.documentosRecebidos||[]).length:s,0);
              const docsReceb   = prodsSim.reduce((s,p)=>s+(p.documentosRecebidos||[]).filter(d=>d.recebido).length,0);
              const docsPend    = totalDocs - docsReceb;

              const gerarPDFDocs = () => {
                const cli2 = clientesELeads.find(x=>x.id===viewItem.clienteId);
                const fmtD3 = d=>d?new Date(d+"T12:00").toLocaleDateString("pt-BR"):"—";
                let regimesStr = viewItem.regime||"—";
                if(viewItem.regimes&&viewItem.regimes.length>0){
                  const partes=[];
                  for(let ri=0;ri<viewItem.regimes.length;ri++){
                    const rg=viewItem.regimes[ri];
                    let s=rg.tipo;
                    if(rg.dataInicio) s+=" ("+fmtD3(rg.dataInicio);
                    if(rg.dataFim) s+="–"+fmtD3(rg.dataFim);
                    if(rg.dataInicio) s+=")";
                    partes.push(s);
                  }
                  regimesStr=partes.join(", ");
                }
                const prodsSim2 = (viewItem.produtos||[]).filter(p=>p.viabilidade==="Sim");

                // ══ Consolidar documentos — deduplicar por diagnóstico ══
                const docsMap = {}; // docId → {nome, recebido, dataRecebimento, produtos[]}
                for(let pi=0;pi<prodsSim2.length;pi++){
                  const prod2=prodsSim2[pi];
                  const docs2=prod2.documentosRecebidos||[];
                  for(let di=0;di<docs2.length;di++){
                    const doc2=docs2[di];
                    const key=doc2.docId||doc2.nome;
                    if(!docsMap[key]){
                      docsMap[key]={
                        nome: doc2.nome,
                        recebido: doc2.recebido,
                        dataRecebimento: doc2.dataRecebimento||"",
                        produtos: [prod2.nome]
                      };
                    } else {
                      // Já existe — atualizar status (se recebido em qualquer produto, marcar recebido)
                      if(doc2.recebido) docsMap[key].recebido=true;
                      if(doc2.dataRecebimento) docsMap[key].dataRecebimento=doc2.dataRecebimento;
                      if(!docsMap[key].produtos.includes(prod2.nome))
                        docsMap[key].produtos.push(prod2.nome);
                    }
                  }
                }
                const docsConsolidados = Object.values(docsMap);
                const totalDocs2   = docsConsolidados.length;
                const recebidos2   = docsConsolidados.filter(d=>d.recebido).length;
                const pendentes2   = totalDocs2 - recebidos2;

                // Ordenar: pendentes primeiro, depois recebidos
                docsConsolidados.sort((a,b)=>a.recebido===b.recebido?a.nome.localeCompare(b.nome,"pt-BR"):(a.recebido?1:-1));

                // Gerar linhas da tabela
                const rows2=[];
                for(let di=0;di<docsConsolidados.length;di++){
                  const doc2=docsConsolidados[di];
                  rows2.push(
                    "<tr style='background:"+(di%2===0?"#fff":"#f9f7f2")+"'>"
                    +"<td style='padding:7px 10px;font-size:11px'>"+(di+1)+". "+doc2.nome+"</td>"
                    +"<td style='padding:7px 10px;font-size:10px;color:#6b7280'>"
                    +doc2.produtos.join(", ")+"</td>"
                    +"<td style='padding:7px 10px;font-size:11px;text-align:center;font-weight:700;color:"
                    +(doc2.recebido?"#16a34a":"#dc2626")+"'>"
                    +(doc2.recebido?"✅ Recebido"+(doc2.dataRecebimento?" ("+fmtD3(doc2.dataRecebimento)+")":""):"⏳ Pendente")
                    +"</td></tr>"
                  );
                }

                const html="<html><head><title>Documentos — "+(cli2?.nome||"")+"</title><style>"
                  +"body{font-family:Arial,sans-serif;padding:24px;color:#2A2420;font-size:12px}"
                  +"h1{font-size:13px;font-weight:bold;text-align:center;text-transform:uppercase;border-bottom:2px solid #C9A84C;padding-bottom:8px;margin-bottom:4px}"
                  +"h2{font-size:11px;font-weight:bold;text-align:center;color:#C9A84C;margin-bottom:16px}"
                  +"table{width:100%;border-collapse:collapse;margin-bottom:16px}"
                  +"th{background:#1a1610;color:#c9a84c;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.5px}"
                  +".kpi{display:inline-block;padding:8px 16px;margin:0 8px 16px 0;border:1px solid #ddd;border-radius:6px;text-align:center}"
                  +".cli{background:#f9f7f2;border-left:3px solid #C9A84C;padding:10px 14px;margin-bottom:16px;border-radius:4px}"
                  +".rf{font-size:10px;color:#999;margin-top:24px;border-top:1px solid #eee;padding-top:8px;text-align:center}"
                  +"@media print{@page{margin:15mm}}"
                  +"</style></head><body>"
                  +"<h1>RELATÓRIO DE DOCUMENTOS NECESSÁRIOS</h1>"
                  +"<h2>Diagnóstico de Recuperação de Crédito Tributário</h2>"
                  +"<div class='cli'>"
                  +"<p style='font-size:16px;font-weight:700;margin:0 0 4px;color:#1a1610'>"+(cli2?.nome||"—")+"</p>"
                  +"<p style='font-size:11px;color:#6b7280;margin:0'>CNPJ: "+(viewItem.cnpj||"—")
                  +" &nbsp;|&nbsp; Regime: "+regimesStr+"</p>"
                  +"</div>"
                  +"<div style='margin-bottom:16px'>"
                  +"<span class='kpi'><b>"+prodsSim2.length+"</b><br><small>Produtos Viáveis</small></span>"
                  +"<span class='kpi'><b style='color:#16a34a'>"+recebidos2+"</b><br><small>Docs Recebidos</small></span>"
                  +"<span class='kpi'><b style='color:#dc2626'>"+pendentes2+"</b><br><small>Docs Pendentes</small></span>"
                  +"<span class='kpi'><b>"+totalDocs2+"</b><br><small>Total de Docs</small></span>"
                  +"</div>"
                  +(pendentes2>0
                    ?"<div style='padding:10px 14px;background:#fef2f2;border:1px solid #fecaca;border-radius:6px;margin-bottom:16px'>"
                    +"<p style='font-size:12px;font-weight:700;color:#dc2626;margin:0 0 4px'>⏳ "+pendentes2+" documento(s) pendente(s)</p>"
                    +"<p style='font-size:11px;color:#6b7280;margin:0'>Solicitamos o envio dos documentos abaixo para darmos início ao diagnóstico tributário.</p>"
                    +"</div>":""
                  )
                  +"<table><thead><tr>"
                  +"<th>Documento</th><th>Produto(s) Relacionado(s)</th><th style='text-align:center'>Status</th>"
                  +"</tr></thead><tbody>"+rows2.join("")+"</tbody></table>"
                  +(viewItem.obs
                    ?"<div style='margin-bottom:24px;padding:12px 16px;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;border-left:4px solid #C9A84C'>"
                    +"<p style='font-size:10px;font-weight:700;color:#92400e;text-transform:uppercase;letter-spacing:.8px;margin:0 0 6px'>Observações Gerais</p>"
                    +"<p style='font-size:11px;color:#374151;margin:0;white-space:pre-line'>"+viewItem.obs+"</p>"
                    +"</div>"
                    :""
                  )
                  +"<p style='font-size:11px;color:#374151;margin-bottom:24px'>"
                  +"Atenciosamente,<br><strong>Alves & Correa Advogados</strong>"
                  +"</p>"
                  +"<div class='rf'>Alves & Correa Advogados · Nelson Jose Alves OAB 398.875/SP · Annie Lemos Alves Correa OAB 446.942/SP · www.alvesecorreaadvogados.com.br · Documento Confidencial</div>"
                  +"</body></html>";
                // Abrir modal de prévia em vez de imprimir direto
                setPreviewPDFDocs(html);
              };

              return (
                <div>
                  {/* KPIs */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
                    {[
                      {l:"Produtos Viáveis",  v:prodsSim.length, c:theme.green},
                      {l:"Docs Recebidos",    v:docsReceb,       c:theme.green},
                      {l:"Docs Pendentes",    v:docsPend,        c:docsPend>0?theme.red:theme.gray400},
                    ].map(k=>(
                      <div key={k.l} style={{padding:"12px",borderRadius:8,background:theme.white,
                        border:`1px solid ${k.c}22`,textAlign:"center"}}>
                        <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>{k.l}</p>
                        <p style={{fontSize:22,fontWeight:700,color:k.c}}>{k.v}</p>
                      </div>
                    ))}
                  </div>
                  {/* Botão PDF */}
                  <div style={{display:"flex",justifyContent:"flex-end",marginBottom:16}}>
                    <button onClick={gerarPDFDocs} style={{padding:"8px 16px",borderRadius:7,cursor:"pointer",
                      border:`1px solid ${theme.gray200}`,background:theme.white,
                      color:theme.gray700,fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>
                      🖨️ Exportar PDF
                    </button>
                  </div>
                  {/* Lista por produto */}
                  {prodsSim.length===0
                    ? <p style={{color:theme.gray400,textAlign:"center",padding:"24px 0"}}>
                        Nenhum produto com viabilidade confirmada.
                      </p>
                    : prodsSim.map(prod=>{
                        const docs = prod.documentosRecebidos||[];
                        const receb = docs.filter(d=>d.recebido).length;
                        const pend  = docs.length - receb;
                        return (
                          <div key={prod.produtoId} style={{marginBottom:14,border:`1px solid ${theme.gray200}`,borderRadius:10,overflow:"hidden"}}>
                            <div style={{padding:"10px 16px",background:pend>0?"#fef2f2":"#f0fdf4",
                              display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                              <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:0}}>{prod.nome}</p>
                              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                                {receb>0&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:"#dcfce7",color:theme.green,fontWeight:600}}>✅ {receb} recebido(s)</span>}
                                {pend>0&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:"#fecaca",color:theme.red,fontWeight:600}}>⏳ {pend} pendente(s)</span>}
                                {docs.length===0&&<span style={{fontSize:11,color:theme.gray400}}>Sem documentos</span>}
                              </div>
                            </div>
                            {docs.length===0
                              ? <p style={{padding:"12px 16px",fontSize:11,color:theme.gray400,fontStyle:"italic"}}>
                                  Nenhum documento cadastrado para este produto. Adicione em Produtos → editar → Documentos.
                                </p>
                              : <div style={{padding:"8px 0"}}>
                                  {docs.map((doc,di)=>(
                                    <div key={doc.docId||di} style={{display:"flex",alignItems:"center",
                                      justifyContent:"space-between",padding:"7px 16px",
                                      borderBottom:di<docs.length-1?`1px solid ${theme.gray100}`:"none",
                                      background:doc.recebido?"#f0fdf4":theme.white}}>
                                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                                        <span style={{fontSize:14,color:doc.recebido?theme.green:theme.amber}}>
                                          {doc.recebido?"✅":"⏳"}
                                        </span>
                                        <div>
                                          <p style={{fontSize:12,color:theme.gray800,margin:0,fontWeight:doc.obrigatorio?600:400}}>
                                            {doc.nome}
                                            {doc.obrigatorio&&<span style={{fontSize:9,color:theme.red,marginLeft:4}}>*</span>}
                                          </p>
                                          {doc.recebido&&doc.dataRecebimento&&(
                                            <p style={{fontSize:10,color:theme.gray400,margin:0}}>Recebido em {new Date(doc.dataRecebimento+"T12:00").toLocaleDateString("pt-BR")}</p>
                                          )}
                                        </div>
                                      </div>
                                      <span style={{fontSize:11,padding:"2px 8px",borderRadius:8,fontWeight:600,
                                        background:doc.recebido?"#dcfce7":"#fef3c7",
                                        color:doc.recebido?theme.green:"#92400e"}}>
                                        {doc.recebido?"Recebido":"Pendente"}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                            }
                          </div>
                        );
                      })
                  }
                </div>
              );
            })()}

            {abaView==="ecac" && (
              <div>
                {(!viewItem.ecacOcorrencias||viewItem.ecacOcorrencias.length===0)
                  ? <p style={{color:theme.gray400,textAlign:"center",padding:"24px 0"}}>Nenhuma ocorrência E-CAC registrada.</p>
                  : <table style={{width:"100%",borderCollapse:"collapse"}}>
                      <thead>
                        <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                          {["Data","Ocorrência","Observação","Valor"].map(h=>(
                            <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {viewItem.ecacOcorrencias.map((e,i)=>(
                          <tr key={i} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                            <td style={{padding:"9px 12px",fontSize:12,color:theme.gray600}}>{e.data}</td>
                            <td style={{padding:"9px 12px",fontSize:13,color:theme.gray800}}>{e.ocorrencia}</td>
                            <td style={{padding:"9px 12px",fontSize:12,color:theme.gray600}}>{e.obs||"-"}</td>
                            <td style={{padding:"9px 12px",fontSize:13,fontWeight:600,color:theme.gold}}>{e.valor?fmtR(e.valor):"-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                }
              </div>
            )}

            {abaView==="observacoes" && (
              <div style={{padding:"14px 18px",background:theme.gray50,borderRadius:10,fontSize:13,color:theme.gray700,lineHeight:1.7,minHeight:80}}>
                {viewItem.obs||<span style={{color:theme.gray400}}>Nenhuma observação registrada.</span>}
              </div>
            )}

            {/* ── Card de Aprovação ── */}
            {(()=>{
              const sa=viewItem.statusAprovacao||"Pendente de Aprovação";
              const cor=aprov_COR[sa]||aprov_COR["Pendente de Aprovação"];
              const fD=d=>d?new Date(d+"T12:00").toLocaleDateString("pt-BR"):"—";
              return(
                <div style={{margin:"16px 0",padding:"14px 16px",borderRadius:10,background:cor.bg,border:"1px solid "+cor.dot+"44"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{width:10,height:10,borderRadius:"50%",background:cor.dot}}/>
                      <p style={{fontSize:13,fontWeight:700,color:cor.color,margin:0}}>{sa}</p>
                    </div>
                    <div style={{display:"flex",gap:16}}>
                      {viewItem.dataConclusao&&<div style={{textAlign:"right"}}>
                        <p style={{fontSize:9,color:cor.color,opacity:.7,textTransform:"uppercase",margin:0}}>Concluído em</p>
                        <p style={{fontSize:12,fontWeight:600,color:cor.color,margin:0}}>{fD(viewItem.dataConclusao)}</p>
                      </div>}
                      {viewItem.dataAprovacao&&<div style={{textAlign:"right"}}>
                        <p style={{fontSize:9,color:cor.color,opacity:.7,textTransform:"uppercase",margin:0}}>Aprovado em</p>
                        <p style={{fontSize:12,fontWeight:600,color:cor.color,margin:0}}>{fD(viewItem.dataAprovacao)}</p>
                      </div>}
                      {viewItem.dataContratoAssinado&&<div style={{textAlign:"right"}}>
                        <p style={{fontSize:9,color:cor.color,opacity:.7,textTransform:"uppercase",margin:0}}>Contrato assinado em</p>
                        <p style={{fontSize:12,fontWeight:600,color:cor.color,margin:0}}>{fD(viewItem.dataContratoAssinado)}</p>
                      </div>}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── Card de Execução — produtos com viabilidade Sim ── */}
            {(()=>{
              const prodsViavel = (viewItem.produtos||[]).filter(p=>p.viabilidade==="Sim");
              if(prodsViavel.length===0) return null;
              const TIPO_ICON = {sistema:"🌐",excel:"📊",google_sheets:"📋",pdf:"📄",outro:"🔧"};
              return (
                <div style={{marginTop:16,marginBottom:16,padding:"16px",
                  borderRadius:10,border:"2px solid #22c55e44",background:"#f0fdf4"}}>
                  <p style={{fontSize:12,fontWeight:700,color:theme.green,
                    textTransform:"uppercase",letterSpacing:.8,marginBottom:12}}>
                    ✅ Execução — Produtos Viáveis ({prodsViavel.length})
                  </p>
                  <div style={{display:"flex",flexDirection:"column",gap:12}}>
                    {prodsViavel.map(prod=>{
                      // Buscar ferramentas do produto cadastrado
                      const prodCad = produtos.find(p=>p.id===prod.produtoId||p.nome===prod.nome);
                      const ferramentas = prodCad?.ferramentasExecucao||[];
                      const prodPadrao = PRODUTOS_RCT_PADRAO.find(p=>p.id===prod.produtoId);
                      return (
                        <div key={prod.produtoId} style={{background:"#fff",borderRadius:8,
                          padding:"12px 14px",border:`1px solid ${theme.green}33`}}>
                          <div style={{display:"flex",justifyContent:"space-between",
                            alignItems:"center",marginBottom:ferramentas.length>0?10:0}}>
                            <div>
                              <p style={{fontSize:13,fontWeight:700,color:theme.gray900,margin:0}}>
                                {prod.nome}
                              </p>
                              {prod.creditoApurado&&(
                                <p style={{fontSize:11,color:theme.green,margin:"2px 0 0",fontWeight:600}}>
                                  💰 Crédito apurado: R$ {parseFloat(prod.creditoApurado).toLocaleString("pt-BR",{minimumFractionDigits:2})}
                                </p>
                              )}
                            </div>
                            <span style={{fontSize:11,padding:"3px 10px",borderRadius:10,
                              background:"#dcfce7",color:theme.green,fontWeight:700}}>
                              ✅ Viável
                            </span>
                          </div>
                          {ferramentas.length>0&&(
                            <div>
                              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",
                                letterSpacing:.8,marginBottom:6}}>Ferramentas de Execução</p>
                              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                                {ferramentas.map((ferr,fi)=>(
                                  <a key={fi} href={ferr.url||"#"} target="_blank" rel="noopener noreferrer"
                                    style={{display:"flex",alignItems:"center",gap:5,
                                      padding:"6px 12px",borderRadius:7,fontSize:12,fontWeight:600,
                                      background:"#eff6ff",color:theme.blue,
                                      border:"1px solid #bfdbfe",textDecoration:"none",
                                      cursor:ferr.url?"pointer":"default"}}>
                                    {TIPO_ICON[ferr.tipo]||"🔧"} {ferr.nome||"Ferramenta"}
                                    {ferr.url&&<span style={{fontSize:10,opacity:.7}}>↗</span>}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                          {ferramentas.length===0&&(
                            <p style={{fontSize:11,color:theme.gray400,fontStyle:"italic"}}>
                              Nenhuma ferramenta cadastrada para este produto. Cadastre em Produtos → editar produto → Ferramentas de Execução.
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div style={{display:"flex",justifyContent:"flex-end",gap:10,paddingTop:20,borderTop:`1px solid ${theme.gray100}`,marginTop:20}}>
              <Btn onClick={()=>{setRelatorioItem(viewItem);setViewItem(null);}}>📄 Gerar Relatório</Btn>
              <Btn variant="secondary" onClick={()=>{setViewItem(null);openEdit(diagnosticosRCT.find(d=>d.id===viewItem.id));}}>Editar</Btn>
              <Btn variant="secondary" onClick={()=>setViewItem(null)}>Fechar</Btn>
            </div>
          </Modal>
        );
      })()}

      {/* Modal Relatório */}
      {relatorioItem && (
        <ModalRelatorioRCT
          diagnostico={relatorioItem}
          clientes={clientes}
          clientesELeads={clientesELeads}
          equipe={equipe}
          onClose={()=>setRelatorioItem(null)}
        />
      )}
    </div>
  );
}



// ============================================================
// GERADOR DE RELATÓRIO RCT
// ============================================================
const LOGO_CLEAN_B64 = "data:image/jpeg;base64,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";

function ModalRelatorioRCT({ diagnostico, clientes, clientesELeads, equipe, onClose }) {
  // Buscar em clientesELeads (inclui leads) ou fallback em clientes
  const cli  = (clientesELeads||[]).find(x => x.id === diagnostico.clienteId)
             || clientes.find(c => c.id === diagnostico.clienteId);
  const resp = equipe.find(e => e.id === diagnostico.responsavel);
  const sup  = equipe.find(e => e.id === diagnostico.supervisor);
  const hoje = new Date().toLocaleDateString("pt-BR", { day:"2-digit", month:"long", year:"numeric" });
  const [aprovado, setAprovado] = useState(false);

  // Todos os produtos com viabilidade = Sim (inclusive sem saldo e Pendência de Documento)
  const produtosViaveis = (diagnostico.produtos || []).filter(p =>
    p.viabilidade === "Sim"
  );

  // CMO: soma automática col.2 (saldoApuradoInss) dos 60 meses
  const produtosRelatorio = produtosViaveis.map(p => {
    if (p.campos === "cmo") {
      const totalCMO = (p.competencias || []).reduce((s, c) => s + (parseFloat(c.saldoApuradoInss) || 0), 0);
      return { ...p, valorFinal: totalCMO };
    }
    return { ...p, valorFinal: parseFloat(p.creditoApurado) || 0 };
  }); // sem filtro por valor — todos os viáveis aparecem

  const totalPrincipal = produtosRelatorio
    .filter(p => p.statusApuracao !== "Pendência de Documento")
    .reduce((s, p) => s + p.valorFinal, 0);
  const fmtR = v => v.toLocaleString("pt-BR", { minimumFractionDigits: 2 });

  const getDescricao = p => {
    const padrao = PRODUTOS_RCT_PADRAO.find(rp => rp.id === p.produtoId);
    const descTecnica = padrao?.descricaoRelatorio || "Crédito tributário identificado conforme legislação vigente.";
    const obs = p.obs ? p.obs.trim() : "";

    // Pendência de Documento: exibir apenas a Observação Técnica do produto
    if (p.statusApuracao === "Pendência de Documento") {
      return obs || "Produto viável — documentação pendente para conclusão da apuração.";
    }

    // Demais status: descrição técnica + observação (se houver)
    return obs ? descTecnica + " " + obs : descTecnica;
  };

  const handlePrint = () => {
    const el = document.getElementById("relatorio-rct");
    if(!el) return;
    const estilos = "*{box-sizing:border-box;margin:0;padding:0}"
      + "body{font-family:'Times New Roman',Times,serif;font-size:12px;color:#111;line-height:1.6;background:#fff}"
      + "@media print{@page{size:A4;margin:10mm 12mm}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}"
      + "img{max-width:100%;height:auto}"
      + "table{width:100%;border-collapse:collapse}"
      + "th,td{padding:6px 10px;border:1px solid #ddd;font-size:11px}"
      + "th{background:#1a1610;color:#C9A84C;font-weight:bold}";
    const html = "<!DOCTYPE html><html><head><meta charset='utf-8'>"
      + "<title>Relatorio de Diagnostico Tributario</title>"
      + "<style>" + estilos + "</style>"
      + "</head><body>" + el.innerHTML + "</body></html>";
    const w = window.open("","_blank","width=900,height=700");
    w.document.write(html);
    w.document.close();
    w.onload = function(){ w.focus(); w.print(); };
  };

  return (
    <Modal open={true} onClose={onClose} title="Relatório de Diagnóstico Tributário" width={880}>
      {/* Barra de ações */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16}}>
        <label style={{display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:13, color:theme.gray700}}>
          <input type="checkbox" checked={aprovado} onChange={e => setAprovado(e.target.checked)}
            style={{accentColor:theme.gold, width:16, height:16}}/>
          <span>Revisei e aprovo o relatório para geração do PDF</span>
        </label>
        <div style={{display:"flex", gap:8}}>
          <Btn variant="secondary" onClick={onClose}>Fechar</Btn>
          <Btn onClick={handlePrint}
            style={{opacity:aprovado?1:0.4, cursor:aprovado?"pointer":"not-allowed", pointerEvents:aprovado?"auto":"none"}}>
            📄 Gerar PDF
          </Btn>
        </div>
      </div>

      {!aprovado && (
        <div style={{padding:"10px 16px", background:`${theme.amber}11`, border:`1px solid ${theme.amber}44`,
          borderRadius:8, marginBottom:16, fontSize:12, color:theme.amber}}>
          ⚠ Revise o relatório abaixo e marque a aprovação para liberar a geração do PDF.
        </div>
      )}

      {/* DOCUMENTO */}
      <div id="relatorio-rct" style={{
        background:"white", padding:"40px 50px",
        fontFamily:"'Times New Roman', Times, serif",
        fontSize:12, color:"#111", lineHeight:1.6,
        border:`1px solid ${theme.gray200}`, borderRadius:8
      }}>

        {/* Cabeçalho */}
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center",
          paddingBottom:16, marginBottom:24, borderBottom:"3px solid #C9A84C"}}>
          <img src={LOGO_CLEAN_B64} alt="Alves & Correa" style={{height:70, objectFit:"contain"}}/>
          <div style={{textAlign:"right"}}>
            <p style={{fontSize:10, color:"#666", marginBottom:2}}>Alves & Correa Advogados</p>
            <p style={{fontSize:10, color:"#666", marginBottom:2}}>Especialistas em Recuperação de Ativos Tributários</p>
            <p style={{fontSize:10, color:"#C9A84C", fontWeight:"bold"}}>www.alvesecorreaadvogados.com.br</p>
          </div>
        </div>

        {/* Título */}
        <div style={{textAlign:"center", marginBottom:28}}>
          <p style={{fontSize:17, fontWeight:"bold", letterSpacing:1, marginBottom:6}}>
            RELATÓRIO EXECUTIVO PRELIMINAR
          </p>
          <p style={{fontSize:14, fontWeight:"bold", letterSpacing:1, marginBottom:6, textAlign:"center"}}>
            DIAGNÓSTICO DE ATIVOS TRIBUTÁRIOS RECUPERÁVEIS
          </p>
          <div style={{width:60, height:2, background:"#C9A84C", margin:"0 auto 12px"}}/>
        </div>

        {/* Identificação */}
        <div style={{marginBottom:20, padding:"14px 18px", background:"#FDFBF5",
          border:"1px solid #E5E0D5", borderRadius:6}}>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 24px"}}>
            {[
              {l:"Data",              v: hoje},
              {l:"Ref.",              v: "Posição de Créditos Disponíveis (Últimos 60 meses)"},
              {l:"Cliente",           v: cli?.nome || diagnostico.clienteId},
              {l:"CNPJ",              v: diagnostico.cnpj || "—"},
              {l:"Faturamento Anual", v: (()=>{
                const fats = diagnostico.faturamentos||[];
                if(fats.length>0) return fats.map(f=>f.ano+": R$ "+f.valor).join("  |  ");
                return diagnostico.faturamentoAnual ? "R$ "+diagnostico.faturamentoAnual : "—";
              })()},
              {l:"Regime Tributário", v: (()=>{
                const regimes = diagnostico.regimes||[];
                if(regimes.length>0){
                  return regimes.map(r=>{
                    let s = r.tipo||"";
                    if(r.dataInicio) s += " ("+new Date(r.dataInicio+"T12:00").toLocaleDateString("pt-BR",{month:"2-digit",year:"numeric"});
                    if(r.dataFim)    s += " – "+new Date(r.dataFim+"T12:00").toLocaleDateString("pt-BR",{month:"2-digit",year:"numeric"});
                    if(r.dataInicio) s += ")";
                    return s;
                  }).join(" | ");
                }
                return diagnostico.regime || "—";
              })()},
              {l:"Atividade", v: diagnostico.atividadePrincipal || diagnostico.atividade || "—"},
            ].map(r => (
              <p key={r.l} style={{margin:0, fontSize:11}}>
                <strong>{r.l}:</strong> {r.v}
              </p>
            ))}
          </div>
        </div>

        {/* Saudação */}
        <p style={{marginBottom:14}}>Prezado(a) Cliente,</p>
        <p style={{marginBottom:16, textAlign:"justify"}}>
          Concluímos a fase inicial do diagnóstico fiscal eletrônico com base nos arquivos fornecidos
          ou disponibilizados junto à SRFB – Secretaria da Receita Federal do Brasil, cruzando as
          informações declaradas, com a legislação vigente e as teses jurídicas pacificadas. O presente
          relatório apresenta, em caráter preliminar e estimativo, os créditos tributários identificados,
          passíveis de recuperação via esfera administrativa ou judicial.
        </p>
        <p style={{marginBottom:24, textAlign:"justify", background:"#fef9ec",
          border:"1px solid #C9A84C", borderRadius:6, padding:"10px 14px",
          fontSize:11}}>
          <strong>NOTA PRELIMINAR:</strong> Os valores apurados são estimativas baseadas nos arquivos
          eletrônicos disponibilizados. Diferenças — a maior ou a menor — poderão ocorrer após a
          auditoria aprofundada. Eventuais inconsistências fiscais/contábeis identificadas serão objeto
          de relatório específico, para correção pela área contábil da empresa, não constituindo objeto
          desta consultoria.
        </p>

        {/* Seção 1 */}
        <p style={{fontSize:13, fontWeight:"bold", margin:"0 0 8px",
          borderBottom:"1px solid #C9A84C", paddingBottom:4}}>
          1. Posicionamento dos Produtos Tributários Viáveis e Créditos Apurados
        </p>
        <p style={{marginBottom:16, textAlign:"justify"}}>
          A análise identificou {produtosRelatorio.length} produto{produtosRelatorio.length !== 1 ? "s" : ""} tributário{produtosRelatorio.length !== 1 ? "s" : ""} viável{produtosRelatorio.length !== 1 ? "is" : ""} elegível{produtosRelatorio.length !== 1 ? "is" : ""} para recuperação de créditos,
          fundamentados no Código Tributário Nacional (Art. 165) e na IN RFB n°. 2.055/2021.
          Os valores indicados como "Em Apuração" correspondem a produtos viáveis cujo crédito está em processo de levantamento:
        </p>

        {/* Tabela */}
        <table style={{width:"100%", borderCollapse:"collapse", marginBottom:28, fontSize:11}}>
          <thead>
            <tr style={{background:"#1a1a2e", color:"white"}}>
              <th style={{padding:"10px 12px", textAlign:"left", width:"28%", fontWeight:"bold"}}>Produto Tributário</th>
              <th style={{padding:"10px 12px", textAlign:"left", width:"56%", fontWeight:"bold"}}>Descrição Técnica da Oportunidade</th>
              <th style={{padding:"10px 12px", textAlign:"right", width:"16%", fontWeight:"bold", whiteSpace:"nowrap"}}>Saldo Apurado (R$)</th>
            </tr>
          </thead>
          <tbody>
            {produtosRelatorio.map((p, i) => (
              <tr key={i} style={{background:i%2===0?"#F9F7F2":"white", borderBottom:"1px solid #E5E0D5"}}>
                <td style={{padding:"10px 12px", fontWeight:"bold", verticalAlign:"top", fontSize:10}}>{p.nome}</td>
                <td style={{padding:"10px 12px", textAlign:"justify", verticalAlign:"top", lineHeight:1.5}}>{getDescricao(p)}</td>
                <td style={{padding:"10px 12px", textAlign:"right", fontWeight:"bold",
                  verticalAlign:"top", whiteSpace:"nowrap",
                  color: p.statusApuracao==="Pendência de Documento"
                    ? "#92400e"
                    : p.valorFinal>0?"#1a1a2e":p.statusApuracao==="Concluído"?"#1a1a2e":"#888"}}>
                  {p.statusApuracao==="Pendência de Documento"
                    ? "⚠️ Pendência de documento"
                    : p.valorFinal>0
                      ? "R$ "+fmtR(p.valorFinal)
                      : (p.statusApuracao==="Concluído" ? "R$ 0,00" : "Em Apuração")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Seção 2 */}
        <p style={{fontSize:13, fontWeight:"bold", margin:"0 0 8px",
          borderBottom:"1px solid #C9A84C", paddingBottom:4}}>
          2. Consolidação dos Resultados
        </p>
        <p style={{marginBottom:16, textAlign:"justify"}}>
          Os valores abaixo representam o ativo que a empresa possui "preso" nos cofres públicos,
          pronto para ser reintegrado ao fluxo de caixa mediante retificações e pedidos de
          restituição/compensação (PER/DCOMP).
        </p>

        {/* Box consolidado — apenas total */}
        <div style={{border:"2px solid #C9A84C", borderRadius:8, padding:"20px 24px",
          marginBottom:28, background:"#FDFBF5", textAlign:"center"}}>
          <p style={{fontSize:11, color:"#888", textTransform:"uppercase",
            letterSpacing:1, marginBottom:8}}>
            Total de Créditos Tributários Identificados
          </p>
          <p style={{fontSize:28, fontWeight:"bold", color:"#1a1a2e", margin:"0 0 4px"}}>
            R$ {fmtR(totalPrincipal)}
          </p>
          <p style={{fontSize:11, color:"#888", marginTop:8}}>
            {produtosRelatorio.filter(p=>p.statusApuracao!=="Pendência de Documento").length} produto{produtosRelatorio.filter(p=>p.statusApuracao!=="Pendência de Documento").length!==1?"s":""} tributário{produtosRelatorio.filter(p=>p.statusApuracao!=="Pendência de Documento").length!==1?"s":""} viável{produtosRelatorio.filter(p=>p.statusApuracao!=="Pendência de Documento").length!==1?"is":""}
            {produtosRelatorio.some(p=>p.statusApuracao==="Pendência de Documento") &&
              <span style={{display:"block",fontSize:10,color:"#92400e",marginTop:4}}>
                ⚠️ {produtosRelatorio.filter(p=>p.statusApuracao==="Pendência de Documento").length} produto(s) com pendência de documento — não incluído(s) no total
              </span>
            }
          </p>
        </div>

        {/* Seção 3 */}
        <p style={{fontSize:13, fontWeight:"bold", margin:"0 0 8px",
          borderBottom:"1px solid #C9A84C", paddingBottom:4}}>
          3. Conclusão e Próximos Passos
        </p>
        <p style={{marginBottom:12, textAlign:"justify"}}>
          Informamos que o direito à recuperação retroage aos últimos 60 meses.
          A recuperação dos créditos pode ser realizada tanto pela esfera administrativa quanto pela
          judicial, dependendo do caso concreto, que será objeto de análise posterior. Nosso compromisso
          é avaliar e apresentar o cenário mais adequado, priorizando a segurança jurídica do processo
          e a solução mais vantajosa para a empresa.
        </p>
        <p style={{marginBottom:32, textAlign:"justify"}}>
          Estamos aptos a iniciar o protocolo das retificações e a habilitação dos créditos identificados,
          com o objetivo de viabilizar a entrada imediata desses valores no caixa da empresa.
        </p>

        <p style={{marginBottom:40}}>Atenciosamente,</p>

        {/* Assinaturas */}
        <div style={{display:"flex", gap:60}}>
          {[resp, sup].filter(Boolean).filter((p,i,a) => a.findIndex(x=>x?.id===p?.id)===i).map((p, i) => (
            <div key={i} style={{flex:1, minWidth:180}}>
              <div style={{borderTop:"1px solid #333", paddingTop:8, marginTop:8}}>
                <p style={{fontWeight:"bold", marginBottom:2}}>{p.nome}</p>
                <p style={{fontSize:10, color:"#555", marginBottom:1}}>{p.cargo || "Especialista em Recuperação de Ativos Tributários"}</p>
                <p style={{fontSize:10, color:"#555"}}>Alves & Correa Advogados</p>
                {p.oab && <p style={{fontSize:10, color:"#555"}}>OAB: {p.oab}</p>}
              </div>
            </div>
          ))}
        </div>

        {/* Rodapé */}
        <div style={{marginTop:36, paddingTop:10, borderTop:"1px solid #C9A84C",
          display:"flex", justifyContent:"space-between", alignItems:"center"}}>
          <p style={{fontSize:9, color:"#999", margin:0}}>
            Documento confidencial · Gerado em {hoje} · Alves & Correa Advogados
          </p>
          <p style={{fontSize:9, color:"#C9A84C", fontWeight:"bold", margin:0}}>
            Especialistas em Recuperação de Ativos Tributários
          </p>
        </div>
      </div>

      {/* CSS impressão */}
      <style>{`
        @media print {
          body > * { display: none !important; }
          #relatorio-rct { display: block !important; position: static !important; }
          #relatorio-rct { font-size: 11pt; padding: 15mm 20mm; }
        }
      `}</style>
    </Modal>
  );
}


// ============================================================
// FASE 7 — MÓDULO: RELATÓRIOS
// ============================================================

const TIPOS_RELATORIO = [
  { id: "clientes",     label: "Clientes",            icon: "👤" },
  { id: "leads",        label: "Lides / Leads",        icon: "🎯" },
  { id: "contratos",   label: "Contratos em Andamento",icon: "📁" },
  { id: "processos_parados", label: "Processos Parados", icon: "⏸" },
  { id: "pops",         label: "POPs",                 icon: "📋" },
  { id: "produtos",     label: "Produtos",             icon: "📦" },
  { id: "financeiro",   label: "Financeiro",           icon: "💰" },
  { id: "timesheet",    label: "Timesheet",            icon: "⏱️" },
  { id: "rct",          label: "RCT Tributário",       icon: "🔍" },
  { id: "equipe",       label: "Equipe",               icon: "👥" },
  { id: "parcerias",    label: "Parcerias",             icon: "🤝" },
];

function ModRelatorios({ data, setData }) {
  const { clientes, contratos, lancamentos, despesas, diagnosticosRCT, leads, equipe, parcerias } = data;
  const baseOperacional = data.baseOperacional || [];
  const pops = data.pops || [];
  const produtos = data.produtos || [];
  const processos = data.processos || [];
  const [tipoAtivo, setTipoAtivo] = useState("clientes");
  const [periodo, setPeriodo]     = useState("90");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroResp, setFiltroResp]     = useState("");
  const [diasFiltro, setDiasFiltro]     = useState(30);
  const [filtroClienteTS, setFiltroClienteTS] = useState(""); // filtro timesheet por cliente

  const hoje = new Date();
  const dataCorte = new Date(hoje);
  dataCorte.setDate(dataCorte.getDate() - parseInt(periodo === "todos" ? 99999 : periodo));

  const fmtR = v => "R$ " + (parseFloat(v)||0).toLocaleString("pt-BR", {minimumFractionDigits:2});
  const fmtD = d => d ? new Date(d+"T00:00:00").toLocaleDateString("pt-BR") : "—";

  // ── Exportar CSV ─────────────────────────────────────────────────────────────
  const exportCSV = (nomeArquivo, cabecalho, linhas) => {
    const bom = "\uFEFF";
    const sep = ";";
    const escapar = v => {
      const s = String(v ?? "").replace(/\r?\n/g, " ");
      return s.includes(sep) || s.includes("\"") ? `"${s.replace(/"/g, "\"\"")}"` : s;
    };
    const csv = bom + [cabecalho, ...linhas].map(r => r.map(escapar).join(sep)).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = nomeArquivo + ".csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Imprimir / PDF ────────────────────────────────────────────────────────────
  const imprimirPDF = (titulo, htmlConteudo) => {
    const win = window.open("", "_blank", "width=960,height=750");
    win.document.write(`<html><head><title>${titulo}</title><style>
      body{font-family:Arial,sans-serif;padding:24px;color:#2A2420;font-size:12px}
      h1{font-size:15px;font-weight:bold;text-align:center;text-transform:uppercase;border-bottom:2px solid #C9A84C;padding-bottom:10px;margin-bottom:20px}
      h2{font-size:12px;font-weight:bold;margin:18px 0 8px 0;color:#5C5448;text-transform:uppercase;letter-spacing:0.5px}
      table{width:100%;border-collapse:collapse;margin-bottom:16px;font-size:11px}
      th{background:#1A1610;color:#C9A84C;padding:7px 10px;text-align:left;font-weight:bold;border:1px solid #555}
      td{padding:6px 10px;border:1px solid #ddd;vertical-align:top}
      tr:nth-child(even) td{background:#F9F7F2}
      .kpis{display:flex;gap:16px;margin-bottom:20px;flex-wrap:wrap}
      .kpi{border:1px solid #ddd;border-radius:6px;padding:10px 16px;min-width:130px;text-align:center}
      .kpi-v{font-size:20px;font-weight:bold;color:#1A1610}
      .kpi-l{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:0.5px}
      .sub{font-size:10px;text-align:right;color:#aaa;margin-top:20px;border-top:1px solid #eee;padding-top:8px}
      @media print{@page{margin:15mm}}
    </style></head><body>${htmlConteudo}<div class="sub">Alves &amp; Correa Advogados · Gerado em ${new Date().toLocaleString("pt-BR")} · Confidencial</div></body></html>`);
    win.document.close(); win.focus(); setTimeout(() => win.print(), 700);
  };

  // ── Barra de ações (PDF + CSV) ─────────────────────────────────────────────
  const BtnActions = ({ onPDF, onCSV }) => (
    <div style={{display:"flex",gap:8,marginBottom:16,justifyContent:"flex-end"}}>
      <button onClick={onPDF} style={{
        padding:"8px 16px",borderRadius:7,border:`1px solid ${theme.gray200}`,
        background:theme.white,color:theme.gray700,fontSize:12,fontWeight:600,cursor:"pointer",
        display:"flex",alignItems:"center",gap:6
      }}>🖨️ PDF</button>
      <button onClick={onCSV} style={{
        padding:"8px 16px",borderRadius:7,border:`1px solid ${theme.green}22`,
        background:`${theme.green}11`,color:theme.green,fontSize:12,fontWeight:600,cursor:"pointer",
        display:"flex",alignItems:"center",gap:6
      }}>⬇️ CSV</button>
    </div>
  );

  // ── RELATÓRIO CLIENTES ──────────────────────────────────────
  const relClientes = () => {
    const lista = clientes.filter(c => {
      const mS = !filtroStatus || c.status === filtroStatus;
      return mS;
    });
    const ativos    = lista.filter(c => c.ativo || c.status === "Ativo").length;
    const inativos  = lista.filter(c => !c.ativo || c.status === "Inativo").length;
    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
          {[
            {l:"Total Clientes",v:lista.length,c:theme.gray800},
            {l:"Ativos",v:ativos,c:theme.green},
            {l:"Inativos",v:inativos,c:theme.gray400},
          ].map(k=>(
            <Card key={k.l} style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
              <p style={{fontSize:24,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
            </Card>
          ))}
        </div>
        <Card>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                  {["Nome","Tipo","Cidade/UF","Produtos","Entrada","Status"].map(h=>(
                    <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lista.map((c,i)=>(
                  <tr key={c.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                    <td style={{padding:"10px 14px",fontSize:13,fontWeight:500,color:theme.gray900}}>{c.nome}</td>
                    <td style={{padding:"10px 14px"}}><Badge color="gray">{c.tipo||"PF"}</Badge></td>
                    <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{c.cidade&&c.uf?`${c.cidade}/${c.uf}`:"—"}</td>
                    <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{(c.produtos||[]).length} produto(s)</td>
                    <td style={{padding:"10px 14px",fontSize:12,color:theme.gray500}}>{fmtD(c.dataEntrada)}</td>
                    <td style={{padding:"10px 14px"}}><Badge color={c.ativo?"green":"gray"}>{c.ativo?"Ativo":"Inativo"}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );
  };

  // ── RELATÓRIO CONTRATOS ─────────────────────────────────────
  const relContratos = () => {
    const lista = (contratos||[]).filter(c => !filtroStatus || c.status === filtroStatus);
    const totalContratado = lista.reduce((s,c)=>s+(parseFloat(c.honorarios)||0),0);
    const vencendo = lista.filter(c=>{
      if(!c.prazo) return false;
      const d = new Date(c.prazo+"T00:00:00");
      const diff = (d-hoje)/86400000;
      return diff >= 0 && diff <= 30;
    }).length;
    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
          {[
            {l:"Total",v:lista.length,c:theme.gray800,fmt:"n"},
            {l:"Ativos",v:lista.filter(c=>c.status==="Ativo").length,c:theme.green,fmt:"n"},
            {l:"Vencendo em 30d",v:vencendo,c:theme.amber,fmt:"n"},
            {l:"Total Contratado",v:totalContratado,c:theme.gold,fmt:"r"},
          ].map(k=>(
            <Card key={k.l} style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
              <p style={{fontSize:k.fmt==="r"?15:24,fontWeight:700,color:k.c,marginTop:6}}>
                {k.fmt==="n"?k.v:<><span style={{fontSize:10}}>R$ </span>{k.v.toLocaleString("pt-BR",{minimumFractionDigits:2})}</>}
              </p>
            </Card>
          ))}
        </div>
        <Card>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                  {["ID","Cliente","Tipo","Área","Responsável","Honorários","Prazo","Status"].map(h=>(
                    <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lista.map((c,i)=>{
                  const cli = clientes.find(cl=>cl.id===c.clienteId);
                  const resp = equipe.find(e=>e.id===c.responsavel);
                  const diasPrazo = c.prazo ? Math.ceil((new Date(c.prazo+"T00:00:00")-hoje)/86400000) : null;
                  return (
                    <tr key={c.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                      <td style={{padding:"10px 14px",fontSize:11,color:theme.gray500,fontFamily:"monospace"}}>{c.id}</td>
                      <td style={{padding:"10px 14px",fontSize:13,fontWeight:500,color:theme.gray900}}>{cli?.nome||"—"}</td>
                      <td style={{padding:"10px 14px"}}><Badge color="gray">{c.tipo||"—"}</Badge></td>
                      <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{c.area||"—"}</td>
                      <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{resp?.nome?.split(" ").slice(0,2).join(" ")||"—"}</td>
                      <td style={{padding:"10px 14px",fontSize:13,fontWeight:600,color:theme.gold}}>{c.honorarios?fmtR(c.honorarios):"—"}</td>
                      <td style={{padding:"10px 14px",fontSize:12}}>
                        {diasPrazo!==null
                          ? <span style={{color:diasPrazo<0?theme.red:diasPrazo<=7?theme.amber:theme.gray600}}>
                              {fmtD(c.prazo)}{diasPrazo<0?` (${Math.abs(diasPrazo)}d atraso)`:diasPrazo<=30?` (${diasPrazo}d)`:""}
                            </span>
                          : "—"}
                      </td>
                      <td style={{padding:"10px 14px"}}><Badge color={c.status==="Ativo"?"green":c.status==="Concluído"?"blue":"gray"}>{c.status||"—"}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );
  };

  // ── RELATÓRIO FINANCEIRO ────────────────────────────────────
  const relFinanceiro = () => {
    const lancs = (lancamentos||[]).filter(l=>{
      if(periodo==="todos") return true;
      return l.dataVencimento >= dataCorte.toISOString().slice(0,10);
    });
    // Receitas = todos os lançamentos (entradas de contratos ou diretos)
    const receitas  = lancs; // sem filtro de tipo — toda entrada é receita
    const totalRecBruto = receitas.reduce((s,l)=>s+(parseFloat(l.valor)||0),0);
    const totalRecebido = receitas.filter(l=>l.status==="Recebido").reduce((s,l)=>s+(parseFloat(l.valor)||0),0);
    const totalAReceber = receitas.filter(l=>!(["Recebido","Cancelado","Estimado (Projeção)"].includes(l.status))).reduce((s,l)=>s+(parseFloat(l.valor)||0),0);
    const totalDesps = (despesas||[]).reduce((s,d)=>s+(parseFloat(d.valor)||0),0);
    const margem = totalRecebido - totalDesps;

    // Receita por área
    const porArea = {};
    receitas.forEach(l=>{
      const a = l.area||"Outros";
      if(!porArea[a]) porArea[a]={contratado:0,recebido:0};
      porArea[a].contratado += parseFloat(l.valor)||0;
      if(l.status==="Pago") porArea[a].recebido += parseFloat(l.valor)||0;
    });
    const maxArea = Math.max(...Object.values(porArea).map(v=>v.contratado),1);

    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
          {[
            {l:"Receita Contratada",v:totalRecBruto,c:theme.gray800},
            {l:"Receita Recebida",  v:totalRecebido,c:theme.green},
            {l:"A Receber",         v:totalAReceber,c:theme.amber},
            {l:"Margem Líquida",    v:margem,        c:margem>=0?theme.gold:theme.red},
          ].map(k=>(
            <Card key={k.l} style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
              <p style={{fontSize:14,fontWeight:700,color:k.c,marginTop:6}}>
                <span style={{fontSize:9}}>R$ </span>{k.v.toLocaleString("pt-BR",{minimumFractionDigits:2})}
              </p>
            </Card>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
          <Card style={{padding:"18px 20px"}}>
            <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,marginBottom:14}}>Receita por Área</p>
            {Object.entries(porArea).sort((a,b)=>b[1].contratado-a[1].contratado).map(([area,vals])=>(
              <div key={area} style={{marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:12,color:theme.gray700}}>{area}</span>
                  <span style={{fontSize:12,fontWeight:600,color:theme.gold}}>{fmtR(vals.contratado)}</span>
                </div>
                <div style={{height:6,background:theme.gray100,borderRadius:3}}>
                  <div style={{height:6,borderRadius:3,background:theme.green,
                    width:`${(vals.recebido/maxArea)*100}%`,transition:"width 0.5s"}}/>
                </div>
                <p style={{fontSize:10,color:theme.gray400,marginTop:2}}>
                  Recebido: {fmtR(vals.recebido)} ({vals.contratado>0?Math.round((vals.recebido/vals.contratado)*100):0}%)
                </p>
              </div>
            ))}
          </Card>
          <Card style={{padding:"18px 20px"}}>
            <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,marginBottom:14}}>DRE Simplificado</p>
            {[
              {l:"Receita Bruta",    v:totalRecBruto, indent:0, bold:false},
              {l:"(-) Despesas",     v:-totalDesps,   indent:0, bold:false},
              {l:"Margem Líquida",   v:margem,        indent:0, bold:true},
            ].map(r=>(
              <div key={r.l} style={{
                display:"flex",justifyContent:"space-between",
                padding:"8px 0",borderBottom:`1px solid ${theme.gray100}`,
                paddingLeft:r.indent*12
              }}>
                <span style={{fontSize:13,color:theme.gray700,fontWeight:r.bold?700:400}}>{r.l}</span>
                <span style={{fontSize:13,fontWeight:700,color:r.v>=0?theme.green:theme.red}}>
                  {r.v>=0?"":"-"}{fmtR(Math.abs(r.v))}
                </span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    );
  };

  // ── RELATÓRIO RCT ───────────────────────────────────────────
  const relRCT = () => {
    const totalCredito = diagnosticosRCT.reduce((s,d)=>
      s+(d.produtos||[]).reduce((ps,p)=>ps+(parseFloat(p.creditoApurado)||0),0),0);
    const porStatus = {};
    diagnosticosRCT.forEach(d=>{
      const s = d.status||"Indefinido";
      porStatus[s] = (porStatus[s]||0)+1;
    });
    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
          {[
            {l:"Diagnósticos",    v:diagnosticosRCT.length,c:theme.gray800,fmt:"n"},
            {l:"Concluídos",      v:diagnosticosRCT.filter(d=>d.status?.startsWith("Concluído")).length,c:theme.green,fmt:"n"},
            {l:"Total Crédito",   v:totalCredito,c:theme.gold,fmt:"r"},
          ].map(k=>(
            <Card key={k.l} style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
              <p style={{fontSize:k.fmt==="r"?15:24,fontWeight:700,color:k.c,marginTop:6}}>
                {k.fmt==="n"?k.v:<><span style={{fontSize:10}}>R$ </span>{k.v.toLocaleString("pt-BR",{minimumFractionDigits:2})}</>}
              </p>
            </Card>
          ))}
        </div>
        <Card>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                  {["Cliente","CNPJ","Regime","Responsável","Produtos Viáveis","Crédito Total","Status"].map(h=>(
                    <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {diagnosticosRCT.map((d,i)=>{
                  const cli  = clientes.find(c=>c.id===d.clienteId);
                  const resp = equipe.find(e=>e.id===d.responsavel);
                  const cred = (d.produtos||[]).reduce((s,p)=>s+(parseFloat(p.creditoApurado)||0),0);
                  const viav = (d.produtos||[]).filter(p=>p.viabilidade==="Sim").length;
                  return (
                    <tr key={d.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                      <td style={{padding:"10px 14px",fontSize:13,fontWeight:500}}>{cli?.nome||d.clienteId}</td>
                      <td style={{padding:"10px 14px",fontSize:11,color:theme.gray500}}>{d.cnpj||"—"}</td>
                      <td style={{padding:"10px 14px"}}><Badge color="gray">{d.regime||"—"}</Badge></td>
                      <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{resp?.nome?.split(" ").slice(0,2).join(" ")||"—"}</td>
                      <td style={{padding:"10px 14px",textAlign:"center",fontSize:13,fontWeight:700,color:theme.green}}>{viav}</td>
                      <td style={{padding:"10px 14px",fontSize:13,fontWeight:700,color:theme.gold}}>{cred>0?fmtR(cred):"—"}</td>
                      <td style={{padding:"10px 14px"}}><Badge color={d.status?.startsWith("Concluído")?"green":d.status==="Em andamento"?"blue":"gray"}>{d.status}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );
  };

  // ── RELATÓRIO CRM ───────────────────────────────────────────
  const relCRM = () => {
    const ativos    = leads.filter(l=>!["fechado","perdido"].includes(l.estagio));
    const fechados  = leads.filter(l=>l.estagio==="fechado");
    const perdidos  = leads.filter(l=>l.estagio==="perdido");
    const pipeline  = ativos.reduce((s,l)=>{
      const v = parseFloat(l.valorEstimado)||0;
      const p = (parseFloat(l.probabilidade)||0)/100;
      return s+(v*p);
    },0);
    const porEstagio = {};
    leads.forEach(l=>{
      porEstagio[l.estagio] = (porEstagio[l.estagio]||0)+1;
    });
    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
          {[
            {l:"Total Leads",    v:leads.length,c:theme.gray800,fmt:"n"},
            {l:"Ativos",         v:ativos.length,c:theme.blue,fmt:"n"},
            {l:"Fechados",       v:fechados.length,c:theme.green,fmt:"n"},
            {l:"Pipeline Pond.", v:pipeline,c:theme.gold,fmt:"r"},
          ].map(k=>(
            <Card key={k.l} style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
              <p style={{fontSize:k.fmt==="r"?15:24,fontWeight:700,color:k.c,marginTop:6}}>
                {k.fmt==="n"?k.v:<><span style={{fontSize:10}}>R$ </span>{k.v.toLocaleString("pt-BR",{minimumFractionDigits:2})}</>}
              </p>
            </Card>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <Card style={{padding:"18px 20px"}}>
            <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,marginBottom:14}}>Funil por Estágio</p>
            {["lead","qualificação","reunião","proposta","negociação","fechado","perdido"].map(e=>{
              const n = porEstagio[e]||0;
              const max = Math.max(...Object.values(porEstagio),1);
              return (
                <div key={e} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:12,color:theme.gray700,textTransform:"capitalize"}}>{e}</span>
                    <span style={{fontSize:12,fontWeight:600,color:theme.gray800}}>{n}</span>
                  </div>
                  <div style={{height:6,background:theme.gray100,borderRadius:3}}>
                    <div style={{height:6,borderRadius:3,
                      background:e==="fechado"?theme.green:e==="perdido"?theme.red:theme.gold,
                      width:`${(n/max)*100}%`}}/>
                  </div>
                </div>
              );
            })}
          </Card>
          <Card style={{padding:"18px 20px"}}>
            <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,marginBottom:14}}>Pipeline por Responsável</p>
            {equipe.filter(e=>e.ativo).map(e=>{
              const leadsResp = leads.filter(l=>l.responsavel===e.id&&!["fechado","perdido"].includes(l.estagio));
              const pipeResp  = leadsResp.reduce((s,l)=>{
                return s+((parseFloat(l.valorEstimado)||0)*((parseFloat(l.probabilidade)||0)/100));
              },0);
              return (
                <div key={e.id} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${theme.gray100}`}}>
                  <span style={{fontSize:12,color:theme.gray700}}>{e.nome.split(" ").slice(0,2).join(" ")}</span>
                  <div style={{textAlign:"right"}}>
                    <p style={{fontSize:12,fontWeight:600,color:theme.gold,margin:0}}>{fmtR(pipeResp)}</p>
                    <p style={{fontSize:10,color:theme.gray400,margin:0}}>{leadsResp.length} leads</p>
                  </div>
                </div>
              );
            })}
          </Card>
        </div>
      </div>
    );
  };

  // ── RELATÓRIO EQUIPE ────────────────────────────────────────
  const relEquipe = () => {
    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
          {[
            {l:"Total Equipe", v:equipe.length,c:theme.gray800},
            {l:"Ativos",       v:equipe.filter(e=>e.ativo).length,c:theme.green},
            {l:"Sócios",       v:equipe.filter(e=>e.vinculo==="Sócio").length,c:theme.gold},
          ].map(k=>(
            <Card key={k.l} style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
              <p style={{fontSize:24,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
            </Card>
          ))}
        </div>
        <Card>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                  {["Nome","Vínculo","Função","Especialidade","Contratos","Leads","Diagnósticos RCT","Status"].map(h=>(
                    <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {equipe.map((e,i)=>{
                  const nContratos = (contratos||[]).filter(c=>c.responsavel===e.id).length;
                  const nLeads     = leads.filter(l=>l.responsavel===e.id).length;
                  const nRCT       = diagnosticosRCT.filter(d=>d.responsavel===e.id).length;
                  return (
                    <tr key={e.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                      <td style={{padding:"10px 14px",fontSize:13,fontWeight:500,color:theme.gray900}}>{e.nome}</td>
                      <td style={{padding:"10px 14px"}}><Badge color={e.vinculo==="Sócio"?"amber":"gray"}>{e.vinculo}</Badge></td>
                      <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{e.funcao||"—"}</td>
                      <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{e.especialidade||"—"}</td>
                      <td style={{padding:"10px 14px",textAlign:"center",fontSize:13,fontWeight:700,color:theme.blue}}>{nContratos}</td>
                      <td style={{padding:"10px 14px",textAlign:"center",fontSize:13,fontWeight:700,color:theme.gold}}>{nLeads}</td>
                      <td style={{padding:"10px 14px",textAlign:"center",fontSize:13,fontWeight:700,color:theme.green}}>{nRCT}</td>
                      <td style={{padding:"10px 14px"}}><Badge color={e.ativo?"green":"gray"}>{e.ativo?"Ativo":"Inativo"}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );
  };

  // ── RELATÓRIO PARCERIAS ─────────────────────────────────────
  const relParcerias = () => {
    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
          {[
            {l:"Total Parcerias",v:parcerias.length,c:theme.gray800},
            {l:"Ativas",         v:parcerias.filter(p=>p.status==="Ativo").length,c:theme.green},
            {l:"Diagnósticos via Parceria",v:diagnosticosRCT.filter(d=>d.parceiraId).length,c:theme.gold},
          ].map(k=>(
            <Card key={k.l} style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
              <p style={{fontSize:24,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
            </Card>
          ))}
        </div>
        <Card>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                  {["Parceria","Área","Cidade/UF","Comissão","Clientes Indicados","RCTs","Status"].map(h=>(
                    <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parcerias.map((p,i)=>{
                  const nClientes = clientes.filter(c=>c.parceiraId===p.id).length;
                  const nRCT      = diagnosticosRCT.filter(d=>d.parceiraId===p.id).length;
                  return (
                    <tr key={p.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                      <td style={{padding:"10px 14px",fontSize:13,fontWeight:500,color:theme.gray900}}>{p.nome}</td>
                      <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{p.areaAtuacao||"—"}</td>
                      <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{p.cidade&&p.uf?`${p.cidade}/${p.uf}`:"—"}</td>
                      <td style={{padding:"10px 14px",fontSize:13,fontWeight:600,color:theme.gold}}>{p.comissao?p.comissao+"%":"—"}</td>
                      <td style={{padding:"10px 14px",textAlign:"center",fontSize:13,fontWeight:700,color:theme.blue}}>{nClientes}</td>
                      <td style={{padding:"10px 14px",textAlign:"center",fontSize:13,fontWeight:700,color:theme.green}}>{nRCT}</td>
                      <td style={{padding:"10px 14px"}}><Badge color={p.status==="Ativo"?"green":"gray"}>{p.status}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );
  };

  // ── RELATÓRIO LIDES / LEADS ──────────────────────────────────────────────────
  const relLeads = () => {
    const lista = leads || [];
    const ativos  = lista.filter(l=>!["fechado","perdido"].includes(l.estagio));
    const fechados = lista.filter(l=>l.estagio==="fechado");
    const perdidos = lista.filter(l=>l.estagio==="perdido");
    const pipeline = ativos.reduce((s,l)=>{
      const v=parseFloat(l.valorEstimado)||0; const p=(parseFloat(l.probabilidade)||0)/100;
      return s+(v*p);
    },0);
    const porEstagio={};
    lista.forEach(l=>{ porEstagio[l.estagio]=(porEstagio[l.estagio]||0)+1; });
    const maxE = Math.max(...Object.values(porEstagio),1);
    const CORES_E={lead:theme.gray400,"qualificação":theme.blue,"reunião":theme.gold,proposta:theme.amber,"negociação":theme.amber,fechado:theme.green,perdido:theme.red};
    return (
      <div>
        <BtnActions
          onPDF={()=>{
            const rows = lista.map(l=>{
              const resp=equipe.find(e=>e.id===l.responsavel);
              return `<tr><td>${l.nomeCliente||"—"}</td><td>${l.estagio||"—"}</td><td>${resp?.nome||"—"}</td><td>${fmtD(l.dataEntrada)}</td><td>${l.valorEstimado?fmtR(l.valorEstimado):"—"}</td><td>${l.probabilidade||"—"}%</td><td>${l.origem||"—"}</td></tr>`;
            }).join("");
            imprimirPDF("Relatório — Lides/Leads",
              `<h1>Relatório de Lides / Leads</h1>
               <div class="kpis">
                 <div class="kpi"><div class="kpi-v">${lista.length}</div><div class="kpi-l">Total</div></div>
                 <div class="kpi"><div class="kpi-v">${ativos.length}</div><div class="kpi-l">Ativos</div></div>
                 <div class="kpi"><div class="kpi-v">${fechados.length}</div><div class="kpi-l">Fechados</div></div>
                 <div class="kpi"><div class="kpi-v">R$ ${pipeline.toLocaleString("pt-BR",{minimumFractionDigits:2})}</div><div class="kpi-l">Pipeline Pond.</div></div>
               </div>
               <table><thead><tr><th>Nome</th><th>Estágio</th><th>Responsável</th><th>Data Entrada</th><th>Valor Est.</th><th>Probab.</th><th>Origem</th></tr></thead><tbody>${rows}</tbody></table>`);
          }}
          onCSV={()=>exportCSV("leads",
            ["Nome","Estágio","Responsável","Data Entrada","Valor Estimado","Probabilidade","Origem","Cidade","UF"],
            lista.map(l=>{
              const resp=equipe.find(e=>e.id===l.responsavel);
              return [l.nomeCliente||"—",l.estagio||"—",resp?.nome||"—",l.dataEntrada||"—",l.valorEstimado||"—",(l.probabilidade||"")+"%",l.origem||"—",l.cidade||"—",l.uf||"—"];
            })
          )}
        />
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
          {[{l:"Total Lides",v:lista.length,c:theme.gray800},{l:"Ativos",v:ativos.length,c:theme.blue},{l:"Fechados",v:fechados.length,c:theme.green},{l:"Pipeline Pond.",v:pipeline,c:theme.gold,r:true}]
            .map(k=>(
            <Card key={k.l} style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
              <p style={{fontSize:k.r?13:24,fontWeight:700,color:k.c,marginTop:6}}>{k.r?fmtR(k.v):k.v}</p>
            </Card>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:16,marginBottom:16}}>
          <Card style={{padding:"18px 20px"}}>
            <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,marginBottom:12}}>Funil de Estágios</p>
            {["lead","qualificação","reunião","proposta","negociação","fechado","perdido"].map(e=>{
              const n=porEstagio[e]||0;
              return (<div key={e} style={{marginBottom:9}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:12,color:theme.gray700,textTransform:"capitalize"}}>{e}</span>
                  <span style={{fontSize:12,fontWeight:700,color:CORES_E[e]||theme.gray600}}>{n}</span>
                </div>
                <div style={{height:8,background:theme.gray100,borderRadius:4}}>
                  <div style={{height:8,borderRadius:4,background:CORES_E[e]||theme.gold,width:`${(n/maxE)*100}%`,transition:"width .4s"}}/>
                </div>
              </div>);
            })}
          </Card>
          <Card>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                  {["Nome","Estágio","Responsável","Entrada","Valor Est.","Prob.","Origem"].map(h=>(
                    <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {lista.map((l,i)=>{
                    const resp=equipe.find(e=>e.id===l.responsavel);
                    const cor=CORES_E[l.estagio]||theme.gray500;
                    return (
                      <tr key={l.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                        <td style={{padding:"10px 14px",fontSize:13,fontWeight:500,color:theme.gray900}}>{l.nomeCliente||"—"}</td>
                        <td style={{padding:"10px 14px"}}><span style={{background:cor+"22",color:cor,padding:"2px 8px",borderRadius:4,fontSize:11,fontWeight:600,textTransform:"capitalize"}}>{l.estagio||"—"}</span></td>
                        <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{resp?.nome?.split(" ").slice(0,2).join(" ")||"—"}</td>
                        <td style={{padding:"10px 14px",fontSize:12,color:theme.gray500}}>{fmtD(l.dataEntrada)}</td>
                        <td style={{padding:"10px 14px",fontSize:12,fontWeight:600,color:theme.gold}}>{l.valorEstimado?fmtR(l.valorEstimado):"—"}</td>
                        <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{l.probabilidade||"—"}%</td>
                        <td style={{padding:"10px 14px",fontSize:12,color:theme.gray500}}>{l.origem||"—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    );
  };

  // ── RELATÓRIO CONTRATOS EM ANDAMENTO ──────────────────────────────────────
  const relContratosAndamento = () => {
    const lista = (contratos||[]).filter(c=>c.status==="Em andamento"||c.status==="Ativo");
    const totalHon = lista.reduce((s,c)=>s+(parseFloat(c.honorarios)||0),0);
    const vencendo30 = lista.filter(c=>{
      if(!c.prazo) return false;
      const d=(new Date(c.prazo+"T00:00:00")-hoje)/86400000;
      return d>=0&&d<=30;
    }).length;
    return (
      <div>
        <BtnActions
          onPDF={()=>{
            const rows=lista.map(c=>{
              const cli=clientes.find(cl=>cl.id===c.clienteId);
              const resp=equipe.find(e=>e.id===c.responsavel);
              const diasP=c.prazo?Math.ceil((new Date(c.prazo+"T00:00:00")-hoje)/86400000):null;
              return `<tr><td>${cli?.nome||"—"}</td><td>${c.tipo||"—"}</td><td>${c.area||"—"}</td><td>${resp?.nome||"—"}</td><td>${c.honorarios?fmtR(c.honorarios):"—"}</td><td>${c.prazo?c.prazo+""+(diasP!==null?" ("+diasP+"d)":""):"—"}</td><td>${c.status||"—"}</td></tr>`;
            }).join("");
            imprimirPDF("Contratos em Andamento",
              `<h1>Contratos em Andamento</h1>
               <div class="kpis">
                 <div class="kpi"><div class="kpi-v">${lista.length}</div><div class="kpi-l">Total</div></div>
                 <div class="kpi"><div class="kpi-v">${vencendo30}</div><div class="kpi-l">Vencendo 30d</div></div>
                 <div class="kpi"><div class="kpi-v">R$ ${totalHon.toLocaleString("pt-BR",{minimumFractionDigits:2})}</div><div class="kpi-l">Honorários</div></div>
               </div>
               <table><thead><tr><th>Cliente</th><th>Tipo</th><th>Área</th><th>Responsável</th><th>Honorários</th><th>Prazo</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>`);
          }}
          onCSV={()=>exportCSV("contratos_andamento",
            ["Cliente","Tipo","Área","Responsável","Honorários","Prazo","Status"],
            lista.map(c=>{
              const cli=clientes.find(cl=>cl.id===c.clienteId);
              const resp=equipe.find(e=>e.id===c.responsavel);
              return [cli?.nome||"—",c.tipo||"—",c.area||"—",resp?.nome||"—",c.honorarios||"—",c.prazo||"—",c.status||"—"];
            })
          )}
        />
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
          {[{l:"Em Andamento",v:lista.length,c:theme.blue},{l:"Vencendo em 30d",v:vencendo30,c:theme.amber},{l:"Total Honorários",v:totalHon,c:theme.gold,r:true}]
            .map(k=>(<Card key={k.l} style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
              <p style={{fontSize:k.r?13:24,fontWeight:700,color:k.c,marginTop:6}}>{k.r?fmtR(k.v):k.v}</p>
            </Card>))
          }
        </div>
        <Card><div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
              {["Cliente","Tipo","Área","Responsável","Honorários","Prazo","Status"].map(h=>(
                <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {lista.map((c,i)=>{
                const cli=clientes.find(cl=>cl.id===c.clienteId);
                const resp=equipe.find(e=>e.id===c.responsavel);
                const diasP=c.prazo?Math.ceil((new Date(c.prazo+"T00:00:00")-hoje)/86400000):null;
                return (
                  <tr key={c.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                    <td style={{padding:"10px 14px",fontSize:13,fontWeight:500}}>{cli?.nome||"—"}</td>
                    <td style={{padding:"10px 14px"}}><Badge color="gray">{c.tipo||"—"}</Badge></td>
                    <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{c.area||"—"}</td>
                    <td style={{padding:"10px 14px",fontSize:12}}>{resp?.nome?.split(" ").slice(0,2).join(" ")||"—"}</td>
                    <td style={{padding:"10px 14px",fontSize:13,fontWeight:600,color:theme.gold}}>{c.honorarios?fmtR(c.honorarios):"—"}</td>
                    <td style={{padding:"10px 14px",fontSize:12}}>
                      {diasP!==null?<span style={{color:diasP<0?theme.red:diasP<=7?theme.amber:theme.gray600}}>{fmtD(c.prazo)}{diasP<=30?` (${diasP}d)`:""}</span>:"—"}
                    </td>
                    <td style={{padding:"10px 14px"}}><Badge color="blue">{c.status}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div></Card>
      </div>
    );
  };

  // ── RELATÓRIO PROCESSOS PARADOS ───────────────────────────────────────────
  const relProcessosParados = () => {
    const parados = processos.filter(p=>{
      if(p.status!=="Em andamento") return false;
      const ocs=(p.ocorrencias||[]).filter(o=>!o.interno);
      if(ocs.length===0) return true;
      const ultima=ocs.slice().sort((a,b)=>a.data>b.data?-1:1)[0];
      if(!ultima?.data) return true;
      const dias=Math.floor((hoje-new Date(ultima.data+"T00:00:00"))/(1000*60*60*24));
      return dias>=diasFiltro;
    }).map(p=>{
      const ocs=(p.ocorrencias||[]).filter(o=>!o.interno);
      const ultima=ocs.length?ocs.slice().sort((a,b)=>a.data>b.data?-1:1)[0]:null;
      const dias=ultima?.data?Math.floor((hoje-new Date(ultima.data+"T00:00:00"))/(1000*60*60*24)):9999;
      return {...p,ultimaOc:ultima,diasParado:dias};
    }).sort((a,b)=>b.diasParado-a.diasParado);
    return (
      <div>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
          <span style={{fontSize:12,color:theme.gray600}}>Parado há mais de:</span>
          {[15,30,60,90].map(d=>(
            <button key={d} onClick={()=>setDiasFiltro(d)} style={{
              padding:"6px 14px",borderRadius:6,fontSize:12,cursor:"pointer",fontWeight:600,
              border:`1px solid ${diasFiltro===d?theme.red:theme.gray200}`,
              background:diasFiltro===d?theme.red+"18":theme.white,
              color:diasFiltro===d?theme.red:theme.gray600
            }}>{d} dias</button>
          ))}
        </div>
        <BtnActions
          onPDF={()=>{
            const rows=parados.map(p=>{
              const cli=clientes.find(c=>c.id===p.clienteId);
              const resp=equipe.find(e=>e.id===p.responsavel);
              const cor=p.diasParado>60?"color:#721c24":"color:#856404";
              return `<tr><td>${p.numeroProcesso||"—"}</td><td>${cli?.nome||"—"}</td><td>${p.area||"—"}</td><td>TJ/${p.tjUf||"?"}</td><td>${resp?.nome||"—"}</td><td>${p.ultimaOc?p.ultimaOc.data+" — "+p.ultimaOc.tipo:"Nenhuma"}</td><td style="${cor}"><b>${p.diasParado===9999?"sem oc.":p.diasParado+"d"}</b></td></tr>`;
            }).join("");
            imprimirPDF("Processos Parados",
              `<h1>Processos Parados (≥${diasFiltro} dias)</h1>
               <div class="kpis"><div class="kpi"><div class="kpi-v">${parados.length}</div><div class="kpi-l">Processos parados</div></div></div>
               <table><thead><tr><th>Nº Processo</th><th>Cliente</th><th>Área</th><th>TJ/UF</th><th>Responsável</th><th>Última Ocorrência</th><th>Dias Parado</th></tr></thead><tbody>${rows}</tbody></table>`);
          }}
          onCSV={()=>exportCSV("processos_parados",
            ["Nº Processo","Cliente","Área","TJ/UF","Responsável","Última Ocorrência Data","Última Ocorrência Tipo","Dias Parado"],
            parados.map(p=>{
              const cli=clientes.find(c=>c.id===p.clienteId);
              const resp=equipe.find(e=>e.id===p.responsavel);
              return [p.numeroProcesso||"—",cli?.nome||"—",p.area||"—","TJ/"+(p.tjUf||"?"),resp?.nome||"—",p.ultimaOc?.data||"—",p.ultimaOc?.tipo||"—",p.diasParado===9999?"sem ocorrência":p.diasParado];
            })
          )}
        />
        {parados.length===0
          ?<Card><div style={{padding:"40px",textAlign:"center",color:theme.gray400,fontSize:13}}>✅ Nenhum processo parado há mais de {diasFiltro} dias.</div></Card>
          :<Card><div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                {["Nº Processo","Cliente","Área","TJ/UF","Responsável","Última Ocorrência","Dias Parado"].map(h=>(
                  <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {parados.map((p,i)=>{
                  const cli=clientes.find(c=>c.id===p.clienteId);
                  const resp=equipe.find(e=>e.id===p.responsavel);
                  const cor=p.diasParado>60?theme.red:p.diasParado>30?theme.amber:theme.gray600;
                  return (
                    <tr key={p.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                      <td style={{padding:"10px 14px",fontFamily:"monospace",fontSize:11,color:theme.gray700}}>{p.numeroProcesso||"—"}</td>
                      <td style={{padding:"10px 14px",fontSize:13,fontWeight:500}}>{cli?.nome||"—"}</td>
                      <td style={{padding:"10px 14px"}}><Badge color="gray">{p.area||"—"}</Badge></td>
                      <td style={{padding:"10px 14px",fontSize:11,color:theme.gray500}}>TJ/{p.tjUf||"?"}</td>
                      <td style={{padding:"10px 14px",fontSize:12}}>{resp?.nome?.split(" ").slice(0,2).join(" ")||"—"}</td>
                      <td style={{padding:"10px 14px",fontSize:11,color:theme.gray500}}>
                        {p.ultimaOc?<><span style={{fontWeight:600}}>{p.ultimaOc.data}</span><br/><span style={{color:theme.gray400}}>{p.ultimaOc.tipo}</span></>:"Nenhuma"}
                      </td>
                      <td style={{padding:"10px 14px",textAlign:"center"}}>
                        <span style={{background:cor+"22",color:cor,padding:"3px 10px",borderRadius:4,fontSize:12,fontWeight:700}}>
                          {p.diasParado===9999?"sem oc.":p.diasParado+"d"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div></Card>
        }
      </div>
    );
  };

  // ── RELATÓRIO POPs ────────────────────────────────────────────────────────
  const relPOPs = () => {
    const ativos=pops.filter(p=>p.status==="Ativo");
    const vencendo=pops.filter(p=>{
      if(!p.dataRevisao) return false;
      const d=(new Date(p.dataRevisao+"T00:00:00")-hoje)/86400000;
      return d<=30&&d>=0;
    });
    const porGrupo={};
    pops.forEach(p=>{ porGrupo[p.grupo]=(porGrupo[p.grupo]||0)+1; });
    const maxG=Math.max(...Object.values(porGrupo),1);
    return (
      <div>
        <BtnActions
          onPDF={()=>{
            const rows=pops.map(p=>{
              const prods=(p.produtos||[]).map(pid=>produtos.find(x=>x.id===pid)?.nome||pid).join(", ")||"—";
              return `<tr><td>${p.id}</td><td>${p.nome}</td><td>${p.grupo||"—"}</td><td>${p.subgrupo||"—"}</td><td>${prods}</td><td>v${p.versao||"—"}</td><td>${p.dataRevisao||"—"}</td><td>${p.status||"—"}</td></tr>`;
            }).join("");
            imprimirPDF("Relatório — POPs",
              `<h1>Procedimentos Operacionais Padrão</h1>
               <div class="kpis">
                 <div class="kpi"><div class="kpi-v">${pops.length}</div><div class="kpi-l">Total</div></div>
                 <div class="kpi"><div class="kpi-v">${ativos.length}</div><div class="kpi-l">Ativos</div></div>
                 <div class="kpi"><div class="kpi-v">${vencendo.length}</div><div class="kpi-l">Revisão Próxima</div></div>
               </div>
               <table><thead><tr><th>ID</th><th>Nome</th><th>Grupo</th><th>Subgrupo</th><th>Produtos</th><th>Versão</th><th>Revisão</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>`);
          }}
          onCSV={()=>exportCSV("pops",
            ["ID","Nome","Grupo","Subgrupo","Produtos","Versão","Data Confecção","Data Revisão","Status","Link","Observações"],
            pops.map(p=>{
              const prods=(p.produtos||[]).map(pid=>produtos.find(x=>x.id===pid)?.nome||pid).join(" | ");
              return [p.id,p.nome,p.grupo||"—",p.subgrupo||"—",prods||"—",p.versao||"—",p.dataConfeccao||"—",p.dataRevisao||"—",p.status||"—",p.link||"—",p.obs||"—"];
            })
          )}
        />
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
          {[{l:"Total POPs",v:pops.length,c:theme.gray800},{l:"Ativos",v:ativos.length,c:theme.green},{l:"Revisão Próxima",v:vencendo.length,c:theme.amber}]
            .map(k=>(<Card key={k.l} style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
              <p style={{fontSize:24,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
            </Card>))
          }
        </div>
        <div style={{display:"grid",gridTemplateColumns:"240px 1fr",gap:16,marginBottom:16}}>
          <Card style={{padding:"18px 20px"}}>
            <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,marginBottom:12}}>Por Grupo</p>
            {Object.entries(porGrupo).sort((a,b)=>b[1]-a[1]).map(([g,n])=>(
              <div key={g} style={{marginBottom:9}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:12,color:theme.gray700}}>{g||"—"}</span>
                  <span style={{fontSize:12,fontWeight:700,color:theme.gold}}>{n}</span>
                </div>
                <div style={{height:7,background:theme.gray100,borderRadius:3}}>
                  <div style={{height:7,borderRadius:3,background:theme.gold,width:`${(n/maxG)*100}%`}}/>
                </div>
              </div>
            ))}
          </Card>
          <Card><div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                {["ID","Nome","Grupo","Produtos Vinculados","Versão","Revisão","Status"].map(h=>(
                  <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {pops.map((p,i)=>{
                  const prods=(p.produtos||[]).map(pid=>produtos.find(x=>x.id===pid)?.nome||pid);
                  const rev=p.dataRevisao?Math.ceil((new Date(p.dataRevisao+"T00:00:00")-hoje)/86400000):null;
                  return (
                    <tr key={p.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                      <td style={{padding:"10px 14px"}}><code style={{fontSize:11,background:theme.gray100,padding:"2px 6px",borderRadius:3}}>{p.id}</code></td>
                      <td style={{padding:"10px 14px",fontSize:13,fontWeight:500,color:theme.gray900}}>{p.nome}</td>
                      <td style={{padding:"10px 14px"}}><Badge color="gray">{p.grupo||"—"}</Badge></td>
                      <td style={{padding:"10px 14px"}}>
                        {prods.length===0?<span style={{color:theme.gray300,fontSize:11}}>—</span>
                          :<div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                            {prods.map((pn,j)=><Badge key={j} color="amber">{pn}</Badge>)}
                          </div>}
                      </td>
                      <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>v{p.versao}</td>
                      <td style={{padding:"10px 14px",fontSize:12}}>
                        <span style={{color:rev!==null&&rev<=30?theme.amber:theme.gray600}}>{p.dataRevisao||"—"}{rev!==null&&rev<=30&&` ⚠️`}</span>
                      </td>
                      <td style={{padding:"10px 14px"}}><Badge color={p.status==="Ativo"?"green":"gray"}>{p.status}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div></Card>
        </div>
      </div>
    );
  };

  // ── RELATÓRIO PRODUTOS ────────────────────────────────────────────────────
  const relProdutos = () => {
    const ativos=produtos.filter(p=>p.status==="Ativo");
    const porArea={};
    produtos.forEach(p=>{
      const a=p.area||"Outros";
      porArea[a]=(porArea[a]||0)+1;
    });
    const maxA=Math.max(...Object.values(porArea),1);
    return (
      <div>
        <BtnActions
          onPDF={()=>{
            const rows=produtos.map(p=>{
              const lps=(p.landingPages||[]).length;
              const popVinc=pops.filter(po=>(po.produtos||[]).includes(p.id)).length;
              return `<tr><td>${p.nome}</td><td>${p.area||"—"}</td><td>${p.modalidade||"—"}</td><td>${p.honorarios||"—"}</td><td>${lps}</td><td>${popVinc}</td><td>${p.status||"—"}</td></tr>`;
            }).join("");
            imprimirPDF("Relatório — Produtos",
              `<h1>Produtos Jurídicos</h1>
               <div class="kpis">
                 <div class="kpi"><div class="kpi-v">${produtos.length}</div><div class="kpi-l">Total</div></div>
                 <div class="kpi"><div class="kpi-v">${ativos.length}</div><div class="kpi-l">Ativos</div></div>
               </div>
               <table><thead><tr><th>Nome</th><th>Área</th><th>Modalidade</th><th>Honorários</th><th>Landing Pages</th><th>POPs Vinculados</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>`);
          }}
          onCSV={()=>exportCSV("produtos",
            ["ID","Nome","Área","Modalidade","Honorários","Descrição","Landing Pages","POPs Vinculados","Status","Observações"],
            produtos.map(p=>{
              const lps=(p.landingPages||[]).map(l=>l.nome).join(" | ");
              const popVinc=pops.filter(po=>(po.produtos||[]).includes(p.id)).map(po=>po.nome).join(" | ");
              return [p.id||"—",p.nome,p.area||"—",p.modalidade||"—",p.honorarios||"—",p.descricao||"—",lps||"—",popVinc||"—",p.status||"—",p.obs||"—"];
            })
          )}
        />
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
          {[{l:"Total Produtos",v:produtos.length,c:theme.gray800},{l:"Ativos",v:ativos.length,c:theme.green},{l:"Áreas Cobertas",v:Object.keys(porArea).length,c:theme.blue}]
            .map(k=>(<Card key={k.l} style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
              <p style={{fontSize:24,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
            </Card>))
          }
        </div>
        <div style={{display:"grid",gridTemplateColumns:"240px 1fr",gap:16}}>
          <Card style={{padding:"18px 20px"}}>
            <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,marginBottom:12}}>Por Área</p>
            {Object.entries(porArea).sort((a,b)=>b[1]-a[1]).map(([a,n])=>(
              <div key={a} style={{marginBottom:9}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:11,color:theme.gray700}}>{a}</span>
                  <span style={{fontSize:12,fontWeight:700,color:theme.blue}}>{n}</span>
                </div>
                <div style={{height:7,background:theme.gray100,borderRadius:3}}>
                  <div style={{height:7,borderRadius:3,background:theme.blue+"99",width:`${(n/maxA)*100}%`}}/>
                </div>
              </div>
            ))}
          </Card>
          <Card><div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                {["Nome","Área","Modalidade","Honorários","LPs","POPs","Status"].map(h=>(
                  <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {produtos.map((p,i)=>{
                  const lps=(p.landingPages||[]).length;
                  const popVinc=pops.filter(po=>(po.produtos||[]).includes(p.id)).length;
                  return (
                    <tr key={p.id||i} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                      <td style={{padding:"10px 14px",fontSize:13,fontWeight:500,color:theme.gray900}}>{p.nome}</td>
                      <td style={{padding:"10px 14px"}}><Badge color="blue">{p.area||"—"}</Badge></td>
                      <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{p.modalidade||"—"}</td>
                      <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{p.honorarios||"—"}</td>
                      <td style={{padding:"10px 14px",textAlign:"center",fontSize:13,fontWeight:700,color:lps>0?theme.blue:theme.gray300}}>{lps}</td>
                      <td style={{padding:"10px 14px",textAlign:"center",fontSize:13,fontWeight:700,color:popVinc>0?theme.gold:theme.gray300}}>{popVinc}</td>
                      <td style={{padding:"10px 14px"}}><Badge color={p.status==="Ativo"?"green":"gray"}>{p.status||"—"}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div></Card>
        </div>
      </div>
    );
  };

  // ── RELATÓRIO TIMESHEET ──────────────────────────────────────
  const relTimesheet = () => {
    const contratosTS = contratos.filter(ct=>{
      const prod = produtos.find(p=>p.id===ct.produtoId);
      if(prod?.tipo!=="pacote_horas") return false;
      if(filtroClienteTS && ct.clienteId !== filtroClienteTS) return false;
      return true;
    });
    // Lista de clientes com contratos timesheet (para o select)
    const clientesTS = [...new Map(
      contratos
        .filter(ct=>produtos.find(p=>p.id===ct.produtoId)?.tipo==="pacote_horas")
        .map(ct=>[ct.clienteId, clientes.find(cl=>cl.id===ct.clienteId)])
        .filter(([,cl])=>cl)
    ).values()];
    if(contratosTS.length===0) return (
      <div style={{textAlign:"center",padding:"40px 0",color:theme.gray400}}>
        <p style={{fontSize:14}}>Nenhum contrato de Consultoria (Pacote) encontrado.</p>
      </div>
    );
    const fmtH = h => (parseFloat(h)||0).toFixed(1)+"h";
    const fmtR = v => "R$ "+(parseFloat(v)||0).toLocaleString("pt-BR",{minimumFractionDigits:2});

    const gerarPDFTimesheet = () => {
      const rows = contratosTS.map(ct => {
        const cli  = clientes.find(cl=>cl.id===ct.clienteId);
        const prod = produtos.find(p=>p.id===ct.produtoId);
        const regBase = baseOperacional.find(r=>r.contratoId===ct.id);
        const entries = [...(regBase?.timesheetEntries || ct.timesheetEntries || [])]
          .sort((a,b)=>(a.data||"")>(b.data||"")?1:-1);
        const horasPacote = regBase?.horasPacote || ct.horasPacote || 0;
        const valorContratado = ct.valorContratado || 0;
        const valorHora = horasPacote>0 ? valorContratado/horasPacote : 0;
        const horasUsadas = entries.reduce((s,e)=>s+(parseFloat(e.horas)||0),0);
        const saldo = horasPacote - horasUsadas;

        const linhasEntries = entries.map((e,i)=>{
          const horasAte = entries.slice(0,i+1).reduce((s,x)=>s+(parseFloat(x.horas)||0),0);
          const saldoAte = horasPacote - horasAte;
          const membro = equipe.find(eq=>eq.id===e.membroId)?.nome||"—";
          return `<tr style="background:${i%2===0?"#fff":"#f9f7f2"}">
            <td style="padding:6px 10px;font-size:11px;white-space:nowrap">${fmtD(e.data)}</td>
            <td style="padding:6px 10px;font-size:11px">${membro}</td>
            <td style="padding:6px 10px;font-size:11px">${e.descricao||"—"}</td>
            <td style="padding:6px 10px;font-size:11px;font-weight:700;color:#3b82f6;text-align:right">${fmtH(e.horas)}</td>
            <td style="padding:6px 10px;font-size:11px;font-weight:700;color:${saldoAte<0?"#dc2626":saldoAte===0?"#d97706":"#16a34a"};text-align:right">${fmtH(saldoAte)}</td>
          </tr>`;
        }).join("");

        return `
          <div style="margin-bottom:28px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;page-break-inside:avoid">
            <div style="background:#1a1610;color:#fff;padding:12px 16px;display:flex;justify-content:space-between">
              <div>
                <p style="font-size:13px;font-weight:700;margin:0">${ct.numContrato||"—"} — ${cli?.nome||"—"}</p>
                <p style="font-size:11px;color:#9ca3af;margin:2px 0 0">${prod?.nome||"—"}</p>
              </div>
              <div style="text-align:right">
                <p style="font-size:12px;color:${saldo<0?"#fca5a5":saldo===0?"#fde68a":"#86efac"};font-weight:700;margin:0">Saldo: ${fmtH(saldo)}</p>
                <p style="font-size:11px;color:#9ca3af;margin:2px 0 0">Valor/hora: ${fmtR(valorHora)}</p>
              </div>
            </div>
            <div style="display:grid;grid-template-columns:repeat(4,1fr);background:#f4f1ea">
              <div style="background:#fff;padding:10px;text-align:center;border-right:1px solid #e5e7eb">
                <p style="font-size:9px;color:#9ca3af;text-transform:uppercase;margin:0 0 3px">Pacote</p>
                <p style="font-size:14px;font-weight:700;color:#374151;margin:0">${fmtH(horasPacote)}</p>
              </div>
              <div style="background:#fff;padding:10px;text-align:center;border-right:1px solid #e5e7eb">
                <p style="font-size:9px;color:#9ca3af;text-transform:uppercase;margin:0 0 3px">Utilizadas</p>
                <p style="font-size:14px;font-weight:700;color:#3b82f6;margin:0">${fmtH(horasUsadas)}</p>
              </div>
              <div style="background:#fff;padding:10px;text-align:center;border-right:1px solid #e5e7eb">
                <p style="font-size:9px;color:#9ca3af;text-transform:uppercase;margin:0 0 3px">Saldo</p>
                <p style="font-size:14px;font-weight:700;color:${saldo<0?"#dc2626":saldo===0?"#d97706":"#16a34a"};margin:0">${fmtH(saldo)}</p>
              </div>
              <div style="background:#fff;padding:10px;text-align:center">
                <p style="font-size:9px;color:#9ca3af;text-transform:uppercase;margin:0 0 3px">Valor Total</p>
                <p style="font-size:14px;font-weight:700;color:#374151;margin:0">${fmtR(valorContratado)}</p>
              </div>
            </div>
            ${entries.length===0
              ? '<p style="padding:16px;font-size:12px;color:#9ca3af;text-align:center">Sem lançamentos de horas.</p>'
              : `<table style="width:100%;border-collapse:collapse">
                  <thead>
                    <tr style="background:#f9f7f2">
                      <th style="padding:8px 10px;text-align:left;font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:.5px">Data</th>
                      <th style="padding:8px 10px;text-align:left;font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:.5px">Membro</th>
                      <th style="padding:8px 10px;text-align:left;font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:.5px">Atividade</th>
                      <th style="padding:8px 10px;text-align:right;font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:.5px">Horas</th>
                      <th style="padding:8px 10px;text-align:right;font-size:10px;color:#9ca3af;text-transform:uppercase;letter-spacing:.5px">Saldo Acum.</th>
                    </tr>
                  </thead>
                  <tbody>${linhasEntries}</tbody>
                </table>`
            }
          </div>`;
      }).join("");

      const html = `<html><head><title>Timesheet — Alves & Correa Advogados</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;color:#2A2420;font-size:12px}
        h1{font-size:14px;font-weight:bold;text-align:center;text-transform:uppercase;border-bottom:2px solid #C9A84C;padding-bottom:8px;margin-bottom:20px}
        .rf{font-size:10px;color:#999;margin-top:24px;border-top:1px solid #eee;padding-top:8px;text-align:center}
        @media print{@page{size:A4;margin:15mm 12mm}body{padding:0}}
      </style></head><body>
      <h1>Relatório de Timesheet — Alves & Correa Advogados</h1>
      <p style="text-align:center;font-size:11px;color:#6b7280;margin-bottom:20px">
        Gerado em ${new Date().toLocaleDateString("pt-BR",{weekday:"long",day:"2-digit",month:"long",year:"numeric"})} às ${new Date().toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"})}
      </p>
      ${rows}
      <div class="rf">Alves & Correa Advogados · Nelson Jose Alves OAB 398.875/SP · Annie Lemos Alves Correa OAB 446.942/SP · Documento Confidencial</div>
      </body></html>`;
      const w = window.open("","_blank");
      w.document.write(html);
      w.document.close();
      w.print();
    };

    return (
      <div>
        {/* Filtro + Botão PDF */}
        <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
          {/* Select de cliente */}
          <select
            value={filtroClienteTS}
            onChange={e=>setFiltroClienteTS(e.target.value)}
            style={{flex:1,minWidth:220,padding:"9px 12px",border:`1px solid ${theme.gray200}`,
              borderRadius:7,fontSize:13,outline:"none",background:theme.white}}>
            <option value="">👤 Todos os clientes</option>
            {clientesTS.map(cl=>(
              <option key={cl.id} value={cl.id}>{cl.nome}</option>
            ))}
          </select>
          {filtroClienteTS&&(
            <button onClick={()=>setFiltroClienteTS("")}
              style={{padding:"9px 12px",borderRadius:7,border:`1px solid ${theme.gray200}`,
                background:theme.white,color:theme.gray500,fontSize:12,cursor:"pointer"}}>
              ✕ Limpar
            </button>
          )}
          <button onClick={gerarPDFTimesheet} style={{
            padding:"9px 18px",borderRadius:8,cursor:"pointer",
            border:`1px solid ${theme.gray200}`,background:theme.white,
            color:theme.gray700,fontSize:12,fontWeight:600,
            display:"flex",alignItems:"center",gap:6,marginLeft:"auto"
          }}>🖨️ {filtroClienteTS?"PDF — "+clientesTS.find(cl=>cl.id===filtroClienteTS)?.nome:"Exportar PDF"}</button>
        </div>
        {/* Resumo quando filtrado */}
        {filtroClienteTS&&contratosTS.length>0&&(()=>{
          const cli = clientesTS.find(cl=>cl.id===filtroClienteTS);
          const totalHorasPacote = contratosTS.reduce((s,ct)=>{
            const regBase = baseOperacional.find(r=>r.contratoId===ct.id);
            return s + (regBase?.horasPacote || ct.horasPacote || 0);
          }, 0);
          const totalUsadas = contratosTS.reduce((s,ct)=>{
            const regBase = baseOperacional.find(r=>r.contratoId===ct.id);
            const entries = regBase?.timesheetEntries || ct.timesheetEntries || [];
            return s + entries.reduce((h,e)=>h+(parseFloat(e.horas)||0),0);
          }, 0);
          const fmtH2 = h=>(parseFloat(h)||0).toFixed(1)+"h";
          const fmtR2 = v=>"R$ "+(parseFloat(v)||0).toLocaleString("pt-BR",{minimumFractionDigits:2});
          const totalContratado = contratosTS.reduce((s,ct)=>s+(ct.valorContratado||0),0);
          return (
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
              {[
                {l:"Cliente",       v:cli?.nome||"—",             c:theme.gray900},
                {l:"Horas Pacote",  v:fmtH2(totalHorasPacote),   c:theme.blue},
                {l:"Horas Usadas",  v:fmtH2(totalUsadas),         c:theme.amber},
                {l:"Saldo Total",   v:fmtH2(totalHorasPacote-totalUsadas), c:(totalHorasPacote-totalUsadas)<0?theme.red:theme.green},
              ].map(k=>(
                <Card key={k.l} style={{padding:"12px 16px",textAlign:"center"}}>
                  <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>{k.l}</p>
                  <p style={{fontSize:k.l==="Cliente"?13:20,fontWeight:700,color:k.c}}>{k.v}</p>
                </Card>
              ))}
            </div>
          );
        })()}
        {contratosTS.map(ct=>{
          const cli  = clientes.find(cl=>cl.id===ct.clienteId);
          const prod = produtos.find(p=>p.id===ct.produtoId);
          const regBase = baseOperacional.find(r=>r.contratoId===ct.id);
          // Ordenar entries por data crescente
          const entries = [...(regBase?.timesheetEntries || ct.timesheetEntries || [])]
            .sort((a,b)=>(a.data||"")>(b.data||"")?1:-1);
          const horasPacote    = regBase?.horasPacote || ct.horasPacote || 0;
          const valorContratado = ct.valorContratado || 0;
          const valorHora      = horasPacote>0 ? valorContratado/horasPacote : 0;
          const horasUsadas    = entries.reduce((s,e)=>s+(parseFloat(e.horas)||0),0);
          const saldo          = horasPacote - horasUsadas;
          return (
            <div key={ct.id} style={{marginBottom:24,border:`1px solid ${theme.gray200}`,borderRadius:10,overflow:"hidden"}}>
              {/* Header */}
              <div style={{background:theme.gray900,color:"#fff",padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <p style={{fontSize:13,fontWeight:700,margin:0}}>{ct.numContrato} — {cli?.nome||"—"}</p>
                  <p style={{fontSize:11,color:theme.gray400,margin:0}}>{prod?.nome||"—"}</p>
                </div>
                <div style={{textAlign:"right"}}>
                  <p style={{fontSize:12,color:saldo<0?"#fca5a5":saldo===0?"#fde68a":"#86efac",margin:0,fontWeight:700}}>
                    Saldo: {fmtH(saldo)}
                  </p>
                  <p style={{fontSize:11,color:theme.gray400,margin:0}}>Valor/hora: {fmtR(valorHora)}</p>
                </div>
              </div>
              {/* KPIs */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:1,background:theme.gray100}}>
                {[
                  {l:"Pacote",    v:fmtH(horasPacote),      c:theme.gray700},
                  {l:"Utilizadas",v:fmtH(horasUsadas),      c:theme.blue},
                  {l:"Saldo",     v:fmtH(saldo),            c:saldo<0?theme.red:saldo===0?"#d97706":theme.green},
                  {l:"Valor Total",v:fmtR(valorContratado), c:theme.gray700},
                ].map(k=>(
                  <div key={k.l} style={{background:"#fff",padding:"10px",textAlign:"center"}}>
                    <p style={{fontSize:9,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,margin:"0 0 3px"}}>{k.l}</p>
                    <p style={{fontSize:14,fontWeight:700,color:k.c,margin:0}}>{k.v}</p>
                  </div>
                ))}
              </div>
              {/* Tabela ordenada por data */}
              {entries.length===0
                ? <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"16px 0"}}>Sem lançamentos de horas.</p>
                : <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:theme.gray50}}>
                        {["Data","Membro","Atividade","Horas","Saldo Acum."].map(h=>(
                          <th key={h} style={{padding:"8px 12px",textAlign:h==="Horas"||h==="Saldo Acum."?"right":"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e,i)=>{
                        const horasAte = entries.slice(0,i+1).reduce((s,x)=>s+(parseFloat(x.horas)||0),0);
                        const saldoAte = horasPacote - horasAte;
                        return (
                          <tr key={e.id||i} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                            <td style={{padding:"8px 12px",color:theme.gray500,whiteSpace:"nowrap"}}>{fmtD(e.data)}</td>
                            <td style={{padding:"8px 12px",color:theme.gray700}}>{equipe.find(eq=>eq.id===e.membroId)?.nome||"—"}</td>
                            <td style={{padding:"8px 12px",color:theme.gray700}}>{e.descricao}</td>
                            <td style={{padding:"8px 12px",fontWeight:600,color:theme.blue,textAlign:"right"}}>{fmtH(e.horas)}</td>
                            <td style={{padding:"8px 12px",fontWeight:600,textAlign:"right",color:saldoAte<0?theme.red:saldoAte===0?"#d97706":theme.green}}>{fmtH(saldoAte)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
              }
            </div>
          );
        })}
      </div>
    );
  };

  const renderRelatorio = () => {
    switch(tipoAtivo) {
      case "clientes":          return relClientes();
      case "leads":             return relLeads();
      case "contratos":         return relContratosAndamento();
      case "processos_parados": return relProcessosParados();
      case "pops":              return relPOPs();
      case "produtos":          return relProdutos();
      case "financeiro":        return relFinanceiro();
      case "timesheet":         return relTimesheet();
      case "rct":               return relRCT();
      case "equipe":            return relEquipe();
      case "parcerias":         return relParcerias();
      default:                   return null;
    }
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="Relatórios"
        subtitle="Selecione um módulo · Use os botões PDF e CSV em cada relatório"
      />

      {/* Seletor de tipo */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:20}}>
        {TIPOS_RELATORIO.map(t=>(
          <button key={t.id} onClick={()=>setTipoAtivo(t.id)} style={{
            padding:"9px 16px",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:600,
            border:`1px solid ${tipoAtivo===t.id?theme.gold:theme.gray200}`,
            background:tipoAtivo===t.id?`${theme.gold}18`:theme.white,
            color:tipoAtivo===t.id?theme.goldDark:theme.gray600,
            display:"flex",alignItems:"center",gap:6,
            transition:"all 0.15s"
          }}>
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
        <div style={{marginLeft:"auto",display:"flex",gap:8,alignItems:"center"}}>
          {tipoAtivo==="financeiro" && (
            <select value={periodo} onChange={e=>setPeriodo(e.target.value)}
              style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none"}}>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
              <option value="180">Últimos 6 meses</option>
              <option value="365">Último ano</option>
              <option value="todos">Todos</option>
            </select>
          )}
        </div>
      </div>

      {/* Conteúdo do relatório */}
      {renderRelatorio()}
    </div>
  );
}

// ============================================================
// FASE 7 — MÓDULO: GOVERNANÇA
// ============================================================

function ModGovernanca({ data, setData }) {
  const { pops, contratos, clientes, equipe, leads, diagnosticosRCT, lancamentos } = data;
  const [aba, setAba] = useState("sla");

  const hoje = new Date();

  const fmtD = d => d ? new Date(d+"T00:00:00").toLocaleDateString("pt-BR") : "—";
  const fmtR = v => "R$ " + (parseFloat(v)||0).toLocaleString("pt-BR",{minimumFractionDigits:2});

  // ── SLA ─────────────────────────────────────────────────────
  const renderSLA = () => {
    const processosAtivos = (contratos||[]).filter(c=>c.status==="Ativo");
    const prazosCriticos  = processosAtivos.filter(c=>{
      if(!c.prazo) return false;
      const diff = Math.ceil((new Date(c.prazo+"T00:00:00")-hoje)/86400000);
      return diff >= 0 && diff <= 7;
    });
    const prazosVencidos  = processosAtivos.filter(c=>{
      if(!c.prazo) return false;
      const diff = Math.ceil((new Date(c.prazo+"T00:00:00")-hoje)/86400000);
      return diff < 0;
    });
    const parados = processosAtivos.filter(c=>{
      if(!c.ultimaMovimentacao) return false;
      const diff = Math.ceil((hoje-new Date(c.ultimaMovimentacao+"T00:00:00"))/86400000);
      return diff > 20;
    });

    const slaScore = processosAtivos.length > 0
      ? Math.round(((processosAtivos.length - prazosCriticos.length - prazosVencidos.length) / processosAtivos.length)*100)
      : 100;

    return (
      <div>
        {/* KPIs SLA */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}}>
          {[
            {l:"Score SLA",        v:`${slaScore}%`,   c:slaScore>=90?theme.green:slaScore>=70?theme.amber:theme.red, icon:"🎯"},
            {l:"Prazos Críticos",  v:prazosCriticos.length, c:prazosCriticos.length>0?theme.amber:theme.green, icon:"⚠️"},
            {l:"Prazos Vencidos",  v:prazosVencidos.length, c:prazosVencidos.length>0?theme.red:theme.green,   icon:"🚨"},
            {l:"Processos Parados",v:parados.length,        c:parados.length>0?theme.amber:theme.green,         icon:"⏸️"},
          ].map(k=>(
            <Card key={k.l} style={{padding:"16px 18px"}}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
                <span style={{fontSize:16}}>{k.icon}</span>
              </div>
              <p style={{fontSize:28,fontWeight:700,color:k.c,marginTop:8}}>{k.v}</p>
            </Card>
          ))}
        </div>

        {/* Tabela prazos críticos */}
        {(prazosCriticos.length > 0 || prazosVencidos.length > 0) && (
          <Card style={{marginBottom:16}}>
            <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,padding:"14px 18px",borderBottom:`1px solid ${theme.gray100}`}}>
              Alertas de Prazo
            </p>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{borderBottom:`1px solid ${theme.gray100}`}}>
                  {["Processo","Cliente","Responsável","Prazo","Situação"].map(h=>(
                    <th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...prazosVencidos, ...prazosCriticos].map((c,i)=>{
                  const cli  = clientes.find(cl=>cl.id===c.clienteId);
                  const resp = equipe.find(e=>e.id===c.responsavel);
                  const diff = Math.ceil((new Date(c.prazo+"T00:00:00")-hoje)/86400000);
                  return (
                    <tr key={c.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:diff<0?"#FEF2F2":i%2===0?theme.white:theme.gray50}}>
                      <td style={{padding:"9px 14px",fontSize:12,fontFamily:"monospace",color:theme.gray600}}>{c.id}</td>
                      <td style={{padding:"9px 14px",fontSize:13,fontWeight:500,color:theme.gray900}}>{cli?.nome||"—"}</td>
                      <td style={{padding:"9px 14px",fontSize:12,color:theme.gray600}}>{resp?.nome?.split(" ").slice(0,2).join(" ")||"—"}</td>
                      <td style={{padding:"9px 14px",fontSize:12,color:diff<0?theme.red:theme.amber,fontWeight:600}}>{fmtD(c.prazo)}</td>
                      <td style={{padding:"9px 14px"}}>
                        <Badge color={diff<0?"red":"amber"}>
                          {diff<0?`Vencido há ${Math.abs(diff)}d`:`Vence em ${diff}d`}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}

        {/* Carga por responsável */}
        <Card style={{padding:"18px 20px"}}>
          <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,marginBottom:14}}>Carga por Responsável</p>
          {equipe.filter(e=>e.ativo).map(e=>{
            const processos = processosAtivos.filter(c=>c.responsavel===e.id);
            const criticos  = processos.filter(c=>{
              if(!c.prazo) return false;
              const diff = Math.ceil((new Date(c.prazo+"T00:00:00")-hoje)/86400000);
              return diff >= 0 && diff <= 7;
            }).length;
            const max = Math.max(...equipe.filter(eq=>eq.ativo).map(eq=>processosAtivos.filter(c=>c.responsavel===eq.id).length),1);
            return (
              <div key={e.id} style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:12,color:theme.gray700}}>{e.nome.split(" ").slice(0,2).join(" ")}</span>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    {criticos>0 && <Badge color="amber">{criticos} crítico{criticos>1?"s":""}</Badge>}
                    <span style={{fontSize:12,fontWeight:700,color:theme.gray800}}>{processos.length} processos</span>
                  </div>
                </div>
                <div style={{height:8,background:theme.gray100,borderRadius:4}}>
                  <div style={{height:8,borderRadius:4,
                    background:processos.length/max>0.8?theme.red:processos.length/max>0.6?theme.amber:theme.green,
                    width:`${(processos.length/max)*100}%`,transition:"width 0.5s"}}/>
                </div>
              </div>
            );
          })}
        </Card>
      </div>
    );
  };

  // ── POPs ─────────────────────────────────────────────────────
  const renderPOPs = () => {
    const vencidos    = pops.filter(p=>{
      if(!p.dataRevisao) return false;
      return new Date(p.dataRevisao+"T00:00:00") < hoje;
    });
    const vencendoEm30 = pops.filter(p=>{
      if(!p.dataRevisao) return false;
      const d = new Date(p.dataRevisao+"T00:00:00");
      const diff = (d-hoje)/86400000;
      return diff >= 0 && diff <= 30;
    });

    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
          {[
            {l:"Total POPs",        v:pops.length,c:theme.gray800},
            {l:"Ativos",            v:pops.filter(p=>p.status==="Ativo").length,c:theme.green},
            {l:"Revisão Vencida",   v:vencidos.length,c:vencidos.length>0?theme.red:theme.green},
            {l:"Vencendo em 30d",   v:vencendoEm30.length,c:vencendoEm30.length>0?theme.amber:theme.green},
          ].map(k=>(
            <Card key={k.l} style={{padding:"14px 18px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
              <p style={{fontSize:24,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
            </Card>
          ))}
        </div>
        <Card>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{borderBottom:`2px solid ${theme.gray100}`}}>
                {["ID","Nome do POP","Grupo","Subgrupo","Versão","Última Revisão","Próx. Revisão","Status"].map(h=>(
                  <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pops.map((p,i)=>{
                const revisaoVencida = p.dataRevisao && new Date(p.dataRevisao+"T00:00:00") < hoje;
                const revisaoEm30   = p.dataRevisao && (() => {
                  const d = new Date(p.dataRevisao+"T00:00:00");
                  const diff = (d-hoje)/86400000;
                  return diff >= 0 && diff <= 30;
                })();
                return (
                  <tr key={p.id} style={{
                    borderBottom:`1px solid ${theme.gray100}`,
                    background:revisaoVencida?"#FEF2F2":revisaoEm30?"#FFFBEB":i%2===0?theme.white:theme.gray50
                  }}>
                    <td style={{padding:"10px 14px",fontSize:11,color:theme.gray500,fontFamily:"monospace"}}>{p.id}</td>
                    <td style={{padding:"10px 14px",fontSize:13,fontWeight:500,color:theme.gray900}}>{p.nome}</td>
                    <td style={{padding:"10px 14px"}}><Badge color="gray">{p.grupo||"—"}</Badge></td>
                    <td style={{padding:"10px 14px",fontSize:12,color:theme.gray600}}>{p.subgrupo||"—"}</td>
                    <td style={{padding:"10px 14px",fontSize:12,fontWeight:700,color:theme.gray700,textAlign:"center"}}>{p.versao||"—"}</td>
                    <td style={{padding:"10px 14px",fontSize:12,color:theme.gray500}}>{fmtD(p.dataConfeccao)}</td>
                    <td style={{padding:"10px 14px",fontSize:12,color:revisaoVencida?theme.red:revisaoEm30?theme.amber:theme.gray600,fontWeight:revisaoVencida||revisaoEm30?700:400}}>
                      {fmtD(p.dataRevisao)}
                      {revisaoVencida && <span style={{fontSize:10,marginLeft:4}}>⚠ vencido</span>}
                    </td>
                    <td style={{padding:"10px 14px"}}><Badge color={p.status==="Ativo"?"green":"gray"}>{p.status}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>
    );
  };

  // ── AUDITORIA ────────────────────────────────────────────────
  const renderAuditoria = () => {
    // Inconsistências detectadas automaticamente
    const alertas = [];

    // Contratos sem responsável
    (contratos||[]).filter(c=>!c.responsavel).forEach(c=>{
      const cli = clientes.find(cl=>cl.id===c.clienteId);
      alertas.push({tipo:"warning",modulo:"Base",msg:`Contrato ${c.id} (${cli?.nome||"?"}) sem responsável definido.`});
    });

    // Clientes sem contrato
    clientes.filter(c=>c.ativo).forEach(c=>{
      const temContrato = (contratos||[]).some(ct=>ct.clienteId===c.id);
      if(!temContrato) alertas.push({tipo:"info",modulo:"Clientes",msg:`Cliente "${c.nome}" ativo sem contratos registrados.`});
    });

    // Diagnósticos RCT sem supervisor
    diagnosticosRCT.filter(d=>!d.supervisor).forEach(d=>{
      const cli = clientes.find(c=>c.id===d.clienteId);
      alertas.push({tipo:"warning",modulo:"RCT",msg:`Diagnóstico RCT de "${cli?.nome||d.clienteId}" sem supervisor definido.`});
    });

    // Leads sem contato há mais de 60 dias
    leads.filter(l=>!["fechado","perdido"].includes(l.estagio)).forEach(l=>{
      const ultOcorr = (l.historico||[]).sort((a,b)=>b.data?.localeCompare(a.data))[0];
      const dataRef  = ultOcorr?.data || l.dataEntrada;
      if(dataRef) {
        const diff = Math.ceil((hoje-new Date(dataRef+"T00:00:00"))/86400000);
        if(diff > 60) alertas.push({tipo:"warning",modulo:"CRM",msg:`Lead "${l.nome||l.nomeCliente||"—"}" sem contato há ${diff} dias.`});
      }
    });

    // POPs com revisão vencida
    pops.filter(p=>p.dataRevisao && new Date(p.dataRevisao+"T00:00:00") < hoje).forEach(p=>{
      alertas.push({tipo:"danger",modulo:"POPs",msg:`POP "${p.nome}" com revisão vencida desde ${fmtD(p.dataRevisao)}.`});
    });

    const porModulo = {};
    alertas.forEach(a=>{ porModulo[a.modulo]=(porModulo[a.modulo]||0)+1; });

    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
          {[
            {l:"Total Alertas",  v:alertas.length,                                      c:alertas.length>0?theme.amber:theme.green,icon:"🔔"},
            {l:"Críticos",       v:alertas.filter(a=>a.tipo==="danger").length,           c:theme.red,                                icon:"🚨"},
            {l:"Atenção",        v:alertas.filter(a=>a.tipo==="warning").length,          c:theme.amber,                             icon:"⚠️"},
          ].map(k=>(
            <Card key={k.l} style={{padding:"14px 18px"}}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:0.8}}>{k.l}</p>
                <span>{k.icon}</span>
              </div>
              <p style={{fontSize:28,fontWeight:700,color:k.c,marginTop:6}}>{k.v}</p>
            </Card>
          ))}
        </div>

        {alertas.length===0
          ? <Card style={{padding:"32px",textAlign:"center"}}>
              <p style={{fontSize:32,marginBottom:8}}>✅</p>
              <p style={{fontSize:16,fontWeight:600,color:theme.green}}>Nenhuma inconsistência detectada</p>
              <p style={{fontSize:13,color:theme.gray400,marginTop:4}}>Todos os módulos estão sem alertas no momento.</p>
            </Card>
          : <Card>
              <p style={{fontSize:11,color:theme.gray500,textTransform:"uppercase",letterSpacing:0.8,padding:"14px 18px",borderBottom:`1px solid ${theme.gray100}`}}>
                Inconsistências Detectadas
              </p>
              {alertas.map((a,i)=>(
                <div key={i} style={{
                  display:"flex",alignItems:"flex-start",gap:14,
                  padding:"12px 18px",borderBottom:`1px solid ${theme.gray100}`,
                  background:a.tipo==="danger"?"#FEF2F2":a.tipo==="warning"?"#FFFBEB":theme.white
                }}>
                  <span style={{fontSize:16,flexShrink:0}}>
                    {a.tipo==="danger"?"🚨":a.tipo==="warning"?"⚠️":"ℹ️"}
                  </span>
                  <div style={{flex:1}}>
                    <Badge color={a.tipo==="danger"?"red":a.tipo==="warning"?"amber":"blue"}
                      style={{marginBottom:4}}>{a.modulo}</Badge>
                    <p style={{fontSize:13,color:theme.gray700,marginTop:4}}>{a.msg}</p>
                  </div>
                </div>
              ))}
            </Card>
        }
      </div>
    );
  };

  const abas = [
    {id:"sla",       label:"SLA e Prazos",     icon:"🎯"},
    {id:"pops",      label:"Auditoria de POPs", icon:"📋"},
    {id:"auditoria", label:"Inconsistências",   icon:"🔍"},
  ];

  return (
    <div className="fade-in">
      <PageHeader
        title="Governança"
        subtitle="Controle de SLA, revisão de POPs e auditoria de inconsistências"
      />

      {/* Abas */}
      <div style={{display:"flex",gap:4,borderBottom:`1px solid ${theme.gray200}`,marginBottom:24}}>
        {abas.map(a=>(
          <button key={a.id} onClick={()=>setAba(a.id)} style={{
            padding:"10px 18px",border:"none",background:"none",cursor:"pointer",
            fontSize:13,fontWeight:aba===a.id?700:400,
            color:aba===a.id?theme.gray900:theme.gray400,
            borderBottom:aba===a.id?`2px solid ${theme.gold}`:"2px solid transparent",
            marginBottom:-1,display:"flex",alignItems:"center",gap:6
          }}>
            <span>{a.icon}</span> {a.label}
          </button>
        ))}
      </div>

      {aba==="sla"       && renderSLA()}
      {aba==="pops"      && renderPOPs()}
      {aba==="auditoria" && renderAuditoria()}
    </div>
  );
}


// ============================================================
// PLACEHOLDER PARA MÓDULOS FUTUROS
// ============================================================
function ModulePlaceholder({ title, fase, descricao }) {
  return (
    <div className="fade-in" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <div style={{ textAlign: "center", maxWidth: 480 }}>
        <div style={{
          width: 80, height: 80, borderRadius: "50%",
          background: `linear-gradient(135deg, ${theme.gray100}, ${theme.gray200})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 20px", fontSize: 32
        }}>🚀</div>
        <h2 className="serif" style={{ fontSize: 26, color: theme.gray800, marginBottom: 8 }}>{title}</h2>
        <Badge color="amber">{fase}</Badge>
        <p style={{ fontSize: 14, color: theme.gray500, marginTop: 16, lineHeight: 1.7 }}>{descricao}</p>
        <div style={{ marginTop: 24, padding: "16px 20px", background: `${theme.gold}11`, border: `1px solid ${theme.gold}33`, borderRadius: 10 }}>
          <p style={{ fontSize: 12, color: theme.goldDark }}>Este módulo será construído na próxima fase do desenvolvimento.</p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// APP PRINCIPAL
// ============================================================

const EMOJIS_IA = {
  "Atendimento e Comunicação com o Cliente": "💬",
  "Audiência e Julgamento": "⚖️",
  "Contratos": "📝",
  "Criação de Peças Jurídicas": "✍️",
  "Criação de Prompts": "💡",
  "Direito Civil": "🏛️",
  "Direito Digital": "💻",
  "Direito do Trabalho": "👷",
  "Direito Empresarial": "🏢",
  "Direito Militar": "🪖",
  "Direito Penal": "🔒",
  "Direito Tributário": "💰",
  "Dosimetria da Pena": "⚖️",
  "Estratégia do Caso": "♟️",
  "Extração de Dados": "🔍",
  "Jurisprudência": "📚",
  "Marketing Jurídico e Vendas": "📣",
  "Negociação e Gestão de Conflitos": "🤝",
  "Revisão de Peças Jurídicas": "✅",
  "Revisão e Melhoria de Textos e Documentos": "📄",
  "Segurança Pública — Policiais": "🚔",
  "Transcrição e Degravação de Áudio": "🎙️",
};



const TIPOS_LINK = {
  "chatgpt":  { label: "🟢 ChatGPT",  cor: "#10a37f" },
  "claude":   { label: "🟣 Claude",   cor: "#7c3aed" },
  "gemini":   { label: "🔴 Gemini",   cor: "#ea4335" },
  "outros":   { label: "🔗 Outros",   cor: "#64748b" },
};


// ── CardAgentePublico: mesmo visual do CardAgente do ModPainelIA, sem edição ──
function CardAgentePublico({ ag }) {
  const gradTemaP = t => {
    const cores = [
      ["#1e3a5f","#2563eb"],["#1a1a2e","#7c3aed"],["#1a2e1a","#16a34a"],
      ["#2e1a1a","#dc2626"],["#2e2a1a","#d97706"],["#1a2e2e","#0891b2"],
      ["#2e1a2e","#9333ea"],["#1e2e1a","#15803d"],["#2e2016","#b45309"],
      ["#16202e","#0284c7"],["#2e1620","#be123c"],["#201630","#6d28d9"],
    ];
    let h = 0;
    for(let i=0;i<(t||"").length;i++) h=(h*31+(t||"").charCodeAt(i))&0xfffffff;
    const [c1,c2] = cores[h%cores.length];
    return `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`;
  };

  return (
    <div style={{
      background: theme.white, borderRadius:10, overflow:"hidden",
      border:`1px solid ${theme.gray100}`,
      boxShadow:"0 1px 4px rgba(0,0,0,.06)",
      display:"flex", flexDirection:"column", transition:"box-shadow .15s",
    }}
      onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,.12)"}
      onMouseLeave={e=>e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,.06)"}
    >
      {/* Header com gradiente por tema */}
      <div style={{background: gradTemaP(ag.tema||ag.nome||""),
        padding:"16px 16px 12px", display:"flex", alignItems:"center", gap:12}}>
        <div style={{
          width:44, height:44, borderRadius:10, flexShrink:0,
          background: ag.img ? `url(${ag.img}) center/cover` : "rgba(255,255,255,.15)",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:22, overflow:"hidden", border:"2px solid rgba(255,255,255,.2)"
        }}>
          {!ag.img && (ag.emoji || "🤖")}
        </div>
        <p style={{fontWeight:700, fontSize:13, color:"#fff", lineHeight:1.3,
          textShadow:"0 1px 3px rgba(0,0,0,.3)", margin:0}}>{ag.nome}</p>
      </div>

      {/* Body */}
      <div style={{padding:"12px 14px", flex:1, display:"flex", flexDirection:"column", gap:10}}>
        {ag.tema && (
          <span style={{fontSize:10,padding:"2px 8px",borderRadius:4,fontWeight:700,
            background:theme.gold+"18",color:theme.gold,width:"fit-content"}}>{ag.tema}</span>
        )}
        {ag.desc && (
          <p style={{fontSize:12, color:theme.gray500, lineHeight:1.5, flex:1,
            display:"-webkit-box", WebkitLineClamp:3,
            WebkitBoxOrient:"vertical", overflow:"hidden", margin:0}}>{ag.desc}</p>
        )}
        {/* Links */}
        <div style={{display:"flex", flexWrap:"wrap", gap:5}}>
          {(ag.links||[]).filter(l=>l.url).map((l,i)=>(
            <a key={i} href={l.url} target="_blank" rel="noreferrer" style={{
              padding:"5px 11px", borderRadius:6, fontSize:11,
              fontWeight:600, textDecoration:"none", color:"#fff",
              background: TIPOS_LINK[l.tipo]?.cor||"#64748b",
              display:"inline-flex", alignItems:"center", gap:4,
            }}
              onMouseEnter={e=>e.currentTarget.style.opacity=".85"}
              onMouseLeave={e=>e.currentTarget.style.opacity="1"}
            >{l.label || TIPOS_LINK[l.tipo]?.label || "🔗 Abrir"}</a>
          ))}
          {!(ag.links||[]).some(l=>l.url) && ag.link && (
            <a href={ag.link} target="_blank" rel="noreferrer" style={{
              padding:"5px 11px", borderRadius:6, fontSize:11,
              fontWeight:600, textDecoration:"none", color:"#fff",
              background:"#64748b", display:"inline-flex", alignItems:"center",
            }}>🔗 Abrir Agente</a>
          )}
          {!(ag.links||[]).some(l=>l.url) && !ag.link && (
            <span style={{fontSize:11, color:theme.gray300, fontStyle:"italic"}}>Sem links</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Assistente IA — componente reutilizável ──────────────────
function AssistenteChat({ data, contextoExtra }) {
  const [hist,       setHist]       = React.useState([]);
  const [input,      setInput]      = React.useState("");
  const [carregando, setCarregando] = React.useState(false);
  const bottomRef = React.useRef(null);

  React.useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:"smooth"}); },[hist]);

  const enviar = async () => {
    const q = input.trim();
    if (!q || carregando) return;
    setInput("");
    const novoHist = [...hist, {tipo:"user",texto:q}];
    setHist(novoHist);
    setCarregando(true);
    try {
      const produtosCtx = (data.produtos||[]).slice(0,40).map(p=>
        `• ${p.nome}${p.area?" ["+p.area+"]":""}`+
        ((p.documentosPadrao||p.trib_documentos||[]).length>0
          ? " | Docs: "+(p.documentosPadrao||p.trib_documentos||[]).map(d=>{
              const id=typeof d==="string"?d:(d.id||d.docId||"");
              return typeof d==="string"?((data.documentosTrib||[]).find(x=>x.id===d)?.nome||d):(d.nome||id);
            }).filter(Boolean).join(", ")
          : "")
      ).join("\n");

      const ctx = `Você é o assistente especializado do Sistema Operacional Jurídico — Alves & Correa Advogados.
Responda de forma objetiva e prática. Indique caminhos de navegação quando relevante.

ESCRITÓRIO: ${data.escritorio?.nome||"Alves & Correa Advogados"}
PRODUTOS (${(data.produtos||[]).length}):
${produtosCtx||"—"}
CONTRATOS ATIVOS: ${(data.contratos||[]).length} | CLIENTES: ${(data.clientes||[]).length} | PROCESSOS: ${(data.processos||[]).length}
${contextoExtra||""}`;

      const apiKey = data.escritorio?.anthropicApiKey || data.anthropicApiKey || "";
      if (!apiKey) throw new Error("chave_ausente");

      const msgs = novoHist.map(h=>({
        role: h.tipo==="user" ? "user" : "assistant",
        content: h.texto
      }));

      const resp = await fetch(CLAUDE_URL,{
        method:"POST",
        headers:{"Content-Type":"application/json","X-Token":sessionStorage.getItem('ac_token')||''},
        body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:1000,
          system:ctx, messages:msgs,
          _apiKey: apiKey   // enviado ao PHP para usar como x-api-key
        })
      });
      if(!resp.ok) throw new Error("HTTP "+resp.status);
      const d = await resp.json();
      const texto = d.content?.[0]?.text || "Sem resposta.";
      setHist([...novoHist,{tipo:"ai",texto}]);
    } catch(e) {
      const msg = e.message==="chave_ausente"
        ? "⚠️ Chave API não configurada. Acesse Dados do Escritório → campo Chave API Anthropic."
        : "⚠️ Erro: "+e.message;
      setHist([...novoHist,{tipo:"ai",texto:msg}]);
    } finally { setCarregando(false); }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:480,
      background:theme.white,borderRadius:12,border:`1px solid ${theme.gray200}`,
      overflow:"hidden"}}>
      {/* Histórico */}
      <div style={{flex:1,overflowY:"auto",padding:"16px 18px",display:"flex",
        flexDirection:"column",gap:12}}>
        {hist.length===0 && (
          <div style={{textAlign:"center",paddingTop:60}}>
            <p style={{fontSize:32,marginBottom:8}}>🤖</p>
            <p style={{fontSize:14,fontWeight:600,color:theme.gray600}}>Assistente IA</p>
            <p style={{fontSize:12,color:theme.gray400,marginTop:4}}>
              Pergunte sobre produtos, documentos, fluxos ou como usar qualquer módulo.
            </p>
          </div>
        )}
        {hist.map((h,i)=>(
          <div key={i} style={{display:"flex",justifyContent:h.tipo==="user"?"flex-end":"flex-start"}}>
            <div style={{maxWidth:"80%",padding:"10px 14px",borderRadius:10,fontSize:13,lineHeight:1.7,
              background:h.tipo==="user"?theme.gray900:theme.gray50,
              color:h.tipo==="user"?"#C9A84C":theme.gray800,
              border:h.tipo==="ai"?`1px solid ${theme.gray200}`:"none",
              whiteSpace:"pre-wrap"}}>
              {h.texto}
            </div>
          </div>
        ))}
        {carregando && (
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{padding:"10px 14px",borderRadius:10,background:theme.gray50,
              border:`1px solid ${theme.gray200}`,fontSize:13,color:theme.gray400}}>
              ⏳ Consultando...
            </div>
          </div>
        )}
        <div ref={bottomRef}/>
      </div>
      {/* Input */}
      <div style={{padding:"12px 14px",borderTop:`1px solid ${theme.gray100}`,
        display:"flex",gap:8,alignItems:"center"}}>
        <input value={input} onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&(e.preventDefault(),enviar())}
          placeholder="Digite sua pergunta..."
          style={{flex:1,padding:"9px 14px",borderRadius:8,border:`1px solid ${theme.gray200}`,
            fontSize:13,outline:"none",fontFamily:"inherit"}}/>
        <button onClick={enviar} disabled={!input.trim()||carregando}
          style={{padding:"9px 18px",borderRadius:8,border:"none",
            background:input.trim()&&!carregando?theme.gray900:theme.gray200,
            color:input.trim()&&!carregando?"#C9A84C":theme.gray400,
            cursor:input.trim()&&!carregando?"pointer":"default",
            fontSize:13,fontWeight:700,fontFamily:"inherit",whiteSpace:"nowrap"}}>
          Enviar →
        </button>
      </div>
    </div>
  );
}

function ModPainelIA({ data, setData, usuario }) {
  const temasIA = data.temasIA || [];
  const agentes = data.agentes || [];
  const isAdmin = usuario?.perfil === "Administrador";

  // "dashboard" | "lista" | string (tema específico)
  const [view,         setView]         = useState("dashboard");
  const [busca,        setBusca]        = useState("");
  const [temaAtivo,    setTemaAtivo]    = useState("");
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editItem,     setEditItem]     = useState(null);
  const [modalTemas,   setModalTemas]   = useState(false);
  const [novoTemaIA,   setNovoTemaIA]   = useState("");
  const [editTemaIdx,  setEditTemaIdx]  = useState(null);
  const [editTemaVal,  setEditTemaVal]  = useState("");
  const [modalFiltro,  setModalFiltro]  = useState(false);
  const [buscaDash,    setBuscaDash]    = useState("");
  const agsFiltradosDash = buscaDash.trim()
    ? agentes.filter(a => {
        const q = buscaDash.toLowerCase();
        return a.nome.toLowerCase().includes(q) ||
               (a.tema||"").toLowerCase().includes(q) ||
               (a.desc||"").toLowerCase().includes(q);
      })
    : [];

  // Temas visíveis no dashboard (padrão: todos)
  const [temasFiltro, setTemasFiltro] = useState(() => {
    try {
      const s = localStorage.getItem("ac_painelia_filtro");
      return s ? JSON.parse(s) : null;
    } catch(e) { return null; }
  });

  const salvarFiltro = (selecionados) => {
    setTemasFiltro(selecionados);
    try { localStorage.setItem("ac_painelia_filtro", JSON.stringify(selecionados)); } catch(e) {}
  };

  const temasVisiveis = temasFiltro
    ? temasIA.filter(t => temasFiltro.includes(t))
    : temasIA;

  // ── helpers visuais ───────────────────────────────────────
  const emojiTema = t => EMOJIS_IA[t] || "🤖";

  const gradTema = t => {
    const cores = [
      ["#1e3a5f","#2563eb"],["#1a1a2e","#7c3aed"],["#1a2e1a","#16a34a"],
      ["#2e1a1a","#dc2626"],["#2e2a1a","#d97706"],["#1a2e2e","#0891b2"],
      ["#2e1a2e","#9333ea"],["#1e2e1a","#15803d"],["#2e2016","#b45309"],
      ["#16202e","#0284c7"],["#2e1620","#be123c"],["#201630","#6d28d9"],
      ["#1a2420","#065f46"],["#2a1e10","#92400e"],["#0f1e30","#1d4ed8"],
      ["#1e1020","#6b21a8"],["#102018","#166534"],["#201010","#991b1b"],
      ["#201c10","#854d0e"],["#0e1a20","#155e75"],["#18101e","#7e22ce"],
      ["#101a10","#14532d"],["#1a1010","#7f1d1d"],
    ];
    let h = 0;
    for(let i=0;i<t.length;i++) h=(h*31+t.charCodeAt(i))&0xfffffff;
    const [c1,c2] = cores[h%cores.length];
    return `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`;
  };

  const agsPorTema = t => agentes.filter(a => a.tema === t);

  // ── CRUD Agentes ─────────────────────────────────────────
  const EMPTY_AGENTE = { id:"", nome:"", tema:"", desc:"", emoji:"🤖", img:null, links:[] };
  const EMPTY_LINK   = { tipo:"gpt", url:"", label:"" };

  const openNew  = (temaInicial) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setEditItem({ ...EMPTY_AGENTE, tema: temaInicial||"", links:[] });
    setModalOpen(true);
  };
  const openEdit = a => { setEditItem({ ...a, links:[...(a.links||[])] }); setModalOpen(true); };

  const addLink    = () => setEditItem(prev => ({ ...prev, links:[...prev.links, {...EMPTY_LINK}] }));
  const removeLink = i  => setEditItem(prev => ({ ...prev, links:prev.links.filter((_,j)=>j!==i) }));
  const updateLink = (i, campo, val) => setEditItem(prev => {
    const links = prev.links.map((l,j) => j===i ? {...l,[campo]:val} : l);
    return { ...prev, links };
  });

  const onImgUpload = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setEditItem(prev => ({ ...prev, img:ev.target.result, emoji:"" }));
    reader.readAsDataURL(file);
  };

  const save = () => {
    if (!editItem.nome.trim()) return alert("Nome é obrigatório.");
    if (!editItem.tema)        return alert("Selecione um tema.");
    const id   = editItem.id || "AG" + String(Date.now()).slice(-8);
    const item = { ...editItem, id };
    setData(prev => {
      const lista = item.id && prev.agentes.find(a => a.id === item.id)
        ? prev.agentes.map(a => a.id === item.id ? item : a)
        : [...(prev.agentes||[]), item];
      return { ...prev, agentes: lista };
    });
    setModalOpen(false);
  };

  const remove = id => {
    if (!confirm("Remover este agente?")) return;
    setData(prev => ({ ...prev, agentes: prev.agentes.filter(a => a.id !== id) }));
  };

  // ── CRUD Temas IA ────────────────────────────────────────
  const addTemaIA = () => {
    const t = novoTemaIA.trim();
    if (!t) return;
    if (temasIA.includes(t)) return alert("Tema já existe.");
    const novos = [...temasIA, t].sort((a,b)=>a.localeCompare(b,"pt-BR"));
    setData(prev => ({ ...prev, temasIA: novos }));
    setNovoTemaIA("");
  };

  const saveEditTema = idx => {
    const t = editTemaVal.trim();
    if (!t) return;
    const antigo = temasIA[idx];
    const novos  = temasIA.map((x,i)=>i===idx?t:x).sort((a,b)=>a.localeCompare(b,"pt-BR"));
    setData(prev => ({
      ...prev,
      temasIA: novos,
      agentes: prev.agentes.map(a => a.tema===antigo ? {...a,tema:t} : a)
    }));
    setEditTemaIdx(null);
  };

  const removeTemaIA = idx => {
    const t = temasIA[idx];
    if (agentes.some(a => a.tema===t)) return alert(`O tema "${t}" tem agentes vinculados. Remova-os primeiro.`);
    if (!confirm(`Remover tema "${t}"?`)) return;
    setData(prev => ({ ...prev, temasIA: prev.temasIA.filter((_,i)=>i!==idx) }));
  };

  // ── Filtros na view lista ─────────────────────────────────
  const agsFiltrados = agentes.filter(a => {
    const q = busca.toLowerCase();
    const matchBusca = !busca ||
      a.nome.toLowerCase().includes(q) ||
      (a.tema||"").toLowerCase().includes(q) ||
      (a.desc||"").toLowerCase().includes(q);
    const matchTema = !temaAtivo || a.tema === temaAtivo;
    return matchBusca && matchTema;
  }).sort((a,b)=>(a.nome||"").localeCompare(b.nome||"","pt-BR"));

  const grupos = {};
  agsFiltrados.forEach(a => {
    const t = a.tema || "Sem tema";
    if (!grupos[t]) grupos[t] = [];
    grupos[t].push(a);
  });
  const temasComAgentes = Object.keys(grupos).sort((a,b)=>a.localeCompare(b,"pt-BR"));
  const temasUsados = [...new Set(agentes.map(a=>a.tema||"Sem tema"))].sort((a,b)=>a.localeCompare(b,"pt-BR"));

  // ── Card de Agente (reutilizável) ─────────────────────────
  const CardAgente = ({ ag }) => (
    <div style={{
      background: theme.white, borderRadius:10, overflow:"hidden",
      border:`1px solid ${theme.gray100}`,
      boxShadow:"0 1px 4px rgba(0,0,0,.06)",
      display:"flex", flexDirection:"column",
      transition:"box-shadow .15s",
    }}
      onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,.12)"}
      onMouseLeave={e=>e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,.06)"}
    >
      {/* Header colorido */}
      <div style={{
        background: gradTema(ag.tema||""),
        padding:"16px 16px 12px", display:"flex", alignItems:"center", gap:12
      }}>
        <div style={{
          width:44, height:44, borderRadius:10, flexShrink:0,
          background: ag.img ? `url(${ag.img}) center/cover` : "rgba(255,255,255,.15)",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:22, overflow:"hidden", border:"2px solid rgba(255,255,255,.2)"
        }}>
          {!ag.img && (ag.emoji || emojiTema(ag.tema))}
        </div>
        <p style={{
          fontWeight:700, fontSize:13, color:"#fff", lineHeight:1.3,
          textShadow:"0 1px 3px rgba(0,0,0,.3)"
        }}>{ag.nome}</p>
      </div>

      {/* Body */}
      <div style={{ padding:"12px 14px", flex:1, display:"flex", flexDirection:"column", gap:10 }}>
        {ag.desc && (
          <p style={{
            fontSize:12, color:theme.gray500, lineHeight:1.5, flex:1,
            display:"-webkit-box", WebkitLineClamp:3,
            WebkitBoxOrient:"vertical", overflow:"hidden"
          }}>{ag.desc}</p>
        )}

        {/* Links */}
        <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
          {(ag.links||[]).filter(l=>l.url).map((l,i) => (
            <a key={i} href={l.url} target="_blank" rel="noreferrer" style={{
              padding:"5px 11px", borderRadius:6, fontSize:11,
              fontWeight:600, textDecoration:"none", color:"#fff",
              background: TIPOS_LINK[l.tipo]?.cor||"#64748b",
              display:"inline-flex", alignItems:"center", gap:4,
              transition:"opacity .15s"
            }}
              onMouseEnter={e=>e.currentTarget.style.opacity=".85"}
              onMouseLeave={e=>e.currentTarget.style.opacity="1"}
            >{l.label || TIPOS_LINK[l.tipo]?.label || "🔗 Abrir"}</a>
          ))}
          {!(ag.links||[]).some(l=>l.url) && (
            <span style={{ fontSize:11, color:theme.gray300, fontStyle:"italic" }}>Sem links</span>
          )}
        </div>

        {/* Admin */}
        {isAdmin && (
          <div style={{ display:"flex", gap:5, paddingTop:8, borderTop:`1px solid ${theme.gray100}` }}>
            <Btn size="sm" variant="secondary" onClick={()=>openEdit(ag)}>✏️ Editar</Btn>
            <Btn size="sm" variant="danger" onClick={()=>remove(ag.id)}>🗑️</Btn>
          </div>
        )}
      </div>
    </div>
  );

  // ── Modal filtro de temas do dashboard ───────────────────
  const [filtroTemp, setFiltroTemp] = useState(temasFiltro || temasIA);
  const abrirFiltro = () => { setFiltroTemp(temasFiltro || [...temasIA]); setModalFiltro(true); };
  const aplicarFiltro = () => { salvarFiltro(filtroTemp); setModalFiltro(false); };
  const toggleFiltroTema = t => setFiltroTemp(prev =>
    prev.includes(t) ? prev.filter(x=>x!==t) : [...prev, t]
  );

  // ── Render principal ──────────────────────────────────────
  return (
    <div className="fade-in">
      <PageHeader
        title="IA - Assistentes Jurídicos"
        subtitle={view === "dashboard"
          ? `${temasVisiveis.length} temas visíveis · ${agentes.length} agentes cadastrados`
          : "Todos os agentes"}
        action={
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {/* Breadcrumb / toggle de view */}
            {view !== "dashboard" && (
              <Btn variant="secondary" onClick={()=>{ setView("dashboard"); setTemaAtivo(""); setBusca(""); }}>
                ← Dashboard
              </Btn>
            )}
            {view === "dashboard" && (
              <Btn variant="secondary" onClick={abrirFiltro}>
                ⚙️ Personalizar painel
              </Btn>
            )}
            <Btn variant="secondary" onClick={()=>setView("assistente")}
              style={view==="assistente"?{background:theme.gray900,color:theme.gold}:{}}>
              🤖 Assistente IA
            </Btn>
            {isAdmin && (
              <>
                <Btn variant="secondary" onClick={()=>{ window.scrollTo({top:0,behavior:"smooth"}); setModalTemas(true); }}>🗂️ Temas</Btn>
                <Btn onClick={()=>{ window.scrollTo({top:0,behavior:"smooth"}); openNew(""); }}>+ Novo Agente</Btn>
              </>
            )}
          </div>
        }
      />

      {view === "assistente" ? (
        <div style={{maxWidth:720,margin:"0 auto"}}>
          <p style={{fontSize:13,color:theme.gray500,marginBottom:16}}>
            Pergunte sobre produtos, documentos, fluxos operacionais ou qualquer funcionalidade do sistema.
          </p>
          <AssistenteChat data={data} contextoExtra="Você está no módulo IA — Assistentes Jurídicos."/>
        </div>
      ) : view === "dashboard" ? (
      <div>
      {/* Barra de busca global */}
      <div style={{ marginBottom:16, position:"relative" }}>
        <input
          value={buscaDash}
          onChange={e => setBuscaDash(e.target.value)}
          placeholder="🔍  Buscar agente por nome, tema ou descrição..."
          style={{
            width:"100%", boxSizing:"border-box",
            padding:"10px 40px 10px 16px",
            border:`1px solid ${theme.gray200}`,
            borderRadius:9, fontSize:13, outline:"none",
            fontFamily:"Jost, sans-serif", background:theme.white,
            color:theme.gray900, boxShadow:"0 1px 4px rgba(0,0,0,.05)"
          }}
          onFocus={e=>e.target.style.borderColor=theme.gold}
          onBlur={e=>e.target.style.borderColor=theme.gray200}
        />
        {buscaDash && (
          <button onClick={()=>setBuscaDash("")} style={{
            position:"absolute", right:12, top:"50%", transform:"translateY(-50%)",
            border:"none", background:"transparent", cursor:"pointer",
            fontSize:13, color:theme.gray400, padding:4
          }}>✕</button>
        )}
        {/* Resultados inline */}
        {buscaDash.trim() && (
          <div style={{
            position:"absolute", top:"calc(100% + 4px)", left:0, right:0,
            background:theme.white, borderRadius:9, zIndex:200,
            border:`1px solid ${theme.gray200}`,
            boxShadow:"0 8px 24px rgba(0,0,0,.12)",
            maxHeight:340, overflowY:"auto"
          }}>
            {agsFiltradosDash.length === 0 ? (
              <div style={{ padding:"16px 20px", fontSize:13, color:theme.gray400, textAlign:"center" }}>
                Nenhum agente encontrado para "{buscaDash}"
              </div>
            ) : (
              agsFiltradosDash.map(ag => (
                <div key={ag.id} style={{
                  display:"flex", alignItems:"center", gap:12,
                  padding:"10px 16px", borderBottom:`1px solid ${theme.gray100}`,
                  cursor:"pointer", transition:"background .1s"
                }}
                  onMouseEnter={e=>e.currentTarget.style.background=theme.gray50}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                  onClick={()=>{ setTemaAtivo(ag.tema); setView("lista"); setBuscaDash(""); }}
                >
                  <div style={{
                    width:34, height:34, borderRadius:8, flexShrink:0,
                    background: ag.img ? `url(${ag.img}) center/cover` : gradTema(ag.tema||""),
                    display:"flex", alignItems:"center", justifyContent:"center", fontSize:16
                  }}>{!ag.img && (ag.emoji || emojiTema(ag.tema))}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ fontWeight:600, fontSize:13, color:theme.gray900, marginBottom:2 }}>{ag.nome}</p>
                    <p style={{ fontSize:11, color:theme.gray400 }}>{ag.tema}</p>
                  </div>
                  <div style={{ display:"flex", gap:4 }}>
                    {(ag.links||[]).filter(l=>l.url).slice(0,2).map((l,i) => (
                      <a key={i} href={l.url} target="_blank" rel="noreferrer"
                        onClick={e=>e.stopPropagation()}
                        style={{
                          padding:"3px 9px", borderRadius:5, fontSize:10,
                          fontWeight:600, color:"#fff", textDecoration:"none",
                          background: TIPOS_LINK[l.tipo]?.cor||"#64748b"
                        }}>{TIPOS_LINK[l.tipo]?.label||"🔗"}</a>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Grid de temas */}
      <div style={{
        display:"grid",
        gridTemplateColumns:"repeat(auto-fill, minmax(195px, 1fr))",
        gap:10
      }}>
        {temasVisiveis.map(tema => {
          const ags = agsPorTema(tema);
          return (
            <div key={tema}
              onClick={() => { setTemaAtivo(tema); setView("lista"); }}
              style={{
                background: gradTema(tema),
                borderRadius:10, padding:"11px 14px",
                cursor:"pointer", position:"relative",
                overflow:"hidden", minHeight:88,
                boxShadow:"0 1px 4px rgba(0,0,0,.10)",
                transition:"transform .15s, box-shadow .15s",
                opacity: 0.88,
              }}
              onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 5px 16px rgba(0,0,0,.18)";e.currentTarget.style.opacity="1";}}
              onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,.10)";e.currentTarget.style.opacity="0.88";}}
            >
              <div style={{
                position:"absolute", right:-8, top:-8,
                fontSize:44, opacity:.10, lineHeight:1, pointerEvents:"none"
              }}>{emojiTema(tema)}</div>

              <div style={{ position:"relative", display:"flex", alignItems:"flex-start", gap:8 }}>
                <span style={{ fontSize:20, flexShrink:0, marginTop:1 }}>{emojiTema(tema)}</span>
                <div>
                  <p style={{
                    color:"rgba(255,255,255,.95)", fontWeight:600, fontSize:11.5,
                    lineHeight:1.35, marginBottom:6,
                    textShadow:"0 1px 2px rgba(0,0,0,.35)"
                  }}>{tema}</p>
                  <span style={{
                    display:"inline-block",
                    background:"rgba(255,255,255,.15)", borderRadius:20,
                    padding:"2px 8px", color:"rgba(255,255,255,.85)",
                    fontSize:10.5, fontWeight:600
                  }}>
                    {ags.length} agente{ags.length!==1?"s":""}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {isAdmin && (
          <div onClick={()=>{ window.scrollTo({top:0,behavior:"smooth"}); setModalTemas(true); }} style={{
            borderRadius:10, padding:"11px 14px", cursor:"pointer",
            border:`2px dashed ${theme.gray200}`, background:theme.gray50,
            display:"flex", flexDirection:"column", alignItems:"center",
            justifyContent:"center", gap:6, minHeight:88,
            transition:"border-color .15s, background .15s",
          }}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=theme.gold;e.currentTarget.style.background="#fffbf0";}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=theme.gray200;e.currentTarget.style.background=theme.gray50;}}
          >
            <span style={{ fontSize:22, opacity:.35 }}>＋</span>
            <span style={{ fontSize:11, color:theme.gray400, fontWeight:500 }}>Gerenciar temas</span>
          </div>
        )}
      </div>

      {/* Rodapé resumo */}
      <div style={{
        marginTop:14, padding:"10px 18px",
        background:theme.white, borderRadius:9,
        border:`1px solid ${theme.gray100}`,
        display:"flex", gap:20, flexWrap:"wrap", alignItems:"center"
      }}>
        <span style={{ fontSize:12, color:theme.gray500 }}>
          🤖 <strong style={{ color:theme.gray800 }}>{agentes.length}</strong> agentes
        </span>
        <span style={{ fontSize:12, color:theme.gray500 }}>
          🗂️ <strong style={{ color:theme.gray800 }}>{temasIA.length}</strong> temas
        </span>
        <span style={{ fontSize:12, color:theme.gray500 }}>
          ✅ <strong style={{ color:theme.gray800 }}>{agentes.filter(a=>(a.links||[]).some(l=>l.url)).length}</strong> com links
        </span>
        <div style={{ marginLeft:"auto" }}>
          <Btn variant="secondary" onClick={()=>setView("lista")}>
            Ver todos os agentes →
          </Btn>
        </div>
      </div>
    </div>
      ) : (
      <div>
      {/* Barra de busca + pills */}
      <Card style={{ padding:"14px 20px", marginBottom:20 }}>
        <div style={{ display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
          <input
            value={busca} onChange={e=>setBusca(e.target.value)}
            placeholder="Buscar por nome, tema ou descrição..."
            style={{
              flex:1, minWidth:220, padding:"8px 12px",
              border:`1px solid ${theme.gray200}`, borderRadius:7,
              fontSize:13, outline:"none", fontFamily:"Jost, sans-serif"
            }}
            onFocus={e=>e.target.style.borderColor=theme.gold}
            onBlur={e=>e.target.style.borderColor=theme.gray200}
          />
          {busca && (
            <button onClick={()=>setBusca("")} style={{
              padding:"6px 10px", border:`1px solid ${theme.gray200}`,
              borderRadius:6, background:"transparent", cursor:"pointer",
              fontSize:12, color:theme.gray500
            }}>✕ Limpar</button>
          )}
        </div>
        {temasUsados.length > 0 && (
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:12 }}>
            <button onClick={()=>setTemaAtivo("")} style={{
              padding:"4px 12px", borderRadius:20, fontSize:12, cursor:"pointer",
              border:`1px solid ${temaAtivo==="" ? theme.gold : theme.gray200}`,
              background: temaAtivo==="" ? theme.gold : "transparent",
              color: temaAtivo==="" ? theme.gray900 : theme.gray600,
              fontWeight: temaAtivo==="" ? 700 : 400
            }}>🗂️ Todos ({agentes.length})</button>
            {temasUsados.map(t => (
              <button key={t} onClick={()=>setTemaAtivo(temaAtivo===t?"":t)} style={{
                padding:"4px 12px", borderRadius:20, fontSize:12, cursor:"pointer",
                border:`1px solid ${temaAtivo===t ? theme.gold : theme.gray200}`,
                background: temaAtivo===t ? theme.gold : "transparent",
                color: temaAtivo===t ? theme.gray900 : theme.gray600,
                fontWeight: temaAtivo===t ? 700 : 400
              }}>{emojiTema(t)} {t} ({(grupos[t]||agsFiltrados.filter(a=>a.tema===t)).length})</button>
            ))}
          </div>
        )}
      </Card>

      {agentes.length === 0 ? (
        <Card>
          <EmptyState icon="🤖" title="Nenhum agente cadastrado"
            subtitle={isAdmin ? "Clique em '+ Novo Agente' para começar." : "Aguarde o administrador cadastrar os agentes."} />
        </Card>
      ) : agsFiltrados.length === 0 ? (
        <Card>
          <EmptyState icon="🔍" title="Nenhum resultado" subtitle="Tente outros termos de busca." />
        </Card>
      ) : (
        temasComAgentes.map(tema => (
          <div key={tema} style={{ marginBottom:28, marginTop:8 }}>
            <div style={{
              background: gradTema(tema), borderRadius:"10px 10px 0 0",
              padding:"10px 20px", display:"flex", alignItems:"center", gap:10
            }}>
              <span style={{ fontSize:20 }}>{emojiTema(tema)}</span>
              <span style={{ color:"#fff", fontWeight:700, fontSize:14 }}>{tema}</span>
              <span style={{ color:"rgba(255,255,255,.6)", fontSize:12, marginLeft:"auto" }}>
                {grupos[tema].length} agente{grupos[tema].length!==1?"s":""}
              </span>
              {isAdmin && (
                <button onClick={()=>openNew(tema)} style={{
                  padding:"3px 10px", borderRadius:5,
                  border:"1px solid rgba(255,255,255,.4)",
                  background:"rgba(255,255,255,.15)", color:"#fff",
                  fontSize:11, cursor:"pointer", fontWeight:600
                }}>+ Agente</button>
              )}
            </div>
            <div style={{ height:10 }} />
            <div style={{
              display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))",
              gap:1, background:theme.gray100,
              border:`1px solid ${theme.gray100}`,
              borderRadius:10, overflow:"hidden"
            }}>
              {grupos[tema].sort((a,b)=>a.nome.localeCompare(b.nome,"pt-BR")).map(ag => (
                <CardAgente key={ag.id} ag={ag} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
      )}

      {/* ── Modal Personalizar Painel ── */}
      <Modal open={modalFiltro} onClose={()=>setModalFiltro(false)}
        title="Personalizar painel de temas" width={480}>
        <p style={{ fontSize:13, color:theme.gray500, marginBottom:16 }}>
          Selecione os temas que deseja exibir no painel principal.
        </p>
        <div style={{ display:"flex", gap:8, marginBottom:12 }}>
          <button onClick={()=>setFiltroTemp([...temasIA])} style={{
            padding:"5px 12px", borderRadius:6, border:`1px solid ${theme.gray200}`,
            background:"transparent", fontSize:12, cursor:"pointer", color:theme.gray600
          }}>✅ Todos</button>
          <button onClick={()=>setFiltroTemp([])} style={{
            padding:"5px 12px", borderRadius:6, border:`1px solid ${theme.gray200}`,
            background:"transparent", fontSize:12, cursor:"pointer", color:theme.gray600
          }}>☐ Nenhum</button>
        </div>
        <div style={{
          display:"grid", gridTemplateColumns:"1fr 1fr", gap:6,
          maxHeight:360, overflowY:"auto", paddingRight:4
        }}>
          {temasIA.map(t => {
            const sel = filtroTemp.includes(t);
            return (
              <div key={t} onClick={()=>toggleFiltroTema(t)} style={{
                display:"flex", alignItems:"center", gap:8, padding:"8px 10px",
                borderRadius:8, cursor:"pointer",
                border:`1px solid ${sel ? theme.gold : theme.gray200}`,
                background: sel ? "#fffbf0" : theme.white,
                transition:"all .12s"
              }}>
                <span style={{ fontSize:18, flexShrink:0 }}>{emojiTema(t)}</span>
                <span style={{ fontSize:12, color: sel ? theme.gray900 : theme.gray600,
                  fontWeight: sel ? 600 : 400, lineHeight:1.3 }}>{t}</span>
                <span style={{ marginLeft:"auto", fontSize:11, color:theme.gray400 }}>
                  {agsPorTema(t).length}
                </span>
              </div>
            );
          })}
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
          paddingTop:16, borderTop:`1px solid ${theme.gray100}`, marginTop:16 }}>
          <span style={{ fontSize:12, color:theme.gray400 }}>
            {filtroTemp.length} de {temasIA.length} selecionados
          </span>
          <div style={{ display:"flex", gap:8 }}>
            <Btn variant="secondary" onClick={()=>setModalFiltro(false)}>Cancelar</Btn>
            <Btn onClick={aplicarFiltro}>Aplicar</Btn>
          </div>
        </div>
      </Modal>

      {/* ── Modal Novo/Editar Agente ── */}
      <Modal open={modalOpen} onClose={()=>setModalOpen(false)}
        title={editItem?.id ? "Editar Agente" : "Novo Agente"} width={600}>
        {editItem && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 20px" }}>
              <div style={{ gridColumn:"1/-1" }}>
                <FormField label="Nome do agente" required>
                  <Input value={editItem.nome} onChange={v=>setEditItem({...editItem,nome:v})}
                    placeholder="Ex: Assistente Previdenciário" />
                </FormField>
              </div>
              <FormField label="Tema / Categoria" required>
                <Select value={editItem.tema} onChange={v=>setEditItem({...editItem,tema:v})}
                  options={temasIA} placeholder="Selecione o tema..." />
              </FormField>
              <FormField label="Emoji ícone">
                <Input value={editItem.emoji} onChange={v=>setEditItem({...editItem,emoji:v,img:null})}
                  placeholder="🤖" />
              </FormField>
              <div style={{ gridColumn:"1/-1" }}>
                <FormField label="Descrição">
                  <Textarea value={editItem.desc} onChange={v=>setEditItem({...editItem,desc:v})}
                    placeholder="Descreva brevemente o que este agente faz..." />
                </FormField>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <FormField label="Imagem do card (opcional)">
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    {editItem.img && (
                      <img src={editItem.img} alt="preview"
                        style={{ width:40, height:40, borderRadius:8, objectFit:"cover" }} />
                    )}
                    <label style={{
                      padding:"7px 14px", border:`1px solid ${theme.gray200}`,
                      borderRadius:7, cursor:"pointer", fontSize:12,
                      color:theme.gray600, background:theme.gray50
                    }}>
                      📷 Escolher imagem
                      <input type="file" accept="image/*" style={{display:"none"}} onChange={onImgUpload} />
                    </label>
                    {editItem.img && (
                      <button onClick={()=>setEditItem({...editItem,img:null})} style={{
                        padding:"6px 10px", borderRadius:6, border:`1px solid ${theme.red}33`,
                        background:"transparent", cursor:"pointer", fontSize:12, color:theme.red
                      }}>Remover</button>
                    )}
                  </div>
                </FormField>
              </div>
            </div>

            <div style={{ marginTop:4, paddingTop:16, borderTop:`1px solid ${theme.gray100}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <p style={{ fontSize:13, fontWeight:600, color:theme.gray700 }}>Links de Acesso</p>
                <Btn size="sm" variant="secondary" onClick={addLink}>+ Adicionar link</Btn>
              </div>
              {editItem.links.length===0 && (
                <p style={{ fontSize:12, color:theme.gray400, fontStyle:"italic", marginBottom:8 }}>
                  Nenhum link adicionado.
                </p>
              )}
              {editItem.links.map((l,i) => (
                <div key={i} style={{
                  display:"grid", gridTemplateColumns:"130px 1fr 1fr auto",
                  gap:8, marginBottom:8, alignItems:"center"
                }}>
                  <select value={l.tipo} onChange={e=>updateLink(i,"tipo",e.target.value)} style={{
                    padding:"7px 8px", border:`1px solid ${theme.gray200}`,
                    borderRadius:6, fontSize:12, outline:"none"
                  }}>
                    {Object.entries(TIPOS_LINK).map(([k,v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                  <input value={l.url} onChange={e=>updateLink(i,"url",e.target.value)}
                    placeholder="https://..." style={{
                      padding:"7px 10px", border:`1px solid ${theme.gray200}`,
                      borderRadius:6, fontSize:12, outline:"none", fontFamily:"Jost,sans-serif"
                    }} />
                  <input value={l.label} onChange={e=>updateLink(i,"label",e.target.value)}
                    placeholder="Rótulo (opcional)" style={{
                      padding:"7px 10px", border:`1px solid ${theme.gray200}`,
                      borderRadius:6, fontSize:12, outline:"none", fontFamily:"Jost,sans-serif"
                    }} />
                  <button onClick={()=>removeLink(i)} style={{
                    padding:"6px 10px", borderRadius:6, border:`1px solid ${theme.red}33`,
                    background:"transparent", color:theme.red, cursor:"pointer", fontSize:13
                  }}>✕</button>
                </div>
              ))}
            </div>

            <div style={{ display:"flex", justifyContent:"flex-end", gap:10,
              paddingTop:16, borderTop:`1px solid ${theme.gray100}`, marginTop:8 }}>
              <Btn variant="secondary" onClick={()=>setModalOpen(false)}>Cancelar</Btn>
              <Btn onClick={save}>Salvar Agente</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* ── Modal Gerenciar Temas IA ── */}
      <Modal open={modalTemas} onClose={()=>setModalTemas(false)}
        title="Gerenciar Temas de IA" width={480}>
        <div style={{ display:"flex", gap:8, marginBottom:20 }}>
          <input value={novoTemaIA} onChange={e=>setNovoTemaIA(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&addTemaIA()}
            placeholder="Novo tema..."
            style={{
              flex:1, padding:"9px 12px", border:`1px solid ${theme.gray200}`,
              borderRadius:7, fontSize:13, outline:"none", fontFamily:"Jost,sans-serif"
            }}
            onFocus={e=>e.target.style.borderColor=theme.gold}
            onBlur={e=>e.target.style.borderColor=theme.gray200}
          />
          <Btn onClick={addTemaIA}>+ Adicionar</Btn>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:5, maxHeight:380, overflowY:"auto" }}>
          {temasIA.map((t,i) => (
            <div key={i} style={{
              display:"flex", alignItems:"center", gap:8, padding:"8px 12px",
              borderRadius:7, border:`1px solid ${theme.gray100}`,
              background: i%2===0 ? theme.white : theme.gray50
            }}>
              <span style={{ fontSize:18, flexShrink:0 }}>{emojiTema(t)}</span>
              {editTemaIdx===i ? (
                <>
                  <input value={editTemaVal} onChange={e=>setEditTemaVal(e.target.value)}
                    onKeyDown={e=>{if(e.key==="Enter")saveEditTema(i);if(e.key==="Escape")setEditTemaIdx(null);}}
                    autoFocus style={{
                      flex:1, padding:"5px 8px", border:`1px solid ${theme.gold}`,
                      borderRadius:5, fontSize:13, outline:"none", fontFamily:"Jost,sans-serif"
                    }} />
                  <button onClick={()=>saveEditTema(i)} style={{
                    padding:"4px 10px", borderRadius:5, border:"none",
                    background:theme.gold, color:theme.gray900, fontSize:12,
                    cursor:"pointer", fontWeight:600
                  }}>✓</button>
                  <button onClick={()=>setEditTemaIdx(null)} style={{
                    padding:"4px 8px", borderRadius:5, border:`1px solid ${theme.gray200}`,
                    background:"transparent", fontSize:12, cursor:"pointer", color:theme.gray500
                  }}>✕</button>
                </>
              ) : (
                <>
                  <span style={{ flex:1, fontSize:13, color:theme.gray800 }}>{t}</span>
                  <span style={{ fontSize:11, color:theme.gray400 }}>
                    {agentes.filter(a=>a.tema===t).length} agente(s)
                  </span>
                  <button onClick={()=>{setEditTemaIdx(i);setEditTemaVal(t);}} style={{
                    padding:"4px 10px", borderRadius:5, border:`1px solid ${theme.gray200}`,
                    background:"transparent", fontSize:12, cursor:"pointer", color:theme.gray600
                  }}>✏️</button>
                  <button onClick={()=>removeTemaIA(i)} style={{
                    padding:"4px 10px", borderRadius:5, border:`1px solid ${theme.red}22`,
                    background:"transparent", fontSize:12, cursor:"pointer", color:theme.red
                  }}>✕</button>
                </>
              )}
            </div>
          ))}
        </div>
        <div style={{ display:"flex", justifyContent:"flex-end", paddingTop:16,
          borderTop:`1px solid ${theme.gray100}`, marginTop:16 }}>
          <Btn onClick={()=>setModalTemas(false)}>Fechar</Btn>
        </div>
      </Modal>
    </div>
  );
}



function CoworkFlutuante({ data, usuario, ativo, aberto, setAberto }) {
  const [historico, setHistorico] = React.useState([]);
  const [pergunta,  setPergunta]  = React.useState("");
  const [carregando,setCarregando]= React.useState(false);
  const [minimizado,setMinimizado]= React.useState(false);
  const fimRef  = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [historico, carregando]);

  React.useEffect(() => {
    if(aberto && !minimizado && historico.length === 0) {
      setHistorico([{ tipo:"bot", texto:`Ola! Sou o Assistente Cowork. Voce esta em ${MENU.find(m=>m.id===ativo)?.label || "tela inicial"}. Como posso ajudar?` }]);
    }
  }, [aberto]);

  const enviar = async () => {
    const q = pergunta.trim();
    if (!q || carregando) return;
    setPergunta("");
    setCarregando(true);
    const novoHist = [...historico, { tipo:"user", texto:q }];
    setHistorico(novoHist);
    try {
      const ctx = buildCoworkContext(data, ativo);
      const msgs = [
        { role:"user", content: ctx + "\n\nPergunta do usuario " + (usuario?.nome||"") + ": " + q }
      ];
      if(novoHist.length > 2) {
        msgs.push(...novoHist.slice(1,-1).map(m=>({ role:m.tipo==="user"?"user":"assistant", content:m.texto })));
        msgs.push({ role:"user", content: q });
      }
      const resp = await fetch(CLAUDE_URL, {
        method:"POST", headers:{"Content-Type":"application/json","X-Token":sessionStorage.getItem('ac_token')||''},
        body: JSON.stringify({ model:"claude-sonnet-4-20250514", max_tokens:600, messages:msgs.slice(-6) })
      });
      const json = await resp.json();
      const txt  = json?.content?.[0]?.text || "Erro na resposta.";
      setHistorico(prev => [...prev, { tipo:"bot", texto:txt }]);
    } catch(e) {
      setHistorico(prev => [...prev, { tipo:"bot", texto:"Erro de conexao." }]);
    }
    setCarregando(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  if (!aberto) return (
    <button
      onClick={() => setAberto(true)}
      title="Assistente Cowork"
      style={{
        position:"fixed", bottom:24, right:24, zIndex:900,
        width:52, height:52, borderRadius:"50%", border:"none",
        background:`linear-gradient(135deg, ${theme.gray900}, #1e3a5c)`,
        cursor:"pointer", fontSize:24, boxShadow:"0 4px 16px rgba(0,0,0,.25)",
        transition:"all .2s", display:"flex", alignItems:"center", justifyContent:"center"
      }}
      onMouseEnter={e=>e.currentTarget.style.transform="scale(1.1)"}
      onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}
    >\ud83e\udde0</button>
  );

  return (
    <div style={{
      position:"fixed", bottom:24, right:24, zIndex:900,
      width: minimizado ? 260 : 380,
      height: minimizado ? 52 : 500,
      borderRadius:16, overflow:"hidden",
      boxShadow:"0 8px 32px rgba(0,0,0,.2)",
      display:"flex", flexDirection:"column",
      background:theme.white,
      transition:"all .25s ease",
      border:`1px solid ${theme.gray200}`
    }}>
      {/* Header flutuante */}
      <div style={{
        background:`linear-gradient(135deg, ${theme.gray900}, #1e3a5c)`,
        padding:"12px 16px", display:"flex", alignItems:"center", gap:10, flexShrink:0
      }}>
        <span style={{fontSize:18}}>\ud83e\udde0</span>
        <div style={{flex:1}}>
          <p style={{color:"#fff", fontWeight:700, fontSize:13}}>Assistente Cowork</p>
          {!minimizado && <p style={{color:"rgba(255,255,255,.6)", fontSize:10, marginTop:1}}>
            {MENU.find(m=>m.id===ativo)?.label || "Sistema"}
          </p>}
        </div>
        <button onClick={()=>setMinimizado(!minimizado)} style={{
          background:"rgba(255,255,255,.15)", border:"none", color:"#fff",
          borderRadius:6, width:26, height:26, cursor:"pointer", fontSize:12
        }}>{minimizado?"\u25b2":"\u25bc"}</button>
        <button onClick={()=>{setAberto(false);setMinimizado(false);}} style={{
          background:"rgba(255,255,255,.15)", border:"none", color:"#fff",
          borderRadius:6, width:26, height:26, cursor:"pointer", fontSize:14
        }}>\u2715</button>
      </div>

      {!minimizado && (<>
        {/* Mensagens */}
        <div style={{flex:1, overflowY:"auto", padding:"14px 16px", display:"flex", flexDirection:"column", gap:10}}>
          {historico.map((msg, i) => (
            <div key={i} style={{display:"flex", gap:8, justifyContent:msg.tipo==="user"?"flex-end":"flex-start"}}>
              {msg.tipo==="bot" && <span style={{fontSize:16, flexShrink:0, marginTop:4}}>\ud83e\udde0</span>}
              <div style={{
                maxWidth:"80%", padding:"9px 13px", borderRadius:10, fontSize:12,
                background:msg.tipo==="user"?`linear-gradient(135deg,${theme.gray900},#1e3a5c)`:theme.gray50,
                color:msg.tipo==="user"?"#fff":theme.gray800,
                border:msg.tipo==="bot"?`1px solid ${theme.gray100}`:"none",
                lineHeight:1.55
              }}>{msg.texto}</div>
            </div>
          ))}
          {carregando && (
            <div style={{display:"flex", gap:8, alignItems:"center"}}>
              <span style={{fontSize:16}}>\ud83e\udde0</span>
              <div style={{padding:"8px 12px", borderRadius:10, background:theme.gray50, border:`1px solid ${theme.gray100}`}}>
                <div style={{display:"flex", gap:4}}>
                  {[0,1,2].map(i=><div key={i} style={{width:6,height:6,borderRadius:"50%",background:theme.gray400,animation:`pulse 1.2s ${i*.2}s infinite`}}/>)}
                </div>
              </div>
            </div>
          )}
          <div ref={fimRef}/>
        </div>

        {/* Input flutuante */}
        <div style={{padding:"10px 14px", borderTop:`1px solid ${theme.gray100}`, background:theme.gray50, flexShrink:0}}>
          <div style={{display:"flex", gap:8}}>
            <input
              ref={inputRef}
              value={pergunta}
              onChange={e=>setPergunta(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter")enviar();}}
              placeholder="Sua pergunta..."
              style={{
                flex:1, padding:"8px 12px", borderRadius:8, border:`1px solid ${theme.gray200}`,
                fontSize:12, fontFamily:"Jost,sans-serif", outline:"none"
              }}
              onFocus={e=>e.target.style.borderColor=theme.gold}
              onBlur={e=>e.target.style.borderColor=theme.gray200}
            />
            <button onClick={enviar} disabled={carregando||!pergunta.trim()} style={{
              width:36, height:36, borderRadius:8, border:"none",
              background:pergunta.trim()&&!carregando?theme.gold:theme.gray200,
              color:pergunta.trim()&&!carregando?"#fff":theme.gray400,
              cursor:"pointer", fontSize:14
            }}>\u27a4</button>
          </div>
        </div>
      </>)}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MÓDULO: SALA VIRTUAL DE TRABALHO
// Leitura pura dos dados existentes + presença via polling
// ═══════════════════════════════════════════════════════════════
function PainelProdutividade({ equipe, contratos, baseOp, lancamentos, clientes_sv, theme, Card, fmtR }) {
  const hoje3 = new Date().toISOString().slice(0,10);
  const [periodoIni3, setPeriodoIni3] = React.useState(new Date().toISOString().slice(0,7)+"-01");
  const [periodoFim3, setPeriodoFim3] = React.useState(hoje3);

  const fmtD3 = d => d?new Date(d+"T12:00:00").toLocaleDateString("pt-BR"):"—";

  const relMembros = equipe.filter(e=>e.ativo!==false).map(membro=>{
    const ctsResp = contratos.filter(ct=>ct.responsavel===membro.id);
    const ctsAtivos = ctsResp.filter(ct=>ct.status==="Em andamento"||ct.status==="Ativo");
    const ctsConcluidos = ctsResp.filter(ct=>
      (ct.dataConclusao||"")>=periodoIni3 && (ct.dataConclusao||"")<=periodoFim3
    );
    const ocsLancadas = baseOp.flatMap(r=>
      (r.ocorrencias||[]).filter(o=>
        o.responsavel===membro.id &&
        (o.data||o.criadoEm||"").slice(0,10)>=periodoIni3 &&
        (o.data||o.criadoEm||"").slice(0,10)<=periodoFim3
      )
    );
    const honorariosRecebidos = lancamentos.filter(l=>
      ctsResp.some(ct=>ct.id===l.contratoId) &&
      l.status==="Recebido" &&
      (l.dataRecebimento||"").slice(0,10)>=periodoIni3 &&
      (l.dataRecebimento||"").slice(0,10)<=periodoFim3
    ).reduce((s,l)=>s+(parseFloat(l.valor)||0),0);
    const tarefasConcluidas = baseOp.flatMap(r=>
      (r.tarefas||[]).filter(t=>
        t.responsavel===membro.id &&
        t.status==="Concluída" &&
        (t.dataConclusao||"").slice(0,10)>=periodoIni3 &&
        (t.dataConclusao||"").slice(0,10)<=periodoFim3
      )
    );
    return { membro, ctsAtivos:ctsAtivos.length, ctsConcluidos:ctsConcluidos.length,
      ocsLancadas:ocsLancadas.length, honorariosReceb:honorariosRecebidos,
      tarefasConcluidas:tarefasConcluidas.length,
      ocsLista:ocsLancadas.slice(0,5), ctsAtivosList:ctsAtivos.slice(0,3) };
  });

  const totalOcs    = relMembros.reduce((s,m)=>s+m.ocsLancadas,0);
  const totalTarefas= relMembros.reduce((s,m)=>s+m.tarefasConcluidas,0);
  const totalHon    = relMembros.reduce((s,m)=>s+m.honorariosReceb,0);
  const totalCts    = relMembros.reduce((s,m)=>s+m.ctsAtivos,0);

  const gerarPDF = () => {
    const rows = relMembros.map(({membro,ctsAtivos,ctsConcluidos,ocsLancadas,honorariosReceb,tarefasConcluidas})=>
      `<tr><td>${membro.nome}</td><td>${membro.funcao||"—"}</td><td>${ctsAtivos}</td><td>${ctsConcluidos}</td><td>${tarefasConcluidas}</td><td>${ocsLancadas}</td><td>${fmtR(honorariosReceb)}</td></tr>`
    ).join("");
    const html=`<html><head><title>Produtividade</title><style>
      body{font-family:Arial,sans-serif;padding:24px;color:#2A2420;font-size:12px}
      h1{font-size:14px;font-weight:bold;text-align:center;text-transform:uppercase;border-bottom:2px solid #C9A84C;padding-bottom:8px;margin-bottom:16px}
      table{width:100%;border-collapse:collapse}
      th{background:#1a1610;color:#c9a84c;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.5px}
      td{padding:7px 10px;border-bottom:1px solid #eee}
      tr:nth-child(even) td{background:#f9f7f2}
      .kpi{display:inline-block;padding:8px 16px;margin:0 8px 16px 0;border:1px solid #ddd;border-radius:6px;text-align:center}
      .rf{font-size:10px;color:#999;margin-top:24px;border-top:1px solid #eee;padding-top:8px;text-align:center}
    </style></head><body>
    <h1>Relatório de Produtividade — Alves & Correa Advogados</h1>
    <p style="text-align:center;color:#666;font-size:11px;margin-bottom:16px">Período: ${fmtD3(periodoIni3)} a ${fmtD3(periodoFim3)}</p>
    <div style="margin-bottom:16px">
      <span class="kpi"><b>${totalCts}</b><br><small>Contratos Ativos</small></span>
      <span class="kpi"><b>${totalTarefas}</b><br><small>Tarefas Concluídas</small></span>
      <span class="kpi"><b>${totalOcs}</b><br><small>Ocorrências Lançadas</small></span>
      <span class="kpi"><b>${fmtR(totalHon)}</b><br><small>Honorários Recebidos</small></span>
    </div>
    <table><thead><tr><th>Membro</th><th>Função</th><th>Contratos Ativos</th><th>Concluídos</th><th>Tarefas</th><th>Ocorrências</th><th>Honorários</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <div class="rf">Alves & Correa Advogados · Gerado em ${new Date().toLocaleString("pt-BR")} · Confidencial</div>
    </body></html>`;
    const w=window.open("","_blank"); w.document.write(html); w.document.close(); w.print();
  };

  return (
    <div>
      {/* Filtros */}
      <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"flex-end",flexWrap:"wrap"}}>
        <div>
          <p style={{fontSize:11,color:theme.gray500,marginBottom:4}}>Data início</p>
          <input type="date" value={periodoIni3} onChange={e=>setPeriodoIni3(e.target.value)}
            style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none"}}/>
        </div>
        <div>
          <p style={{fontSize:11,color:theme.gray500,marginBottom:4}}>Data fim</p>
          <input type="date" value={periodoFim3} onChange={e=>setPeriodoFim3(e.target.value)}
            style={{padding:"8px 12px",border:`1px solid ${theme.gray200}`,borderRadius:7,fontSize:12,outline:"none"}}/>
        </div>
        <button onClick={gerarPDF} style={{padding:"8px 16px",borderRadius:7,
          border:`1px solid ${theme.gold}`,background:`${theme.gold}18`,
          color:theme.goldDark||"#92400e",fontSize:12,fontWeight:600,cursor:"pointer",alignSelf:"flex-end"}}>
          🖨️ Exportar PDF
        </button>
      </div>
      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
        {[
          {l:"Contratos Ativos",     v:totalCts,        c:theme.blue},
          {l:"Tarefas Concluídas",   v:totalTarefas,    c:theme.green},
          {l:"Ocorrências Lançadas", v:totalOcs,        c:theme.amber},
          {l:"Honorários Recebidos", v:fmtR(totalHon),  c:theme.gold},
        ].map(k=>(
          <Card key={k.l} style={{padding:"12px 16px",textAlign:"center"}}>
            <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>{k.l}</p>
            <p style={{fontSize:typeof k.v==="string"?15:24,fontWeight:700,color:k.c}}>{k.v}</p>
          </Card>
        ))}
      </div>
      {/* Tabela */}
      <Card style={{marginBottom:14}}>
        <p style={{fontSize:11,fontWeight:700,color:theme.gray700,textTransform:"uppercase",letterSpacing:.8,marginBottom:12}}>
          📈 Produtividade por Membro
        </p>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:theme.gray50}}>
              {["Membro","Função","Contratos Ativos","Concluídos","Tarefas","Ocorrências","Honorários Receb."].map(h=>(
                <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:10,color:theme.gray400,
                  letterSpacing:.8,textTransform:"uppercase",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {relMembros.map(({membro,ctsAtivos,ctsConcluidos,ocsLancadas,honorariosReceb,tarefasConcluidas,ctsAtivosList},i)=>(
                <tr key={membro.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?"#fff":theme.gray50}}>
                  <td style={{padding:"10px 12px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:30,height:30,borderRadius:"50%",background:theme.gold+"22",
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontSize:13,fontWeight:700,color:theme.goldDark||"#92400e"}}>
                        {membro.nome?.charAt(0)||"?"}
                      </div>
                      <div>
                        <p style={{fontSize:12,fontWeight:700,color:theme.gray900,margin:0}}>{membro.nome}</p>
                        {ctsAtivosList.length>0&&(
                          <p style={{fontSize:10,color:theme.gray400,margin:0}}>
                            {ctsAtivosList.map(ct=>{
                              const cli=clientes_sv.find(cl=>cl.id===ct.clienteId);
                              return ct.numContrato+(cli?" · "+cli.nome:"");
                            }).join(" | ")}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{padding:"10px 12px",fontSize:11,color:theme.gray600}}>{membro.funcao||"—"}</td>
                  <td style={{padding:"10px 12px",textAlign:"center"}}>
                    <span style={{fontSize:16,fontWeight:700,color:ctsAtivos>0?theme.blue:theme.gray300}}>{ctsAtivos}</span>
                  </td>
                  <td style={{padding:"10px 12px",textAlign:"center"}}>
                    <span style={{fontSize:16,fontWeight:700,color:ctsConcluidos>0?theme.green:theme.gray300}}>{ctsConcluidos}</span>
                  </td>
                  <td style={{padding:"10px 12px",textAlign:"center"}}>
                    <span style={{fontSize:16,fontWeight:700,color:tarefasConcluidas>0?theme.green:theme.gray300}}>{tarefasConcluidas}</span>
                  </td>
                  <td style={{padding:"10px 12px",textAlign:"center"}}>
                    <span style={{fontSize:16,fontWeight:700,color:ocsLancadas>0?theme.amber:theme.gray300}}>{ocsLancadas}</span>
                  </td>
                  <td style={{padding:"10px 12px",fontWeight:700,
                    color:honorariosReceb>0?theme.green:theme.gray300,whiteSpace:"nowrap"}}>
                    {fmtR(honorariosReceb)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Detalhamento de ocorrências por membro */}
      {relMembros.filter(m=>m.ocsLancadas>0).map(({membro,ocsLista})=>(
        <Card key={membro.id} style={{marginBottom:10}}>
          <p style={{fontSize:12,fontWeight:700,color:theme.gray900,marginBottom:8}}>
            📋 {membro.nome} — Últimas Ocorrências Lançadas
          </p>
          {ocsLista.map((oc,i)=>(
            <div key={oc.id||i} style={{display:"flex",gap:10,alignItems:"flex-start",
              padding:"6px 0",borderBottom:i<ocsLista.length-1?`1px solid ${theme.gray100}`:"none"}}>
              <span style={{fontSize:10,color:theme.gray400,whiteSpace:"nowrap",minWidth:70}}>
                {fmtD3(oc.data||oc.criadoEm?.slice(0,10))}
              </span>
              {(oc.tipoNome||oc.tipo)&&(
                <span style={{fontSize:10,padding:"1px 6px",borderRadius:6,
                  background:"#dbeafe",color:theme.blue,fontWeight:600,whiteSpace:"nowrap"}}>
                  {oc.tipoNome||oc.tipo}
                </span>
              )}
              <span style={{fontSize:11,color:theme.gray700}}>{oc.obs||oc.descricao||"—"}</span>
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
}


function ModSalaVirtual({ data, setData, usuario, navegarPara, salaAtiva, setSalaAtiva }) {
  const sv        = data.salaVirtual || { salas:[], presencas:[], mensagens:[], configuracoes:{} };
  const todasSalas = sv.salas || [];
  // Admin vê todas; usuários com exigeSala veem apenas as permitidas
  // Mas TODOS podem ver as salas para saber quem está lá
  // Apenas a ENTRADA fica restrita
  const salas = todasSalas; // todos veem todas as salas
  const salasEntrada = usuario?.perfil==="Administrador"
    ? todasSalas.map(s=>s.id)
    : (usuario?.exigeSala
        ? (usuario?.salasPermitidas||[])
        : todasSalas.map(s=>s.id)
      );
  const presencas = sv.presencas || [];
  const mensagens = sv.mensagens || [];
  const equipe    = data.equipe  || [];
  const contratos = data.contratos || [];
  const processos = data.processos || [];
  const baseOp       = data.baseOperacional || [];
  const lancamentos  = data.lancamentos    || [];
  const clientes_sv  = data.clientes       || [];

  const [abaMonitor, setAbaMonitor] = React.useState("sala");
  const [salaSel,    setSalaSel]    = React.useState(
    // Iniciar na primeira sala permitida
    usuario?.perfil==="Administrador"
      ? (salas[0]?.id || "sala_operacional")
      : (usuario?.exigeSala
          ? ((usuario?.salasPermitidas||[])[0] || salas[0]?.id || "sala_operacional")
          : (salas[0]?.id || "sala_operacional")
        )
  );
  const [modalChat,  setModalChat]  = React.useState(null); // usuarioId
  const [modalCfg,   setModalCfg]   = React.useState(false);
  const [msgTexto,   setMsgTexto]   = React.useState("");
  const [statusEdit, setStatusEdit] = React.useState(false);
  const [ticker,     setTicker]     = React.useState(0);

  const hoje = new Date().toISOString().slice(0,10);
  const agora = () => new Date().toISOString();
  const fmtHora = iso => iso ? new Date(iso).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"}) : "—";
  const fmtDuracao = iso => {
    if(!iso) return "—";
    const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if(min < 1) return "agora";
    if(min < 60) return min+"min";
    return Math.floor(min/60)+"h"+(min%60>0?String(min%60).padStart(2,"0")+"min":"");
  };

  // ── Entrar na sala ao abrir o módulo — resetar entradaEm ──
  React.useEffect(() => {
    if(setSalaAtiva) setSalaAtiva(true);
    // Resetar entradaEm para agora (nova sessão de trabalho)
    const agora_entrada = new Date().toISOString();
    setData(prev => {
      const svP = prev.salaVirtual || {};
      const pres = svP.presencas || [];
      const minha = pres.find(p => p.usuarioId === usuario.id);
      if(!minha) return prev; // será criado no primeiro polling
      return {...prev, salaVirtual: {...svP,
        presencas: pres.map(p =>
          p.usuarioId === usuario.id
            ? {...p, entradaEm: agora_entrada, status: "Online", ultimaAtiv: agora_entrada}
            : p
        )
      }};
    });
    return () => {};
  }, []);

  // ── Timeout de inatividade: 30min sem polling = sair da sala ──
  const inativTimer = React.useRef(null);
  React.useEffect(() => {
    const resetTimer = () => {
      if(inativTimer.current) clearTimeout(inativTimer.current);
      inativTimer.current = setTimeout(() => {
        if(setSalaAtiva) setSalaAtiva(false);
        // Atualizar status para Ausente
        setData(prev => {
          const svP = prev.salaVirtual || {};
          return {...prev, salaVirtual: {...svP,
            presencas: (svP.presencas||[]).map(p =>
              p.usuarioId === usuario.id ? {...p, status:"Ausente"} : p
            )
          }};
        });
      }, 30 * 60 * 1000); // 30 minutos
    };
    resetTimer();
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('click', resetTimer);
    return () => {
      if(inativTimer.current) clearTimeout(inativTimer.current);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('click', resetTimer);
    };
  }, []);

  // ── Polling: registrar presença a cada 30s ──────────────────
  React.useEffect(() => {
    const registrar = () => {
      const agr = agora();
      // Tarefa ativa: último contrato acessado via _navBase ou inferir por usuário
      const membroEquipe = equipe.find(e => e.id === (usuario.equipeId || usuario.id));
      const contratosResp = contratos.filter(ct =>
        ct.responsavel === (usuario.equipeId || usuario.id) &&
        (ct.status === "Em andamento" || ct.status === "Ativo")
      );
      const tarefaAtiva = contratosResp.length > 0
        ? contratosResp[0].numContrato + " · " + (data.clientes||[]).find(cl=>cl.id===contratosResp[0].clienteId)?.nome
        : "";

      setData(prev => {
        const svPrev = prev.salaVirtual || { salas:[], presencas:[], mensagens:[], configuracoes:{} };
        const presAntes = svPrev.presencas || [];
        // Atualizar ou inserir presença do usuário atual
        const minha = presAntes.find(p => p.usuarioId === usuario.id);
        const novaP = {
          usuarioId:    usuario.id,
          nome:         usuario.nome,
          perfil:       usuario.perfil || "",
          salaId:       salaSel,
          status:       minha?.status || "Online",
          tarefaAtiva:  tarefaAtiva,
          ultimaAtiv:   agr,
          // Resetar entradaEm se estava ausente há mais de 2h (nova sessão)
          entradaEm: (()=>{
            if(!minha?.entradaEm) return agr;
            const diffMin = (Date.now() - new Date(minha.entradaEm).getTime()) / 60000;
            // Se entrada foi há mais de 2h E estava Ausente = nova sessão
            if(diffMin > 120 && minha?.status === "Ausente") return agr;
            // Se entrada foi há mais de 8h = certamente nova sessão
            if(diffMin > 480) return agr;
            return minha.entradaEm;
          })(),
        };
        const presNovas = minha
          ? presAntes.map(p => p.usuarioId === usuario.id ? novaP : p)
          : [...presAntes, novaP];

        // Marcar ausentes (sem atividade há >10min)
        const LIMITE = 10 * 60 * 1000;
        const presAjust = presNovas.map(p => {
          if(p.usuarioId === usuario.id) return p;
          const diff = Date.now() - new Date(p.ultimaAtiv||0).getTime();
          if(diff > LIMITE && p.status === "Online") return {...p, status:"Ausente"};
          return p;
        });

        return {...prev, salaVirtual: {...svPrev, presencas: presAjust}};
      });
      setTicker(t => t+1);
    };

    registrar();
    const interval = setInterval(registrar, 30000);
    return () => clearInterval(interval);
  }, [salaSel, usuario.id]);

  // ── Presença do usuário atual ───────────────────────────────
  const minhaPresenca = presencas.find(p => p.usuarioId === usuario.id);

  const setMeuStatus = (novoStatus) => {
    setData(prev => {
      const svP = prev.salaVirtual || {};
      const pres = svP.presencas || [];
      return {...prev, salaVirtual: {...svP,
        presencas: pres.map(p => p.usuarioId === usuario.id ? {...p, status: novoStatus} : p)
      }};
    });
    setStatusEdit(false);
  };

  // ── Membros na sala selecionada ─────────────────────────────
  const membrosNaSala = presencas.filter(p => p.salaId === salaSel);
  const outros = membrosNaSala.filter(p => p.usuarioId !== usuario.id);

  // ── Mensagens do chat da sala ───────────────────────────────
  const msgsSala = mensagens
    .filter(m => m.salaId === salaSel || (modalChat && (
      (m.de === usuario.id && m.para === modalChat) ||
      (m.de === modalChat  && m.para === usuario.id)
    )))
    .sort((a,b) => (a.criadoEm||"") > (b.criadoEm||"") ? 1 : -1);

  const msgsDiretas = mensagens
    .filter(m =>
      (m.de === usuario.id && m.para === modalChat) ||
      (m.de === modalChat  && m.para === usuario.id)
    )
    .sort((a,b) => (a.criadoEm||"") > (b.criadoEm||"") ? 1 : -1);

  const naoLidas = (uid) => mensagens.filter(m =>
    m.de === uid && m.para === usuario.id && !m.lida
  ).length;

  const enviarMsg = (paraId) => {
    if(!(msgTexto||"").trim()) return;
    const novaMsg = {
      id:       "MSG"+Date.now(),
      de:       usuario.id,
      deNome:   usuario.nome,
      para:     paraId || null,
      salaId:   paraId ? null : salaSel,
      texto:    msgTexto.trim(),
      criadoEm: agora(),
      lida:     false,
    };
    setData(prev => {
      const svP = prev.salaVirtual || {};
      return {...prev, salaVirtual: {...svP, mensagens: [...(svP.mensagens||[]), novaMsg]}};
    });
    setMsgTexto("");
  };

  const marcarLidas = (uid) => {
    setData(prev => {
      const svP = prev.salaVirtual || {};
      return {...prev, salaVirtual: {...svP,
        mensagens: (svP.mensagens||[]).map(m =>
          m.de === uid && m.para === usuario.id ? {...m, lida:true} : m
        )
      }};
    });
  };

  const enviarKnock = (uid, nome) => {
    const knock = {
      id: "KNK"+Date.now(), de: usuario.id, deNome: usuario.nome,
      para: uid, salaId: null,
      texto: "🔔 " + usuario.nome + " está te chamando!",
      criadoEm: agora(), lida: false, tipo: "knock"
    };
    setData(prev => {
      const svP = prev.salaVirtual || {};
      return {...prev, salaVirtual: {...svP, mensagens: [...(svP.mensagens||[]), knock]}};
    });
    alert("Notificação enviada para " + nome + "!");
  };

  // ── Notificações recebidas ──────────────────────────────────
  const knocksRecebidos = mensagens.filter(m =>
    m.para === usuario.id && m.tipo === "knock" && !m.lida
  );

  const totalNaoLidas = mensagens.filter(m =>
    m.para === usuario.id && !m.lida && m.tipo !== "knock"
  ).length;

  // ── Cores de status ─────────────────────────────────────────
  const COR_ST = {
    "Online":     "#22c55e",
    "Ocupado":    "#f59e0b",
    "Em reunião": "#ef4444",
    "Ausente":    "#9ca3af",
  };
  const STATUS_OPTS = ["Online","Ocupado","Em reunião","Ausente"];

  const salaCor = salas.find(s=>s.id===salaSel)?.cor || theme.blue;

  return (
    <div className="fade-in">
      <PageHeader
        title="🏢 Sala Virtual de Trabalho"
        subtitle={"Alves & Correa Advogados · " + new Date().toLocaleDateString("pt-BR",{weekday:"long",day:"2-digit",month:"long",year:"numeric"})}
        action={
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {/* Botão sair da sala */}
            {setSalaAtiva&&(usuario?.exigeSala)&&(
              <button onClick={()=>{
                if(confirm("Sair da Sala Virtual? Os módulos ficarão bloqueados até você retornar.")){
                  setSalaAtiva(false);
                  setData(prev=>{
                    const svP=prev.salaVirtual||{};
                    return {...prev,salaVirtual:{...svP,
                      presencas:(svP.presencas||[]).map(p=>
                        p.usuarioId===usuario.id?{...p,status:"Ausente"}:p
                      )
                    }};
                  });
                }
              }} style={{padding:"7px 14px",borderRadius:8,border:"1px solid #fecaca",
                background:"#fff5f5",color:"#dc2626",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                🚪 Sair da Sala
              </button>
            )}
            {/* Meu status */}
            <div style={{position:"relative"}}>
              <button onClick={()=>setStatusEdit(v=>!v)} style={{
                display:"flex",alignItems:"center",gap:6,padding:"7px 14px",
                borderRadius:20,border:`1px solid ${theme.gray200}`,background:theme.white,
                cursor:"pointer",fontSize:12,fontWeight:600
              }}>
                <span style={{width:9,height:9,borderRadius:"50%",background:COR_ST[minhaPresenca?.status||"Online"],display:"inline-block"}}/>
                {minhaPresenca?.status||"Online"}
                <span style={{fontSize:10,color:theme.gray400}}>▼</span>
              </button>
              {statusEdit&&(
                <div style={{position:"absolute",top:"110%",right:0,background:"#fff",
                  border:`1px solid ${theme.gray200}`,borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,.1)",
                  zIndex:100,minWidth:140,overflow:"hidden"}}>
                  {STATUS_OPTS.map(s=>(
                    <button key={s} onClick={()=>setMeuStatus(s)} style={{
                      display:"flex",alignItems:"center",gap:8,width:"100%",padding:"9px 14px",
                      border:"none",background:"none",cursor:"pointer",fontSize:12,
                      fontWeight:minhaPresenca?.status===s?700:400,
                      color:minhaPresenca?.status===s?COR_ST[s]:theme.gray700,
                      borderBottom:`1px solid ${theme.gray100}`
                    }}>
                      <span style={{width:9,height:9,borderRadius:"50%",background:COR_ST[s],display:"inline-block"}}/>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Badge notificações */}
            {totalNaoLidas>0&&(
              <span style={{background:theme.red,color:"#fff",borderRadius:10,
                padding:"2px 8px",fontSize:11,fontWeight:700}}>
                {totalNaoLidas} mensagem(s)
              </span>
            )}
          </div>
        }
      />

      {/* Knocks recebidos */}
      {knocksRecebidos.length>0&&(
        <div style={{background:"#fef3c7",border:"1px solid #fde68a",borderRadius:8,
          padding:"10px 16px",marginBottom:14,display:"flex",alignItems:"center",
          justifyContent:"space-between"}}>
          <span style={{fontSize:13,fontWeight:600,color:"#92400e"}}>
            🔔 {knocksRecebidos.map(k=>k.deNome).join(", ")} está te chamando!
          </span>
          <button onClick={()=>setData(prev=>{
            const svP=prev.salaVirtual||{};
            return {...prev,salaVirtual:{...svP,
              mensagens:(svP.mensagens||[]).map(m=>
                m.tipo==="knock"&&m.para===usuario.id?{...m,lida:true}:m
              )
            }};
          })} style={{background:"none",border:"none",cursor:"pointer",
            fontSize:12,color:"#92400e",fontWeight:600}}>✓ Dispensar</button>
        </div>
      )}

      {/* ── Abas: Sala / Monitoramento (só admin) ── */}
      {usuario?.perfil==="Administrador"&&(
        <div style={{display:"flex",gap:6,marginBottom:14}}>
          {[["sala","🏢 Sala"],["monitor","📊 Monitoramento"],["produtividade","📈 Produtividade"]].map(([id,label])=>(
            <button key={id} onClick={()=>setAbaMonitor(id)} style={{
              padding:"8px 18px",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:600,
              border:`1px solid ${abaMonitor===id?theme.gold:theme.gray200}`,
              background:abaMonitor===id?`${theme.gold}18`:theme.white,
              color:abaMonitor===id?theme.goldDark||"#92400e":theme.gray600,
            }}>{label}</button>
          ))}
        </div>
      )}

      {/* ── ABA SALA ── */}
      {(abaMonitor==="sala"||usuario?.perfil!=="Administrador")&&(
      <div style={{display:"grid",gridTemplateColumns:"220px 1fr",gap:16}}>

        {/* ── Sidebar de salas ── */}
        <div>
          <div style={{background:theme.white,borderRadius:10,border:`1px solid ${theme.gray100}`,
            overflow:"hidden",marginBottom:12}}>
            <div style={{padding:"10px 14px",background:theme.gray900,
              fontSize:11,fontWeight:700,color:theme.gold,letterSpacing:.8,
              textTransform:"uppercase"}}>Salas</div>
            {salas.map(sala=>{
              const count = presencas.filter(p=>p.salaId===sala.id).length;
              const ativa = salaSel===sala.id;
              return (
                <button key={sala.id} onClick={()=>setSalaSel(sala.id)} style={{
                  display:"flex",alignItems:"center",justifyContent:"space-between",
                  width:"100%",padding:"11px 14px",border:"none",
                  borderBottom:`1px solid ${theme.gray100}`,cursor:"pointer",
                  background:ativa?sala.cor+"18":theme.white,
                  borderLeft:ativa?`3px solid ${sala.cor}`:"3px solid transparent",
                }}>
                  <span style={{fontSize:13,fontWeight:ativa?700:400,
                    color:ativa?sala.cor:theme.gray700}}>
                    {sala.icon} {sala.nome}
                  </span>
                  <span style={{fontSize:10,padding:"2px 7px",borderRadius:10,
                    background:count>0?sala.cor+"22":theme.gray100,
                    color:count>0?sala.cor:theme.gray400,fontWeight:600}}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Minha presença */}
          {minhaPresenca&&(
            <div style={{background:theme.white,borderRadius:10,border:`1px solid ${theme.gray100}`,
              padding:"12px 14px"}}>
              <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",
                letterSpacing:.8,marginBottom:8,fontWeight:700}}>Você</p>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:36,height:36,borderRadius:"50%",
                  background:salaCor+"22",display:"flex",alignItems:"center",
                  justifyContent:"center",fontSize:16,fontWeight:700,color:salaCor}}>
                  {usuario.nome?.charAt(0)||"?"}
                </div>
                <div>
                  <p style={{fontSize:12,fontWeight:700,color:theme.gray900}}>
                    {usuario.nome}
                  </p>
                  <p style={{fontSize:10,color:theme.gray400}}>{usuario.perfil}</p>
                </div>
              </div>
              {minhaPresenca.tarefaAtiva&&(
                <div style={{marginTop:8,padding:"6px 8px",background:theme.gray50,
                  borderRadius:6,borderLeft:`2px solid ${salaCor}`}}>
                  <p style={{fontSize:10,color:theme.gray500,marginBottom:1}}>Trabalhando em:</p>
                  <p style={{fontSize:11,color:theme.gray800,fontWeight:600}}>
                    📋 {minhaPresenca.tarefaAtiva}
                  </p>
                </div>
              )}
              <p style={{fontSize:10,color:theme.gray400,marginTop:6}}>
                ⏱ Online há {fmtDuracao(minhaPresenca.entradaEm)}
              </p>
            </div>
          )}
        </div>

        {/* ── Área principal ── */}
        <div style={{display:"flex",flexDirection:"column",gap:14}}>

          {/* Header da sala */}
          <div style={{background:salaCor,borderRadius:10,padding:"14px 20px",
            display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <p style={{fontSize:16,fontWeight:700,color:"#fff",margin:0}}>
                {salas.find(s=>s.id===salaSel)?.icon} {salas.find(s=>s.id===salaSel)?.nome}
              </p>
              <p style={{fontSize:12,color:"rgba(255,255,255,.8)",margin:"2px 0 0"}}>
                {membrosNaSala.length} membro(s) na sala
              </p>
            </div>
            <div style={{fontSize:12,color:"rgba(255,255,255,.8)"}}>
              Atualizado às {fmtHora(agora())}
            </div>
          </div>

          {/* Membros */}
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {membrosNaSala.length===0
              ? <Card><p style={{textAlign:"center",padding:"24px 0",color:theme.gray400,
                  fontSize:13}}>Nenhum membro nesta sala no momento.</p></Card>
              : membrosNaSala.map(p=>{
                  const eh_eu = p.usuarioId === usuario.id;
                  const nl = naoLidas(p.usuarioId);
                  // Buscar tarefas/contratos do membro
                  const memEquipe = equipe.find(e=>e.id===p.usuarioId||e.nome===p.nome);
                  const ctsMembro = contratos.filter(ct=>
                    ct.responsavel===p.usuarioId &&
                    (ct.status==="Em andamento"||ct.status==="Ativo")
                  ).slice(0,2);
                  return (
                    <Card key={p.usuarioId} style={{
                      border:`1px solid ${p.status==="Online"?"#22c55e22":theme.gray100}`,
                      borderLeft:`4px solid ${COR_ST[p.status]||theme.gray200}`
                    }}>
                      <div style={{display:"flex",alignItems:"center",
                        justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
                        {/* Avatar + info */}
                        <div style={{display:"flex",alignItems:"center",gap:12}}>
                          <div style={{position:"relative"}}>
                            <div style={{width:44,height:44,borderRadius:"50%",
                              background:COR_ST[p.status]+"22",
                              display:"flex",alignItems:"center",justifyContent:"center",
                              fontSize:18,fontWeight:700,color:COR_ST[p.status]}}>
                              {p.nome?.charAt(0)||"?"}
                            </div>
                            <span style={{position:"absolute",bottom:1,right:1,
                              width:11,height:11,borderRadius:"50%",
                              background:COR_ST[p.status]||"#9ca3af",
                              border:"2px solid #fff",display:"block"}}/>
                          </div>
                          <div>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <p style={{fontSize:14,fontWeight:700,color:theme.gray900,margin:0}}>
                                {p.nome} {eh_eu&&<span style={{fontSize:10,color:theme.gray400}}>(você)</span>}
                              </p>
                              <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,
                                fontWeight:600,background:COR_ST[p.status]+"18",
                                color:COR_ST[p.status]}}>
                                {p.status}
                              </span>
                            </div>
                            <p style={{fontSize:11,color:theme.gray400,margin:"2px 0 0"}}>
                              {p.perfil} · ⏱ {fmtDuracao(p.entradaEm)}
                            </p>
                            {p.tarefaAtiva&&(
                              <div style={{marginTop:4,display:"flex",alignItems:"center",gap:5}}>
                                <span style={{fontSize:10,padding:"1px 6px",borderRadius:6,
                                  background:"#dbeafe",color:theme.blue,fontWeight:600}}>
                                  📋 {p.tarefaAtiva}
                                </span>
                                <button onClick={()=>navegarPara&&navegarPara("base")}
                                  style={{fontSize:10,color:theme.blue,background:"none",
                                    border:"none",cursor:"pointer",padding:0,
                                    textDecoration:"underline"}}>
                                  → Ver na Base
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Ações */}
                        {!eh_eu&&(
                          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                            <button onClick={()=>{setModalChat(p.usuarioId);marcarLidas(p.usuarioId);}}
                              style={{display:"flex",alignItems:"center",gap:5,
                                padding:"7px 12px",borderRadius:8,cursor:"pointer",
                                border:`1px solid ${theme.blue}44`,
                                background:"#eff6ff",color:theme.blue,fontSize:12,fontWeight:600}}>
                              💬 Mensagem {nl>0&&<span style={{background:theme.red,color:"#fff",
                                borderRadius:8,padding:"1px 5px",fontSize:10}}>{nl}</span>}
                            </button>
                            <button onClick={()=>enviarKnock(p.usuarioId,p.nome)}
                              style={{display:"flex",alignItems:"center",gap:5,
                                padding:"7px 12px",borderRadius:8,cursor:"pointer",
                                border:"1px solid #fde68a",background:"#fffbeb",
                                color:"#92400e",fontSize:12,fontWeight:600}}>
                              🔔 Chamar
                            </button>
                          </div>
                        )}
                      </div>
                      {/* Contratos em andamento do membro (apenas admin vê todos) */}
                      {!eh_eu && ctsMembro.length>0 && (usuario.perfil==="Administrador")&&(
                        <div style={{marginTop:10,paddingTop:8,
                          borderTop:`1px solid ${theme.gray100}`}}>
                          <p style={{fontSize:10,color:theme.gray400,
                            textTransform:"uppercase",letterSpacing:.7,marginBottom:4}}>
                            Contratos em andamento
                          </p>
                          {ctsMembro.map(ct=>{
                            const cli=(data.clientes||[]).find(cl=>cl.id===ct.clienteId);
                            return (
                              <div key={ct.id} style={{fontSize:11,color:theme.gray700,
                                padding:"3px 0",display:"flex",alignItems:"center",gap:5}}>
                                <span style={{color:theme.gold}}>•</span>
                                {ct.numContrato} · {cli?.nome||"—"}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Card>
                  );
                })
            }
          </div>

          {/* Chat da sala */}
          <Card>
            <p style={{fontSize:11,fontWeight:700,color:theme.gray700,
              textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>
              💬 Chat da Sala — {salas.find(s=>s.id===salaSel)?.nome}
            </p>
            <div style={{maxHeight:200,overflowY:"auto",display:"flex",
              flexDirection:"column",gap:6,marginBottom:10,
              padding:"8px",background:theme.gray50,borderRadius:7}}>
              {msgsSala.filter(m=>!m.para&&m.salaId===salaSel).length===0
                ? <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"8px 0"}}>
                    Nenhuma mensagem nesta sala ainda.
                  </p>
                : msgsSala.filter(m=>!m.para&&m.salaId===salaSel).map(m=>{
                    const eh_eu = m.de === usuario.id;
                    return (
                      <div key={m.id} style={{display:"flex",
                        justifyContent:eh_eu?"flex-end":"flex-start"}}>
                        <div style={{maxWidth:"75%",padding:"7px 11px",borderRadius:10,
                          background:eh_eu?salaCor:theme.white,
                          color:eh_eu?"#fff":theme.gray800,
                          border:eh_eu?"none":`1px solid ${theme.gray200}`,
                          fontSize:12}}>
                          {!eh_eu&&<p style={{fontSize:10,fontWeight:700,
                            marginBottom:2,color:salaCor}}>{m.deNome}</p>}
                          {m.texto}
                          <p style={{fontSize:9,opacity:.6,marginTop:2,textAlign:"right"}}>
                            {fmtHora(m.criadoEm)}
                          </p>
                        </div>
                      </div>
                    );
                  })
              }
            </div>
            <div style={{display:"flex",gap:8}}>
              <input value={msgTexto} onChange={e=>setMsgTexto(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&(enviarMsg(null),e.preventDefault())}
                placeholder="Mensagem para a sala... (Enter para enviar)"
                style={{flex:1,padding:"9px 12px",border:`1px solid ${theme.gray200}`,
                  borderRadius:7,fontSize:13,outline:"none"}}/>
              <button onClick={()=>enviarMsg(null)} style={{
                padding:"9px 16px",borderRadius:7,background:salaCor,
                color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:600}}>
                ➤
              </button>
            </div>
          </Card>
        </div>
      </div>
      )} {/* fim aba sala */}

      {/* ── ABA MONITORAMENTO ── */}
      {abaMonitor==="monitor"&&usuario?.perfil==="Administrador"&&(()=>{
        const hoje2 = new Date().toISOString().slice(0,10);
        const fmtH2 = iso => iso?new Date(iso).toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"}):"—";
        const fmtDur2 = iso => {
          if(!iso) return "—";
          const min=Math.floor((Date.now()-new Date(iso).getTime())/60000);
          if(min<1) return "agora";
          if(min<60) return min+"min";
          return Math.floor(min/60)+"h"+(min%60>0?String(min%60).padStart(2,"0")+"min":"");
        };
        const COR2={"Online":"#22c55e","Ocupado":"#f59e0b","Em reunião":"#ef4444","Ausente":"#9ca3af"};
        const totalOnline2  = presencas.filter(p=>p.status==="Online").length;
        const totalOcupado2 = presencas.filter(p=>p.status==="Ocupado"||p.status==="Em reunião").length;
        const totalAusente2 = presencas.filter(p=>p.status==="Ausente").length;
        return (
          <div>
            {/* KPIs */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
              {[
                {l:"Online",    v:totalOnline2,  c:"#22c55e"},
                {l:"Ocupado",   v:totalOcupado2, c:"#f59e0b"},
                {l:"Ausente",   v:totalAusente2, c:"#9ca3af"},
                {l:"Equipe",    v:equipe.filter(e=>e.ativo!==false).length, c:theme.blue},
              ].map(k=>(
                <Card key={k.l} style={{padding:"12px 16px",textAlign:"center"}}>
                  <p style={{fontSize:10,color:theme.gray400,textTransform:"uppercase",letterSpacing:.8,marginBottom:4}}>{k.l}</p>
                  <p style={{fontSize:24,fontWeight:700,color:k.c}}>{k.v}</p>
                </Card>
              ))}
            </div>
            {/* Tabela da equipe */}
            <Card style={{marginBottom:14}}>
              <p style={{fontSize:11,fontWeight:700,color:theme.gray700,textTransform:"uppercase",letterSpacing:.8,marginBottom:12}}>
                👥 Equipe — {new Date().toLocaleDateString("pt-BR",{weekday:"long",day:"2-digit",month:"long"})}
              </p>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead><tr style={{background:theme.gray50}}>
                    {["Membro","Status","Sala","Entrada","Tempo Online","Tarefa Ativa","Contratos em Andamento"].map(h=>(
                      <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:10,color:theme.gray400,
                        letterSpacing:.8,textTransform:"uppercase",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {equipe.filter(e=>e.ativo!==false).map((membro,i)=>{
                      const pres2 = presencas.find(p=>p.usuarioId===membro.id||p.nome===membro.nome);
                      const sala2 = salas.find(s=>s.id===pres2?.salaId);
                      const cts2  = contratos.filter(ct=>ct.responsavel===membro.id&&(ct.status==="Em andamento"||ct.status==="Ativo"));
                      return (
                        <tr key={membro.id} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                          <td style={{padding:"10px 12px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <div style={{width:32,height:32,borderRadius:"50%",background:(COR2[pres2?.status||"Ausente"])+"22",
                                display:"flex",alignItems:"center",justifyContent:"center",
                                fontSize:13,fontWeight:700,color:COR2[pres2?.status||"Ausente"]}}>
                                {membro.nome?.charAt(0)||"?"}
                              </div>
                              <div>
                                <p style={{fontSize:12,fontWeight:700,color:theme.gray900,margin:0}}>{membro.nome}</p>
                                <p style={{fontSize:10,color:theme.gray400,margin:0}}>{membro.funcao||"—"}</p>
                              </div>
                            </div>
                          </td>
                          <td style={{padding:"10px 12px"}}>
                            {pres2
                              ? <span style={{fontSize:11,padding:"3px 8px",borderRadius:10,fontWeight:600,
                                  background:(COR2[pres2.status]||"#9ca3af")+"18",color:COR2[pres2.status]||"#9ca3af",
                                  display:"flex",alignItems:"center",gap:4,width:"fit-content"}}>
                                  <span style={{width:7,height:7,borderRadius:"50%",background:COR2[pres2.status]||"#9ca3af",display:"inline-block"}}/>
                                  {pres2.status}
                                </span>
                              : <span style={{fontSize:11,color:theme.gray300}}>Não conectado</span>
                            }
                          </td>
                          <td style={{padding:"10px 12px"}}>
                            {sala2
                              ? <span style={{fontSize:10,padding:"2px 8px",borderRadius:8,background:sala2.cor+"18",color:sala2.cor,fontWeight:600}}>
                                  {sala2.icon} {sala2.nome}
                                </span>
                              : <span style={{color:theme.gray300,fontSize:11}}>—</span>
                            }
                          </td>
                          <td style={{padding:"10px 12px",fontSize:11,color:theme.gray500,whiteSpace:"nowrap"}}>
                            {pres2?.entradaEm?fmtH2(pres2.entradaEm):"—"}
                          </td>
                          <td style={{padding:"10px 12px",fontSize:12,fontWeight:600,
                            color:pres2?.status==="Online"?"#22c55e":theme.gray400,whiteSpace:"nowrap"}}>
                            {pres2?.entradaEm?fmtDur2(pres2.entradaEm):"—"}
                          </td>
                          <td style={{padding:"10px 12px"}}>
                            {pres2?.tarefaAtiva
                              ? <span style={{fontSize:11,padding:"2px 8px",borderRadius:6,background:"#dbeafe",color:theme.blue,fontWeight:600}}>
                                  📋 {pres2.tarefaAtiva}
                                </span>
                              : <span style={{fontSize:11,color:theme.gray300}}>—</span>
                            }
                          </td>
                          <td style={{padding:"10px 12px",fontSize:11,color:theme.gray600}}>
                            {cts2.length>0
                              ? cts2.slice(0,2).map(ct=>{
                                  const cli2=(data.clientes||[]).find(cl=>cl.id===ct.clienteId);
                                  return <div key={ct.id}><span style={{color:theme.gold}}>•</span> {ct.numContrato} · {cli2?.nome||"—"}</div>;
                                })
                              : <span style={{color:theme.gray300}}>—</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
            {/* Histórico do dia */}
            <Card>
              <p style={{fontSize:11,fontWeight:700,color:theme.gray700,textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>
                📋 Histórico de Presença — Hoje
              </p>
              {presencas.filter(p=>p.entradaEm?.slice(0,10)===hoje2).length===0
                ? <p style={{fontSize:12,color:theme.gray400,textAlign:"center",padding:"12px 0"}}>Nenhum registro hoje.</p>
                : <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead><tr style={{background:theme.gray50}}>
                      {["Membro","Sala","Entrada","Tempo","Status"].map(h=>(
                        <th key={h} style={{padding:"7px 12px",textAlign:"left",fontSize:10,color:theme.gray400,letterSpacing:.8,textTransform:"uppercase",fontWeight:600}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {presencas.filter(p=>p.entradaEm?.slice(0,10)===hoje2)
                        .sort((a,b)=>(a.entradaEm||"")>(b.entradaEm||"")?1:-1)
                        .map((p2,i)=>{
                          const s2=salas.find(s=>s.id===p2.salaId);
                          return (
                            <tr key={p2.usuarioId+i} style={{borderBottom:`1px solid ${theme.gray100}`,background:i%2===0?theme.white:theme.gray50}}>
                              <td style={{padding:"8px 12px",fontWeight:600,color:theme.gray900}}>{p2.nome}</td>
                              <td style={{padding:"8px 12px"}}>
                                {s2?<span style={{fontSize:10,padding:"2px 7px",borderRadius:8,background:s2.cor+"18",color:s2.cor,fontWeight:600}}>{s2.icon} {s2.nome}</span>:"—"}
                              </td>
                              <td style={{padding:"8px 12px",color:theme.gray500,whiteSpace:"nowrap"}}>{fmtH2(p2.entradaEm)}</td>
                              <td style={{padding:"8px 12px",fontWeight:600,color:p2.status==="Online"?"#22c55e":theme.gray500}}>{fmtDur2(p2.entradaEm)}</td>
                              <td style={{padding:"8px 12px"}}>
                                <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:600,
                                  background:(COR2[p2.status]||"#9ca3af")+"18",color:COR2[p2.status]||"#9ca3af"}}>
                                  {p2.status||"—"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
              }
            </Card>
          </div>
        );
      })()}

      {/* ── ABA PRODUTIVIDADE ── */}
      {abaMonitor==="produtividade"&&usuario?.perfil==="Administrador"&&(
        <PainelProdutividade
          equipe={equipe} contratos={contratos} baseOp={baseOp}
          lancamentos={lancamentos} clientes_sv={clientes_sv}
          theme={theme} Card={Card} fmtR={v=>"R$ "+(parseFloat(v)||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}
        />
      )}

    </div>
  );
}


export default function App() {
  React.useEffect(() => {
    console.log("%c📦 SOJ — build em execução: v35-contrato-autofill (2026-06-19 10:00)", "background:#1a1610;color:#C9A84C;padding:4px 10px;border-radius:4px;font-weight:bold");
  }, []);
  const [usuario, setUsuario] = useState(null);
  const [ativo, setAtivo] = useState(null);
  const [anterior, setAnterior] = useState(null); // módulo anterior para breadcrumb
  const [expanded, setExpanded] = useState(true);
  const [data, setDataRaw] = useState(INITIAL_DATA);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const salvarTimeout = useRef(null);
  const [coworkAberto,  setCoworkAberto]  = useState(false);
  const [coworkMini,    setCoworkMini]    = useState(false);
  const [salaAtiva,     setSalaAtiva]     = useState(false); // true = usuário entrou na sala
  const [novaVersao,    setNovaVersao]    = useState(false); // banner de atualização
  const [statusSave,    setStatusSave]    = useState(null);  // null | "salvando" | "salvo" | "erro"
  const versaoAtualRef = useRef(null);
  const dadosParaSalvar = useRef(null); // referência aos dados mais recentes para o beforeunload
  const ultimoAtualizadoEmRef = useRef(null); // controle de concorrência: timestamp da última sincronização conhecida com o servidor

  // Verificar nova versão a cada 5 minutos
  useEffect(() => {
    const checarVersao = () => {
      fetch('/asset-manifest.json?_=' + Date.now(), { cache: 'no-store' })
        .then(r => r.json())
        .then(manifest => {
          // Extrair o hash do nome do arquivo JS (ex: /static/js/main.1a0e40b3.js → 1a0e40b3)
          const files = manifest.files || {};
          const jsPath = files['main.js'] || Object.values(files).find(v=>v&&v.includes('/static/js/main.')&&v.endsWith('.js')) || '';
          const hashMatch = jsPath.match(/main\.([a-f0-9]+)\.js/);
          const hashAtual = hashMatch ? hashMatch[1] : jsPath;
          if (!hashAtual) return;
          if (!versaoAtualRef.current) {
            versaoAtualRef.current = hashAtual;
          } else if (hashAtual !== versaoAtualRef.current) {
            setNovaVersao(true);
          }
        })
        .catch(() => {});
    };
    checarVersao();
    const intervalo = setInterval(checarVersao, 5 * 60 * 1000);
    return () => clearInterval(intervalo);
  }, []);

  // Carregar dados do banco ao iniciar (após login)
  useEffect(() => {
    if (!usuario) return;
    setCarregando(true);
    apiGet('/dados.php')
      .then(res => {
        if (res.dados) {
          // Merge: INITIAL_DATA como base + dados do banco por cima
          // Merge de produtos: banco tem prioridade sobre INITIAL_DATA
          // Se produto foi editado no banco (mesmo ID), usa versão do banco
          const prodsBanco = res.dados.produtos || [];
          // Separar: excluídos, editados/novos
          const prodsExcluidos = new Set(prodsBanco.filter(p=>p._excluido).map(p=>p.id));
          const prodsEditados  = prodsBanco.filter(p=>!p._excluido);
          const idsEditados    = new Set(prodsEditados.map(p=>p.id));
          // Reconstruir lista completa:
          // 1. Produtos do INITIAL_DATA que NÃO foram excluídos e NÃO foram editados
          const prodsBaseIntactos = INITIAL_DATA.produtos.filter(p =>
            !prodsExcluidos.has(p.id) && !idsEditados.has(p.id)
          );
          // 2. Produtos editados (versão do banco tem prioridade)
          // 3. Produtos novos (só existem no banco)
          const prodsUsuario = prodsEditados;
          // Deduplicar por ID — remove duplicatas mantendo o último registro
          const dedup = (arr) => {
            if(!Array.isArray(arr)) return arr;
            const map = {};
            arr.forEach(item => { if(item?.id) map[item.id] = item; });
            return Object.values(map);
          };
          // Deduplicar produtos por NOME também — banco tem prioridade sobre INITIAL_DATA
          // Isso evita duplicatas quando o usuário criou manualmente um produto que
          // agora também existe no INITIAL_DATA com ID diferente
          const dedupProdutos = (base, usuario) => {
            const nomesUsuario = new Set(
              usuario.map(p => (p.nome||"").trim().toLowerCase())
            );
            const baseUnica = base.filter(p =>
              !nomesUsuario.has((p.nome||"").trim().toLowerCase())
            );
            return [...baseUnica, ...usuario];
          };
          const prodsFinal = dedupProdutos(prodsBaseIntactos, prodsUsuario);
          // IDs demo do INITIAL_DATA — nunca devem persistir no banco
          const DEMO_IDS = new Set(["LAN001","LAN002","LAN003","LAN004","LAN005","LAN006",
            "CLI001","CLI002","CONT001","DESP001","DESP002"]);
          const lancsLimpos = dedup((res.dados.lancamentos||[])).filter(l=>!DEMO_IDS.has(l.id));
          const despLimpas  = dedup((res.dados.despesas||[])).filter(d=>!DEMO_IDS.has(d.id));
          const contrsLimpos= dedup((res.dados.contratos||[])).filter(ct=>!DEMO_IDS.has(ct.id));
          const clisLimpos  = dedup((res.dados.clientes||[])).filter(cl=>!DEMO_IDS.has(cl.id));
          // Migrar campoRCT para produtos do banco que não têm o campo
          // (produtos criados antes desta versão)
          const MAPA_CAMPO_RCT_POR_NOME = {};
          INITIAL_DATA.produtos.forEach(p => {
            if (p.campoRCT && p.nome) {
              MAPA_CAMPO_RCT_POR_NOME[(p.nome||"").trim().toLowerCase()] = p.campoRCT;
            }
          });
          const prodsMigrados = prodsFinal.map(p => {
            if (p.campoRCT) return p; // já tem — não altera
            const chave = (p.nome||"").trim().toLowerCase();
            const tmpl = MAPA_CAMPO_RCT_POR_NOME[chave];
            if (tmpl) return {...p, campoRCT: tmpl};
            // Heurística por palavras-chave: se não tem match exato
            if (p.area === "Tributário" && !["campo_unico"].includes(p.campoRCT)) {
              const nL = chave;
              const isUnico = ["gestão de dívida","defesa em execução","defesa em crimes",
                "estudo e planejamento","brasileiros no exterior","transação tributária","capag",
                "revisão de capag"].some(k => nL.includes(k));
              return {...p, campoRCT: isUnico ? "campo_unico" : "60_meses"};
            }
            return p;
          });

          setDataRaw({
            ...INITIAL_DATA,
            ...res.dados,
            lancamentos: lancsLimpos,
            despesas:    despLimpas,
            contratos:   contrsLimpos,
            clientes:    clisLimpos,
            produtos: prodsMigrados,
            areas: res.dados.areas || INITIAL_DATA.areas,
            vinculos: res.dados.vinculos || INITIAL_DATA.vinculos,
            funcoes: res.dados.funcoes || INITIAL_DATA.funcoes,
            fichasCredito:    res.dados.fichasCredito    || INITIAL_DATA.fichasCredito    || [],
            catalogoTarefas:  res.dados.catalogoTarefas  || INITIAL_DATA.catalogoTarefas  || [],
          });
          ultimoAtualizadoEmRef.current = res.atualizado_em || null;
        }
        setCarregando(false);
      })
      .catch(e => {
        console.error('Erro ao carregar dados:', e);
        setErro('Erro ao conectar com o servidor: ' + e.message);
        setCarregando(false);
      });
  }, [usuario]);

  // ── Constantes e helpers de save ────────────────────────────────────
  const DEMO_IDS_SAVE = new Set(["LAN001","LAN002","LAN003","LAN004","LAN005","LAN006",
    "CLI001","CLI002","CONT001","DESP001","DESP002"]);
  const dedup3 = arr => {
    if(!Array.isArray(arr)) return arr||[];
    const m={}; arr.forEach(x=>{if(x?.id)m[x.id]=x;}); return Object.values(m);
  };

  // IDs dos produtos do INITIAL_DATA — para identificar produtos base não editados
  const INITIAL_PROD_MAP = {};
  INITIAL_DATA.produtos.forEach(p => { INITIAL_PROD_MAP[p.id] = JSON.stringify(p); });

  // Salvar apenas produtos que DIFEREM do INITIAL_DATA (editados, excluídos ou novos)
  // Produto "editado" = mesmo ID mas conteúdo diferente
  // Produto "novo"    = ID não existe no INITIAL_DATA
  // Produto "excluído" = ID do INITIAL_DATA não está mais na lista atual
  const produtosParaSalvar = (prods) => {
    const prodsAtuais = dedup3(prods||[]);
    const idsAtuais = new Set(prodsAtuais.map(p=>p.id));

    // 1. Produtos editados ou novos (presentes na lista atual)
    const editadosOuNovos = prodsAtuais.filter(p => {
      const base = INITIAL_PROD_MAP[p.id];
      // Novo (não existe no INITIAL_DATA) ou Editado (conteúdo diferente)
      return !base || base !== JSON.stringify(p);
    });

    // 2. Produtos excluídos: IDs que existiam no INITIAL_DATA mas não estão mais
    const excluidos = INITIAL_DATA.produtos
      .filter(p => !idsAtuais.has(p.id))
      .map(p => ({...p, _excluido: true})); // marcar como excluído

    return [...editadosOuNovos, ...excluidos];
  };

  // Limpar dados antes de enviar ao banco
  const limparDados = (nd) => ({
    ...nd,
    // Arrays com filtro de IDs demo
    lancamentos:     dedup3((nd.lancamentos||[]).filter(l=>!DEMO_IDS_SAVE.has(l.id))),
    despesas:        dedup3((nd.despesas||[]).filter(d=>!DEMO_IDS_SAVE.has(d.id))),
    contratos:       dedup3((nd.contratos||[]).filter(ct=>!DEMO_IDS_SAVE.has(ct.id))),
    clientes:        dedup3((nd.clientes||[]).filter(cl=>!DEMO_IDS_SAVE.has(cl.id))),
    // Arrays sem filtro de IDs demo — apenas deduplicação
    baseOperacional: dedup3(nd.baseOperacional||[]),
    leads:           dedup3(nd.leads||[]),
    // Produtos: salvar apenas os que diferem do INITIAL_DATA
    produtos:        produtosParaSalvar(nd.produtos||[]),
    diagnosticosRCT: dedup3(nd.diagnosticosRCT||[]),
    processos:       dedup3(nd.processos||[]),
    equipe:          dedup3(nd.equipe||[]),
    parcerias:       dedup3(nd.parcerias||[]),
    agenda:          dedup3(nd.agenda||[]),
    vendas:          dedup3(nd.vendas||[]),
    ocorrencias:     dedup3(nd.ocorrencias||[]),
  });

  // Enviar ao banco — com 1 retry automático após 3s
  // Controle de concorrência otimista: antes de gravar, confere se outra sessão
  // (outra aba/computador, ou um script) salvou depois da última sincronização
  // conhecida desta aba. Se sim, ABORTA o save para não sobrescrever silenciosamente
  // o trabalho de outra pessoa — o usuário é avisado e precisa recarregar (F5).
  const executarSave = (dadosLimpos, tentativa = 1) => {
    setStatusSave("salvando");
    return apiGet('/dados.php')
      .then(atual => {
        const conhecido = ultimoAtualizadoEmRef.current;
        const noServidor = atual?.atualizado_em || null;
        if (conhecido && noServidor && noServidor !== conhecido) {
          setStatusSave("conflito");
          window.alert(
            "⚠️ CONFLITO DE GRAVAÇÃO\n\n" +
            "Os dados foram alterados por outra sessão (outra aba, outro computador ou uma automação) depois que esta tela carregou.\n\n" +
            "Para não apagar o trabalho de ninguém, suas últimas alterações NÃO foram salvas.\n\n" +
            "Recarregue a página (F5) agora, refaça a última alteração e salve novamente."
          );
          throw new Error("CONFLITO_CONCORRENCIA");
        }
        return apiPost('/dados.php', { dados: dadosLimpos });
      })
      .then(respostaSave => {
        if (respostaSave?.atualizado_em) ultimoAtualizadoEmRef.current = respostaSave.atualizado_em;
        setStatusSave("salvo");
        setTimeout(() => setStatusSave(null), 3000);
      })
      .catch(e => {
        if (e.message === "CONFLITO_CONCORRENCIA") return; // já tratado acima — não retry, não sobrescrever
        console.error(`Erro ao salvar (tentativa ${tentativa}):`, e);
        if (tentativa < 2) {
          // Retry automático após 3 segundos
          setTimeout(() => executarSave(dadosLimpos, tentativa + 1), 3000);
        } else {
          setStatusSave("erro");
        }
      });
  };

  // Coleções identificadas por "id" — usadas para detectar exclusões automaticamente
  const COLECOES_COM_ID = ['clientes','leads','contratos','processos','parcerias','equipe',
    'produtos','agenda','pops','tarefas','baseOperacional','ocorrencias','documentosTrib',
    'agentes','catalogoTarefas','fichasCredito','lancamentos'];

  // Compara o estado anterior com o novo e detecta quais IDs desapareceram (exclusões)
  const detectarExclusoes = (prev, novo) => {
    const exclusoes = [];
    const agora = new Date().toISOString();
    COLECOES_COM_ID.forEach(col => {
      const idsAntes  = new Set((prev[col]||[]).map(it=>it && it.id).filter(Boolean));
      const idsDepois = new Set((novo[col]||[]).map(it=>it && it.id).filter(Boolean));
      idsAntes.forEach(id => { if (!idsDepois.has(id)) exclusoes.push({colecao:col, id, em:agora}); });
    });
    return exclusoes;
  };

  // setData: atualiza estado React + agenda save com debounce
  const setData = (newDataOrFn) => {
    setDataRaw(prev => {
      const newData = typeof newDataOrFn === "function" ? newDataOrFn(prev) : newDataOrFn;
      // Registrar exclusões como "tombstones" — impede que o merge do servidor as traga de volta
      const novasExclusoes = detectarExclusoes(prev, newData);
      const deletadosAtuais = newData._deletados || prev._deletados || [];
      const deletadosFinal = novasExclusoes.length > 0
        ? [...deletadosAtuais, ...novasExclusoes].slice(-2000) // limite para não crescer indefinidamente
        : deletadosAtuais;
      const newDataComTombstones = { ...newData, _deletados: deletadosFinal };
      const dadosLimpos = limparDados(newDataComTombstones);
      // Sempre manter ref atualizada com dados mais recentes — ANTES do debounce
      dadosParaSalvar.current = dadosLimpos;
      // Debounce: aguarda 1.5s de inatividade para salvar
      if (salvarTimeout.current) clearTimeout(salvarTimeout.current);
      salvarTimeout.current = setTimeout(() => {
        salvarTimeout.current = null; // limpar ref após execução
        executarSave(dadosParaSalvar.current);
      }, 1500);
      return newDataComTombstones;
    });
  };

  // Salvar imediatamente ao fechar/atualizar — usa dados da ref (sempre atualizados)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!dadosParaSalvar.current) return;
      // Cancelar debounce pendente se existir
      if (salvarTimeout.current) {
        clearTimeout(salvarTimeout.current);
        salvarTimeout.current = null;
      }
      const token = sessionStorage.getItem('ac_token') || '';
      // fetch com keepalive=true mantém a requisição viva mesmo após a página fechar
      // e permite enviar o header X-Token corretamente (sendBeacon não suporta headers)
      try {
        fetch(API_BASE + '/dados.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Token': token
          },
          body: JSON.stringify({ dados: dadosParaSalvar.current }),
          keepalive: true  // garante envio mesmo ao fechar a página
        });
      } catch(e) {
        // Fallback: sendBeacon se fetch keepalive não for suportado
        const blob = new Blob(
          [JSON.stringify({ dados: dadosParaSalvar.current })],
          { type: 'application/json' }
        );
        navigator.sendBeacon(API_BASE + '/dados.php?_token=' + token, blob);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Navegação com histórico — registra módulo anterior para breadcrumb
  const navegarPara = (destino) => {
    if (destino !== ativo) setAnterior(ativo);
    setAtivo(destino);
  };

  const handleLogin = (usr, token) => {
    if (token) sessionStorage.setItem('ac_token', token);
    setUsuario(usr);
    // Redireciona para primeiro modulo permitido
    const mods = usr.modulos || [];
    const primeiroMod = mods.includes("dashboard") ? "dashboard" : (mods.find(m => m !== "acesso") || mods[0] || "dashboard");
    setAtivo(primeiroMod);
  };

  if (!usuario) return (
    <ErrorBoundary>
      <style>{globalStyles}</style>
      <Login onLogin={handleLogin} usuarios={data.usuarios} />
    </ErrorBoundary>
  );

  const temAcesso = (modId) => usuario.perfil === "Administrador" || (usuario.modulos || []).includes(modId);
  const temProduto = (prodId) => !usuario.produtos?.length || usuario.produtos.includes(prodId);

  const sideWidth = expanded ? 220 : 64;

  const SemAcesso = () => (
    <div className="fade-in" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <div style={{ textAlign: "center", maxWidth: 400 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2 className="serif" style={{ fontSize: 24, color: theme.gray700, marginBottom: 8 }}>Acesso Restrito</h2>
        <p style={{ fontSize: 14, color: theme.gray500 }}>Você não tem permissão para acessar este módulo.</p>
        <p style={{ fontSize: 12, color: theme.gray400, marginTop: 6 }}>Solicite acesso ao administrador do sistema.</p>
      </div>
    </div>
  );

  // Tela de carregamento
  if (usuario && carregando) return (
    <ErrorBoundary>
      <style>{globalStyles}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#1A1610', flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 48, height: 48, border: '4px solid #C9A84C33', borderTop: '4px solid #C9A84C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <p style={{ color: '#C9A84C', fontFamily: 'Jost, sans-serif', fontSize: 14 }}>Carregando dados do servidor...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </ErrorBoundary>
  );

  // Tela de erro de conexão
  if (erro) return (
    <ErrorBoundary>
      <style>{globalStyles}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#1A1610' }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 40, maxWidth: 480, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ color: '#C94C4C', marginBottom: 12 }}>Erro de conexão</h2>
          <p style={{ color: '#666', marginBottom: 20, fontSize: 14 }}>{erro}</p>
          <button onClick={() => window.location.reload()} style={{ padding: '10px 24px', background: '#C9A84C', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>
            🔄 Tentar novamente
          </button>
        </div>
      </div>
    </ErrorBoundary>
  );

  const usuarioAtual = data.usuarios.find(u => u.id === usuario.id) || usuario;

  const renderConteudo = () => {
    if (!ativo) return null;
    if (!temAcesso(ativo)) return <SemAcesso />;
    // ── Guarda Sala Virtual ──────────────────────────────────
    const precisaSala = usuarioAtual?.exigeSala && usuarioAtual?.perfil !== "Administrador";
    if (precisaSala && !salaAtiva && ativo !== "sala" && ativo !== "acesso") {
      return (
        <div className="fade-in" style={{display:"flex",flexDirection:"column",
          alignItems:"center",justifyContent:"center",minHeight:"60vh",gap:20}}>
          <div style={{background:"#fff",borderRadius:16,padding:"40px 48px",
            boxShadow:"0 4px 24px rgba(0,0,0,.08)",textAlign:"center",maxWidth:420}}>
            <div style={{fontSize:48,marginBottom:16}}>🏢</div>
            <h2 style={{fontSize:20,fontWeight:700,color:theme.gray900,marginBottom:8}}>
              Acesso via Sala Virtual
            </h2>
            <p style={{fontSize:14,color:theme.gray500,marginBottom:24,lineHeight:1.6}}>
              Seu perfil requer entrada pela Sala Virtual antes de acessar os módulos do sistema.
            </p>
            <button onClick={()=>navegarPara("sala")} style={{
              padding:"12px 28px",borderRadius:8,background:theme.gold,
              color:"#fff",border:"none",cursor:"pointer",
              fontSize:14,fontWeight:700,letterSpacing:.3}}>
              🚪 Entrar na Sala Virtual
            </button>
          </div>
        </div>
      );
    }
    switch (ativo) {
      case "atendimentos": return <ModAtendimentos data={data} setData={setData} />;
      case "areas":    return <ModAreas data={data} setData={setData} />;
      case "processos": return <ModProcessos data={data} setData={setData} navegarPara={navegarPara} />;
      case "clientes": return <ModClientes data={data} setData={setData} />;
      case "produtos": return <ModProdutos data={data} setData={setData} setAtivo={navegarPara} />;
      case "dashtrib": return <ModDashTrib data={data} setData={setData} setAtivo={navegarPara} anterior={anterior} />;
      case "dashexec": return <ModDashboardExecutivo data={data} />;
      case "equipe": return <ModEquipe data={data} setData={setData} />;
      case "pops": return <ModPOPs data={data} setData={setData} />;
      case "arquivo": return <ModArquivo data={data} setData={setData} />;
      case "agenda": return <ModAgenda data={data} setData={setData} />;
      case "vendas": return <ModVendas data={data} setData={setData} />;
      case "marketing": return <ModMarketing data={data} setData={setData} />;
      case "parcerias": return <ModParcerias data={data} setData={setData} />;
      case "base": return <ModBase data={data} setData={setData} usuario={usuario} />;
      case "financeiro": return <ModFinanceiro data={data} setData={setData} />;
      case "acesso":     return <ModAcesso data={data} setData={setData} usuario={usuario} />;
      case "escritorio": return <ModEscritorio data={data} setData={setData} />;
      case "dashboard": return <ModDashboard data={data} setAtivo={navegarPara} />;
      case "crm": return <ModCRM data={data} setData={setData} />;
      case "rct": return <ModRCT data={data} setData={setData} />;
      case "relatorios": return <ModRelatorios data={data} setData={setData} />;
      case "governanca": return <ModGovernanca data={data} setData={setData} />;
      case "painelia":   return <ModPainelIA data={data} setData={setData} usuario={usuario} />;
      case "manual":    return <ModManual data={data} usuario={usuario} />;
      case "sala":     return <ModSalaVirtual data={data} setData={setData} usuario={usuario} navegarPara={navegarPara} salaAtiva={salaAtiva} setSalaAtiva={setSalaAtiva} />;
      case "cowork":    return <ModulePlaceholder title="Assistente Cowork" fase="Temporariamente desativado" descricao="Este módulo foi desativado temporariamente nesta versão." />;
      default: return (
        <ModulePlaceholder
          title={MENU.find(m => m.id === ativo)?.label || "Módulo"}
          fase={MENU.find(m => m.id === ativo)?.badge || "Próxima fase"}
          descricao="Este módulo será construído na próxima fase do desenvolvimento."
        />
      );
    }
  };

  // Sincroniza usuario com dados atualizados (ex: admin muda suas próprias permissões)

  return (
    <ErrorBoundary>
    <UsuarioContext.Provider value={usuarioAtual}>
      <style>{globalStyles}</style>
      <div style={{ display: "flex", minHeight: "100vh" }}>
        <Sidebar
          ativo={ativo} setAtivo={setAtivo} setAnterior={setAnterior}
          expanded={expanded} setExpanded={setExpanded}
          usuario={usuarioAtual} onLogout={() => {
              // Limpar presença e resetar entradaEm antes de sair
              setDataRaw(prev => {
                const svP = prev.salaVirtual || {};
                const presLimpas = (svP.presencas||[]).map(p =>
                  p.usuarioId === usuario?.id
                    ? {...p, status:"Ausente", entradaEm: null, ultimaAtiv: null, tarefaAtiva:""}
                    : p
                );
                return {...prev, salaVirtual: {...svP, presencas: presLimpas}};
              });
              // Salvar imediatamente antes do logout
              setTimeout(() => {
                apiPost('/auth.php?action=logout', {}).catch(()=>{});
                sessionStorage.removeItem('ac_token');
                setUsuario(null);
                setAtivo(null);
                setSalaAtiva(false);
                setDataRaw(INITIAL_DATA);
              }, 800);
            }}
          modulosPermitidos={usuarioAtual.perfil === "Administrador" ? TODOS_MODULOS.map(m=>m.id) : (usuarioAtual.modulos || [])}
        />
        <main style={{
          marginLeft: sideWidth,
          width: `calc(100% - ${sideWidth}px)`,
          boxSizing: "border-box", overflowX: "hidden",
          padding: "32px 36px", minHeight: "100vh",
          background: theme.gray50, transition: "margin-left 0.3s ease, width 0.3s ease"
        }}>
          {anterior && anterior !== ativo && (() => {
            const itemAnterior = MENU.find(m => m.id === anterior);
            if (!itemAnterior) return null;
            return (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                marginBottom: 16, padding: "7px 14px",
                background: theme.white, borderRadius: 8,
                border: `1px solid ${theme.gray200}`,
              }}>
                <button
                  onClick={() => { setAtivo(anterior); setAnterior(null); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: 13, color: theme.gray600, fontWeight: 500, padding: 0
                  }}
                >
                  <span>←</span>
                  <span>Voltar para {itemAnterior.icon} {itemAnterior.label}</span>
                </button>
              </div>
            );
          })()}
          {renderConteudo()}
        </main>
        {/* CoworkFlutuante temporariamente desativado nesta versão */}
      </div>

      {/* ── Indicador de status de save ── */}
      {statusSave && (
        <div style={{
          position: "fixed", top: 12, right: 20, zIndex: 99998,
          display: "flex", alignItems: "center", gap: 8,
          padding: "6px 14px", borderRadius: 20,
          fontSize: 12, fontWeight: 600,
          background: statusSave==="salvando" ? "#1a1610"
                    : statusSave==="salvo"    ? "#166534"
                    : statusSave==="conflito" ? "#b45309"
                    : "#dc2626",
          color: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          transition: "all 0.3s ease"
        }}>
          {statusSave==="salvando" && <><span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span> Salvando...</>}
          {statusSave==="salvo"    && <>✓ Salvo</>}
          {statusSave==="conflito" && <>⚠️ Conflito — recarregue a página (F5)</>}
          {statusSave==="erro"     && <>⚠️ Erro ao salvar — verifique a conexão</>}
        </div>
      )}

      {/* ── Banner de nova versão disponível ── */}
      {novaVersao && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          zIndex: 99999, display: "flex", alignItems: "center", gap: 14,
          background: "#1a1610", color: "#fff",
          padding: "14px 24px", borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
          border: "1px solid #C9A84C",
          fontSize: 13, fontWeight: 500,
          animation: "slideUp 0.3s ease"
        }}>
          <span style={{fontSize: 20}}>🔄</span>
          <span>Nova versão disponível — atualize para usar as últimas funcionalidades.</span>
          <button
            onClick={() => window.location.reload(true)}
            style={{
              background: "#C9A84C", color: "#1a1610",
              border: "none", borderRadius: 8,
              padding: "8px 18px", cursor: "pointer",
              fontWeight: 700, fontSize: 13,
              whiteSpace: "nowrap"
            }}>
            Atualizar agora
          </button>
          <button
            onClick={() => setNovaVersao(false)}
            style={{
              background: "none", color: "#aaa",
              border: "none", cursor: "pointer",
              fontSize: 18, padding: "0 4px", lineHeight: 1
            }}>
            ✕
          </button>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>

    </UsuarioContext.Provider>
    </ErrorBoundary>
  );
}